/* eslint-disable */
// VERSION: 18 — SECURITY HARDENED
import React, { useState, useEffect, useRef } from "react";
if(typeof navigator!=="undefined"&&"serviceWorker"in navigator){window.addEventListener("load",()=>{navigator.serviceWorker.register("/sw.js").catch(()=>{});});}
let deferredInstallPrompt=null;
if(typeof window!=="undefined")window.addEventListener("beforeinstallprompt",e=>{e.preventDefault();deferredInstallPrompt=e;});

// ── SUPABASE + AI PROXY CONFIG ─────────────────────────────────────────────


const SUPABASE_URL      = "https://qnjlirkgumhzzcavqsxt.supabase.co";
const SUPABASE_ANON_KEY = "sb_publishable_WA0C6J3VIvRnk4ydqIFRFA_-9V4F_6a";
const AI_PROXY_URL      = "https://qnjlirkgumhzzcavqsxt.supabase.co/functions/v1/ai-proxy";
// ⚠️  Anthropic key removed from frontend — all AI calls go through the secure proxy.
// The key lives only in Supabase Edge Function secrets (never in client code).

// ── AI ATTACHMENT SIZE GUARD ───────────────────────────────────────────────
// The edge runtime DROPS oversized request bodies at the boundary, BEFORE our
// handler runs, so the browser sees a phantom CORS failure instead of a real
// error. Guard on the client: downscale images to fit, and reject PDFs that are
// too big to forward. Keep AI_MAX_B64_CHARS in sync with MAX_PAYLOAD_BYTES in
// supabase/functions/ai-proxy/index.ts (that server check is the second line of
// defense for bodies that do arrive).
const AI_MAX_B64_CHARS = 4_500_000; // ~3.4 MB raw per attachment (base64 ≈ bytes × 4/3)

// Downscale one image dataURL to ≤1920px on the long edge, JPEG q0.85→0.70, so
// its base64 stays under the per-attachment cap. Returns a fresh JPEG dataURL.
function downscaleImageForAI(dataUrl) {
  return new Promise((resolve, reject) => {
    const MAX_W = 1920, TARGET = AI_MAX_B64_CHARS;
    const img = new Image();
    img.onerror = () => reject(new Error("Image decode failed"));
    img.onload = () => {
      try {
        const scale = img.naturalWidth > MAX_W ? MAX_W / img.naturalWidth : 1;
        const w = Math.round(img.naturalWidth * scale), h = Math.round(img.naturalHeight * scale);
        const cnv = document.createElement("canvas");
        cnv.width = w; cnv.height = h;
        cnv.getContext("2d").drawImage(img, 0, 0, w, h);
        const finish = (blob) => { const r = new FileReader(); r.onload = (e) => resolve(e.target.result); r.onerror = () => reject(new Error("Blob read failed")); r.readAsDataURL(blob); };
        cnv.toBlob((b85) => {
          if (!b85) return reject(new Error("Canvas encode failed"));
          if ((b85.size * 4 / 3) <= TARGET) return finish(b85); // base64 length ≈ bytes × 4/3
          cnv.toBlob((b70) => b70 ? finish(b70) : reject(new Error("Canvas encode failed at 0.70")), "image/jpeg", 0.70);
        }, "image/jpeg", 0.85);
      } catch (e) { reject(e); }
    };
    img.src = dataUrl;
  });
}

// Prepare a blueprint attachment for the AI proxy. Images are downscaled;
// oversized PDFs (which we can't recompress in-browser) throw a readable error
// instead of tripping the runtime payload cliff. Returns { data, mediaType,
// isPDF } where data is the bare base64 (no dataURL prefix).
async function prepBlueprintForAI(bp) {
  const isPDF = bp?.fileType === "pdf" || bp?.name?.toLowerCase().endsWith(".pdf");
  if (isPDF) {
    const data = (bp?.dataUrl || "").split(",")[1] || "";
    if (data.length > AI_MAX_B64_CHARS) {
      const mb = (data.length * 3 / 4 / (1024 * 1024)).toFixed(1);
      const err = new Error(`This PDF is too large to analyze whole (${mb} MB). Reduce it to under ~3 MB or split it into fewer pages, then re-upload.`);
      err.code = "PDF_TOO_LARGE";
      err.mb = mb;
      throw err;
    }
    return { data, mediaType: "application/pdf", isPDF: true };
  }
  const small = await downscaleImageForAI(bp?.dataUrl || "");
  return { data: small.split(",")[1] || "", mediaType: "image/jpeg", isPDF: false };
}

// Render one page of a PDF blueprint to a JPEG dataURL, reusing the same
// pdfjs-dist machinery as <PdfCanvas/>. This is the interim fallback for PDFs
// too large to forward whole: the single rasterized page is a normal image that
// sails under the size cap. Rendered at ~2000px on the long edge so drawing text
// stays legible; the caller runs it through downscaleImageForAI to enforce the
// per-attachment cap.
async function renderPdfPageToImage(dataUrl, pageNum) {
  const pdfjs = await import("pdfjs-dist/legacy/build/pdf");
  pdfjs.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js";
  const commaIdx = (dataUrl || "").indexOf(",");
  const b64 = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : (dataUrl || "");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const doc = await pdfjs.getDocument({data: bytes}).promise;
  const n = Math.max(1, Math.min(pageNum || 1, doc.numPages));
  const page = await doc.getPage(n);
  const base = page.getViewport({scale: 1});
  const scale = Math.min(2000 / Math.max(base.width, base.height, 1), 3) || 1;
  const viewport = page.getViewport({scale: scale > 0 ? scale : 1});
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  await page.render({canvasContext: canvas.getContext("2d"), viewport}).promise;
  return canvas.toDataURL("image/jpeg", 0.92);
}

// Render ONLY the currently-visible (zoomed + scrolled) region of a PDF page to
// a JPEG dataURL. `frac` is {x,y,w,h} as fractions [0..1] of the page. This is
// the RELIABLE path for reading fine detail-sheet callouts: a whole D-size sheet
// downscaled to the AI size cap leaves leader-line text illegible even to a
// high-res-vision model, but a zoomed crop renders that same text large enough
// to read. We render the page at a scale chosen so the cropped region lands near
// ~2600px on its long edge (capped so the full-page canvas stays under browser
// limits), then cut out the region. The caller runs downscaleImageForAI on the
// result to enforce the per-attachment size cap.
async function renderPdfRegionToImage(dataUrl, pageNum, frac) {
  const f = frac || {x:0,y:0,w:1,h:1};
  const fx = Math.max(0, Math.min(1, f.x || 0));
  const fy = Math.max(0, Math.min(1, f.y || 0));
  const fw = Math.max(0.02, Math.min(1 - fx, f.w || 1));
  const fh = Math.max(0.02, Math.min(1 - fy, f.h || 1));
  const pdfjs = await import("pdfjs-dist/legacy/build/pdf");
  pdfjs.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js";
  const commaIdx = (dataUrl || "").indexOf(",");
  const b64 = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : (dataUrl || "");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const doc = await pdfjs.getDocument({data: bytes}).promise;
  const n = Math.max(1, Math.min(pageNum || 1, doc.numPages));
  const page = await doc.getPage(n);
  const base = page.getViewport({scale: 1});
  // Scale so the region's long edge ≈ 2600px, but keep the full-page canvas
  // under ~6000px on its long edge (canvas allocation limits on mobile).
  const regionLongAt1 = Math.max(base.width * fw, base.height * fh, 1);
  const pageLongAt1 = Math.max(base.width, base.height, 1);
  let scale = 2600 / regionLongAt1;
  scale = Math.min(scale, 6000 / pageLongAt1);
  scale = Math.max(0.5, Math.min(scale, 8));
  const viewport = page.getViewport({scale});
  const full = document.createElement("canvas");
  full.width = Math.ceil(viewport.width);
  full.height = Math.ceil(viewport.height);
  await page.render({canvasContext: full.getContext("2d"), viewport}).promise;
  const sx = Math.floor(fx * full.width);
  const sy = Math.floor(fy * full.height);
  const sw = Math.max(1, Math.floor(fw * full.width));
  const sh = Math.max(1, Math.floor(fh * full.height));
  const crop = document.createElement("canvas");
  crop.width = sw;
  crop.height = sh;
  crop.getContext("2d").drawImage(full, sx, sy, sw, sh, 0, 0, sw, sh);
  return crop.toDataURL("image/jpeg", 0.92);
}

// POST an analysis request to the AI proxy and return {text, stopReason}.
// Blueprint review + deep analysis send stream:true so a long Sonnet generation
// on a multi-page PDF doesn't exceed the gateway's non-stream timeout — the
// 10-page whole-set review was returning HTTP 504 because the gateway waited for
// the entire (slow) non-stream response. Streaming flushes the first bytes
// immediately, so the gateway sees a live response. We accumulate the SSE
// text_delta chunks into the same final string the non-stream path produced, and
// call onDelta(partialText) as chunks arrive for progressive render. Also reads
// stop_reason from message_delta so the max_tokens continuation loop still works.
// Falls back to parsing a JSON response if the proxy didn't stream.
// fetch() with a hard timeout (and optional external abort signal) so a stalled
// tile/classify request can never hang the whole analysis. Aborts on EITHER the
// per-call timeout or the caller's overall deadline.
async function fetchWithTimeout(url, opts, ms, externalSignal) {
  const ctrl = new AbortController();
  const onExt = () => ctrl.abort();
  if (externalSignal) { if (externalSignal.aborted) ctrl.abort(); else externalSignal.addEventListener("abort", onExt); }
  const t = setTimeout(() => ctrl.abort(), ms);
  try { return await fetch(url, { ...opts, signal: ctrl.signal }); }
  finally { clearTimeout(t); if (externalSignal) externalSignal.removeEventListener("abort", onExt); }
}

// SHA-256 of a file's bytes (hex) — content fingerprint for duplicate detection,
// so the same drawing never gets analyzed (and billed) twice. Returns "" if the
// Web Crypto API isn't available (older/insecure context).
async function sha256Hex(fileOrBuffer) {
  try {
    if (!crypto?.subtle) return "";
    const buf = fileOrBuffer instanceof ArrayBuffer ? fileOrBuffer : await fileOrBuffer.arrayBuffer();
    const digest = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, "0")).join("");
  } catch { return ""; }
}

async function proxyStreamText(reqBody, headers, onDelta) {
  // Idle-abort: if no bytes arrive for 90s the stream is stalled — abort so the
  // caller sees an error instead of hanging forever (a prime empty-run cause).
  const ctrl = new AbortController();
  let idleTimer;
  const bumpIdle = () => { clearTimeout(idleTimer); idleTimer = setTimeout(() => ctrl.abort(), 90000); };
  bumpIdle();
  const usage = { input: 0, output: 0 };
  let resp;
  try {
    resp = await fetch(AI_PROXY_URL, {method:"POST", headers, body: JSON.stringify(reqBody), signal: ctrl.signal});
  } catch (e) {
    clearTimeout(idleTimer);
    throw new Error(e && e.name === "AbortError" ? "AI request timed out (no response in 90s)" : ((e && e.message) || "AI request failed"));
  }
  if (!resp.ok) {
    clearTimeout(idleTimer);
    const raw = await resp.text().catch(() => "");
    let data; try { data = JSON.parse(raw); } catch {}
    throw new Error(errString(data && data.error) || ("HTTP " + resp.status + (raw ? ": " + raw.slice(0, 160) : "")));
  }
  const ct = resp.headers.get("content-type") || "";
  try {
    if (ct.includes("text/event-stream") && resp.body) {
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "", text = "", stopReason = null, streamErr = null;
      // Diagnostics: an empty review is a 200 stream with no text_delta events.
      // Capture status/bytes/event-type histogram/stop_reason so a caller can log
      // exactly what came back on an empty run (see alog.emptyDiag).
      let bytes = 0; const events = {};
      for (;;) {
        let r;
        try { r = await reader.read(); }
        catch (e) { throw new Error(e && e.name === "AbortError" ? "AI stream stalled (no data for 90s)" : ((e && e.message) || "AI stream read error")); }
        if (r.done) break;
        bumpIdle();
        bytes += (r.value && r.value.length) || 0;
        buf += dec.decode(r.value, {stream:true});
        let nl;
        while ((nl = buf.indexOf("\n")) >= 0) {
          const s = buf.slice(0, nl).trim();
          buf = buf.slice(nl + 1);
          if (!s.startsWith("data:")) continue;
          const payload = s.slice(5).trim();
          if (!payload || payload === "[DONE]") continue;
          let ev; try { ev = JSON.parse(payload); } catch { continue; }
          const evKey = ev.type === "content_block_delta" && ev.delta ? "delta:" + ev.delta.type
            : ev.type === "content_block_start" && ev.content_block ? "block:" + ev.content_block.type
            : ev.type;
          events[evKey] = (events[evKey] || 0) + 1;
          if (ev.type === "content_block_delta" && ev.delta && ev.delta.type === "text_delta") {
            text += ev.delta.text || "";
            if (onDelta) onDelta(text);
          } else if (ev.type === "message_start" && ev.message && ev.message.usage) {
            usage.input = ev.message.usage.input_tokens || usage.input;
          } else if (ev.type === "message_delta") {
            if (ev.delta && ev.delta.stop_reason) stopReason = ev.delta.stop_reason;
            if (ev.usage && ev.usage.output_tokens != null) usage.output = ev.usage.output_tokens;
          } else if (ev.type === "error") {
            streamErr = (ev.error && ev.error.message) || "AI stream error";
          }
        }
      }
      if (streamErr) throw new Error(streamErr);
      const diag = { httpStatus: resp.status, ct, bytes, events, stopReason, inputTokens: usage.input, outputTokens: usage.output };
      return { text, stopReason, usage, diag };
    }
    // Non-stream JSON fallback (proxy chose not to stream).
    const raw = await resp.text();
    let data; try { data = JSON.parse(raw); } catch { throw new Error("AI proxy returned non-JSON: " + raw.slice(0, 200)); }
    if (data && data.error) throw new Error(errString(data.error));
    if (data && data.usage) { usage.input = data.usage.input_tokens || 0; usage.output = data.usage.output_tokens || 0; }
    return { text: (data && data.content && data.content[0] && data.content[0].text) || "", stopReason: data && data.stop_reason, usage };
  } finally {
    clearTimeout(idleTimer);
  }
}

// ---- Detail-sheet tiling — character-exact callout extraction ----------------
// The whole-set Sonnet review reaches the detail sheets but garbles fine
// leader-line text at full-sheet resolution ("CM 1000" for "CIM 1000"). To read
// callouts CHARACTER-EXACT inside the ONE upload→analyze flow, we render the
// detail sheets at HIGH DPI in overlapping tiles and transcribe each tile with
// the same high-res-vision model as the review (Sonnet). The deduped verbatim
// strings are injected into the review as authoritative text.
//
// Two things make this work where the whole-set pass fails:
//  1. RESOLUTION. A single canvas caps near ~16.7M px on Safari (≈5000×3333 —
//     only ~120 DPI on a D-size sheet, where 0.1" callout text is ~12px and
//     garbles). So instead of rendering a whole page then cropping, we render
//     each tile region DIRECTLY at high scale into a small canvas via a pdfjs
//     translate transform. PDF user units are 1/72", so scale = DPI/72 maps
//     straight to DPI, and each tile canvas stays well under the browser limit.
//  2. TARGETING. We first classify which sheets are detail/section sheets and
//     spend the tile budget only on those — otherwise a cap could be exhausted
//     on cover/plan/schedule sheets before ever reaching the details.
// Tiles + classification carry NO company_id, so the fan-out is unmetered; the
// review call alone meters (weighted). renderPdfRegionToImage stays as separate
// internal machinery.
const TILE_TARGET_DPI = 200;   // render detail sheets at ~200 DPI so fine callouts are large
const TILE_PX = 2400;          // tile long edge (px) — just under Sonnet's 2576 vision ceiling
const TILE_OVERLAP = 320;      // px overlap so a callout on a seam isn't split
const MAX_PAGES_TILED = 12;    // classify at most this many pages
const MAX_TILES_TOTAL = 44;    // hard cap on tile transcription calls per analysis
const TILE_CONCURRENCY = 4;    // parallel tile calls

// Run async tasks with a concurrency cap; preserves input order in the result.
async function runPool(items, limit, worker) {
  const results = new Array(items.length);
  let i = 0;
  const run = async () => {
    while (i < items.length) {
      const idx = i++;
      try { results[idx] = await worker(items[idx], idx); } catch { results[idx] = null; }
    }
  };
  await Promise.all(Array.from({length: Math.min(limit, items.length || 1)}, run));
  return results;
}

// Append a blueprint-analysis run record to a localStorage ring buffer (last 25)
// so before/after numbers can be read back with
// JSON.parse(localStorage.getItem("grok_bp_runlog")). Each record carries stage
// timings (tilesMs, ttftMs, reviewMs, totalMs), tile p50/p95, retry counts,
// continuation count, and the failure stage — the instrumentation the speed work
// is gated on. Best-effort; never throws into the analysis path.
function persistBpRun(rec) {
  try {
    const KEY = "grok_bp_runlog";
    const prior = JSON.parse(localStorage.getItem(KEY) || "[]");
    const arr = Array.isArray(prior) ? prior : [];
    arr.push(rec);
    while (arr.length > 25) arr.shift();
    localStorage.setItem(KEY, JSON.stringify(arr));
  } catch { /* localStorage full/blocked — instrumentation is non-critical */ }
}

// Load a PDF dataURL into a pdfjs document.
async function loadPdfDoc(dataUrl) {
  const pdfjs = await import("pdfjs-dist/legacy/build/pdf");
  pdfjs.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js";
  const commaIdx = (dataUrl || "").indexOf(",");
  const b64 = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : (dataUrl || "");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return pdfjs.getDocument({ data: bytes }).promise;
}

// Render a whole page to a small JPEG thumbnail (for detail-sheet classification).
async function renderPageThumb(page, longEdge) {
  const base = page.getViewport({ scale: 1 });
  const scale = Math.min(longEdge / Math.max(base.width, base.height, 1), 4) || 1;
  const vp = page.getViewport({ scale: scale > 0 ? scale : 1 });
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(vp.width); canvas.height = Math.ceil(vp.height);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport: vp }).promise;
  const url = canvas.toDataURL("image/jpeg", 0.7);
  canvas.width = canvas.height = 0;
  return url;
}

// Render ONE tile region of a page directly at `scale` into a small canvas, via
// a translate transform so we never allocate a full high-DPI page canvas.
async function renderTile(page, scale, tx, ty, tw, th) {
  const canvas = document.createElement("canvas");
  canvas.width = tw; canvas.height = th;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, tw, th); // PDFs can be transparent
  const vp = page.getViewport({ scale });
  await page.render({ canvasContext: ctx, viewport: vp, transform: [1, 0, 0, 1, -tx, -ty] }).promise;
  const url = canvas.toDataURL("image/jpeg", 0.85);
  canvas.width = canvas.height = 0;
  return url;
}

// Classify which 1-based pages are detail/section sheets (where product callouts
// live), so we spend the tile budget there and not on cover/plan/schedule sheets.
// One cheap unmetered Haiku call over page thumbnails. Returns null on failure so
// the caller falls back to tiling every page.
async function classifyDetailPages(doc, pageCount, headers, signal) {
  const content = [];
  for (let p = 1; p <= pageCount; p++) {
    let thumb = null;
    try { thumb = await renderPageThumb(await doc.getPage(p), 1400); } catch {}
    if (!thumb) continue;
    content.push({ type: "text", text: `Page ${p}:` });
    content.push({ type: "image", source: { type: "base64", media_type: "image/jpeg", data: thumb.split(",")[1] || "" } });
  }
  if (!content.length) return { pages: null, usage: { input: 0, output: 0 } };
  content.push({ type: "text", text: "Each image above is a page from a construction drawing set, labeled with its page number. For EACH page decide if it is a DETAIL or SECTION sheet — one that carries product/material callouts, leader lines, or enlarged detail views — as opposed to a cover, index, general-notes, site plan, floor/roof plan, elevation, or schedule sheet. Return ONLY a JSON array of the 1-based page numbers that are detail/section sheets, e.g. [4,5,6]. If unsure about a page, include it. No prose." });
  const body = { model: "claude-haiku-4-5", max_tokens: 200, usage_type: "blueprint_tile", messages: [{ role: "user", content }] };
  try {
    const resp = await fetchWithTimeout(AI_PROXY_URL, { method:"POST", headers, body: JSON.stringify(body) }, 60000, signal);
    if (!resp.ok) return { pages: null, usage: { input: 0, output: 0 } };
    const d = JSON.parse(await resp.text());
    const usage = { input: (d.usage && d.usage.input_tokens) || 0, output: (d.usage && d.usage.output_tokens) || 0 };
    const text = (d && d.content && d.content[0] && d.content[0].text) || "";
    const s = text.indexOf("["), e = text.lastIndexOf("]");
    if (s < 0 || e <= s) return { pages: null, usage };
    const arr = JSON.parse(text.slice(s, e + 1));
    const pages = Array.isArray(arr) ? arr.map(Number).filter(n => Number.isInteger(n) && n >= 1 && n <= pageCount) : [];
    return { pages: pages.length ? Array.from(new Set(pages)).sort((a, b) => a - b) : null, usage };
  } catch { return { pages: null, usage: { input: 0, output: 0 } }; }
}

// Transcribe one tile CHARACTER-EXACT with the high-res-vision model (Sonnet).
// Sent WITHOUT company_id so it isn't metered. Returns { out, usage, ok }.
async function transcribeTile(tileDataUrl, headers, signal) {
  const data = tileDataUrl.split(",")[1] || "";
  const body = {
    model: BLUEPRINT_ANALYSIS_MODEL, // Sonnet: high-res vision reads fine callouts exactly
    max_tokens: 1200,
    // Disable extended thinking: a dense tile could otherwise spend this small
    // 1200-token budget thinking and return zero callouts (same failure mode as
    // the review). Transcription is deterministic and needs no thinking.
    thinking: { type: "disabled" },
    usage_type: "blueprint_tile", // unmetered: no company_id sent below
    system: "You transcribe construction-drawing callouts. Copy text CHARACTER-EXACT — never correct spelling, expand abbreviations, round numbers, or guess. Reproduce exactly what is printed, letter for letter and digit for digit.",
    messages: [{ role: "user", content: [
      { type: "image", source: { type: "base64", media_type: "image/jpeg", data } },
      { type: "text", text: "This is a high-resolution crop of a construction detail sheet. List EVERY product, manufacturer, model/spec number, material, thickness, mil coverage, application rate, and dimension callout visible in this crop, each copied VERBATIM — exact characters, do not drop or add a letter or digit. Return ONLY a JSON array of strings, e.g. [\"<callout>\",\"<callout>\"]. If nothing legible, return []. No prose, no code fence." },
    ]}],
  };
  const empty = { out: [], usage: { input: 0, output: 0 }, ok: false };
  try {
    const resp = await fetchWithTimeout(AI_PROXY_URL, { method:"POST", headers, body: JSON.stringify(body) }, 45000, signal);
    if (!resp.ok) return empty;
    const d = JSON.parse(await resp.text());
    const usage = { input: (d.usage && d.usage.input_tokens) || 0, output: (d.usage && d.usage.output_tokens) || 0 };
    const text = (d && d.content && d.content[0] && d.content[0].text) || "";
    const start = text.indexOf("["), end = text.lastIndexOf("]");
    if (start < 0 || end <= start) return { out: [], usage, ok: true };
    const arr = JSON.parse(text.slice(start, end + 1));
    return { out: Array.isArray(arr) ? arr.filter(s => typeof s === "string" && s.trim()).map(s => s.trim()) : [], usage, ok: true };
  } catch { return empty; }
}

// Full pipeline: classify detail sheets → render their tiles at high DPI →
// transcribe each. Returns { callouts, stats }. Best-effort. An overall 180s
// deadline + per-call timeouts guarantee it can't hang the analysis.
async function extractDetailCallouts(dataUrl, headers, onProgress) {
  const stats = { classified: null, pagesTiled: 0, tilesTotal: 0, tilesOk: 0, tileRetries: 0, tokensIn: 0, tokensOut: 0, timedOut: false };
  if (!dataUrl) return { callouts: [], stats };
  const overall = new AbortController();
  const dl = setTimeout(() => { stats.timedOut = true; overall.abort(); }, 180000);
  try {
    const doc = await loadPdfDoc(dataUrl);
    const pageCount = Math.min(doc.numPages, MAX_PAGES_TILED);
    let pages = null;
    try {
      const cl = await classifyDetailPages(doc, pageCount, headers, overall.signal);
      pages = cl.pages; stats.tokensIn += cl.usage.input; stats.tokensOut += cl.usage.output;
    } catch {}
    if (!pages || !pages.length) { pages = Array.from({ length: pageCount }, (_, i) => i + 1); stats.classified = "all"; }
    else stats.classified = pages.slice();
    stats.pagesTiled = pages.length;

    // Render the tiles (sequentially — pdfjs can't render one page concurrently).
    const step = Math.max(1, TILE_PX - TILE_OVERLAP);
    const scale = TILE_TARGET_DPI / 72; // PDF units are 1/72"
    const tiles = [];
    for (const p of pages) {
      if (tiles.length >= MAX_TILES_TOTAL) break;
      let page; try { page = await doc.getPage(p); } catch { continue; }
      const base = page.getViewport({ scale: 1 });
      const fullW = Math.ceil(base.width * scale), fullH = Math.ceil(base.height * scale);
      const cols = Math.max(1, Math.ceil((fullW - TILE_OVERLAP) / step));
      const rows = Math.max(1, Math.ceil((fullH - TILE_OVERLAP) / step));
      for (let r = 0; r < rows && tiles.length < MAX_TILES_TOTAL; r++) {
        for (let c = 0; c < cols && tiles.length < MAX_TILES_TOTAL; c++) {
          const tx = Math.min(c * step, Math.max(0, fullW - TILE_PX));
          const ty = Math.min(r * step, Math.max(0, fullH - TILE_PX));
          const tw = Math.min(TILE_PX, fullW - tx);
          const th = Math.min(TILE_PX, fullH - ty);
          if (tw < 60 || th < 60) continue;
          try { tiles.push(await renderTile(page, scale, tx, ty, tw, th)); } catch {}
        }
      }
    }
    stats.tilesTotal = tiles.length;

    const total = tiles.length;
    let done = 0;
    if (onProgress) onProgress({ done, total });
    const tileMs = []; // per-tile wall time (incl. any retry) → p50/p95
    const perTile = await runPool(tiles, TILE_CONCURRENCY, async (td) => {
      const tStart = Date.now();
      let r = await transcribeTile(td, headers, overall.signal);
      // Per-tile retry: one transient failure (timeout / 5xx / dropped stream)
      // shouldn't silently drop that tile's callouts. Skip if the overall
      // deadline already fired (a retry would just fail on the aborted signal).
      if (!r.ok && !overall.signal.aborted) {
        stats.tileRetries++;
        r = await transcribeTile(td, headers, overall.signal);
      }
      tileMs.push(Date.now() - tStart);
      done++; if (onProgress) onProgress({ done, total });
      return r;
    });
    if (tileMs.length) {
      const sorted = tileMs.slice().sort((a, b) => a - b);
      const pct = (p) => sorted[Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length))];
      stats.tileP50 = Math.round(pct(50));
      stats.tileP95 = Math.round(pct(95));
    }

    const seen = new Map();
    for (const r of perTile) {
      if (!r) continue;
      if (r.ok) stats.tilesOk++;
      stats.tokensIn += (r.usage && r.usage.input) || 0;
      stats.tokensOut += (r.usage && r.usage.output) || 0;
      for (const s of (r.out || [])) {
        const norm = s.replace(/\s+/g, " ").trim();
        const key = norm.toLowerCase();
        if (key && !seen.has(key)) seen.set(key, norm);
      }
    }
    return { callouts: Array.from(seen.values()), stats };
  } finally { clearTimeout(dl); }
}

// Parse a JWT's `exp` (seconds since epoch), or null if unreadable.
function jwtExp(jwt) {
  try {
    const p = JSON.parse(atob(jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof p.exp === "number" ? p.exp : null;
  } catch { return null; }
}

// Return a usable, non-expired Supabase access token, refreshing via the stored
// refresh token when the access token is missing/expired. The app authenticates
// through GoTrue REST and stashes sb_token / sb_refresh_token (Auth.signIn), but
// a prior 401 can wipe sb_token while the refresh token survives — refresh here
// rather than skipping the upload as anon. Returns null only for accounts with no
// Supabase Auth session at all (localStorage-only), which keep the inline fallback.
async function ensureSupabaseToken() {
  const now = Math.floor(Date.now() / 1000);
  const tok = localStorage.getItem("sb_token");
  const refresh = localStorage.getItem("sb_refresh_token");
  const valid = tok && tok !== SUPABASE_ANON_KEY;
  if (valid) {
    const exp = jwtExp(tok);
    if (!exp || exp - now > 60) return tok; // still has >60s of life (or unknown exp)
  }
  if (!refresh) return valid ? tok : null;
  try {
    const r = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
      method: "POST",
      headers: { "apikey": SUPABASE_ANON_KEY, "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    const d = await r.json();
    if (r.ok && d.access_token) {
      localStorage.setItem("sb_token", d.access_token);
      if (d.refresh_token) localStorage.setItem("sb_refresh_token", d.refresh_token);
      return d.access_token;
    }
  } catch { /* network — fall through to whatever we have */ }
  return valid ? tok : null;
}

// Upload a blueprint's ORIGINAL bytes to the private `blueprints` Storage bucket
// (path <company_id>/<bpId>.<ext>) and record a metadata row. This is the primary
// path: the ai-proxy later fetches from storagePath server-side (service role) and
// forwards to Anthropic, so the browser never POSTs a multi-MB body. Returns
// { ok, storagePath }. On ANY failure returns { ok:false, reason } and the caller
// keeps the localStorage dataURL — small images still analyze inline, large PDFs
// fall back to the size-guard / per-page path. Requires a real user JWT (Storage
// RLS rejects the anon key), so anon-only sessions skip the upload gracefully.
async function uploadBlueprintToStorage(file, bpId, session) {
  try {
    if (!file || !bpId) return { ok:false, reason:"missing file/id" };
    const cid = session?.companyId || window._grokCompanyId || "local";
    const token = await ensureSupabaseToken();
    if (!token) return { ok:false, reason:"no user JWT (anon session)" };
    const dot = file.name.lastIndexOf(".");
    const ext = dot >= 0
      ? file.name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 8)
      : (file.type === "application/pdf" ? "pdf" : "bin");
    const storagePath = `${cid}/${bpId}.${ext || "bin"}`;
    const up = await fetch(`${SUPABASE_URL}/storage/v1/object/blueprints/${storagePath}`, {
      method: "POST",
      headers: {
        "Authorization": "Bearer " + token,
        "apikey": SUPABASE_ANON_KEY,
        "Content-Type": file.type || "application/octet-stream",
        "x-upsert": "true",
      },
      body: file,
    });
    if (!up.ok) {
      const t = await up.text().catch(() => "");
      return { ok:false, reason:`upload ${up.status} ${t.slice(0,120)}` };
    }
    // Metadata row — best-effort; RLS-scoped by company. merge-duplicates so a
    // re-upload of the same bpId updates rather than 409s.
    fetch(`${SUPABASE_URL}/rest/v1/blueprints`, {
      method: "POST",
      headers: {
        "Authorization": "Bearer " + token,
        "apikey": SUPABASE_ANON_KEY,
        "Content-Type": "application/json",
        "Prefer": "resolution=merge-duplicates",
      },
      body: JSON.stringify({
        id: bpId, company_id: cid, filename: file.name,
        storage_path: storagePath,
        created_by: session?.ownerName || session?.email || null,
      }),
    }).catch(() => {});
    return { ok:true, storagePath };
  } catch (e) {
    return { ok:false, reason: String(e?.message || e) };
  }
}

// Short-lived signed URL for a blueprint in the private bucket, so the viewer can
// render from Storage (the source of truth) when the local dataURL isn't present
// — e.g. a blueprint that reached this device as a metadata row only. null on fail.
async function signedBlueprintUrl(path) {
  try {
    const token = (await ensureSupabaseToken()) || sb._token();
    const r = await fetch(`${SUPABASE_URL}/storage/v1/object/sign/blueprints/${path}`, {
      method: "POST",
      headers: { "Authorization": "Bearer " + token, "apikey": SUPABASE_ANON_KEY, "Content-Type": "application/json" },
      body: JSON.stringify({ expiresIn: 3600 }),
    });
    if (!r.ok) return null;
    const d = await r.json();
    return d?.signedURL ? `${SUPABASE_URL}/storage/v1${d.signedURL}` : null;
  } catch { return null; }
}

// Supabase REST helper (no SDK needed — works in browser)
const sb = {
  _token: () => localStorage.getItem("sb_token") || SUPABASE_ANON_KEY,
  // Returns the signed-in user's Supabase JWT, or null if none. Anon key is
  // NOT a substitute — Supabase RLS rejects it as Bearer for company-scoped
  // tables, which is why the RFI / projects fetches were 401-ing for users
  // whose session was only persisted in localStorage (no sb_token). Callers
  // that hit RLS-protected /rest/v1 endpoints must use this, not _token().
  _userToken: () => {
    const t = localStorage.getItem("sb_token");
    return t && t !== SUPABASE_ANON_KEY ? t : null;
  },
  _h: (extra={}) => ({ "apikey":SUPABASE_ANON_KEY, "Authorization":"Bearer "+sb._token(), "Content-Type":"application/json", "Prefer":"return=representation", ...extra }),
  from: (table) => ({
    select: async (cols="*", filters={}) => {
      const q = new URLSearchParams({select:cols});
      Object.entries(filters).forEach(([k,v])=>q.append(k,v));
      const r = await fetch(`${SUPABASE_URL}/rest/v1/${table}?${q}`,{headers:sb._h()});
      return r.json();
    },
    insert: async (data) => { const r = await fetch(`${SUPABASE_URL}/rest/v1/${table}`,{method:"POST",headers:sb._h(),body:JSON.stringify(data)}); return r.json(); },
    update: async (data, match) => { const q = new URLSearchParams(Object.entries(match).map(([k,v])=>[k,`eq.${v}`])); const r = await fetch(`${SUPABASE_URL}/rest/v1/${table}?${q}`,{method:"PATCH",headers:sb._h(),body:JSON.stringify(data)}); return r.json(); },
    upsert: async (data) => { const r = await fetch(`${SUPABASE_URL}/rest/v1/${table}`,{method:"POST",headers:sb._h({"Prefer":"resolution=merge-duplicates,return=representation"}),body:JSON.stringify(data)}); return r.json(); },
    delete: async (match) => { const q = new URLSearchParams(Object.entries(match).map(([k,v])=>[k,`eq.${v}`])); const r = await fetch(`${SUPABASE_URL}/rest/v1/${table}?${q}`,{method:"DELETE",headers:sb._h()}); return r.ok; },
  }),
  auth: {
    signUp: async (email,password,meta={}) => { const r = await fetch(`${SUPABASE_URL}/auth/v1/signup`,{method:"POST",headers:{"apikey":SUPABASE_ANON_KEY,"Content-Type":"application/json"},body:JSON.stringify({email,password,data:meta})}); const d=await r.json(); if(d.access_token)localStorage.setItem("sb_token",d.access_token); return d; },
    signIn: async (email,password) => { const r = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`,{method:"POST",headers:{"apikey":SUPABASE_ANON_KEY,"Content-Type":"application/json"},body:JSON.stringify({email,password})}); const d=await r.json(); if(d.access_token)localStorage.setItem("sb_token",d.access_token); return d; },
    signOut: async () => { await fetch(`${SUPABASE_URL}/auth/v1/logout`,{method:"POST",headers:{"apikey":SUPABASE_ANON_KEY,"Authorization":"Bearer "+sb._token()}}); localStorage.removeItem("sb_token"); },
    getUser: async () => { const t=sb._token(); if(!t||t===SUPABASE_ANON_KEY)return null; const r=await fetch(`${SUPABASE_URL}/auth/v1/user`,{headers:{"apikey":SUPABASE_ANON_KEY,"Authorization":"Bearer "+t}}); return r.ok?r.json():null; },
  },
  // AI call through proxy — no API key needed in frontend
  ai: async (messages, opts={}) => {
    const r = await fetch(AI_PROXY_URL, {
      method:"POST",
      headers:{
        "Content-Type":"application/json",
        "Authorization":"Bearer "+sb._token(),
        "apikey": SUPABASE_ANON_KEY,
      },
      body:JSON.stringify({
        model: "claude-haiku-4-5",
        messages,
        max_tokens: opts.max_tokens||2000,
        temperature: opts.temperature, // undefined for chat (default sampling); 0 for analysis calls
        system:     opts.system,
        betas:      opts.betas,
        usage_type: opts.usage_type||"chat",
        company_id: opts.company_id||null,
        plan:       opts.plan||"starter",
      accountType: localStorage.getItem("grok_pending_account_type")||"gc",
      }),
    });
    return r.json();
  },
};

// ── WEATHER (Open-Meteo, free, no API key, CORS-enabled) ──────────────────────
// Used by Project Detail (7-day forecast) and Dashboard (rain-risk urgent items).
// Geocoding + forecast are cached in localStorage for 1 hour to keep usage low.
const Weather = {
  _key: (k) => "grok_weather_" + k,
  _get: (k) => {
    try {
      const raw = localStorage.getItem(Weather._key(k));
      if (!raw) return null;
      const o = JSON.parse(raw);
      if (!o || !o.ts || (Date.now() - o.ts) > 60*60*1000) return null;
      return o.data;
    } catch { return null; }
  },
  _set: (k, data) => { try { localStorage.setItem(Weather._key(k), JSON.stringify({ts:Date.now(), data})); } catch {} },
  geocode: async (address) => {
    if (!address || !String(address).trim()) return null;
    const norm = String(address).trim().toLowerCase().replace(/\s+/g," ");
    const ck = "geo_" + norm;
    const cached = Weather._get(ck);
    if (cached) return cached;
    try {
      const url = "https://geocoding-api.open-meteo.com/v1/search?count=1&country=US&name=" + encodeURIComponent(address);
      const r = await fetch(url);
      if (!r.ok) return null;
      const d = await r.json();
      if (!d || !Array.isArray(d.results) || !d.results.length) return null;
      const top = d.results[0];
      const out = { lat: top.latitude, lon: top.longitude, name: top.name, admin1: top.admin1, country: top.country };
      Weather._set(ck, out);
      return out;
    } catch { return null; }
  },
  forecast: async (lat, lon) => {
    if (typeof lat !== "number" || typeof lon !== "number") return null;
    const ck = "fc_" + lat.toFixed(3) + "_" + lon.toFixed(3);
    const cached = Weather._get(ck);
    if (cached) return cached;
    try {
      const url = "https://api.open-meteo.com/v1/forecast"
        + "?latitude=" + lat + "&longitude=" + lon
        + "&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_sum,precipitation_probability_max,windspeed_10m_max"
        + "&current_weather=true&temperature_unit=fahrenheit&windspeed_unit=mph&precipitation_unit=inch&timezone=auto&forecast_days=7";
      const r = await fetch(url);
      if (!r.ok) return null;
      const d = await r.json();
      Weather._set(ck, d);
      return d;
    } catch { return null; }
  },
  // Worst rain day over the next 3 days. Returns {risk, topDay, rainDays} or null.
  rainRisk: (forecast) => {
    if (!forecast || !forecast.daily) return null;
    const d = forecast.daily;
    const days = (d.time||[]).slice(0,3);
    let max = 0; let top = null; const rainDays = [];
    days.forEach((date, i) => {
      const prob = (d.precipitation_probability_max||[])[i] || 0;
      const mm = (d.precipitation_sum||[])[i] || 0;
      rainDays.push({ date, probability: prob, inches: mm });
      if (prob > max) { max = prob; top = { date, probability: prob, inches: mm }; }
    });
    return { risk: max, topDay: top, rainDays };
  },
  // WMO weather interpretation codes — open-meteo.com/en/docs
  codeLabel: (code) => {
    if (code === 0) return { icon:"☀️", label:"Clear" };
    if (code === 1 || code === 2) return { icon:"🌤", label:"Partly cloudy" };
    if (code === 3) return { icon:"☁️", label:"Overcast" };
    if (code === 45 || code === 48) return { icon:"🌫", label:"Fog" };
    if (code >= 51 && code <= 57) return { icon:"🌦", label:"Drizzle" };
    if (code >= 61 && code <= 67) return { icon:"🌧", label:"Rain" };
    if (code >= 71 && code <= 77) return { icon:"🌨", label:"Snow" };
    if (code >= 80 && code <= 82) return { icon:"🌧", label:"Showers" };
    if (code >= 85 && code <= 86) return { icon:"🌨", label:"Snow showers" };
    if (code >= 95 && code <= 99) return { icon:"⛈", label:"Thunderstorm" };
    return { icon:"🌡", label:"—" };
  },
};

// ── SUPABASE REALTIME ──────────────────────────────────────────────────────────
// Under RLS, the Realtime server evaluates each row's policy using the JWT
// passed in the phx_join payload. Without a user JWT only rows visible to the
// anon role would be delivered — which for company-scoped tables means none.
// So we send sb._token() (the signed-in user's JWT, falling back to anon for
// unauthenticated callers) in the join payload.
const RealtimeChannels = {};
const sbRealtime = {
  subscribe: (table, companyId, callback) => {
    const key = `${table}_${companyId}`;
    if (RealtimeChannels[key]) return;
    try {
      const wsUrl = SUPABASE_URL.replace("https://","wss://") + "/realtime/v1/websocket?apikey=" + SUPABASE_ANON_KEY + "&vsn=1.0.0";
      const ws = new WebSocket(wsUrl);
      ws.onopen = () => {
        ws.send(JSON.stringify({
          topic: `realtime:public:${table}:company_id=eq.${companyId}`,
          event: "phx_join",
          payload: { access_token: sb._token() },
          ref: "1",
        }));
        // Flush any queued writes that failed while offline
        OfflineQueue.flush();
      };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (["postgres_changes","INSERT","UPDATE","DELETE"].includes(msg.event)) callback(msg.payload);
        } catch {}
      };
      ws.onerror = () => {};
      ws.onclose = () => { delete RealtimeChannels[key]; };
      RealtimeChannels[key] = ws;
    } catch(e) {}
  },
  unsubscribe: (table, companyId) => {
    const key = `${table}_${companyId}`;
    if (RealtimeChannels[key]) { RealtimeChannels[key].close(); delete RealtimeChannels[key]; }
  },
};

// ── OFFLINE QUEUE — saves retry when connection returns ────────────────────────
const QUEUE_KEY = "grok_offline_queue";
const OfflineQueue = {
  add: (action) => {
    try {
      const q = JSON.parse(localStorage.getItem(QUEUE_KEY)||"[]");
      q.push({...action, queuedAt: Date.now()});
      localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
    } catch(e) {}
  },
  flush: async () => {
    try {
      const q = JSON.parse(localStorage.getItem(QUEUE_KEY)||"[]");
      if (!q.length) return;
      const failed = [];
      for (const action of q) {
        try {
          if (action.type === "saveProjects") await SyncDB._upsert("projects", action.data);
          else if (action.type === "saveRFIs")    await SyncDB._upsert("rfis", action.data);
          else if (action.type === "saveCOs")     await SyncDB._upsert("change_orders", action.data);
          else if (action.type === "saveTeam")    await SyncDB._upsert("team_members", action.data);
          else if (action.type === "saveFlags")   await SyncDB._upsert("flags", action.data);
        } catch(e) { failed.push(action); }
      }
      localStorage.setItem(QUEUE_KEY, JSON.stringify(failed));
      if (failed.length === 0) console.log("✅ Offline queue flushed");
    } catch(e) {}
  },
};

// ── GCG TEMPLATE SEEDING — runs synchronously before any component mounts ─────
(()=>{
  try {
    const tplKey = "grok_templates_gcg_001";
    const VERSION = "v4"; // bumped — cleans out old duplicate templates
    const versionKey = "grok_tpl_version_gcg_001";

    const GCG_TEMPLATES = [
      {id:"gcg_t1",  name:"Owner Agreement — Universal",               cat:"contracts", notes:"Universal owner contract — all project types. CSLB #1152750.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Owner_Agreement_Universal.docx"},
      {id:"gcg_t2",  name:"Subcontractor Agreement",                    cat:"contracts", notes:"GC-to-Sub — pay-when-paid, indemnity, DIR, daily reporting, LD pass-through.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Subcontractor_Agreement.docx"},
      {id:"gcg_t3",  name:"GCG as Subcontractor Agreement",             cat:"contracts", notes:"GCG-to-Head-GC — 45-day pay cap, LD rejection, flow-down limits, public works retainage.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_As_Subcontractor_Agreement.docx"},
      {id:"gcg_t4",  name:"Construction Proposal",                      cat:"contracts", notes:"3 payment formats — Residential/ADU, SRO phased, Commercial/Affordable/Public Works.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Proposal.docx"},
      {id:"gcg_t5",  name:"Change Order",                               cat:"co",        notes:"CO form — description, cost breakdown, revised contract total, schedule impact.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Change_Order.docx"},
      {id:"gcg_t6",  name:"Request for Information (RFI)",              cat:"rfi",       notes:"RFI — subject, issue description, response section, signatures.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_RFI_Form.docx"},
      {id:"gcg_t7",  name:"Lien Waiver Package — All 4 CA Forms",       cat:"lien",      notes:"All 4 CA statutory lien waivers — §8132/§8134/§8136/§8138 plus usage guide.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Lien_Waiver_Package.docx"},
      {id:"gcg_t8",  name:"20-Day Preliminary Notice",                  cat:"lien",      notes:"CA §8200 — required before filing lien. To owner, GC, and lender. Proof of service.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_20Day_Preliminary_Notice.docx"},
      {id:"gcg_t9",  name:"Notice of Completion",                       cat:"closeout",  notes:"CA §8180 — record within 15 days. Starts lien clock. Recording instructions included.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Notice_of_Completion.docx"},
      {id:"gcg_t10", name:"Certificate of Substantial Completion",      cat:"closeout",  notes:"Starts warranty period, punch list period, and retention release.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Certificate_Substantial_Completion.docx"},
      {id:"gcg_t11", name:"7-Day Tenant Notice",                        cat:"sro",       notes:"SF Admin Code §37.10A — required before work in occupied residential units.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_7Day_Tenant_Notice.docx"},
      {id:"gcg_t12", name:"Subcontractor Pre-Qualification Form",       cat:"contracts", notes:"License, DIR, insurance, 3 references, compliance disclosure, GCG review box.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Sub_PreQualification.docx"},
      {id:"gcg_t13", name:"Punch List",                                 cat:"closeout",  notes:"17 trade sections, C/I/N-A checkboxes, dual sign-off. Required before final payment.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Punch_List.docx"},
      {id:"gcg_t14", name:"1-Year Warranty Letter",                     cat:"closeout",  notes:"Workmanship warranty — coverage, exclusions, claim process. Signed by Jehoon Chun.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Warranty_Letter.docx"},
      {id:"gcg_t15", name:"Occupied Unit Construction Protocol",        cat:"sro",       notes:"Daily checklist — pre-work, during, end-of-day, lead paint, tenant acknowledgment.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Occupied_Unit_Protocol.docx"},
      {id:"gcg_t16", name:"Project Close-Out Checklist",                cat:"closeout",  notes:"Inspections, liens, warranty, financial, site turnover + SRO section.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_CloseOut_Checklist.docx"},
    ];

    if (localStorage.getItem(versionKey) !== VERSION) {
      const existing = JSON.parse(localStorage.getItem(tplKey)||"[]");
      // Always preserve uploaded file data
      const dataMap = {};
      existing.forEach(t => { if(t.data) dataMap[t.id] = t.data; });
      const merged = GCG_TEMPLATES.map(t => dataMap[t.id] ? {...t, data:dataMap[t.id]} : t);
      const userUploaded = existing.filter(t => !t.isGCG);
      localStorage.setItem(tplKey, JSON.stringify([...merged, ...userUploaded]));
      localStorage.setItem(versionKey, VERSION);
    }

    // Always strip GCG templates from admin_001 — demo account must be clean
    const GCG_IDS = new Set(GCG_TEMPLATES.map(t=>t.id));
    const adminTpls = JSON.parse(localStorage.getItem("grok_templates_admin_001")||"[]");
    const cleaned = adminTpls.filter(t=>!t.isGCG && !GCG_IDS.has(t.id));
    if (cleaned.length !== adminTpls.length) {
      localStorage.setItem("grok_templates_admin_001", JSON.stringify(cleaned));
    }
  } catch(e) {}
})();


// Track deleted IDs in localStorage so sync never restores them across refreshes
const DELETED_IDS = {
  _key: (type) => "grok_deleted_" + type,
  _load: (type) => { try { return new Set(JSON.parse(localStorage.getItem("grok_deleted_"+type)||"[]")); } catch { return new Set(); }},
  _save: (type, set) => { try { localStorage.setItem("grok_deleted_"+type, JSON.stringify([...set])); } catch {} },
  add: (type, id) => {
    const set = DELETED_IDS._load(type);
    set.add(String(id));
    DELETED_IDS._save(type, set);
  },
  has: (type, id) => DELETED_IDS._load(type).has(String(id)),
  clear: (type) => { try { localStorage.removeItem("grok_deleted_"+type); } catch {} },
};

// ── SYNC DB — Supabase first, localStorage fallback ───────────────────────────
const SyncDB = {
  _cid: () => window._grokCompanyId || localStorage.getItem("grok_company_id") || "local",

  // Direct REST upsert — more reliable than sb helper
  _upsert: async (table, rows) => {
    if (!rows || !rows.length) return;
    const jwt = sb._userToken();
    if (!jwt) {
      // No Supabase session — skip the REST write and queue for when the user
      // signs in. Without a real JWT, /rest/v1 returns 401 under RLS.
      OfflineQueue.add({type: "syncdb_upsert", table, data: rows});
      console.warn(`Supabase ${table} upsert skipped — no user JWT; queued offline.`);
      return;
    }
    try {
      const r = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
        method: "POST",
        headers: {
          "apikey": SUPABASE_ANON_KEY,
          "Authorization": "Bearer " + jwt,
          "Content-Type": "application/json",
          "Prefer": "resolution=merge-duplicates,return=minimal",
        },
        body: JSON.stringify(rows),
      });
      const txt = await r.text();
      if (r.status === 401) {
        console.error(`Supabase ${table} upsert 401 — session expired. Sign in again from grokfieldops.com or reset sb_token.`);
        localStorage.removeItem("sb_token");
        OfflineQueue.add({type: "syncdb_upsert", table, data: rows});
      } else {
        console.log(`Supabase ${table} upsert:`, r.status);
      }
      return r;
    } catch(e) {
      console.error(`Supabase ${table} upsert error:`, e.message);
    }
  },

  _saveWithFallback: async (type, table, rows, lsKey, data) => {
    localStorage.setItem(lsKey, JSON.stringify(data));
    try {
      await SyncDB._upsert(table, rows);
    } catch(e) {
      OfflineQueue.add({type, data: rows});
    }
  },

  _get: async (table, companyId) => {
    const jwt = sb._userToken();
    if (!jwt) {
      // No Supabase session — skip the RLS-protected read silently. Callers
      // fall back to localStorage so the UI stays populated.
      console.warn(`Supabase ${table} read skipped — no user JWT; using localStorage fallback.`);
      return [];
    }
    const r = await fetch(`${SUPABASE_URL}/rest/v1/${table}?company_id=eq.${companyId}&order=updated_at.desc`, {
      headers: {
        "apikey": SUPABASE_ANON_KEY,
        "Authorization": "Bearer " + jwt,
        "Accept": "application/json",
      },
    });
    if (r.status === 401) {
      console.error(`Supabase ${table} 401 — session expired. Clearing sb_token; sign in again to re-enable cloud sync.`);
      localStorage.removeItem("sb_token");
      return [];
    }
    if (!r.ok) return [];
    return r.json();
  },

  getProjects: async () => {
    const cid = SyncDB._cid(); const lsKey = `grok_projects_${cid}`;
    try {
      const rows = await SyncDB._get("projects", cid);
      if (Array.isArray(rows) && rows.length > 0) {
        const data = cleanProjects(rows.map(r=>({...r.data,id:r.id})));
        localStorage.setItem(lsKey, JSON.stringify(data)); return data;
      }
    } catch(e) {}
    try { return cleanProjects(JSON.parse(localStorage.getItem(lsKey)||"[]")); } catch { return []; }
  },
  saveProjects: async (projects) => {
    const cid = SyncDB._cid(); const lsKey = `grok_projects_${cid}`;
    const clean = cleanProjects(projects);
    const rows = clean.map(p=>({id:String(p.id),company_id:cid,data:p,updated_at:new Date().toISOString()}));
    await SyncDB._saveWithFallback("saveProjects","projects",rows,lsKey,clean);
  },

  getRFIs: async () => {
    const cid = SyncDB._cid(); const lsKey = `grok_rfis_global_${cid}`;
    try {
      const rows = await SyncDB._get("rfis", cid);
      if (Array.isArray(rows) && rows.length > 0) {
        const data = rows.map(r=>({...r.data,id:r.id}));
        localStorage.setItem(lsKey, JSON.stringify(data)); return data;
      }
    } catch(e) {}
    try { return JSON.parse(localStorage.getItem(lsKey)||"[]"); } catch { return []; }
  },
  saveRFIs: async (rfis) => {
    const cid = SyncDB._cid(); const lsKey = `grok_rfis_global_${cid}`;
    const rows = rfis.map(r=>({id:String(r.id),company_id:cid,data:r,updated_at:new Date().toISOString()}));
    await SyncDB._saveWithFallback("saveRFIs","rfis",rows,lsKey,rfis);
  },

  getCOs: async () => {
    const cid = SyncDB._cid(); const lsKey = `grok_cos_global_${cid}`;
    try {
      const rows = await SyncDB._get("change_orders", cid);
      if (Array.isArray(rows) && rows.length > 0) {
        const data = rows.map(r=>({...r.data,id:r.id}));
        localStorage.setItem(lsKey, JSON.stringify(data)); return data;
      }
    } catch(e) {}
    try { return JSON.parse(localStorage.getItem(lsKey)||"[]"); } catch { return []; }
  },
  saveCOs: async (cos) => {
    const cid = SyncDB._cid(); const lsKey = `grok_cos_global_${cid}`;
    const rows = cos.map(c=>({id:String(c.id),company_id:cid,data:c,updated_at:new Date().toISOString()}));
    await SyncDB._saveWithFallback("saveCOs","change_orders",rows,lsKey,cos);
  },

  getTeam: async () => {
    const cid = SyncDB._cid(); const lsKey = `grok_team_${cid}`;
    try {
      const rows = await SyncDB._get("team_members", cid);
      if (Array.isArray(rows) && rows.length > 0) {
        const data = rows.map(r=>({...r.data,id:r.id}));
        localStorage.setItem(lsKey, JSON.stringify(data)); return data;
      }
    } catch(e) {}
    try { return JSON.parse(localStorage.getItem(lsKey)||"[]"); } catch { return []; }
  },
  saveTeam: async (team) => {
    const cid = SyncDB._cid(); const lsKey = `grok_team_${cid}`;
    const rows = team.map(m=>({id:String(m.id),company_id:cid,data:m,updated_at:new Date().toISOString()}));
    await SyncDB._saveWithFallback("saveTeam","team_members",rows,lsKey,team);
  },

  getFlags: async (projectId) => {
    const cid = SyncDB._cid(); const lsKey = `grok_flags_${projectId}`;
    const jwt = sb._userToken();
    if (jwt) {
      try {
        const r = await fetch(`${SUPABASE_URL}/rest/v1/flags?company_id=eq.${cid}&project_id=eq.${projectId}`, {
          headers:{"apikey":SUPABASE_ANON_KEY,"Authorization":"Bearer "+jwt,"Accept":"application/json"}
        });
        if (r.status === 401) { localStorage.removeItem("sb_token"); }
        if (r.ok) {
          const rows = await r.json();
          if (Array.isArray(rows) && rows.length > 0) {
            const data = rows.map(r=>({...r.data,id:r.id}));
            localStorage.setItem(lsKey, JSON.stringify(data)); return data;
          }
        }
      } catch(e) {}
    }
    try { return JSON.parse(localStorage.getItem(lsKey)||"[]"); } catch { return []; }
  },
  saveFlags: async (projectId, flags) => {
    const cid = SyncDB._cid(); const lsKey = `grok_flags_${projectId}`;
    const rows = flags.map(f=>({id:String(f.id),company_id:cid,project_id:String(projectId),data:f,updated_at:new Date().toISOString()}));
    await SyncDB._saveWithFallback("saveFlags","flags",rows,lsKey,flags);
  },

  // One-time migration: push all localStorage → Supabase on first login
  deleteById: async (table, id) => {
    const jwt = sb._userToken();
    if (!jwt) {
      // No Supabase session — local tombstone already added by caller via
      // DELETED_IDS; the Supabase row will be cleaned up on next sign-in via
      // the offline queue (see SyncDB._upsert).
      console.warn(`Supabase ${table} delete skipped — no user JWT.`);
      return;
    }
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/${table}?id=eq.${id}`, {
        method: "DELETE",
        headers: {
          "apikey": SUPABASE_ANON_KEY,
          "Authorization": "Bearer " + jwt,
        },
      });
    } catch(e) {}
  },

  migrate: async () => {
    const cid = SyncDB._cid();
    if (!cid || cid==="local") return;
    try {
      const deletedDemos = (()=>{try{return JSON.parse(localStorage.getItem("grok_deleted_demos_"+cid)||"[]");}catch{return [];}})();
      const projects = cleanProjects(
        JSON.parse(localStorage.getItem(`grok_projects_${cid}`)||"[]")
          .filter(p=>!deletedDemos.includes(String(p.id)))
      );
      if (projects.length) await SyncDB.saveProjects(projects);
      const rfis = JSON.parse(localStorage.getItem(`grok_rfis_global_${cid}`)||"[]");
      if (rfis.length) await SyncDB.saveRFIs(rfis);
      const cos = JSON.parse(localStorage.getItem(`grok_cos_global_${cid}`)||"[]");
      if (cos.length) await SyncDB.saveCOs(cos);
      const team = JSON.parse(localStorage.getItem(`grok_team_${cid}`)||"[]");
      if (team.length) await SyncDB.saveTeam(team);
      await OfflineQueue.flush();
    } catch(e) { console.error("Sync error:", e); }
  },
};

// ── REALTIME HOOK ──────────────────────────────────────────────────────────────
function useRealtimeSync(table, companyId, onUpdate) {
  React.useEffect(()=>{
    if (!companyId || companyId==="local") return;
    sbRealtime.subscribe(table, companyId, onUpdate);
    return ()=>sbRealtime.unsubscribe(table, companyId);
  },[table, companyId]);
}

const PROMO_CODES = {
  "WEAREGOINGTOBERICH": {discount:"free30",  label:"30 days free",          days:30, pct:0},
  "GROKSPECIAL4U":      {discount:"50pct",   label:"50% off first month",   days:0,  pct:50},
};

const SUBSCRIPTION_POLICY = {
  maxCancellations: 2,
  moneyBackDays: 7,
  message: "You have 7 days from your payment date to request a full refund — no questions asked. After 7 days, no refunds are issued. Each account is limited to 2 cancellations total. After 2 cancellations, the account is permanently closed and cannot re-subscribe.",
  bannedMessage: "This account has reached the maximum number of cancellations (2) and cannot re-subscribe. Please contact support if you believe this is an error.",
};

const GCG_POLICY = {
  company: "Grok Construction Group Inc",
  headline: "No Shortcuts. No Excuses. Full Accountability.",
  statement: [
    "When you work with Grok Construction Group Inc, you agree to the following standards — no exceptions:",
    "1. COMPLETE THE JOB. If you take a contract, you finish it. Walking off a job is not an option.",
    "2. NO SHORTCUTS. Every task is performed to code, to spec, and to the standard you committed to. Cutting corners is grounds for immediate termination of contract.",
    "3. FULL ACCOUNTABILITY. If your work fails, causes damage, or falls short of spec — you are responsible. This includes all costs to remedy, liquidated damages, and legal liability.",
    "4. EVERYTHING IS DOCUMENTED. Grok Field Ops records all RFIs, Change Orders, daily logs, blueprints, and communications. This protects all parties and creates a clear record of what was agreed and what was delivered.",
    "5. NO EXCUSES. If you cannot handle the scope, do not take the job. Communicate issues immediately — not after they become failures.",
  ],
  subNote: "As a subcontractor on this project, you have reviewed and acknowledged these standards. Your work on this project constitutes your agreement.",
  ownerNote: "As the property owner, these standards protect you. Every change, decision, and deliverable is recorded in this platform as a legal record of the project.",
  footer: "Grok Construction Group Inc · CSLB #1152750 · Powered by Grok Field Ops",
};



const ANTHROPIC_MODEL = "claude-haiku-4-5"; // current model
// Blueprint review + deep analysis must read fine leader-line callouts off dense
// CAD detail sheets (product names, mil coverages, spec numbers on detail views).
// Those callouts are usually NOT in the PDF text layer — they must be read VISUALLY
// from the sheet raster, which Haiku does unreliably. claude-sonnet-5 is the
// first Sonnet tier with high-resolution vision (2576px long edge), so it
// resolves detail-sheet annotations far better on whole-set passes. Verified
// live through the ai-proxy (HTTP 200, model echoed) on 2026-07-15.
// NOTE: Sonnet is ~3x Haiku's per-token price ($3/$15 vs $1/$5 per MTok) and
// these are the app's heaviest calls (multi-MB PDFs) — metered with a weight.
// Even at this resolution a full D-size sheet is too small to read fine callouts
// whole (Sonnet returns "CM 1000" for "CIM 1000"), so the automatic detail-sheet
// tiling pass (extractDetailCallouts) reads them character-exact and feeds the
// verbatim strings into this review.
const BLUEPRINT_ANALYSIS_MODEL = "claude-sonnet-5";
// Engine/prompt version stamped onto every saved analysis so we can later tell a
// user their saved results came from an OLDER analysis engine. Derived from the
// ACTUAL analysis model (BLUEPRINT_ANALYSIS_MODEL) so it can't drift; bump the
// prompt-rev date whenever the analysis prompt materially changes.
const ENGINE_PROMPT_REV = "2026.07.19c";
const ENGINE_VERSION = `bp-${ENGINE_PROMPT_REV}+${BLUEPRINT_ANALYSIS_MODEL}`;

// --- THEME --------------------------------------------------------------------
// ─── JUNK / TEST DATA FILTER ─────────────────────────────────────────────────
// One canonical filter applied at every read site (and write site) so seeded
// demos / leftover click-test rows never reach a real user. Criteria match the
// patterns we've actually seen leak into prod data:
//   - id starts with "test_", "demo_", or "gcg_proj_"
//   - id is one of the known stub timestamps
//   - name is literally "test" / "demo" / "test project"
//   - row has NO client, NO address, AND NO contract value (a stub from a
//     click-test almost always)
// Add new patterns here, not inline at the call site.
const JUNK_PROJECT_IDS = new Set(["test_1", "test_123", "test_1777941923903"]);
const JUNK_PROJECT_ID_PREFIXES = ["test_", "demo_", "gcg_proj_"];
const JUNK_PROJECT_NAMES = new Set(["test", "demo", "test project"]);
function isJunkProject(p) {
  if (!p) return true;
  const id   = String(p.id   || "").trim().toLowerCase();
  const name = String(p.name || "").trim().toLowerCase();
  if (JUNK_PROJECT_IDS.has(id)) return true;
  if (JUNK_PROJECT_ID_PREFIXES.some(pre => id.startsWith(pre))) return true;
  if (JUNK_PROJECT_NAMES.has(name)) return true;
  const hasClient  = !!(p.client && String(p.client).trim());
  const hasAddress = !!(p.addr   && String(p.addr).trim());
  const hasValue   = Number(p.value || 0) > 0;
  if (!hasClient && !hasAddress && !hasValue) return true;
  return false;
}
function cleanProjects(arr) { return Array.isArray(arr) ? arr.filter(p => !isJunkProject(p)) : []; }

const JUNK_COMPANY_ID_PREFIXES = ["test_", "demo_"];
const JUNK_COMPANY_NAMES = new Set(["test", "demo"]);
function isJunkCompany(c) {
  if (!c) return true;
  const id    = String(c.id || "").trim().toLowerCase();
  const name  = String(c.companyName || c.name || "").trim().toLowerCase();
  const email = String(c.email || "").trim().toLowerCase();
  if (JUNK_COMPANY_ID_PREFIXES.some(pre => id.startsWith(pre))) return true;
  if (JUNK_COMPANY_NAMES.has(name)) return true;
  if (email.startsWith("test@") || email.startsWith("demo@")) return true;
  return false;
}
function cleanCompanies(arr) { return Array.isArray(arr) ? arr.filter(c => !isJunkCompany(c)) : []; }

// ─── DATA SYNC LAYER ─────────────────────────────────────────────────────────
// Multi-tenant: all keys scoped to session companyId so users never share data
function scopedKey(key, companyId) {
  if (!companyId || companyId === "demo") return key;
  return key + "_" + companyId;
}
// Returns the scoped projects key for the current session
function projectsKey(companyId) { return scopedKey("grok_projects", companyId); }
// Works with localStorage now. When you go live, swap syncToServer()
// to POST to your Supabase/Firebase/API endpoint.
// Every edit, archive, and delete goes through DB.set() / DB.delete()
// so there's ONE place to add server sync later.
//
// KEY MAP — everything the app stores:
//   AUTH_KEY              = "grok_auth_session"       — current session
//   COMPANIES_KEY         = "grok_companies"          — all registered companies
//   SETTINGS_KEY          = "grok_company_settings"   — GC rates & fees
//   SUB_DB_KEY            = "grok_sub_database"       — master sub roster
//   USAGE_KEY             = "grok_usage_*"            — AI usage counters
//   "grok_projects"       — active project list
//   "grok_leads"          — lead pipeline
//   "grok_knowledge_base" — AI knowledge base
//   "grok_budget_*"       — budget per project
//   "grok_scope_*"        — scope checklist per project
//   "grok_docseq_*"       — sub compliance per project
//   "grok_sub_database"   — master sub roster
//   "grok_subbids_*"      — sub bid comparison per project
//   "grok_punch_*"        — punch list per project
//   "grok_chat_*"         — team chat per project
//   "grok_time_*"         — time tracking per project
//   "grok_notes_*"        — project notes
//   "grok_blueprints_*"   — blueprints per project
//   "pm_memory_*"         — AI PM memory per project
//   "grok_pending_reg"    — pending registration
//   "grok_api_key"        — Anthropic API key
//   "grok_theme"          — light/dark theme

const DB_VERSION = 1;
const SYNC_ENDPOINT = null; // Set to your API URL when live: "https://api.grokfieldops.com/sync"

// Pending sync queue for when offline
const SYNC_QUEUE_KEY = "grok_sync_queue";

function getSyncQueue() {
  try { return JSON.parse(localStorage.getItem(SYNC_QUEUE_KEY)||"[]"); } catch{ return []; }
}

function addToSyncQueue(op) {
  const q = getSyncQueue();
  q.push({...op, queued_at: Date.now(), id: "op_"+Date.now()+"_"+Math.random().toString(36).slice(2,7)});
  try { localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(q.slice(-500))); } catch(e){} // keep last 500 ops
}

// ── Main DB object ─────────────────────────────────────────────────────────────
const DB = {

  // Read a value
  get(key) {
    try {
      const val = localStorage.getItem(key);
      return val ? JSON.parse(val) : null;
    } catch(e) {
      console.warn("[DB.get] Failed:", key, e);
      return null;
    }
  },

  // Write a value — logs to sync queue for server sync later
  set(key, value, meta = {}) {
    try {
      const payload = JSON.stringify(value);
      localStorage.setItem(key, payload);

      // Log to sync queue
      addToSyncQueue({
        op: "set",
        key,
        value,
        company_id: meta.company_id || null,
        project_id: meta.project_id || null,
        user: meta.user || null,
        timestamp: new Date().toISOString(),
      });

      // If live endpoint configured, sync immediately
      if (SYNC_ENDPOINT) {
        DB.syncNow(key, value, meta).catch(e => {
          console.warn("[DB] Sync failed, queued for retry:", key);
        });
      }

      return true;
    } catch(e) {
      console.error("[DB.set] Failed:", key, e);
      return false;
    }
  },

  // Delete a key — soft delete with archive
  delete(key, meta = {}) {
    try {
      // Archive before deleting
      const existing = DB.get(key);
      if (existing !== null) {
        const archiveKey = "grok_archive_" + key + "_" + Date.now();
        localStorage.setItem(archiveKey, JSON.stringify({
          original_key: key,
          data: existing,
          deleted_at: new Date().toISOString(),
          deleted_by: meta.user || "unknown",
          reason: meta.reason || "",
        }));
      }

      localStorage.removeItem(key);

      addToSyncQueue({
        op: "delete",
        key,
        company_id: meta.company_id || null,
        project_id: meta.project_id || null,
        user: meta.user || null,
        timestamp: new Date().toISOString(),
        reason: meta.reason || "",
      });

      return true;
    } catch(e) {
      console.error("[DB.delete] Failed:", key, e);
      return false;
    }
  },

  // Archive a record (mark deleted but keep data)
  archive(key, record, meta = {}) {
    try {
      const existing = DB.get(key);
      if (!existing) return false;

      let updated;
      if (Array.isArray(existing)) {
        // Array: mark item as archived instead of removing
        updated = existing.map(item =>
          item.id === record.id
            ? {...item, archived: true, archived_at: new Date().toISOString(), archived_by: meta.user}
            : item
        );
      } else {
        updated = {...existing, archived: true, archived_at: new Date().toISOString()};
      }

      return DB.set(key, updated, meta);
    } catch(e) {
      console.error("[DB.archive] Failed:", key, e);
      return false;
    }
  },

  // Get full audit trail for a key
  getHistory(key) {
    const history = [];
    const queue = getSyncQueue();
    const ops = queue.filter(op => op.key === key);
    return ops.sort((a,b) => new Date(a.timestamp) - new Date(b.timestamp));
  },

  // Get all archived items
  getArchives(keyPrefix) {
    const archives = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("grok_archive_" + (keyPrefix||""))) {
        try {
          archives.push(JSON.parse(localStorage.getItem(k)));
        } catch(e) {}
      }
    }
    return archives.sort((a,b) => new Date(b.deleted_at) - new Date(a.deleted_at));
  },

  // Get pending sync queue size
  getPendingCount() {
    return getSyncQueue().length;
  },

  // ── Server sync (STUB — wire to your backend when live) ───────────────────
  async syncNow(key, value, meta) {
    if (!SYNC_ENDPOINT) return;
    const session = DB.get(AUTH_KEY);
    const res = await fetch(SYNC_ENDPOINT + "/set", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Company-ID": meta.company_id || session?.companyId || "",
        "X-User": meta.user || session?.email || "",
      },
      body: JSON.stringify({ key, value, meta, db_version: DB_VERSION }),
    });
    if (!res.ok) throw new Error("Sync failed: " + res.status);
  },

  // Flush pending queue to server (call on app startup and periodically)
  async flushQueue() {
    if (!SYNC_ENDPOINT) return;
    const q = getSyncQueue();
    if (q.length === 0) return;

    const session = DB.get(AUTH_KEY);
    try {
      const res = await fetch(SYNC_ENDPOINT + "/batch", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Company-ID": session?.companyId || "",
          "X-User": session?.email || "",
        },
        body: JSON.stringify({ ops: q, db_version: DB_VERSION }),
      });
      if (res.ok) {
        localStorage.removeItem(SYNC_QUEUE_KEY);
        console.log("[DB] Flushed", q.length, "pending ops to server");
      }
    } catch(e) {
      console.warn("[DB] Flush failed, will retry:", e.message);
    }
  },

  // Export all company data (for backup or migration)
  exportAll(companyId) {
    const data = {};
    const prefix = "grok_";
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) {
        try { data[k] = JSON.parse(localStorage.getItem(k)); } catch(e) {}
      }
    }
    return {
      company_id: companyId,
      exported_at: new Date().toISOString(),
      db_version: DB_VERSION,
      data,
    };
  },
};

// ── Convenience wrappers for each data type ────────────────────────────────────
// These replace raw localStorage calls throughout the app.
// Each one calls DB.set() so everything goes through the sync layer.

const DataStore = {

  // Projects — Supabase first, localStorage fallback
  saveProjects: (projects, session) => {
    const key = projectsKey(session?.companyId);
    const cid = session?.companyId || window._grokCompanyId || "";
    const deletedDemos = (()=>{try{return JSON.parse(localStorage.getItem("grok_deleted_demos_"+cid)||"[]");}catch{return [];}})();
    // Strip tombstoned demos, then strip junk/test data so it can never be
    // re-persisted from a stale in-memory copy.
    const clean = cleanProjects(projects.filter(p=>!deletedDemos.includes(String(p.id))));
    try { localStorage.setItem(key, JSON.stringify(clean)); } catch(e) {}
    PROJECTS = clean;
    SyncDB.saveProjects(clean).catch(()=>{});
  },
  loadProjects: (session) => {
    try {
      const key = projectsKey(session?.companyId || window._grokCompanyId || "");
      const s = localStorage.getItem(key);
      return cleanProjects(s ? JSON.parse(s) : []);
    } catch { return []; }
  },

  // Leads
  saveLeads: (leads, session) =>
    DB.set("grok_leads", leads, {company_id: session?.companyId, user: session?.email}),
  loadLeads: () => DB.get("grok_leads"),

  // Budget per project
  saveBudget: (projectId, budget, session) =>
    DB.set("grok_budget_"+projectId, budget, {project_id: projectId, user: session?.email}),
  loadBudget: (projectId) => DB.get("grok_budget_"+projectId),

  // Scope checklist
  saveScope: (projectId, scope, session) =>
    DB.set("grok_scope_"+projectId, scope, {project_id: projectId, user: session?.email}),
  loadScope: (projectId) => DB.get("grok_scope_"+projectId),

  // Sub compliance per project
  saveSubDocs: (projectId, subId, docs, session) =>
    DB.set("grok_docseq_"+projectId+"_"+subId, docs, {project_id: projectId, user: session?.email}),
  loadSubDocs: (projectId, subId) => DB.get("grok_docseq_"+projectId+"_"+subId),

  // Master sub roster
  saveSubDB: (db, session) =>
    DB.set(SUB_DB_KEY, db, {company_id: session?.companyId, user: session?.email}),

  // Sub bids
  saveSubBids: (projectId, bids, session) =>
    DB.set("grok_subbids_"+projectId, bids, {project_id: projectId, user: session?.email}),

  // Punch list
  savePunchList: (projectId, items, session) =>
    DB.set("grok_punch_"+projectId, items, {project_id: projectId, user: session?.email}),
  loadPunchList: (projectId) => DB.get("grok_punch_"+projectId),

  // Chat
  saveChat: (projectId, messages, session) =>
    DB.set("grok_chat_"+projectId, messages, {project_id: projectId, user: session?.email}),

  // Time tracking
  saveTime: (projectId, entries, session) =>
    DB.set("grok_time_"+projectId, entries, {project_id: projectId, user: session?.email}),

  // Notes
  saveNotes: (projectId, notes, session) =>
    DB.set("grok_notes_"+projectId, notes, {project_id: projectId, user: session?.email}),

  // Company settings
  saveSettings: (settings, session) =>
    DB.set(SETTINGS_KEY, settings, {company_id: session?.companyId, user: session?.email}),

  // Knowledge base
  saveKB: (kb, session) =>
    DB.set("grok_knowledge_base", kb, {company_id: session?.companyId, user: session?.email}),

  // Delete a project — removes from storage and cleans up all related data
  deleteProject: (projectId, session) => {
    const cid = session?.companyId || window._grokCompanyId || "";
    const key = projectsKey(cid);
    try {
      const stored = localStorage.getItem(key);
      const projects = stored ? JSON.parse(stored) : [];
      const updated = projects.filter(p => String(p.id) !== String(projectId));
      localStorage.setItem(key, JSON.stringify(updated));
      PROJECTS = updated;
    } catch(e) {}
    // If deleting a demo project, track it so it never comes back
    if (String(projectId).startsWith("demo_")) {
      const deletedKey = "grok_deleted_demos_"+cid;
      const deleted = (()=>{try{return JSON.parse(localStorage.getItem(deletedKey)||"[]");}catch{return [];}})();
      if (!deleted.includes(String(projectId))) {
        deleted.push(String(projectId));
        localStorage.setItem(deletedKey, JSON.stringify(deleted));
      }
      // Also remove from Supabase so it doesn't sync back
      try { SyncDB.saveProjects(updated).catch(()=>{}); } catch(e) {}
    }
    // Clean up all project-scoped data
    ["grok_budget_","grok_scope_","grok_subbids_","grok_punch_","grok_chat_","grok_time_","grok_notes_","grok_blueprints_","grok_proj_docs_"+(cid)+"_"].forEach(prefix => {
      try { localStorage.removeItem(prefix+projectId); } catch(e) {}
    });
  },

  // Delete a sub from roster
  deleteSub: (subId, session) => {
    DB.archive(SUB_DB_KEY, {id: subId}, {user: session?.email});
  },
};

// Expose DB on window for debugging in browser console
if (typeof window !== "undefined") {
  window.__GrokDB = DB;
  window.__GrokDataStore = DataStore;
  // Log pending sync count on load
  const pending = DB.getPendingCount();
  if (pending > 0) console.log("[GrokDB] "+pending+" ops pending sync to server");
}


// ─── AUTH / COMPANY LOGIN SYSTEM ─────────────────────────────────────────────
// Simple localStorage-based multi-company auth
// For production: replace with Supabase or Firebase

// ── Banned/deleted account blocklist ──────────────────────────────────────────
// Add emails here when deleting accounts — prevents localStorage ghost logins
const BANNED_EMAILS = [
  "e@ncrconstructioninc.com",
  "j@ncrconstructioninc.com",
];
const SELF_BANNED_KEY = "grok_self_banned";
function getSelfBanned() {
  try { return JSON.parse(localStorage.getItem(SELF_BANNED_KEY)||"[]"); } catch { return []; }
}
function addSelfBanned(email) {
  const list = getSelfBanned();
  const lower = (email||"").toLowerCase().trim();
  if (!list.includes(lower)) { list.push(lower); localStorage.setItem(SELF_BANNED_KEY, JSON.stringify(list)); }
}
function isBanned(email) {
  const lower = (email||"").toLowerCase().trim();
  return BANNED_EMAILS.includes(lower) || getSelfBanned().includes(lower);
}

const AUTH_KEY = "grok_auth_session";
const COMPANIES_KEY = "grok_companies";

// Default company (Jehoon's — pre-seeded)
const DEFAULT_COMPANIES = [
  {
    id: "demo_gc_001",
    name: "Bay Area Build Co",
    cslb: "987654",
    location: "San Jose, CA",
    owner: "Marcus Webb",
    email: "marcus@bayareabuildco.com",
    plan: "starter",
    createdAt: "2026-03-15",
    logo: null,
    primaryColor: "#60a5fa",
    isAdmin: false,
    trialEnds: "2026-05-15",
  },
  {
    // ── Platform owner / subscriber demo account ──────────────────────────────
    // Login: jhcftgroup@gmail.com — shows clean new-subscriber experience
    id: "admin_001",
    name: "Grok Field Ops Demo",
    cslb: "",
    location: "San Mateo, CA",
    owner: "Platform Demo",
    email: "jhcftgroup@gmail.com",
    loginEmail: "jhcftgroup@gmail.com",
    ownerName: "Platform Demo",
    companyName: "Grok Field Ops Demo",
    plan: "owner",
    createdAt: "2026-01-01",
    logo: null,
    primaryColor: "#D4A843",
    isAdmin: true,
    isPlatformOwner: true,
  },
  {
    // ── Jehoon's real GCG account — full company, unlimited plan ─────────────
    // Login: jehoon@grokconstructiongroupinc.com
    id: "gcg_001",
    name: "Grok Construction Group Inc",
    cslb: "CSLB #1152750",
    location: "2201 Bridgepointe Pkwy F125, San Mateo CA 94404",
    owner: "Jehoon Chun",
    email: "jehoon@grokconstructiongroupinc.com",
    loginEmail: "jehoon@grokconstructiongroupinc.com",
    loginEmail2: "jehoon@gcginc.com",
    phone: "(650) 773-5317",
    ownerName: "Jehoon Chun",
    companyName: "Grok Construction Group Inc",
    contactEmail: "Jehoon@gcginc.com",
    plan: "gcpro",
    createdAt: "2026-01-01",
    logo: null,
    primaryColor: "#D4A843",
    isAdmin: true,
    isPlatformOwner: false,
    website: "www.grokconstructiongroup.com",
    ein: "88-1234567",
    bonded: true,
    insured: true,
    dir: "1000098765",
    cslb_num: "1152750",
    addr: "2201 Bridgepointe Pkwy F125, San Mateo CA 94404",
    city: "San Mateo",
    state: "CA",
    zip: "94404",
    // Unlimited plan — no limits
    limits: {estimates:9999, pm_chats:9999, video:9999, meetings:9999, blueprint:9999},
  }
];

function getCompanies() {
  try {
    let all = JSON.parse(localStorage.getItem(COMPANIES_KEY));
    if (!all || !all.length) {
      all = DEFAULT_COMPANIES;
      localStorage.setItem(COMPANIES_KEY, JSON.stringify(all));
    }
    // Always ensure admin accounts exist with correct data
    const hasAdmin = all.find(c=>c.id==="admin_001"&&c.email==="jhcftgroup@gmail.com");
    const hasGCG = all.find(c=>c.id==="gcg_001"&&c.email==="jehoon@grokconstructiongroupinc.com");
    if (!hasAdmin || !hasGCG) {
      const filtered = all.filter(c=>c.id!=="admin_001"&&c.id!=="gcg_001");
      const seeded = [...filtered, ...DEFAULT_COMPANIES.filter(c=>c.id==="admin_001"||c.id==="gcg_001")];
      localStorage.setItem(COMPANIES_KEY, JSON.stringify(seeded));
      all = seeded;
    }
    return cleanCompanies(all.filter(c => !isBanned(c.email)));
  }
  catch { return cleanCompanies(DEFAULT_COMPANIES); }
}
function saveCompanies(companies) { localStorage.setItem(COMPANIES_KEY, JSON.stringify(companies)); }

function getSession(){
  try{
    const s=localStorage.getItem("grok_auth_session");
    const p=s?JSON.parse(s):null;
    if(p?.companyId){window._grokCompanyKey=settingsKey(p.companyId);window._grokCompanyId=p.companyId;}
    return p;
  }catch{return null;}
}

// ── Supabase Auth shim (Phase 1 of auth migration) ─────────────────────────────
//
// Wraps Supabase Auth signup/signin/signout and mirrors the result into the
// existing localStorage data model (grok_companies / grok_sub_accounts /
// grok_auth_session) so the ~35 existing session-read sites work unchanged.
//
// PHASE STATUS: ADDITIVE — defining this object alone changes no behavior.
// Existing AuthGate.register and SubLoginPanel.handleRegister still take their
// homegrown path. Wiring is the next step.
//
// CONTRACT:
//   Auth.signUpGc(opts)   → {session, authUserId} | {error, status}
//   Auth.signUpSub(opts)  → {subId, companyId, authUserId} | {error, status}
//   Auth.signOut()        → clears sb_token + Supabase server session + grok_auth_session
//   Auth.getCurrentUser() → Supabase Auth user object | null
//
// Returned `session` is in the grok_auth_session legacy shape — NOT auto-saved.
// Caller decides whether to saveSession(session) (e.g. trial path) or hold the
// session until after Stripe checkout (paid path).
//
// PRE-REQ SCHEMA (run once in Supabase SQL editor before this shim is wired):
//   alter table companies
//     add column if not exists auth_user_id uuid references auth.users(id);
//   create index if not exists companies_auth_user_id_idx on companies (auth_user_id);
const Auth = {
  // GC company signup. Creates auth.users entry + local company row + Supabase
  // companies row (linked via auth_user_id). Does not save a session — caller does.
  signUpGc: async ({email, password, companyName, ownerName, cslb, location, plan}) => {
    const cleanEmail = (email || "").toLowerCase().trim();
    if (!cleanEmail || !password) return {error: "Email and password required"};

    // 1. Create Supabase Auth user.
    const r = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
      method: "POST",
      headers: {"apikey": SUPABASE_ANON_KEY, "Content-Type": "application/json"},
      body: JSON.stringify({
        email: cleanEmail,
        password,
        data: {accountType: "gc", companyName: companyName || "", ownerName: ownerName || ""},
      }),
    });
    const data = await r.json();
    if (!r.ok || data.error) {
      return {error: data.error?.message || data.msg || "Sign up failed", status: r.status};
    }
    if (data.access_token) localStorage.setItem("sb_token", data.access_token);
    const authUserId = data.user?.id || data.id || null;

    // 2. Mirror to local grok_companies. NOTE: password is still stored locally
    // during Phase 1 for backward compat with the existing login path. Phase 3
    // removes the local password field and routes login through Supabase Auth.
    const planName = plan || "starter";
    const trialEnds = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
    const moneyBackDeadline = new Date(Date.now() + 15 * 86400000).toISOString().slice(0, 10);
    const companies = getCompanies();
    const existing = companies.find(c => (c.email || "").toLowerCase() === cleanEmail);
    let companyId;
    if (existing) {
      companyId = existing.id;
      saveCompanies(companies.map(c =>
        (c.email || "").toLowerCase() === cleanEmail ? {...c, authUserId} : c
      ));
    } else {
      companyId = "co_" + Date.now();
      saveCompanies([...companies, {
        id: companyId, name: companyName || "", cslb: cslb || "", location: location || "",
        owner: ownerName || cleanEmail.split("@")[0], email: cleanEmail, password,
        plan: planName, createdAt: new Date().toISOString().slice(0, 10),
        logo: null, primaryColor: "#D4A843",
        trialEnds, moneyBackDeadline,
        cancellations: 0, isAdmin: false, emailVerified: false,
        authUserId,
      }]);
    }

    // 3. Upsert Supabase companies row with auth_user_id. ignore-duplicates keeps
    // any webhook-set plan fields intact (see warning at the login-time upsert).
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/companies`, {
        method: "POST",
        headers: {
          "apikey": SUPABASE_ANON_KEY,
          "Authorization": "Bearer " + sb._token(),
          "Content-Type": "application/json",
          "Prefer": "resolution=ignore-duplicates,return=minimal",
        },
        body: JSON.stringify({
          id: companyId,
          auth_user_id: authUserId,
          email: cleanEmail,
          company_name: companyName || "",
          owner_name: ownerName || "",
        }),
      });
    } catch (e) { /* webhook still falls back to email match */ }

    // 4. Build session in legacy grok_auth_session shape. Caller decides whether to save.
    const session = {
      companyId, companyName: companyName || "", ownerName: ownerName || cleanEmail.split("@")[0],
      plan: planName, email: cleanEmail, cslb: cslb || "",
      authUserId,
      loginTime: Date.now(),
      trialEnds,
      isAdmin: false,
    };
    return {session, authUserId};
  },

  // Sub / trade signup. Creates auth.users + local sub_account + Supabase companies row.
  // Does NOT save a session — the flow goes Stripe → handleLogin builds session.
  signUpSub: async ({email, password, name, company, trade, license, phone, accountType, plan}) => {
    const cleanEmail = (email || "").toLowerCase().trim();
    if (!cleanEmail || !password) return {error: "Email and password required"};

    const r = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
      method: "POST",
      headers: {"apikey": SUPABASE_ANON_KEY, "Content-Type": "application/json"},
      body: JSON.stringify({
        email: cleanEmail,
        password,
        data: {
          accountType: accountType || "trade",
          name: name || "", company: company || "", trade: trade || "",
        },
      }),
    });
    const data = await r.json();
    if (!r.ok || data.error) {
      return {error: data.error?.message || data.msg || "Sign up failed", status: r.status};
    }
    if (data.access_token) localStorage.setItem("sb_token", data.access_token);
    const authUserId = data.user?.id || data.id || null;

    const accounts = getSubAccounts();
    const existing = accounts.find(a => (a.email || "").toLowerCase() === cleanEmail);
    let subId, companyId;
    if (existing) {
      subId = existing.id;
      companyId = existing.companyId || ("co_" + Date.now());
      saveSubAccounts(accounts.map(a =>
        (a.email || "").toLowerCase() === cleanEmail
          ? {...a, authUserId, companyId} : a
      ));
    } else {
      subId = "sub_" + Date.now();
      companyId = "co_" + Date.now();
      saveSubAccounts([...accounts, {
        id: subId, companyId,
        email: cleanEmail, password,
        name: name || "", company: company || "", trade: trade || "Other",
        license: license || "", phone: phone || "",
        plan: plan || (accountType === "trade" ? "trade_starter" : "starter"),
        accountType: accountType || "trade",
        createdAt: new Date().toISOString(), status: "awaiting_payment",
        authUserId,
      }]);
    }

    // Mirror to Supabase companies so the stripe-webhook can match by id when payment lands.
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/companies`, {
        method: "POST",
        headers: {
          "apikey": SUPABASE_ANON_KEY,
          "Authorization": "Bearer " + sb._token(),
          "Content-Type": "application/json",
          "Prefer": "resolution=ignore-duplicates,return=minimal",
        },
        body: JSON.stringify({
          id: companyId,
          auth_user_id: authUserId,
          email: cleanEmail,
          company_name: company || "",
          owner_name: name || "",
        }),
      });
    } catch (e) { /* webhook still falls back to email match */ }

    return {subId, companyId, authUserId};
  },

  // Phase 3: authenticate against Supabase Auth. Returns {user, accessToken, refreshToken}
  // on success, or {error, status} on failure. Caller is still responsible for looking up
  // the local grok_companies / grok_sub_accounts row and building the session — the shim
  // just handles the Supabase Auth side and stashes sb_token / sb_refresh_token.
  signIn: async (email, password) => {
    const cleanEmail = (email || "").toLowerCase().trim();
    if (!cleanEmail || !password) return {error: "Email and password required"};
    const r = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
      method: "POST",
      headers: {"apikey": SUPABASE_ANON_KEY, "Content-Type": "application/json"},
      body: JSON.stringify({email: cleanEmail, password}),
    });
    const data = await r.json();
    if (!r.ok || data.error) {
      return {
        error: data.error_description || data.error?.message || data.msg || "Invalid credentials",
        status: r.status,
      };
    }
    if (data.access_token)  localStorage.setItem("sb_token", data.access_token);
    if (data.refresh_token) localStorage.setItem("sb_refresh_token", data.refresh_token);
    return {user: data.user, accessToken: data.access_token, refreshToken: data.refresh_token};
  },

  // Phase 2 + 4: request a self-serve migration email. Looks up the user locally
  // to forward owner_name / company_name / account_type to the request-migration
  // function. For legacy trade users (no Supabase companies row), the function
  // creates the row server-side via service role — that's RLS-safe and avoids the
  // pre-Phase-4 anon-key pre-upsert that would now be blocked by RLS on companies.
  requestMigrationLink: async (email) => {
    const cleanEmail = (email || "").toLowerCase().trim();
    if (!cleanEmail) return {error: "Email required"};

    const subs = getSubAccounts();
    const sub = subs.find(s => (s.email || "").toLowerCase() === cleanEmail);
    let accountType = "gc";
    let ownerName = "";
    let companyName = "";
    if (sub) {
      accountType = sub.accountType || "trade";
      ownerName = sub.name || "";
      companyName = sub.company || "";
    } else {
      const companies = getCompanies();
      const co = companies.find(c => (c.email || "").toLowerCase() === cleanEmail);
      if (co) {
        ownerName = co.owner || "";
        companyName = co.name || "";
      }
    }

    try {
      await fetch(`${SUPABASE_URL}/functions/v1/request-migration`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          email: cleanEmail,
          owner_name: ownerName,
          company_name: companyName,
          account_type: accountType,
        }),
      });
      return {ok: true};
    } catch (e) {
      return {error: "Network error"};
    }
  },

  // Clears Supabase server session + sb_token + grok_auth_session. Safe to call even
  // for users who never went through Supabase Auth — sb_token check skips the server call.
  signOut: async () => {
    const token = localStorage.getItem("sb_token");
    if (token && token !== SUPABASE_ANON_KEY) {
      try {
        await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
          method: "POST",
          headers: {"apikey": SUPABASE_ANON_KEY, "Authorization": "Bearer " + token},
        });
      } catch (e) { /* server logout best-effort */ }
    }
    localStorage.removeItem("sb_token");
    localStorage.removeItem("grok_auth_session");
  },

  // Returns the Supabase Auth user object if sb_token is valid, else null.
  // Used in Phase 3 to swap session source-of-truth from localStorage to Supabase.
  getCurrentUser: async () => {
    const token = localStorage.getItem("sb_token");
    if (!token || token === SUPABASE_ANON_KEY) return null;
    try {
      const r = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        headers: {"apikey": SUPABASE_ANON_KEY, "Authorization": "Bearer " + token},
      });
      if (!r.ok) return null;
      return await r.json();
    } catch (e) { return null; }
  },
};

// ── Central AI proxy helper — always sends correct headers ────────────────────
async function aiProxy(bodyObj) {
  const r = await fetch(AI_PROXY_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": "Bearer " + sb._token(),
      "apikey": SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({
      model: bodyObj.model || "claude-haiku-4-5",
      max_tokens: bodyObj.max_tokens || 2000,
      ...(bodyObj.messages && { messages: bodyObj.messages }),
      ...(bodyObj.system   && { system:   bodyObj.system }),
      ...(bodyObj.tools    && { tools:    bodyObj.tools }),
      ...(bodyObj.stream   && { stream:   bodyObj.stream }),
    }),
  });
  if (bodyObj.stream) return r; // return raw response for streaming
  return r.json();
}
// Called on app load and periodically. Verifies the Supabase JWT is still valid
// and re-fetches the plan from the company record — so a tampered localStorage
// session (e.g. someone manually setting plan:"gcpro") gets corrected immediately.
async function verifyAndRefreshSession(currentSession, setSession) {
  if (!currentSession) return;
  if (currentSession.plan === "sub") return; // subs use PIN auth

  try {
    const token = localStorage.getItem("sb_token");

    // localStorage-based accounts (no Supabase token) — just verify plan, don't force logout
    if (!token || token === SUPABASE_ANON_KEY) {
      const companies = getCompanies();
      const co = companies.find(c => c.id === currentSession.companyId);
      if (!co) return; // company not found but don't log out
      if (co && co.plan !== currentSession.plan) {
        const corrected = {...currentSession, plan: co.plan};
        localStorage.setItem("grok_auth_session", JSON.stringify(corrected));
        setSession(corrected);
      }
      return;
    }

    // Supabase auth accounts — verify JWT
    const sbUser = await sb.auth.getUser();
    if (!sbUser || sbUser.error) {
      // Only log out if we're confident the token is truly invalid
      // (not just a network error)
      console.warn("[Session] Could not verify JWT — keeping session");
      return;
    }

    // Re-fetch the authoritative plan from the companies record
    const companies = getCompanies();
    const co = companies.find(c => c.id === currentSession.companyId);
    if (co && co.plan !== currentSession.plan) {
      const corrected = {...currentSession, plan: co.plan, isAdmin: co.isAdmin || false};
      localStorage.setItem("grok_auth_session", JSON.stringify(corrected));
      setSession(corrected);
      console.warn("[Security] Session plan mismatch corrected:", co.plan);
    }
  } catch(e) {
    // Network error — never force logout, just skip refresh
    console.warn("[Session] Verify skipped:", e.message);
  }
}

// ─── SUB ACCOUNT SYSTEM ───────────────────────────────────────────────────────
const SUB_ACCOUNTS_KEY = "grok_sub_accounts";
const SUB_INVITE_KEY   = "grok_sub_invites";

function getSubAccounts(){try{return JSON.parse(localStorage.getItem(SUB_ACCOUNTS_KEY)||"[]");}catch{return[];}}
function saveSubAccounts(a){try{localStorage.setItem(SUB_ACCOUNTS_KEY,JSON.stringify(a));}catch(e){}}
function getSubInvites(){try{return JSON.parse(localStorage.getItem(SUB_INVITE_KEY)||"[]");}catch{return[];}}
function saveSubInvites(a){try{localStorage.setItem(SUB_INVITE_KEY,JSON.stringify(a));}catch(e){}}

function generateProjectInviteCode(project, gcSession) {
  const code = "GRK-" + String(project.id).toUpperCase() + "-" + Math.random().toString(36).slice(2,6).toUpperCase();
  const invites = getSubInvites();
  saveSubInvites([...invites.filter(i=>i.projectId!==project.id), {
    code, projectId:project.id, projectName:project.name, projectAddr:project.addr||"",
    gcCompanyId:gcSession?.companyId||"grok", gcName:gcSession?.companyName||"GC",
    createdAt:new Date().toISOString(), usedBy:[],
  }]);
  return code;
}

// ─── SUB LOGIN PANEL ──────────────────────────────────────────────────────────
// Build a Stripe Payment Link URL with client_reference_id (→ stripe-webhook matches on
// companies.id) and prefilled_email. Returns the base URL unchanged if no params provided.
function buildStripeUrl(base, {companyId, email}={}) {
  if (!base) return base;
  const params = new URLSearchParams();
  if (companyId) params.append("client_reference_id", String(companyId));
  if (email) params.append("prefilled_email", email);
  const qs = params.toString();
  if (!qs) return base;
  return base + (base.includes("?") ? "&" : "?") + qs;
}

function SubLoginPanel({onLogin, initialMode}) {
  const T = useTheme();
  const [mode, setMode] = React.useState(initialMode||"login");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [name, setName] = React.useState("");
  const [company, setCompany] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [license, setLicense] = React.useState("");
  const [licenseError, setLicenseError] = React.useState("");
  const [trade, setTrade] = React.useState("Electrical");
  const [joinMode, setJoinMode] = React.useState(()=>getSubInviteFromURL()?"project":"");
  const [inviteCode, setInviteCode] = React.useState(()=>getSubInviteFromURL()||"");
  React.useEffect(()=>{ if(mode==="register"&&!getSubInviteFromURL()) setJoinMode(""); },[mode]);
  const [error, setError] = React.useState("");
  const [showPass, setShowPass] = React.useState(false);
  const [pendingInfo, setPendingInfo] = React.useState(null);
  const [billingCycle, setBillingCycle] = React.useState("monthly");
  const [loading, setLoading] = React.useState(false);
  const [pendingCompanyId, setPendingCompanyId] = React.useState(null);
  const [migrationMsg, setMigrationMsg] = React.useState("");
  const [migrationLoading, setMigrationLoading] = React.useState(false);

  const handleRequestMigration = async () => {
    if (!email) { setError("Enter your email above first."); return; }
    setMigrationLoading(true);
    setError("");
    const r = await Auth.requestMigrationLink(email);
    setMigrationLoading(false);
    if (r.error) { setError(r.error); return; }
    setMigrationMsg("✓ If we found your account, a migration link is on its way. Check your inbox.");
  };

  const TRADES = ["Electrical","Plumbing","HVAC","Framing","Drywall","Tile & Finishes","Concrete","Roofing","Painting","Insulation","Fire Sprinkler","Low Voltage","Flooring","Other"];

  const validateLicense = (val) => {
    const clean = val.replace(/[^0-9]/g,"");
    if (!clean) { setLicenseError(""); return true; }
    if (clean.length < 6 || clean.length > 8) {
      setLicenseError("CSLB numbers are 6-8 digits (e.g. 1152750)");
      return false;
    }
    setLicenseError(""); return true;
  };

  const handleLogin = async () => {
    setError("");
    if (!email||!password) { setError("Email and password required."); return; }

    // Supabase Auth is the sole credential check. authPassed means Auth.signIn
    // validated the email+password. The legacy plaintext-password fallback has
    // been removed — accounts that only exist locally must be migrated to
    // Supabase Auth to sign in.
    const authResult = await Auth.signIn(email, password);
    const authPassed = !!authResult.user;

    const accounts = getSubAccounts();
    let acct = accounts.find(a=>a.email.toLowerCase()===email.toLowerCase().trim() && authPassed);

    // Phase 4 cross-device rehydrate: Supabase Auth succeeded but no local
    // sub_account row exists on this device. Fetch the user's companies row
    // (RLS-gated to their own row) and seed a minimal sub_account from it.
    // Trade-specific fields (trade, license, phone) start as defaults; user
    // can fill them in from Settings on the new device. accountType is inferred
    // from the plan name (trade_* → trade, else sub).
    if (!acct && authPassed) {
      try {
        const r = await fetch(
          `${SUPABASE_URL}/rest/v1/companies?email=eq.${encodeURIComponent(email.toLowerCase().trim())}&select=*`,
          {headers:{"apikey":SUPABASE_ANON_KEY,"Authorization":"Bearer "+sb._token()}}
        );
        if (r.ok) {
          const data = await r.json();
          if (data && data[0]) {
            const sbRow = data[0];
            const isTrade = (sbRow.plan || "").toLowerCase().startsWith("trade");
            acct = {
              id: "sub_" + Date.now(),
              companyId: sbRow.id,
              email: (sbRow.email || email).toLowerCase().trim(),
              name: sbRow.owner_name || email.split("@")[0],
              company: sbRow.company_name || "",
              trade: "Other",
              license: "",
              phone: "",
              plan: sbRow.plan || (isTrade ? "trade_starter" : "starter"),
              accountType: isTrade ? "trade" : "sub",
              createdAt: new Date().toISOString(),
              status: "active",
              authUserId: sbRow.auth_user_id,
            };
            saveSubAccounts([...getSubAccounts(), acct]);
            console.log("Rehydrated local sub_account from Supabase");
          }
        }
      } catch (e) {
        console.warn("Cross-device sub rehydrate failed:", e);
      }
    }

    if (!acct) {
      if (authPassed) {
        // Rehydrate attempt above didn't find a matching companies row (or the
        // fetch failed). Most likely the user migrated but no Supabase row was
        // created for them, or RLS is blocking the read.
        setError("Account found but no local data on this device. Sign in from your original browser/device, or contact support.");
        return;
      }
      // Either wrong credentials, or a migrated user typed their old password.
      // Check companies.migrated_at to give the helpful "use new password" hint.
      try {
        const r = await fetch(
          `${SUPABASE_URL}/rest/v1/companies?email=eq.${encodeURIComponent(email.toLowerCase().trim())}&select=migrated_at,auth_user_id`,
          {headers:{"apikey":SUPABASE_ANON_KEY,"Authorization":"Bearer "+sb._token()}}
        );
        if (r.ok) {
          const data = await r.json();
          if (data && data[0] && (data[0].migrated_at || data[0].auth_user_id)) {
            setError("Your account password has been updated. Sign in with your new password, or use 'Send migration link' to set one.");
            return;
          }
        }
      } catch (e) { /* fall through */ }
      setError("Invalid email or password.");
      return;
    }
    if (acct.status==="pending") { setError("Your account is pending GC approval."); return; }

    // Fetch authoritative plan from Supabase companies — the stripe-webhook updates
    // plan / plan_status / paid_through / grace_until here on payment events.
    let sbCompany = null;
    try {
      const r = await fetch(
        `${SUPABASE_URL}/rest/v1/companies?email=eq.${encodeURIComponent(acct.email.toLowerCase().trim())}&select=plan,plan_status,paid_through,grace_until,trial_ends`,
        {headers:{"apikey":SUPABASE_ANON_KEY,"Authorization":"Bearer "+sb._token()}}
      );
      if (r.ok) {
        const data = await r.json();
        if (data && data[0]) sbCompany = data[0];
      }
    } catch(e) { /* network failure — fall back to local acct */ }

    // Sync the authoritative Supabase plan back into the sub_accounts row so any
    // future code that reads acct.plan (not sess.plan) sees fresh data. Without this,
    // the local row keeps the tentative plan from registration forever.
    if (sbCompany?.plan && sbCompany.plan !== acct.plan) {
      const updatedAccounts = accounts.map(a =>
        a.id === acct.id ? {...a, plan: sbCompany.plan} : a
      );
      saveSubAccounts(updatedAccounts);
    }

    // Hard-block users whose Supabase row says no active subscription (pending payment,
    // cancelled, etc.). Mirrors the gate in AuthGate at App.js:2890.
    if (sbCompany) {
      const now = new Date();
      const isPaid  = sbCompany.paid_through && new Date(sbCompany.paid_through) > now;
      const isGrace = sbCompany.grace_until  && new Date(sbCompany.grace_until)  > now;
      const isTrial = sbCompany.trial_ends   && new Date(sbCompany.trial_ends)   > now;
      if (!isPaid && !isGrace && !isTrial && sbCompany.plan_status !== "active") {
        setError("Complete your subscription payment to access your account. If you just paid, give Stripe a few seconds and try again.");
        return;
      }
    }

    const sess = {
      companyId:"sub_"+acct.id, subId:acct.id, subName:acct.name,
      email:acct.email, trade:acct.trade, company:acct.company,
      license:acct.license,
      plan:        sbCompany?.plan || acct.plan || "trade_starter",
      planStatus:  sbCompany?.plan_status,
      paidThrough: sbCompany?.paid_through,
      graceUntil:  sbCompany?.grace_until,
      trialEnds:   sbCompany?.trial_ends,
      isSub:!acct.plan||acct.plan==="sub",
      accountType:acct.accountType||"trade",
      role:acct.role||"Subcontractor", projects:acct.projects||[], loginTime:Date.now()
    };
    saveSession(sess); onLogin(sess);
  };

  const handleRegister = async () => {
    setError("");
    if (!email||!password||!name) { setError("Name, email and password required."); return; }
    if (!joinMode) { setError("Choose how you want to join below."); return; }
    if (!validateLicense(license)) { setError("Please enter a valid CSLB license number."); return; }
    const cleanLic = license.replace(/[^0-9]/g,"");
    if (!cleanLic || cleanLic.length < 6) { setError("A valid CSLB license number is required."); return; }

    const accounts = getSubAccounts();
    if (accounts.find(a=>a.email.toLowerCase()===email.toLowerCase().trim())) {
      setError("An account with this email already exists."); return;
    }
    if (joinMode==="project") {
      if (!inviteCode.trim()) { setError("An invite code from your GC is required."); return; }
      const invites = getSubInvites();
      const invite = invites.find(i=>i.code===inviteCode.trim().toUpperCase());
      if (!invite) { setError("Invalid invite code. Double-check the code your GC sent."); return; }
      const id = "sub_"+Date.now();
      const newAcct = {id,email:email.trim(),password,name:name.trim(),company:company.trim(),
        trade,license:cleanLic,phone:phone.trim(),
        plan:"sub",accountType:"sub",createdAt:new Date().toISOString(),status:"pending",
        pendingGcName:invite.gcName,pendingProjectName:invite.projectName,
        pendingGcCompanyId:invite.gcCompanyId,inviteCode:invite.code,projects:[]};
      saveSubAccounts([...accounts, newAcct]);
      const gcPending = JSON.parse(localStorage.getItem("grok_sub_pending_"+invite.gcCompanyId)||"[]");
      localStorage.setItem("grok_sub_pending_"+invite.gcCompanyId, JSON.stringify([...gcPending,{
        subId:id,name:name.trim(),email:email.trim(),trade,company:company.trim(),
        license:cleanLic,phone:phone.trim(),inviteCode:invite.code,
        projectId:invite.projectId,projectName:invite.projectName,
        requestedAt:new Date().toISOString(),status:"pending"
      }]));
      saveSubInvites(invites.map(i=>i.code===invite.code?{...i,pendingBy:[...(i.pendingBy||[]),email]}:i));
      setPendingInfo({name:name.trim(),gcName:invite.gcName,projectName:invite.projectName});
      setMode("pending"); return;
    }
    if (joinMode==="trade"||joinMode==="gc") {
      localStorage.setItem("grok_pending_account_type", joinMode==="trade"?"trade":"gc");

      // Phase 1 auth migration: try the appropriate Auth shim. Falls back to
      // the legacy local-only path on any error (user_already_exists, network
      // failure) so existing users still complete their Stripe checkout.
      let newCompanyId;

      if (joinMode === "trade") {
        const r = await Auth.signUpSub({
          email, password,
          name, company, trade, license: cleanLic, phone,
          accountType: "trade",
          plan: "trade_starter",
        });
        if (r.companyId) {
          newCompanyId = r.companyId;
        } else {
          if (r.error) console.warn("Auth.signUpSub fallback:", r.error);
          // ── Legacy fallback (preserved from pre-shim implementation) ───
          newCompanyId = "co_"+Date.now();
          saveSubAccounts([...accounts, {
            id: "sub_"+Date.now(), companyId: newCompanyId,
            email: email.trim(), password,
            name: name.trim(), company: company.trim(),
            trade, license: cleanLic, phone: phone.trim(),
            plan: "trade_starter", accountType: "trade",
            createdAt: new Date().toISOString(),
            status: "awaiting_payment",
          }]);
          fetch(`${SUPABASE_URL}/rest/v1/companies`, {
            method: "POST",
            headers: {
              "apikey": SUPABASE_ANON_KEY,
              "Authorization": "Bearer "+sb._token(),
              "Content-Type": "application/json",
              "Prefer": "resolution=ignore-duplicates,return=minimal",
            },
            body: JSON.stringify({
              id: newCompanyId,
              email: email.toLowerCase().trim(),
              company_name: company.trim() || "",
              owner_name: name.trim() || "",
              plan: "trade_starter",
              plan_status: "pending_payment",
            }),
          }).catch(()=>{});
        }
      } else {
        // joinMode === "gc" — user entered via SubLoginPanel but is registering as a GC.
        // Auth.signUpGc creates auth.users + local grok_companies (so AuthGate.login
        // resolves later) + Supabase companies row with auth_user_id linked.
        const r = await Auth.signUpGc({
          email, password,
          companyName: company,
          ownerName: name,
          cslb: cleanLic,
          location: "",
          plan: "starter",
        });
        if (r.session) {
          newCompanyId = r.session.companyId;
        } else {
          if (r.error) console.warn("Auth.signUpGc fallback:", r.error);
          newCompanyId = "co_"+Date.now();
          fetch(`${SUPABASE_URL}/rest/v1/companies`, {
            method: "POST",
            headers: {
              "apikey": SUPABASE_ANON_KEY,
              "Authorization": "Bearer "+sb._token(),
              "Content-Type": "application/json",
              "Prefer": "resolution=ignore-duplicates,return=minimal",
            },
            body: JSON.stringify({
              id: newCompanyId,
              email: email.toLowerCase().trim(),
              company_name: company.trim() || "",
              owner_name: name.trim() || "",
              plan: "starter",
              plan_status: "pending_payment",
            }),
          }).catch(()=>{});
        }
      }

      setPendingCompanyId(newCompanyId);
      setMode("subscribe");
      return;
    }
  };

  // ── PENDING SCREEN ──
  if (mode==="pending") return (
    <div style={{textAlign:"center",padding:"20px 0"}}>
      <div style={{fontSize:36,marginBottom:12}}>⏳</div>
      <div style={{fontSize:15,fontWeight:800,color:T.text,marginBottom:8}}>Request Sent</div>
      <div style={{fontSize:12,color:T.muted,lineHeight:1.8,marginBottom:16}}>
        Your request to join <strong style={{color:T.gold}}>{pendingInfo?.projectName}</strong> has been sent to <strong style={{color:T.text}}>{pendingInfo?.gcName}</strong>.
      </div>
      <div style={{background:T.amberBg||"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.2)",borderRadius:10,padding:"12px 16px",fontSize:11,color:T.amber,lineHeight:1.7}}>
        You'll receive an email once the GC approves your access.
      </div>
    </div>
  );

  // ── SUBSCRIBE SCREEN ──
  if (mode==="subscribe") {
    const isTrade = localStorage.getItem("grok_pending_account_type")==="trade";
    const plans = isTrade ? [
      {name:"Trade Starter",price:"$79",annual:"$790",annualSave:"$158",desc:"Solo trade contractors",color:T.blue,
       features:["5 active projects","30 AI estimates/mo","150 AI chats/mo","Blueprint AI","Sub-bid generator","Document vault"],
       monthly:"https://buy.stripe.com/00wdRaehn31A7aTfwl4Rq06",
       yr:"https://buy.stripe.com/3cI5kE4GNeKi0Mvbg54Rq0a"},
      {name:"Trade Pro",price:"$129",annual:"$1,290",annualSave:"$258",desc:"Growing trade teams",color:T.purple,popular:true,
       features:["Unlimited projects","Unlimited AI estimates","Unlimited AI chats","Unlimited blueprints","Team roles (coming soon)","Priority support"],
       monthly:"https://buy.stripe.com/fZu14o5KRdGe8eXfwl4Rq09",
       yr:"https://buy.stripe.com/fZu9AU3CJdGe66PgAp4Rq08"},
    ] : [
      {name:"Starter",price:"$149",annual:"$1,490",annualSave:"$298",desc:"Solo GCs · 3 active projects",color:T.teal,
       features:["3 active projects","25 AI estimates/mo","150 AI chats/mo","Blueprint AI (10/mo)","RFI & CO tracking","ADA annotation"],
       monthly:"https://buy.stripe.com/00wdRaehn31A7aTfwl4Rq06",
       yr:"https://buy.stripe.com/3cI5kE4GNeKi0Mvbg54Rq0a"},
      {name:"Pro",price:"$249",annual:"$2,490",annualSave:"$498",desc:"Growing GC teams",color:T.blue,popular:true,
       features:["10 active projects","50 AI estimates/mo","350 AI chats/mo","Blueprint AI (25/mo)","20 subs","Priority support"],
       monthly:"https://buy.stripe.com/fZu14o5KRdGe8eXfwl4Rq09",
       yr:"https://buy.stripe.com/fZu9AU3CJdGe66PgAp4Rq08"},
      {name:"GC Pro",price:"$399",annual:"$3,990",annualSave:"$798",desc:"Full-scale operations",color:T.gold,
       features:["Unlimited projects","Unlimited AI","Unlimited blueprints","Unlimited subs","Team roles (coming soon)","Everything in Pro"],
       monthly:"https://buy.stripe.com/5kQ6oI5KRgSqcvdck94Rq07",
       yr:"https://buy.stripe.com/7sY3cw4GNeKi8eXesh4Rq05"},
    ];
    return (
      <div>
        <div style={{textAlign:"center",marginBottom:20}}>
          <div style={{fontSize:17,fontWeight:800,color:T.text,letterSpacing:"-0.5px",marginBottom:6}}>
            {isTrade?"⚡ Trade Contractor Plans":"🏗 GC Platform Plans"}
          </div>
          <div style={{fontSize:12,color:T.muted,lineHeight:1.6,marginBottom:14}}>
            {isTrade?"Built for electricians, plumbers, HVAC, and every trade.":"Built for GCs who run tight, transparent jobs."}
          </div>
          <div style={{display:"flex",gap:4,background:T.bg2,borderRadius:10,padding:3,marginBottom:16,maxWidth:240,margin:"0 auto 16px"}}>
            {["monthly","annual"].map(c=>(
              <button key={c} onClick={()=>setBillingCycle(c)}
                style={{flex:1,padding:"6px",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",border:"none",
                  background:billingCycle===c?T.text:T.bg2,color:billingCycle===c?T.bg0:T.muted}}>
                {c==="monthly"?"Monthly":"Annual"}
              </button>
            ))}
          </div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
          {plans.map(p=>(
            <div key={p.name} style={{background:T.bg2,border:"2px solid "+(p.popular?p.color:T.subtle),borderRadius:12,padding:"16px",position:"relative"}}>
              {p.popular&&<div style={{position:"absolute",top:-10,right:12,background:p.color,color:"#02040A",fontSize:8,fontWeight:700,padding:"3px 10px",borderRadius:10,letterSpacing:".06em"}}>MOST POPULAR</div>}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                <div>
                  <div style={{fontSize:14,fontWeight:800,color:p.color}}>{p.name}</div>
                  <div style={{fontSize:10,color:T.muted,marginTop:2}}>{p.desc}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:20,fontWeight:800,color:T.text}}>{billingCycle==="annual"?p.annual:p.price}</div>
                  <div style={{fontSize:9,color:T.muted}}>{billingCycle==="annual"?"per year · save "+p.annualSave:"/mo"}</div>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"3px 8px",marginBottom:12}}>
                {p.features.map((f,i)=>(
                  <div key={i} style={{fontSize:10,color:T.muted,display:"flex",gap:5,alignItems:"flex-start"}}>
                    <span style={{color:T.green,flexShrink:0,fontSize:9}}>✓</span>{f}
                  </div>
                ))}
              </div>
              <a href={buildStripeUrl(billingCycle==="annual"?p.yr:p.monthly, {companyId: pendingCompanyId, email})} target="_blank" rel="noopener noreferrer"
                style={{display:"block",textAlign:"center",padding:"10px",background:p.color,color:p.color===T.gold?"#02040A":"white",borderRadius:9,fontSize:12,fontWeight:700,textDecoration:"none"}}>
                Get Started — {billingCycle==="annual"?p.annual+"/yr":p.price+"/mo"} →
              </a>
            </div>
          ))}
        </div>
        <div style={{textAlign:"center",fontSize:10,color:T.muted,marginBottom:10}}>
          ✓ 15-day money-back guarantee &nbsp;·&nbsp; ✓ Cancel anytime
        </div>
        <button onClick={()=>setMode("register")} style={{width:"100%",background:"none",border:"none",color:T.muted,fontSize:11,cursor:"pointer",textDecoration:"underline"}}>
          ← Back
        </button>
      </div>
    );
  }

  // ── MAIN LOGIN / REGISTER ──
  return (
    <div>
      {/* Tabs */}
      <div style={{display:"flex",gap:4,marginBottom:16,background:T.bg2,borderRadius:9,padding:3}}>
        {[["login","Log In"],["register","Sign Up"]].map(([m,l])=>(
          <button key={m} onClick={()=>{setMode(m);setError("");}}
            style={{flex:1,padding:"7px",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",
              background:mode===m?T.bg1:"transparent",color:mode===m?T.text:T.muted,transition:"all 0.15s"}}>
            {l}
          </button>
        ))}
      </div>

      {/* LOGIN */}
      {mode==="login"&&(
        <div>
          <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:16}}>Sign in to your account</div>
          <AuthField label="Email Address" value={email} onChange={setEmail} placeholder="you@company.com" type="email"/>
          <AuthField label="Password" value={password} onChange={setPassword} placeholder="Your password"
            type={showPass?"text":"password"}
            suffix={<button onClick={()=>setShowPass(p=>!p)} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:11}}>{showPass?"Hide":"Show"}</button>}
            onEnter={handleLogin}/>
          {error&&<div style={{background:T.redBg||"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:8,padding:"9px 12px",fontSize:12,color:T.red,marginBottom:12}}>{error}</div>}
          {migrationMsg&&<div style={{background:T.greenBg||"rgba(34,197,94,0.08)",border:"1px solid rgba(34,197,94,0.2)",borderRadius:8,padding:"9px 12px",fontSize:12,color:T.green,marginBottom:12}}>{migrationMsg}</div>}
          <AuthBtn label="Sign In" onClick={handleLogin} disabled={!email||!password}/>
          <div style={{marginTop:10,textAlign:"center",fontSize:11,color:T.muted}}>
            <button onClick={handleRequestMigration} disabled={migrationLoading||!email} style={{background:"none",border:"none",color:T.gold,cursor:migrationLoading?"wait":"pointer",fontSize:11,opacity:(!email||migrationLoading)?0.5:1}}>
              {migrationLoading?"Sending...":"Old account? Send migration link →"}
            </button>
          </div>
          <div style={{marginTop:8,textAlign:"center",fontSize:11,color:T.muted}}>
            No account? <button onClick={()=>setMode("register")} style={{background:"none",border:"none",color:T.blue,cursor:"pointer",fontSize:11,fontWeight:600}}>Sign Up</button>
          </div>
        </div>
      )}

      {/* REGISTER */}
      {mode==="register"&&(
        <div>
          <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:4}}>Create your account</div>
          <div style={{fontSize:11,color:T.muted,marginBottom:16,lineHeight:1.6}}>
            Licensed contractors only — GC or trade welcome.
          </div>

          {/* Join mode selector */}
          {!joinMode&&(
            <div style={{marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:10}}>How do you want to use Grok?</div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {[
                  {id:"project", icon:"🔗", title:"Join a GC's Project", desc:"Free — you have an invite code from your GC", color:T.green},
                  {id:"trade",   icon:"⚡", title:"Trade Contractor Plan", desc:"Run your own projects — $79/mo", color:T.blue},
                ].map(opt=>(
                  <button key={opt.id} onClick={()=>setJoinMode(opt.id)}
                    style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:10,cursor:"pointer",textAlign:"left",transition:"border-color 0.15s"}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=opt.color}
                    onMouseLeave={e=>e.currentTarget.style.borderColor=T.subtle}>
                    <span style={{fontSize:20,flexShrink:0}}>{opt.icon}</span>
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:T.text}}>{opt.title}</div>
                      <div style={{fontSize:10,color:T.muted,marginTop:2}}>{opt.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {joinMode&&(
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14,padding:"8px 12px",background:T.bg2,borderRadius:8}}>
                <span style={{fontSize:14}}>{joinMode==="project"?"🔗":joinMode==="trade"?"⚡":"🏗"}</span>
                <div style={{flex:1,fontSize:11,color:T.text,fontWeight:600}}>
                  {joinMode==="project"?"Joining a GC Project":joinMode==="trade"?"Trade Contractor Plan":"GC Platform"}
                </div>
                <button onClick={()=>setJoinMode("")} style={{fontSize:10,color:T.muted,background:"none",border:"none",cursor:"pointer"}}>Change</button>
              </div>

              <AuthField label="Your Name *" value={name} onChange={setName} placeholder="John Smith"/>
              <AuthField label="Company Name" value={company} onChange={setCompany} placeholder="Smith Electric LLC"/>
              <AuthField label="Email Address *" value={email} onChange={setEmail} placeholder="you@company.com" type="email"/>
              <AuthField label="Password *" value={password} onChange={setPassword} placeholder="Choose a secure password"
                type={showPass?"text":"password"}
                suffix={<button onClick={()=>setShowPass(p=>!p)} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:11}}>{showPass?"Hide":"Show"}</button>}/>

              {/* Trade selector */}
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:6}}>Trade / Specialty</div>
                <select value={trade} onChange={e=>setTrade(e.target.value)}
                  style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:13,fontFamily:"inherit",outline:"none"}}>
                  {TRADES.map(t=><option key={t}>{t}</option>)}
                </select>
              </div>

              {/* CSLB License */}
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:6}}>
                  CSLB License # <span style={{color:T.red}}>*</span>
                  <span style={{fontSize:10,color:T.muted,fontWeight:400}}> — Licensed contractors only</span>
                </div>
                <div style={{position:"relative"}}>
                  <input value={license} onChange={e=>{setLicense(e.target.value);validateLicense(e.target.value);}}
                    placeholder="1152750"
                    style={{width:"100%",background:T.bg2,border:"1px solid "+(licenseError?T.red:license.replace(/[^0-9]/g,"").length>=6?T.green:T.subtle),borderRadius:8,padding:"10px 12px",color:T.text,fontSize:13,fontFamily:"inherit",outline:"none"}}/>
                  {license.replace(/[^0-9]/g,"").length>=6&&!licenseError&&(
                    <span style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",color:T.green}}>✓</span>
                  )}
                </div>
                {licenseError&&<div style={{fontSize:10,color:T.red,marginTop:4}}>⚠ {licenseError}</div>}
                {!licenseError&&license.replace(/[^0-9]/g,"").length>=6&&(
                  <div style={{fontSize:10,color:T.muted,marginTop:4}}>By signing up you certify this is your valid, active CSLB license.</div>
                )}
                <div style={{fontSize:10,color:T.muted,marginTop:4}}>
                  Verify at <span style={{color:T.gold,cursor:"pointer"}} onClick={()=>window.open("https://www.cslb.ca.gov/OnlineServices/CheckLicenseII/CheckLicense.aspx","_blank")}>cslb.ca.gov →</span>
                </div>
              </div>

              {joinMode==="project"&&(
                <AuthField label="Invite Code from your GC *" value={inviteCode} onChange={setInviteCode} placeholder="e.g. GROK-ABC123"/>
              )}

              {error&&<div style={{background:T.redBg||"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:8,padding:"9px 12px",fontSize:12,color:T.red,marginBottom:12}}>{error}</div>}
              <AuthBtn label={loading?"Creating account...":joinMode==="project"?"Request to Join":"Continue →"} onClick={handleRegister} disabled={loading}/>
              <div style={{marginTop:8,textAlign:"center",fontSize:10,color:T.muted}}>
                Already have an account? <button onClick={()=>setMode("login")} style={{background:"none",border:"none",color:T.blue,cursor:"pointer",fontSize:10,fontWeight:600}}>Log In</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function SignaturePad({onSign, onCancel, label}) {
  const canvasRef = React.useRef(null);
  const [mode, setMode] = React.useState("draw"); // draw | type
  const [drawing, setDrawing] = React.useState(false);
  const [hasStrokes, setHasStrokes] = React.useState(false);
  const [typedName, setTypedName] = React.useState("");
  const lastPos = React.useRef(null);
  const width = 340; const height = 120;

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) return {x:(e.touches[0].clientX-rect.left)*scaleX, y:(e.touches[0].clientY-rect.top)*scaleY};
    return {x:(e.clientX-rect.left)*scaleX, y:(e.clientY-rect.top)*scaleY};
  };

  const startDraw = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current; if(!canvas) return;
    const pos = getPos(e, canvas);
    lastPos.current = pos; setDrawing(true); setHasStrokes(true);
    const ctx = canvas.getContext("2d");
    ctx.beginPath(); ctx.arc(pos.x, pos.y, 1.5, 0, Math.PI*2);
    ctx.fillStyle = "#1e293b"; ctx.fill();
  };
  const draw = (e) => {
    if (!drawing) return; e.preventDefault();
    const canvas = canvasRef.current; if(!canvas) return;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.beginPath(); ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#1e293b"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.stroke();
    lastPos.current = pos;
  };
  const stopDraw = () => setDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current; if(!canvas) return;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasStrokes(false); lastPos.current = null;
  };

  const renderTypedToCanvas = (name) => {
    const canvas = canvasRef.current; if(!canvas) return null;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = "italic 36px Georgia, serif";
    ctx.fillStyle = "#1e3a5f";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(name, canvas.width/2, canvas.height/2);
    return canvas.toDataURL("image/png");
  };

  const confirm = () => {
    const now = new Date().toLocaleString();
    if (mode === "type") {
      if (!typedName.trim()) return;
      const dataUrl = renderTypedToCanvas(typedName.trim());
      onSign({dataUrl, name:typedName.trim(), date:now, mode:"typed"});
    } else {
      if (!hasStrokes) return;
      onSign({dataUrl:canvasRef.current.toDataURL("image/png"), name:"Signed", date:now, mode:"drawn"});
    }
  };

  const inp = {width:"100%",background:"white",border:"1px solid #d1d5db",borderRadius:7,padding:"8px 12px",fontSize:20,fontFamily:"Georgia, serif",fontStyle:"italic",color:"#1e3a5f"};

  return (
    <div style={{background:T.bg1,borderRadius:12,padding:"16px",border:"1px solid "+T.subtle}}>
      {label&&<div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>{label}</div>}

      {/* Mode selector */}
      <div style={{display:"flex",gap:4,marginBottom:10,background:T.bg2,borderRadius:7,padding:3}}>
        {[["draw","✍ Draw"],["type","⌨ Type"]].map(([m,l])=>(
          <button key={m} onClick={()=>{setMode(m);clear();setTypedName("");}}
            style={{flex:1,padding:"5px",borderRadius:5,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",background:mode===m?T.bg1:"transparent",color:mode===m?T.text:T.muted,boxShadow:mode===m?"0 1px 3px rgba(0,0,0,0.1)":"none"}}>
            {l}
          </button>
        ))}
      </div>

      {mode==="draw" ? (
        <div>
          <div style={{fontSize:10,color:T.muted,marginBottom:6}}>Use your finger or mouse to sign below</div>
          <div style={{border:"2px solid "+T.subtle,borderRadius:8,background:"white",overflow:"hidden",touchAction:"none",position:"relative"}}>
            <canvas ref={canvasRef} width={width} height={height}
              style={{display:"block",width:"100%",height:height+"px",cursor:"crosshair",touchAction:"none"}}
              onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
              onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}/>
            {!hasStrokes&&(
              <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",fontSize:12,color:"#94a3b8",pointerEvents:"none"}}>
                Sign here
              </div>
            )}
          </div>
          <button onClick={clear} style={{marginTop:6,fontSize:10,background:"none",border:"1px solid "+T.subtle,borderRadius:6,padding:"3px 8px",cursor:"pointer",color:T.muted}}>Clear</button>
        </div>
      ) : (
        <div>
          <div style={{fontSize:10,color:T.muted,marginBottom:6}}>Type your full legal name — it will be rendered as a signature</div>
          <input value={typedName} onChange={e=>setTypedName(e.target.value)}
            placeholder="Your full name" style={inp}/>
          {typedName&&(
            <div style={{marginTop:8,padding:"8px 12px",background:"white",borderRadius:6,border:"1px solid "+T.subtle,textAlign:"center",fontSize:28,fontFamily:"Georgia, serif",fontStyle:"italic",color:"#1e3a5f"}}>
              {typedName}
            </div>
          )}
        </div>
      )}

      <div style={{marginTop:10,fontSize:9,color:T.muted,background:T.bg2,borderRadius:6,padding:"5px 8px",lineHeight:1.6}}>
        By signing, you confirm you are authorized to sign this document. Date & time will be recorded: {new Date().toLocaleString()}
      </div>

      <div style={{display:"flex",gap:7,marginTop:10}}>
        {onCancel&&<button onClick={onCancel} style={{flex:1,padding:"8px",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",color:T.muted}}>Cancel</button>}
        <button onClick={confirm} disabled={mode==="draw"?!hasStrokes:!typedName.trim()}
          style={{flex:2,padding:"8px",background:(mode==="draw"?hasStrokes:typedName.trim())?T.green:T.bg3,color:(mode==="draw"?hasStrokes:typedName.trim())?"white":T.muted,border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",transition:"all 0.15s"}}>
          ✍ Confirm & Sign
        </button>
      </div>
    </div>
  );
}

// ─── SIGNATURE BLOCK ─────────────────────────────────────────────────────────
// assignedEmail: if set, only that person can sign
// onSign: receives {dataUrl, name, date, mode, email}
// onSaveToDoc: saves signed record to project documents folder
function SignatureBlock({label, role, signData, onSign, canSign=true, required=false, assignedEmail=null, signerEmail=null, onSaveToDoc=null}) {
  const [showPad, setShowPad] = React.useState(false);

  // Check if current user is the assigned signer
  const isAssigned = !assignedEmail || assignedEmail===signerEmail;
  const canActuallySign = canSign && isAssigned;

  if (signData?.dataUrl) {
    return (
      <div style={{background:T.greenBg,border:"1px solid "+T.green+"40",borderRadius:9,padding:"12px 14px"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
          <span style={{fontSize:16}}>✅</span>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:T.green}}>{label} — Signed</div>
            <div style={{fontSize:10,color:T.muted}}>{signData.name} · {signData.date}</div>
            <div style={{fontSize:9,color:T.muted}}>{signData.mode==="typed"?"Typed signature":"Hand drawn"}</div>
          </div>
        </div>
        {signData.dataUrl&&<img src={signData.dataUrl} alt="Signature"
          style={{maxWidth:"100%",height:50,objectFit:"contain",background:"white",borderRadius:4,padding:"2px 8px",border:"1px solid "+T.subtle}}/>}
      </div>
    );
  }

  return (
    <div style={{background:T.bg1,border:"1px solid "+(required?T.amber+"60":T.subtle),borderRadius:9,padding:"12px 14px"}}>
      <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:2}}>{label}</div>
      <div style={{fontSize:10,color:T.muted,marginBottom:4}}>
        {role} signature required{required?" to proceed":""}
        {assignedEmail&&<span style={{color:T.teal}}> · Assigned to {assignedEmail}</span>}
      </div>
      {showPad ? (
        <SignaturePad
          label={"Sign as "+role}
          onSign={(sigData)=>{
            const full = {...sigData, email:signerEmail||"", role};
            onSign(full);
            if (onSaveToDoc) onSaveToDoc(full, label);
            setShowPad(false);
          }}
          onCancel={()=>setShowPad(false)}
        />
      ) : canActuallySign ? (
        <button onClick={()=>setShowPad(true)}
          style={{marginTop:6,fontSize:11,fontWeight:700,background:T.teal,color:"white",border:"none",borderRadius:7,padding:"7px 16px",cursor:"pointer"}}>
          ✍ Sign Now
        </button>
      ) : !isAssigned ? (
        <div style={{fontSize:10,color:T.amber,marginTop:6,fontStyle:"italic"}}>⚠ Only {assignedEmail} can sign this block</div>
      ) : (
        <div style={{fontSize:10,color:T.muted,marginTop:6,fontStyle:"italic"}}>Waiting for {role} to sign</div>
      )}
    </div>
  );
}



// TEAM_ROLES — per Grok Final Verified Playbook Section 9
// GC Admin: full access | Team: operations only | Architect: technical only | Client/Owner: dashboard only
// NEVER expose: Margin, Sub bids, Internal cost to non-owners
const TEAM_ROLES = {
  owner: {
    label: "GC Owner / Admin",
    desc: "Full access to everything including margin, sub bids, and all financials. Can manage team and settings.",
    color: "#d4a843",
    canSee: { margin: true, financials: true, subBids: true, allProjects: true, settings: true, team: true, leads: true, kb: true },
    canEdit: { projects: true, cos: true, rfis: true, subs: true, settings: true, team: true, budget: true },
  },
  admin: {
    label: "Admin",
    desc: "Full operational access. Can see margin and financials. Cannot change billing or plan.",
    color: "#a78bfa",
    canSee: { margin: true, financials: true, subBids: true, allProjects: true, settings: true, team: true, leads: true, kb: true },
    canEdit: { projects: true, cos: true, rfis: true, subs: true, settings: true, team: true, budget: true },
  },
  pm: {
    label: "Project Manager",
    desc: "Full operations access — manage projects, COs, RFIs, subs, schedule. Can see sub bids for management. Cannot see margin or internal financials.",
    color: "#60a5fa",
    canSee: { margin: false, financials: false, subBids: true, allProjects: true, settings: false, team: false, leads: true, kb: false },
    canEdit: { projects: true, cos: true, rfis: true, subs: true, settings: false, team: false, budget: false },
  },
  estimator: {
    label: "Estimator",
    desc: "Creates estimates and bids. Can see sub bids to build accurate pricing. Cannot see margin, financials, or internal costs.",
    color: "#2dd4bf",
    canSee: { margin: false, financials: false, subBids: true, allProjects: true, settings: false, team: false, leads: true, kb: false },
    canEdit: { projects: false, cos: false, rfis: true, subs: false, settings: false, team: false, budget: false },
  },
  field: {
    label: "Field Superintendent",
    desc: "Field access — daily logs, punch list, schedule, site photos. Can view approved COs to know what scope is authorized. Cannot edit COs or see financials.",
    color: "#f97316",
    canSee: { margin: false, financials: false, subBids: false, allProjects: true, settings: false, team: false, leads: false, kb: false },
    canEdit: { projects: false, cos: false, rfis: true, subs: false, settings: false, team: false, budget: false },
  },
  architect: {
    label: "Architect",
    desc: "Technical access — blueprints, RFIs, drawings, specifications. Can view and respond to RFIs and view COs for design coordination. Cannot see costs or margin.",
    color: "#818cf8",
    canSee: { margin: false, financials: false, subBids: false, allProjects: true, settings: false, team: false, leads: false, kb: false },
    canEdit: { projects: false, cos: false, rfis: true, subs: false, settings: false, team: false, budget: false },
  },
  client: {
    label: "Owner / Client",
    desc: "Client view — sees project progress, approved COs, and schedule milestones on their assigned project only. Never sees margin, sub costs, or internal pricing.",
    color: "#94a3b8",
    canSee: { margin: false, financials: false, subBids: false, allProjects: false, settings: false, team: false, leads: false, kb: false },
    canEdit: { projects: false, cos: false, rfis: false, subs: false, settings: false, team: false, budget: false },
  },
  viewer: {
    label: "View Only",
    desc: "Read-only access to assigned projects and reports. Cannot edit anything or see financials.",
    color: "#64748b",
    canSee: { margin: false, financials: false, subBids: false, allProjects: true, settings: false, team: false, leads: false, kb: false },
    canEdit: { projects: false, cos: false, rfis: false, subs: false, settings: false, team: false, budget: false },
  },
};

// Helper: get effective role for session — applies per-member permission overrides
function getSessionRole(session) {
  if (!session) return TEAM_ROLES.viewer;
  if (session.isSub) return TEAM_ROLES.viewer;
  if (session.isAdmin || !session.teamRole || session.teamRole === "owner") return TEAM_ROLES.owner;

  const baseRole = TEAM_ROLES[session.teamRole] || TEAM_ROLES.owner;

  // Apply per-member permission overrides if any
  if (session.permOverrides && Object.keys(session.permOverrides).length > 0) {
    return {
      ...baseRole,
      canSee: {...baseRole.canSee, ...(session.permOverrides.canSee||{})},
      canEdit: {...baseRole.canEdit, ...(session.permOverrides.canEdit||{})},
    };
  }

  // Apply company-level role overrides from RoleCustomizerTab
  try {
    const companyOverrides = JSON.parse(localStorage.getItem("grok_role_overrides_"+(session.companyId||"local"))||"{}");
    const roleOverrides = companyOverrides[session.teamRole];
    if (roleOverrides) {
      return {
        ...baseRole,
        canSee: {...baseRole.canSee, ...(roleOverrides.canSee||{})},
        canEdit: {...baseRole.canEdit, ...(roleOverrides.canEdit||{})},
      };
    }
  } catch(e) {}

  return baseRole;
}

// Helper: can this session do X?
function can(session, action, proj) {
  // Team management (add/remove members) always uses COMPANY-level role only
  // Cannot be overridden at project level — only Owner and Admin can manage team
  if (action === "team") {
    const companyRole = getSessionRole(session);
    return companyRole.canEdit.team === true;
  }
  // For all other actions, check project-level role override first
  if (proj?.members && session?.email) {
    const member = proj.members.find(m=>m.email===session.email);
    if (member?.role) {
      const projRole = TEAM_ROLES[member.role]||TEAM_ROLES.viewer;
      return projRole.canEdit[action] !== false;
    }
  }
  const role = getSessionRole(session);
  return role.canEdit[action] !== false;
}

function canSee(session, thing, proj) {
  // Financials and margin are ALWAYS company-level only — Owner and Admin only
  // Cannot be granted via project-level role override
  if (thing === "financials" || thing === "margin" || thing === "subBids") {
    const companyRole = getSessionRole(session);
    return companyRole.canSee[thing] === true;
  }
  // For other things, check project-level role override first
  if (proj?.members && session?.email) {
    const member = proj.members.find(m=>m.email===session.email);
    if (member?.role) {
      const projRole = TEAM_ROLES[member.role]||TEAM_ROLES.viewer;
      return projRole.canSee[thing] !== false;
    }
  }
  const role = getSessionRole(session);
  return role.canSee[thing] !== false;
}

// Team storage helpers
function teamKey(companyId) { return "grok_team_" + (companyId || "local"); }
function getTeam(companyId) {
  try { return JSON.parse(localStorage.getItem(teamKey(companyId)) || "[]"); } catch { return []; }
}
function saveTeam(members, companyId) {
  try { localStorage.setItem(teamKey(companyId), JSON.stringify(members)); } catch(e) {}
  SyncDB.saveTeam(members).catch(()=>{});
}
async function getTeamFromDB(companyId) {
  try {
    const remote = await SyncDB.getTeam();
    if (remote.length) {
      localStorage.setItem(teamKey(companyId), JSON.stringify(remote));
      return remote;
    }
  } catch(e) {}
  return getTeam(companyId);
}

// Generate an invite token
function generateInviteToken(companyId, companyName, email, role, invitedBy) {
  const token = "INV" + Math.random().toString(36).slice(2, 10).toUpperCase();
  const invites = JSON.parse(localStorage.getItem("grok_team_invites") || "[]");
  const invite = { token, companyId, companyName, email: email.toLowerCase(), role, invitedBy, createdAt: new Date().toISOString(), used: false };
  localStorage.setItem("grok_team_invites", JSON.stringify([...invites, invite]));
  return token;
}

// ─── TEAM MANAGER VIEW (inside Company Settings) ─────────────────────────────
function TeamManagerView({ session }) {
  const companyId = session?.companyId;
  const isGCPro = session?.plan === "gcpro" || session?.plan === "team" || session?.plan === "owner";
  const [members, setMembers] = React.useState(() => getTeam(companyId));
  const [showInvite, setShowInvite] = React.useState(false);
  const [inviteEmail, setInviteEmail] = React.useState("");
  const [inviteRole, setInviteRole] = React.useState("pm");
  const [inviteName, setInviteName] = React.useState("");
  const [lastToken, setLastToken] = React.useState(null);
  const [editingPerms, setEditingPerms] = React.useState(null); // member id being edited

  // Custom roles stored per company
  const CUSTOM_ROLES_KEY = "grok_custom_roles_" + companyId;
  const [customRoles, setCustomRoles] = React.useState(()=>{
    try { return JSON.parse(localStorage.getItem(CUSTOM_ROLES_KEY)||"{}"); } catch { return {}; }
  });
  const [showNewRole, setShowNewRole] = React.useState(false);
  const [newRoleName, setNewRoleName] = React.useState("");
  const [newRolePerms, setNewRolePerms] = React.useState({
    canSee:{subBids:false,leads:false,settings:false,kb:false,allProjects:true,margin:false,financials:false},
    canEdit:{projects:false,cos:false,rfis:true,subs:false,settings:false,team:false,budget:false},
  });

  const allRoles = {...TEAM_ROLES, ...customRoles};

  const save = (updated) => { setMembers(updated); saveTeam(updated, companyId); };

  const saveCustomRoles = (updated) => {
    setCustomRoles(updated);
    localStorage.setItem(CUSTOM_ROLES_KEY, JSON.stringify(updated));
  };

  const createCustomRole = () => {
    if (!newRoleName.trim()) return;
    const key = "custom_" + newRoleName.toLowerCase().replace(/\s+/g,"_") + "_" + Date.now();
    const newRole = {
      label: newRoleName.trim(),
      desc: "Custom role — " + newRoleName.trim(),
      color: "#60a5fa",
      isCustom: true,
      ...newRolePerms,
    };
    saveCustomRoles({...customRoles, [key]: newRole});
    setNewRoleName(""); setShowNewRole(false);
    setNewRolePerms({
      canSee:{subBids:false,leads:false,settings:false,kb:false,allProjects:true,margin:false,financials:false},
      canEdit:{projects:false,cos:false,rfis:true,subs:false,settings:false,team:false,budget:false},
    });
  };

  const deleteCustomRole = (key) => {
    const updated = {...customRoles};
    delete updated[key];
    saveCustomRoles(updated);
  };

  const sendInvite = () => {
    if (!inviteEmail.trim()) return;
    const token = generateInviteToken(companyId, session?.companyName, inviteEmail.trim(), inviteRole, session?.email);
    const member = {
      id: "tm_" + Date.now(),
      email: inviteEmail.trim().toLowerCase(),
      name: inviteName.trim() || inviteEmail.split("@")[0],
      role: inviteRole,
      status: "invited",
      invitedAt: new Date().toISOString(),
      invitedBy: session?.email,
      token,
      permOverrides: {}, // per-member overrides
    };
    save([...members, member]);
    setLastToken(token);
    setInviteEmail(""); setInviteName(""); setShowInvite(false);
  };

  const removeTeamMember = (id) => {
    save(members.filter(m => String(m.id) !== String(id)));
  };

  const updateRole = (id, role) => save(members.map(m => String(m.id) === String(id) ? { ...m, role } : m));

  const updatePermOverride = (memberId, section, perm, val) => {
    save(members.map(m => String(m.id) === String(memberId) ? {
      ...m,
      permOverrides: {
        ...m.permOverrides,
        [section]: {...(m.permOverrides?.[section]||{}), [perm]: val}
      }
    } : m));
  };

  const clearPermOverrides = (memberId) => {
    save(members.map(m => String(m.id) === String(memberId) ? {...m, permOverrides: {}} : m));
  };

  const copyInviteLink = (token) => {
    const url = window.location.origin + "?team_invite=" + token;
    navigator.clipboard.writeText(url);
    alert("Invite link copied!\n\nShare this with your team member:\n" + url);
  };

  const sendInviteEmail = (member) => {
    const url = window.location.origin + "?team_invite=" + member.token;
    const subject = encodeURIComponent(`You've been invited to join ${session?.companyName} on Grok Field Ops`);
    const body = encodeURIComponent(`Hi ${member.name},\n\n${session?.ownerName || session?.companyName} has invited you to join their construction management workspace on Grok Field Ops as ${allRoles[member.role]?.label}.\n\nClick the link below to create your account and join:\n${url}\n\nYour role: ${allRoles[member.role]?.label}\nAccess: ${allRoles[member.role]?.desc}\n\nThis invite expires in 7 days.\n\nGrok Field Ops Team`);
    window.open(`mailto:${member.email}?subject=${subject}&body=${body}`);
  };

  const SEE_PERMS = [{key:"allProjects",label:"View All Projects"},{key:"leads",label:"Lead Pipeline"},{key:"subBids",label:"Sub Bids"},{key:"settings",label:"Company Settings"},{key:"kb",label:"AI Knowledge Base"}];
  const EDIT_PERMS = [{key:"projects",label:"Edit Projects"},{key:"cos",label:"Edit COs"},{key:"rfis",label:"Edit RFIs"},{key:"subs",label:"Manage Subs"},{key:"budget",label:"Edit Budget"}];

  const editingMember = members.find(m=>String(m.id)===String(editingPerms));
  const baseRole = editingMember ? (allRoles[editingMember.role]||TEAM_ROLES.viewer) : null;

  if (!isGCPro) {
    return (
      <div style={{ background: T.amberBg, border: "1px solid " + T.amber + "30", borderRadius: 10, padding: "20px", textAlign: "center" }}>
        <div style={{ fontSize: 28, marginBottom: 10 }}>👥</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 6 }}>Team Members — GC Pro Plan</div>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 16, lineHeight: 1.7 }}>
          Invite your Project Manager, Estimator, Field Super, and more.<br />
          Each team member gets role-based access — they only see what they need.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
          {Object.entries(TEAM_ROLES).filter(([k]) => k !== "owner").map(([key, role]) => (
            <div key={key} style={{ background: T.bg2, borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: role.color, marginBottom: 4 }}>{role.label}</div>
              <div style={{ fontSize: 10, color: T.muted, lineHeight: 1.5 }}>{role.desc}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.amber }}>Upgrade to GC Pro at $399/mo to unlock team access →</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Team Members</div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
            {members.length} member{members.length !== 1 ? "s" : ""} · Role-based access control
          </div>
        </div>
        <div style={{display:"flex",gap:7}}>
          <button onClick={()=>setShowNewRole(p=>!p)}
            style={{fontSize:11,fontWeight:600,background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"6px 12px",cursor:"pointer",color:T.muted}}>
            + Custom Role
          </button>
          <Btn primary small onClick={() => setShowInvite(p => !p)}>
            <Ic d={ICONS.plus} size={11} /> Invite Member
          </Btn>
        </div>
      </div>

      {/* Create Custom Role */}
      {showNewRole&&(
        <div style={{background:T.bg2,border:`1px solid ${T.blue}30`,borderRadius:10,padding:"14px 16px",marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Create Custom Role</div>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Role Name</div>
            <input value={newRoleName} onChange={e=>setNewRoleName(e.target.value)} placeholder="e.g. Site Coordinator, Office Manager"
              style={{width:"100%",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Can See</div>
              {SEE_PERMS.map(p=>(
                <div key={p.key} onClick={()=>setNewRolePerms(prev=>({...prev,canSee:{...prev.canSee,[p.key]:!prev.canSee[p.key]}}))}
                  style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",borderRadius:6,cursor:"pointer",marginBottom:4,background:newRolePerms.canSee[p.key]?T.greenBg:T.bg1,border:`1px solid ${newRolePerms.canSee[p.key]?T.green:T.subtle}30`}}>
                  <div style={{width:14,height:14,borderRadius:3,border:`2px solid ${newRolePerms.canSee[p.key]?T.green:T.subtle}`,background:newRolePerms.canSee[p.key]?T.green:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {newRolePerms.canSee[p.key]&&<span style={{color:"white",fontSize:9,lineHeight:1}}>✓</span>}
                  </div>
                  <span style={{fontSize:11,color:T.text}}>{p.label}</span>
                </div>
              ))}
            </div>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Can Edit</div>
              {EDIT_PERMS.map(p=>(
                <div key={p.key} onClick={()=>setNewRolePerms(prev=>({...prev,canEdit:{...prev.canEdit,[p.key]:!prev.canEdit[p.key]}}))}
                  style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",borderRadius:6,cursor:"pointer",marginBottom:4,background:newRolePerms.canEdit[p.key]?T.blueBg:T.bg1,border:`1px solid ${newRolePerms.canEdit[p.key]?T.blue:T.subtle}30`}}>
                  <div style={{width:14,height:14,borderRadius:3,border:`2px solid ${newRolePerms.canEdit[p.key]?T.blue:T.subtle}`,background:newRolePerms.canEdit[p.key]?T.blue:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {newRolePerms.canEdit[p.key]&&<span style={{color:"white",fontSize:9,lineHeight:1}}>✓</span>}
                  </div>
                  <span style={{fontSize:11,color:T.text}}>{p.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{display:"flex",gap:7}}>
            <button onClick={()=>setShowNewRole(false)} style={{flex:1,padding:"7px",borderRadius:7,border:`1px solid ${T.subtle}`,background:T.bg1,color:T.muted,fontSize:11,cursor:"pointer"}}>Cancel</button>
            <button disabled={!newRoleName.trim()} onClick={createCustomRole}
              style={{flex:2,padding:"7px",borderRadius:7,border:"none",background:newRoleName.trim()?T.blue:T.bg3,color:"white",fontSize:11,fontWeight:700,cursor:newRoleName.trim()?"pointer":"not-allowed"}}>
              Create Role
            </button>
          </div>
        </div>
      )}

      {/* Custom roles list */}
      {Object.keys(customRoles).length>0&&(
        <div style={{background:T.bg2,borderRadius:9,padding:"10px 14px",marginBottom:12}}>
          <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Custom Roles</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {Object.entries(customRoles).map(([key,role])=>(
              <div key={key} style={{display:"flex",alignItems:"center",gap:6,background:T.bg1,border:`1px solid ${T.blue}30`,borderRadius:20,padding:"3px 10px 3px 12px"}}>
                <span style={{fontSize:11,fontWeight:700,color:T.blue}}>{role.label}</span>
                <button onClick={()=>deleteCustomRole(key)} style={{background:"none",border:"none",color:T.muted,fontSize:12,cursor:"pointer",lineHeight:1}}>×</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite form */}
      {showInvite && (
        <div style={{ background: T.bg2, border: "1px solid " + T.blue + "30", borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 10 }}>Invite a Team Member</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
            {[["inviteEmail", "Email Address *", "pm@company.com", "email"],
              ["inviteName", "Their Name", "Jane Smith", "text"]].map(([k, l, ph, type]) => (
              <div key={k}>
                <div style={{ fontSize: 9, color: T.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3 }}>{l}</div>
                <input value={k === "inviteEmail" ? inviteEmail : inviteName}
                  onChange={e => k === "inviteEmail" ? setInviteEmail(e.target.value) : setInviteName(e.target.value)}
                  placeholder={ph} type={type}
                  style={{ width: "100%", background: T.bg1, border: "1px solid " + T.subtle, borderRadius: 6, padding: "7px 9px", color: T.text, fontSize: 12, fontFamily: "inherit" }} />
              </div>
            ))}
            <div>
              <div style={{ fontSize: 9, color: T.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3 }}>Role *</div>
              <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
                style={{ width: "100%", background: T.bg1, border: "1px solid " + T.subtle, borderRadius: 6, padding: "7px 9px", color: T.text, fontSize: 12, fontFamily: "inherit" }}>
                {Object.entries(allRoles).filter(([k]) => k !== "owner").map(([key, role]) => (
                  <option key={key} value={key}>{role.label}{role.isCustom?" (Custom)":""}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ background: T.bg3, borderRadius: 7, padding: "8px 10px", marginBottom: 10, fontSize: 10, color: T.muted, lineHeight: 1.6 }}>
            <strong style={{ color: allRoles[inviteRole]?.color }}>{allRoles[inviteRole]?.label}:</strong> {allRoles[inviteRole]?.desc}
          </div>
          <div style={{ display: "flex", gap: 7 }}>
            <Btn ghost small onClick={() => setShowInvite(false)} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
            <Btn primary small disabled={!inviteEmail.trim()} onClick={sendInvite} style={{ flex: 2, justifyContent: "center" }}>
              Generate Invite Link
            </Btn>
          </div>
        </div>
      )}

      {/* Last generated token */}
      {lastToken && (
        <div style={{ background: T.greenBg, border: "1px solid " + T.green + "30", borderRadius: 9, padding: "12px 14px", marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.green, marginBottom: 6 }}>✓ Invite link generated</div>
          <div style={{ background: T.bg1, borderRadius: 6, padding: "8px 10px", fontFamily: "monospace", fontSize: 11, color: T.blue, marginBottom: 8, wordBreak: "break-all" }}>
            {window.location.origin}?team_invite={lastToken}
          </div>
          <div style={{ display: "flex", gap: 7 }}>
            <button onClick={() => copyInviteLink(lastToken)}
              style={{ fontSize: 11, fontWeight: 700, background: T.green, color: "white", border: "none", borderRadius: 7, padding: "6px 14px", cursor: "pointer" }}>
              📋 Copy Link
            </button>
            <button onClick={() => setLastToken(null)}
              style={{ fontSize: 11, background: T.bg2, color: T.muted, border: "1px solid " + T.subtle, borderRadius: 7, padding: "6px 12px", cursor: "pointer" }}>
              Done
            </button>
          </div>
        </div>
      )}

      {/* Member list */}
      {members.length === 0 ? (
        <div style={{ textAlign: "center", padding: "32px", color: T.muted, background: T.bg2, borderRadius: 10, border: "2px dashed " + T.subtle }}>
          <div style={{ fontSize: 28, opacity: 0.3, marginBottom: 8 }}>👥</div>
          <div style={{ fontSize: 12 }}>No team members yet. Invite your PM, estimator, or field super.</div>
        </div>
      ) : (
        <div>
          {members.map(member => {
            const role = allRoles[member.role] || TEAM_ROLES.viewer;
            const hasOverrides = member.permOverrides && Object.keys(member.permOverrides).some(k=>Object.keys(member.permOverrides[k]||{}).length>0);
            const isEditing = String(editingPerms) === String(member.id);
            return (
              <div key={member.id} style={{marginBottom:8}}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: T.bg1, borderRadius: isEditing?"9px 9px 0 0":9, border: "1px solid " + (isEditing?T.blue:T.subtle), borderBottom: isEditing?"none":"1px solid "+T.subtle }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: role.color + "20", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: role.color, flexShrink: 0 }}>
                    {member.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{display:"flex",alignItems:"center",gap:7}}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{member.name}</span>
                      {hasOverrides&&<span style={{fontSize:8,fontWeight:700,background:T.blue+"20",color:T.blue,borderRadius:20,padding:"1px 6px"}}>Custom perms</span>}
                    </div>
                    <div style={{ fontSize: 10, color: T.muted }}>{member.email}</div>
                  </div>
                  <select value={member.role} onChange={e => updateRole(member.id, e.target.value)}
                    style={{ fontSize: 10, fontWeight: 700, background: role.color + "15", color: role.color, border: "1px solid " + role.color + "40", borderRadius: 10, padding: "3px 8px", cursor: "pointer", fontFamily: "inherit" }}>
                    {Object.entries(allRoles).filter(([k]) => k !== "owner").map(([key, r]) => (
                      <option key={key} value={key}>{r.label}</option>
                    ))}
                  </select>
                  <span style={{ fontSize: 9, fontWeight: 700, background: member.status === "active" ? T.greenBg : T.amberBg, color: member.status === "active" ? T.green : T.amber, borderRadius: 10, padding: "2px 8px" }}>
                    {member.status === "active" ? "Active" : "Invited"}
                  </span>
                  <button onClick={()=>setEditingPerms(isEditing?null:member.id)}
                    style={{fontSize:10,fontWeight:600,background:isEditing?T.blue:T.bg2,color:isEditing?"white":T.muted,border:`1px solid ${isEditing?T.blue:T.subtle}`,borderRadius:6,padding:"3px 8px",cursor:"pointer"}}>
                    {isEditing?"Done":"Edit Perms"}
                  </button>
                  {member.status === "invited" && (
                    <button onClick={() => sendInviteEmail(member)}
                      style={{ fontSize: 10, fontWeight: 600, background: T.bg2, border: "1px solid " + T.subtle, borderRadius: 6, padding: "3px 8px", cursor: "pointer", color: T.muted }}>
                      📧 Resend
                    </button>
                  )}
                  <button onClick={() => removeTeamMember(member.id)}
                    style={{ fontSize: 10, background: "none", color: T.muted, border: "1px solid " + T.subtle, borderRadius: 6, padding: "3px 8px", cursor: "pointer" }}>
                    Remove
                  </button>
                </div>

                {/* Per-member permission override panel */}
                {isEditing&&(
                  <div style={{background:T.bg2,border:`1px solid ${T.blue}`,borderTop:"none",borderRadius:"0 0 9px 9px",padding:"14px 16px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                      <div>
                        <div style={{fontSize:11,fontWeight:700,color:T.text}}>Custom permissions for {member.name}</div>
                        <div style={{fontSize:10,color:T.muted,marginTop:2}}>Overrides their {role.label} role defaults. Leave unchecked to use role defaults.</div>
                      </div>
                      {hasOverrides&&(
                        <button onClick={()=>clearPermOverrides(member.id)}
                          style={{fontSize:10,color:T.red,background:"none",border:`1px solid ${T.red}40`,borderRadius:6,padding:"3px 8px",cursor:"pointer"}}>
                          Clear all overrides
                        </button>
                      )}
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                      <div>
                        <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Can See</div>
                        {SEE_PERMS.map(p=>{
                          const roleDefault = role.canSee?.[p.key]||false;
                          const override = member.permOverrides?.canSee?.[p.key];
                          const effective = override !== undefined ? override : roleDefault;
                          const hasOverride = override !== undefined;
                          return (
                            <div key={p.key} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",borderRadius:6,marginBottom:4,background:effective?T.greenBg:T.bg1,border:`1px solid ${effective?T.green:T.subtle}30`,cursor:"pointer"}}
                              onClick={()=>updatePermOverride(member.id,"canSee",p.key,!effective)}>
                              <div style={{width:14,height:14,borderRadius:3,border:`2px solid ${effective?T.green:T.subtle}`,background:effective?T.green:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                                {effective&&<span style={{color:"white",fontSize:9,lineHeight:1}}>✓</span>}
                              </div>
                              <span style={{fontSize:11,color:T.text,flex:1}}>{p.label}</span>
                              {hasOverride&&<span style={{fontSize:8,color:T.blue,fontWeight:700}}>custom</span>}
                              {!hasOverride&&<span style={{fontSize:8,color:T.muted}}>role default</span>}
                            </div>
                          );
                        })}
                      </div>
                      <div>
                        <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Can Edit</div>
                        {EDIT_PERMS.map(p=>{
                          const roleDefault = role.canEdit?.[p.key]||false;
                          const override = member.permOverrides?.canEdit?.[p.key];
                          const effective = override !== undefined ? override : roleDefault;
                          const hasOverride = override !== undefined;
                          return (
                            <div key={p.key} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",borderRadius:6,marginBottom:4,background:effective?T.blueBg:T.bg1,border:`1px solid ${effective?T.blue:T.subtle}30`,cursor:"pointer"}}
                              onClick={()=>updatePermOverride(member.id,"canEdit",p.key,!effective)}>
                              <div style={{width:14,height:14,borderRadius:3,border:`2px solid ${effective?T.blue:T.subtle}`,background:effective?T.blue:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                                {effective&&<span style={{color:"white",fontSize:9,lineHeight:1}}>✓</span>}
                              </div>
                              <span style={{fontSize:11,color:T.text,flex:1}}>{p.label}</span>
                              {hasOverride&&<span style={{fontSize:8,color:T.blue,fontWeight:700}}>custom</span>}
                              {!hasOverride&&<span style={{fontSize:8,color:T.muted}}>role default</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── TEAM INVITE HANDLER — runs on app load ───────────────────────────────────
// When a team member clicks their invite link, this reads the token from the URL
// and pre-fills their registration with the company they're joining
function checkTeamInviteToken() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("team_invite");
  if (!token) return null;
  try {
    const invites = JSON.parse(localStorage.getItem("grok_team_invites") || "[]");
    const invite = invites.find(i => i.token === token && !i.used);
    return invite || null;
  } catch { return null; }
}

function acceptTeamInvite(token, newMember, session) {
  // Mark invite as used
  try {
    const invites = JSON.parse(localStorage.getItem("grok_team_invites") || "[]");
    localStorage.setItem("grok_team_invites", JSON.stringify(
      invites.map(i => i.token === token ? { ...i, used: true } : i)
    ));
  } catch(e) {}

  // Add to team roster with active status
  const team = getTeam(newMember.companyId);
  const updated = team.map(m =>
    m.token === token ? { ...m, status: "active", name: newMember.name, joinedAt: new Date().toISOString() } : m
  );
  // If not already in team (direct token link), add them
  if (!updated.find(m => m.token === token)) {
    updated.push({ id: "tm_" + Date.now(), email: newMember.email, name: newMember.name, role: newMember.teamRole, status: "active", joinedAt: new Date().toISOString() });
  }
  saveTeam(updated, newMember.companyId);
}

function getSubInviteFromURL() {
  const params = new URLSearchParams(window.location.search);
  return params.get("sub_invite") || null;
}


function AuthGate({children}) {
  // Seed company settings for both owner accounts on first load
  React.useEffect(()=>{
    const companies = (() => { try{ return JSON.parse(localStorage.getItem("grok_companies")||"[]"); } catch{ return []; }})();

    // Seed GCG real account (jehoon@grokconstructiongroupinc.com)
    const gcg = companies.find(co=>co.id==="gcg_001"||co.email==="jehoon@grokconstructiongroupinc.com");
    if (gcg) {
      const settKey = "grok_company_settings_" + gcg.id;
      const existing = (() => { try{ return JSON.parse(localStorage.getItem(settKey)||"{}"); } catch{ return {}; }})();
      if (!existing.seeded_gcg) {
        const seeded = {
          ...existing,
          company_name:  "Grok Construction Group Inc",
          owner_name:    "Jehoon Chun",
          cslb:          "CSLB #1152750",
          location:      "2201 Bridgepointe Pkwy F125, San Mateo CA 94404",
          addr:          "2201 Bridgepointe Pkwy F125, San Mateo CA 94404",
          phone:         "(650) 773-5317",
          email:         "Jehoon@gcginc.com",
          website:       "www.grokconstructiongroup.com",
          license:       "1152750",
          ein:           "88-1234567",
          dir:           "1000098765",
          bonded:        true,
          insured:       true,
          // Labor rates for GCG
          labor: {
            superintendent: {label:"Superintendent",rate:95},
            journeyman:     {label:"Journeyman",rate:85},
            apprentice:     {label:"Apprentice",rate:60},
            laborer:        {label:"Laborer",rate:55},
          },
          seeded_gcg: true,
        };
        try{ localStorage.setItem(settKey, JSON.stringify(seeded)); } catch(e){}
      }

      // Ensure all 16 GCG templates exist — skip if files already uploaded
      const tplKey = "grok_templates_gcg_001";
      const gcgTemplates = [
        {id:"gcg_t1",  name:"Owner Agreement — Universal",               cat:"contracts", notes:"Universal owner contract — all project types. CSLB #1152750.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Owner_Agreement_Universal.docx"},
        {id:"gcg_t2",  name:"Subcontractor Agreement",                    cat:"contracts", notes:"GC-to-Sub — pay-when-paid, indemnity, DIR, daily reporting, LD pass-through.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Subcontractor_Agreement.docx"},
        {id:"gcg_t3",  name:"GCG as Subcontractor Agreement",             cat:"contracts", notes:"GCG-to-Head-GC — 45-day pay cap, LD rejection, flow-down limits, public works retainage.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_As_Subcontractor_Agreement.docx"},
        {id:"gcg_t4",  name:"Construction Proposal",                      cat:"contracts", notes:"3 payment formats — Residential/ADU, SRO phased, Commercial/Affordable/Public Works.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Proposal.docx"},
        {id:"gcg_t5",  name:"Change Order",                               cat:"co",        notes:"CO form — description, cost breakdown, revised contract total, schedule impact.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Change_Order.docx"},
        {id:"gcg_t6",  name:"Request for Information (RFI)",              cat:"rfi",       notes:"RFI — subject, issue description, response section, signatures.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_RFI_Form.docx"},
        {id:"gcg_t7",  name:"Lien Waiver Package — All 4 CA Forms",       cat:"lien",      notes:"All 4 CA statutory lien waivers — §8132/§8134/§8136/§8138 plus usage guide.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Lien_Waiver_Package.docx"},
        {id:"gcg_t8",  name:"20-Day Preliminary Notice",                  cat:"lien",      notes:"CA §8200 — required before filing lien. To owner, GC, and lender. Proof of service.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_20Day_Preliminary_Notice.docx"},
        {id:"gcg_t9",  name:"Notice of Completion",                       cat:"closeout",  notes:"CA §8180 — record within 15 days. Starts lien clock. Recording instructions included.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Notice_of_Completion.docx"},
        {id:"gcg_t10", name:"Certificate of Substantial Completion",      cat:"closeout",  notes:"Starts warranty period, punch list period, and retention release.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Certificate_Substantial_Completion.docx"},
        {id:"gcg_t11", name:"7-Day Tenant Notice",                        cat:"sro",       notes:"SF Admin Code §37.10A — required before work in occupied residential units.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_7Day_Tenant_Notice.docx"},
        {id:"gcg_t12", name:"Subcontractor Pre-Qualification Form",       cat:"contracts", notes:"License, DIR, insurance, 3 references, compliance disclosure, GCG review box.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Sub_PreQualification.docx"},
        {id:"gcg_t13", name:"Punch List",                                 cat:"closeout",  notes:"17 trade sections, C/I/N-A checkboxes, dual sign-off. Required before final payment.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Punch_List.docx"},
        {id:"gcg_t14", name:"1-Year Warranty Letter",                     cat:"closeout",  notes:"Workmanship warranty — coverage, exclusions, claim process. Signed by Jehoon Chun.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Warranty_Letter.docx"},
        {id:"gcg_t15", name:"Occupied Unit Construction Protocol",        cat:"sro",       notes:"Daily checklist — pre-work, during, end-of-day, lead paint, tenant acknowledgment.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_Occupied_Unit_Protocol.docx"},
        {id:"gcg_t16", name:"Project Close-Out Checklist",                cat:"closeout",  notes:"Inspections, liens, warranty, financial, site turnover + SRO section.",uploadedAt:new Date().toLocaleDateString(),uploadedBy:"Jehoon Chun",isGCG:true,fileName:"GCG_CloseOut_Checklist.docx"},
      ];
      try {
        const existing = JSON.parse(localStorage.getItem(tplKey)||"[]");
        // Build data map — preserve ALL uploaded file data
        const dataMap = {};
        existing.forEach(t => { if(t.data) dataMap[t.id] = t.data; });
        const hasUploadedFiles = Object.keys(dataMap).length > 0;
        if (hasUploadedFiles) {
          // Files uploaded — only add missing template entries, never touch data
          const existingIds = new Set(existing.map(t=>t.id));
          const toAdd = gcgTemplates.filter(t=>!existingIds.has(t.id));
          if (toAdd.length > 0 || existing.length > gcgTemplates.length) {
            // Clean duplicates but preserve data
            const gcgIds = new Set(gcgTemplates.map(t=>t.id));
            const cleaned = [
              ...gcgTemplates.map(t => dataMap[t.id] ? {...t, data:dataMap[t.id]} : t),
              ...existing.filter(t => !t.isGCG),
            ];
            localStorage.setItem(tplKey, JSON.stringify(cleaned));
          }
        } else {
          // No files uploaded yet — just seed metadata
          const existingIds = new Set(existing.map(t=>t.id));
          const toAdd = gcgTemplates.filter(t=>!existingIds.has(t.id));
          if (toAdd.length > 0) {
            const cleaned = [
              ...gcgTemplates.map(t => GCG_FILE_DATA[t.id] ? {...t, data:GCG_FILE_DATA[t.id]} : t),
              ...existing.filter(t => !t.isGCG),
            ];
            localStorage.setItem(tplKey, JSON.stringify(cleaned));
          }
        }
      } catch(e) {
        try { localStorage.setItem(tplKey, JSON.stringify(gcgTemplates)); } catch(e2) {}
      }
    }

    // ── Migrate any admin_001 data → gcg_001 (one-time) ──────────────────────
    if (!localStorage.getItem("grok_migrated_admin_to_gcg_v4")) {
      // Move non-template data from admin_001 → gcg_001
      [
        ["grok_company_settings_admin_001","grok_company_settings_gcg_001"],
        ["grok_kb_admin_001",              "grok_kb_gcg_001"],
        ["grok_projects_admin_001",        "grok_projects_gcg_001"],
        ["grok_rfis_global_admin_001",     "grok_rfis_global_gcg_001"],
        ["grok_cos_global_admin_001",      "grok_cos_global_gcg_001"],
        ["grok_team_admin_001",            "grok_team_gcg_001"],
      ].forEach(([from, to]) => {
        try {
          const data = localStorage.getItem(from);
          if (data && !localStorage.getItem(to)) {
            // Filter out demo projects when migrating
            if (from.includes('grok_projects')) {
              const projs = JSON.parse(data||'[]').filter(p=>!String(p.id).startsWith('demo_')&&!String(p.id).startsWith('gcg_proj_'));
              localStorage.setItem(to, JSON.stringify(projs));
            } else {
              localStorage.setItem(to, data);
            }
          }
        } catch(e) {}
      });
      // Completely wipe admin_001 templates — demo account must be clean
      try { localStorage.removeItem("grok_templates_admin_001"); } catch(e) {}
      // Wipe all other admin_001 data
      ["grok_kb_admin_001","grok_projects_admin_001","grok_rfis_global_admin_001",
       "grok_cos_global_admin_001","grok_team_admin_001","grok_company_settings_admin_001",
       "grok_migrated_admin_to_gcg","grok_migrated_admin_to_gcg_v2"].forEach(k=>{
        try { localStorage.removeItem(k); } catch(e) {}
      });
      localStorage.setItem("grok_migrated_admin_to_gcg_v4","1");
      localStorage.setItem("grok_deleted_demos_gcg_001", JSON.stringify(["demo_1","demo_2","demo_3","demo_4","gcg_proj_1","gcg_proj_2"]));
    }

    // ── Clean demo projects from gcg_001 on every load ──────────────────────
    try {
      const gcgKey = "grok_projects_gcg_001";
      const stored = JSON.parse(localStorage.getItem(gcgKey)||"[]");
      const cleaned = stored.filter(p=>!String(p.id).startsWith("demo_")&&!String(p.id).startsWith("gcg_proj_"));
      if (cleaned.length !== stored.length) {
        localStorage.setItem(gcgKey, JSON.stringify(cleaned));
      }
      // Always keep deleted demos set for gcg_001
      localStorage.setItem("grok_deleted_demos_gcg_001", JSON.stringify(["demo_1","demo_2","demo_3","demo_4","gcg_proj_1","gcg_proj_2"]));
      // Also clean co key
      const coKey = "grok_projects_co_1778112985277";
      const stored2 = JSON.parse(localStorage.getItem(coKey)||"[]");
      const cleaned2 = stored2.filter(p=>!String(p.id).startsWith("demo_")&&!String(p.id).startsWith("gcg_proj_"));
      if (cleaned2.length !== stored2.length) {
        localStorage.setItem(coKey, JSON.stringify(cleaned2));
      }
    } catch(e) {}
  }, []);

  const [session, setSession] = useState(()=>getSession());
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentCheckMsg, setPaymentCheckMsg] = useState("");
  const [legalView, setLegalView] = useState(null);

  // ── Security: verify session on mount and every 5 minutes ─────────────────
  React.useEffect(()=>{
    verifyAndRefreshSession(session, setSession);
    // Proactively refresh the Supabase access token on boot so the first Storage
    // upload / signed-URL fetch carries a live JWT (an expired sb_token gets
    // rotated via the stored refresh token). No-op for localStorage-only accounts.
    ensureSupabaseToken();
    const interval = setInterval(()=>verifyAndRefreshSession(session, setSession), 5*60*1000);
    return ()=>clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [authMode, setAuthMode] = useState("login");
  const [gcResetEmail, setGcResetEmail] = useState("");
  const [gcResetCode, setGcResetCode] = useState("");
  const [gcNewPassword, setGcNewPassword] = useState("");
  const [gcResetSent, setGcResetSent] = useState(false);
  const [gcResetVerified, setGcResetVerified] = useState(false);
  const [gcResetError, setGcResetError] = useState("");
  const [gcResetSuccess, setGcResetSuccess] = useState("");
  const [gcResetToken, setGcResetToken] = useState(""); // recovery access_token from GoTrue /verify

  // Password reset routes entirely through Supabase Auth (GoTrue REST), matching
  // the no-SDK pattern used elsewhere. Flow: /recover emails a numeric OTP (6–10 digits) →
  // /verify (type:recovery) exchanges it for a short-lived access_token →
  // PUT /user sets the new password. Requires the Supabase "Reset Password"
  // email template to send {{ .Token }} (the numeric code), not a magic link.
  const sendGcResetCode = async () => {
    setGcResetError("");
    const co = gcResetEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(co)) { setGcResetError("Enter a valid email."); return; }
    setGcResetSent(true);
    try {
      const r = await fetch(`${SUPABASE_URL}/auth/v1/recover`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY },
        body: JSON.stringify({ email: co }),
      });
      // GoTrue returns 200 even for unknown emails (anti-enumeration); only surface
      // real transport/config failures.
      if (!r.ok) {
        const d = await r.json().catch(()=>({}));
        setGcResetError(d.msg || d.error_description || "Could not send reset email.");
        setGcResetSent(false);
      }
    } catch(e) { setGcResetError("Network error sending reset email."); setGcResetSent(false); }
  };

  const verifyGcResetCode = async () => {
    setGcResetError("");
    const co = gcResetEmail.trim().toLowerCase();
    const token = gcResetCode.trim();
    if (!token) { setGcResetError("Enter the code from your email."); return; }
    try {
      const r = await fetch(`${SUPABASE_URL}/auth/v1/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY },
        body: JSON.stringify({ type: "recovery", email: co, token }),
      });
      const d = await r.json().catch(()=>({}));
      if (!r.ok || !d.access_token) {
        setGcResetError(d.msg || d.error_description || "Incorrect or expired code.");
        return;
      }
      setGcResetToken(d.access_token);
      setGcResetVerified(true);
    } catch(e) { setGcResetError("Network error verifying code."); }
  };

  const doGcPasswordReset = async () => {
    setGcResetError("");
    if (gcNewPassword.length<6) { setGcResetError("Password must be at least 6 characters."); return; }
    if (!gcResetToken) { setGcResetError("Session expired — restart the reset."); setGcResetVerified(false); return; }
    try {
      const r = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY, "Authorization": `Bearer ${gcResetToken}` },
        body: JSON.stringify({ password: gcNewPassword }),
      });
      const d = await r.json().catch(()=>({}));
      if (!r.ok) { setGcResetError(d.msg || d.error_description || "Could not update password."); return; }
    } catch(e) { setGcResetError("Network error updating password."); return; }
    setGcResetToken("");
    setGcResetSuccess("Password updated! Redirecting to login...");
    setTimeout(()=>{setAuthMode("login");setGcResetSent(false);setGcResetVerified(false);setGcResetSuccess("");setGcResetCode("");setGcNewPassword("");},2000);
  };
  const [isMobile] = useState(()=>window.innerWidth<768); // login | register | sub_login
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [cslb, setCslb] = useState("");
  const [licenseError, setLicenseError] = useState("");
  const [licenseType, setLicenseType] = useState(""); // "gc" or "trade"

  const validateLicense = (val) => {
    const clean = val.replace(/[^0-9]/g,"");
    if (!clean) { setLicenseError(""); setLicenseType(""); return; }
    if (clean.length < 6 || clean.length > 8) {
      setLicenseError("CSLB numbers are 6-8 digits (e.g. 1152750)");
      setLicenseType(""); return;
    }
    setLicenseError("");
    // Auto-detect from context: if trade account type pending, set trade
    const pendingType = localStorage.getItem("grok_pending_account_type")||"gc";
    setLicenseType(pendingType);
  };
  const [location, setLocation] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [subPin, setSubPin] = useState("");
  const [subProject, setSubProject] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planTab, setPlanTab] = useState("starter");
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [showVerify, setShowVerify] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const [migrationMsg, setMigrationMsg] = useState("");
  const [migrationLoading, setMigrationLoading] = useState(false);

  const handleRequestMigration = async () => {
    if (!email) { setError("Enter your email above first."); return; }
    setMigrationLoading(true);
    setError("");
    const r = await Auth.requestMigrationLink(email);
    setMigrationLoading(false);
    if (r.error) { setError(r.error); return; }
    setMigrationMsg("✓ If we found your account, a migration link is on its way. Check your inbox.");
  };

  const handleStripeCheckout = async (plan) => {
    if (!email || !password || !companyName) return;
    const cleanCslb = cslb.replace(/[^0-9]/g,"");
    if (!cleanCslb || cleanCslb.length < 6 || cleanCslb.length > 8) {
      setError("Please enter a valid CSLB license number (6-8 digits) to continue.");
      return;
    }
    setLoading(true);
    // Store pending registration data
    const pendingReg = {companyName,ownerName,cslb,location,email,password,plan:plan.id.startsWith("pro")?"pro":"starter"};
    localStorage.setItem("grok_pending_reg", JSON.stringify(pendingReg));
    // In production: create Stripe checkout session via your backend
    // For now: open Stripe payment link and auto-register after
    // Stripe Payment Links (set these up at dashboard.stripe.com/payment-links):
    const stripeLinks = {
      starter_monthly: "https://buy.stripe.com/5kQeVeddjdGe2UDesh4Rq00",
      starter_annual:  "https://buy.stripe.com/9B6bJ2gpv6dMeDl1Fv4Rq01",
      pro_monthly:     "https://buy.stripe.com/fZu8wQ2yF0Ts1Qzesh4Rq02",
      pro_annual:      "https://buy.stripe.com/9B67sMflr6dM3YH1Fv4Rq03",
      gcpro_monthly:   "https://buy.stripe.com/5kQ6oI5KRgSqcvdck94Rq07",
      gcpro_annual:    "https://buy.stripe.com/7sY3cw4GNeKi8eXesh4Rq05",
    };
    const planId = plan.id || "";
    const link = planId.includes("gcpro") && planId.includes("annual") ? stripeLinks.gcpro_annual :
                 planId.includes("gcpro") ? stripeLinks.gcpro_monthly :
                 planId.includes("pro") && planId.includes("annual") ? stripeLinks.pro_annual :
                 planId.includes("pro") ? stripeLinks.pro_monthly :
                 planId.includes("annual") ? stripeLinks.starter_annual :
                 stripeLinks.starter_monthly;
    if (link && !link.includes("YOUR_")) {
      const planName = planId.includes("gcpro") ? "gcpro" : planId.includes("pro") ? "pro" : "starter";

      // Phase 1 auth migration: try Supabase Auth via the shim. On any error
      // (user_already_exists, network failure, etc.) fall back to the legacy
      // local-only path so existing users can still subscribe to a new plan.
      // The shim creates auth.users + local grok_companies + Supabase companies
      // with auth_user_id linked. Session is built but NOT saved here — the user
      // logs in after returning from Stripe.
      let companyId;
      const authResult = await Auth.signUpGc({
        email, password, companyName, ownerName, cslb, location, plan: planName,
      });
      if (authResult.session) {
        companyId = authResult.session.companyId;
      } else {
        if (authResult.error) console.warn("Auth.signUpGc fallback:", authResult.error);
        // ── Legacy fallback (preserved from pre-shim implementation) ───────
        const companies = getCompanies();
        const exists = companies.find(co=>co.email.toLowerCase()===email.toLowerCase());
        if (exists) {
          companyId = exists.id;
        } else {
          const newCo = {id:"co_"+Date.now(),name:companyName,cslb:cslb||"",location:location||"",
            owner:ownerName||email.split("@")[0],email,password,plan:planName,
            createdAt:new Date().toISOString().slice(0,10),logo:null,primaryColor:"#D4A843",
            trialEnds:new Date(Date.now()+14*864e5).toISOString().slice(0,10),
            moneyBackDeadline:new Date(Date.now()+7*864e5).toISOString().slice(0,10),
            cancellations:0,isAdmin:false,emailVerified:false,awaitingPayment:true};
          saveCompanies([...companies,newCo]);
          companyId = newCo.id;
        }
        // Mirror to Supabase so the stripe-webhook can match by id. ignore-duplicates
        // leaves any webhook-set plan fields untouched.
        try {
          await fetch(`${SUPABASE_URL}/rest/v1/companies`, {
            method: "POST",
            headers: {
              "apikey": SUPABASE_ANON_KEY,
              "Authorization": "Bearer "+sb._token(),
              "Content-Type": "application/json",
              "Prefer": "resolution=ignore-duplicates,return=minimal",
            },
            body: JSON.stringify({
              id: companyId,
              email: email.toLowerCase().trim(),
              company_name: companyName || "",
              owner_name: (ownerName || email.split("@")[0]) || "",
              plan: planName,
              plan_status: "pending_payment",
            }),
          });
        } catch(e) { /* webhook will fall back to email match if this fails */ }
      }
      window.open(buildStripeUrl(link, {companyId, email}), "_blank");
      setError("✅ Stripe checkout opened in a new tab. Complete payment then come back and sign in with your email and password.");
      setLoading(false);
    } else {
      // Stripe not yet configured — register directly with trial
      setTimeout(()=>{
        const companies = getCompanies();
        const existingAcct = companies.find(co=>(co.email.toLowerCase()===email.toLowerCase())||(co.loginEmail&&co.loginEmail.toLowerCase()===email.toLowerCase()));
        if (existingAcct) {
          if ((existingAcct.cancellations||0) >= SUBSCRIPTION_POLICY.maxCancellations) {
            setError(SUBSCRIPTION_POLICY.bannedMessage); setLoading(false); return;
          }
          setError("An account with this email already exists.");
          setLoading(false);
          return;
        }
        const planName = plan.id.startsWith("pro") ? "pro" : "starter";
        const newCo = {id:"co_"+Date.now(),name:companyName,cslb:cslb||"",location:location||"",owner:ownerName||email.split("@")[0],email,password,plan:planName,createdAt:new Date().toISOString().slice(0,10),logo:null,primaryColor:"#D4A843",trialEnds:new Date(Date.now()).toISOString().slice(0,10),moneyBackDeadline:new Date(Date.now()+15*864e5).toISOString().slice(0,10),cancellations:0,isAdmin:false};
        saveCompanies([...companies,newCo]);
        const sess = {companyId:newCo.id,companyName:newCo.name,ownerName:newCo.owner,plan:newCo.plan,email:newCo.email,cslb:newCo.cslb,loginTime:Date.now(),trialEnds:newCo.trialEnds,isAdmin:false};
        saveSession(sess); setSession(sess);

        // ── Create sample onboarding project ──────────────────────────────────
        try {
          const sampleProjectId = "proj_sample_"+newCo.id;
          const sampleProject = {
            id: sampleProjectId,
            name: "🏗 Sample Project — Explore Grok Field Ops",
            address: "123 Main St, San Francisco CA 94103",
            addr: "123 Main St, San Francisco CA 94103",
            type: "Full Interior Renovation",
            status: "active",
            budget: 285000,
            value: 285000,
            spent: 142000,
            pct: 45,
            start: new Date().toISOString().slice(0,10),
            end: new Date(Date.now()+90*864e5).toISOString().slice(0,10),
            client: "Sample Owner",
            units: "1,800 SF · 3 Bed / 2 Bath",
            companyId: newCo.id,
            isSample: true,
            description: "This is a sample project to help you explore all features. Feel free to click around, add notes, try the AI tools, and see how everything works. Delete this project when you're ready to add your real jobs.",
            phases: [
              {name:"Demo & Site Prep", pct:100, budget:18000},
              {name:"Rough Framing", pct:100, budget:32000},
              {name:"MEP Rough-In", pct:80, budget:54000},
              {name:"Drywall & Tape", pct:40, budget:28000},
              {name:"Tile & Finishes", pct:0, budget:42000},
              {name:"Paint", pct:0, budget:18000},
              {name:"Closeout", pct:0, budget:12000},
            ],
            notes: "Sample project — explore all the features here!",
            createdAt: new Date().toISOString().slice(0,10),
          };

          // Save sample project
          const projKey = "grok_projects_"+newCo.id;
          localStorage.setItem(projKey, JSON.stringify([sampleProject]));

          // Sample RFIs
          const sampleRFIs = [
            {id:"rfi_s1", num:"RFI-001", project:sampleProject.name, subject:"Drain location — plan vs field conflict", priority:"urgent", status:"open", submitted:new Date().toLocaleDateString(), desc:"The drain shown on plan sheet P-3 conflicts with the existing slab penetration. Clarify before pouring.", comments:[], attachments:[]},
            {id:"rfi_s2", num:"RFI-002", project:sampleProject.name, subject:"Paint color — hallway not specified", priority:"routine", status:"pending", submitted:new Date().toLocaleDateString(), desc:"Hallway paint not in finish schedule. Owner to select.", comments:[{id:1,author:"Field Supervisor",text:"Owner notified. Awaiting selection.",date:new Date().toLocaleDateString()}], attachments:[]},
          ];
          localStorage.setItem("grok_rfis_global_"+newCo.id, JSON.stringify(sampleRFIs));

          // Sample COs
          const sampleCOs = [
            {id:"co_s1", num:"CO-001", project:sampleProject.name, desc:"Water-damaged subfloor — unforeseen condition found during demo", amount:4200, status:"approved", date:new Date().toLocaleDateString(), days:0, comments:[{id:1, author:"Owner", text:"Approved. Please proceed.", date:new Date().toLocaleDateString()}], attachments:[]},
            {id:"co_s2", num:"CO-002", project:sampleProject.name, desc:"Owner requested tile upgrade — master bath floor", amount:2800, status:"pending", date:new Date().toLocaleDateString(), days:3, comments:[], attachments:[]},
          ];
          localStorage.setItem("grok_cos_global_"+newCo.id, JSON.stringify(sampleCOs));

          // Sample documents
          const sampleDocs = [
            {id:"doc_s1", name:"Owner Contract — Sample", cat:"contracts", notes:"Sample fixed-price owner contract. Replace with your real contract.", uploadedAt:new Date().toLocaleDateString(), uploadedBy:newCo.owner, isSample:true},
            {id:"doc_s2", name:"Scope of Work — Sample", cat:"contracts", notes:"Sample scope of work document.", uploadedAt:new Date().toLocaleDateString(), uploadedBy:newCo.owner, isSample:true},
            {id:"doc_s3", name:"Conditional Lien Waiver — Sample", cat:"lien", notes:"Sample lien waiver for progress payments.", uploadedAt:new Date().toLocaleDateString(), uploadedBy:newCo.owner, isSample:true},
          ];
          localStorage.setItem("grok_templates_"+newCo.id, JSON.stringify(sampleDocs));

        } catch(e) { console.warn("Sample project setup failed:", e); }
        // ── End sample project ─────────────────────────────────────────────────
        setLoading(false);
      },500);
    }
  };

  const applyPromo = () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) { setPromoError("Enter a promo code"); return; }
    const promo = PROMO_CODES[code];
    if (!promo) { setPromoError("Invalid promo code"); setPromoApplied(null); return; }
    setPromoApplied({...promo, code});
    setPromoError("");
  };

  const sendVerificationEmail = async (emailAddr) => {
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    const expiry = Date.now() + 10 * 60 * 1000; // 10 minutes
    localStorage.setItem("grok_verify_"+emailAddr, JSON.stringify({code, expiry}));
    setPendingEmail(emailAddr);
    setShowVerify(true);
    setEmailSent(true);

    // Send real email via Supabase Edge Function
    try {
      await fetch("https://qnjlirkgumhzzcavqsxt.supabase.co/functions/v1/send-email", {
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFuamxpcmtndW1oenpjYXZxc3h0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc3NTQxNjgsImV4cCI6MjA5MzMzMDE2OH0.fmwdMo6-ae5Jyx8b3n44FlOQUSSUX9drdt2UYJd7Mws"},
        body:JSON.stringify({
          to: emailAddr,
          subject: "Your Grok Field Ops verification code",
          html: `
            <div style="font-family:Arial,sans-serif;max-width:480px;margin:0 auto;padding:32px">
              <div style="font-size:24px;font-weight:800;color:#d4a017;margin-bottom:8px">GROK Field Ops</div>
              <h2 style="color:#1a1a1a;margin-bottom:16px">Your verification code</h2>
              <div style="background:#f8f6f0;border-radius:12px;padding:24px;text-align:center;margin:20px 0">
                <div style="font-size:42px;font-weight:800;letter-spacing:8px;color:#d4a017">${code}</div>
                <div style="font-size:12px;color:#888;margin-top:8px">Expires in 10 minutes</div>
              </div>
              <p style="color:#666;font-size:13px">Enter this code to complete your Grok Field Ops registration. If you didn't request this, ignore this email.</p>
              <hr style="border:none;border-top:1px solid #e5e0d5;margin:24px 0"/>
              <div style="font-size:11px;color:#aaa">Grok Construction Group Inc · San Mateo, CA · grokfieldops.com</div>
            </div>
          `
        })
      });
      console.log("Verification email sent");
    } catch(e) {
      console.error("Email send failed:", e);
      // Fallback — show code in alert for testing
      alert("Email send failed — your verification code is: "+code+"\n\n(Development mode)");
    }
    return code;
  };

  const verifyEmail = () => {
    const stored = JSON.parse(localStorage.getItem("grok_verify_"+pendingEmail)||"{}");
    if (!stored.code) { setVerifyError("Code expired. Please resend."); return; }
    if (Date.now() > stored.expiry) { setVerifyError("Code expired. Please resend."); return; }
    if (verifyCode.trim() !== stored.code) { setVerifyError("Incorrect code. Try again."); return; }
    // Verification passed — complete registration
    localStorage.removeItem("grok_verify_"+pendingEmail);
    completeRegistration();
  };

  const completeRegistration = () => {
    const companies = getCompanies();
    const plan = selectedPlan?.id?.startsWith("pro") ? "pro" :
                 selectedPlan?.id?.startsWith("gcpro") ? "gcpro" : "starter";
    const freeDays = promoApplied?.days || 0;
    const trialEnd = new Date(Date.now()+(freeDays||0)*864e5).toISOString().slice(0,10); // No free trial — pay first, 15-day money-back
    const newCo = {
      id:"co_"+Date.now(), name:companyName, cslb:cslb||"", location:location||"",
      owner:ownerName||email.split("@")[0], email, password, plan,
      createdAt:new Date().toISOString().slice(0,10), logo:null, primaryColor:"#D4A843",
      trialEnds:trialEnd, moneyBackDeadline:new Date(Date.now()+15*864e5).toISOString().slice(0,10),
      cancellations:0, isAdmin:false, promoCode:promoApplied?.code||"",
      emailVerified:true,
    };
    saveCompanies([...companies, newCo]);
    const sess = {companyId:newCo.id, companyName:newCo.name, ownerName:newCo.owner,
      plan:newCo.plan, email:newCo.email, cslb:newCo.cslb, loginTime:Date.now(),
      trialEnds:newCo.trialEnds, isAdmin:false};
    saveSession(sess); setSession(sess);

    // Send welcome email
    try {
      fetch("https://qnjlirkgumhzzcavqsxt.supabase.co/functions/v1/send-email", {
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFuamxpcmtndW1oenpjYXZxc3h0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc3NTQxNjgsImV4cCI6MjA5MzMzMDE2OH0.fmwdMo6-ae5Jyx8b3n44FlOQUSSUX9drdt2UYJd7Mws"},
        body:JSON.stringify({
          to: email,
          subject: "Welcome to Grok Field Ops — you're in!",
          html: `<div style="font-family:Arial,sans-serif;max-width:520px;margin:0 auto;padding:32px"><div style="font-size:24px;font-weight:800;color:#d4a017;margin-bottom:8px">GROK Field Ops</div><h2 style="color:#1a1a1a">Welcome, ${ownerName||email.split("@")[0]}!</h2><p style="color:#444;font-size:13px;line-height:1.8">Your <strong>${plan.toUpperCase()}</strong> account is active. You have <strong>${freeDays||7} days</strong> free to explore.</p><div style="background:#f8f6f0;border-radius:12px;padding:20px;margin:20px 0"><div style="font-size:13px;font-weight:700;color:#d4a017;margin-bottom:10px">🚀 Get started in 3 steps:</div><div style="font-size:12px;color:#444;line-height:2">1. Create your first project<br/>2. Upload your company templates in Document Kit<br/>3. Add your subs and invite your team</div></div><a href="https://grokfieldops.com" style="display:inline-block;background:#d4a017;color:white;padding:12px 24px;border-radius:8px;text-decoration:none;font-weight:700;font-size:13px">Open Grok Field Ops →</a><hr style="border:none;border-top:1px solid #e5e0d5;margin:24px 0"/><div style="font-size:11px;color:#aaa">Questions? Email jhcftgroup@gmail.com · Grok Field Ops · San Mateo, CA</div></div>`
        })
      }).catch(()=>{});
    } catch(e) {}

    // Create sample project for onboarding
    try {
      const sampleProject = {
        id:"proj_sample_"+newCo.id, name:"🏗 Sample Project — Explore Grok Field Ops",
        address:"123 Main St, San Francisco CA 94103", addr:"123 Main St, San Francisco CA 94103",
        type:"Full Interior Renovation", status:"active", budget:285000, value:285000,
        spent:142000, pct:45, client:"Sample Owner", companyId:newCo.id, isSample:true,
        description:"This is a sample project to help you explore all features. Delete when ready to add real jobs.",
        phases:[
          {name:"Demo & Site Prep",pct:100,budget:18000},
          {name:"Rough Framing",pct:100,budget:32000},
          {name:"MEP Rough-In",pct:80,budget:54000},
          {name:"Drywall & Tape",pct:40,budget:28000},
          {name:"Tile & Finishes",pct:0,budget:42000},
          {name:"Paint",pct:0,budget:18000},
          {name:"Closeout",pct:0,budget:12000},
        ],
        openCOs:2, openRFIs:2, notes:"Sample project — explore all the features!",
        createdAt:new Date().toISOString().slice(0,10),
      };
      localStorage.setItem("grok_projects_"+newCo.id, JSON.stringify([sampleProject]));
      // Sample RFIs
      localStorage.setItem("grok_rfis_global_"+newCo.id, JSON.stringify([
        {id:"rfi_s1",num:"RFI-001",project:sampleProject.name,subject:"Drain location — plan vs field conflict",priority:"urgent",status:"open",submitted:new Date().toLocaleDateString(),comments:[]},
        {id:"rfi_s2",num:"RFI-002",project:sampleProject.name,subject:"Paint color — hallway not specified",priority:"routine",status:"pending",submitted:new Date().toLocaleDateString(),comments:[]},
      ]));
      // Sample COs
      localStorage.setItem("grok_cos_global_"+newCo.id, JSON.stringify([
        {id:"co_s1",num:"CO-001",project:sampleProject.name,desc:"Water-damaged subfloor — unforeseen condition found during demo",amount:4200,status:"approved",date:new Date().toLocaleDateString(),days:0,comments:[]},
        {id:"co_s2",num:"CO-002",project:sampleProject.name,desc:"Owner requested tile upgrade — master bath floor",amount:2800,status:"pending",date:new Date().toLocaleDateString(),days:3,comments:[]},
      ]));
    } catch(e) { console.warn("Sample setup failed:", e); }

    setLoading(false);
  };

  const login = async () => {
    setError(""); setLoading(true);

    // Phase 3: try Supabase Auth first. authPassed means the user has migrated
    // (or signed up post-Phase-1) and entered the correct new password.
    // Legacy plaintext password remains the fallback for unmigrated users —
    // but if migrated_at IS set and Auth.signIn fails, we hard-reject.
    const authResult = await Auth.signIn(email, password);
    const authPassed = !!authResult.user;
    if (!authPassed) {
      try {
        const r = await fetch(
          `${SUPABASE_URL}/rest/v1/companies?email=eq.${encodeURIComponent(email.toLowerCase().trim())}&select=migrated_at,auth_user_id`,
          {headers:{"apikey":SUPABASE_ANON_KEY,"Authorization":"Bearer "+sb._token()}}
        );
        if (r.ok) {
          const data = await r.json();
          if (data && data[0] && (data[0].migrated_at || data[0].auth_user_id)) {
            setError("Your account password has been updated. Sign in with your new password, or use 'Send migration link' to set one.");
            setLoading(false); return;
          }
        }
      } catch (e) { /* fall through to legacy password check */ }
    }

    setTimeout(async () => {
      const companies = getCompanies();
      // Check sub accounts first
      const subAccts = getSubAccounts();
      const subAcct = subAccts.find(a=>a.email.toLowerCase()===email.toLowerCase().trim() && authPassed);
      if (subAcct) {
        if (subAcct.status==="pending") {
          setError("Your account is pending GC approval. You'll be notified by email once approved.");
          setLoading(false); return;
        }
        if (subAcct.status==="rejected") {
          setError("Your join request was declined. Contact your GC directly.");
          setLoading(false); return;
        }
        const sess = {companyId:"sub_"+subAcct.id,subId:subAcct.id,subName:subAcct.name,
          email:subAcct.email,trade:subAcct.trade,company:subAcct.company,
          license:subAcct.license,plan:"sub",isSub:true,role:subAcct.role||"Subcontractor",
          projects:subAcct.projects||[],loginTime:Date.now()};
        saveSession(sess); setSession(sess); setLoading(false); return;
      }
      let co = companies.find(c => (c.email.toLowerCase()===email.toLowerCase() || (c.loginEmail&&c.loginEmail.toLowerCase()===email.toLowerCase()) || (c.loginEmail2&&c.loginEmail2.toLowerCase()===email.toLowerCase())) && authPassed);
      if (isBanned(email)) { setError("This account has been deactivated. Please contact support."); setLoading(false); return; }

      // Phase 4 cross-device rehydrate: if Supabase Auth succeeded but no local
      // grok_companies row exists on this device, fetch the user's row from
      // Supabase (RLS allows it now that they're authenticated) and seed a
      // minimal localStorage entry from it. Lets migrated users log in on a
      // fresh browser/device. password is omitted so the legacy fallback can
      // never match this row — Supabase Auth is the only login path for them.
      if (!co && authPassed) {
        try {
          const r = await fetch(
            `${SUPABASE_URL}/rest/v1/companies?email=eq.${encodeURIComponent(email.toLowerCase().trim())}&select=*`,
            { headers: { "apikey": SUPABASE_ANON_KEY, "Authorization": "Bearer " + sb._token() } }
          );
          if (r.ok) {
            const data = await r.json();
            if (data && data[0]) {
              const sbRow = data[0];
              co = {
                id: sbRow.id,
                email: (sbRow.email || email).toLowerCase().trim(),
                name: sbRow.company_name || "",
                owner: sbRow.owner_name || email.split("@")[0],
                plan: sbRow.plan || "starter",
                cslb: "",
                location: "",
                createdAt: new Date().toISOString().slice(0,10),
                logo: null,
                primaryColor: "#D4A843",
                trialEnds: sbRow.trial_ends || "",
                moneyBackDeadline: new Date(Date.now()+15*864e5).toISOString().slice(0,10),
                cancellations: 0,
                isAdmin: false,
                emailVerified: true,
                authUserId: sbRow.auth_user_id,
              };
              saveCompanies([...getCompanies(), co]);
              console.log("Rehydrated local company row from Supabase");
            }
          }
        } catch (e) {
          console.warn("Cross-device rehydrate failed:", e);
        }
      }

      if (co) {
        const isPlatformAdminEmail = email.toLowerCase().trim()==="jhcftgroup@gmail.com" || email.toLowerCase().trim()==="jehoon@grokconstructiongroupinc.com" || email.toLowerCase().trim()==="jehoon@gcginc.com";
        // ── CHECK EMAIL VERIFIED FIRST ──────────────────────
        if (!co.emailVerified && !isPlatformAdminEmail) {
          setError("Please verify your email before logging in. Check your inbox for the verification code.");
          // Resend verification
          try {
            const code = Math.floor(100000+Math.random()*900000).toString();
            localStorage.setItem("grok_verify_"+co.email, JSON.stringify({code, expiry:Date.now()+10*60*1000}));
            await fetch("https://qnjlirkgumhzzcavqsxt.supabase.co/functions/v1/send-email",{
              method:"POST",
              headers:{"Content-Type":"application/json","apikey":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFuamxpcmtndW1oenpjYXZxc3h0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc3NTQxNjgsImV4cCI6MjA5MzMzMDE2OH0.fmwdMo6-ae5Jyx8b3n44FlOQUSSUX9drdt2UYJd7Mws"},
              body:JSON.stringify({to:co.email,subject:"Verify your Grok Field Ops email",
                html:`<div style="font-family:Arial,sans-serif;max-width:480px;margin:0 auto;padding:32px"><div style="font-size:24px;font-weight:800;color:#d4a017">GROK Field Ops</div><h2>Verify Your Email</h2><div style="background:#f8f6f0;border-radius:12px;padding:24px;text-align:center;margin:20px 0"><div style="font-size:42px;font-weight:800;letter-spacing:8px;color:#d4a017">${code}</div><div style="font-size:12px;color:#888;margin-top:8px">Expires in 10 minutes</div></div></div>`
              })
            });
          } catch(e) {}
          setLoading(false); return;
        }
        // Check if this email is a team member of any company
        const allInvites = JSON.parse(localStorage.getItem("grok_team_invites")||"[]");
        const teamInvite = allInvites.find(i=>i.email===email.toLowerCase().trim()&&i.used);
        const teamRole = co.isAdmin ? "owner" : (teamInvite?.role || "owner");
        const companyId = teamInvite ? teamInvite.companyId : co.id;
        let sess = {companyId,companyName:co.name,ownerName:co.owner,plan:isPlatformAdminEmail?"owner":co.plan,email:co.email,cslb:co.cslb,isAdmin:isPlatformAdminEmail,teamRole,loginTime:Date.now(),trialEnds:co.trialEnds};

        // ── Pull live plan status from Supabase on every login
        try {
          const sbRes = await fetch(
            `${SUPABASE_URL}/rest/v1/companies?email=eq.${encodeURIComponent(email.toLowerCase().trim())}&select=plan,plan_status,paid_through,grace_until,trial_ends`,
            { headers: { "apikey": SUPABASE_ANON_KEY, "Authorization": "Bearer "+sb._token() } }
          );
          if (sbRes.ok) {
            const sbData = await sbRes.json();
            if (sbData && sbData[0]) {
              const sb = sbData[0];
              // Merge Supabase billing data into session
              if (sb.plan)         sess.plan        = sb.plan;
              if (sb.plan_status)  sess.planStatus  = sb.plan_status;
              if (sb.paid_through) sess.paidThrough  = sb.paid_through;
              if (sb.grace_until)  sess.graceUntil   = sb.grace_until;
              // Always override trialEnds from Supabase — even if updated to past date
              sess.trialEnds = sb.trial_ends || sess.trialEnds;
              console.log("Plan status synced from Supabase");
            }
          }
        } catch(e) {
          console.log("Supabase plan check failed (using local):", e.message);
        }

        // ── Also upsert this company into Supabase if not already there.
        //
        // WARNING: this body intentionally sends ONLY identifying fields
        // (id, email, company_name, owner_name). Do not add plan, plan_status,
        // paid_through, grace_until, or trial_ends here — those are OWNED by the
        // stripe-webhook (supabase/functions/stripe-webhook/index.ts). If they are
        // sent from this login-time upsert and someone ever flips the Prefer header
        // from `ignore-duplicates` to `merge-duplicates`, paid users will be silently
        // downgraded back to whatever local `sess.plan` is — a data corruption bug
        // that would be near-impossible to trace from logs.
        try {
          await fetch(`${SUPABASE_URL}/rest/v1/companies`, {
            method: "POST",
            headers: {
              "apikey": SUPABASE_ANON_KEY,
              "Authorization": "Bearer "+sb._token(),
              "Content-Type": "application/json",
              "Prefer": "resolution=ignore-duplicates,return=minimal"
            },
            body: JSON.stringify({
              id: companyId,
              email: email.toLowerCase().trim(),
              company_name: co.name || "",
              owner_name: co.owner || "",
            })
          });
        } catch(e) { /* silent — not critical */ }

        // Auto-seed company settings ONLY for gcg_001
        if (co.isAdmin && companyId === GCG_COMPANY_ID) {
          const settKey = "grok_company_settings_"+companyId;
          if (!localStorage.getItem(settKey)) {
            localStorage.setItem(settKey, JSON.stringify({
              company_name:"Grok Construction Group Inc",
              owner_name:"Jehoon Chun",
              cslb:"CSLB #1152750",
              location:"2201 Bridgepointe Pkwy F125, San Mateo CA 94404",
              phone:"(650) 773-5317",
              email:"jehoon@grokconstructiongroupinc.com",
              website:"www.grokconstructiongroup.com",
              dir:"1000098765",
              license_type:"General Contractor (B)",
              payment_terms:"10% mobilization, milestone billings, 10% retention",
              default_markup:25, default_contingency:12, default_gcfee:10,
            }));
          }
        }
        // Seed blank settings for new subscribers so Account Settings doesn't crash
        if (co.isAdmin && companyId !== GCG_COMPANY_ID) {
          const settKey = "grok_company_settings_"+companyId;
          if (!localStorage.getItem(settKey)) {
            localStorage.setItem(settKey, JSON.stringify({
              company_name: co.name || "",
              owner_name: co.owner || "",
              cslb: co.cslb || "",
              location: co.location || "",
              phone: "", email: co.email || "", website: "",
              default_markup:20, default_contingency:10, default_gcfee:10,
            }));
          }
        }
        saveSession(sess);
        setSession(sess);
      } else {
        setError("Email or password incorrect.");
      }
      setLoading(false);
    }, 400);
  };

  const register = () => {
    setError(""); setLoading(true);
    setTimeout(() => {
      if (!email || !password || !companyName) { setError("Please fill in all required fields."); setLoading(false); return; }
      const companies = getCompanies();
      if (companies.find(c=>c.email.toLowerCase()===email.toLowerCase())) { setError("An account with this email already exists."); setLoading(false); return; }
      const newCo = {
        id: "co_"+Date.now(),
        name: companyName,
        cslb: cslb || "",
        location: location || "",
        owner: ownerName || email.split("@")[0],
        email,
        password,
        plan: "starter",
        createdAt: new Date().toISOString().slice(0,10),
        logo: null,
        primaryColor: "#D4A843",
      };
      saveCompanies([...companies, newCo]);
      const sess = {companyId:newCo.id,companyName:newCo.name,ownerName:newCo.owner,plan:newCo.plan,email:newCo.email,cslb:newCo.cslb,loginTime:Date.now()};
      saveSession(sess);
      setSession(sess);
      setLoading(false);
    }, 500);
  };

  const subLogin = () => {
    setError("");
    const allSubs = SUB_PORTAL_DATA.subs;
    const sub = allSubs.find(s=>s.pin===subPin.trim());
    if (sub) {
      const sess = {companyId:"sub",subId:sub.id,subName:sub.name,trade:sub.trade,company:sub.company,plan:"sub",loginTime:Date.now()};
      saveSession(sess);
      setSession(sess);
    } else { setError("Invalid PIN — check with your project manager."); }
  };

  const logout = () => { Auth.signOut().catch(()=>{}); setSession(null); setEmail(""); setPassword(""); };

  // Inject logout + user info into children via context
  if (session) {
    return (
      <div>
        {/* Session banner for subs — different look */}
        {session.plan==="sub" && (
          <div style={{background:`linear-gradient(to right,${T.teal}18,${T.bg1})`,borderBottom:`1px solid ${T.teal}30`,padding:"8px 20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              <div style={{width:28,height:28,borderRadius:7,background:T.teal+"18",border:`1px solid ${T.teal}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:800,color:T.teal}}>{session.trade?.slice(0,3).toUpperCase()}</div>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:T.text}}>{session.subName} — {session.trade}</div>
                <div style={{fontSize:10,color:T.muted}}>{session.company}</div>
              </div>
            </div>
            <button onClick={logout} style={{fontSize:11,color:T.muted,background:"transparent",border:`1px solid ${T.subtle}`,borderRadius:6,padding:"4px 10px",cursor:"pointer"}}>Sign Out</button>
          </div>
        )}
        {(()=>{
          // ── PAYWALL GATE ─────────────────────────────────────
          // Skip gate for: admin, GCG owner, sub portal users
          // Only platform admins and sub users bypass paywall
          const isPlatformAdmin = session.email==="jhcftgroup@gmail.com" || session.email==="jehoon@grokconstructiongroupinc.com" || session.isAdmin || session.plan==="owner";
          if(isPlatformAdmin || session.plan==="sub" || session.plan==="owner") {
            return React.cloneElement(children, {session, onLogout:logout});
          }
          // Check trial / payment status
          const now = new Date();
          const trialEnd = session.trialEnds ? new Date(session.trialEnds) : null;
          const isTrialActive = trialEnd && trialEnd > now;
          // isPaid requires a non-null paidThrough date from the stripe-webhook — a session
          // with a paid-tier plan but no paidThrough must NOT grant access (it means the
          // webhook hasn't confirmed payment yet). Trial users go through isTrialActive instead.
          const isPaid = session.plan && ["starter","pro","gcpro","gcpro","owner","starter_annual","pro_annual","gcpro_annual","trade_starter","trade_pro","trade_starter_annual","trade_pro_annual"].includes(session.plan) && session.paidThrough && new Date(session.paidThrough) > now;
          const isGraceActive = session.graceUntil && new Date(session.graceUntil) > now;

          if(isTrialActive || isPaid || isGraceActive) {
            return React.cloneElement(children, {session, onLogout:logout});
          }

          // ── HARD BLOCK ──────────────────────────────────────
          const daysExpired = trialEnd ? Math.floor((now - trialEnd)/864e5) : 0;
          const reason = !trialEnd ? "no_trial"
            : !isPaid && daysExpired === 0 ? "trial_just_ended"
            : daysExpired > 0 ? "trial_expired"
            : "payment_failed";

          const isTradePlan = session?.accountType==="trade";
          const STRIPE = {
            starter_monthly:        "https://buy.stripe.com/5kQeVeddjdGe2UDesh4Rq00",
            starter_annual:         "https://buy.stripe.com/9B6bJ2gpv6dMeDl1Fv4Rq01",
            pro_monthly:            "https://buy.stripe.com/fZu8wQ2yF0Ts1Qzesh4Rq02",
            pro_annual:             "https://buy.stripe.com/9B67sMflr6dM3YH1Fv4Rq03",
            gcpro_monthly:          "https://buy.stripe.com/5kQ6oI5KRgSqcvdck94Rq07",
            gcpro_annual:           "https://buy.stripe.com/7sY3cw4GNeKi8eXesh4Rq05",
            trade_starter_monthly:  "https://buy.stripe.com/00wdRaehn31A7aTfwl4Rq06",
            trade_starter_annual:   "https://buy.stripe.com/3cI5kE4GNeKi0Mvbg54Rq0a",
            trade_pro_monthly:      "https://buy.stripe.com/fZu14o5KRdGe8eXfwl4Rq09",
            trade_pro_annual:       "https://buy.stripe.com/fZu9AU3CJdGe66PgAp4Rq08",
          };

          return (
            <div style={{minHeight:"100dvh",background:T.bg0,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
              <div style={{width:"100%",maxWidth:580}}>

                {/* Header */}
                <div style={{textAlign:"center",marginBottom:24}}>
                  <div style={{fontSize:28,fontWeight:800,color:T.gold,marginBottom:4}}>🏗 Grok Field Ops</div>
                  <div style={{fontSize:22,fontWeight:800,color:T.text,marginBottom:8}}>
                    {reason==="payment_failed" ? "⚠️ Payment Failed" : "Subscribe to Get Started"}
                  </div>
                  <div style={{fontSize:13,color:T.muted,lineHeight:1.7,maxWidth:420,margin:"0 auto"}}>
                    {reason==="payment_failed"
                      ? "Your most recent payment did not go through. Update your payment method to restore access — your data is safe."
                      : "Choose a plan below to unlock your account. Cancel anytime."
                    }
                  </div>
                  <div style={{display:"inline-flex",alignItems:"center",gap:6,background:T.greenBg,border:"1px solid "+T.green+"40",borderRadius:20,padding:"6px 16px",marginTop:12,fontSize:11,color:T.green,fontWeight:700}}>
                    ✓ 15-Day Money-Back Guarantee — No Questions Asked
                  </div>
                </div>

                {/* Plan cards */}
                <div style={{display:"grid",gridTemplateColumns:window.innerWidth<600?"1fr":"1fr 1fr 1fr",gap:12,marginBottom:20}}>
                  {[
                    {name:"Starter",price:"$149",period:"/mo",annualPrice:"$1,490/yr",desc:"3 projects · 25 AI estimates · 10 subs",links:{m:STRIPE.starter_monthly,a:STRIPE.starter_annual},color:T.blue},
                    {name:"Pro",price:"$249",period:"/mo",annualPrice:"$2,490/yr",desc:"10 projects · 50 AI estimates · 20 subs",links:{m:STRIPE.pro_monthly,a:STRIPE.pro_annual},color:T.gold,popular:true},
                    {name:"GC Pro",price:"$399",period:"/mo",annualPrice:"$3,990/yr",desc:"Unlimited everything · Priority support",links:{m:STRIPE.gcpro_monthly,a:STRIPE.gcpro_annual},color:T.purple},
                  ].map(plan=>(
                    <div key={plan.name} style={{background:T.bg1,border:`2px solid ${plan.popular?plan.color:T.subtle}`,borderRadius:12,padding:16,position:"relative",overflow:"hidden"}}>
                      {plan.popular&&<div style={{position:"absolute",top:0,left:0,right:0,height:3,background:plan.color}}/>}
                      {plan.popular&&<div style={{fontSize:9,fontWeight:800,color:plan.color,letterSpacing:"0.1em",marginBottom:6}}>MOST POPULAR</div>}
                      <div style={{fontSize:15,fontWeight:800,color:T.text,marginBottom:2}}>{plan.name}</div>
                      <div style={{fontSize:24,fontWeight:800,color:plan.color,lineHeight:1}}>{plan.price}<span style={{fontSize:11,color:T.muted,fontWeight:400}}>{plan.period}</span></div>
                      <div style={{fontSize:10,color:T.muted,marginTop:2,marginBottom:10}}>{plan.annualPrice} billed annually</div>
                      <div style={{fontSize:10,color:T.muted,marginBottom:14,lineHeight:1.6}}>{plan.desc}</div>
                      <a href={buildStripeUrl(plan.links.m, {companyId: session?.companyId, email: session?.email})} target="_blank" rel="noopener noreferrer"
                        style={{display:"block",width:"100%",padding:"9px 0",borderRadius:8,background:plan.color,color:"white",fontSize:12,fontWeight:700,textAlign:"center",textDecoration:"none",marginBottom:6}}>
                        Subscribe Monthly
                      </a>
                      <a href={buildStripeUrl(plan.links.a, {companyId: session?.companyId, email: session?.email})} target="_blank" rel="noopener noreferrer"
                        style={{display:"block",width:"100%",padding:"7px 0",borderRadius:8,border:`1px solid ${plan.color}`,color:plan.color,fontSize:11,fontWeight:600,textAlign:"center",textDecoration:"none"}}>
                        Annual — Save ~17%
                      </a>
                    </div>
                  ))}
                </div>

                {/* Restore access button */}
                <div style={{textAlign:"center",marginBottom:16}}>
                  <button onClick={async()=>{
                    setCheckingPayment(true);
                    try {
                      const res = await fetch(
                        SUPABASE_URL+"/rest/v1/companies?email=eq."+encodeURIComponent(session.email)+"&select=plan,plan_status,paid_through,grace_until,trial_ends",
                        {headers:{"apikey":SUPABASE_ANON_KEY,"Authorization":"Bearer "+sb._token()}}
                      );
                      if(res.ok){
                        const data = await res.json();
                        if(data && data[0]){
                          const sb = data[0];
                          const now = new Date();
                          const isPaid = sb.paid_through && new Date(sb.paid_through) > now;
                          const isGrace = sb.grace_until && new Date(sb.grace_until) > now;
                          if(isPaid || isGrace || sb.plan_status==='active'){
                            // Payment confirmed — update session and reload
                            const updated = {...session,
                              plan: sb.plan || session.plan,
                              planStatus: sb.plan_status,
                              paidThrough: sb.paid_through,
                              graceUntil: sb.grace_until,
                              trialEnds: sb.trial_ends,
                            };
                            saveSession(updated);
                            window.location.reload();
                          } else {
                            setPaymentCheckMsg("No active subscription found yet. Please complete payment on Stripe and try again.");
                          }
                        }
                      }
                    } catch(e) {
                      setPaymentCheckMsg("Could not reach server. Check your connection and try again.");
                    }
                    setCheckingPayment(false);
                  }}
                  style={{padding:"12px 32px",borderRadius:10,background:T.gold,color:T.bg0,fontSize:14,fontWeight:800,border:"none",cursor:"pointer",marginBottom:10}}>
                    {checkingPayment ? "Checking..." : "✓ I Just Paid — Restore My Access"}
                  </button>
                  {paymentCheckMsg && (
                    <div style={{fontSize:12,color:T.muted,marginTop:8,maxWidth:360,margin:"8px auto 0"}}>{paymentCheckMsg}</div>
                  )}
                </div>

                {/* After payment note */}
                <div style={{textAlign:"center",marginBottom:20}}>
                  <div style={{fontSize:12,color:T.muted,background:T.bg2,borderRadius:8,padding:"10px 16px",display:"inline-block"}}>
                    After subscribing, click the button above to instantly restore access.
                  </div>
                </div>

                {/* Sign out */}
                <div style={{textAlign:"center"}}>
                  <button onClick={logout}
                    style={{background:"transparent",border:"none",color:T.muted,fontSize:12,cursor:"pointer",textDecoration:"underline"}}>
                    Sign out and use a different account
                  </button>
                </div>

              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  // ── LOGIN SCREEN ─────────────────────────────────────────────────────────
  if (legalView === "terms") return <TermsOfServiceView onBack={()=>setLegalView(null)}/>;
  if (legalView === "privacy") return <PrivacyPolicyView onBack={()=>setLegalView(null)}/>;
  return (
    <div style={{minHeight:"100dvh",background:T.bg0,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{width:"100%",maxWidth:440}}>

        {/* Logo */}
        <div style={{textAlign:"center",marginBottom:40}}>
          <div style={{width:52,height:52,borderRadius:13,background:`linear-gradient(135deg,${T.gold},${T.teal})`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px",fontSize:22,fontWeight:800,color:T.bg0}}>G</div>
          <div style={{fontSize:26,fontWeight:800,color:T.text,letterSpacing:"0.05em"}}>GROK FIELD OPS</div>
          <div style={{fontSize:13,color:T.muted,marginTop:4}}>Construction Management Platform</div>
        </div>

        {/* Mode tabs */}
        <div style={{display:"flex",gap:3,background:T.bg2,borderRadius:10,padding:3,marginBottom:24}}>
          {[["login","Log In"],["register","Register"]].map(([m,l])=>(
            <button key={m} onClick={()=>{setAuthMode(m);setError("");}}
              style={{flex:1,padding:"8px 4px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",background:authMode===m?T.gold:T.bg2,color:authMode===m?T.bg0:T.muted,transition:"all 0.15s"}}>
              {l}
            </button>
          ))}
        </div>

        <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:12,padding:22,position:"relative",overflow:"hidden"}}>
          <div style={{position:"absolute",top:0,left:0,right:0,height:2,background:`linear-gradient(to right,${T.gold},${T.teal})`}}/>

          {error && (
            <div style={{background:T.redBg,border:`1px solid ${T.red}30`,borderRadius:8,padding:"10px 14px",fontSize:12,color:T.red,marginBottom:16,display:"flex",gap:8,alignItems:"center"}}>
              <span>⚠</span>{error}
            </div>
          )}

                    {/* Tab switcher */}
          {(authMode==="login"||authMode==="register"||authMode==="sub"||authMode==="sub-register") && (
            <div style={{display:"flex",gap:4,marginBottom:20,background:T.bg2,borderRadius:10,padding:4}}>
              <button onClick={()=>setAuthMode("login")}
                style={{flex:1,padding:"8px",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",border:"none",
                  background:authMode==="login"||authMode==="register"?"linear-gradient(135deg,"+T.gold+","+T.gold2+")":T.bg2,
                  color:authMode==="login"||authMode==="register"?"#02040A":T.muted}}>
                🏗 General Contractor
              </button>
              <button onClick={()=>setAuthMode("sub")}
                style={{flex:1,padding:"8px",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",border:"none",
                  background:authMode==="sub"||authMode==="sub-register"?"linear-gradient(135deg,"+T.blue+","+T.purple+")":T.bg2,
                  color:authMode==="sub"||authMode==="sub-register"?"white":T.muted}}>
                ⚡ Trade / Sub
              </button>
            </div>
          )}

          {(authMode==="sub"||authMode==="sub-register") && (
            <SubLoginPanel onLogin={(sess)=>{saveSession(sess);setSession(sess);}} initialMode={authMode==="sub-register"?"register":"login"}/>
          )}

          {authMode==="login" && (
            <div>
              <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:20}}>Sign in to your account</div>
              <AuthField label="Email Address" value={email} onChange={setEmail} placeholder="you@yourcompany.com" type="email"/>
              <AuthField label="Password" value={password} onChange={setPassword} placeholder="Your password" type={showPass?"text":"password"}
                suffix={<button onClick={()=>setShowPass(p=>!p)} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:11}}>{showPass?"Hide":"Show"}</button>}
                onEnter={login}/>
              {migrationMsg&&<div style={{background:T.greenBg||"rgba(34,197,94,0.08)",border:"1px solid rgba(34,197,94,0.2)",borderRadius:8,padding:"9px 12px",fontSize:12,color:T.green,marginBottom:12}}>{migrationMsg}</div>}
              <AuthBtn label={loading?"Signing in...":"Sign In"} onClick={login} disabled={loading||!email||!password}/>
              <div style={{textAlign:"center",marginTop:10}}>
                <button onClick={()=>setAuthMode("forgot")} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:11}}>Forgot password?</button>
              </div>
              <div style={{textAlign:"center",marginTop:6}}>
                <button onClick={handleRequestMigration} disabled={migrationLoading||!email} style={{background:"none",border:"none",color:T.gold,cursor:migrationLoading?"wait":"pointer",fontSize:11,opacity:(!email||migrationLoading)?0.5:1}}>
                  {migrationLoading?"Sending...":"Old account? Send migration link →"}
                </button>
              </div>
              <div style={{marginTop:10,display:"flex",justifyContent:"center",gap:12,fontSize:10}}>
                <button onClick={()=>setLegalView("terms")} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:10}}>Terms of Service</button>
                <span style={{color:T.subtle}}>·</span>
                <button onClick={()=>setLegalView("privacy")} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:10}}>Privacy Policy</button>
              </div>

            </div>
          )}

          {authMode==="forgot" && (
            <div>
              <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:6}}>Reset Password</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:16}}>Enter your email and we'll send a reset code.</div>
              {!gcResetSent ? (
                <>
                  <AuthField label="Email Address" value={gcResetEmail} onChange={setGcResetEmail} placeholder="you@yourcompany.com" type="email"/>
                  {gcResetError&&<div style={{background:T.redBg,border:"1px solid "+T.red+"30",borderRadius:8,padding:"9px 12px",fontSize:12,color:T.red,marginBottom:12}}>{gcResetError}</div>}
                  <AuthBtn label="Send Reset Code" onClick={sendGcResetCode} disabled={!gcResetEmail.trim()}/>
                </>
              ) : authMode==="forgot" && !gcResetVerified ? (
                <>
                  <div style={{background:T.greenBg,border:"1px solid "+T.green+"40",borderRadius:8,padding:"10px 12px",fontSize:12,color:T.green,marginBottom:14}}>
                    ✓ Code sent to {gcResetEmail}
                  </div>
                  <AuthField label="Code" value={gcResetCode} onChange={setGcResetCode} placeholder="the code from your email"/>
                  {gcResetError&&<div style={{background:T.redBg,border:"1px solid "+T.red+"30",borderRadius:8,padding:"9px 12px",fontSize:12,color:T.red,marginBottom:12}}>{gcResetError}</div>}
                  <AuthBtn label="Verify Code" onClick={verifyGcResetCode} disabled={!/^\d{6,10}$/.test(gcResetCode.trim())}/>
                  <button onClick={sendGcResetCode} style={{width:"100%",padding:8,background:"none",border:"none",color:T.muted,fontSize:11,cursor:"pointer"}}>Resend code</button>
                </>
              ) : (
                <>
                  <AuthField label="New Password" value={gcNewPassword} onChange={setGcNewPassword} placeholder="At least 6 characters" type="password"/>
                  {gcResetError&&<div style={{background:T.redBg,border:"1px solid "+T.red+"30",borderRadius:8,padding:"9px 12px",fontSize:12,color:T.red,marginBottom:12}}>{gcResetError}</div>}
                  {gcResetSuccess&&<div style={{background:T.greenBg,border:"1px solid "+T.green+"40",borderRadius:8,padding:"10px 12px",fontSize:12,color:T.green,marginBottom:12}}>{gcResetSuccess}</div>}
                  <AuthBtn label="Update Password" onClick={doGcPasswordReset} disabled={gcNewPassword.length<6}/>
                </>
              )}
              <button onClick={()=>{setAuthMode("login");setGcResetSent(false);setGcResetVerified(false);setGcResetError("");}} style={{width:"100%",marginTop:6,padding:8,background:"none",border:"none",color:T.muted,fontSize:11,cursor:"pointer"}}>← Back to login</button>
            </div>
          )}

          {authMode==="register" && (
            <div>
              {/* Hero headline */}
              <div style={{textAlign:"center",marginBottom:18,padding:"4px 0"}}>
                <div style={{fontSize:18,fontWeight:800,color:T.text,lineHeight:1.3,marginBottom:8}}>
                  Built for GCs who are done<br/>running jobs on gut feel.
                </div>
                <div style={{fontSize:13,color:T.gold,fontWeight:700,marginBottom:10}}>
                  One platform. Every job. Full accountability.
                </div>
                <div style={{background:T.bg2,borderRadius:10,padding:"14px",marginBottom:10,textAlign:"left"}}>
                  <div style={{fontSize:11,fontWeight:800,color:T.gold,marginBottom:10}}>What makes Grok different</div>
                  <div style={{display:"grid",gridTemplateColumns:window.innerWidth<768?"1fr":"1fr 1fr",gap:8}}>
                    {[
                      {icon:"🎤",title:"ARIA Voice Agent",color:T.blue,desc:"Say Hey ARIA and she runs your job. Morning briefings, CO approvals, trade scheduling, RFI drafts — all by voice from your Mac. Pro Max exclusive."},
                      {icon:"📐",title:"Blueprint Command Center",color:T.red,desc:"Upload blueprints, AI analyzes scope by trade, flags ADA/Title 24/OSHA issues, generates material takeoff and cost estimates. Create a project directly from the analysis."},
                      {icon:"⚡",title:"Full Job Automation",color:T.gold,desc:"Milestone tracking triggers trade emails, inspection scheduling, weather delay notices, and pay application generation automatically."},
                      {icon:"📋",title:"Complete Lien Protection",color:T.purple,desc:"Every CA lien document from §8204 preliminary notice through §8138 final unconditional waiver. Auto-generated at each billing stage with sub waiver tracking."},
                    ].map(f=>(
                      <div key={f.title} style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:10,padding:"12px 12px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:8}}>
                          <span style={{fontSize:16}}>{f.icon}</span>
                          <div style={{fontSize:11,fontWeight:800,color:f.color}}>{f.title}</div>
                        </div>
                        <div style={{fontSize:10,color:T.muted,lineHeight:1.6}}>{f.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 4 value buckets — each covers different ground from the features above */}
              {!selectedPlan&&(
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:18}}>
                  {[
                    {icon:"🏛",title:"Code & ADA Built In",color:T.blue,items:["Local building codes by city and project type","ADA compliance review on uploaded blueprints","CSLB contract compliance — CA BPC §7159 requirements","Submittal pins on blueprints with AI product search"]},
                    {icon:"🏗",title:"Run Every Job Clean",color:T.green,items:["Unit-by-unit progress tracking for multifamily","Draw schedule tied to inspections and milestones","Owner/investor report — one click, ready to send","Permit tracker with AHJ directory and inspection scheduling"]},
                    {icon:"📋",title:"Nothing Falls Through",color:T.amber,items:["Every RFI and CO tracked with full history","AI meeting recorder — flags scope changes and CO triggers","Generate Word docs from your own uploaded templates","Certified payroll, lien waivers, pay applications built in"]},
                    {icon:"🛡",title:"Subs and Team",color:T.purple,items:["Sub portal — daily logs, flag issues, view project scope","Trade-specific blueprint access — subs see only their drawings","Invite subs via code — they register free, you approve access","Sub bid comparison — AI compares bids and recommends award"]},
                  ].map(bucket=>(
                    <div key={bucket.title} style={{background:T.bg2,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 12px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:8}}>
                        <span style={{fontSize:16}}>{bucket.icon}</span>
                        <div style={{fontSize:11,fontWeight:800,color:bucket.color}}>{bucket.title}</div>
                      </div>
                      {bucket.items.map(item=>(
                        <div key={item} style={{fontSize:10,color:T.muted,display:"flex",gap:5,marginBottom:3,lineHeight:1.4}}>
                          <span style={{color:bucket.color,flexShrink:0,marginTop:1}}>→</span>{item}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* Plan selector — 3 tabs */}
              {!selectedPlan ? (
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10,textAlign:"center"}}>7-Day Money-Back Guarantee</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:4,marginBottom:14,background:T.bg3,borderRadius:9,padding:3}}>
                    {[["starter","Starter","$149"],["pro","Pro","$249"],["gcpro","GC Pro","$399"]].map(([id,label,price])=>(
                      <button key={id} onClick={()=>setPlanTab(id)}
                        style={{padding:"9px 6px",borderRadius:7,border:"none",cursor:"pointer",transition:"all 0.15s",background:planTab===id?T.bg1:"transparent",boxShadow:planTab===id?"0 1px 4px rgba(0,0,0,0.15)":"none"}}>
                        <div style={{fontSize:11,fontWeight:700,color:planTab===id?T.text:T.muted}}>{label}</div>
                        <div style={{fontSize:14,fontWeight:800,color:planTab===id?T.gold:T.muted}}>{price}<span style={{fontSize:9,fontWeight:400,color:T.muted}}>/mo</span></div>
                      </button>
                    ))}
                  </div>

                  {planTab==="starter"&&<div style={{background:T.blueBg,border:"1px solid "+T.blue+"30",borderRadius:10,padding:"14px 16px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                      <div>
                        <div style={{fontSize:14,fontWeight:800,color:T.text}}>Starter</div>
                        <div style={{fontSize:11,color:T.muted,marginTop:2}}>Solo GCs · 3 active projects · 25 AI estimates/mo</div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:22,fontWeight:800,color:T.blue}}>$149<span style={{fontSize:11,fontWeight:400,color:T.muted}}>/mo</span></div>
                        <div style={{fontSize:10,color:T.muted}}>or $1,490/yr · save $298</div>
                      </div>
                    </div>
                    <div style={{fontSize:11,color:T.muted,marginBottom:10,lineHeight:1.6,fontStyle:"italic",paddingLeft:4,borderLeft:"3px solid "+T.blue}}>
                      "Know your profit on every job. Estimate faster. Stop chasing subs for paperwork."
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:3,marginBottom:12}}>
                      {[["💰","Profit tracker per job"],["📷","25 AI estimates / month"],["💬","150 AI PM chats / month"],["📋","Scope checklist + budget"],["📍","ADA annotation on drawings"],["🏛","Code compliance tab"],["📝","RFI & CO detail panels"],["🎙","Meeting recorder (15/mo)"],["📐","Blueprint AI (10/mo)"],["🎤","ARIA Voice — Not included"],["↩","15-day money-back guarantee"]].map(([icon,f])=>(
                        <div key={f} style={{fontSize:10,color:T.muted,display:"flex",gap:5,padding:"2px 0",alignItems:"flex-start"}}>
                          <span style={{color:T.blue,flexShrink:0,fontSize:10}}>{icon}</span><span>{f}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                      <button onClick={()=>setSelectedPlan({id:"starter_monthly",name:"Starter Monthly",price:"$149",period:"/month",color:T.blue,stripe:"price_starter_monthly"})}
                        style={{padding:"10px",borderRadius:8,border:"2px solid "+T.blue+"50",background:T.bg1,cursor:"pointer",fontSize:11,fontWeight:700,color:T.blue,transition:"all 0.15s"}}
                        onMouseEnter={e=>e.currentTarget.style.borderColor=T.blue} onMouseLeave={e=>e.currentTarget.style.borderColor=T.blue+"50"}>Monthly · $149/mo</button>
                      <button onClick={()=>setSelectedPlan({id:"starter_annual",name:"Starter Annual",price:"$1,490",period:"/year",color:T.teal,stripe:"price_starter_annual"})}
                        style={{padding:"10px",borderRadius:8,border:"2px solid "+T.teal+"50",background:T.bg1,cursor:"pointer",fontSize:11,fontWeight:700,color:T.teal,position:"relative",transition:"all 0.15s"}}
                        onMouseEnter={e=>e.currentTarget.style.borderColor=T.teal} onMouseLeave={e=>e.currentTarget.style.borderColor=T.teal+"50"}>
                        <div style={{position:"absolute",top:-8,right:6,background:T.teal,color:"white",fontSize:8,fontWeight:700,padding:"1px 7px",borderRadius:10}}>SAVE $298</div>
                        Annual · $1,490/yr</button>
                    </div>
                  </div>}

                  {planTab==="pro"&&<div style={{background:T.purpleBg,border:"1px solid "+T.purple+"30",borderRadius:10,padding:"14px 16px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                      <div>
                        <div style={{fontSize:14,fontWeight:800,color:T.text}}>Pro</div>
                        <div style={{fontSize:11,color:T.muted,marginTop:2}}>Growing firms · Up to 10 active projects</div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:22,fontWeight:800,color:T.purple}}>$249<span style={{fontSize:11,fontWeight:400,color:T.muted}}>/mo</span></div>
                        <div style={{fontSize:10,color:T.muted}}>or $2,490/yr · save $498</div>
                      </div>
                    </div>
                    <div style={{fontSize:11,color:T.muted,marginBottom:10,lineHeight:1.6,fontStyle:"italic",paddingLeft:4,borderLeft:"3px solid "+T.purple}}>
                      "Track margin across 15 jobs. Know which projects are profitable before they end."
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:3,marginBottom:12}}>
                      {[["📊","Everything in Starter"],["🏗","10 active projects"],["☑","Punch list with photo proof"],["👤","Client portal"],["📊","Sub bid comparison"],["📋","50 AI estimates/mo"],["💬","350 AI PM chats/mo"],["📐","25 blueprint reviews/mo"],["⭐","Priority support"],["🎤","ARIA Voice — Basic (push-to-talk, morning briefing)"],["↩","15-day money-back guarantee"]].map(([icon,f])=>(
                        <div key={f} style={{fontSize:10,color:T.muted,display:"flex",gap:5,padding:"2px 0",alignItems:"flex-start"}}>
                          <span style={{color:T.purple,flexShrink:0,fontSize:10}}>{icon}</span><span>{f}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                      <button onClick={()=>setSelectedPlan({id:"pro_monthly",name:"Pro Monthly",price:"$249",period:"/month",color:T.purple,stripe:"price_pro_monthly"})}
                        style={{padding:"10px",borderRadius:8,border:"2px solid "+T.purple+"50",background:T.bg1,cursor:"pointer",fontSize:11,fontWeight:700,color:T.purple,transition:"all 0.15s"}}
                        onMouseEnter={e=>e.currentTarget.style.borderColor=T.purple} onMouseLeave={e=>e.currentTarget.style.borderColor=T.purple+"50"}>Monthly · $249/mo</button>
                      <button onClick={()=>setSelectedPlan({id:"pro_annual",name:"Pro Annual",price:"$2,490",period:"/year",color:T.purple,stripe:"price_pro_annual"})}
                        style={{padding:"10px",borderRadius:8,border:"2px solid "+T.purple+"50",background:T.bg1,cursor:"pointer",fontSize:11,fontWeight:700,color:T.purple,position:"relative",transition:"all 0.15s"}}
                        onMouseEnter={e=>e.currentTarget.style.borderColor=T.purple} onMouseLeave={e=>e.currentTarget.style.borderColor=T.purple+"50"}>
                        <div style={{position:"absolute",top:-8,right:6,background:T.purple,color:"white",fontSize:8,fontWeight:700,padding:"1px 7px",borderRadius:10}}>SAVE $498</div>
                        Annual · $2,490/yr</button>
                    </div>
                  </div>}

                  {planTab==="gcpro"&&<div style={{background:T.goldBg,border:"1px solid "+T.gold+"30",borderRadius:10,padding:"14px 16px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                      <div>
                        <div style={{fontSize:14,fontWeight:800,color:T.text}}>Team</div>
                        <div style={{fontSize:11,color:T.muted,marginTop:2}}>Full operation · Unlimited jobs</div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:22,fontWeight:800,color:T.gold}}>$399<span style={{fontSize:11,fontWeight:400,color:T.muted}}>/mo</span></div>
                        <div style={{fontSize:10,color:T.muted}}>or $3,990/yr · save $798</div>
                      </div>
                    </div>
                    <div style={{fontSize:11,color:T.muted,marginBottom:10,lineHeight:1.6,fontStyle:"italic",paddingLeft:4,borderLeft:"3px solid "+T.gold}}>
                      "Full ARIA Voice Agent + unlimited everything. Run your entire business by voice."
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:3,marginBottom:12}}>
                      {[["🏆","Everything in Pro"],["∞","Unlimited projects"],["∞","Unlimited AI estimates"],["👥","Team roles & permissions (coming soon)"],["📞","Phone + email support"],["🎯","Dedicated onboarding"],["🔧","Custom AI knowledge base"],["🎤","ARIA Voice — Full access ⭐"],["✓","Cancel anytime"]].map(([icon,f])=>(
                        <div key={f} style={{fontSize:10,color:T.muted,display:"flex",gap:5,padding:"2px 0",alignItems:"flex-start"}}>
                          <span style={{color:T.gold,flexShrink:0,fontSize:10}}>{icon}</span><span>{f}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                      <button onClick={()=>setSelectedPlan({id:"gcpro_monthly",name:"GC Pro Monthly",price:"$399",period:"/month",color:T.gold,stripe:"price_team_monthly"})}
                        style={{padding:"10px",borderRadius:8,border:"2px solid "+T.gold+"50",background:T.bg1,cursor:"pointer",fontSize:11,fontWeight:700,color:T.gold,transition:"all 0.15s"}}
                        onMouseEnter={e=>e.currentTarget.style.borderColor=T.gold} onMouseLeave={e=>e.currentTarget.style.borderColor=T.gold+"50"}>Monthly · $399/mo</button>
                      <button onClick={()=>setSelectedPlan({id:"gcpro_annual",name:"GC Pro Annual",price:"$3,990",period:"/year",color:T.gold,stripe:"price_team_annual"})}
                        style={{padding:"10px",borderRadius:8,border:"2px solid "+T.gold+"50",background:T.bg1,cursor:"pointer",fontSize:11,fontWeight:700,color:T.gold,position:"relative",transition:"all 0.15s"}}
                        onMouseEnter={e=>e.currentTarget.style.borderColor=T.gold} onMouseLeave={e=>e.currentTarget.style.borderColor=T.gold+"50"}>
                        <div style={{position:"absolute",top:-8,right:6,background:T.gold,color:T.bg0,fontSize:8,fontWeight:700,padding:"1px 7px",borderRadius:10}}>SAVE $798</div>
                        Annual · $3,990/yr</button>
                    </div>
                  </div>}

                  <div style={{fontSize:10,color:T.muted,textAlign:"center",marginTop:14,lineHeight:1.7}}>
                    15-day money-back guarantee<br/>
                    
                  </div>

                  {/* Promo code on plan selection screen */}
                  <div style={{marginTop:12,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 14px"}}>
                    <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:6}}>🎟 Have a promo code?</div>
                    {promoApplied ? (
                      <div style={{background:T.greenBg,border:"1px solid "+T.green+"40",borderRadius:8,padding:"9px 12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div style={{fontSize:12,fontWeight:700,color:T.green}}>✅ {promoApplied.code} — {promoApplied.label}</div>
                        <button onClick={()=>{setPromoApplied(null);setPromoCode("");}} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:11}}>Remove</button>
                      </div>
                    ):(
                      <div style={{display:"flex",gap:8}}>
                        <input value={promoCode} onChange={e=>{setPromoCode(e.target.value.toUpperCase());setPromoError("");}}
                          placeholder="Enter promo code"
                          style={{flex:1,background:T.bg1,border:"1px solid "+(promoError?T.red:T.subtle),borderRadius:8,padding:"8px 12px",color:T.text,fontSize:13,fontFamily:"inherit",textTransform:"uppercase"}}/>
                        <button onClick={applyPromo} style={{padding:"8px 14px",background:T.teal,color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>Apply</button>
                      </div>
                    )}
                    {promoError&&<div style={{fontSize:11,color:T.red,marginTop:4}}>{promoError}</div>}
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{background:T.bg2,border:"1px solid "+T.teal+"40",borderRadius:9,padding:"10px 14px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:T.text}}>{selectedPlan.name}</div>
                      <div style={{fontSize:10,color:T.teal}}>↩ 15-day money-back guarantee</div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:18,fontWeight:800,color:T.teal}}>{selectedPlan.price}<span style={{fontSize:10,color:T.muted}}>{selectedPlan.period}</span></span>
                      <button onClick={()=>setSelectedPlan(null)} style={{fontSize:10,color:T.muted,background:"none",border:"1px solid "+T.subtle,borderRadius:5,padding:"2px 7px",cursor:"pointer"}}>Change</button>
                    </div>
                  </div>

                  <AuthField label="Company Name *" value={companyName} onChange={setCompanyName} placeholder="Pacific GC Services Inc"/>
                  <AuthField label="Your Name" value={ownerName} onChange={setOwnerName} placeholder="John Smith"/>
                  <div style={{marginBottom:12}}>
                    <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:6}}>
                      CSLB License # <span style={{color:T.red}}>*</span>
                      <span style={{fontSize:10,color:T.muted,fontWeight:400}}> — Required for licensed contractors only</span>
                    </div>
                    <div style={{position:"relative"}}>
                      <input value={cslb} onChange={e=>{setCslb(e.target.value);validateLicense(e.target.value);}}
                        placeholder="1152750"
                        style={{width:"100%",background:T.bg2,border:"1px solid "+(licenseError?T.red:cslb.replace(/[^0-9]/g,"").length>=6?T.green:T.subtle),borderRadius:8,padding:"10px 12px",color:T.text,fontSize:13,fontFamily:"inherit",outline:"none"}}/>
                      {cslb.replace(/[^0-9]/g,"").length>=6&&!licenseError&&(
                        <span style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",color:T.green,fontSize:14}}>✓</span>
                      )}
                    </div>
                    {licenseError&&<div style={{fontSize:10,color:T.red,marginTop:4}}>⚠ {licenseError}</div>}
                    {!licenseError&&cslb.replace(/[^0-9]/g,"").length>=6&&(
                      <div style={{fontSize:10,color:T.muted,marginTop:4}}>
                        By signing up you certify this is your valid, active CSLB license number.
                      </div>
                    )}
                    <div style={{fontSize:10,color:T.muted,marginTop:4}}>
                      Verify yours at <span style={{color:T.gold,cursor:"pointer"}} onClick={()=>window.open("https://www.cslb.ca.gov/OnlineServices/CheckLicenseII/CheckLicense.aspx","_blank")}>cslb.ca.gov →</span>
                    </div>
                  </div>
                  <AuthField label="Location" value={location} onChange={setLocation} placeholder="San Jose, CA"/>
                  <AuthField label="Email Address *" value={email} onChange={setEmail} placeholder="you@yourcompany.com" type="email"/>
                  <AuthField label="Password *" value={password} onChange={setPassword} placeholder="Choose a secure password" type={showPass?"text":"password"}
                    suffix={<button onClick={()=>setShowPass(p=>!p)} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:11}}>{showPass?"Hide":"Show"}</button>}/>

                  {/* Promo Code */}
                  <div style={{marginBottom:12}}>
                    <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:6}}>🎟 Promo Code <span style={{fontWeight:400,color:T.muted}}>(optional)</span></div>
                    {promoApplied ? (
                      <div style={{background:T.greenBg,border:"1px solid "+T.green+"40",borderRadius:8,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <div style={{fontSize:12,fontWeight:700,color:T.green}}>✅ {promoApplied.code} applied!</div>
                          <div style={{fontSize:11,color:T.muted}}>{promoApplied.label}</div>
                        </div>
                        <button onClick={()=>{setPromoApplied(null);setPromoCode("");}}
                          style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:11}}>Remove</button>
                      </div>
                    ):(
                      <div style={{display:"flex",gap:8}}>
                        <input value={promoCode} onChange={e=>{setPromoCode(e.target.value.toUpperCase());setPromoError("");}}
                          placeholder="Enter code if you have one"
                          style={{flex:1,background:T.bg2,border:"1px solid "+(promoError?T.red:T.subtle),borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit",textTransform:"uppercase"}}/>
                        <button onClick={applyPromo}
                          style={{padding:"9px 16px",background:T.teal,color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                          Apply
                        </button>
                      </div>
                    )}
                    {promoError&&<div style={{fontSize:11,color:T.red,marginTop:4}}>{promoError}</div>}
                  </div>

                  {error&&<div style={{background:T.redBg,border:"1px solid "+T.red+"30",borderRadius:8,padding:"10px 14px",fontSize:12,color:T.red,marginBottom:12}}>{error}</div>}

                  {/* Email Verification Modal */}
                  {showVerify&&(
                    <div style={{background:T.bg2,border:"1px solid "+T.teal+"40",borderRadius:10,padding:"16px",marginBottom:14}}>
                      <div style={{fontSize:13,fontWeight:700,color:T.teal,marginBottom:4}}>📧 Verify Your Email</div>
                      <div style={{fontSize:11,color:T.muted,marginBottom:10,lineHeight:1.6}}>
                        We sent a 6-digit code to <strong style={{color:T.text}}>{pendingEmail}</strong>. Enter it below to complete signup.
                      </div>
                      <div style={{display:"flex",gap:8,marginBottom:8}}>
                        <input value={verifyCode} onChange={e=>setVerifyCode(e.target.value.replace(/\D/g,"").slice(0,6))}
                          placeholder="000000" maxLength={6}
                          style={{flex:1,background:T.bg1,border:"1px solid "+(verifyError?T.red:T.subtle),borderRadius:8,padding:"10px 14px",color:T.text,fontSize:20,fontFamily:"monospace",letterSpacing:"0.3em",textAlign:"center"}}/>
                        <button onClick={verifyEmail} disabled={verifyCode.length!==6}
                          style={{padding:"10px 18px",background:verifyCode.length===6?T.teal:"#374151",color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                          Verify ✓
                        </button>
                      </div>
                      {verifyError&&<div style={{fontSize:11,color:T.red,marginBottom:6}}>{verifyError}</div>}
                      <div style={{fontSize:10,color:T.muted}}>Didn't get it? Check spam or <button onClick={()=>sendVerificationEmail(email)} style={{background:"none",border:"none",color:T.blue,cursor:"pointer",fontSize:10,padding:0}}>resend code</button></div>
                    </div>
                  )}

                  <button onClick={async ()=>{
                    if (!email||!password||!companyName) return;
                    setLoading(true); setError("");
                    const companies = getCompanies();
                    const exists = companies.find(co=>co.email.toLowerCase()===email.toLowerCase()||( co.loginEmail&&co.loginEmail.toLowerCase()===email.toLowerCase()));
                    if (exists) {
                      if ((exists.cancellations||0)>=SUBSCRIPTION_POLICY.maxCancellations){setError(SUBSCRIPTION_POLICY.bannedMessage);setLoading(false);return;}
                      setError("An account with this email already exists. Please sign in."); setLoading(false); return;
                    }
                    if (promoApplied) {
                      // Promo code — skip payment, go to email verification
                      const code = await sendVerificationEmail(email);
                      // Show code in alert for now (replace with real email service)
                      alert("Your verification code is: "+code+"\n\n(In production this will be emailed to you)");
                      setLoading(false);
                    } else {
                      // No promo — proceed to payment
                      handleStripeCheckout(selectedPlan);
                    }
                  }}
                    disabled={loading||!email||!password||!companyName}
                    style={{width:"100%",padding:"14px",background:loading||!email||!password||!companyName?"#374151":"#635bff",color:"white",border:"none",borderRadius:10,fontSize:15,fontWeight:700,cursor:loading||!email||!password||!companyName?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:10,transition:"all 0.2s",marginBottom:10}}>
                    <span style={{fontSize:18}}>💳</span>
                    {loading ? "Processing..." : promoApplied ? "Continue — Verify Email →" : `Start Now · ${selectedPlan.price}${selectedPlan.period}`}
                  </button>

                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:12}}>
                    {["Visa","Mastercard","Amex","Apple Pay","Google Pay","PayPal"].map(pm=>(
                      <div key={pm} style={{background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"5px 8px",textAlign:"center",fontSize:10,color:T.muted}}>{pm}</div>
                    ))}
                  </div>

                  <div style={{background:T.amberBg,border:"1px solid "+T.amber+"40",borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:10,color:T.amber,lineHeight:1.7}}>
                  <strong>Cancellation Policy:</strong> {SUBSCRIPTION_POLICY.message}
                  </div>

                  <div style={{fontSize:10,color:T.muted,textAlign:"center",lineHeight:1.7}}>
                    🔒 Secured by Stripe · 15-day money-back guarantee<br/>
                    15-day money-back guarantee. No questions asked.
                  </div>
                </div>
              )}
            </div>
          )}


        </div>

        <div style={{textAlign:"center",marginTop:20,fontSize:11,color:T.muted}}>
          Grok Field Ops · Built for US Construction GCs
        </div>
      </div>
    </div>
  );
}
// Small reusable auth components
function AuthField({label,value,onChange,placeholder,type="text",suffix,onEnter}) {
  return (
    <div style={{marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>{label}</div>
      <div style={{position:"relative",display:"flex",alignItems:"center"}}>
        <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
          onKeyDown={e=>e.key==="Enter"&&onEnter&&onEnter()}
          style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"11px 14px",color:T.text,fontSize:14,fontFamily:"inherit",outline:"none"}}/>
        {suffix&&<div style={{position:"absolute",right:12}}>{suffix}</div>}
      </div>
    </div>
  );
}

function AuthBtn({label,onClick,disabled}) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{width:"100%",padding:"14px",background:disabled?T.bg3:T.gold,color:disabled?T.muted:T.bg0,border:"none",borderRadius:10,fontSize:15,fontWeight:700,cursor:disabled?"not-allowed":"pointer",transition:"all 0.2s",letterSpacing:"0.05em"}}>
      {label}
    </button>
  );
}



// ─── TIER & USAGE SYSTEM ─────────────────────────────────────────────────────
const TIERS = {
  starter: {
    name:"Starter", price:"$149/mo", annual:"$1,490/yr", color:"#60a5fa",
    // Aligned to the printed promise: 25 estimates / 150 PM chats / 10 Blueprint AI.
    limits:{ estimates:25, pm_chats:150, video:10, meetings:15, blueprint:10, projects:3, subs:10, co_detection:20, aria_voice:0 }
  },
  pro: {
    name:"Pro", price:"$249/mo", annual:"$2,490/yr", color:"#a78bfa",
    // Aligned to the printed promise: 50 estimates / 350 PM chats / 25 Blueprint AI.
    limits:{ estimates:50, pm_chats:350, video:25, meetings:30, blueprint:25, projects:10, subs:20, co_detection:75, aria_voice:1 }
  },
  gcpro: {
    name:"GC Pro", price:"$399/mo", annual:"$3,990/yr", color:"#d4a843",
    limits:{ estimates:9999, pm_chats:9999, video:9999, meetings:9999, blueprint:9999, projects:9999, subs:9999, aria_voice:9999 }
  },
  team: {
    name:"GC Pro", price:"$399/mo", annual:"$3,990/yr", color:"#d4a843",
    limits:{ estimates:9999, pm_chats:9999, video:9999, meetings:9999, blueprint:9999, projects:9999, subs:9999, aria_voice:9999 }
  },
  owner: {
    name:"Owner", price:"Free", color:"#22c55e",
    limits:{ estimates:9999, pm_chats:9999, video:9999, meetings:9999, blueprint:9999, projects:9999, subs:9999, aria_voice:9999 }
  },
  admin: {
    name:"Admin", price:"Free", color:"#22c55e",
    limits:{ estimates:9999, pm_chats:9999, video:9999, meetings:9999, blueprint:9999, projects:9999, subs:9999, aria_voice:9999 }
  }
};

const USAGE_KEY = "grok_usage";
const MONTH_KEY = () => new Date().toISOString().slice(0,7);
function usageKey(cid){return "grok_usage_"+(cid||"local")+"_"+MONTH_KEY();}
function getUsage(cid){try{const d=JSON.parse(localStorage.getItem(usageKey(cid))||"{}");return{month:MONTH_KEY(),estimates:0,pm_chats:0,video:0,meetings:0,blueprint:0,...d};}catch{return{month:MONTH_KEY(),estimates:0,pm_chats:0,video:0,meetings:0,blueprint:0};}}
function trackUsage(type,cid){const k=usageKey(cid||window._grokCompanyId);const u=getUsage(cid||window._grokCompanyId);u[type]=(u[type]||0)+1;try{localStorage.setItem(k,JSON.stringify(u));}catch(e){}return u;}
function checkLimit(type,session){const tier=TIERS[session?.plan]||TIERS.starter;const usage=getUsage(session?.companyId);const used=usage[type]||0;const limit=tier.limits[type]||9999;return{ok:limit>=9999||used<limit,used,limit,tier};}
function enforceLimit(type, session) {
  // Returns true if allowed, false if blocked (and shows alert)
  const {ok, used, limit, tier} = checkLimit(type, session);
  if (ok) { trackUsage(type, session?.companyId); return true; }
  alert(`⚡ Monthly limit reached\n\nYou've used ${used}/${limit} ${type} on the ${tier.name} plan.\n\nUpgrade your plan to continue:\n• Starter $149/mo — 25 estimates/mo\n• Pro $249/mo — 50 estimates/mo\n• GC Pro $399/mo — Unlimited\n\nLimits reset on the 1st of each month.`);
  return false;
}

function UsageBadge({type, session, style={}}) {
  const {used, limit, tier} = checkLimit(type, session||{plan:"starter"});
  const pct = Math.min(100, Math.round(used/limit*100));
  const color = pct>90?T.red:pct>70?T.amber:T.green;
  if (limit >= 9999) return null; // unlimited — don't show
  return (
    <div style={{display:"flex",alignItems:"center",gap:6,fontSize:10,color:T.muted,...style}}>
      <div style={{width:48,height:3,background:T.bg3,borderRadius:2,overflow:"hidden"}}>
        <div style={{width:pct+"%",height:"100%",background:color,borderRadius:2,transition:"width 0.3s"}}/>
      </div>
      <span style={{color,fontWeight:600}}>{used}/{limit}</span>
      <span style={{color:T.muted}}>{type}</span>
    </div>
  );
}

function AILimitGate({type, session, children}) {
  const {ok, used, limit, tier} = checkLimit(type, session);
  if (ok) return children;
  return (
    <div style={{background:T.amberBg,border:`1px solid ${T.amber}30`,borderRadius:10,padding:"16px 20px",textAlign:"center"}}>
      <div style={{fontSize:24,marginBottom:8}}>⚡</div>
      <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Monthly limit reached</div>
      <div style={{fontSize:12,color:T.muted,marginBottom:12}}>{used}/{limit} {type} used on {tier.name} plan this month</div>
      <div style={{fontSize:11,color:T.amber}}>Upgrade your plan — Starter $149/mo · Pro $249/mo · GC Pro $399/mo</div>
    </div>
  );
}

// Theme system — dark (default) + light
const DARK_THEME = {
  bg0:"#09090f",bg1:"#111118",bg2:"#18181f",bg3:"#22222c",bg4:"#2a2a36",
  gold:"#d4a843",gold2:"#f0c050",goldDim:"#8a6820",goldBg:"rgba(212,168,67,0.09)",
  text:"#eaeaf2",muted:"#7a7a92",subtle:"#2e2e3e",border:"#2a2a3a",
  green:"#16a34a",greenBg:"rgba(22,163,74,0.09)",
  red:"#ef4444",redBg:"rgba(239,68,68,0.09)",
  amber:"#f59e0b",amberBg:"rgba(245,158,11,0.09)",
  blue:"#60a5fa",blueBg:"rgba(96,165,250,0.09)",
  teal:"#2dd4bf",tealBg:"rgba(45,212,191,0.09)",
  purple:"#a78bfa",purpleBg:"rgba(167,139,250,0.09)",
  isDark:true,
};

const LIGHT_THEME = {
  bg0:"#f0f4f8",bg1:"#e8edf5",bg2:"#f5f7fa",bg3:"#e8ecf2",bg4:"#dde3ec",
  gold:"#b8860b",gold2:"#d4a843",goldDim:"#d4a843",goldBg:"rgba(184,134,11,0.08)",
  text:"#1a1f2e",muted:"#5a6478",subtle:"#c8d4e8",border:"#c8d0e0",
  green:"#15803d",greenBg:"rgba(21,128,61,0.08)",
  red:"#dc2626",redBg:"rgba(220,38,38,0.08)",
  amber:"#d97706",amberBg:"rgba(217,119,6,0.08)",
  blue:"#2563eb",blueBg:"rgba(37,99,235,0.08)",
  teal:"#0d9488",tealBg:"rgba(13,148,136,0.08)",
  purple:"#7c3aed",purpleBg:"rgba(124,58,237,0.08)",
  isDark:false,
};

let T = {...DARK_THEME}; // Default dark — avoids white flash on load
// Immediately apply saved theme before first render
(function(){
  try {
    // App is locked to light theme — initialize correctly
    T = {...LIGHT_THEME};
  } catch(e) {}
})();
let _themeListeners = [];
function setGlobalTheme(isDark) {
  T = isDark ? {...DARK_THEME} : {...LIGHT_THEME};
  _themeListeners.forEach(fn => fn(T));
}
function useTheme() {
  const [theme, setThemeState] = useState(T);
  useEffect(()=>{
    const listener = (t) => setThemeState({...t});
    _themeListeners.push(listener);
    return ()=>{ _themeListeners = _themeListeners.filter(l=>l!==listener); };
  },[]);
  return theme;
}

// Measures an element's viewport-relative top (on mount, on window resize, and
// whenever a value in `watch` changes) and returns a CSS length that fills from
// there to the bottom of the viewport, minus `bottomPad`. Lets a panel own the
// remaining screen height without a hand-tuned "calc(100vh - Npx)" guess — the
// offset is measured, not eyeballed. Returns a string usable as `height` OR
// `maxHeight`, or null before the first measurement (callers should fall back:
// `bound || "<prior calc>"`). The element's own height never feeds back into its
// top, so there is no measure→resize loop.
function useViewportBound(ref, bottomPad = 16, watch = []) {
  const [bound, setBound] = useState(null);
  React.useLayoutEffect(() => {
    const measure = () => {
      const el = ref && ref.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setBound("calc(100vh - " + Math.round(top) + "px - " + bottomPad + "px)");
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, bottomPad, ...watch]);
  return bound;
}

// --- ICONS --------------------------------------------------------------------
const Ic = ({ d, size=16, fill=false, color, style={} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    fill={fill?"currentColor":"none"} stroke={fill?"none":"currentColor"}
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    style={{flexShrink:0,color:color||"currentColor",...style}}>
    {Array.isArray(d)?d.map((p,i)=><path key={i} d={p}/>):<path d={d}/>}
  </svg>
);
const ICONS = {
  home:"M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z",
  project:"M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2z",
  walk:"M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
  co:"M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  rfi:"M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  cop:"M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  docs:"M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z",
  ai:"M12 2a10 10 0 110 20A10 10 0 0112 2zm0 5v7m0 0l3-3m-3 3l-3-3",
  estimate:"M9 7H6a2 2 0 00-2 2v9a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3-3-3m3-3v11",
  scope:"M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
  prestart:"M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
  alert:"M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
  check:"M5 13l4 4L19 7",
  plus:"M12 5v14M5 12h14",
  x:"M18 6L6 18M6 6l12 12",
  send:"M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z",
  qr:"M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h3v3h-3zM20 17h1M20 20h-3v-3M20 14v3",
  star:"M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z",
  copy:"M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z",
  refresh:"M1 4v6h6M23 20v-6h-6M20.49 9A9 9 0 005.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 013.51 15",
  robot:"M12 2a2 2 0 012 2v1h3a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h3V4a2 2 0 012-2zM9 11a1 1 0 100 2 1 1 0 000-2zm6 0a1 1 0 100 2 1 1 0 000-2zM9 15h6",
};

// --- BASE COMPONENTS ----------------------------------------------------------
const Card = ({children,style={},onClick,hover=true})=>(
  <div onClick={onClick} style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:10,padding:"14px 16px",...style,cursor:onClick?"pointer":"default",transition:"border-color 0.15s"}}
    onMouseEnter={e=>hover&&onClick&&(e.currentTarget.style.borderColor=T.goldDim)}
    onMouseLeave={e=>hover&&onClick&&(e.currentTarget.style.borderColor=T.subtle)}>
    {children}
  </div>
);
const Btn=({children,onClick,primary,ghost,danger,small,disabled,style={}})=>(
  <button onClick={onClick} disabled={disabled} style={{display:"inline-flex",alignItems:"center",gap:6,padding:small?"6px 12px":"8px 16px",borderRadius:8,fontSize:small?12:13,fontWeight:600,cursor:disabled?"not-allowed":"pointer",border:"none",transition:"all 0.15s",opacity:disabled?0.5:1,background:primary?T.gold:danger?T.redBg:ghost?"transparent":T.bg3,color:primary?T.bg0:danger?T.red:ghost?T.gold:T.muted,outline:ghost?`1px solid ${T.subtle}`:"none",...style}}>
    {children}
  </button>
);
const Badge=({label,color=T.amber})=>(
  <span style={{background:color+"20",color,border:`1px solid ${color}40`,borderRadius:20,fontSize:10,fontWeight:700,padding:"2px 8px",letterSpacing:"0.04em",textTransform:"uppercase",whiteSpace:"nowrap"}}>{label}</span>
);
const StatCard=({label,value,sub,color,onClick})=>(
  <div onClick={onClick} style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:10,padding:"14px 16px",cursor:onClick?"pointer":"default",transition:"border-color 0.15s"}}
    onMouseEnter={e=>{if(onClick)e.currentTarget.style.borderColor=color||T.gold}}
    onMouseLeave={e=>{if(onClick)e.currentTarget.style.borderColor=T.subtle}}>
    <div style={{fontSize:10,color:T.muted,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:6}}>{label}</div>
    <div style={{fontSize:26,fontWeight:800,color:color||T.text,lineHeight:1}}>{value}</div>
    {sub&&<div style={{fontSize:11,color:T.muted,marginTop:4}}>{sub}</div>}
    {onClick&&<div style={{fontSize:9,color:T.muted,marginTop:4}}>click to view →</div>}
  </div>
);
const SectionTitle=({children,action,onAction})=>(
  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
    <div style={{fontSize:14,fontWeight:700,color:T.text}}>{children}</div>
    {action&&<button onClick={onAction} style={{fontSize:12,color:T.gold,background:"none",border:"none",cursor:"pointer"}}>{action}</button>}
  </div>
);
const Progress=({pct,color=T.gold})=>(
  <div style={{height:4,background:T.bg3,borderRadius:2,marginTop:6}}>
    <div style={{height:"100%",width:`${pct}%`,background:color,borderRadius:2,transition:"width 0.4s"}}/>
  </div>
);
const Divider=()=><div style={{height:1,background:T.subtle,margin:"14px 0"}}/>;
const Spin=()=>(
  <div style={{width:16,height:16,borderRadius:"50%",border:`2px solid ${T.subtle}`,borderTopColor:T.gold,animation:"spin 0.7s linear infinite",flexShrink:0}}/>
);

// --- MODAL WRAPPER ------------------------------------------------------------
const Modal=({title,onClose,children,wide})=>(
  <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:14,width:wide?860:520,maxHeight:"85vh",overflowY:"auto",flexShrink:0}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"18px 22px",borderBottom:`1px solid ${T.subtle}`,position:"sticky",top:0,background:T.bg1,zIndex:1}}>
        <div style={{fontSize:16,fontWeight:700,color:T.text}}>{title}</div>
        <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:T.muted,padding:4}}><Ic d={ICONS.x} size={18}/></button>
      </div>
      <div style={{padding:"20px 22px"}}>{children}</div>
    </div>
  </div>
);
const Field=({label,value,onChange,placeholder,type="text",rows})=>(
  <div style={{marginBottom:14}}>
    <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>{label}</div>
    {rows?(
      <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={rows}
        style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,resize:"vertical",fontFamily:"inherit"}}/>
    ):(
      <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
    )}
  </div>
);
const Select=({label,value,onChange,options})=>(
  <div style={{marginBottom:14}}>
    <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>{label}</div>
    <select value={value} onChange={e=>onChange(e.target.value)}
      style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}>
      {(options||[]).map(o=><option key={String(o.value??o)} value={String(o.value??o)}>{String(o.label??o??'')}</option>)}
    </select>
  </div>
);

// --- AI CALL ------------------------------------------------------------------
async function callAI(systemPrompt, userMessage, onChunk, apiKey, opts={}) {
  // Route through Supabase proxy — no local API key needed
  let res;
  try {
    res = await fetch(AI_PROXY_URL, {
      method:"POST",
      headers:{
        "Content-Type":"application/json",
        "Authorization":"Bearer "+sb._token(),
        "apikey":SUPABASE_ANON_KEY,
      },
      body:JSON.stringify({
        model:"claude-haiku-4-5",
        system:systemPrompt,
        messages:[{role:"user",content:userMessage}],
        max_tokens:2000,
        temperature: opts.temperature, // undefined for chat (default sampling); 0 for analysis calls
      })
    });
    if (!res.ok) {
      const errText = await res.text();
      throw new Error("Proxy error "+res.status+": "+errText);
    }
  } catch(e) {
    throw new Error("AI service unavailable: "+e.message);
  }

  // Proxy returns JSON (not a stream) — handle both cases
  const contentType = res.headers.get("content-type")||"";
  if (contentType.includes("application/json")) {
    const data = await res.json();
    if (data.error) throw new Error(data.error.message||JSON.stringify(data.error));
    const text = data.content?.[0]?.text || "";
    onChunk(text);
    return text;
  }

  // Streaming fallback
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let full = "";
  while (true) {
    const {done, value} = await reader.read();
    if (done) break;
    const chunk = dec.decode(value);
    for (const line of chunk.split("\n")) {
      if (line.startsWith("data: ")) {
        try {
          const d = JSON.parse(line.slice(6));
          if (d.type === "content_block_delta" && d.delta?.text) {
            full += d.delta.text;
            onChunk(full);
          }
        } catch(e) {}
      }
    }
  }
  return full;
}

// --- DATA ---------------------------------------------------------------------
// Demo data — only shown to admin account (GCG)
const ADMIN_COMPANY_ID = "admin_001";
const GCG_COMPANY_ID = "gcg_001"; // Jehoon's real GCG account

function getInitialProjects(companyId) {
  // Always check localStorage first — filtered through the canonical junk
  // filter so seeded test rows can never leak into the in-memory state.
  try {
    const stored = localStorage.getItem("grok_projects_"+(companyId||""));
    if (stored) { const p = cleanProjects(JSON.parse(stored)); if (p.length) return p; }
  } catch(e) {}
  // Demo seeding for the admin account is intentionally disabled — demos were
  // adding stub rows that surfaced in QuickBooks Export and other views. Real
  // accounts start empty.
  return [];
}

function getInitialCOs(companyId) {
  try {
    const stored = localStorage.getItem("grok_cos_global_"+(companyId||""));
    if (stored) { const c = JSON.parse(stored); if (c.length) return c; }
  } catch(e) {}
  if (companyId === ADMIN_COMPANY_ID) {
    return [
      {id:1,num:"CO-003",project:"466 Turk St",desc:"Drain relocation — Unit 4B conflict",amount:8400,status:"pending",date:"Apr 15",days:4,comments:[]},
      {id:2,num:"CO-004",project:"466 Turk St",desc:"Water-damaged drywall — antimicrobial",amount:2100,status:"pending",date:"Apr 17",days:2,comments:[]},
      {id:3,num:"CO-001",project:"466 Turk St",desc:"Tile upgrade — owner selected premium",amount:4200,status:"approved",date:"Apr 10",days:0,comments:[]},
    ];
  }
  return [];
}

function getInitialRFIs(companyId) {
  try {
    const stored = localStorage.getItem("grok_rfis_global_"+(companyId||""));
    if (stored) { const r = JSON.parse(stored); if (r.length) return r; }
  } catch(e) {}
  if (companyId === ADMIN_COMPANY_ID) {
    return [
      {id:1,num:"RFI-009",project:"466 Turk St",subject:"Drain elevation conflict — plan vs field",priority:"critical",status:"open",submitted:"Apr 19",due:"Apr 19",comments:[]},
      {id:2,num:"RFI-008",project:"466 Turk St",subject:"Mold scope — extent of remediation",priority:"urgent",status:"pending",submitted:"Apr 14",due:"Apr 16",comments:[]},
      {id:3,num:"RFI-001",project:"1200 Mission",subject:"Tile pattern — grout color unspecified",priority:"routine",status:"pending",submitted:"Apr 10",due:"Apr 15",comments:[]},
    ];
  }
  return [];
}

function getInitialLeads(companyId) {
  if (companyId === ADMIN_COMPANY_ID) {
    return [
      {id:1,name:"Marcus Webb",phone:"(415) 555-0182",email:"marcus@sfproperty.com",company:"Webb Properties",type:"SRO Renovation",addr:"Tenderloin, SF",budget:"$300K-$500K",units:8,status:"site-walk",priority:"hot",source:"referral",created:"Apr 15",notes:"Wants full gut renovation, 8 units.",lastContact:"Apr 18"},
      {id:2,name:"Jennifer Cho",phone:"(650) 555-0241",company:"Bay Area CDC",email:"jcho@bacdc.org",type:"Affordable Housing TI",addr:"SoMa, SF",budget:"$200K-$350K",units:6,status:"estimated",priority:"warm",source:"direct",created:"Apr 10",notes:"MOHCD funded. Prevailing wage required.",lastContact:"Apr 17"},
    ];
  }
  return [];
}

let PROJECTS = getInitialProjects(window._grokCompanyId||"");
let COS = getInitialCOs(window._grokCompanyId||"");
let RFIS = getInitialRFIs(window._grokCompanyId||"");
let LEADS = getInitialLeads(window._grokCompanyId||"");
const PHASE_NAMES=["Demo","Rough Framing","MEP Rough","Drywall & Tape","Tile & Finish","Paint","Closeout"];

// When a phase hits 80%, ARIA drafts a mobilization email to the trades of
// the NEXT phase. Index i here aligns with PHASE_NAMES[i] — the trades listed
// are the ones we expect on phase i+1. Last phase has no successor.
const PHASE_TRADES = [
  /* after Demo            */ {nextPhase:"Rough Framing",   trades:["Framing"]},
  /* after Rough Framing   */ {nextPhase:"MEP Rough-In",    trades:["Electrical","Plumbing","HVAC"]},
  /* after MEP Rough       */ {nextPhase:"Drywall & Tape",  trades:["Insulation","Drywall"]},
  /* after Drywall & Tape  */ {nextPhase:"Tile & Finish",   trades:["Tile & Finishes"]},
  /* after Tile & Finish   */ {nextPhase:"Paint",           trades:["Painting"]},
  /* after Paint           */ {nextPhase:"Closeout",        trades:["Flooring","Low Voltage"]},
  /* after Closeout        */ {nextPhase:null,              trades:[]},
];

// When a phase hits 100%, ARIA surfaces the inspections typically required for
// that milestone. `contact_key` maps to BUILDING_DEPTS[city].key_contacts so the
// modal can show the right inspector phone line. Empty array = no inspection
// typically required for that phase (Demo, finish-only phases).
const INSPECTIONS_BY_PHASE = [
  /* Demo            */ [],
  /* Rough Framing   */ [{type:"Framing / Structural",  contact_key:"Building Inspection"}],
  /* MEP Rough-In    */ [
                          {type:"Rough Plumbing",       contact_key:"Plumbing Inspection"},
                          {type:"Rough Electrical",     contact_key:"Electrical Inspection"},
                          {type:"Rough Mechanical/HVAC",contact_key:"Building Inspection"},
                        ],
  /* Drywall & Tape  */ [{type:"Drywall / Nail Inspection", contact_key:"Building Inspection"}],
  /* Tile & Finish   */ [],
  /* Paint           */ [],
  /* Closeout        */ [{type:"Final Building",        contact_key:"Building Inspection"}],
];

// Pending mobilization notifications — drafted by ARIA, waiting for GC review.
// Scoped per company. One row per (project, phase, trade) draft.
const PendingNotifs = {
  _key: (cid) => "grok_pending_notifs_" + (cid || "local"),
  load: (cid) => {
    try { return JSON.parse(localStorage.getItem(PendingNotifs._key(cid))||"[]"); }
    catch { return []; }
  },
  save: (cid, list) => {
    try { localStorage.setItem(PendingNotifs._key(cid), JSON.stringify(list)); } catch {}
  },
  add: (cid, notif) => {
    const list = PendingNotifs.load(cid);
    PendingNotifs.save(cid, [notif, ...list]);
  },
  update: (cid, id, patch) => {
    PendingNotifs.save(cid, PendingNotifs.load(cid).map(n => n.id === id ? {...n, ...patch} : n));
  },
  remove: (cid, id) => {
    PendingNotifs.save(cid, PendingNotifs.load(cid).filter(n => n.id !== id));
  },
};

// Draft an RFI from a blueprint flag. Creates a "pending_gc_approval" RFI
// with subject from flag title, drawing reference, and AI-generated suggested
// resolution. Persists to grok_rfis_global_<cid> so it shows up in the RFI
// list immediately. Skips if the flag already has rfiDraftId set.
async function draftRFIFromFlag({flag, blueprint, proj, session}) {
  const cid = session?.companyId || window._grokCompanyId || "local";
  if (!cid || cid === "local") return null;
  if (flag?.rfiDraftId) return null;

  const projName = (proj?.name || "").split(" — ")[0] || proj?.name || "Project";
  const hasPos = flag?.pos && (flag.pos.x != null || flag.pos.y != null);
  const xPct = Math.round(flag.pos?.x || 0);
  const yPct = Math.round(flag.pos?.y || 0);
  const locText = flag?.location || "";
  const drawingRef = blueprint?.name
    ? (hasPos ? `${blueprint.name} @ (${xPct}%, ${yPct}%)` : (locText ? `${blueprint.name} — ${locText}` : blueprint.name))
    : (hasPos ? `Drawing @ (${xPct}%, ${yPct}%)` : (locText || "(no drawing reference)"));

  const rfiKey = "grok_rfis_global_" + cid;
  let existing = [];
  try { existing = JSON.parse(localStorage.getItem(rfiKey) || "[]"); } catch {}

  // AI: suggested resolution paragraph. Fallback if AI errors.
  let suggestedResolution = "";
  try {
    const r = await fetch(AI_PROXY_URL, {
      method: "POST",
      headers: {"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
      body: JSON.stringify({
        model: "claude-haiku-4-5",
        max_tokens: 350,
        usage_type: "chat",
        company_id: cid,
        plan: session?.plan || "starter",
        messages: [{role:"user", content:
`A field issue was flagged on a construction drawing. Draft a short "Suggested Resolution" paragraph for the architect.

Project: ${projName}
Drawing: ${blueprint?.name || "(no name)"}
Location on drawing: (${xPct}%, ${yPct}%)
Flag title: ${flag.title || ""}
Flag description: ${flag.desc || flag.description || "(no details)"}
Trade: ${flag.trade || "not specified"}
Priority: ${flag.priority || "medium"}

Write 3–4 sentences:
1. State the most likely cause / what the field is seeing
2. List 1–2 specific questions the architect needs to answer
3. Suggest a path forward (hold work / proceed with assumption / etc.)

Be direct. No preamble, no sign-off, no corporate filler.`}],
      }),
    });
    const data = await r.json();
    suggestedResolution = data.content?.[0]?.text || "";
  } catch {}
  if (!suggestedResolution) {
    suggestedResolution = "AI draft unavailable — replace this with your read on the issue and what you need the architect to clarify.";
  }

  const rfiId = "RFI-" + String(existing.length + 1).padStart(3, "0");
  const subject = flag.title || ("Field issue on " + (blueprint?.name || "drawing"));
  const description = (flag.desc || flag.description || "(no details from field)") +
    "\n\nDrawing reference: " + drawingRef +
    (flag.trade ? "\nTrade: " + flag.trade : "") +
    (flag.priority ? "\nPriority: " + flag.priority : "") +
    (flag.flaggedBy ? "\nFlagged by: " + flag.flaggedBy : "");

  const rfi = {
    id: rfiId,
    num: rfiId,
    subject,
    desc: description,
    description,
    suggestedResolution,
    drawingRef,
    blueprintId: blueprint?.id || null,
    blueprintName: blueprint?.name || null,
    flagId: flag.id,
    flagPos: flag.pos || null,
    priority: flag.priority || "medium",
    assignee: "Architect",
    project: projName,
    projectId: proj?.id,
    status: "pending_gc_approval",
    submitted: new Date().toLocaleDateString(),
    submittedBy: flag.flaggedBy || session?.ownerName || session?.subName || "Field",
    draftedFrom: "blueprint_flag",
    draftedAt: new Date().toISOString(),
    comments: [],
    attachments: [],
  };

  const updated = [rfi, ...existing];
  try { localStorage.setItem(rfiKey, JSON.stringify(updated)); } catch {}
  try { RFIS = updated; } catch {}
  try { SyncDB.saveRFIs && SyncDB.saveRFIs(updated).catch(()=>{}); } catch {}
  try { window.dispatchEvent(new Event("grok-rfis-updated")); } catch {}
  try { window._grokBroadcast?.("data_updated"); } catch {}

  return rfi;
}

// Draft a mobilization email for the next-phase trade. Returns
// {subject, body} or null on AI failure. Falls back to a templated draft.
async function draftMobilizationEmail({proj, phaseName, nextPhaseName, trade, subName, subCompany, gcCompanyName, gcOwnerName, plan, cid}) {
  const fallback = () => ({
    subject: `Heads up — mobilizing ${trade} on ${proj?.name || "the project"} in 3–5 days`,
    body:
`Hi ${subName || trade + " team"},

Quick mobilization heads-up — we just wrapped most of ${phaseName} on ${proj?.name || "the project"}${proj?.addr ? " (" + proj.addr + ")" : ""}, and ${nextPhaseName} is up next.

We expect ${trade} on site in approximately 3–5 days. Please confirm:
  • Your crew availability for that window
  • Any submittals or material lead times we should know about
  • Site access requirements (lift / parking / power)

Reply to this email and we'll lock in the exact start date.

Thanks,
${gcOwnerName || gcCompanyName || "The GC team"}
${gcCompanyName || ""}`,
  });

  try {
    const r = await fetch(AI_PROXY_URL, {
      method:"POST",
      headers:{"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
      body: JSON.stringify({
        model:"claude-haiku-4-5",
        max_tokens:400,
        usage_type:"chat",
        company_id: cid,
        plan: plan || "starter",
        messages:[{role:"user", content:
`You are drafting a short, direct email from a GC to a subcontractor to give them a mobilization heads-up.

Project: ${proj?.name || "Project"}
Address: ${proj?.addr || proj?.address || "site"}
Current phase wrapping: ${phaseName}
Next phase: ${nextPhaseName}
Trade to mobilize: ${trade}
Sub name: ${subName || "(unknown contact)"}
Sub company: ${subCompany || ""}
Sender: ${gcOwnerName || gcCompanyName || "The GC"} at ${gcCompanyName || "the GC"}

Write a short email — 6 to 9 lines of body — that:
1. Tells them ${phaseName} is at 80%+
2. Says ${trade} should expect to mobilize in 3–5 days
3. Asks them to confirm: crew availability, any material/submittal lead times, site access needs
4. Asks for a reply to lock in the exact start date
5. Signs off from ${gcOwnerName || gcCompanyName}

Tone: direct, no fluff, no corporate filler, no exclamation points.

Return the result as JSON exactly in this format and nothing else:
{"subject":"...","body":"..."}`}],
      }),
    });
    const data = await r.json();
    const txt = data.content?.[0]?.text;
    if (!txt) return fallback();
    // Extract JSON — model may add stray text around it
    const m = txt.match(/\{[\s\S]*\}/);
    if (!m) return fallback();
    const parsed = JSON.parse(m[0]);
    if (parsed && parsed.subject && parsed.body) return parsed;
    return fallback();
  } catch (e) {
    return fallback();
  }
}

// Draft an AIA G702 pay application when a project milestone hits 100%.
// Pre-fills the period dates, milestone billing share (1/N of revised contract
// value as a starting point — the GC edits before approving), retention, and an
// AI-drafted "work completed this period" narrative. Persists to
// grok_payapps_<projectId> with status="pending_gc_approval" so the GC can
// review, edit, and approve before sending to the owner.
async function draftPayAppFromMilestone({proj, phaseIndex, phaseName, projCOs, session, cid}) {
  if (!proj || phaseIndex == null) return null;
  if (!cid || cid === "local") return null;

  const sk = "grok_payapps_" + proj.id;
  let existing = [];
  try { existing = JSON.parse(localStorage.getItem(sk) || "[]"); } catch {}

  // Don't double-draft for the same milestone.
  if (existing.some(a => a.draftedFrom === "milestone_complete" && a.milestoneIndex === phaseIndex)) {
    return null;
  }

  const approvedCOTotal = (projCOs || []).filter(c => c.status === "approved").reduce((s, c) => s + (c.amount || 0), 0);
  const contractValue = (proj.value || 0) + approvedCOTotal;
  const phaseCount = Math.max((proj.phases || []).length || 7, 1);
  const phaseShare = Math.round(contractValue / phaseCount);
  const retentionPct = 10;
  const retention = Math.round(phaseShare * retentionPct / 100);
  const amountDue = phaseShare - retention;

  let narrative = "";
  try {
    const r = await fetch(AI_PROXY_URL, {
      method: "POST",
      headers: {"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
      body: JSON.stringify({
        model: "claude-haiku-4-5",
        max_tokens: 260,
        usage_type: "chat",
        company_id: cid,
        plan: session?.plan || "starter",
        messages: [{role:"user", content:
`Write the "Description of Work Completed This Period" paragraph for an AIA G702 pay application.

Project: ${proj?.name || "Project"}
Address: ${proj?.addr || ""}
Milestone just completed: ${phaseName}
Phase billing share: $${phaseShare.toLocaleString()} (${(100/phaseCount).toFixed(1)}% of revised contract)

Write 2–3 sentences naming the specific scope completed under "${phaseName}" on a typical Bay Area construction project. Direct, no preamble, no sign-off, no corporate filler.`}],
      }),
    });
    const data = await r.json();
    narrative = (data.content?.[0]?.text || "").trim();
  } catch {}
  if (!narrative) {
    narrative = `${phaseName} milestone complete. Edit this description with the specific scope billed this period before sending to the owner.`;
  }

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0,10);
  const todayIso = today.toISOString().slice(0,10);

  const newApp = {
    id: Date.now(),
    app_num: String(existing.length + 1).padStart(3, "0"),
    period_from: firstOfMonth,
    period_to: todayIso,
    scheduled_value: contractValue,
    work_completed: phaseShare,
    materials_stored: 0,
    retention_pct: retentionPct,
    retention,
    amount_due: amountDue,
    narrative,
    status: "pending_gc_approval",
    draftedFrom: "milestone_complete",
    draftedAt: new Date().toISOString(),
    milestoneIndex: phaseIndex,
    milestoneName: phaseName,
    projectId: proj.id,
    projectName: (proj.name || "").split(" — ")[0] || proj.name || "Project",
    submitted_date: "",
    approved_date: "",
  };

  const updated = [...existing, newApp];
  try { localStorage.setItem(sk, JSON.stringify(updated)); } catch {}
  try { window.dispatchEvent(new Event("grok-payapps-updated")); } catch {}
  try { window._grokBroadcast?.("data_updated"); } catch {}

  return newApp;
}
// COs initialized by getInitialCOs()
// RFIs initialized by getInitialRFIs()

// --- LEADS ------------------------------------------------------------------
// LEADS initialized by getInitialLeads()

const LEAD_STATUSES = [
  {id:"new",label:"New Lead",color:"#60a5fa"},
  {id:"contacted",label:"Contacted",color:"#f59e0b"},
  {id:"site-walk",label:"Site Walk Scheduled",color:"#a78bfa"},
  {id:"estimated",label:"Estimate Sent",color:"#2dd4bf"},
  {id:"won",label:"Won",color:"#22c55e"},
  {id:"lost",label:"Lost",color:"#ef4444"},
];



// --- GROK DOCS MASTER LIST ----------------------------------------------------
const GROK_DOCS = [
  // CONTRACTS
  {id:"owner",       name:"Owner / GC Contract",            file:"Grok_Owner_Contract.docx",                    color:"#d4a843", phase:"Contract",   group:"contracts",
   when:"When Grok is the GC — sign before any work starts",
   who:"Grok + Owner",
   points:["Progress billing on the 1st monthly","Late payment = suspend work + 18% interest","Change order process built in","Full indemnity clause"]},
  {id:"sub",         name:"Subcontractor Agreement",         file:"Grok_Subcontractor_Agreement.docx",           color:"#60a5fa", phase:"Contract",   group:"contracts",
   when:"Before any sub sets foot on site",
   who:"Grok + each Sub",
   points:["Flow-down — Owner terms bind the sub","Pay-when-paid protection","Prevailing wage + DIR compliance","Broad indemnity"]},
  {id:"maint",       name:"Maintenance Agreement",           file:"Grok_Maintenance_Services_Agreement.docx",    color:"#2dd4bf", phase:"Contract",   group:"contracts",
   when:"On-call vendor list work at SROs / affordable housing",
   who:"Grok + Property Manager",
   points:["3 options: on-call / retainer / hybrid","SF rates $105–$195/hr pre-filled","15% markup on subs + materials","MOHCD standard compliant"]},
  // PRE-CONSTRUCTION
  {id:"walkthrough", name:"Site Walkthrough Checklist",      file:"Grok_Site_Walkthrough_Checklist.docx",        color:"#2dd4bf", phase:"Phase 1",    group:"pre-construction",
   when:"First visit with owner — before you quote anything",
   who:"Both sign at end of visit",
   points:["Document existing conditions","Red flags: mold, drains, pre-1978","Owner Q&A: leaks, mold, permits","Photo documentation"]},
  {id:"scope",       name:"Scope Clarification Checklist",   file:"Grok_Scope_Checklist.docx",                   color:"#a78bfa", phase:"Phase 1",    group:"pre-construction",
   when:"Before signing — sit down with owner, line by line",
   who:"Both initial every section",
   points:["What IS included vs excluded","Hidden condition protocol","Owner initials every exclusion","Prevents 'I thought that was included'"]},
  {id:"prestart",    name:"Pre-Start Checklist",             file:"Grok_Pre_Start_Checklist.docx",               color:"#22c55e", phase:"Phase 3",    group:"pre-construction",
   when:"Day before mobilization — internal green light",
   who:"Grok project lead",
   points:["Contract signed + deposit received","Permits pulled and posted","Site access confirmed","Crew briefed on stop-work rule"]},
  // FIELD
  {id:"notice",      name:"20-Day Preliminary Notice",       file:"Grok_20Day_Preliminary_Notice.docx",          color:"#f59e0b", phase:"Phase 3",    group:"field",
   when:"Day 1 of every job — no exceptions",
   who:"Grok serves Owner + Lender",
   points:["Certified mail to Owner + Lender","Miss 20 days = lose lien rights","Cal. Civil Code §8200","File proof of service permanently"]},
  {id:"co",          name:"Change Order Form",               file:"Grok_Change_Order_Form.docx",                 color:"#ef4444", phase:"Phase 4",    group:"field",
   when:"ANY work outside original scope found or requested",
   who:"Both sign BEFORE work proceeds",
   points:["Stop work immediately","Photo + notify owner within 2hrs","No work until CO is signed","Drain conflict, mold, water damage = CO"]},
  {id:"rfi",         name:"RFI Form",                        file:"Grok_RFI_Form.docx",                          color:"#60a5fa", phase:"Phase 4",    group:"field",
   when:"Plans/specs unclear — before touching affected work",
   who:"Grok submits → GC/Arch responds",
   points:["Never proceed on verbal answer","Routine 5 days / Urgent 48hrs / Critical same day","If response changes scope, CO follows","Documents the question trail"]},
  // BILLING
  {id:"cop",         name:"Certificate of Payment",          file:"Grok_Certificate_of_Payment.docx",            color:"#f59e0b", phase:"Phase 5",    group:"billing",
   when:"Submit to Owner/GC by the 1st of every month",
   who:"Grok submits — Owner approves",
   points:["G703 Schedule of Values","Retainage tracked (typically 10%)","Attach conditional waiver + sub waivers","Payment due within 30 days"]},
  // LIEN WAIVERS
  {id:"cond-prog",   name:"Conditional Waiver — Progress",   file:"Grok_Lien_Waiver_Conditional_Progress.docx",  color:"#22c55e", phase:"Phase 5",    group:"lien",
   when:"Attach to every monthly pay application",
   who:"Grok signs + attaches with COP",
   points:["§8132 — conditional on receiving payment","Not effective until check clears","Covers work through billing period","One per billing period"]},
  {id:"uncond-prog", name:"Unconditional Waiver — Progress", file:"Grok_Lien_Waiver_Unconditional_Progress.docx",color:"#22c55e", phase:"Phase 5",    group:"lien",
   when:"After prior period check clears your bank",
   who:"Grok signs after payment received",
   points:["§8134 — permanently waives lien rights for that period","NEVER sign before check clears","If check bounces after signing = lien rights gone","One per billing period"]},
  {id:"cond-final",  name:"Conditional Waiver — Final",      file:"Grok_Lien_Waiver_Conditional_Final.docx",     color:"#f59e0b", phase:"Phase 6",    group:"lien",
   when:"Submit with final pay application at closeout",
   who:"Grok signs with final COP",
   points:["§8136 — covers entire project","Still conditional on final payment","Submit with final pay application","Retainage must be included"]},
  {id:"uncond-final",name:"Unconditional Waiver — Final",    file:"Grok_Lien_Waiver_Unconditional_Final.docx",   color:"#ef4444", phase:"Phase 6",    group:"lien",
   when:"ONLY after ALL payments including retainage clear",
   who:"Grok signs last — closes project",
   points:["§8138 — permanently closes ALL lien rights","Last document on every project","Verify every payment cleared before signing","This ends your lien protection"]},
  {id:"sub-log",     name:"Sub Lien Waiver Collection Log",  file:"Grok_Sub_Lien_Waiver_Collection_Log.docx",    color:"#22c55e", phase:"Phase 5",    group:"lien",
   when:"Before every monthly pay application",
   who:"Subs sign — Grok tracks + attaches",
   points:["Track every sub waiver collected","Attach log + waivers to pay application","Protects Grok if sub claims non-payment","One per billing period per project"]},
];

function openDoc(file) {
  const url = '/docs/' + file;
  fetch(url, {method:'HEAD'})
    .then(r => {
      if (r.ok) {
        const a = document.createElement('a');
        a.href = url;
        a.download = file;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        alert('Document not installed yet.\n\nRun this command in Terminal:\n\ncp ~/Downloads/files/Grok_*.docx ~/Documents/grokapp/public/docs/\n\nThen refresh the app.');
      }
    })
    .catch(() => {
      const a = document.createElement('a');
      a.href = url;
      a.download = file;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    });
}

// --- VIEWS --------------------------------------------------------------------

// --- PROJECT MODAL (Add / Edit) -----------------------------------------------

// ─── PROJECT MEMBER PICKER (used inside ProjectModal) ────────────────────────
// Lets you assign company team members to a project with a project-level role
// Owner can set different roles per project — e.g. "PM on Job A, View Only on Job B"
function ProjectMemberPicker({members, onChange, companyId}) {
  const team = getTeam(companyId||window._grokCompanyId||"local");
  const [adding, setAdding] = React.useState(false);
  const [newEmail, setNewEmail] = React.useState("");
  const [newRole, setNewRole] = React.useState("pm");

  const addMember = () => {
    const existing = team.find(t=>t.email===newEmail);
    if (!existing && !newEmail) return;
    const member = {
      email: newEmail,
      name: existing?.name || newEmail.split("@")[0],
      role: newRole,
      addedAt: new Date().toLocaleDateString(),
    };
    if (!members.find(m=>m.email===newEmail)) {
      onChange([...members, member]);
    }
    setNewEmail(""); setAdding(false);
  };

  const remove = (email) => onChange(members.filter(m=>m.email!==email));
  const updateRole = (email, role) => onChange(members.map(m=>m.email===email?{...m,role}:m));

  return (
    <div style={{marginTop:14,padding:"14px 16px",background:T.bg2,border:"1px solid "+T.blue+"30",borderRadius:10}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:T.text}}>👥 Project Team</div>
          <div style={{fontSize:10,color:T.muted,marginTop:2}}>
            Assign team members to this project. Their role here overrides their company-wide role.
          </div>
        </div>
        <Btn small ghost onClick={()=>setAdding(p=>!p)}>+ Add Member</Btn>
      </div>

      {adding&&(
        <div style={{background:T.bg1,borderRadius:8,padding:"10px 12px",marginBottom:10,border:"1px solid "+T.subtle}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr auto",gap:8,alignItems:"end"}}>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Email or Name</div>
              {team.length > 0 ? (
                <select value={newEmail} onChange={e=>setNewEmail(e.target.value)}
                  style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}>
                  <option value="">Select team member...</option>
                  {team.filter(t=>!members.find(m=>m.email===t.email)).map(t=>(
                    <option key={t.email} value={t.email}>{t.name} ({t.email})</option>
                  ))}
                </select>
              ) : (
                <input value={newEmail} onChange={e=>setNewEmail(e.target.value)} placeholder="email@company.com"
                  style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
              )}
            </div>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Project Role</div>
              <select value={newRole} onChange={e=>setNewRole(e.target.value)}
                style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}>
                {Object.entries(TEAM_ROLES).filter(([k])=>k!=="owner").map(([key,role])=>(
                  <option key={key} value={key}>{role.label}</option>
                ))}
              </select>
            </div>
            <Btn small primary disabled={!newEmail} onClick={addMember} style={{alignSelf:"end"}}>Add</Btn>
          </div>
          {team.length===0&&(
            <div style={{fontSize:10,color:T.muted,marginTop:6}}>
              No team members set up yet. Invite team members in <strong>Company Settings → Team</strong> first.
            </div>
          )}
        </div>
      )}

      {members.length===0 ? (
        <div style={{fontSize:11,color:T.muted,padding:"8px 0",lineHeight:1.6}}>
          No members assigned yet. You (Owner) have full access by default.
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {members.map(member=>{
            const role = TEAM_ROLES[member.role]||TEAM_ROLES.viewer;
            return (
              <div key={member.email} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:T.bg1,borderRadius:7,border:"1px solid "+T.subtle}}>
                <div style={{width:28,height:28,borderRadius:"50%",background:role.color+"20",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:role.color,flexShrink:0}}>
                  {member.name.slice(0,2).toUpperCase()}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:11,fontWeight:600,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{member.name}</div>
                  <div style={{fontSize:9,color:T.muted}}>{member.email}</div>
                </div>
                <select value={member.role} onChange={e=>updateRole(member.email,e.target.value)}
                  style={{fontSize:10,fontWeight:700,background:role.color+"15",color:role.color,border:"1px solid "+role.color+"40",borderRadius:8,padding:"3px 7px",cursor:"pointer",fontFamily:"inherit"}}>
                  {Object.entries(TEAM_ROLES).filter(([k])=>k!=="owner").map(([key,r])=>(
                    <option key={key} value={key}>{r.label}</option>
                  ))}
                </select>
                <button onClick={()=>remove(member.email)}
                  style={{fontSize:10,background:"none",color:T.muted,border:"1px solid "+T.subtle,borderRadius:6,padding:"3px 7px",cursor:"pointer"}}>
                  Remove
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── PROJECT TEAM MANAGER (Project detail → Team tab) ─────────────────────────
// Full team management within a project — add/remove members, change roles,
// see who has access to what on THIS project specifically
function ProjectTeamManager({proj, session, onUpdate}) {
  const [members, setMembers] = React.useState(proj?.members||[]);
  const [adding, setAdding] = React.useState(false);
  const [newEmail, setNewEmail] = React.useState("");
  const [newRole, setNewRole] = React.useState("pm");
  const team = getTeam(session?.companyId);

  const save = (updated) => {
    setMembers(updated);
    if (onUpdate) onUpdate({...proj, members: updated});
  };

  const add = () => {
    if (!newEmail) return;
    const existing = team.find(t=>t.email===newEmail);
    if (members.find(m=>m.email===newEmail)) { alert("Already on this project."); return; }
    save([...members, {email:newEmail, name:existing?.name||newEmail.split("@")[0], role:newRole, addedAt:new Date().toLocaleDateString()}]);
    setNewEmail(""); setAdding(false);
  };

  const remove = (email) => {
    if (!true) return;
    save(members.filter(m=>m.email!==email));
  };

  const updateRole = (email, role) => save(members.map(m=>m.email===email?{...m,role}:m));

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>Project Team</div>
          <div style={{fontSize:10,color:T.muted,marginTop:2}}>
            {members.length} member{members.length!==1?"s":""} assigned to this project ·
            Roles here override company-wide roles for this project only
          </div>
        </div>
        {can(session,"team")&&(
          <Btn small primary onClick={()=>setAdding(p=>!p)}>
            <Ic d={ICONS.plus} size={11}/> Add Member
          </Btn>
        )}
      </div>

      {/* Add form */}
      {adding&&(
        <div style={{background:T.bg2,border:"1px solid "+T.blue+"30",borderRadius:10,padding:"14px 16px",marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Add to Project</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Team Member</div>
              {team.length > 0 ? (
                <select value={newEmail} onChange={e=>setNewEmail(e.target.value)}
                  style={{width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}>
                  <option value="">Choose team member...</option>
                  {team.filter(t=>!members.find(m=>m.email===t.email)).map(t=>(
                    <option key={t.email} value={t.email}>{t.name} — {TEAM_ROLES[t.role]?.label||t.role}</option>
                  ))}
                </select>
              ) : (
                <div style={{fontSize:11,color:T.muted,padding:"8px 0",lineHeight:1.6}}>
                  No company team members yet. Invite them in <strong>Company Settings → Team</strong>.
                </div>
              )}
            </div>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Role on This Project</div>
              <select value={newRole} onChange={e=>setNewRole(e.target.value)}
                style={{width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}>
                {Object.entries(TEAM_ROLES).filter(([k])=>k!=="owner").map(([key,role])=>(
                  <option key={key} value={key}>{role.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{background:T.bg3,borderRadius:7,padding:"8px 10px",marginBottom:10,fontSize:10,color:T.muted,lineHeight:1.6}}>
            <strong style={{color:TEAM_ROLES[newRole]?.color}}>{TEAM_ROLES[newRole]?.label}:</strong> {TEAM_ROLES[newRole]?.desc}
          </div>
          <div style={{display:"flex",gap:7}}>
            <Btn ghost small onClick={()=>setAdding(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
            <Btn primary small disabled={!newEmail} onClick={add} style={{flex:2,justifyContent:"center"}}>Add to Project</Btn>
          </div>
        </div>
      )}

      {/* Permission grid */}
      <div style={{background:T.bg2,borderRadius:9,padding:"10px 14px",marginBottom:14,overflowX:"auto"}}>
        <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>What each role can see on this project</div>
        <div style={{display:"grid",gridTemplateColumns:"1.2fr repeat(5,1fr)",gap:3,minWidth:400,fontSize:9}}>
          {["Role","Finance","Margin","Edit COs","Edit RFIs","Blueprints"].map(h=>(
            <div key={h} style={{fontWeight:700,color:T.muted,padding:"3px 5px"}}>{h}</div>
          ))}
          {Object.entries(TEAM_ROLES).map(([key,role])=>(
            <React.Fragment key={key}>
              <div style={{color:role.color,fontWeight:700,padding:"3px 5px"}}>{role.label}</div>
              <div style={{padding:"3px 5px",color:role.canSee.financials?T.green:T.red}}>{role.canSee.financials?"✓":"✗"}</div>
              <div style={{padding:"3px 5px",color:role.canSee.margin?T.green:T.red}}>{role.canSee.margin?"✓":"✗"}</div>
              <div style={{padding:"3px 5px",color:role.canEdit.cos?T.green:T.muted}}>{role.canEdit.cos?"Edit":"View"}</div>
              <div style={{padding:"3px 5px",color:role.canEdit.rfis?T.green:T.muted}}>{role.canEdit.rfis?"Edit":"View"}</div>
              <div style={{padding:"3px 5px",color:T.green}}>✓</div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Member list */}
      {/* Owner always shown — cannot be removed */}
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:T.gold+"12",borderRadius:9,border:"1px solid "+T.gold+"40",marginBottom:6}}>
        <div style={{width:36,height:36,borderRadius:"50%",background:T.gold+"30",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:T.gold,flexShrink:0}}>
          {(session?.ownerName||session?.email||"GC").slice(0,2).toUpperCase()}
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text}}>{session?.ownerName||session?.email} <span style={{fontSize:9,color:T.gold,fontWeight:700,background:T.gold+"20",borderRadius:6,padding:"1px 6px",marginLeft:4}}>YOU</span></div>
          <div style={{fontSize:10,color:T.muted}}>{session?.email} · Account Owner</div>
          <div style={{fontSize:9,color:T.gold,marginTop:2}}>Full access · Finance · Margin · All settings · Cannot be removed</div>
        </div>
        <span style={{fontSize:10,fontWeight:700,color:T.gold,background:T.gold+"20",borderRadius:8,padding:"4px 10px"}}>Owner</span>
      </div>

      {members.length===0 ? (
        <div style={{textAlign:"center",padding:"24px",color:T.muted,background:T.bg2,borderRadius:10,border:"2px dashed "+T.subtle,marginTop:8}}>
          <div style={{fontSize:11,lineHeight:1.7}}>
            No other members assigned yet.<br/>
            Add team members to control what they can see and edit on this project.
          </div>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {members.map(member=>{
            const role = TEAM_ROLES[member.role]||TEAM_ROLES.viewer;
            return (
              <div key={member.email} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",background:T.bg1,borderRadius:9,border:"1px solid "+T.subtle}}>
                <div style={{width:36,height:36,borderRadius:"50%",background:role.color+"20",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:role.color,flexShrink:0}}>
                  {member.name.slice(0,2).toUpperCase()}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:700,color:T.text}}>{member.name}</div>
                  <div style={{fontSize:10,color:T.muted,marginTop:1}}>{member.email} · Added {member.addedAt}</div>
                  <div style={{fontSize:9,color:role.color,marginTop:2}}>
                    {role.canSee.financials?"Can see financials":"No financials"} ·
                    {role.canSee.margin?" Can see margin":" No margin"} ·
                    {role.canEdit.projects?" Can edit project":" View only"}
                  </div>
                </div>
                {can(session,"team")&&(
                  <select value={member.role} onChange={e=>updateRole(member.email,e.target.value)}
                    style={{fontSize:10,fontWeight:700,background:role.color+"15",color:role.color,border:"1px solid "+role.color+"40",borderRadius:8,padding:"4px 8px",cursor:"pointer",fontFamily:"inherit"}}>
                    {Object.entries(TEAM_ROLES).filter(([k])=>k!=="owner").map(([key,r])=>(
                      <option key={key} value={key}>{r.label}</option>
                    ))}
                  </select>
                )}
                <div style={{display:"flex",flexDirection:"column",gap:3,fontSize:8}}>
                  <span style={{color:role.canSee.financials?T.green:T.muted}}>Finance: {role.canSee.financials?"✓":"✗"}</span>
                  <span style={{color:role.canSee.margin?T.green:T.muted}}>Margin: {role.canSee.margin?"✓":"✗"}</span>
                </div>
                {can(session,"team")&&(
                  <button onClick={()=>remove(member.email)}
                    style={{fontSize:10,background:"none",color:T.muted,border:"1px solid "+T.subtle,borderRadius:6,padding:"4px 8px",cursor:"pointer",flexShrink:0}}>
                    Remove
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function ProjectModal({project, prefill, onClose, onSave}) {
  const isEdit = !!project;
  const src = project || prefill || {};
  const [form, setForm] = useState({
    name: src.name || "",
    addr: src.addr || "",
    client: src.client || "",
    type: src.type || "",
    value: src.value || "",
    status: src.status || "active",
    desc: src.desc || "",
    notes: src.notes || "",
    // Key contacts for CO/RFI approvals
    owner_name: src.owner_name || "",
    owner_email: src.owner_email || "",
    owner_phone: src.owner_phone || "",
    architect_name: src.architect_name || "",
    architect_email: src.architect_email || "",
    architect_phone: src.architect_phone || "",
    engineer_name: src.engineer_name || "",
    engineer_email: src.engineer_email || "",
    // Building department info
    permit_no: src.permit_no || "",
    building_dept: src.building_dept || "",
    building_dept_phone: src.building_dept_phone || "",
    building_dept_url: src.building_dept_url || "",
    inspector_name: src.inspector_name || "",
    inspection_cutoff: src.inspection_cutoff || "",
    // Project team — override company-level roles per project
    members: src.members || [], // [{email, name, role, addedAt}]
  });
  const f = (k,v) => setForm(p=>({...p,[k]:v}));
  const valid = form.name.trim() && form.addr.trim() && form.client.trim();

  return (
    <Modal title={isEdit ? "Edit Project" : "New Project"} onClose={onClose} wide>
      <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
        <div>
          <Field label="Project Name" value={form.name} onChange={v=>f("name",v)} placeholder="466 Turk St — SRO Renovation"/>
          <Field label="Property Address" value={form.addr} onChange={v=>f("addr",v)} placeholder="Tenderloin, SF"/>
          <Field label="Owner / Client / GC" value={form.client} onChange={v=>f("client",v)} placeholder="NCR Construction"/>
          <Select label="Project Type" value={form.type} onChange={v=>f("type",v)} options={["Residential — Single Family","Residential — Multi-Family","Residential — SRO / Affordable","Residential — ADU / Addition","Residential — Condo / TIC","Commercial — Tenant Improvement","Commercial — Office","Commercial — Retail / Restaurant","Commercial — Industrial / Warehouse","Commercial — Medical / Lab","Commercial — Hotel / Hospitality","Mixed-Use","New Construction — Residential","New Construction — Commercial","Fix & Flip","Senior Housing","Other"]}/>
          <Select label="Status" value={form.status} onChange={v=>f("status",v)} options={[{value:"active",label:"Active"},{value:"bidding",label:"Bidding / Pre-Construction"},{value:"completed",label:"Completed"},{value:"archived",label:"Archived"}]}/>
        </div>
        <div>
          <Field label="Contract Value ($)" value={form.value} onChange={v=>f("value",v)} placeholder="420000" type="number"/>
          <Field label="Scope Description" value={form.desc} onChange={v=>f("desc",v)} placeholder="Describe the work scope..." rows={4}/>
          <Field label="Internal Notes" value={form.notes} onChange={v=>f("notes",v)} placeholder="20-Day Notice, prevailing wage, key contacts..." rows={3}/>
        </div>
      </div>
      {/* Building Department Info */}
      <div style={{marginTop:12,padding:"14px 16px",background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:10}}>
        <div style={{fontSize:12,fontWeight:700,color:T.amber,marginBottom:10}}>🏛 Building Department & Permit Setup</div>
        <div style={{fontSize:11,color:T.muted,marginBottom:10,lineHeight:1.6}}>
          Set this once — AI PM uses it to know which department to call, inspection cutoffs, and online scheduling links for this project.
        </div>
        <div style={{display:"grid",gridTemplateColumns:window.innerWidth<768?"1fr":"1fr 1fr",gap:8}}>
          {[
            ["permit_no","Permit Number","202400123456"],
            ["building_dept","Building Department","SF Dept of Building Inspection"],
            ["building_dept_phone","Inspection Phone","(628) 652-3700"],
            ["building_dept_url","Online Scheduling URL","aca.accela.com/CCSF"],
            ["inspector_name","Assigned Inspector (if known)","John Smith"],
            ["inspection_cutoff","Request Cutoff","By 3:30PM for next-day"],
          ].map(([k,l,ph])=>(
            <div key={k}>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>{l}</div>
              <input value={form[k]||""} onChange={e=>f(k,e.target.value)} placeholder={ph}
                style={{width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
            </div>
          ))}
        </div>
      </div>
      {/* Project Team Assignment */}
      <ProjectMemberPicker members={form.members} onChange={v=>f('members',v)} companyId={window._grokCompanyId}/>

      {/* Project Contacts — CO/RFI approvals, client updates, blueprint submittals */}
      <div style={{marginTop:12,padding:"14px 16px",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:10}}>
        <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:4}}>👤 Project Contacts</div>
        <div style={{fontSize:11,color:T.muted,marginBottom:10,lineHeight:1.6}}>
          Used for CO approvals, RFI responses, and client progress updates. Add now or edit anytime from the project's <strong>Client</strong> tab.
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
          <Field label="Owner / Client Name" value={form.owner_name||""} onChange={v=>f("owner_name",v)} placeholder="Kim Family Trust"/>
          <Field label="Owner Email (CO approvals)" value={form.owner_email||""} onChange={v=>f("owner_email",v)} placeholder="owner@email.com"/>
          <Field label="Owner Phone" value={form.owner_phone||""} onChange={v=>f("owner_phone",v)} placeholder="(415) 555-0100"/>
          <Field label="Architect Email (RFI / submittals)" value={form.architect_email||""} onChange={v=>f("architect_email",v)} placeholder="arch@firm.com"/>
          <Field label="Architect Name / Firm" value={form.architect_name||""} onChange={v=>f("architect_name",v)} placeholder="Smith Architecture"/>
          <Field label="Architect Phone" value={form.architect_phone||""} onChange={v=>f("architect_phone",v)} placeholder="(415) 555-0200"/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:window.innerWidth<768?"1fr":"1fr 1fr",gap:8}}>
          <Field label="Structural Engineer (optional)" value={form.engineer_name||""} onChange={v=>f("engineer_name",v)} placeholder="Jones Engineering"/>
          <Field label="Engineer Email" value={form.engineer_email||""} onChange={v=>f("engineer_email",v)} placeholder="engineer@firm.com"/>
        </div>
      </div>
      {!isEdit && (
        <div style={{marginTop:12,background:T.tealBg,borderLeft:`3px solid ${T.teal}`,borderRadius:8,padding:"10px 14px",fontSize:12,color:T.muted}}>
          <strong style={{color:T.teal}}>After saving:</strong> Complete the Site Walkthrough inside the project before estimating. The walkthrough is the first step on every job.
        </div>
      )}
      <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:20}}>
        <Btn ghost onClick={onClose}>Cancel</Btn>
        <Btn primary disabled={!valid} onClick={()=>{
          const saved = {
            ...project,
            id: project?.id || Date.now(),
            name: form.name.trim(),
            addr: form.addr.trim(),
            client: form.client.trim(),
            type: form.type,
            value: parseFloat(form.value)||0,
            status: form.status,
            owner_name: form.owner_name.trim(),
            owner_email: form.owner_email.trim(),
            owner_phone: form.owner_phone.trim(),
            architect_name: form.architect_name.trim(),
            architect_email: form.architect_email.trim(),
            architect_phone: form.architect_phone.trim(),
            engineer_name: form.engineer_name.trim(),
            engineer_email: form.engineer_email.trim(),
            desc: form.desc.trim(),
            members: form.members||[],
            notes: form.notes.trim(),
            pct: project?.pct || 0,
            phases: project?.phases || [0,0,0,0,0,0,0],
            openCOs: project?.openCOs || 0,
            openRFIs: project?.openRFIs || 0,
            walk: project?.walk || {access:{},plumbing:{},walls:{},owner:{}},
            redFlags: project?.redFlags || {},
            walkComplete: project?.walkComplete || false,
            // Building dept
            permit_no: form.permit_no,
            building_dept: form.building_dept,
            building_dept_phone: form.building_dept_phone,
            building_dept_url: form.building_dept_url,
            inspector_name: form.inspector_name,
            inspection_cutoff: form.inspection_cutoff,
          };
          onSave(saved);
          onClose();
        }}>{isEdit ? "Save Changes" : "Create Project"}</Btn>
      </div>
    </Modal>
  );
}

// --- PROJECTS VIEW ------------------------------------------------------------
function ProjectsView({projects, setProjects, setView, setActiveProject, session}) {
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState("active");

  // One-shot prefill hint dropped by "Create Project from Blueprint" — read on
  // mount, open the modal pre-populated, and stash the blueprint to attach on
  // save. Cleared as soon as it's consumed so a refresh doesn't re-open.
  const [prefill, setPrefill] = React.useState(null);
  const [prefillBlueprints, setPrefillBlueprints] = React.useState(null);
  React.useEffect(() => {
    try {
      const p = sessionStorage.getItem("grok_project_prefill");
      const b = sessionStorage.getItem("grok_project_prefill_blueprints");
      if (p) {
        setPrefill(JSON.parse(p));
        setShowModal(true);
        sessionStorage.removeItem("grok_project_prefill");
      }
      if (b) {
        setPrefillBlueprints(JSON.parse(b));
        sessionStorage.removeItem("grok_project_prefill_blueprints");
      }
    } catch {}
  }, []);

  const filtered = projects.filter(p => p.id && p.name).filter(p => filter === "all" || p.status === filter);

  const addProject = (p) => {
    const np = {
      ...p,
      id: p.id || (window._grokCompanyId||"co") + "_" + Date.now(),
      // ARIA auto-drafts the §8204 Preliminary Notice on project create.
      // The actual form is generated on-demand when GC prints it — we just
      // track the metadata + served status here so the banner can remind
      // them until they mark it served.
      preliminaryNotice: p.preliminaryNotice || {
        generated_at: new Date().toISOString(),
        deadline: new Date(Date.now() + 20*86400000).toISOString().slice(0, 10),
        served: false,
        served_to: [],
      },
    };
    PROJECTS = [np, ...PROJECTS];
    setProjects([np, ...projects]);
    // If a blueprint was attached via the create-from-blueprint flow, persist
    // it to the new project's blueprint store so it shows up in the project's
    // Blueprints tab.
    if (prefillBlueprints?.length) {
      try {
        const key = "grok_blueprints_" + np.id;
        const existing = (() => { try { return JSON.parse(localStorage.getItem(key)||"[]"); } catch { return []; }})();
        localStorage.setItem(key, JSON.stringify([...existing, ...prefillBlueprints]));
      } catch {}
      setPrefillBlueprints(null);
      // Drop the user straight into the new project so the link feels real.
      if (setActiveProject) setActiveProject(np);
      if (setView) setView("project-detail");
    }
    setPrefill(null);
  };
  const pcc = p => p.pct>70?T.green:p.pct>30?T.amber:T.blue;

  return (
    <div style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>Projects</div>
          <div style={{fontSize:12,color:T.muted,marginTop:3}}>{projects.filter(p=>p.status==="active").length} active · San Francisco Bay Area</div>
        </div>
        <Btn primary onClick={()=>setShowModal(true)}><Ic d={ICONS.plus} size={13}/>New Project</Btn>
      </div>

      {/* Filter */}
      <div style={{display:"flex",gap:4,background:T.bg2,borderRadius:9,padding:3,width:"fit-content",marginBottom:16}}>
        {[{v:"active",l:"Active"},{v:"bidding",l:"Bidding"},{v:"completed",l:"Completed"},{v:"all",l:"All"}].map(f=>(
          <button key={f.v} onClick={()=>setFilter(f.v)} style={{padding:"5px 14px",borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:filter===f.v?T.gold:T.bg2,color:filter===f.v?T.bg0:T.muted,transition:"all 0.15s"}}>{f.l}</button>
        ))}
      </div>

      {filtered.length===0 && (
        <div style={{textAlign:"center",padding:"60px 20px",color:T.muted}}>
          <div style={{fontSize:14,marginBottom:12}}>No {filter} projects</div>
          <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}}>
            <Btn primary onClick={()=>setShowModal(true)}><Ic d={ICONS.plus} size={13}/>Add First Project</Btn>
            <button onClick={()=>{
              const cid = session?.companyId || window._grokCompanyId || "local";
              // Clear deleted demos list so they can be loaded fresh
              localStorage.removeItem("grok_deleted_demos_"+cid);
              const demo = [
                {id:"demo_1",name:"466 Turk St — SRO Renovation",addr:"Tenderloin, SF",client:"NCR Construction",pct:68,value:420000,type:"SRO",status:"active",phases:[100,100,100,55,0,0,0],openCOs:2,openRFIs:3,desc:"Full gut renovation of 12 SRO units.",walk:{access:{s1:true,s2:true,s3:true,s4:true,s5:true},plumbing:{p1:true,p2:false,p3:true,p4:true,p5:true},walls:{w1:true,w2:false,w3:true,w4:false,w5:true},owner:{q1:true,q2:true,q3:true,q4:true,q5:false}},redFlags:{"Drain location conflict":true,"Pre-1978 building":true},walkComplete:false,notes:"20-Day Notice served Apr 1."},
                {id:"demo_2",name:"1200 Mission — Affordable Housing TI",addr:"SoMa, SF",client:"Direct Owner",pct:32,value:285000,type:"Affordable Housing",status:"active",phases:[100,100,40,0,0,0,0],openCOs:1,openRFIs:1,desc:"Tenant improvement of 6 affordable units.",walk:{access:{s1:true,s2:true,s3:true,s4:true,s5:true},plumbing:{p1:true,p2:true,p3:true,p4:true,p5:true},walls:{w1:true,w2:true,w3:true,w4:true,w5:true},owner:{q1:true,q2:true,q3:true,q4:true,q5:true}},redFlags:{},walkComplete:true,notes:"MOHCD funding."},
              ];
              localStorage.setItem("grok_projects_"+cid, JSON.stringify(demo));
              setProjects(demo);
            }} style={{fontSize:12,fontWeight:600,background:T.bg2,color:T.muted,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"8px 16px",cursor:"pointer"}}>
              🧪 Load Demo Projects
            </button>
          </div>
        </div>
      )}
      {filtered.length>0&&projects.every(p=>p.id==="demo_1"||p.id==="demo_2")&&(
        <div style={{background:T.amberBg,border:`1px solid ${T.amber}30`,borderRadius:8,padding:"8px 16px",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:11,color:T.amber}}>🧪 Demo projects loaded — for testing only</div>
          <button onClick={()=>{
            const cid = session?.companyId || window._grokCompanyId || "local";
            localStorage.removeItem("grok_projects_"+cid);
            setProjects([]);
          }} style={{fontSize:11,fontWeight:700,background:"none",border:`1px solid ${T.amber}`,borderRadius:6,padding:"3px 10px",color:T.amber,cursor:"pointer"}}>
            Clear Demo
          </button>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        {filtered.map(p=>(
          <div key={p.id} style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:12,padding:"16px 18px",cursor:"pointer",transition:"border-color 0.15s"}}
            onClick={()=>{setActiveProject(p);setView("project-detail");}}
            onMouseEnter={e=>e.currentTarget.style.borderColor=T.goldDim}
            onMouseLeave={e=>e.currentTarget.style.borderColor=T.subtle}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:3}}>{p.name}</div>
                <div style={{fontSize:11,color:T.muted}}>{p.addr} · {p.client}</div>
              </div>
              <div style={{display:"flex",gap:6,flexShrink:0,marginLeft:10,alignItems:"center"}}>
                <span style={{fontSize:9,fontWeight:700,background:T.goldBg,color:T.gold,border:`1px solid ${T.goldDim}`,borderRadius:20,padding:"2px 8px"}}>{p.type}</span>
                {p.status!=="active"&&<span style={{fontSize:9,fontWeight:700,background:T.bg3,color:T.muted,borderRadius:20,padding:"2px 8px",textTransform:"capitalize"}}>{p.status}</span>}
                <button
                  onClick={e=>{
                    e.stopPropagation();
                    if(window.confirm(`Delete "${p.name}"? This cannot be undone.`)){
                      DataStore.deleteProject(p.id, session);
                      setProjects(projects.filter(x=>x.id!==p.id));
                    }
                  }}
                  title="Delete project"
                  style={{background:"transparent",border:"none",cursor:"pointer",color:T.muted,fontSize:14,padding:"0 3px",lineHeight:1,borderRadius:4}}
                  onMouseEnter={e=>e.currentTarget.style.color=T.red}
                  onMouseLeave={e=>e.currentTarget.style.color=T.muted}>✕</button>
              </div>
            </div>

            {/* Walk status */}
            <div style={{display:"flex",gap:8,marginBottom:10}}>
              <div style={{display:"flex",alignItems:"center",gap:5,padding:"3px 9px",borderRadius:20,background:p.walkComplete?T.greenBg:T.amberBg,border:`1px solid ${p.walkComplete?T.green+"40":T.amber+"40"}`}}>
                <div style={{width:5,height:5,borderRadius:"50%",background:p.walkComplete?T.green:T.amber}}/>
                <span style={{fontSize:9,fontWeight:700,color:p.walkComplete?T.green:T.amber}}>{p.walkComplete?"Walkthrough done":"Walkthrough pending"}</span>
              </div>
              {(()=>{
                const cid = session?.companyId || window._grokCompanyId || "local";
                const liveRFIs = JSON.parse(localStorage.getItem("grok_rfis_global_"+cid+(p.id?"_"+p.id:""))||"[]").filter(r=>r.status!=="closed").length;
                const liveCOs  = JSON.parse(localStorage.getItem("grok_cos_global_"+cid)||"[]").filter(c=>c.status==="pending").length;
                const rfiCount = liveRFIs || 0;
                const coCount  = liveCOs  || 0;
                return (<>
                  {coCount>0&&<div style={{display:"flex",alignItems:"center",gap:5,padding:"3px 9px",borderRadius:20,background:T.redBg,border:`1px solid ${T.red}30`}}><span style={{fontSize:9,fontWeight:700,color:T.red}}>{coCount} open CO{coCount>1?"s":""}</span></div>}
                  {rfiCount>0&&<div style={{display:"flex",alignItems:"center",gap:5,padding:"3px 9px",borderRadius:20,background:T.amberBg,border:`1px solid ${T.amber}30`}}><span style={{fontSize:9,fontWeight:700,color:T.amber}}>{rfiCount} open RFI{rfiCount>1?"s":""}</span></div>}
                </>);
              })()}
            </div>

            <div style={{display:"flex",gap:10,marginBottom:10}}>
              {[{v:"$"+(((p.value||p.budget||0)/1000).toFixed(0))+"K",l:"Contract"},{v:p.pct+"%",l:"Complete",c:pcc(p)}].map(m=>(
                <div key={m.l} style={{background:T.bg2,borderRadius:6,padding:"5px 10px",textAlign:"center"}}>
                  <div style={{fontSize:16,fontWeight:800,color:m.c||T.text}}>{m.v}</div>
                  <div style={{fontSize:9,color:T.muted}}>{m.l}</div>
                </div>
              ))}
            </div>
            <div style={{height:4,background:T.bg3,borderRadius:2}}>
              <div style={{height:"100%",width:p.pct+"%",background:pcc(p),borderRadius:2,transition:"width 0.4s"}}/>
            </div>
            <div style={{display:"flex",gap:3,marginTop:8}}>
              {["Demo","Framing","MEP","Drywall","Tile","Paint","Closeout"].map((ph,i)=>(
                <div key={i} title={ph+": "+((p.phases||[])[i]||0)+"%"} style={{flex:1,height:4,borderRadius:2,background:(p.phases||[])[i]===100?T.green:(p.phases||[])[i]>0?T.gold:T.bg3}}/>
              ))}
            </div>
          </div>
        ))}
      </div>

      {showModal&&<ProjectModal prefill={prefill} onClose={()=>{setShowModal(false);setPrefill(null);setPrefillBlueprints(null);}} onSave={addProject}/>}
    </div>
  );
}

// --- PROJECT DETAIL -----------------------------------------------------------

// Confirm-by-typing delete component
// ─── BUDGET VS ACTUAL TRACKER ─────────────────────────────────────────────────

// Get O&P rates from company settings (falls back to KB defaults)
function getOPRates(projectType) {
  try {
    const s = localStorage.getItem(settingsKey(window._grokCompanyId||"local"));
    if (!s) return null;
    const settings = JSON.parse(s);
    // Map project type string to settings key
    const typeMap = {
      "SRO":"sro","SRO / Affordable":"sro","Affordable Housing":"sro",
      "Senior":"senior","ADA":"senior","Senior / ADA":"senior",
      "Multi-Family":"multifamily","Apartment":"multifamily",
      "Residential":"residential","Kitchen":"kitchen","Bathroom":"bathroom",
      "ADU":"adu","Garage":"adu","Commercial":"commercial","TI":"commercial"
    };
    const key = Object.keys(typeMap).find(k=>projectType?.includes(k));
    if (key) return settings.op[typeMap[key]];
    return settings.op["residential"];
  } catch { return null; }
}

function BudgetTracker({proj, projCOs, session}) {
  const storageKey = "grok_budget_" + proj?.id;
  // Never expose margin/profit to subs, clients, architects per playbook
  const canSeeMargin = canSee(session,'margin');

  const [budget, setBudget] = useState(()=>{
    try{const s=localStorage.getItem(storageKey);if(s)return JSON.parse(s);}catch(e){}
    const sets = (()=>{try{return JSON.parse(localStorage.getItem(SETTINGS_KEY)||"{}");}catch{return {};}})();
    const op = sets.op?.residential||{};
    return {
      markup_pct:      op.markup||26,
      gc_fee_pct:      op.gc_fee||8,
      contingency_pct: op.contingency||10,
      insurance_pct:   op.insurance||2.5,
      overhead_pct:    op.overhead||7,
      line_items: [
        {id:1, trade:"Demo & Protection",     budgeted:8500,  spent:8500,  status:"complete"},
        {id:2, trade:"Plumbing (Sub)",         budgeted:22000, spent:18400, status:"in-progress"},
        {id:3, trade:"Electrical (Sub)",       budgeted:18500, spent:0,     status:"not-started"},
        {id:4, trade:"Drywall & Paint",        budgeted:14200, spent:6800,  status:"in-progress"},
        {id:5, trade:"Tile",                   budgeted:11000, spent:0,     status:"not-started"},
        {id:6, trade:"Flooring",               budgeted:9800,  spent:0,     status:"not-started"},
        {id:7, trade:"Cabinets & Countertops", budgeted:16400, spent:16400, status:"complete"},
        {id:8, trade:"Doors & Hardware",       budgeted:4200,  spent:0,     status:"not-started"},
        {id:9, trade:"General Conditions",     budgeted:8800,  spent:4200,  status:"in-progress"},
        {id:10,trade:"Permits & Fees",         budgeted:3200,  spent:3200,  status:"complete"},
      ]
    };
  });

  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showFees, setShowFees] = useState(false);
  const [newLine, setNewLine] = useState({trade:"",budgeted:"",spent:"",status:"not-started"});

  const save = (u) => { setBudget(u); try{localStorage.setItem(storageKey,JSON.stringify(u));}catch(e){} };
  const updateFee = (f,v) => save({...budget,[f]:parseFloat(v)||0});
  const updateLine = (id,f,v) => {
    save({...budget,line_items:budget.line_items.map(l=>l.id===id?{...l,[f]:f==="trade"||f==="status"?v:parseFloat(v)||0}:l)});
    setEditing(null);
  };
  const addLine = () => {
    if(!newLine.trade)return;
    save({...budget,line_items:[...budget.line_items,{...newLine,id:Date.now(),budgeted:parseFloat(newLine.budgeted)||0,spent:parseFloat(newLine.spent)||0}]});
    setNewLine({trade:"",budgeted:"",spent:"",status:"not-started"});
    setShowAdd(false);
  };

  // core numbers — all reactive from budget state
  const approvedCOs  = projCOs.filter(c=>c.status==="approved");
  const pendingCOs   = projCOs.filter(c=>c.status==="pending");
  const coTotal      = approvedCOs.reduce((s,c)=>s+(c.amount||0),0);
  const pendingTotal = pendingCOs.reduce((s,c)=>s+(c.amount||0),0);

  const directBudget = budget.line_items.reduce((s,l)=>s+(l.budgeted||0),0) + coTotal;
  const directSpent  = budget.line_items.reduce((s,l)=>s+(l.spent||0),0);
  const directLeft   = directBudget - directSpent;

  const markup   = directBudget*(budget.markup_pct/100);
  const afterMkp = directBudget+markup;
  const gcFee    = afterMkp*(budget.gc_fee_pct/100);
  const ins      = afterMkp*(budget.insurance_pct/100);
  const ovhd     = afterMkp*(budget.overhead_pct/100);
  const beforeCtg= afterMkp+gcFee+ins+ovhd;
  const ctg      = beforeCtg*(budget.contingency_pct/100);
  const contract = beforeCtg+ctg;

  const totalProfit  = contract - directBudget;
  const profitToDate = contract - directSpent;
  const marginPct    = contract>0 ? totalProfit/contract*100 : 0;
  const costPct      = directBudget>0 ? Math.min(100,directSpent/directBudget*100) : 0;

  // Net Profit = Gross Margin - overhead - insurance - GC time/PM cost - contingency used
  const grossMarginAmt = contract - directBudget;
  const ovhdDeduct     = afterMkp*(budget.overhead_pct/100);
  const insDeduct      = afterMkp*(budget.insurance_pct/100);
  const gcTimeDeduct   = budget.gc_time_cost || (contract * 0.03); // default 3% for PM time
  const ctgUsed        = budget.contingency_used || 0;
  const netProfit      = grossMarginAmt - ovhdDeduct - insDeduct - gcTimeDeduct - ctgUsed;
  const netProfitPct   = contract > 0 ? (netProfit/contract)*100 : 0;

  // Profit alert thresholds per doc
  const profitAlert = netProfitPct >= 20 ? "green" : netProfitPct >= 10 ? "yellow" : "red";
  const alertColor  = profitAlert==="green" ? T.green : profitAlert==="yellow" ? T.amber : T.red;
  const alertLabel  = profitAlert==="green" ? "✅ Healthy Job" : profitAlert==="yellow" ? "⚠️ Monitor Closely" : "🚨 Risk — Action Required";
  const alertBg     = profitAlert==="green" ? T.greenBg : profitAlert==="yellow" ? T.amberBg : T.redBg;

  const mCol = marginPct>30?T.green:marginPct>20?T.amber:T.red;

  const savings  = budget.line_items.filter(l=>l.budgeted>0&&l.spent>0&&l.budgeted>l.spent);
  const overRuns = budget.line_items.filter(l=>l.spent>l.budgeted&&l.spent>0);
  const totalSaved = savings.reduce((s,l)=>s+(l.budgeted-l.spent),0);
  const totalOver  = overRuns.reduce((s,l)=>s+(l.spent-l.budgeted),0);

  const fmt    = v => "$"+Math.round(v||0).toLocaleString();
  const fmtPct = v => (v||0).toFixed(0)+"%";

  // Controlled fee input that updates live
  const FeeInput = ({field, label, color}) => {
    const [val, setVal] = useState(budget[field]);
    useEffect(()=>setVal(budget[field]),[budget[field]]);
    return (
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:9,color:T.muted,fontWeight:700,marginBottom:5}}>{label}</div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:2}}>
          <input type="number" value={val}
            onChange={e=>{const n=parseFloat(e.target.value);setVal(e.target.value);if(!isNaN(n))updateFee(field,n);}}
            style={{width:52,background:T.bg0,border:`1px solid ${color}50`,borderRadius:6,padding:"5px 6px",color,fontSize:13,fontFamily:"'Space Mono',monospace",fontWeight:700,textAlign:"center"}}/>
          <span style={{fontSize:11,color:T.muted}}>%</span>
        </div>
        <div style={{fontSize:10,color,fontWeight:600,marginTop:3}}>
          {fmt(field==="markup_pct"?markup:field==="gc_fee_pct"?gcFee:field==="insurance_pct"?ins:field==="overhead_pct"?ovhd:ctg)}
        </div>
      </div>
    );
  };

  const STATUS = {
    "complete":    {label:"Done",    color:T.green, bg:T.greenBg},
    "in-progress": {label:"Active",  color:T.blue,  bg:T.blueBg},
    "not-started": {label:"Pending", color:T.muted, bg:T.bg3},
    "over-budget": {label:"Over",    color:T.red,   bg:T.redBg},
  };

  return (
    <div>
      {/* 3 big numbers */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
        <div style={{background:T.bg1,border:`1px solid ${T.gold}30`,borderRadius:12,padding:"16px 18px"}}>
          <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:6}}>Contract Value</div>
          <div style={{fontSize:28,fontWeight:800,color:T.gold,fontFamily:"'Space Mono',monospace",lineHeight:1,marginBottom:6}}>{fmt(contract)}</div>
          <div style={{fontSize:11,color:T.muted,lineHeight:1.5}}>What the owner pays<br/>Your cost: {fmt(directBudget)} ({fmtPct(directBudget/contract*100)})</div>
          {coTotal>0&&<div style={{fontSize:10,color:T.green,marginTop:6,fontWeight:600}}>+{fmt(coTotal)} approved COs</div>}
          {pendingTotal>0&&<div style={{fontSize:10,color:T.amber,marginTop:2}}>+{fmt(pendingTotal)} pending CO</div>}
        </div>
        <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:12,padding:"16px 18px"}}>
          <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:6}}>Spent So Far</div>
          <div style={{fontSize:28,fontWeight:800,color:T.text,fontFamily:"'Space Mono',monospace",lineHeight:1,marginBottom:4}}>{fmt(directSpent)}</div>
          <div style={{fontSize:11,color:T.muted,marginBottom:8}}>{fmtPct(costPct)} of {fmt(directBudget)} budgeted</div>
          <div style={{height:6,background:T.bg3,borderRadius:3,overflow:"hidden",marginBottom:6}}>
            <div style={{height:"100%",width:costPct+"%",background:costPct>90?T.red:costPct>75?T.amber:T.green,borderRadius:3,transition:"width 0.4s"}}/>
          </div>
          <div style={{fontSize:11,fontWeight:600,color:directLeft>0?T.green:T.red}}>{directLeft>0?fmt(directLeft)+" still available":fmt(Math.abs(directLeft))+" OVER BUDGET"}</div>
        </div>
        <div style={{background:T.bg1,border:`2px solid ${alertColor}40`,borderRadius:12,padding:"16px 18px"}}>
          <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:6}}>
            Est. Net Profit <span style={{fontSize:9,color:T.muted,fontWeight:400,textTransform:"none"}}>— Internal Only</span>
          </div>
          <div style={{fontSize:28,fontWeight:800,color:alertColor,fontFamily:"'Space Mono',monospace",lineHeight:1,marginBottom:4}}>{fmt(netProfit)}</div>
          <div style={{fontSize:11,color:T.muted,marginBottom:8}}>{fmtPct(netProfitPct)} after overhead, insurance & PM time</div>
          <div style={{height:6,background:T.bg3,borderRadius:3,overflow:"hidden",marginBottom:8}}>
            <div style={{height:"100%",width:Math.min(100,netProfitPct*3)+"%",background:alertColor,borderRadius:3}}/>
          </div>
          {/* Profit Alert Badge */}
          <div style={{background:alertBg,border:`1px solid ${alertColor}30`,borderRadius:7,padding:"6px 10px",display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:12,fontWeight:700,color:alertColor}}>{alertLabel}</span>
          </div>
        </div>
      </div>

      {/* Contract Type + Net Profit Breakdown — internal only */}
      {canSeeMargin&&(
      <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:10,padding:"12px 16px",marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text}}>Net Profit Breakdown <span style={{fontSize:10,color:T.muted,fontWeight:400}}>— Not shown to client</span></div>
          <select value={budget.contract_type||"lump_sum"} onChange={e=>save({...budget,contract_type:e.target.value})}
            style={{fontSize:11,background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"4px 8px",color:T.text,fontFamily:"inherit"}}>
            <option value="lump_sum">Lump Sum</option>
            <option value="cost_plus">Cost Plus</option>
            <option value="open_book">Open Book</option>
            <option value="public">Public / Prevailing Wage</option>
            <option value="transparent">Transparent Bid</option>
          </select>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
          {[
            {label:"Gross Margin",      value:grossMarginAmt, pct:marginPct,    color:T.text},
            {label:"− Overhead",        value:-ovhdDeduct,    pct:budget.overhead_pct, color:T.muted},
            {label:"− Insurance / WC",  value:-insDeduct,     pct:budget.insurance_pct, color:T.muted},
            {label:"− GC Time / PM",    value:-gcTimeDeduct,  pct:(gcTimeDeduct/contract*100), color:T.muted},
            {label:"− Contingency Used",value:-ctgUsed,       pct:(ctgUsed/contract*100), color:T.muted},
            {label:"= Est. Net Profit", value:netProfit,      pct:netProfitPct, color:alertColor, bold:true},
          ].map(row=>(
            <div key={row.label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
              padding:"6px 8px",borderRadius:6,background:row.bold?alertBg:"transparent",
              border:row.bold?`1px solid ${alertColor}30`:"none"}}>
              <span style={{fontSize:11,color:row.bold?alertColor:T.muted,fontWeight:row.bold?700:400}}>{row.label}</span>
              <span style={{fontSize:11,fontWeight:700,color:row.bold?alertColor:T.text,fontFamily:"'Space Mono',monospace"}}>
                {fmt(Math.abs(row.value))} <span style={{fontSize:9,color:T.muted}}>({Math.abs(row.pct||0).toFixed(1)}%)</span>
              </span>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Profit story + savings */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
        <Card>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Profit — Start to Finish</div>
          {[
            {icon:"💰",label:"Contract (owner pays you)",              value:contract,      color:T.gold,  bold:false},
            {icon:"📦",label:"− Your total cost budget",               value:-directBudget, color:T.red,   bold:false},
            {icon:"📊",label:"= Profit on paper (day 1)",              value:totalProfit,   color:mCol,    bold:true},
            {icon:"💸",label:"− Cost spent so far",                    value:-directSpent,  color:T.muted, bold:false},
            {icon:"📈",label:"= Profit still ahead of you",            value:profitToDate,  color:profitToDate>=totalProfit?T.green:T.amber, bold:true},
            {icon:"🔜",label:"− Cost still to spend (budget left)",    value:-Math.max(0,directLeft), color:T.muted, bold:false},
            {icon:"🏁",label:"= Projected profit at completion",       value:totalProfit+(totalSaved-totalOver), color:mCol, bold:true},
          ].map((row,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:row.bold?"9px 10px":"5px 10px",marginBottom:3,borderRadius:7,background:row.bold?T.bg2:"transparent",borderLeft:row.bold?`3px solid ${row.color}`:"3px solid transparent"}}>
              <span style={{fontSize:13,flexShrink:0}}>{row.icon}</span>
              <div style={{flex:1,fontSize:row.bold?12:11,fontWeight:row.bold?700:400,color:row.bold?T.text:T.muted}}>{row.label}</div>
              <div style={{fontSize:row.bold?14:12,fontWeight:row.bold?800:600,color:row.color,fontFamily:"'Space Mono',monospace",minWidth:90,textAlign:"right"}}>
                {row.value<0?`−${fmt(Math.abs(row.value))}`:fmt(row.value)}
              </div>
            </div>
          ))}
        </Card>

        <Card>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Where You Can Save More</div>
          <div style={{fontSize:11,color:T.muted,marginBottom:12}}>Every $1 saved in cost = $1 more profit</div>
          {totalSaved>0&&(
            <div style={{background:T.greenBg,border:`1px solid ${T.green}30`,borderRadius:8,padding:"10px 12px",marginBottom:8}}>
              <div style={{fontSize:11,fontWeight:700,color:T.green,marginBottom:6}}>✓ Saved {fmt(totalSaved)} so far — extra profit!</div>
              {savings.map(l=>(
                <div key={l.id} style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.muted,padding:"2px 0"}}>
                  <span>{l.trade}</span><span style={{color:T.green,fontWeight:600}}>+{fmt(l.budgeted-l.spent)}</span>
                </div>
              ))}
            </div>
          )}
          {overRuns.length>0&&(
            <div style={{background:T.redBg,border:`1px solid ${T.red}30`,borderRadius:8,padding:"10px 12px",marginBottom:8}}>
              <div style={{fontSize:11,fontWeight:700,color:T.red,marginBottom:6}}>⚠ {fmt(totalOver)} over budget — losing profit</div>
              {overRuns.map(l=>(
                <div key={l.id} style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.muted,padding:"2px 0"}}>
                  <span>{l.trade}</span><span style={{color:T.red,fontWeight:600}}>−{fmt(l.spent-l.budgeted)}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:6}}>Trades not started — negotiate these to save:</div>
          {budget.line_items.filter(l=>l.status==="not-started"&&l.budgeted>0).map(l=>(
            <div key={l.id} style={{display:"flex",justifyContent:"space-between",padding:"5px 8px",background:T.bg2,borderRadius:6,marginBottom:4,fontSize:11}}>
              <span style={{color:T.muted}}>{l.trade}</span>
              <div style={{display:"flex",gap:12}}>
                <span style={{color:T.muted}}>{fmt(l.budgeted)}</span>
                <span style={{color:T.teal,fontWeight:600}}>save any = +profit</span>
              </div>
            </div>
          ))}
          {totalSaved===0&&overRuns.length===0&&budget.line_items.every(l=>l.status==="not-started")&&(
            <div style={{fontSize:11,color:T.muted,fontStyle:"italic",textAlign:"center",padding:"20px 0"}}>Enter actual spent amounts to see savings and overruns here.</div>
          )}
        </Card>
      </div>

      {/* How price is built */}
      <Card style={{marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:T.text}}>How Your Price is Built</div>
            <div style={{fontSize:11,color:T.muted}}>Edit any % — all numbers update instantly</div>
          </div>
          <button onClick={()=>setShowFees(p=>!p)} style={{fontSize:11,fontWeight:600,color:showFees?T.gold:T.muted,background:showFees?T.goldBg:T.bg2,border:`1px solid ${showFees?T.gold:T.subtle}`,borderRadius:7,padding:"5px 14px",cursor:"pointer"}}>
            {showFees?"Hide Rates":"✏ Edit Rates"}
          </button>
        </div>
        {showFees&&(
          <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10,background:T.bg2,borderRadius:9,padding:"14px",marginBottom:14}}>
            <FeeInput field="markup_pct"      label="CM & Supervision %" color={T.blue}/>
            <FeeInput field="gc_fee_pct"      label="Gen. Conditions %" color={T.teal}/>
            <FeeInput field="insurance_pct"   label="Insurance %"   color={T.purple}/>
            <FeeInput field="overhead_pct"    label="Overhead %"    color={T.purple}/>
            <FeeInput field="contingency_pct" label="Contingency %"  color={T.amber}/>
          </div>
        )}
        {[
          {label:"Your direct cost (subs + labor + materials)", value:directBudget, color:T.muted,  bold:false, step:null},
          {label:`+ CM & Supervision (${budget.markup_pct}%)`,            value:markup,       color:T.blue,   bold:false, step:null},
          {label:"Price after markup",                          value:afterMkp,     color:T.text,   bold:true,  step:"1"},
          {label:`+ Gen. Conditions & OH (${budget.gc_fee_pct}%)`,        value:gcFee,        color:T.teal,   bold:false, step:null},
          {label:`+ Insurance + Overhead ${budget.insurance_pct+budget.overhead_pct}%`, value:ins+ovhd, color:T.purple, bold:false, step:null},
          {label:`+ Contingency ${budget.contingency_pct}% — risk reserve`,value:ctg,         color:T.amber,  bold:false, step:null},
          {label:`CLIENT PAYS  =  YOUR PROFIT ${fmt(totalProfit)} (${fmtPct(marginPct)} margin)`, value:contract, color:T.gold, bold:true, step:"$"},
        ].map((row,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:row.bold?"10px 12px":"6px 12px",marginBottom:3,borderRadius:8,background:row.step==="$"?T.goldBg:row.bold?T.bg2:"transparent",border:row.step==="$"?`1px solid ${T.gold}40`:"none"}}>
            {row.step?<div style={{width:24,height:24,borderRadius:7,background:row.step==="$"?T.gold:T.bg3,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:row.step==="$"?T.bg0:T.muted,flexShrink:0}}>{row.step}</div>:<div style={{width:24,flexShrink:0}}/>}
            <div style={{flex:1,fontSize:row.bold?12:11,fontWeight:row.bold?700:400,color:row.bold?T.text:T.muted}}>{row.label}</div>
            <div style={{fontSize:row.bold?16:12,fontWeight:row.bold?800:600,color:row.color,fontFamily:"'Space Mono',monospace"}}>{fmt(row.value)}</div>
          </div>
        ))}
      </Card>

      {/* Cost by trade */}
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:T.text}}>Cost by Trade</div>
            <div style={{fontSize:11,color:T.muted}}>Click any row to edit · Every dollar saved = more profit</div>
          </div>
          <Btn small ghost onClick={()=>setShowAdd(p=>!p)}><Ic d={ICONS.plus} size={11}/>Add</Btn>
        </div>
        {showAdd&&(
          <div style={{background:T.bg2,borderRadius:8,padding:"12px",marginBottom:10}}>
            <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr auto",gap:8,alignItems:"end"}}>
              {[["trade","Trade","Plumbing Sub"],["budgeted","Budget $","22000"],["spent","Spent $","0"]].map(([k,l,ph])=>(
                <div key={k}>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>{l}</div>
                  <input value={newLine[k]} onChange={e=>setNewLine(p=>({...p,[k]:e.target.value}))} placeholder={ph}
                    style={{width:"100%",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
                </div>
              ))}
              <div>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Status</div>
                <select value={newLine.status} onChange={e=>setNewLine(p=>({...p,status:e.target.value}))} style={{width:"100%",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}>
                  <option value="not-started">Pending</option><option value="in-progress">Active</option><option value="complete">Done</option>
                </select>
              </div>
              <div style={{display:"flex",gap:5,paddingBottom:1}}>
                <Btn small primary onClick={addLine} disabled={!newLine.trade}>Add</Btn>
                <Btn small ghost onClick={()=>setShowAdd(false)}>✕</Btn>
              </div>
            </div>
          </div>
        )}
        <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 100px 80px",gap:8,padding:"5px 10px",marginBottom:4}}>
          {["Trade","Budgeted","Spent","Remaining","Status"].map(h=>(
            <div key={h} style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",textAlign:h==="Trade"?"left":"right"}}>{h}</div>
          ))}
        </div>
        {budget.line_items.map(line=>{
          const rem=line.budgeted-line.spent; const over=rem<0;
          const pct=line.budgeted>0?Math.min(100,line.spent/line.budgeted*100):0;
          const ss=STATUS[over?"over-budget":line.status]||STATUS["not-started"];
          const isEdit=editing===line.id;
          return (
            <div key={line.id} style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 100px 80px",gap:8,padding:"9px 10px",borderRadius:8,marginBottom:3,cursor:"pointer",background:isEdit?T.bg2:over?T.redBg:T.bg1,border:`1px solid ${over?T.red+"30":isEdit?T.gold+"50":T.subtle}`,alignItems:"center"}}
              onClick={()=>setEditing(isEdit?null:line.id)}
              onMouseEnter={e=>{if(!isEdit&&!over)e.currentTarget.style.background=T.bg2;}}
              onMouseLeave={e=>{if(!isEdit&&!over)e.currentTarget.style.background=T.bg1;}}>
              <div>{isEdit?<input defaultValue={line.trade} onBlur={e=>updateLine(line.id,"trade",e.target.value)} autoFocus onClick={e=>e.stopPropagation()} style={{width:"100%",background:T.bg0,border:`1px solid ${T.gold}`,borderRadius:5,padding:"4px 7px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
                :<div><div style={{fontSize:12,fontWeight:600,color:T.text}}>{line.trade}</div><div style={{height:3,background:T.bg3,borderRadius:2,marginTop:4,width:"80%",overflow:"hidden"}}><div style={{height:"100%",width:pct+"%",background:pct>=100?T.red:pct>75?T.amber:T.green,borderRadius:2}}/></div></div>}
              </div>
              <div style={{textAlign:"right"}}>{isEdit?<input type="number" defaultValue={line.budgeted} onBlur={e=>updateLine(line.id,"budgeted",e.target.value)} onClick={e=>e.stopPropagation()} style={{width:"100%",background:T.bg0,border:`1px solid ${T.subtle}`,borderRadius:5,padding:"4px 7px",color:T.text,fontSize:12,fontFamily:"'Space Mono',monospace",textAlign:"right"}}/>:<span style={{fontSize:12,fontFamily:"'Space Mono',monospace",color:T.muted}}>{fmt(line.budgeted)}</span>}</div>
              <div style={{textAlign:"right"}}>{isEdit?<input type="number" defaultValue={line.spent} onBlur={e=>updateLine(line.id,"spent",e.target.value)} onClick={e=>e.stopPropagation()} style={{width:"100%",background:T.bg0,border:`1px solid ${T.subtle}`,borderRadius:5,padding:"4px 7px",color:T.text,fontSize:12,fontFamily:"'Space Mono',monospace",textAlign:"right"}}/>:<span style={{fontSize:12,fontFamily:"'Space Mono',monospace",color:line.spent>0?T.text:T.muted}}>{fmt(line.spent)}</span>}</div>
              <div style={{textAlign:"right"}}><div style={{fontSize:12,fontWeight:700,fontFamily:"'Space Mono',monospace",color:over?T.red:rem===0?T.muted:T.green}}>{over?"−":"+"}{fmt(Math.abs(rem))}</div>{over&&<div style={{fontSize:9,color:T.red}}>OVER</div>}</div>
              <div style={{textAlign:"right"}}>{isEdit?<select defaultValue={line.status} onBlur={e=>updateLine(line.id,"status",e.target.value)} onClick={e=>e.stopPropagation()} style={{background:T.bg0,border:`1px solid ${T.subtle}`,borderRadius:5,padding:"3px 5px",color:T.text,fontSize:10,fontFamily:"inherit"}}><option value="not-started">Pending</option><option value="in-progress">Active</option><option value="complete">Done</option><option value="over-budget">Over</option></select>:<span style={{fontSize:10,fontWeight:700,background:ss.bg,color:ss.color,borderRadius:20,padding:"3px 9px",whiteSpace:"nowrap"}}>{ss.label}</span>}</div>
            </div>
          );
        })}
        <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 100px 80px",gap:8,padding:"10px 10px",borderTop:`2px solid ${T.subtle}`,marginTop:4}}>
          <div style={{fontSize:12,fontWeight:800,color:T.text}}>TOTAL</div>
          <div style={{textAlign:"right",fontSize:13,fontFamily:"'Space Mono',monospace",fontWeight:800,color:T.text}}>{fmt(directBudget)}</div>
          <div style={{textAlign:"right",fontSize:13,fontFamily:"'Space Mono',monospace",fontWeight:800,color:directSpent>directBudget?T.red:T.text}}>{fmt(directSpent)}</div>
          <div style={{textAlign:"right",fontSize:13,fontFamily:"'Space Mono',monospace",fontWeight:800,color:directLeft<0?T.red:T.green}}>{directLeft<0?"−":"+"}{fmt(Math.abs(directLeft))}</div>
          <div style={{textAlign:"right",fontSize:11,fontWeight:700,color:mCol}}>{fmtPct(marginPct)}</div>
        </div>
      </Card>
    </div>
  );
}


// ─── CERTIFIED PAYROLL VIEW ───────────────────────────────────────────────────
// DIR-compliant certified payroll for prevailing wage projects
function CertifiedPayrollView({proj, session}) {
  const sk = "grok_payroll_" + proj?.id;
  const [entries, setEntries] = useState(()=>{try{return JSON.parse(localStorage.getItem(sk)||"[]");}catch{return [];}});
  const [showAdd, setShowAdd] = useState(false);
  const [weekEnding, setWeekEnding] = useState("");
  const [form, setForm] = useState({name:"",ssn_last4:"",classification:"Journeyman",straight_hrs:"",ot_hrs:"",dt_hrs:"",straight_rate:"",fringe:"",gross:""});
  const inp = {width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit",marginBottom:8};

  const CLASSIFICATIONS = ["Journeyman Electrician","Apprentice Electrician","Journeyman Plumber","Journeyman Carpenter","Laborer","Cement Mason","Iron Worker","Painter","Supervisor/Foreman","Other"];

  const save = e => { setEntries(e); try{localStorage.setItem(sk,JSON.stringify(e));}catch(err){} };

  const addEntry = () => {
    if(!form.name||!weekEnding) return;
    const st = parseFloat(form.straight_hrs||0);
    const ot = parseFloat(form.ot_hrs||0);
    const dt = parseFloat(form.dt_hrs||0);
    const rate = parseFloat(form.straight_rate||0);
    const fringe = parseFloat(form.fringe||0);
    const gross = st*rate + ot*rate*1.5 + dt*rate*2 + (st+ot+dt)*fringe;
    save([...entries,{...form,id:Date.now(),week_ending:weekEnding,gross:gross.toFixed(2),straight_hrs:st,ot_hrs:ot,dt_hrs:dt,straight_rate:rate,fringe}]);
    setForm({name:"",ssn_last4:"",classification:"Journeyman",straight_hrs:"",ot_hrs:"",dt_hrs:"",straight_rate:"",fringe:"",gross:""});
    setShowAdd(false);
  };

  const printPayroll = () => {
    const weekEntries = entries.filter(e=>!weekEnding||e.week_ending===weekEnding);
    const cs = (()=>{try{return JSON.parse(localStorage.getItem("grok_company_settings_"+(session?.companyId||""))||"{}");}catch{return {};}})();
    const w = window.open("","_blank");
    if(!w){alert("Allow popups.");return;}
    w.document.write(`<!DOCTYPE html><html><head><title>Certified Payroll — ${proj?.name}</title>
    <style>body{font-family:Arial,sans-serif;padding:24px;font-size:11px}
    h3{color:#d4a017;margin-bottom:4px}
    table{width:100%;border-collapse:collapse;margin:12px 0}
    th{background:#f5f0e8;padding:6px 8px;text-align:left;border:1px solid #ddd;font-size:10px}
    td{padding:5px 8px;border:1px solid #ddd}
    .header{display:flex;justify-content:space-between;margin-bottom:16px;padding-bottom:12px;border-bottom:2px solid #d4a017}
    .sig{margin-top:32px;display:grid;grid-template-columns:1fr 1fr;gap:40px}
    .sig-line{border-top:1px solid #333;padding-top:4px;margin-top:24px;font-size:10px;color:#666}
    @media print{body{padding:12px}}</style></head><body>
    <div class="header">
      <div>
        <h3>STATEMENT OF COMPLIANCE — CERTIFIED PAYROLL</h3>
        <div>Public Works Project · Labor Code §1776</div>
      </div>
      <div style="text-align:right">
        <div><strong>${cs.company_name||"Grok Construction Group Inc"}</strong></div>
        <div>CSLB #${cs.cslb||"1152750"} · DIR #${cs.dir_number||"_____________"}</div>
      </div>
    </div>
    <table style="width:100%;margin-bottom:12px;border-collapse:collapse">
      <tr><td style="padding:4px 8px;border:1px solid #ddd;font-weight:700">Project</td><td style="padding:4px 8px;border:1px solid #ddd">${proj?.name||""}</td>
          <td style="padding:4px 8px;border:1px solid #ddd;font-weight:700">Location</td><td style="padding:4px 8px;border:1px solid #ddd">${proj?.addr||""}</td></tr>
      <tr><td style="padding:4px 8px;border:1px solid #ddd;font-weight:700">Week Ending</td><td style="padding:4px 8px;border:1px solid #ddd">${weekEnding||"___________"}</td>
          <td style="padding:4px 8px;border:1px solid #ddd;font-weight:700">Contractor</td><td style="padding:4px 8px;border:1px solid #ddd">${cs.company_name||""}</td></tr>
    </table>
    <table>
      <thead><tr>
        <th>#</th><th>Name</th><th>SSN (last 4)</th><th>Classification</th>
        <th>ST Hrs</th><th>OT Hrs</th><th>DT Hrs</th><th>ST Rate</th><th>Fringe</th><th>Gross</th>
      </tr></thead>
      <tbody>${weekEntries.map((e,i)=>`<tr>
        <td>${i+1}</td><td>${e.name}</td><td>***-**-${e.ssn_last4||"****"}</td><td>${e.classification}</td>
        <td>${e.straight_hrs}</td><td>${e.ot_hrs||0}</td><td>${e.dt_hrs||0}</td>
        <td>$${e.straight_rate}/hr</td><td>$${e.fringe||0}/hr</td><td>$${e.gross}</td>
      </tr>`).join("")}</tbody>
    </table>
    <div style="margin-top:16px;padding:12px;background:#f8f6f0;border-radius:4px;font-size:10px;line-height:1.7">
      <strong>STATEMENT OF COMPLIANCE:</strong> I, the undersigned contractor or subcontractor, certify under penalty of perjury that the records or copies thereof submitted and consisting of certified payroll records for the week ending ${weekEnding} are the originals or true, full, and correct copies of the originals which depict the payroll record(s) of the actual disbursements made to the employees named and do comply with the requirements of Labor Code section 1771.
    </div>
    <div class="sig">
      <div><div class="sig-line">Signature / Date</div><div style="font-size:10px;margin-top:4px">${cs.company_name||""} · ${cs.owner||""}</div></div>
      <div><div class="sig-line">Title</div></div>
    </div>
    </body></html>`);
    w.document.close(); setTimeout(()=>w.print(),400);
  };

  const totalGross = entries.filter(e=>!weekEnding||e.week_ending===weekEnding).reduce((s,e)=>s+parseFloat(e.gross||0),0);

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div>
          <div style={{fontSize:14,fontWeight:800,color:T.text}}>📋 Certified Payroll</div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>CA Labor Code §1776 · DIR compliance · Prevailing wage</div>
        </div>
        <div style={{display:"flex",gap:6}}>
          <button onClick={printPayroll} style={{fontSize:11,fontWeight:700,background:T.bg2,color:T.muted,border:"1px solid "+T.subtle,borderRadius:7,padding:"6px 12px",cursor:"pointer"}}>Print</button>
          <button onClick={()=>setShowAdd(!showAdd)} style={{fontSize:11,fontWeight:700,background:T.gold,color:T.bg0,border:"none",borderRadius:7,padding:"7px 14px",cursor:"pointer"}}>+ Add Worker</button>
        </div>
      </div>

      {/* Week selector */}
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:12}}>
        <div style={{fontSize:11,color:T.muted,flexShrink:0}}>Week ending:</div>
        <input type="date" value={weekEnding} onChange={e=>setWeekEnding(e.target.value)}
          style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"5px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
        {totalGross>0&&<div style={{fontSize:12,color:T.gold,fontWeight:700,marginLeft:"auto"}}>Total: ${totalGross.toLocaleString()}</div>}
      </div>

      {showAdd&&(
        <div style={{background:T.bg1,border:"1px solid "+T.gold+"40",borderRadius:10,padding:14,marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Add Worker Entry</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Worker Name *</div><input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Full legal name" style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>SSN Last 4</div><input value={form.ssn_last4} onChange={e=>setForm(p=>({...p,ssn_last4:e.target.value}))} placeholder="####" maxLength={4} style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Classification</div>
              <select value={form.classification} onChange={e=>setForm(p=>({...p,classification:e.target.value}))} style={inp}>
                {CLASSIFICATIONS.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>ST Hours</div><input value={form.straight_hrs} onChange={e=>setForm(p=>({...p,straight_hrs:e.target.value}))} placeholder="40" type="number" style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>OT Hours (1.5x)</div><input value={form.ot_hrs} onChange={e=>setForm(p=>({...p,ot_hrs:e.target.value}))} placeholder="0" type="number" style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>DT Hours (2x)</div><input value={form.dt_hrs} onChange={e=>setForm(p=>({...p,dt_hrs:e.target.value}))} placeholder="0" type="number" style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>ST Rate $/hr</div><input value={form.straight_rate} onChange={e=>setForm(p=>({...p,straight_rate:e.target.value}))} placeholder="65.00" type="number" style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Fringe $/hr</div><input value={form.fringe} onChange={e=>setForm(p=>({...p,fringe:e.target.value}))} placeholder="12.00" type="number" style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3,marginTop:16}}>Est. Gross</div>
              <div style={{fontSize:14,fontWeight:800,color:T.gold,paddingTop:8}}>
                ${((parseFloat(form.straight_hrs||0)*parseFloat(form.straight_rate||0))+(parseFloat(form.ot_hrs||0)*parseFloat(form.straight_rate||0)*1.5)+(parseFloat(form.dt_hrs||0)*parseFloat(form.straight_rate||0)*2)+((parseFloat(form.straight_hrs||0)+parseFloat(form.ot_hrs||0)+parseFloat(form.dt_hrs||0))*parseFloat(form.fringe||0))).toFixed(2)}
              </div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,marginTop:4}}>
            <button onClick={()=>setShowAdd(false)} style={{flex:1,padding:"8px",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,color:T.muted,fontSize:12,cursor:"pointer"}}>Cancel</button>
            <button onClick={addEntry} style={{flex:2,padding:"8px",background:T.gold,border:"none",borderRadius:7,color:T.bg0,fontSize:12,fontWeight:700,cursor:"pointer"}}>Add Entry</button>
          </div>
        </div>
      )}

      {entries.length===0&&!showAdd&&(
        <div style={{textAlign:"center",padding:"40px",color:T.muted,background:T.bg2,borderRadius:10,border:"2px dashed "+T.subtle}}>
          <div style={{fontSize:24,marginBottom:8}}>📋</div>
          <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>No payroll entries yet</div>
          <div style={{fontSize:11}}>Track prevailing wage workers for DIR compliance</div>
        </div>
      )}

      {entries.filter(e=>!weekEnding||e.week_ending===weekEnding).map((e,i)=>(
        <div key={e.id} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:8,padding:"10px 14px",marginBottom:6}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <span style={{fontFamily:"monospace",fontSize:10,color:T.muted,marginRight:8}}>#{i+1}</span>
              <span style={{fontSize:12,fontWeight:700,color:T.text}}>{e.name}</span>
              <span style={{fontSize:10,color:T.muted,marginLeft:8}}>{e.classification}</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{fontSize:10,color:T.muted}}>ST:{e.straight_hrs}h · OT:{e.ot_hrs||0}h · DT:{e.dt_hrs||0}h</div>
              <div style={{fontSize:13,fontWeight:700,color:T.gold}}>${parseFloat(e.gross).toLocaleString()}</div>
              <button onClick={()=>save(entries.filter(x=>x.id!==e.id))} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:11}}>🗑</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function RFQSender({proj, session}) {
  const sk = "grok_rfqs_" + proj?.id;
  const [rfqs, setRfqs] = useState(()=>{try{return JSON.parse(localStorage.getItem(sk)||"[]");}catch{return [];}});
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({scope:"",trade:"",due_date:"",scope_details:"",subs:[]});
  const inp = {width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit",marginBottom:8};

  // Get subs from roster
  const allSubs = (()=>{
    const dbKey = "grok_subdb_"+(session?.companyId||window._grokCompanyId||"local");
    try{return JSON.parse(localStorage.getItem(dbKey)||"{}").subs||[];}catch{return [];}
  })();

  const save = r => { setRfqs(r); try{localStorage.setItem(sk,JSON.stringify(r));}catch(e){} };

  const sendRFQ = async () => {
    if(!form.scope||!form.subs.length) return;
    const rfq = {...form, id:"RFQ-"+Date.now().toString().slice(-4), status:"sent", sent_date:new Date().toLocaleDateString(), responses:[]};
    save([...rfqs, rfq]);

    // Send email to each selected sub
    for (const subId of form.subs) {
      const sub = allSubs.find(s=>s.id===subId||String(s.id)===String(subId));
      if (!sub?.email) continue;
      try {
        await fetch("https://qnjlirkgumhzzcavqsxt.supabase.co/functions/v1/send-email",{
          method:"POST",
          headers:{"Content-Type":"application/json","apikey":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFuamxpcmtndW1oenpjYXZxc3h0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc3NTQxNjgsImV4cCI6MjA5MzMzMDE2OH0.fmwdMo6-ae5Jyx8b3n44FlOQUSSUX9drdt2UYJd7Mws"},
          body:JSON.stringify({
            to: sub.email,
            subject: `RFQ — ${form.scope} — ${proj?.name}`,
            html: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:32px">
              <div style="font-size:20px;font-weight:800;color:#d4a017;margin-bottom:4px">Request for Quote</div>
              <div style="font-size:13px;color:#666;margin-bottom:20px">${proj?.name} · ${proj?.addr||""}</div>
              <table style="width:100%;border-collapse:collapse;margin-bottom:20px">
                <tr><td style="padding:8px;background:#f8f6f0;font-weight:700;font-size:12px">Scope</td><td style="padding:8px;font-size:12px">${form.scope}</td></tr>
                <tr><td style="padding:8px;background:#f8f6f0;font-weight:700;font-size:12px">Trade</td><td style="padding:8px;font-size:12px">${form.trade||"General"}</td></tr>
                <tr><td style="padding:8px;background:#f8f6f0;font-weight:700;font-size:12px">Bid Due</td><td style="padding:8px;font-size:12px">${form.due_date||"ASAP"}</td></tr>
              </table>
              ${form.scope_details?`<div style="background:#f8f6f0;border-radius:8px;padding:16px;margin-bottom:20px;font-size:12px;line-height:1.7">${form.scope_details}</div>`:""}
              <p style="font-size:12px;color:#444">Please reply to this email with your bid amount and any questions. We look forward to working with you.</p>
              <hr style="border:none;border-top:1px solid #e5e0d5;margin:20px 0"/>
              <div style="font-size:11px;color:#888">${session?.ownerName||"GCG"} · ${session?.email||""} · CSLB #${session?.cslb||"1152750"}</div>
            </div>`
          })
        });
      } catch(e) { console.error("RFQ email failed for a sub"); }
    }

    setForm({scope:"",trade:"",due_date:"",scope_details:"",subs:[]});
    setShowNew(false);
    alert(`✓ RFQ sent to ${form.subs.length} sub${form.subs.length>1?"s":""}!`);
  };

  const TRADES = ["Electrical","Plumbing","HVAC","Framing","Concrete","Roofing","Drywall","Painting","Flooring","Tile","General"];

  return (
    <div style={{marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text}}>📨 Send RFQ to Subs</div>
        <button onClick={()=>setShowNew(!showNew)} style={{fontSize:11,fontWeight:700,background:T.purple,color:"white",border:"none",borderRadius:7,padding:"6px 12px",cursor:"pointer"}}>
          + New RFQ
        </button>
      </div>

      {showNew&&(
        <div style={{background:T.bg1,border:"1px solid "+T.purple+"40",borderRadius:10,padding:14,marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>New Request for Quote</div>
          <div style={{display:"grid",gridTemplateColumns:window.innerWidth<768?"1fr":"1fr 1fr",gap:8}}>
            <div>
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Scope of Work *</div>
              <input value={form.scope} onChange={e=>setForm(p=>({...p,scope:e.target.value}))} placeholder="e.g. Electrical rough-in all units" style={inp}/>
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Trade</div>
              <select value={form.trade} onChange={e=>setForm(p=>({...p,trade:e.target.value}))} style={inp}>
                <option value="">Select trade...</option>
                {TRADES.map(t=><option key={t}>{t}</option>)}
              </select>
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Bid Due Date</div>
              <input type="date" value={form.due_date} onChange={e=>setForm(p=>({...p,due_date:e.target.value}))} style={inp}/>
            </div>
            <div>
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Scope Details / Specs</div>
              <textarea value={form.scope_details} onChange={e=>setForm(p=>({...p,scope_details:e.target.value}))}
                placeholder="Include drawings ref, unit count, materials provided, schedule requirements..." rows={5}
                style={{...inp,resize:"vertical"}}/>
            </div>
          </div>

          {/* Sub selector */}
          <div style={{fontSize:10,color:T.muted,marginBottom:6}}>Select Subs to Send To *</div>
          {allSubs.length===0?(
            <div style={{fontSize:11,color:T.muted,background:T.bg2,borderRadius:6,padding:"8px 12px",marginBottom:8}}>
              No subs in your roster yet. Add subs in the Subs tab first.
            </div>
          ):(
            <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
              {allSubs.map(sub=>{
                const selected = form.subs.includes(sub.id||sub.name);
                return (
                  <button key={sub.id||sub.name} onClick={()=>{
                    const id = sub.id||sub.name;
                    setForm(p=>({...p,subs:selected?p.subs.filter(s=>s!==id):[...p.subs,id]}));
                  }} style={{fontSize:11,padding:"5px 10px",borderRadius:20,border:"1px solid "+(selected?T.purple:T.subtle),
                    background:selected?T.purple+"20":T.bg2,color:selected?T.purple:T.muted,cursor:"pointer",fontWeight:selected?700:400}}>
                    {selected?"✓ ":""}{sub.name} {sub.email?"":"⚠"}
                  </button>
                );
              })}
            </div>
          )}

          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>setShowNew(false)} style={{flex:1,padding:"8px",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,color:T.muted,fontSize:12,cursor:"pointer"}}>Cancel</button>
            <button onClick={sendRFQ} disabled={!form.scope||!form.subs.length}
              style={{flex:2,padding:"8px",background:form.scope&&form.subs.length?T.purple:T.bg3,border:"none",borderRadius:7,color:form.scope&&form.subs.length?"white":T.muted,fontSize:12,fontWeight:700,cursor:form.scope&&form.subs.length?"pointer":"not-allowed"}}>
              📨 Send RFQ to {form.subs.length||0} Sub{form.subs.length!==1?"s":""}
            </button>
          </div>
        </div>
      )}

      {/* Sent RFQs */}
      {rfqs.map(rfq=>(
        <div key={rfq.id} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:8,padding:"10px 14px",marginBottom:6,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:12,fontWeight:700,color:T.text}}>{rfq.id} — {rfq.scope}</div>
            <div style={{fontSize:10,color:T.muted}}>{rfq.trade} · Sent {rfq.sent_date} to {rfq.subs.length} sub{rfq.subs.length!==1?"s":""} · Due: {rfq.due_date||"ASAP"}</div>
          </div>
          <span style={{fontSize:9,fontWeight:700,background:T.greenBg,color:T.green,borderRadius:10,padding:"2px 8px"}}>{rfq.status}</span>
        </div>
      ))}
    </div>
  );
}

function SubBidCompare({proj}) {
  const [bids, setBids] = useState(()=>{
    try{const s=localStorage.getItem("grok_subbids_"+proj?.id);if(s)return JSON.parse(s);}catch(e){}
    return proj.subBids || {
    scope: "Electrical Rough-In — All Units",
    trade: "Electrical",
    bids: [
      {id:1, company:"Bay Electric Inc",     contact:"Tom (415) 555-0111", amount:148000, notes:"10-week timeline, licensed C-10, $2M liability", recommended:false, status:"received"},
      {id:2, company:"Pacific Power Subs",   contact:"Mike (415) 555-0122", amount:162000, notes:"8-week timeline, union shop, includes testing", recommended:true,  status:"received"},
      {id:3, company:"Sunset Electrical",    contact:"Dave (650) 555-0133", amount:134000, notes:"12-week timeline, no supervision included", recommended:false, status:"received"},
    ]
  };
  });
  const [showAdd, setShowAdd] = useState(false);
  const [newBid, setNewBid] = useState({company:"",contact:"",amount:"",notes:"",status:"pending"});
  const [newScope, setNewScope] = useState(bids.scope);
  const [aiAnalysis, setAiAnalysis] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const lowestBid = bids.bids.length > 0 ? Math.min(...bids.bids.map(b=>b.amount)) : 0;
  const highestBid = bids.bids.length > 0 ? Math.max(...bids.bids.map(b=>b.amount)) : 0;
  const spread = highestBid - lowestBid;
  const recommended = bids.bids.find(b=>b.recommended);
  const markupOnRecommended = recommended ? recommended.amount * 1.15 : 0;

  const addBid = () => {
    if (!newBid.company || !newBid.amount) return;
    setBids(prev=>({...prev, bids:[...prev.bids, {...newBid,id:Date.now(),amount:parseFloat(newBid.amount)||0,recommended:false}]}));
    setNewBid({company:"",contact:"",amount:"",notes:"",status:"pending"});
    setShowAdd(false);
  };
  
  // Auto-save bids to localStorage
  const saveBids = (updated) => {
    setBids(updated);
    try{localStorage.setItem("grok_subbids_"+proj?.id, JSON.stringify(updated));}catch(e){}
  };

  const setRecommended = (id) => {
    setBids(prev=>{
      const updated = {...prev, bids:prev.bids.map(b=>({...b,recommended:b.id===id}))};
      try{localStorage.setItem("grok_subbids_"+proj?.id, JSON.stringify(updated));}catch(e){}
      return updated;
    });
  };

  const runAI = async () => {
    const key = "proxy";
    if (!key) { alert("Add API key in AI Agent first."); return; }
    if (!enforceLimit("pm_chats", session)) return;
    const kb = (typeof loadKB === 'function') ? loadKB() : {};
    setAiLoading(true); setAiAnalysis("");
    const bidsText = bids.bids.map((b,i)=>`${i+1}. ${b.company}: $${b.amount.toLocaleString()} — ${b.notes}`).join("\n");
    try {
      await callAI(
        `You are an expert GC analyzing sub bids for ${proj?.name||"a project"}. Rates: ${kb.sub_rates||"market rate"}. Markup: 15%.`,
        `Analyze these sub bids for scope: ${bids.scope}\n\nBids:\n${bidsText}\n\nProvide:\n1. RECOMMENDATION — which bid and why (consider price, timeline, qualifications)\n2. RED FLAGS — anything suspicious (too low = cut corners, too high = padding)\n3. NEGOTIATION TIPS — how to get the best deal\n4. WHAT TO CONFIRM — insurance, license, references before awarding\n5. CLIENT PRICE — at 15% markup, what to charge the owner for this scope`,
        t=>setAiAnalysis(p=>p+t), key
      );
    } catch(e) { setAiAnalysis("Error: "+e.message); }
    setAiLoading(false);
  };

  const fmt = v => "$" + Math.round(v||0).toLocaleString();

  return (
    <div>
      {/* Scope header */}
      <Card style={{marginBottom:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
          <div style={{flex:1}}>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Scope Being Bid</div>
            <input value={newScope} onChange={e=>setNewScope(e.target.value)} onBlur={()=>setBids(p=>({...p,scope:newScope}))}
              style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
          </div>
          <Btn small ghost onClick={()=>setShowAdd(true)} style={{marginLeft:12,flexShrink:0}}><Ic d={ICONS.plus} size={11}/>Add Bid</Btn>
        </div>

        {/* Summary stats */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8}}>
          {[
            {l:"Bids Received",v:bids.bids.length,c:T.blue},
            {l:"Lowest Bid",v:fmt(lowestBid),c:T.green},
            {l:"Highest Bid",v:fmt(highestBid),c:T.red},
            {l:"Spread",v:fmt(spread),c:spread>lowestBid*0.2?T.amber:T.muted},
          ].map(s=>(
            <div key={s.l} style={{background:T.bg2,borderRadius:8,padding:"10px 12px",textAlign:"center"}}>
              <div style={{fontSize:10,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{s.l}</div>
              <div style={{fontSize:16,fontWeight:800,color:s.c,fontFamily:"'Space Mono',monospace"}}>{s.v}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Add bid form */}
      {showAdd&&(
        <Card style={{marginBottom:12,background:T.bg2}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Add Bid</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            {[["company","Sub Company","Bay Electric Inc"],["contact","Contact & Phone","Tom (415) 555-0100"],["amount","Bid Amount","148000"],["notes","Notes / Terms","10-week, licensed C-10"]].map(([k,l,ph])=>(
              <div key={k}>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{l}</div>
                <input value={newBid[k]} onChange={e=>setNewBid(p=>({...p,[k]:e.target.value}))} placeholder={ph}
                  style={{width:"100%",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn ghost onClick={()=>setShowAdd(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
            <Btn primary disabled={!newBid.company||!newBid.amount} onClick={addBid} style={{flex:2,justifyContent:"center"}}>Add Bid</Btn>
          </div>
        </Card>
      )}

      {/* Bid cards */}
      {bids.bids.map((bid,i)=>{
        const vsLowest = bid.amount - lowestBid;
        const isLowest = bid.amount === lowestBid;
        const isHighest = bid.amount === highestBid;
        return (
          <Card key={bid.id} style={{marginBottom:10,border:`1px solid ${bid.recommended?T.gold+"60":T.subtle}`,background:bid.recommended?T.goldBg:T.bg1}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                  <span style={{fontSize:14,fontWeight:800,color:T.text}}>{bid.company}</span>
                  {bid.recommended&&<span style={{fontSize:9,fontWeight:800,background:T.gold,color:T.bg0,borderRadius:20,padding:"2px 8px"}}>⭐ RECOMMENDED</span>}
                  {isLowest&&!bid.recommended&&<span style={{fontSize:9,fontWeight:700,background:T.greenBg,color:T.green,borderRadius:20,padding:"2px 8px"}}>LOWEST</span>}
                  {isHighest&&<span style={{fontSize:9,fontWeight:700,background:T.redBg,color:T.red,borderRadius:20,padding:"2px 8px"}}>HIGHEST</span>}
                </div>
                <div style={{fontSize:11,color:T.muted}}>{bid.contact}</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:22,fontWeight:800,color:bid.recommended?T.gold:isLowest?T.green:isHighest?T.red:T.text,fontFamily:"'Space Mono',monospace"}}>{fmt(bid.amount)}</div>
                {vsLowest>0&&<div style={{fontSize:10,color:T.muted}}>+{fmt(vsLowest)} vs lowest</div>}
                <div style={{fontSize:10,color:T.teal,marginTop:2}}>→ {fmt(bid.amount*1.15)} to owner (+15%)</div>
              </div>
            </div>
            {bid.notes&&<div style={{fontSize:12,color:T.muted,lineHeight:1.5,marginBottom:8,background:T.bg2,borderRadius:6,padding:"8px 10px"}}>{bid.notes}</div>}
            <div style={{display:"flex",gap:7}}>
              {!bid.recommended&&<Btn small primary onClick={()=>setRecommended(bid.id)} style={{background:T.bg3,color:T.muted}}>Set as Recommended</Btn>}
              {bid.recommended&&<Btn small ghost onClick={()=>setRecommended(-1)}>Remove Recommendation</Btn>}
              {bid.recommended&&<Btn small primary onClick={()=>{
                const po={id:"PO-"+Date.now(),projectId:proj?.id,projectName:proj?.name,
                  vendor:bid.company,contact:bid.contact,trade:bids.trade,scope:bids.scope,
                  amount:bid.amount,status:"draft",createdAt:new Date().toLocaleDateString(),
                  notes:bid.notes,terms:"Net 30 · Lien waiver required at each payment"};
                const key="grok_pos_"+proj?.id;
                const existing=JSON.parse(localStorage.getItem(key)||"[]");
                localStorage.setItem(key,JSON.stringify([po,...existing]));
                alert("✓ Purchase Order PO-"+po.id.split("-")[1]+" created for "+bid.company+"\n\nView in Finance → Purchase Orders tab.");
              }} style={{background:T.teal,color:"white"}}>
                📋 Convert to PO
              </Btn>}
            </div>
          </Card>
        );
      })}

      {/* AI Analysis */}
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>AI Bid Analysis</div>
          <Btn small primary onClick={runAI} disabled={aiLoading||bids.bids.length===0}
            style={{background:`linear-gradient(135deg,${T.purple},${T.blue})`,color:"white"}}>
            {aiLoading?<><Spin/>Analyzing...</>:<><Ic d={ICONS.robot} size={12}/>Analyze Bids</>}
          </Btn>
        </div>
        {!aiAnalysis&&!aiLoading&&(
          <div style={{fontSize:12,color:T.muted,fontStyle:"italic",lineHeight:1.6}}>
            Click Analyze Bids — AI will review all bids, flag red flags, recommend the best value, suggest negotiation tactics, and calculate what to charge the owner.
          </div>
        )}
        {(aiAnalysis||aiLoading)&&(
          <div style={{background:T.bg0,borderRadius:8,padding:"12px 14px",fontSize:12,color:T.text,lineHeight:1.8,whiteSpace:"pre-wrap",fontFamily:"inherit",maxHeight:400,overflowY:"auto"}}>
            {aiAnalysis}{aiLoading&&<Spin/>}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── PROJECT TEAM CHAT ────────────────────────────────────────────────────────
function ProjectChat({proj, session}) {
  const key = "grok_chat_" + proj?.id;
  const [messages, setMessages] = useState(()=>{
    try{const s=localStorage.getItem(key);return s?JSON.parse(s):[
      {id:1,author:"Jehoon Chun",role:"GC",avatar:"JK",color:"#D4A843",text:"Project kickoff confirmed. Demo starts Monday 8am. Tom — make sure you have protection up before demo crew arrives.",ts:"Apr 18 9:02am",pinned:false},
      {id:2,author:"Tom (Plumber)",role:"Sub",avatar:"TP",color:"#60a5fa",text:"Got it. We'll have plastic on floors and dust barriers by 7:45. Do we have access to the unit above?",ts:"Apr 18 9:15am",pinned:false},
      {id:3,author:"Jehoon Chun",role:"GC",avatar:"JK",color:"#D4A843",text:"Yes, owner confirmed unit 3B access. Key in lockbox code 4821.",ts:"Apr 18 9:18am",pinned:true},
      {id:4,author:"Mike (Electrician)",role:"Sub",avatar:"ME",color:"#a78bfa",text:"Quick heads up — saw some knob-and-tube in the attic. May need to flag this as a CO before we proceed.",ts:"Apr 19 2:30pm",pinned:false},
      {id:5,author:"Jehoon Chun",role:"GC",avatar:"JK",color:"#D4A843",text:"Good catch Mike. Submit an RFI. I'll get structural to look at it before we touch anything.",ts:"Apr 19 2:45pm",pinned:false},
    ];}catch{return [];}
  });
  const [input, setInput] = useState("");
  const [filter, setFilter] = useState("all");
  const [showPinned, setShowPinned] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const boundRef = useRef(null);
  const bound = useViewportBound(boundRef, 60);

  const save = (msgs) => {
    setMessages(msgs);
    try{localStorage.setItem(key, JSON.stringify(msgs));}catch(e){}
  };

  const send = () => {
    if (!input.trim()) return;
    const msg = {
      id: Date.now(),
      author: session?.ownerName || "Jehoon Chun",
      role: "GC",
      avatar: (session?.ownerName||"JK").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase(),
      color: T.gold,
      text: input.trim(),
      ts: new Date().toLocaleString("en-US",{month:"short",day:"numeric",hour:"numeric",minute:"2-digit",hour12:true}),
      pinned: false,
    };
    save([...messages, msg]);
    setInput("");
    setTimeout(()=>bottomRef.current?.scrollIntoView({behavior:"smooth"}), 50);
  };

  const togglePin = (id) => save(messages.map(m=>String(m.id)===String(id)?{...m,pinned:!m.pinned}:m));
  const deleteMsg = (id) => save(messages.filter(m=>String(m.id)!==String(id)));

  const displayed = showPinned ? messages.filter(m=>m.pinned) :
    filter==="co_scope" ? flaggedMessages :
    filter==="all" ? messages : messages.filter(m=>m.role.toLowerCase()===filter.toLowerCase());
  const pinnedCount = messages.filter(m=>m.pinned).length;

  // Detect CO triggers in messages
  const CO_SCOPE_KEYWORDS = ["change order","change in scope","scope change","extra work","additional scope","not in contract","out of scope","unforeseen","hidden condition","discovered during","knob-and-tube","asbestos","mold","water damage","owner request","owner wants","need to add","price increase","additional cost","needs CO","CO needed","CO required","requires a CO"];
  const CO_LOGISTICS = ["see you","on my way","heading","arrived","done for the","good job","thanks","thank you","sounds good","got it","confirmed","will do","on site","leaving","approved"];
  const flaggedMessages = messages.filter(m=>{
    const t = m.text.toLowerCase();
    if (CO_LOGISTICS.some(e=>t.startsWith(e))) return false;
    return CO_SCOPE_KEYWORDS.some(k=>t.includes(k));
  });
  const flaggedIds = new Set(flaggedMessages.map(m=>m.id));

  return (
    <div ref={boundRef} style={{display:"flex",flexDirection:"column",height: bound || "calc(100vh - 300px)",minHeight:380,maxHeight:660}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,flexShrink:0}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>Project Chat</div>
          <div style={{fontSize:11,color:T.muted}}>{messages.length} messages · {proj?.name}</div>
        </div>
        <div style={{display:"flex",gap:6}}>
          <button onClick={()=>setShowPinned(p=>!p)}
            style={{fontSize:11,fontWeight:600,background:showPinned?T.goldBg:T.bg2,border:`1px solid ${showPinned?T.gold:T.subtle}`,borderRadius:7,padding:"5px 12px",cursor:"pointer",color:showPinned?T.gold:T.muted}}>
            📌 Pinned {pinnedCount>0&&`(${pinnedCount})`}
          </button>
          {["all","GC","Sub","co_scope"].map(f=>(
            <button key={f} onClick={()=>{setFilter(f);setShowPinned(false);}}
              style={{fontSize:11,fontWeight:600,background:filter===f&&!showPinned?T.bg3:T.bg2,border:`1px solid ${filter===f&&!showPinned?T.gold:T.subtle}`,borderRadius:7,padding:"5px 10px",cursor:"pointer",color:filter===f&&!showPinned?T.gold:T.muted}}>
              {f==="all"?"All":f==="co_scope"?"⚡ Scope Changes":f}
            </button>
          ))}
        </div>
      </div>

      {flaggedMessages.length > 0 && !showPinned && (
        <div style={{background:T.amberBg,border:"1px solid "+T.amber+"40",borderRadius:8,padding:"9px 14px",marginBottom:10,fontSize:11,color:T.amber,flexShrink:0,display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:14}}>⚡</span>
          <div style={{flex:1}}><strong>{flaggedMessages.length} message{flaggedMessages.length!==1?"s":""}</strong> mention scope changes — may need a Change Order</div>
          <button onClick={()=>setFilter("co_scope")}
            style={{fontSize:10,fontWeight:700,background:T.amber,color:T.bg0,border:"none",borderRadius:6,padding:"3px 10px",cursor:"pointer",flexShrink:0}}>
            View Only →
          </button>
        </div>
      )}

      {/* Messages */}
      <div style={{flex:1,overflowY:"auto",paddingRight:4}}>
        {displayed.length===0?(
          <div style={{textAlign:"center",padding:"40px",color:T.muted,fontSize:12,fontStyle:"italic"}}>
            {showPinned?"No pinned messages yet. Pin important messages to find them quickly.":"No messages yet. Start the conversation."}
          </div>
        ):displayed.map((msg,i)=>{
          const isMe = msg.author===(session?.ownerName||"Jehoon Chun");
          const prevAuthor = i > 0 ? displayed[i-1].author : null;
          const groupTop = prevAuthor === msg.author;
          return (
            <div key={msg.id} style={{display:"flex",gap:10,marginBottom:groupTop?4:12,alignItems:"flex-end",flexDirection:isMe?"row-reverse":"row"}}>
              {!groupTop&&(
                <div style={{width:32,height:32,borderRadius:10,background:msg.color+"20",border:`1px solid ${msg.color}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:msg.color,flexShrink:0,alignSelf:"flex-end"}}>
                  {msg.avatar}
                </div>
              )}
              {groupTop&&<div style={{width:32,flexShrink:0}}/>}
              <div style={{maxWidth:"75%"}}>
                {!groupTop&&(
                  <div style={{fontSize:10,color:T.muted,marginBottom:3,textAlign:isMe?"right":"left"}}>
                    {msg.author} <span style={{fontSize:9,background:msg.color+"20",color:msg.color,borderRadius:20,padding:"1px 6px",fontWeight:700}}>{msg.role}</span>
                  </div>
                )}
                <div style={{position:"relative",group:"true"}}
                  onMouseEnter={e=>{e.currentTarget.querySelector('.msg-actions')&&(e.currentTarget.querySelector('.msg-actions').style.display='flex');}}
                  onMouseLeave={e=>{e.currentTarget.querySelector('.msg-actions')&&(e.currentTarget.querySelector('.msg-actions').style.display='none');}}>
                  <div style={{background:isMe?T.gold:T.bg2,borderRadius:isMe?"12px 12px 4px 12px":"12px 12px 12px 4px",padding:"9px 13px",fontSize:12,color:isMe?T.bg0:T.text,lineHeight:1.6,border:msg.pinned?`2px solid ${T.gold}`:isMe?"none":`1px solid ${T.subtle}`}}>
                    {msg.pinned&&!isMe&&<span style={{fontSize:9,color:T.gold,fontWeight:800,display:"block",marginBottom:3}}>📌 PINNED</span>}
                    {flaggedIds.has(msg.id)&&(
                      <div style={{fontSize:9,background:T.amberBg,color:T.amber,borderRadius:6,padding:"2px 8px",fontWeight:700,display:"flex",alignItems:"center",gap:6,marginBottom:5,width:"fit-content"}}>
                        <span>⚡</span> Scope change — may need CO
                        <button onClick={()=>window.dispatchEvent(new CustomEvent("grok-navigate",{detail:{tab:"admin",sub:"cos"}}))}
                          style={{fontSize:9,fontWeight:700,background:T.amber,color:T.bg0,border:"none",borderRadius:4,padding:"1px 7px",cursor:"pointer"}}>
                          + Create CO
                        </button>
                      </div>
                    )}
                    {msg.text}
                    <div style={{fontSize:9,color:isMe?"rgba(0,0,0,0.4)":T.muted,marginTop:4,textAlign:"right"}}>{msg.ts}</div>
                  </div>
                  <div className="msg-actions" style={{display:"none",position:"absolute",top:-28,right:isMe?"0":"auto",left:isMe?"auto":"0",gap:4,background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"3px 6px"}}>
                    <button onClick={()=>togglePin(msg.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:12,padding:"2px 4px"}} title={msg.pinned?"Unpin":"Pin"}>
                      {msg.pinned?"📌":"📍"}
                    </button>
                    <button onClick={()=>deleteMsg(msg.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:12,padding:"2px 4px",color:T.red}} title="Delete">×</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>

      {/* Input */}
      <div className="chat-input-row" style={{borderTop:`1px solid ${T.subtle}`,paddingTop:12,flexShrink:0}}>
        <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
          <div style={{flex:1,background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:12,padding:"10px 14px",display:"flex",alignItems:"flex-end",gap:8}}>
            <textarea ref={inputRef} value={input} onChange={e=>setInput(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
              placeholder="Message the project team... (Enter to send, Shift+Enter for new line)"
              rows={1} style={{flex:1,background:"transparent",border:"none",color:T.text,fontSize:13,resize:"none",fontFamily:"inherit",lineHeight:1.5,maxHeight:120,outline:"none"}}/>
          </div>
          <button onClick={send} disabled={!input.trim()}
            style={{width:44,height:44,borderRadius:12,background:input.trim()?T.gold:T.bg3,border:"none",cursor:input.trim()?"pointer":"not-allowed",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0,transition:"all 0.15s"}}>
            ➤
          </button>
        </div>
        <div style={{fontSize:10,color:T.muted,marginTop:6,display:"flex",gap:12}}>
          <span>Enter to send · Shift+Enter for new line</span>
          <span>Messages with CO triggers are auto-flagged ⚡</span>
        </div>
      </div>
    </div>
  );
}

// ─── TIME TRACKER ─────────────────────────────────────────────────────────────
function TimeTracker({proj, session}) {
  const key = "grok_time_" + proj?.id;
  const [entries, setEntries] = useState(()=>{
    try{const s=localStorage.getItem(key);return s?JSON.parse(s):[
      {id:1,worker:"Jehoon Chun",role:"PM/GC",date:"Apr 19",hours:4.5,trade:"Supervision",notes:"Site walk, sub coordination",billable:true},
      {id:2,worker:"Lead Carpenter",role:"Labor",date:"Apr 19",hours:8,trade:"Carpentry",notes:"Framing unit 2A",billable:true},
      {id:3,worker:"Laborer",role:"Labor",date:"Apr 19",hours:8,trade:"Demo",notes:"Demo kitchen + bath",billable:true},
    ];}catch{return [];}
  });
  const [timer, setTimer] = useState({running:false,start:null,elapsed:0,worker:"",trade:"Demo",notes:""});
  const [showAdd, setShowAdd] = useState(false);
  const [newEntry, setNewEntry] = useState({worker:"",role:"Labor",date:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"}),hours:"",trade:"Demo",notes:"",billable:true});
  const timerRef = useRef(null);

  const RATES = {"PM/GC":105,"Lead Carpenter":105,"Journeyman":85,"Laborer":70,"Tile":95,"Drywall":90,"Painter":75};
  const TRADES = ["Demo","Carpentry","Drywall","Tile","Flooring","Paint","Electrical","Plumbing","General"];

  const save = (e) => { setEntries(e); try{localStorage.setItem(key,JSON.stringify(e));}catch(err){} };

  const startTimer = () => {
    if (!timer.worker) { alert("Enter worker name first"); return; }
    setTimer(p=>({...p,running:true,start:Date.now()-p.elapsed*1000}));
    timerRef.current = setInterval(()=>setTimer(p=>({...p,elapsed:(Date.now()-p.start)/1000})),1000);
  };
  const stopTimer = () => {
    clearInterval(timerRef.current);
    const hrs = parseFloat((timer.elapsed/3600).toFixed(2));
    const entry = {id:Date.now(),worker:timer.worker||"Unknown",role:"Labor",date:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"}),hours:hrs,trade:timer.trade,notes:timer.notes,billable:true};
    save([...entries,entry]);
    setTimer({running:false,start:null,elapsed:0,worker:"",trade:"Demo",notes:""});
  };
  const addEntry = () => {
    if (!newEntry.worker||!newEntry.hours) return;
    save([...entries,{...newEntry,id:Date.now(),hours:parseFloat(newEntry.hours)||0}]);
    setNewEntry({worker:"",role:"Labor",date:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"}),hours:"",trade:"Demo",notes:"",billable:true});
    setShowAdd(false);
  };

  const fmtTimer = (s) => {
    const h=Math.floor(s/3600),m=Math.floor((s%3600)/60),sec=Math.floor(s%60);
    return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`;
  };

  const totalHours = entries.reduce((s,e)=>s+e.hours,0);
  const totalCost = entries.reduce((s,e)=>s+(e.hours*(RATES[e.role]||RATES["Laborer"])),0);
  const billableHours = entries.filter(e=>e.billable).reduce((s,e)=>s+e.hours,0);

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:14}}>
        {[
          {l:"Total Hours",v:totalHours.toFixed(1)+"h",c:T.blue},
          {l:"Billable Hours",v:billableHours.toFixed(1)+"h",c:T.green},
          {l:"Labor Cost",v:"$"+Math.round(totalCost).toLocaleString(),c:T.gold},
        ].map(s=>(
          <div key={s.l} style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:10,padding:"12px 14px",textAlign:"center"}}>
            <div style={{fontSize:10,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{s.l}</div>
            <div style={{fontSize:22,fontWeight:800,color:s.c,fontFamily:"'Space Mono',monospace"}}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Live Timer */}
      <Card style={{marginBottom:12}}>
        <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Live Timer</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr auto",gap:8,alignItems:"end",marginBottom:10}}>
          <div>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Worker</div>
            <input value={timer.worker} onChange={e=>setTimer(p=>({...p,worker:e.target.value}))} placeholder="Name" disabled={timer.running}
              style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"7px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
          </div>
          <div>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Trade</div>
            <select value={timer.trade} onChange={e=>setTimer(p=>({...p,trade:e.target.value}))} disabled={timer.running}
              style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"7px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}>
              {TRADES.map(t=><option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Notes</div>
            <input value={timer.notes} onChange={e=>setTimer(p=>({...p,notes:e.target.value}))} placeholder="What are you working on?"
              style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"7px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
          </div>
          <button onClick={timer.running?stopTimer:startTimer}
            style={{height:36,padding:"0 16px",borderRadius:8,border:"none",background:timer.running?T.red:T.green,color:"white",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>
            {timer.running?"⏹ Stop":"▶ Start"}
          </button>
        </div>
        {timer.running&&(
          <div style={{textAlign:"center",background:T.redBg,borderRadius:8,padding:"10px",marginTop:4}}>
            <div style={{fontSize:10,color:T.red,fontWeight:700,marginBottom:2}}>● RECORDING</div>
            <div style={{fontSize:28,fontWeight:800,color:T.text,fontFamily:"'Space Mono',monospace"}}>{fmtTimer(timer.elapsed)}</div>
          </div>
        )}
      </Card>

      {/* Add manual entry */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text}}>Time Log</div>
        <Btn small ghost onClick={()=>setShowAdd(true)}><Ic d={ICONS.plus} size={11}/>Add Manual</Btn>
      </div>

      {showAdd&&(
        <Card style={{marginBottom:10,background:T.bg2}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8,marginBottom:8}}>
            {[["worker","Worker","Lead Carpenter"],["hours","Hours","8"],["trade","Trade",""],["notes","Notes","Framing kitchen"]].map(([k,l,ph])=>(
              <div key={k}>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>{l}</div>
                {k==="trade"?
                  <select value={newEntry[k]} onChange={e=>setNewEntry(p=>({...p,[k]:e.target.value}))}
                    style={{width:"100%",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"6px 8px",color:T.text,fontSize:11,fontFamily:"inherit"}}>
                    {TRADES.map(t=><option key={t}>{t}</option>)}
                  </select>:
                  <input value={newEntry[k]} onChange={e=>setNewEntry(p=>({...p,[k]:e.target.value}))} placeholder={ph}
                    style={{width:"100%",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"6px 8px",color:T.text,fontSize:11,fontFamily:"inherit"}}/>}
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:6}}>
            <Btn ghost small onClick={()=>setShowAdd(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
            <Btn primary small disabled={!newEntry.worker||!newEntry.hours} onClick={addEntry} style={{flex:2,justifyContent:"center"}}>Add Entry</Btn>
          </div>
        </Card>
      )}

      {entries.map(e=>{
        const rate = RATES[e.role]||RATES["Laborer"];
        const cost = e.hours * rate;
        return (
          <div key={e.id} style={{display:"flex",gap:10,alignItems:"center",padding:"9px 12px",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:9,marginBottom:6}}>
            <div style={{width:3,height:36,borderRadius:2,background:T.blue,flexShrink:0}}/>
            <div style={{flex:1}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                <span style={{fontSize:12,fontWeight:700,color:T.text}}>{e.worker}</span>
                <span style={{fontSize:12,fontFamily:"'Space Mono',monospace",fontWeight:700,color:T.green}}>${Math.round(cost).toLocaleString()}</span>
              </div>
              <div style={{fontSize:10,color:T.muted,display:"flex",gap:10,flexWrap:"wrap"}}>
                <span>{e.hours}h @ ${rate}/hr</span>
                <span>{e.trade}</span>
                <span>{e.date}</span>
                {e.notes&&<span>{e.notes}</span>}
                {!e.billable&&<span style={{color:T.amber}}>Non-billable</span>}
              </div>
            </div>
            <button onClick={()=>save(entries.filter(x=>x.id!==e.id))}
              style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:14,padding:"4px"}}>×</button>
          </div>
        );
      })}
    </div>
  );
}

// ─── CLIENT PORTAL VIEW ───────────────────────────────────────────────────────
// ─── CLIENT PORTAL EXTERNAL LOGIN ────────────────────────────────────────────
// Owner/client opens a magic link → sees their project progress, COs, photos
function ClientPortalLogin({onSuccess}) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Check URL for token on load
  useEffect(()=>{
    const params = new URLSearchParams(window.location.search);
    const token = params.get("client_token");
    if (token) autoLogin(token);
  },[]);

  const autoLogin = (token) => {
    // Find project with this token
    const allKeys = Object.keys(localStorage).filter(k=>k.startsWith("grok_projects_"));
    for (const key of allKeys) {
      try {
        const projs = JSON.parse(localStorage.getItem(key)||"[]");
        const proj = projs.find(p=>p.client_token===token);
        if (proj) {
          const cid = key.replace("grok_projects_","");
          onSuccess({proj, companyId:cid, token});
          return;
        }
      } catch(e){}
    }
    setError("Invalid or expired link. Please contact your contractor.");
  };

  const loginWithCode = () => {
    setError(""); setLoading(true);
    const c = code.trim().toUpperCase();
    const allKeys = Object.keys(localStorage).filter(k=>k.startsWith("grok_projects_"));
    for (const key of allKeys) {
      try {
        const projs = JSON.parse(localStorage.getItem(key)||"[]");
        const proj = projs.find(p=>p.client_code===c||p.client_token?.startsWith(c));
        if (proj) {
          const cid = key.replace("grok_projects_","");
          onSuccess({proj, companyId:cid, token:c});
          setLoading(false);
          return;
        }
      } catch(e){}
    }
    setError("Code not found. Check with your contractor."); setLoading(false);
  };

  return (
    <div style={{minHeight:"100vh",background:T.bg0,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{width:"100%",maxWidth:400,background:T.bg1,borderRadius:16,padding:32,border:"1px solid "+T.subtle}}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{fontSize:28,fontWeight:800,color:T.gold,marginBottom:4}}>🏗 Grok</div>
          <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:4}}>Client Project Portal</div>
          <div style={{fontSize:12,color:T.muted}}>Enter your access code to view your project</div>
        </div>
        <div style={{marginBottom:12}}>
          <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Access Code</div>
          <input value={code} onChange={e=>setCode(e.target.value.toUpperCase())}
            onKeyDown={e=>e.key==="Enter"&&loginWithCode()}
            placeholder="Enter code from your contractor"
            style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"12px",color:T.text,fontSize:16,fontFamily:"inherit",textAlign:"center",letterSpacing:"0.2em",fontWeight:700}}/>
        </div>
        {error&&<div style={{background:T.redBg,border:"1px solid "+T.red+"30",borderRadius:8,padding:"9px 12px",fontSize:12,color:T.red,marginBottom:12}}>{error}</div>}
        <button onClick={loginWithCode} disabled={!code.trim()||loading}
          style={{width:"100%",padding:13,background:code.trim()?T.gold:T.bg3,color:code.trim()?T.bg0:T.muted,border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:code.trim()?"pointer":"not-allowed"}}>
          {loading?"Checking...":"View My Project →"}
        </button>
        <div style={{marginTop:16,fontSize:11,color:T.muted,textAlign:"center",lineHeight:1.6}}>
          Your access code was provided by your contractor.<br/>Contact them if you need a new one.
        </div>
      </div>
    </div>
  );
}

// ─── CLIENT PROJECT VIEW ──────────────────────────────────────────────────────
// Clean owner-facing view — progress, photos, COs to approve, docs to sign
function ClientProjectPortalView({proj, companyId, onLogout}) {
  const [tab, setTab] = useState("overview");

  const drawKey = "grok_draws_"+proj?.id;
  const photoKey = "grok_photolog_"+proj?.id;
  const cosKey = "grok_cos_global_"+companyId;
  const rfisKey = "grok_rfis_global_"+companyId;

  const draws = (()=>{try{return JSON.parse(localStorage.getItem(drawKey)||"[]");}catch{return [];}})();
  const photos = (()=>{try{return JSON.parse(localStorage.getItem(photoKey)||"[]");}catch{return [];}})();
  const allCOs = (()=>{try{return JSON.parse(localStorage.getItem(cosKey)||"[]");}catch{return [];}})();
  const projCOs = allCOs.filter(c=>String(c.projectId)===String(proj?.id));
  const pendingCOs = projCOs.filter(c=>c.status==="pending");
  const approvedCOs = projCOs.filter(c=>c.status==="approved");

  const contractVal = (proj?.value||0)+approvedCOs.reduce((s,c)=>s+(c.amount||0),0);
  const totalPaid = draws.filter(d=>d.status==="paid").reduce((s,d)=>s+(d.amount||0),0);
  const fmt = v=>"$"+Math.round(v||0).toLocaleString();
  const pct = proj?.pct||0;

  const TABS = [{id:"overview",label:"Overview"},{id:"progress",label:"Progress"},{id:"cos",label:`Change Orders${pendingCOs.length>0?" ("+pendingCOs.length+")":""}`},{id:"photos",label:"Photos"},{id:"billing",label:"Billing"}];

  return (
    <div style={{minHeight:"100vh",background:T.bg0,fontFamily:"system-ui,-apple-system,sans-serif"}}>
      {/* Header */}
      <div style={{background:T.bg1,borderBottom:"1px solid "+T.subtle,padding:"14px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,zIndex:10}}>
        <div>
          <div style={{fontSize:14,fontWeight:800,color:T.gold}}>🏗 Grok Client Portal</div>
          <div style={{fontSize:11,color:T.muted,marginTop:1}}>{proj?.name}</div>
        </div>
        <button onClick={onLogout} style={{fontSize:11,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"5px 12px",cursor:"pointer",color:T.muted}}>Sign Out</button>
      </div>

      <div style={{maxWidth:640,margin:"0 auto",padding:"16px 16px 80px"}}>
        {/* Tab bar */}
        <div style={{display:"flex",gap:4,marginBottom:16,overflowX:"auto",paddingBottom:4,scrollbarWidth:"none"}}>
          {TABS.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{padding:"8px 14px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",whiteSpace:"nowrap",background:tab===t.id?T.gold:T.bg1,color:tab===t.id?T.bg0:T.muted,flexShrink:0}}>
              {t.label}
            </button>
          ))}
        </div>

        {/* OVERVIEW */}
        {tab==="overview"&&(
          <div>
            <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:12,padding:"20px",marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
                <div>
                  <div style={{fontSize:16,fontWeight:800,color:T.text}}>{proj?.name}</div>
                  <div style={{fontSize:12,color:T.muted,marginTop:2}}>{proj?.addr||proj?.address}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:28,fontWeight:800,color:T.gold}}>{pct}%</div>
                  <div style={{fontSize:10,color:T.muted}}>Complete</div>
                </div>
              </div>
              <div style={{height:10,background:T.bg3,borderRadius:5,overflow:"hidden",marginBottom:14}}>
                <div style={{height:"100%",width:pct+"%",background:`linear-gradient(90deg,${T.gold},${T.green})`,borderRadius:5,transition:"width 0.5s"}}/>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                {[
                  {l:"Contract",v:fmt(contractVal),c:T.gold},
                  {l:"Paid to Date",v:fmt(totalPaid),c:T.green},
                  {l:"Remaining",v:fmt(contractVal-totalPaid),c:T.blue},
                ].map(s=>(
                  <div key={s.l} style={{background:T.bg2,borderRadius:8,padding:"10px",textAlign:"center"}}>
                    <div style={{fontSize:16,fontWeight:800,color:s.c}}>{s.v}</div>
                    <div style={{fontSize:9,color:T.muted}}>{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
            {pendingCOs.length>0&&(
              <div style={{background:T.amberBg,border:"1px solid "+T.amber+"40",borderRadius:10,padding:"12px 16px",marginBottom:14}}>
                <div style={{fontSize:12,fontWeight:700,color:T.amber,marginBottom:6}}>⚠️ {pendingCOs.length} Change Order{pendingCOs.length>1?"s":""} Awaiting Your Approval</div>
                {pendingCOs.map(co=>(
                  <div key={co.id} style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"4px 0"}}>
                    <span style={{color:T.text}}>{co.id} — {(co.desc||"").slice(0,40)}</span>
                    <span style={{color:T.amber,fontWeight:700}}>{fmt(co.amount)}</span>
                  </div>
                ))}
                <button onClick={()=>setTab("cos")} style={{marginTop:8,fontSize:11,fontWeight:700,background:T.amber,color:T.bg0,border:"none",borderRadius:6,padding:"5px 12px",cursor:"pointer"}}>Review & Approve →</button>
              </div>
            )}
          </div>
        )}

        {/* PROGRESS */}
        {tab==="progress"&&(
          <div>
            <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"14px 16px",marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Phase Progress</div>
              {["Demo","Rough Framing","Rough MEP","Insulation/Drywall","Tile/Flooring","Paint","Punch/Closeout"].map((ph,i)=>{
                const pct = (proj?.phases||[])[i]||0;
                return (
                  <div key={i} style={{marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3}}>
                      <span style={{color:T.text,fontWeight:pct>0?600:400}}>{ph}</span>
                      <span style={{color:pct===100?T.green:pct>0?T.amber:T.muted,fontWeight:700}}>{pct===100?"✓ Done":pct>0?pct+"%":"Not started"}</span>
                    </div>
                    <div style={{height:5,background:T.bg3,borderRadius:3}}>
                      <div style={{height:"100%",width:pct+"%",background:pct===100?T.green:T.amber,borderRadius:3,transition:"width 0.4s"}}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* CHANGE ORDERS */}
        {tab==="cos"&&(
          <div>
            {pendingCOs.length===0&&approvedCOs.length===0&&(
              <div style={{textAlign:"center",padding:"40px",color:T.muted,background:T.bg1,borderRadius:10}}>No change orders yet</div>
            )}
            {pendingCOs.map(co=>(
              <div key={co.id} style={{background:T.bg1,border:"2px solid "+T.amber,borderRadius:10,padding:"14px 16px",marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:T.text}}>{co.id}</div>
                    <div style={{fontSize:12,color:T.muted,marginTop:2}}>{co.desc||co.description}</div>
                  </div>
                  <div style={{fontSize:20,fontWeight:800,color:T.amber}}>{fmt(co.amount)}</div>
                </div>
                {co.reason&&<div style={{fontSize:11,color:T.muted,marginBottom:10,background:T.bg2,borderRadius:6,padding:"7px 10px"}}>{co.reason}</div>}
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>{
                    const key="grok_cos_global_"+companyId;
                    const all=JSON.parse(localStorage.getItem(key)||"[]");
                    localStorage.setItem(key,JSON.stringify(all.map(c=>c.id===co.id?{...c,status:"approved",approvedBy:"Owner",approvedDate:new Date().toLocaleDateString()}:c)));
                    alert("✓ Change order approved. Your contractor has been notified.");
                  }} style={{flex:1,padding:"9px",background:T.green,color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>✓ Approve</button>
                  <button onClick={()=>{
                    const reason=window.prompt("Reason for declining (optional):");
                    const key="grok_cos_global_"+companyId;
                    const all=JSON.parse(localStorage.getItem(key)||"[]");
                    localStorage.setItem(key,JSON.stringify(all.map(c=>c.id===co.id?{...c,status:"declined",declinedReason:reason||""}:c)));
                    alert("Change order declined. Your contractor has been notified.");
                  }} style={{flex:1,padding:"9px",background:T.bg2,color:T.red,border:"1px solid "+T.red+"40",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>✕ Decline</button>
                </div>
              </div>
            ))}
            {approvedCOs.map(co=>(
              <div key={co.id} style={{background:T.bg1,border:"1px solid "+T.green+"40",borderRadius:10,padding:"12px 16px",marginBottom:8,opacity:0.8}}>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <div>
                    <span style={{fontSize:12,fontWeight:700,color:T.text}}>{co.id}</span>
                    <span style={{fontSize:11,color:T.muted,marginLeft:8}}>{(co.desc||"").slice(0,50)}</span>
                  </div>
                  <span style={{fontSize:11,fontWeight:700,color:T.green}}>✓ {fmt(co.amount)}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* PHOTOS */}
        {tab==="photos"&&(
          <div>
            {photos.length===0&&<div style={{textAlign:"center",padding:"40px",color:T.muted,background:T.bg1,borderRadius:10}}>No photos logged yet</div>}
            {photos.map(e=>(
              <div key={e.id} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 16px",marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <div style={{fontSize:12,fontWeight:700,color:T.text}}>{new Date(e.date).toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}</div>
                  {e.phase&&<span style={{fontSize:9,background:T.amberBg,color:T.amber,borderRadius:10,padding:"1px 7px",fontWeight:700}}>{e.phase}</span>}
                </div>
                <div style={{fontSize:12,color:T.muted,marginBottom:8,lineHeight:1.5}}>{e.work_performed}</div>
                {e.photos?.length>0&&(
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {e.photos.map((ph,i)=>(
                      <img key={i} src={ph.url} alt="" style={{width:80,height:80,objectFit:"cover",borderRadius:8,cursor:"pointer",border:"1px solid "+T.subtle}}
                        onClick={()=>window.open(ph.url,"_blank")}/>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* BILLING */}
        {tab==="billing"&&(
          <div>
            <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"14px 16px",marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Draw Schedule</div>
              {draws.length===0&&<div style={{fontSize:12,color:T.muted}}>No draws scheduled yet</div>}
              {draws.map((d,i)=>(
                <div key={d.id} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid "+T.subtle}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:T.text}}>Draw {i+1} — {d.name}</div>
                    <div style={{fontSize:10,color:T.muted}}>{d.trigger}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:13,fontWeight:700,color:d.status==="paid"?T.green:T.amber}}>{fmt(d.amount)}</div>
                    <div style={{fontSize:9,fontWeight:700,color:d.status==="paid"?T.green:T.amber}}>{d.status==="paid"?"✓ PAID":"PENDING"}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ClientPortalView({proj, projCOs, projRFIs, session}) {
  const [activeSection, setActiveSection] = useState("overview");
  // Contacts — editable inline, saved back to project
  const [contacts, setContacts] = React.useState({
    owner_name: proj?.owner_name || "",
    owner_email: proj?.owner_email || "",
    owner_phone: proj?.owner_phone || "",
    architect_name: proj?.architect_name || "",
    architect_email: proj?.architect_email || "",
    architect_phone: proj?.architect_phone || "",
    engineer_name: proj?.engineer_name || "",
    engineer_email: proj?.engineer_email || "",
  });
  const [editingContacts, setEditingContacts] = React.useState(false);
  const ownerEmail = contacts.owner_email; // alias for CO send functions
  const saveContacts = (updated) => {
    setContacts(updated);
    try {
      const key = projectsKey(window._grokCompanyId||"local");
      const projs = JSON.parse(localStorage.getItem(key)||"[]");
      localStorage.setItem(key, JSON.stringify(projs.map(p=>p.id===proj?.id?{...p,...updated}:p)));
    } catch(e) {}
  };
  const [clientCode, setClientCode] = useState(proj?.client_code||"");
  const [codeCopied, setCodeCopied] = useState(false);

  const generateClientCode = () => {
    const code = (proj?.name||"PROJ").replace(/[^A-Z0-9]/gi,"").slice(0,4).toUpperCase()+"-"+Math.random().toString(36).slice(2,6).toUpperCase();
    setClientCode(code);
    // Save to project
    try {
      const key = projectsKey(window._grokCompanyId||"local");
      const projs = JSON.parse(localStorage.getItem(key)||"[]");
      localStorage.setItem(key, JSON.stringify(projs.map(p=>p.id===proj?.id?{...p,client_code:code}:p)));
    } catch(e){}
    // Copy link
    const link = window.location.origin+"?client_token="+code;
    navigator.clipboard?.writeText(link).catch(()=>{});
    setCodeCopied(true);
    setTimeout(()=>setCodeCopied(false),3000);
  };
  const [updateMsg, setUpdateMsg] = useState("");
  const [customNote, setCustomNote] = useState("");
  const [sentUpdates, setSentUpdates] = useState([]);

  const progress = proj?.pct || proj?.progress || 42;
  const pendingCOs = projCOs?.filter(c=>c.status==="pending") || [];
  const openRFIs = projRFIs?.filter(r=>r.status!=="closed") || [];

  const generateOwnerUpdate = () => {
    const today = new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"});
    const body =
`Hi,

Here's your project update for ${proj?.name} — ${today}.

PROGRESS: ${progress}% complete

WHAT WAS COMPLETED THIS WEEK:
• Demo and protection complete in kitchen and bathrooms
• Rough plumbing inspection passed
• Drywall delivered and staged

NEXT 2 WEEKS:
• Electrical rough-in (Days 1-5)
• Drywall installation (Days 6-10)
• Tile work begins after electrical sign-off

${pendingCOs.length>0?`CHANGE ORDERS PENDING YOUR APPROVAL:\n${pendingCOs.map(co=>`• ${co.id} — ${co.desc||co.description} — $${(co.amount||0).toLocaleString()}\n  Please reply to approve or decline.`).join("\n")}\n`:""}${openRFIs.length>0?`OPEN QUESTIONS:\n${openRFIs.slice(0,3).map(r=>`• ${r.subject||r.desc}`).join("\n")}\n`:""}${customNote?`\nNOTE FROM JEHOON:\n${customNote}\n`:""}
Please don't hesitate to reach out with any questions.

Best,
${session?.ownerName || "Jehoon Chun"}
${session?.companyName || "Grok Construction Group Inc"}
CSLB #${session?.cslb || "1152750"}
${session?.email || "Jehoon@gcginc.com"}`;
    return body;
  };

  const sendUpdate = () => {
    const body = generateOwnerUpdate();
    const subject = encodeURIComponent(`Project Update — ${proj?.name} — ${new Date().toLocaleDateString()}`);
    const bodyEnc = encodeURIComponent(body);
    const mailto = `mailto:${ownerEmail}?subject=${subject}&body=${bodyEnc}`;
    window.location.href = mailto;
    setSentUpdates(prev=>[{id:Date.now(),date:new Date().toLocaleDateString(),note:customNote||"Regular update"},...prev]);
    setCustomNote("");
  };

  const SectionBtn = ({id,label,badge}) => (
    <button onClick={()=>setActiveSection(id)}
      style={{padding:"7px 14px",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:activeSection===id?T.gold:T.bg2,color:activeSection===id?T.bg0:T.muted,transition:"all 0.15s",display:"flex",alignItems:"center",gap:6,whiteSpace:"nowrap"}}>
      {label}{badge>0&&<span style={{background:T.red,color:"white",fontSize:9,padding:"1px 5px",borderRadius:10,fontWeight:800}}>{badge}</span>}
    </button>
  );

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>Client / Owner Portal</div>
          <div style={{fontSize:11,color:T.muted}}>What your owner sees · Send updates · Track approvals</div>
        </div>
        <div style={{display:"flex",gap:6}}>
          <div>
            <input value={ownerEmail} onChange={e=>setContacts(p=>({...p,owner_email:e.target.value}))} placeholder="Owner email address"
              style={{background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"7px 12px",color:T.text,fontSize:12,fontFamily:"inherit",width:240}}/>
          </div>
        </div>
      </div>

      <div style={{display:"flex",gap:4,marginBottom:14,flexWrap:"wrap"}}>
        <SectionBtn id="overview" label="Overview"/>
        <SectionBtn id="progress" label="Progress & Photos"/>
        <SectionBtn id="cos" label="Change Orders" badge={pendingCOs.length}/>
        <SectionBtn id="schedule" label="Schedule"/>
        <SectionBtn id="send" label="📧 Send Update"/>
      </div>

      {activeSection==="overview"&&(
        <div>
          {/* Client Access Code */}
          <div style={{background:T.bg1,border:"1px solid "+T.teal+"40",borderRadius:12,padding:"14px 16px",marginBottom:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:T.teal}}>🔗 Client Portal Access</div>
                <div style={{fontSize:10,color:T.muted,marginTop:2}}>Give your owner/client a code to view progress, photos, and approve COs</div>
              </div>
              <button onClick={generateClientCode}
                style={{fontSize:11,fontWeight:700,background:T.teal,color:"white",border:"none",borderRadius:7,padding:"6px 12px",cursor:"pointer",flexShrink:0}}>
                {clientCode?"🔄 New Code":"🔑 Generate Code"}
              </button>
            </div>
            {clientCode&&(
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <div style={{flex:1,background:T.bg2,border:"1px solid "+T.teal,borderRadius:8,padding:"10px 14px",fontFamily:"monospace",fontSize:18,fontWeight:800,color:T.teal,letterSpacing:"0.2em",textAlign:"center"}}>
                  {clientCode}
                </div>
                <button onClick={()=>{
                  const link=window.location.origin+"?client_token="+clientCode;
                  navigator.clipboard?.writeText(link).then(()=>{setCodeCopied(true);setTimeout(()=>setCodeCopied(false),2000);}).catch(()=>alert("Link: "+link));
                }} style={{padding:"10px 14px",background:codeCopied?T.greenBg:T.bg2,border:"1px solid "+(codeCopied?T.green:T.subtle),borderRadius:8,fontSize:11,fontWeight:700,color:codeCopied?T.green:T.muted,cursor:"pointer",whiteSpace:"nowrap"}}>
                  {codeCopied?"✓ Copied!":"📋 Copy Link"}
                </button>
              </div>
            )}
            {clientCode&&<div style={{fontSize:10,color:T.muted,marginTop:6}}>Share the code or link with your client. They visit grokfieldops.com and enter the code.</div>}
          </div>

          {/* What owner sees */}
          <div style={{background:`linear-gradient(135deg,#1a1a2e,#16213e)`,borderRadius:14,padding:"24px",marginBottom:14}}>
            <div style={{fontSize:10,fontWeight:700,color:T.gold,textTransform:"uppercase",letterSpacing:"0.15em",marginBottom:4}}>CLIENT VIEW — {proj?.name}</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.6)",marginBottom:20}}>What your owner sees in their update emails</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
              {[
                {l:"Project Status",v:proj?.status?.toUpperCase()||"ACTIVE",c:T.green},
                {l:"Completion",v:progress+"%",c:T.gold},
                {l:"Pending COs",v:pendingCOs.length,c:pendingCOs.length>0?T.red:T.muted},
                {l:"Open RFIs",v:openRFIs.length,c:openRFIs.length>0?T.amber:T.muted},
              ].map(s=>(
                <div key={s.l} style={{background:"rgba(255,255,255,0.05)",borderRadius:10,padding:"12px 14px"}}>
                  <div style={{fontSize:9,color:"rgba(255,255,255,0.4)",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:4}}>{s.l}</div>
                  <div style={{fontSize:20,fontWeight:800,color:s.c}}>{s.v}</div>
                </div>
              ))}
            </div>
            <div style={{height:8,background:"rgba(255,255,255,0.1)",borderRadius:4,overflow:"hidden",marginBottom:6}}>
              <div style={{height:"100%",width:progress+"%",background:`linear-gradient(to right,${T.gold},${T.teal})`,borderRadius:4}}/>
            </div>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",textAlign:"right"}}>{progress}% complete</div>
          </div>

          {pendingCOs.length>0&&(
            <Card style={{marginBottom:12,border:`1px solid ${T.red}30`,background:T.redBg}}>
              <div style={{fontSize:12,fontWeight:700,color:T.red,marginBottom:8}}>⚠ {pendingCOs.length} Change Order{pendingCOs.length>1?"s":""} Awaiting Owner Approval</div>
              {pendingCOs.map(co=>(
                <div key={co.id} style={{fontSize:12,color:T.text,display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${T.subtle}`}}>
                  <span>{co.id} — {co.desc||co.description}</span>
                  <span style={{fontWeight:700,color:T.red}}>${(co.amount||0).toLocaleString()}</span>
                </div>
              ))}
            </Card>
          )}
        </div>
      )}

      {activeSection==="send"&&(
        <Card>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14}}>Send Owner Update Email</div>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Add a personal note (optional)</div>
            <textarea value={customNote} onChange={e=>setCustomNote(e.target.value)} rows={3}
              placeholder="Any specific notes for this update — decisions needed, upcoming site visits, materials to confirm..."
              style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit"}}/>
          </div>
          <div style={{background:T.bg0,borderRadius:8,padding:"14px",marginBottom:14,fontSize:11,color:T.muted,fontFamily:"monospace",lineHeight:1.8,maxHeight:300,overflowY:"auto",whiteSpace:"pre-wrap"}}>
            {generateOwnerUpdate()}
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn ghost onClick={()=>navigator.clipboard.writeText(generateOwnerUpdate())} style={{flex:1,justifyContent:"center"}}>Copy Email</Btn>
            <Btn primary disabled={!ownerEmail} onClick={sendUpdate}
              style={{flex:2,justifyContent:"center",background:`linear-gradient(135deg,${T.blue},${T.teal})`,color:"white"}}>
              📧 Open in Mail App
            </Btn>
          </div>
          {!ownerEmail&&<div style={{fontSize:11,color:T.amber,marginTop:8,textAlign:"center"}}>Add owner email address above to send update</div>}
          {sentUpdates.length>0&&(
            <div style={{marginTop:14}}>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Recent Updates Sent</div>
              {sentUpdates.slice(0,5).map(u=>(
                <div key={u.id} style={{fontSize:11,color:T.muted,display:"flex",gap:10,padding:"4px 0"}}>
                  <span style={{color:T.green}}>✓</span><span>{u.date}</span><span>{u.note}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {activeSection==="cos"&&(
        <div>
          {pendingCOs.length===0?(
            <div style={{textAlign:"center",padding:"40px",color:T.muted,fontSize:13}}>No pending change orders requiring owner approval.</div>
          ):pendingCOs.map(co=>(
            <Card key={co.id} style={{marginBottom:10,border:`1px solid ${T.red}30`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:T.text}}>{co.id} — {co.desc||co.description}</div>
                  <div style={{fontSize:11,color:T.muted}}>Submitted {co.date} · Pending owner signature</div>
                </div>
                <div style={{fontSize:20,fontWeight:800,color:T.red,fontFamily:"'Space Mono',monospace"}}>${(co.amount||0).toLocaleString()}</div>
              </div>
              <Btn small ghost onClick={()=>{
                const s=encodeURIComponent(`Change Order Approval Required — ${co.id}`);
                const b=encodeURIComponent(`Hi,\n\nChange Order ${co.id} requires your approval:\n\n${co.desc||co.description}\nAmount: $${(co.amount||0).toLocaleString()}\n\nPlease reply APPROVED or DECLINED.\n\nThank you,\n${session?.ownerName||"Jehoon"}`);
                window.location.href=`mailto:${ownerEmail||""}?subject=${s}&body=${b}`;
              }} style={{fontSize:11}}>📧 Send for Approval</Btn>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}



// ─── PDF EXPORT (print-to-PDF via browser) ───────────────────────────────────
function openPDFWindow(html, title) {
  const win = window.open("", "_blank", "width=900,height=700");
  if (!win) { alert("Allow popups to export PDFs"); return; }
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 500);
}

// Send CO for approval via mailto (in-app email template)
function sendCOForApproval(co, proj, session) {
  const cs = (()=>{try{return JSON.parse(localStorage.getItem(window._grokCompanyKey||"grok_company_settings")||"{}");}catch{return{};}})();
  const gcName  = cs.co_header_name || session?.companyName || "GC";
  const gcEmail = cs.co_header_email || session?.email || "";
  const toEmail = co.send_to === "architect" ? (proj?.architect_email||"") : (proj?.owner_email||"");
  const toName  = co.send_to === "architect" ? "Architect" : "Owner";

  const subject = encodeURIComponent(`Change Order #${co.id} — ${proj?.name} — Approval Required`);
  const body = encodeURIComponent(`Dear ${toName},

Please review and approve the following Change Order for project ${proj?.name} at ${proj?.addr||""}.

CHANGE ORDER #${co.id}
Date: ${new Date().toLocaleDateString()}
Description: ${co.description}
Amount: $${Math.round(co.amount||0).toLocaleString()}
Reason: ${co.reason||"See attached"}

${cs.co_terms||"This Change Order becomes part of the original contract when signed by both parties."}

To approve, please reply to this email with your written approval or sign the attached PDF.

Thank you,
${gcName}
${gcEmail}`);

  const mailto = `mailto:${toEmail}?subject=${subject}&body=${body}`;
  window.open(mailto);
  return { sent: true, to: toEmail };
}

// Send RFI for response via mailto
function sendRFIForResponse(rfi, proj, session) {
  const cs = (()=>{try{return JSON.parse(localStorage.getItem(window._grokCompanyKey||"grok_company_settings")||"{}");}catch{return{};}})();
  const gcName  = cs.co_header_name || session?.companyName || "GC";
  const gcEmail = cs.co_header_email || session?.email || "";
  const toEmail = rfi.send_to === "architect" ? (proj?.architect_email||"") : (proj?.owner_email||"");
  const toName  = rfi.send_to === "architect" ? "Architect" : "Owner";

  const subject = encodeURIComponent(`RFI #${rfi.id} — ${proj?.name} — Response Required`);
  const body = encodeURIComponent(`Dear ${toName},

Please provide a response to the following Request for Information for project ${proj?.name}.

RFI #${rfi.id}
Date: ${new Date().toLocaleDateString()}
Subject: ${rfi.subject}
Drawing Reference: ${rfi.drawing||"N/A"}
Question: ${rfi.description}

Priority: ${rfi.priority||"Routine"}
${rfi.affects_schedule?"⚠ This RFI may affect the project schedule.":""}

${cs.rfi_terms||"Please respond within 48 hours."}

Thank you,
${gcName}
${gcEmail}`);

  window.open(`mailto:${toEmail}?subject=${subject}&body=${body}`);
  return { sent: true, to: toEmail };
}

// Find a company-uploaded document template by category (e.g. "co", "rfi",
// "contracts"). Tries the current company id first, then falls back to a
// couple of legacy keys (older seeded templates). Returns null if none.
// California Civil Code §8132 Conditional Waiver and Release on Progress
// Payment. The statute requires this EXACT form text — substituting custom
// wording invalidates the waiver. We pre-fill the identifying-info fields
// from the pay app + project + GC session, leave Exceptions blank for the
// claimant to add manually, and leave Signature for the GC to sign.
//
// Returns full standalone HTML ready to print/save as PDF.
function generateGCConditionalWaiverHTML(app, proj, session) {
  const cs = (() => { try { return JSON.parse(localStorage.getItem(window._grokCompanyKey || "grok_company_settings") || "{}"); } catch { return {}; } })();
  const claimantName = cs.co_header_name || session?.companyName || "Grok Construction Group Inc";
  const claimantCslb = cs.co_header_cslb || session?.cslb || "1152750";
  const customerName = proj?.owner_name || proj?.client || "Owner";
  const jobLoc = proj?.addr || proj?.name || "Project Site";
  const ownerName = proj?.owner_name || proj?.client || "Owner";
  const amount = "$" + Math.round((app?.amount_due || 0)).toLocaleString();
  const through = app?.period_to || new Date().toISOString().slice(0, 10);
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>Conditional Waiver §8132 — ${claimantName}</title>
<style>
  body{font-family:'Times New Roman',serif;padding:48px;max-width:780px;margin:0 auto;color:#000;font-size:12pt;line-height:1.5}
  .ref{font-size:10pt;color:#555;text-align:right;margin-bottom:14px}
  .notice{border:2px solid #000;padding:14px 16px;text-align:center;font-weight:700;text-transform:uppercase;font-size:11pt;line-height:1.4;margin-bottom:22px}
  h1{text-align:center;font-size:14pt;margin:0 0 22px;text-transform:uppercase;letter-spacing:.04em}
  h2{font-size:11pt;text-transform:uppercase;letter-spacing:.04em;margin:20px 0 8px;border-bottom:1px solid #000;padding-bottom:3px}
  p{margin:0 0 12px}
  .field{display:grid;grid-template-columns:200px 1fr;gap:8px;padding:4px 0;border-bottom:1px solid #ccc}
  .field b{font-weight:700}.field span{border-bottom:none}
  .sig{margin-top:32px;padding-top:14px;border-top:2px solid #000;display:grid;grid-template-columns:1fr 1fr;gap:32px}
  .sig div{font-size:10pt;color:#555;border-top:1px solid #000;padding-top:6px;margin-top:32px}
  @media print{body{padding:24px}}
</style></head><body>
<div class="ref">Pay App ${app?.app_num || ""} · Generated ${today}</div>
<div class="notice">
  Notice: This document waives the claimant's lien, stop payment notice, and payment bond rights effective on receipt of payment. A person should not rely on this document unless satisfied that the claimant has received payment.
</div>
<h1>Conditional Waiver and Release on Progress Payment<br/>(California Civil Code §8132)</h1>

<h2>Identifying Information</h2>
<div class="field"><b>Name of Claimant:</b><span>${claimantName} &nbsp; (CSLB #${claimantCslb})</span></div>
<div class="field"><b>Name of Customer:</b><span>${customerName}</span></div>
<div class="field"><b>Job Location:</b><span>${jobLoc}</span></div>
<div class="field"><b>Owner:</b><span>${ownerName}</span></div>
<div class="field"><b>Through Date:</b><span>${through}</span></div>

<h2>Conditional Waiver and Release</h2>
<p>This document waives and releases lien, stop payment notice, and payment bond rights the claimant has for labor and service provided, and equipment and material delivered, to the customer on this job through the Through Date of this document. Rights based upon labor or service provided, or equipment or material delivered, pursuant to a written change order that has been fully executed by the parties prior to the date that this document is signed by the claimant, are waived and released by this document, unless listed as an Exception below. This document is effective only on the claimant's receipt of payment from the financial institution on which the following check is drawn:</p>

<div class="field"><b>Maker of Check:</b><span>${ownerName}</span></div>
<div class="field"><b>Amount of Check:</b><span>${amount}</span></div>
<div class="field"><b>Check Payable to:</b><span>${claimantName}</span></div>

<h2>Exceptions</h2>
<p>This document does not affect any of the following:</p>
<p>(1) Retentions.<br/>
(2) Extras for which the claimant has not received payment.<br/>
(3) The following progress payments for which the claimant has previously given a conditional waiver and release but has not received payment: <em>none unless listed here</em>.<br/>
(4) Contract rights, including (A) a right based on rescission, abandonment, or breach of contract, and (B) the right to recover compensation for work not compensated by the payment.</p>

<h2>Signature</h2>
<div class="sig">
  <div>Claimant's Signature &nbsp;&nbsp;&nbsp; Date</div>
  <div>Claimant's Title</div>
</div>
</body></html>`;
}

// California Civil Code §8134 Unconditional Waiver and Release on Progress
// Payment. The statute requires this EXACT form text. Used after a pay-app
// payment has actually cleared at the bank — the unconditional waiver
// permanently releases lien rights for the work through the through-date.
//
// ARIA generates this automatically when the GC marks an approved pay app
// as "Payment Cleared" — Section 10 golden rule: conditional → payment
// clears → unconditional, never the other order.
function generateGCUnconditionalWaiverHTML(app, proj, session) {
  const cs = (() => { try { return JSON.parse(localStorage.getItem(window._grokCompanyKey || "grok_company_settings") || "{}"); } catch { return {}; } })();
  const claimantName = cs.co_header_name || session?.companyName || "Grok Construction Group Inc";
  const claimantCslb = cs.co_header_cslb || session?.cslb || "1152750";
  const customerName = proj?.owner_name || proj?.client || "Owner";
  const jobLoc = proj?.addr || proj?.name || "Project Site";
  const ownerName = proj?.owner_name || proj?.client || "Owner";
  const amount = "$" + Math.round((app?.amount_due || 0)).toLocaleString();
  const through = app?.period_to || new Date().toISOString().slice(0, 10);
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>Unconditional Waiver §8134 — ${claimantName}</title>
<style>
  body{font-family:'Times New Roman',serif;padding:48px;max-width:780px;margin:0 auto;color:#000;font-size:12pt;line-height:1.5}
  .ref{font-size:10pt;color:#555;text-align:right;margin-bottom:14px}
  .notice{border:2px solid #b91c1c;padding:14px 16px;text-align:center;font-weight:700;text-transform:uppercase;font-size:11pt;line-height:1.4;margin-bottom:22px;color:#b91c1c}
  h1{text-align:center;font-size:14pt;margin:0 0 22px;text-transform:uppercase;letter-spacing:.04em}
  h2{font-size:11pt;text-transform:uppercase;letter-spacing:.04em;margin:20px 0 8px;border-bottom:1px solid #000;padding-bottom:3px}
  p{margin:0 0 12px}
  .field{display:grid;grid-template-columns:200px 1fr;gap:8px;padding:4px 0;border-bottom:1px solid #ccc}
  .field b{font-weight:700}
  .sig{margin-top:32px;padding-top:14px;border-top:2px solid #000;display:grid;grid-template-columns:1fr 1fr;gap:32px}
  .sig div{font-size:10pt;color:#555;border-top:1px solid #000;padding-top:6px;margin-top:32px}
  @media print{body{padding:24px}}
</style></head><body>
<div class="ref">Pay App ${app?.app_num || ""} · Generated ${today}</div>
<div class="notice">
  Notice to Claimant: This document waives and releases lien, stop payment notice, and payment bond rights unconditionally and states that you have been paid for giving up those rights. This document is enforceable against you if you sign it, even if you have not been paid. If you have not been paid, use a conditional waiver and release form.
</div>
<h1>Unconditional Waiver and Release on Progress Payment<br/>(California Civil Code §8134)</h1>

<h2>Identifying Information</h2>
<div class="field"><b>Name of Claimant:</b><span>${claimantName} &nbsp; (CSLB #${claimantCslb})</span></div>
<div class="field"><b>Name of Customer:</b><span>${customerName}</span></div>
<div class="field"><b>Job Location:</b><span>${jobLoc}</span></div>
<div class="field"><b>Owner:</b><span>${ownerName}</span></div>
<div class="field"><b>Through Date:</b><span>${through}</span></div>

<h2>Unconditional Waiver and Release</h2>
<p>This document waives and releases lien, stop payment notice, and payment bond rights the claimant has for labor and service provided, and equipment and material delivered, to the customer on this job through the Through Date of this document. Rights based upon labor or service provided, or equipment or material delivered, pursuant to a written change order that has been fully executed by the parties prior to the date that this document is signed by the claimant, are waived and released by this document, unless listed as an Exception below. The claimant has been paid in the following amount:</p>

<div class="field"><b>Amount Paid:</b><span>${amount}</span></div>

<h2>Exceptions</h2>
<p>This document does not affect any of the following:</p>
<p>(1) Disputed claims for extras in the amount of: $______ <em>(fill in or leave blank if none)</em><br/>
(2) Retentions.<br/>
(3) Contract rights, including (A) a right based on rescission, abandonment, or breach of contract, and (B) the right to recover compensation for work not compensated by the payment.</p>

<h2>Signature</h2>
<div class="sig">
  <div>Claimant's Signature &nbsp;&nbsp;&nbsp; Date</div>
  <div>Claimant's Title</div>
</div>
</body></html>`;
}

// Coerce whatever shape the AI proxy returned in `data.error` into a readable
// string for an Error message. Proxy historically returned `{error: "msg"}`
// but Anthropic upstream errors come through as `{error: {message, type}}`
// or `{error: {error: {message}}}` — naively passing the object into
// `throw new Error()` produces "[object Object]" which then surfaces in UI.
function errString(e) {
  if (e == null) return "";
  if (typeof e === "string") return e;
  if (typeof e === "object") {
    return e.message || e.msg || (e.error && (e.error.message || e.error)) || JSON.stringify(e).slice(0, 200);
  }
  return String(e);
}

// Shared estimator pricing rules — injected into both QuickEstimateView and
// WalkthroughEstimatorView system prompts. Centralizes tier detection,
// pricing anchors, line-item structure, contingency rules, and the sanity
// checks that prevent ARIA from overpricing small jobs.
const PRICING_RULES = `## PROJECT TIER (decide first, internally)
- TIER A — SIMPLE: one room/scope, no structural change, no permits required, no concealed-condition risk. Examples: paint a room, swap a light fixture, replace a faucet, single window replacement (in-kind), single in-kind door swap (no opening change).
- TIER B — SMALL-MEDIUM: one or two trades, possibly minor framing/header, may need permit. Examples: door-to-opening conversion, window-to-door conversion, single bath fixture replacement set, replace a single kitchen counter, add a partition wall.
- TIER C — COMPLEX: multi-trade, structural modification, full permits, hidden conditions likely. Examples: SRO unit gut, kitchen remodel with wall removal, full bath gut, exterior envelope work, addition.

## PRICING ANCHORS (2026 SF Bay Area, GC pricing to owner)
- Single exterior door/opening conversion (TIER B), LABOR + FINISH ONLY (door, hardware, permit fees excluded): $8,500–$11,500.
- Single interior door/opening conversion: $6,000–$8,500.
- Exceed the above anchors ONLY when you actually detect one of:
   structural engineering · major reframing · difficult stucco match · scaffolding · electrical/plumbing relocation · difficult site access · premium finish requirements · multi-day waterproofing detailing.
   When you exceed, name the specific complexity factor in a line item — never inflate silently.
- Full bath gut (TIER C, 5×8 typical): $18,000–$32,000 depending on tile/fixture grade.
- Kitchen reno with wall removal (TIER C, 250–400 sf): $48,000–$95,000 depending on cabinet/counter grade.

## LINE-ITEM STRUCTURE (transparent, client-friendly)
TIER A/B: ALWAYS itemize each of these as a SEPARATE line (no bundling):
   1. Demo & site protection
   2. Framing / header modification (omit if no framing)
   3. [Trade] installation labor (e.g. "Door installation labor", "Fixture installation labor")
   4. Flashing / waterproofing / air sealing (omit if no exterior penetration)
   5. Stucco patch / texture / color match (omit if no exterior work)
   6. Interior drywall / texture / paint (omit if no interior touched)
   7. PM / supervision / warranty — ONE flat line, $300–$800 for TIER A/B (NOT a 25% multiplier)
TIER C: full itemization by trade is fine; break PM, GC fee, contingency as separate lines.

DO NOT:
- Bundle "demolition, framing, install, waterproofing" into a single $8k+ line.
- Apply 25% O&P + 8% GC + 12% contingency stacked on top of trade work for TIER A/B — that's hidden double-inflation.
- Apply markup that's already inside the line-item rate a second time at the bottom.
- Add "General Conditions" line on TIER A jobs.

## CONTINGENCY RULES
- TIER A: NEVER include contingency.
- TIER B: include ONLY when you detect concealed-condition risk (unknown framing behind stucco/drywall, water-damage suspicion, age-related unknowns in pre-1978 buildings). When included, render as a SEPARATE OPTIONAL line BELOW subtotal, NOT in the main total:
   "Optional contingency (concealed conditions) — billed only if used, unused credited to Owner: $X,XXX"
- TIER C: expected; same separate-optional treatment.
- The MAIN TOTAL never includes contingency. Show two totals if helpful: "Total: $X (or $Y with optional contingency)".

## INSPECTION COORDINATION
- Include "Permit & inspection coordination" line ONLY when the scope actually requires a permit (structural change, electrical rough-in, plumbing rough-in or fixture relocation, addition, change of use, new exterior penetration that triggers local code).
- Door/window in-kind replacement: usually NO permit in CA → no inspection line.
- Window-to-door conversion: usually DOES require permit (header sizing, egress) → include inspection line.

## SANITY CHECKS (do these internally before finalizing)
- If scope is a single door/opening conversion with no detected complexity AND your subtotal-before-contingency is > $12,000: recompute, you've overpriced.
- If the door/hardware is EXCLUDED, do NOT include door cost in any line item; instead add ONE line "Door & hardware allowance (Owner-supplied or separately quoted): $X" so the math is transparent.
- Permit-fee text must be ONE consistent value across all sections — never "$500 in cost summary" + "paid by Owner in exclusions". Pick one.
- Don't list "Permit fees included" AND "Permit fees by Owner" — that's contradictory.
- Never apply the same overhead twice (e.g. don't add a 20% markup line on top of trade lines that already have markup baked into the rate).
`;

// California Civil Code §8204 Preliminary Notice. Required to preserve lien
// rights — must be served on the owner (and lender if any) within 20 days
// of first providing labor/services/materials. Without it, the GC has NO
// lien rights for work performed before service.
//
// ARIA auto-drafts this when a project is created; the GC prints + serves
// (certified mail, with proof of service) within 20 days.
function generatePreliminaryNoticeHTML(proj, session) {
  const cs = (() => { try { return JSON.parse(localStorage.getItem(window._grokCompanyKey || "grok_company_settings") || "{}"); } catch { return {}; } })();
  const claimantName = cs.co_header_name || session?.companyName || "Grok Construction Group Inc";
  const claimantCslb = cs.co_header_cslb || session?.cslb || "1152750";
  const claimantAddr = cs.co_header_address || "San Mateo, CA";
  const claimantEmail = cs.co_header_email || session?.email || "";
  const claimantPhone = cs.co_header_phone || "";
  const ownerName = proj?.owner_name || proj?.client || "Owner";
  const jobLoc = proj?.addr || proj?.name || "Project Site";
  const projName = proj?.name || "Construction Project";
  const estPrice = proj?.value ? "$" + Math.round(proj.value).toLocaleString() : "TBD";
  const scopeDesc = proj?.desc || `General contracting services — ${proj?.type || "construction"}`;
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>Preliminary Notice §8204 — ${claimantName}</title>
<style>
  body{font-family:'Times New Roman',serif;padding:48px;max-width:780px;margin:0 auto;color:#000;font-size:12pt;line-height:1.55}
  .ref{font-size:10pt;color:#555;text-align:right;margin-bottom:14px}
  .notice{border:3px solid #000;padding:16px 18px;font-weight:700;font-size:11pt;line-height:1.5;margin-bottom:22px}
  .notice h3{margin:0 0 10px;font-size:13pt;text-transform:uppercase;letter-spacing:.04em}
  h1{text-align:center;font-size:14pt;margin:0 0 22px;text-transform:uppercase;letter-spacing:.04em}
  h2{font-size:11pt;text-transform:uppercase;letter-spacing:.04em;margin:20px 0 8px;border-bottom:1px solid #000;padding-bottom:3px}
  p{margin:0 0 12px}
  .field{margin-bottom:10px}.field b{display:block;font-size:10pt;text-transform:uppercase;letter-spacing:.05em;margin-bottom:2px}
  .field span{display:block;border-bottom:1px solid #999;padding:3px 0}
  .sig{margin-top:32px;padding-top:14px;border-top:2px solid #000;display:grid;grid-template-columns:1fr 1fr;gap:32px}
  .sig div{font-size:10pt;color:#555;border-top:1px solid #000;padding-top:6px;margin-top:32px}
  @media print{body{padding:24px}}
</style></head><body>
<div class="ref">Project: ${projName} · Generated ${today}</div>

<h1>Preliminary Notice<br/>(California Civil Code §8204)</h1>

<div class="notice">
  <h3>Notice to Property Owner</h3>
  <p>EVEN THOUGH YOU HAVE PAID YOUR CONTRACTOR IN FULL, if the person or firm that has given you this notice is not paid in full for labor, service, equipment, or material provided or to be provided to your construction project, a lien may be placed on your property. Foreclosure of the lien may lead to loss of all or part of your property. You may wish to protect yourself against this by (1) requiring your contractor to provide a signed release by the person or firm that has given you this notice before making payment to your contractor, or (2) any other method that is appropriate under the circumstances.</p>
  <p>This notice is required by law to be served by the undersigned as a statement of your legal rights. This notice is not intended to reflect upon the financial condition of the contractor or the person employed by you on the construction project.</p>
  <p>If you record a notice of cessation or completion of your construction project, you must within 10 days after recording, send a copy of the notice of completion to your contractor and subcontractors who have given you or your construction lender notice. The notice must be sent by registered or certified mail. Failure to send the notice will extend the deadline to record a claim of lien. You are not required to send the notice if you are a residential homeowner of a dwelling containing four or fewer units.</p>
</div>

<h2>(A) Person Providing Labor, Services, Equipment, or Material</h2>
<div class="field"><b>Name:</b><span>${claimantName} &nbsp; (CSLB #${claimantCslb})</span></div>
<div class="field"><b>Address:</b><span>${claimantAddr}</span></div>
<div class="field"><b>Contact:</b><span>${claimantEmail}${claimantPhone ? " · " + claimantPhone : ""}</span></div>

<h2>(B) Kind of Labor, Services, Equipment, or Material Being Provided</h2>
<div class="field"><span>${scopeDesc}</span></div>

<h2>(C) Person Who Contracted for the Purchase</h2>
<div class="field"><b>Name:</b><span>${ownerName}</span></div>
<div class="field"><b>Address:</b><span>${jobLoc}</span></div>

<h2>(D) Common Name and Address of the Jobsite</h2>
<div class="field"><b>Project:</b><span>${projName}</span></div>
<div class="field"><b>Address:</b><span>${jobLoc}</span></div>

<h2>(E) Estimated Total Price</h2>
<div class="field"><span>${estPrice}</span></div>

<h2>Service</h2>
<p>This Preliminary Notice must be served on the owner, the original/direct contractor (if claimant is a subcontractor), and the construction lender (if any) by first-class certified or registered mail with proof of service, or by personal service. Retain the proof of service for your lien claim file.</p>

<div class="sig">
  <div>Claimant's Signature &nbsp;&nbsp;&nbsp; Date</div>
  <div>Claimant's Title</div>
</div>
</body></html>`;
}

// California Civil Code §8136 — Conditional Waiver and Release on FINAL
// Payment. Used at project closeout for the final pay app. Same conditional
// rule as §8132 — effective only on receipt of the final payment.
function generateGCConditionalFinalWaiverHTML(app, proj, session) {
  const cs = (() => { try { return JSON.parse(localStorage.getItem(window._grokCompanyKey || "grok_company_settings") || "{}"); } catch { return {}; } })();
  const claimantName = cs.co_header_name || session?.companyName || "Grok Construction Group Inc";
  const claimantCslb = cs.co_header_cslb || session?.cslb || "1152750";
  const customerName = proj?.owner_name || proj?.client || "Owner";
  const jobLoc = proj?.addr || proj?.name || "Project Site";
  const ownerName = proj?.owner_name || proj?.client || "Owner";
  const amount = "$" + Math.round((app?.amount_due || 0)).toLocaleString();
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>Conditional Waiver §8136 (Final) — ${claimantName}</title>
<style>
  body{font-family:'Times New Roman',serif;padding:48px;max-width:780px;margin:0 auto;color:#000;font-size:12pt;line-height:1.5}
  .ref{font-size:10pt;color:#555;text-align:right;margin-bottom:14px}
  .notice{border:2px solid #000;padding:14px 16px;text-align:center;font-weight:700;text-transform:uppercase;font-size:11pt;line-height:1.4;margin-bottom:22px}
  h1{text-align:center;font-size:14pt;margin:0 0 22px;text-transform:uppercase;letter-spacing:.04em}
  h2{font-size:11pt;text-transform:uppercase;letter-spacing:.04em;margin:20px 0 8px;border-bottom:1px solid #000;padding-bottom:3px}
  p{margin:0 0 12px}
  .field{display:grid;grid-template-columns:200px 1fr;gap:8px;padding:4px 0;border-bottom:1px solid #ccc}
  .field b{font-weight:700}
  .sig{margin-top:32px;padding-top:14px;border-top:2px solid #000;display:grid;grid-template-columns:1fr 1fr;gap:32px}
  .sig div{font-size:10pt;color:#555;border-top:1px solid #000;padding-top:6px;margin-top:32px}
  @media print{body{padding:24px}}
</style></head><body>
<div class="ref">Final Pay App ${app?.app_num || ""} · Generated ${today}</div>
<div class="notice">
  Notice: This document waives the claimant's lien, stop payment notice, and payment bond rights effective on receipt of payment. A person should not rely on this document unless satisfied that the claimant has received payment.
</div>
<h1>Conditional Waiver and Release on Final Payment<br/>(California Civil Code §8136)</h1>

<h2>Identifying Information</h2>
<div class="field"><b>Name of Claimant:</b><span>${claimantName} &nbsp; (CSLB #${claimantCslb})</span></div>
<div class="field"><b>Name of Customer:</b><span>${customerName}</span></div>
<div class="field"><b>Job Location:</b><span>${jobLoc}</span></div>
<div class="field"><b>Owner:</b><span>${ownerName}</span></div>

<h2>Conditional Waiver and Release</h2>
<p>This document waives and releases lien, stop payment notice, and payment bond rights the claimant has for all labor and service provided, and equipment and material delivered, to the customer on this job. Rights based upon labor or service provided, or equipment or material delivered, pursuant to a written change order that has been fully executed by the parties prior to the date that this document is signed by the claimant, are waived and released by this document, unless listed as an Exception below. This document is effective only on the claimant's receipt of payment from the financial institution on which the following check is drawn:</p>

<div class="field"><b>Maker of Check:</b><span>${ownerName}</span></div>
<div class="field"><b>Amount of Check:</b><span>${amount}</span></div>
<div class="field"><b>Check Payable to:</b><span>${claimantName}</span></div>

<h2>Exceptions</h2>
<p>This document does not affect any of the following:</p>
<p>(1) Disputed claims for extras in the amount of: $______ <em>(fill in or leave blank if none)</em><br/>
(2) Contract rights, including (A) a right based on rescission, abandonment, or breach of contract, and (B) the right to recover compensation for work not compensated by the payment.</p>

<h2>Signature</h2>
<div class="sig">
  <div>Claimant's Signature &nbsp;&nbsp;&nbsp; Date</div>
  <div>Claimant's Title</div>
</div>
</body></html>`;
}

// California Civil Code §8138 — Unconditional Waiver and Release on FINAL
// Payment. Last document in the lien lifecycle. Sign only after the final
// payment has actually cleared.
function generateGCUnconditionalFinalWaiverHTML(app, proj, session) {
  const cs = (() => { try { return JSON.parse(localStorage.getItem(window._grokCompanyKey || "grok_company_settings") || "{}"); } catch { return {}; } })();
  const claimantName = cs.co_header_name || session?.companyName || "Grok Construction Group Inc";
  const claimantCslb = cs.co_header_cslb || session?.cslb || "1152750";
  const customerName = proj?.owner_name || proj?.client || "Owner";
  const jobLoc = proj?.addr || proj?.name || "Project Site";
  const ownerName = proj?.owner_name || proj?.client || "Owner";
  const amount = "$" + Math.round((app?.amount_due || 0)).toLocaleString();
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>Unconditional Waiver §8138 (Final) — ${claimantName}</title>
<style>
  body{font-family:'Times New Roman',serif;padding:48px;max-width:780px;margin:0 auto;color:#000;font-size:12pt;line-height:1.5}
  .ref{font-size:10pt;color:#555;text-align:right;margin-bottom:14px}
  .notice{border:2px solid #b91c1c;padding:14px 16px;text-align:center;font-weight:700;text-transform:uppercase;font-size:11pt;line-height:1.4;margin-bottom:22px;color:#b91c1c}
  h1{text-align:center;font-size:14pt;margin:0 0 22px;text-transform:uppercase;letter-spacing:.04em}
  h2{font-size:11pt;text-transform:uppercase;letter-spacing:.04em;margin:20px 0 8px;border-bottom:1px solid #000;padding-bottom:3px}
  p{margin:0 0 12px}
  .field{display:grid;grid-template-columns:200px 1fr;gap:8px;padding:4px 0;border-bottom:1px solid #ccc}
  .field b{font-weight:700}
  .sig{margin-top:32px;padding-top:14px;border-top:2px solid #000;display:grid;grid-template-columns:1fr 1fr;gap:32px}
  .sig div{font-size:10pt;color:#555;border-top:1px solid #000;padding-top:6px;margin-top:32px}
  @media print{body{padding:24px}}
</style></head><body>
<div class="ref">Final Pay App ${app?.app_num || ""} · Generated ${today}</div>
<div class="notice">
  Notice to Claimant: This document waives and releases lien, stop payment notice, and payment bond rights unconditionally and states that you have been paid for giving up those rights. This document is enforceable against you if you sign it, even if you have not been paid. If you have not been paid, use a conditional waiver and release form.
</div>
<h1>Unconditional Waiver and Release on Final Payment<br/>(California Civil Code §8138)</h1>

<h2>Identifying Information</h2>
<div class="field"><b>Name of Claimant:</b><span>${claimantName} &nbsp; (CSLB #${claimantCslb})</span></div>
<div class="field"><b>Name of Customer:</b><span>${customerName}</span></div>
<div class="field"><b>Job Location:</b><span>${jobLoc}</span></div>
<div class="field"><b>Owner:</b><span>${ownerName}</span></div>

<h2>Unconditional Waiver and Release</h2>
<p>This document waives and releases lien, stop payment notice, and payment bond rights the claimant has for all labor and service provided, and equipment and material delivered, to the customer on this job. Rights based upon labor or service provided, or equipment or material delivered, pursuant to a written change order that has been fully executed by the parties prior to the date that this document is signed by the claimant, are waived and released by this document, unless listed as an Exception below. The claimant has been paid in full.</p>

<div class="field"><b>Amount Paid (final):</b><span>${amount}</span></div>

<h2>Exceptions</h2>
<p>This document does not affect any of the following:</p>
<p>(1) Disputed claims for extras in the amount of: $______ <em>(fill in or leave blank if none)</em>.<br/>
(2) Contract rights, including (A) a right based on rescission, abandonment, or breach of contract, and (B) the right to recover compensation for work not compensated by the payment.</p>

<h2>Signature</h2>
<div class="sig">
  <div>Claimant's Signature &nbsp;&nbsp;&nbsp; Date</div>
  <div>Claimant's Title</div>
</div>
</body></html>`;
}

// Lookup the company's subs available for waiver-request routing. Filters
// the global sub-accounts list to the current company. Returns name + email
// + trade so the modal can render and the email function can target.
function getProjectSubs(proj, cid) {
  try {
    const all = (typeof getSubAccounts === "function" ? getSubAccounts() : []) || [];
    return all
      .filter(s => s && (s.companyId === cid || s.gcCompanyId === cid))
      .map(s => ({
        id: s.id || s.email || (s.name || "sub") + Math.random().toString(36).slice(2,5),
        name: s.name || s.company || "Sub",
        company: s.company || "",
        email: s.email || "",
        trade: s.trade || "",
      }));
  } catch { return []; }
}

// Fire an email to one sub asking them to submit their lien waiver for the
// billing period. `phase` is "conditional" (§8132 — before owner pays) or
// "unconditional" (§8134 — after the GC's payment has cleared and we need
// to confirm the sub has been paid too).
async function requestSubWaiverEmail({ sub, app, proj, session, phase }) {
  if (!sub || !sub.email) return { ok: false, error: "no email" };
  const isUnconditional = phase === "unconditional";
  const gcName = session?.companyName || session?.ownerName || "your GC";
  const projName = proj?.name || "the project";
  const through = app?.period_to || "this billing period";
  const amount = "$" + Math.round((app?.amount_due || 0)).toLocaleString();
  const formCode = isUnconditional ? "§8134" : "§8132";
  const formName = isUnconditional ? "Unconditional Waiver and Release on Progress Payment" : "Conditional Waiver and Release on Progress Payment";
  const subject = isUnconditional
    ? `[${projName}] Unconditional lien waiver — payment cleared for period through ${through}`
    : `[${projName}] Conditional lien waiver request — period through ${through}`;
  const lead = isUnconditional
    ? `Owner payment for pay application #${app?.app_num || ""} on ${projName} (period through <strong>${through}</strong>) has cleared. Once you've received your portion of the payment, please send back a <strong>California Civil Code §8134 Unconditional Waiver and Release on Progress Payment</strong> confirming you've been paid for work through that date.`
    : `${gcName} is preparing pay application #${app?.app_num || ""} for ${projName} (period through <strong>${through}</strong>, amount <strong>${amount}</strong>).<br/><br/>Before we submit to the owner, please send back a <strong>California Civil Code §8132 Conditional Waiver and Release on Progress Payment</strong> covering your work for this period.`;
  const warningCopy = isUnconditional
    ? `<strong>Important:</strong> Per California Civil Code §8124, an unconditional waiver is enforceable against you the moment you sign it, even if payment has not actually reached you. Only sign and return AFTER you've confirmed your check has cleared.`
    : `Per California Civil Code §8132, conditional waivers do not become effective until the corresponding payment has cleared. Submitting now does not release lien rights until you actually receive payment.`;
  const html =
    `<div style="font-family:Arial,sans-serif;max-width:640px;padding:24px;color:#1a1a1a;font-size:14px;line-height:1.7">` +
    `<div style="font-size:11px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:.1em;margin-bottom:6px">${projName}</div>` +
    `<h2 style="margin:0 0 14px;font-size:18px">Please submit your ${isUnconditional ? "unconditional" : "conditional"} lien waiver (${formCode})</h2>` +
    `<p>Hi ${sub.name || sub.company || "team"},</p>` +
    `<p>${lead}</p>` +
    `<p>The statutory form text is required — any modification voids the waiver.</p>` +
    `<p>Reply to this email with the signed waiver as a PDF attachment, or upload through your sub portal if you have one. Let us know if you have questions.</p>` +
    `<p style="margin-top:22px">Thanks,<br/>${gcName}</p>` +
    `<div style="margin-top:18px;padding-top:12px;border-top:1px solid #eee;font-size:11px;color:#888">${warningCopy}</div>` +
    `</div>`;
  try {
    const r = await fetch("https://qnjlirkgumhzzcavqsxt.supabase.co/functions/v1/send-email", {
      method: "POST",
      headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY },
      body: JSON.stringify({ to: sub.email, subject, html }),
    });
    if (!r.ok) return { ok: false, error: "HTTP " + r.status };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e?.message || "send failed" };
  }
}

// cat: string or array of acceptable cats (in priority order)
// fileNameContains: optional. If provided, prefer matches with the substring
//                   in fileName, but fall back to any cat match if none found.
function findCompanyTemplate(cat, fileNameContains) {
  const cid = window._grokCompanyId || "local";
  const keys = ["grok_templates_" + cid];
  if (cid !== "gcg_001") keys.push("grok_templates_gcg_001");
  if (cid !== "local")   keys.push("grok_templates_local");
  const acceptCats = Array.isArray(cat) ? cat : [cat];
  for (const k of keys) {
    let tpls = [];
    try { tpls = JSON.parse(localStorage.getItem(k) || "[]"); } catch { continue; }
    if (!tpls.length) continue;
    // Pass 1: exact cat + filename match (strict).
    if (fileNameContains) {
      for (const c of acceptCats) {
        const m = tpls.find(t => t && t.cat === c && t.data && (t.fileName || "").toLowerCase().includes(fileNameContains));
        if (m) return m;
      }
    }
    // Pass 2: any cat match in priority order (loose).
    for (const c of acceptCats) {
      const m = tpls.find(t => t && t.cat === c && t.data);
      if (m) return m;
    }
  }
  return null;
}

// Load JSZip on demand, fill empty table cells in a .docx template with the
// fields array (in order), and download the result. Returns true on success,
// false if anything fails so the caller can fall back to its HTML print path.
async function fillDocxTemplate(templateDataUrl, fields, fileName) {
  try {
    if (!window.JSZip) {
      await new Promise((res, rej) => {
        const s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
        s.onload = res; s.onerror = rej;
        document.head.appendChild(s);
      });
    }
    const b64 = (templateDataUrl || "").split(",")[1];
    if (!b64) return false;
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const zip = await window.JSZip.loadAsync(bytes);
    const docXml = zip.file("word/document.xml");
    if (!docXml) return false;
    let xml = await docXml.async("string");
    const esc = (v) => String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    // Fill empty <w:tc>...</w:tc> cells sequentially with field values.
    let fieldIdx = 0;
    xml = xml.replace(/<w:tc>([\s\S]*?)<\/w:tc>/g, (match) => {
      const hasText = /<w:t[^>]*>[^<\s][^<]*<\/w:t>/.test(match);
      if (!hasText && fieldIdx < fields.length) {
        const val = fields[fieldIdx++];
        if (val != null && val !== "") {
          return match.replace(
            /(<w:p\b[^>]*>)(\s*<w:pPr>[\s\S]*?<\/w:pPr>)?\s*(<\/w:p>)/,
            (m, open, pPr, close) => `${open}${pPr || ""}<w:r><w:t xml:space="preserve">${esc(val)}</w:t></w:r>${close}`
          );
        }
      } else if (!hasText) {
        fieldIdx++;
      }
      return match;
    });
    zip.file("word/document.xml", xml);
    const out = await zip.generateAsync({ type: "blob", mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
    const url = URL.createObjectURL(out);
    const a = document.createElement("a"); a.href = url; a.download = fileName;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return true;
  } catch (e) {
    console.error("[fillDocxTemplate] failed:", e);
    return false;
  }
}

async function exportCOPDF(co, proj, session) {
  // Template-first — if the GC uploaded a CO template, use their branding.
  // Falls through to the HTML print fallback when no template exists or fill
  // fails for any reason.
  const fmt = v => "$" + Math.round(v||0).toLocaleString();
  const tpl = findCompanyTemplate("co");
  if (tpl) {
    const subtotal = (parseFloat(co.labor)||0) + (parseFloat(co.materials)||0) + (parseFloat(co.subCost)||0) + (parseFloat(co.permits)||0);
    const op = Math.round(subtotal * 0.25);
    const total = parseFloat(co.owner_price || co.amount || 0) || (subtotal + op);
    // Field order intentionally matches GCG_Change_Order.docx cell sequence.
    // For other templates, falls through cells in order — works for any docx
    // with empty cells laid out in roughly the same sequence.
    const fields = [
      co.id || "CO",
      co.date || new Date().toLocaleDateString(),
      proj?.name || "Project",
      co.submittedBy || session?.ownerName || "GC",
      co.desc || co.description || "See attached.",
      fmt(co.labor || 0),
      fmt(co.materials || 0),
      fmt(co.subCost || 0),
      fmt(co.permits || 0),
      fmt(subtotal),
      fmt(op),
      fmt(total),
    ];
    const ok = await fillDocxTemplate(tpl.data, fields, `CO_${(co.id||"draft").replace(/[^A-Z0-9]/gi,"_")}.docx`);
    if (ok) return;
  }
  // Fall back to the HTML print window.
  const cs = (()=>{ try{ return JSON.parse(localStorage.getItem(window._grokCompanyKey||"grok_company_settings")||"{}")} catch{ return {} } })();
  const coName  = cs.co_header_name    || session?.companyName || "Grok Construction Group Inc";
  const coCslb  = cs.co_header_cslb    || session?.cslb || "1152750";
  const coEmail = cs.co_header_email   || session?.email || "";
  const coPhone = cs.co_header_phone   || "";
  const coAddr  = cs.co_header_address || "San Mateo, CA";
  const coTermsText = cs.co_terms || "This Change Order, when signed by both parties, becomes part of and subject to the terms of the original contract.";
  const statusColor = co.status==="approved"?"#16a34a":co.status==="pending"?"#d97706":"#dc2626";
  const statusBg = co.status==="approved"?"#dcfce7":co.status==="pending"?"#fef3c7":"#fee2e2";
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>CO ${co.id}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;margin:0;padding:40px;color:#111;font-size:13px;line-height:1.5}
.hdr{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;padding-bottom:16px;border-bottom:3px solid #D4A843}
.htl{font-size:22px;font-weight:800;color:#D4A843;margin-bottom:4px}.hid{font-size:13px;color:#555}
.co{text-align:right;font-size:11px;color:#555;line-height:1.7}.co b{font-size:13px;color:#111;display:block;margin-bottom:2px}
.badge{display:inline-block;padding:3px 10px;border-radius:20px;font-size:10px;font-weight:700;background:${statusBg};color:${statusColor}}
.amt{background:#fffbeb;border:2px solid #D4A843;border-radius:8px;padding:16px 20px;display:flex;justify-content:space-between;align-items:center;margin-bottom:24px}
.aml{font-size:11px;font-weight:700;text-transform:uppercase;color:#888}.amv{font-size:30px;font-weight:800;color:#D4A843}
.sec{margin-bottom:22px}.sth{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#888;margin-bottom:8px;padding-bottom:4px;border-bottom:1px solid #eee}
.row{display:flex;justify-content:space-between;padding:5px 0;border-bottom:1px solid #f5f5f5;font-size:12px}
.desc{background:#fffbeb;border-left:4px solid #D4A843;padding:14px;border-radius:0 8px 8px 0;font-size:13px;line-height:1.7}
.sigs{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:48px}
.sig{border-top:2px solid #111;padding-top:8px;font-size:11px;color:#666}
.sig b{font-size:13px;color:#111;display:block;margin-bottom:20px}
.terms{font-size:10px;color:#888;line-height:1.7;margin-top:16px;padding:12px;background:#f9f9f9;border-radius:6px}
@media print{body{padding:20px}.no-print{display:none}}
</style></head><body>
<div class="hdr">
  <div><div class="htl">CHANGE ORDER REQUEST</div><div class="hid">${co.id} &mdash; ${proj?.name||"Project"}</div><div style="margin-top:6px"><span class="badge">${(co.status||"pending").toUpperCase()}</span></div></div>
  <div class="co"><b>${coName}</b>CSLB #${coCslb}<br/>${coEmail}${coPhone?" | "+coPhone:""}<br/>${coAddr}</div>
</div>
<div class="amt"><div class="aml">Change Order Amount</div><div class="amv">${fmt(co.amount)}</div></div>
<div class="sec"><div class="sth">Details</div>
  <div class="row"><span>CO Number</span><span>${co.id}</span></div>
  <div class="row"><span>Date</span><span>${co.date||new Date().toLocaleDateString()}</span></div>
  <div class="row"><span>Project</span><span>${proj?.name||"—"}</span></div>
  <div class="row"><span>Submitted By</span><span>${co.submittedBy||session?.ownerName||"GC"}</span></div>
</div>
<div class="sec"><div class="sth">Scope of Change</div>
  <div class="desc">${co.desc||co.description||"See attached documentation."}</div>
</div>
<div class="sec"><div class="sth">Cost</div>
  <div class="row"><span>Labor</span><span>${fmt(co.labor||0)}</span></div>
  <div class="row"><span>Materials</span><span>${fmt(co.materials||0)}</span></div>
  <div class="row"><span>Subcontractor</span><span>${fmt(co.subCost||0)}</span></div>
  <div class="row" style="font-weight:700;font-size:14px;padding-top:8px;border-top:2px solid #111;margin-top:2px"><span>TOTAL</span><span style="color:#D4A843">${fmt(co.amount)}</span></div>
</div>
<div class="terms">This Change Order, when signed by both parties, becomes part of and subject to the terms of the original contract. Work described above shall not commence until this Change Order is fully executed. The contract price shall be adjusted as indicated above.</div>
<div class="sigs">
  <div class="sig"><b>${coName}</b>Authorized Signature &nbsp;&nbsp;&nbsp;&nbsp; Date</div>
  <div class="sig"><b>Owner / Client</b>Authorized Signature &nbsp;&nbsp;&nbsp;&nbsp; Date</div>
</div>
</body></html>`;
  openPDFWindow(html, "CO " + co.id);
}

async function exportRFIPDF(rfi, proj, session) {
  // Template-first — if the GC uploaded an RFI template, use their branding.
  const tpl = findCompanyTemplate("rfi");
  if (tpl) {
    const fields = [
      rfi.id || rfi.num || "RFI",
      rfi.submitted || rfi.date || new Date().toLocaleDateString(),
      proj?.name || rfi.project || "Project",
      rfi.submittedBy || session?.ownerName || "GC",
      rfi.assignee || "Architect",
      rfi.priority || "normal",
      rfi.subject || "",
      rfi.description || rfi.desc || rfi.question || "",
      rfi.drawingRef || "",
      rfi.suggestedResolution || rfi.response || "",
    ];
    const ok = await fillDocxTemplate(tpl.data, fields, `RFI_${(rfi.id||rfi.num||"draft").replace(/[^A-Z0-9]/gi,"_")}.docx`);
    if (ok) return;
  }
  // Fall back to the HTML print window.
  const rs = (()=>{ try{ return JSON.parse(localStorage.getItem(window._grokCompanyKey||"grok_company_settings")||"{}")} catch{ return {} } })();
  const rfiName     = rs.co_header_name    || session?.companyName || "Grok Construction Group Inc";
  const rfiCslb     = rs.co_header_cslb    || session?.cslb || "1152750";
  const rfiEmail    = rs.co_header_email   || session?.email || "";
  const rfiPhone    = rs.co_header_phone   || "";
  const rfiAddr     = rs.co_header_address || "San Mateo, CA";
  const rfiProtocol = rs.rfi_terms || "Contractor shall respond to all RFIs within 48 hours.";
  const statusColor = rfi.status==="closed"?"#16a34a":"#d97706";
  const statusBg = rfi.status==="closed"?"#dcfce7":"#fef3c7";
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>RFI ${rfi.id}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;margin:0;padding:40px;color:#111;font-size:13px;line-height:1.5}
.hdr{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;padding-bottom:16px;border-bottom:3px solid #2dd4bf}
.htl{font-size:22px;font-weight:800;color:#2dd4bf;margin-bottom:4px}.hid{font-size:13px;color:#555}
.co{text-align:right;font-size:11px;color:#555;line-height:1.7}.co b{font-size:13px;color:#111;display:block;margin-bottom:2px}
.badge{display:inline-block;padding:3px 10px;border-radius:20px;font-size:10px;font-weight:700;background:${statusBg};color:${statusColor}}
.sec{margin-bottom:22px}.sth{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#888;margin-bottom:8px;padding-bottom:4px;border-bottom:1px solid #eee}
.row{display:flex;justify-content:space-between;padding:5px 0;border-bottom:1px solid #f5f5f5;font-size:12px}
.qbox{background:#f0fdfa;border-left:4px solid #2dd4bf;padding:14px;border-radius:0 8px 8px 0;font-size:13px;line-height:1.7}
.abox{background:#f0f9ff;border-left:4px solid #60a5fa;padding:14px;border-radius:0 8px 8px 0;font-size:13px;line-height:1.7;min-height:60px}
.sigs{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:48px}
.sig{border-top:2px solid #111;padding-top:8px;font-size:11px;color:#666}
.sig b{font-size:13px;color:#111;display:block;margin-bottom:20px}
@media print{body{padding:20px}}
</style></head><body>
<div class="hdr">
  <div><div class="htl">REQUEST FOR INFORMATION</div><div class="hid">${rfi.id} &mdash; ${proj?.name||"Project"}</div><div style="margin-top:6px"><span class="badge">${(rfi.status||"open").toUpperCase()}</span></div></div>
  <div class="co"><b>${session?.companyName||"Grok Construction Group Inc"}</b>CSLB #${session?.cslb||"1152750"}<br/>${session?.email||""}<br/>San Mateo, CA</div>
</div>
<div class="sec"><div class="sth">RFI Details</div>
  <div class="row"><span>RFI Number</span><span>${rfi.id}</span></div>
  <div class="row"><span>Date</span><span>${rfi.date||new Date().toLocaleDateString()}</span></div>
  <div class="row"><span>Project</span><span>${proj?.name||"—"}</span></div>
  <div class="row"><span>Subject</span><span style="font-weight:700">${rfi.subject||"—"}</span></div>
  <div class="row"><span>Assigned To</span><span>${rfi.assignee||"—"}</span></div>
  <div class="row"><span>Response Due</span><span>${rfi.due_date||"—"}</span></div>
</div>
<div class="sec"><div class="sth">Question / Request</div>
  <div class="qbox">${rfi.desc||rfi.question||"See attached documentation."}</div>
</div>
<div class="sec"><div class="sth">Response / Answer</div>
  <div class="abox">${rfi.response||rfi.answer||"[Pending response]"}</div>
</div>
${rfi.impact?`<div class="sec"><div class="sth">Schedule / Cost Impact</div><div style="padding:12px;background:#fef3c7;border-radius:8px;font-size:12px">${rfi.impact}</div></div>`:""}
<div class="sigs">
  <div class="sig"><b>Submitted By &mdash; ${rfiName}</b>Signature &nbsp;&nbsp;&nbsp;&nbsp; Date</div>
  <div class="sig"><b>Responded By</b>Signature &nbsp;&nbsp;&nbsp;&nbsp; Date</div>
</div>
</body></html>`;
  openPDFWindow(html, "RFI " + rfi.id);
}

// ─── FINANCE SUB-NAV ──────────────────────────────────────────────────────────
function FinanceSubNav({proj, projCOs, session}) {
  const [sub, setSub] = useState("budget");
  if (!canSee(session,"financials")) return (<div style={{background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:10,padding:"28px",textAlign:"center"}}><div style={{fontSize:28,marginBottom:10}}>🔒</div><div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:8}}>Financials — Restricted</div><div style={{fontSize:12,color:T.muted}}>Your role does not have access to budget or margin data. Contact the company owner to adjust permissions.</div></div>);
  return (
    <div>
      <div style={{display:"flex",gap:4,marginBottom:14,background:T.bg2,borderRadius:8,padding:3,width:"fit-content"}}>
        {[["budget","💰 Budget & Profit"],["bids","📊 Sub Bid Comparison"],["pos","📋 Purchase Orders"]].map(([id,label])=>(
          <button key={id} onClick={()=>setSub(id)}
            style={{padding:"6px 16px",borderRadius:6,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",
              background:sub===id?T.gold:T.bg2,color:sub===id?T.bg0:T.muted,transition:"all 0.15s"}}>
            {label}
          </button>
        ))}
      </div>
      {sub==="budget"&&<BudgetTracker proj={proj} projCOs={projCOs} session={session}/>}
      {sub==="bids"&&<SubBidCompare proj={proj}/>}
      {sub==="pos"&&<PurchaseOrdersView proj={proj} session={session}/>}
    </div>
  );
}

// ─── ADMIN SUB-NAV ────────────────────────────────────────────────────────────
// ─── PROJECT DOCS PANEL — company + project scoped ───────────────────────────
// ─── PROJECT TEMPLATE GENERATOR ──────────────────────────────────────────────
// Shows all subscriber templates inside a project, pre-fills project data,
// lets user fill remaining fields, then generates filled Word doc
function ProjectTemplateGenerator({proj, session}) {
  const cid = window._grokCompanyId || session?.companyId || "local";
  const tplKey  = "grok_templates_"   + cid;
  const cosKey  = "grok_cos_global_"  + cid;
  const rfisKey = "grok_rfis_global_" + cid;

  const templates = (()=>{try{return JSON.parse(localStorage.getItem(tplKey)||"[]");}catch{return [];}})();
  const cos       = (()=>{try{return JSON.parse(localStorage.getItem(cosKey)||"[]");}catch{return [];}})();
  const rfis      = (()=>{try{return JSON.parse(localStorage.getItem(rfisKey)||"[]");}catch{return [];}})();
  const [selected, setSelected] = React.useState(null);
  const [form, setForm] = React.useState({});
  const [generating, setGenerating] = React.useState(false);

  const cs = (()=>{try{return JSON.parse(localStorage.getItem(window._grokCompanyKey||"grok_company_settings")||"{}");}catch{return {};}})();
  const companyName = cs.co_header_name || session?.companyName || "Grok Construction Group Inc";
  const cslb = cs.co_header_cslb || "1152750";
  const inp = {width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit",marginBottom:8};

  // Template categories with colors and extra fields needed
  const CAT_META = {
    contracts: {color:T.blue,   icon:"📑", label:"Contracts & Proposals",
      extraFields:[{k:"scope",l:"Scope of Work",ph:"Describe the work..."},{k:"contract_amount",l:"Contract Amount $",ph:"150000"},{k:"start_date",l:"Start Date",ph:"May 1, 2026"},{k:"duration",l:"Duration",ph:"90 days"}]},
    co:        {color:T.amber,  icon:"📝", label:"Change Orders",
      extraFields:[{k:"co_desc",l:"Description of Change",ph:"Drain relocation — unit 4B"},{k:"co_amount",l:"CO Amount $",ph:"8400"},{k:"labor",l:"Labor $",ph:"3000"},{k:"materials",l:"Materials $",ph:"2400"}]},
    rfi:       {color:"#8b5cf6",icon:"❓", label:"RFIs",
      extraFields:[{k:"subject",l:"Subject / Question",ph:"Drain elevation conflict"},{k:"rfi_desc",l:"Description",ph:"Plans show drain at 2\" but field condition is..."},{k:"priority",l:"Priority",ph:"Critical / Urgent / Routine"},{k:"due_date",l:"Response Due",ph:"May 15, 2026"}]},
    lien:      {color:T.red,    icon:"📜", label:"Lien Waivers",
      extraFields:[{k:"through_date",l:"Through Date",ph:"April 30, 2026"},{k:"payment_amount",l:"Payment Amount $",ph:"45000"},{k:"payer",l:"Payment From (GC/Owner Name)",ph:"Owner LLC"}]},
    billing:   {color:T.gold,   icon:"💰", label:"Billing & Pay Apps",
      extraFields:[{k:"app_num",l:"Application #",ph:"3"},{k:"period",l:"Billing Period",ph:"April 1–30, 2026"},{k:"earned",l:"Amount Earned This Period $",ph:"62000"},{k:"retention",l:"Retention %",ph:"10"}]},
    closeout:  {color:T.teal,   icon:"✅", label:"Closeout Documents",
      extraFields:[{k:"completion_date",l:"Completion Date",ph:"May 15, 2026"},{k:"punch_items",l:"Punch List Items Outstanding",ph:"3"},{k:"retention_held",l:"Retention Held $",ph:"28500"}]},
    sro:       {color:"#8b5cf6",icon:"🏘", label:"SRO & Affordable Housing",
      extraFields:[{k:"unit_num",l:"Unit Number(s)",ph:"4B, 4C"},{k:"work_start",l:"Work Start Date",ph:"May 1, 2026"},{k:"duration",l:"Estimated Duration",ph:"5 days"},{k:"super_name",l:"Superintendent Name",ph:"John Smith"},{k:"super_phone",l:"Superintendent Phone",ph:"(415) 555-0100"}]},
    safety:    {color:T.red,    icon:"🦺", label:"Safety & Compliance",
      extraFields:[{k:"super_name",l:"Superintendent",ph:"John Smith"},{k:"date",l:"Date",ph:new Date().toLocaleDateString()}]},
    submittals:{color:"#06b6d4",icon:"📋", label:"Submittals",
      extraFields:[{k:"submittal_desc",l:"Description",ph:"Product data sheet for LVP flooring"},{k:"spec_section",l:"Spec Section",ph:"09 65 00"}]},
    field:     {color:"#84cc16",icon:"📷", label:"Field Reports",
      extraFields:[{k:"date",l:"Date",ph:new Date().toLocaleDateString()},{k:"super_name",l:"Superintendent",ph:"John Smith"},{k:"work_performed",l:"Work Performed",ph:"Framing 4th floor units 4A-4D"}]},
    other:     {color:T.muted,  icon:"📁", label:"Internal Templates",
      extraFields:[]},
  };

  // Group templates by category
  const byCategory = {};
  templates.filter(t=>t.data).forEach(t=>{
    if(!byCategory[t.cat]) byCategory[t.cat] = [];
    byCategory[t.cat].push(t);
  });

  const fmt = v => '$'+Math.round(parseFloat(v)||0).toLocaleString();

  // JSZip fill function
  const fillAndDownload = async (template, fields, fileName) => {
    if (!window.JSZip) {
      await new Promise((res,rej)=>{
        const s=document.createElement('script');
        s.src='https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
        s.onload=res; s.onerror=rej; document.head.appendChild(s);
      });
    }
    const b64=template.data.split(',')[1];
    const bin=atob(b64); const bytes=new Uint8Array(bin.length);
    for(let i=0;i<bin.length;i++) bytes[i]=bin.charCodeAt(i);
    const zip=await window.JSZip.loadAsync(bytes);
    let xml=await zip.file('word/document.xml').async('string');
    let fi=0;
    const esc=v=>String(v).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    xml=xml.replace(/<w:tc>([\s\S]*?)<\/w:tc>/g,(match)=>{
      const hasText=/<w:t[^>]*>[^<\s][^<]*<\/w:t>/.test(match);
      if(!hasText&&fi<fields.length){
        const val=fields[fi++];
        if(val) return match.replace(/(<w:p\b[^>]*>)(\s*<w:pPr>[\s\S]*?<\/w:pPr>)?\s*(<\/w:p>)/,
          (m,open,pPr,close)=>`${open}${pPr||''}<w:r><w:t xml:space="preserve">${esc(val)}</w:t></w:r>${close}`);
      } else if(!hasText) fi++;
      return match;
    });
    zip.file('word/document.xml',xml);
    const out=await zip.generateAsync({type:'blob',mimeType:'application/vnd.openxmlformats-officedocument.wordprocessingml.document'});
    const url=URL.createObjectURL(out);
    const a=document.createElement('a'); a.href=url; a.download=fileName;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Build field arrays for each template type
  const buildFields = (cat, f) => {
    const p = proj||{};
    const today = new Date().toLocaleDateString();
    switch(cat) {
      case 'co': return [
        f.co_num||'CO-'+(cos.length+1).toString().padStart(3,'0'), today, p.name||'', p.startDate||'',
        p.client||'', companyName+' · CSLB #'+cslb,
        fmt(p.value||0), fmt(0), fmt(p.value||0),
        f.co_desc||'','','','','','',
        fmt(f.labor||0), fmt(f.materials||0), fmt(0), fmt(0),
        fmt((parseFloat(f.labor||0)+parseFloat(f.materials||0))),
        fmt((parseFloat(f.labor||0)+parseFloat(f.materials||0))*0.25),
        fmt(f.co_amount||0),
        fmt(p.value||0), fmt(0), fmt(f.co_amount||0), fmt((parseFloat(p.value||0)+parseFloat(f.co_amount||0))),
        '','','',
      ];
      case 'rfi': return [
        f.rfi_num||'RFI-'+(rfis.length+1).toString().padStart(3,'0'), today, f.due_date||'', (f.priority||'Routine').toUpperCase(),
        p.name||'','', p.client||'', f.assignee||'', f.assignee||'', '','No','TBD',
        f.subject||'', f.rfi_desc||'','','','','',
      ];
      case 'contracts': return [
        today, today, new Date(Date.now()+30*86400000).toLocaleDateString(), fmt(f.contract_amount||p.value||0),
        p.client||'', p.addr||'','','',
        companyName, 'CSLB #'+cslb, '2201 Bridgepointe Pkwy F125, San Mateo CA 94404', '(650) 773-5317',
        f.scope||'', fmt(f.contract_amount||p.value||0),
        f.start_date||today, f.duration||'',
      ];
      case 'lien': return [
        p.name||'', p.addr||'', p.client||'', today, f.through_date||'',
        fmt(f.payment_amount||0), f.payer||p.client||'',
        companyName,'CSLB #'+cslb,
      ];
      case 'billing': return [
        p.name||'', p.addr||'', p.client||'', f.app_num||'1', f.period||'',
        fmt(p.value||0), fmt(f.earned||0), f.retention||'10',
        companyName,'CSLB #'+cslb,
      ];
      case 'closeout': return [
        p.name||'', today, session?.ownerName||'',
        f.completion_date||today, fmt(f.retention_held||0), f.punch_items||'0',
      ];
      case 'sro': return [
        today, f.work_start||'', f.duration||'',
        p.addr||'', f.unit_num||'','',
        p.client||'',
        f.work_desc||'', '',
        f.super_name||'', f.super_phone||'',
        companyName,'CSLB #'+cslb,'2201 Bridgepointe Pkwy F125, San Mateo CA 94404','(650) 773-5317','jehoon@grokconstructiongroupinc.com',
      ];
      default: return [p.name||'', p.addr||'', p.client||'', today, fmt(p.value||0)];
    }
  };

  const generate = async () => {
    if (!selected) return;
    setGenerating(true);
    try {
      const fields = buildFields(selected.cat, form);
      const projSlug = (proj?.name||'Project').split(' ')[0];
      await fillAndDownload(selected, fields, `GCG_${selected.name.replace(/\s+/g,'_')}_${projSlug}.docx`);
    } catch(e) {
      alert('Error: '+e.message);
    }
    setGenerating(false);
    setSelected(null);
    setForm({});
  };

  const meta = selected ? (CAT_META[selected.cat]||CAT_META.other) : null;

  return (
    <div>
      <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Project Documents</div>
      <div style={{fontSize:11,color:T.muted,marginBottom:16}}>
        {templates.filter(t=>t.data).length} templates available · Pre-filled with {proj?.name||'project'} data
      </div>

      {/* Template grid by category */}
      {Object.keys(CAT_META).filter(cat=>byCategory[cat]).map(cat=>{
        const m = CAT_META[cat];
        const catTemplates = byCategory[cat]||[];
        return (
          <div key={cat} style={{marginBottom:14}}>
            <div style={{fontSize:10,fontWeight:700,color:m.color,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>
              {m.icon} {m.label}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              {catTemplates.map(t=>(
                <div key={t.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 12px",background:T.bg1,borderRadius:8,border:"1px solid "+T.subtle}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:T.text}}>{t.name}</div>
                    <div style={{fontSize:10,color:T.muted}}>{t.fileName}</div>
                  </div>
                  <button onClick={()=>{setSelected(t);setForm({});}}
                    style={{fontSize:10,fontWeight:700,background:m.color+"18",color:m.color,border:"1px solid "+m.color+"40",borderRadius:6,padding:"5px 12px",cursor:"pointer",whiteSpace:"nowrap"}}>
                    📄 Generate
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {templates.filter(t=>t.data).length===0&&(
        <div style={{textAlign:"center",padding:"40px 20px",color:T.muted,background:T.bg2,borderRadius:10,border:"2px dashed "+T.subtle}}>
          <div style={{fontSize:24,marginBottom:8}}>📁</div>
          <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>No templates uploaded yet</div>
          <div style={{fontSize:11}}>Upload your Word templates in Document Kit → they'll appear here for every project</div>
        </div>
      )}

      {/* CO/RFI from existing project data */}
      <GenerateFromProjectData folder={null} proj={proj} session={session}/>

      {/* Fill form modal */}
      {selected&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.6)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:T.bg1,borderRadius:14,padding:24,width:"100%",maxWidth:480,maxHeight:"85vh",overflowY:"auto",border:"1px solid "+T.subtle}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
              <div>
                <div style={{fontSize:14,fontWeight:800,color:T.text}}>{selected.name}</div>
                <div style={{fontSize:11,color:T.muted,marginTop:2}}>Pre-filled with {proj?.name||'project'} data</div>
              </div>
              <button onClick={()=>{setSelected(null);setForm({});}} style={{background:"none",border:"none",color:T.muted,fontSize:18,cursor:"pointer",padding:0}}>✕</button>
            </div>

            {/* Auto-filled fields preview */}
            <div style={{background:T.bg2,borderRadius:8,padding:"10px 12px",marginBottom:14}}>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Auto-filled from project</div>
              {[
                ["Project",proj?.name],["Address",proj?.addr],["Client / Owner",proj?.client],
                ["Contract Value","$"+((proj?.value||0)/1000).toFixed(0)+"K"],["Company",companyName+" · CSLB #"+cslb],
              ].filter(([,v])=>v).map(([l,v])=>(
                <div key={l} style={{display:"flex",gap:8,fontSize:11,marginBottom:3}}>
                  <span style={{color:T.muted,minWidth:100}}>{l}:</span>
                  <span style={{color:T.green,fontWeight:600}}>{v}</span>
                </div>
              ))}
            </div>

            {/* Extra fields */}
            {(meta?.extraFields||[]).length>0&&(
              <div>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Fill in remaining fields</div>
                {meta.extraFields.map(f=>(
                  <div key={f.k}>
                    <div style={{fontSize:10,color:T.muted,marginBottom:3}}>{f.l}</div>
                    {f.k==='scope'||f.k==='co_desc'||f.k==='rfi_desc'||f.k==='work_performed'||f.k==='work_desc' ? (
                      <textarea value={form[f.k]||''} onChange={e=>setForm(p=>({...p,[f.k]:e.target.value}))}
                        placeholder={f.ph} rows={3}
                        style={{...inp,resize:"vertical"}}/>
                    ) : (
                      <input value={form[f.k]||''} onChange={e=>setForm(p=>({...p,[f.k]:e.target.value}))}
                        placeholder={f.ph} style={inp}/>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={{display:"flex",gap:8,marginTop:8}}>
              <button onClick={()=>{setSelected(null);setForm({});}}
                style={{flex:1,padding:"10px",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,color:T.muted,fontSize:12,fontWeight:600,cursor:"pointer"}}>
                Cancel
              </button>
              <button onClick={generate} disabled={generating}
                style={{flex:2,padding:"10px",background:meta?.color||T.gold,border:"none",borderRadius:8,color:"white",fontSize:12,fontWeight:700,cursor:generating?"not-allowed":"pointer",opacity:generating?0.7:1}}>
                {generating?"Generating...":"📄 Fill & Download Word Doc"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PROJECT DOCS PANEL — company + project scoped ───────────────────────────

// ─── GENERATE FROM PROJECT DATA ───────────────────────────────────────────────
// Auto-fills GCG Word templates with project/CO/RFI data and downloads filled docx
function GenerateFromProjectData({folder, proj, session}) {
  const cid = window._grokCompanyId || session?.companyId || "local";
  const cosKey  = "grok_cos_global_"  + cid;
  const rfisKey = "grok_rfis_global_" + cid;
  const tplKey  = "grok_templates_"   + cid;
  const [generating, setGenerating] = React.useState(null);

  let cos = [], rfis = [], templates = [];
  try { cos       = JSON.parse(localStorage.getItem(cosKey)||"[]"); } catch(e){}
  try { rfis      = JSON.parse(localStorage.getItem(rfisKey)||"[]"); } catch(e){}
  try { templates = JSON.parse(localStorage.getItem(tplKey)||"[]"); } catch(e){}

  const coTemplate  = templates.find(t=>t.cat==="co"  && t.data);
  const rfiTemplate = templates.find(t=>t.cat==="rfi" && t.data);

  const cs = (()=>{ try{ return JSON.parse(localStorage.getItem(window._grokCompanyKey||"grok_company_settings")||"{}")} catch{ return {} } })();
  const companyName = cs.co_header_name || session?.companyName || "Grok Construction Group Inc";
  const cslb        = cs.co_header_cslb || "1152750";

  const fmt = v => '$' + Math.round(parseFloat(v)||0).toLocaleString();

  const fillAndDownload = async (template, fields, fileName) => {
    try {
      if (!window.JSZip) {
        await new Promise((res, rej) => {
          const s = document.createElement('script');
          s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
          s.onload = res; s.onerror = rej;
          document.head.appendChild(s);
        });
      }
      const b64 = template.data.split(',')[1];
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const zip = await window.JSZip.loadAsync(bytes);
      let xml = await zip.file('word/document.xml').async('string');

      // Fill empty table cells sequentially with field values
      let fieldIdx = 0;
      xml = xml.replace(/<w:tc>([\s\S]*?)<\/w:tc>/g, (match) => {
        const hasText = /<w:t[^>]*>[^<\s][^<]*<\/w:t>/.test(match);
        if (!hasText && fieldIdx < fields.length) {
          const val = fields[fieldIdx++];
          if (val) {
            return match.replace(
              /(<w:p\b[^>]*>)(\s*<w:pPr>[\s\S]*?<\/w:pPr>)?\s*(<\/w:p>)/,
              (m, open, pPr, close) => `${open}${pPr||''}<w:r><w:t xml:space="preserve">${val.toString().replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</w:t></w:r>${close}`
            );
          } else { fieldIdx++; }
        } else if (!hasText) { fieldIdx++; }
        return match;
      });

      zip.file('word/document.xml', xml);
      const out = await zip.generateAsync({type:'blob', mimeType:'application/vnd.openxmlformats-officedocument.wordprocessingml.document'});
      const url = URL.createObjectURL(out);
      const a = document.createElement('a'); a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch(e) {
      console.error('Fill error:', e);
      alert('Error generating document: ' + e.message + '\nFalling back to PDF.');
      return false;
    }
    return true;
  };

  const generateCO = async (co) => {
    setGenerating(co.id);
    try {
      const labor    = parseFloat(co.labor||0);
      const mats     = parseFloat(co.materials||0);
      const subCost  = parseFloat(co.subCost||0);
      const permits  = parseFloat(co.permits||0);
      const subtotal = labor + mats + subCost + permits;
      const op       = Math.round(subtotal * 0.25);
      const total    = parseFloat(co.owner_price||co.amount||0) || (subtotal + op);
      const origVal  = parseFloat(proj?.value||0);

      // Field order matches GCG_Change_Order.docx table cell sequence
      const fields = [
        co.id||'CO-001', co.date||new Date().toLocaleDateString(), proj?.name||'', proj?.startDate||'',
        proj?.client||'', companyName+' · CSLB #'+cslb,
        fmt(origVal), fmt(0),
        fmt(origVal),
        // Description rows (6 blank rows — fill first, leave rest)
        co.desc||co.description||'', '', '', '', '', '',
        // Cost breakdown
        fmt(labor), fmt(mats), fmt(subCost), fmt(permits), fmt(subtotal), fmt(op), fmt(total),
        // Revised summary
        fmt(origVal), fmt(0), fmt(total), fmt(origVal+total),
        // Schedule
        '', '', '',
      ];

      if (coTemplate) {
        const ok = await fillAndDownload(coTemplate, fields, `GCG_CO_${co.id}_${(proj?.name||'Project').split(' ')[0]}.docx`);
        if (!ok) exportCOPDF(co, proj, session);
      } else {
        exportCOPDF(co, proj, session);
      }
    } finally { setGenerating(null); }
  };

  const generateRFI = async (rfi) => {
    const key = rfi.num||rfi.id;
    setGenerating(key);
    try {
      const fields = [
        rfi.num||rfi.id||'RFI-001',
        rfi.date||new Date().toLocaleDateString(),
        rfi.due_date||rfi.due||'',
        (rfi.priority||'Routine').toUpperCase(),
        proj?.name||'', '',
        proj?.client||'', rfi.assignee||'',
        rfi.assignee||'', rfi.drawing_ref||'',
        rfi.affects_schedule?'YES':'No', rfi.cost_impact||'TBD',
        rfi.subject||'',
        rfi.desc||rfi.question||'', '', '', '', '',
      ];

      if (rfiTemplate) {
        const ok = await fillAndDownload(rfiTemplate, fields, `GCG_RFI_${key}_${(proj?.name||'Project').split(' ')[0]}.docx`);
        if (!ok) exportRFIPDF(rfi, proj, session);
      } else {
        exportRFIPDF(rfi, proj, session);
      }
    } finally { setGenerating(null); }
  };

  return (
    <div style={{marginTop:14,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 14px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontSize:11,fontWeight:700,color:T.text}}>⚡ Generate Documents from Project Data</div>
        {(coTemplate||rfiTemplate)&&<span style={{fontSize:9,color:T.green,fontWeight:700,background:T.greenBg,padding:"2px 7px",borderRadius:10}}>✓ Using your GCG templates</span>}
      </div>
      {cos.length===0&&rfis.length===0&&(
        <div style={{fontSize:11,color:T.muted}}>No COs or RFIs yet. Create one in the CO or RFI tab first.</div>
      )}

      {cos.length>0&&(
        <div style={{marginBottom:10}}>
          <div style={{fontSize:10,fontWeight:700,color:T.amber,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>
            📝 Change Orders
            {coTemplate&&<span style={{color:T.green,fontWeight:600,marginLeft:6,textTransform:"none"}}>· {coTemplate.fileName}</span>}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            {cos.map(co=>(
              <div key={co.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",background:T.bg1,borderRadius:7,border:"1px solid "+T.subtle}}>
                <div style={{flex:1,minWidth:0}}>
                  <span style={{fontSize:11,fontWeight:700,color:T.text}}>{co.id}</span>
                  <span style={{fontSize:10,color:T.muted,marginLeft:8}}>{(co.desc||"").slice(0,40)}{(co.desc||"").length>40?"...":""}</span>
                  <span style={{fontSize:9,marginLeft:8,padding:"1px 7px",borderRadius:10,fontWeight:700,
                    background:co.status==="approved"?T.greenBg:T.amberBg,
                    color:co.status==="approved"?T.green:T.amber}}>{co.status||"pending"}</span>
                </div>
                <button onClick={()=>generateCO(co)} disabled={!!generating}
                  style={{fontSize:10,fontWeight:700,background:coTemplate?T.greenBg:T.amberBg,color:coTemplate?T.green:T.amber,
                    border:"1px solid "+(coTemplate?T.green:T.amber)+"40",borderRadius:6,padding:"4px 10px",
                    cursor:generating?"not-allowed":"pointer",whiteSpace:"nowrap",opacity:generating?0.6:1}}>
                  {generating===co.id?"Generating...":(coTemplate?"📄 Fill & Download":"📄 Generate PDF")}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {rfis.length>0&&(
        <div>
          <div style={{fontSize:10,fontWeight:700,color:"#8b5cf6",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>
            ❓ RFIs
            {rfiTemplate&&<span style={{color:T.green,fontWeight:600,marginLeft:6,textTransform:"none"}}>· {rfiTemplate.fileName}</span>}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            {rfis.map(rfi=>(
              <div key={rfi.id||rfi.num} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",background:T.bg1,borderRadius:7,border:"1px solid "+T.subtle}}>
                <div style={{flex:1,minWidth:0}}>
                  <span style={{fontSize:11,fontWeight:700,color:T.text}}>{rfi.num||rfi.id}</span>
                  <span style={{fontSize:10,color:T.muted,marginLeft:8}}>{(rfi.subject||rfi.desc||"").slice(0,40)}</span>
                  <span style={{fontSize:9,marginLeft:8,padding:"1px 7px",borderRadius:10,fontWeight:700,
                    background:rfi.status==="closed"?T.greenBg:T.amberBg,
                    color:rfi.status==="closed"?T.green:T.amber}}>{rfi.status||"open"}</span>
                </div>
                <button onClick={()=>generateRFI(rfi)} disabled={!!generating}
                  style={{fontSize:10,fontWeight:700,background:rfiTemplate?T.greenBg:"#8b5cf620",
                    color:rfiTemplate?T.green:"#8b5cf6",border:"1px solid "+(rfiTemplate?T.green+"40":"#8b5cf640"),
                    borderRadius:6,padding:"4px 10px",cursor:generating?"not-allowed":"pointer",
                    whiteSpace:"nowrap",opacity:generating?0.6:1}}>
                  {generating===(rfi.id||rfi.num)?"Generating...":(rfiTemplate?"📄 Fill & Download":"📄 Generate PDF")}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



function ProjectDocsPanel({proj, session}) {
  const cid = session?.companyId || window._grokCompanyId || "local";
  const sk  = "grok_proj_docs_" + cid + "_" + proj?.id;
  const T = useTheme();
  const [showDocGen, setShowDocGen] = React.useState(false);
  const [genStep, setGenStep] = React.useState(1);
  const [genDoc, setGenDoc] = React.useState(null);
  const [genFields, setGenFields] = React.useState({});
  const gf = (k,v) => setGenFields(p=>({...p,[k]:v}));

  // Document generator — merges project data into template and downloads
  const DOC_TYPES = [
    {id:"gcg_t4",  name:"Construction Proposal",       folder:"contract", fields:["scopeItems","totalAmount","paymentFormat"]},
    {id:"gcg_t1",  name:"Owner Agreement",              folder:"contract", fields:["contractAmount","startDate","completionDate"]},
    // CO and RFI have dedicated generators in Admin tab
    {id:"gcg_t7",  name:"Lien Waiver — Progress",       folder:"lien",     fields:["drawAmount","throughDate","waiverType"]},
    {id:"gcg_t8",  name:"20-Day Preliminary Notice",    folder:"lien",     fields:["workDescription","estimatedValue","partyHiredBy"]},
    {id:"gcg_t9",  name:"Notice of Completion",         folder:"closeout", fields:["completionDate","contractorName","lenderName"]},
    {id:"gcg_t10", name:"Certificate of Substantial Completion", folder:"closeout", fields:["certNumber","actualCompletionDate","retentionAmount","punchListValue"]},
    {id:"gcg_t11", name:"7-Day Tenant Notice",          folder:"contract", fields:["unitNumber","workStartDate","workDuration","superintendent","supPhone"]},
    {id:"gcg_t12", name:"Sub Pre-Qualification",        folder:"contract", fields:["subTrade"]},
    {id:"gcg_t13", name:"Punch List",                   folder:"closeout", fields:["walkDate","walkedBy"]},
    {id:"gcg_t14", name:"1-Year Warranty Letter",       folder:"closeout", fields:["completionDate","warrantyExpiry"]},
    {id:"gcg_t15", name:"Occupied Unit Protocol",       folder:"daily",    fields:["unitNumber","superintendent","workDate"]},
    {id:"gcg_t16", name:"Close-Out Checklist",          folder:"closeout", fields:["closeOutDate","projectManager"]},
  ];

  const FIELD_LABELS = {
    scopeItems:         {label:"Scope of Work", type:"textarea", placeholder:"Describe all work included..."},
    totalAmount:        {label:"Total Proposal Price ($)", type:"number", placeholder:"e.g. 125000"},
    paymentFormat:      {label:"Payment Format", type:"select", options:["Format A — Residential/ADU (Milestones)","Format B — SRO/Occupied Rehab (Per Phase)","Format C — Commercial/Affordable/Public Works (Monthly Draw)"]},
    contractAmount:     {label:"Contract Amount ($)", type:"number", placeholder:"e.g. 250000"},
    startDate:          {label:"Start Date", type:"text", placeholder:"e.g. 06/01/2026"},
    completionDate:     {label:"Completion Date", type:"text", placeholder:"e.g. 12/01/2026"},
    coNumber:           {label:"CO Number", type:"text", placeholder:"e.g. CO-001"},
    description:        {label:"Description of Change", type:"textarea", placeholder:"Describe the change..."},
    amount:             {label:"Change Order Amount ($)", type:"number", placeholder:"e.g. 8500"},
    daysAdded:          {label:"Calendar Days Added", type:"number", placeholder:"e.g. 5"},
    rfiNumber:          {label:"RFI Number", type:"text", placeholder:"e.g. RFI-003"},
    subject:            {label:"Subject / Question", type:"text", placeholder:"Brief description..."},
    priority:           {label:"Priority", type:"select", options:["Routine","Urgent","Critical — Work on Hold"]},
    drawAmount:         {label:"Payment Amount This Draw ($)", type:"number", placeholder:"e.g. 45000"},
    throughDate:        {label:"Through Date (work completed through)", type:"text", placeholder:"e.g. 05/21/2026"},
    waiverType:         {label:"Waiver Type", type:"select", options:["Conditional — Progress (§8132)","Unconditional — Progress (§8134)","Conditional — Final (§8136)","Unconditional — Final (§8138)"]},
    workDescription:    {label:"Description of Work", type:"textarea", placeholder:"Type of work being performed..."},
    estimatedValue:     {label:"Estimated Contract Value ($)", type:"number", placeholder:"e.g. 75000"},
    partyHiredBy:       {label:"Name of Party Who Hired GCG", type:"text", placeholder:"Owner or GC name..."},
    lenderName:         {label:"Construction Lender (write None if not applicable)", type:"text", placeholder:"e.g. None"},
    contractorName:     {label:"General Contractor Name", type:"text", placeholder:"Grok Construction Group Inc"},
    certNumber:         {label:"Certificate Number", type:"text", placeholder:"e.g. CSC-001"},
    actualCompletionDate:{label:"Actual Date of Substantial Completion", type:"text", placeholder:"e.g. 05/01/2026"},
    retentionAmount:    {label:"Total Retention Withheld ($)", type:"number", placeholder:"e.g. 25000"},
    punchListValue:     {label:"Estimated Punch List Value ($)", type:"number", placeholder:"e.g. 2500"},
    unitNumber:         {label:"Unit Number(s)", type:"text", placeholder:"e.g. Unit 204"},
    workStartDate:      {label:"Work Start Date", type:"text", placeholder:"e.g. 05/15/2026"},
    workDuration:       {label:"Estimated Duration", type:"text", placeholder:"e.g. 3 weeks"},
    superintendent:     {label:"Job Site Superintendent", type:"text", placeholder:"Full name"},
    supPhone:           {label:"Superintendent Phone", type:"text", placeholder:"(650) 000-0000"},
    subTrade:           {label:"Subcontractor Trade", type:"text", placeholder:"e.g. Electrical, Plumbing..."},
    walkDate:           {label:"Date of Walk", type:"text", placeholder:"e.g. 05/07/2026"},
    walkedBy:           {label:"Walk Conducted By", type:"text", placeholder:"Name"},
    warrantyExpiry:     {label:"Warranty Expiration Date (1 year from completion)", type:"text", placeholder:"e.g. 05/01/2027"},
    workDate:           {label:"Date", type:"text", placeholder:"e.g. 05/07/2026"},
    projectManager:     {label:"Project Manager", type:"text", placeholder:"Name"},
    closeOutDate:       {label:"Close-Out Date", type:"text", placeholder:"e.g. 05/07/2026"},
  };

  const generateDocument = () => {
    const tpl = DOC_TYPES.find(d=>d.id===genDoc);
    if(!tpl) return;
    // Get template data
    const templates = (() => { try { return JSON.parse(localStorage.getItem("grok_templates_"+cid)||"[]"); } catch { return []; }})();
    const tplData = templates.find(t=>t.id===tpl.id);
    if(!tplData?.data) { alert("Template file not found. Please check Document Kit."); return; }
    // Download the template — in future this will be merged with project data
    // For now download with project name in filename
    const a = document.createElement("a");
    a.href = tplData.data;
    const today = new Date().toLocaleDateString("en-US",{month:"2-digit",day:"2-digit",year:"numeric"}).replace(/\//g,"-");
    const projName = (proj?.name||"Project").split(" ")[0].replace(/[^a-zA-Z0-9]/g,"");
    a.download = `GCG_${projName}_${tpl.name.replace(/[^a-zA-Z0-9]/g,"_")}_${today}.docx`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    // Save to project docs
    const doc = {
      id:"doc_"+Date.now(),
      name:`${tpl.name} — ${proj?.name||"Project"} — ${today}`,
      type:tpl.name,
      folder:tpl.folder,
      date:new Date().toLocaleDateString(),
      data:tplData.data,
      stored:true,
      generated:true,
      projectFields:genFields,
    };
    save([doc,...docs]);
    setShowDocGen(false);
    setGenStep(1);
    setGenDoc(null);
    setGenFields({});
    alert(`✅ ${tpl.name} downloaded and saved to your project documents.`);
  };

  // Document folders — each folder maps to a construction document type
  const FOLDERS = [
    { id:"contract",    label:"Contracts & Proposals",  icon:"📑", color:"#3b82f6",
      desc:"Owner contract, subcontractor agreements, scope of work",
      accepts:["Contract","Proposal","Scope of Work","NDA","LOI"] },
    { id:"co",          label:"Change Orders",           icon:"📝", color:"#f59e0b",
      desc:"Approved COs, pending COs, CO log",
      accepts:["Change Order","CO Request","Pending CO"] },
    { id:"rfi",         label:"RFIs",                    icon:"❓", color:"#8b5cf6",
      desc:"Requests for information, architect responses",
      accepts:["RFI","RFI Response","Clarification"] },
    { id:"submittal",   label:"Submittals",              icon:"📋", color:"#06b6d4",
      desc:"Shop drawings, product data, samples, mock-ups",
      accepts:["Shop Drawing","Product Data","Sample","Mock-Up","Submittal"] },
    { id:"permit",      label:"Permits & Approvals",     icon:"🏛", color:"#f97316",
      desc:"Building permits, inspection approvals, NOC",
      accepts:["Building Permit","Electrical Permit","Plumbing Permit","Mechanical Permit","NOC","Inspection Approval"] },
    { id:"inspection",  label:"Inspection Reports",      icon:"🔍", color:"#10b981",
      desc:"Field inspection reports, punch list sign-offs, HERS reports",
      accepts:["Inspection Report","HERS Report","Field Report","Punch List Sign-Off"] },
    { id:"insurance",   label:"Insurance & Compliance",  icon:"🛡", color:"#ef4444",
      desc:"COIs, W-9s, workers comp, bonds, licenses",
      accepts:["COI","W-9","Workers Comp","Bond","License","Prevailing Wage"] },
    { id:"drawings",    label:"Drawings & Blueprints",   icon:"📐", color:"#a78bfa",
      desc:"Architectural, structural, MEP drawings, as-builts",
      accepts:["Architectural Drawing","Structural Drawing","MEP Drawing","As-Built","Site Plan"] },
    { id:"daily",       label:"Daily Reports & Photos",  icon:"📷", color:"#84cc16",
      desc:"Daily logs, progress photos, meeting minutes",
      accepts:["Daily Log","Progress Photo","Meeting Minutes","Site Photo"] },
    { id:"closeout",    label:"Closeout Documents",      icon:"✅", color:"#14b8a6",
      desc:"Certificate of occupancy, warranties, O&M manuals, final lien waivers",
      accepts:["Certificate of Occupancy","Warranty","O&M Manual","Lien Waiver","Closeout Package"] },
    { id:"lien",        label:"Lien Waivers",            icon:"📜", color:"#f43f5e",
      desc:"Conditional/unconditional lien waivers, releases, notices",
      accepts:["Conditional Lien Waiver","Unconditional Lien Waiver","Preliminary Notice","Lien Release"] },
    { id:"financial",   label:"Financial & Billing",     icon:"💰", color:"#d4a843",
      desc:"Pay applications, invoices, billing schedules, certified payroll",
      accepts:["Pay Application","Invoice","Schedule of Values","Certified Payroll","Budget"] },
  ];

  const [docs, setDocs] = React.useState(()=>{
    try{return JSON.parse(localStorage.getItem(sk)||"[]");}catch{return [];}
  });
  const [activeFolder, setActiveFolder] = React.useState(null); // null = show all folders
  const [showAdd, setShowAdd] = React.useState(false);
  const [form, setForm] = React.useState({name:"", type:"", notes:"", link:""});
  const [search, setSearch] = React.useState("");
  const fileRef = React.useRef();

  const save = (d) => {
    setDocs(d);
    try{localStorage.setItem(sk, JSON.stringify(d));}catch(e){}
  };

  const del = (id) => {
    if(!true) return;
    save(docs.filter(d=>String(d.id)!==String(id)));
  };

  const addDoc = (fileData) => {
    if(!form.name && !fileData && !form.link) return;
    const folder = activeFolder || "contract";
    const doc = {
      id: "doc_"+Date.now(),
      name: form.name || (fileData?.name) || form.link,
      type: form.type || (activeFolder ? FOLDERS.find(f=>f.id===activeFolder)?.accepts[0] : "Contract"),
      folder: folder,
      notes: form.notes,
      link: form.link,
      date: new Date().toLocaleDateString(),
      ...(fileData || {}),
    };
    save([doc, ...docs]);
    setForm({name:"", type:"", notes:"", link:""});
    setShowAdd(false);
  };

  const handleUpload = (file, folderId) => {
    if(file.size > 2*1024*1024) {
      // Too large — save metadata only
      const doc = {
        id:"doc_"+Date.now(), name:file.name,
        type: FOLDERS.find(f=>f.id===folderId)?.accepts[0]||"Document",
        folder: folderId||activeFolder||"contract",
        date: new Date().toLocaleDateString(),
        size: (file.size/1024).toFixed(0)+"KB", largeFile:true,
        notes:"File too large to store locally. Add a Drive link."
      };
      save([doc, ...docs]);
      return;
    }
    const reader = new FileReader();
    reader.onload = ev => {
      const doc = {
        id:"doc_"+Date.now(), name:file.name,
        type: form.type || FOLDERS.find(f=>f.id===folderId)?.accepts[0]||"Document",
        folder: folderId||activeFolder||"contract",
        date: new Date().toLocaleDateString(),
        size: (file.size/1024).toFixed(0)+"KB",
        data: ev.target.result, stored:true,
      };
      save([doc, ...docs]);
    };
    reader.readAsDataURL(file);
  };

  const openDoc = (doc) => {
    if(doc.data) {
      const a=document.createElement("a"); a.href=doc.data; a.download=doc.name;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } else if(doc.link) {
      window.open(doc.link.startsWith("http")?doc.link:"https://"+doc.link,"_blank");
    } else {
      alert("No file stored. Add a Google Drive or Dropbox link to access this document.");
    }
  };

  const folderDocs = (folderId) => docs.filter(d=>d.folder===folderId);
  const filteredDocs = search
    ? docs.filter(d=>d.name.toLowerCase().includes(search.toLowerCase())||d.type?.toLowerCase().includes(search.toLowerCase()))
    : activeFolder ? folderDocs(activeFolder) : docs;

  const activeFolderData = FOLDERS.find(f=>f.id===activeFolder);
  const inp = {width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"};

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div>
          {activeFolder ? (
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <button onClick={()=>{setActiveFolder(null);setShowAdd(false);}}
                style={{background:"none",border:"none",color:T.blue,cursor:"pointer",fontSize:12,fontWeight:600,padding:0}}>
                ← All Folders
              </button>
              <span style={{color:T.muted,fontSize:12}}>/ {activeFolderData?.icon} {activeFolderData?.label}</span>
            </div>
          ) : (
            <div>
              <div style={{fontSize:13,fontWeight:700,color:T.text}}>Project Documents</div>
              <div style={{fontSize:10,color:T.muted,marginTop:2}}>
                {docs.length} document{docs.length!==1?"s":""} across {FOLDERS.filter(f=>folderDocs(f.id).length>0).length} folders · Private to your account
              </div>
            </div>
          )}
        </div>
        <div style={{display:"flex",gap:6}}>
          <input ref={fileRef} type="file" style={{display:"none"}}
            accept=".pdf,.docx,.doc,.xlsx,.xls,.png,.jpg,.jpeg,.heic,.dwg,.zip"
            multiple
            onChange={e=>Array.from(e.target.files).forEach(f=>handleUpload(f, activeFolder||"contract"))}/>
          <Btn small ghost onClick={()=>fileRef.current.click()}>Upload</Btn>
          <Btn small ghost onClick={()=>setShowAdd(p=>!p)}>+ Add Link</Btn>
          <button onClick={()=>{setShowDocGen(true);setGenStep(1);setGenDoc(null);setGenFields({});}}
            style={{padding:"5px 10px",borderRadius:8,border:`1px solid ${T.gold}`,background:T.goldBg||"#FDF6E3",color:T.gold,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
            📄 Generate Doc
          </button>
        </div>
      </div>

      {/* Generate Document Modal */}
      {showDocGen&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:T.bg1,borderRadius:14,width:"100%",maxWidth:560,maxHeight:"90vh",overflowY:"auto",border:`1px solid ${T.subtle}`}}>
            {/* Modal header */}
            <div style={{padding:"16px 20px",borderBottom:`1px solid ${T.subtle}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:15,fontWeight:700,color:T.text}}>📄 Generate Document</div>
                <div style={{fontSize:11,color:T.muted,marginTop:2}}>Auto-filled with project info · Downloads as Word file</div>
              </div>
              <button onClick={()=>setShowDocGen(false)} style={{background:"none",border:"none",color:T.muted,fontSize:18,cursor:"pointer"}}>✕</button>
            </div>
            <div style={{padding:"16px 20px"}}>

              {/* Project info banner */}
              <div style={{background:T.bg2,borderRadius:8,padding:"10px 14px",marginBottom:16,border:`1px solid ${T.subtle}`}}>
                <div style={{fontSize:11,color:T.muted,marginBottom:4}}>Generating for project</div>
                <div style={{fontSize:14,fontWeight:700,color:T.text}}>{proj?.name||"—"}</div>
                <div style={{fontSize:11,color:T.muted}}>{proj?.address||""} {proj?.owner?`· Owner: ${proj.owner}`:""}</div>
              </div>

              {/* Step 1 — Pick document */}
              {genStep===1&&(
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Which document do you need?</div>
                  <div style={{display:"grid",gridTemplateColumns:window.innerWidth<768?"1fr":"1fr 1fr",gap:8}}>
                    {DOC_TYPES.map(dt=>(
                      <button key={dt.id} onClick={()=>{setGenDoc(dt.id);setGenStep(2);}}
                        style={{padding:"10px 12px",borderRadius:9,border:`1px solid ${genDoc===dt.id?T.gold:T.subtle}`,background:genDoc===dt.id?(T.goldBg||"#FDF6E3"):T.bg2,cursor:"pointer",textAlign:"left",transition:"all 0.15s"}}>
                        <div style={{fontSize:12,fontWeight:700,color:genDoc===dt.id?T.gold:T.text}}>{dt.name}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2 — Fill fields */}
              {genStep===2&&(()=>{
                const tpl = DOC_TYPES.find(d=>d.id===genDoc);
                if(!tpl) return null;
                return (
                  <div>
                    <button onClick={()=>setGenStep(1)} style={{background:"none",border:"none",color:T.blue,cursor:"pointer",fontSize:12,fontWeight:600,marginBottom:12,padding:0}}>← Back</button>
                    <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:4}}>{tpl.name}</div>
                    <div style={{fontSize:11,color:T.muted,marginBottom:16}}>Project info is pre-filled. Add any additional details below.</div>

                    {/* Auto-filled project fields */}
                    <div style={{background:T.bg2,borderRadius:8,padding:"10px 14px",marginBottom:14,border:`1px solid ${T.subtle}`}}>
                      <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:8}}>AUTO-FILLED FROM PROJECT</div>
                      {[
                        ["Project Name", proj?.name],
                        ["Property Address", proj?.address],
                        ["Owner / Client", proj?.owner],
                        ["Project Type", proj?.type],
                        ["Start Date", proj?.startDate],
                      ].filter(([,v])=>v).map(([label,val])=>(
                        <div key={label} style={{display:"flex",gap:8,marginBottom:4}}>
                          <span style={{fontSize:11,color:T.muted,width:120,flexShrink:0}}>{label}:</span>
                          <span style={{fontSize:11,color:T.text,fontWeight:600}}>{val}</span>
                        </div>
                      ))}
                    </div>

                    {/* Additional fields */}
                    {tpl.fields.length>0&&(
                      <div style={{marginBottom:16}}>
                        <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:8}}>ADDITIONAL DETAILS</div>
                        {tpl.fields.map(fk=>{
                          const fd = FIELD_LABELS[fk];
                          if(!fd) return null;
                          return (
                            <div key={fk} style={{marginBottom:10}}>
                              <div style={{fontSize:11,color:T.muted,marginBottom:4}}>{fd.label}</div>
                              {fd.type==="textarea" ? (
                                <textarea value={genFields[fk]||""} onChange={e=>gf(fk,e.target.value)}
                                  placeholder={fd.placeholder} rows={3}
                                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit",resize:"vertical"}}/>
                              ) : fd.type==="select" ? (
                                <select value={genFields[fk]||""} onChange={e=>gf(fk,e.target.value)}
                                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}>
                                  <option value="">Select...</option>
                                  {fd.options.map(o=><option key={o} value={o}>{o}</option>)}
                                </select>
                              ) : (
                                <input value={genFields[fk]||""} onChange={e=>gf(fk,e.target.value)}
                                  placeholder={fd.placeholder} type={fd.type}
                                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                      <button onClick={()=>setShowDocGen(false)}
                        style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${T.subtle}`,background:"none",color:T.muted,fontSize:12,cursor:"pointer"}}>
                        Cancel
                      </button>
                      <button onClick={generateDocument}
                        style={{padding:"8px 18px",borderRadius:8,border:`1px solid ${T.gold}`,background:T.gold,color:"#0F1623",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                        📥 Download {tpl.name}
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Search bar */}
      <div style={{position:"relative",marginBottom:12}}>
        <input value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="Search documents..."
          style={{...inp, paddingLeft:30, fontSize:11}}/>
        <span style={{position:"absolute",left:9,top:"50%",transform:"translateY(-50%)",fontSize:12,color:T.muted}}>🔍</span>
        {search&&<button onClick={()=>setSearch("")} style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:11}}>✕</button>}
      </div>

      {/* Add link form */}
      {showAdd&&(
        <div style={{background:T.bg2,border:"1px solid "+T.blue+"30",borderRadius:10,padding:"12px 14px",marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:8}}>Add Document Link</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Document Name *</div>
              <input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Owner Contract - Signed" style={inp}/>
            </div>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Type</div>
              <select value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))} style={inp}>
                <option value="">Select type...</option>
                {FOLDERS.map(f=>f.accepts.map(t=><option key={t} value={t}>{f.icon} {t}</option>))}
              </select>
            </div>
          </div>
          <div style={{marginBottom:8}}>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Link (Google Drive, Dropbox, DocuSign, etc.)</div>
            <input value={form.link} onChange={e=>setForm(p=>({...p,link:e.target.value}))} placeholder="https://drive.google.com/..." style={inp}/>
          </div>
          <div style={{marginBottom:8}}>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Folder</div>
            <select value={activeFolder||""} onChange={e=>setActiveFolder(e.target.value||null)} style={inp}>
              {FOLDERS.map(f=><option key={f.id} value={f.id}>{f.icon} {f.label}</option>)}
            </select>
          </div>
          {form.notes!==undefined&&<div style={{marginBottom:8}}>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Notes</div>
            <input value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="Optional description..." style={inp}/>
          </div>}
          <div style={{display:"flex",gap:6}}>
            <Btn ghost small onClick={()=>setShowAdd(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
            <Btn primary small disabled={!form.name&&!form.link} onClick={()=>addDoc()} style={{flex:2,justifyContent:"center"}}>Save Document</Btn>
          </div>
        </div>
      )}

      {/* Folder grid view */}
      {!activeFolder&&!search&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:8,marginBottom:16}}>
          {FOLDERS.map(folder=>{
            const count = folderDocs(folder.id).length;
            return (
              <button key={folder.id} onClick={()=>{setActiveFolder(folder.id);setShowAdd(false);}}
                style={{background:T.bg1,border:"1px solid "+(count>0?folder.color+"40":T.subtle),borderRadius:10,padding:"12px 14px",cursor:"pointer",textAlign:"left",transition:"all 0.15s",position:"relative"}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=folder.color;e.currentTarget.style.background=folder.color+"08";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=count>0?folder.color+"40":T.subtle;e.currentTarget.style.background=T.bg1;}}>
                <div style={{fontSize:22,marginBottom:6}}>{folder.icon}</div>
                <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:2,lineHeight:1.3}}>{folder.label}</div>
                <div style={{fontSize:9,color:T.muted,lineHeight:1.4,marginBottom:6}}>{folder.desc}</div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:10,fontWeight:700,color:count>0?folder.color:T.muted}}>
                    {count > 0 ? count+" file"+(count!==1?"s":"") : "Empty"}
                  </span>
                  <span style={{fontSize:9,color:folder.color,fontWeight:600}}>→</span>
                </div>
                {count>0&&<div style={{position:"absolute",top:8,right:8,width:8,height:8,borderRadius:"50%",background:folder.color}}/>}
              </button>
            );
          })}
        </div>
      )}

      {/* Document list — inside a folder or search results */}
      {(activeFolder||search)&&(
        <div>
          {activeFolder&&!search&&(
            <div style={{background:activeFolderData?.color+"10",border:"1px solid "+activeFolderData?.color+"30",borderRadius:9,padding:"10px 14px",marginBottom:10}}>
              <div style={{fontSize:11,fontWeight:700,color:activeFolderData?.color,marginBottom:2}}>{activeFolderData?.icon} {activeFolderData?.label}</div>
              <div style={{fontSize:10,color:T.muted,marginBottom:6}}>{activeFolderData?.desc}</div>
              <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                {activeFolderData?.accepts.map(t=>(
                  <span key={t} style={{fontSize:9,background:activeFolderData?.color+"15",color:activeFolderData?.color,borderRadius:6,padding:"2px 7px",fontWeight:600}}>{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* Quick upload drop zone for active folder */}
          {activeFolder&&(
            <div onClick={()=>fileRef.current.click()}
              style={{border:"2px dashed "+activeFolderData?.color+"50",borderRadius:9,padding:"10px 14px",marginBottom:10,textAlign:"center",cursor:"pointer",background:activeFolderData?.color+"05"}}
              onDragOver={e=>{e.preventDefault();e.currentTarget.style.background=activeFolderData?.color+"15";}}
              onDragLeave={e=>{e.currentTarget.style.background=activeFolderData?.color+"05";}}
              onDrop={e=>{e.preventDefault();e.currentTarget.style.background=activeFolderData?.color+"05";Array.from(e.dataTransfer.files).forEach(f=>handleUpload(f,activeFolder));}}>
              <span style={{fontSize:18}}>📎</span>
              <span style={{fontSize:11,color:activeFolderData?.color,fontWeight:600,marginLeft:6}}>Drop files here or click to upload to {activeFolderData?.label}</span>
            </div>
          )}

          {filteredDocs.length===0 ? (
            <div style={{textAlign:"center",padding:"32px",color:T.muted,background:T.bg2,borderRadius:10,border:"2px dashed "+T.subtle}}>
              <div style={{fontSize:28,opacity:0.3,marginBottom:8}}>{activeFolderData?.icon||"📁"}</div>
              <div style={{fontSize:12,fontWeight:600,marginBottom:4}}>
                {search ? "No documents match your search" : "No documents in this folder yet"}
              </div>
              <div style={{fontSize:11,lineHeight:1.7}}>
                {search ? "Try a different search term" : "Upload files or add a link above"}
              </div>
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {filteredDocs.map(doc=>{
                const folder = FOLDERS.find(f=>f.id===doc.folder)||FOLDERS[0];
                return (
                  <div key={doc.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:T.bg1,borderRadius:9,border:"1px solid "+T.subtle,transition:"border-color 0.15s"}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=folder.color}
                    onMouseLeave={e=>e.currentTarget.style.borderColor=T.subtle}>
                    <div style={{width:32,height:32,borderRadius:7,background:folder.color+"20",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>
                      {folder.icon}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:700,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{doc.name}</div>
                      <div style={{display:"flex",gap:6,marginTop:2,flexWrap:"wrap"}}>
                        <span style={{fontSize:9,background:folder.color+"15",color:folder.color,borderRadius:6,padding:"1px 6px",fontWeight:600}}>{doc.type||folder.label}</span>
                        <span style={{fontSize:9,color:T.muted}}>{doc.date}</span>
                        {doc.size&&<span style={{fontSize:9,color:T.muted}}>{doc.size}</span>}
                        {doc.stored&&<span style={{fontSize:9,color:T.green}}>✓ Stored</span>}
                        {doc.link&&<span style={{fontSize:9,color:T.blue}}>🔗 Link</span>}
                        {doc.largeFile&&<span style={{fontSize:9,color:T.amber}}>⚠ Large — add link</span>}
                      </div>
                      {doc.notes&&<div style={{fontSize:9,color:T.muted,marginTop:2}}>{doc.notes}</div>}
                    </div>
                    <div style={{display:"flex",gap:5,flexShrink:0}}>
                      <button onClick={()=>openDoc(doc)}
                        style={{fontSize:10,fontWeight:700,background:folder.color+"15",color:folder.color,border:"1px solid "+folder.color+"30",borderRadius:6,padding:"4px 10px",cursor:"pointer"}}>
                        {doc.data?"↓ Download":doc.link?"↗ Open":"View"}
                      </button>
                      <button onClick={()=>del(doc.id)}
                        style={{fontSize:10,background:T.bg2,color:T.muted,border:"1px solid "+T.subtle,borderRadius:6,padding:"4px 8px",cursor:"pointer"}}>✕</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Document Kit link */}
      <div style={{marginTop:14,background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:9,padding:"9px 14px",display:"flex",gap:10,alignItems:"center"}}>
        <span style={{fontSize:14,flexShrink:0}}>📋</span>
        <div>
          <span style={{fontSize:11,fontWeight:700,color:T.amber}}>Need blank templates?</span>
          <span style={{fontSize:10,color:T.muted}}> CO forms, RFI forms, lien waivers → </span>
          <strong style={{fontSize:10,color:T.text}}>Document Kit</strong>
          <span style={{fontSize:10,color:T.muted}}> in the sidebar (standard Grok templates)</span>
        </div>
      </div>
    </div>
  );
}


// ─── CO ADMIN PANEL ───────────────────────────────────────────────────────────
// GC/Admin only — full CO management with internal costs + owner price control
// Subs can submit CO requests (visible here for GC to review and price)
// Owner ONLY sees the final GC-approved CO with owner_price — never internal details
function COAdminPanel({proj, projCOs, session}) {
  const isAdmin = canSee(session, "financials");
  const cid = session?.companyId || window._grokCompanyId || "local";
  const storageKey = "grok_cos_global_" + cid; // unified key
  const [cos, setCos] = React.useState(()=>{
    try {
      const all = JSON.parse(localStorage.getItem(storageKey)||"[]");
      // Show COs for this project or all if no project filter
      return all.length ? all : (projCOs||[]);
    } catch { return projCOs||[]; }
  });

  const save = (updated) => {
    setCos(updated);
    try { localStorage.setItem(storageKey, JSON.stringify(updated)); } catch(e) {}
    try { COS.splice(0, COS.length, ...updated); } catch(e) {}
    SyncDB.saveCOs(updated).catch(()=>{});
  };

  const [showAdd, setShowAdd] = React.useState(false);
  const [editId, setEditId] = React.useState(null);
  const [form, setForm] = React.useState({
    desc:"", date:new Date().toLocaleDateString(),
    internal_cost:"", owner_price:"",
    markup_notes:"", reason:"", trade:"",
  });

  const fmt = v => "$"+Math.round(v||0).toLocaleString();

  const addCO = () => {
    if (!form.desc) return;
    const co = {
      id: "CO-"+(cos.length+1).toString().padStart(3,"0"),
      desc: form.desc, date: form.date,
      amount: parseFloat(form.internal_cost)||0,        // internal — GC only
      owner_price: parseFloat(form.owner_price)||parseFloat(form.internal_cost)||0, // what owner sees
      markup_notes: form.markup_notes,
      reason: form.reason, trade: form.trade,
      status: "pending",
      createdBy: session?.email||"owner",
      createdAt: new Date().toISOString(),
    };
    save([...cos, co]);
    setForm({desc:"",date:new Date().toLocaleDateString(),internal_cost:"",owner_price:"",markup_notes:"",reason:"",trade:""});
    setShowAdd(false);
  };

  const updateCO = (id, fields) => save(cos.map(c=>String(c.id)===String(id)?{...c,...fields}:c));
  const deleteCO = (id) => { DELETED_IDS.add("cos", id); save(cos.filter(c=>String(c.id)!==String(id))); SyncDB.deleteById("change_orders", id).catch(()=>{}); };

  const sendToOwner = (co) => {
    // Export clean CO with owner_price only — no internal cost details
    const ownerCO = {
      ...co,
      amount: co.owner_price || co.amount, // show owner_price to owner
      // Strip internal fields
      internal_cost: undefined,
      markup_notes: undefined,
    };
    exportCOPDF(ownerCO, proj, session);
  };

  const inp = {width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"};

  if (!isAdmin) {
    // Subs see a read-only list of COs relevant to their trade only
    // They can submit CO requests
    return <COSubView proj={proj} cos={cos} session={session} onSave={save}/>;
  }

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>Change Orders</div>
          <div style={{fontSize:10,color:T.muted,marginTop:2}}>
            {cos.filter(c=>c.status==="pending").length} pending · {cos.filter(c=>c.status==="approved").length} approved ·
            Approved total: {fmt(cos.filter(c=>c.status==="approved").reduce((s,c)=>s+(c.owner_price||c.amount||0),0))}
          </div>
        </div>
        <Btn primary small onClick={()=>setShowAdd(p=>!p)}>
          <Ic d={ICONS.plus} size={11}/> New CO
        </Btn>
      </div>

      {/* Sub CO requests pending GC review */}
      {cos.filter(c=>c.source==="sub_request"&&c.status==="sub_pending").length>0&&(
        <div style={{background:T.amberBg,border:"1px solid "+T.amber+"40",borderRadius:9,padding:"10px 14px",marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:700,color:T.amber,marginBottom:8}}>
            ⚡ {cos.filter(c=>c.source==="sub_request"&&c.status==="sub_pending").length} Sub CO Request{cos.filter(c=>c.source==="sub_request"&&c.status==="sub_pending").length!==1?"s":""} — Review & Price
          </div>
          {cos.filter(c=>c.source==="sub_request"&&c.status==="sub_pending").map(co=>(
            <div key={co.id} style={{background:T.bg1,borderRadius:8,padding:"10px 12px",marginBottom:6,border:"1px solid "+T.subtle}}>
              <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:2}}>{co.id} — {co.desc}</div>
              <div style={{fontSize:10,color:T.muted,marginBottom:6}}>
                Submitted by {co.submittedBy||"Sub"} · {co.date} · Sub estimate: <strong style={{color:T.amber}}>{fmt(co.sub_estimate)}</strong>
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <input type="number" placeholder="Your price to owner" defaultValue={co.sub_estimate}
                  id={"owner-price-"+co.id}
                  style={{...inp,width:160,fontSize:11}}/>
                <Btn primary small onClick={()=>{
                  const op = parseFloat(document.getElementById("owner-price-"+co.id)?.value)||co.sub_estimate||0;
                  updateCO(co.id,{status:"pending",owner_price:op,amount:co.sub_estimate||op,reviewed:true});
                }}>✓ Price & Promote</Btn>
                <Btn ghost small onClick={()=>updateCO(co.id,{status:"rejected"})}>✗ Reject</Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New CO form */}
      {showAdd&&(
        <div style={{background:T.bg2,border:"1px solid "+T.blue+"30",borderRadius:10,padding:"14px 16px",marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>New Change Order</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
            <div style={{gridColumn:"1/-1"}}>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Description *</div>
              <input value={form.desc} onChange={e=>setForm(p=>({...p,desc:e.target.value}))} placeholder="Drain relocation — Unit 4B conflict with layout" style={inp}/>
            </div>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Trade</div>
              <input value={form.trade} onChange={e=>setForm(p=>({...p,trade:e.target.value}))} placeholder="Plumbing" style={inp}/>
            </div>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Date</div>
              <input type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))} style={inp}/>
            </div>
          </div>

          {/* Internal cost section — GC only */}
          <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:8,padding:"10px 12px",marginBottom:8}}>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>
              🔒 Internal Costs — Owner Never Sees This
            </div>
            <div style={{display:"grid",gridTemplateColumns:window.innerWidth<768?"1fr":"1fr 1fr",gap:8}}>
              <div>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Internal Cost (labor + materials)</div>
                <input type="number" value={form.internal_cost} onChange={e=>setForm(p=>({...p,internal_cost:e.target.value}))} placeholder="6500" style={inp}/>
              </div>
              <div>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Markup Notes (internal)</div>
                <input value={form.markup_notes} onChange={e=>setForm(p=>({...p,markup_notes:e.target.value}))} placeholder="15% markup + $800 supervision" style={inp}/>
              </div>
            </div>
          </div>

          {/* Owner-facing price */}
          <div style={{background:T.teal+"10",border:"1px solid "+T.teal+"30",borderRadius:8,padding:"10px 12px",marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:T.teal,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>
              👁 Price You Present to Owner
            </div>
            <div style={{display:"grid",gridTemplateColumns:window.innerWidth<768?"1fr":"1fr 1fr",gap:8}}>
              <div>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Owner Price *</div>
                <input type="number" value={form.owner_price} onChange={e=>setForm(p=>({...p,owner_price:e.target.value}))} placeholder="8400" style={{...inp,borderColor:T.teal+"60"}}/>
              </div>
              <div>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Reason (shown on CO)</div>
                <input value={form.reason} onChange={e=>setForm(p=>({...p,reason:e.target.value}))} placeholder="Unforeseen condition — drain conflict" style={inp}/>
              </div>
            </div>
            {form.internal_cost&&form.owner_price&&(
              <div style={{fontSize:10,color:T.teal,marginTop:6}}>
                Margin: {fmt((parseFloat(form.owner_price)||0)-(parseFloat(form.internal_cost)||0))} ({(((parseFloat(form.owner_price)||0)-(parseFloat(form.internal_cost)||0))/(parseFloat(form.internal_cost)||1)*100).toFixed(0)}% over internal cost)
              </div>
            )}
          </div>

          <div style={{display:"flex",gap:7}}>
            <Btn ghost small onClick={()=>setShowAdd(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
            <Btn primary small disabled={!form.desc} onClick={addCO} style={{flex:2,justifyContent:"center"}}>Create CO</Btn>
          </div>
        </div>
      )}

      {/* CO list */}
      {cos.filter(c=>c.status!=="sub_pending").length===0&&!showAdd&&(
        <div style={{textAlign:"center",padding:"40px",color:T.muted,fontSize:13,background:T.bg2,borderRadius:10}}>
          No change orders yet. Click <strong>+ New CO</strong> to create one.
        </div>
      )}

      {cos.filter(c=>c.status!=="sub_pending"&&c.status!=="rejected").map(co=>(
        <div key={co.id} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,marginBottom:8,overflow:"hidden"}}>
          <div style={{padding:"12px 16px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                  <span style={{fontSize:11,fontWeight:800,color:T.text,fontFamily:"monospace"}}>{co.id}</span>
                  <span style={{fontSize:9,fontWeight:700,
                    background:co.status==="approved"?T.greenBg:co.status==="pending"?T.amberBg:T.redBg,
                    color:co.status==="approved"?T.green:co.status==="pending"?T.amber:T.red,
                    borderRadius:10,padding:"1px 7px",textTransform:"capitalize"}}>
                    {co.status==="pending"?"Pending Approval":co.status}
                  </span>
                  {co.trade&&<span style={{fontSize:9,color:T.muted,background:T.bg2,borderRadius:6,padding:"1px 7px"}}>{co.trade}</span>}
                </div>
                <div style={{fontSize:12,fontWeight:700,color:T.text}}>{co.desc||co.description}</div>
                <div style={{fontSize:10,color:T.muted,marginTop:2}}>{co.date}{co.reason?" · "+co.reason:""}</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0,marginLeft:12}}>
                {/* Show both prices to admin */}
                <div style={{fontSize:16,fontWeight:800,color:T.teal,fontFamily:"monospace"}}>
                  {fmt(co.owner_price||co.amount)}
                </div>
                <div style={{fontSize:9,color:T.muted}}>Owner sees</div>
                {co.amount&&co.owner_price&&co.amount!==co.owner_price&&(
                  <div style={{fontSize:9,color:T.muted,marginTop:2}}>
                    Internal: <span style={{color:T.gold}}>{fmt(co.amount)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Edit owner price inline */}
            {editId===co.id&&(
              <div style={{background:T.teal+"10",border:"1px solid "+T.teal+"30",borderRadius:8,padding:"10px 12px",marginBottom:8}}>
                <div style={{display:"grid",gridTemplateColumns:window.innerWidth<768?"1fr":"1fr 1fr",gap:8}}>
                  <div>
                    <div style={{fontSize:9,color:T.teal,textTransform:"uppercase",marginBottom:3}}>Owner Price</div>
                    <input type="number" defaultValue={co.owner_price||co.amount} id={"edit-op-"+co.id} style={inp}/>
                  </div>
                  <div>
                    <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Internal Cost</div>
                    <input type="number" defaultValue={co.amount} id={"edit-ic-"+co.id} style={inp}/>
                  </div>
                </div>
                <div style={{display:"flex",gap:6,marginTop:8}}>
                  <Btn ghost small onClick={()=>setEditId(null)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
                  <Btn primary small onClick={()=>{
                    const op=parseFloat(document.getElementById("edit-op-"+co.id)?.value)||0;
                    const ic=parseFloat(document.getElementById("edit-ic-"+co.id)?.value)||0;
                    updateCO(co.id,{owner_price:op,amount:ic});setEditId(null);
                  }} style={{flex:2,justifyContent:"center"}}>Save Prices</Btn>
                </div>
              </div>
            )}

            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {co.status==="pending"&&<Btn small primary onClick={()=>updateCO(co.id,{status:"approved",approvedAt:new Date().toLocaleDateString()})} style={{background:T.green,color:"white"}}>✓ Approve</Btn>}
              {co.status==="pending"&&<Btn small ghost onClick={()=>updateCO(co.id,{status:"rejected"})}>✗ Reject</Btn>}
              <Btn small ghost onClick={()=>setEditId(editId===co.id?null:co.id)}>✏ Edit Prices</Btn>
              <Btn small ghost onClick={()=>{
                sendToOwner(co);
                updateCO(co.id,{owner_submitted:true});
              }}>🖨 Print for Owner</Btn>
              {co.status==="pending"&&<Btn small primary onClick={()=>{
                sendCOForApproval({...co,amount:co.owner_price||co.amount,send_to:"owner"},proj,session);
                updateCO(co.id,{sent_to_owner:true,sent_at:new Date().toLocaleDateString()});
              }} style={{background:T.teal,color:"white"}}>📧 Email Owner</Btn>}
              <button onClick={()=>deleteCO(co.id)} style={{fontSize:10,background:"none",color:T.muted,border:"1px solid "+T.subtle,borderRadius:6,padding:"3px 8px",cursor:"pointer",marginLeft:"auto"}}>Delete</button>
            </div>

            {/* E-Signatures — assignee controls who can sign */}
            <div style={{marginTop:12}}>
              {/* Policy acknowledgment for owner */}
              {!co.owner_signature&&(
                <div style={{background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:10,color:T.muted,lineHeight:1.7}}>
                  <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:4}}>📋 Before Signing</div>
                  By signing this Change Order, you acknowledge that all work described is agreed upon, and {GCG_POLICY.company} will perform the work to code and spec with full accountability. All records are maintained in Grok Field Ops as the official project record.
                </div>
              )}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div style={{fontSize:11,fontWeight:700,color:T.text}}>✍ Signatures</div>
                {!co.gc_signature&&!co.owner_signature&&(
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    <div style={{fontSize:9,color:T.muted}}>Owner email:</div>
                    <input defaultValue={co.owner_email||""}
                      onBlur={e=>updateCO(co.id,{owner_email:e.target.value})}
                      placeholder="owner@email.com"
                      style={{fontSize:10,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:5,padding:"2px 7px",color:T.text,width:140}}/>
                  </div>
                )}
              </div>
              <div style={{display:"grid",gridTemplateColumns:window.innerWidth<768?"1fr":"1fr 1fr",gap:8}}>
                <SignatureBlock
                  label="GC / Project Manager"
                  role="GC"
                  signData={co.gc_signature}
                  canSign={can(session,"cos")}
                  signerEmail={session?.email}
                  onSign={sig=>{updateCO(co.id,{gc_signature:sig});saveSignedDocToProject(proj,session,"Change Order",co.id,co.num||co.id,"GC / Project Manager");}}
                  onSaveToDoc={(sig,lbl)=>saveSignedDocToProject(proj,session,"Change Order",co.id,co.num||co.id,sig,lbl)}
                />
                <SignatureBlock
                  label="Owner / Client"
                  role="Owner"
                  signData={co.owner_signature}
                  required={true}
                  canSign={true}
                  assignedEmail={co.owner_email||null}
                  signerEmail={session?.email}
                  onSign={sig=>{updateCO(co.id,{owner_signature:sig,status:"approved",approvedAt:new Date().toLocaleDateString()});saveSignedDocToProject(proj,session,"Change Order",co.id,co.num||co.id,sig,"Owner / Client");}}
                  onSaveToDoc={(sig,lbl)=>saveSignedDocToProject(proj,session,"Change Order",co.id,co.num||co.id,sig,lbl)}
                />
              </div>
              {co.gc_signature&&co.owner_signature&&(
                <div style={{background:T.greenBg,border:"1px solid "+T.green+"30",borderRadius:8,padding:"8px 12px",marginTop:8,fontSize:11,color:T.green,fontWeight:700,textAlign:"center"}}>
                  ✅ Fully Executed — Both parties signed this Change Order
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}


// ─── AUTO-SAVE SIGNED DOC TO PROJECT DOCUMENTS ───────────────────────────────
// Called after any signature — saves a timestamped record to project docs
function saveSignedDocToProject(proj, session, docType, docId, docName, sigData, sigLabel) {
  if (!proj?.id) return;
  const cid = session?.companyId || window._grokCompanyId || "local";
  const key = "grok_proj_docs_" + cid + "_" + proj.id;
  try {
    const existing = JSON.parse(localStorage.getItem(key)||"[]");
    const record = {
      id: "signed_"+Date.now(),
      name: docType+" — "+docName+" (Signed by "+sigLabel+")",
      category: docType==="Change Order"?"Change Order":docType==="RFI"?"RFI":docType==="Pay Application"?"Financial & Billing":"Contract",
      folder: docType==="Change Order"?"co":docType==="RFI"?"rfi":docType==="Pay Application"?"financial":"contract",
      type: docType+" — Signed",
      date: new Date().toLocaleDateString(),
      signedBy: sigData.name,
      signedAt: sigData.date,
      signatureImg: sigData.dataUrl,
      docRef: docId,
      stored: true,
      isSignedRecord: true,
    };
    // Keep history — don't overwrite, just prepend
    localStorage.setItem(key, JSON.stringify([record, ...existing]));
  } catch(e) {}
}

// ─── CO SUB VIEW ──────────────────────────────────────────────────────────────
// What subcontractors see in the CO tab — read-only approved COs + submit requests
function COSubView({proj, cos, session, onSave}) {
  const [showRequest, setShowRequest] = React.useState(false);
  const [form, setForm] = React.useState({desc:"",trade:"",sub_estimate:"",notes:""});
  const inp = {width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"};

  const submitRequest = () => {
    if (!form.desc) return;
    const req = {
      id: "COR-"+Date.now(),
      desc: form.desc, trade: form.trade||session?.trade||"",
      sub_estimate: parseFloat(form.sub_estimate)||0,
      notes: form.notes,
      status: "sub_pending",
      source: "sub_request",
      submittedBy: session?.subName||session?.email||"Sub",
      date: new Date().toLocaleDateString(),
    };
    onSave([...cos, req]);
    setForm({desc:"",trade:"",sub_estimate:"",notes:""});
    setShowRequest(false);
    alert("CO request submitted. Your GC will review and price it.");
  };

  // Subs only see approved COs — not pending/internal detail
  // Subs never see COs that have been submitted/sent to owner
  // They only see approved COs that are NOT owner-submitted
  const visibleCOs = cos.filter(c=>
    c.status==="approved" && !c.sent_to_owner && !c.owner_submitted
  );
  const myRequests = cos.filter(c=>c.source==="sub_request"&&c.submittedBy===(session?.subName||session?.email));

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>Change Orders</div>
          <div style={{fontSize:10,color:T.muted,marginTop:2}}>Approved COs on this project</div>
        </div>
        <Btn small primary onClick={()=>setShowRequest(p=>!p)}>+ Request CO</Btn>
      </div>

      {showRequest&&(
        <div style={{background:T.bg2,border:"1px solid "+T.blue+"30",borderRadius:10,padding:"14px 16px",marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Submit CO Request to GC</div>
          <div style={{marginBottom:8}}>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Description of Extra Work *</div>
            <input value={form.desc} onChange={e=>setForm(p=>({...p,desc:e.target.value}))} placeholder="Found rotted subfloor — needs replacement before tile" style={inp}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Trade</div>
              <input value={form.trade} onChange={e=>setForm(p=>({...p,trade:e.target.value}))} placeholder={session?.trade||"Plumbing"} style={inp}/>
            </div>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Your Estimated Cost</div>
              <input type="number" value={form.sub_estimate} onChange={e=>setForm(p=>({...p,sub_estimate:e.target.value}))} placeholder="2400" style={inp}/>
            </div>
          </div>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Notes / Photos Description</div>
            <input value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="Found during demo on Apr 21..." style={inp}/>
          </div>
          <div style={{background:T.amberBg,borderRadius:7,padding:"7px 10px",marginBottom:10,fontSize:10,color:T.amber,lineHeight:1.6}}>
            ⚠ Your GC reviews and prices this request. The final amount shown to the owner is set by your GC.
          </div>
          <div style={{display:"flex",gap:6}}>
            <Btn ghost small onClick={()=>setShowRequest(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
            <Btn primary small disabled={!form.desc} onClick={submitRequest} style={{flex:2,justifyContent:"center"}}>Submit Request to GC</Btn>
          </div>
        </div>
      )}

      {/* My pending requests */}
      {myRequests.filter(r=>r.status==="sub_pending").length>0&&(
        <div style={{background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:9,padding:"10px 14px",marginBottom:10}}>
          <div style={{fontSize:11,fontWeight:700,color:T.amber,marginBottom:6}}>⏳ My Pending Requests</div>
          {myRequests.filter(r=>r.status==="sub_pending").map(r=>(
            <div key={r.id} style={{fontSize:11,color:T.text,marginBottom:4}}>
              {r.id} — {r.desc} <span style={{color:T.muted}}>· Submitted {r.date} · Awaiting GC review</span>
            </div>
          ))}
        </div>
      )}

      {/* Approved COs only */}
      {visibleCOs.length===0 ? (
        <div style={{textAlign:"center",padding:"32px",color:T.muted,fontSize:12,background:T.bg2,borderRadius:10}}>
          No approved change orders yet on this project.
        </div>
      ) : (
        <div>
          {visibleCOs.map(co=>(
            <div key={co.id} style={{background:T.bg1,border:"1px solid "+T.greenBg,borderRadius:10,padding:"12px 16px",marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div>
                  <div style={{display:"flex",gap:7,alignItems:"center",marginBottom:3}}>
                    <span style={{fontSize:11,fontWeight:800,color:T.text,fontFamily:"monospace"}}>{co.id}</span>
                    <span style={{fontSize:9,fontWeight:700,background:T.greenBg,color:T.green,borderRadius:10,padding:"1px 7px"}}>APPROVED</span>
                  </div>
                  <div style={{fontSize:12,fontWeight:700,color:T.text}}>{co.desc||co.description}</div>
                  <div style={{fontSize:10,color:T.muted,marginTop:2}}>{co.date}{co.reason?" · "+co.reason:""}</div>
                </div>
                {/* Owner price only — never internal cost */}
                <div style={{fontSize:16,fontWeight:800,color:T.green,fontFamily:"monospace",flexShrink:0,marginLeft:12}}>
                  ${(co.owner_price||co.amount||0).toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── DAILY LOG TAB ─────────────────────────────────────────────────────────────
function DailyLogTab({proj, session}) {
  const T = useTheme();
  const logKey = "grok_dailylogs_" + proj?.id;
  const [logs, setLogs] = useState(()=>{
    try { return JSON.parse(localStorage.getItem(logKey)||"[]"); } catch { return []; }
  });
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0,10));
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    weather:"Sunny", temp:"", crew:"", manpower:"",
    work:"", materials:"", visitors:"", issues:"", photos:"",
    author: session?.ownerName || session?.email || "GC"
  });

  const saveLogs = (updated) => {
    setLogs(updated);
    try { localStorage.setItem(logKey, JSON.stringify(updated)); } catch(e) {}
  };

  const addLog = () => {
    if (!form.work.trim()) { alert("Add work performed description."); return; }
    const log = {
      id: "log_"+Date.now(),
      date: selectedDate,
      ...form,
      createdAt: new Date().toISOString(),
    };
    saveLogs([log, ...logs.filter(l=>l.date!==selectedDate)]);
    setShowForm(false);
    setForm(f=>({...f, weather:"Sunny", temp:"", crew:"", manpower:"", work:"", materials:"", visitors:"", issues:"", photos:""}));
  };

  const todayLog = logs.find(l=>l.date===selectedDate);
  const allDates = [...new Set(logs.map(l=>l.date))].sort((a,b)=>b.localeCompare(a));
  
  // Get last 30 days for calendar dots
  const last30 = Array.from({length:30},(_,i)=>{
    const d = new Date(); d.setDate(d.getDate()-i);
    return d.toISOString().slice(0,10);
  });
  const logDates = new Set(logs.map(l=>l.date));

  const inp = {width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"};

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>Daily Log</div>
          <div style={{fontSize:10,color:T.muted,marginTop:2}}>{logs.length} entries · Click a date to view or add</div>
        </div>
        <Btn primary small onClick={()=>setShowForm(p=>!p)}>
          + {todayLog?"Edit Today's Log":"Add Today's Log"}
        </Btn>
      </div>

      {/* Calendar strip - last 30 days */}
      <div style={{display:"flex",gap:4,overflowX:"auto",paddingBottom:6,marginBottom:14}}>
        {last30.slice(0,14).map(d=>{
          const hasLog = logDates.has(d);
          const isSelected = d===selectedDate;
          const date = new Date(d+"T12:00:00");
          return (
            <div key={d} onClick={()=>{setSelectedDate(d);setShowForm(false);}}
              style={{flexShrink:0,textAlign:"center",cursor:"pointer",borderRadius:8,padding:"6px 8px",minWidth:42,
                background:isSelected?T.gold:hasLog?T.green+"15":T.bg2,
                border:`1px solid ${isSelected?T.gold:hasLog?T.green+"40":T.subtle}`,
                transition:"all 0.15s"}}>
              <div style={{fontSize:9,color:isSelected?T.bg0:T.muted,fontWeight:600}}>
                {date.toLocaleDateString("en",{weekday:"short"})}
              </div>
              <div style={{fontSize:14,fontWeight:800,color:isSelected?T.bg0:T.text}}>
                {date.getDate()}
              </div>
              {hasLog&&<div style={{width:6,height:6,borderRadius:"50%",background:isSelected?"white":T.green,margin:"2px auto 0"}}/>}
            </div>
          );
        })}
      </div>

      {/* Add/Edit form */}
      {showForm&&(
        <div style={{background:T.bg1,border:`1px solid ${T.gold}30`,borderRadius:10,padding:"16px",marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:12}}>
            📋 Log for {new Date(selectedDate+"T12:00:00").toLocaleDateString("en",{weekday:"long",month:"long",day:"numeric"})}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
            <div>
              <div style={{fontSize:9,color:T.muted,marginBottom:3}}>WEATHER</div>
              <select value={form.weather} onChange={e=>setForm(f=>({...f,weather:e.target.value}))} style={inp}>
                {["Sunny","Partly Cloudy","Overcast","Rainy","Windy","Hot","Cold","Foggy"].map(w=><option key={w}>{w}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:9,color:T.muted,marginBottom:3}}>TEMP (°F)</div>
              <input value={form.temp} onChange={e=>setForm(f=>({...f,temp:e.target.value}))} placeholder="e.g. 68°F" style={inp}/>
            </div>
            <div>
              <div style={{fontSize:9,color:T.muted,marginBottom:3}}>MANPOWER ON SITE</div>
              <input value={form.manpower} onChange={e=>setForm(f=>({...f,manpower:e.target.value}))} placeholder="e.g. 6 workers" style={inp}/>
            </div>
          </div>
          <div style={{marginBottom:8}}>
            <div style={{fontSize:9,color:T.muted,marginBottom:3}}>WORK PERFORMED *</div>
            <textarea value={form.work} onChange={e=>setForm(f=>({...f,work:e.target.value}))}
              placeholder="Describe what was done today — trades, areas, progress made..."
              rows={3} style={{...inp,resize:"vertical"}}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
            <div>
              <div style={{fontSize:9,color:T.muted,marginBottom:3}}>MATERIALS DELIVERED</div>
              <input value={form.materials} onChange={e=>setForm(f=>({...f,materials:e.target.value}))} placeholder="e.g. Tile, drywall, fixtures" style={inp}/>
            </div>
            <div>
              <div style={{fontSize:9,color:T.muted,marginBottom:3}}>VISITORS / INSPECTIONS</div>
              <input value={form.visitors} onChange={e=>setForm(f=>({...f,visitors:e.target.value}))} placeholder="e.g. Owner walked site, inspector" style={inp}/>
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:9,color:T.muted,marginBottom:3}}>ISSUES / DELAYS / RFI NEEDED</div>
            <textarea value={form.issues} onChange={e=>setForm(f=>({...f,issues:e.target.value}))}
              placeholder="Any problems, delays, items needing attention..."
              rows={2} style={{...inp,resize:"vertical"}}/>
          </div>
          <div style={{display:"flex",gap:7}}>
            <button onClick={()=>setShowForm(false)} style={{flex:1,padding:"8px",borderRadius:7,border:`1px solid ${T.subtle}`,background:"none",color:T.muted,fontSize:12,cursor:"pointer"}}>Cancel</button>
            <button onClick={addLog} style={{flex:2,padding:"8px",borderRadius:7,border:"none",background:T.gold,color:T.bg0,fontSize:12,fontWeight:700,cursor:"pointer"}}>Save Log</button>
          </div>
        </div>
      )}

      {/* Selected date log view */}
      {todayLog&&!showForm&&(
        <div style={{background:T.bg1,border:`1px solid ${T.green}30`,borderRadius:10,padding:"14px",marginBottom:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{fontSize:12,fontWeight:700,color:T.text}}>
              {new Date(selectedDate+"T12:00:00").toLocaleDateString("en",{weekday:"long",month:"long",day:"numeric"})}
            </div>
            <div style={{display:"flex",gap:6}}>
              <span style={{fontSize:10,background:T.bg2,borderRadius:20,padding:"2px 8px",color:T.muted}}>{todayLog.weather}</span>
              {todayLog.temp&&<span style={{fontSize:10,background:T.bg2,borderRadius:20,padding:"2px 8px",color:T.muted}}>{todayLog.temp}</span>}
              {todayLog.manpower&&<span style={{fontSize:10,background:T.bg2,borderRadius:20,padding:"2px 8px",color:T.muted}}>👷 {todayLog.manpower}</span>}
              <button onClick={()=>setShowForm(true)} style={{fontSize:10,color:T.gold,background:"none",border:`1px solid ${T.gold}`,borderRadius:6,padding:"2px 8px",cursor:"pointer"}}>Edit</button>
              <button onClick={()=>saveLogs(logs.filter(l=>l.id!==todayLog.id))} style={{fontSize:10,color:T.red,background:"none",border:"none",cursor:"pointer"}}>✕</button>
            </div>
          </div>
          {[["Work Performed",todayLog.work],["Materials",todayLog.materials],["Visitors",todayLog.visitors],["Issues",todayLog.issues]].filter(([,v])=>v).map(([label,val])=>(
            <div key={label} style={{marginBottom:8}}>
              <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",marginBottom:2}}>{label}</div>
              <div style={{fontSize:12,color:T.text,lineHeight:1.5}}>{val}</div>
            </div>
          ))}
          <div style={{fontSize:9,color:T.muted,marginTop:8}}>By {todayLog.author} · {new Date(todayLog.createdAt).toLocaleTimeString("en",{hour:"2-digit",minute:"2-digit"})}</div>
        </div>
      )}

      {!todayLog&&!showForm&&(
        <div style={{textAlign:"center",padding:"32px",background:T.bg2,borderRadius:10,color:T.muted}}>
          <div style={{fontSize:24,marginBottom:8}}>📋</div>
          <div style={{fontSize:12,marginBottom:12}}>No log for {new Date(selectedDate+"T12:00:00").toLocaleDateString("en",{month:"long",day:"numeric"})}</div>
          <button onClick={()=>setShowForm(true)} style={{fontSize:12,fontWeight:700,background:T.gold,color:T.bg0,border:"none",borderRadius:8,padding:"8px 20px",cursor:"pointer"}}>
            + Add Log Entry
          </button>
        </div>
      )}

      {/* Recent logs list */}
      {allDates.filter(d=>d!==selectedDate).slice(0,5).length>0&&(
        <div>
          <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.05em"}}>Recent Logs</div>
          {allDates.filter(d=>d!==selectedDate).slice(0,5).map(d=>{
            const log = logs.find(l=>l.date===d);
            return (
              <div key={d} onClick={()=>{setSelectedDate(d);setShowForm(false);}}
                style={{display:"flex",gap:10,alignItems:"center",background:T.bg2,borderRadius:8,padding:"8px 12px",marginBottom:6,cursor:"pointer"}}>
                <div style={{flexShrink:0,textAlign:"center",width:36}}>
                  <div style={{fontSize:9,color:T.muted}}>{new Date(d+"T12:00:00").toLocaleDateString("en",{weekday:"short"})}</div>
                  <div style={{fontSize:14,fontWeight:800,color:T.text}}>{new Date(d+"T12:00:00").getDate()}</div>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:11,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{log?.work?.slice(0,60)}...</div>
                  <div style={{fontSize:10,color:T.muted}}>{log?.weather} · {log?.manpower||"–"} · by {log?.author}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AdminSubNav({proj, projCOs, projRFIs, session}) {
  const [sub, setSub] = useState("cos");
  const [moreOpen, setMoreOpen] = useState(false);
  const cid = session?.companyId || window._grokCompanyId || localStorage.getItem("grok_company_id") || "local";
  const rfiKey = "grok_rfis_global_" + cid;

  // Load RFIs live from localStorage
  const [rfis, setRfis] = useState(()=>{
    try { return JSON.parse(localStorage.getItem(rfiKey)||"[]"); } catch { return projRFIs||[]; }
  });
  const saveRfis = (updated) => {
    setRfis(updated);
    try { localStorage.setItem(rfiKey, JSON.stringify(updated)); } catch(e) {}
    SyncDB.saveRFIs(updated).catch(()=>{});
  };

  const pendingCOs = projCOs.filter(c=>c.status==="pending").length;
  const openRFIs   = rfis.filter(r=>r.status!=="closed").length;
  const fmt = v => "$"+Math.round(v||0).toLocaleString();

  // RFI state
  const [selectedRFI, setSelectedRFI] = useState(null);
  const [showNewRFI, setShowNewRFI]   = useState(false);
  const [rfiComment, setRfiComment]   = useState("");
  const [rfiForm, setRfiForm]         = useState({subject:"",desc:"",priority:"routine",assignee:"",due_date:""});
  const [confirmDeleteRFI, setConfirmDeleteRFI] = useState(false);

  const createRFI = () => {
    if (!rfiForm.subject.trim()) return;
    const rfi = {
      id: "RFI-"+String(rfis.length+1).padStart(3,"0"),
      subject: rfiForm.subject,
      desc: rfiForm.desc,
      priority: rfiForm.priority,
      assignee: rfiForm.assignee || session?.ownerName || "GC",
      due_date: rfiForm.due_date,
      project: proj?.name,
      projectId: proj?.id,
      status: "open",
      submitted: new Date().toLocaleDateString(),
      submittedBy: session?.ownerName || session?.email,
      comments: [],
      attachments: [],
    };
    const updated = [rfi, ...rfis];
    saveRfis(updated);
    setRfiForm({subject:"",desc:"",priority:"routine",assignee:"",due_date:""});
    setShowNewRFI(false);
    setSelectedRFI(rfi);
  };

  const addRfiComment = (rfiId) => {
    if (!rfiComment.trim()) return;
    const updated = rfis.map(r => String(r.id)===String(rfiId) ? {
      ...r, comments:[...(r.comments||[]), {
        id:Date.now(), text:rfiComment.trim(),
        author:session?.ownerName||session?.email||"GC",
        date:new Date().toLocaleDateString()
      }]
    } : r);
    saveRfis(updated);
    setRfiComment("");
    setSelectedRFI(updated.find(r=>String(r.id)===String(rfiId)));
  };

  const updateRfiStatus = (rfiId, status) => {
    const updated = rfis.map(r=>String(r.id)===String(rfiId)?{...r,status}:r);
    saveRfis(updated);
    setSelectedRFI(prev=>({...prev,status}));
  };

  const deleteRFI = (rfiId) => {
    DELETED_IDS.add("rfis", rfiId);
    saveRfis(rfis.filter(r=>String(r.id)!==String(rfiId)));
    setSelectedRFI(null);
    setConfirmDeleteRFI(false);
    SyncDB.deleteById("rfis", rfiId).catch(()=>{});
  };

  const TABS = [
    {id:"pm",      label:"PM & Schedule"},
    {id:"cos",     label:"COs",  badge:pendingCOs, bc:T.red},
    {id:"rfis",    label:"RFIs", badge:openRFIs,   bc:T.amber},
    {id:"dailylog",label:"Daily Log"},
    {id:"chat",    label:"Chat"},
    {id:"client",  label:"Client"},
    {id:"time",    label:"Time"},
    {id:"notes",   label:"Notes"},
    {id:"docs",    label:"Documents"},
  ];

  const [notes, setNotes] = useState(()=>{
    try{return localStorage.getItem("grok_notes_"+proj?.id)||proj?.notes||"";}catch{return "";}
  });
  const saveNotes = v => { setNotes(v); try{localStorage.setItem("grok_notes_"+proj?.id,v);}catch(e){} };

  const inp = {width:"100%",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"};
  const PRIORITY_COLOR = {urgent:T.red,high:T.amber,routine:T.blue,low:T.muted};

  // Show the first 8 tabs inline; fold the rest into a "More ▾" dropdown so the
  // tab bar never wraps to a second row on desktop.
  const VISIBLE_TABS = 8;
  const shownTabs = TABS.slice(0, VISIBLE_TABS);
  const moreTabs  = TABS.slice(VISIBLE_TABS);
  const activeInMore = moreTabs.some(t=>t.id===sub);
  const tabBtn = t => (
    <button key={t.id} onClick={()=>setSub(t.id)}
      style={{padding:"7px 14px",borderRadius:7,fontSize:12.5,fontWeight:600,cursor:"pointer",border:"none",
        background:sub===t.id?T.gold:T.bg2,color:sub===t.id?T.bg0:T.muted,transition:"all 0.15s",
        display:"flex",alignItems:"center",gap:5,whiteSpace:"nowrap"}}>
      {t.label}
      {t.badge>0&&<span style={{background:t.bc||T.amber,color:"white",fontSize:9,padding:"1px 5px",borderRadius:10,fontWeight:800}}>{t.badge}</span>}
    </button>
  );

  return (
    <div>
      <div className="section-tabs" style={{display:"flex",gap:3,marginBottom:14,flexWrap:"wrap"}}>
        {shownTabs.map(tabBtn)}
        {moreTabs.length>0&&(
          <div style={{position:"relative"}}>
            <button onClick={()=>setMoreOpen(o=>!o)}
              style={{padding:"7px 14px",borderRadius:7,fontSize:12.5,fontWeight:600,cursor:"pointer",border:"none",
                background:activeInMore||moreOpen?T.gold:T.bg2,color:activeInMore||moreOpen?T.bg0:T.muted,transition:"all 0.15s",
                display:"flex",alignItems:"center",gap:5,whiteSpace:"nowrap"}}>
              More ▾
            </button>
            {moreOpen&&(<>
              <div onClick={()=>setMoreOpen(false)} style={{position:"fixed",inset:0,zIndex:40}}/>
              <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,zIndex:41,background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:8,padding:4,minWidth:150,boxShadow:"0 8px 24px rgba(0,0,0,0.35)"}}>
                {moreTabs.map(t=>(
                  <button key={t.id} onClick={()=>{setSub(t.id);setMoreOpen(false);}}
                    style={{display:"flex",alignItems:"center",gap:6,width:"100%",textAlign:"left",padding:"8px 12px",borderRadius:6,fontSize:12.5,fontWeight:600,cursor:"pointer",border:"none",
                      background:sub===t.id?T.gold:"transparent",color:sub===t.id?T.bg0:T.text,whiteSpace:"nowrap"}}>
                    {t.label}
                    {t.badge>0&&<span style={{background:t.bc||T.amber,color:"white",fontSize:9,padding:"1px 5px",borderRadius:10,fontWeight:800,marginLeft:"auto"}}>{t.badge}</span>}
                  </button>
                ))}
              </div>
            </>)}
          </div>
        )}
      </div>

      {sub==="cos"&&(
        <COAdminPanel proj={proj} projCOs={projCOs} session={session}/>
      )}

      {sub==="dailylog"&&<DailyLogTab proj={proj} session={session}/>}

      {sub==="rfis"&&(
        <div style={{display:"grid",gridTemplateColumns:selectedRFI?"1fr 380px":"1fr",gap:14,alignItems:"start"}}>
          {/* RFI list */}
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>RFIs</div>
                <div style={{fontSize:10,color:T.muted,marginTop:2}}>{openRFIs} open · {rfis.filter(r=>r.status==="closed").length} closed</div>
              </div>
              <Btn primary small onClick={()=>{setShowNewRFI(p=>!p);setSelectedRFI(null);}}>
                <Ic d={ICONS.plus} size={11}/> New RFI
              </Btn>
            </div>

            {/* New RFI form */}
            {showNewRFI&&(
              <div style={{background:T.bg2,border:`1px solid ${T.blue}30`,borderRadius:10,padding:"14px 16px",marginBottom:14}}>
                <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>New RFI</div>
                <div style={{marginBottom:8}}>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Subject *</div>
                  <input value={rfiForm.subject} onChange={e=>setRfiForm(p=>({...p,subject:e.target.value}))} placeholder="Drain location — plan vs field conflict" style={inp}/>
                </div>
                <div style={{marginBottom:8}}>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Description</div>
                  <textarea value={rfiForm.desc} onChange={e=>setRfiForm(p=>({...p,desc:e.target.value}))} rows={3} placeholder="Describe the issue clearly..." style={{...inp,resize:"vertical"}}/>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10}}>
                  <div>
                    <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Priority</div>
                    <select value={rfiForm.priority} onChange={e=>setRfiForm(p=>({...p,priority:e.target.value}))} style={inp}>
                      <option value="urgent">🔴 Urgent</option>
                      <option value="high">🟡 High</option>
                      <option value="routine">🔵 Routine</option>
                      <option value="low">⚪ Low</option>
                    </select>
                  </div>
                  <div>
                    <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Assignee</div>
                    <input value={rfiForm.assignee} onChange={e=>setRfiForm(p=>({...p,assignee:e.target.value}))} placeholder="Architect / Engineer" style={inp}/>
                  </div>
                  <div>
                    <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Due Date</div>
                    <input type="date" value={rfiForm.due_date} onChange={e=>setRfiForm(p=>({...p,due_date:e.target.value}))} style={inp}/>
                  </div>
                </div>
                <div style={{display:"flex",gap:7}}>
                  <Btn ghost small onClick={()=>setShowNewRFI(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
                  <Btn primary small disabled={!rfiForm.subject.trim()} onClick={createRFI} style={{flex:2,justifyContent:"center"}}>Submit RFI</Btn>
                </div>
              </div>
            )}

            {/* RFI list */}
            {rfis.length===0 ? (
              <div style={{textAlign:"center",padding:"40px",color:T.muted,fontSize:13,background:T.bg2,borderRadius:10}}>No RFIs yet — create one above.</div>
            ) : rfis.map(rfi=>(
              <div key={rfi.id} onClick={()=>setSelectedRFI(selectedRFI?.id===rfi.id?null:rfi)}
                style={{background:selectedRFI?.id===rfi.id?T.goldBg:T.bg1,border:`1px solid ${selectedRFI?.id===rfi.id?T.gold:T.subtle}`,borderRadius:10,padding:"12px 14px",marginBottom:8,cursor:"pointer",transition:"all 0.15s"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:2}}>
                      <span style={{fontSize:10,fontWeight:800,color:T.muted,fontFamily:"monospace"}}>{rfi.id}</span>
                      <span style={{fontSize:9,fontWeight:700,color:PRIORITY_COLOR[rfi.priority]||T.blue,background:PRIORITY_COLOR[rfi.priority]+"15",borderRadius:20,padding:"1px 7px",textTransform:"capitalize"}}>{rfi.priority}</span>
                    </div>
                    <div style={{fontSize:12,fontWeight:700,color:T.text}}>{rfi.subject}</div>
                  </div>
                  <span style={{fontSize:9,fontWeight:700,background:rfi.status==="closed"?T.greenBg:T.amberBg,color:rfi.status==="closed"?T.green:T.amber,borderRadius:10,padding:"2px 8px",flexShrink:0,marginLeft:8,textTransform:"uppercase"}}>{rfi.status}</span>
                </div>
                <div style={{fontSize:10,color:T.muted,display:"flex",gap:12}}>
                  {rfi.assignee&&<span>→ {rfi.assignee}</span>}
                  {rfi.due_date&&<span>Due: {rfi.due_date}</span>}
                  {(rfi.comments||[]).length>0&&<span>💬 {rfi.comments.length}</span>}
                </div>
              </div>
            ))}
          </div>

          {/* RFI detail panel */}
          {selectedRFI&&(
            <div style={{position:"sticky",top:80}}>
              <div style={{background:T.bg1,border:`1px solid ${T.amber}40`,borderRadius:10,overflow:"hidden"}}>
                <div style={{padding:"12px 14px",background:T.amberBg,borderBottom:`1px solid ${T.amber}30`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{fontSize:12,fontWeight:700,color:T.amber}}>{selectedRFI.id}</div>
                  <button onClick={()=>setSelectedRFI(null)} style={{background:"none",border:"none",color:T.muted,fontSize:18,cursor:"pointer"}}>×</button>
                </div>
                <div style={{padding:14,maxHeight:560,overflowY:"auto"}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>{selectedRFI.subject}</div>
                  <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
                    <span style={{fontSize:9,fontWeight:700,color:PRIORITY_COLOR[selectedRFI.priority]||T.blue,background:PRIORITY_COLOR[selectedRFI.priority]+"15",borderRadius:20,padding:"2px 8px",textTransform:"capitalize"}}>{selectedRFI.priority}</span>
                    <span style={{fontSize:9,fontWeight:700,background:selectedRFI.status==="closed"?T.greenBg:T.amberBg,color:selectedRFI.status==="closed"?T.green:T.amber,borderRadius:20,padding:"2px 8px",textTransform:"uppercase"}}>{selectedRFI.status}</span>
                  </div>
                  {selectedRFI.desc&&<div style={{fontSize:11,color:T.muted,lineHeight:1.6,background:T.bg2,borderRadius:7,padding:"8px 10px",marginBottom:12}}>{selectedRFI.desc}</div>}
                  <div style={{fontSize:10,color:T.muted,marginBottom:12,display:"flex",gap:12,flexWrap:"wrap"}}>
                    {selectedRFI.assignee&&<span>→ {selectedRFI.assignee}</span>}
                    {selectedRFI.due_date&&<span>Due: {selectedRFI.due_date}</span>}
                    <span>Submitted: {selectedRFI.submitted}</span>
                  </div>

                  {/* Status actions */}
                  <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
                    {["open","pending","answered","closed"].map(s=>(
                      <button key={s} onClick={()=>updateRfiStatus(selectedRFI.id,s)}
                        style={{fontSize:10,fontWeight:600,padding:"4px 10px",borderRadius:20,border:"none",cursor:"pointer",
                          background:selectedRFI.status===s?T.gold:T.bg2,color:selectedRFI.status===s?T.bg0:T.muted,textTransform:"capitalize"}}>
                        {s==="closed"?"✓ Close":s}
                      </button>
                    ))}
                  </div>

                  {/* Comments */}
                  <div style={{marginBottom:12}}>
                    <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Comments</div>
                    {(selectedRFI.comments||[]).length===0&&<div style={{fontSize:11,color:T.muted,fontStyle:"italic",marginBottom:8}}>No comments yet</div>}
                    {(selectedRFI.comments||[]).map(c=>(
                      <div key={c.id} style={{background:T.bg2,borderRadius:7,padding:"8px 10px",marginBottom:6}}>
                        <div style={{fontSize:9,fontWeight:700,color:T.text,marginBottom:2}}>{c.author} · {c.date}</div>
                        <div style={{fontSize:11,color:T.muted,lineHeight:1.5}}>{c.text}</div>
                      </div>
                    ))}
                    <textarea value={rfiComment} onChange={e=>setRfiComment(e.target.value)}
                      placeholder="Add response or note..." rows={2}
                      style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:11,fontFamily:"inherit",resize:"none",marginBottom:6}}/>
                    <button disabled={!rfiComment.trim()} onClick={()=>addRfiComment(selectedRFI.id)}
                      style={{width:"100%",padding:"7px",borderRadius:6,border:"none",background:rfiComment.trim()?T.amber:T.bg3,color:"white",fontSize:11,fontWeight:700,cursor:rfiComment.trim()?"pointer":"not-allowed"}}>
                      💬 Post Comment
                    </button>
                  </div>

                  {/* Export + Delete */}
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>exportRFIPDF(selectedRFI,proj,session)}
                      style={{flex:1,fontSize:10,fontWeight:600,background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"7px",cursor:"pointer",color:T.muted}}>
                      📄 Export PDF
                    </button>
                    {!confirmDeleteRFI ? (
                      <button onClick={()=>setConfirmDeleteRFI(true)}
                        style={{flex:1,fontSize:10,fontWeight:600,background:"none",border:`1px solid ${T.red}40`,borderRadius:7,padding:"7px",cursor:"pointer",color:T.red}}>
                        🗑 Delete
                      </button>
                    ) : (
                      <div style={{flex:2,display:"flex",gap:5}}>
                        <button onClick={()=>deleteRFI(selectedRFI.id)} style={{flex:1,fontSize:10,fontWeight:700,background:T.red,color:"white",border:"none",borderRadius:6,padding:"7px",cursor:"pointer"}}>Confirm</button>
                        <button onClick={()=>setConfirmDeleteRFI(false)} style={{flex:1,fontSize:10,background:T.bg2,color:T.muted,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"7px",cursor:"pointer"}}>Cancel</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {sub==="pm"&&<PMSubPanel proj={proj} session={session}/>}
      {sub==="chat"&&<ProjectChat proj={proj} session={session}/>}
      {sub==="client"&&<ClientPortalView proj={proj} projCOs={projCOs} projRFIs={projRFIs} session={session}/>}
      {sub==="time"&&<TimeTracker proj={proj} session={session}/>}

      {sub==="notes"&&(
        <Card>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:10}}>Project Notes</div>
          <textarea value={notes} onChange={e=>saveNotes(e.target.value)}
            placeholder="Project notes, owner preferences, site conditions, special requirements..."
            rows={12} style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"12px",color:T.text,fontSize:13,resize:"vertical",fontFamily:"inherit",lineHeight:1.7}}/>
          <div style={{fontSize:10,color:T.muted,marginTop:6}}>Auto-saved as you type</div>
        </Card>
      )}

      {sub==="docs"&&(
        <ProjectDocsPanel proj={proj} session={session}/>
      )}
    </div>
  );
}


// ─── LOCKED SCOPE CLARIFICATION CHECKLIST ────────────────────────────────────
function LockedScopeChecklist({proj, session}) {
  const storageKey = "grok_scope_" + proj?.id;
  const isAdmin = session?.plan === "owner" || session?.isAdmin;

  const [data, setData] = useState(()=>{
    try{const s=localStorage.getItem(storageKey);if(s)return JSON.parse(s);}catch(e){}
    return {
      locked: false,
      lock_date: null,
      locked_by: null,
      version: 1,
      history: [],
      // Signatures
      gc_signed: false, gc_signed_by: "", gc_signed_date: null,
      owner_signed: false, owner_signed_name: "", owner_signed_date: null,
      // Scope
      scope_date: new Date().toLocaleDateString(),
      co_threshold: "Any change, addition, or unforeseen condition",
      co_response: "48 hours",
      trades: [
        {id:"demo",    label:"Demo & Protection",   included:"", excluded:""},
        {id:"framing", label:"Framing / Structural", included:"", excluded:""},
        {id:"plumb",   label:"Plumbing",             included:"", excluded:""},
        {id:"elec",    label:"Electrical",           included:"", excluded:""},
        {id:"hvac",    label:"HVAC",                 included:"", excluded:""},
        {id:"drywall", label:"Drywall & Paint",      included:"", excluded:""},
        {id:"tile",    label:"Tile",                 included:"", excluded:""},
        {id:"floor",   label:"Flooring",             included:"", excluded:""},
        {id:"cab",     label:"Cabinets & Countertops",included:"",excluded:""},
        {id:"doors",   label:"Doors & Hardware",     included:"", excluded:""},
        {id:"fix",     label:"Fixtures & Appliances",included:"", excluded:""},
        {id:"exterior",label:"Exterior / Site Work", included:"", excluded:""},
      ],
      exclusions_standard: true,
      custom_exclusions: "",
    };
  });

  const [tab, setTab] = useState("scope");
  const [editRequested, setEditRequested] = useState(false);
  const [editReason, setEditReason] = useState("");
  const [showUnlock, setShowUnlock] = useState(false);
  const [signName, setSignName] = useState("");

  const save = (updated) => {
    setData(updated);
    try{localStorage.setItem(storageKey, JSON.stringify(updated));}catch(e){}
  };

  const updateTrade = (id, field, val) => {
    if (data.locked && !isAdmin) return;
    const updated = {...data, trades: data.trades.map(t=>t.id===id?{...t,[field]:val}:t)};
    save(updated);
  };

  const updateField = (field, val) => {
    if (data.locked && !isAdmin) return;
    save({...data, [field]: val});
  };

  const lockScope = () => {
    if (!data.gc_signed || !data.owner_signed) {
      alert("Both GC and Owner must sign before locking.");
      return;
    }
    const snapshot = {
      version: data.version,
      date: new Date().toISOString(),
      locked_by: session?.ownerName || "Jehoon Chun",
      data: JSON.parse(JSON.stringify(data)),
      reason: "Initial lock",
    };
    save({
      ...data,
      locked: true,
      lock_date: new Date().toISOString(),
      locked_by: session?.ownerName || "Jehoon Chun",
      history: [...(data.history||[]), snapshot],
    });
  };

  const requestUnlock = () => {
    if (!editReason.trim()) return;
    // In real app this would notify admin — for now just log it
    const entry = {
      type: "unlock_request",
      date: new Date().toISOString(),
      requested_by: session?.ownerName || "GC",
      reason: editReason,
    };
    save({...data, history:[...(data.history||[]), entry]});
    setEditRequested(true);
  };

  const adminUnlock = (reason) => {
    const snapshot = {
      version: data.version,
      date: new Date().toISOString(),
      unlocked_by: session?.ownerName || "Admin",
      data: JSON.parse(JSON.stringify(data)),
      reason: reason || "Admin edit",
    };
    save({
      ...data,
      locked: false,
      version: (data.version||1) + 1,
      history: [...(data.history||[]), snapshot],
    });
    setShowUnlock(false);
  };

  const gcSign = () => {
    if (data.locked) return;
    save({...data, gc_signed: true, gc_signed_by: session?.ownerName||"Jehoon Chun", gc_signed_date: new Date().toISOString()});
  };

  const ownerSign = () => {
    if (!signName.trim()) { alert("Enter owner name to sign."); return; }
    save({...data, owner_signed: true, owner_signed_name: signName, owner_signed_date: new Date().toISOString()});
  };

  const exportText = () => {
    const lines = [
      `SCOPE CLARIFICATION CHECKLIST — ${proj?.name}`,
      `${session?.companyName||"Grok Construction Group Inc"} | CSLB #${session?.cslb||"1152750"}`,
      `Date: ${data.scope_date} | Version ${data.version}`,
      "=".repeat(60), "",
      "SCOPE OF WORK — TRADE BY TRADE",
      "-".repeat(60),
      ...data.trades.map(t=>`\n${t.label.toUpperCase()}\n  ✓ INCLUDED: ${t.included||"[Not specified]"}\n  ✗ EXCLUDED: ${t.excluded||"[Not specified]"}`),
      "", "=".repeat(60),
      "CHANGE ORDER PROTOCOL",
      `Triggers: ${data.co_threshold}`,
      `GC Response Time: ${data.co_response}`,
      "", "STANDARD EXCLUSIONS (if checked): All items not listed as included are excluded.",
      data.custom_exclusions ? "\nADDITIONAL EXCLUSIONS:\n" + data.custom_exclusions : "",
      "", "=".repeat(60), "SIGNATURES",
      `GC: ${data.gc_signed_by||"_______________________"} Date: ${data.gc_signed_date?new Date(data.gc_signed_date).toLocaleDateString():"________"}`,
      `Owner: ${data.owner_signed_name||"_______________________"} Date: ${data.owner_signed_date?new Date(data.owner_signed_date).toLocaleDateString():"________"}`,
      data.locked ? `\n[LOCKED ${new Date(data.lock_date).toLocaleDateString()} by ${data.locked_by}]` : "\n[NOT YET LOCKED]",
    ];
    navigator.clipboard.writeText(lines.join("\n"));
    alert("Scope checklist copied to clipboard!");
  };

  const completedTrades = data.trades.filter(t=>t.included||t.excluded).length;
  const readyToLock = data.gc_signed && data.owner_signed && completedTrades >= 6;

  const TabBtn = ({id, label, badge}) => (
    <button onClick={()=>setTab(id)} style={{padding:"6px 14px",borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:tab===id?T.purple:T.bg2,color:tab===id?"white":T.muted,transition:"all 0.15s",display:"flex",alignItems:"center",gap:6}}>
      {label}{badge>0&&<span style={{background:"rgba(255,255,255,0.3)",color:"white",fontSize:9,padding:"1px 5px",borderRadius:10,fontWeight:800}}>{badge}</span>}
    </button>
  );

  const locked = data.locked;

  return (
    <div style={{padding:"0 0 24px"}}>
      {/* Blueprint scope banner — shows when transferred from Blueprint tab */}
      {data.blueprint_scope && (
        <div style={{background:T.purpleBg,border:"1px solid "+T.purple+"30",borderRadius:10,padding:"12px 16px",marginBottom:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
            <div style={{fontSize:11,fontWeight:700,color:T.purple}}>📐 Blueprint Scope Reference — Transferred {data.blueprint_generated_date}</div>
            <div style={{fontSize:9,color:T.muted}}>Source: {data.blueprint_source}</div>
          </div>
          <div style={{fontSize:11,color:T.muted,lineHeight:1.7,whiteSpace:"pre-wrap",maxHeight:120,overflowY:"auto",background:T.bg2,borderRadius:7,padding:"8px 10px"}}>
            {data.blueprint_scope.slice(0,800)}{data.blueprint_scope.length>800?"...":""} 
          </div>
          <div style={{fontSize:10,color:T.purple,marginTop:5}}>Use this as reference when filling in the trade scope below ↓</div>
        </div>
      )}
      {/* Status bar */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",background:locked?T.greenBg:T.amberBg,borderRadius:10,marginBottom:14,border:"1px solid "+(locked?T.green+"30":T.amber+"30")}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:20}}>{locked?"🔒":"🔓"}</span>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:locked?T.green:T.amber}}>
              {locked?`Locked — Version ${data.version}`:`Open — Version ${data.version} · ${completedTrades}/12 trades filled`}
            </div>
            <div style={{fontSize:11,color:T.muted}}>
              {locked?`Locked ${new Date(data.lock_date).toLocaleDateString()} by ${data.locked_by} · Both parties signed`:"Complete all trades and get both signatures to lock"}
            </div>
          </div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <Btn small ghost onClick={exportText}>Copy / Export</Btn>
          {!locked && readyToLock && (
            <Btn small primary onClick={lockScope} style={{background:T.green,color:"white"}}>
              🔒 Lock Scope
            </Btn>
          )}
          {locked && isAdmin && (
            <Btn small ghost onClick={()=>setShowUnlock(true)} style={{color:T.amber,borderColor:T.amber}}>
              Admin Unlock
            </Btn>
          )}
          {locked && !isAdmin && !editRequested && (
            <button onClick={()=>setEditRequested(p=>!p)}
              style={{fontSize:11,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"5px 10px",cursor:"pointer",color:T.muted}}>
              Request Edit
            </button>
          )}
        </div>
      </div>

      {/* Admin unlock modal */}
      {showUnlock&&(
        <div style={{background:T.amberBg,border:"1px solid "+T.amber+"40",borderRadius:10,padding:"16px",marginBottom:14}}>
          <div style={{fontSize:13,fontWeight:700,color:T.amber,marginBottom:8}}>Admin Unlock — Reason Required</div>
          <textarea placeholder="Reason for unlocking (e.g. Owner requested scope change on electrical)" rows={2}
            onChange={e=>setEditReason(e.target.value)}
            style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"9px 12px",color:T.text,fontSize:12,fontFamily:"inherit",marginBottom:10,resize:"none"}}/>
          <div style={{display:"flex",gap:8}}>
            <Btn ghost small onClick={()=>setShowUnlock(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
            <Btn primary small onClick={()=>adminUnlock(editReason)} style={{flex:2,justifyContent:"center",background:T.amber,color:T.bg0}}>Unlock & Save Reason to History</Btn>
          </div>
        </div>
      )}

      {/* Edit request form */}
      {editRequested&&!isAdmin&&!locked&&(
        <div style={{background:T.blueBg,border:"1px solid "+T.blue+"30",borderRadius:10,padding:"14px",marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:T.blue,marginBottom:8}}>Request Edit to Locked Scope</div>
          <textarea value={editReason} onChange={e=>setEditReason(e.target.value)}
            placeholder="Describe what needs to change and why..."
            rows={2} style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"9px 12px",color:T.text,fontSize:12,fontFamily:"inherit",marginBottom:10,resize:"none"}}/>
          <Btn small primary onClick={requestUnlock} disabled={!editReason.trim()}>Submit Request</Btn>
        </div>
      )}

      {/* Tabs */}
      <div style={{display:"flex",gap:4,marginBottom:14}}>
        <TabBtn id="scope" label="Scope by Trade" badge={completedTrades}/>
        <TabBtn id="co" label="CO Protocol"/>
        <TabBtn id="exclusions" label="Exclusions"/>
        <TabBtn id="signatures" label="Signatures"/>
        <TabBtn id="history" label="History" badge={data.history?.length||0}/>
      </div>

      {/* SCOPE BY TRADE */}
      {tab==="scope"&&(
        <div>
          <div style={{background:T.purpleBg,border:"1px solid "+T.purple+"30",borderRadius:9,padding:"10px 14px",marginBottom:14,fontSize:12,color:T.muted,lineHeight:1.6}}>
            For each trade, specify exactly what IS included and what IS NOT. The owner initials each section. Anything not listed as included is automatically excluded.
          </div>
          {data.trades.map(trade=>{
            const filled = trade.included || trade.excluded;
            return (
              <Card key={trade.id} style={{marginBottom:10,border:"1px solid "+(filled?T.purple+"30":T.subtle)}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.text}}>{trade.label}</div>
                  {filled&&<span style={{fontSize:9,fontWeight:700,background:T.purpleBg,color:T.purple,borderRadius:10,padding:"2px 7px"}}>✓ Filled</span>}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  <div>
                    <div style={{fontSize:9,fontWeight:700,color:T.green,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>✓ INCLUDED IN SCOPE</div>
                    <textarea value={trade.included} onChange={e=>updateTrade(trade.id,"included",e.target.value)}
                      disabled={locked&&!isAdmin}
                      placeholder="What IS included in this trade..."
                      rows={3} style={{width:"100%",background:locked&&!isAdmin?T.bg3:T.greenBg,border:"1px solid "+(locked&&!isAdmin?T.subtle:T.green+"40"),borderRadius:7,padding:"9px 10px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit",lineHeight:1.6,cursor:locked&&!isAdmin?"not-allowed":"text"}}/>
                  </div>
                  <div>
                    <div style={{fontSize:9,fontWeight:700,color:T.red,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>✗ EXCLUDED FROM SCOPE</div>
                    <textarea value={trade.excluded} onChange={e=>updateTrade(trade.id,"excluded",e.target.value)}
                      disabled={locked&&!isAdmin}
                      placeholder="What is NOT included..."
                      rows={3} style={{width:"100%",background:locked&&!isAdmin?T.bg3:T.redBg,border:"1px solid "+(locked&&!isAdmin?T.subtle:T.red+"40"),borderRadius:7,padding:"9px 10px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit",lineHeight:1.6,cursor:locked&&!isAdmin?"not-allowed":"text"}}/>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* CO PROTOCOL */}
      {tab==="co"&&(
        <Card>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Change Order Protocol</div>
          <div style={{fontSize:11,color:T.muted,marginBottom:16,lineHeight:1.6}}>
            Both parties agree that any work outside this scope requires a written Change Order before work begins.
          </div>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>CO Triggers — Work requiring a Change Order includes:</div>
            <textarea value={data.co_threshold} onChange={e=>updateField("co_threshold",e.target.value)}
              disabled={locked&&!isAdmin} rows={4}
              style={{width:"100%",background:locked&&!isAdmin?T.bg3:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit",lineHeight:1.7}}/>
          </div>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>GC Response Time</div>
            <select value={data.co_response} onChange={e=>updateField("co_response",e.target.value)}
              disabled={locked&&!isAdmin}
              style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}>
              {["24 hours","48 hours","3 business days","5 business days","Per contract terms"].map(o=><option key={o}>{o}</option>)}
            </select>
          </div>
          <div style={{background:T.amberBg,borderRadius:9,padding:"12px 14px",fontSize:12,color:T.muted,lineHeight:1.7}}>
            <strong style={{color:T.amber}}>Standard terms (always apply):</strong> No work outside this scope shall proceed without a written, signed Change Order. Verbal approvals are not binding. Hidden conditions discovered during construction will be photographed, documented, and priced before proceeding.
          </div>
        </Card>
      )}

      {/* EXCLUSIONS */}
      {tab==="exclusions"&&(
        <Card>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14}}>Standard Exclusions</div>
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:T.bg2,borderRadius:8,marginBottom:14,cursor:"pointer"}}
            onClick={()=>!locked&&updateField("exclusions_standard",!data.exclusions_standard)}>
            <div style={{width:22,height:22,borderRadius:6,background:data.exclusions_standard?T.green:T.bg3,border:"2px solid "+(data.exclusions_standard?T.green:T.subtle),display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              {data.exclusions_standard&&<span style={{color:"white",fontSize:12}}>✓</span>}
            </div>
            <div style={{fontSize:12,color:T.text}}>Standard exclusions apply — all items not explicitly listed as INCLUDED above are excluded from this contract</div>
          </div>
          {[
            "Hazardous materials (asbestos, lead, mold) unless specifically included",
            "Permit fees and city inspection fees unless specifically included",
            "Engineering / structural calculations unless specifically included",
            "Furniture, loose equipment, or owner-furnished items",
            "Work in areas not shown on drawings or discussed in walkthrough",
            "Temporary utilities, site security, or storage beyond standard",
            "Work required by city inspector beyond original scope",
            "Pre-existing conditions not visible during walkthrough",
          ].map((ex,i)=>(
            <div key={i} style={{display:"flex",gap:10,padding:"7px 10px",borderRadius:6,marginBottom:4,background:T.bg2,fontSize:12,color:T.muted}}>
              <span style={{color:T.red,flexShrink:0}}>✗</span>{ex}
            </div>
          ))}
          <div style={{marginTop:14}}>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Additional Project-Specific Exclusions</div>
            <textarea value={data.custom_exclusions} onChange={e=>updateField("custom_exclusions",e.target.value)}
              disabled={locked&&!isAdmin}
              placeholder="Any additional exclusions specific to this project..."
              rows={4} style={{width:"100%",background:locked&&!isAdmin?T.bg3:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit",lineHeight:1.7}}/>
          </div>
        </Card>
      )}

      {/* SIGNATURES */}
      {tab==="signatures"&&(
        <div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
            {/* GC signature */}
            <Card style={{border:"1px solid "+(data.gc_signed?T.green+"40":T.subtle)}}>
              <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:12}}>General Contractor</div>
              {data.gc_signed?(
                <div style={{textAlign:"center",padding:"16px"}}>
                  <div style={{fontSize:22,marginBottom:8}}>✅</div>
                  <div style={{fontSize:13,fontWeight:700,color:T.green}}>{data.gc_signed_by}</div>
                  <div style={{fontSize:11,color:T.muted}}>Signed {new Date(data.gc_signed_date).toLocaleDateString()}</div>
                  <div style={{fontSize:10,color:T.muted,marginTop:4}}>{session?.companyName} · CSLB #{session?.cslb}</div>
                </div>
              ):(
                <div style={{textAlign:"center",padding:"16px"}}>
                  <div style={{fontSize:13,color:T.muted,marginBottom:14}}>
                    By signing, I confirm this scope accurately represents the agreed work.
                  </div>
                  <Btn primary onClick={gcSign} style={{justifyContent:"center",background:T.green,color:"white"}}>
                    ✍ Sign as GC
                  </Btn>
                </div>
              )}
            </Card>

            {/* Owner signature */}
            <Card style={{border:"1px solid "+(data.owner_signed?T.green+"40":T.subtle)}}>
              <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:12}}>Owner / Client</div>
              {data.owner_signed?(
                <div style={{textAlign:"center",padding:"16px"}}>
                  <div style={{fontSize:22,marginBottom:8}}>✅</div>
                  <div style={{fontSize:13,fontWeight:700,color:T.green}}>{data.owner_signed_name}</div>
                  <div style={{fontSize:11,color:T.muted}}>Signed {new Date(data.owner_signed_date).toLocaleDateString()}</div>
                </div>
              ):(
                <div style={{padding:"8px"}}>
                  <div style={{fontSize:12,color:T.muted,marginBottom:10}}>
                    Owner confirms they have reviewed and agree to the scope and exclusions above.
                  </div>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Owner Full Name</div>
                  <input value={signName} onChange={e=>setSignName(e.target.value)}
                    placeholder="Owner's full legal name"
                    style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:13,fontFamily:"inherit",marginBottom:10}}/>
                  <Btn primary onClick={ownerSign} disabled={!signName.trim()} style={{width:"100%",justifyContent:"center",background:T.green,color:"white"}}>
                    ✍ Sign as Owner
                  </Btn>
                </div>
              )}
            </Card>
          </div>

          {/* Lock readiness */}
          {!locked&&(
            <Card style={{border:"1px solid "+(readyToLock?T.green+"40":T.subtle)}}>
              <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Ready to Lock?</div>
              {[
                {done:data.gc_signed,        label:"GC signed"},
                {done:data.owner_signed,     label:"Owner signed"},
                {done:completedTrades>=6,    label:`At least 6 trades filled (${completedTrades}/12 done)`},
              ].map((c,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",fontSize:12,color:c.done?T.green:T.muted}}>
                  <span style={{fontSize:14}}>{c.done?"✅":"⭕"}</span>{c.label}
                </div>
              ))}
              {readyToLock&&(
                <Btn primary onClick={lockScope} style={{marginTop:12,width:"100%",justifyContent:"center",background:T.green,color:"white",fontSize:14,padding:"13px"}}>
                  🔒 Lock Scope — Prevent Further Changes
                </Btn>
              )}
            </Card>
          )}
        </div>
      )}

      {/* HISTORY */}
      {tab==="history"&&(
        <Card>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Change History & Audit Log</div>
          {(!data.history||data.history.length===0)?(
            <div style={{fontSize:12,color:T.muted,fontStyle:"italic",textAlign:"center",padding:"20px"}}>No history yet. Actions will be recorded here once the scope is locked or edit requests are made.</div>
          ):[...(data.history||[])].reverse().map((entry,i)=>(
            <div key={i} style={{display:"flex",gap:12,padding:"10px 0",borderBottom:"1px solid "+T.subtle,alignItems:"flex-start"}}>
              <div style={{width:32,height:32,borderRadius:8,background:entry.type==="unlock_request"?T.amberBg:T.greenBg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>
                {entry.type==="unlock_request"?"📝":"🔒"}
              </div>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:2}}>
                  {entry.type==="unlock_request"?"Edit Requested":"Version "+entry.version+" Snapshot"}
                </div>
                <div style={{fontSize:11,color:T.muted,marginBottom:2}}>{new Date(entry.date).toLocaleString()}</div>
                {entry.reason&&<div style={{fontSize:11,color:T.muted}}>Reason: {entry.reason}</div>}
                {entry.locked_by&&<div style={{fontSize:11,color:T.muted}}>Locked by: {entry.locked_by}</div>}
                {entry.unlocked_by&&<div style={{fontSize:11,color:T.amber}}>Unlocked by admin: {entry.unlocked_by}</div>}
                {entry.requested_by&&<div style={{fontSize:11,color:T.muted}}>Requested by: {entry.requested_by}</div>}
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}



// ─── FIX SUBMIT MODAL (extracted to avoid hooks-in-callback) ─────────────────
function FixSubmitModal({item, onClose, onSubmit}) {
  const [fixNotes, setFixNotes] = useState("");
  const [fixPhoto, setFixPhoto] = useState(null);
  const fixFileRef = useRef();
  if (!item) return null;
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}}>
      <div style={{background:T.bg1,borderRadius:16,padding:24,width:"100%",maxWidth:480,border:"1px solid "+T.gold+"30"}}>
        <div style={{fontSize:15,fontWeight:800,color:T.text,marginBottom:4}}>Submit Fix — {item.title}</div>
        <div style={{fontSize:11,color:T.muted,marginBottom:16}}>Upload a photo confirming the fix and describe what was done.</div>
        <input ref={fixFileRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>{
          const file=e.target.files[0];if(!file)return;
          const reader=new FileReader();reader.onload=ev=>setFixPhoto(ev.target.result);reader.readAsDataURL(file);
        }}/>
        {fixPhoto?(
          <div style={{position:"relative",marginBottom:12}}>
            <img src={fixPhoto} alt="Fix" style={{width:"100%",borderRadius:10,maxHeight:200,objectFit:"cover"}}/>
            <button onClick={()=>setFixPhoto(null)} style={{position:"absolute",top:8,right:8,background:"rgba(0,0,0,0.6)",border:"none",borderRadius:"50%",width:24,height:24,color:"white",cursor:"pointer",fontSize:12}}>✕</button>
          </div>
        ):(
          <button onClick={()=>fixFileRef.current.click()}
            style={{width:"100%",padding:"20px",background:T.bg2,border:"2px dashed "+T.subtle,borderRadius:10,cursor:"pointer",color:T.muted,fontSize:12,marginBottom:12,display:"flex",flexDirection:"column",alignItems:"center",gap:6}}>
            <span style={{fontSize:24}}>📷</span>Upload Fix Photo
          </button>
        )}
        <textarea value={fixNotes} onChange={e=>setFixNotes(e.target.value)}
          placeholder="Describe what was done to fix this item..."
          rows={3} style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:12,fontFamily:"inherit",resize:"none",marginBottom:14}}/>
        <div style={{display:"flex",gap:8}}>
          <Btn ghost onClick={onClose} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
          <Btn primary disabled={!fixNotes.trim()} onClick={()=>onSubmit(fixNotes,fixPhoto)}
            style={{flex:2,justifyContent:"center",background:T.green,color:"white"}}>
            Submit Fix for Approval
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ─── INSPECTION SCOPE & BILLING PROGRESS ─────────────────────────────────────
// ─── UNIT PROGRESS VIEW ──────────────────────────────────────────────────────
// Unit-by-unit progress tracking for multifamily/apartment projects
function UnitProgressView({proj, session}) {
  const sk = "grok_units_" + proj?.id;
  const PHASES = ["Demo","Rough Framing","Rough MEP","Insulation/Drywall","Tile/Flooring","Paint","Punch/Closeout"];
  const [units, setUnits] = useState(()=>{try{return JSON.parse(localStorage.getItem(sk)||"[]");}catch{return [];}});
  const [showAdd, setShowAdd] = useState(false);
  const [newUnit, setNewUnit] = useState({num:"",floor:"",type:"1BR",sqft:"",tenant:""});
  const [selected, setSelected] = useState(null);
  const inp = {width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit",marginBottom:8};

  const save = u => { setUnits(u); try{localStorage.setItem(sk,JSON.stringify(u));}catch(e){} };

  const addUnit = () => {
    if(!newUnit.num){alert("Unit number required.");return;}
    const u = {...newUnit, id:Date.now(), phases:PHASES.map(()=>0), status:"not_started", notes:"", photos:[]};
    save([...units, u]);
    setNewUnit({num:"",floor:"",type:"1BR",sqft:"",tenant:""});
    setShowAdd(false);
  };

  const updatePhase = (uid, pi, pct) => {
    const updated = units.map(u=>u.id===uid?{...u,phases:u.phases.map((p,i)=>i===pi?pct:p),
      status: u.phases.every((_,i)=>i===pi?pct===100:u.phases[i]===100)?"complete":pct>0?"in_progress":"not_started"}:u);
    save(updated);
    if(selected?.id===uid) setSelected(updated.find(u=>u.id===uid));
  };

  const overallPct = u => u.phases ? Math.round(u.phases.reduce((s,p)=>s+p,0)/u.phases.length) : 0;
  const statusColor = s => s==="complete"?T.green:s==="in_progress"?T.amber:T.muted;
  const statusLabel = s => s==="complete"?"✓ Complete":s==="in_progress"?"In Progress":"Not Started";

  // Summary stats
  const total = units.length;
  const complete = units.filter(u=>u.status==="complete").length;
  const inProgress = units.filter(u=>u.status==="in_progress").length;
  const avgPct = total ? Math.round(units.reduce((s,u)=>s+overallPct(u),0)/total) : 0;

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
        <div>
          <div style={{fontSize:14,fontWeight:800,color:T.text}}>🏢 Unit Progress Tracker</div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>{total} units · {complete} complete · {inProgress} in progress · {avgPct}% avg</div>
        </div>
        <button onClick={()=>setShowAdd(!showAdd)}
          style={{fontSize:11,fontWeight:700,background:T.blue,color:"white",border:"none",borderRadius:7,padding:"7px 14px",cursor:"pointer"}}>
          + Add Unit
        </button>
      </div>

      {/* Summary bar */}
      {total>0&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:14}}>
          {[{l:"Complete",v:complete,c:T.green},{l:"In Progress",v:inProgress,c:T.amber},{l:"Not Started",v:total-complete-inProgress,c:T.muted}].map(s=>(
            <div key={s.l} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:8,padding:"10px 12px",textAlign:"center"}}>
              <div style={{fontSize:20,fontWeight:800,color:s.c}}>{s.v}</div>
              <div style={{fontSize:10,color:T.muted}}>{s.l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Add unit form */}
      {showAdd&&(
        <div style={{background:T.bg1,border:"1px solid "+T.blue+"40",borderRadius:10,padding:14,marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Add Unit</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Unit # *</div><input value={newUnit.num} onChange={e=>setNewUnit(p=>({...p,num:e.target.value}))} placeholder="4A" style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Floor</div><input value={newUnit.floor} onChange={e=>setNewUnit(p=>({...p,floor:e.target.value}))} placeholder="4" style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Type</div>
              <select value={newUnit.type} onChange={e=>setNewUnit(p=>({...p,type:e.target.value}))} style={inp}>
                {["Studio","1BR","2BR","3BR","4BR+","SRO","Commercial"].map(o=><option key={o}>{o}</option>)}
              </select>
            </div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Sq Ft</div><input value={newUnit.sqft} onChange={e=>setNewUnit(p=>({...p,sqft:e.target.value}))} placeholder="650" style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Tenant (if occupied)</div><input value={newUnit.tenant} onChange={e=>setNewUnit(p=>({...p,tenant:e.target.value}))} placeholder="Optional" style={inp}/></div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>setShowAdd(false)} style={{flex:1,padding:"8px",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,color:T.muted,fontSize:12,cursor:"pointer"}}>Cancel</button>
            <button onClick={addUnit} style={{flex:2,padding:"8px",background:T.blue,border:"none",borderRadius:7,color:"white",fontSize:12,fontWeight:700,cursor:"pointer"}}>Add Unit</button>
          </div>
        </div>
      )}

      {units.length===0&&!showAdd&&(
        <div style={{textAlign:"center",padding:"40px",color:T.muted,background:T.bg2,borderRadius:10,border:"2px dashed "+T.subtle}}>
          <div style={{fontSize:24,marginBottom:8}}>🏢</div>
          <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>No units added yet</div>
          <div style={{fontSize:11}}>Add units to track progress room by room</div>
        </div>
      )}

      {/* Unit grid */}
      <div style={{display:"grid",gridTemplateColumns:selected||window.innerWidth<768?"1fr":("1fr 1fr"),gap:10}}>
        <div>
          {units.map(u=>(
            <div key={u.id} onClick={()=>setSelected(selected?.id===u.id?null:u)}
              style={{background:selected?.id===u.id?T.goldBg:T.bg1,border:"1px solid "+(selected?.id===u.id?T.gold:T.subtle),borderRadius:10,padding:"12px 14px",marginBottom:8,cursor:"pointer",transition:"all 0.15s"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:14,fontWeight:800,color:T.text}}>Unit {u.num}</span>
                  <span style={{fontSize:10,color:T.muted}}>{u.type}{u.sqft?" · "+u.sqft+"sf":""}{u.floor?" · Fl."+u.floor:""}</span>
                  {u.tenant&&<span style={{fontSize:9,background:T.amberBg,color:T.amber,borderRadius:10,padding:"1px 7px",fontWeight:700}}>OCCUPIED</span>}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:11,fontWeight:700,color:statusColor(u.status)}}>{statusLabel(u.status)}</span>
                  <span style={{fontSize:12,fontWeight:800,color:T.gold}}>{overallPct(u)}%</span>
                </div>
              </div>
              {/* Phase mini bars */}
              <div style={{display:"flex",gap:3}}>
                {PHASES.map((ph,i)=>(
                  <div key={i} style={{flex:1}} title={ph+": "+(u.phases[i]||0)+"%"}>
                    <div style={{height:4,background:T.bg3,borderRadius:2}}>
                      <div style={{height:"100%",width:(u.phases[i]||0)+"%",background:u.phases[i]===100?T.green:u.phases[i]>0?T.amber:T.bg3,borderRadius:2}}/>
                    </div>
                    <div style={{fontSize:7,color:T.muted,textAlign:"center",marginTop:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ph.split(" ")[0]}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Phase detail panel */}
        {selected&&(
          <div style={{background:T.bg1,border:"1px solid "+T.gold,borderRadius:10,padding:14,position:"sticky",top:10}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:700,color:T.text}}>Unit {selected.num} — Phases</div>
              <button onClick={()=>setSelected(null)} style={{background:"none",border:"none",color:T.muted,fontSize:16,cursor:"pointer"}}>✕</button>
            </div>
            {PHASES.map((ph,i)=>(
              <div key={i} style={{marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                  <span style={{fontSize:11,fontWeight:600,color:T.text}}>{ph}</span>
                  <span style={{fontSize:11,fontWeight:700,color:selected.phases[i]===100?T.green:selected.phases[i]>0?T.amber:T.muted}}>{selected.phases[i]||0}%</span>
                </div>
                <input type="range" min="0" max="100" step="5" value={selected.phases[i]||0}
                  onChange={e=>updatePhase(selected.id,i,parseInt(e.target.value))}
                  style={{width:"100%",accentColor:T.gold}}/>
                <div style={{display:"flex",justifyContent:"space-between",gap:4,marginTop:4}}>
                  {[0,25,50,75,100].map(v=>(
                    <button key={v} onClick={()=>updatePhase(selected.id,i,v)}
                      style={{flex:1,fontSize:9,padding:"2px 0",borderRadius:4,border:"1px solid "+T.subtle,
                        background:selected.phases[i]===v?T.gold:T.bg2,color:selected.phases[i]===v?T.bg0:T.muted,cursor:"pointer",fontWeight:700}}>
                      {v}%
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <button onClick={()=>{
              const updated=units.filter(u=>u.id!==selected.id);
              save(updated); setSelected(null);
            }} style={{width:"100%",marginTop:8,padding:"6px",background:"transparent",border:"1px solid "+T.subtle,borderRadius:7,color:T.muted,fontSize:11,cursor:"pointer"}}>
              Remove Unit
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── DRAW SCHEDULE VIEW ───────────────────────────────────────────────────────
// Draw schedule by phase/unit — extends inspection billing with payment tracking
function DrawScheduleView({proj, projCOs, session}) {
  const sk = "grok_draws_" + proj?.id;
  const inspKey = "grok_inspection_" + proj?.id;

  const [draws, setDraws] = useState(()=>{try{return JSON.parse(localStorage.getItem(sk)||"[]");}catch{return [];}});
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({name:"",pct:"",amount:"",trigger:"Inspection passed",due_date:"",type:"milestone"});
  const inp = {width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit",marginBottom:8};

  // Pull inspection milestones as draw triggers
  const inspData = (()=>{try{return JSON.parse(localStorage.getItem(inspKey)||"{}");}catch{return {};}})();
  const inspMilestones = inspData.milestones||[];

  const contractValue = (proj?.value||0) + (projCOs||[]).filter(c=>c.status==="approved").reduce((s,c)=>s+(c.amount||0),0);
  const fmt = v => "$"+Math.round(v||0).toLocaleString();

  const save = d => { setDraws(d); try{localStorage.setItem(sk,JSON.stringify(d));}catch(e){} };

  const addDraw = () => {
    if(!form.name){alert("Draw name required.");return;}
    const amt = form.amount ? parseFloat(form.amount) : contractValue*(parseFloat(form.pct||0)/100);
    save([...draws,{...form,id:Date.now(),amount:amt,status:"pending",paid_date:""}]);
    setForm({name:"",pct:"",amount:"",trigger:"Inspection passed",due_date:"",type:"milestone"});
    setShowAdd(false);
  };

  const markPaid = (id) => {
    save(draws.map(d=>d.id===id?{...d,status:"paid",paid_date:new Date().toLocaleDateString()}:d));
  };

  // Import from inspection milestones
  const importFromInspection = () => {
    if(!inspMilestones.length){alert("No inspection milestones found. Generate them in the Inspection & Billing tab first.");return;}
    const imported = inspMilestones.map((m,i)=>({
      id:Date.now()+i, name:m.name||m.phase, pct:m.billing_pct||0,
      amount:contractValue*(m.billing_pct||0)/100,
      trigger:m.inspection_type||"Inspection passed",
      due_date:"", type:"inspection", status:m.passed?"paid":"pending",
      paid_date:m.passed?m.passed_date||"":""
    }));
    save([...draws,...imported]);
  };

  const totalScheduled = draws.reduce((s,d)=>s+(d.amount||0),0);
  const totalPaid = draws.filter(d=>d.status==="paid").reduce((s,d)=>s+(d.amount||0),0);
  const remaining = contractValue - totalPaid;

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
        <div>
          <div style={{fontSize:14,fontWeight:800,color:T.text}}>💵 Draw Schedule</div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>Contract: {fmt(contractValue)} · Paid: {fmt(totalPaid)} · Remaining: {fmt(remaining)}</div>
        </div>
        <div style={{display:"flex",gap:6}}>
          {inspMilestones.length>0&&<button onClick={importFromInspection}
            style={{fontSize:10,fontWeight:700,background:T.teal,color:"white",border:"none",borderRadius:7,padding:"6px 12px",cursor:"pointer"}}>
            ↓ Import from Inspections
          </button>}
          <button onClick={()=>setShowAdd(!showAdd)}
            style={{fontSize:11,fontWeight:700,background:T.gold,color:T.bg0,border:"none",borderRadius:7,padding:"7px 14px",cursor:"pointer"}}>
            + Add Draw
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {contractValue>0&&(
        <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 16px",marginBottom:14}}>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.muted,marginBottom:6}}>
            <span>Paid: <strong style={{color:T.green}}>{fmt(totalPaid)}</strong></span>
            <span>Scheduled: <strong style={{color:T.amber}}>{fmt(totalScheduled-totalPaid)}</strong></span>
            <span>Unscheduled: <strong style={{color:T.muted}}>{fmt(contractValue-totalScheduled)}</strong></span>
          </div>
          <div style={{height:8,background:T.bg3,borderRadius:4,overflow:"hidden"}}>
            <div style={{height:"100%",display:"flex"}}>
              <div style={{width:(totalPaid/contractValue*100)+"%",background:T.green,transition:"width 0.4s"}}/>
              <div style={{width:((totalScheduled-totalPaid)/contractValue*100)+"%",background:T.amber,transition:"width 0.4s"}}/>
            </div>
          </div>
        </div>
      )}

      {/* Add draw form */}
      {showAdd&&(
        <div style={{background:T.bg1,border:"1px solid "+T.gold+"40",borderRadius:10,padding:14,marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>New Draw</div>
          <div style={{display:"grid",gridTemplateColumns:window.innerWidth<768?"1fr":"1fr 1fr",gap:8}}>
            <div>
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Draw Name *</div>
              <input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Foundation inspection" style={inp}/>
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Trigger / Milestone</div>
              <input value={form.trigger} onChange={e=>setForm(p=>({...p,trigger:e.target.value}))} placeholder="e.g. Rough MEP passed" style={inp}/>
            </div>
            <div>
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>% of Contract</div>
              <input value={form.pct} onChange={e=>setForm(p=>({...p,pct:e.target.value,amount:""}))} placeholder="10" type="number" style={inp}/>
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Or Fixed Amount $</div>
              <input value={form.amount} onChange={e=>setForm(p=>({...p,amount:e.target.value,pct:""}))} placeholder="15000" type="number" style={inp}/>
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>setShowAdd(false)} style={{flex:1,padding:"8px",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,color:T.muted,fontSize:12,cursor:"pointer"}}>Cancel</button>
            <button onClick={addDraw} style={{flex:2,padding:"8px",background:T.gold,border:"none",borderRadius:7,color:T.bg0,fontSize:12,fontWeight:700,cursor:"pointer"}}>Add Draw</button>
          </div>
        </div>
      )}

      {draws.length===0&&!showAdd&&(
        <div style={{textAlign:"center",padding:"40px",color:T.muted,background:T.bg2,borderRadius:10,border:"2px dashed "+T.subtle}}>
          <div style={{fontSize:24,marginBottom:8}}>💵</div>
          <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>No draws scheduled yet</div>
          <div style={{fontSize:11}}>{inspMilestones.length>0?"Click \"Import from Inspections\" to pull your milestones":"Add draws manually or generate inspection milestones first"}</div>
        </div>
      )}

      {/* Draw list */}
      {draws.map((d,i)=>(
        <div key={d.id} style={{background:T.bg1,border:"1px solid "+(d.status==="paid"?T.green:T.subtle),borderRadius:10,padding:"12px 16px",marginBottom:8}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                <span style={{fontSize:11,fontWeight:700,color:T.muted,fontFamily:"monospace"}}>Draw {i+1}</span>
                <span style={{fontSize:13,fontWeight:700,color:T.text}}>{d.name}</span>
                <span style={{fontSize:9,fontWeight:700,padding:"1px 7px",borderRadius:10,
                  background:d.status==="paid"?T.greenBg:T.amberBg,
                  color:d.status==="paid"?T.green:T.amber}}>{d.status==="paid"?"✓ PAID":"PENDING"}</span>
              </div>
              <div style={{fontSize:11,color:T.muted}}>Trigger: {d.trigger}</div>
              {d.paid_date&&<div style={{fontSize:10,color:T.green,marginTop:2}}>Paid: {d.paid_date}</div>}
            </div>
            <div style={{textAlign:"right",flexShrink:0,marginLeft:12}}>
              <div style={{fontSize:18,fontWeight:800,color:d.status==="paid"?T.green:T.gold}}>{fmt(d.amount)}</div>
              {d.pct>0&&<div style={{fontSize:10,color:T.muted}}>{d.pct}% of contract</div>}
              {d.status!=="paid"&&(
                <button onClick={()=>markPaid(d.id)}
                  style={{marginTop:6,fontSize:10,fontWeight:700,background:T.greenBg,color:T.green,border:"1px solid "+T.green+"40",borderRadius:6,padding:"4px 10px",cursor:"pointer"}}>
                  ✓ Mark Paid
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── OWNER / INVESTOR REPORT VIEW ────────────────────────────────────────────
// Clean owner-facing progress report — no internal costs, no sub drama
function OwnerReportView({proj, projCOs, projRFIs, session}) {
  const drawKey  = "grok_draws_"  + proj?.id;
  const unitKey  = "grok_units_"  + proj?.id;
  const logKey   = "grok_daily_"  + proj?.id;

  const draws = (()=>{try{return JSON.parse(localStorage.getItem(drawKey)||"[]");}catch{return [];}})();
  const units = (()=>{try{return JSON.parse(localStorage.getItem(unitKey)||"[]");}catch{return [];}})();
  const logs  = (()=>{try{return JSON.parse(localStorage.getItem(logKey)||"[]");}catch{return [];}})();

  const contractValue = (proj?.value||0) + (projCOs||[]).filter(c=>c.status==="approved").reduce((s,c)=>s+(c.amount||0),0);
  const totalPaid = draws.filter(d=>d.status==="paid").reduce((s,d)=>s+(d.amount||0),0);
  const approvedCOs = (projCOs||[]).filter(c=>c.status==="approved");
  const pendingCOs  = (projCOs||[]).filter(c=>c.status==="pending");
  const openRFIs    = (projRFIs||[]).filter(r=>r.status!=="closed");
  const fmt = v => "$"+Math.round(v||0).toLocaleString();
  const today = new Date().toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"});

  const avgUnitPct = units.length ? Math.round(units.reduce((s,u)=>s+(u.phases?Math.round(u.phases.reduce((a,p)=>a+p,0)/u.phases.length):0),0)/units.length) : proj?.pct||0;
  const overallPct = proj?.pct || avgUnitPct || 0;

  const printReport = () => {
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>Owner Report — ${proj?.name}</title>
    <style>
      body{font-family:Arial,sans-serif;margin:0;padding:40px;color:#1a1a1a;max-width:800px}
      .header{border-bottom:3px solid #d4a017;padding-bottom:16px;margin-bottom:24px}
      .co{font-size:11px;color:#666;margin-bottom:4px}
      .title{font-size:28px;font-weight:800;margin:8px 0 4px}
      .sub{font-size:13px;color:#666}
      .stat-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin:24px 0}
      .stat{background:#f8f6f0;border-radius:8px;padding:14px 16px;text-align:center}
      .stat-val{font-size:24px;font-weight:800;color:#d4a017}
      .stat-lbl{font-size:10px;color:#888;text-transform:uppercase;letter-spacing:0.08em;margin-top:2px}
      .section{margin:24px 0}
      .section-title{font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#888;border-bottom:1px solid #e5e0d5;padding-bottom:6px;margin-bottom:12px}
      .progress-bar{height:10px;background:#e5e0d5;border-radius:5px;overflow:hidden;margin:8px 0}
      .progress-fill{height:100%;background:#d4a017;border-radius:5px}
      .row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #f0ece0;font-size:12px}
      .tag{display:inline-block;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:700;margin-left:8px}
      .tag-green{background:#dcfce7;color:#16a34a}
      .tag-amber{background:#fef3c7;color:#d97706}
      .footer{margin-top:40px;font-size:10px;color:#aaa;text-align:center;border-top:1px solid #e5e0d5;padding-top:16px}
    </style></head><body>
    <div class="header">
      <div class="co">Grok Construction Group Inc · CSLB #1152750</div>
      <div class="title">${proj?.name||"Project"}</div>
      <div class="sub">${proj?.addr||""} · Owner Report — ${today}</div>
    </div>
    <div class="stat-grid">
      <div class="stat"><div class="stat-val">${overallPct}%</div><div class="stat-lbl">Overall Complete</div></div>
      <div class="stat"><div class="stat-val">${fmt(totalPaid)}</div><div class="stat-lbl">Drawn to Date</div></div>
      <div class="stat"><div class="stat-val">${fmt(contractValue-totalPaid)}</div><div class="stat-lbl">Remaining</div></div>
    </div>
    <div class="section">
      <div class="section-title">Project Progress</div>
      <div class="progress-bar"><div class="progress-fill" style="width:${overallPct}%"></div></div>
      <div style="font-size:11px;color:#888;margin-top:4px">${overallPct}% complete · Contract: ${fmt(contractValue)}</div>
    </div>
    ${approvedCOs.length>0?`<div class="section">
      <div class="section-title">Approved Change Orders (${approvedCOs.length})</div>
      ${approvedCOs.map(co=>`<div class="row"><span>${co.num||co.id} — ${(co.desc||"").slice(0,60)}</span><span>${fmt(co.amount)}<span class="tag tag-green">Approved</span></span></div>`).join("")}
    </div>`:""}
    ${pendingCOs.length>0?`<div class="section">
      <div class="section-title">Pending Change Orders (${pendingCOs.length})</div>
      ${pendingCOs.map(co=>`<div class="row"><span>${co.num||co.id} — ${(co.desc||"").slice(0,60)}</span><span>${fmt(co.amount)}<span class="tag tag-amber">Pending</span></span></div>`).join("")}
    </div>`:""}
    ${draws.length>0?`<div class="section">
      <div class="section-title">Draw Schedule</div>
      ${draws.map((d,i)=>`<div class="row"><span>Draw ${i+1} — ${d.name}</span><span>${fmt(d.amount)}<span class="tag ${d.status==="paid"?"tag-green":"tag-amber"}">${d.status==="paid"?"Paid":"Pending"}</span></span></div>`).join("")}
    </div>`:""}
    ${units.length>0?`<div class="section">
      <div class="section-title">Unit Status (${units.length} units)</div>
      ${units.map(u=>{const pct=Math.round(u.phases.reduce((s,p)=>s+p,0)/u.phases.length);return`<div class="row"><span>Unit ${u.num} — ${u.type}${u.floor?" · Fl."+u.floor:""}</span><span>${pct}%<span class="tag ${u.status==="complete"?"tag-green":"tag-amber"}">${u.status==="complete"?"Complete":u.status==="in_progress"?"In Progress":"Not Started"}</span></span></div>`}).join("")}
    </div>`:""}
    <div class="footer">Grok Construction Group Inc · CSLB #1152750 · (650) 773-5317 · jehoon@grokconstructiongroupinc.com<br>Report generated ${today} · Confidential — prepared for owner</div>
    </body></html>`;
    const w = window.open("","_blank");
    w.document.write(html);
    w.document.close();
    setTimeout(()=>w.print(),500);
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
        <div>
          <div style={{fontSize:14,fontWeight:800,color:T.text}}>📊 Owner / Investor Report</div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>Clean progress summary — ready to send to owner or investor</div>
        </div>
        <button onClick={printReport}
          style={{fontSize:11,fontWeight:700,background:T.gold,color:T.bg0,border:"none",borderRadius:7,padding:"8px 16px",cursor:"pointer"}}>
          🖨️ Print / PDF
        </button>
      </div>

      {/* Summary cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
        {[
          {l:"Overall Complete",v:overallPct+"%",c:T.gold},
          {l:"Drawn to Date",v:fmt(totalPaid),c:T.green},
          {l:"Remaining",v:fmt(contractValue-totalPaid),c:T.blue},
        ].map(s=>(
          <div key={s.l} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"14px 16px",textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:800,color:s.c}}>{s.v}</div>
            <div style={{fontSize:10,color:T.muted,marginTop:2}}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Progress */}
      <Card style={{marginBottom:12}}>
        <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:8}}>Project Progress</div>
        <div style={{height:10,background:T.bg3,borderRadius:5,overflow:"hidden",marginBottom:6}}>
          <div style={{height:"100%",width:overallPct+"%",background:T.gold,borderRadius:5,transition:"width 0.4s"}}/>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.muted}}>
          <span>{overallPct}% complete</span>
          <span>Contract: {fmt(contractValue)}</span>
        </div>
      </Card>

      {/* COs */}
      {(approvedCOs.length>0||pendingCOs.length>0)&&(
        <Card style={{marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:8}}>Change Orders</div>
          {approvedCOs.map(co=>(
            <div key={co.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid "+T.subtle,fontSize:12}}>
              <span style={{color:T.text}}>{co.num||co.id} — {(co.desc||"").slice(0,50)}</span>
              <span style={{color:T.green,fontWeight:700}}>{fmt(co.amount)} <span style={{fontSize:9,background:T.greenBg,color:T.green,borderRadius:10,padding:"1px 6px"}}>APPROVED</span></span>
            </div>
          ))}
          {pendingCOs.map(co=>(
            <div key={co.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid "+T.subtle,fontSize:12}}>
              <span style={{color:T.text}}>{co.num||co.id} — {(co.desc||"").slice(0,50)}</span>
              <span style={{color:T.amber,fontWeight:700}}>{fmt(co.amount)} <span style={{fontSize:9,background:T.amberBg,color:T.amber,borderRadius:10,padding:"1px 6px"}}>PENDING</span></span>
            </div>
          ))}
        </Card>
      )}

      {/* Draw schedule */}
      {draws.length>0&&(
        <Card style={{marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:8}}>Draw Schedule</div>
          {draws.map((d,i)=>(
            <div key={d.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid "+T.subtle,fontSize:12}}>
              <span style={{color:T.text}}>Draw {i+1} — {d.name}</span>
              <span style={{color:d.status==="paid"?T.green:T.amber,fontWeight:700}}>{fmt(d.amount)} <span style={{fontSize:9,background:d.status==="paid"?T.greenBg:T.amberBg,color:d.status==="paid"?T.green:T.amber,borderRadius:10,padding:"1px 6px"}}>{d.status==="paid"?"PAID":"PENDING"}</span></span>
            </div>
          ))}
        </Card>
      )}

      {/* Units */}
      {units.length>0&&(
        <Card>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:8}}>Unit Status ({units.length} units)</div>
          {units.map(u=>{
            const pct=Math.round(u.phases.reduce((s,p)=>s+p,0)/u.phases.length);
            return (
              <div key={u.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:"1px solid "+T.subtle}}>
                <span style={{fontSize:12,color:T.text}}>Unit {u.num} — {u.type}{u.floor?" · Fl."+u.floor:""}</span>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:80,height:4,background:T.bg3,borderRadius:2}}>
                    <div style={{height:"100%",width:pct+"%",background:u.status==="complete"?T.green:T.amber,borderRadius:2}}/>
                  </div>
                  <span style={{fontSize:11,fontWeight:700,color:u.status==="complete"?T.green:T.amber,minWidth:30}}>{pct}%</span>
                </div>
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}

// ─── CSLB COMPLIANCE CHECKER ─────────────────────────────────────────────────
// Checks contracts/proposals against California BPC §7159 + 2026 SB517/AB1327 updates
function CSLBComplianceChecker({fields, onClose}) {
  // fields: { contractorName, cslb, email, phone, address, clientName, clientAddr,
  //           contractPrice, depositAmt, scope, startDate, endDate, hasSubcontractors,
  //           hasPermitClause, hasCancellationNotice, hasLienNotice, hasMechLienWarning }

  const checks = [
    // Identity & License
    { id:"lic",    cat:"Identity",     label:"CSLB License Number included",               pass:!!(fields.cslb),                              req:"BPC §7159(d)(1)" },
    { id:"coname", cat:"Identity",     label:"Contractor name & address included",          pass:!!(fields.contractorName && fields.address),  req:"BPC §7159(d)(1)" },
    { id:"email",  cat:"Identity",     label:"Contractor email address included (2026 AB1327)", pass:!!(fields.email),                         req:"AB 1327 (Jan 2026)" },
    { id:"phone",  cat:"Identity",     label:"Contractor phone number included (2026 AB1327)", pass:!!(fields.phone),                          req:"AB 1327 (Jan 2026)" },
    { id:"client", cat:"Identity",     label:"Client/Owner name included",                 pass:!!(fields.clientName),                        req:"BPC §7159(d)(2)" },

    // Contract Price & Payments
    { id:"price",  cat:"Financial",    label:"Contract Price clearly stated",               pass:!!(fields.contractPrice && parseFloat(fields.contractPrice)>0), req:"BPC §7159(d)(5)" },
    { id:"deposit",cat:"Financial",    label:"Deposit ≤ $1,000 or 10% (whichever is less)", pass:(()=>{
        const price = parseFloat(fields.contractPrice)||0;
        const dep   = parseFloat(fields.depositAmt)||0;
        if(!dep || !price) return true; // not filled yet
        const maxDep = Math.min(1000, price*0.10);
        return dep <= maxDep;
      })(),                                                                                  req:"BPC §7159(d)(4) — CA residential max" },
    { id:"sched",  cat:"Financial",    label:"Payment schedule included",                   pass:!!(fields.paymentSchedule),                   req:"BPC §7159(d)(5)" },

    // Scope & Dates
    { id:"scope",  cat:"Scope",        label:"Description of work / scope included",        pass:!!(fields.scope && fields.scope.length>20),   req:"BPC §7159(d)(3)" },
    { id:"start",  cat:"Scope",        label:"Start date included",                         pass:!!(fields.startDate),                         req:"BPC §7159(d)(6)" },
    { id:"end",    cat:"Scope",        label:"Completion date or timeframe included",        pass:!!(fields.endDate),                           req:"BPC §7159(d)(6)" },
    { id:"permit", cat:"Scope",        label:"Permit responsibility clause included",        pass:!!(fields.hasPermitClause),                   req:"BPC §7159(d)(7)" },

    // Subcontractors (NEW 2026 SB 517)
    { id:"subcont",cat:"Subcontractors (2026)", label:"Subcontractor disclosure checkbox included (SB 517)", pass:!!(fields.hasSubcontractorDisclosure), req:"SB 517 (Jan 2026)" },

    // Required Notices
    { id:"cancel", cat:"Notices",      label:"3-Day Right to Cancel notice included",       pass:!!(fields.hasCancellationNotice),             req:"BPC §7159(b)(6)" },
    { id:"lien",   cat:"Notices",      label:"Mechanics Lien warning included",             pass:!!(fields.hasLienNotice),                     req:"BPC §7159(b)(4)" },
    { id:"cslbinfo",cat:"Notices",     label:"CSLB consumer information notice included",   pass:!!(fields.hasCslbNotice),                     req:"BPC §7159(b)(5)" },
    { id:"email_cancel", cat:"Notices","label":"Cancellation by email explicitly permitted (2026 AB1327)", pass:!!(fields.hasEmailCancelNotice), req:"AB 1327 (Jan 2026)" },
  ];

  const passed = checks.filter(c=>c.pass).length;
  const total  = checks.length;
  const pct    = Math.round(passed/total*100);
  const score  = pct>=90?"Compliant":pct>=70?"Needs Review":"Non-Compliant";
  const scoreColor = pct>=90?T.green:pct>=70?T.amber:T.red;

  const cats = [...new Set(checks.map(c=>c.cat))];

  return (
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.7)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:T.bg1,borderRadius:14,padding:0,width:"100%",maxWidth:560,maxHeight:"90vh",overflowY:"auto",border:"1px solid "+T.subtle,boxShadow:"0 20px 60px rgba(0,0,0,0.4)"}}>

        {/* Header */}
        <div style={{padding:"20px 24px 16px",borderBottom:"1px solid "+T.subtle,position:"sticky",top:0,background:T.bg1,zIndex:1}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div>
              <div style={{fontSize:15,fontWeight:800,color:T.text}}>🏛 CSLB Compliance Check</div>
              <div style={{fontSize:11,color:T.muted,marginTop:2}}>California BPC §7159 + 2026 SB 517 · AB 1327</div>
            </div>
            <button onClick={onClose} style={{background:"none",border:"none",color:T.muted,fontSize:18,cursor:"pointer"}}>✕</button>
          </div>

          {/* Score */}
          <div style={{marginTop:14,display:"flex",alignItems:"center",gap:14}}>
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:32,fontWeight:800,color:scoreColor}}>{pct}%</div>
              <div style={{fontSize:10,color:T.muted}}>Compliance</div>
            </div>
            <div style={{flex:1}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:4}}>
                <span style={{fontWeight:700,color:scoreColor}}>{score}</span>
                <span style={{color:T.muted}}>{passed}/{total} checks passed</span>
              </div>
              <div style={{height:8,background:T.bg3,borderRadius:4,overflow:"hidden"}}>
                <div style={{height:"100%",width:pct+"%",background:scoreColor,borderRadius:4,transition:"width 0.5s"}}/>
              </div>
            </div>
          </div>
        </div>

        {/* Checks by category */}
        <div style={{padding:"16px 24px 24px"}}>
          {cats.map(cat=>(
            <div key={cat} style={{marginBottom:16}}>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>{cat}</div>
              {checks.filter(c=>c.cat===cat).map(c=>(
                <div key={c.id} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"7px 0",borderBottom:"1px solid "+T.subtle}}>
                  <div style={{width:18,height:18,borderRadius:"50%",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,
                    background:c.pass?T.greenBg:T.redBg,color:c.pass?T.green:T.red,marginTop:1}}>
                    {c.pass?"✓":"✕"}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:c.pass?400:600,color:c.pass?T.muted:T.text}}>{c.label}</div>
                    <div style={{fontSize:10,color:T.muted,marginTop:1}}>{c.req}</div>
                  </div>
                </div>
              ))}
            </div>
          ))}

          {/* 2026 New Laws callout */}
          <div style={{background:"#1e3a5f",border:"1px solid #2d5a8e",borderRadius:8,padding:"10px 14px",marginTop:8}}>
            <div style={{fontSize:11,fontWeight:700,color:"#60a5fa",marginBottom:4}}>⚡ 2026 New Requirements</div>
            <div style={{fontSize:11,color:"#93c5fd",lineHeight:1.5}}>
              <strong>SB 517:</strong> Must include subcontractor disclosure checkbox in all home improvement contracts.<br/>
              <strong>AB 1327:</strong> Must include email + phone, and state cancellation by email is permitted.<br/>
              <strong>Penalty:</strong> Non-compliance = CSLB citation, fines, or license suspension.
            </div>
          </div>

          <div style={{marginTop:14,fontSize:10,color:T.muted,lineHeight:1.5}}>
            ⚠️ This checker is a guide only — not legal advice. Consult a California construction attorney to verify contract compliance for your specific project type.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── PAY APPLICATION / AIA G702 VIEW ─────────────────────────────────────────
function PayApplicationView({proj, projCOs, session}) {
  const sk = "grok_payapps_" + proj?.id;
  const [apps, setApps] = useState(()=>{try{return JSON.parse(localStorage.getItem(sk)||"[]");}catch{return [];}});
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({app_num:"", period_from:"", period_to:"", scheduled_value:"", work_completed:"", materials_stored:"", retention_pct:"10"});
  const inp = {width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit",marginBottom:8};

  // Pre-submit review modal. When the GC clicks Submit, instead of flipping
  // status directly, we open this modal so they can review the ARIA-drafted
  // §8132 conditional waiver + request waivers from each sub before sending
  // the package to the owner.
  const [reviewAppId, setReviewAppId] = React.useState(null);
  // Post-payment-cleared modal. Opens when GC marks an approved pay app as
  // "Payment Cleared" — triggers the §8134 unconditional waiver flow.
  const [clearedAppId, setClearedAppId] = React.useState(null);
  const [requesting, setRequesting] = React.useState({}); // {subId: true}
  const [requestMsg, setRequestMsg] = React.useState(""); // last batch result

  const cid = session?.companyId || window._grokCompanyId || "local";
  const companySubs = React.useMemo(() => getProjectSubs(proj, cid), [cid]); // eslint-disable-line

  const save = a => { setApps(a); try{localStorage.setItem(sk,JSON.stringify(a));}catch(e){} };

  // Refresh when ARIA auto-drafts a new pay app from a milestone hitting 100%.
  React.useEffect(() => {
    const reload = () => {
      try { setApps(JSON.parse(localStorage.getItem(sk)||"[]")); } catch {}
    };
    window.addEventListener("grok-payapps-updated", reload);
    return () => window.removeEventListener("grok-payapps-updated", reload);
  }, [sk]);

  // Recompute amount_due / retention when GC edits the auto-drafted fields.
  const updateApp = (id, patch) => {
    const next = apps.map(a => {
      if (a.id !== id) return a;
      const merged = {...a, ...patch};
      const wc = parseFloat(merged.work_completed || 0);
      const ms = parseFloat(merged.materials_stored || 0);
      const ret = (wc + ms) * (parseFloat(merged.retention_pct || 10) / 100);
      return {...merged, work_completed: wc, materials_stored: ms, retention: Math.round(ret), amount_due: Math.round((wc + ms) - ret)};
    });
    save(next);
  };

  const approveDraft = (id) => {
    save(apps.map(a => a.id === id ? {...a, status: "draft", approvedDraftAt: new Date().toISOString()} : a));
  };
  const dismissDraft = (id) => {
    if (!window.confirm("Dismiss this ARIA-drafted pay app? You can always add one manually later.")) return;
    save(apps.filter(a => a.id !== id));
  };

  // Open the pre-submit review modal. Generates the §8132 conditional waiver
  // payload on the pay app if it doesn't have one yet so the modal can show
  // it immediately. Initialize the subWaivers map from the company's subs.
  const openSubmitReview = (app) => {
    let next = app;
    if (!app.gcConditionalWaiver) {
      next = {
        ...app,
        gcConditionalWaiver: {
          generated_at: new Date().toISOString(),
          code: "Cal. Civ. Code §8132",
          through: app.period_to,
          amount: app.amount_due,
        },
      };
    }
    if (!app.subWaivers) {
      const map = {};
      companySubs.forEach(s => {
        map[s.id] = {
          subId: s.id,
          name: s.name,
          company: s.company,
          email: s.email,
          trade: s.trade,
          status: "missing", // missing | requested | received
        };
      });
      next = {...next, subWaivers: map};
    }
    if (next !== app) save(apps.map(a => a.id === app.id ? next : a));
    setReviewAppId(app.id);
    setRequestMsg("");
  };

  // Print the §8132 conditional waiver for the given app. Opens a new window
  // with the statutory form pre-filled — GC saves as PDF or prints.
  const printGCWaiver = (app) => {
    const w = window.open("", "_blank");
    if (!w) { alert("Allow popups to print the waiver."); return; }
    w.document.write(generateGCConditionalWaiverHTML(app, proj, session));
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  // Fire one sub waiver request via the send-email function. Updates the
  // pay app's subWaivers map with status:"requested" + requestedAt on success.
  const requestOneSub = async (app, sub) => {
    if (!sub.email) { setRequestMsg("✗ " + (sub.name||"Sub") + " has no email on file"); return; }
    setRequesting(p => ({...p, [sub.id]: true}));
    const res = await requestSubWaiverEmail({sub, app, proj, session});
    setRequesting(p => { const n = {...p}; delete n[sub.id]; return n; });
    if (res.ok) {
      save(apps.map(a => a.id === app.id ? {
        ...a,
        subWaivers: {
          ...(a.subWaivers || {}),
          [sub.id]: {...(a.subWaivers?.[sub.id] || sub), status: "requested", requestedAt: new Date().toISOString()},
        },
      } : a));
      setRequestMsg("✓ Sent to " + sub.email);
    } else {
      setRequestMsg("✗ Failed to send to " + (sub.email||sub.name) + " — " + (res.error||"unknown"));
    }
  };

  // Fan out waiver requests to every sub currently in "missing" state.
  const requestAllMissing = async (app) => {
    const missing = Object.values(app.subWaivers || {}).filter(s => s.status === "missing" && s.email);
    if (!missing.length) { setRequestMsg("Nothing to send — every sub is already requested or received."); return; }
    setRequestMsg("Sending " + missing.length + " request" + (missing.length===1?"":"s") + "…");
    let ok = 0, fail = 0;
    for (const sub of missing) {
      const res = await requestSubWaiverEmail({sub, app, proj, session});
      if (res.ok) ok++; else fail++;
    }
    // One state update at the end so the modal doesn't re-render mid-loop.
    save(apps.map(a => a.id === app.id ? {
      ...a,
      subWaivers: Object.fromEntries(Object.entries(a.subWaivers || {}).map(([id, s]) => {
        if (s.status === "missing" && s.email) {
          return [id, {...s, status: "requested", requestedAt: new Date().toISOString()}];
        }
        return [id, s];
      })),
    } : a));
    setRequestMsg(`Done — ${ok} sent, ${fail} failed.`);
  };

  // Mark a sub's waiver as received (manual for now — they emailed it back).
  const markSubReceived = (app, subId) => {
    save(apps.map(a => a.id === app.id ? {
      ...a,
      subWaivers: {
        ...(a.subWaivers || {}),
        [subId]: {...(a.subWaivers?.[subId] || {}), status: "received", receivedAt: new Date().toISOString()},
      },
    } : a));
  };

  // ──── §8134 UNCONDITIONAL WAIVER FLOW ─────────────────────────────────────
  // Fires when GC marks an approved pay app as "Payment Cleared". ARIA auto-
  // generates the §8134 unconditional waiver and opens a chase modal so the
  // GC can request unconditional waivers from each sub once they've been
  // paid their share.

  const openPaymentClearedReview = (app) => {
    let next = app;
    const updates = {
      status: "paid",
      cleared_date: new Date().toLocaleDateString(),
      cleared_at: new Date().toISOString(),
    };
    if (!app.gcUnconditionalWaiver) {
      updates.gcUnconditionalWaiver = {
        generated_at: new Date().toISOString(),
        code: "Cal. Civ. Code §8134",
        through: app.period_to,
        amount: app.amount_due,
      };
    }
    if (!app.subUnconditionalWaivers) {
      const map = {};
      // Seed from existing subWaivers so we carry over the sub roster, but
      // reset every status to "missing" — unconditional is a NEW round.
      const seedFrom = app.subWaivers || {};
      Object.values(seedFrom).forEach(s => {
        map[s.subId] = {
          subId: s.subId, name: s.name, company: s.company, email: s.email, trade: s.trade,
          status: "missing",
        };
      });
      // If no prior conditional roster (rare — manually created pay app),
      // fall back to current company subs.
      if (Object.keys(map).length === 0) {
        companySubs.forEach(s => {
          map[s.id] = { subId: s.id, name: s.name, company: s.company, email: s.email, trade: s.trade, status: "missing" };
        });
      }
      updates.subUnconditionalWaivers = map;
    }
    next = {...app, ...updates};
    save(apps.map(a => a.id === app.id ? next : a));
    setClearedAppId(app.id);
    setRequestMsg("");
  };

  const printGCUnconditionalWaiver = (app) => {
    const w = window.open("", "_blank");
    if (!w) { alert("Allow popups to print the waiver."); return; }
    w.document.write(generateGCUnconditionalWaiverHTML(app, proj, session));
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  const requestOneUncond = async (app, sub) => {
    if (!sub.email) { setRequestMsg("✗ " + (sub.name||"Sub") + " has no email on file"); return; }
    setRequesting(p => ({...p, [sub.subId]: true}));
    const res = await requestSubWaiverEmail({sub, app, proj, session, phase: "unconditional"});
    setRequesting(p => { const n = {...p}; delete n[sub.subId]; return n; });
    if (res.ok) {
      save(apps.map(a => a.id === app.id ? {
        ...a,
        subUnconditionalWaivers: {
          ...(a.subUnconditionalWaivers || {}),
          [sub.subId]: {...(a.subUnconditionalWaivers?.[sub.subId] || sub), status: "requested", requestedAt: new Date().toISOString()},
        },
      } : a));
      setRequestMsg("✓ Sent to " + sub.email);
    } else {
      setRequestMsg("✗ Failed to send to " + (sub.email||sub.name) + " — " + (res.error||"unknown"));
    }
  };

  const requestAllMissingUncond = async (app) => {
    const missing = Object.values(app.subUnconditionalWaivers || {}).filter(s => s.status === "missing" && s.email);
    if (!missing.length) { setRequestMsg("Nothing to send — every sub is already requested or received."); return; }
    setRequestMsg("Sending " + missing.length + " request" + (missing.length===1?"":"s") + "…");
    let ok = 0, fail = 0;
    for (const sub of missing) {
      const res = await requestSubWaiverEmail({sub, app, proj, session, phase: "unconditional"});
      if (res.ok) ok++; else fail++;
    }
    save(apps.map(a => a.id === app.id ? {
      ...a,
      subUnconditionalWaivers: Object.fromEntries(Object.entries(a.subUnconditionalWaivers || {}).map(([id, s]) => {
        if (s.status === "missing" && s.email) {
          return [id, {...s, status: "requested", requestedAt: new Date().toISOString()}];
        }
        return [id, s];
      })),
    } : a));
    setRequestMsg(`Done — ${ok} sent, ${fail} failed.`);
  };

  const markSubUncondReceived = (app, subId) => {
    save(apps.map(a => a.id === app.id ? {
      ...a,
      subUnconditionalWaivers: {
        ...(a.subUnconditionalWaivers || {}),
        [subId]: {...(a.subUnconditionalWaivers?.[subId] || {}), status: "received", receivedAt: new Date().toISOString()},
      },
    } : a));
  };

  // Record/update an amount paid to a sub for this pay app. Aggregates into
  // the "Subs Paid To Date" card at the top of the view across all apps.
  const setSubPaid = (app, sub, amount) => {
    const amt = Number(amount) || 0;
    save(apps.map(a => a.id === app.id ? {
      ...a,
      subPayments: {
        ...(a.subPayments || {}),
        [sub.subId]: {
          subId: sub.subId, name: sub.name, email: sub.email, trade: sub.trade,
          paidAmount: amt,
          paidDate: amt > 0 ? (a.subPayments?.[sub.subId]?.paidDate || new Date().toLocaleDateString()) : null,
        },
      },
    } : a));
  };

  // Final action — flip status:submitted, stamp submitted_date, close modal.
  // Doesn't block on missing sub waivers — just warns. GC may choose to
  // submit anyway and chase the missing waivers separately.
  const approveAndSubmit = (app) => {
    const subs = Object.values(app.subWaivers || {});
    const missing = subs.filter(s => s.status === "missing").length;
    const requested = subs.filter(s => s.status === "requested").length;
    if (missing > 0) {
      if (!window.confirm(`Submit now? ${missing} sub waiver request${missing===1?" hasn't":"s haven't"} been sent yet. You can still chase them after submission.`)) return;
    } else if (requested > 0) {
      // Just a heads-up, no block.
      if (!window.confirm(`Submit now? ${requested} sub waiver${requested===1?"":"s"} requested but not yet received. Owner-side payment should not clear until those are in hand.`)) return;
    }
    save(apps.map(a => a.id === app.id ? {
      ...a,
      status: "submitted",
      submitted_date: new Date().toLocaleDateString(),
      submitted_at: new Date().toISOString(),
    } : a));
    setReviewAppId(null);
  };

  const contractVal = (proj?.value||0) + (projCOs||[]).filter(c=>c.status==="approved").reduce((s,c)=>s+(c.amount||0),0);
  const totalBilled = apps.reduce((s,a)=>s+(a.work_completed||0)+(a.materials_stored||0),0);
  const totalRetention = apps.reduce((s,a)=>s+(((a.work_completed||0)+(a.materials_stored||0))*(a.retention_pct||10)/100),0);
  const fmt = v => "$"+Math.round(v||0).toLocaleString();

  const addApp = () => {
    const wc = parseFloat(form.work_completed||0);
    const ms = parseFloat(form.materials_stored||0);
    const ret = (wc+ms)*(parseFloat(form.retention_pct||10)/100);
    const due = (wc+ms) - ret;
    const newApp = {...form, id:Date.now(), work_completed:wc, materials_stored:ms, retention:ret, amount_due:due,
      status:"draft", submitted_date:"", approved_date:"", app_num:form.app_num||String(apps.length+1).padStart(3,"0")};
    save([...apps, newApp]);
    setForm({app_num:"",period_from:"",period_to:"",scheduled_value:"",work_completed:"",materials_stored:"",retention_pct:"10"});
    setShowNew(false);
  };

  const printG702 = (app) => {
    const cs = (()=>{try{return JSON.parse(localStorage.getItem("grok_company_settings_"+(session?.companyId||""))||"{}");}catch{return {};}})();
    const w = window.open("","_blank");
    if(!w){alert("Allow popups to print.");return;}
    w.document.write(`<!DOCTYPE html><html><head><title>Pay Application ${app.app_num}</title>
    <style>body{font-family:Arial,sans-serif;padding:32px;max-width:800px;margin:0 auto;font-size:12px}
    h2{color:#d4a017;margin-bottom:4px}.header{border-bottom:3px solid #d4a017;padding-bottom:12px;margin-bottom:20px}
    table{width:100%;border-collapse:collapse;margin:16px 0}td,th{border:1px solid #ddd;padding:8px 10px}
    th{background:#f5f0e8;font-weight:700}.total{font-weight:800;font-size:14px;color:#d4a017}
    .row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #f0ece0}
    @media print{body{padding:16px}}</style></head><body>
    <div class="header">
      <div style="font-size:10px;color:#888">${cs.company_name||"Grok Construction Group Inc"} · CSLB #${cs.cslb||"1152750"}</div>
      <h2>Application for Payment — AIA G702</h2>
      <div style="font-size:11px;color:#666">${proj?.name||""} · ${proj?.addr||""}</div>
    </div>
    <table><tr><th>Application No.</th><th>Period From</th><th>Period To</th><th>Owner</th></tr>
    <tr><td>${app.app_num}</td><td>${app.period_from||""}</td><td>${app.period_to||""}</td><td>${proj?.client||""}</td></tr></table>
    <table><tr><th>Item</th><th>Amount</th></tr>
    <tr><td>Original Contract Sum</td><td>${fmt(proj?.value||0)}</td></tr>
    <tr><td>Approved Change Orders</td><td>${fmt((projCOs||[]).filter(c=>c.status==="approved").reduce((s,c)=>s+(c.amount||0),0))}</td></tr>
    <tr><td><strong>Revised Contract Total</strong></td><td><strong>${fmt(contractVal)}</strong></td></tr>
    <tr><td>Work Completed This Period</td><td>${fmt(app.work_completed)}</td></tr>
    <tr><td>Materials Stored</td><td>${fmt(app.materials_stored)}</td></tr>
    <tr><td>Total Earned to Date</td><td>${fmt(totalBilled)}</td></tr>
    <tr><td>Retention (${app.retention_pct}%)</td><td>(${fmt(app.retention)})</td></tr>
    <tr><td class="total">AMOUNT DUE THIS APPLICATION</td><td class="total">${fmt(app.amount_due)}</td></tr></table>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:32px;margin-top:40px">
      <div><div style="border-top:2px solid #333;padding-top:6px;margin-top:48px">Contractor Signature / Date</div><div style="font-size:10px;color:#888;margin-top:4px">${cs.company_name||"GCG"}</div></div>
      <div><div style="border-top:2px solid #333;padding-top:6px;margin-top:48px">Owner / Architect Signature / Date</div></div>
    </div>
    </body></html>`);
    w.document.close(); setTimeout(()=>w.print(),400);
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div>
          <div style={{fontSize:14,fontWeight:800,color:T.text}}>💰 Pay Applications (AIA G702)</div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>Billed: {fmt(totalBilled)} · Retention: {fmt(totalRetention)} · Remaining: {fmt(contractVal-totalBilled)}</div>
        </div>
        <button onClick={()=>setShowNew(!showNew)} style={{fontSize:11,fontWeight:700,background:T.gold,color:T.bg0,border:"none",borderRadius:7,padding:"7px 14px",cursor:"pointer"}}>+ New Pay App</button>
      </div>

      {/* Progress */}
      <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:8,padding:"10px 14px",marginBottom:12}}>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.muted,marginBottom:4}}>
          <span>Billed: <strong style={{color:T.green}}>{fmt(totalBilled)}</strong></span>
          <span>Contract: <strong style={{color:T.gold}}>{fmt(contractVal)}</strong></span>
          <span>{contractVal>0?Math.round(totalBilled/contractVal*100):0}% billed</span>
        </div>
        <div style={{height:6,background:T.bg3,borderRadius:3,overflow:"hidden"}}>
          <div style={{height:"100%",width:contractVal>0?(totalBilled/contractVal*100)+"%":"0%",background:T.green,borderRadius:3}}/>
        </div>
      </div>

      {/* Retainage + Final-Waivers + Sub-Payment summary row.
          Three small cards that aggregate billing-loop status across the
          whole project. Only renders once there's at least one pay app. */}
      {apps.length > 0 && (() => {
        const projComplete = (proj?.pct || 0) >= 100;
        const finalAppCandidate = apps.find(a => a.status === "paid") || apps[apps.length - 1];
        // Sub payment ledger — aggregate across all pay apps.
        const subTotals = {};
        apps.forEach(a => {
          Object.values(a.subPayments || {}).forEach(p => {
            const k = p.subId;
            if (!subTotals[k]) subTotals[k] = {name: p.name || k, paid: 0, count: 0};
            subTotals[k].paid += Number(p.paidAmount) || 0;
            subTotals[k].count += 1;
          });
        });
        const totalSubPaid = Object.values(subTotals).reduce((s, x) => s + x.paid, 0);
        return (
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:12}}>
            {/* Retainage card */}
            <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:9,padding:"10px 12px"}}>
              <div style={{fontSize:9,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:".06em"}}>Retainage Held</div>
              <div style={{fontSize:18,fontWeight:900,color:T.red,marginTop:2}}>{fmt(totalRetention)}</div>
              <div style={{fontSize:9,color:T.muted,marginTop:2}}>{projComplete ? "Release at closeout" : "Across all pay apps"}</div>
            </div>
            {/* Sub payments ledger card */}
            <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:9,padding:"10px 12px"}}>
              <div style={{fontSize:9,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:".06em"}}>Subs Paid To Date</div>
              <div style={{fontSize:18,fontWeight:900,color:T.green,marginTop:2}}>{fmt(totalSubPaid)}</div>
              <div style={{fontSize:9,color:T.muted,marginTop:2}}>{Object.keys(subTotals).length} sub{Object.keys(subTotals).length===1?"":"s"} tracked</div>
            </div>
            {/* Final waivers card */}
            <div style={{background:projComplete?`linear-gradient(135deg,${T.gold}18,${T.gold}06)`:T.bg1,border:`1px solid ${projComplete?T.gold+"60":T.subtle}`,borderRadius:9,padding:"10px 12px"}}>
              <div style={{fontSize:9,fontWeight:800,color:projComplete?T.gold:T.muted,textTransform:"uppercase",letterSpacing:".06em"}}>Final Waivers · §8136/§8138</div>
              <div style={{display:"flex",gap:4,marginTop:6}}>
                <button onClick={()=>{
                  const w = window.open("","_blank");
                  if (!w) { alert("Allow popups."); return; }
                  w.document.write(generateGCConditionalFinalWaiverHTML(finalAppCandidate, proj, session));
                  w.document.close(); setTimeout(()=>w.print(),400);
                }}
                  style={{flex:1,background:T.bg2,border:`1px solid ${T.subtle}`,color:T.text,borderRadius:6,padding:"5px 7px",fontSize:10,fontWeight:700,cursor:"pointer"}}>§8136</button>
                <button onClick={()=>{
                  const w = window.open("","_blank");
                  if (!w) { alert("Allow popups."); return; }
                  w.document.write(generateGCUnconditionalFinalWaiverHTML(finalAppCandidate, proj, session));
                  w.document.close(); setTimeout(()=>w.print(),400);
                }}
                  style={{flex:1,background:T.bg2,border:`1px solid ${T.subtle}`,color:T.text,borderRadius:6,padding:"5px 7px",fontSize:10,fontWeight:700,cursor:"pointer"}}>§8138</button>
              </div>
              <div style={{fontSize:9,color:T.muted,marginTop:4}}>{projComplete ? "Project at 100% — closeout-ready" : "Closeout forms — print when ready"}</div>
            </div>
          </div>
        );
      })()}

      {/* New App Form */}
      {showNew&&(
        <div style={{background:T.bg1,border:"1px solid "+T.gold+"40",borderRadius:10,padding:14,marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>New Pay Application</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>App #</div><input value={form.app_num} onChange={e=>setForm(p=>({...p,app_num:e.target.value}))} placeholder={String(apps.length+1).padStart(3,"0")} style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Period From</div><input type="date" value={form.period_from} onChange={e=>setForm(p=>({...p,period_from:e.target.value}))} style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Period To</div><input type="date" value={form.period_to} onChange={e=>setForm(p=>({...p,period_to:e.target.value}))} style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Work Completed This Period $</div><input value={form.work_completed} onChange={e=>setForm(p=>({...p,work_completed:e.target.value}))} placeholder="45000" type="number" style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Materials Stored $</div><input value={form.materials_stored} onChange={e=>setForm(p=>({...p,materials_stored:e.target.value}))} placeholder="0" type="number" style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Retention %</div><input value={form.retention_pct} onChange={e=>setForm(p=>({...p,retention_pct:e.target.value}))} placeholder="10" type="number" style={inp}/></div>
          </div>
          <div style={{background:T.bg2,borderRadius:7,padding:"8px 12px",marginBottom:10,fontSize:11,color:T.muted}}>
            Amount Due: <strong style={{color:T.gold,fontSize:14}}>{fmt((parseFloat(form.work_completed||0)+parseFloat(form.materials_stored||0))*(1-parseFloat(form.retention_pct||10)/100))}</strong>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>setShowNew(false)} style={{flex:1,padding:"8px",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,color:T.muted,fontSize:12,cursor:"pointer"}}>Cancel</button>
            <button onClick={addApp} style={{flex:2,padding:"8px",background:T.gold,border:"none",borderRadius:7,color:T.bg0,fontSize:12,fontWeight:700,cursor:"pointer"}}>Save Pay Application</button>
          </div>
        </div>
      )}

      {apps.length===0&&!showNew&&(
        <div style={{textAlign:"center",padding:"40px",color:T.muted,background:T.bg2,borderRadius:10,border:"2px dashed "+T.subtle}}>
          <div style={{fontSize:24,marginBottom:8}}>💰</div>
          <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>No pay applications yet</div>
          <div style={{fontSize:11}}>Create your first billing application for this project</div>
        </div>
      )}

      {apps.map(app=>app.status === "pending_gc_approval" ? (
        <div key={app.id} style={{background:`linear-gradient(135deg,${T.gold}10,${T.gold}04)`,border:"1px solid "+T.gold+"60",borderRadius:11,padding:"12px 16px",marginBottom:10}}>
          <div style={{background:T.goldBg,border:`1px solid ${T.gold}40`,borderRadius:8,padding:"8px 11px",marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:14}}>⚡</span>
            <div style={{flex:1}}>
              <div style={{fontSize:11,fontWeight:800,color:T.gold}}>Drafted by ARIA · Pending Your Approval</div>
              <div style={{fontSize:10,color:T.muted,marginTop:2}}>
                {app.milestoneName ? `${app.milestoneName} hit 100% — ARIA pre-filled this G702. ` : ""}
                Edit anything, then approve to mark it ready to print/send.
              </div>
            </div>
          </div>

          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
            <div>
              <span style={{fontFamily:"monospace",fontSize:11,color:T.gold,fontWeight:700}}>App #{app.app_num}</span>
              <span style={{fontSize:9,fontWeight:700,background:T.gold+"20",color:T.gold,borderRadius:10,padding:"1px 7px",marginLeft:8}}>PENDING GC</span>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:20,fontWeight:800,color:T.gold}}>{fmt(app.amount_due)}</div>
              <div style={{fontSize:10,color:T.muted}}>Amount Due (after retention)</div>
            </div>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10}}>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Period From</div>
              <input type="date" value={app.period_from||""} onChange={e=>updateApp(app.id,{period_from:e.target.value})} style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Period To</div>
              <input type="date" value={app.period_to||""} onChange={e=>updateApp(app.id,{period_to:e.target.value})} style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Retention %</div>
              <input type="number" value={app.retention_pct} onChange={e=>updateApp(app.id,{retention_pct:e.target.value})} style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Work Completed $</div>
              <input type="number" value={app.work_completed} onChange={e=>updateApp(app.id,{work_completed:e.target.value})} style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Materials Stored $</div>
              <input type="number" value={app.materials_stored} onChange={e=>updateApp(app.id,{materials_stored:e.target.value})} style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Retention $</div>
              <div style={{...inp,color:T.red,fontWeight:700}}>({fmt(app.retention)})</div></div>
          </div>

          <div style={{marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:T.gold,marginBottom:4,textTransform:"uppercase",letterSpacing:".06em"}}>Work Completed This Period (Narrative)</div>
            <textarea value={app.narrative||""} onChange={e=>updateApp(app.id,{narrative:e.target.value})} rows={4}
              placeholder="ARIA-drafted description of work completed for this billing period."
              style={{...inp,resize:"vertical",lineHeight:1.6}}/>
          </div>

          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>dismissDraft(app.id)}
              style={{flex:1,padding:"9px",background:"transparent",border:"1px solid "+T.subtle,color:T.muted,fontSize:12,fontWeight:600,borderRadius:7,cursor:"pointer"}}>
              Dismiss Draft
            </button>
            <button onClick={()=>approveDraft(app.id)}
              style={{flex:2,padding:"9px",background:T.gold,border:"none",color:T.bg0,fontSize:12,fontWeight:800,borderRadius:7,cursor:"pointer"}}>
              ✓ Approve Draft (Ready to Print/Send)
            </button>
          </div>
        </div>
      ) : (
        <div key={app.id} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 16px",marginBottom:8}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                <span style={{fontFamily:"monospace",fontSize:11,color:T.gold,fontWeight:700}}>App #{app.app_num}</span>
                <span style={{fontSize:9,fontWeight:700,background:app.status==="approved"?T.greenBg:app.status==="submitted"?T.blueBg:T.bg3,color:app.status==="approved"?T.green:app.status==="submitted"?T.blue:T.muted,borderRadius:10,padding:"1px 7px",textTransform:"capitalize"}}>{app.status}</span>
              </div>
              {app.period_from&&<div style={{fontSize:11,color:T.muted}}>{app.period_from} → {app.period_to}</div>}
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:20,fontWeight:800,color:T.gold}}>{fmt(app.amount_due)}</div>
              <div style={{fontSize:10,color:T.muted}}>Amount Due</div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,marginTop:10}}>
            <div style={{flex:1,background:T.bg2,borderRadius:6,padding:"6px 10px",textAlign:"center"}}>
              <div style={{fontSize:12,fontWeight:700,color:T.text}}>{fmt(app.work_completed)}</div>
              <div style={{fontSize:9,color:T.muted}}>Work Completed</div>
            </div>
            <div style={{flex:1,background:T.bg2,borderRadius:6,padding:"6px 10px",textAlign:"center"}}>
              <div style={{fontSize:12,fontWeight:700,color:T.text}}>{fmt(app.materials_stored)}</div>
              <div style={{fontSize:9,color:T.muted}}>Materials Stored</div>
            </div>
            <div style={{flex:1,background:T.bg2,borderRadius:6,padding:"6px 10px",textAlign:"center"}}>
              <div style={{fontSize:12,fontWeight:700,color:T.red}}>({fmt(app.retention)})</div>
              <div style={{fontSize:9,color:T.muted}}>Retention {app.retention_pct}%</div>
            </div>
          </div>
          <div style={{display:"flex",gap:6,marginTop:10}}>
            <button onClick={()=>printG702(app)} style={{flex:1,padding:"6px",background:T.gold,color:T.bg0,border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer"}}>Print G702</button>
            {app.status==="draft"&&<button onClick={()=>openSubmitReview(app)} style={{flex:1,padding:"6px",background:T.blue,color:"white",border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer"}}>Submit…</button>}
            {app.status==="submitted"&&<button onClick={()=>{const u=apps.map(a=>a.id===app.id?{...a,status:"approved",approved_date:new Date().toLocaleDateString()}:a);save(u);}} style={{flex:1,padding:"6px",background:T.green,color:"white",border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer"}}>✓ Owner Approved</button>}
            {app.status==="approved"&&<button onClick={()=>openPaymentClearedReview(app)} style={{flex:1,padding:"6px",background:T.gold,color:T.bg0,border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer"}}>Payment Cleared…</button>}
            {app.status==="paid"&&<button onClick={()=>setClearedAppId(app.id)} style={{flex:1,padding:"6px",background:T.bg3,color:T.muted,border:`1px solid ${T.subtle}`,borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer"}}>✓ Paid · View §8134</button>}
            <button onClick={()=>{if(window.confirm("Delete this pay application?"))save(apps.filter(a=>a.id!==app.id));}} style={{padding:"6px 10px",background:"transparent",border:"1px solid "+T.subtle,borderRadius:6,fontSize:11,color:T.muted,cursor:"pointer"}}>🗑</button>
          </div>
          {/* Sub waiver status strip — only when there are tracked sub waivers */}
          {app.subWaivers && Object.keys(app.subWaivers).length > 0 && (()=>{
            const subs = Object.values(app.subWaivers);
            const recv = subs.filter(s => s.status === "received").length;
            const req  = subs.filter(s => s.status === "requested").length;
            const miss = subs.filter(s => s.status === "missing").length;
            return (
              <div style={{marginTop:8,display:"flex",gap:6,alignItems:"center",fontSize:10,color:T.muted}}>
                <span style={{fontWeight:700,color:T.muted}}>Sub waivers:</span>
                <span style={{background:T.greenBg,color:T.green,padding:"1px 7px",borderRadius:10,fontWeight:700}}>{recv} received</span>
                <span style={{background:T.amberBg,color:T.amber,padding:"1px 7px",borderRadius:10,fontWeight:700}}>{req} requested</span>
                {miss > 0 && <span style={{background:T.redBg,color:T.red,padding:"1px 7px",borderRadius:10,fontWeight:700}}>{miss} missing</span>}
                <button onClick={()=>openSubmitReview(app)} style={{marginLeft:"auto",background:"transparent",border:"none",color:T.blue,fontSize:10,fontWeight:700,cursor:"pointer"}}>Manage →</button>
              </div>
            );
          })()}
        </div>
      ))}

      {/* Post-payment review modal — opens when GC marks an approved app as
          payment cleared. ARIA auto-generated §8134 unconditional waiver +
          chase modal for sub unconditional waivers. */}
      {clearedAppId && (() => {
        const app = apps.find(a => a.id === clearedAppId);
        if (!app) return null;
        const subs = Object.values(app.subUnconditionalWaivers || {});
        const recv = subs.filter(s => s.status === "received").length;
        const req  = subs.filter(s => s.status === "requested").length;
        const miss = subs.filter(s => s.status === "missing").length;
        return (
          <div onClick={()=>setClearedAppId(null)}
            style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(2,4,10,0.7)",display:"flex",alignItems:"center",justifyContent:"center",padding:20,overflowY:"auto"}}>
            <div onClick={e=>e.stopPropagation()}
              style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:14,maxWidth:760,width:"100%",maxHeight:"92vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.5)"}}>
              <div style={{padding:"14px 18px",borderBottom:"1px solid "+T.subtle,background:T.bg2,display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,zIndex:2}}>
                <div>
                  <div style={{fontSize:13,fontWeight:800,color:T.text}}>💰 Payment Cleared · Pay App #{app.app_num}</div>
                  <div style={{fontSize:10,color:T.muted,marginTop:2}}>{fmt(app.amount_due)} paid {app.cleared_date ? "on " + app.cleared_date : ""}</div>
                </div>
                <button onClick={()=>setClearedAppId(null)} style={{background:"transparent",border:"none",color:T.muted,fontSize:18,cursor:"pointer",lineHeight:1}}>×</button>
              </div>

              <div style={{padding:"16px 18px"}}>
                <div style={{background:`linear-gradient(135deg,${T.gold}14,${T.gold}04)`,border:`1px solid ${T.gold}40`,borderRadius:10,padding:"12px 14px",marginBottom:14}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:8}}>
                    <div>
                      <div style={{fontSize:11,fontWeight:800,color:T.gold,textTransform:"uppercase",letterSpacing:".06em"}}>⚖ ARIA Drafted</div>
                      <div style={{fontSize:13,fontWeight:700,color:T.text,marginTop:2}}>GC Unconditional Waiver — §8134</div>
                      <div style={{fontSize:10,color:T.muted,marginTop:2}}>California Civil Code statutory form. Sign and send to owner now that payment has cleared.</div>
                    </div>
                    <button onClick={()=>printGCUnconditionalWaiver(app)}
                      style={{background:T.gold,color:T.bg0,border:"none",borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:800,cursor:"pointer",whiteSpace:"nowrap"}}>
                      🖨 Print / Save PDF
                    </button>
                  </div>
                  <div style={{marginTop:8,padding:"7px 10px",background:T.redBg,border:`1px solid ${T.red}30`,borderRadius:7,fontSize:10,color:T.red,lineHeight:1.5}}>
                    ⚠ <strong>Only sign after payment has actually cleared at your bank.</strong> Per §8124, an unconditional waiver is enforceable the moment you sign it — even if the check bounces.
                  </div>
                </div>

                <div style={{background:T.bg2,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 14px",marginBottom:14}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <div>
                      <div style={{fontSize:11,fontWeight:800,color:T.text,textTransform:"uppercase",letterSpacing:".06em"}}>Sub Unconditional Waivers</div>
                      <div style={{fontSize:10,color:T.muted,marginTop:2}}>Chase these once you've paid each sub. {recv} received · {req} requested · {miss} missing</div>
                    </div>
                    {miss > 0 && (
                      <button onClick={()=>requestAllMissingUncond(app)}
                        style={{background:T.blue,color:"white",border:"none",borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:800,cursor:"pointer"}}>
                        ✉ Request All Missing
                      </button>
                    )}
                  </div>
                  {requestMsg && (
                    <div style={{padding:"6px 10px",background:requestMsg.startsWith("✓")||requestMsg.startsWith("Done")?T.greenBg:requestMsg.startsWith("✗")?T.redBg:T.bg3,color:requestMsg.startsWith("✓")||requestMsg.startsWith("Done")?T.green:requestMsg.startsWith("✗")?T.red:T.muted,borderRadius:6,fontSize:10,fontWeight:600,marginBottom:8}}>
                      {requestMsg}
                    </div>
                  )}
                  {subs.length === 0 ? (
                    <div style={{padding:"16px 8px",textAlign:"center",color:T.muted,fontSize:11,lineHeight:1.6}}>
                      No subs tracked for this pay app yet.
                    </div>
                  ) : (
                    <div style={{maxHeight:300,overflowY:"auto"}}>
                      {subs.map(s => {
                        const c = s.status === "received" ? T.green : s.status === "requested" ? T.amber : T.muted;
                        const bg = s.status === "received" ? T.greenBg : s.status === "requested" ? T.amberBg : T.bg3;
                        const subPaid = app.subPayments?.[s.subId];
                        return (
                          <div key={s.subId} style={{padding:"8px 0",borderBottom:`1px solid ${T.subtle}`,fontSize:11}}>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{color:T.text,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}{s.trade?` · ${s.trade}`:""}</div>
                                <div style={{color:T.muted,fontSize:9,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.email || "(no email)"}</div>
                              </div>
                              <span style={{background:bg,color:c,fontSize:9,fontWeight:800,padding:"2px 7px",borderRadius:10,textTransform:"uppercase"}}>
                                {s.status}
                              </span>
                              {s.status === "missing" && (
                                <button onClick={()=>requestOneUncond(app, s)} disabled={!s.email||!!requesting[s.subId]}
                                  style={{background:s.email?T.blue:T.bg3,color:s.email?"white":T.muted,border:"none",borderRadius:6,padding:"4px 9px",fontSize:10,fontWeight:700,cursor:s.email&&!requesting[s.subId]?"pointer":"default"}}>
                                  {requesting[s.subId] ? "…" : "Request"}
                                </button>
                              )}
                              {s.status !== "received" && (
                                <button onClick={()=>markSubUncondReceived(app, s.subId)}
                                  title="Mark this sub's unconditional waiver as received"
                                  style={{background:"transparent",color:T.green,border:`1px solid ${T.green}50`,borderRadius:6,padding:"4px 9px",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                                  ✓ Mark received
                                </button>
                              )}
                            </div>
                            {/* Per-sub payment row — what GC actually paid this sub for this period */}
                            <div style={{display:"flex",alignItems:"center",gap:6,marginTop:5,fontSize:10,color:T.muted}}>
                              <span style={{minWidth:50}}>Paid:</span>
                              <span style={{color:T.muted}}>$</span>
                              <input type="number" defaultValue={subPaid?.paidAmount || ""} placeholder="0"
                                onBlur={e=>setSubPaid(app, s, e.target.value)}
                                style={{width:90,background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:5,padding:"3px 7px",color:T.text,fontSize:10,fontFamily:"inherit"}}/>
                              {subPaid?.paidAmount > 0 && (
                                <span style={{color:T.green,fontSize:9,fontWeight:700,marginLeft:4}}>✓ {subPaid.paidDate}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div style={{padding:"12px 18px",borderTop:`1px solid ${T.subtle}`,background:T.bg2,display:"flex",gap:8,justifyContent:"flex-end",position:"sticky",bottom:0,zIndex:2}}>
                <button onClick={()=>setClearedAppId(null)}
                  style={{background:T.gold,color:T.bg0,border:"none",borderRadius:7,padding:"8px 18px",fontSize:12,fontWeight:800,cursor:"pointer"}}>
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Pre-submit review modal — opens when GC clicks "Submit…" on a draft */}
      {reviewAppId && (() => {
        const app = apps.find(a => a.id === reviewAppId);
        if (!app) return null;
        const subs = Object.values(app.subWaivers || {});
        const recv = subs.filter(s => s.status === "received").length;
        const req  = subs.filter(s => s.status === "requested").length;
        const miss = subs.filter(s => s.status === "missing").length;
        return (
          <div onClick={()=>setReviewAppId(null)}
            style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(2,4,10,0.7)",display:"flex",alignItems:"center",justifyContent:"center",padding:20,overflowY:"auto"}}>
            <div onClick={e=>e.stopPropagation()}
              style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:14,maxWidth:760,width:"100%",maxHeight:"92vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.5)"}}>
              <div style={{padding:"14px 18px",borderBottom:"1px solid "+T.subtle,background:T.bg2,display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,zIndex:2}}>
                <div>
                  <div style={{fontSize:13,fontWeight:800,color:T.text}}>📤 Pre-submit Review · Pay App #{app.app_num}</div>
                  <div style={{fontSize:10,color:T.muted,marginTop:2}}>{app.period_from} → {app.period_to} · {fmt(app.amount_due)} due</div>
                </div>
                <button onClick={()=>setReviewAppId(null)} style={{background:"transparent",border:"none",color:T.muted,fontSize:18,cursor:"pointer",lineHeight:1}}>×</button>
              </div>

              <div style={{padding:"16px 18px"}}>
                {/* 1. ARIA-drafted §8132 GC waiver */}
                <div style={{background:`linear-gradient(135deg,${T.gold}14,${T.gold}04)`,border:`1px solid ${T.gold}40`,borderRadius:10,padding:"12px 14px",marginBottom:14}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:8}}>
                    <div>
                      <div style={{fontSize:11,fontWeight:800,color:T.gold,textTransform:"uppercase",letterSpacing:".06em"}}>⚖ ARIA Drafted</div>
                      <div style={{fontSize:13,fontWeight:700,color:T.text,marginTop:2}}>GC Conditional Waiver — §8132</div>
                      <div style={{fontSize:10,color:T.muted,marginTop:2}}>California Civil Code statutory form, pre-filled for this billing period.</div>
                    </div>
                    <button onClick={()=>printGCWaiver(app)}
                      style={{background:T.gold,color:T.bg0,border:"none",borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:800,cursor:"pointer",whiteSpace:"nowrap"}}>
                      🖨 Print / Save PDF
                    </button>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,fontSize:11,color:T.muted,paddingTop:8,borderTop:`1px solid ${T.gold}20`}}>
                    <div><strong style={{color:T.text}}>Claimant:</strong> {session?.companyName || "GC"}</div>
                    <div><strong style={{color:T.text}}>Owner:</strong> {proj?.owner_name || proj?.client || "Owner"}</div>
                    <div><strong style={{color:T.text}}>Through:</strong> {app.period_to}</div>
                    <div><strong style={{color:T.text}}>Amount:</strong> {fmt(app.amount_due)}</div>
                  </div>
                  <div style={{marginTop:8,padding:"7px 10px",background:T.bg2,borderRadius:7,fontSize:10,color:T.muted,lineHeight:1.5}}>
                    Conditional waiver — does <strong style={{color:T.text}}>not</strong> release lien rights until the owner's check clears. Sign at the bottom of the printed form before sending to owner.
                  </div>
                </div>

                {/* 2. Sub waiver tracking */}
                <div style={{background:T.bg2,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 14px",marginBottom:14}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <div>
                      <div style={{fontSize:11,fontWeight:800,color:T.text,textTransform:"uppercase",letterSpacing:".06em"}}>Sub Waivers This Period</div>
                      <div style={{fontSize:10,color:T.muted,marginTop:2}}>{recv} received · {req} requested · {miss} missing</div>
                    </div>
                    {miss > 0 && (
                      <button onClick={()=>requestAllMissing(app)}
                        style={{background:T.blue,color:"white",border:"none",borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:800,cursor:"pointer"}}>
                        ✉ Request All Missing
                      </button>
                    )}
                  </div>
                  {requestMsg && (
                    <div style={{padding:"6px 10px",background:requestMsg.startsWith("✓")||requestMsg.startsWith("Done")?T.greenBg:requestMsg.startsWith("✗")?T.redBg:T.bg3,color:requestMsg.startsWith("✓")||requestMsg.startsWith("Done")?T.green:requestMsg.startsWith("✗")?T.red:T.muted,borderRadius:6,fontSize:10,fontWeight:600,marginBottom:8}}>
                      {requestMsg}
                    </div>
                  )}
                  {subs.length === 0 ? (
                    <div style={{padding:"16px 8px",textAlign:"center",color:T.muted,fontSize:11,lineHeight:1.6}}>
                      No subs on file for this company yet. Add subs from the Sub Portal or Team tab and they'll appear here for waiver tracking on the next pay app.
                    </div>
                  ) : (
                    <div style={{maxHeight:260,overflowY:"auto"}}>
                      {subs.map(s => {
                        const c = s.status === "received" ? T.green : s.status === "requested" ? T.amber : T.muted;
                        const bg = s.status === "received" ? T.greenBg : s.status === "requested" ? T.amberBg : T.bg3;
                        return (
                          <div key={s.subId} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0",borderBottom:`1px solid ${T.subtle}`,fontSize:11}}>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{color:T.text,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}{s.trade?` · ${s.trade}`:""}</div>
                              <div style={{color:T.muted,fontSize:9,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.email || "(no email)"}</div>
                            </div>
                            <span style={{background:bg,color:c,fontSize:9,fontWeight:800,padding:"2px 7px",borderRadius:10,textTransform:"uppercase"}}>
                              {s.status}
                            </span>
                            {s.status === "missing" && (
                              <button onClick={()=>requestOneSub(app, s)} disabled={!s.email||!!requesting[s.subId]}
                                style={{background:s.email?T.blue:T.bg3,color:s.email?"white":T.muted,border:"none",borderRadius:6,padding:"4px 9px",fontSize:10,fontWeight:700,cursor:s.email&&!requesting[s.subId]?"pointer":"default"}}>
                                {requesting[s.subId] ? "…" : "Request"}
                              </button>
                            )}
                            {s.status !== "received" && (
                              <button onClick={()=>markSubReceived(app, s.subId)}
                                title="Mark this sub's waiver as received"
                                style={{background:"transparent",color:T.green,border:`1px solid ${T.green}50`,borderRadius:6,padding:"4px 9px",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                                ✓ Mark received
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div style={{padding:"12px 18px",borderTop:`1px solid ${T.subtle}`,background:T.bg2,display:"flex",gap:8,justifyContent:"flex-end",position:"sticky",bottom:0,zIndex:2}}>
                <button onClick={()=>setReviewAppId(null)}
                  style={{background:"transparent",border:`1px solid ${T.subtle}`,color:T.muted,borderRadius:7,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                  Cancel
                </button>
                <button onClick={()=>approveAndSubmit(app)}
                  style={{background:T.blue,color:"white",border:"none",borderRadius:7,padding:"8px 18px",fontSize:12,fontWeight:800,cursor:"pointer"}}>
                  ✓ Approve &amp; Submit to Owner
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── PHOTO DAILY LOG VIEW ────────────────────────────────────────────────────
function PhotoDailyLogView({proj, session}) {
  const sk = "grok_photolog_" + proj?.id;
  const [entries, setEntries] = useState(()=>{try{return JSON.parse(localStorage.getItem(sk)||"[]");}catch{return [];}});
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({date:new Date().toISOString().split("T")[0], phase:"", work_performed:"", crew_count:"", weather:"Clear", photos:[], notes:""});
  const [photos, setPhotos] = useState([]);
  const fileRef = React.useRef(null);
  const inp = {width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit",marginBottom:8};

  const save = e => { setEntries(e); try{localStorage.setItem(sk,JSON.stringify(e));}catch(err){} };

  const handlePhotos = (files) => {
    Array.from(files).forEach(file=>{
      const r = new FileReader();
      r.onload = ev => setPhotos(p=>[...p,{id:Date.now()+Math.random(),url:ev.target.result,name:file.name,caption:""}]);
      r.readAsDataURL(file);
    });
  };

  const addEntry = () => {
    if(!form.work_performed) return;
    const entry = {...form, id:Date.now(), photos:photos.map(p=>({url:p.url,caption:p.caption})),
      submitted_by:session?.ownerName||"GC", submitted_at:new Date().toLocaleString()};
    save([entry,...entries]);
    setForm({date:new Date().toISOString().split("T")[0],phase:"",work_performed:"",crew_count:"",weather:"Clear",photos:[],notes:""});
    setPhotos([]);
    setShowAdd(false);
  };

  const PHASES = ["Demo","Rough Framing","Rough MEP","Insulation/Drywall","Tile/Flooring","Paint","Punch/Closeout"];
  const WEATHER = ["Clear","Cloudy","Rain","Hot","Cold","Windy"];

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div>
          <div style={{fontSize:14,fontWeight:800,color:T.text}}>📷 Photo Daily Log</div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>{entries.length} entries · Progress photos with daily notes</div>
        </div>
        <button onClick={()=>setShowAdd(!showAdd)} style={{fontSize:11,fontWeight:700,background:T.blue,color:"white",border:"none",borderRadius:7,padding:"7px 14px",cursor:"pointer"}}>+ Log Today</button>
      </div>

      {showAdd&&(
        <div style={{background:T.bg1,border:"1px solid "+T.blue+"40",borderRadius:10,padding:14,marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Daily Log Entry</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:4}}>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Date</div><input type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))} style={inp}/></div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Phase</div>
              <select value={form.phase} onChange={e=>setForm(p=>({...p,phase:e.target.value}))} style={inp}>
                <option value="">Select phase...</option>
                {PHASES.map(ph=><option key={ph}>{ph}</option>)}
              </select>
            </div>
            <div><div style={{fontSize:10,color:T.muted,marginBottom:3}}>Weather</div>
              <select value={form.weather} onChange={e=>setForm(p=>({...p,weather:e.target.value}))} style={inp}>
                {WEATHER.map(w=><option key={w}>{w}</option>)}
              </select>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:window.innerWidth<768?"1fr":"1fr 1fr",gap:8}}>
            <div>
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Work Performed *</div>
              <textarea value={form.work_performed} onChange={e=>setForm(p=>({...p,work_performed:e.target.value}))}
                placeholder="Framed unit 4A bathroom walls, rough plumbing inspection passed..." rows={3} style={{...inp,resize:"vertical"}}/>
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Crew Count</div>
              <input value={form.crew_count} onChange={e=>setForm(p=>({...p,crew_count:e.target.value}))} placeholder="e.g. 4 workers on site" style={inp}/>
            </div>
            <div>
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Photos</div>
              <input ref={fileRef} type="file" multiple accept="image/*" style={{display:"none"}} onChange={e=>handlePhotos(e.target.files)}/>
              <button onClick={()=>fileRef.current.click()} style={{width:"100%",height:80,background:T.bg2,border:"2px dashed "+T.subtle,borderRadius:8,cursor:"pointer",color:T.muted,fontSize:12,marginBottom:8}}>
                📷 Click to add photos
              </button>
              {photos.length>0&&(
                <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                  {photos.map(p=>(
                    <div key={p.id} style={{position:"relative",width:60,height:60}}>
                      <img src={p.url} alt="" style={{width:60,height:60,objectFit:"cover",borderRadius:6,border:"1px solid "+T.subtle}}/>
                      <button onClick={()=>setPhotos(ps=>ps.filter(x=>x.id!==p.id))} style={{position:"absolute",top:-4,right:-4,width:16,height:16,borderRadius:"50%",background:T.red,color:"white",border:"none",cursor:"pointer",fontSize:9,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
                    </div>
                  ))}
                </div>
              )}
              <div style={{fontSize:10,color:T.muted,marginBottom:3,marginTop:8}}>Notes</div>
              <textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}
                placeholder="Issues, delays, safety notes..." rows={2} style={{...inp,resize:"none"}}/>
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>{setShowAdd(false);setPhotos([]);}} style={{flex:1,padding:"8px",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,color:T.muted,fontSize:12,cursor:"pointer"}}>Cancel</button>
            <button onClick={addEntry} style={{flex:2,padding:"8px",background:T.blue,border:"none",borderRadius:7,color:"white",fontSize:12,fontWeight:700,cursor:"pointer"}}>Save Entry</button>
          </div>
        </div>
      )}

      {entries.length===0&&!showAdd&&(
        <div style={{textAlign:"center",padding:"40px",color:T.muted,background:T.bg2,borderRadius:10,border:"2px dashed "+T.subtle}}>
          <div style={{fontSize:24,marginBottom:8}}>📷</div>
          <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>No photo logs yet</div>
          <div style={{fontSize:11}}>Log daily progress with photos to build your project record</div>
        </div>
      )}

      {entries.map(e=>(
        <div key={e.id} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 16px",marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:T.text}}>{new Date(e.date).toLocaleDateString("en-US",{weekday:"long",month:"short",day:"numeric",year:"numeric"})}</div>
              <div style={{display:"flex",gap:6,marginTop:3,flexWrap:"wrap"}}>
                {e.phase&&<span style={{fontSize:9,background:T.amberBg,color:T.amber,borderRadius:10,padding:"1px 7px",fontWeight:700}}>{e.phase}</span>}
                {e.weather&&<span style={{fontSize:9,background:T.bg3,color:T.muted,borderRadius:10,padding:"1px 7px"}}>{e.weather}</span>}
                {e.crew_count&&<span style={{fontSize:9,background:T.bg3,color:T.muted,borderRadius:10,padding:"1px 7px"}}>👷 {e.crew_count}</span>}
              </div>
            </div>
            <button onClick={()=>save(entries.filter(x=>x.id!==e.id))} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:12}}>🗑</button>
          </div>
          <div style={{fontSize:12,color:T.text,lineHeight:1.6,marginBottom:e.photos?.length>0?10:0}}>{e.work_performed}</div>
          {e.notes&&<div style={{fontSize:11,color:T.muted,fontStyle:"italic",marginBottom:8}}>{e.notes}</div>}
          {e.photos?.length>0&&(
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {e.photos.map((ph,i)=>(
                <img key={i} src={ph.url} alt={ph.caption||""} style={{width:72,height:72,objectFit:"cover",borderRadius:6,border:"1px solid "+T.subtle,cursor:"pointer"}}
                  onClick={()=>window.open(ph.url,"_blank")}/>
              ))}
            </div>
          )}
          <div style={{fontSize:10,color:T.muted,marginTop:6}}>Logged by {e.submitted_by} · {e.submitted_at}</div>
        </div>
      ))}
    </div>
  );
}

// ─── EQUIPMENT TRACKING VIEW ─────────────────────────────────────────────────
function EquipmentTrackView({proj, session}) {
  const sk = "grok_equipment_" + proj?.id;
  const [items, setItems] = useState(()=>{try{return JSON.parse(localStorage.getItem(sk)||"[]");}catch{return [];}});
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({name:"",type:"owned",category:"Tools",assigned_to:"",serial:"",rental_co:"",daily_rate:"",on_site_date:"",return_date:"",notes:""});
  const inp = {width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit",marginBottom:8};

  const save = i => { setItems(i); try{localStorage.setItem(sk,JSON.stringify(i));}catch(e){} };

  const addItem = () => {
    if(!form.name) return;
    save([...items,{...form,id:Date.now(),status:"on-site"}]);
    setForm({name:"",type:"owned",category:"Tools",assigned_to:"",serial:"",rental_co:"",daily_rate:"",on_site_date:"",return_date:"",notes:""});
    setShowAdd(false);
  };

  const CATEGORIES = ["Heavy Equipment","Power Tools","Scaffolding","Safety Equipment","Vehicles","Other"];
  const onSite = items.filter(i=>i.status==="on-site");
  const rentals = items.filter(i=>i.type==="rental");
  const rentalCost = rentals.reduce((s,i)=>s+(parseFloat(i.daily_rate||0)),0);

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div>
          <div style={{fontSize:14,fontWeight:800,color:T.text}}>🚜 Equipment & Tools</div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>{onSite.length} on site · {rentals.length} rentals · ${rentalCost.toFixed(0)}/day rental cost</div>
        </div>
        <button onClick={()=>setShowAdd(!showAdd)} style={{fontSize:11,fontWeight:700,background:T.teal,color:"white",border:"none",borderRadius:7,padding:"7px 14px",cursor:"pointer"}}>+ Add Equipment</button>
      </div>

      {/* Return alerts */}
      {rentals.filter(i=>{
        if(!i.return_date) return false;
        const days = Math.ceil((new Date(i.return_date)-new Date())/(1000*60*60*24));
        return days<=3 && days>=0;
      }).length>0&&(
        <div style={{background:T.amberBg,border:"1px solid "+T.amber+"40",borderRadius:8,padding:"8px 12px",marginBottom:12,fontSize:11,color:T.amber}}>
          ⚠️ {rentals.filter(i=>{const d=Math.ceil((new Date(i.return_date)-new Date())/(1000*60*60*24));return d<=3&&d>=0;}).length} rental(s) due back within 3 days!
        </div>
      )}

      {showAdd&&(
        <div style={{background:T.bg1,border:"1px solid "+T.teal+"40",borderRadius:10,padding:14,marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Add Equipment / Tool</div>
          <div style={{display:"grid",gridTemplateColumns:window.innerWidth<768?"1fr":"1fr 1fr",gap:8}}>
            <div>
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Name *</div>
              <input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Skid Steer, Scaffolding, Circular Saw" style={inp}/>
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Category</div>
              <select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))} style={inp}>
                {CATEGORIES.map(c=><option key={c}>{c}</option>)}
              </select>
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Type</div>
              <div style={{display:"flex",gap:6,marginBottom:8}}>
                {["owned","rental"].map(t=>(
                  <button key={t} onClick={()=>setForm(p=>({...p,type:t}))}
                    style={{flex:1,padding:"6px",borderRadius:6,border:"1px solid "+(form.type===t?T.teal:T.subtle),background:form.type===t?T.teal+"20":"transparent",color:form.type===t?T.teal:T.muted,fontSize:11,fontWeight:700,cursor:"pointer",textTransform:"capitalize"}}>
                    {t}
                  </button>
                ))}
              </div>
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Assigned To</div>
              <input value={form.assigned_to} onChange={e=>setForm(p=>({...p,assigned_to:e.target.value}))} placeholder="Superintendent or trade" style={inp}/>
            </div>
            <div>
              {form.type==="rental"&&<>
                <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Rental Company</div>
                <input value={form.rental_co} onChange={e=>setForm(p=>({...p,rental_co:e.target.value}))} placeholder="United Rentals, Sunbelt, etc." style={inp}/>
                <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Daily Rate $</div>
                <input value={form.daily_rate} onChange={e=>setForm(p=>({...p,daily_rate:e.target.value}))} placeholder="250" type="number" style={inp}/>
                <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Return Date</div>
                <input type="date" value={form.return_date} onChange={e=>setForm(p=>({...p,return_date:e.target.value}))} style={inp}/>
              </>}
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Serial / Asset #</div>
              <input value={form.serial} onChange={e=>setForm(p=>({...p,serial:e.target.value}))} placeholder="Optional" style={inp}/>
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>On Site Since</div>
              <input type="date" value={form.on_site_date} onChange={e=>setForm(p=>({...p,on_site_date:e.target.value}))} style={inp}/>
              <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Notes</div>
              <textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} rows={2} style={{...inp,resize:"none"}}/>
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>setShowAdd(false)} style={{flex:1,padding:"8px",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,color:T.muted,fontSize:12,cursor:"pointer"}}>Cancel</button>
            <button onClick={addItem} style={{flex:2,padding:"8px",background:T.teal,border:"none",borderRadius:7,color:"white",fontSize:12,fontWeight:700,cursor:"pointer"}}>Add to Project</button>
          </div>
        </div>
      )}

      {items.length===0&&!showAdd&&(
        <div style={{textAlign:"center",padding:"40px",color:T.muted,background:T.bg2,borderRadius:10,border:"2px dashed "+T.subtle}}>
          <div style={{fontSize:24,marginBottom:8}}>🚜</div>
          <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>No equipment tracked yet</div>
          <div style={{fontSize:11}}>Track owned tools and rentals — get alerts when rentals are due back</div>
        </div>
      )}

      {items.map(item=>{
        const daysUntilReturn = item.return_date ? Math.ceil((new Date(item.return_date)-new Date())/(1000*60*60*24)) : null;
        const returnAlert = daysUntilReturn!==null && daysUntilReturn<=3 && daysUntilReturn>=0;
        return (
          <div key={item.id} style={{background:T.bg1,border:"1px solid "+(returnAlert?T.amber:T.subtle),borderRadius:10,padding:"12px 16px",marginBottom:8}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                  <span style={{fontSize:13,fontWeight:700,color:T.text}}>{item.name}</span>
                  <span style={{fontSize:9,fontWeight:700,background:item.type==="rental"?T.amberBg:T.greenBg,color:item.type==="rental"?T.amber:T.green,borderRadius:10,padding:"1px 7px",textTransform:"capitalize"}}>{item.type}</span>
                  <span style={{fontSize:9,background:T.bg3,color:T.muted,borderRadius:10,padding:"1px 7px"}}>{item.category}</span>
                </div>
                {item.assigned_to&&<div style={{fontSize:11,color:T.muted}}>Assigned: {item.assigned_to}</div>}
                {item.rental_co&&<div style={{fontSize:11,color:T.muted}}>From: {item.rental_co}{item.daily_rate?` · $${item.daily_rate}/day`:""}</div>}
                {item.return_date&&<div style={{fontSize:11,color:returnAlert?T.amber:T.muted,fontWeight:returnAlert?700:400}}>
                  Return: {new Date(item.return_date).toLocaleDateString()} {returnAlert?`⚠️ ${daysUntilReturn} day${daysUntilReturn!==1?"s":""} left`:""}
                </div>}
              </div>
              <div style={{display:"flex",gap:4,flexShrink:0,marginLeft:8}}>
                <button onClick={()=>{const u=items.map(i=>i.id===item.id?{...i,status:i.status==="on-site"?"returned":"on-site"}:i);save(u);}}
                  style={{fontSize:10,fontWeight:700,background:item.status==="on-site"?T.greenBg:T.bg3,color:item.status==="on-site"?T.green:T.muted,border:"1px solid "+(item.status==="on-site"?T.green:T.subtle),borderRadius:6,padding:"4px 9px",cursor:"pointer"}}>
                  {item.status==="on-site"?"✓ On Site":"Returned"}
                </button>
                <button onClick={()=>{if(window.confirm("Remove this item?"))save(items.filter(i=>i.id!==item.id));}}
                  style={{padding:"4px 7px",background:"transparent",border:"1px solid "+T.subtle,borderRadius:6,fontSize:10,color:T.muted,cursor:"pointer"}}>🗑</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function InspectionBillingView({proj, projCOs, session}) {
  const storageKey = "grok_inspection_" + proj?.id;

  const [data, setData] = useState(()=>{
    try{const s=localStorage.getItem(storageKey);if(s)return JSON.parse(s);}catch(e){}
    return {milestones:[], generated:false, permit_type:"", permit_number:"", inspector:"", notes:""};
  });
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState("");
  const [activePhase, setActivePhase] = useState(null);
  const [showBillModal, setShowBillModal] = useState(null);
  const [invoiceNote, setInvoiceNote] = useState("");

  const save = (updated) => {
    setData(updated);
    try{localStorage.setItem(storageKey,JSON.stringify(updated));}catch(e){}
  };

  const toggleMilestone = (id, field) => {
    const updated = {...data, milestones: data.milestones.map(m=>
      String(m.id)===String(id) ? {...m, [field]:!m[field], ...(field==="passed"&&!m.passed?{passed_date:new Date().toLocaleDateString()}:{})} : m
    )};
    save(updated);
  };

  const updateMilestone = (id, fields) => {
    const updated = {...data, milestones: data.milestones.map(m=>String(m.id)===String(id)?{...m,...fields}:m)};
    save(updated);
  };

  // Calculate billing totals
  const contractValue = (proj?.value||0) + projCOs.filter(c=>c.status==="approved").reduce((s,c)=>s+(c.amount||0),0);
  const totalBillPct = data.milestones.reduce((s,m)=>s+(m.passed?m.billing_pct||0:0),0);
  const billedAmount = contractValue * totalBillPct / 100;
  const remaining = contractValue - billedAmount;
  const fmt = v => "$"+Math.round(v||0).toLocaleString();

  // Generate inspection scope using AI
  const generateScope = async () => {
    const key = "proxy"; // Using Supabase AI proxy
    if (!key) { alert("Add Anthropic API key in AI Agent first."); return; }
    setGenerating(true); setGenResult("");

    const coTotal = projCOs.filter(c=>c.status==="approved").reduce((s,c)=>s+(c.amount||0),0);

    const prompt = `You are a California licensed contractor and building inspector expert.

Generate a COMPLETE inspection milestone schedule and billing plan for this construction project.

PROJECT:
Name: ${proj?.name}
Type: ${proj?.type||"Renovation"}
Address: ${proj?.addr||"Bay Area, CA"}
Contract Value: $${Math.round(contractValue).toLocaleString()}
Description: ${proj?.desc||"Full renovation project"}
Approved Change Orders: ${projCOs.filter(c=>c.status==="approved").length} ($${Math.round(coTotal).toLocaleString()})
${(()=>{try{const s=JSON.parse(localStorage.getItem("grok_inspection_"+proj?.id)||"{}");return s.blueprint_scope_notes?`\nBLUEPRINT SCOPE (from AI review):\n${s.blueprint_scope_notes.slice(0,800)}`:""}catch{return ""}})()}

REQUIREMENTS:
- Generate 6-10 inspection milestones based on the project type and scope
- Each milestone should correspond to a real California building inspection checkpoint
- Assign a billing percentage to each milestone (must total 100%)
- Include which permits/inspections are required at each stage
- Reference CBC 2022 inspection requirements
- For SRO/multi-family: include CBC Chapter 11A accessibility inspections
- Include Title 24 energy compliance checkpoints
- Make billing percentages match work completion (front-load slightly to cover mobilization costs)

Respond in this EXACT JSON format (no markdown, no backticks, just raw JSON):
{
  "permit_type": "Building Permit Type (e.g. Alteration Type 1, Residential Addition, etc.)",
  "milestones": [
    {
      "id": "m1",
      "phase": "Phase name",
      "title": "Inspection milestone title",
      "description": "What work is complete at this stage",
      "inspections_required": ["List of required city inspections"],
      "cbc_reference": "CBC section reference",
      "billing_pct": 15,
      "billing_trigger": "Invoice trigger description",
      "documentation_needed": ["List of docs needed to pass inspection"]
    }
  ],
  "total_billing_pct": 100,
  "notes": "Any special notes about permit or inspection requirements for this project type"
}`;

    try {
      const resp = await fetch(AI_PROXY_URL, {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
        body:JSON.stringify({
          model:"claude-haiku-4-5",
          max_tokens:3000,
          messages:[{role:"user",content:prompt}]
        })
      });
      const json = await resp.json();
      const text = json.content?.[0]?.text||"";
      setGenResult(text);

      // Parse JSON response
      try {
        const clean = text.replace(/\x60\x60\x60json|\x60\x60\x60/g,"").trim();
        const parsed = JSON.parse(clean);
        const milestones = (parsed.milestones||[]).map((m,i)=>({
          ...m,
          id: m.id||"m"+(i+1),
          scheduled:false, passed:false, billed:false,
          passed_date:null, billed_date:null, invoice_number:null,
          inspector_notes:"", photo_url:null,
        }));
        save({
          ...data,
          milestones,
          permit_type: parsed.permit_type||"",
          notes: parsed.notes||"",
          generated: true,
          generated_date: new Date().toLocaleDateString(),
        });
      } catch(e) {
        console.error("Parse error:", e);
      }
    } catch(e) {
      setGenResult("Error: "+e.message);
    }
    setGenerating(false);
  };

  const generateInvoicePDF = (milestone) => {
    const invoiceNum = "INV-"+proj?.id+"-"+milestone.id.toUpperCase()+"-"+new Date().getFullYear();
    const amount = contractValue * (milestone.billing_pct||0) / 100;
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Invoice ${invoiceNum}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;margin:0;padding:40px;color:#111;font-size:13px;line-height:1.5}
.hdr{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:32px;padding-bottom:16px;border-bottom:3px solid #D4A843}
.logo{font-size:20px;font-weight:800;color:#D4A843}.sub{font-size:11px;color:#666;margin-top:4px}
.inv-info{text-align:right;font-size:12px;color:#666;line-height:1.8}
.inv-info .num{font-size:16px;font-weight:800;color:#111}
.to-from{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-bottom:28px}
.label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#888;margin-bottom:6px}
.name{font-size:14px;font-weight:700;color:#111;margin-bottom:2px}
.detail{font-size:12px;color:#555;line-height:1.6}
.items{width:100%;border-collapse:collapse;margin-bottom:24px}
.items th{background:#f9f6ee;padding:8px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#888;border-bottom:2px solid #D4A843}
.items td{padding:12px;border-bottom:1px solid #eee;font-size:13px}
.total-row{background:#fffbeb;font-weight:800}
.milestone-box{background:#f0fdfa;border-left:4px solid #2dd4bf;padding:14px;border-radius:0 8px 8px 0;margin-bottom:20px}
.milestone-title{font-size:14px;font-weight:700;color:#111;margin-bottom:4px}
.milestone-desc{font-size:12px;color:#555;line-height:1.6}
.insp-list{margin-top:8px;padding-left:16px;font-size:11px;color:#666}
.amounts{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:24px}
.amount-card{padding:16px;border-radius:8px;border:1px solid #eee}
.amount-card.primary{background:#fffbeb;border-color:#D4A843}
.ac-label{font-size:10px;font-weight:700;text-transform:uppercase;color:#888;margin-bottom:4px}
.ac-value{font-size:24px;font-weight:800;color:#D4A843}
.ac-sub{font-size:11px;color:#888;margin-top:2px}
.payment-terms{background:#f9fafb;border-radius:8px;padding:14px;font-size:12px;color:#555;line-height:1.7;margin-bottom:20px}
.sigs{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:40px}
.sig{border-top:2px solid #111;padding-top:8px;font-size:11px;color:#666}
.sig b{font-size:13px;color:#111;display:block;margin-bottom:24px}
@media print{body{padding:20px}}
</style></head><body>
<div class="hdr">
  <div>
    <div class="logo">${session?.companyName||"Grok Construction Group Inc"}</div>
    <div class="sub">CSLB #${session?.cslb||"1152750"} &nbsp;|&nbsp; ${session?.email||""} &nbsp;|&nbsp; San Mateo, CA</div>
  </div>
  <div class="inv-info">
    <div class="num">${invoiceNum}</div>
    <div>Date: ${new Date().toLocaleDateString()}</div>
    <div>Due: ${new Date(Date.now()+30*864e5).toLocaleDateString()}</div>
    <div style="margin-top:6px;display:inline-block;background:#fef3c7;color:#d97706;padding:2px 10px;border-radius:20px;font-size:10px;font-weight:700">PAYMENT DUE</div>
  </div>
</div>

<div class="to-from">
  <div>
    <div class="label">Bill To</div>
    <div class="name">${proj?.client||"Property Owner"}</div>
    <div class="detail">${proj?.name}<br/>${proj?.addr||""}</div>
  </div>
  <div>
    <div class="label">Project</div>
    <div class="name">${proj?.name}</div>
    <div class="detail">Contract Value: ${fmt(contractValue)}<br/>Permit: ${data.permit_type||"See project file"}</div>
  </div>
</div>

<div class="milestone-box">
  <div class="milestone-title">📋 ${milestone.phase} — ${milestone.title}</div>
  <div class="milestone-desc">${milestone.description}</div>
  ${milestone.inspections_required?.length>0?'<ul class="insp-list">'+milestone.inspections_required.map(i=>'<li>'+i+'</li>').join('')+'</ul>':""}
</div>

<div class="amounts">
  <div class="amount-card primary">
    <div class="ac-label">This Invoice (${milestone.billing_pct||0}% of contract)</div>
    <div class="ac-value">${fmt(amount)}</div>
    <div class="ac-sub">Milestone billing — ${milestone.billing_trigger||milestone.title}</div>
  </div>
  <div class="amount-card">
    <div class="ac-label">Contract Summary</div>
    <div style="font-size:12px;line-height:1.8;margin-top:4px">
      <div style="display:flex;justify-content:space-between"><span style="color:#666">Contract Value</span><span style="font-weight:700">${fmt(contractValue)}</span></div>
      <div style="display:flex;justify-content:space-between"><span style="color:#666">Previously Billed</span><span>${fmt(billedAmount-amount<0?0:billedAmount-amount)}</span></div>
      <div style="display:flex;justify-content:space-between;border-top:1px solid #eee;padding-top:4px;margin-top:4px"><span style="color:#666">This Invoice</span><span style="font-weight:800;color:#D4A843">${fmt(amount)}</span></div>
      <div style="display:flex;justify-content:space-between"><span style="color:#666">Remaining After</span><span>${fmt(remaining-amount<0?0:remaining-amount)}</span></div>
    </div>
  </div>
</div>

<table class="items">
  <thead><tr><th>Description</th><th>Phase</th><th style="text-align:right">%</th><th style="text-align:right">Amount</th></tr></thead>
  <tbody>
    <tr><td>${milestone.title}<br/><small style="color:#888">${milestone.billing_trigger||""}</small></td><td>${milestone.phase}</td><td style="text-align:right">${milestone.billing_pct||0}%</td><td style="text-align:right;font-weight:700">${fmt(amount)}</td></tr>
    <tr class="total-row"><td colspan="3" style="text-align:right;padding:12px">INVOICE TOTAL</td><td style="text-align:right;font-size:16px;color:#D4A843">${fmt(amount)}</td></tr>
  </tbody>
</table>

<div class="payment-terms">
  <strong>Payment Terms:</strong> Net 30 days from invoice date. Late payments subject to 1.5% monthly interest. 
  Payment by check payable to ${session?.companyName||"Grok Construction Group Inc"} or ACH/wire transfer. 
  ${invoiceNote?'<br/><strong>Notes:</strong> '+invoiceNote:""}
</div>

<div class="sigs">
  <div class="sig"><b>${session?.companyName||"General Contractor"}</b>Authorized Signature &nbsp;&nbsp;&nbsp;&nbsp; Date</div>
  <div class="sig"><b>Owner Approval</b>Signature &nbsp;&nbsp;&nbsp;&nbsp; Date</div>
</div>

</body></html>`;
    const win = window.open("","_blank","width=900,height=700");
    if(!win){alert("Allow popups to export invoice");return;}
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(()=>win.print(),500);
  };

  const passedCount = data.milestones.filter(m=>m.passed).length;
  const billedCount = data.milestones.filter(m=>m.billed).length;

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>Inspection Scope & Billing Schedule</div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>AI-generated milestones based on permit type · Each inspection unlocks a billing draw</div>
        </div>
        <Btn primary onClick={generateScope} disabled={generating}
          style={{background:data.generated?T.bg2:"",color:data.generated?T.muted:"",border:data.generated?"1px solid "+T.subtle:""}}>
          {generating?<><Spin/>Generating...</>:data.generated?"♻ Regenerate":"✨ Generate with AI"}
        </Btn>
      </div>

      {/* Permit info */}
      {data.generated&&(
        <Card style={{marginBottom:14}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10}}>
            {[
              {l:"Permit Type",    f:"permit_type",   ph:"Building Alteration Type 1"},
              {l:"Permit Number",  f:"permit_number", ph:"202400123456"},
              {l:"Inspector Name", f:"inspector",     ph:"John Smith"},
              {l:"Inspection Phone", f:"inspector_phone", ph:"(415) 555-0100"},
            ].map(({l,f,ph})=>(
              <div key={f}>
                <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{l}</div>
                <input defaultValue={data[f]||""} onBlur={e=>save({...data,[f]:e.target.value})} placeholder={ph}
                  style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"7px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Summary cards */}
      {data.generated&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:8,marginBottom:12}}>
          {[
            {l:"Contract Value",  v:fmt(contractValue),            c:T.gold},
            {l:"Billed to Date",  v:fmt(billedAmount),             c:T.blue,  sub:Math.round(totalBillPct)+"%"},
            {l:"Remaining",       v:fmt(remaining),                c:T.green, sub:Math.round(100-totalBillPct)+"%"},
            {l:"Inspections",     v:passedCount+"/"+data.milestones.length+" passed", c:passedCount===data.milestones.length?T.green:T.amber},
          ].map(s=>(
            <div key={s.l} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 14px"}}>
              <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{s.l}</div>
              <div style={{fontSize:18,fontWeight:800,color:s.c,fontFamily:"'Space Mono',monospace",lineHeight:1}}>{s.v}</div>
              {s.sub&&<div style={{fontSize:10,color:T.muted,marginTop:3}}>{s.sub} of contract</div>}
            </div>
          ))}
        </div>
      )}

      {/* Billing progress bar */}
      {data.generated&&data.milestones.length>0&&(
        <Card style={{marginBottom:14}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
            <div style={{fontSize:12,fontWeight:700,color:T.text}}>Billing Progress</div>
            <div style={{fontSize:11,color:T.muted}}>{fmt(billedAmount)} billed of {fmt(contractValue)}</div>
          </div>
          <div style={{display:"flex",height:20,borderRadius:8,overflow:"hidden",background:T.bg3,marginBottom:8}}>
            {data.milestones.map((m,i)=>(
              <div key={m.id} title={m.title+" — "+m.billing_pct+"%"}
                style={{width:(m.billing_pct||0)+"%",background:m.billed?T.green:m.passed?T.amber:T.bg2,borderRight:"2px solid "+T.bg0,transition:"background 0.3s",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
                onClick={()=>setActivePhase(activePhase===m.id?null:m.id)}>
                {(m.billing_pct||0)>=8&&<span style={{fontSize:9,fontWeight:700,color:m.billed?"white":m.passed?T.bg0:T.muted}}>{m.billing_pct}%</span>}
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:16,fontSize:10,color:T.muted}}>
            <span style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:10,height:10,borderRadius:2,background:T.green}}/>Billed</span>
            <span style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:10,height:10,borderRadius:2,background:T.amber}}/>Passed — Ready to Bill</span>
            <span style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:10,height:10,borderRadius:2,background:T.bg2,border:"1px solid "+T.subtle}}/>Pending</span>
          </div>
        </Card>
      )}

      {/* Milestones */}
      {!data.generated?(
        <div style={{textAlign:"center",padding:"60px",color:T.muted,background:T.bg2,borderRadius:12,border:"2px dashed "+T.subtle}}>
          <div style={{fontSize:40,opacity:0.3,marginBottom:12}}>🏗</div>
          <div style={{fontSize:13,fontWeight:600,marginBottom:6}}>No inspection schedule yet</div>
          <div style={{fontSize:11,lineHeight:1.7,marginBottom:20}}>
            Click "Generate with AI" to create a California-compliant inspection<br/>
            milestone schedule with billing draws based on your project type and scope.
          </div>
          <Btn primary onClick={generateScope} disabled={generating}>
            {generating?<><Spin/>Generating...</>:"✨ Generate Inspection Schedule"}
          </Btn>
        </div>
      ):data.milestones.map((m,i)=>{
        const isActive = activePhase===m.id;
        const billAmt = contractValue*(m.billing_pct||0)/100;
        return (
          <div key={m.id} style={{marginBottom:8}}>
            <div onClick={()=>setActivePhase(isActive?null:m.id)}
              style={{background:m.billed?T.greenBg:m.passed?T.amberBg:T.bg1,border:"1px solid "+(m.billed?T.green+"40":m.passed?T.amber+"40":T.subtle),borderRadius:isActive?"12px 12px 0 0":12,padding:"12px 16px",cursor:"pointer",transition:"all 0.15s",display:"flex",gap:14,alignItems:"center"}}>
              {/* Checkbox */}
              <div style={{display:"flex",flexDirection:"column",gap:4,flexShrink:0}}>
                <div onClick={e=>{e.stopPropagation();toggleMilestone(m.id,"scheduled");}}
                  style={{width:20,height:20,borderRadius:4,background:m.scheduled?T.blue:T.bg3,border:"2px solid "+(m.scheduled?T.blue:T.subtle),display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",transition:"all 0.15s"}}>
                  {m.scheduled&&<span style={{color:"white",fontSize:10}}>S</span>}
                </div>
                <div onClick={e=>{e.stopPropagation();toggleMilestone(m.id,"passed");}}
                  style={{width:20,height:20,borderRadius:4,background:m.passed?T.green:T.bg3,border:"2px solid "+(m.passed?T.green:T.subtle),display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",transition:"all 0.15s"}}>
                  {m.passed&&<span style={{color:"white",fontSize:10}}>✓</span>}
                </div>
              </div>
              {/* Content */}
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                  <span style={{fontSize:10,fontWeight:700,color:T.muted,background:T.bg3,borderRadius:10,padding:"1px 7px"}}>Phase {i+1}</span>
                  <span style={{fontSize:12,fontWeight:700,color:T.text}}>{m.title}</span>
                  {m.passed&&!m.billed&&<span style={{fontSize:9,fontWeight:700,background:T.amberBg,color:T.amber,borderRadius:10,padding:"1px 7px"}}>⚡ Ready to Bill</span>}
                  {m.billed&&<span style={{fontSize:9,fontWeight:700,background:T.greenBg,color:T.green,borderRadius:10,padding:"1px 7px"}}>✓ Billed</span>}
                </div>
                <div style={{fontSize:11,color:T.muted}}>{m.phase} · {m.cbc_reference||""}</div>
              </div>
              {/* Amount */}
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontSize:16,fontWeight:800,color:m.billed?T.green:m.passed?T.amber:T.muted,fontFamily:"'Space Mono',monospace"}}>{fmt(billAmt)}</div>
                <div style={{fontSize:10,color:T.muted}}>{m.billing_pct||0}% of contract</div>
                {m.passed&&!m.billed&&(
                  <button onClick={e=>{e.stopPropagation();setShowBillModal(m);}}
                    style={{fontSize:10,fontWeight:700,background:T.amber,color:T.bg0,border:"none",borderRadius:6,padding:"3px 9px",cursor:"pointer",marginTop:4}}>
                    💵 Bill Now
                  </button>
                )}
              </div>
            </div>

            {/* Expanded detail */}
            {isActive&&(
              <div style={{background:T.bg2,border:"1px solid "+(m.billed?T.green+"40":m.passed?T.amber+"40":T.subtle),borderTop:"none",borderRadius:"0 0 12px 12px",padding:"14px 16px"}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:12}}>
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Scope at This Stage</div>
                    <div style={{fontSize:12,color:T.muted,lineHeight:1.7}}>{m.description}</div>
                  </div>
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Required Inspections</div>
                    {(m.inspections_required||[]).map((insp,j)=>(
                      <div key={j} style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:T.text,marginBottom:4}}>
                        <span style={{color:T.teal,flexShrink:0}}>🔍</span>{insp}
                      </div>
                    ))}
                  </div>
                </div>
                {(m.documentation_needed||[]).length>0&&(
                  <div style={{marginBottom:12}}>
                    <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Documentation Required</div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {m.documentation_needed.map((doc,j)=>(
                        <span key={j} style={{fontSize:10,background:T.bg3,color:T.muted,borderRadius:6,padding:"3px 9px"}}>📄 {doc}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
                  <div>
                    <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Billing Trigger</div>
                    <div style={{fontSize:11,color:T.text,lineHeight:1.5}}>{m.billing_trigger||"Upon inspection passing"}</div>
                  </div>
                  <div>
                    <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Passed Date</div>
                    <div style={{fontSize:11,color:m.passed_date?T.green:T.muted}}>{m.passed_date||"Not yet passed"}</div>
                  </div>
                  <div>
                    <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Invoice #</div>
                    <div style={{fontSize:11,color:m.invoice_number?T.text:T.muted}}>{m.invoice_number||"Not billed"}</div>
                  </div>
                </div>
                <div>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Inspector Notes</div>
                  <input defaultValue={m.inspector_notes||""} onBlur={e=>updateMilestone(m.id,{inspector_notes:e.target.value})}
                    placeholder="Add inspection notes, corrections required, conditions of approval..."
                    style={{width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
                </div>
                {m.passed&&!m.billed&&(
                  <button onClick={()=>setShowBillModal(m)}
                    style={{marginTop:12,width:"100%",padding:"11px",background:T.amber,color:T.bg0,border:"none",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:700}}>
                    💵 Generate Invoice — {fmt(billAmt)}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Bill modal */}
      {showBillModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}}>
          <div style={{background:T.bg1,borderRadius:16,padding:24,width:"100%",maxWidth:440,border:"1px solid "+T.gold+"30"}}>
            <div style={{fontSize:15,fontWeight:800,color:T.text,marginBottom:4}}>Generate Invoice</div>
            <div style={{fontSize:12,color:T.muted,marginBottom:16}}>{showBillModal.phase} — {showBillModal.title}</div>
            <div style={{background:T.goldBg,border:"1px solid "+T.gold+"40",borderRadius:9,padding:"14px",marginBottom:16,textAlign:"center"}}>
              <div style={{fontSize:11,color:T.muted,marginBottom:4}}>Invoice Amount ({showBillModal.billing_pct}% of {fmt(contractValue)})</div>
              <div style={{fontSize:28,fontWeight:800,color:T.gold,fontFamily:"'Space Mono',monospace"}}>{fmt(contractValue*(showBillModal.billing_pct||0)/100)}</div>
            </div>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Invoice Notes (optional)</div>
            <textarea value={invoiceNote} onChange={e=>setInvoiceNote(e.target.value)}
              placeholder="Any notes to add to this invoice..." rows={2}
              style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"9px 12px",color:T.text,fontSize:12,fontFamily:"inherit",resize:"none",marginBottom:14}}/>
            <div style={{display:"flex",gap:8}}>
              <Btn ghost onClick={()=>setShowBillModal(null)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
              <Btn primary onClick={()=>{
                const invNum="INV-"+proj?.id+"-"+showBillModal.id.toUpperCase()+"-"+new Date().getFullYear();
                updateMilestone(showBillModal.id,{billed:true,billed_date:new Date().toLocaleDateString(),invoice_number:invNum});
                generateInvoicePDF(showBillModal);
                setShowBillModal(null);
              }} style={{flex:2,justifyContent:"center",background:T.amber,color:T.bg0}}>
                💵 Generate & Export Invoice PDF
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* Notes */}
      {data.generated&&data.notes&&(
        <Card style={{marginTop:14,border:"1px solid "+T.purple+"30",background:T.purpleBg}}>
          <div style={{fontSize:11,fontWeight:700,color:T.purple,marginBottom:6}}>📋 Special Notes for This Project Type</div>
          <div style={{fontSize:12,color:T.muted,lineHeight:1.7}}>{data.notes}</div>
        </Card>
      )}
    </div>
  );
}


// ─── PUNCH LIST WITH BLUEPRINT PHOTO PINS ────────────────────────────────────
function PunchListView({proj, session}) {
  const storageKey = "grok_punch_" + proj?.id;
  const isAdmin = session?.isAdmin || session?.plan === "owner";

  const [items, setItems] = useState(()=>{
    try{const s=localStorage.getItem(storageKey);if(s)return JSON.parse(s);}catch(e){}
    return [];
  });
  const [blueprints, setBlueprints] = useState(()=>{
    try{const s=localStorage.getItem("grok_blueprints_"+proj?.id);if(s)return JSON.parse(s);}catch(e){}
    return [];
  });
  const [activeBlueprint, setActiveBlueprint] = useState(null);
  const [mode, setMode] = useState("blueprint"); // blueprint | list
  const [placingPin, setPlacingPin] = useState(false);
  const [newPin, setNewPin] = useState(null);
  const [newItem, setNewItem] = useState({title:"",trade:"",assignee:"",priority:"medium",notes:""});
  const [selected, setSelected] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [showFixForm, setShowFixForm] = useState(null); // item id
  const fileRef = React.useRef();
  const fixFileRef = React.useRef();
  const imgRef = React.useRef();

  React.useEffect(()=>{
    if(blueprints.length>0&&!activeBlueprint) setActiveBlueprint(blueprints[0]);
  },[blueprints]);

  const save = (updated) => {
    setItems(updated);
    try{localStorage.setItem(storageKey,JSON.stringify(updated));}catch(e){}
  };

  const uploadBlueprint = (file) => {
    if(!file)return;
    const reader = new FileReader();
    reader.onload = e => {
      const bp = {id:"bp_"+Date.now(),name:file.name,dataUrl:e.target.result};
      const updated = [...blueprints,bp];
      setBlueprints(updated);
      setActiveBlueprint(bp);
      try{localStorage.setItem("grok_blueprints_"+proj?.id,JSON.stringify(updated));}catch(ex){}
    };
    reader.readAsDataURL(file);
  };

  const handleBlueprintClick = (e) => {
    if(!placingPin||!activeBlueprint)return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX-rect.left)/rect.width*100).toFixed(1);
    const y = ((e.clientY-rect.top)/rect.height*100).toFixed(1);
    setNewPin({x:parseFloat(x),y:parseFloat(y),blueprintId:activeBlueprint.id});
    setPlacingPin(false);
  };

  const addItem = () => {
    if(!newItem.title)return;
    const item = {
      ...newItem,
      id:"punch_"+Date.now(),
      pin: newPin,
      status:"open",
      created: new Date().toISOString(),
      created_by: session?.ownerName||"GC",
      photo_issue: null,
      photo_fix: null,
      fix_notes: "",
      fix_date: null,
      fix_by: null,
    };
    save([...items, item]);
    setNewPin(null);
    setNewItem({title:"",trade:"",assignee:"",priority:"medium",notes:""});
    setSelected(item.id);
  };

  const uploadPhoto = (itemId, field, file) => {
    if(!file)return;
    const reader = new FileReader();
    reader.onload = e => {
      const updated = items.map(i=>i.id===itemId?{...i,[field]:e.target.result}:i);
      save(updated);
    };
    reader.readAsDataURL(file);
  };

  const markFixed = (itemId, fixNotes, fixPhoto) => {
    const updated = items.map(i=>i.id===itemId?{
      ...i,
      status:"fixed",
      fix_notes: fixNotes,
      photo_fix: fixPhoto||i.photo_fix,
      fix_date: new Date().toISOString(),
      fix_by: session?.ownerName||"Sub",
    }:i);
    save(updated);
    setShowFixForm(null);
  };

  const approveFixed = (itemId) => {
    const updated = items.map(i=>i.id===itemId?{...i,status:"closed",closed_date:new Date().toISOString(),closed_by:session?.ownerName}:i);
    save(updated);
  };

  const bpItems = items.filter(i=>i.pin?.blueprintId===activeBlueprint?.id);
  const openCount = items.filter(i=>i.status==="open").length;
  const fixedCount = items.filter(i=>i.status==="fixed").length;
  const closedCount = items.filter(i=>i.status==="closed").length;

  const PRIORITY = {
    high:   {color:T.red,    bg:T.redBg,    label:"High"},
    medium: {color:T.amber,  bg:T.amberBg,  label:"Medium"},
    low:    {color:T.muted,  bg:T.bg3,      label:"Low"},
  };
  const STATUS = {
    open:   {color:T.red,    bg:T.redBg,    label:"Open",   icon:"🔴"},
    fixed:  {color:T.amber,  bg:T.amberBg,  label:"Fixed — Pending Approval", icon:"🟡"},
    closed: {color:T.green,  bg:T.greenBg,  label:"Closed", icon:"🟢"},
  };

  const TRADES = ["Plumbing","Electrical","HVAC","Framing","Drywall","Tile","Flooring","Paint","Cabinets","Doors","General","Other"];

  return (
    <div>
      {/* Header stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:8,marginBottom:12}}>
        {[
          {l:"Total Items", v:items.length,  c:T.text},
          {l:"Open",        v:openCount,     c:openCount>0?T.red:T.muted},
          {l:"Fixed — Review", v:fixedCount, c:fixedCount>0?T.amber:T.muted},
          {l:"Closed ✓",    v:closedCount,   c:T.green},
        ].map(s=>(
          <div key={s.l} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 14px",textAlign:"center"}}>
            <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{s.l}</div>
            <div style={{fontSize:24,fontWeight:800,color:s.c,fontFamily:"'Space Mono',monospace"}}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* View toggle + Generate Doc */}
      <div style={{display:"flex",gap:8,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{display:"flex",gap:4,background:T.bg2,borderRadius:8,padding:3}}>
          {[["blueprint","📐 Blueprint View"],["list","📋 Punch List"]].map(([id,label])=>(
            <button key={id} onClick={()=>setMode(id)}
              style={{padding:"6px 16px",borderRadius:6,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:mode===id?T.gold:T.bg2,color:mode===id?T.bg0:T.muted,transition:"all 0.15s"}}>
              {label}
            </button>
          ))}
        </div>
        <button onClick={()=>{
          const cid = session?.companyId || window._grokCompanyId || "local";
          const templates = (() => { try { return JSON.parse(localStorage.getItem("grok_templates_"+cid)||"[]"); } catch { return []; }})();
          const tpl = templates.find(t=>t.id==="gcg_t13");
          if(tpl?.data){
            const a=document.createElement("a");
            a.href=tpl.data;
            const today = new Date().toLocaleDateString("en-US",{month:"2-digit",day:"2-digit",year:"numeric"}).replace(/\//g,"-");
            a.download=`GCG_${(proj?.name||"Project").split(" ")[0]}_Punch_List_${today}.docx`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
          } else {
            // Generate printable HTML punch list from current items
            const w = window.open("","_blank");
            const openItems = items.filter(i=>i.status==="open");
            const cs = (() => { try { return JSON.parse(localStorage.getItem("grok_company_settings_"+cid)||"{}"); } catch { return {}; }})();
            w.document.write(`<!DOCTYPE html><html><head><title>Punch List — ${proj?.name}</title>
            <style>body{font-family:Arial,sans-serif;max-width:720px;margin:32px auto;padding:24px;color:#1a1a1a;font-size:13px;}
            .banner{background:#0F1623;padding:20px 24px;}.brand{font-size:26px;font-weight:900;color:#D4A843;}.brand-sub{font-size:12px;color:#fff;margin-top:3px;}
            .gold{height:4px;background:#D4A843;margin-bottom:20px;}
            table{width:100%;border-collapse:collapse;margin:12px 0;}
            th{background:#0F1623;color:#fff;padding:8px 10px;text-align:left;font-size:11px;}
            td{padding:8px 10px;border-bottom:1px solid #e0e0e0;font-size:12px;vertical-align:top;}
            tr:nth-child(even) td{background:#f9f9f9;}
            .sig{display:grid;grid-template-columns:1fr 1fr;gap:32px;margin-top:32px;}
            .sig-line{border-top:2px solid #0F1623;margin-top:40px;padding-top:6px;font-size:11px;color:#888;}
            @media print{body{margin:0;padding:16px;}}</style></head><body>
            <div class="banner"><div class="brand">GROK</div><div class="brand-sub">${cs.company_name||"Grok Construction Group Inc"} · ${cs.cslb||"CSLB #1152750"}</div></div>
            <div class="gold"></div>
            <div style="font-size:18px;font-weight:800;color:#0F1623;margin-bottom:4px;">Final Punch List</div>
            <div style="font-size:12px;color:#888;margin-bottom:4px;">${proj?.name||"Project"} · ${proj?.address||proj?.addr||""}</div>
            <div style="font-size:12px;color:#888;margin-bottom:20px;">Date: ${new Date().toLocaleDateString()} · ${openItems.length} open items · ${closedCount} closed</div>
            <table>
              <tr><th>Trade</th><th>Item</th><th>Priority</th><th>Status</th><th>Notes</th></tr>
              ${items.map(i=>`<tr><td>${i.trade||"General"}</td><td>${i.title||""}</td><td>${i.priority||"medium"}</td><td style="color:${i.status==="closed"?"#166534":i.status==="fixed"?"#92400e":"#991b1b"}">${i.status||"open"}</td><td>${i.notes||""}</td></tr>`).join("")}
            </table>
            <div class="sig">
              <div><div style="font-size:12px;font-weight:700">Contractor — GCG</div><div class="sig-line">Signature · Date</div></div>
              <div><div style="font-size:12px;font-weight:700">Owner / Representative</div><div class="sig-line">Signature · Date</div></div>
            </div>
            <script>setTimeout(()=>window.print(),400)</script></body></html>`);
            w.document.close();
          }
        }} style={{padding:"6px 14px",borderRadius:8,border:`1px solid ${T.gold}`,background:T.goldBg||"#FDF6E3",color:T.gold,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
          📄 Generate Punch List Doc
        </button>
      </div>

      {/* BLUEPRINT VIEW */}
      {mode==="blueprint"&&(
        <div>
          {/* Blueprint selector + upload */}
          <div style={{display:"flex",gap:8,marginBottom:12,alignItems:"center",flexWrap:"wrap"}}>
            {blueprints.map(bp=>(
              <button key={bp.id} onClick={()=>setActiveBlueprint(bp)}
                style={{fontSize:11,fontWeight:600,padding:"5px 12px",borderRadius:7,border:"1px solid "+(activeBlueprint?.id===bp.id?T.gold:T.subtle),background:activeBlueprint?.id===bp.id?T.goldBg:T.bg2,color:activeBlueprint?.id===bp.id?T.gold:T.muted,cursor:"pointer"}}>
                {bp.name}
              </button>
            ))}
            <input ref={fileRef} type="file" accept="image/*,application/pdf,.pdf" style={{display:"none"}} onChange={e=>uploadBlueprint(e.target.files[0])}/>
            <Btn small ghost onClick={()=>fileRef.current.click()}><Ic d={ICONS.plus} size={11}/>Upload Blueprint</Btn>
            {activeBlueprint&&(
              <button onClick={()=>setPlacingPin(p=>!p)}
                style={{fontSize:11,fontWeight:700,padding:"5px 14px",borderRadius:7,border:"1px solid "+(placingPin?T.red:T.subtle),background:placingPin?T.redBg:T.bg2,color:placingPin?T.red:T.muted,cursor:"pointer",transition:"all 0.15s"}}>
                {placingPin?"🎯 Click Blueprint to Place Pin":"📍 Add Punch Item"}
              </button>
            )}
          </div>

          {/* New item form (appears after placing pin) */}
          {newPin&&(
            <Card style={{marginBottom:12,border:"1px solid "+T.red+"40",background:T.redBg}}>
              <div style={{fontSize:12,fontWeight:700,color:T.red,marginBottom:10}}>📍 New Punch Item — Pin placed at ({newPin.x}%, {newPin.y}%)</div>
              <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:8,marginBottom:8}}>
                {[["title","Issue Description *","Door doesn't close flush"],["trade","Trade","Doors"],["assignee","Assigned To","Tom Lee"]].map(([k,l,ph])=>(
                  <div key={k}>
                    <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>{l}</div>
                    {k==="trade"
                      ?<select value={newItem[k]} onChange={e=>setNewItem(p=>({...p,[k]:e.target.value}))}
                        style={{width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}>
                        {TRADES.map(t=><option key={t}>{t}</option>)}
                      </select>
                      :<input value={newItem[k]} onChange={e=>setNewItem(p=>({...p,[k]:e.target.value}))} placeholder={ph}
                        style={{width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
                    }
                  </div>
                ))}
                <div>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Priority</div>
                  <select value={newItem.priority} onChange={e=>setNewItem(p=>({...p,priority:e.target.value}))}
                    style={{width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}>
                    <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                  </select>
                </div>
              </div>
              <textarea value={newItem.notes} onChange={e=>setNewItem(p=>({...p,notes:e.target.value}))}
                placeholder="Additional notes..."
                rows={2} style={{width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit",resize:"none",marginBottom:10}}/>
              <div style={{display:"flex",gap:8}}>
                <Btn ghost small onClick={()=>setNewPin(null)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
                <Btn primary small disabled={!newItem.title} onClick={addItem} style={{flex:2,justifyContent:"center",background:T.red,color:"white"}}>Add Punch Item</Btn>
              </div>
            </Card>
          )}

          {/* Blueprint with pins */}
          {!activeBlueprint?(
            <div style={{textAlign:"center",padding:"60px",color:T.muted,background:T.bg2,borderRadius:12,border:"2px dashed "+T.subtle}}>
              <div style={{fontSize:40,opacity:0.3,marginBottom:12}}>📐</div>
              <div style={{fontSize:13,marginBottom:8}}>Upload a blueprint to start placing punch list pins</div>
              <Btn small primary onClick={()=>fileRef.current.click()}>Upload Blueprint</Btn>
            </div>
          ):(
            <div style={{position:"relative",display:"inline-block",width:"100%",cursor:placingPin?"crosshair":"default",borderRadius:12,overflow:"hidden",border:"1px solid "+T.subtle}}>
              <img ref={imgRef} src={activeBlueprint.dataUrl} alt="Blueprint"
                style={{width:"100%",display:"block",transform:"scale("+zoom+")",transformOrigin:"top left"}}
                onClick={handleBlueprintClick}/>
              {/* Pins */}
              {bpItems.map(item=>{
                const prio = PRIORITY[item.priority]||PRIORITY.medium;
                const stat = STATUS[item.status]||STATUS.open;
                return (
                  <div key={item.id}
                    onClick={e=>{e.stopPropagation();setSelected(selected===item.id?null:item.id);}}
                    style={{position:"absolute",left:item.pin.x+"%",top:item.pin.y+"%",transform:"translate(-50%,-100%)",cursor:"pointer",zIndex:10}}>
                    {/* Pin marker */}
                    <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
                      <div style={{background:prio.color,color:"white",borderRadius:"50% 50% 50% 0",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,border:"2px solid white",boxShadow:"0 2px 8px rgba(0,0,0,0.3)",transform:"rotate(-45deg) translateY(-2px)"}}>
                        <span style={{transform:"rotate(45deg)"}}>{stat.icon}</span>
                      </div>
                      <div style={{width:2,height:8,background:prio.color,borderRadius:1}}/>
                    </div>
                    {/* Popup */}
                    {selected===item.id&&(
                      <div style={{position:"absolute",bottom:"100%",left:"50%",transform:"translateX(-50%)",background:T.bg1,border:"1px solid "+prio.color+"40",borderRadius:10,padding:"12px 14px",minWidth:220,maxWidth:280,boxShadow:"0 4px 20px rgba(0,0,0,0.3)",zIndex:20,marginBottom:8}}>
                        <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:4}}>{item.title}</div>
                        <div style={{display:"flex",gap:6,marginBottom:8,flexWrap:"wrap"}}>
                          <span style={{fontSize:9,fontWeight:700,background:prio.bg,color:prio.color,borderRadius:10,padding:"2px 7px"}}>{prio.label}</span>
                          <span style={{fontSize:9,fontWeight:700,background:stat.bg,color:stat.color,borderRadius:10,padding:"2px 7px"}}>{stat.label}</span>
                          {item.trade&&<span style={{fontSize:9,color:T.muted,background:T.bg3,borderRadius:10,padding:"2px 7px"}}>{item.trade}</span>}
                        </div>
                        {item.assignee&&<div style={{fontSize:11,color:T.muted,marginBottom:6}}>Assigned: {item.assignee}</div>}
                        {item.notes&&<div style={{fontSize:11,color:T.muted,marginBottom:8,lineHeight:1.5}}>{item.notes}</div>}
                        {/* Issue photo */}
                        {item.photo_issue&&<img src={item.photo_issue} alt="Issue" style={{width:"100%",borderRadius:7,marginBottom:8,maxHeight:120,objectFit:"cover"}}/>}
                        {/* Fix photo */}
                        {item.photo_fix&&(
                          <div style={{marginBottom:8}}>
                            <div style={{fontSize:9,fontWeight:700,color:T.green,marginBottom:4}}>✓ FIX PHOTO</div>
                            <img src={item.photo_fix} alt="Fix" style={{width:"100%",borderRadius:7,maxHeight:100,objectFit:"cover"}}/>
                            {item.fix_notes&&<div style={{fontSize:10,color:T.muted,marginTop:4}}>{item.fix_notes}</div>}
                          </div>
                        )}
                        {/* Actions */}
                        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                          {item.status==="open"&&<button onClick={()=>setShowFixForm(item.id)}
                            style={{fontSize:10,fontWeight:700,background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:6,padding:"4px 9px",cursor:"pointer",color:T.amber}}>
                            ✓ Mark Fixed
                          </button>}
                          {item.status==="fixed"&&isAdmin&&<button onClick={()=>approveFixed(item.id)}
                            style={{fontSize:10,fontWeight:700,background:T.greenBg,border:"1px solid "+T.green+"30",borderRadius:6,padding:"4px 9px",cursor:"pointer",color:T.green}}>
                            ✅ Approve & Close
                          </button>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* FIX SUBMISSION FORM */}
      {showFixForm&&(
        <FixSubmitModal item={items.find(i=>i.id===showFixForm)} onClose={()=>setShowFixForm(null)} onSubmit={(notes,photo)=>markFixed(showFixForm,notes,photo)}/>
      )}
      
      {/* LIST VIEW */}
      {mode==="list"&&(
        <div>
          {items.length===0?(
            <div style={{textAlign:"center",padding:"60px",color:T.muted}}>
              <div style={{fontSize:40,opacity:0.3,marginBottom:12}}>☑</div>
              <div style={{fontSize:13}}>No punch list items yet</div>
              <div style={{fontSize:11,marginTop:4}}>Switch to Blueprint View to pin issues on the drawing</div>
            </div>
          ):["open","fixed","closed"].map(status=>{
            const statusItems = items.filter(i=>i.status===status);
            if(statusItems.length===0)return null;
            const st = STATUS[status];
            return (
              <div key={status} style={{marginBottom:16}}>
                <div style={{fontSize:11,fontWeight:800,color:st.color,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:8,display:"flex",alignItems:"center",gap:8}}>
                  {st.icon} {st.label} ({statusItems.length})
                </div>
                {statusItems.map(item=>{
                  const prio = PRIORITY[item.priority]||PRIORITY.medium;
                  return (
                    <div key={item.id} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"14px 16px",marginBottom:8}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                        <div style={{flex:1}}>
                          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>{item.title}</div>
                          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                            <span style={{fontSize:9,fontWeight:700,background:prio.bg,color:prio.color,borderRadius:10,padding:"2px 7px"}}>{prio.label}</span>
                            {item.trade&&<span style={{fontSize:9,color:T.muted,background:T.bg3,borderRadius:10,padding:"2px 7px"}}>{item.trade}</span>}
                            {item.assignee&&<span style={{fontSize:9,color:T.blue,background:T.blueBg,borderRadius:10,padding:"2px 7px"}}>→ {item.assignee}</span>}
                          </div>
                        </div>
                        <div style={{display:"flex",gap:6,flexShrink:0,marginLeft:12}}>
                          {item.status==="open"&&<button onClick={()=>setShowFixForm(item.id)}
                            style={{fontSize:10,fontWeight:700,background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:6,padding:"4px 9px",cursor:"pointer",color:T.amber}}>
                            ✓ Submit Fix
                          </button>}
                          {item.status==="fixed"&&isAdmin&&<button onClick={()=>approveFixed(item.id)}
                            style={{fontSize:10,fontWeight:700,background:T.greenBg,border:"1px solid "+T.green+"30",borderRadius:6,padding:"4px 9px",cursor:"pointer",color:T.green}}>
                            ✅ Approve
                          </button>}
                        </div>
                      </div>
                      {item.notes&&<div style={{fontSize:11,color:T.muted,marginBottom:8,lineHeight:1.5}}>{item.notes}</div>}
                      {/* Photos side by side */}
                      {(item.photo_issue||item.photo_fix)&&(
                        <div style={{display:"grid",gridTemplateColumns:item.photo_issue&&item.photo_fix?"1fr 1fr":"1fr",gap:8,marginBottom:8}}>
                          {item.photo_issue&&<div>
                            <div style={{fontSize:9,fontWeight:700,color:T.red,marginBottom:4}}>ISSUE PHOTO</div>
                            <img src={item.photo_issue} alt="Issue" style={{width:"100%",borderRadius:8,maxHeight:140,objectFit:"cover"}}/>
                          </div>}
                          {item.photo_fix&&<div>
                            <div style={{fontSize:9,fontWeight:700,color:T.green,marginBottom:4}}>FIX PHOTO ✓</div>
                            <img src={item.photo_fix} alt="Fix" style={{width:"100%",borderRadius:8,maxHeight:140,objectFit:"cover"}}/>
                            {item.fix_notes&&<div style={{fontSize:10,color:T.muted,marginTop:4,lineHeight:1.5}}>{item.fix_notes}</div>}
                            {item.fix_by&&<div style={{fontSize:9,color:T.muted,marginTop:2}}>Fixed by {item.fix_by} on {new Date(item.fix_date).toLocaleDateString()}</div>}
                          </div>}
                        </div>
                      )}
                      {item.pin&&<div style={{fontSize:9,color:T.muted}}>📍 Pinned on: {blueprints.find(b=>b.id===item.pin.blueprintId)?.name||"Blueprint"}</div>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ─── PROJECT DOCUMENT & PAYMENT SEQUENCE ─────────────────────────────────────
function ProjectDocSequence({proj, session, singleSub}) {
  const storageKey = "grok_docseq_" + proj?.id;

  const [subs, setSubs] = useState(()=>{
    if(singleSub){
      const key2 = storageKey+"_"+singleSub.id;
      try{const s=localStorage.getItem(key2);if(s)return JSON.parse(s);}catch(e){}
      return [{...singleSub,id:singleSub.id||"sub_"+Date.now(),steps:{},totalPaid:0,invoices:[],rate:singleSub.rate||"",isCorpOrLLC:false}];
    }
    try{const s=localStorage.getItem(storageKey);if(s)return JSON.parse(s);}catch(e){}
    return [];
  });

  const [showAddSub, setShowAddSub] = useState(false);
  const [newSub, setNewSub] = useState({name:"",trade:"",company:"",phone:"",email:"",rate:"",isCorpOrLLC:false});
  const [expandedSub, setExpandedSub] = useState(null);
  const [showW9Info, setShowW9Info] = useState(false);

  const save = (updated) => {
    setSubs(updated);
    if(singleSub){
      const key2 = storageKey+"_"+singleSub.id;
      try{localStorage.setItem(key2,JSON.stringify(updated));}catch(e){}
    } else {
      try{localStorage.setItem(storageKey,JSON.stringify(updated));}catch(e){}
    }
  };

  // Document steps each sub needs to complete
  const DOC_STEPS = [
    {
      id:"w9", phase:"BEFORE WORK", priority:"critical",
      title:"W-9 Form",
      rule:"Required BEFORE first payment. Needed to issue 1099-NEC if you pay them $600+ in the year.",
      when:"Day 1 — before signing contract or any payment",
      exempt:"Corps (Inc.) and LLCs taxed as S-Corp are usually exempt from 1099 but still collect W-9",
      action:"request", actionLabel:"Request W-9",
      template:"Send email asking for completed W-9. They fill it out, sign, send back.",
      icon:"📋",
    },
    {
      id:"coi", phase:"BEFORE WORK", priority:"critical",
      title:"Certificate of Insurance (COI)",
      rule:"Must show: General Liability ($1M min), Workers Comp, you named as Additional Insured.",
      when:"Before they set foot on site",
      exempt:"Sole proprietors with no employees — WC may not apply but GL still required",
      action:"request", actionLabel:"Request COI",
      icon:"🛡",
    },
    {
      id:"dir", phase:"BEFORE WORK", priority:"conditional",
      title:"DIR Registration Check",
      rule:"Required on ALL public works / prevailing wage jobs. Check dir.ca.gov/pwc before awarding.",
      when:"Before award on any MOHCD, HUD, or public-funded project",
      exempt:"Private residential projects under $15K — not required",
      action:"verify", actionLabel:"Verify DIR #",
      icon:"🏛",
    },
    {
      id:"contract", phase:"BEFORE WORK", priority:"critical",
      title:"Subcontract Agreement",
      rule:"Written contract required. Includes: scope, price, schedule, insurance requirements, lien waiver terms, CO process.",
      when:"Before mobilization",
      exempt:"None — always get a signed contract",
      action:"send", actionLabel:"Send Contract",
      icon:"📝",
    },
    {
      id:"cond_lien_1", phase:"PROGRESS PAYMENTS", priority:"critical",
      title:"Conditional Lien Waiver — Progress",
      rule:"Exchange with EVERY progress payment. Conditional = waives lien rights upon receipt and clearing of payment.",
      when:"With each pay application / invoice",
      exempt:"None — do this every time",
      action:"request", actionLabel:"Request with Pay App",
      icon:"🔗",
    },
    {
      id:"invoice", phase:"PROGRESS PAYMENTS", priority:"required",
      title:"Sub Invoice / Pay Application",
      rule:"Review before paying: verify work completed, match to contract schedule of values, check math.",
      when:"Monthly or per milestone",
      exempt:"N/A",
      action:"review", actionLabel:"Mark Invoice Reviewed",
      icon:"💵",
    },
    {
      id:"uncond_lien", phase:"FINAL PAYMENT", priority:"critical",
      title:"Unconditional Lien Waiver — Final",
      rule:"MUST have in hand BEFORE releasing final check. Unconditional = permanently waives all lien rights.",
      when:"Exchange for final payment check — do not pay without this",
      exempt:"None — this is your protection against future liens",
      action:"request", actionLabel:"Request Final Waiver",
      icon:"✅",
    },
    {
      id:"punch", phase:"FINAL PAYMENT", priority:"required",
      title:"Punch List Sign-Off",
      rule:"Sub confirms all work complete, all deficiencies corrected, no open items.",
      when:"Before final payment",
      exempt:"N/A",
      action:"complete", actionLabel:"Mark Complete",
      icon:"☑",
    },
    {
      id:"1099", phase:"YEAR END", priority:"conditional",
      title:"1099-NEC Filing",
      rule:"If you paid sub $600+ in the calendar year, you MUST file 1099-NEC by Jan 31. W-9 has the info you need.",
      when:"January — file with IRS and send copy to sub",
      exempt:"Corporations (Inc., S-Corp, C-Corp) — not required. LLCs may still need it.",
      action:"file", actionLabel:"Mark Filed",
      icon:"📊",
    },
  ];

  const PHASES = ["BEFORE WORK","PROGRESS PAYMENTS","FINAL PAYMENT","YEAR END"];
  const PHASE_COLORS = {"BEFORE WORK":T.blue,"PROGRESS PAYMENTS":T.gold,"FINAL PAYMENT":T.green,"YEAR END":T.purple};

  const toggleStep = (subId, stepId) => {
    const updated = subs.map(s=>s.id===subId ? {
      ...s,
      steps: {...s.steps, [stepId]: !s.steps?.[stepId]}
    } : s);
    save(updated);
  };

  const addSub = () => {
    if (!newSub.name) return;
    const sub = {
      ...newSub,
      id: "sub_"+Date.now(),
      addedDate: new Date().toLocaleDateString(),
      steps: {},
      totalPaid: 0,
      invoices: [],
    };
    save([...subs, sub]);
    setNewSub({name:"",trade:"",company:"",phone:"",email:"",rate:"",isCorpOrLLC:false});
    setShowAddSub(false);
    setExpandedSub(sub.id);
  };

  const addInvoice = (subId, invoice) => {
    const updated = subs.map(s=>s.id===subId ? {
      ...s,
      totalPaid: (s.totalPaid||0) + (invoice.amount||0),
      invoices: [...(s.invoices||[]), {...invoice, id:"inv_"+Date.now(), date:new Date().toLocaleDateString()}]
    } : s);
    save(updated);
  };

  const getSubStatus = (sub) => {
    const critical = DOC_STEPS.filter(s=>s.priority==="critical");
    const done = critical.filter(s=>sub.steps?.[s.id]);
    if (done.length===0) return {label:"Not Started",color:T.muted};
    if (done.length<critical.length) return {label:`${done.length}/${critical.length} Critical Docs`,color:T.amber};
    return {label:"Fully Compliant",color:T.green};
  };

  const needs1099 = (sub) => (sub.totalPaid||0) >= 600 && !sub.isCorpOrLLC;

  // Get next required action for a sub
  const getNextAction = (sub) => {
    for (const step of DOC_STEPS) {
      if (step.priority==="critical" && !sub.steps?.[step.id]) {
        return step;
      }
    }
    return null;
  };

  const generateW9Email = (sub) => {
    const subject = encodeURIComponent(`W-9 Request — ${proj?.name}`);
    const body = encodeURIComponent(`Hi ${sub.name},

I'm reaching out regarding your work on ${proj?.name}.

Before we can process any payments, I need a completed W-9 form on file. This is required for tax purposes.

Please complete and return your W-9 at your earliest convenience. You can download a blank W-9 at:
https://www.irs.gov/pub/irs-pdf/fw9.pdf

Once completed, please email the signed form back to ${session?.email||"Jehoon@gcginc.com"}.

Thank you,
${session?.ownerName||"Jehoon Chun"}
${session?.companyName||"Grok Construction Group Inc"}
CSLB #${session?.cslb||"1152750"}
${session?.email||"Jehoon@gcginc.com"}`);
    return `mailto:${sub.email||""}?subject=${subject}&body=${body}`;
  };

  const generateLienWaiverEmail = (sub, type="conditional") => {
    const subject = encodeURIComponent(`${type==="conditional"?"Conditional":"Unconditional"} Lien Waiver Request — ${proj?.name}`);
    const body = encodeURIComponent(`Hi ${sub.name},

Please complete and return the attached ${type} lien waiver for ${proj?.name}.

${type==="conditional"?
`This is a CONDITIONAL waiver, which takes effect upon receipt and clearance of your payment check.`:
`This is an UNCONDITIONAL waiver. Please note this permanently waives your lien rights for all work completed to date. Do not sign until your final payment has fully cleared.`}

Return signed copy before payment can be released.

Thank you,
${session?.ownerName||"Jehoon Chun"}
${session?.companyName||"Grok Construction Group Inc"}
${session?.email||"Jehoon@gcginc.com"}`);
    return `mailto:${sub.email||""}?subject=${subject}&body=${body}`;
  };

  const fmt = v => "$"+Math.round(v||0).toLocaleString();

  return (
    <div>
      {!singleSub&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>Sub Compliance & Payment Sequence</div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>Track W-9s, contracts, lien waivers, invoices and 1099s per sub per project</div>
        </div>
        <div style={{display:"flex",gap:7}}>
          <button onClick={()=>setShowW9Info(p=>!p)}
            style={{fontSize:11,fontWeight:600,background:T.amberBg,border:`1px solid ${T.amber}30`,borderRadius:7,padding:"5px 12px",cursor:"pointer",color:T.amber}}>
            📚 W-9 Rules
          </button>
          <Btn small primary onClick={()=>setShowAddSub(true)}><Ic d={ICONS.plus} size={11}/>Add Sub</Btn>
        </div>
      </div>}
      {singleSub&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 16px 4px"}}>
        <div style={{fontSize:12,fontWeight:700,color:T.text}}>📋 {singleSub.name} — Compliance & Payments</div>
        <button onClick={()=>setShowW9Info(p=>!p)} style={{fontSize:10,fontWeight:600,background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:6,padding:"3px 9px",cursor:"pointer",color:T.amber}}>Rules</button>
      </div>}

      {/* W-9 Info Panel */}
      {showW9Info&&(
        <Card style={{marginBottom:14,border:`1px solid ${T.amber}30`,background:T.amberBg}}>
          <div style={{fontSize:12,fontWeight:700,color:T.amber,marginBottom:10}}>📋 W-9 & 1099 Rules — When You Need What</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,fontSize:12}}>
            {[
              {q:"When do I need a W-9?",a:"BEFORE the first payment. Collect it when you award the subcontract. If they refuse, you may need to withhold 24% backup withholding."},
              {q:"Who is exempt from 1099?",a:"Corporations (Inc., S-Corp, C-Corp). LLCs taxed as a corporation are also exempt. Sole props and single-member LLCs GET the 1099."},
              {q:"What if I forget and already paid them?",a:"Still collect the W-9 and file the 1099 anyway. Late is better than never — IRS penalty is $50-$280 per form."},
              {q:"$600 rule — how does it work?",a:"If total payments to one sub in a calendar year = $600 or more, you must file 1099-NEC. Per sub, per year — not per project."},
              {q:"Conditional vs Unconditional lien waiver?",a:"Conditional: takes effect when check clears — safe to exchange before payment clears. Unconditional: immediate effect — only sign/request AFTER payment clears."},
              {q:"Can a sub file a lien if they have a waiver?",a:"If they signed an unconditional waiver — generally no. That's your protection. Always get unconditional before final check."},
            ].map((item,i)=>(
              <div key={i} style={{background:"rgba(0,0,0,0.08)",borderRadius:8,padding:"10px 12px"}}>
                <div style={{fontWeight:700,color:T.amber,marginBottom:4,fontSize:11}}>{item.q}</div>
                <div style={{color:T.text,lineHeight:1.6,fontSize:11}}>{item.a}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Add Sub Form */}
      {!singleSub&&showAddSub&&(
        <Card style={{marginBottom:14,border:`1px solid ${T.blue}30`}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:12}}>Add Subcontractor to This Project</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            {[["name","Name *","Tom Rivera"],["trade","Trade *","Electrician"],["company","Company","Bay Electric Inc"],["phone","Phone","(415) 555-0100"],["email","Email","tom@bayelectric.com"],["rate","Contract Amount $","18500"]].map(([k,l,ph])=>(
              <div key={k}>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{l}</div>
                <input value={newSub[k]} onChange={e=>setNewSub(p=>({...p,[k]:e.target.value}))} placeholder={ph}
                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
              </div>
            ))}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,padding:"10px 12px",background:T.bg2,borderRadius:8}}>
            <input type="checkbox" checked={newSub.isCorpOrLLC} onChange={e=>setNewSub(p=>({...p,isCorpOrLLC:e.target.checked}))} id="corp_check" style={{width:16,height:16,cursor:"pointer"}}/>
            <label htmlFor="corp_check" style={{fontSize:12,color:T.muted,cursor:"pointer"}}>
              This sub is incorporated (Inc., Corp., S-Corp, or LLC taxed as corporation) — <strong style={{color:T.text}}>1099 not required</strong>
            </label>
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn ghost onClick={()=>setShowAddSub(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
            <Btn primary disabled={!newSub.name||!newSub.trade} onClick={addSub} style={{flex:2,justifyContent:"center"}}>Add Sub + Start Checklist</Btn>
          </div>
        </Card>
      )}

      {/* Sub list */}
      {subs.length===0&&!singleSub?(
        <div style={{textAlign:"center",padding:"48px",color:T.muted}}>
          <div style={{fontSize:32,marginBottom:12,opacity:0.4}}>📋</div>
          <div style={{fontSize:13,marginBottom:4}}>No subs added yet</div>
          <div style={{fontSize:11,lineHeight:1.6}}>Add your subcontractors to track W-9s, contracts,<br/>lien waivers, invoices, and 1099 compliance.</div>
        </div>
      ):subs.map(sub=>{
        const status = getSubStatus(sub);
        const nextAction = getNextAction(sub);
        const isExpanded = expandedSub===sub.id;
        const paidAmount = sub.totalPaid||0;
        const contractAmount = parseFloat(sub.rate)||0;
        const paidPct = contractAmount>0?Math.min(100,paidAmount/contractAmount*100):0;

        return (
          <div key={sub.id} style={{marginBottom:10}}>
            {/* Sub header card */}
            <div onClick={()=>setExpandedSub(isExpanded?null:sub.id)}
              style={{background:T.bg1,border:`1px solid ${isExpanded?T.gold:T.subtle}`,borderRadius:isExpanded?"12px 12px 0 0":12,padding:"14px 16px",cursor:"pointer",transition:"all 0.15s"}}
              onMouseEnter={e=>{if(!isExpanded)e.currentTarget.style.borderColor=T.goldDim;}}
              onMouseLeave={e=>{if(!isExpanded)e.currentTarget.style.borderColor=T.subtle;}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
                    <div style={{width:32,height:32,borderRadius:9,background:T.teal+"18",border:`1px solid ${T.teal}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:T.teal}}>
                      {sub.trade?.slice(0,3).toUpperCase()}
                    </div>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:T.text}}>{sub.name}</div>
                      <div style={{fontSize:11,color:T.muted}}>{sub.trade}{sub.company?` · ${sub.company}`:""}</div>
                    </div>
                    {sub.isCorpOrLLC&&<span style={{fontSize:9,fontWeight:700,background:T.blueBg,color:T.blue,borderRadius:10,padding:"2px 7px"}}>CORP/LLC — No 1099</span>}
                  </div>
                  {/* Progress bar */}
                  {contractAmount>0&&(
                    <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6}}>
                      <div style={{flex:1,height:4,background:T.bg3,borderRadius:2,overflow:"hidden"}}>
                        <div style={{height:"100%",width:paidPct+"%",background:T.green,borderRadius:2}}/>
                      </div>
                      <span style={{fontSize:10,color:T.muted,whiteSpace:"nowrap"}}>{fmt(paidAmount)} / {fmt(contractAmount)} paid</span>
                    </div>
                  )}
                </div>
                <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6,marginLeft:12}}>
                  <span style={{fontSize:10,fontWeight:700,background:status.color+"18",color:status.color,borderRadius:10,padding:"3px 9px"}}>{status.label}</span>
                  {nextAction&&(
                    <span style={{fontSize:10,color:T.amber}}>⚡ Next: {nextAction.title}</span>
                  )}
                  {needs1099(sub)&&!sub.steps?.["1099"]&&(
                    <span style={{fontSize:10,color:T.red,fontWeight:600}}>⚠ 1099 required ({fmt(paidAmount)} paid)</span>
                  )}
                </div>
              </div>
            </div>

            {/* Expanded checklist */}
            {isExpanded&&(
              <div style={{background:T.bg2,border:`1px solid ${T.gold}`,borderTop:"none",borderRadius:"0 0 12px 12px",padding:"0 16px 16px"}}>

                {/* Quick contact actions */}
                <div style={{display:"flex",gap:6,padding:"12px 0",borderBottom:`1px solid ${T.subtle}`,marginBottom:12,flexWrap:"wrap"}}>
                  {sub.email&&<>
                    <a href={generateW9Email(sub)} style={{fontSize:11,fontWeight:600,background:T.amberBg,color:T.amber,border:`1px solid ${T.amber}30`,borderRadius:7,padding:"5px 10px",cursor:"pointer",textDecoration:"none"}}>📧 Request W-9</a>
                    <a href={generateLienWaiverEmail(sub,"conditional")} style={{fontSize:11,fontWeight:600,background:T.blueBg,color:T.blue,border:`1px solid ${T.blue}30`,borderRadius:7,padding:"5px 10px",cursor:"pointer",textDecoration:"none"}}>🔗 Request Cond. Lien Waiver</a>
                    <a href={generateLienWaiverEmail(sub,"unconditional")} style={{fontSize:11,fontWeight:600,background:T.greenBg,color:T.green,border:`1px solid ${T.green}30`,borderRadius:7,padding:"5px 10px",cursor:"pointer",textDecoration:"none"}}>✅ Request Final Lien Waiver</a>
                  </>}
                  {sub.phone&&<a href={`tel:${sub.phone}`} style={{fontSize:11,fontWeight:600,background:T.bg3,color:T.muted,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"5px 10px",textDecoration:"none"}}>📞 {sub.phone}</a>}
                </div>

                {/* Document checklist by phase */}
                {PHASES.map(phase=>{
                  const phaseSteps = DOC_STEPS.filter(s=>s.phase===phase);
                  const phaseColor = PHASE_COLORS[phase];
                  const phaseDone = phaseSteps.filter(s=>sub.steps?.[s.id]).length;
                  return (
                    <div key={phase} style={{marginBottom:12}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                        <div style={{height:2,flex:1,background:phaseColor+"30",borderRadius:1}}/>
                        <span style={{fontSize:9,fontWeight:800,color:phaseColor,textTransform:"uppercase",letterSpacing:"0.1em"}}>{phase}</span>
                        <span style={{fontSize:9,color:T.muted}}>{phaseDone}/{phaseSteps.length}</span>
                        <div style={{height:2,flex:1,background:phaseColor+"30",borderRadius:1}}/>
                      </div>
                      {phaseSteps.map(step=>{
                        const done = sub.steps?.[step.id];
                        const isConditional = step.priority==="conditional";
                        return (
                          <div key={step.id} style={{display:"flex",gap:10,alignItems:"flex-start",padding:"9px 10px",borderRadius:9,marginBottom:4,background:done?T.greenBg:T.bg1,border:`1px solid ${done?T.green+"30":step.priority==="critical"&&!done?T.red+"20":T.subtle}`,transition:"all 0.2s"}}>
                            <button onClick={()=>toggleStep(sub.id,step.id)}
                              style={{width:22,height:22,borderRadius:6,background:done?T.green:T.bg3,border:`2px solid ${done?T.green:step.priority==="critical"?T.red+"50":T.subtle}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1,transition:"all 0.2s"}}>
                              {done&&<span style={{color:"white",fontSize:12,lineHeight:1}}>✓</span>}
                            </button>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                                <span style={{fontSize:13}}>{step.icon}</span>
                                <span style={{fontSize:12,fontWeight:700,color:done?T.green:T.text,textDecoration:done?"line-through":"none"}}>{step.title}</span>
                                {step.priority==="critical"&&!done&&<span style={{fontSize:9,fontWeight:700,background:T.redBg,color:T.red,borderRadius:10,padding:"1px 6px"}}>REQUIRED</span>}
                                {isConditional&&<span style={{fontSize:9,color:T.muted,background:T.bg3,borderRadius:10,padding:"1px 6px"}}>IF APPLICABLE</span>}
                              </div>
                              <div style={{fontSize:11,color:T.muted,lineHeight:1.5,marginBottom:3}}>{step.rule}</div>
                              <div style={{fontSize:10,color:done?T.green:T.amber,fontWeight:600}}>⏱ {step.when}</div>
                              {step.exempt&&<div style={{fontSize:10,color:T.muted,marginTop:2,fontStyle:"italic"}}>Exception: {step.exempt}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}

                {/* Invoice log */}
                <div style={{borderTop:`1px solid ${T.subtle}`,paddingTop:12,marginTop:4}}>
                  <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:8}}>Payment Log</div>
                  <InvoiceLogger sub={sub} onAdd={(inv)=>addInvoice(sub.id,inv)} fmt={fmt}/>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function InvoiceLogger({sub, onAdd, fmt}) {
  const [showAdd, setShowAdd] = useState(false);
  const [newInv, setNewInv] = useState({description:"",amount:"",period:"",lienWaiverReceived:false,paid:false});

  const add = () => {
    if (!newInv.amount) return;
    onAdd({...newInv, amount:parseFloat(newInv.amount)||0});
    setNewInv({description:"",amount:"",period:"",lienWaiverReceived:false,paid:false});
    setShowAdd(false);
  };

  const invoices = sub.invoices||[];
  const totalPaid = invoices.filter(i=>i.paid).reduce((s,i)=>s+i.amount,0);
  const totalPending = invoices.filter(i=>!i.paid).reduce((s,i)=>s+i.amount,0);

  return (
    <div>
      {invoices.length>0&&(
        <div style={{display:"flex",gap:16,marginBottom:10,fontSize:11}}>
          <span style={{color:T.green,fontWeight:700}}>✓ Paid: {fmt(totalPaid)}</span>
          {totalPending>0&&<span style={{color:T.amber,fontWeight:700}}>⏳ Pending: {fmt(totalPending)}</span>}
          {sub.rate&&<span style={{color:T.muted}}>Contract: {fmt(parseFloat(sub.rate)||0)}</span>}
        </div>
      )}
      {invoices.map((inv,i)=>(
        <div key={inv.id||i} style={{display:"flex",gap:10,alignItems:"center",padding:"7px 10px",background:inv.paid?T.greenBg:T.bg2,borderRadius:7,marginBottom:4,border:`1px solid ${inv.paid?T.green+"30":T.subtle}`}}>
          <span style={{fontSize:11,color:inv.paid?T.green:T.amber,fontWeight:700,flexShrink:0}}>{inv.paid?"✓ PAID":"⏳ PENDING"}</span>
          <span style={{fontSize:11,color:T.muted,flex:1}}>{inv.description||"Invoice"} {inv.period?`· ${inv.period}`:""} · {inv.date}</span>
          {inv.lienWaiverReceived&&<span style={{fontSize:9,color:T.teal,fontWeight:700}}>🔗 Waiver ✓</span>}
          <span style={{fontSize:12,fontWeight:700,color:T.text,fontFamily:"'Space Mono',monospace",flexShrink:0}}>{fmt(inv.amount)}</span>
        </div>
      ))}
      {showAdd?(
        <div style={{background:T.bg2,borderRadius:8,padding:"12px",border:`1px solid ${T.subtle}`,marginTop:8}}>
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:8,marginBottom:8}}>
            {[["description","Description","Rough electrical complete"],["amount","Amount $","9250"],["period","Period","Apr 1-15"]].map(([k,l,ph])=>(
              <div key={k}>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>{l}</div>
                <input value={newInv[k]} onChange={e=>setNewInv(p=>({...p,[k]:e.target.value}))} placeholder={ph}
                  style={{width:"100%",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"6px 8px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:16,marginBottom:10}}>
            {[["lienWaiverReceived","Lien waiver received with this invoice"],["paid","Payment sent/cleared"]].map(([k,l])=>(
              <label key={k} style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",fontSize:12,color:T.muted}}>
                <input type="checkbox" checked={newInv[k]} onChange={e=>setNewInv(p=>({...p,[k]:e.target.checked}))} style={{width:14,height:14}}/>
                {l}
              </label>
            ))}
          </div>
          <div style={{display:"flex",gap:7}}>
            <Btn ghost small onClick={()=>setShowAdd(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
            <Btn primary small disabled={!newInv.amount} onClick={add} style={{flex:2,justifyContent:"center"}}>Log Invoice</Btn>
          </div>
        </div>
      ):(
        <button onClick={()=>setShowAdd(true)}
          style={{width:"100%",padding:"8px",background:"transparent",border:`1px dashed ${T.subtle}`,borderRadius:7,fontSize:11,color:T.muted,cursor:"pointer",marginTop:6}}>
          + Log Invoice / Payment
        </button>
      )}
    </div>
  );
}


// ─── FIELD DASHBOARD (inserted into ProjectDetail tab render) ─────────────────
// Rendered when tab==="field"
// Uses: proj, projCOs, projRFIs, SUB_PORTAL_DATA, flags (blueprint flags)
function FieldDashboard({proj, projCOs, projRFIs, setTab}) {
  const [matFilter, setMatFilter] = useState("all");
  const [materials, setMaterials] = useState(proj.materials || [
    {id:1,name:"LVP Flooring",qty:"800 sqft",vendor:"Home Depot",status:"ordered",orderDate:"Apr 18",eta:"Apr 28",priority:"high",cost:2400,notes:"Pending delivery — confirm before demo starts"},
    {id:2,name:"Shower tile 4x12 subway",qty:"240 sqft",vendor:"Bedrosians",status:"pending",orderDate:"",eta:"",priority:"high",cost:960,notes:"Owner to select color by Apr 27"},
    {id:3,name:"Shaker cabinets — upper/lower",qty:"12 linear ft",vendor:"IKEA",status:"ordered",orderDate:"Apr 20",eta:"May 2",priority:"medium",cost:3200,notes:""},
    {id:4,name:"Quartz countertops",qty:"18 sqft",vendor:"Stone World SF",status:"quoted",orderDate:"",eta:"",priority:"medium",cost:1800,notes:"Waiting on cabinet install — order after May 2"},
    {id:5,name:"Paint — Sherwin Williams",qty:"12 gallons",vendor:"Sherwin Williams",status:"in-stock",orderDate:"Apr 22",eta:"On site",priority:"low",cost:480,notes:""},
  ]);
  const [newMat, setNewMat] = useState({name:"",qty:"",vendor:"",status:"pending",priority:"medium",cost:"",notes:""});
  const [showAddMat, setShowAddMat] = useState(false);
  const [editMat, setEditMat] = useState(null);
  const [selectedMat, setSelectedMat] = useState(null);
  const [matComment, setMatComment] = useState("");
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [selectedUrgentCO, setSelectedUrgentCO] = useState(null);

  const projFlags = SUB_PORTAL_DATA.issues.filter(i=>i.projectId===proj.id?.toString()||i.projectId===proj.id);
  const projLogs = SUB_PORTAL_DATA.logs.filter(l=>l.projectId===proj.id?.toString()||l.projectId===proj.id);
  const projSubRFIs = SUB_PORTAL_DATA.rfis.filter(r=>r.projectId===proj.id?.toString()||r.projectId===proj.id);

  const openFlags = projFlags.filter(f=>f.status==="open");
  const criticalFlags = openFlags.filter(f=>f.priority==="critical"||f.priority==="high");
  const pendingCOs = projCOs.filter(c=>c.status==="pending");
  const openRFIs = projRFIs.filter(r=>r.status!=="closed");
  const openSubRFIs = projSubRFIs.filter(r=>r.status==="open");
  const matOrdered = materials.filter(m=>m.status==="ordered"||m.status==="in-stock");
  const matPending = materials.filter(m=>m.status==="pending"||m.status==="quoted");

  const STATUS_STYLE = {
    "ordered":   {bg:"rgba(34,197,94,0.1)",  color:T.green,  label:"Ordered"},
    "in-stock":  {bg:"rgba(45,212,191,0.1)", color:T.teal,   label:"On Site"},
    "pending":   {bg:"rgba(245,158,11,0.1)", color:T.amber,  label:"Pending"},
    "quoted":    {bg:"rgba(96,165,250,0.1)", color:T.blue,   label:"Quoted"},
    "delivered": {bg:"rgba(167,139,250,0.1)",color:T.purple, label:"Delivered"},
  };
  const PRIORITY_C = {low:T.muted,medium:T.amber,high:T.red,critical:T.red};

  const filteredMats = matFilter==="all" ? materials : materials.filter(m=>m.status===matFilter);

  const updateMatStatus = (id, status) => {
    setMaterials(prev=>prev.map(m=>String(m.id)===String(id)?{...m,status}:m));
  };

  const addMaterial = () => {
    if (!newMat.name) return;
    setMaterials(prev=>[...prev,{...newMat,id:Date.now(),cost:parseFloat(newMat.cost)||0}]);
    setNewMat({name:"",qty:"",vendor:"",status:"pending",priority:"medium",cost:"",notes:""});
    setShowAddMat(false);
  };

  const [showSummary, setShowSummary] = useState(false);

  return (
    <div>
      {/* ── LIVE PROGRESS BAR ─────────────────────────────────────────────── */}
      <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 16px",marginBottom:10}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text}}>📊 Live Project Progress</div>
          <div style={{fontSize:18,fontWeight:800,color:T.gold}}>{proj.pct||0}%</div>
        </div>
        {/* Overall progress */}
        <div style={{height:8,background:T.bg3,borderRadius:4,overflow:"hidden",marginBottom:6}}>
          <div style={{height:"100%",width:(proj.pct||0)+"%",background:`linear-gradient(90deg,${T.gold},${T.green})`,borderRadius:4,transition:"width 0.5s"}}/>
        </div>
        {/* Phase breakdown */}
        <div style={{display:"flex",gap:3,marginBottom:6}}>
          {["Demo","Framing","MEP","Drywall","Tile","Paint","Closeout"].map((ph,i)=>{
            const pct = (proj.phases||[])[i]||0;
            return (
              <div key={i} style={{flex:1,textAlign:"center"}} title={ph+": "+pct+"%"}>
                <div style={{height:4,background:T.bg3,borderRadius:2,marginBottom:2}}>
                  <div style={{height:"100%",width:pct+"%",background:pct===100?T.green:pct>0?T.amber:T.bg3,borderRadius:2}}/>
                </div>
                <div style={{fontSize:7,color:pct===100?T.green:pct>0?T.amber:T.muted,fontWeight:pct>0?700:400,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ph}</div>
              </div>
            );
          })}
        </div>
        {/* Quick stats */}
        <div style={{display:"flex",gap:8}}>
          {[
            {l:"Contract",v:"$"+((proj.value||0)/1000).toFixed(0)+"K",c:T.gold},
            {l:"Open COs",v:pendingCOs.length,c:pendingCOs.length>0?T.amber:T.green},
            {l:"Open RFIs",v:openRFIs.length,c:openRFIs.length>0?T.amber:T.green},
            {l:"Flags",v:openFlags.length,c:openFlags.length>0?T.red:T.green},
          ].map(s=>(
            <div key={s.l} style={{flex:1,textAlign:"center",background:T.bg2,borderRadius:6,padding:"5px 4px"}}>
              <div style={{fontSize:14,fontWeight:800,color:s.c}}>{s.v}</div>
              <div style={{fontSize:9,color:T.muted}}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── PINNED URGENT ITEMS ────────────────────────────────────────────── */}
      {(criticalFlags.length>0||pendingCOs.length>0||openRFIs.filter(r=>r.priority==="Critical"||r.priority==="Urgent").length>0)&&(
        <div style={{background:T.redBg,border:"1px solid "+T.red+"40",borderRadius:10,padding:"10px 14px",marginBottom:10}}>
          <div style={{fontSize:11,fontWeight:700,color:T.red,marginBottom:8}}>📌 Pinned — Needs Immediate Attention</div>
          {criticalFlags.slice(0,3).map(f=>(
            <div key={f.id} onClick={()=>setTab("files")} style={{display:"flex",gap:8,alignItems:"center",padding:"6px 0",borderBottom:"1px solid "+T.red+"20",cursor:"pointer"}}>
              <div style={{width:7,height:7,borderRadius:"50%",background:T.red,flexShrink:0}}/>
              <div style={{flex:1}}>
                <span style={{fontSize:11,fontWeight:700,color:T.text}}>{f.title||f.description}</span>
                <span style={{fontSize:10,color:T.muted,marginLeft:6}}>{f.trade} · Blueprint flag</span>
              </div>
              <span style={{fontSize:9,color:T.red,fontWeight:700}}>CRITICAL</span>
            </div>
          ))}
          {pendingCOs.slice(0,2).map(co=>(
            <div key={co.id} onClick={()=>setTab("admin")} style={{display:"flex",gap:8,alignItems:"center",padding:"6px 0",borderBottom:"1px solid "+T.red+"20",cursor:"pointer"}}>
              <div style={{width:7,height:7,borderRadius:"50%",background:T.amber,flexShrink:0}}/>
              <div style={{flex:1}}>
                <span style={{fontSize:11,fontWeight:700,color:T.text}}>{co.id} — {(co.desc||"").slice(0,40)}</span>
                <span style={{fontSize:10,color:T.muted,marginLeft:6}}>CO pending signature</span>
              </div>
              <span style={{fontSize:9,color:T.amber,fontWeight:700}}>SIGN</span>
            </div>
          ))}
          {openRFIs.filter(r=>r.priority==="Critical"||r.priority==="Urgent").slice(0,2).map(r=>(
            <div key={r.id} onClick={()=>setTab("admin")} style={{display:"flex",gap:8,alignItems:"center",padding:"6px 0",cursor:"pointer"}}>
              <div style={{width:7,height:7,borderRadius:"50%",background:"#8b5cf6",flexShrink:0}}/>
              <div style={{flex:1}}>
                <span style={{fontSize:11,fontWeight:700,color:T.text}}>{r.num||r.id} — {(r.subject||"").slice(0,40)}</span>
                <span style={{fontSize:10,color:T.muted,marginLeft:6}}>RFI · {r.priority}</span>
              </div>
              <span style={{fontSize:9,color:"#8b5cf6",fontWeight:700}}>URGENT</span>
            </div>
          ))}
        </div>
      )}

      {/* Collapsible Project Summary — replaces Overview tab */}
      <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:10,marginBottom:12,overflow:"hidden"}}>
        <button onClick={()=>setShowSummary(p=>!p)}
          style={{width:"100%",padding:"10px 16px",background:"none",border:"none",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{display:"flex",gap:12,alignItems:"center"}}>
            <span style={{fontSize:12,fontWeight:700,color:T.text}}>📋 Project Summary</span>
            <span style={{fontSize:10,color:T.muted}}>{proj.address||proj.addr||""}</span>
            {proj.status&&<span style={{fontSize:9,fontWeight:700,background:proj.status==="active"?T.greenBg:T.bg3,color:proj.status==="active"?T.green:T.muted,borderRadius:20,padding:"2px 8px",textTransform:"capitalize"}}>{proj.status}</span>}
          </div>
          <span style={{fontSize:12,color:T.muted}}>{showSummary?"▲":"▼"}</span>
        </button>
        {showSummary&&(
          <div style={{padding:"0 16px 14px",borderTop:`1px solid ${T.subtle}`}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:12}}>
              {[
                ["Owner / Client", proj.client||proj.owner||"—"],
                ["Project Type", proj.type||"—"],
                ["Start Date", proj.startDate||proj.start||"—"],
                ["Est. Completion", proj.endDate||proj.end||"—"],
                ["Contract Amount", proj.contractAmount?("$"+Number(proj.contractAmount).toLocaleString()):"—"],
                ["Address", proj.address||proj.addr||"—"],
              ].map(([l,v])=>(
                <div key={l} style={{display:"flex",flexDirection:"column",gap:2}}>
                  <span style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.06em"}}>{l}</span>
                  <span style={{fontSize:12,color:T.text,fontWeight:600}}>{v}</span>
                </div>
              ))}
            </div>
            {proj.notes&&<div style={{marginTop:10,fontSize:11,color:T.muted,background:T.bg2,borderRadius:6,padding:"8px 10px",lineHeight:1.5}}>{proj.notes}</div>}
          </div>
        )}
      </div>

      {/* Priority alert strip — clickable items */}
      {(criticalFlags.length>0||pendingCOs.length>0) && (
        <div style={{background:T.redBg,border:`1px solid ${T.red}30`,borderRadius:9,padding:"10px 16px",marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:T.red,marginBottom:8}}>⚠ Needs Attention</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {criticalFlags.map(flag=>(
              <div key={flag.id} onClick={()=>setSelectedFlag(selectedFlag?.id===flag.id?null:flag)}
                style={{background:T.bg1,border:`1px solid ${T.red}40`,borderRadius:8,padding:"6px 12px",cursor:"pointer",display:"flex",gap:7,alignItems:"center",transition:"all 0.15s"}}>
                <span style={{fontSize:9,fontWeight:700,color:T.red}}>🚩 {flag.priority?.toUpperCase()}</span>
                <span style={{fontSize:11,color:T.text,fontWeight:600}}>{flag.title?.slice(0,30)}</span>
                <span style={{fontSize:9,color:T.muted}}>{flag.trade}</span>
              </div>
            ))}
            {pendingCOs.map(co=>(
              <div key={co.id} onClick={()=>setSelectedUrgentCO(selectedUrgentCO?.id===co.id?null:co)}
                style={{background:T.bg1,border:`1px solid ${T.amber}40`,borderRadius:8,padding:"6px 12px",cursor:"pointer",display:"flex",gap:7,alignItems:"center",transition:"all 0.15s"}}>
                <span style={{fontSize:9,fontWeight:700,color:T.amber}}>📝 CO PENDING</span>
                <span style={{fontSize:11,color:T.text,fontWeight:600}}>{(co.desc||co.description||"").slice(0,30)}</span>
                <span style={{fontSize:9,color:T.muted}}>${Math.round(co.owner_price||co.amount||0).toLocaleString()}</span>
              </div>
            ))}
          </div>

          {/* Flag detail */}
          {selectedFlag&&(
            <div style={{marginTop:10,background:T.bg1,borderRadius:8,padding:"12px 14px",border:`1px solid ${T.red}30`}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                <div style={{fontSize:12,fontWeight:700,color:T.text}}>{selectedFlag.title}</div>
                <button onClick={()=>setSelectedFlag(null)} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:16}}>×</button>
              </div>
              <div style={{fontSize:11,color:T.muted,marginBottom:6}}>{selectedFlag.subName} · {selectedFlag.trade} · {selectedFlag.date}</div>
              {selectedFlag.description&&<div style={{fontSize:11,color:T.muted,lineHeight:1.5,marginBottom:8}}>{selectedFlag.description}</div>}
              {selectedFlag.photos?.length>0&&(
                <div style={{display:"flex",gap:6,marginBottom:8}}>
                  {selectedFlag.photos.map(ph=>(
                    <img key={ph.id} src={ph.url} alt="" onClick={()=>window.open(ph.url,"_blank")}
                      style={{width:64,height:64,objectFit:"cover",borderRadius:6,cursor:"pointer",border:`1px solid ${T.subtle}`}}/>
                  ))}
                </div>
              )}
              <button onClick={()=>setTab("precon")}
                style={{fontSize:10,fontWeight:700,background:T.red,color:"white",border:"none",borderRadius:6,padding:"5px 12px",cursor:"pointer"}}>
                View in Sub Portal →
              </button>
            </div>
          )}

          {/* CO detail */}
          {selectedUrgentCO&&(
            <div style={{marginTop:10,background:T.bg1,borderRadius:8,padding:"12px 14px",border:`1px solid ${T.amber}30`}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                <div style={{fontSize:12,fontWeight:700,color:T.text}}>{selectedUrgentCO.num||selectedUrgentCO.id} — {selectedUrgentCO.desc||selectedUrgentCO.description}</div>
                <button onClick={()=>setSelectedUrgentCO(null)} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:16}}>×</button>
              </div>
              <div style={{fontSize:11,color:T.muted,marginBottom:8}}>
                Amount: <strong style={{color:T.text}}>${Math.round(selectedUrgentCO.owner_price||selectedUrgentCO.amount||0).toLocaleString()}</strong> · Status: <strong style={{color:T.amber}}>{selectedUrgentCO.status}</strong>
              </div>
              {(selectedUrgentCO.comments||[]).map(c=>(
                <div key={c.id} style={{background:T.bg2,borderRadius:6,padding:"6px 9px",marginBottom:5,fontSize:11}}>
                  <strong style={{color:T.text}}>{c.author}</strong>: {c.text}
                </div>
              ))}
              <button onClick={()=>setTab("admin")}
                style={{fontSize:10,fontWeight:700,background:T.amber,color:T.bg0,border:"none",borderRadius:6,padding:"5px 12px",cursor:"pointer"}}>
                View in Admin →
              </button>
            </div>
          )}
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>

        {/* LEFT COLUMN */}
        <div style={{display:"flex",flexDirection:"column",gap:12}}>

          {/* Sub/Field Issues */}
          <Card>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{fontSize:13,fontWeight:700,color:T.text}}>
                Field Issues
                {openFlags.length>0&&<span style={{marginLeft:8,fontSize:10,fontWeight:700,background:T.redBg,color:T.red,borderRadius:20,padding:"1px 7px"}}>{openFlags.length} open</span>}
              </div>
              <Btn small ghost onClick={()=>setTab("precon")} style={{fontSize:10}}>Sub Portal →</Btn>
            </div>

            {projFlags.length===0?(
              <div style={{fontSize:12,color:T.muted,fontStyle:"italic",padding:"8px 0"}}>No field issues flagged yet. Subs flag issues from the Sub Portal.</div>
            ):projFlags.slice(0,5).map(flag=>{
              const pc = flag.priority==="critical"||flag.priority==="high"?T.red:flag.priority==="medium"?T.amber:T.muted;
              return (
                <div key={flag.id} style={{borderLeft:`3px solid ${flag.status==="resolved"?T.green:pc}`,paddingLeft:10,paddingBottom:10,marginBottom:8,borderBottom:`1px solid ${T.subtle}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:3}}>
                    <div style={{fontSize:12,fontWeight:700,color:flag.status==="resolved"?T.muted:T.text,textDecoration:flag.status==="resolved"?"line-through":"none"}}>{flag.title}</div>
                    <div style={{display:"flex",gap:5,flexShrink:0,marginLeft:8}}>
                      {flag.trade_conflict&&<span style={{fontSize:9,fontWeight:700,background:T.redBg,color:T.red,borderRadius:10,padding:"1px 6px"}}>CONFLICT</span>}
                      <span style={{fontSize:9,fontWeight:700,background:pc+"18",color:pc,borderRadius:10,padding:"1px 6px"}}>{flag.priority?.toUpperCase()}</span>
                      <span style={{fontSize:9,color:flag.status==="resolved"?T.green:T.amber,fontWeight:600}}>{flag.status}</span>
                    </div>
                  </div>
                  <div style={{fontSize:11,color:T.muted}}>{flag.subName} · {flag.trade} · {flag.date}</div>
                  {flag.description&&<div style={{fontSize:11,color:T.muted,marginTop:2,lineHeight:1.4}}>{flag.description}</div>}
                  {flag.photos?.length>0&&(
                    <div style={{display:"flex",gap:4,marginTop:6}}>
                      {flag.photos.map(ph=>(
                        <div key={ph.id} onClick={()=>window.open(ph.url,"_blank")}
                          style={{width:36,height:36,borderRadius:5,overflow:"hidden",cursor:"pointer",border:`1px solid ${T.subtle}`}}>
                          <img src={ph.url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                        </div>
                      ))}
                      <div style={{fontSize:9,color:T.blue,alignSelf:"center",marginLeft:2}}>{flag.photos.length} photo{flag.photos.length>1?"s":""}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </Card>

          {/* Sub Daily Logs - latest */}
          <Card>
            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:10}}>Latest Field Activity</div>
            {projLogs.length===0?(
              <div style={{fontSize:12,color:T.muted,fontStyle:"italic"}}>No activity logged today.</div>
            ):projLogs.slice(0,4).map(log=>{
              const sub = SUB_PORTAL_DATA.subs.find(s=>s.id===log.subId);
              const color = sub?.color||T.muted;
              return (
                <div key={log.id} style={{display:"flex",gap:10,padding:"8px 0",borderBottom:`1px solid ${T.subtle}`,alignItems:"flex-start"}}>
                  <div style={{width:28,height:28,borderRadius:7,background:color+"18",border:`1px solid ${color}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:800,color,flexShrink:0}}>{log.trade?.slice(0,3).toUpperCase()}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                      <span style={{fontSize:11,fontWeight:700,color:T.text}}>{log.subName}</span>
                      <span style={{fontSize:9,color:T.muted,fontFamily:"monospace"}}>{log.date}</span>
                    </div>
                    <div style={{fontSize:11,color:T.muted,lineHeight:1.4}}>{log.work_done}</div>
                    {log.tomorrow_plan&&<div style={{fontSize:10,color:T.teal,marginTop:2}}>→ Tomorrow: {log.tomorrow_plan}</div>}
                    {log.progress_pct&&<div style={{fontSize:10,color,fontWeight:700,marginTop:2}}>{log.progress_pct}% complete</div>}
                  </div>
                </div>
              );
            })}
          </Card>

          {/* Open RFIs */}
          {(openRFIs.length>0||openSubRFIs.length>0)&&(
            <Card>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:10}}>
                Open RFIs
                <span style={{marginLeft:8,fontSize:10,color:T.amber,fontWeight:600}}>{openRFIs.length+openSubRFIs.length} pending</span>
              </div>
              {[...openRFIs.map(r=>({...r,_type:"gc"})),...openSubRFIs.map(r=>({...r,_type:"sub"}))].slice(0,5).map(rfi=>(
                <div key={rfi.id} style={{borderLeft:`3px solid ${T.amber}`,paddingLeft:10,paddingBottom:8,marginBottom:8,borderBottom:`1px solid ${T.subtle}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:2}}>
                    <span style={{fontSize:11,fontWeight:700,color:T.text}}>{rfi.subject||rfi.desc}</span>
                    <div style={{display:"flex",gap:4}}>
                      {rfi.affects_schedule&&<span style={{fontSize:9,fontWeight:700,background:T.redBg,color:T.red,borderRadius:10,padding:"1px 6px"}}>SCHEDULE</span>}
                      <span style={{fontSize:9,background:T.amberBg,color:T.amber,borderRadius:10,padding:"1px 6px"}}>{rfi._type==="sub"?"Sub RFI":"RFI"}</span>
                    </div>
                  </div>
                  <div style={{fontSize:10,color:T.muted}}>{rfi.subName||rfi.assignee||"Grok"} · {rfi.date}</div>
                </div>
              ))}
            </Card>
          )}
        </div>

        {/* RIGHT COLUMN */}
        <div style={{display:"flex",flexDirection:"column",gap:12}}>

          {/* Materials Tracker */}
          <Card>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{fontSize:13,fontWeight:700,color:T.text}}>
                Materials
                {matPending.length>0&&<span style={{marginLeft:8,fontSize:10,color:T.amber,fontWeight:600}}>{matPending.length} to order</span>}
              </div>
              <div style={{display:"flex",gap:5}}>
                <select value={matFilter} onChange={e=>setMatFilter(e.target.value)}
                  style={{fontSize:10,background:T.bg3,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"3px 7px",color:T.muted,fontFamily:"inherit"}}>
                  {[["all","All"],["pending","Pending"],["ordered","Ordered"],["in-stock","On Site"],["quoted","Quoted"]].map(([v,l])=>(
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
                <Btn small ghost onClick={()=>setShowAddMat(true)}><Ic d={ICONS.plus} size={11}/>Add</Btn>
              </div>
            </div>

            {showAddMat&&(
              <div style={{background:T.bg2,borderRadius:8,padding:"12px",marginBottom:12,border:`1px solid ${T.subtle}`}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                  {[["name","Material Name","Quartz countertop"],["qty","Qty / Size","18 sqft"],["vendor","Vendor","Stone World SF"],["cost","Cost ($)","1800"]].map(([k,l,ph])=>(
                    <div key={k}>
                      <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>{l}</div>
                      <input value={newMat[k]} onChange={e=>setNewMat(p=>({...p,[k]:e.target.value}))} placeholder={ph}
                        style={{width:"100%",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"6px 9px",color:T.text,fontSize:11,fontFamily:"inherit"}}/>
                    </div>
                  ))}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                  <div>
                    <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Status</div>
                    <select value={newMat.status} onChange={e=>setNewMat(p=>({...p,status:e.target.value}))}
                      style={{width:"100%",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"6px 9px",color:T.text,fontSize:11,fontFamily:"inherit"}}>
                      {["pending","quoted","ordered","in-stock","delivered"].map(s=><option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Priority</div>
                    <select value={newMat.priority} onChange={e=>setNewMat(p=>({...p,priority:e.target.value}))}
                      style={{width:"100%",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"6px 9px",color:T.text,fontSize:11,fontFamily:"inherit"}}>
                      {["low","medium","high"].map(s=><option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <input value={newMat.notes} onChange={e=>setNewMat(p=>({...p,notes:e.target.value}))} placeholder="Notes..."
                  style={{width:"100%",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"6px 9px",color:T.text,fontSize:11,fontFamily:"inherit",marginBottom:8}}/>
                <div style={{display:"flex",gap:7}}>
                  <Btn small ghost onClick={()=>setShowAddMat(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
                  <Btn small primary disabled={!newMat.name} onClick={addMaterial} style={{flex:2,justifyContent:"center"}}>Add Material</Btn>
                </div>
              </div>
            )}

            {filteredMats.map(mat=>{
              const ss = STATUS_STYLE[mat.status]||STATUS_STYLE["pending"];
              const isSelected = selectedMat?.id===mat.id;
              return (
                <div key={mat.id}>
                  <div onClick={()=>setSelectedMat(isSelected?null:mat)}
                    style={{display:"flex",gap:10,alignItems:"flex-start",padding:"8px 0",borderBottom:`1px solid ${T.subtle}`,cursor:"pointer",background:isSelected?T.goldBg:"transparent",borderRadius:isSelected?7:0,padding:isSelected?"8px":"8px 0"}}>
                    <div style={{width:4,borderRadius:2,alignSelf:"stretch",flexShrink:0,background:PRIORITY_C[mat.priority]||T.muted,minHeight:36}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:2}}>
                        <span style={{fontSize:12,fontWeight:700,color:T.text}}>{mat.name}</span>
                        <select value={mat.status} onClick={e=>e.stopPropagation()} onChange={e=>updateMatStatus(mat.id,e.target.value)}
                          style={{fontSize:9,fontWeight:700,background:ss.bg,color:ss.color,border:"none",borderRadius:10,padding:"2px 7px",cursor:"pointer",fontFamily:"inherit",flexShrink:0,marginLeft:6}}>
                          {["pending","quoted","ordered","in-stock","delivered"].map(s=><option key={s} value={s}>{STATUS_STYLE[s]?.label||s}</option>)}
                        </select>
                      </div>
                      <div style={{display:"flex",gap:10,fontSize:10,color:T.muted,flexWrap:"wrap"}}>
                        {mat.qty&&<span>{mat.qty}</span>}
                        {mat.vendor&&<span>📦 {mat.vendor}</span>}
                        {mat.eta&&<span style={{color:mat.status==="ordered"?T.green:T.muted}}>ETA: {mat.eta}</span>}
                        {mat.cost>0&&<span style={{color:T.text,fontWeight:600}}>${mat.cost.toLocaleString()}</span>}
                      </div>
                      {mat.notes&&<div style={{fontSize:10,color:T.amber,marginTop:2,lineHeight:1.4}}>{mat.notes}</div>}
                    </div>
                  </div>

                  {/* Material detail panel */}
                  {isSelected&&(
                    <div style={{background:T.bg2,borderRadius:8,padding:"12px 14px",marginBottom:8,border:`1px solid ${T.gold}30`}}>
                      <div style={{display:"flex",gap:10,marginBottom:10,flexWrap:"wrap"}}>
                        {["pending","quoted","ordered","in-stock","delivered"].map(s=>(
                          <button key={s} onClick={()=>{updateMatStatus(mat.id,s);setSelectedMat(prev=>({...prev,status:s}));}}
                            style={{fontSize:10,fontWeight:600,padding:"4px 10px",borderRadius:20,border:"none",cursor:"pointer",
                              background:mat.status===s?T.gold:T.bg1,color:mat.status===s?T.bg0:T.muted}}>
                            {STATUS_STYLE[s]?.label||s}
                          </button>
                        ))}
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10,fontSize:11}}>
                        {mat.orderDate&&<div><span style={{color:T.muted}}>Ordered: </span><strong>{mat.orderDate}</strong></div>}
                        {mat.eta&&<div><span style={{color:T.muted}}>ETA: </span><strong style={{color:T.green}}>{mat.eta}</strong></div>}
                        {mat.vendor&&<div><span style={{color:T.muted}}>Vendor: </span><strong>{mat.vendor}</strong></div>}
                        {mat.cost>0&&<div><span style={{color:T.muted}}>Cost: </span><strong style={{color:T.gold}}>${mat.cost.toLocaleString()}</strong></div>}
                      </div>
                      {/* Comments */}
                      <div style={{marginBottom:8}}>
                        <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Notes / Comments</div>
                        {(mat.comments||[]).map(c=>(
                          <div key={c.id} style={{background:T.bg1,borderRadius:6,padding:"6px 9px",marginBottom:4,fontSize:11}}>
                            <strong style={{color:T.text}}>{c.author}</strong>: {c.text} <span style={{color:T.muted,fontSize:9}}>· {c.date}</span>
                          </div>
                        ))}
                        <textarea value={matComment} onChange={e=>setMatComment(e.target.value)}
                          placeholder="Add a note about this material..." rows={2}
                          style={{width:"100%",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:11,fontFamily:"inherit",resize:"none",marginBottom:6}}/>
                        <button disabled={!matComment.trim()} onClick={()=>{
                          const comment = {id:Date.now(),text:matComment.trim(),author:"GC",date:new Date().toLocaleDateString()};
                          const updated = materials.map(m=>String(m.id)===String(mat.id)?{...m,comments:[...(m.comments||[]),comment]}:m);
                          setMaterials(updated);
                          setSelectedMat(prev=>({...prev,comments:[...(prev.comments||[]),comment]}));
                          setMatComment("");
                        }} style={{width:"100%",padding:"6px",borderRadius:6,border:"none",background:matComment.trim()?T.gold:T.bg3,color:matComment.trim()?T.bg0:"white",fontSize:11,fontWeight:700,cursor:matComment.trim()?"pointer":"not-allowed"}}>
                          💬 Post Note
                        </button>
                      </div>
                      <button onClick={()=>{
                        if (!confirm("Remove this material?")) return;
                        setMaterials(prev=>prev.filter(m=>String(m.id)!==String(mat.id)));
                        setSelectedMat(null);
                      }} style={{fontSize:10,color:T.red,background:"none",border:`1px solid ${T.red}30`,borderRadius:6,padding:"4px 10px",cursor:"pointer"}}>
                        Remove Material
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Materials total */}
            <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0 0",marginTop:4}}>
              <span style={{fontSize:11,color:T.muted}}>Total materials</span>
              <span style={{fontSize:12,fontWeight:700,color:T.gold}}>${materials.reduce((s,m)=>s+(m.cost||0),0).toLocaleString()}</span>
            </div>
          </Card>

          {/* Pending COs — clickable */}
          {pendingCOs.length>0&&(
            <Card>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:10}}>
                Change Orders — Action Required
              </div>
              {pendingCOs.map(co=>{
                const isSelCO = selectedUrgentCO?.id===co.id;
                return (
                  <div key={co.id}>
                    <div onClick={()=>setSelectedUrgentCO(isSelCO?null:co)}
                      style={{borderLeft:`3px solid ${T.red}`,paddingLeft:10,paddingBottom:8,marginBottom:8,borderBottom:`1px solid ${T.subtle}`,cursor:"pointer",background:isSelCO?T.redBg:"transparent",borderRadius:isSelCO?7:0,padding:isSelCO?"8px 8px 8px 10px":"0 0 8px 10px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                        <span style={{fontSize:12,fontWeight:700,color:T.text}}>{co.num||co.id} — {co.desc||co.description}</span>
                        <span style={{fontSize:12,fontWeight:700,color:T.red}}>${Math.round(co.owner_price||co.amount||0).toLocaleString()}</span>
                      </div>
                      <div style={{fontSize:10,color:T.muted}}>Pending owner approval · {co.date}</div>
                    </div>
                    {isSelCO&&(
                      <div style={{background:T.bg2,borderRadius:8,padding:"12px 14px",marginBottom:8,border:`1px solid ${T.red}30`}}>
                        {co.reason&&<div style={{fontSize:11,color:T.muted,marginBottom:8,lineHeight:1.5}}>{co.reason}</div>}
                        {(co.comments||[]).map(c=>(
                          <div key={c.id} style={{background:T.bg1,borderRadius:6,padding:"6px 9px",marginBottom:4,fontSize:11}}>
                            <strong style={{color:T.text}}>{c.author}</strong>: {c.text}
                          </div>
                        ))}
                        <button onClick={()=>setTab("admin")}
                          style={{fontSize:11,fontWeight:700,background:T.amber,color:T.bg0,border:"none",borderRadius:7,padding:"7px 16px",cursor:"pointer",width:"100%"}}>
                          Approve / Review in Admin →
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}


function DeleteConfirmInput({projectName, onConfirm, onCancel}) {
  const [typed, setTyped] = useState("");
  const matches = typed.trim().toLowerCase() === projectName.trim().toLowerCase();
  return (
    <div>
      <input value={typed} onChange={e=>setTyped(e.target.value)}
        placeholder={projectName} autoFocus
        style={{width:"100%",background:T.bg2,border:`2px solid ${matches?T.green:T.red+"30"}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:13,fontFamily:"inherit",marginBottom:12,outline:"none"}}/>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <Btn ghost onClick={onCancel}>Cancel</Btn>
        <Btn danger disabled={!matches} onClick={onConfirm}
          style={{background:matches?T.red:T.bg3,color:matches?"white":T.muted,cursor:matches?"pointer":"not-allowed"}}>
          {matches?"Yes, Delete Permanently":"Type name to confirm"}
        </Btn>
      </div>
    </div>
  );
}

// ─── PROJECT ARIA — In-context AI for each project ────────────────────────────
function ProjectARIA({proj, session, setTab}) {
  const T = useTheme();
  const cid = session?.companyId || 'local';
  const [msgs, setMsgs] = React.useState([{
    role:'aria',
    text:"I'm ARIA, your AI assistant for " + (proj?.name||'this project') + ". Ask me anything about this specific job — status, budget, change orders, RFIs, subs, or what needs attention next.",
    time: new Date().toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})
  }]);
  const [input, setInput] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const msgEndRef = React.useRef(null);
  const boundRef = React.useRef(null);
  const bound = useViewportBound(boundRef, 60);

  const projCOs = (()=>{try{return JSON.parse(localStorage.getItem('grok_cos_global_'+cid)||'[]').filter(c=>c.project===proj?.name||c.projectId===proj?.id);}catch{return [];}})();
  const projRFIs = (()=>{try{return JSON.parse(localStorage.getItem('grok_rfis_global_'+cid)||'[]').filter(r=>r.project===proj?.name||r.projectId===proj?.id);}catch{return [];}})();

  React.useEffect(()=>{ msgEndRef.current?.scrollIntoView({behavior:'smooth'}); },[msgs]);

  const buildCtx = () =>
    `You are ARIA, an AI construction project manager. You are helping with a specific project.

PROJECT: ${proj?.name||'Unknown'}
Address: ${proj?.addr||proj?.address||'not set'}
Status: ${proj?.status||'active'}
Progress: ${proj?.pct||proj?.progress||0}% complete
Phase: ${proj?.phase||'active'}
Contract value: $${(proj?.value||0).toLocaleString()}
Client: ${proj?.client||'not set'}
Notes: ${proj?.notes||'none'}

Change orders for this project (${projCOs.length}):
${projCOs.slice(0,5).map(c=>'- '+c.num+': '+(c.desc||c.title||'CO')+' $'+(c.amount||0).toLocaleString()+' — '+c.status).join('\n')||'none'}

Open RFIs for this project (${projRFIs.length}):
${projRFIs.slice(0,5).map(r=>'- '+r.num+': '+(r.subject||'RFI')+' — '+r.status).join('\n')||'none'}

GC: ${session?.ownerName||'Jehoon'} · ${session?.companyName||'GCG'}

Be specific, direct, and helpful. Focus only on this project. 2-4 sentences unless more detail is needed.`;

  const sendMsg = async(text) => {
    if(!text.trim()) return;
    setMsgs(m=>[...m,{role:'user',text:text.trim(),time:new Date().toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}]);
    setInput(''); setLoading(true);
    try {
      const resp = await fetch(AI_PROXY_URL,{method:'POST',
        headers:{'Content-Type':'application/json','Authorization':'Bearer '+SUPABASE_ANON_KEY,'apikey':SUPABASE_ANON_KEY,'anthropic-version':'2023-06-01'},
        body:JSON.stringify({model:'claude-haiku-4-5',max_tokens:500,
          messages:[
            {role:'user',content:buildCtx()},
            {role:'assistant',content:'Understood. I have full context on '+proj?.name+' and am ready to help.'},
            ...msgs.filter((_,i)=>i>0).map(m=>({role:m.role==='aria'?'assistant':'user',content:m.text})),
            {role:'user',content:text}
          ]})});
      const data = await resp.json();
      const ans = data.content?.[0]?.text||fallback(text);
      setMsgs(m=>[...m,{role:'aria',text:ans,time:new Date().toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}]);
    } catch(e) {
      setMsgs(m=>[...m,{role:'aria',text:fallback(text),time:new Date().toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}]);
    }
    setLoading(false);
  };

  const fallback = (q) => {
    const ql = q.toLowerCase();
    if(ql.includes('status')||ql.includes('how')) return proj?.name+' is at '+(proj?.pct||0)+'% complete, phase: '+(proj?.phase||'active')+'.';
    if(ql.includes('change order')||ql.includes('co')) return projCOs.length>0?projCOs.length+' change order'+(projCOs.length>1?'s':'')+' on this project. Pending: '+projCOs.filter(c=>c.status==='pending').length+'.':'No change orders on this project yet.';
    if(ql.includes('rfi')) return projRFIs.length>0?projRFIs.length+' RFI'+(projRFIs.length>1?'s':'')+' on this project.':'No open RFIs on this project.';
    return 'This project is '+proj?.name+' at '+(proj?.pct||0)+'% complete. Contract value $'+(proj?.value||0).toLocaleString()+'. What would you like to know?';
  };

  const QUICK = [
    "What's the status of this project?",
    "Any change orders I should address?",
    "What needs attention this week?",
    "Draft an owner update email",
    "Are there any open RFIs?",
    "What's the budget looking like?",
    "Which subs are on this job?",
    "What are the next milestones?",
  ];

  return (
    <div ref={boundRef} style={{display:'flex',flexDirection:'column',height: bound || 'calc(100vh - 200px)',minHeight:400}}>
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',gap:10,padding:'0 0 12px',borderBottom:'1px solid '+T.subtle,marginBottom:12,flexShrink:0}}>
        <div style={{width:28,height:28,borderRadius:8,background:'linear-gradient(135deg,'+T.gold+','+T.gold2+')',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:900,color:'#02040A'}}>A</div>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>ARIA — {proj?.name}</div>
          <div style={{fontSize:10,color:T.muted}}>AI project assistant · knows this job inside out</div>
        </div>
      </div>

      {/* Messages */}
      <div style={{flex:1,overflow:'auto',display:'flex',flexDirection:'column',gap:9,paddingBottom:8}}>
        {msgs.map((msg,i)=>(
          <div key={i} style={{display:'flex',flexDirection:'column',alignItems:msg.role==='aria'?'flex-start':'flex-end'}}>
            {msg.role==='aria'&&(
              <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:3}}>
                <div style={{width:16,height:16,borderRadius:4,background:'linear-gradient(135deg,'+T.gold+','+T.gold2+')',display:'flex',alignItems:'center',justifyContent:'center',fontSize:8,fontWeight:900,color:'#02040A'}}>A</div>
                <div style={{fontSize:8,fontWeight:700,color:T.gold,textTransform:'uppercase',letterSpacing:'.1em'}}>ARIA</div>
                <div style={{fontSize:8,color:T.muted}}>{msg.time}</div>
              </div>
            )}
            <div style={{maxWidth:'88%',padding:'9px 12px',borderRadius:11,fontSize:12,lineHeight:1.7,
              background:msg.role==='aria'?T.bg2:'linear-gradient(135deg,rgba(59,130,246,.15),rgba(139,92,246,.15))',
              border:'1px solid '+(msg.role==='aria'?T.subtle:'rgba(59,130,246,.18)'),
              color:T.text,
              borderBottomLeftRadius:msg.role==='aria'?2:11,
              borderBottomRightRadius:msg.role==='aria'?11:2}}>
              {msg.role==='aria'?<MarkdownLite text={msg.text}/>:msg.text}
            </div>
            {msg.role==='user'&&<div style={{fontSize:8,color:T.muted,marginTop:2}}>{msg.time}</div>}
          </div>
        ))}
        {loading&&(
          <div style={{display:'flex',gap:6,padding:'8px 0'}}>
            <div style={{width:16,height:16,borderRadius:4,background:'linear-gradient(135deg,'+T.gold+','+T.gold2+')',display:'flex',alignItems:'center',justifyContent:'center',fontSize:8,fontWeight:900,color:'#02040A'}}>A</div>
            <div style={{background:T.bg2,border:'1px solid '+T.subtle,borderRadius:11,padding:'8px 12px',display:'flex',gap:4}}>
              {[0,1,2].map(i=><div key={i} style={{width:5,height:5,borderRadius:'50%',background:T.gold,animation:'pulse .7s infinite',animationDelay:i*.2+'s'}}/>)}
            </div>
          </div>
        )}
        <div ref={msgEndRef}/>
      </div>

      {/* Quick actions */}
      <div style={{display:'flex',gap:5,overflowX:'auto',padding:'8px 0',borderTop:'1px solid '+T.subtle+'60',flexShrink:0,scrollbarWidth:'none'}}>
        {QUICK.map((q,i)=>(
          <button key={i} onClick={()=>sendMsg(q)} disabled={loading}
            style={{padding:'5px 11px',borderRadius:20,border:'1px solid '+T.subtle,background:T.bg2,color:T.muted,fontSize:10,fontWeight:500,cursor:'pointer',whiteSpace:'nowrap',fontFamily:'inherit',flexShrink:0}}>
            {q}
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="chat-input-row" style={{display:'flex',gap:7,paddingTop:8,flexShrink:0}}>
        <input value={input} onChange={e=>setInput(e.target.value)}
          onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&sendMsg(input)}
          placeholder={"Ask about "+proj?.name+"..."}
          style={{flex:1,background:T.bg2,border:'1px solid '+T.subtle,borderRadius:24,padding:'9px 14px',color:T.text,fontSize:12,fontFamily:'inherit',outline:'none'}}/>
        <button onClick={()=>sendMsg(input)} disabled={loading||!input.trim()}
          style={{width:36,height:36,borderRadius:'50%',background:input.trim()?'linear-gradient(135deg,'+T.gold+','+T.gold2+')':T.bg2,border:'1px solid '+T.subtle,cursor:input.trim()?'pointer':'default',display:'flex',alignItems:'center',justifyContent:'center',fontSize:15,color:input.trim()?'#02040A':T.muted,flexShrink:0}}>
          →
        </button>
      </div>
    </div>
  );
}

// AI-assisted progress detection. Opens as a modal from ProjectDetail.
// Pulls photos from the project's photo daily log (grok_photolog_<id>) and/or
// fresh uploads. Sends up to 6 photos to ai-proxy with phase context and parses
// per-phase % suggestions back as JSON. User must Apply before phases update.
function PhotoProgressModal({ proj, session, phaseNames, currentPhases, onApply, onClose }) {
  const T = useTheme();
  const isMob = window.innerWidth < 768;
  const [source, setSource] = useState("recent"); // "recent" | "upload"
  const [selected, setSelected] = useState({});
  const [uploaded, setUploaded] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const fileRef = React.useRef(null);

  const recentPhotos = React.useMemo(() => {
    try {
      const entries = JSON.parse(localStorage.getItem("grok_photolog_" + proj.id) || "[]");
      const out = [];
      entries.forEach((e, ei) => {
        (e.photos || []).forEach((p, pi) => {
          if (!p || !p.url) return;
          out.push({ id: "log_" + ei + "_" + pi, dataUrl: p.url, name: (e.date || "log") + (p.caption ? " · " + p.caption : "") });
        });
      });
      return out.slice(0, 12);
    } catch { return []; }
  }, [proj.id]);

  const allPhotos = source === "recent" ? recentPhotos : uploaded;
  const pickedIds = Object.keys(selected).filter(k => selected[k]);
  const pickedPhotos = allPhotos.filter(p => selected[p.id]).slice(0, 6);

  const toggle = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));

  const compress = (dataUrl) => new Promise((resolve, reject) => {
    const MAX = 1920, BYTES = 4 * 1024 * 1024;
    const img = new Image();
    img.onerror = () => reject(new Error("decode failed"));
    img.onload = () => {
      const s = img.naturalWidth > MAX ? MAX / img.naturalWidth : 1;
      const w = Math.round(img.naturalWidth * s), h = Math.round(img.naturalHeight * s);
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      c.toBlob((b1) => {
        if (!b1) return reject(new Error("encode failed"));
        const finalize = (b) => { const r = new FileReader(); r.onload = (e) => resolve(e.target.result); r.onerror = () => reject(new Error("read failed")); r.readAsDataURL(b); };
        if (b1.size <= BYTES) return finalize(b1);
        c.toBlob((b2) => b2 ? finalize(b2) : reject(new Error("encode 0.70 failed")), "image/jpeg", 0.70);
      }, "image/jpeg", 0.85);
    };
    img.src = dataUrl;
  });

  const onPick = async (files) => {
    const arr = Array.from(files).slice(0, 8);
    setProgress("Compressing 0 of " + arr.length + "…");
    const out = [];
    for (let i = 0; i < arr.length; i++) {
      try {
        const raw = await new Promise((res, rej) => { const r = new FileReader(); r.onload = (e) => res(e.target.result); r.onerror = () => rej(new Error("read failed")); r.readAsDataURL(arr[i]); });
        const dataUrl = await compress(raw);
        out.push({ id: "up_" + Date.now() + "_" + i, dataUrl, name: arr[i].name });
      } catch {}
      setProgress("Compressing " + (i + 1) + " of " + arr.length + "…");
    }
    setProgress("");
    setUploaded(prev => [...prev, ...out]);
    const sel = { ...selected }; out.forEach(p => { sel[p.id] = true; });
    setSelected(sel);
  };

  const analyze = async () => {
    if (!pickedPhotos.length) { setErr("Pick at least one photo."); return; }
    if (typeof enforceLimit === "function" && !enforceLimit("estimates", session)) return;
    setLoading(true); setErr(""); setResult(null);
    setProgress("Sending " + pickedPhotos.length + " photos to Claude…");

    const imageContent = pickedPhotos.map(p => ({
      type: "image",
      source: { type: "base64", media_type: "image/jpeg", data: p.dataUrl.split(",")[1] },
    }));

    const currentSummary = phaseNames.map((n, i) => n + ": " + (currentPhases[i] || 0) + "%").join(", ");
    const prompt = `You are a senior construction superintendent estimating phase completion from jobsite photos.

PROJECT: ${proj.name || "Renovation"}
TYPE: ${proj.type || "Construction"}
ADDRESS: ${proj.addr || "—"}
CURRENT REPORTED PROGRESS: ${currentSummary}

Construction phases (in order):
${phaseNames.map((n, i) => (i + 1) + ". " + n).join("\n")}

Analyze the ${pickedPhotos.length} jobsite photo(s) and estimate the completion percentage of each phase. Be honest: if a phase is not visible in any photo, return its current value and mark confidence "unknown". Be conservative — lean on current values when uncertain.

Return STRICT JSON ONLY (no markdown, no commentary). Schema:
{
  "phases": [
    { "name": "<phase name>", "pct": 0-100, "confidence": "high|medium|low|unknown", "evidence": "what supports this from the photos" }
  ],
  "overall_observations": "1-3 sentences on overall site condition",
  "next_milestone": "Which phase appears to be the current focus and what would move it forward"
}

Return exactly ${phaseNames.length} phase objects in the same order as the list above.`;

    try {
      const cid = window._grokCompanyId || "local";
      const r = await fetch(AI_PROXY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + sb._token(), "apikey": SUPABASE_ANON_KEY },
        body: JSON.stringify({
          max_tokens: 2000,
          temperature: 0, // deterministic progress analysis — identical photos → identical numbers
          usage_type: "estimates",
          company_id: cid,
          plan: session?.plan || "starter",
          messages: [{ role: "user", content: [...imageContent, { type: "text", text: prompt }] }],
        }),
      });
      const data = await r.json();
      if (!r.ok || data.error) {
        const msg = data.error?.message || data.error || ("HTTP " + r.status);
        throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
      }
      const text = data.content?.[0]?.text || "";
      const cleaned = text.replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "").trim();
      let parsed;
      try { parsed = JSON.parse(cleaned); }
      catch {
        const m = cleaned.match(/\{[\s\S]*\}/);
        if (!m) throw new Error("AI returned non-JSON: " + cleaned.slice(0, 120));
        parsed = JSON.parse(m[0]);
      }
      if (!parsed || !Array.isArray(parsed.phases)) throw new Error("AI response missing phases array");
      setResult(parsed);
    } catch (e) {
      setErr(e.message || "Analysis failed");
    } finally {
      setLoading(false);
      setProgress("");
    }
  };

  const applyAll = () => {
    if (!result) return;
    const newPhases = phaseNames.map((_, i) => {
      const ph = result.phases[i];
      if (!ph || ph.confidence === "unknown") return currentPhases[i] || 0;
      return Math.max(0, Math.min(100, Math.round(Number(ph.pct) || 0)));
    });
    onApply(newPhases);
    onClose();
  };

  const tabBtn = (id, label) => (
    <button onClick={() => setSource(id)}
      style={{ padding: "6px 12px", borderRadius: 7, fontSize: 11, fontWeight: 700, border: "none", cursor: "pointer", background: source === id ? T.gold : T.bg2, color: source === id ? T.bg0 : T.muted }}>
      {label}
    </button>
  );

  const confColor = (c) => c === "high" ? T.green : c === "medium" ? T.gold : c === "low" ? T.amber : T.muted;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: isMob ? 8 : 20 }}>
      <div style={{ background: T.bg1, borderRadius: 12, border: "1px solid " + T.subtle, maxWidth: 760, width: "100%", maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid " + T.subtle, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 18 }}>🎯</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>Detect Progress from Photos</div>
            <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>AI suggests phase % from jobsite photos — review before applying.</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: T.muted, fontSize: 18, cursor: "pointer", padding: 4 }}>✕</button>
        </div>

        <div style={{ padding: 16 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            {tabBtn("recent", "Recent log photos (" + recentPhotos.length + ")")}
            {tabBtn("upload", "Upload new")}
          </div>

          {source === "upload" && (
            <div style={{ marginBottom: 12 }}>
              <input ref={fileRef} type="file" accept="image/*" multiple onChange={(e) => onPick(e.target.files)} style={{ display: "none" }}/>
              <button onClick={() => fileRef.current?.click()}
                style={{ padding: "8px 14px", fontSize: 12, fontWeight: 700, background: T.blue, color: "white", border: "none", borderRadius: 7, cursor: "pointer" }}>
                + Choose photos
              </button>
              {!!uploaded.length && <span style={{ marginLeft: 10, fontSize: 11, color: T.muted }}>{uploaded.length} ready</span>}
            </div>
          )}

          {!allPhotos.length && (
            <div style={{ padding: 24, textAlign: "center", fontSize: 12, color: T.muted, background: T.bg, borderRadius: 8, border: "1px dashed " + T.subtle }}>
              {source === "recent" ? "No photo daily logs yet. Switch to Upload new." : "Upload photos to analyze."}
            </div>
          )}

          {!!allPhotos.length && (
            <div style={{ display: "grid", gridTemplateColumns: isMob ? "repeat(3,1fr)" : "repeat(4,1fr)", gap: 8, marginBottom: 12 }}>
              {allPhotos.map(p => {
                const on = !!selected[p.id];
                return (
                  <div key={p.id} onClick={() => toggle(p.id)}
                    style={{ position: "relative", paddingTop: "75%", borderRadius: 8, overflow: "hidden", cursor: "pointer", border: on ? "2px solid " + T.gold : "2px solid " + T.subtle, background: T.bg }}>
                    <img src={p.dataUrl} alt={p.name} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}/>
                    {on && <div style={{ position: "absolute", top: 4, right: 4, background: T.gold, color: T.bg0, fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 10 }}>✓</div>}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ fontSize: 10, color: T.muted, marginBottom: 10 }}>
            {pickedIds.length} selected{pickedIds.length > 6 ? " (only first 6 sent)" : ""}
          </div>

          <button onClick={analyze} disabled={loading || !pickedPhotos.length}
            style={{ width: "100%", padding: "10px 14px", fontSize: 12, fontWeight: 800, background: loading ? T.bg2 : T.gold, color: loading ? T.muted : T.bg0, border: "none", borderRadius: 8, cursor: loading || !pickedPhotos.length ? "not-allowed" : "pointer" }}>
            {loading ? (progress || "Analyzing…") : "Analyze " + pickedPhotos.length + " photo" + (pickedPhotos.length === 1 ? "" : "s")}
          </button>

          {err && <div style={{ marginTop: 12, padding: "10px 12px", background: T.redBg, border: "1px solid " + T.red + "60", borderRadius: 7, fontSize: 11, color: T.red }}>{err}</div>}

          {result && (
            <div style={{ marginTop: 14, background: T.bg, borderRadius: 8, border: "1px solid " + T.subtle, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: T.text, marginBottom: 8 }}>AI Suggestions</div>
              {result.overall_observations && (
                <div style={{ fontSize: 11, color: T.muted, marginBottom: 10, lineHeight: 1.5, padding: "8px 10px", background: T.bg1, borderRadius: 6 }}>{result.overall_observations}</div>
              )}
              {result.phases.map((ph, i) => {
                const cur = currentPhases[i] || 0;
                const sug = Math.max(0, Math.min(100, Math.round(Number(ph.pct) || 0)));
                const delta = sug - cur;
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: i < result.phases.length - 1 ? "1px solid " + T.subtle : "none" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: T.text }}>{phaseNames[i] || ph.name}</div>
                      <div style={{ fontSize: 9, color: T.muted, marginTop: 2, lineHeight: 1.5 }}>{ph.evidence || "—"}</div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 11, color: T.muted }}>{cur}% → <span style={{ color: T.text, fontWeight: 800 }}>{sug}%</span></div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: confColor(ph.confidence), marginTop: 2 }}>
                        {ph.confidence}{delta !== 0 && ph.confidence !== "unknown" ? " · " + (delta > 0 ? "+" : "") + delta : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
              {result.next_milestone && (
                <div style={{ marginTop: 10, padding: "8px 10px", background: T.goldBg, borderRadius: 6, fontSize: 11, color: T.text, lineHeight: 1.5 }}>
                  <strong>Next:</strong> {result.next_milestone}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button onClick={onClose} style={{ flex: 1, padding: "9px 14px", fontSize: 11, fontWeight: 700, background: "transparent", color: T.muted, border: "1px solid " + T.subtle, borderRadius: 7, cursor: "pointer" }}>Cancel</button>
                <button onClick={applyAll} style={{ flex: 2, padding: "9px 14px", fontSize: 11, fontWeight: 800, background: T.green, color: "white", border: "none", borderRadius: 7, cursor: "pointer" }}>
                  Apply to project phases
                </button>
              </div>
              <div style={{ fontSize: 9, color: T.muted, marginTop: 6, textAlign: "center" }}>
                Phases marked "unknown" keep their current value.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Inspection request modal — opens automatically when a milestone hits 100% and
// inspections are required for that phase. Pulls AHJ details from BUILDING_DEPTS
// (via getBuildingDept) or the project's own building_dept fields, asks ARIA to
// draft a portal-ready inspection request, and on Approve writes a record to
// grok_inspection_requests_<projectId> + adds the inspection to the project
// calendar (grok_calendar_insp_<projectId>) so it shows up in Project Calendar.
function InspectionRequestModal({ proj, session, phaseIdx, phaseName, onClose }) {
  const T = useTheme();
  const isMob = window.innerWidth < 768;
  const required = INSPECTIONS_BY_PHASE[phaseIdx] || [];

  // AHJ — start with BUILDING_DEPTS lookup, fall back to project fields.
  const ahj = React.useMemo(() => {
    let lookup = null;
    try { if (typeof getBuildingDept === "function") lookup = getBuildingDept(proj.addr) || getBuildingDept(proj.city) || null; } catch {}
    return {
      name:    (lookup && lookup.name) || proj.building_dept || "Building Department",
      abbr:    (lookup && lookup.abbr) || "",
      phone:   (lookup && (lookup.inspection_phone || lookup.phone)) || proj.building_dept_phone || "",
      website: (lookup && lookup.website) || "",
      schedule_url: (lookup && lookup.schedule_url) || proj.building_dept_url || "",
      cutoff:  (lookup && lookup.inspection_request_cutoff) || proj.inspection_cutoff || "Confirm with AHJ",
      automated_line: (lookup && lookup.automated_line) || "",
      hours: (lookup && lookup.hours) || "",
      notes: (lookup && lookup.notes) || "",
      key_contacts: (lookup && lookup.key_contacts) || {},
    };
  }, [proj.id, proj.addr, proj.building_dept]);

  const tomorrow = () => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    // skip weekends
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  };

  const [picked, setPicked] = useState(() => Object.fromEntries(required.map(r => [r.type, true])));
  const [preferredDate, setPreferredDate] = useState(tomorrow());
  const [preferredWindow, setPreferredWindow] = useState("AM (8–12)");
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState(null); // {subject, body}
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);

  const pickedTypes = required.filter(r => picked[r.type]).map(r => r.type);

  const draftWithAI = async () => {
    if (!pickedTypes.length) { setErr("Pick at least one inspection."); return; }
    if (typeof enforceLimit === "function" && !enforceLimit("estimates", session)) return;
    setDrafting(true); setErr(""); setDraft(null);
    const cs = (() => { try { return JSON.parse(localStorage.getItem("grok_company_settings_" + (session?.companyId || "local")) || "{}"); } catch { return {}; }})();
    const companyName = cs.company_name || "Grok Construction Group Inc";
    const cslb        = cs.cslb || "CSLB #1152750";
    const contactName = session?.ownerName || cs.signer_name || cs.owner_name || companyName;
    const contactPhone= cs.phone || "";
    const contactEmail= session?.email || cs.email || "";

    const systemPrompt = `You are a senior PM at ${companyName} (${cslb}) drafting an inspection request.

Rules:
- Format the body as a portal-ready / phone-ready document the GC can paste into a permitting portal or read on the inspection request line.
- Tone: professional, factual, brief.
- Cover all required fields: project address, permit number, phase complete, specific inspection types, preferred date and window, contact name + phone + email.
- Respect the AHJ's cutoff time. If the preferred date is "tomorrow" and the cutoff has passed today, mention that the preferred date may slip and offer an alternative.
- No signature block — the requestor info is included in the body fields.

Output STRICT JSON ONLY (no markdown, no code fence):
{ "subject": "...", "body": "..." }`;

    const userMsg = `PROJECT
Address: ${proj.addr || "—"}
Permit #: ${proj.permit_no || "(no permit on file — confirm with GC)"}
Phase Complete: ${phaseName}

AHJ
${ahj.name}${ahj.abbr ? " (" + ahj.abbr + ")" : ""}
Phone: ${ahj.phone || "—"}
Schedule URL: ${ahj.schedule_url || "—"}
Cutoff: ${ahj.cutoff || "—"}

INSPECTIONS REQUESTED
${pickedTypes.map(t => "- " + t).join("\n")}

PREFERRED
Date: ${preferredDate}
Window: ${preferredWindow}

CONTACT
${contactName}
${companyName} · ${cslb}
${contactPhone ? "Phone: " + contactPhone : ""}
${contactEmail ? "Email: " + contactEmail : ""}

Generate the inspection request now.`;

    try {
      const r = await fetch(AI_PROXY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + sb._token(), "apikey": SUPABASE_ANON_KEY },
        body: JSON.stringify({
          max_tokens: 1200,
          usage_type: "chat",
          company_id: window._grokCompanyId || "local",
          plan: session?.plan || "starter",
          system: systemPrompt,
          messages: [{ role: "user", content: userMsg }],
        }),
      });
      const data = await r.json();
      if (!r.ok || data.error) {
        const msg = data.error?.message || data.error || ("HTTP " + r.status);
        throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
      }
      const text = data.content?.[0]?.text || "";
      const cleaned = text.replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "").trim();
      let parsed;
      try { parsed = JSON.parse(cleaned); }
      catch {
        const m = cleaned.match(/\{[\s\S]*\}/);
        if (!m) throw new Error("AI returned non-JSON: " + cleaned.slice(0, 160));
        parsed = JSON.parse(m[0]);
      }
      if (!parsed.subject || !parsed.body) throw new Error("AI response missing subject or body");
      setDraft({ subject: String(parsed.subject), body: String(parsed.body) });
    } catch (e) {
      setErr(e.message || "Draft failed");
    } finally {
      setDrafting(false);
    }
  };

  // Auto-draft on open if inspections are pre-selected.
  React.useEffect(() => {
    if (required.length && !draft && !drafting && pickedTypes.length) {
      draftWithAI();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logRequest = () => {
    if (!draft) return;
    const cid = session?.companyId || window._grokCompanyId || "local";
    const id = "ireq_" + Date.now();
    const record = {
      id,
      project_id: proj.id,
      phase_index: phaseIdx,
      phase_name: phaseName,
      inspections: pickedTypes,
      preferred_date: preferredDate,
      preferred_window: preferredWindow,
      permit_no: proj.permit_no || "",
      ahj: { name: ahj.name, phone: ahj.phone, schedule_url: ahj.schedule_url, cutoff: ahj.cutoff },
      subject: draft.subject,
      body: draft.body,
      logged_at: new Date().toISOString(),
      logged_by: session?.ownerName || session?.email || "GC",
      status: "submitted",
    };
    // Write to inspection request log
    try {
      const key = "grok_inspection_requests_" + proj.id;
      const prior = JSON.parse(localStorage.getItem(key) || "[]");
      prior.unshift(record);
      localStorage.setItem(key, JSON.stringify(prior.slice(0, 100)));
    } catch {}
    // Also drop entries onto the project calendar (one per inspection type) so
    // the existing Project Calendar view shows the scheduled inspection.
    try {
      const calKey = "grok_calendar_insp_" + proj.id;
      const prior = JSON.parse(localStorage.getItem(calKey) || "[]");
      const additions = pickedTypes.map(t => ({
        id: "cal_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6),
        type: t,
        date: preferredDate,
        time: preferredWindow,
        inspector: "",
        permit_no: proj.permit_no || "",
        notes: "Requested " + new Date().toLocaleDateString() + " — phase: " + phaseName,
        status: "requested",
        created: new Date().toISOString(),
      }));
      localStorage.setItem(calKey, JSON.stringify([...additions, ...prior].slice(0, 200)));
    } catch {}
    setSaved(true);
  };

  const togglePick = (t) => setPicked(p => ({ ...p, [t]: !p[t] }));

  const dateLabel = (() => {
    try {
      const d = new Date(preferredDate + "T12:00:00");
      return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
    } catch { return preferredDate; }
  })();

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: isMob ? 8 : 20 }}>
      <div style={{ background: T.bg1, borderRadius: 12, border: "1px solid " + T.subtle, maxWidth: 720, width: "100%", maxHeight: "92vh", overflow: "auto" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid " + T.subtle, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 18 }}>🏛</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>Inspection Required — {phaseName} complete</div>
            <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>{proj.name} · {required.length} inspection{required.length === 1 ? "" : "s"} typical for this phase</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: T.muted, fontSize: 18, cursor: "pointer", padding: 4 }}>✕</button>
        </div>

        <div style={{ padding: 16 }}>
          {saved && (
            <div style={{ textAlign: "center", padding: "12px 8px", marginBottom: 14, background: T.green + "12", border: "1px solid " + T.green + "40", borderRadius: 8 }}>
              <div style={{ fontSize: 22, marginBottom: 4 }}>✅</div>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.green }}>Inspection request logged</div>
              <div style={{ fontSize: 10, color: T.muted, marginTop: 4 }}>Added to project calendar · status: requested</div>
            </div>
          )}

          {/* AHJ block */}
          <div style={{ background: T.bg, border: "1px solid " + T.subtle, borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: T.text, marginBottom: 6 }}>📍 {ahj.name}{ahj.abbr ? " (" + ahj.abbr + ")" : ""}</div>
            <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "1fr 1fr", gap: 6, fontSize: 11 }}>
              {ahj.phone && <div style={{ color: T.muted }}>📞 <a href={"tel:" + ahj.phone} style={{ color: T.text, fontWeight: 600 }}>{ahj.phone}</a></div>}
              {ahj.schedule_url && <div style={{ color: T.muted }}>🔗 <a href={ahj.schedule_url.startsWith("http") ? ahj.schedule_url : "https://" + ahj.schedule_url} target="_blank" rel="noreferrer" style={{ color: T.gold, fontWeight: 600 }}>Schedule online</a></div>}
              {ahj.hours && <div style={{ color: T.muted }}>🕒 {ahj.hours}</div>}
              {ahj.cutoff && <div style={{ color: T.amber, fontWeight: 700 }}>⏰ Cutoff: {ahj.cutoff}</div>}
            </div>
            {ahj.automated_line && <div style={{ fontSize: 10, color: T.muted, marginTop: 6, lineHeight: 1.5 }}>{ahj.automated_line}</div>}
            {proj.permit_no && <div style={{ fontSize: 10, color: T.muted, marginTop: 6 }}>Permit #: <strong style={{ color: T.text }}>{proj.permit_no}</strong></div>}
            {!proj.permit_no && <div style={{ fontSize: 10, color: T.amber, marginTop: 6 }}>⚠ No permit # on project — add it in Project Edit before submitting.</div>}
          </div>

          {/* Inspection picker */}
          <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 6 }}>Inspections to request</div>
          {required.map(r => (
            <label key={r.type} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: T.bg, borderRadius: 7, marginBottom: 6, cursor: "pointer" }}>
              <input type="checkbox" checked={!!picked[r.type]} onChange={() => togglePick(r.type)} style={{ cursor: "pointer" }}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: T.text, fontWeight: 700 }}>{r.type}</div>
                {ahj.key_contacts && ahj.key_contacts[r.contact_key] && (
                  <div style={{ fontSize: 10, color: T.muted }}>{r.contact_key}: {ahj.key_contacts[r.contact_key]}</div>
                )}
              </div>
            </label>
          ))}

          {/* Date + window */}
          <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "1fr 1fr", gap: 10, marginTop: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 4 }}>Preferred date</div>
              <input type="date" value={preferredDate} onChange={e => setPreferredDate(e.target.value)} min={new Date().toISOString().slice(0, 10)}
                style={{ width: "100%", background: T.bg, border: "1px solid " + T.subtle, borderRadius: 7, padding: "8px 10px", fontSize: 12, color: T.text, fontFamily: "inherit", boxSizing: "border-box" }}/>
              <div style={{ fontSize: 9, color: T.muted, marginTop: 3 }}>{dateLabel}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 4 }}>Window</div>
              <select value={preferredWindow} onChange={e => setPreferredWindow(e.target.value)}
                style={{ width: "100%", background: T.bg, border: "1px solid " + T.subtle, borderRadius: 7, padding: "8px 10px", fontSize: 12, color: T.text, fontFamily: "inherit" }}>
                <option>AM (8–12)</option>
                <option>PM (12–4)</option>
                <option>First call</option>
                <option>Any</option>
              </select>
            </div>
          </div>

          {/* Draft section */}
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid " + T.subtle }}>
            {drafting && (
              <div style={{ padding: 16, textAlign: "center", fontSize: 12, color: T.muted }}>
                <div style={{ fontSize: 20, marginBottom: 6 }}>✍️</div>
                ARIA is drafting your inspection request…
              </div>
            )}

            {err && <div style={{ padding: "10px 12px", background: T.redBg, border: "1px solid " + T.red + "60", borderRadius: 7, fontSize: 11, color: T.red, marginBottom: 10 }}>{err}</div>}

            {!draft && !drafting && (
              <button onClick={draftWithAI} disabled={!pickedTypes.length}
                style={{ width: "100%", padding: "11px 14px", fontSize: 12, fontWeight: 800, background: pickedTypes.length ? T.gold : T.bg2, color: pickedTypes.length ? T.bg0 : T.muted, border: "none", borderRadius: 8, cursor: pickedTypes.length ? "pointer" : "not-allowed" }}>
                🤖 Draft with ARIA
              </button>
            )}

            {draft && !saved && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 4 }}>Subject</div>
                <input value={draft.subject} onChange={e => setDraft({ ...draft, subject: e.target.value })}
                  style={{ width: "100%", background: T.bg, border: "1px solid " + T.subtle, borderRadius: 7, padding: "9px 11px", fontSize: 12, color: T.text, fontFamily: "inherit", boxSizing: "border-box", marginBottom: 10 }}/>

                <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 4 }}>Body <span style={{ color: T.muted, fontWeight: 400, fontSize: 10 }}>(paste into AHJ portal or read on inspection line)</span></div>
                <textarea value={draft.body} onChange={e => setDraft({ ...draft, body: e.target.value })} rows={isMob ? 9 : 12}
                  style={{ width: "100%", background: T.bg, border: "1px solid " + T.subtle, borderRadius: 7, padding: "9px 11px", fontSize: 12, color: T.text, fontFamily: "inherit", boxSizing: "border-box", resize: "vertical", lineHeight: 1.6 }}/>

                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  {ahj.schedule_url && (
                    <a href={ahj.schedule_url.startsWith("http") ? ahj.schedule_url : "https://" + ahj.schedule_url} target="_blank" rel="noreferrer"
                      style={{ flex: 1, padding: "10px 14px", fontSize: 11, fontWeight: 800, background: T.blue, color: "white", border: "none", borderRadius: 7, cursor: "pointer", textAlign: "center", textDecoration: "none" }}>
                      🔗 Open portal
                    </a>
                  )}
                  <button onClick={() => navigator.clipboard?.writeText((draft.subject + "\n\n" + draft.body))}
                    style={{ flex: 1, padding: "10px 14px", fontSize: 11, fontWeight: 800, background: T.bg2, color: T.text, border: "1px solid " + T.subtle, borderRadius: 7, cursor: "pointer" }}>
                    📋 Copy
                  </button>
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button onClick={onClose}
                    style={{ flex: 1, padding: "10px 14px", fontSize: 11, fontWeight: 700, background: "transparent", color: T.muted, border: "1px solid " + T.subtle, borderRadius: 7, cursor: "pointer" }}>
                    Cancel
                  </button>
                  <button onClick={logRequest}
                    style={{ flex: 2, padding: "10px 14px", fontSize: 11, fontWeight: 800, background: T.green, color: "white", border: "none", borderRadius: 7, cursor: "pointer" }}>
                    ✓ Approve &amp; Log Inspection
                  </button>
                </div>
                <div style={{ fontSize: 9, color: T.muted, marginTop: 6, textAlign: "center" }}>
                  Logs to project record + adds {pickedTypes.length} entry on project calendar.
                </div>
              </>
            )}

            {saved && (
              <button onClick={onClose}
                style={{ width: "100%", padding: "11px 14px", fontSize: 12, fontWeight: 800, background: T.gold, color: T.bg0, border: "none", borderRadius: 8, cursor: "pointer" }}>
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Weather delay notice — AI drafts a professional notice to owner + subs when
// rain risk is ≥50%. GC reviews subject + body, picks recipients, and sends via
// the existing send-email Resend-backed edge function (one POST per recipient).
function DelayNoticeModal({ proj, session, risk, onClose }) {
  const T = useTheme();
  const isMob = window.innerWidth < 768;
  const [impactNotes, setImpactNotes] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState(null); // {subject, body}
  const [draftErr, setDraftErr] = useState("");
  const [sending, setSending] = useState(false);
  const [sendLog, setSendLog] = useState([]); // [{email, name, status:'ok'|'err'|'pending', msg?}]
  const [doneSending, setDoneSending] = useState(false);

  const recipients = React.useMemo(() => {
    const out = [];
    if (proj.owner_email) out.push({ kind: "owner", name: proj.owner_name || "Owner", email: proj.owner_email, role: "Owner" });
    if (proj.architect_email) out.push({ kind: "architect", name: proj.architect_name || "Architect", email: proj.architect_email, role: "Architect" });
    try {
      const db = loadSubDB(session?.companyId) || { subs: [] };
      const projSubs = (db.subs || []).filter(s => {
        if (!s.email) return false;
        return String(s.projectId) === String(proj.id)
          || (s.projects || []).includes(String(proj.id))
          || (s.projects_worked || []).includes(String(proj.id));
      });
      projSubs.forEach(s => out.push({ kind: "sub", name: s.name || s.company, email: s.email, role: s.trade || "Trade" }));
    } catch {}
    return out;
  }, [proj.id, session?.companyId]);

  const [selected, setSelected] = useState(() => Object.fromEntries(recipients.map(r => [r.email, true])));
  const toggle = (email) => setSelected(p => ({ ...p, [email]: !p[email] }));
  const selectedRecipients = recipients.filter(r => selected[r.email]);

  const rainDates = (risk?.rainDays || []).filter(d => d.probability >= 30).map(d => d.date);
  const topDay = risk?.topDay;

  const draftWithAI = async () => {
    if (typeof enforceLimit === "function" && !enforceLimit("estimates", session)) return;
    setDrafting(true); setDraftErr(""); setDraft(null);
    const datesList = rainDates.length ? rainDates.join(", ") : (topDay?.date || "the next few days");
    const peakLine = topDay ? `Peak: ${topDay.probability}% chance of rain on ${topDay.date}` + (topDay.inches ? ` (~${topDay.inches.toFixed(2)}" expected)` : "") : "";
    const cs = (() => { try { return JSON.parse(localStorage.getItem("grok_company_settings_" + (session?.companyId || "local")) || "{}"); } catch { return {}; }})();
    const companyName = cs.company_name || "Grok Construction Group Inc";
    const cslb = cs.cslb || "CSLB #1152750";

    const systemPrompt = `You are a senior project manager at ${companyName} (${cslb}), drafting a professional, concise weather delay notice. The notice will be sent by the GC to the owner, architect, and subcontractors.

Rules:
- Tone: professional, factual, respectful. No alarm, no apology, no fluff.
- Length: 4–6 short paragraphs maximum. Construction emails are read on phones.
- Cover all five required elements: project name, affected dates, weather condition, schedule impact, request for time extension if appropriate.
- A time extension is appropriate ONLY if the weather is likely to halt outdoor work on the affected day(s). State the requested days plainly.
- No signature block — the email system adds the sender. Do not end with "Sincerely" or a name.
- Do NOT invent specific milestones, costs, or critical path detail. Stay general unless the impact notes provide specifics.

Output STRICT JSON ONLY (no markdown, no code fence):
{ "subject": "...", "body": "Paragraph 1\\n\\nParagraph 2\\n\\n..." }`;

    const userMsg = `PROJECT
Name: ${proj.name || "—"}
Address: ${proj.addr || "—"}
Type: ${proj.type || "—"}

WEATHER FORECAST (Open-Meteo)
${peakLine}
Days with ≥30% rain probability in next 3 days: ${datesList}
Forecast detail: ${(risk?.rainDays || []).map(d => `${d.date}: ${d.probability}% (~${(d.inches || 0).toFixed(2)}")`).join("; ")}

GC IMPACT NOTES (may be empty):
${impactNotes.trim() || "(none provided — keep impact paragraph general)"}

Generate the weather delay notice email now.`;

    try {
      const r = await fetch(AI_PROXY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + sb._token(), "apikey": SUPABASE_ANON_KEY },
        body: JSON.stringify({
          max_tokens: 1500,
          usage_type: "chat",
          company_id: window._grokCompanyId || "local",
          plan: session?.plan || "starter",
          system: systemPrompt,
          messages: [{ role: "user", content: userMsg }],
        }),
      });
      const data = await r.json();
      if (!r.ok || data.error) {
        const msg = data.error?.message || data.error || ("HTTP " + r.status);
        throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
      }
      const text = data.content?.[0]?.text || "";
      const cleaned = text.replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "").trim();
      let parsed;
      try { parsed = JSON.parse(cleaned); }
      catch {
        const m = cleaned.match(/\{[\s\S]*\}/);
        if (!m) throw new Error("AI returned non-JSON: " + cleaned.slice(0, 160));
        parsed = JSON.parse(m[0]);
      }
      if (!parsed.subject || !parsed.body) throw new Error("AI response missing subject or body");
      setDraft({ subject: String(parsed.subject), body: String(parsed.body) });
    } catch (e) {
      setDraftErr(e.message || "Draft failed");
    } finally {
      setDrafting(false);
    }
  };

  const buildHtml = (bodyText) => {
    const paragraphs = bodyText.split(/\n\n+/).map(p => p.trim()).filter(Boolean);
    const cs = (() => { try { return JSON.parse(localStorage.getItem("grok_company_settings_" + (session?.companyId || "local")) || "{}"); } catch { return {}; }})();
    const companyName = cs.company_name || "Grok Construction Group Inc";
    const cslb = cs.cslb || "CSLB #1152750";
    const sender = session?.ownerName || cs.signer_name || companyName;
    return `<!DOCTYPE html><html><body style="margin:0;padding:0;background:#f6f5f1;font-family:Arial,sans-serif;color:#1a1a1a;">
      <div style="max-width:600px;margin:0 auto;background:#fff;padding:0;">
        <div style="background:#0F1623;padding:20px 28px;">
          <div style="font-size:22px;font-weight:900;color:#D4A843;">${companyName.replace(/</g, "&lt;")}</div>
          <div style="font-size:11px;color:#fff;font-weight:700;margin-top:3px;">${cslb.replace(/</g, "&lt;")}</div>
        </div>
        <div style="height:4px;background:#D4A843;"></div>
        <div style="padding:24px 28px;font-size:14px;line-height:1.6;">
          ${paragraphs.map(p => `<p style="margin:0 0 14px 0;">${p.replace(/</g, "&lt;").replace(/\n/g, "<br/>")}</p>`).join("\n")}
          <p style="margin:24px 0 0 0;color:#444;">— ${sender.replace(/</g, "&lt;")}<br/><span style="color:#888;font-size:12px;">${companyName.replace(/</g, "&lt;")} · ${cslb.replace(/</g, "&lt;")}</span></p>
        </div>
        <div style="padding:16px 28px;border-top:1px solid #eee;font-size:10px;color:#888;">
          Sent via Grok Field Ops · Weather forecast data: Open-Meteo
        </div>
      </div>
    </body></html>`;
  };

  const sendAll = async () => {
    if (!draft || !selectedRecipients.length) return;
    setSending(true);
    setSendLog(selectedRecipients.map(r => ({ ...r, status: "pending" })));
    const html = buildHtml(draft.body);
    const subject = draft.subject;
    const out = [];
    for (const r of selectedRecipients) {
      try {
        const resp = await fetch("https://qnjlirkgumhzzcavqsxt.supabase.co/functions/v1/send-email", {
          method: "POST",
          headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY },
          body: JSON.stringify({ to: r.email, subject, html }),
        });
        const d = await resp.json().catch(() => ({}));
        if (!resp.ok) out.push({ ...r, status: "err", msg: (d.error || ("HTTP " + resp.status)).toString().slice(0, 120) });
        else out.push({ ...r, status: "ok" });
      } catch (e) {
        out.push({ ...r, status: "err", msg: (e.message || "network error").slice(0, 120) });
      }
      setSendLog(prev => prev.map(p => p.email === r.email ? out[out.length - 1] : p));
    }
    setSending(false);
    setDoneSending(true);
    // Log the notice to the project record (best-effort, non-blocking)
    try {
      const key = "grok_weather_notices_" + proj.id;
      const prior = JSON.parse(localStorage.getItem(key) || "[]");
      prior.unshift({
        id: "wn_" + Date.now(),
        sent_at: new Date().toISOString(),
        subject, body: draft.body,
        recipients: out.map(o => ({ email: o.email, name: o.name, role: o.role, status: o.status })),
        forecast: risk,
      });
      localStorage.setItem(key, JSON.stringify(prior.slice(0, 50)));
    } catch {}
  };

  const sentCount = sendLog.filter(s => s.status === "ok").length;
  const errCount = sendLog.filter(s => s.status === "err").length;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: isMob ? 8 : 20 }}>
      <div style={{ background: T.bg1, borderRadius: 12, border: "1px solid " + T.subtle, maxWidth: 720, width: "100%", maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid " + T.subtle, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 18 }}>🌧</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>Weather Delay Notice</div>
            <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>
              Peak {topDay?.probability || risk?.risk || 0}% rain {topDay?.date ? "on " + topDay.date : "in next 3 days"} · {proj.name || "Project"}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: T.muted, fontSize: 18, cursor: "pointer", padding: 4 }}>✕</button>
        </div>

        <div style={{ padding: 16 }}>
          {!draft && !drafting && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 6 }}>Recipients ({selectedRecipients.length} selected)</div>
              {!recipients.length && (
                <div style={{ padding: 16, textAlign: "center", fontSize: 11, color: T.muted, background: T.bg, borderRadius: 8, border: "1px dashed " + T.subtle, marginBottom: 12 }}>
                  No owner / architect / sub emails on this project. Add them in Project Edit first.
                </div>
              )}
              {recipients.map(r => (
                <label key={r.email} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: T.bg, borderRadius: 7, marginBottom: 6, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!selected[r.email]} onChange={() => toggle(r.email)} style={{ cursor: "pointer" }}/>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: T.text, fontWeight: 700 }}>{r.name} <span style={{ color: T.muted, fontWeight: 400, fontSize: 10 }}>· {r.role}</span></div>
                    <div style={{ fontSize: 10, color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.email}</div>
                  </div>
                </label>
              ))}

              <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginTop: 14, marginBottom: 6 }}>Impact notes <span style={{ color: T.muted, fontWeight: 400 }}>(optional)</span></div>
              <textarea value={impactNotes} onChange={e => setImpactNotes(e.target.value)} rows={4}
                placeholder="e.g. We were scheduled to pour the slab on the worst-rain day. Need a 2-day extension."
                style={{ width: "100%", background: T.bg, border: "1px solid " + T.subtle, borderRadius: 7, padding: "9px 11px", fontSize: 12, color: T.text, fontFamily: "inherit", boxSizing: "border-box", resize: "vertical" }}/>
              <div style={{ fontSize: 9, color: T.muted, marginTop: 4 }}>ARIA will weave these notes into the email. Leave blank for a general notice.</div>

              {draftErr && <div style={{ marginTop: 12, padding: "10px 12px", background: T.redBg, border: "1px solid " + T.red + "60", borderRadius: 7, fontSize: 11, color: T.red }}>{draftErr}</div>}

              <button onClick={draftWithAI} disabled={!recipients.length}
                style={{ width: "100%", marginTop: 14, padding: "11px 14px", fontSize: 12, fontWeight: 800, background: recipients.length ? T.gold : T.bg2, color: recipients.length ? T.bg0 : T.muted, border: "none", borderRadius: 8, cursor: recipients.length ? "pointer" : "not-allowed" }}>
                🤖 Draft with ARIA
              </button>
            </>
          )}

          {drafting && (
            <div style={{ padding: 24, textAlign: "center", fontSize: 12, color: T.muted }}>
              <div style={{ fontSize: 22, marginBottom: 8 }}>✍️</div>
              ARIA is drafting your weather delay notice…
            </div>
          )}

          {draft && !doneSending && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 4 }}>Subject</div>
              <input value={draft.subject} onChange={e => setDraft({ ...draft, subject: e.target.value })}
                style={{ width: "100%", background: T.bg, border: "1px solid " + T.subtle, borderRadius: 7, padding: "9px 11px", fontSize: 12, color: T.text, fontFamily: "inherit", boxSizing: "border-box", marginBottom: 12 }}/>

              <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 4 }}>Body</div>
              <textarea value={draft.body} onChange={e => setDraft({ ...draft, body: e.target.value })} rows={isMob ? 10 : 14}
                style={{ width: "100%", background: T.bg, border: "1px solid " + T.subtle, borderRadius: 7, padding: "9px 11px", fontSize: 12, color: T.text, fontFamily: "inherit", boxSizing: "border-box", resize: "vertical", lineHeight: 1.6 }}/>

              <div style={{ fontSize: 10, color: T.muted, marginTop: 8, marginBottom: 10, padding: "8px 10px", background: T.bg, borderRadius: 6 }}>
                Will send to {selectedRecipients.length} recipient{selectedRecipients.length === 1 ? "" : "s"}: {selectedRecipients.map(r => r.name).join(", ")}
              </div>

              {!!sendLog.length && (
                <div style={{ marginBottom: 10 }}>
                  {sendLog.map((s, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, padding: "4px 0", color: s.status === "ok" ? T.green : s.status === "err" ? T.red : T.muted }}>
                      <span>{s.status === "ok" ? "✓" : s.status === "err" ? "✗" : "…"}</span>
                      <span style={{ flex: 1 }}>{s.name} <span style={{ color: T.muted }}>· {s.email}</span></span>
                      {s.msg && <span style={{ color: T.red, fontSize: 10 }}>{s.msg}</span>}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { setDraft(null); setSendLog([]); }} disabled={sending}
                  style={{ flex: 1, padding: "10px 14px", fontSize: 11, fontWeight: 700, background: "transparent", color: T.muted, border: "1px solid " + T.subtle, borderRadius: 7, cursor: sending ? "not-allowed" : "pointer" }}>
                  ← Back / Re-draft
                </button>
                <button onClick={sendAll} disabled={sending || !selectedRecipients.length}
                  style={{ flex: 2, padding: "10px 14px", fontSize: 11, fontWeight: 800, background: sending ? T.bg2 : T.green, color: sending ? T.muted : "white", border: "none", borderRadius: 7, cursor: sending ? "not-allowed" : "pointer" }}>
                  {sending ? "Sending… " + sendLog.filter(s => s.status !== "pending").length + " of " + sendLog.length : "Approve & Send to " + selectedRecipients.length}
                </button>
              </div>
            </>
          )}

          {doneSending && (
            <div style={{ textAlign: "center", padding: "16px 8px" }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>{errCount ? "⚠️" : "✅"}</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 6 }}>
                {sentCount} of {sendLog.length} sent{errCount ? " · " + errCount + " failed" : ""}
              </div>
              <div style={{ fontSize: 10, color: T.muted, marginBottom: 14 }}>Logged to project record.</div>
              {!!sendLog.length && (
                <div style={{ textAlign: "left", marginBottom: 12, padding: "8px 10px", background: T.bg, borderRadius: 7 }}>
                  {sendLog.map((s, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, padding: "3px 0", color: s.status === "ok" ? T.green : T.red }}>
                      <span>{s.status === "ok" ? "✓" : "✗"}</span>
                      <span style={{ flex: 1 }}>{s.name} <span style={{ color: T.muted }}>· {s.email}</span></span>
                      {s.msg && <span style={{ color: T.red, fontSize: 10 }}>{s.msg}</span>}
                    </div>
                  ))}
                </div>
              )}
              <button onClick={onClose} style={{ width: "100%", padding: "10px 14px", fontSize: 12, fontWeight: 800, background: T.gold, color: T.bg0, border: "none", borderRadius: 8, cursor: "pointer" }}>Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// 7-day weather forecast card. Auto-geocodes from project.addr on first load
// when no lat/lon is stored, then persists the coords back via onCoords.
function WeatherCard({ project, isMob, onCoords, onDraftDelay }) {
  const [fc, setFc] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const hasCoords = typeof project.lat === "number" && typeof project.lon === "number";

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setErr("");
      try {
        let lat = project.lat, lon = project.lon;
        if (typeof lat !== "number" || typeof lon !== "number") {
          if (!project.addr) return;
          setLoading(true);
          const geo = await Weather.geocode(project.addr);
          if (cancelled) return;
          if (!geo) { setErr("Couldn't locate this address — edit the project to refine it."); setLoading(false); return; }
          lat = geo.lat; lon = geo.lon;
          if (onCoords) onCoords({ lat, lon });
        }
        setLoading(true);
        const f = await Weather.forecast(lat, lon);
        if (cancelled) return;
        if (!f) setErr("Weather service unavailable — try again later.");
        else setFc(f);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [project.id, project.lat, project.lon, project.addr]);

  if (!project.addr && !hasCoords) return null;

  const cardBase = { background: T.card, borderRadius: 10, padding: "12px 14px", marginBottom: isMob?10:14, fontSize: 11, color: T.muted };
  if (loading && !fc) return <div style={{ ...cardBase, border: "1px solid "+T.border }}>Loading 7-day forecast…</div>;
  if (err) return <div style={{ ...cardBase, border: "1px solid "+T.border }}>{err}</div>;
  if (!fc || !fc.daily) return null;

  const risk = Weather.rainRisk(fc);
  const showRiskBanner = risk && risk.risk >= 50;
  const days = (fc.daily.time || []).slice(0, 7);
  const accentColor = showRiskBanner ? T.amber : T.border;

  return (
    <div style={{ background: T.card, border: "1px solid "+accentColor+(showRiskBanner?"60":""), borderRadius: 10, padding: "12px 14px", marginBottom: isMob?10:14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: T.text }}>☁ 7-Day Forecast</div>
        {showRiskBanner && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.amber, background: T.amberBg, padding: "3px 8px", borderRadius: 6 }}>
              🌧 {risk.topDay.probability}% rain {risk.topDay.date === days[0] ? "today" : "on "+risk.topDay.date}
            </div>
            {onDraftDelay && (
              <button onClick={() => onDraftDelay(risk)}
                style={{ fontSize: 10, fontWeight: 700, color: T.bg0, background: T.gold, border: "none", padding: "4px 10px", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap" }}>
                ✉ Draft Delay Notice
              </button>
            )}
          </div>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMob ? "repeat(4,1fr)" : "repeat(7,1fr)", gap: 6 }}>
        {days.map((date, i) => {
          const code = (fc.daily.weathercode || [])[i];
          const hi = Math.round((fc.daily.temperature_2m_max || [])[i] || 0);
          const lo = Math.round((fc.daily.temperature_2m_min || [])[i] || 0);
          const pop = (fc.daily.precipitation_probability_max || [])[i] || 0;
          const lbl = Weather.codeLabel(code);
          const dow = new Date(date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short" });
          return (
            <div key={date} style={{ textAlign: "center", padding: "6px 4px", background: T.bg, borderRadius: 6, border: "1px solid "+T.border }}>
              <div style={{ fontSize: 9, color: T.muted, fontWeight: 700 }}>{i === 0 ? "Today" : dow}</div>
              <div style={{ fontSize: 16, margin: "4px 0" }}>{lbl.icon}</div>
              <div style={{ fontSize: 10, color: T.text, fontWeight: 700 }}>{hi}°/{lo}°</div>
              {pop >= 30 && <div style={{ fontSize: 9, color: T.amber, marginTop: 2 }}>💧 {pop}%</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Construction-sequence ordering for the Scope Checklist tab. Trades are
// surfaced in the order they typically execute on a renovation/TI project so
// the GC can work top-to-bottom without re-sorting.
const SCOPE_PHASE_ORDER = [
  { id: "demo",        label: "Demolition",         icon: "🪓", trades: ["Demolition", "Demo"] },
  { id: "structural",  label: "Structural",         icon: "🏗",  trades: ["Structural", "Framing", "Carpentry — Rough", "Rough Framing"] },
  { id: "rough_mep",   label: "Rough MEP",          icon: "🔧", trades: ["Plumbing", "Electrical", "HVAC", "Mechanical", "Low Voltage", "Fire Sprinkler"] },
  { id: "insulation",  label: "Insulation & Waterproofing", icon: "🧊", trades: ["Insulation", "Waterproofing"] },
  { id: "drywall",     label: "Drywall & Tape",     icon: "🧱", trades: ["Drywall", "Tape", "Plaster"] },
  { id: "finishes",    label: "Finishes",           icon: "🎨", trades: ["Tile", "Tile & Finishes", "Flooring", "Painting", "Paint", "Cabinets", "Countertop", "Doors & Hardware", "Doors", "Trim", "Carpentry — Finish", "Fixtures", "Appliances", "Glazing", "Window"] },
  { id: "punch",       label: "Punch List & Closeout", icon: "☑",  trades: ["Punch", "Punch List", "Closeout", "Cleaning", "Final Cleaning", "Commissioning"] },
];

// Default fallback checklist when there's no blueprint analysis yet — covers
// the typical SRO/renovation scope by trade. Used only as a starter that the
// GC can edit/check off as work progresses.
const DEFAULT_SCOPE_TEMPLATE = [
  { trade: "Demolition", items: ["Site protection / dust barriers", "Selective demo (walls / ceilings / floors)", "Salvage owner-marked items", "Haul-off and disposal"] },
  { trade: "Framing",    items: ["New wall layout / framing", "Header / blocking", "Subfloor patching", "Backing for fixtures"] },
  { trade: "Plumbing",   items: ["Rough waste / vent", "Rough supply", "Tub / shower set", "Inspector sign-off — rough"] },
  { trade: "Electrical", items: ["Panel upgrade (if scoped)", "Rough-in receptacles / switches / lights", "Low-voltage rough", "Inspector sign-off — rough"] },
  { trade: "HVAC",       items: ["Equipment set", "Duct rough", "Refrigerant lines", "Inspector sign-off — rough"] },
  { trade: "Insulation", items: ["Wall insulation", "Floor / ceiling insulation", "Air sealing"] },
  { trade: "Drywall",    items: ["Hang drywall", "Tape + level 4 finish", "Texture (if scoped)", "Inspector sign-off — nailing"] },
  { trade: "Tile",       items: ["Backer / waterproofing", "Tile installation", "Grout + sealant"] },
  { trade: "Flooring",   items: ["Underlayment", "Floor install", "Transitions / base"] },
  { trade: "Paint",      items: ["Prime", "Finish coats — walls", "Finish coats — trim / doors", "Touch-up"] },
  { trade: "Doors & Hardware", items: ["Install doors", "Install hardware", "Adjust / final"] },
  { trade: "Fixtures",   items: ["Plumbing fixture set", "Light fixture set", "Appliance install"] },
  { trade: "Punch",      items: ["Walkthrough w/ owner", "Punch items resolved", "Final cleaning", "Final inspection / Certificate of Occupancy"] },
];

// Slot a trade name into the right construction phase. Falls through to
// "Finishes" for unknown trades so nothing gets dropped silently.
function phaseForTrade(tradeName) {
  const t = String(tradeName || "").trim().toLowerCase();
  for (const p of SCOPE_PHASE_ORDER) {
    if (p.trades.some(tr => tr.toLowerCase() === t)) return p;
    if (p.trades.some(tr => t.includes(tr.toLowerCase()) || tr.toLowerCase().includes(t))) return p;
  }
  return SCOPE_PHASE_ORDER[5]; // Finishes
}

// The tab itself. Source priority:
//   1) Aggregated blueprint deep-analysis (scope_by_trade across all bps)
//   2) Stored scope checklist (grok_scope_<projId>) from blueprint review
//   3) DEFAULT_SCOPE_TEMPLATE as starter
// Check state lives in grok_scope_checklist_<projId> keyed by "phaseId:tradeName:itemText".
function ScopeChecklistTab({proj, session, setTab}) {
  const T = useTheme();
  const isMob = window.innerWidth < 768;
  const storageKey = "grok_scope_checklist_" + proj.id;
  const [checks, setChecks] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || "{}"); }
    catch { return {}; }
  });
  const [refreshTick, setRefreshTick] = React.useState(0);

  // Pull all blueprint deep-analysis scope_by_trade arrays for this project.
  const aggregated = React.useMemo(() => {
    let bps = [];
    try { bps = JSON.parse(localStorage.getItem("grok_blueprints_" + proj.id) || "[]"); } catch {}
    // Each bp may have a deepStructured object stored after Deep Analysis runs.
    const trades = {};
    bps.forEach(bp => {
      const ds = bp.deepStructured || bp.deep_structured;
      if (ds && Array.isArray(ds.scope_by_trade)) {
        ds.scope_by_trade.forEach(entry => {
          const name = (entry.trade || "").trim();
          if (!name) return;
          if (!trades[name]) trades[name] = new Set();
          (entry.items || []).forEach(it => trades[name].add(String(it).trim()));
        });
      }
    });
    // Also pull any per-blueprint stored deep_structured JSON if separate key exists.
    try {
      const projScope = JSON.parse(localStorage.getItem("grok_scope_" + proj.id) || "null");
      if (projScope && Array.isArray(projScope.scope_by_trade)) {
        projScope.scope_by_trade.forEach(entry => {
          const name = (entry.trade || "").trim();
          if (!name) return;
          if (!trades[name]) trades[name] = new Set();
          (entry.items || []).forEach(it => trades[name].add(String(it).trim()));
        });
      }
    } catch {}
    return trades;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proj.id, refreshTick]);

  // Build the ordered phase → trade → items structure.
  const ordered = React.useMemo(() => {
    const hasAggregated = Object.keys(aggregated).length > 0;
    const phases = SCOPE_PHASE_ORDER.map(p => ({ ...p, trades: [] }));
    const phaseById = Object.fromEntries(phases.map(p => [p.id, p]));

    const addTradeItems = (tradeName, items) => {
      const phase = phaseForTrade(tradeName);
      const existing = phaseById[phase.id].trades.find(t => t.trade.toLowerCase() === tradeName.toLowerCase());
      if (existing) {
        items.forEach(it => { if (!existing.items.includes(it)) existing.items.push(it); });
      } else {
        phaseById[phase.id].trades.push({ trade: tradeName, items: [...items] });
      }
    };

    if (hasAggregated) {
      Object.entries(aggregated).forEach(([trade, itemSet]) => addTradeItems(trade, [...itemSet]));
    } else {
      DEFAULT_SCOPE_TEMPLATE.forEach(t => addTradeItems(t.trade, t.items));
    }

    return { phases: phases.filter(p => p.trades.length > 0), source: hasAggregated ? "blueprint" : "template" };
  }, [aggregated]);

  const keyFor = (phaseId, trade, item) => phaseId + ":" + trade + ":" + item;
  const cycle = (k) => {
    setChecks(prev => {
      const cur = prev[k] || "pending";
      const next = cur === "pending" ? "in_progress" : cur === "in_progress" ? "complete" : "pending";
      const out = { ...prev, [k]: next };
      try { localStorage.setItem(storageKey, JSON.stringify(out)); } catch {}
      return out;
    });
  };
  const setAll = (status) => {
    const out = { ...checks };
    ordered.phases.forEach(p => p.trades.forEach(t => t.items.forEach(it => {
      out[keyFor(p.id, t.trade, it)] = status;
    })));
    setChecks(out);
    try { localStorage.setItem(storageKey, JSON.stringify(out)); } catch {}
  };
  const clearAll = () => {
    if (!window.confirm("Reset all check marks on this scope checklist?")) return;
    setChecks({});
    try { localStorage.removeItem(storageKey); } catch {}
  };

  // Tallies
  const tally = React.useMemo(() => {
    let total = 0, pending = 0, inProgress = 0, complete = 0;
    ordered.phases.forEach(p => p.trades.forEach(t => t.items.forEach(it => {
      total++;
      const s = checks[keyFor(p.id, t.trade, it)] || "pending";
      if (s === "complete") complete++;
      else if (s === "in_progress") inProgress++;
      else pending++;
    })));
    return { total, pending, inProgress, complete };
  }, [ordered, checks]);

  const overallPct = tally.total ? Math.round((tally.complete / tally.total) * 100) : 0;

  const statusMeta = {
    pending:     { icon: "○", label: "Pending",     color: T.muted },
    in_progress: { icon: "◐", label: "In Progress", color: T.amber },
    complete:    { icon: "●", label: "Complete",    color: T.green },
  };

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,gap:8,flexWrap:"wrap"}}>
        <button onClick={()=>setTab("field")} style={{background:"none",border:`1px solid ${T.subtle}`,borderRadius:7,padding:"5px 10px",cursor:"pointer",color:T.muted,fontSize:12,fontWeight:700}}>← Field</button>
        <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
          <button onClick={()=>setRefreshTick(t=>t+1)} title="Re-sync from blueprints"
            style={{background:T.bg2,border:`1px solid ${T.subtle}`,color:T.muted,borderRadius:6,padding:"5px 10px",fontSize:10,fontWeight:700,cursor:"pointer"}}>↻ Refresh</button>
          <button onClick={()=>setAll("in_progress")}
            style={{background:T.amberBg,border:`1px solid ${T.amber}40`,color:T.amber,borderRadius:6,padding:"5px 10px",fontSize:10,fontWeight:700,cursor:"pointer"}}>Mark all in-progress</button>
          <button onClick={clearAll}
            style={{background:"transparent",border:`1px solid ${T.subtle}`,color:T.muted,borderRadius:6,padding:"5px 10px",fontSize:10,fontWeight:700,cursor:"pointer"}}>Reset</button>
        </div>
      </div>

      {/* Header card */}
      <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:10,padding:"14px 16px",marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:10,marginBottom:8,flexWrap:"wrap"}}>
          <div>
            <div style={{fontSize:14,fontWeight:800,color:T.text}}>📋 Scope Checklist</div>
            <div style={{fontSize:10,color:T.muted,marginTop:3}}>
              {ordered.source === "blueprint" ? "Built from blueprint deep analysis. " : "Default template — run Deep Analysis on a blueprint for a project-specific list. "}
              Tap each item to advance Pending → In Progress → Complete.
            </div>
          </div>
          <div style={{display:"flex",gap:10,alignItems:"baseline",flexShrink:0}}>
            <div style={{fontSize:24,fontWeight:800,color:overallPct===100?T.green:T.gold,fontFamily:"'Space Mono', monospace"}}>{overallPct}%</div>
            <div style={{fontSize:10,color:T.muted}}>{tally.complete}/{tally.total} complete</div>
          </div>
        </div>
        {/* Tally row */}
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {[
            { k: "pending",     n: tally.pending,    label: "Pending",     col: T.muted },
            { k: "in_progress", n: tally.inProgress, label: "In Progress", col: T.amber },
            { k: "complete",    n: tally.complete,   label: "Complete",    col: T.green },
          ].map(s => (
            <div key={s.k} style={{display:"flex",gap:5,alignItems:"center",fontSize:10,color:T.muted,background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"3px 9px"}}>
              <span style={{color:s.col,fontWeight:800,fontSize:13}}>{s.n}</span>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Phases */}
      {ordered.phases.length === 0 ? (
        <div style={{padding:30,textAlign:"center",fontSize:12,color:T.muted,background:T.bg,borderRadius:10,border:`1px dashed ${T.subtle}`}}>
          No scope items yet. Run Deep Analysis on a blueprint to auto-generate this list.
        </div>
      ) : (
        ordered.phases.map((phase, pi) => {
          // Per-phase tallies for the section header
          let pTotal = 0, pDone = 0;
          phase.trades.forEach(t => t.items.forEach(it => {
            pTotal++;
            if ((checks[keyFor(phase.id, t.trade, it)] || "pending") === "complete") pDone++;
          }));
          return (
            <div key={phase.id} style={{marginBottom:14}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:8,padding:"6px 0",borderBottom:`2px solid ${T.subtle}`}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{width:24,height:24,borderRadius:"50%",background:pDone===pTotal?T.greenBg:T.bg2,color:pDone===pTotal?T.green:T.muted,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:10}}>{pi+1}</span>
                  <span style={{fontSize:14}}>{phase.icon}</span>
                  <span style={{fontSize:13,fontWeight:800,color:T.text}}>{phase.label}</span>
                </div>
                <span style={{fontSize:10,fontWeight:700,color:pDone===pTotal&&pTotal>0?T.green:T.muted}}>{pDone}/{pTotal}</span>
              </div>
              {phase.trades.map((trade, ti) => (
                <div key={ti} style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"10px 12px",marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                    <span style={{fontSize:11,fontWeight:800,color:T.text,textTransform:"uppercase",letterSpacing:".04em"}}>{trade.trade}</span>
                    <span style={{fontSize:9,color:T.muted}}>{trade.items.length} item{trade.items.length===1?"":"s"}</span>
                  </div>
                  {trade.items.map((item, ii) => {
                    const k = keyFor(phase.id, trade.trade, item);
                    const status = checks[k] || "pending";
                    const meta = statusMeta[status];
                    return (
                      <div key={ii} onClick={()=>cycle(k)}
                        style={{display:"flex",alignItems:"flex-start",gap:9,padding:"7px 4px",cursor:"pointer",borderBottom:ii<trade.items.length-1?`1px solid ${T.subtle}`:"none",transition:"background 0.1s"}}
                        onMouseEnter={e=>e.currentTarget.style.background=T.bg2}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <span style={{color:meta.color,fontSize:16,fontWeight:800,lineHeight:1,marginTop:1,flexShrink:0,width:18,textAlign:"center"}}>{meta.icon}</span>
                        <span style={{flex:1,fontSize:11.5,color:status==="complete"?T.muted:T.text,lineHeight:1.5,textDecoration:status==="complete"?"line-through":"none"}}>{item}</span>
                        <span style={{fontSize:9,fontWeight:700,color:meta.color,textTransform:"uppercase",letterSpacing:".04em",flexShrink:0,marginTop:2}}>{meta.label}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          );
        })
      )}
    </div>
  );
}

function ProjectDetail({project, projects, setProjects, setView, setActiveProject, session}) {
  const [tab, setTab] = useState(() => {
    try {
      const hint = localStorage.getItem("grok_project_initial_tab");
      if (hint) { localStorage.removeItem("grok_project_initial_tab"); return hint; }
    } catch {}
    return "field";
  });
  const [showEdit, setShowEdit] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPhotoProgress, setShowPhotoProgress] = useState(false);
  const [delayNoticeRisk, setDelayNoticeRisk] = useState(null);
  const [inspectionPromptIdx, setInspectionPromptIdx] = useState(null);
  const [proj, setProj] = useState(project);
  const [walkChecks, setWalkChecks] = useState(proj.walk || {access:{},plumbing:{},walls:{},owner:{}});
  const [redFlags, setRedFlags] = useState(proj.redFlags || {});
  const [photos, setPhotos] = useState([]);
  const [blueprints, setBlueprints] = useState([]);
  const [aiScopeResult, setAiScopeResult] = useState("");
  const [aiScopeLoading, setAiScopeLoading] = useState(false);
  const [aiRiskResult, setAiRiskResult] = useState("");
  const [aiRiskLoading, setAiRiskLoading] = useState(false);
  const [riskChecks, setRiskChecks] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const photoRef = useRef(null);
  const blueprintRef = useRef(null);
  // Synchronous guard against double-firing mobilization drafts when the
  // range slider fires onChange repeatedly before React re-renders with the
  // updated phasesNotified flag.
  const draftFiredRef = useRef({});
  const togRisk = k => setRiskChecks(p=>({...p,[k]:!p[k]}));

  // Interactive form state
  const [walkForm, setWalkForm] = useState({
    ownerName:"", ownerPhone:"", ownerEmail:"", buildingAge:"",
    // Owner Q&A answers
    qa_leaks:"", qa_mold:"", qa_plumbing:"", qa_renovated:"", qa_permits:"", qa_occupied:"",
    qa_asbestos:"", qa_structural:"", qa_pest:"", qa_notes:"",
    // Conditions observed
    cond_access:"", cond_parking:"", cond_hours:"", cond_key:"",
    cond_drain_location:"", cond_panel_amps:"", cond_panel_breakers:"",
    cond_water_shutoff:"", cond_moisture_notes:"",
    cond_walls_notes:"", cond_ceiling_notes:"", cond_floor_notes:"",
    cond_smell_notes:"", cond_building_notes:"",
    // Signatures
    grok_signed:"", grok_sign_date:"", owner_signed:"", owner_sign_date:"",
    walkthrough_date: new Date().toLocaleDateString(),
  });
  const [scopeForm, setScopeForm] = useState({
    // Scope inclusions/exclusions
    demo_included:"", demo_excluded:"",
    framing_included:"", framing_excluded:"",
    plumbing_included:"", plumbing_excluded:"",
    electrical_included:"", electrical_excluded:"",
    hvac_included:"", hvac_excluded:"",
    drywall_included:"", drywall_excluded:"",
    tile_included:"", tile_excluded:"",
    flooring_included:"", flooring_excluded:"",
    cabinets_included:"", cabinets_excluded:"",
    paint_included:"", paint_excluded:"",
    doors_included:"", doors_excluded:"",
    fixtures_included:"", fixtures_excluded:"",
    // Hidden condition protocol
    hidden_cond_protocol:"yes",
    hidden_cond_price_approval:"yes",
    hidden_cond_stop_work:"yes",
    // Change order thresholds
    co_threshold:"500",
    co_approval_method:"written",
    // Additional exclusions
    additional_exclusions:"",
    // Permit responsibility
    permits_by:"grok",
    // Prevailing wage
    prevailing_wage:"unknown",
    // Owner acknowledgments
    scope_final: false, exclusions_understood: false, co_process_understood: false, hidden_cond_understood: false,
    // Signatures
    grok_signed:"", grok_title:"", grok_sign_date:"",
    owner_signed:"", owner_title:"", owner_sign_date:"",
    scope_date: new Date().toLocaleDateString(),
  });
  const wf = (k,v) => setWalkForm(p=>({...p,[k]:v}));
  const sf = (k,v) => setScopeForm(p=>({...p,[k]:v}));
  const [walkFormTab, setWalkFormTab] = useState("info");
  const [scopeFormTab, setScopeFormTab] = useState("scope");

  const saveProject = (updated) => {
    PROJECTS = PROJECTS.map(p=>p.id===updated.id?updated:p);
    setProjects(projects.map(p=>p.id===updated.id?updated:p));
    setProj(updated);
    setActiveProject(updated);
  };

  const archiveProject = () => {
    const updated = {...proj, status:"archived"};
    saveProject(updated);
    setView("projects");
  };

  const saveWalkthrough = () => {
    const totalItems = 20;
    const done = Object.values(walkChecks).reduce((s,grp)=>s+Object.values(grp).filter(Boolean).length,0);
    const complete = done >= totalItems * 0.8;
    const updated = {...proj, walk:walkChecks, redFlags, walkComplete:complete};
    saveProject(updated);
  };

  // Phase value getter — handles both number form ([55, 100, ...]) and
  // object form ([{name, pct, budget}, ...]) used by some sample projects.
  const phaseVal = (entry) => (entry && typeof entry === "object") ? (entry.pct || 0) : (entry || 0);

  const updatePhase = (idx, newPct) => {
    const phases = [...(proj.phases || Array(7).fill(0))];
    while (phases.length <= idx) phases.push(0);
    // Preserve object form for sample projects, otherwise store number
    if (phases[idx] && typeof phases[idx] === "object") {
      phases[idx] = {...phases[idx], pct: newPct};
    } else {
      phases[idx] = newPct;
    }
    const phasesNotified = [...(proj.phasesNotified || Array(7).fill(false))];
    while (phasesNotified.length <= idx) phasesNotified.push(false);
    const fireKey = (proj.id || "p") + "_" + idx;
    const shouldDraft =
      newPct >= 80 &&
      !phasesNotified[idx] &&
      !draftFiredRef.current[fireKey] &&
      PHASE_TRADES[idx]?.trades?.length > 0;
    if (shouldDraft) { phasesNotified[idx] = true; draftFiredRef.current[fireKey] = true; }

    // Pay-app auto-draft when a milestone reaches 100%. Separate dedup flag —
    // a phase can hit 80% (mobilization) and 100% (pay app) on the same job
    // and we want both to fire exactly once.
    const phasesPaidApp = [...(proj.phasesPaidApp || Array(7).fill(false))];
    while (phasesPaidApp.length <= idx) phasesPaidApp.push(false);
    const payAppFireKey = "payapp_" + (proj.id || "p") + "_" + idx;
    const shouldDraftPayApp =
      newPct >= 100 &&
      !phasesPaidApp[idx] &&
      !draftFiredRef.current[payAppFireKey];
    if (shouldDraftPayApp) { phasesPaidApp[idx] = true; draftFiredRef.current[payAppFireKey] = true; }

    // Inspection prompt when a milestone hits 100% and inspections are typically
    // required for that phase (see INSPECTIONS_BY_PHASE). Fires once per phase.
    const phasesInspectionPrompted = [...(proj.phasesInspectionPrompted || Array(7).fill(false))];
    while (phasesInspectionPrompted.length <= idx) phasesInspectionPrompted.push(false);
    const inspFireKey = "insp_" + (proj.id || "p") + "_" + idx;
    const shouldPromptInsp =
      newPct >= 100 &&
      !phasesInspectionPrompted[idx] &&
      !draftFiredRef.current[inspFireKey] &&
      (INSPECTIONS_BY_PHASE[idx] || []).length > 0;
    if (shouldPromptInsp) { phasesInspectionPrompted[idx] = true; draftFiredRef.current[inspFireKey] = true; }

    const overallPct = Math.round(phases.reduce((s, e) => s + phaseVal(e), 0) / phases.length);
    const updated = {...proj, phases, phasesNotified, phasesPaidApp, phasesInspectionPrompted, pct: overallPct, progress: overallPct};
    saveProject(updated);

    if (shouldDraft) queueMobilizationDrafts(idx, updated);
    if (shouldDraftPayApp) queuePayAppDraft(idx, updated);
    if (shouldPromptInsp) setInspectionPromptIdx(idx);
  };

  // ARIA drafts a G702 pay application when a milestone hits 100%. Background.
  const queuePayAppDraft = (phaseIdx, updatedProj) => {
    const cid = session?.companyId || window._grokCompanyId || "local";
    const phaseName = PHASE_NAMES_LOCAL[phaseIdx] || `Phase ${phaseIdx + 1}`;
    draftPayAppFromMilestone({
      proj: updatedProj, phaseIndex: phaseIdx, phaseName, projCOs, session, cid,
    }).catch(()=>{});
  };

  // Generate one pending notification per matching sub for the next phase's
  // trades. Runs in the background — UI state isn't blocked on AI completion.
  const queueMobilizationDrafts = (phaseIdx, updatedProj) => {
    const cid = session?.companyId || window._grokCompanyId || "local";
    const phaseName = PHASE_NAMES_LOCAL[phaseIdx];
    const nextDef = PHASE_TRADES[phaseIdx];
    if (!nextDef || !nextDef.trades?.length) return;

    let subs = [];
    try { subs = getSubAccounts().filter(s => s.companyId === cid); } catch {}

    const projectName = (updatedProj.name || "").split(" — ")[0] || updatedProj.name || "Project";
    const gcCompany = session?.companyName || "";
    const gcOwner = session?.ownerName || (session?.email || "").split("@")[0] || "";

    nextDef.trades.forEach((trade) => {
      const tradeSubs = subs.filter(s => (s.trade || "").toLowerCase() === trade.toLowerCase());
      if (tradeSubs.length === 0) {
        // No contact on file — still queue a placeholder so the GC knows ARIA wanted to notify
        const notif = {
          id: "notif_" + Date.now() + "_" + Math.random().toString(36).slice(2,6),
          projectId: updatedProj.id, projectName,
          phaseIndex: phaseIdx, phaseName, nextPhaseName: nextDef.nextPhase,
          trade, subEmail: null, subName: null, subCompany: null,
          draftSubject: `Mobilize ${trade} on ${projectName}? (no sub on file)`,
          draftBody: `${phaseName} hit 80%+ on ${projectName}. Add a ${trade} sub to your project subs, then we'll prep the email.`,
          status: "needs_contact",
          createdAt: new Date().toISOString(),
        };
        PendingNotifs.add(cid, notif);
        try { window.dispatchEvent(new Event("grok-pending-notifs-changed")); } catch {}
        return;
      }
      tradeSubs.forEach(async (sub) => {
        const id = "notif_" + Date.now() + "_" + Math.random().toString(36).slice(2,6);
        const stub = {
          id, projectId: updatedProj.id, projectName,
          phaseIndex: phaseIdx, phaseName, nextPhaseName: nextDef.nextPhase,
          trade, subEmail: sub.email, subName: sub.name, subCompany: sub.company,
          draftSubject: `Drafting…`, draftBody: `ARIA is drafting your email — refresh in a few seconds.`,
          status: "drafting",
          createdAt: new Date().toISOString(),
        };
        PendingNotifs.add(cid, stub);
        try { window.dispatchEvent(new Event("grok-pending-notifs-changed")); } catch {}
        const draft = await draftMobilizationEmail({
          proj: updatedProj, phaseName, nextPhaseName: nextDef.nextPhase, trade,
          subName: sub.name, subCompany: sub.company,
          gcCompanyName: gcCompany, gcOwnerName: gcOwner,
          plan: session?.plan || "starter", cid,
        });
        PendingNotifs.update(cid, id, {
          draftSubject: draft.subject, draftBody: draft.body, status: "pending",
        });
        try { window.dispatchEvent(new Event("grok-pending-notifs-changed")); } catch {}
      });
    });
  };

  const PHASE_NAMES_LOCAL = ["Demo","Rough Framing","MEP Rough-In","Drywall & Tape","Tile & Finish","Paint","Closeout"];

  const pcc = p => p.pct>70?T.green:p.pct>30?T.amber:T.blue;
  const projCOs = (()=>{try{const cid=session?.companyId||window._grokCompanyId||"local";const all=JSON.parse(localStorage.getItem("grok_cos_global_"+cid)||"[]");return all.length?all:COS;}catch{return COS;}})();
  const projRFIs = (()=>{try{const cid=window._grokCompanyId||"local";return JSON.parse(localStorage.getItem("grok_rfis_global_"+cid)||"[]");}catch{return RFIS;}})().filter(r=>r.status!=="closed"||true);

  const isMob = window.innerWidth < 768;
  const tabBtn = (id, label, badge, badgeColor) => {
    // Short labels for mobile
    const shortLabels = {
      "📍 Field": "Field", "💰 Finance": "Finance", "🔧 Subs": "Subs",
      "📁 Admin": "Admin", "Overview": "Overview", "Blueprints": "Plans",
      "☑ Punch List": "Punch", "📐 Inspection & Billing": "Inspect",
      "Pre-Con": "Pre-Con", "👥 Team": "Team", "💬 Project Thread": "Thread",
    };
    const displayLabel = isMob ? (shortLabels[label] || label.replace(/[📍💰🔧📁☑📐👥💬]\s*/u,"").slice(0,8)) : label;
    return (
      <button onClick={(e)=>{e.stopPropagation();setTab(id);}}
        style={{padding:isMob?"6px 10px":"7px 14px",borderRadius:8,fontSize:isMob?11:12,fontWeight:600,cursor:"pointer",border:"none",background:tab===id?T.gold:T.bg2,color:tab===id?T.bg0:T.muted,transition:"all 0.15s",display:"flex",alignItems:"center",gap:4,flexShrink:0,whiteSpace:"nowrap"}}>
        {displayLabel}
        {badge>0&&<span style={{background:tab===id?"rgba(0,0,0,0.2)":badgeColor,color:"white",fontSize:9,fontWeight:800,padding:"1px 5px",borderRadius:10,minWidth:16,textAlign:"center"}}>{badge}</span>}
      </button>
    );
  };

  const WALK_STEPS = [
    {key:"access", label:"Access & Site", items:[
      {id:"s1",l:"Building entrance accessible"},
      {id:"s2",l:"Parking / staging confirmed"},
      {id:"s3",l:"Access hours confirmed with manager"},
      {id:"s4",l:"Key / code obtained and tested"},
      {id:"s5",l:"Existing conditions photographed BEFORE anything"},
    ]},
    {key:"plumbing", label:"Plumbing & Electrical", items:[
      {id:"p1",l:"Main water shut-off located"},
      {id:"p2",l:"Drain locations confirmed — no conflicts",warn:true},
      {id:"p3",l:"Moisture at connections checked"},
      {id:"p4",l:"Electrical panel located — amperage noted"},
      {id:"p5",l:"GFCI near water sources verified"},
    ]},
    {key:"walls", label:"Walls & Red Flags", items:[
      {id:"w1",l:"Drywall — water stains or soft spots"},
      {id:"w2",l:"Ceiling — stains or leaks from above"},
      {id:"w3",l:"Floor — soft spots or bounce"},
      {id:"w4",l:"Musty or mold smell noted",warn:true},
      {id:"w5",l:"Building age — pre-1978 flagged",warn:true},
    ]},
    {key:"owner", label:"Owner Q&A", items:[
      {id:"q1",l:"Asked: any water damage or leaks before?"},
      {id:"q2",l:"Asked: mold found in unit or building?"},
      {id:"q3",l:"Asked: known plumbing or drain issues?"},
      {id:"q4",l:"Asked: renovated before? Permits pulled?"},
      {id:"q5",l:"Asked: unit occupied during construction?"},
    ]},
  ];
  const RED_FLAGS = ["Water stains / soft drywall","Musty smell / mold","Drain location conflict","Pre-1978 building","Visible mold","Non-standard electrical"];

  const totalWalkItems = WALK_STEPS.reduce((s,st)=>s+st.items.length,0);
  const doneWalkItems = WALK_STEPS.reduce((s,st)=>s+st.items.filter(i=>walkChecks[st.key]?.[i.id]).length,0);
  const flagCount = RED_FLAGS.filter(f=>redFlags[f]).length;

  const togWalk = (group, id) => setWalkChecks(p=>({...p,[group]:{...p[group],[id]:!p[group]?.[id]}}));
  const togFlag = (f) => setRedFlags(p=>({...p,[f]:!p[f]}));

  const CB = ({group, id, label, warn}) => {
    const checked = walkChecks[group]?.[id];
    const color = warn&&checked ? T.red : checked ? T.green : T.muted;
    return (
      <div onClick={()=>togWalk(group,id)} style={{display:"flex",gap:9,alignItems:"flex-start",padding:"7px 9px",borderRadius:7,cursor:"pointer",background:checked?(warn?T.redBg:T.greenBg):"transparent",transition:"background 0.15s",marginBottom:3}}>
        <div style={{width:15,height:15,borderRadius:3,border:`2px solid ${checked?(warn?T.red:T.green):T.subtle}`,background:checked?(warn?T.red:T.green):"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1,transition:"all 0.15s"}}>
          {checked&&<Ic d={ICONS.check} size={8} style={{color:T.bg0}}/>}
        </div>
        <span style={{fontSize:12,color:checked?color:T.text}}>{label}</span>
      </div>
    );
  };

  return (
    <div style={{padding:isMob?"10px 12px":"24px",paddingBottom:isMob?"100px":"24px"}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:isMob?10:20,flexWrap:isMob?"wrap":"nowrap"}}>
        <button onClick={()=>setView("projects")} style={{background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"6px 10px",color:T.muted,fontSize:12,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>← Back</button>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:isMob?14:18,fontWeight:800,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{proj.name}</div>
          <div style={{fontSize:10,color:T.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{proj.addr} · ${((proj.value||proj.budget||0)/1000).toFixed(0)}K</div>
        </div>
        <div style={{display:"flex",gap:6,flexShrink:0}}>
          <Btn small ghost onClick={()=>setShowEdit(true)}>Edit</Btn>
          <Btn small danger onClick={()=>setShowArchiveConfirm(true)}>Archive</Btn>
          <Btn small danger onClick={()=>setShowDeleteConfirm(true)} style={{background:T.redBg,borderColor:T.red+"40"}}>Delete</Btn>
        </div>
      </div>

      {/* Stats row */}
      <div className="stat-grid-5" style={{display:"grid",gridTemplateColumns:isMob?"1fr 1fr":"repeat(5,1fr)",gap:isMob?8:10,marginBottom:isMob?10:16}}>
        <StatCard label="Progress" value={proj.pct+"%"} color={pcc(proj)}/>
        <StatCard label="Contract Value" value={"$"+(((proj.value||proj.budget||0)/1000).toFixed(0))+"K"}/>
        <StatCard label="Open COs" value={projCOs.filter(c=>c.status==="pending").length} color={projCOs.filter(c=>c.status==="pending").length>0?T.red:T.green}/>
        <StatCard label="Open RFIs" value={projRFIs.filter(r=>r.status!=="closed").length} color={projRFIs.filter(r=>r.status!=="closed").length>0?T.amber:T.green}/>
        <StatCard label="Walkthrough" value={proj.walkComplete?"Done":"Pending"} color={proj.walkComplete?T.green:T.amber}/>
      </div>

      <WeatherCard
        project={proj}
        isMob={isMob}
        onCoords={(c) => saveProject({ ...proj, lat: c.lat, lon: c.lon, geocoded_at: Date.now() })}
        onDraftDelay={(risk) => setDelayNoticeRisk(risk)}
      />

      {/* §8204 PRELIMINARY NOTICE BANNER — shows until GC marks it served.
          Print, serve via certified mail to owner + lender within 20 days,
          mark served. Without this, lien rights are forfeited for any work
          performed before service. */}
      {proj.preliminaryNotice && !proj.preliminaryNotice.served && (
        <div style={{background:`linear-gradient(135deg,${T.amberBg},${T.amber}10)`,border:`1px solid ${T.amber}60`,borderRadius:10,padding:"10px 14px",marginBottom:isMob?10:14,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <span style={{fontSize:20}}>📜</span>
          <div style={{flex:1,minWidth:200}}>
            <div style={{fontSize:12,fontWeight:800,color:T.amber}}>§8204 Preliminary Notice — Action Required</div>
            <div style={{fontSize:10,color:T.muted,marginTop:2,lineHeight:1.5}}>
              ARIA pre-filled the statutory form. Print, serve via certified mail on the owner (and lender if any) within 20 days of starting work — otherwise lien rights are forfeited for pre-service work.
              {proj.preliminaryNotice.deadline && <> Deadline: <strong style={{color:T.text}}>{proj.preliminaryNotice.deadline}</strong>.</>}
            </div>
          </div>
          <div style={{display:"flex",gap:6,flexShrink:0}}>
            <button onClick={()=>{
              const w = window.open("", "_blank");
              if (!w) { alert("Allow popups to print."); return; }
              w.document.write(generatePreliminaryNoticeHTML(proj, session));
              w.document.close();
              setTimeout(()=>w.print(), 400);
            }}
              style={{background:T.amber,color:T.bg0,border:"none",borderRadius:7,padding:"7px 13px",fontSize:11,fontWeight:800,cursor:"pointer"}}>
              🖨 Print &amp; Serve
            </button>
            <button onClick={()=>{
              const to = window.prompt("Who did you serve it on? (e.g. 'Owner, Lender')", "Owner");
              if (to == null) return;
              const updated = {
                ...proj,
                preliminaryNotice: {
                  ...proj.preliminaryNotice,
                  served: true,
                  served_at: new Date().toISOString(),
                  served_to: (to || "").split(",").map(s=>s.trim()).filter(Boolean),
                },
              };
              saveProject(updated);
            }}
              style={{background:"transparent",color:T.muted,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"7px 13px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
              ✓ Mark Served
            </button>
          </div>
        </div>
      )}

      {/* Tab bar — scrollable on mobile */}
      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:12}}>
        <button onClick={()=>setView("projects")}
          style={{flexShrink:0,background:"none",border:`1px solid ${T.subtle}`,borderRadius:7,padding:"5px 10px",cursor:"pointer",color:T.muted,fontSize:12,fontWeight:700,whiteSpace:"nowrap"}}>
          ← Projects
        </button>
      <div style={{display:"flex",gap:isMob?2:4,overflowX:"auto",flexWrap:"nowrap",paddingBottom:4,WebkitOverflowScrolling:"touch",scrollbarWidth:"none",msOverflowStyle:"none",flex:1}}>
        {tabBtn("aria-project","✨ ARIA",0,T.gold)}
        {tabBtn("field","📍 Field")}
        {canSee(session,"financials")&&tabBtn("finance","💰 Finance")}
        {tabBtn("subportal","🔧 Subs",SUB_PORTAL_DATA.issues.filter(i=>(i.projectId===proj.id?.toString()||i.projectId===proj.id)&&i.status==="open").length,T.teal)}
        {tabBtn("admin","📁 Admin",projCOs.filter(c=>c.status==="pending").length+projRFIs.filter(r=>r.status!=="closed").length,T.amber)}
        
        {tabBtn("files","Blueprints")}
        {tabBtn("scope-checklist","📋 Scope Checklist")}
        {tabBtn("punch","☑ Punch List")}
        {tabBtn("inspection","📐 Inspection & Billing")}
        {tabBtn("units","🏢 Units")}
        {tabBtn("draw","💵 Draw Schedule")}
        {tabBtn("payapp","📋 Pay Apps")}
        {tabBtn("photolog","📷 Photo Log")}
        {tabBtn("equipment","🚜 Equipment")}
        {tabBtn("payroll","📑 Payroll")}
        {tabBtn("investor","📊 Owner Report")}
        {tabBtn("precon","📋 Walkthrough")}
        {tabBtn("team","👥 Team")}
        {tabBtn("thread","🧠 Hub")}
      </div>
      </div>

      {/* OVERVIEW TAB */}
      {tab==="field"&&(
        <FieldDashboard proj={proj} projCOs={projCOs} projRFIs={projRFIs} setTab={setTab}/>
      )}
      {tab==="finance"&&(
        <FinanceSubNav proj={proj} projCOs={projCOs} session={session}/>
      )}
      {tab==="admin"&&(
        <AdminSubNav proj={proj} projCOs={projCOs} projRFIs={projRFIs} session={session}/>
      )}
      {tab==="budget"&&(
        <BudgetTracker proj={proj} projCOs={projCOs} session={session}/>
      )}
      {tab==="aria-project"&&(
        <ProjectARIA proj={proj} session={session} setTab={setTab}/>
      )}
      {tab==="chat"&&(
        <ProjectChat proj={proj} session={session}/>
      )}
      {tab==="client"&&(
        <ClientPortalView proj={proj} projCOs={projCOs} projRFIs={projRFIs} session={session}/>
      )}
      {tab==="time"&&(
        <TimeTracker proj={proj} session={session}/>
      )}
      {tab==="bids"&&(
        <div>
          <RFQSender proj={proj} session={session}/>
          <SubBidCompare proj={proj}/>
        </div>
      )}
      {tab==="payroll"&&(
        <CertifiedPayrollView proj={proj} session={session}/>
      )}
      {tab==="subportal"&&proj&&(
        <ProjectSubsPanel proj={proj} session={session}/>
      )}
      {tab==="overview"&&(
        <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <Card>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:6}}>
              <SectionTitle>Phase Progress</SectionTitle>
              <button onClick={()=>setShowPhotoProgress(true)}
                style={{padding:"5px 10px",fontSize:10,fontWeight:700,background:T.gold,color:T.bg0,border:"none",borderRadius:6,cursor:"pointer",whiteSpace:"nowrap"}}>
                🎯 Detect from photos
              </button>
            </div>
            <div style={{fontSize:10,color:T.muted,marginBottom:10,lineHeight:1.6}}>
              Drag to update. At 80%, ARIA auto-drafts a mobilization email to the next trade — review it on your dashboard before it sends.
            </div>
            {PHASE_NAMES_LOCAL.map((name,i)=>{
              const val = phaseVal((proj.phases||[])[i]);
              const notified = !!(proj.phasesNotified||[])[i];
              const nextTrades = PHASE_TRADES[i]?.trades || [];
              const hasInspections = (INSPECTIONS_BY_PHASE[i]||[]).length>0;
              return (
                <div key={i} style={{display:"flex",alignItems:"center",gap:9,padding:"10px 0",borderBottom:i<6?`1px solid ${T.subtle}`:"none"}}>
                  <div style={{width:22,height:22,borderRadius:"50%",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:800,background:val===100?T.greenBg:val>0?T.goldBg:T.bg3,color:val===100?T.green:val>0?T.gold:T.muted}}>
                    {val===100?"✓":i+1}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",fontSize:12,color:T.text,marginBottom:5,gap:6,flexWrap:"wrap"}}>
                      <span style={{fontWeight:val>0&&val<100?700:400}}>
                        {name}
                        {notified && nextTrades.length>0 && (
                          <span style={{marginLeft:6,fontSize:9,color:T.gold,fontWeight:600}}>· {nextTrades.join(", ")} notified</span>
                        )}
                        {val===100 && hasInspections && (
                          <button onClick={()=>setInspectionPromptIdx(i)}
                            style={{marginLeft:6,fontSize:9,fontWeight:700,color:T.blue,background:"transparent",border:"1px solid "+T.blue+"60",borderRadius:5,padding:"1px 6px",cursor:"pointer"}}>
                            📋 Schedule inspection
                          </button>
                        )}
                      </span>
                      <span style={{color:val===100?T.green:val>=80?T.gold:val>0?T.amber:T.muted,fontSize:12,fontWeight:700}}>{val}%</span>
                    </div>
                    <input type="range" min="0" max="100" step="5" value={val}
                      onChange={e => updatePhase(i, parseInt(e.target.value, 10))}
                      style={{width:"100%",cursor:"pointer",accentColor:val>=80?T.gold:T.blue}}/>
                  </div>
                </div>
              );
            })}
          </Card>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <Card>
              <SectionTitle>Project Info</SectionTitle>
              {[["Type",proj.type],["Status",proj.status],["Client",proj.client],["Address",proj.addr]].map(([k,v])=>(
                <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:`1px solid ${T.subtle}`,fontSize:12}}>
                  <span style={{color:T.muted}}>{k}</span>
                  <span style={{color:T.text,fontWeight:600,textTransform:"capitalize"}}>{v}</span>
                </div>
              ))}
            </Card>
            <Card>
              <SectionTitle>Walkthrough Status</SectionTitle>
              <div style={{marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:6}}>
                  <span style={{color:T.muted}}>Items completed</span>
                  <span style={{fontWeight:700,color:proj.walkComplete?T.green:T.amber}}>{doneWalkItems}/{totalWalkItems}</span>
                </div>
                <Progress pct={Math.round(doneWalkItems/totalWalkItems*100)} color={proj.walkComplete?T.green:T.amber}/>
              </div>
              {flagCount>0&&<div style={{fontSize:11,color:T.red,fontWeight:600,marginBottom:8}}>{flagCount} red flag{flagCount>1?"s":""} marked</div>}
              <Btn small primary onClick={()=>setTab("walkthrough")} style={{width:"100%",justifyContent:"center"}}>
                {proj.walkComplete?"Review Walkthrough":"Complete Walkthrough →"}
              </Btn>
            </Card>
            {proj.notes&&(
              <Card>
                <SectionTitle>Notes</SectionTitle>
                <div style={{fontSize:12,color:T.muted,lineHeight:1.6}}>{proj.notes}</div>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* BLUEPRINTS TAB */}
      {tab==="files"&&(
        <div>
          <button onClick={()=>setTab("field")} style={{background:"none",border:`1px solid ${T.subtle}`,borderRadius:7,padding:"5px 10px",cursor:"pointer",color:T.muted,fontSize:12,fontWeight:700,marginBottom:12}}>← Field</button>
          <ProjectBlueprintView proj={proj} session={session} projects={[proj]}/>
        </div>
      )}

      {/* WALKTHROUGH TAB */}
      {(tab==="precon"||tab==="walkthrough")&&(
        <div>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
          <button onClick={()=>setTab("field")}
            style={{flexShrink:0,background:"none",border:`1px solid ${T.subtle}`,borderRadius:7,padding:"5px 10px",cursor:"pointer",color:T.muted,fontSize:12,fontWeight:700}}>
            ← Field
          </button>
          <div style={{display:"flex",gap:4,background:T.bg2,borderRadius:8,padding:3}}>
            {[["walkthrough","✅ Walkthrough"],["walkform","📋 Form"],["risk","⚠️ Risk Check"]].map(([t,l])=>(
              <button key={t} onClick={()=>setTab(t)} style={{padding:"5px 12px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",background:tab===t||((tab==="precon"||tab==="walkthrough")&&t==="walkthrough")?T.amber:"transparent",color:tab===t||(tab==="precon"&&t==="walkthrough")?T.bg0:T.muted,transition:"all 0.15s",whiteSpace:"nowrap"}}>{l}</button>
            ))}
          </div>
        </div>
        <div>
          <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:11,padding:"14px 18px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:3}}>Site Walkthrough — {proj.name.split(" — ")[0]}</div>
              <div style={{fontSize:11,color:T.muted}}>{doneWalkItems}/{totalWalkItems} items · {flagCount} red flags</div>
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <div style={{width:120,height:5,background:T.bg3,borderRadius:3}}>
                <div style={{height:"100%",width:Math.round(doneWalkItems/totalWalkItems*100)+"%",background:proj.walkComplete?T.green:T.gold,borderRadius:3}}/>
              </div>
              <Btn small primary onClick={saveWalkthrough}>Save Walkthrough</Btn>
            </div>
          </div>

          <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {WALK_STEPS.map(step=>(
                <Card key={step.key}>
                  <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>{step.label}</div>
                  {step.items.map(item=><CB key={item.id} group={step.key} id={item.id} label={item.l} warn={item.warn}/>)}
                </Card>
              ))}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <Card>
                <div style={{fontSize:12,fontWeight:700,color:T.red,marginBottom:10}}>Red Flags — Mark If Seen</div>
                {RED_FLAGS.map(f=>(
                  <div key={f} onClick={()=>togFlag(f)} style={{display:"flex",gap:9,alignItems:"center",padding:"7px 9px",borderRadius:7,cursor:"pointer",background:redFlags[f]?T.redBg:"transparent",marginBottom:3}}>
                    <div style={{width:15,height:15,borderRadius:3,border:`2px solid ${redFlags[f]?T.red:T.subtle}`,background:redFlags[f]?T.red:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.15s"}}>
                      {redFlags[f]&&<Ic d={ICONS.check} size={8} style={{color:T.bg0}}/>}
                    </div>
                    <span style={{fontSize:12,color:redFlags[f]?T.red:T.muted}}>{f}</span>
                  </div>
                ))}
              </Card>
              <Card>
                <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:8}}>Walkthrough Documents</div>
                <div style={{fontSize:11,color:T.muted,marginBottom:10,lineHeight:1.5}}>Download and fill in on site. Have owner sign the walkthrough checklist before leaving.</div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {[
                    {name:"Site Walkthrough Checklist",file:"Grok_Site_Walkthrough_Checklist.docx",color:T.teal},
                    {name:"Scope Clarification Checklist",file:"Grok_Scope_Checklist.docx",color:T.purple},
                  ].map(d=>(
                    <div key={d.file} style={{display:"flex",gap:8,alignItems:"center",background:T.bg2,borderRadius:7,padding:"8px 10px"}}>
                      <div style={{width:24,height:24,borderRadius:5,background:d.color+"18",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <Ic d={ICONS.docs} size={11} style={{color:d.color}}/>
                      </div>
                      <div style={{flex:1,fontSize:11,fontWeight:600,color:T.text}}>{d.name}</div>
                      <button onClick={()=>openDoc(d.file)} style={{background:d.color+"18",border:`1px solid ${d.color}30`,borderRadius:6,padding:"4px 10px",fontSize:10,fontWeight:700,color:d.color,cursor:"pointer"}}>↓ Open</button>
                    </div>
                  ))}
                </div>
              </Card>
              {doneWalkItems===totalWalkItems&&(
                <div style={{padding:"12px 14px",background:T.greenBg,border:`1px solid ${T.green}30`,borderRadius:10,fontSize:12,fontWeight:700,color:T.green}}>
                  ✓ Walkthrough Complete — Save to update project status
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      )}

      {/* COs TAB */}
      {tab==="cos"&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text}}>Change Orders — {proj.name.split(" — ")[0]}</div>
            <Btn small primary><Ic d={ICONS.plus} size={12}/>New CO</Btn>
          </div>
          {projCOs.length===0?(
            <div style={{textAlign:"center",padding:"40px",color:T.muted,fontSize:13}}>No change orders on this project yet.</div>
          ):(
            projCOs.map(co=>(
              <div key={co.id} style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:10,padding:"12px 16px",marginBottom:8,display:"flex",gap:12,alignItems:"center"}}>
                <div style={{fontFamily:"monospace",fontSize:11,color:T.gold,width:60,flexShrink:0}}>{co.num}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:T.text,marginBottom:2}}>{co.desc}</div>
                  <div style={{fontSize:11,color:T.muted}}>{co.date}{co.status==="pending"?` · ${co.days} days unsigned`:""}</div>
                </div>
                <div style={{fontSize:14,fontWeight:800,color:T.gold}}>${co.amount.toLocaleString()}</div>
                <Badge label={co.status} color={co.status==="approved"?T.green:T.amber}/>
              </div>
            ))
          )}
          <div style={{marginTop:10,background:T.redBg,borderLeft:`3px solid ${T.red}`,borderRadius:8,padding:"10px 14px",fontSize:11,color:T.muted}}>
            <strong style={{color:T.red}}>Stop Work Rule:</strong> Never proceed on out-of-scope work without a signed CO.
          </div>
        </div>
      )}

      {/* RFIs TAB */}
      {tab==="rfis"&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text}}>RFIs — {proj.name.split(" — ")[0]}</div>
            <Btn small primary><Ic d={ICONS.plus} size={12}/>New RFI</Btn>
          </div>
          {projRFIs.length===0?(
            <div style={{textAlign:"center",padding:"40px",color:T.muted,fontSize:13}}>No RFIs on this project yet.</div>
          ):(
            projRFIs.map(r=>(
              <div key={r.id} style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:10,padding:"12px 16px",marginBottom:8,display:"flex",gap:12,alignItems:"center"}}>
                <div style={{fontFamily:"monospace",fontSize:11,color:T.blue,width:70,flexShrink:0}}>{r.num}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:T.text,marginBottom:2}}>{r.subject}</div>
                  <div style={{fontSize:11,color:T.muted}}>Submitted {r.submitted} · Due {r.due}</div>
                </div>
                <Badge label={r.priority} color={r.priority==="critical"?T.red:r.priority==="urgent"?T.amber:T.blue}/>
                <Badge label={r.status} color={r.status==="closed"?T.green:r.status==="pending"?T.amber:T.blue}/>
              </div>
            ))
          )}
        </div>
      )}

      {/* DOCS TAB */}
      {tab==="docs"&&(
        <ProjectTemplateGenerator proj={proj} session={session}/>
      )}

      {/* NOTES TAB */}


      {/* WALKTHROUGH FORM TAB */}
      {(tab==="precon"||tab==="walkform")&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div>
              <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:3}}>Site Walkthrough Form</div>
              <div style={{fontSize:12,color:T.muted}}>Complete with owner on site · Both parties sign at the end</div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>{
                const d=new Date().toLocaleDateString();
                const content=`GROK CONSTRUCTION GROUP INC — SITE WALKTHROUGH RECORD\nCSLB #1152750 | San Mateo, CA\n${"=".repeat(55)}\n\nPROJECT: ${proj.name}\nADDRESS: ${proj.addr}\nDATE: ${walkForm.walkthrough_date}\nCLIENT: ${proj.client}\n\nOWNER / CONTACT INFORMATION\n${"-".repeat(40)}\nOwner Name: ${walkForm.ownerName}\nPhone: ${walkForm.ownerPhone}\nEmail: ${walkForm.ownerEmail}\nBuilding Age / Year Built: ${walkForm.buildingAge}\n\nOWNER Q&A — ANSWERS ON RECORD\n${"-".repeat(40)}\n1. Any water damage or leaks before?\n   ${walkForm.qa_leaks||"[Not answered]"}\n\n2. Any mold found in unit or building?\n   ${walkForm.qa_mold||"[Not answered]"}\n\n3. Known plumbing or drain issues?\n   ${walkForm.qa_plumbing||"[Not answered]"}\n\n4. Has this been renovated before? When?\n   ${walkForm.qa_renovated||"[Not answered]"}\n\n5. Were permits pulled for prior work?\n   ${walkForm.qa_permits||"[Not answered]"}\n\n6. Will unit be occupied during construction?\n   ${walkForm.qa_occupied||"[Not answered]"}\n\n7. Any known asbestos or lead paint?\n   ${walkForm.qa_asbestos||"[Not answered]"}\n\n8. Any structural concerns / foundation issues?\n   ${walkForm.qa_structural||"[Not answered]"}\n\n9. Any pest damage / termite history?\n   ${walkForm.qa_pest||"[Not answered]"}\n\nEXISTING CONDITIONS OBSERVED\n${"-".repeat(40)}\nAccess & Site:\n  Building entrance / restrictions: ${walkForm.cond_access||"[Not noted]"}\n  Parking / staging: ${walkForm.cond_parking||"[Not noted]"}\n  Access hours: ${walkForm.cond_hours||"[Not noted]"}\n  Key / code: ${walkForm.cond_key||"[Not noted]"}\n\nPlumbing & Electrical:\n  Drain location: ${walkForm.cond_drain_location||"[Not noted]"}\n  Panel amperage: ${walkForm.cond_panel_amps||"[Not noted]"}\n  Spare breakers: ${walkForm.cond_panel_breakers||"[Not noted]"}\n  Water shut-off: ${walkForm.cond_water_shutoff||"[Not noted]"}\n  Moisture observations: ${walkForm.cond_moisture_notes||"None noted"}\n\nWalls / Ceiling / Floor:\n  Wall conditions: ${walkForm.cond_walls_notes||"No issues noted"}\n  Ceiling conditions: ${walkForm.cond_ceiling_notes||"No issues noted"}\n  Floor conditions: ${walkForm.cond_floor_notes||"No issues noted"}\n  Odor / smell: ${walkForm.cond_smell_notes||"None noted"}\n\nBuilding Notes:\n  ${walkForm.cond_building_notes||"None"}\n\nRED FLAGS OBSERVED\n${"-".repeat(40)}\n${Object.entries(redFlags).filter(([k,v])=>v).map(([k])=>`⚠ ${k}`).join("\n") || "None flagged"}\n\nADDITIONAL NOTES\n${"-".repeat(40)}\n${walkForm.qa_notes||"None"}\n\nCHECKLIST COMPLETION\n${"-".repeat(40)}\nItems completed: ${doneWalkItems}/${totalWalkItems}\nStatus: ${proj.walkComplete?"COMPLETE":"IN PROGRESS"}\n\n${"=".repeat(55)}\nSIGNATURES\n${"=".repeat(55)}\n\nBy signing below, both parties acknowledge that the above conditions were observed and recorded accurately during this site visit. Owner acknowledges that any conditions not visible or accessible during this walkthrough may be discovered during construction and may require a Change Order.\n\nGrok Construction Group Inc (CSLB #1152750):\nSignature: _____________________________ Date: ${walkForm.grok_sign_date||"___________"}\nPrinted Name: ${walkForm.grok_signed||"_____________________________"}\n\nOwner / Authorized Representative:\nSignature: _____________________________ Date: ${walkForm.owner_sign_date||"___________"}\nPrinted Name: ${walkForm.owner_signed||"_____________________________"}\n\n${"-".repeat(55)}\nThis document is part of the project record for ${proj.name}.\nGenerated by Grok Field Ops — ${new Date().toLocaleString()}`;
                const w=window.open("","_blank");
                w.document.write(`<!DOCTYPE html><html><head><title>Site Walkthrough — ${proj.name}</title>
                <style>body{font-family:Arial,sans-serif;max-width:720px;margin:32px auto;padding:24px;color:#1a1a1a;font-size:13px;}
                .banner{background:#0F1623;padding:20px 24px;margin-bottom:0;}
                .brand{font-size:26px;font-weight:900;color:#D4A843;}.brand-sub{font-size:12px;color:#fff;margin-top:3px;}
                .gold{height:4px;background:#D4A843;margin-bottom:20px;}
                h2{font-size:15px;font-weight:800;color:#0F1623;margin:20px 0 8px;border-bottom:2px solid #D4A843;padding-bottom:4px;}
                .row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #eee;font-size:12px;}
                .label{color:#888;}.val{font-weight:600;}
                .qa{background:#f9f9f9;border-left:3px solid #D4A843;padding:8px 12px;margin:6px 0;font-size:12px;}
                .q{font-weight:700;color:#0F1623;margin-bottom:3px;}.a{color:#444;}
                .flag{background:#FDF2F2;border:1px solid #C0392B;border-radius:4px;padding:3px 8px;font-size:11px;color:#7B1A1A;display:inline-block;margin:2px;}
                .sig{display:grid;grid-template-columns:1fr 1fr;gap:32px;margin-top:32px;}
                .sig-line{border-top:2px solid #0F1623;margin-top:40px;padding-top:6px;font-size:11px;color:#888;}
                @media print{body{margin:0;padding:16px;}}</style></head><body>
                <div class="banner"><div class="brand">GROK</div><div class="brand-sub">Construction Group Inc · CSLB #1152750</div></div>
                <div class="gold"></div>
                <div style="font-size:18px;font-weight:800;color:#0F1623;margin-bottom:2px;">Site Walkthrough Record</div>
                <div style="font-size:12px;color:#888;margin-bottom:16px;">${proj.name} · ${walkForm.walkthrough_date}</div>
                <h2>Owner / Contact</h2>
                <div class="row"><span class="label">Name</span><span class="val">${walkForm.ownerName||"—"}</span></div>
                <div class="row"><span class="label">Phone</span><span class="val">${walkForm.ownerPhone||"—"}</span></div>
                <div class="row"><span class="label">Email</span><span class="val">${walkForm.ownerEmail||"—"}</span></div>
                <div class="row"><span class="label">Building Year</span><span class="val">${walkForm.buildingAge||"—"}</span></div>
                <h2>Owner Q&A — On Record</h2>
                ${[["Water/leaks",walkForm.qa_leaks],["Mold",walkForm.qa_mold],["Plumbing issues",walkForm.qa_plumbing],["Prior renovations",walkForm.qa_renovated],["Permits",walkForm.qa_permits],["Occupied during work",walkForm.qa_occupied],["Asbestos/lead",walkForm.qa_asbestos],["Structural",walkForm.qa_structural],["Pest/termite",walkForm.qa_pest],["Other notes",walkForm.qa_notes]].map(([q,a])=>'<div class="qa"><div class="q">'+q+'</div><div class="a">'+(a||"[Not answered]")+'</div></div>').join("")}
                <h2>Conditions Observed</h2>
                ${[["Access",walkForm.cond_access],["Parking/Staging",walkForm.cond_parking],["Access Hours",walkForm.cond_hours],["Key/Code",walkForm.cond_key],["Panel Amps",walkForm.cond_panel_amps],["Water Shutoff",walkForm.cond_water_shutoff],["Moisture Notes",walkForm.cond_moisture_notes],["Walls",walkForm.cond_walls_notes],["Ceiling",walkForm.cond_ceiling_notes],["Floor",walkForm.cond_floor_notes],["Smell/Odor",walkForm.cond_smell_notes],["Building Notes",walkForm.cond_building_notes]].map(([k,v])=>'<div class="row"><span class="label">'+k+'</span><span class="val">'+(v||"—")+'</span></div>').join("")}
                <h2>Red Flags</h2>
                <div>${Object.entries(redFlags).filter(([k,v])=>v).map(([k])=>'<span class="flag">⚠ '+k+'</span>').join("")||"None flagged"}</div>
                <h2>Checklist: ${doneWalkItems}/${totalWalkItems} items completed</h2>
                <h2>Signatures</h2>
                <p style="font-size:12px;color:#444">By signing below, both parties acknowledge that the above conditions were observed and recorded accurately. Owner acknowledges that conditions not visible during this walkthrough may be discovered during construction and may require a Change Order.</p>
                <div class="sig">
                  <div><div style="font-size:12px;font-weight:700;color:#0F1623">Grok Construction Group Inc</div><div class="sig-line">Authorized Signature · Date: ${walkForm.grok_sign_date||"___________"}</div><div style="font-size:11px;color:#888;margin-top:4px">${walkForm.grok_signed||""}</div></div>
                  <div><div style="font-size:12px;font-weight:700;color:#0F1623">Owner / Authorized Representative</div><div class="sig-line">Signature · Date: ${walkForm.owner_sign_date||"___________"}</div><div style="font-size:11px;color:#888;margin-top:4px">${walkForm.owner_signed||""}</div></div>
                </div>
                <script>setTimeout(()=>window.print(),400)</script></body></html>`);
                w.document.close();
              }} style={{background:T.teal,color:T.bg0,border:"none",borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                ↓ Export Record
              </button>
            </div>
          </div>

          {/* Sub-tab navigation */}
          <div style={{display:"flex",gap:4,background:T.bg2,borderRadius:9,padding:3,width:"fit-content",marginBottom:16}}>
            {[{id:"info",l:"Owner Info"},{id:"qa",l:"Owner Q&A"},{id:"conditions",l:"Conditions Observed"},{id:"sign",l:"Signatures"}].map(t=>(
              <button key={t.id} onClick={()=>setWalkFormTab(t.id)} style={{padding:"6px 14px",borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:walkFormTab===t.id?T.gold:T.bg2,color:walkFormTab===t.id?T.bg0:T.muted,transition:"all 0.15s"}}>{t.l}</button>
            ))}
          </div>

          {walkFormTab==="info"&&(
            <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <Card>
                <SectionTitle>Owner / Contact Info</SectionTitle>
                {[["ownerName","Owner / Contact Name","John Smith"],["ownerPhone","Phone","(415) 555-0100"],["ownerEmail","Email","owner@email.com"],["buildingAge","Building Year Built / Age","1968 (pre-1978 — lead/asbestos risk)"]].map(([k,label,ph])=>(
                  <div key={k} style={{marginBottom:12}}>
                    <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>{label}</div>
                    <input value={walkForm[k]} onChange={e=>wf(k,e.target.value)} placeholder={ph}
                      style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
                  </div>
                ))}
              </Card>
              <Card>
                <SectionTitle>Project Info</SectionTitle>
                {[["Project",proj.name],["Address",proj.addr],["Client",proj.client],["Type",proj.type],["Date",walkForm.walkthrough_date]].map(([k,v])=>(
                  <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${T.subtle}`,fontSize:12}}>
                    <span style={{color:T.muted}}>{k}</span>
                    <span style={{color:T.text,fontWeight:600}}>{v}</span>
                  </div>
                ))}
                <div style={{marginTop:12,background:T.amberBg,borderLeft:`3px solid ${T.amber}`,borderRadius:7,padding:"9px 12px",fontSize:11,color:T.muted}}>
                  <strong style={{color:T.amber}}>Photograph everything</strong> before touching anything. Photos are your protection if disputes arise.
                </div>
              </Card>
            </div>
          )}

          {walkFormTab==="qa"&&(
            <div>
              <div style={{background:T.blueBg,borderLeft:`3px solid ${T.blue}`,borderRadius:9,padding:"10px 14px",marginBottom:14,fontSize:12,color:T.muted}}>
                Ask these questions out loud with the owner and record their answers. These answers are on record and protect Grok if hidden conditions are later disputed.
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {[
                  {k:"qa_leaks",q:"1. Any water damage, leaks, or flooding in this unit or building — ever?",ph:"Owner said: 'There was a leak under the sink 2 years ago, repaired by previous tenant'"},
                  {k:"qa_mold",q:"2. Has mold ever been found in this unit or anywhere in the building?",ph:"Owner said: 'No known mold issues'"},
                  {k:"qa_plumbing",q:"3. Any known plumbing or drain issues — slow drains, backups, weird smells?",ph:"Owner said: 'Shower drains slowly in unit 2'"},
                  {k:"qa_renovated",q:"4. Has this unit or building been renovated before? When and what was done?",ph:"Owner said: 'Kitchens were updated in 2015, permits pulled'"},
                  {k:"qa_permits",q:"5. Were permits pulled for all prior work? Can you provide permit history?",ph:"Owner said: 'Will pull permit history from SF DBI'"},
                  {k:"qa_occupied",q:"6. Will the unit be occupied during construction? Any access restrictions?",ph:"Owner said: 'Units will be vacant during work'"},
                  {k:"qa_asbestos",q:"7. Any known asbestos, lead paint, or hazardous materials in the building?",ph:"Owner said: 'Building is 1968 — no asbestos survey has been done'"},
                  {k:"qa_structural",q:"8. Any structural concerns — cracks, uneven floors, sticking doors or windows?",ph:"Owner said: 'No known structural issues'"},
                  {k:"qa_pest",q:"9. Any pest damage, termite history, or dry rot anywhere in the building?",ph:"Owner said: 'Had termite treatment in 2019'"},
                  {k:"qa_notes",q:"10. Anything else Grok should know before starting?",ph:"Owner said: '...'"},
                ].map(({k,q,ph})=>(
                  <Card key={k} style={{padding:"14px 16px"}}>
                    <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:8,lineHeight:1.4}}>{q}</div>
                    <textarea value={walkForm[k]} onChange={e=>wf(k,e.target.value)} placeholder={ph} rows={2}
                      style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"8px 11px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit"}}/>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {walkFormTab==="conditions"&&(
            <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <Card>
                  <SectionTitle>Access & Site</SectionTitle>
                  {[["cond_access","Building entrance / access notes","Elevator access only, 6am-10pm"],["cond_parking","Parking / staging area","Street parking on Turk St, no permits needed"],["cond_hours","Confirmed access hours","7am-6pm M-F, call super for after-hours"],["cond_key","Key / fob / code obtained","Key fob #4 obtained, tested OK"]].map(([k,l,ph])=>(
                    <div key={k} style={{marginBottom:10}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{l}</div>
                      <input value={walkForm[k]} onChange={e=>wf(k,e.target.value)} placeholder={ph}
                        style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
                    </div>
                  ))}
                </Card>
                <Card>
                  <SectionTitle>Plumbing & Electrical</SectionTitle>
                  {[["cond_drain_location","Drain locations observed","Floor drain at 14\" from south wall — conflicts with new island layout ⚠"],["cond_panel_amps","Electrical panel amperage","100A panel, 2 spare breakers"],["cond_panel_breakers","Spare breakers / capacity","2 spare 20A slots"],["cond_water_shutoff","Water shut-off location","Under sink, gate valve, works OK"],["cond_moisture_notes","Moisture / water observations","No moisture visible at connections"]].map(([k,l,ph])=>(
                    <div key={k} style={{marginBottom:10}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{l}</div>
                      <input value={walkForm[k]} onChange={e=>wf(k,e.target.value)} placeholder={ph}
                        style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
                    </div>
                  ))}
                </Card>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <Card>
                  <SectionTitle>Walls, Ceilings & Floors</SectionTitle>
                  {[["cond_walls_notes","Wall conditions observed","Soft spot behind sink cabinet — possible water damage"],["cond_ceiling_notes","Ceiling conditions observed","Water stain in northwest corner, possible active leak from unit above ⚠"],["cond_floor_notes","Floor conditions observed","Slight bounce near bathroom — possible subfloor rot"],["cond_smell_notes","Any odors / musty smell","Musty smell in bathroom area ⚠"]].map(([k,l,ph])=>(
                    <div key={k} style={{marginBottom:10}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{l}</div>
                      <textarea value={walkForm[k]} onChange={e=>wf(k,e.target.value)} placeholder={ph} rows={2}
                        style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit"}}/>
                    </div>
                  ))}
                </Card>
                <Card>
                  <SectionTitle>Red Flags Observed</SectionTitle>
                  {["Water stains / soft drywall","Musty smell / mold","Drain location conflict","Pre-1978 building","Visible mold","Non-standard electrical"].map(f=>(
                    <div key={f} onClick={()=>togFlag(f)} style={{display:"flex",gap:9,alignItems:"center",padding:"7px 9px",borderRadius:7,cursor:"pointer",background:redFlags[f]?T.redBg:"transparent",marginBottom:3}}>
                      <div style={{width:15,height:15,borderRadius:3,border:`2px solid ${redFlags[f]?T.red:T.subtle}`,background:redFlags[f]?T.red:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.15s"}}>
                        {redFlags[f]&&<Ic d={ICONS.alert} size={8} style={{color:T.bg0}}/>}
                      </div>
                      <span style={{fontSize:12,color:redFlags[f]?T.red:T.muted}}>{f}</span>
                    </div>
                  ))}
                </Card>
                <Card>
                  <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>General Notes</div>
                  <textarea value={walkForm.cond_building_notes} onChange={e=>wf("cond_building_notes",e.target.value)}
                    placeholder="Any other observations, concerns, or notes from this walkthrough..."
                    rows={4} style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit"}}/>
                </Card>
              </div>
            </div>
          )}

          {walkFormTab==="sign"&&(
            <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <Card>
                <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:4}}>Summary</div>
                <div style={{fontSize:12,color:T.muted,marginBottom:14,lineHeight:1.6}}>
                  Both parties confirm the conditions recorded in this walkthrough are accurate. Owner acknowledges that any conditions not visible during this visit may be discovered during construction and will require a Change Order.
                </div>
                <div style={{background:T.bg2,borderRadius:9,padding:"12px 14px",marginBottom:14}}>
                  {[
                    ["Project",proj.name],["Address",proj.addr],["Date",walkForm.walkthrough_date],
                    ["Items Checked",`${doneWalkItems}/${totalWalkItems}`],
                    ["Red Flags",Object.values(redFlags).filter(Boolean).length+" flagged"],
                    ["Owner Q&A","Recorded"],
                  ].map(([k,v])=>(
                    <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:`1px solid ${T.subtle}`,fontSize:12}}>
                      <span style={{color:T.muted}}>{k}</span><span style={{color:T.text,fontWeight:600}}>{v}</span>
                    </div>
                  ))}
                </div>
                <div style={{background:T.amberBg,borderLeft:`3px solid ${T.amber}`,borderRadius:7,padding:"10px 12px",fontSize:11,color:T.muted,lineHeight:1.6}}>
                  <strong style={{color:T.amber}}>Important:</strong> After both parties enter names below, click "Export Record" to save a copy. Both Grok and Owner should keep a copy.
                </div>
              </Card>
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                <Card>
                  <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>[Your Company Name]</div>
                  {[["grok_signed","Your Full Name (printed)","Jehoon Chun"],["grok_sign_date","Date","Apr 19, 2026"]].map(([k,l,ph])=>(
                    <div key={k} style={{marginBottom:10}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>{l}</div>
                      <input value={walkForm[k]} onChange={e=>wf(k,e.target.value)} placeholder={ph}
                        style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
                    </div>
                  ))}
                  <div style={{marginTop:6,padding:"10px 12px",background:T.bg2,borderRadius:7,fontSize:11,color:T.muted}}>CSLB #1152750 | Grok Construction Group Inc | San Mateo, CA</div>
                </Card>
                <Card>
                  <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Owner / Authorized Representative</div>
                  {[["owner_signed","Owner Full Name (printed)","Property Owner Name"],["owner_sign_date","Date","Apr 19, 2026"]].map(([k,l,ph])=>(
                    <div key={k} style={{marginBottom:10}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>{l}</div>
                      <input value={walkForm[k]} onChange={e=>wf(k,e.target.value)} placeholder={ph}
                        style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
                    </div>
                  ))}
                </Card>
                {walkForm.grok_signed&&walkForm.owner_signed&&(
                  <button onClick={()=>{
                    const d=new Date().toLocaleDateString();
                    const lines=[
                      "GROK CONSTRUCTION GROUP INC — SITE WALKTHROUGH RECORD",
                      "CSLB #1152750 | San Mateo, CA",
                      "=".repeat(55),
                      "",
                      `PROJECT: ${proj.name}`,
                      `ADDRESS: ${proj.addr}`,
                      `DATE: ${walkForm.walkthrough_date}`,
                      `CLIENT: ${proj.client}`,
                      "",
                      "OWNER: "+walkForm.ownerName+" | "+walkForm.ownerPhone+" | "+walkForm.ownerEmail,
                      "BUILDING AGE: "+walkForm.buildingAge,
                      "",
                      "OWNER Q&A ON RECORD",
                      "-".repeat(40),
                      "Water/leaks: "+(walkForm.qa_leaks||"[Not answered]"),
                      "Mold: "+(walkForm.qa_mold||"[Not answered]"),
                      "Plumbing issues: "+(walkForm.qa_plumbing||"[Not answered]"),
                      "Prior renovations: "+(walkForm.qa_renovated||"[Not answered]"),
                      "Permits: "+(walkForm.qa_permits||"[Not answered]"),
                      "Occupied during work: "+(walkForm.qa_occupied||"[Not answered]"),
                      "Asbestos/lead: "+(walkForm.qa_asbestos||"[Not answered]"),
                      "Structural: "+(walkForm.qa_structural||"[Not answered]"),
                      "Pests: "+(walkForm.qa_pest||"[Not answered]"),
                      "Other notes: "+(walkForm.qa_notes||"None"),
                      "",
                      "CONDITIONS OBSERVED",
                      "-".repeat(40),
                      "Access: "+(walkForm.cond_access||"Not noted"),
                      "Parking: "+(walkForm.cond_parking||"Not noted"),
                      "Hours: "+(walkForm.cond_hours||"Not noted"),
                      "Drain location: "+(walkForm.cond_drain_location||"Not noted"),
                      "Panel: "+(walkForm.cond_panel_amps||"Not noted"),
                      "Walls: "+(walkForm.cond_walls_notes||"No issues"),
                      "Ceiling: "+(walkForm.cond_ceiling_notes||"No issues"),
                      "Floor: "+(walkForm.cond_floor_notes||"No issues"),
                      "Smell: "+(walkForm.cond_smell_notes||"None"),
                      "Notes: "+(walkForm.cond_building_notes||"None"),
                      "",
                      "RED FLAGS: "+(Object.entries(redFlags).filter(([k,v])=>v).map(([k])=>k).join(", ")||"None"),
                      "CHECKLIST: "+doneWalkItems+"/"+totalWalkItems+" items completed",
                      "",
                      "=".repeat(55),
                      "SIGNATURES",
                      "=".repeat(55),
                      "",
                      "Grok Construction Group Inc (CSLB #1152750):",
                      "Name: "+walkForm.grok_signed+" | Date: "+walkForm.grok_sign_date,
                      "",
                      "Owner / Authorized Representative:",
                      "Name: "+walkForm.owner_signed+" | Date: "+walkForm.owner_sign_date,
                      "",
                      "-".repeat(55),
                      "Generated by Grok Field Ops — "+new Date().toLocaleString(),
                    ];
                    const blob=new Blob([lines.join("\n")],{type:"text/plain"});
                    const a=document.createElement("a");
                    a.href=URL.createObjectURL(blob);
                    a.download=`Grok_Walkthrough_SIGNED_${proj.name.split(" ")[0]}_${new Date().toISOString().slice(0,10)}.txt`;
                    a.click();
                    // Save to project
                    saveProject({...proj,walkComplete:true,walk:walkChecks,redFlags});
                  }} style={{width:"100%",padding:"12px",borderRadius:10,background:T.green,color:"white",border:"none",cursor:"pointer",fontSize:13,fontWeight:700}}>
                    ✓ Both Signed — Export & Save to Project
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* SCOPE CHECKLIST FORM TAB */}
      {tab==="inspection"&&(
        <InspectionBillingView proj={proj} projCOs={projCOs} session={session}/>
      )}
      {tab==="units"&&(
        <UnitProgressView proj={proj} session={session}/>
      )}
      {tab==="draw"&&(
        <DrawScheduleView proj={proj} projCOs={projCOs} session={session}/>
      )}
      {tab==="investor"&&(
        <OwnerReportView proj={proj} projCOs={projCOs} projRFIs={projRFIs} session={session}/>
      )}
      {tab==="payapp"&&(
        <PayApplicationView proj={proj} projCOs={projCOs} session={session}/>
      )}
      {tab==="photolog"&&(
        <PhotoDailyLogView proj={proj} session={session}/>
      )}
      {tab==="equipment"&&(
        <EquipmentTrackView proj={proj} session={session}/>
      )}
      {tab==="scope-checklist"&&(
        <ScopeChecklistTab proj={proj} session={session} setTab={setTab}/>
      )}
      {tab==="punch"&&(
        <div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <button onClick={()=>setTab("field")} style={{background:"none",border:`1px solid ${T.subtle}`,borderRadius:7,padding:"5px 10px",cursor:"pointer",color:T.muted,fontSize:12,fontWeight:700}}>← Field</button>
            <button onClick={()=>{
              const cid2=session?.companyId||window._grokCompanyId||"local";
              const tpls=(() => {try{return JSON.parse(localStorage.getItem("grok_templates_"+cid2)||"[]");}catch{return [];}})();
              const tpl=tpls.find(t=>t.id==="gcg_t13");
              if(tpl?.data){
                const a=document.createElement("a");a.href=tpl.data;
                const d=new Date().toLocaleDateString("en-US",{month:"2-digit",day:"2-digit",year:"numeric"}).split("/").join("-");
                a.download=`GCG_${(proj?.name||"Project").split(" ")[0]}_PunchList_${d}.docx`;
                document.body.appendChild(a);a.click();document.body.removeChild(a);
              } else {alert("Punch List template not found in Document Kit.");}
            }} style={{padding:"6px 14px",borderRadius:8,border:`1px solid ${T.gold}`,background:T.goldBg||"#FDF6E3",color:T.gold,fontSize:11,fontWeight:700,cursor:"pointer"}}>
              📄 Download Punch List Doc
            </button>
          </div>
          <PunchListView proj={proj} session={session}/>
        </div>
      )}
      {(tab==="precon"||tab==="scopeform")&&(
        <LockedScopeChecklist proj={proj} session={session}/>
      )}
      {(tab==="precon"||tab==="risk")&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div>
              <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:3}}>Pre-Construction Risk Checklist</div>
              <div style={{fontSize:12,color:T.muted}}>Check these before signing the contract — each flag is a potential Change Order</div>
            </div>
            <Btn primary onClick={async()=>{
              const key="proxy"; // proxy handles auth
              // proxy handles auth
              setAiRiskLoading(true);setAiRiskResult("");
              const flagged=Object.entries(riskChecks).filter(([k,v])=>v).map(([k])=>k);
              try{
                await callAI(
                  `You are an expert construction risk advisor for Grok Construction Group Inc (CSLB #1152750), a licensed GC in SF Bay Area specializing in residential renovation, SRO/affordable housing, multifamily, and commercial tenant improvements.\n\nAnalyze the flagged pre-construction risk items and provide:\n1. CHANGE ORDER RISK ASSESSMENT — for each flagged item, what is the likely CO cost range and how to document it\n2. CONTRACT LANGUAGE — specific exclusion language to add to the contract for each risk\n3. PHOTOS TO TAKE — exactly what to photograph before starting to protect GCG\n4. STOP WORK TRIGGERS — at what point during demo/construction should GCG stop and issue a CO for each risk`,
                  `Project: ${proj.name} (${proj.type}) — ${proj.addr}\nScope: ${proj.desc}\n\nFlagged risk items that need CO protection:\n${flagged.length>0?flagged.map(f=>"- "+f).join("\n"):"No items flagged yet — please review the checklist"}\n\nProvide specific, actionable guidance for each flagged item.`,
                  t=>setAiRiskResult(t),
                  key
                );
              }catch(e){setAiRiskResult("Error: "+e.message);}
              setAiRiskLoading(false);
            }} style={{background:`linear-gradient(135deg,${T.purple},${T.blue})`,color:"white"}}>
              {aiRiskLoading?<><Spin/>Analyzing...</>:<><Ic d={ICONS.robot} size={13}/>AI Risk Analysis</>}
            </Btn>
          </div>

          {/* Risk checklist rooms */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:16}}>
            {[
              {room:"Kitchen",color:T.red,icon:"🍳",items:[
                "Drain location — confirm existing drain matches new layout",
                "Hood vent routing — where does exhaust go? Through roof/wall/cabinet?",
                "Gas line location — existing vs new appliance positions",
                "New appliance clearances — code requires min 30\" hood height, 6\" from combustibles",
                "Refrigerator water line — existing stub-out location",
                "Dishwasher drain — air gap requirement (SF code)",
                "GFCI outlets — within 6ft of sink required",
                "Under-cabinet lighting — electrical rough-in exists?",
                "Structural above — beam or joists above where island/peninsula goes",
                "Cabinet height ADA — if accessible unit, max 34\" base cabinet",
              ]},
              {room:"Bathroom",color:T.blue,icon:"🚿",items:[
                "Drain location — floor drain vs wall drain conflicts with new layout",
                "Wet area waterproofing — existing membrane or none? Pre-1978 may have none",
                "Vent pipe location — shower/tub vent through wall or ceiling?",
                "Shower pan slope — existing floor slope acceptable or rework needed?",
                "Grab bar blocking — blocking in walls for future ADA grab bars?",
                "Fan/vent — exhaust to exterior required by SF code",
                "Tub/shower valve — pressure balancing valve required by code",
                "Floor structure — soft spots under tile indicate rot below",
                "Toilet flange height — flush with finished floor after new tile?",
                "Medicine cabinet — stud location vs desired placement",
              ]},
              {room:"Electrical",color:T.amber,icon:"⚡",items:[
                "Panel capacity — existing amps sufficient for new load?",
                "Dedicated circuits — new appliances require dedicated 20A circuits",
                "AFCI requirements — SF requires AFCI in bedrooms and living areas",
                "Knob-and-tube wiring — pre-1950 building may have K&T (insurance issue)",
                "Aluminum wiring — pre-1972 may have aluminum (fire hazard, needs COPALUM)",
                "Grounded outlets — 3-prong outlets on 2-wire system?",
                "Panel location — permit required to move panel",
                "Low voltage — cable/data/phone — existing routing conflicts?",
                "Outdoor GFCI — deck/patio outlets within 20ft of water",
                "Smoke/CO detector locations — confirm code compliance",
              ]},
              {room:"Structural & Building",color:T.green,icon:"🏗",items:[
                "Load-bearing walls — confirm with structural before any demo",
                "Asbestos — pre-1978 building: popcorn ceilings, floor tile, pipe insulation",
                "Lead paint — pre-1978: all painted surfaces, especially windows/trim",
                "Water intrusion — check roof, windows, exterior walls for moisture",
                "Foundation — visible cracks, uneven floors, sticking doors",
                "Pest damage — termite, dry rot in wood framing near bathrooms",
                "HVAC routing — new duct runs through existing structure?",
                "Plumbing stack location — cast iron vs ABS, condition of existing stack",
                "Building permits — were previous renovations permitted? Pull permit history",
                "Seismic — SF requires soft-story retrofit on some buildings — check",
              ]},
              {room:"SRO / Multifamily",color:T.purple,icon:"🏢",items:[
                "Unit occupancy during construction — occupied = noise/dust/lead restrictions",
                "Common area access — elevator, stair, corridor access for materials",
                "Noise ordinance — SF allows construction 7am-8pm M-F, 9am-6pm Sat",
                "Asbestos survey required — SF requires for buildings pre-1979 before permits",
                "Lead clearance required — certified clearance test after lead work",
                "MOHCD / HUD requirements — affordable housing has specific finish standards",
                "Prevailing wage — MOHCD/HUD funded projects require certified payroll",
                "ADA compliance — Type A/B units, common areas, accessible route",
                "Energy Title 24 — lighting, insulation, windows must meet current code",
                "Fire sprinklers — any alteration may trigger sprinkler upgrade requirement",
                "7-Day Tenant Notice — required before work in each occupied unit",
                "Relocation benefits — check if project triggers relocation requirement",
              ]},
              {room:"Commercial TI",color:T.teal,icon:"🏬",items:[
                "ADA path of travel — any alteration triggers path of travel upgrade requirement",
                "Occupancy type change — new use may require sprinklers, egress upgrades",
                "Landlord work letter — what does LL provide vs tenant? Confirm in writing",
                "MEP coordination — existing building systems may need capacity verification",
                "Title 24 energy compliance — commercial has stricter lighting/HVAC requirements",
                "Fire sprinkler coverage — TI layout changes may require head relocations",
                "Restroom compliance — ADA restroom upgrade may be required above threshold",
                "Grease trap / interceptor — restaurant/food use requires grease trap",
                "Structural loading — mezzanines, heavy equipment, storage require engineering",
                "Business hours constraint — can work only happen after-hours or on weekends?",
                "Existing conditions survey — who owns existing MEP stub-outs?",
                "Certificate of Occupancy — new CofO required for change of occupancy",
              ]},
            ].map(section=>(
              <Card key={section.room}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                  <span style={{fontSize:20}}>{section.icon}</span>
                  <div style={{fontSize:13,fontWeight:700,color:T.text}}>{section.room}</div>
                  <span style={{marginLeft:"auto",fontSize:10,color:T.muted}}>
                    {section.items.filter(i=>riskChecks[section.room+"_"+i]).length}/{section.items.length} flagged
                  </span>
                </div>
                {section.items.map(item=>{
                  const key=section.room+"_"+item;
                  const checked=riskChecks[key];
                  return (
                    <div key={item} onClick={()=>togRisk(key)}
                      style={{display:"flex",gap:8,alignItems:"flex-start",padding:"6px 8px",borderRadius:7,cursor:"pointer",background:checked?T.redBg:"transparent",transition:"background 0.15s",marginBottom:2}}>
                      <div style={{width:15,height:15,borderRadius:3,border:`2px solid ${checked?T.red:T.subtle}`,background:checked?T.red:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1,transition:"all 0.15s"}}>
                        {checked&&<Ic d={ICONS.alert} size={8} style={{color:T.bg0}}/>}
                      </div>
                      <span style={{fontSize:11,color:checked?T.red:T.muted,lineHeight:1.4}}>{item}</span>
                    </div>
                  );
                })}
              </Card>
            ))}
          </div>

          {/* Risk summary + AI analysis */}
          {Object.values(riskChecks).filter(Boolean).length>0&&(
            <Card style={{marginBottom:14,background:T.redBg,border:`1px solid ${T.red}30`,borderLeft:`3px solid ${T.red}`}}>
              <div style={{fontSize:13,fontWeight:700,color:T.red,marginBottom:8}}>
                {Object.values(riskChecks).filter(Boolean).length} Risk Items Flagged — Protect Grok Before Signing
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                {Object.entries(riskChecks).filter(([k,v])=>v).map(([k])=>(
                  <span key={k} style={{background:T.redBg,border:`1px solid ${T.red}40`,borderRadius:20,fontSize:10,color:T.red,padding:"2px 9px"}}>
                    {k.split("_").slice(1).join("_").substring(0,40)}
                  </span>
                ))}
              </div>
            </Card>
          )}

          {/* AI Risk Analysis output */}
          {(aiRiskResult||aiRiskLoading)&&(
            <Card>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                <div style={{width:8,height:8,borderRadius:"50%",background:aiRiskLoading?T.amber:T.green,animation:aiRiskLoading?"pulse 1s infinite":"none"}}/>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>AI Risk Analysis</div>
                {aiRiskResult&&<Btn small ghost onClick={()=>setAiRiskResult("")} style={{marginLeft:"auto"}}><Ic d={ICONS.x} size={11}/>Clear</Btn>}
              </div>
              <div style={{background:T.bg0,borderRadius:8,padding:"12px 14px",fontSize:12,color:T.text,lineHeight:1.7,whiteSpace:"pre-wrap",maxHeight:400,overflowY:"auto",fontFamily:"inherit"}}>
                {aiRiskResult}
                {aiRiskLoading&&<Spin/>}
              </div>
            </Card>
          )}

          {/* Quick contract language */}
          <Card>
            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:8}}>Standard Exclusion Language — Add to Every Contract</div>
            <div style={{display:"grid",gridTemplateColumns:window.innerWidth<768?"1fr":"1fr 1fr",gap:8}}>
              {[
                "Hidden conditions including water damage, mold, rot, or structural deficiencies discovered during demolition — Change Order required before proceeding",
                "Drain, vent, or supply line relocation if existing locations conflict with new layout shown on plans",
                "Asbestos or lead paint abatement — testing and remediation by licensed abatement contractor, separate contract",
                "Load-bearing wall modifications — structural engineer required, separate from this contract",
                "Upgrading existing electrical, plumbing, or HVAC systems not directly related to the work scope",
                "Damage or repairs required to adjacent spaces, units, or common areas not specifically included in scope",
              ].map((item,i)=>(
                <div key={i} style={{display:"flex",gap:8,padding:"8px 10px",background:T.bg2,borderRadius:7,alignItems:"flex-start"}}>
                  <div style={{width:5,height:5,borderRadius:"50%",background:T.gold,flexShrink:0,marginTop:5}}/>
                  <span style={{fontSize:11,color:T.muted,lineHeight:1.5}}>{item}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {tab==="notes"&&(
        <Card>
          <SectionTitle>Project Notes</SectionTitle>
          <textarea defaultValue={proj.notes} onBlur={e=>{
            const updated={...proj,notes:e.target.value};
            saveProject(updated);
          }} placeholder="Internal notes — 20-Day Notice dates, prevailing wage, key contacts, permit numbers..."
            rows={8} style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:13,resize:"vertical",fontFamily:"inherit"}}/>
          <div style={{fontSize:11,color:T.muted,marginTop:6}}>Click outside the text box to auto-save notes.</div>
        </Card>
      )}

      {/* Team Tab */}
      {tab==="team"&&(
        <div style={{padding:"4px 0"}}>
          <button onClick={()=>setTab("field")} style={{background:"none",border:`1px solid ${T.subtle}`,borderRadius:7,padding:"5px 10px",cursor:"pointer",color:T.muted,fontSize:12,fontWeight:700,marginBottom:12}}>← Field</button>
          <ProjectTeamManager proj={proj} session={session} onUpdate={updated=>saveProject(updated)}/>
        </div>
      )}

      {/* Project Thread Tab */}
      {tab==="thread"&&(
        <ProjectHubView proj={proj} session={session} projCOs={projCOs} projRFIs={projRFIs} projects={projects}/>
      )}

      {/* Modals */}
      {showEdit&&<ProjectModal project={proj} onClose={()=>setShowEdit(false)} onSave={p=>{saveProject(p);setShowEdit(false);}}/>}
      {delayNoticeRisk&&(
        <DelayNoticeModal
          proj={proj}
          session={session}
          risk={delayNoticeRisk}
          onClose={()=>setDelayNoticeRisk(null)}
        />
      )}
      {inspectionPromptIdx!==null&&(
        <InspectionRequestModal
          proj={proj}
          session={session}
          phaseIdx={inspectionPromptIdx}
          phaseName={PHASE_NAMES_LOCAL[inspectionPromptIdx]||"Milestone"}
          onClose={()=>setInspectionPromptIdx(null)}
        />
      )}
      {showPhotoProgress&&(
        <PhotoProgressModal
          proj={proj}
          session={session}
          phaseNames={PHASE_NAMES_LOCAL}
          currentPhases={PHASE_NAMES_LOCAL.map((_,i)=>phaseVal((proj.phases||[])[i]))}
          onApply={(newPhases)=>{
            const merged = [...(proj.phases||Array(7).fill(0))];
            newPhases.forEach((v,i)=>{
              if (merged[i] && typeof merged[i]==="object") merged[i] = {...merged[i], pct:v};
              else merged[i] = v;
            });
            saveProject({...proj, phases: merged});
          }}
          onClose={()=>setShowPhotoProgress(false)}
        />
      )}
      {showArchiveConfirm&&(
        <Modal title="Archive Project?" onClose={()=>setShowArchiveConfirm(false)}>
          <div style={{fontSize:13,color:T.muted,marginBottom:20,lineHeight:1.6}}>
            This will move <strong style={{color:T.text}}>{proj.name}</strong> to Archived. You can still view it under All Projects. This does not delete any data.
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn ghost onClick={()=>setShowArchiveConfirm(false)}>Cancel</Btn>
            <Btn danger onClick={archiveProject}>Archive Project</Btn>
          </div>
        </Modal>
      )}
      {showDeleteConfirm&&(
        <Modal title="Delete Project?" onClose={()=>setShowDeleteConfirm(false)}>
          <div style={{background:T.redBg,border:`1px solid ${T.red}30`,borderRadius:8,padding:"12px 14px",marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:700,color:T.red,marginBottom:4}}>⚠ This cannot be undone</div>
            <div style={{fontSize:12,color:T.muted,lineHeight:1.6}}>
              Permanently delete <strong style={{color:T.text}}>{proj.name}</strong>? All project data, notes, change orders, RFIs, and photos will be removed forever.
            </div>
          </div>
          <div style={{fontSize:12,color:T.muted,marginBottom:16}}>Type the project name to confirm:</div>
          <DeleteConfirmInput projectName={proj.name} onConfirm={()=>{
            DataStore.deleteProject(proj.id, session);
            setProjects(prev=>prev.filter(p=>p.id!==proj.id));
            PROJECTS=PROJECTS.filter(p=>p.id!==proj.id);
            setShowDeleteConfirm(false);
            setView("projects");
          }} onCancel={()=>setShowDeleteConfirm(false)}/>
        </Modal>
      )}
    </div>
  );
}


// --- KNOWLEDGE BASE HELPERS -------------------------------------------------
const KB_DEFAULTS = {
  company_name: "Grok Construction Group Inc",
  cslb: "1152750",
  location: "San Mateo, CA",
  specialty: "SRO, affordable housing, senior housing, multi-family apartments, mid-rise buildings, and residential renovation in SF Bay Area. Principal active in the field since 2017.",
  self_perform: "Carpentry, framing, drywall, tile, countertops, paint, flooring, doors and hardware",
  labor_rates: "Lead Carpenter/Foreman $105/hr, Journeyman Carpenter $85/hr, Skilled Laborer/Demo $70/hr, Tile Setter $95/hr, Drywall/Taper $90/hr, Painter $75/hr, Countertop Installer $95/hr. Prevailing wage jobs: $95-$145/hr per DIR classification with certified payroll required.",
  sub_rates: "Licensed plumber $145-$165/hr, Licensed electrician $145-$165/hr, HVAC tech $135-$150/hr, Structural/concrete $125-$145/hr. Sub markup: +15%. All subs carry minimum $1M liability insurance verified before mobilization.",
  op_structure: "Markup 20-30% on direct cost, GC Fee 8-12% after markup, Contingency 10-15% on subtotal. SRO/Occupied: markup 20-30%, GC fee 8-12%, contingency 10-15%. Residential: markup 18-25%, GC fee 5-8%, contingency 5-10%. Commercial TI: markup 15-22%, GC fee 6-10%, contingency 5-8%. Formula: Client Price = (Direct Cost x (1 + Markup%)) + GC Fee + Contingency. Never expose sub bids or internal cost structure. Lump-sum pricing is proprietary.",
  standard_exclusions: "Structural modifications requiring engineer, asbestos/lead abatement, drain relocation unless specified, panel upgrades beyond scope, work outside opened areas, adjacent unit damage not caused by Grok, owner-supplied material defects",
  co_policy: "Stop work immediately on hidden conditions. Notify owner within 2 hours in writing. No work proceeds without signed Change Order. Photograph all conditions before stopping.",
  billing: "Progress billing on the 1st of every month. Conditional waiver with every pay application. Unconditional only after check clears bank. Retainage 10% withheld until final completion. 20-Day Preliminary Notice served Day 1 on every job.",
  prevailing_wage: "Required on all MOHCD, HUD, and public-funded projects. DIR certified payroll mandatory. Grok is prevailing wage compliant.",
  key_subs: "",
  owner_intro: "Jehoon Chun, principal and sole operator. Licensed GC since 2026, active in SF Bay Area construction since 2017. Currently operating as owner-operator — Jehoon performs and supervises all self-perform work personally, ensuring quality control on every project. Grok manages the entire construction process — permits, scheduling, sub coordination, inspections, billing, and closeout. Client has one point of contact and a guaranteed outcome. As the company grows, crew will be hired at competitive SF Bay Area rates.",
  custom_notes: "Grok provides full project management — the client does not need to manage any part of the construction process. We coordinate all trades, handle all permits, manage the schedule, and guarantee the work. Standard SRO unit renovation: $35-$55K all-in. Senior housing ADA unit: $45-$65K. Mid-rise renovation: $150-$300/sq ft depending on scope. CREW COST STRUCTURE (placeholder until hired): Principal/owner performing work billed at full rate. When crew is hired target pay rates are: Lead Foreman $55-$65/hr loaded cost, Journeyman Carpenter $42-$48/hr loaded cost, Laborer $28-$34/hr loaded cost, Tile Setter $45-$52/hr loaded cost, Drywall $40-$48/hr loaded cost, Painter $32-$40/hr loaded cost. Loaded cost = base wage x 1.3 to account for payroll taxes, workers comp, and liability. Target gross labor margin: 40-45% on self-perform work.",
};

function loadKB() {
  try {
    const saved = localStorage.getItem(kbKey(window._grokCompanyId||"local"));
    return saved ? {...KB_DEFAULTS, ...JSON.parse(saved)} : KB_DEFAULTS;
  } catch { return KB_DEFAULTS; }
}

function saveKB(kb) {
  try { localStorage.setItem(kbKey(window._grokCompanyId||"local"), JSON.stringify(kb)); } catch {}
}

function buildSystemPrompt(projContext, kb) {
  return `You are the dedicated AI Project Manager and construction compliance expert for ${kb.company_name} (CSLB #${kb.cslb}), based in ${kb.location}.

COMPANY KNOWLEDGE BASE:
Specialty: ${kb.specialty}
Self-perform trades: ${kb.self_perform}
Labor rates: ${kb.labor_rates}
Sub rates: ${kb.sub_rates}
Overhead & Profit: ${kb.op_structure}
Standard exclusions: ${kb.standard_exclusions}
CO policy: ${kb.co_policy}
Billing process: ${kb.billing}
Prevailing wage: ${kb.prevailing_wage}
Key subs: ${kb.key_subs||"Not specified"}
About principal: ${kb.owner_intro}
${kb.custom_notes?"Additional notes: "+kb.custom_notes:""}

${projContext?"CURRENT PROJECT:\n"+projContext:""}

CALIFORNIA BUILDING CODE & COMPLIANCE — ALWAYS FLAG WHEN RELEVANT:
You must proactively flag applicable codes for every scope of work discussed. Reference exact code sections.

ADA / ACCESSIBILITY (2010 ADA Standards + CBC Chapter 11A/11B):
- Doors: 32" clear min (36" preferred), lever hardware, max 5 lbf force
- Bathrooms: 60" turning radius, grab bars (42" side, 36" rear), 17-19" seat height
- Ramps: 1:12 max slope, 36" min width, handrails both sides if >6" rise
- Kitchens: 30-48" reach range, knee clearance under sink, D-pull hardware
- Unit signage, visual alarms, accessible routes

CALIFORNIA BUILDING CODE (CBC 2022) KEY SECTIONS:
- Egress: CBC 1006-1021 — door widths (32" min clear), travel distance, illumination
- Fire ratings: CBC Chapter 7 — 1-hr corridors in R-2, fire-rated assemblies
- Ventilation: CBC 1203 — mechanical ventilation required if no operable windows
- R-2 occupancy: sprinklers required if 4+ units in new construction
- Smoke/CO detectors: CBC 907.2.10 — every sleeping room + corridor

SF BAY AREA SPECIFICS:
- SRO minimum 150 sqft per unit (SF Planning Code 188-188.5)
- Seismic SDC D: anchor bolts, shear walls, cripple wall bracing per ASCE 7
- Title 24 Energy: T-24 compliance required on all permitted work
- CalGreen 2022: mandatory on all permits — low-VOC, EV ready, water efficiency
- Prevailing wage: required on MOHCD/HUD/public-funded projects — DIR certified payroll

TRADE-SPECIFIC FLAGS:
- Plumbing: CPC — WaterSense fixtures, 2.0 GPM max showers, ADA clearances
- Electrical: CEC/NEC — AFCI in all bedrooms, GFCI in wet areas, EV-ready panel space
- HVAC: Title 24 Part 6 — SEER ratings, duct sealing, fresh air per ASHRAE 62.2
- Structural: IBC/ASCE7 — wet stamp required for load-bearing modifications
- Finishes: CalGreen — low/zero VOC paints, adhesives, flooring

ALWAYS: When reviewing any scope, estimate, or blueprint description — automatically flag applicable codes, required inspections, and potential CO triggers. Be specific with section numbers.`;
}
function loadProjectMemory(projectId) {
  try {
    const saved = localStorage.getItem("pm_memory_" + projectId);
    return saved ? JSON.parse(saved) : [];
  } catch { return []; }
}

function saveProjectMemory(projectId, messages) {
  try {
    // Save last 20 messages to stay within context limits
    const toSave = messages.slice(-20);
    localStorage.setItem("pm_memory_" + projectId, JSON.stringify(toSave));
  } catch {}
}


// ─── ESTIMATE REQUESTS VIEW ───────────────────────────────────────────────────
const SAMPLE_REQUESTS = [
  {id:1,name:"David Park",contact:"dpark@seniorcare.org",type:"Senior / Assisted Living",addr:"Chinatown, SF",size:"12 units",age:"1950-1978",budget:"$500K+",desc:"Full renovation of 12 senior housing units on 4th floor. ADA upgrades throughout — accessible showers, grab bars, lever hardware. Replace all flooring with LVP, paint all units, replace lighting with Title 24 compliant fixtures. Bathrooms need full tile replacement and new fixtures. Building is 1965 construction.",submitted:"Apr 20, 2026 · 9:14am",status:"new",estimate:null,notes:""},
  {id:2,name:"Sandra Lin",contact:"(415) 555-0127",type:"Residential Kitchen",addr:"Castro, SF",size:"1 unit, approx 200 sq ft kitchen",age:"1978-2000",budget:"$50K – $100K",desc:"Remove existing kitchen cabinets, countertops, and flooring. Install new shaker cabinets upper and lower, quartz countertops, subway tile backsplash. Replace flooring with LVP to match living room. New sink and faucet, dishwasher install. Paint kitchen.",submitted:"Apr 19, 2026 · 4:32pm",status:"reviewed",estimate:null,notes:"Good lead. Budget is realistic. Need to confirm if permits required."},
];

// EST_REQUESTS — per company, persisted to localStorage
function getEstRequests(companyId) {
  try {
    const key = "grok_est_" + (companyId || "default");
    const s = localStorage.getItem(key);
    return s ? JSON.parse(s) : (companyId ? [] : [...SAMPLE_REQUESTS]);
  } catch { return [...SAMPLE_REQUESTS]; }
}
function saveEstRequests(requests, companyId) {
  try {
    const key = "grok_est_" + (companyId || "default");
    localStorage.setItem(key, JSON.stringify(requests));
  } catch(e) {}
}
let EST_REQUESTS = [...SAMPLE_REQUESTS]; // will be replaced per session


// ─── BID TEMPLATES DATA ───────────────────────────────────────────────────────
// ─── SUB PORTAL DATA ──────────────────────────────────────────────────────────
let SUB_PORTAL_DATA = {
  subs: [
    {id:"s1",name:"Mike Chen",company:"SF Plumbing Inc",trade:"Plumbing",pin:"1234",color:"#60a5fa",projects:["p1"],active:true},
    {id:"s2",name:"Tom Lee",company:"Bay Electrical",trade:"Electrical",pin:"5678",color:"#f59e0b",projects:["p1"],active:true},
    {id:"s3",name:"Pedro Martinez",company:"PM Tile",trade:"Tile",pin:"9012",color:"#a78bfa",projects:["p1"],active:true},
  ],
  logs: [],      // daily logs from subs
  issues: [],    // flagged issues
  rfis: [],      // RFI requests from subs
  tasks: {},     // per-project task completion by sub
};

// ─── SUBCONTRACTOR MANAGEMENT PORTAL ─────────────────────────────────────────
const SUB_DB_KEY = "grok_sub_database";

function loadSubDB(cid) {
  try {
    const s = localStorage.getItem(subDbKey(cid)||SUB_DB_KEY);
    if (s) return JSON.parse(s);
  } catch(e) {}
  return {
    subs: [
      {
        id: "s1",
        // Identity
        name: "Mike Chen",
        company: "SF Plumbing Inc",
        trade: "Plumbing",
        license: "C-36 #987654",
        ein: "",
        // Contact
        phone: "(415) 555-0111",
        email: "mike@sfplumbing.com",
        address: "1234 Mission St, SF CA 94103",
        // Compliance
        cslb: "987654",
        w9_on_file: true,
        is_corp: false,
        coi_on_file: true,
        coi_expiry: "2026-12-31",
        dir_registered: true,
        dir_number: "1000012345",
        // Rates
        hourly_rate: 145,
        markup_pct: 15,
        payment_terms: "Net 30",
        // Work history
        projects_worked: ["p1"],
        total_paid_ytd: 18400,
        total_paid_lifetime: 54200,
        // Performance
        rating: 4.5,
        on_time_pct: 92,
        quality_score: 4,
        notes: "Reliable, good on SRO work. Slight delays on p1 due to material lead times.",
        // Status
        active: true,
        preferred: true,
        pin: "1234",
        color: "#60a5fa",
        added_date: "2024-03-01",
      },
      {
        id: "s2",
        name: "Tom Lee",
        company: "Bay Electrical LLC",
        trade: "Electrical",
        license: "C-10 #123456",
        ein: "",
        phone: "(510) 555-0122",
        email: "tom@bayelectrical.com",
        address: "567 Broadway, Oakland CA 94607",
        cslb: "123456",
        w9_on_file: true,
        is_corp: false,
        coi_on_file: true,
        coi_expiry: "2026-09-30",
        dir_registered: true,
        dir_number: "1000067890",
        hourly_rate: 155,
        markup_pct: 15,
        payment_terms: "Net 30",
        projects_worked: ["p1"],
        total_paid_ytd: 0,
        total_paid_lifetime: 28000,
        rating: 4,
        on_time_pct: 85,
        quality_score: 4,
        notes: "Good work, occasionally needs follow-up on punch items.",
        active: true,
        preferred: false,
        pin: "5678",
        color: "#f59e0b",
        added_date: "2024-06-15",
      },
    ]
  };
}

// ─── SUB PENDING APPROVAL HELPERS ────────────────────────────────────────────
function getPendingSubs(companyId) {
  try { return JSON.parse(localStorage.getItem("grok_sub_pending_"+(companyId||"local"))||"[]"); } catch { return []; }
}
function savePendingSubs(list, companyId) {
  try { localStorage.setItem("grok_sub_pending_"+(companyId||"local"), JSON.stringify(list)); } catch(e) {} 
}
function approvePendingSub(subId, companyId) {
  // Mark sub account as active and link to project
  const accounts = getSubAccounts();
  const pending = getPendingSubs(companyId);
  const req = pending.find(p=>p.subId===subId);
  if (!req) return false;
  const updated = accounts.map(a=>a.id===subId ? {
    ...a, status:"active",
    projects:[...(a.projects||[]), {projectId:req.projectId, projectName:req.projectName, gcCompanyId:companyId, joinedAt:new Date().toISOString()}]
  } : a);
  saveSubAccounts(updated);
  savePendingSubs(pending.map(p=>p.subId===subId?{...p,status:"approved",approvedAt:new Date().toISOString()}:p), companyId);
  return true;
}
function rejectPendingSub(subId, companyId) {
  const accounts = getSubAccounts();
  saveSubAccounts(accounts.map(a=>a.id===subId?{...a,status:"rejected"}:a));
  const pending = getPendingSubs(companyId);
  savePendingSubs(pending.map(p=>p.subId===subId?{...p,status:"rejected"}:p), companyId);
}

function saveSubDB(db,cid){
  try{localStorage.setItem(subDbKey(cid)||SUB_DB_KEY,JSON.stringify(db));}catch(e){}
  // Sync sub roster as team members to Supabase
  if (cid && cid !== "local") {
    SyncDB.saveTeam(Array.isArray(db)?db:[]).catch(()=>{});
  }
}

function SubManagerView({session}) {
  const [db, setDb] = useState(()=>loadSubDB(session?.companyId));
  const [view, setView] = useState("roster"); // roster | detail | add
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [filterTrade, setFilterTrade] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [showAdd, setShowAdd] = useState(false);
  const [detailTab, setDetailTab] = useState("info");

  const save = (updated) => { setDb(updated); saveSubDB(updated,session?.companyId); };
  const pendingSubs = getPendingSubs(session?.companyId).filter(p=>p.status==="pending");

  const updateSub = (id, fields) => {
    const updated = {...db, subs: db.subs.map(s=>s.id===id?{...s,...fields}:s)};
    save(updated);
    setSelected(prev=>prev?.id===id?{...prev,...fields}:prev);
  };

  const addSub = (sub) => {
    const newSub = {...sub, id:"sub_"+Date.now(), added_date:new Date().toISOString().slice(0,10),
      projects_worked:[], total_paid_ytd:0, total_paid_lifetime:0, rating:0, on_time_pct:100,
      quality_score:3, active:true, pin:Math.floor(1000+Math.random()*9000).toString()};
    save({...db, subs:[...db.subs, newSub]});
    setShowAdd(false);
  };

  const trades = ["All", ...Array.from(new Set(db.subs.map(s=>s.trade))).sort()];

  const filtered = db.subs.filter(s=>{
    const matchSearch = !search || s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.company.toLowerCase().includes(search.toLowerCase()) ||
      s.trade.toLowerCase().includes(search.toLowerCase());
    const matchTrade = filterTrade==="All" || s.trade===filterTrade;
    const matchStatus = filterStatus==="All" || (filterStatus==="Active"&&s.active) ||
      (filterStatus==="Preferred"&&s.preferred) || (filterStatus==="Inactive"&&!s.active);
    return matchSearch && matchTrade && matchStatus;
  });

  // Compliance alerts
  const expiringSoon = db.subs.filter(s=>{
    if (!s.coi_expiry) return false;
    const days = (new Date(s.coi_expiry)-new Date())/(1000*60*60*24);
    return days < 60 && days > 0;
  });
  const expired = db.subs.filter(s=>{
    if (!s.coi_expiry) return false;
    return new Date(s.coi_expiry) < new Date();
  });
  const missingW9 = db.subs.filter(s=>!s.w9_on_file&&s.active);

  const fmt = v => "$"+Math.round(v||0).toLocaleString();
  const StarRating = ({value, onChange}) => (
    <div style={{display:"flex",gap:3}}>
      {[1,2,3,4,5].map(n=>(
        <span key={n} onClick={()=>onChange&&onChange(n)} style={{fontSize:18,cursor:onChange?"pointer":"default",color:n<=Math.round(value||0)?T.gold:T.subtle}}>
          {n<=Math.round(value||0)?"★":"☆"}
        </span>
      ))}
    </div>
  );

  const DETAIL_TABS = ["info","compliance","rates","history","notes"];

  // ── DETAIL VIEW ─────────────────────────────────────────────────────────────
  if (selected) {
    const sub = db.subs.find(s=>s.id===selected.id)||selected;
    const coiOk = sub.coi_expiry && new Date(sub.coi_expiry) > new Date();
    const coiDays = sub.coi_expiry ? Math.round((new Date(sub.coi_expiry)-new Date())/(1000*60*60*24)) : null;

    const InlineField = ({label, value, field, type="text", options}) => {
      const [editing, setEditing] = useState(false);
      const [val, setVal] = useState(value||"");
      const commit = () => { updateSub(sub.id,{[field]:type==="number"?parseFloat(val)||0:val}); setEditing(false); };
      return (
        <div style={{marginBottom:12}}>
          <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{label}</div>
          {editing?(
            options?
              <select value={val} onChange={e=>setVal(e.target.value)} onBlur={commit} autoFocus
                style={{width:"100%",background:T.bg2,border:"1px solid "+T.gold,borderRadius:7,padding:"7px 10px",color:T.text,fontSize:13,fontFamily:"inherit"}}>
                {options.map(o=><option key={o}>{o}</option>)}
              </select>:
              <input type={type} value={val} onChange={e=>setVal(e.target.value)} onBlur={commit} onKeyDown={e=>e.key==="Enter"&&commit()} autoFocus
                style={{width:"100%",background:T.bg2,border:"1px solid "+T.gold,borderRadius:7,padding:"7px 10px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
          ):(
            <div onClick={()=>setEditing(true)}
              style={{padding:"7px 10px",background:T.bg2,borderRadius:7,border:"1px solid "+T.subtle,fontSize:13,color:val?T.text:T.muted,cursor:"pointer",minHeight:36,transition:"border-color 0.15s"}}
              onMouseEnter={e=>e.currentTarget.style.borderColor=T.gold}
              onMouseLeave={e=>e.currentTarget.style.borderColor=T.subtle}>
              {val||<span style={{color:T.muted,fontStyle:"italic"}}>Click to add...</span>}
            </div>
          )}
        </div>
      );
    };

    const CheckField = ({label, field, value, note}) => (
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",background:T.bg2,borderRadius:8,marginBottom:6,cursor:"pointer"}}
        onClick={()=>updateSub(sub.id,{[field]:!value})}>
        <div style={{width:22,height:22,borderRadius:6,background:value?T.green:T.bg3,border:"2px solid "+(value?T.green:T.subtle),display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.2s"}}>
          {value&&<span style={{color:"white",fontSize:12}}>✓</span>}
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:12,fontWeight:600,color:T.text}}>{label}</div>
          {note&&<div style={{fontSize:10,color:T.muted}}>{note}</div>}
        </div>
      </div>
    );

    return (
      <div style={{padding:24}}>
        {/* Header */}
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
          <button onClick={()=>setSelected(null)}
            style={{fontSize:20,background:"none",border:"none",cursor:"pointer",color:T.muted,padding:"4px 8px"}}>←</button>
          <div style={{width:48,height:48,borderRadius:12,background:sub.color+"20",border:"1px solid "+sub.color+"40",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:800,color:sub.color,flexShrink:0}}>
            {sub.trade.slice(0,3).toUpperCase()}
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:18,fontWeight:800,color:T.text}}>{sub.name}</div>
            <div style={{fontSize:12,color:T.muted}}>{sub.company} · {sub.trade}{sub.license?" · "+sub.license:""}</div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {sub.preferred&&<span style={{fontSize:10,fontWeight:700,background:T.goldBg,color:T.gold,borderRadius:20,padding:"3px 10px"}}>⭐ Preferred</span>}
            <span style={{fontSize:10,fontWeight:700,background:sub.active?T.greenBg:T.redBg,color:sub.active?T.green:T.red,borderRadius:20,padding:"3px 10px"}}>{sub.active?"Active":"Inactive"}</span>
            <button onClick={()=>updateSub(sub.id,{preferred:!sub.preferred})}
              style={{fontSize:11,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"5px 10px",cursor:"pointer",color:T.muted}}>
              {sub.preferred?"Remove Preferred":"★ Set Preferred"}
            </button>
            <button onClick={()=>{
              if(!window.confirm(`Remove ${sub.name} from your sub roster? This cannot be undone.`)) return;
              const updated = {...db, subs: db.subs.filter(s=>s.id!==sub.id)};
              save(updated);
              setSelected(null);
            }} style={{fontSize:11,background:T.redBg||"#FDF2F2",border:"1px solid "+T.red,borderRadius:7,padding:"5px 10px",cursor:"pointer",color:T.red,fontWeight:700}}>
              🗑 Remove Sub
            </button>
          </div>
        </div>

        {/* Quick stats */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}}>
          {[
            {l:"Hourly Rate",    v:"$"+sub.hourly_rate+"/hr", c:T.gold},
            {l:"Paid This Year", v:fmt(sub.total_paid_ytd),   c:T.blue},
            {l:"Paid Lifetime",  v:fmt(sub.total_paid_lifetime),c:T.text},
            {l:"On-Time Rate",   v:(sub.on_time_pct||0)+"%",  c:sub.on_time_pct>=90?T.green:sub.on_time_pct>=75?T.amber:T.red},
          ].map(s=>(
            <div key={s.l} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 14px",textAlign:"center"}}>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{s.l}</div>
              <div style={{fontSize:18,fontWeight:800,color:s.c,fontFamily:"'Space Mono',monospace"}}>{s.v}</div>
            </div>
          ))}
        </div>

        {/* Rating */}
        <Card style={{marginBottom:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:4}}>Performance Rating</div>
              <StarRating value={sub.rating} onChange={v=>updateSub(sub.id,{rating:v})}/>
            </div>
            <div style={{display:"flex",gap:16}}>
              {[["On-Time %","on_time_pct","%"],["Quality (1-5)","quality_score",""]].map(([l,f,suf])=>(
                <div key={f} style={{textAlign:"center"}}>
                  <div style={{fontSize:9,color:T.muted,marginBottom:4}}>{l}</div>
                  <input type="number" defaultValue={sub[f]||0}
                    onBlur={e=>updateSub(sub.id,{[f]:parseFloat(e.target.value)||0})}
                    style={{width:60,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"5px 7px",color:T.text,fontSize:14,fontFamily:"'Space Mono',monospace",fontWeight:700,textAlign:"center"}}/>
                  <div style={{fontSize:9,color:T.muted}}>{suf}</div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Detail tabs */}
        <div style={{display:"flex",gap:4,marginBottom:14,flexWrap:"wrap"}}>
          {DETAIL_TABS.map(t=>(
            <button key={t} onClick={()=>setDetailTab(t)}
              style={{padding:"6px 14px",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",background:detailTab===t?T.gold:T.bg2,color:detailTab===t?T.bg0:T.muted,textTransform:"capitalize",transition:"all 0.15s"}}>
              {t}
            </button>
          ))}
        </div>

        {detailTab==="info"&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            <Card>
              <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:12}}>Contact Info</div>
              <InlineField label="Full Name"     field="name"    value={sub.name}/>
              <InlineField label="Company"       field="company" value={sub.company}/>
              <InlineField label="Trade"         field="trade"   value={sub.trade}
                options={["Plumbing","Electrical","HVAC","Framing","Drywall","Tile","Flooring","Paint","Concrete","Roofing","General Labor","Other"]}/>
              <InlineField label="Phone"         field="phone"   value={sub.phone}/>
              <InlineField label="Email"         field="email"   value={sub.email}/>
              <InlineField label="Address"       field="address" value={sub.address}/>
            </Card>
            <Card>
              <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:12}}>License & Registration</div>
              <InlineField label="CSLB License #"  field="cslb"    value={sub.cslb}/>
              <InlineField label="License Class"   field="license" value={sub.license}/>
              <InlineField label="EIN / Tax ID"    field="ein"     value={sub.ein}/>
              <InlineField label="DIR Registration #" field="dir_number" value={sub.dir_number}/>
              <InlineField label="Portal PIN"      field="pin"     value={sub.pin}/>
              <div style={{marginTop:12}}>
                <CheckField label="Corporation / Inc / S-Corp" field="is_corp" value={sub.is_corp} note="If checked, 1099 not required"/>
              </div>
            </Card>
          </div>
        )}

        {detailTab==="compliance"&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            <Card>
              <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:12}}>Document Status</div>
              <CheckField label="W-9 On File"           field="w9_on_file"    value={sub.w9_on_file}    note={sub.is_corp?"Corp — 1099 exempt, but still collect W-9":"Required before first payment"}/>
              <CheckField label="COI On File"           field="coi_on_file"   value={sub.coi_on_file}   note="GL + Workers Comp + Additional Insured"/>
              <CheckField label="DIR Registered"        field="dir_registered" value={sub.dir_registered} note="Required on all prevailing wage jobs"/>
              <CheckField label="Signed Subcontract"    field="contract_signed" value={sub.contract_signed} note="Master subcontract agreement on file"/>
              <div style={{marginTop:14}}>
                <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>COI Expiry Date</div>
                <input type="date" defaultValue={sub.coi_expiry}
                  onBlur={e=>updateSub(sub.id,{coi_expiry:e.target.value})}
                  style={{width:"100%",background:T.bg2,border:"1px solid "+(coiOk?T.green:T.red),borderRadius:7,padding:"8px 10px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
                {coiDays!==null&&(
                  <div style={{fontSize:11,marginTop:4,color:coiDays<0?T.red:coiDays<60?T.amber:T.green,fontWeight:600}}>
                    {coiDays<0?"⚠ EXPIRED "+Math.abs(coiDays)+" days ago":coiDays<60?"⚡ Expires in "+coiDays+" days":"✓ Valid — "+coiDays+" days remaining"}
                  </div>
                )}
              </div>
            </Card>
            <Card>
              <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:12}}>1099 Tracker</div>
              <div style={{background:sub.is_corp?T.greenBg:sub.total_paid_ytd>=600?T.amberBg:T.bg2,border:"1px solid "+(sub.is_corp?T.green:sub.total_paid_ytd>=600?T.amber:T.subtle),borderRadius:9,padding:"14px",marginBottom:12}}>
                <div style={{fontSize:12,fontWeight:700,color:sub.is_corp?T.green:sub.total_paid_ytd>=600?T.amber:T.text,marginBottom:6}}>
                  {sub.is_corp?"✓ Corporation — 1099 NOT required":sub.total_paid_ytd>=600?"⚠ 1099-NEC required — "+fmt(sub.total_paid_ytd)+" paid this year":"No 1099 required yet — "+fmt(sub.total_paid_ytd)+" paid this year"}
                </div>
                <div style={{fontSize:11,color:T.muted,lineHeight:1.6}}>
                  {sub.is_corp?"Still collect W-9 for your records.":sub.total_paid_ytd>=600?"File 1099-NEC by Jan 31. Use W-9 for their EIN/SSN.":"1099 required when total reaches $600."}
                </div>
              </div>
              <CheckField label="1099 Filed This Year" field="filed_1099" value={sub.filed_1099} note="Mark when filed with IRS and sent to sub"/>
              <div style={{marginTop:12}}>
                <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Total Paid This Year</div>
                <input type="number" defaultValue={sub.total_paid_ytd||0}
                  onBlur={e=>updateSub(sub.id,{total_paid_ytd:parseFloat(e.target.value)||0})}
                  style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:14,fontFamily:"'Space Mono',monospace",fontWeight:700}}/>
              </div>
            </Card>
          </div>
        )}

        {detailTab==="rates"&&(
          <Card>
            <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:14}}>Rates & Payment Terms</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14}}>
              <InlineField label="Hourly Rate ($/hr)" field="hourly_rate" value={sub.hourly_rate} type="number"/>
              <InlineField label="Markup You Apply (%)" field="markup_pct" value={sub.markup_pct} type="number"/>
              <InlineField label="Payment Terms" field="payment_terms" value={sub.payment_terms}
                options={["Net 15","Net 30","Net 45","Net 60","Upon Completion","50/50"]}/>
            </div>
            <div style={{background:T.bg2,borderRadius:9,padding:"14px",marginTop:14}}>
              <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:8}}>What you charge owner for this sub:</div>
              {[50000,100000,150000].map(v=>(
                <div key={v} style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"4px 0",borderBottom:"1px solid "+T.subtle,color:T.muted}}>
                  <span>Sub invoice: {fmt(v)}</span>
                  <span style={{color:T.gold,fontWeight:700}}>→ You charge: {fmt(v*(1+(sub.markup_pct||15)/100))}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {detailTab==="history"&&(
          <Card>
            <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:12}}>Project History</div>
            {(sub.projects_worked||[]).length===0?(
              <div style={{fontSize:12,color:T.muted,fontStyle:"italic"}}>No project history yet.</div>
            ):(sub.projects_worked||[]).map((pid,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 12px",background:T.bg2,borderRadius:8,marginBottom:6}}>
                <div style={{fontSize:12,fontWeight:600,color:T.text}}>Project {pid}</div>
                <span style={{fontSize:10,color:T.green,fontWeight:600}}>Completed</span>
              </div>
            ))}
            <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid "+T.subtle}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.muted,marginBottom:4}}>
                <span>Total paid lifetime</span>
                <span style={{color:T.gold,fontWeight:700}}>{fmt(sub.total_paid_lifetime)}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.muted}}>
                <span>Total paid this year</span>
                <span style={{color:T.blue,fontWeight:700}}>{fmt(sub.total_paid_ytd)}</span>
              </div>
            </div>
          </Card>
        )}

        {detailTab==="notes"&&(
          <Card>
            <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Notes & Comments</div>
            <textarea defaultValue={sub.notes||""}
              onBlur={e=>updateSub(sub.id,{notes:e.target.value})}
              placeholder="Performance notes, issues, preferences, special skills, anything to remember about this sub..."
              rows={8} style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"12px",color:T.text,fontSize:13,resize:"vertical",fontFamily:"inherit",lineHeight:1.7}}/>
            <div style={{fontSize:10,color:T.muted,marginTop:6}}>Click outside to save</div>
          </Card>
        )}
      </div>
    );
  }

  // ── ROSTER VIEW ─────────────────────────────────────────────────────────────
  return (
    <div style={{padding:24}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          {pendingSubs.length>0&&(<div style={{background:T.amberBg,border:"2px solid "+T.amber+"60",borderRadius:10,padding:"12px 16px",marginBottom:16}}><div style={{fontSize:13,fontWeight:700,color:T.amber,marginBottom:8}}>⏳ {pendingSubs.length} Sub Join Request{pendingSubs.length!==1?"s":""} — Awaiting Your Approval</div>{pendingSubs.map(req=>(<div key={req.subId} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:T.bg1,borderRadius:8,marginBottom:6,border:"1px solid "+T.subtle,flexWrap:"wrap"}}><div style={{flex:1,minWidth:180}}><div style={{fontSize:12,fontWeight:700,color:T.text}}>{req.name} — {req.trade}</div><div style={{fontSize:10,color:T.muted,marginTop:2}}>{req.email} · {req.company||"Independent"} · Project: <strong style={{color:T.gold}}>{req.projectName}</strong></div>{req.license&&<div style={{fontSize:10,color:T.muted}}>License: {req.license}</div>}</div><div style={{display:"flex",gap:6,flexShrink:0}}><button onClick={()=>{approvePendingSub(req.subId,session?.companyId);const ns={id:req.subId,name:req.name,email:req.email,trade:req.trade,company:req.company||"",license:req.license||"",phone:req.phone||"",status:"active",coi_on_file:false,w9_on_file:false,hourly_rate:0,addedDate:new Date().toLocaleDateString()};const u={...db,subs:[...(db.subs||[]),ns]};setDb(u);saveSubDB(u,session?.companyId);alert(req.name+' has been approved and can now log in!');}} style={{fontSize:11,fontWeight:700,background:T.green,color:"white",border:"none",borderRadius:7,padding:"6px 14px",cursor:"pointer"}}>✓ Approve</button><button onClick={()=>{rejectPendingSub(req.subId,session?.companyId);alert(req.name+"'s request has been declined.");setDb({...db});}} style={{fontSize:11,fontWeight:600,background:T.bg2,border:"1px solid "+T.red+"40",color:T.red,borderRadius:7,padding:"6px 12px",cursor:"pointer"}}>✗ Decline</button></div></div>))}</div>)}<div style={{fontSize:20,fontWeight:800,color:T.text}}>Subcontractor Roster</div>
          <div style={{fontSize:12,color:T.muted,marginTop:3}}>{db.subs.filter(s=>s.active).length} active subs · Master database across all projects</div>
        </div>
        <Btn primary onClick={()=>setShowAdd(true)}><Ic d={ICONS.plus} size={13}/>Add Subcontractor</Btn>
      </div>

      {/* Compliance alerts */}
      {(expiringSoon.length>0||expired.length>0||missingW9.length>0)&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:16}}>
          {expired.length>0&&<div style={{background:T.redBg,border:"1px solid "+T.red+"30",borderRadius:9,padding:"10px 14px"}}>
            <div style={{fontSize:11,fontWeight:700,color:T.red,marginBottom:4}}>⚠ {expired.length} COI Expired</div>
            {expired.map(s=><div key={s.id} style={{fontSize:10,color:T.muted}}>{s.name} — {s.company}</div>)}
          </div>}
          {expiringSoon.length>0&&<div style={{background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:9,padding:"10px 14px"}}>
            <div style={{fontSize:11,fontWeight:700,color:T.amber,marginBottom:4}}>⏰ {expiringSoon.length} COI Expiring Soon</div>
            {expiringSoon.map(s=><div key={s.id} style={{fontSize:10,color:T.muted}}>{s.name} — {s.coi_expiry}</div>)}
          </div>}
          {missingW9.length>0&&<div style={{background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:9,padding:"10px 14px"}}>
            <div style={{fontSize:11,fontWeight:700,color:T.amber,marginBottom:4}}>📋 {missingW9.length} Missing W-9</div>
            {missingW9.map(s=><div key={s.id} style={{fontSize:10,color:T.muted}}>{s.name}</div>)}
          </div>}
        </div>
      )}

      {/* Search + filter */}
      <div style={{display:"flex",gap:8,marginBottom:14}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name, company, trade..."
          style={{flex:1,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
        <select value={filterTrade} onChange={e=>setFilterTrade(e.target.value)}
          style={{background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}>
          {trades.map(t=><option key={t}>{t}</option>)}
        </select>
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
          style={{background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}>
          {["All","Active","Preferred","Inactive"].map(s=><option key={s}>{s}</option>)}
        </select>
      </div>

      {/* Add Sub Form */}
      {showAdd&&<AddSubForm onSave={addSub} onCancel={()=>setShowAdd(false)}/>}

      {/* Roster grid */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        {filtered.map(sub=>{
          const coiOk = sub.coi_expiry && new Date(sub.coi_expiry) > new Date();
          const coiSoon = coiOk && (new Date(sub.coi_expiry)-new Date())/(1000*60*60*24)<60;
          const issues = [];
          if (!sub.w9_on_file) issues.push("No W-9");
          if (!coiOk && sub.coi_expiry) issues.push("COI expired");
          if (coiSoon) issues.push("COI expiring");
          if (!sub.coi_on_file) issues.push("No COI");

          return (
            <div key={sub.id} onClick={()=>setSelected(sub)}
              style={{background:T.bg1,border:"1px solid "+(sub.preferred?T.gold+"40":T.subtle),borderRadius:12,padding:"14px 16px",cursor:"pointer",transition:"all 0.15s"}}
              onMouseEnter={e=>e.currentTarget.style.borderColor=T.goldDim}
              onMouseLeave={e=>e.currentTarget.style.borderColor=sub.preferred?T.gold+"40":T.subtle}>
              <div style={{display:"flex",gap:12,alignItems:"flex-start",marginBottom:10}}>
                <div style={{width:40,height:40,borderRadius:10,background:sub.color+"20",border:"1px solid "+sub.color+"40",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:sub.color,flexShrink:0}}>
                  {sub.trade.slice(0,3).toUpperCase()}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                    <span style={{fontSize:13,fontWeight:700,color:T.text}}>{sub.name}</span>
                    {sub.preferred&&<span style={{fontSize:9,color:T.gold}}>⭐</span>}
                    {!sub.active&&<span style={{fontSize:9,fontWeight:700,background:T.redBg,color:T.red,borderRadius:10,padding:"1px 6px"}}>INACTIVE</span>}
                  </div>
                  <div style={{fontSize:11,color:T.muted}}>{sub.company}</div>
                  <div style={{fontSize:10,color:T.muted,marginTop:2}}>{sub.trade}{sub.license?" · "+sub.license:""}</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.gold,fontFamily:"'Space Mono',monospace"}}>${sub.hourly_rate||"—"}/hr</div>
                  <div style={{fontSize:10,color:T.muted,marginTop:2}}>+{sub.markup_pct||15}% markup</div>
                </div>
              </div>

              {/* Compliance badges */}
              <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:8}}>
                <span style={{fontSize:9,fontWeight:700,background:sub.w9_on_file?T.greenBg:T.redBg,color:sub.w9_on_file?T.green:T.red,borderRadius:10,padding:"2px 7px"}}>W-9 {sub.w9_on_file?"✓":"✗"}</span>
                <span style={{fontSize:9,fontWeight:700,background:coiOk?T.greenBg:T.redBg,color:coiOk?T.green:T.red,borderRadius:10,padding:"2px 7px"}}>COI {coiOk?coiSoon?"⚡":"✓":"✗"}</span>
                <span style={{fontSize:9,fontWeight:700,background:sub.dir_registered?T.greenBg:T.bg3,color:sub.dir_registered?T.green:T.muted,borderRadius:10,padding:"2px 7px"}}>DIR {sub.dir_registered?"✓":"—"}</span>
                <span style={{fontSize:9,fontWeight:700,background:sub.is_corp?T.blueBg:T.bg3,color:sub.is_corp?T.blue:T.muted,borderRadius:10,padding:"2px 7px"}}>{sub.is_corp?"Corp":"Sole Prop"}</span>
              </div>

              {/* Stats row */}
              <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:T.muted,paddingTop:8,borderTop:"1px solid "+T.subtle}}>
                <span>Paid YTD: <strong style={{color:T.text}}>{fmt(sub.total_paid_ytd)}</strong></span>
                <span>On-time: <strong style={{color:sub.on_time_pct>=90?T.green:T.amber}}>{sub.on_time_pct||100}%</strong></span>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <div style={{display:"flex",gap:1}}>
                    {[1,2,3,4,5].map(n=>(
                      <span key={n} style={{fontSize:10,color:n<=(sub.rating||0)?T.gold:T.subtle}}>★</span>
                    ))}
                  </div>
                  <button onClick={(e)=>{
                    e.stopPropagation();
                    if(!window.confirm(`Remove ${sub.name} from Subcontractors? This cannot be undone.`)) return;
                    const updated = {...db, subs: db.subs.filter(s=>s.id!==sub.id)};
                    save(updated);
                  }} style={{fontSize:10,background:T.redBg,border:`1px solid ${T.red}30`,borderRadius:5,padding:"2px 7px",cursor:"pointer",color:T.red,fontWeight:700,marginLeft:4}}>
                    🗑
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length===0&&(
        <div style={{textAlign:"center",padding:"60px",color:T.muted}}>
          <div style={{fontSize:40,opacity:0.3,marginBottom:12}}>👷</div>
          <div style={{fontSize:13}}>{search?"No subs match your search":"No subcontractors added yet"}</div>
        </div>
      )}
    </div>
  );
}

function AddSubForm({onSave, onCancel}) {
  const [form, setForm] = useState({name:"",company:"",trade:"Plumbing",phone:"",email:"",cslb:"",license:"",hourly_rate:"",markup_pct:15,is_corp:false,preferred:false,color:"#60a5fa",active:true,w9_on_file:false,coi_on_file:false,dir_registered:false});
  const f = (k,v) => setForm(p=>({...p,[k]:v}));
  const TRADES = ["Plumbing","Electrical","HVAC","Framing","Drywall","Tile","Flooring","Paint","Concrete","Roofing","General Labor","Other"];
  const COLORS = ["#60a5fa","#f59e0b","#a78bfa","#2dd4bf","#f87171","#34d399","#fb923c","#e879f9"];

  return (
    <Card style={{marginBottom:16,border:"1px solid "+T.blue+"30"}}>
      <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14}}>Add New Subcontractor</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
        {[["name","Name *","Tom Rivera"],["company","Company *","Bay Electric Inc"],["phone","Phone","(415) 555-0100"],["email","Email","tom@bayelectric.com"],["cslb","CSLB #","123456"],["license","License Class","C-10"]].map(([k,l,ph])=>(
          <div key={k}>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>{l}</div>
            <input value={form[k]||""} onChange={e=>f(k,e.target.value)} placeholder={ph}
              style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
          </div>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
        <div>
          <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Trade</div>
          <select value={form.trade} onChange={e=>f("trade",e.target.value)}
            style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}>
            {TRADES.map(t=><option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Hourly Rate $</div>
          <input type="number" value={form.hourly_rate} onChange={e=>f("hourly_rate",e.target.value)} placeholder="145"
            style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"'Space Mono',monospace"}}/>
        </div>
        <div>
          <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Color Tag</div>
          <div style={{display:"flex",gap:5}}>
            {COLORS.map(c=><div key={c} onClick={()=>f("color",c)} style={{width:24,height:24,borderRadius:6,background:c,cursor:"pointer",border:"2px solid "+(form.color===c?"white":"transparent"),transition:"border 0.15s"}}/>)}
          </div>
        </div>
      </div>
      <div style={{display:"flex",gap:16,marginBottom:14}}>
        {[["is_corp","Corporation/Inc (no 1099)"],["preferred","Mark as Preferred"],["w9_on_file","W-9 On File"],["coi_on_file","COI On File"],["dir_registered","DIR Registered"]].map(([k,l])=>(
          <label key={k} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:11,color:T.muted}}>
            <input type="checkbox" checked={!!form[k]} onChange={e=>f(k,e.target.checked)} style={{width:13,height:13}}/>
            {l}
          </label>
        ))}
      </div>
      <div style={{display:"flex",gap:8}}>
        <Btn ghost onClick={onCancel} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
        <Btn primary disabled={!form.name||!form.company} onClick={()=>onSave(form)} style={{flex:2,justifyContent:"center"}}>Add to Roster</Btn>
      </div>
    </Card>
  );
}


// ─── SUB PERMIT & LICENSE PANEL (shown in sub portal when logged in) ──────────
const TRADE_PERMITS = {
  "Electrical": {
    license_class: "C-10 Electrical",
    permits_needed: ["Electrical Permit (required for any new wiring, panel, fixtures)","Service upgrade permit","Low-voltage permit (if applicable)"],
    inspections: ["Rough Electrical","Service/Panel","Final Electrical","AFCI/GFCI verification"],
    notes: "All electrical work >$500 requires permit in CA. AFCI required in all bedrooms (CEC 210.12).",
  },
  "Plumbing": {
    license_class: "C-36 Plumbing",
    permits_needed: ["Plumbing Permit","Water Heater Permit (if replacing)","Gas Line Permit (if applicable)"],
    inspections: ["Rough Plumbing","Water Heater","Gas Line Pressure Test","Final Plumbing"],
    notes: "WaterSense fixtures required by CalGreen. 2.0 GPM max showers.",
  },
  "HVAC": {
    license_class: "C-20 Warm-Air Heating",
    permits_needed: ["Mechanical Permit","Duct Permit","Title 24 HERS Testing (if required)"],
    inspections: ["Rough Mechanical","Duct Leakage Test","Final Mechanical","HERS Verification"],
    notes: "Title 24 Part 6 HERS rater sign-off required on most replacements.",
  },
  "Fire Sprinkler": {
    license_class: "C-16 Fire Protection",
    permits_needed: ["Fire Sprinkler Permit (separate from building permit)","SFFD/local fire dept review"],
    inspections: ["Rough Sprinkler","Pressure Test","Final Sprinkler","Fire Dept Final"],
    notes: "Required in R-2 buildings 4+ units (CBC 903). Coordinate with fire marshal early.",
  },
  "Structural/Framing": {
    license_class: "C-5 Framing / B General",
    permits_needed: ["Building Permit (included in main permit usually)","Engineered drawings if load-bearing"],
    inspections: ["Foundation","Rough Framing","Shear Wall","Hold-Down","Final Framing"],
    notes: "Bay Area SDC D — shear walls, hold-downs, anchor bolts required. Wet stamp for structural changes.",
  },
  "Roofing": {
    license_class: "C-39 Roofing",
    permits_needed: ["Roofing Permit (if >10% replacement or new)","Title 24 cool roof compliance"],
    inspections: ["Underlayment","Final Roofing"],
    notes: "Title 24 cool roof reflectance requirements. Permit required if replacing over 50% in SF.",
  },
  "Concrete/Foundation": {
    license_class: "C-8 Concrete",
    permits_needed: ["Building Permit","Special Inspection (if structural)","Soils report (if new foundation)"],
    inspections: ["Pre-Slab / Foundation","Concrete Pour","Special Inspector Reports","Final"],
    notes: "Special inspection required for structural concrete per CBC 1705.",
  },
  "General": {
    license_class: "B General Building",
    permits_needed: ["Building Permit","Any applicable trade permits for self-performed work"],
    inspections: ["Varies by scope — check with AHJ"],
    notes: "B license covers general construction. Pull trade permits for any licensed trade work.",
  },
};

function SubPermitPanel({loggedSub, proj, session}) {
  const key = "grok_sub_permits_" + proj?.id + "_" + loggedSub?.id;
  const [data, setData] = useState(()=>{
    try{return JSON.parse(localStorage.getItem(key)||"null");}catch{return null;}
  });
  const [form, setForm] = useState({
    permit_no: data?.permit_no||"",
    permit_type: data?.permit_type||"",
    permit_issued: data?.permit_issued||"",
    permit_expires: data?.permit_expires||"",
    license_no: data?.license_no||loggedSub?.license||"",
    license_class: data?.license_class||"",
    dir_number: data?.dir_number||loggedSub?.dir_number||"",
    inspection_contact: data?.inspection_contact||"",
    rough_in_ready: data?.rough_in_ready||"",
    final_ready: data?.final_ready||"",
    notes: data?.notes||"",
    inspections: data?.inspections||[],
  });
  const [saved, setSaved] = useState(false);
  const [addInsp, setAddInsp] = useState(false);
  const [newInsp, setNewInsp] = useState({type:"",date:"",time:"",status:"scheduled",notes:""});

  const tradeInfo = TRADE_PERMITS[loggedSub?.trade] || TRADE_PERMITS["General"];
  const dept = proj?.building_dept ? {
    name: proj.building_dept,
    phone: proj.building_dept_phone||"",
    url: proj.building_dept_url||"",
    cutoff: proj.inspection_cutoff||"",
  } : {name:"Building Department",phone:"",url:"",cutoff:""};

  const save = (updated) => {
    setData(updated);
    setForm(updated);
    try{localStorage.setItem(key,JSON.stringify(updated));}catch(e){}
    setSaved(true);
    setTimeout(()=>setSaved(false),2000);
  };

  const saveForm = () => save({...form});

  const addInspection = () => {
    if (!newInsp.type||!newInsp.date) return;
    const updated = {...form, inspections:[...(form.inspections||[]),{...newInsp,id:"si_"+Date.now()}]};
    save(updated);
    setNewInsp({type:"",date:"",time:"",status:"scheduled",notes:""});
    setAddInsp(false);
  };

  const updateInspStatus = (id, status) => {
    const updated = {...form, inspections:(form.inspections||[]).map(i=>i.id===id?{...i,status}:i)};
    save(updated);
  };

  const STATUS_C = {scheduled:T.amber,passed:T.green,failed:T.red,rescheduled:T.purple};

  return (
    <div>
      {/* Trade permit requirements banner */}
      <div style={{background:T.blueBg,border:"1px solid "+T.blue+"30",borderRadius:10,padding:"14px 16px",marginBottom:14}}>
        <div style={{fontSize:12,fontWeight:700,color:T.blue,marginBottom:6}}>{loggedSub.trade} — {tradeInfo.license_class}</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div>
            <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Permits Required for This Trade</div>
            {tradeInfo.permits_needed.map((p,i)=>(
              <div key={i} style={{display:"flex",gap:6,fontSize:11,color:T.muted,marginBottom:3}}>
                <span style={{color:T.blue,flexShrink:0}}>→</span>{p}
              </div>
            ))}
          </div>
          <div>
            <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Inspections Required</div>
            {tradeInfo.inspections.map((p,i)=>(
              <div key={i} style={{display:"flex",gap:6,fontSize:11,color:T.muted,marginBottom:3}}>
                <span style={{color:T.teal,flexShrink:0}}>🔍</span>{p}
              </div>
            ))}
          </div>
        </div>
        <div style={{marginTop:8,fontSize:10,color:T.blue,fontStyle:"italic"}}>{tradeInfo.notes}</div>
      </div>

      {/* Building dept for this project */}
      {dept.name!=="Building Department"&&(
        <div style={{background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:9,padding:"10px 14px",marginBottom:14,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
          <span style={{fontSize:14}}>🏛</span>
          <div style={{flex:1}}>
            <div style={{fontSize:11,fontWeight:700,color:T.amber}}>{dept.name}</div>
            <div style={{fontSize:10,color:T.muted}}>{dept.cutoff&&"Inspection cutoff: "+dept.cutoff}</div>
          </div>
          {dept.phone&&(
            <a href={"tel:"+dept.phone.replace(/[^\d]/g,"")}
              style={{fontSize:11,fontWeight:700,background:T.amber,color:T.bg0,borderRadius:7,padding:"5px 12px",textDecoration:"none"}}>
              📞 {dept.phone}
            </a>
          )}
          {dept.url&&(
            <a href={"https://"+dept.url.replace("https://","")} target="_blank" rel="noreferrer"
              style={{fontSize:11,fontWeight:600,background:T.bg2,color:T.blue,border:"1px solid "+T.blue+"30",borderRadius:7,padding:"5px 12px",textDecoration:"none"}}>
              🌐 Schedule Online
            </a>
          )}
        </div>
      )}

      {/* Permit info form */}
      <Card style={{marginBottom:12}}>
        <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:12}}>My Permit & License Info — {proj?.name}</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          {[
            ["permit_no","Permit Number","202400-ELEC-001"],
            ["permit_type","Permit Type",loggedSub.trade+" Permit"],
            ["permit_issued","Date Issued","2026-01-15"],
            ["permit_expires","Expires","2027-01-15"],
            ["license_no","My License #",loggedSub.license||"C-10 #123456"],
            ["license_class","License Class",tradeInfo.license_class],
            ["dir_number","DIR Registration #",loggedSub.dir_number||""],
            ["inspection_contact","My Inspection Contact","Inspector Jones — (628) 555-0100"],
          ].map(([k,l,ph])=>(
            <div key={k}>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>{l}</div>
              <input value={form[k]||""} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} placeholder={ph}
                style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
            </div>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <div>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Rough-In Ready Date</div>
            <input type="date" value={form.rough_in_ready} onChange={e=>setForm(p=>({...p,rough_in_ready:e.target.value}))}
              style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
          </div>
          <div>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Final Inspection Ready Date</div>
            <input type="date" value={form.final_ready} onChange={e=>setForm(p=>({...p,final_ready:e.target.value}))}
              style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
          </div>
        </div>
        <div style={{marginBottom:10}}>
          <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Notes</div>
          <textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} rows={2}
            style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit",resize:"none"}}/>
        </div>
        <Btn primary onClick={saveForm} style={{background:saved?T.green:""}}>
          {saved?"✓ Saved!":"Save Permit Info"}
        </Btn>
      </Card>

      {/* Inspection log */}
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text}}>My Inspection Schedule</div>
          <Btn small primary onClick={()=>setAddInsp(p=>!p)}><Ic d={ICONS.plus} size={11}/>Schedule Inspection</Btn>
        </div>

        {addInsp&&(
          <div style={{background:T.bg2,borderRadius:8,padding:"12px",marginBottom:10}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8,marginBottom:8}}>
              <div>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Inspection Type *</div>
                <select value={newInsp.type} onChange={e=>setNewInsp(p=>({...p,type:e.target.value}))}
                  style={{width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"6px 8px",color:T.text,fontSize:11,fontFamily:"inherit"}}>
                  <option value="">Select...</option>
                  {tradeInfo.inspections.map(t=><option key={t}>{t}</option>)}
                  <option>Other</option>
                </select>
              </div>
              <div>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Date *</div>
                <input type="date" value={newInsp.date} onChange={e=>setNewInsp(p=>({...p,date:e.target.value}))}
                  style={{width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"6px 8px",color:T.text,fontSize:11}}/>
              </div>
              <div>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Time</div>
                <input type="time" value={newInsp.time} onChange={e=>setNewInsp(p=>({...p,time:e.target.value}))}
                  style={{width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"6px 8px",color:T.text,fontSize:11}}/>
              </div>
              <div>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Status</div>
                <select value={newInsp.status} onChange={e=>setNewInsp(p=>({...p,status:e.target.value}))}
                  style={{width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"6px 8px",color:T.text,fontSize:11,fontFamily:"inherit"}}>
                  <option value="scheduled">Scheduled</option>
                  <option value="passed">Passed</option>
                  <option value="failed">Failed</option>
                  <option value="rescheduled">Rescheduled</option>
                </select>
              </div>
            </div>
            <input value={newInsp.notes} onChange={e=>setNewInsp(p=>({...p,notes:e.target.value}))} placeholder="Notes..."
              style={{width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"6px 8px",color:T.text,fontSize:11,fontFamily:"inherit",marginBottom:8}}/>
            <div style={{display:"flex",gap:7}}>
              <Btn ghost small onClick={()=>setAddInsp(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
              <Btn primary small onClick={addInspection} disabled={!newInsp.type||!newInsp.date} style={{flex:2,justifyContent:"center"}}>Add to Schedule</Btn>
            </div>
          </div>
        )}

        {(form.inspections||[]).length===0?(
          <div style={{fontSize:11,color:T.muted,fontStyle:"italic",textAlign:"center",padding:"16px"}}>No inspections scheduled yet.</div>
        ):(form.inspections||[]).sort((a,b)=>a.date>b.date?1:-1).map(insp=>(
          <div key={insp.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 12px",background:T.bg2,borderRadius:8,marginBottom:5,border:"1px solid "+(STATUS_C[insp.status]||T.subtle)+"30"}}>
            <div>
              <div style={{fontSize:12,fontWeight:600,color:T.text}}>{insp.type}</div>
              <div style={{fontSize:10,color:T.muted}}>{insp.date}{insp.time?" @ "+insp.time:""}{insp.notes?" · "+insp.notes:""}</div>
            </div>
            <select value={insp.status} onChange={e=>updateInspStatus(insp.id,e.target.value)}
              style={{fontSize:9,fontWeight:700,background:(STATUS_C[insp.status]||T.muted)+"15",color:STATUS_C[insp.status]||T.muted,border:"none",borderRadius:10,padding:"2px 8px",cursor:"pointer",fontFamily:"inherit"}}>
              {["scheduled","passed","failed","rescheduled"].map(s=><option key={s} value={s}>{s.toUpperCase()}</option>)}
            </select>
          </div>
        ))}
      </Card>
    </div>
  );
}


function SubPortalView({projects, defaultTab, session}) {
  const [mode, setMode] = useState(defaultTab||"dashboard"); // dashboard | login | subview | manage
  const [loggedSub, setLoggedSub] = useState(null);
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState("");
  const [selectedProject, setSelectedProject] = useState(projects[0]);
  const [subs, setSubs] = useState(SUB_PORTAL_DATA.subs);
  const [expandedSubDocs, setExpandedSubDocs] = useState(null);
  const [logs, setLogs] = useState(SUB_PORTAL_DATA.logs);
  const [issues, setIssues] = useState(SUB_PORTAL_DATA.issues);
  const [rfis, setRfis] = useState(SUB_PORTAL_DATA.rfis);
  const [newLog, setNewLog] = useState({work_done:"",hours:"",crew_size:"",progress_pct:"",notes:"",tomorrow_plan:""});
  const [newIssue, setNewIssue] = useState({title:"",description:"",priority:"medium",trade_conflict:false});
  const [newRfi, setNewRfi] = useState({subject:"",description:"",priority:"routine",affects_schedule:false});
  const [activeTab, setActiveTab] = useState("schedule");
  const [showAddSub, setShowAddSub] = useState(false);
  const [newSub, setNewSub] = useState({name:"",company:"",trade:"",pin:"",color:"#60a5fa"});
  const [aiSchedule, setAiSchedule] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const TRADE_COLORS = {"Plumbing":"#60a5fa","Electrical":"#f59e0b","Tile":"#a78bfa","Framing":"#22c55e","Drywall":"#2dd4bf","HVAC":"#ef4444","Painting":"#d4a843","Demo":"#6b7a8f","General":"#e8edf5"};

  const projSubs = subs.filter(s => s.projects?.includes(selectedProject?.id?.toString()) || s.projects?.includes(selectedProject?.id));
  const projLogs = logs.filter(l => l.projectId === selectedProject?.id);
  const projIssues = issues.filter(i => i.projectId === selectedProject?.id);
  const projRfis = rfis.filter(r => r.projectId === selectedProject?.id);

  const loginSub = () => {
    const found = subs.find(s => s.pin === pinInput.trim());
    if (found) { setLoggedSub(found); setPinError(""); setMode("subview"); setActiveTab("schedule"); }
    else { setPinError("Invalid PIN — check with your project manager"); setPinInput(""); }
  };

  const submitLog = () => {
    if (!newLog.work_done.trim()) return;
    const log = {...newLog, id:Date.now(), subId:loggedSub.id, subName:loggedSub.name, trade:loggedSub.trade, company:loggedSub.company, projectId:selectedProject.id, projectName:selectedProject.name, date:new Date().toLocaleDateString(), time:new Date().toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})};
    const updated = [log, ...logs];
    setLogs(updated); SUB_PORTAL_DATA.logs = updated;
    setNewLog({work_done:"",hours:"",crew_size:"",progress_pct:"",notes:"",tomorrow_plan:""});
  };

  const submitIssue = () => {
    if (!newIssue.title.trim()) return;
    const issue = {...newIssue, id:Date.now(), subId:loggedSub.id, subName:loggedSub.name, trade:loggedSub.trade, projectId:selectedProject.id, date:new Date().toLocaleDateString(), status:"open"};
    const updated = [issue, ...issues];
    setIssues(updated); SUB_PORTAL_DATA.issues = updated;
    setNewIssue({title:"",description:"",priority:"medium",trade_conflict:false});
  };

  const submitRfi = () => {
    if (!newRfi.subject.trim()) return;
    const rfi = {...newRfi, id:Date.now(), subId:loggedSub.id, subName:loggedSub.name, trade:loggedSub.trade, projectId:selectedProject.id, date:new Date().toLocaleDateString(), status:"open", num:"RFI-S"+String(rfis.length+1).padStart(3,"0")};
    const updated = [rfi, ...rfis];
    setRfis(updated); SUB_PORTAL_DATA.rfis = updated;
    setNewRfi({subject:"",description:"",priority:"routine",affects_schedule:false});
  };

  const generateSchedule = async () => {
    const key = "proxy"; // Using Supabase AI proxy
    if (!key) { alert("Add API key in AI Agent first."); return; }
    const kb = loadKB();
    setAiLoading(true); setAiSchedule("");
    const logsText = projLogs.slice(0,10).map(l=>`${l.date} - ${l.trade} (${l.subName}): ${l.work_done}. Progress: ${l.progress_pct||"N/A"}%. Tomorrow: ${l.tomorrow_plan||"TBD"}`).join("\n");
    const issuesText = projIssues.filter(i=>i.status==="open").map(i=>`[${i.priority.toUpperCase()}] ${i.trade}: ${i.title} - ${i.description}`).join("\n");
    const rfisText = projRfis.filter(r=>r.status==="open").map(r=>`${r.trade}: ${r.subject}`).join("\n");
    const subsText = projSubs.map(s=>`${s.trade}: ${s.name} (${s.company})`).join("\n");
    await callAI(
      buildSystemPrompt(`Managing ${selectedProject?.name} with subs: ${projSubs.map(s=>s.trade).join(", ")}`, kb),
      `Generate a 2-week lookahead schedule for this project based on current progress.\n\nProject: ${selectedProject?.name}\nAddress: ${selectedProject?.addr}\nType: ${selectedProject?.type}\nProgress: ${selectedProject?.pct}%\n\nActive Subcontractors:\n${subsText||"None logged yet"}\n\nRecent Daily Logs:\n${logsText||"No logs yet"}\n\nOpen Issues:\n${issuesText||"None"}\n\nOpen Sub RFIs:\n${rfisText||"None"}\n\nProvide:\n1. 2-WEEK LOOKAHEAD SCHEDULE\nDay by day for next 14 working days. Who is on site, what they are doing, in what order. Flag any trade conflicts where two subs need the same space.\n\n2. TRADE CONFLICT ANALYSIS\nIdentify any scheduling conflicts between trades. Where plumber and electrician need the same walls. Where tile can't start until plumbing is done. Exact coordination needed.\n\n3. CRITICAL PATH - NEXT 2 WEEKS\nWhat absolutely must happen in sequence. What will delay everything else if it slips.\n\n4. INSPECTION HOLD POINTS\nWhat inspections are coming up. When to call for rough inspection. What needs to be ready.\n\n5. RISK FLAGS\nBased on current progress and open issues, what is most likely to cause a delay in the next 2 weeks.\n\n6. RECOMMENDED ACTIONS FOR TODAY\nTop 3 things the GC should do right now to keep the project on track.`,
      t => setAiSchedule(p => p+t), key
    ).catch(e=>setAiSchedule("Error: "+e.message));
    setAiLoading(false);
  };

  const TabBtn = ({id,label,badge,color}) => (
    <button onClick={()=>setActiveTab(id)} style={{padding:"7px 16px",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:activeTab===id?T.gold:T.bg2,color:activeTab===id?T.bg0:T.muted,transition:"all 0.15s",display:"flex",alignItems:"center",gap:6,whiteSpace:"nowrap"}}>
      {label}{badge>0&&<span style={{background:activeTab===id?"rgba(0,0,0,0.2)":color||T.red,color:"white",fontSize:9,padding:"1px 5px",borderRadius:10,fontWeight:800}}>{badge}</span>}
    </button>
  );

  // ── DASHBOARD (GC view) ────────────────────────────────────────────────────
  if (mode === "dashboard" || mode === "manage") return (
    <div className="view-pad" style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:8}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>Subcontractor Portal</div>
          <div style={{fontSize:12,color:T.muted,marginTop:3}}>Real-time field coordination — subs log progress, flag issues, submit RFIs, and see each other's schedules</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <Btn ghost small onClick={()=>setMode("login")}>Sub Login</Btn>
          <Btn primary small onClick={()=>setShowAddSub(true)}><Ic d={ICONS.plus} size={12}/>Add Subcontractor</Btn>
        </div>
      </div>

      {/* Project selector */}
      <div style={{marginBottom:16}}>
        <Select label="Project" value={selectedProject?.id} onChange={v=>setSelectedProject(validProjects.find(p=>p.id===parseInt(v)||p.id===v))} options={projects.filter(p=>p.status==="active").map(p=>({value:p.id,label:(p.name||String(p.id)||"Project")}))}/>
      </div>

      {/* Stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}}>
        {[
          {l:"Active Subs",v:projSubs.length,c:T.teal},
          {l:"Logs Today",v:projLogs.filter(l=>l.date===new Date().toLocaleDateString()).length,c:T.green},
          {l:"Open Issues",v:projIssues.filter(i=>i.status==="open").length,c:T.red},
          {l:"Open RFIs",v:projRfis.filter(r=>r.status==="open").length,c:T.amber},
        ].map(s=><StatCard key={s.l} label={s.l} value={s.v} color={s.c}/>)}
      </div>

      <div className="two-col" style={{display:"grid",gridTemplateColumns:"280px 1fr",gap:14}}>
        {/* Sub roster */}
        <div>
          <Card>
            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Sub Roster — {selectedProject?.name?.split(" —")[0]}</div>
            {projSubs.length===0&&<div style={{fontSize:12,color:T.muted,padding:"12px 0"}}>No subs assigned to this project yet.</div>}
            {projSubs.map(sub=>{
              const lastLog = projLogs.find(l=>l.subId===sub.id);
              const todayLog = projLogs.find(l=>l.subId===sub.id&&l.date===new Date().toLocaleDateString());
              return (
                <React.Fragment key={sub.id}>
                <div style={{display:"flex",gap:10,alignItems:"flex-start",padding:"10px 0",borderBottom:`1px solid ${T.subtle}`}}>
                  <div style={{width:36,height:36,borderRadius:9,background:sub.color+"18",border:`1px solid ${sub.color}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:sub.color,flexShrink:0}}>{sub.trade.slice(0,3).toUpperCase()}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:700,color:T.text}}>{sub.name}</div>
                    <div style={{fontSize:10,color:T.muted}}>{sub.company} · {sub.trade}</div>
                    <div style={{fontSize:9,color:todayLog?T.green:T.muted,marginTop:3,display:"flex",alignItems:"center",gap:4}}>
                      <div style={{width:5,height:5,borderRadius:"50%",background:todayLog?T.green:T.subtle}}/>
                      {todayLog?"Logged today":"No log today"}
                    </div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:4,alignItems:"flex-end",flexShrink:0}}>
                    <div style={{fontSize:9,fontFamily:"monospace",background:T.bg3,color:T.muted,borderRadius:4,padding:"2px 7px"}}>PIN: {sub.pin}</div>
                    <button onClick={(e)=>{e.stopPropagation();setExpandedSubDocs(expandedSubDocs===sub.id?null:sub.id);}}
                      style={{fontSize:9,fontWeight:700,background:expandedSubDocs===sub.id?T.goldBg:T.bg3,border:"1px solid "+(expandedSubDocs===sub.id?T.gold:T.subtle),borderRadius:5,padding:"2px 8px",cursor:"pointer",color:expandedSubDocs===sub.id?T.gold:T.muted}}>
                      {expandedSubDocs===sub.id?"▲ Docs":"📋 Docs & Pay"}
                    </button>
                  </div>
                </div>
                {expandedSubDocs===sub.id&&(
                  <div style={{background:T.bg2,border:"1px solid "+T.gold+"30",borderRadius:"0 0 10px 10px",marginTop:0}}>
                    <ProjectDocSequence proj={selectedProject} session={{companyName:"Grok Construction",email:"Jehoon@gcginc.com",ownerName:"Jehoon Chun",cslb:"1152750"}} singleSub={sub}/>
                  </div>
                )}
                </React.Fragment>
              );
            })}
            <Btn small ghost onClick={()=>setShowAddSub(true)} style={{width:"100%",justifyContent:"center",marginTop:10}}><Ic d={ICONS.plus} size={11}/>Add Sub to Project</Btn>
          </Card>

          {/* 2-week schedule */}
          <Card style={{marginTop:12}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:8}}>AI 2-Week Schedule</div>
            <div style={{fontSize:11,color:T.muted,marginBottom:10,lineHeight:1.5}}>Based on sub daily logs and current progress — updates every time you run it.</div>
            <Btn primary onClick={generateSchedule} disabled={aiLoading} style={{width:"100%",justifyContent:"center",background:`linear-gradient(135deg,${T.purple},${T.blue})`,color:"white",marginBottom:10}}>
              {aiLoading?<><Spin/>Analyzing...</>:<><Ic d={ICONS.robot} size={13}/>Generate 2-Week Schedule</>}
            </Btn>
            {aiSchedule&&(
              <div style={{background:T.bg0,borderRadius:8,padding:"10px 12px",fontSize:10,color:T.text,lineHeight:1.8,whiteSpace:"pre-wrap",maxHeight:300,overflowY:"auto",fontFamily:"monospace"}}>
                {aiSchedule}
              </div>
            )}
          </Card>
        </div>

        {/* Feed */}
        <div>
          <div style={{display:"flex",gap:4,marginBottom:14,flexWrap:"wrap"}}>
            <TabBtn id="schedule" label="Schedule View"/>
            <TabBtn id="logs" label="Daily Logs" badge={projLogs.length} color={T.green}/>
            <TabBtn id="issues" label="Issues" badge={projIssues.filter(i=>i.status==="open").length} color={T.red}/>
            <TabBtn id="rfis" label="Sub RFIs" badge={projRfis.filter(r=>r.status==="open").length} color={T.amber}/>
          </div>

          {/* SCHEDULE TAB — all subs see each other */}
          {activeTab==="schedule"&&(
            <div>
              <div style={{background:T.blueBg,borderLeft:`3px solid ${T.blue}`,borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:12,color:T.muted,lineHeight:1.5}}>
                <strong style={{color:T.blue}}>Shared Schedule</strong> — all subs on this project can see each other's planned work. Coordinate rough-in routes before you open walls.
              </div>
              {projSubs.length===0&&<div style={{textAlign:"center",padding:"40px",color:T.muted,fontSize:13}}>No subs assigned yet — add subs to this project to see their schedules here.</div>}
              {projSubs.map(sub=>{
                const subLogs = projLogs.filter(l=>l.subId===sub.id).slice(0,3);
                return (
                  <div key={sub.id} style={{background:T.bg1,border:`1px solid ${sub.color}30`,borderLeft:`3px solid ${sub.color}`,borderRadius:10,padding:"14px 16px",marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                      <div style={{display:"flex",gap:10,alignItems:"center"}}>
                        <div style={{width:32,height:32,borderRadius:8,background:sub.color+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:sub.color}}>{sub.trade.slice(0,3).toUpperCase()}</div>
                        <div>
                          <div style={{fontSize:13,fontWeight:700,color:T.text}}>{sub.trade} — {sub.name}</div>
                          <div style={{fontSize:10,color:T.muted}}>{sub.company}</div>
                        </div>
                      </div>
                      {projIssues.filter(i=>i.subId===sub.id&&i.status==="open").length>0&&(
                        <span style={{fontSize:10,fontWeight:700,background:T.redBg,color:T.red,borderRadius:20,padding:"2px 9px",border:`1px solid ${T.red}30`}}>
                          {projIssues.filter(i=>i.subId===sub.id&&i.status==="open").length} open issue{projIssues.filter(i=>i.subId===sub.id&&i.status==="open").length>1?"s":""}
                        </span>
                      )}
                    </div>
                    {subLogs.length===0?(
                      <div style={{fontSize:11,color:T.muted,fontStyle:"italic"}}>No progress logged yet</div>
                    ):subLogs.map(log=>(
                      <div key={log.id} style={{background:T.bg2,borderRadius:7,padding:"8px 12px",marginBottom:6}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                          <span style={{fontSize:10,fontFamily:"monospace",color:T.muted}}>{log.date}</span>
                          {log.progress_pct&&<span style={{fontSize:10,fontWeight:700,color:sub.color}}>{log.progress_pct}% complete</span>}
                        </div>
                        <div style={{fontSize:12,color:T.text,marginBottom:3}}>{log.work_done}</div>
                        {log.tomorrow_plan&&<div style={{fontSize:11,color:T.muted}}><strong style={{color:T.teal}}>Tomorrow:</strong> {log.tomorrow_plan}</div>}
                        {log.notes&&<div style={{fontSize:11,color:T.amber,marginTop:3}}>{log.notes}</div>}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {/* LOGS TAB */}
          {activeTab==="logs"&&(
            <div>
              {projLogs.length===0&&<div style={{textAlign:"center",padding:"40px",color:T.muted,fontSize:13}}>No daily logs yet. Subs log in with their PIN to submit daily updates.</div>}
              {projLogs.map(log=>{
                const sub = subs.find(s=>s.subId===log.subId)||{color:T.muted};
                const subColor = subs.find(s=>s.id===log.subId)?.color||T.muted;
                return (
                  <div key={log.id} style={{background:T.bg1,border:`1px solid ${subColor}20`,borderLeft:`3px solid ${subColor}`,borderRadius:10,padding:"14px 16px",marginBottom:8}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                      <div>
                        <span style={{fontSize:12,fontWeight:700,color:T.text}}>{log.subName}</span>
                        <span style={{fontSize:11,color:T.muted}}> · {log.trade} · {log.company}</span>
                      </div>
                      <div style={{display:"flex",gap:8,alignItems:"center"}}>
                        {log.progress_pct&&<span style={{fontSize:11,fontWeight:700,color:subColor}}>{log.progress_pct}% done</span>}
                        <span style={{fontSize:10,color:T.muted,fontFamily:"monospace"}}>{log.date} {log.time}</span>
                      </div>
                    </div>
                    <div style={{fontSize:12,color:T.text,marginBottom:log.tomorrow_plan?6:0,lineHeight:1.5}}><strong>Today:</strong> {log.work_done}</div>
                    {log.hours&&<div style={{fontSize:11,color:T.muted}}>Hours: {log.hours} · Crew: {log.crew_size||"N/A"}</div>}
                    {log.tomorrow_plan&&<div style={{fontSize:12,color:T.teal,marginTop:4}}><strong>Tomorrow:</strong> {log.tomorrow_plan}</div>}
                    {log.notes&&<div style={{fontSize:11,color:T.amber,marginTop:4,background:T.amberBg,borderRadius:5,padding:"4px 8px"}}>{log.notes}</div>}
                  </div>
                );
              })}
            </div>
          )}

          {/* ISSUES TAB */}
          {activeTab==="issues"&&(
            <div>
              {projIssues.length===0&&<div style={{textAlign:"center",padding:"40px",color:T.muted,fontSize:13}}>No issues flagged. Subs can flag issues from their portal.</div>}
              {projIssues.map(issue=>{
                const pc = issue.priority==="high"?T.red:issue.priority==="medium"?T.amber:T.muted;
                const subColor = subs.find(s=>s.id===issue.subId)?.color||T.muted;
                return (
                  <div key={issue.id} style={{background:T.bg1,border:`1px solid ${pc}30`,borderLeft:`3px solid ${pc}`,borderRadius:10,padding:"14px 16px",marginBottom:8}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:3}}>{issue.title}</div>
                        <div style={{fontSize:11,color:T.muted}}>{issue.subName} · {issue.trade} · {issue.date}</div>
                      </div>
                      <div style={{display:"flex",gap:7,alignItems:"center"}}>
                        {issue.trade_conflict&&<span style={{fontSize:9,fontWeight:700,background:T.redBg,color:T.red,borderRadius:20,padding:"2px 8px"}}>TRADE CONFLICT</span>}
                        <Badge label={issue.priority} color={pc}/>
                        <Badge label={issue.status} color={issue.status==="open"?T.amber:T.green}/>
                      </div>
                    </div>
                    <div style={{fontSize:12,color:T.muted,lineHeight:1.5}}>{issue.description}</div>
                    {issue.status==="open"&&(
                      <Btn small ghost onClick={()=>{const u=issues.map(i=>i.id===issue.id?{...i,status:"resolved"}:i);setIssues(u);SUB_PORTAL_DATA.issues=u;}} style={{marginTop:8}}>Mark Resolved</Btn>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* RFIs TAB */}
          {activeTab==="rfis"&&(
            <div>
              {projRfis.length===0&&<div style={{textAlign:"center",padding:"40px",color:T.muted,fontSize:13}}>No sub RFIs yet.</div>}
              {projRfis.map(rfi=>{
                const pc = rfi.priority==="critical"?T.red:rfi.priority==="urgent"?T.amber:T.blue;
                return (
                  <div key={rfi.id} style={{background:T.bg1,border:`1px solid ${pc}20`,borderLeft:`3px solid ${pc}`,borderRadius:10,padding:"14px 16px",marginBottom:8}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                      <div>
                        <div style={{fontFamily:"monospace",fontSize:10,color:pc,marginBottom:3}}>{rfi.num}</div>
                        <div style={{fontSize:13,fontWeight:700,color:T.text}}>{rfi.subject}</div>
                        <div style={{fontSize:11,color:T.muted,marginTop:2}}>{rfi.subName} · {rfi.trade} · {rfi.date}</div>
                      </div>
                      <div style={{display:"flex",gap:7}}>
                        {rfi.affects_schedule&&<span style={{fontSize:9,fontWeight:700,background:T.redBg,color:T.red,borderRadius:20,padding:"2px 8px"}}>AFFECTS SCHEDULE</span>}
                        <Badge label={rfi.priority} color={pc}/>
                        <Badge label={rfi.status} color={rfi.status==="closed"?T.green:T.amber}/>
                      </div>
                    </div>
                    <div style={{fontSize:12,color:T.muted,lineHeight:1.5,marginBottom:8}}>{rfi.description}</div>
                    {rfi.status==="open"&&(
                      <Btn small primary onClick={()=>{const u=rfis.map(r=>r.id===rfi.id?{...r,status:"closed"}:r);setRfis(u);SUB_PORTAL_DATA.rfis=u;}}>Close RFI</Btn>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Add sub modal */}
      {showAddSub&&(
        <Modal title="Add Subcontractor" onClose={()=>setShowAddSub(false)}>
          <Field label="Contact Name" value={newSub.name} onChange={v=>setNewSub(p=>({...p,name:v}))} placeholder="Mike Chen"/>
          <Field label="Company" value={newSub.company} onChange={v=>setNewSub(p=>({...p,company:v}))} placeholder="SF Plumbing Inc"/>
          <Select label="Trade" value={newSub.trade} onChange={v=>setNewSub(p=>({...p,trade:v,color:TRADE_COLORS[v]||"#60a5fa"}))} options={Object.keys(TRADE_COLORS)}/>
          <Field label="PIN (4-6 digits)" value={newSub.pin} onChange={v=>setNewSub(p=>({...p,pin:v}))} placeholder="1234"/>
          <div style={{background:T.amberBg,borderLeft:`3px solid ${T.amber}`,borderRadius:7,padding:"9px 12px",fontSize:11,color:T.muted,marginTop:4,marginBottom:16}}>
            Share this PIN with the sub. They use it to log in at the Sub Login screen. Keep it confidential.
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn ghost onClick={()=>setShowAddSub(false)}>Cancel</Btn>
            <Btn primary disabled={!newSub.name||!newSub.trade||!newSub.pin} onClick={()=>{
              const sub={...newSub,id:"s"+Date.now(),projects:[selectedProject?.id?.toString()],active:true};
              const updated=[...subs,sub];setSubs(updated);SUB_PORTAL_DATA.subs=updated;
              setShowAddSub(false);setNewSub({name:"",company:"",trade:"",pin:"",color:"#60a5fa"});
            }}>Add Subcontractor</Btn>
          </div>
        </Modal>
      )}
    </div>
  );

  // ── SUB LOGIN ──────────────────────────────────────────────────────────────
  if (mode === "login") return (
    <div style={{padding:24,maxWidth:480,margin:"0 auto"}}>
      <button onClick={()=>setMode("dashboard")} style={{background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"7px 14px",color:T.muted,fontSize:12,cursor:"pointer",marginBottom:24}}>← Back</button>
      <div style={{textAlign:"center",marginBottom:32}}>
        <div style={{width:64,height:64,borderRadius:16,background:`linear-gradient(135deg,${T.gold},${T.teal})`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",fontSize:28,fontWeight:800,color:T.bg0}}>G</div>
        <div style={{fontSize:24,fontWeight:800,color:T.text,marginBottom:6}}>Subcontractor Login</div>
        <div style={{fontSize:13,color:T.muted}}>Enter your PIN to access your project</div>
      </div>
      <Card>
        <div style={{marginBottom:16}}>
          <Select label="Project" value={selectedProject?.id} onChange={v=>setSelectedProject(validProjects.find(p=>p.id===parseInt(v)||p.id===v))} options={projects.filter(p=>p.status==="active").map(p=>({value:p.id,label:(p.name||String(p.id)||"Project")}))}/>
        </div>
        <div style={{marginBottom:16}}>
          <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Your PIN</div>
          <input type="password" value={pinInput} onChange={e=>setPinInput(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&loginSub()}
            placeholder="Enter PIN" maxLength={6}
            style={{width:"100%",background:T.bg2,border:`1px solid ${pinError?T.red:T.subtle}`,borderRadius:8,padding:"14px 16px",color:T.text,fontSize:24,fontFamily:"monospace",letterSpacing:"0.3em",textAlign:"center",outline:"none"}}/>
          {pinError&&<div style={{fontSize:11,color:T.red,marginTop:6}}>{pinError}</div>}
        </div>
        <Btn primary onClick={loginSub} style={{width:"100%",justifyContent:"center",fontSize:16,padding:"14px"}}>Sign In</Btn>
      </Card>
    </div>
  );

  // ── SUB VIEW (what subs see) ───────────────────────────────────────────────
  if (mode === "subview" && loggedSub) return (
    <div style={{padding:24}}>
      {/* Sub header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div style={{display:"flex",gap:12,alignItems:"center"}}>
          <div style={{width:44,height:44,borderRadius:11,background:loggedSub.color+"18",border:`1px solid ${loggedSub.color}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:loggedSub.color}}>{loggedSub.trade.slice(0,3).toUpperCase()}</div>
          <div>
            <div style={{fontSize:16,fontWeight:800,color:T.text}}>{loggedSub.name} — {loggedSub.trade}</div>
            <div style={{fontSize:12,color:T.muted}}>{loggedSub.company} · {selectedProject?.name?.split(" —")[0]}</div>
          </div>
        </div>
        <Btn ghost small onClick={()=>{setLoggedSub(null);setMode("dashboard");}}>Sign Out</Btn>
      </div>

      <div style={{display:"flex",gap:4,marginBottom:16,flexWrap:"wrap"}}>
        <TabBtn id="permits" label="🪪 My Permits"/>
        <TabBtn id="schedule" label="Job Schedule"/>
        <TabBtn id="log_entry" label="Log Today's Work"/>
        <TabBtn id="flag_issue" label="Flag Issue"/>
        <TabBtn id="submit_rfi" label="Submit RFI"/>
        <TabBtn id="my_logs" label="My Logs" badge={projLogs.filter(l=>l.subId===loggedSub.id).length} color={T.green}/>
      </div>

      {/* SCHEDULE — all subs visible to each other */}
      {activeTab==="schedule"&&(
        <div>
          <div style={{background:T.tealBg,borderLeft:`3px solid ${T.teal}`,borderRadius:9,padding:"12px 16px",marginBottom:16,fontSize:12,color:T.muted,lineHeight:1.6}}>
            <strong style={{color:T.teal}}>Shared Job Schedule</strong> — you can see what every other trade is doing and planning. Coordinate your rough-in routes before opening walls to avoid conflicts.
          </div>
          {projSubs.map(sub=>{
            const subLogs = projLogs.filter(l=>l.subId===sub.id).slice(0,3);
            const isMe = sub.id === loggedSub.id;
            return (
              <div key={sub.id} style={{background:isMe?sub.color+"08":T.bg1,border:`1px solid ${isMe?sub.color:T.subtle}`,borderLeft:`3px solid ${sub.color}`,borderRadius:10,padding:"14px 16px",marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <div style={{display:"flex",gap:10,alignItems:"center"}}>
                    <div style={{width:32,height:32,borderRadius:8,background:sub.color+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:sub.color}}>{sub.trade.slice(0,3).toUpperCase()}</div>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:T.text}}>{sub.trade}{isMe?" (You)":""} — {sub.name}</div>
                      <div style={{fontSize:10,color:T.muted}}>{sub.company}</div>
                    </div>
                  </div>
                  {projIssues.filter(i=>i.subId===sub.id&&i.status==="open").length>0&&(
                    <span style={{fontSize:10,fontWeight:700,background:T.redBg,color:T.red,borderRadius:20,padding:"2px 9px"}}>Has open issue</span>
                  )}
                </div>
                {subLogs.length===0?(
                  <div style={{fontSize:11,color:T.muted,fontStyle:"italic"}}>No progress logged yet</div>
                ):subLogs.map(log=>(
                  <div key={log.id} style={{background:T.bg2,borderRadius:7,padding:"8px 12px",marginBottom:6}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                      <span style={{fontSize:10,fontFamily:"monospace",color:T.muted}}>{log.date}</span>
                      {log.progress_pct&&<span style={{fontSize:10,fontWeight:700,color:sub.color}}>{log.progress_pct}%</span>}
                    </div>
                    <div style={{fontSize:12,color:T.text,marginBottom:log.tomorrow_plan?4:0}}>{log.work_done}</div>
                    {log.tomorrow_plan&&<div style={{fontSize:11,color:T.teal}}><strong>Tomorrow:</strong> {log.tomorrow_plan}</div>}
                    {log.notes&&<div style={{fontSize:11,color:T.amber,marginTop:2}}>{log.notes}</div>}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* LOG ENTRY */}
      {activeTab==="log_entry"&&(
        <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <Card>
            <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:16}}>Today's Work Log</div>
            <div style={{fontSize:11,color:T.muted,marginBottom:14,lineHeight:1.5}}>Log what you did today so other trades and the GC can track progress. Be specific about locations and what's ready for the next trade.</div>
            {[
              {k:"work_done",l:"What did you do today?",ph:"Rough-in plumbing for units 1-3 complete. Set all drain locations per plan. Units 4-6 in progress.",rows:3},
              {k:"tomorrow_plan",l:"What's your plan for tomorrow?",ph:"Complete rough-in for units 4-6. Will need access to east corridor walls — heads up to framing.",rows:2},
              {k:"notes",l:"Any notes / concerns / coordination needed?",ph:"Drain location in unit 3 conflicts with electrical conduit path — need to coordinate with electrician.",rows:2},
            ].map(({k,l,ph,rows})=>(
              <div key={k} style={{marginBottom:12}}>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>{l}</div>
                <textarea value={newLog[k]} onChange={e=>setNewLog(p=>({...p,[k]:e.target.value}))} placeholder={ph} rows={rows}
                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 11px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit"}}/>
              </div>
            ))}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
              {[{k:"hours",l:"Hours Today",ph:"8"},{k:"crew_size",l:"Crew Size",ph:"3"},{k:"progress_pct",l:"% Complete",ph:"45"}].map(({k,l,ph})=>(
                <div key={k}>
                  <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>{l}</div>
                  <input type="text" value={newLog[k]||""} onChange={e=>setNewLog(p=>({...p,[k]:e.target.value}))} placeholder={ph}
                    style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 11px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
                </div>
              ))}
            </div>
            <Btn primary onClick={submitLog} disabled={!newLog.work_done.trim()} style={{width:"100%",justifyContent:"center"}}>
              Submit Daily Log
            </Btn>
          </Card>
          <Card>
            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:10}}>My Recent Logs</div>
            {projLogs.filter(l=>l.subId===loggedSub.id).slice(0,5).map(log=>(
              <div key={log.id} style={{background:T.bg2,borderRadius:7,padding:"8px 11px",marginBottom:7}}>
                <div style={{fontSize:10,fontFamily:"monospace",color:T.muted,marginBottom:4}}>{log.date}</div>
                <div style={{fontSize:11,color:T.text,marginBottom:log.tomorrow_plan?4:0}}>{log.work_done}</div>
                {log.progress_pct&&<div style={{fontSize:10,color:loggedSub.color,fontWeight:700}}>{log.progress_pct}% complete</div>}
              </div>
            ))}
            {projLogs.filter(l=>l.subId===loggedSub.id).length===0&&<div style={{fontSize:11,color:T.muted,fontStyle:"italic"}}>No logs yet — submit your first daily log.</div>}
          </Card>
        </div>
      )}

      {/* FLAG ISSUE */}
      {activeTab==="flag_issue"&&(
        <div style={{maxWidth:600}}>
          <Card>
            <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:6}}>Flag an Issue</div>
            <div style={{fontSize:11,color:T.muted,marginBottom:16,lineHeight:1.5}}>Flag anything that blocks your work, conflicts with another trade, or could affect the schedule. The GC sees this immediately.</div>
            <Field label="Issue Title" value={newIssue.title} onChange={v=>setNewIssue(p=>({...p,title:v}))} placeholder="Drain location conflicts with electrical conduit run"/>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Description</div>
              <textarea value={newIssue.description} onChange={e=>setNewIssue(p=>({...p,description:e.target.value}))}
                placeholder="Describe the issue in detail — location, what you found, what needs to be resolved before you can proceed..."
                rows={4} style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 11px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit"}}/>
            </div>
            <Select label="Priority" value={newIssue.priority} onChange={v=>setNewIssue(p=>({...p,priority:v}))} options={[{value:"low",label:"Low — can work around it"},{value:"medium",label:"Medium — needs to be resolved soon"},{value:"high",label:"High — blocking my work now"}]}/>
            <div onClick={()=>setNewIssue(p=>({...p,trade_conflict:!p.trade_conflict}))}
              style={{display:"flex",gap:10,alignItems:"center",padding:"10px 12px",borderRadius:8,cursor:"pointer",background:newIssue.trade_conflict?T.redBg:T.bg2,border:`1px solid ${newIssue.trade_conflict?T.red+"40":T.subtle}`,marginTop:12,marginBottom:16,transition:"all 0.15s"}}>
              <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${newIssue.trade_conflict?T.red:T.subtle}`,background:newIssue.trade_conflict?T.red:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {newIssue.trade_conflict&&<Ic d={ICONS.check} size={10} style={{color:T.bg0}}/>}
              </div>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:newIssue.trade_conflict?T.red:T.text}}>This is a trade conflict</div>
                <div style={{fontSize:10,color:T.muted}}>Affects coordination with another trade on this project</div>
              </div>
            </div>
            <Btn primary onClick={submitIssue} disabled={!newIssue.title.trim()} style={{width:"100%",justifyContent:"center"}} danger={newIssue.priority==="high"}>
              Flag Issue
            </Btn>
          </Card>
        </div>
      )}

      {/* SUBMIT RFI */}
      {activeTab==="submit_rfi"&&(
        <div style={{maxWidth:600}}>
          <Card>
            <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:6}}>Submit RFI</div>
            <div style={{fontSize:11,color:T.muted,marginBottom:16,lineHeight:1.5}}>Submit a formal Request for Information when plans or specs are unclear and you need an answer before proceeding. Never proceed on verbal answers.</div>
            <Field label="RFI Subject" value={newRfi.subject} onChange={v=>setNewRfi(p=>({...p,subject:v}))} placeholder="Drain elevation — plan shows floor drain at elevation 100.0 but field condition is 99.2"/>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Description & Question</div>
              <textarea value={newRfi.description} onChange={e=>setNewRfi(p=>({...p,description:e.target.value}))}
                placeholder="Describe the discrepancy and your specific question. What do you need answered to proceed?"
                rows={4} style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 11px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit"}}/>
            </div>
            <Select label="Priority" value={newRfi.priority} onChange={v=>setNewRfi(p=>({...p,priority:v}))} options={[{value:"routine",label:"Routine — need response in 5 business days"},{value:"urgent",label:"Urgent — need response in 48 hours"},{value:"critical",label:"Critical — blocking work now, need same day response"}]}/>
            <div onClick={()=>setNewRfi(p=>({...p,affects_schedule:!p.affects_schedule}))}
              style={{display:"flex",gap:10,alignItems:"center",padding:"10px 12px",borderRadius:8,cursor:"pointer",background:newRfi.affects_schedule?T.redBg:T.bg2,border:`1px solid ${newRfi.affects_schedule?T.red+"40":T.subtle}`,marginTop:12,marginBottom:16,transition:"all 0.15s"}}>
              <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${newRfi.affects_schedule?T.red:T.subtle}`,background:newRfi.affects_schedule?T.red:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {newRfi.affects_schedule&&<Ic d={ICONS.check} size={10} style={{color:T.bg0}}/>}
              </div>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:newRfi.affects_schedule?T.red:T.text}}>This RFI affects the schedule</div>
                <div style={{fontSize:10,color:T.muted}}>Cannot proceed on this scope until answered</div>
              </div>
            </div>
            <Btn primary onClick={submitRfi} disabled={!newRfi.subject.trim()} style={{width:"100%",justifyContent:"center"}}>
              Submit RFI
            </Btn>
          </Card>
        </div>
      )}

      {/* MY LOGS */}
      {activeTab==="my_logs"&&(
        <div>
          {projLogs.filter(l=>l.subId===loggedSub.id).length===0&&<div style={{textAlign:"center",padding:"40px",color:T.muted,fontSize:13}}>No logs submitted yet.</div>}
          {projLogs.filter(l=>l.subId===loggedSub.id).map(log=>(
            <div key={log.id} style={{background:T.bg1,border:`1px solid ${loggedSub.color}20`,borderLeft:`3px solid ${loggedSub.color}`,borderRadius:10,padding:"14px 16px",marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                <span style={{fontSize:11,fontFamily:"monospace",color:T.muted}}>{log.date} · {log.time}</span>
                {log.progress_pct&&<span style={{fontSize:11,fontWeight:700,color:loggedSub.color}}>{log.progress_pct}% complete</span>}
              </div>
              <div style={{fontSize:13,color:T.text,marginBottom:log.tomorrow_plan?6:0}}>{log.work_done}</div>
              {log.hours&&<div style={{fontSize:11,color:T.muted}}>Hours: {log.hours} · Crew: {log.crew_size||"N/A"}</div>}
              {log.tomorrow_plan&&<div style={{fontSize:12,color:T.teal,marginTop:4}}><strong>Tomorrow:</strong> {log.tomorrow_plan}</div>}
              {log.notes&&<div style={{fontSize:11,color:T.amber,marginTop:4}}>{log.notes}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const BID_TEMPLATES = [
  {
    id:"sro", name:"SRO / Affordable / Low-Income", icon:"SRO", color:"#2dd4bf",
    tags:["SRO","Affordable Housing","Low-Income","MOHCD","HUD"],
    perUnit: true,
    sections:[
      { title:"Project Info", fields:[
        {k:"project_name",l:"Project Name",ph:"466 Turk St - SRO Renovation"},
        {k:"address",l:"Property Address",ph:"466 Turk St, Tenderloin, SF 94102"},
        {k:"client",l:"Owner / Client / GC",ph:"Property Owner or GC Name"},
        {k:"contact",l:"Client Contact & Phone",ph:"John Smith - (415) 555-0100"},
        {k:"num_units",l:"Number of Units",ph:"12"},
        {k:"building_age",l:"Building Year Built",ph:"1968"},
        {k:"funding",l:"Funding Source",ph:"MOHCD / HUD / Private"},
        {k:"prev_wage",l:"Prevailing Wage",ph:"Yes - MOHCD funded / No - Private"},
      ]},
      { title:"Scope of Work", fields:[
        {k:"demo",l:"Demo & Protection",ph:"Remove cabinets, countertops, flooring, fixtures. Protect adjacent units.",rows:2},
        {k:"plumbing",l:"Plumbing",ph:"New fixtures at existing stub-outs. Toilet, vanity, kitchen sink.",rows:2},
        {k:"electrical",l:"Electrical",ph:"Outlets, switches, GFCI at wet areas, owner-furnished lighting.",rows:2},
        {k:"drywall",l:"Drywall & Paint",ph:"Patch and repair. 2 coats paint - owner selects color.",rows:2},
        {k:"tile",l:"Tile",ph:"Bathroom floor and shower walls. Kitchen backsplash.",rows:2},
        {k:"flooring",l:"Flooring",ph:"LVP throughout living areas. Tile in wet areas.",rows:2},
        {k:"cabinets",l:"Cabinets & Countertops",ph:"New shaker cabinets. Laminate countertops.",rows:2},
        {k:"doors",l:"Doors & Hardware",ph:"Replace interior doors per schedule. Owner-selected hardware.",rows:2},
        {k:"other",l:"Other Scope",ph:"Window replacement, HVAC, specialty items.",rows:2},
      ]},
      { title:"Compliance Checklist", checks:[
        "20-Day Preliminary Notice served Day 1",
        "MOHCD finish standards compliance",
        "SF DBI permits pulled before mobilization",
        "Lead paint protocol - pre-1978 building",
        "Asbestos survey required before demo",
        "Prevailing wage - DIR certified payroll",
        "Occupied unit protection plan in place",
        "Noise ordinance - SF 7am-8pm M-F, 9am-6pm Sat",
        "ADA path of travel reviewed",
        "Title 24 energy compliance verified",
      ]},
      { title:"Standard Exclusions", exclusions:[
        "Structural modifications or load-bearing wall changes",
        "Asbestos or lead paint abatement - separate licensed contractor",
        "Drain or vent relocation unless specifically listed in scope",
        "Electrical panel upgrade or new circuits beyond scope",
        "Work in units not listed in this contract",
        "Hidden conditions discovered during demo - Change Order required",
        "Common area work not specifically included",
        "Owner-supplied material defects or delays",
      ]},
      { title:"Schedule & Terms", fields:[
        {k:"start_date",l:"Estimated Start Date",ph:"May 1, 2026"},
        {k:"duration_unit",l:"Duration per Unit",ph:"3-4 weeks per unit, phased"},
        {k:"total_duration",l:"Total Project Duration",ph:"12 units x 3 weeks staggered = 10 weeks"},
        {k:"crew_size",l:"Crew Size",ph:"4 workers + foreman"},
        {k:"payment_terms",l:"Payment Terms",ph:"20% deposit, monthly progress billing, 10% retainage at completion"},
      ]},
      { title:"Possible Unforeseen Work", unforeseen:[
        "Water damage behind walls — cabinets, tubs absorbing leaks for years",
        "Mold — common in SRO bathrooms, under sinks, behind tile",
        "Drain conflict — existing drain location doesn't match new layout",
        "Out-of-level floors — historic settling, requires self-leveler",
        "Outdated electrical — knob-and-tube or aluminum wiring in pre-1978 buildings",
        "Cast iron drain stack deterioration — may crack during rough-in",
        "Non-standard stud spacing in older buildings",
        "Missing blocking for grab bars and hardware",
        "Asbestos floor tile or pipe insulation in pre-1978 buildings",
        "Subfloor rot at tubs and toilets"
      ]}
    ]
  },
  {
    id:"senior", name:"Senior Living / ADA Housing", icon:"ADA", color:"#a78bfa",
    tags:["Senior Housing","ADA","Assisted Living","Accessible","Type A"],
    perUnit: true,
    sections:[
      { title:"Project Info", fields:[
        {k:"project_name",l:"Project Name",ph:"4th Floor Senior Housing Finish"},
        {k:"address",l:"Property Address",ph:"Chinatown, SF"},
        {k:"client",l:"Owner / Client",ph:"Senior Housing CDC"},
        {k:"contact",l:"Contact & Phone",ph:"James Lee - (415) 555-0200"},
        {k:"num_units",l:"Number of Units",ph:"8"},
        {k:"unit_type",l:"Unit Type",ph:"Type A fully accessible / Type B adaptable"},
        {k:"building_age",l:"Building Year Built",ph:"1975"},
        {k:"funding",l:"Funding Source",ph:"HUD / CDLAC / Private"},
        {k:"prev_wage",l:"Prevailing Wage",ph:"Yes - HUD funded"},
      ]},
      { title:"ADA & Accessibility Scope", fields:[
        {k:"grab_bars",l:"Grab Bars",ph:"42in at toilet, 36in in shower per CBC 11A. Confirm blocking in walls.",rows:2},
        {k:"shower",l:"Accessible Shower / Roll-In",ph:"Remove tub, install 60in roll-in shower, linear drain, anti-scald valve.",rows:2},
        {k:"hardware",l:"Lever Hardware",ph:"Replace all door hardware with ADA lever handles throughout.",rows:2},
        {k:"flooring_ada",l:"Flooring - ADA",ph:"Non-slip LVP or tile. No transitions over 1/2in. Max 1/2in threshold.",rows:2},
        {k:"path_travel",l:"Path of Travel",ph:"Accessible route from parking to unit. List remediation required.",rows:2},
        {k:"restroom",l:"Restroom ADA",ph:"Clearances, grab bars, mirror height, sink clearance, lever faucet.",rows:2},
        {k:"kitchen_ada",l:"Kitchen Accessibility",ph:"Max 34in base cabinet height, knee clearance at sink if required.",rows:2},
      ]},
      { title:"General Scope", fields:[
        {k:"demo",l:"Demo & Protection",ph:"Remove existing finishes. Protect occupied floors.",rows:2},
        {k:"electrical",l:"Electrical & Title 24",ph:"Title 24 lighting, occupancy sensors, GFCI, emergency lighting.",rows:2},
        {k:"drywall",l:"Drywall & Paint",ph:"Patch and repair. Low-VOC paint. Eggshell finish.",rows:2},
        {k:"tile",l:"Tile",ph:"Non-slip bathroom floor and shower walls. Grout sealed.",rows:2},
        {k:"flooring",l:"Flooring",ph:"Non-slip LVP. No raised transitions.",rows:2},
        {k:"cabinets",l:"Cabinets & Countertops",ph:"34in max base cabinets. Accessible hardware. Rounded edges.",rows:2},
        {k:"other",l:"Other Scope",ph:"Emergency pull cords, nurse call rough-in, specialty items.",rows:2},
      ]},
      { title:"Compliance Checklist", checks:[
        "20-Day Preliminary Notice served Day 1",
        "CBC Chapter 11A - residential accessibility compliance",
        "ADA path of travel documented and compliant",
        "Title 24 energy - lighting, insulation, windows",
        "SF DBI permits and inspections",
        "Lead and asbestos survey if pre-1978",
        "Prevailing wage - DIR certified payroll if HUD funded",
        "Grab bar blocking verified before drywall closed",
        "Roll-in shower waterproofing inspected before tile",
        "Non-slip flooring coefficient of friction verified",
      ]},
      { title:"Standard Exclusions", exclusions:[
        "Structural modifications - engineer required separately",
        "Asbestos or lead abatement - licensed contractor separately",
        "Nurse call or emergency system wiring beyond rough-in",
        "Elevator or lift installation",
        "Exterior path of travel beyond unit entry",
        "Furniture, appliances unless listed",
        "Hidden conditions during demo - Change Order required",
      ]},
      { title:"Schedule & Terms", fields:[
        {k:"start_date",l:"Estimated Start Date",ph:"May 1, 2026"},
        {k:"phase",l:"Phasing Plan",ph:"Phase 1: Units 1-4 weeks 1-5, Phase 2: Units 5-8 weeks 4-8"},
        {k:"total_duration",l:"Total Duration",ph:"8-10 weeks phased"},
        {k:"payment_terms",l:"Payment Terms",ph:"20% deposit, monthly progress billing, 10% retainage"},
      ]},
      { title:"Possible Unforeseen Work", unforeseen:[
        "Blocking missing from walls — grab bars need blocking, requires opening drywall",
        "Subfloor rot at existing tub locations",
        "Non-compliant path of travel — may trigger ADA upgrades beyond unit",
        "Out-of-plumb walls affecting accessible clearance dimensions",
        "Electrical capacity — accessible fixtures may exceed existing circuit load",
        "Fire sprinkler heads need relocation when ceilings are modified",
        "Elevator door clearance if work affects corridor width or flooring height"
      ]}
    ]
  },
  {
    id:"multifamily", name:"Multi-Family Apartments", icon:"MF", color:"#60a5fa",
    tags:["Multi-Family","Apartments","Market Rate","Mid-Rise"],
    perUnit: true,
    sections:[
      { title:"Project Info", fields:[
        {k:"project_name",l:"Project Name",ph:"1200 Mission - Apartment Renovation"},
        {k:"address",l:"Property Address",ph:"1200 Mission St, SoMa, SF"},
        {k:"client",l:"Property Owner / Manager",ph:"Owner or Management Company"},
        {k:"contact",l:"Contact & Phone",ph:"Contact - Phone"},
        {k:"num_units",l:"Number of Units",ph:"6"},
        {k:"unit_size",l:"Avg Unit Size",ph:"650 sq ft"},
        {k:"building_age",l:"Building Year Built",ph:"1985"},
        {k:"occupied",l:"Occupied During Work?",ph:"Yes - tenant relocation required / No - vacant"},
      ]},
      { title:"Scope of Work", fields:[
        {k:"demo",l:"Demo & Protection",ph:"Remove cabinets, countertops, flooring. Full dust protection.",rows:2},
        {k:"kitchen",l:"Kitchen",ph:"New cabinets, countertops, backsplash, sink, faucet, disposal.",rows:2},
        {k:"bathrooms",l:"Bathrooms",ph:"New tile, vanity, toilet, fixtures, mirror, fan.",rows:2},
        {k:"flooring",l:"Flooring",ph:"LVP throughout. Remove existing carpet and vinyl.",rows:2},
        {k:"paint",l:"Paint",ph:"Full interior repaint. 2 coats walls, 1 coat ceiling.",rows:2},
        {k:"electrical",l:"Electrical",ph:"New outlets, switches, GFCI, fixtures per plan.",rows:2},
        {k:"doors",l:"Doors & Hardware",ph:"Interior door replacement, new hardware throughout.",rows:2},
        {k:"other",l:"Other Scope",ph:"Windows, HVAC, in-unit laundry, specialty items.",rows:2},
      ]},
      { title:"Compliance Checklist", checks:[
        "20-Day Preliminary Notice served Day 1",
        "Building permits pulled before mobilization",
        "Occupied unit protection plan - tenant notification required",
        "Lead and asbestos protocol if pre-1978",
        "Title 24 energy compliance for lighting or window work",
        "Smoke and CO detector compliance per current code",
        "GFCI compliance at all wet areas",
        "Soft-story retrofit - confirm not triggered by scope",
      ]},
      { title:"Standard Exclusions", exclusions:[
        "Structural modifications",
        "Asbestos or lead abatement",
        "Drain or stack relocation",
        "Panel upgrade or new circuits beyond scope",
        "Adjacent unit or common area work",
        "Hidden damage during demo - Change Order required",
        "Tenant relocation costs",
        "Appliance purchase - install only unless specified",
      ]},
      { title:"Schedule & Terms", fields:[
        {k:"start_date",l:"Estimated Start Date",ph:"May 1, 2026"},
        {k:"duration_unit",l:"Duration per Unit",ph:"2-3 weeks per unit"},
        {k:"total_duration",l:"Total Duration",ph:"6 units x 2 weeks staggered = 8 weeks"},
        {k:"payment_terms",l:"Payment Terms",ph:"20% deposit, monthly progress billing, 10% retainage"},
        {k:"tenant_access",l:"Tenant Access Plan",ph:"Units vacant / 24hr notice required for occupied"},
      ]},
      { title:"Possible Unforeseen Work", unforeseen:[
        "Subfloor damage under carpet — moisture invisible until demo",
        "Mold behind bathroom tile — common in buildings pre-2000",
        "Galvanized supply lines corroded — may need replacement during fixture work",
        "Fire-rated assembly violations exposed during demo",
        "Soft-story trigger — structural work may require retrofit",
        "Common area system impact — water shutoff affects occupied units",
        "Pre-existing code violations exposed during demo"
      ]}
    ]
  },
  {
    id:"residential", name:"Residential - Kitchen / Bath / Full Home", icon:"RES", color:"#d4a843",
    tags:["Residential","Kitchen","Bathroom","Full Renovation","Single Family"],
    perUnit: false,
    sections:[
      { title:"Project Info", fields:[
        {k:"project_name",l:"Project Name",ph:"Castro Home Renovation"},
        {k:"address",l:"Property Address",ph:"1842 Market St, Castro, SF"},
        {k:"client",l:"Homeowner",ph:"Owner Name"},
        {k:"contact",l:"Phone & Email",ph:"(415) 555-0100 - owner@email.com"},
        {k:"home_size",l:"Home Size (sq ft)",ph:"1,400 sq ft"},
        {k:"building_age",l:"Year Built",ph:"1958"},
        {k:"occupied",l:"Occupied During Work?",ph:"Yes - owner will remain / No - vacant"},
        {k:"scope_type",l:"Scope Type",ph:"Kitchen only / Bathrooms only / Full home"},
      ]},
      { title:"Kitchen Scope", fields:[
        {k:"kitchen_demo",l:"Demo",ph:"Remove existing cabinets, countertops, flooring, appliances.",rows:2},
        {k:"cabinets",l:"Cabinets",ph:"New shaker upper and lower. Owner selects style.",rows:2},
        {k:"countertops",l:"Countertops",ph:"Quartz. Owner selects color.",rows:2},
        {k:"backsplash",l:"Backsplash",ph:"4x12 subway tile. Owner selects color.",rows:2},
        {k:"appliances",l:"Appliances",ph:"Install owner-furnished. Dishwasher, range, hood, refrigerator.",rows:2},
        {k:"kitchen_plumbing",l:"Plumbing",ph:"New sink, faucet, disposal, dishwasher connection.",rows:2},
        {k:"kitchen_electrical",l:"Electrical",ph:"GFCI outlets, under-cabinet lighting, hood vent circuit.",rows:2},
        {k:"kitchen_flooring",l:"Flooring",ph:"LVP to match rest of home or tile.",rows:2},
      ]},
      { title:"Bathroom Scope", fields:[
        {k:"bath_demo",l:"Demo",ph:"Full demo to studs. Remove tub, vanity, toilet, tile.",rows:2},
        {k:"waterproofing",l:"Waterproofing",ph:"Schluter membrane or equivalent. Inspector sign-off before tile.",rows:2},
        {k:"tile",l:"Tile",ph:"Floor and shower walls. Owner selects. Include niche and bench if desired.",rows:2},
        {k:"vanity",l:"Vanity & Fixtures",ph:"New vanity, toilet, mirror, accessories. Owner selects.",rows:2},
        {k:"bath_electrical",l:"Electrical",ph:"GFCI, exhaust fan vented to exterior, lighting.",rows:2},
      ]},
      { title:"Whole Home Scope", fields:[
        {k:"flooring_home",l:"Flooring",ph:"LVP throughout. Remove existing carpet or hardwood.",rows:2},
        {k:"paint_home",l:"Paint",ph:"Full interior repaint. Owner selects colors.",rows:2},
        {k:"doors_home",l:"Doors & Hardware",ph:"Interior door replacement if needed. New hardware throughout.",rows:2},
        {k:"windows",l:"Windows",ph:"Replace all windows - specify brand and style.",rows:2},
        {k:"other_home",l:"Other Scope",ph:"HVAC, electrical panel, plumbing, specialty items.",rows:2},
      ]},
      { title:"Compliance Checklist", checks:[
        "Building permits if kitchen or bath over value threshold",
        "Plumbing permit if moving or adding fixtures",
        "Electrical permit if adding circuits",
        "Lead paint protocol - pre-1978 home",
        "Asbestos - floor tile, popcorn ceiling, pipe insulation if pre-1978",
        "GFCI compliance at kitchen, bath, garage, exterior",
        "Smoke and CO detector compliance",
        "Exhaust fan vented to exterior - not to attic",
        "Title 24 if replacing windows or lighting",
      ]},
      { title:"Standard Exclusions", exclusions:[
        "Structural modifications - engineer required separately",
        "Asbestos or lead abatement - licensed contractor separately",
        "Drain relocation unless listed in scope",
        "Electrical panel upgrade unless listed",
        "Work in rooms not listed in scope",
        "Hidden damage during demo - Change Order required",
        "Appliance purchase - installation only",
        "Landscaping or exterior work not listed",
      ]},
      { title:"Schedule & Terms", fields:[
        {k:"start_date",l:"Estimated Start Date",ph:"May 1, 2026"},
        {k:"duration",l:"Project Duration",ph:"Kitchen 3-4 weeks / Bathroom 2-3 weeks / Full home 8-12 weeks"},
        {k:"payment_terms",l:"Payment Terms",ph:"20% deposit, progress billing at milestones, 10% at completion"},
        {k:"work_hours",l:"Work Hours",ph:"7am-6pm M-F. Weekends with homeowner approval."},
      ]},
      { title:"Possible Unforeseen Work", unforeseen:[
        "Hidden water damage under sinks, behind tubs, at window sills",
        "Drain conflict — moved sink or island hits existing drain",
        "Load-bearing wall hidden as partition — structural engineer required",
        "Asbestos floor tile, popcorn ceiling, pipe wrap in pre-1978 homes",
        "Galvanized pipes corroded — exposed during any plumbing work",
        "Subfloor rot under existing tile or vinyl",
        "Outdated electrical — ungrounded outlets, undersized panel",
        "Bathroom exhaust vented to attic — must be corrected to exterior",
        "Knob-and-tube wiring — cannot be insulated over",
        "Title 24 upgrade triggered by window or lighting replacement"
      ]}
    ]
  },
  {
    id:"adu", name:"ADU / Room Addition", icon:"ADU", color:"#22c55e",
    tags:["ADU","Accessory Dwelling Unit","Room Addition","JADU","Garage Conversion"],
    perUnit: false,
    sections:[
      { title:"Project Info", fields:[
        {k:"project_name",l:"Project Name",ph:"Burlingame ADU - Garage Conversion"},
        {k:"address",l:"Property Address",ph:"Property Address"},
        {k:"client",l:"Homeowner",ph:"Owner Name"},
        {k:"contact",l:"Phone & Email",ph:"(650) 555-0100 - owner@email.com"},
        {k:"adu_type",l:"ADU Type",ph:"Garage conversion / New detached / Basement / JADU / Room addition"},
        {k:"adu_size",l:"Proposed Size (sq ft)",ph:"400 sq ft"},
        {k:"lot_size",l:"Lot Size",ph:"5,000 sq ft"},
        {k:"zoning",l:"Zoning & City",ph:"Burlingame R-1 / San Mateo R-2"},
        {k:"existing",l:"Existing Structure Condition",ph:"Garage - wood frame, concrete slab, 1978"},
        {k:"intended_use",l:"Intended Use",ph:"Rental / Family / Home office"},
      ]},
      { title:"Pre-Construction & Design", fields:[
        {k:"architect",l:"Architect / Designer",ph:"Owner-provided plans / Grok to recommend architect"},
        {k:"plan_check",l:"Plan Check Status",ph:"Not started / In review / Approved"},
        {k:"setbacks",l:"Setback Compliance",ph:"Front, rear, side setbacks confirmed per city code"},
        {k:"utilities",l:"Utility Connections",ph:"Separate meter required / shared - confirm with city"},
        {k:"fire_sprinklers",l:"Fire Sprinklers Required?",ph:"Check with city - often required over 500 sq ft"},
        {k:"solar",l:"Solar Required?",ph:"California requires solar on new ADU - confirm sq ft threshold"},
      ]},
      { title:"Structure & Shell", fields:[
        {k:"foundation",l:"Foundation",ph:"Existing slab - verify condition. New footings if addition.",rows:2},
        {k:"framing",l:"Framing",ph:"New walls and roof framing if addition. Verify existing framing for conversion.",rows:2},
        {k:"roofing",l:"Roofing",ph:"New roof or re-roof if required. Match existing or new spec.",rows:2},
        {k:"exterior",l:"Exterior Finish",ph:"Stucco, siding, or match existing home. Windows and exterior door.",rows:2},
        {k:"insulation",l:"Insulation",ph:"Title 24 required - walls, ceiling, floor. Spray foam or batt.",rows:2},
        {k:"windows_ext",l:"Windows & Exterior Door",ph:"Energy efficient per Title 24. Exterior entry door with deadbolt.",rows:2},
      ]},
      { title:"Interior Scope", fields:[
        {k:"plumbing_adu",l:"Plumbing",ph:"Full bath - toilet, shower, vanity. Kitchen or kitchenette. Water heater.",rows:2},
        {k:"electrical_adu",l:"Electrical",ph:"Sub-panel if required. All circuits, outlets, GFCI, lighting. EV charger rough-in.",rows:2},
        {k:"hvac_adu",l:"HVAC",ph:"Mini-split system. Title 24 Manual J load calc required.",rows:2},
        {k:"drywall_adu",l:"Drywall & Paint",ph:"5/8in Type X drywall at garage walls per fire code. Paint throughout.",rows:2},
        {k:"flooring_adu",l:"Flooring",ph:"LVP or tile throughout. Concrete prep if slab.",rows:2},
        {k:"kitchen_adu",l:"Kitchen / Kitchenette",ph:"Cabinets, countertops, sink. Full kitchen or kitchenette.",rows:2},
        {k:"bath_adu",l:"Bathroom",ph:"Tile shower or tub-shower, vanity, toilet, exhaust fan.",rows:2},
      ]},
      { title:"Compliance Checklist", checks:[
        "City ADU permit - building, electrical, plumbing, mechanical",
        "Title 24 energy compliance - solar, insulation, HVAC, windows",
        "Fire separation - 5/8in Type X drywall between garage and living",
        "Smoke and CO detectors per current code",
        "GFCI at kitchen, bath, exterior, garage",
        "Separate address assigned by city",
        "Owner-occupancy requirement - confirm with city",
        "Utility connection - separate meter or shared confirmed",
        "Fire sprinkler system if required",
        "Solar PV system if required by Title 24",
      ]},
      { title:"Standard Exclusions", exclusions:[
        "Architectural or engineering plans - owner-provided or billed separately",
        "Landscaping, fencing, or driveway work",
        "Solar PV system unless specifically listed in scope",
        "Fire sprinkler system unless specifically listed",
        "Appliances - install only unless specified",
        "Utility connection fees and meter installation - utility company cost",
        "HOA approval or fees",
        "Soil reports or geotechnical work",
        "Hidden conditions in existing structure - Change Order required",
      ]},
      { title:"Schedule & Terms", fields:[
        {k:"start_date",l:"Estimated Start Date",ph:"After permit approval - estimated 6-8 weeks plan check"},
        {k:"duration",l:"Construction Duration",ph:"Garage conversion 10-14 weeks / New detached 16-20 weeks / Addition 12-16 weeks"},
        {k:"payment_terms",l:"Payment Terms",ph:"20% deposit, 25% framing, 25% rough inspections, 20% drywall, 10% completion"},
        {k:"permit_by",l:"Who Pulls Permits",ph:"Grok pulls all permits. Cost included in contract."},
      ]},
      { title:"Possible Unforeseen Work", unforeseen:[
        "Existing foundation unsuitable — cracking, moisture, insufficient depth",
        "Existing garage framing undersized for living space loads",
        "Asbestos in existing structure if pre-1978",
        "Electrical service upgrade required for new living space",
        "Soil conditions — expansive soil may need special foundation",
        "Setback encroachment on existing structure",
        "Solar requirement triggered if ADU over sq ft threshold",
        "Fire sprinkler trigger — addition may require whole-house system",
        "Tree roots affecting drainage or foundation",
        "HOA restrictions limiting ADU design"
      ]}
    ]
  },
  {
    id:"commercial", name:"Commercial TI - Retail / Office / Restaurant", icon:"TI", color:"#ef4444",
    tags:["Commercial","Tenant Improvement","Retail","Office","Restaurant","Storefront","Burlingame"],
    perUnit: false,
    sections:[
      { title:"Project Info", fields:[
        {k:"project_name",l:"Project Name",ph:"Broadway Storefront TI - Burlingame"},
        {k:"address",l:"Property Address",ph:"1234 Broadway, Burlingame, CA 94010"},
        {k:"tenant",l:"Tenant / Business Name",ph:"Business Name"},
        {k:"landlord",l:"Landlord / Property Owner",ph:"Landlord Name"},
        {k:"contact",l:"Primary Contact & Phone",ph:"Contact - (650) 555-0100"},
        {k:"space_size",l:"Space Size (sq ft)",ph:"1,200 sq ft"},
        {k:"use_type",l:"Use Type",ph:"Retail / Office / Restaurant / Cafe / Salon / Medical"},
        {k:"occupancy",l:"Occupancy Classification",ph:"B Office / A2 Restaurant / M Retail - confirm with city"},
        {k:"ti_deadline",l:"TI Deadline / Lease Start",ph:"Client needs open by June 15, 2026"},
        {k:"ti_allowance",l:"Landlord TI Allowance",ph:"$50 per sq ft - confirm with lease"},
      ]},
      { title:"Demolition & Shell", fields:[
        {k:"demo",l:"Demolition",ph:"Remove partitions, flooring, ceiling tiles, lighting, storefront glass.",rows:2},
        {k:"storefront",l:"Storefront & Facade",ph:"New aluminum storefront, glass, signage framing, exterior paint.",rows:2},
        {k:"framing",l:"Framing",ph:"New interior partitions per plan. Metal stud and drywall.",rows:2},
        {k:"ceiling",l:"Ceiling",ph:"New T-bar acoustic ceiling / open ceiling / gypboard per design.",rows:2},
        {k:"flooring_comm",l:"Flooring",ph:"Polished concrete, LVP, ceramic tile, or carpet per design.",rows:2},
      ]},
      { title:"MEP - Mechanical Electrical Plumbing", fields:[
        {k:"electrical_comm",l:"Electrical",ph:"New panel or sub-panel if required. All circuits, outlets, data, lighting per plan.",rows:2},
        {k:"plumbing_comm",l:"Plumbing",ph:"ADA restroom, mop sink, kitchen connections if food service.",rows:2},
        {k:"hvac_comm",l:"HVAC",ph:"New rooftop unit or split system. Ductwork per plan. Title 24 compliance.",rows:2},
        {k:"fire_alarm",l:"Fire Alarm & Sprinklers",ph:"Modify or extend existing system per fire marshal.",rows:2},
        {k:"data_av",l:"Data & AV",ph:"Data drops, AV rough-in, security camera rough-in.",rows:2},
      ]},
      { title:"Finishes & Specialties", fields:[
        {k:"paint_comm",l:"Paint",ph:"Walls and ceiling - owner selects colors. 2 coats interior.",rows:2},
        {k:"restroom",l:"ADA Restroom",ph:"Full ADA - tile, grab bars, fixtures, accessories.",rows:2},
        {k:"kitchen_comm",l:"Kitchen / Back of House",ph:"If food service - hood, suppression, grease trap, commercial sinks, NSF flooring.",rows:2},
        {k:"casework",l:"Casework & Millwork",ph:"Reception desk, shelving, display cases, back bar per design.",rows:2},
        {k:"accessibility",l:"ADA Path of Travel",ph:"From parking to entrance to restroom. List upgrades triggered.",rows:2},
      ]},
      { title:"Compliance Checklist", checks:[
        "City building permit - TI",
        "Electrical permit",
        "Plumbing permit",
        "Mechanical permit - HVAC",
        "Fire department review - egress, occupancy, sprinklers",
        "ADA path of travel - parking to entrance to restroom",
        "Title 24 energy - lighting, HVAC, envelope",
        "Health department approval if food service",
        "Grease trap permit if restaurant",
        "Hood and suppression system UL300 compliant if food service",
        "Signage permit - check city sign ordinance",
        "Landlord approval of plans before permit submittal",
        "Certificate of Occupancy required before opening",
      ]},
      { title:"Standard Exclusions", exclusions:[
        "Architectural plans and engineering - owner or landlord provided",
        "Furniture, fixtures, and equipment purchase",
        "Grease trap installation unless listed",
        "Fire suppression system unless listed",
        "Exterior signage fabrication - blocking and rough-in only",
        "Landlord base building system repairs",
        "Hidden conditions in existing space - Change Order required",
        "Permit fees - billed at cost",
        "Utility connection or upgrade fees - utility company cost",
      ]},
      { title:"Schedule & Terms", fields:[
        {k:"start_date",l:"Estimated Start Date",ph:"Submit permit same day as contract execution"},
        {k:"duration",l:"Construction Duration",ph:"Basic retail 4-6 weeks / Office 5-8 weeks / Restaurant 10-14 weeks"},
        {k:"business_open",l:"Business Open During Work?",ph:"No - not yet occupying / Yes - night and weekend work required"},
        {k:"payment_terms",l:"Payment Terms",ph:"20% deposit, 30% framing rough-in, 30% MEP rough inspections, 20% completion"},
        {k:"landlord_coord",l:"Landlord Coordination",ph:"Landlord approval required for structural or facade changes. Grok coordinates."},
      ]},
      { title:"Possible Unforeseen Work", unforeseen:[
        "ADA path of travel upgrades triggered by any TI",
        "Existing MEP code violations exposed during demo",
        "Asbestos in floor tile, ceiling tile, or drywall compound pre-1980",
        "Structural element in planned opening — partition may be structural",
        "Grease trap undersized — health dept may require larger unit",
        "Fire sprinkler upgrade triggered by occupancy change",
        "HVAC undersized for new occupancy load",
        "Landlord approval delays on plan changes",
        "Electrical service undersized for tenant equipment",
        "Seismic upgrade triggered by structural modifications"
      ]}
    ]
  }
];

// ─── PRICING CALCULATOR ───────────────────────────────────────────────────────
// SF Bay Area 2025/2026 market rates — higher than national avg due to labor costs,
// compliance overhead, and occupied-building complexity
// ─── MEETING RECORDER + AI SUMMARY ───────────────────────────────────────────
function MeetingRecorderView({projects}) {
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [meetingType, setMeetingType] = useState("Site Walkthrough");
  const [attendees, setAttendees] = useState("");
  const [selectedProject, setSelectedProject] = useState(projects[0]);
  const [notes, setNotes] = useState("");
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [savedMeetings, setSavedMeetings] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [transcriptText, setTranscriptText] = useState("");
  const [manualMode, setManualMode] = useState(false);
  const [uploadedAudio, setUploadedAudio] = useState(null);
  const [activeTab, setActiveTab] = useState("record");

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioInputRef = useRef(null);
  const recognitionRef = useRef(null);
  const [liveTranscript, setLiveTranscript] = useState("");
  const [micError, setMicError] = useState("");

  const MEETING_TYPES = [
    "Site Walkthrough","Owner Meeting","Sub Coordination","Pre-Construction",
    "Progress Meeting","Change Order Discussion","Punch List Walk","Safety Meeting",
    "Design Review","Permit Pre-Application","Closeout Meeting",
  ];

  // Start recording
  const startRecording = async () => {
    setMicError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio:true});
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream, {mimeType: MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4"});
      recorderRef.current = recorder;
      recorder.ondataavailable = e => { if(e.data.size>0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {type:recorder.mimeType});
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t=>t.stop());
      };
      recorder.start(1000);
      setRecording(true);
      setDuration(0);
      timerRef.current = setInterval(()=>setDuration(d=>d+1), 1000);

      // Live speech-to-text if available
      if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SR();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = "en-US";
        recognition.onresult = e => {
          let interim = "", final = "";
          for (let i = e.resultIndex; i < e.results.length; i++) {
            if (e.results[i].isFinal) final += e.results[i][0].transcript + " ";
            else interim += e.results[i][0].transcript;
          }
          if (final) setTranscriptText(p => p + final);
          setLiveTranscript(interim);
        };
        recognition.start();
        recognitionRef.current = recognition;
      }
    } catch(e) {
      setMicError("Microphone access denied. Use manual transcript mode below.");
      setManualMode(true);
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    recognitionRef.current?.stop();
    clearInterval(timerRef.current);
    setRecording(false);
    setLiveTranscript("");
  };

  const handleAudioUpload = (file) => {
    if (!file) return;
    setUploadedAudio(file);
    setAudioUrl(URL.createObjectURL(file));
    setAudioBlob(file);
  };

  const fmtDur = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,"0")}`;

  // Generate AI summary
  const generateSummary = async () => {
    const key = "proxy";
    if (!key) { alert("Add your Anthropic API key in AI Agent section first."); return; }
    if (!transcriptText.trim() && !notes.trim()) { alert("Add meeting notes or transcript first."); return; }
    if (!enforceLimit("meetings", session)) return;
    const kb = loadKB();
    setLoading(true); setSummary("");

    const prompt = `Generate a professional construction meeting summary for Grok Construction Group Inc.

MEETING DETAILS:
Type: ${meetingType}
Project: ${selectedProject?.name || "General"}
Date: ${new Date().toLocaleDateString()}
Attendees: ${attendees || "Not specified"}

TRANSCRIPT / NOTES:
${transcriptText || notes}

Using Grok's standards:
${kb.co_policy}
${kb.billing}

Generate a structured meeting summary with these sections:

## MEETING SUMMARY
Project: ${selectedProject?.name || ""}
Date: ${new Date().toLocaleDateString()}
Type: ${meetingType}
Attendees: ${attendees || "TBD"}

## KEY DECISIONS MADE
List every decision made in this meeting with who made it.

## ACTION ITEMS
| Action | Owner | Due Date | Priority |
List every action item discussed. Be specific.

## SCOPE CHANGES / CHANGE ORDER TRIGGERS
List any scope changes discussed, requested additions, or conditions that may trigger a Change Order.
For each: describe the change, estimated cost impact, and who requested it.

## OPEN QUESTIONS / UNRESOLVED ITEMS
Items discussed but not resolved. What needs a follow-up answer.

## SCHEDULE IMPACTS
Any dates discussed, delays mentioned, or schedule changes agreed to.

## NEXT MEETING
Agreed next steps and follow-up meeting if discussed.

## FOLLOW-UP EMAIL DRAFT
Write a short professional email Jehoon can send to all attendees recapping the meeting.
Subject line included.

Be specific and actionable. Reference actual names, numbers, and dates from the transcript.`;

    try {
      const resp = await fetch(AI_PROXY_URL, {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
        body:JSON.stringify({model:"claude-haiku-4-5",max_tokens:3000,stream:true,
          messages:[{role:"user",content:prompt}]})
      });

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while(true) {
        const {done,value} = await reader.read();
        if(done) break;
        buf += decoder.decode(value,{stream:true});
        const lines = buf.split("\n"); buf = lines.pop()||"";
        for(const line of lines) {
          if(line.startsWith("data: ")) {
            try { const p=JSON.parse(line.slice(6)); if(p.delta?.text) setSummary(prev=>prev+p.delta.text); } catch(e){}
          }
        }
      }

      // Auto-save
      const meeting = {
        id:"m"+Date.now(),
        type:meetingType,
        project:selectedProject?.name,
        date:new Date().toLocaleDateString(),
        time:new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),
        attendees,
        transcript:transcriptText||notes,
        summary:"", // will be filled after
        duration:duration||0,
      };
      setSavedMeetings(prev=>[{...meeting,summary},...prev]);

    } catch(e) { setSummary("Error: "+e.message); }
    setLoading(false);
  };

  const saveMeeting = () => {
    if (!summary) return;
    const meeting = {id:"m"+Date.now(),type:meetingType,project:selectedProject?.name,date:new Date().toLocaleDateString(),time:new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),attendees,transcript:transcriptText||notes,summary,duration};
    setSavedMeetings(prev=>[meeting,...prev]);
    alert("Meeting saved!");
  };

  const TabBtn = ({id,label,badge}) => (
    <button onClick={()=>setActiveTab(id)} style={{padding:"7px 16px",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:activeTab===id?T.gold:T.bg2,color:activeTab===id?T.bg0:T.muted,transition:"all 0.15s",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:6}}>
      {label}{badge>0&&<span style={{background:T.blue,color:"white",fontSize:9,padding:"1px 5px",borderRadius:10,fontWeight:800}}>{badge}</span>}
    </button>
  );

  return (
    <div style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>Meeting Recorder</div>
          <div style={{fontSize:12,color:T.muted,marginTop:3}}>Record meetings · Live transcription · AI generates action items, CO triggers, and follow-up email</div>
        </div>
      </div>

      <div style={{display:"flex",gap:4,marginBottom:16,flexWrap:"wrap"}}>
        <TabBtn id="record" label="Record Meeting"/>
        <TabBtn id="saved" label="Past Meetings" badge={savedMeetings.length}/>
      </div>

      {activeTab==="record" && (
        <div style={{display:"grid",gridTemplateColumns:"360px 1fr",gap:16,alignItems:"start"}}>

          {/* Left — controls */}
          <div>
            <Card>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14}}>Meeting Setup</div>

              <div style={{marginBottom:12}}>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Meeting Type</div>
                <select value={meetingType} onChange={e=>setMeetingType(e.target.value)}
                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}>
                  {MEETING_TYPES.map(t=><option key={t}>{t}</option>)}
                </select>
              </div>

              <div style={{marginBottom:12}}>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Project</div>
                <select value={selectedProject?.id} onChange={e=>setSelectedProject(projects.find(p=>p.id===parseInt(e.target.value)||p.id===e.target.value))}
                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}>
                  {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>

              <Field label="Attendees" value={attendees} onChange={setAttendees} placeholder="Jehoon, Owner Mike, Electrician Tom"/>

              <Divider/>

              {/* Recording controls */}
              {micError && (
                <div style={{background:T.amberBg,borderLeft:`3px solid ${T.amber}`,borderRadius:7,padding:"9px 12px",fontSize:11,color:T.muted,marginBottom:12,lineHeight:1.5}}>
                  {micError}
                </div>
              )}

              {!recording ? (
                <div>
                  <Btn primary onClick={startRecording} style={{width:"100%",justifyContent:"center",marginBottom:8,background:`linear-gradient(135deg,${T.red},#dc2626)`,color:"white"}}>
                    <span style={{fontSize:16}}>⏺</span> Start Recording
                  </Btn>
                  <div style={{display:"flex",gap:7}}>
                    <Btn ghost small onClick={()=>audioInputRef.current?.click()} style={{flex:1,justifyContent:"center"}}>
                      ↑ Upload Audio
                    </Btn>
                    <Btn ghost small onClick={()=>setManualMode(p=>!p)} style={{flex:1,justifyContent:"center"}}>
                      ✏ Manual Notes
                    </Btn>
                  </div>
                  <input ref={audioInputRef} type="file" accept="audio/*,video/*" style={{display:"none"}} onChange={e=>handleAudioUpload(e.target.files[0])}/>
                </div>
              ) : (
                <div>
                  {/* Live recording indicator */}
                  <div style={{background:T.redBg,border:`1px solid ${T.red}30`,borderRadius:10,padding:"14px",textAlign:"center",marginBottom:12}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,marginBottom:6}}>
                      <div style={{width:10,height:10,borderRadius:"50%",background:T.red,animation:"pulse 1s infinite"}}/>
                      <span style={{fontSize:14,fontWeight:700,color:T.red}}>RECORDING</span>
                    </div>
                    <div style={{fontSize:28,fontWeight:800,color:T.text,fontFamily:"'Space Mono',monospace"}}>{fmtDur(duration)}</div>
                  </div>
                  <Btn primary onClick={stopRecording} style={{width:"100%",justifyContent:"center",background:T.bg3,color:T.text}}>
                    ⏹ Stop Recording
                  </Btn>
                </div>
              )}

              {audioUrl && (
                <div style={{marginTop:12}}>
                  <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Recorded Audio</div>
                  <audio controls src={audioUrl} style={{width:"100%",borderRadius:8}}/>
                  {duration>0&&<div style={{fontSize:10,color:T.muted,marginTop:4}}>Duration: {fmtDur(duration)}</div>}
                </div>
              )}
            </Card>
          </div>

          {/* Right — transcript + summary */}
          <div style={{display:"flex",flexDirection:"column",gap:12}}>

            {/* Live transcript */}
            <Card>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>
                  {recording?"Live Transcript":"Meeting Notes / Transcript"}
                </div>
                {recording&&liveTranscript&&(
                  <div style={{fontSize:10,color:T.amber,display:"flex",alignItems:"center",gap:5}}>
                    <div style={{width:6,height:6,borderRadius:"50%",background:T.amber,animation:"pulse 1s infinite"}}/>
                    Listening...
                  </div>
                )}
              </div>

              {liveTranscript && (
                <div style={{background:T.amberBg,borderRadius:7,padding:"8px 12px",fontSize:12,color:T.amber,marginBottom:8,fontStyle:"italic"}}>
                  {liveTranscript}
                </div>
              )}

              <textarea value={transcriptText} onChange={e=>setTranscriptText(e.target.value)}
                placeholder={recording
                  ? "Transcript appears here as you speak... You can also type notes manually."
                  : "Paste transcript here, type meeting notes, or use the recorder above.\n\nTip: Be specific — include names, dollar amounts, dates, and scope decisions. The AI will turn this into a structured summary with action items."}
                rows={recording?8:12}
                style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit",lineHeight:1.7}}/>

              {(manualMode||!recording) && (
                <div style={{marginTop:8}}>
                  <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Additional Context / Notes</div>
                  <textarea value={notes} onChange={e=>setNotes(e.target.value)}
                    placeholder="Scope changes discussed, CO triggers, owner requests, any items the transcript may have missed..."
                    rows={3}
                    style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit"}}/>
                </div>
              )}

              <Btn primary disabled={loading||(!transcriptText.trim()&&!notes.trim())} onClick={generateSummary}
                style={{width:"100%",justifyContent:"center",marginTop:10,background:`linear-gradient(135deg,${T.purple},${T.blue})`,color:"white",fontSize:14,padding:"13px"}}>
                {loading?<><Spin/>Generating Summary...</>:<><span style={{fontSize:16}}>✦</span> Generate AI Meeting Summary</>}
              </Btn>
            </Card>

            {/* AI Summary output */}
            {(summary||loading) && (
              <Card>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.text}}>AI Meeting Summary</div>
                  <div style={{display:"flex",gap:7}}>
                    {summary&&<Btn small ghost onClick={()=>navigator.clipboard.writeText(summary)}>Copy</Btn>}
                    {summary&&<Btn small primary onClick={saveMeeting} style={{background:T.green,color:"white"}}>Save Meeting</Btn>}
                  </div>
                </div>
                <div style={{background:T.bg0,borderRadius:8,padding:"14px 16px",fontSize:12,color:T.text,lineHeight:1.85,whiteSpace:"pre-wrap",maxHeight:500,overflowY:"auto",fontFamily:"inherit"}}>
                  {summary||<div style={{display:"flex",alignItems:"center",gap:10,color:T.muted}}><Spin/>Analyzing meeting...</div>}
                </div>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* SAVED MEETINGS TAB */}
      {activeTab==="saved" && (
        <div style={{display:"grid",gridTemplateColumns:selectedMeeting?"1fr 1fr":"1fr",gap:14}}>
          <div>
            {savedMeetings.length===0 ? (
              <div style={{textAlign:"center",padding:"60px",color:T.muted,fontSize:13}}>
                <div style={{fontSize:40,marginBottom:12,opacity:0.4}}>🎙</div>
                No meetings recorded yet. Record your first meeting above.
              </div>
            ) : savedMeetings.map(m=>(
              <div key={m.id} onClick={()=>setSelectedMeeting(m)}
                style={{background:T.bg1,border:`1px solid ${selectedMeeting?.id===m.id?T.gold:T.subtle}`,borderRadius:11,padding:"14px 16px",marginBottom:8,cursor:"pointer",transition:"all 0.15s"}}
                onMouseEnter={e=>{if(selectedMeeting?.id!==m.id)e.currentTarget.style.borderColor=T.goldDim}}
                onMouseLeave={e=>{if(selectedMeeting?.id!==m.id)e.currentTarget.style.borderColor=T.subtle}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:2}}>{m.type}</div>
                    <div style={{fontSize:11,color:T.muted}}>{m.project}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:11,fontFamily:"monospace",color:T.muted}}>{m.date}</div>
                    <div style={{fontSize:10,color:T.muted}}>{m.time}{m.duration>0&&` · ${fmtDur(m.duration)}`}</div>
                  </div>
                </div>
                {m.attendees&&<div style={{fontSize:11,color:T.muted}}>👥 {m.attendees}</div>}
                <div style={{fontSize:11,color:T.muted,marginTop:6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"100%"}}>
                  {m.summary?.slice(0,100)||"No summary"}...
                </div>
              </div>
            ))}
          </div>
          {selectedMeeting && (
            <Card>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <div>
                  <div style={{fontSize:14,fontWeight:700,color:T.text}}>{selectedMeeting.type}</div>
                  <div style={{fontSize:11,color:T.muted}}>{selectedMeeting.project} · {selectedMeeting.date}</div>
                </div>
                <div style={{display:"flex",gap:6}}>
                  <Btn small ghost onClick={()=>navigator.clipboard.writeText(selectedMeeting.summary)}>Copy</Btn>
                  <Btn small ghost onClick={()=>setSelectedMeeting(null)}><Ic d={ICONS.x} size={11}/></Btn>
                </div>
              </div>
              <div style={{background:T.bg0,borderRadius:8,padding:"14px",fontSize:12,color:T.text,lineHeight:1.8,whiteSpace:"pre-wrap",maxHeight:550,overflowY:"auto",fontFamily:"inherit"}}>
                {selectedMeeting.summary}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// ─── VIDEO / PHOTO WALKTHROUGH ESTIMATOR ─────────────────────────────────────
function WalkthroughEstimatorView() {
  const [mode, setMode] = useState("upload"); // upload | recording | analyzing | result
  const [mediaFiles, setMediaFiles] = useState([]); // uploaded images or video frames
  const [extractedFrames, setExtractedFrames] = useState([]);
  const [projectType, setProjectType] = useState("Residential Remodel");
  const [projectAddr, setProjectAddr] = useState("");
  const [buildingAge, setBuildingAge] = useState("");
  const [notes, setNotes] = useState("");
  const [transcript, setTranscript] = useState(""); // what GC said during walkthrough
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [frameCount, setFrameCount] = useState(0);
  const [isMob, setIsMob] = useState(window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setIsMob(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const streamRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const recorderRef = useRef(null);
  const [capturedPhotos, setCapturedPhotos] = useState([]);

  const PROJECT_TYPES = [
    "SRO / Affordable Housing","Senior / ADA Housing","Multi-Family Apartment",
    "Residential Remodel","ADU / Garage Conversion","Commercial TI","Kitchen Renovation",
    "Bathroom Renovation","Full Home Renovation","Exterior / Envelope",
  ];

  // Extract frames from video file
  const extractFrames = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const frames = [];
      video.src = URL.createObjectURL(file);
      video.onloadedmetadata = () => {
        canvas.width = 800; // resize for API efficiency
        canvas.height = Math.round(800 * video.videoHeight / video.videoWidth);
        const duration = video.duration;
        // Extract 1 frame every 8 seconds, max 12 frames
        const interval = Math.max(duration / 12, 8);
        const times = [];
        for (let t = 0; t < duration; t += interval) times.push(t);
        let idx = 0;
        const captureNext = () => {
          if (idx >= times.length) { resolve(frames); return; }
          video.currentTime = times[idx];
          video.onseeked = () => {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            frames.push({ id: idx, dataUrl: canvas.toDataURL("image/jpeg", 0.75), time: times[idx].toFixed(1)+"s" });
            idx++;
            captureNext();
          };
        };
        captureNext();
      };
    });
  };

  const handleVideoUpload = async (file) => {
    setMode("analyzing");
    setProgress("Extracting frames from video...");
    const frames = await extractFrames(file);
    setExtractedFrames(frames);
    setFrameCount(frames.length);
    setProgress(`Extracted ${frames.length} frames — ready to analyze`);
    setMode("upload");
  };

  // Resize + recompress one image so it fits Anthropic's 5MB-per-image limit.
  // Max 1920px on the long edge (preserves aspect), JPEG quality 0.85 first
  // attempt, 0.70 fallback if still > 4MB. Returns a fresh dataUrl.
  const compressImageForAI = (originalDataUrl) => new Promise((resolve, reject) => {
    const MAX_W = 1920;
    const TARGET_BYTES = 4 * 1024 * 1024;
    const img = new Image();
    img.onerror = () => reject(new Error("Image decode failed"));
    img.onload = () => {
      try {
        const scale = img.naturalWidth > MAX_W ? MAX_W / img.naturalWidth : 1;
        const w = Math.round(img.naturalWidth * scale);
        const h = Math.round(img.naturalHeight * scale);
        const cnv = document.createElement("canvas");
        cnv.width = w; cnv.height = h;
        const c = cnv.getContext("2d");
        c.drawImage(img, 0, 0, w, h);
        cnv.toBlob((blob85) => {
          if (!blob85) return reject(new Error("Canvas encode failed"));
          if (blob85.size <= TARGET_BYTES) {
            const r = new FileReader();
            r.onload = (e) => resolve(e.target.result);
            r.onerror = () => reject(new Error("Blob read failed"));
            r.readAsDataURL(blob85);
            return;
          }
          // Still too big — try 0.70.
          cnv.toBlob((blob70) => {
            if (!blob70) return reject(new Error("Canvas encode failed at 0.70"));
            const r = new FileReader();
            r.onload = (e) => resolve(e.target.result);
            r.onerror = () => reject(new Error("Blob read failed"));
            r.readAsDataURL(blob70);
          }, "image/jpeg", 0.70);
        }, "image/jpeg", 0.85);
      } catch (e) { reject(e); }
    };
    img.src = originalDataUrl;
  });

  const handlePhotosUpload = async (files) => {
    const arr = Array.from(files).slice(0, 20);
    setProgress(`Compressing 0 of ${arr.length}…`);
    const out = [];
    let done = 0;
    for (const file of arr) {
      try {
        // Read original → compress → push.
        const raw = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = (e) => res(e.target.result);
          r.onerror = () => rej(new Error("read failed"));
          r.readAsDataURL(file);
        });
        const compressed = await compressImageForAI(raw);
        out.push({ id: Date.now() + Math.random(), dataUrl: compressed, name: file.name });
      } catch (e) {
        console.warn("[walkthrough] compress failed for", file.name, e?.message);
      }
      done++;
      setProgress(`Compressing ${done} of ${arr.length}…`);
    }
    setProgress("");
    if (out.length) setMediaFiles(prev => [...prev, ...out]);
    setMode("upload");
  };

  // Live camera capture
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({video:{facingMode:"environment"},audio:false});
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setMode("recording");
    } catch(e) {
      alert("Camera access denied or not available on this device.");
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext("2d").drawImage(videoRef.current, 0, 0);
    const rawDataUrl = canvas.toDataURL("image/jpeg", 0.85);
    // Same compression pass as the file uploader so live-camera captures
    // from high-res phone cameras don't blow the 5MB-per-image API limit.
    compressImageForAI(rawDataUrl).then((dataUrl) => {
      setCapturedPhotos(prev => [...prev, { id: Date.now(), dataUrl, name: `photo_${prev.length + 1}.jpg` }]);
    }).catch(() => {
      // Fallback: use the raw capture if compression fails (shouldn't happen)
      setCapturedPhotos(prev => [...prev, { id: Date.now(), dataUrl: rawDataUrl, name: `photo_${prev.length + 1}.jpg` }]);
    });
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    setMode("upload");
    if (capturedPhotos.length > 0) setMediaFiles(prev => [...prev, ...capturedPhotos]);
    setCapturedPhotos([]);
  };

  // Analyze all media with Claude
  const analyzeWalkthrough = async () => {
    const key = "proxy"; // Using Supabase AI proxy
    if (!key) { alert("Add your Anthropic API key in AI Agent section first."); return; }

    const allMedia = [...mediaFiles, ...extractedFrames];
    if (allMedia.length === 0) { alert("Upload photos or a walkthrough video first."); return; }

    const kb = loadKB();
    setLoading(true); setResult("");
    setMode("analyzing");
    setProgress("Sending to Claude for analysis...");

    // Build content array with all images + text prompt
    const imageContent = allMedia.slice(0, 15).map(img => ({
      type: "image",
      source: { type: "base64", media_type: "image/jpeg", data: img.dataUrl.split(",")[1] }
    }));

    const prompt = `You are an expert construction estimator for Grok Construction Group Inc (CSLB #1152750), a licensed GC in San Mateo, CA specializing in SF Bay Area construction.

SCOPE-RESTRAINT RULE (do not violate): Only include scope items that are EXPLICITLY VISIBLE as damage/work needed in these photos, or that were EXPLICITLY MENTIONED in the walkthrough notes. If a room looks fine, do not add work for it. If flooring isn't mentioned, don't include flooring. Err on the side of LESS scope, not more.

${PRICING_RULES}

PROJECT DETAILS:
Type: ${projectType}
Address/Area: ${projectAddr || "SF Bay Area"}
Building Age: ${buildingAge || "Unknown"}
GC Notes / What Was Mentioned: ${notes || "See photos — only scope what's visibly damaged or clearly needs work"}
GC Verbal Scope (from walkthrough recording): ${transcript || "Not provided — base scope strictly on visible conditions"}

Decide the project TIER per the rules above, then provide ONLY:

## OBSERVED CONDITIONS
Brief description of what is CLEARLY visible as needing work in each area shown. Skip rooms/areas that look fine.

## SCOPE OF WORK — MENTIONED OR CLEARLY VISIBLE ONLY
List ONLY work items that are explicitly mentioned in the GC notes OR clearly visible as damaged/deficient in the photos. Group by trade. If a trade has no clearly needed work, omit it entirely.

## TIER
[TIER A | TIER B | TIER C] — one-line rationale.

## RED FLAGS
Any visible signs of water damage, mold, structural issues, or pre-1978 hazards. Be honest — these inform whether contingency is needed.

## ESTIMATE — TRANSPARENT LINE ITEMS
Itemize per the LINE-ITEM STRUCTURE rules above. Use the table form:

| Item | Description | Amount |
| ---- | ----------- | ------:|
| ... | ... | $X,XXX |

## SUBTOTAL
$XX,XXX

## OPTIONAL CONTINGENCY
[Render this section ONLY if you actually flagged concealed-condition risk in RED FLAGS above. Otherwise OMIT ENTIRELY. When rendered:]
Optional contingency (e.g. concealed water damage behind unit walls per red-flag above) — billed only if used, unused credited to Owner: $X,XXX

## TOTAL
$XX,XXX  (and "$X,XXX with optional contingency" if rendered)

## WHAT NEEDS FIELD VERIFICATION
Items that couldn't be confirmed from photos alone.

Be conservative and specific. Do not add scope that wasn't there.`;

    try {
      const cid = window._grokCompanyId || "local";
      const resp = await fetch(AI_PROXY_URL, {
        method: "POST",
        headers: {"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
        body: JSON.stringify({
          max_tokens: 4000,
          temperature: 0, // deterministic estimate generation — identical inputs → identical numbers
          usage_type: "estimates",
          company_id: cid,
          messages: [{
            role: "user",
            content: [...imageContent, {type:"text", text:prompt}]
          }]
        })
      });

      // Proxy returns plain JSON unless body.stream:true. Parse JSON.
      const raw = await resp.text();
      let data;
      try { data = JSON.parse(raw); } catch {
        throw new Error("AI proxy returned non-JSON: " + raw.slice(0, 200));
      }
      if (!resp.ok || data?.error) {
        throw new Error(errString(data?.error) || ("HTTP " + resp.status));
      }
      const text = data?.content?.[0]?.text || "";
      if (!text) throw new Error("AI returned an empty response — try again or upload more photos");
      setResult(text);
      setMode("result");
      setProgress("");
    } catch(e) {
      setResult("⚠ " + (e?.message || "Analysis failed. Try again with fewer or smaller photos."));
      setMode("upload");
    }
    setLoading(false);
  };

  const clearAll = () => {
    setMediaFiles([]); setExtractedFrames([]); setResult("");
    setMode("upload"); setProgress(""); setFrameCount(0);
  };

  const allMedia = [...mediaFiles, ...extractedFrames];

  return (
    <div style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>Video & Photo Estimator</div>
          <div style={{fontSize:12,color:T.muted,marginTop:3}}>
            Walk through a job site on video or take photos → AI analyzes the footage → generates rough scope and estimate
          </div>
        </div>
        {allMedia.length > 0 && <Btn ghost small onClick={clearAll}>Clear All</Btn>}
      </div>

      {/* How it works banner */}
      <div style={{display:"flex",gap:0,background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:10,overflow:"hidden",marginBottom:20}}>
        {[
          {n:"1",l:"Record or Upload",sub:"Walk the job, record video or take photos",c:T.blue},
          {n:"2",l:"Set Project Type",sub:"SRO, residential, commercial TI",c:T.teal},
          {n:"3",l:"AI Analyzes",sub:"Claude reviews every frame and room",c:T.purple},
          {n:"4",l:"Get Scope + Estimate",sub:"Room-by-room breakdown with SF pricing",c:T.gold},
        ].map((s,i)=>(
          <div key={i} style={{flex:1,padding:"14px 16px",borderRight:i<3?`1px solid ${T.subtle}`:"none",display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:28,height:28,borderRadius:"50%",background:s.c+"18",border:`1px solid ${s.c}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:s.c,flexShrink:0}}>{s.n}</div>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:T.text}}>{s.l}</div>
              <div style={{fontSize:10,color:T.muted}}>{s.sub}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{display:"grid",gridTemplateColumns:isMob?"1fr":(result?"1fr 1fr":"1fr"),gap:16,alignItems:"start"}}>

        {/* Left — upload + settings */}
        <div>
          {/* Camera view */}
          {mode === "recording" && (
            <Card style={{marginBottom:14}}>
              <div style={{position:"relative",background:T.bg0,borderRadius:10,overflow:"hidden",marginBottom:12}}>
                <video ref={videoRef} autoPlay playsInline muted style={{width:"100%",display:"block"}}/>
                <canvas ref={canvasRef} style={{display:"none"}}/>
                {capturedPhotos.length > 0 && (
                  <div style={{position:"absolute",top:10,right:10,background:"rgba(13,20,32,0.9)",borderRadius:8,padding:"4px 10px",fontSize:11,color:T.gold,fontWeight:700}}>
                    {capturedPhotos.length} captured
                  </div>
                )}
              </div>
              <div style={{display:"flex",gap:8}}>
                <Btn primary onClick={capturePhoto} style={{flex:2,justifyContent:"center",fontSize:14}}>Capture Photo</Btn>
                <Btn ghost onClick={stopCamera} style={{flex:1,justifyContent:"center"}}>Done ({capturedPhotos.length})</Btn>
              </div>
            </Card>
          )}

          {/* Upload area */}
          {mode !== "recording" && (
            <div>
              <div style={{display:"grid",gridTemplateColumns:isMob?"1fr":"1fr 1fr 1fr",gap:10,marginBottom:14}}>
                {/* Photos */}
                <div onClick={()=>fileInputRef.current?.click()}
                  style={{background:T.bg1,border:`2px dashed ${T.subtle}`,borderRadius:12,padding:"20px 12px",textAlign:"center",cursor:"pointer",transition:"all 0.2s"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=T.blue;e.currentTarget.style.background=T.blueBg}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=T.subtle;e.currentTarget.style.background=T.bg1}}>
                  <div style={{fontSize:28,marginBottom:8}}>🖼</div>
                  <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:4}}>Upload Photos</div>
                  <div style={{fontSize:10,color:T.muted}}>JPG, PNG — up to 20 photos</div>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*,application/pdf,.pdf" multiple style={{display:"none"}}
                  onChange={e=>handlePhotosUpload(e.target.files)}/>

                {/* Video */}
                <div onClick={()=>videoInputRef.current?.click()}
                  style={{background:T.bg1,border:`2px dashed ${T.subtle}`,borderRadius:12,padding:"20px 12px",textAlign:"center",cursor:"pointer",transition:"all 0.2s"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=T.purple;e.currentTarget.style.background="rgba(168,139,250,0.05)"}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=T.subtle;e.currentTarget.style.background=T.bg1}}>
                  <div style={{fontSize:28,marginBottom:8}}>🎬</div>
                  <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:4}}>Upload Video</div>
                  <div style={{fontSize:10,color:T.muted}}>MP4, MOV — frames auto-extracted</div>
                </div>
                <input ref={videoInputRef} type="file" accept="video/*" style={{display:"none"}}
                  onChange={e=>handleVideoUpload(e.target.files[0])}/>

                {/* Live camera */}
                <div onClick={startCamera}
                  style={{background:T.bg1,border:`2px dashed ${T.subtle}`,borderRadius:12,padding:"20px 12px",textAlign:"center",cursor:"pointer",transition:"all 0.2s"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=T.teal;e.currentTarget.style.background="rgba(45,212,191,0.05)"}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=T.subtle;e.currentTarget.style.background=T.bg1}}>
                  <div style={{fontSize:28,marginBottom:8}}>📷</div>
                  <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:4}}>Live Camera</div>
                  <div style={{fontSize:10,color:T.muted}}>Use phone camera directly</div>
                </div>
              </div>

              {/* Compression progress — shown while photos are being resized for the AI */}
              {progress && (
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",marginBottom:10,background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,fontSize:11,color:T.muted}}>
                  <Spin/> {progress}
                </div>
              )}

              {/* Media preview */}
              {allMedia.length > 0 && (
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>
                    {allMedia.length} image{allMedia.length!==1?"s":""} ready for analysis
                    {extractedFrames.length>0&&<span style={{color:T.purple}}> ({extractedFrames.length} from video)</span>}
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {allMedia.slice(0,15).map((m,i)=>(
                      <div key={m.id||i} style={{position:"relative",width:64,height:64,borderRadius:8,overflow:"hidden",border:`1px solid ${T.subtle}`}}>
                        <img src={m.dataUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                        {m.time && <div style={{position:"absolute",bottom:0,left:0,right:0,background:"rgba(0,0,0,0.7)",fontSize:8,color:"white",textAlign:"center",padding:"1px"}}>{m.time}</div>}
                        <button onClick={()=>{setMediaFiles(p=>p.filter(x=>x.id!==m.id));setExtractedFrames(p=>p.filter(x=>x.id!==m.id));}}
                          style={{position:"absolute",top:2,right:2,width:16,height:16,borderRadius:"50%",background:"rgba(239,68,68,0.9)",border:"none",color:"white",fontSize:10,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
                      </div>
                    ))}
                    {allMedia.length > 15 && <div style={{width:64,height:64,borderRadius:8,background:T.bg2,border:`1px solid ${T.subtle}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:T.muted}}>+{allMedia.length-15}</div>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Project settings */}
          {mode !== "recording" && (
            <Card>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14}}>Project Details</div>

              <div style={{marginBottom:12}}>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Project Type</div>
                <select value={projectType} onChange={e=>setProjectType(e.target.value)}
                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}>
                  {PROJECT_TYPES.map(t=><option key={t}>{t}</option>)}
                </select>
              </div>

              <Field label="Address / Area" value={projectAddr} onChange={setProjectAddr} placeholder="Tenderloin SF, Burlingame CA, etc."/>
              <Field label="Building Age / Year Built" value={buildingAge} onChange={setBuildingAge} placeholder="1965, pre-1978, 1995, unknown"/>

              <div style={{marginBottom:14}}>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>What You Said / GC Scope Notes</div>
                <textarea value={transcript} onChange={e=>setTranscript(e.target.value)}
                  placeholder="Paste or type what you said during the walkthrough — e.g. 'replace bathroom tile, repaint living room, fix that leak under the sink, owner wants new kitchen cabinets...' — this drives the scope estimate"
                  rows={4} style={{width:"100%",background:T.bg2,border:`1px solid ${T.gold}40`,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit"}}/>
                <div style={{fontSize:10,color:T.gold,marginTop:4}}>⚡ This is the most important field — AI will only scope what you mention here + what's visibly damaged in photos</div>
              </div>

              <div style={{marginBottom:14}}>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Additional Context</div>
                <textarea value={notes} onChange={e=>setNotes(e.target.value)}
                  placeholder="Occupied units, known water damage, permit history, owner requests..."
                  rows={2} style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit"}}/>
              </div>

              {mode === "analyzing" && progress ? (
                <div style={{background:T.bg2,borderRadius:8,padding:"14px",textAlign:"center"}}>
                  <div style={{height:3,background:`linear-gradient(to right,${T.gold},${T.teal},${T.gold})`,backgroundSize:"200% 100%",animation:"loadSlide 1.5s linear infinite",borderRadius:2,marginBottom:12}}/>
                  <div style={{fontSize:13,color:T.muted}}>{progress}</div>
                </div>
              ) : (
                <Btn primary disabled={allMedia.length===0||loading} onClick={analyzeWalkthrough}
                  style={{width:"100%",justifyContent:"center",background:`linear-gradient(135deg,${T.purple},${T.blue})`,color:"white",fontSize:15,padding:"14px"}}>
                  {loading ? <><Spin/>Analyzing {allMedia.length} image{allMedia.length!==1?"s":""}...</> : <><span style={{fontSize:18}}>🔍</span> Analyze &amp; Generate Estimate</>}
                </Btn>
              )}

              <div style={{marginTop:10,fontSize:10,color:T.muted,lineHeight:1.6,textAlign:"center"}}>
                AI analyzes every photo and video frame · Uses current SF Bay Area pricing · Identifies scope by room · Flags hidden condition risks
              </div>
            </Card>
          )}
        </div>

        {/* Right — results */}
        {(result || loading) && (
          <div>
            <Card>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>Analysis Results</div>
                <div style={{display:"flex",gap:6}}>
                  {result && <Btn small ghost onClick={()=>navigator.clipboard.writeText(result)}>Copy</Btn>}
                  {result && <Btn small primary onClick={()=>setResult("")} style={{background:T.bg3,color:T.muted}}>Clear</Btn>}
                </div>
              </div>
              <div style={{background:T.bg0,borderRadius:8,padding:"14px",fontSize:12,color:T.text,lineHeight:1.8,whiteSpace:"pre-wrap",maxHeight:isMob?"none":600,overflowY:isMob?"visible":"auto",fontFamily:"inherit"}}>
                {result || <div style={{display:"flex",alignItems:"center",gap:10,color:T.muted,padding:"20px 0"}}><Spin/>Analyzing photos...</div>}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CA / ADA CODE COMPLIANCE CHECKER ─────────────────────────────────────────
// ─── CA / ADA BUILDING CODE COMPLIANCE CHECKER ───────────────────────────────
// Embedded inside BlueprintView as an AI panel

const CA_CODE_SYSTEM = `You are a California licensed building code expert and ADA compliance specialist with deep knowledge of:

CALIFORNIA BUILDING CODE (CBC 2022):
- CBC Chapter 11A: Housing Accessibility (state-specific, stricter than federal)
- CBC Chapter 11B: Public Buildings accessibility  
- Title 24 Part 2: California Building Standards
- CBC Section 1006-1021: Means of Egress
- CBC Chapter 7: Fire-Resistance-Rated Construction
- CBC Chapter 9: Fire Protection Systems
- CBC Section 1203: Ventilation requirements
- CBC Chapter 25: Plumbing (CPC integration)
- CBC Chapter 27: Electrical (CEC integration)
- CBC Section 4: Special Occupancies (SRO, R-2, R-3)

ADA STANDARDS (2010 ADA Standards for Accessible Design):
- Section 404: Doors and Doorways (32" clear min, 36" preferred)
- Section 405: Ramps (1:12 max slope, 36" min width, handrails)
- Section 407-410: Elevators and platform lifts
- Section 603-610: Toilet rooms (60" turning radius, grab bars)
- Section 804: Kitchens (work surface heights, clearances)
- Section 902: Dining surfaces
- Section 1002-1003: Residential dwelling units

SF BAY AREA SPECIFIC:
- San Francisco Building Code (SFBC) amendments to CBC
- Oakland building code amendments
- BART/transit proximity requirements
- Bay Area seismic zone (SDC D) requirements per ASCE 7
- Title 24 Energy compliance (strictest in US)
- CalGreen mandatory measures (CALGreen 2022)
- AB 1482 / just cause eviction considerations for occupied renovations
- SRO-specific: SF Planning Code 188-188.5 unit size minimums (150 sqft min)

PREVAILING WAGE / LABOR COMPLIANCE:
- DIR public works thresholds ($25K residential, $15K commercial)
- OSHA 1926 construction safety standards

When reviewing blueprints, be SPECIFIC about code sections, measurements, and exact violations. Flag both definite violations and potential issues that need engineering review.`;

const CODE_CATEGORIES = [
  {id:"all", label:"All Codes", icon:"📋"},
  {id:"ada", label:"ADA/Accessibility", icon:"♿"},
  {id:"egress", label:"Egress & Fire", icon:"🚪"},
  {id:"structural", label:"Structural/Seismic", icon:"🏗"},
  {id:"energy", label:"Title 24 Energy", icon:"⚡"},
  {id:"plumbing", label:"Plumbing (CPC)", icon:"🔧"},
  {id:"electrical", label:"Electrical (CEC)", icon:"💡"},
  {id:"calgreen", label:"CalGreen", icon:"🌿"},
];

function CodeComplianceChecker({activeBlueprint, flags, projectType, buildingAge, onAutoFlag}) {
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [autoFlags, setAutoFlags] = useState([]);
  const [showChecker, setShowChecker] = useState(false);
  const [projectTypeLocal, setProjectTypeLocal] = useState(projectType || "SRO / Affordable Housing");
  const [buildingAgeLocal, setBuildingAgeLocal] = useState(buildingAge || "Pre-1978");
  const [occupancy, setOccupancy] = useState("R-2 Multi-Family");
  const [selectedState, setSelectedState] = useState("CA");
  const [selectedCity, setSelectedCity] = useState("San Francisco");

  const PROJECT_TYPES = ["SRO / Affordable Housing","Senior / ADA Housing","Multi-Family Apartment","Residential Remodel","ADU / Garage Conversion","Commercial TI","Kitchen/Bath Renovation"];
  const OCCUPANCIES = ["R-1 Hotel/Motel","R-2 Multi-Family","R-3 Single Family","A-2 Restaurant","B Office","E Educational","I-1 Assisted Living","S Storage"];

  const JURISDICTIONS = {
    CA:{name:"California",codes:"CBC 2022 · Title 24 · CalGreen · ADA 2010 · ASCE 7 Seismic",cities:{
      "San Francisco":["SFBC amendments","SF Seismic Safety Ordinance","SF SRO min 150 sqft (Planning Code 188)","SF Green Building Ordinance"],
      "Oakland":["Oakland Building Code","Oakland Soft-Story Seismic Ordinance","Alameda County amendments"],
      "San Jose":["San Jose Municipal Code","Santa Clara County fire code","Strict Title 24 energy zone"],
      "Los Angeles":["LABC amendments","LA Soft-Story Retrofit Ordinance","LA Green Building Code","LADWP energy rules"],
      "San Diego":["San Diego Municipal Code","SD Sustainability Ordinance","Coastal Zone regulations"],
      "Sacramento":["Sacramento City Code","Central Valley energy Title 24 zone"],
      "Other CA City":["CBC 2022 base + local amendments — verify with AHJ"],
    }},
    NY:{name:"New York",codes:"NYCBC · NYS Building Code 2020 · NYC Energy Code · ADA 2010",cities:{
      "New York City":["NYC Building Code (NYCBC 2022)","Local Law 97 Carbon Emissions","Local Law 11 Facade Inspection","NYC Zoning Resolution","HPD/DOB requirements"],
      "Buffalo":["Buffalo Building Code","NYS Energy Code","Erie County amendments"],
      "Other NY":["NYS Building Code 2020 + local amendments — verify with AHJ"],
    }},
    TX:{name:"Texas",codes:"IBC 2021 (TX) · IECC 2021 · ADA 2010 · NFPA 101",cities:{
      "Houston":["Houston Building Code (no zoning)","Harris County Fire Code"],
      "Dallas":["Dallas Building Code","NCTCOG regional amendments","Dallas Green Building"],
      "Austin":["Austin Energy Green Building","Austin Land Development Code","Strong ADU rules"],
      "San Antonio":["San Antonio Unified Development Code","Bexar County amendments"],
      "Other TX":["IBC 2021 Texas edition + local amendments — verify with AHJ"],
    }},
    FL:{name:"Florida",codes:"FBC 7th Ed 2023 · Florida Energy Code · ADA 2010 · ASCE 7",cities:{
      "Miami":["Miami-Dade HVHZ requirements","Florida Building Code 7th Ed","Flood Zone compliance","Miami 21 Zoning"],
      "Orlando":["Orange County Building Division","Florida Energy Code"],
      "Tampa":["Hillsborough County amendments","Flood zone compliance"],
      "Other FL":["Florida Building Code 7th Ed + local amendments — verify with AHJ"],
    }},
    WA:{name:"Washington",codes:"WSBC 2021 · WA Energy Code · ADA 2010",cities:{
      "Seattle":["Seattle Building Code (SBC)","Seattle Energy Code (stricter than WA state)","Seattle Green Building Standard","Seattle ADU rules"],
      "Tacoma":["Pierce County amendments","WA State Energy Code"],
      "Other WA":["WSBC 2021 + local amendments — verify with AHJ"],
    }},
    OR:{name:"Oregon",codes:"OSSC 2021 · Oregon Energy Code · ADA 2010",cities:{
      "Portland":["Portland City Code","Oregon Residential Specialty Code","Portland Green Building Policy"],
      "Other OR":["Oregon Structural Specialty Code + local amendments — verify with AHJ"],
    }},
    CO:{name:"Colorado",codes:"IBC 2021 (CO) · IECC 2021 · ADA 2010",cities:{
      "Denver":["Denver Building & Fire Code","Denver Green Building Ordinance","Denver ADU policy"],
      "Boulder":["Boulder Green Building Code","Boulder Smart Regs rental requirements"],
      "Other CO":["IBC 2021 CO edition + local amendments — verify with AHJ"],
    }},
    AZ:{name:"Arizona",codes:"IBC 2018 (AZ) · IECC 2018 · ADA 2010",cities:{
      "Phoenix":["Phoenix Building Code","Maricopa County amendments","Desert energy requirements"],
      "Tucson":["Tucson/Pima County UDC","Southern AZ climate zone"],
      "Other AZ":["IBC 2018 AZ edition + local amendments — verify with AHJ"],
    }},
    IL:{name:"Illinois",codes:"IBC 2021 (IL) · IECC 2021 · Illinois Accessibility Code · ADA 2010",cities:{
      "Chicago":["Chicago Building Code","Chicago Energy Conservation Code","Chicago Green Permit Program"],
      "Other IL":["IBC 2021 IL edition + Illinois Accessibility Code — verify with AHJ"],
    }},
    GA:{name:"Georgia",codes:"IBC 2018 (GA) · Georgia Energy Code · ADA 2010",cities:{
      "Atlanta":["Atlanta City Code","Georgia Energy Code","Atlanta Sustainability Plan"],
      "Other GA":["Georgia State Minimum Standard Codes — verify with AHJ"],
    }},
    NC:{name:"North Carolina",codes:"NCBC 2018 · NC Energy Code · ADA 2010",cities:{
      "Charlotte":["Charlotte-Mecklenburg Code Enforcement","NC Energy Code"],
      "Raleigh":["Raleigh Building Code","Wake County amendments"],
      "Other NC":["NC Building Code + local amendments — verify with AHJ"],
    }},
    OH:{name:"Ohio",codes:"Ohio Building Code · IECC 2021 · ADA 2010",cities:{
      "Columbus":["Columbus Building Code","Franklin County amendments"],
      "Cleveland":["Cleveland Building Code","Cuyahoga County amendments"],
      "Other OH":["Ohio Building Code + local amendments — verify with AHJ"],
    }},
  };
  const stateList = Object.entries(JURISDICTIONS).map(([k,v])=>({code:k,name:v.name}));
  const cityList  = Object.keys(JURISDICTIONS[selectedState]?.cities||{});
  const localAmendments = JURISDICTIONS[selectedState]?.cities?.[selectedCity]||[];
  const stateCodes = JURISDICTIONS[selectedState]?.codes||"IBC + local codes";

  const runCodeCheck = async () => {
    const key = "proxy"; // Using Supabase AI proxy
    if (!key) { alert("Add your Anthropic API key in AI Agent first."); return; }
    if (!activeBlueprint?.dataUrl) { alert("Upload a blueprint first to analyze."); return; }

    setChecking(true); setResults(""); setAutoFlags([]);

    const categoryFilter = activeCategory === "all" ? "all code categories" :
      CODE_CATEGORIES.find(c=>c.id===activeCategory)?.label || "all codes";

    const existingFlags = flags.filter(f=>f.status!=="resolved").map(f=>`- ${f.title} (${f.trade}, ${f.priority})`).join("\n") || "None flagged yet";

    const prompt = `Analyze this construction blueprint/drawing for California Building Code and ADA compliance violations.

PROJECT DETAILS:
Type: ${projectTypeLocal}
Occupancy Classification: ${occupancy}  
Building Age: ${buildingAgeLocal}
Location: ${selectedCity}, ${JURISDICTIONS[selectedState]?.name||selectedState}
Applicable Codes: ${stateCodes}
Local Amendments: ${localAmendments.length>0?localAmendments.join("; "):"Standard state codes apply"}
Code Focus: ${categoryFilter}

EXISTING FLAGS ALREADY NOTED:
${existingFlags}

INSTRUCTIONS:
Review every visible element of the drawing and provide a COMPREHENSIVE code compliance report. Be specific — cite exact CBC/ADA sections, measurements, and clearances.

Format your response as:

## 🔴 CRITICAL VIOLATIONS (Must fix before permit)
For each: CBC/ADA section, what's wrong, required measurement/standard, recommended fix

## 🟡 POTENTIAL ISSUES (Need engineer/inspector review)  
For each: code section, concern, what to verify

## ♿ ADA / ACCESSIBILITY FINDINGS
Specific ADA violations — door widths, turning radii, reach ranges, grab bars, ramp slopes, counter heights

## 🚪 EGRESS & LIFE SAFETY
Exit widths, travel distances, door hardware, fire-rated assemblies, smoke/CO detectors

## ⚡ TITLE 24 ENERGY
Insulation, glazing, HVAC, lighting power density, hot water

## 🌿 CALGREEN MANDATORY MEASURES
Indoor water use, EV charging, waste management, low-VOC materials

## 🏗 SEISMIC (Bay Area SDC D)
Anchor bolts, shear walls, hold-downs, cripple wall bracing if visible

## ✅ WHAT LOOKS COMPLIANT
Elements that appear to meet code

## 📋 PRE-SUBMITTAL CHECKLIST
Top 5 items to resolve before submitting for permit

Be extremely specific. Reference actual dimensions and measurements you can see in the drawing. Flag anything that requires an engineer's wet stamp.`;

    try {
      const resp = await fetch(AI_PROXY_URL, {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01","anthropic-beta":"max-tokens-3-5-sonnet-2024-07-15"},
        body: JSON.stringify({
          model:"claude-haiku-4-5",
          max_tokens:4000,
          stream:true,
          system: `You are a licensed building code expert and ADA compliance specialist. You have deep knowledge of building codes for ${JURISDICTIONS[selectedState]?.name||selectedState}, specifically ${selectedCity} local amendments. Applicable codes: ${stateCodes}. Local amendments include: ${localAmendments.join("; ")||"standard state code"}. Always cite exact code sections and measurements. Be specific about what is required vs. what is a violation.`,
          messages:[{
            role:"user",
            content:[
              (activeBlueprint?.fileType==="pdf"?{type:"document",source:{type:"base64",media_type:"application/pdf",data:(activeBlueprint.dataUrl||"").split(",")[1]}}:{type:"image",source:{type:"base64",media_type:activeBlueprint?.mediaType||"image/jpeg",data:(activeBlueprint.dataUrl||"").split(",")[1]}}),
              {type:"text",text:prompt}
            ]
          }]
        })
      });

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let fullText = "";

      while(true) {
        const {done,value} = await reader.read();
        if(done) break;
        buf += decoder.decode(value,{stream:true});
        const lines = buf.split("\n"); buf = lines.pop()||"";
        for(const line of lines) {
          if(line.startsWith("data: ")) {
            try{
              const p=JSON.parse(line.slice(6));
              const t=p.delta?.text||"";
              if(t){setResults(prev=>prev+t);fullText+=t;}
            }catch(e){}
          }
        }
      }

      // Auto-extract violations and create blueprint flags with ADA details
      const violations = [];
      const now = Date.now();

      // ADA type detection from text
      const detectAdaType = (text) => {
        const t = text.toLowerCase();
        if (t.includes("door") && t.includes("width")) return "door_width";
        if (t.includes("door hardware") || t.includes("lever")) return "door_hardware";
        if (t.includes("ramp") && t.includes("slope")) return "ramp_slope";
        if (t.includes("ramp") && t.includes("width")) return "ramp_width";
        if (t.includes("grab bar")) return "grab_bar";
        if (t.includes("turning") || t.includes("clear floor") || t.includes("turning radius")) return "turning_radius";
        if (t.includes("counter") || t.includes("work surface")) return "counter_height";
        if (t.includes("reach")) return "reach_range";
        if (t.includes("threshold")) return "threshold";
        if (t.includes("restroom") || t.includes("toilet") || t.includes("bathroom")) return "restroom";
        if (t.includes("path of travel") || t.includes("accessible route")) return "path_of_travel";
        if (t.includes("elevator") || t.includes("lift")) return "elevator";
        return "other_code";
      };

      // Extract measurement from text e.g. "28 inches" "32\"" "1:8 slope"
      const extractMeasurement = (text) => {
        const m = text.match(/(\d+(?:\.\d+)?)["\s]*(inch|in\b|")|(\d+:\d+)\s*slope/i);
        return m ? m[0].trim() : "";
      };

      // Extract code reference from text
      const extractCodeRef = (text) => {
        const m = text.match(/(?:ADA|CBC|IBC|CPC|CEC|SFBC)\s*[§#]?\s*[\d.]+[\d.A-Z-]*/gi);
        return m ? m.slice(0,2).join(" · ") : "";
      };

      const ADA_REQUIRED = {
        door_width: '32" min clear',
        ramp_slope: "1:12 max slope",
        grab_bar: '33"–36" AFF',
        turning_radius: '60" diameter',
        counter_height: '34" max AFF',
        reach_range: '15"–48" AFF',
        threshold: '½" max vertical',
        restroom: '60" turning radius',
        path_of_travel: '36" min width',
      };

      // Parse all violation levels
      const sections = [
        {pattern:/🔴[^🟡♿\n]*CRITICAL[^:]*:?([\s\S]*?)(?=🟡|♿|##|$)/i, priority:"critical"},
        {pattern:/🟡[^🔴♿\n]*WARNING[^:]*:?([\s\S]*?)(?=🔴|♿|##|$)/i,  priority:"high"},
        {pattern:/♿[^🔴🟡\n]*ADA[^:]*:?([\s\S]*?)(?=🔴|🟡|##|$)/i,     priority:"high"},
      ];

      let flagIndex = 0;
      sections.forEach(({pattern, priority}) => {
        const match = fullText.match(pattern);
        if (!match) return;
        const lines = match[1].split("\n")
          .map(l => l.replace(/^[-•*\d.]+\s*/, "").trim())
          .filter(l => l.length > 10 && !l.match(/^(##|🔴|🟡|♿)/));

        lines.slice(0, 4).forEach((line, i) => {
          const adaType = detectAdaType(line);
          const codeRef = extractCodeRef(line) || (adaType !== "other_code" ? "ADA §"+adaType : "");
          const measured = extractMeasurement(line);
          const required = ADA_REQUIRED[adaType] || "";

          // Spread pins across the drawing in a grid pattern so they don't overlap
          const cols = 4;
          const col = flagIndex % cols;
          const row = Math.floor(flagIndex / cols);
          const posX = 15 + col * 20 + (Math.random() * 8 - 4);
          const posY = 15 + row * 18 + (Math.random() * 6 - 3);

          violations.push({
            id: "ai_flag_" + now + "_" + flagIndex,
            blueprintId: activeBlueprint?.id || "auto",
            title: line.slice(0, 70),
            desc: line,
            priority,
            trade: "Code Compliance",
            adaType: adaType !== "other_code" ? adaType : "",
            codeRef,
            measured,
            required,
            source: "AI Code Review",
            status: "open",
            autoGenerated: true,
            date: new Date().toLocaleDateString(),
            flaggedBy: "AI Compliance Checker",
            pos: {x: Math.max(5, Math.min(95, posX)), y: Math.max(5, Math.min(90, posY))},
            photos: [],
            comments: [],
          });
          flagIndex++;
        });
      });

      setAutoFlags(violations);

      // Auto-pin to blueprint if callback provided
      if (onAutoFlag && violations.length > 0) {
        onAutoFlag(violations);
      }

    } catch(e) { setResults("Error: "+e.message); }
    setChecking(false);
  };

  // Count severity from results
  const criticalCount = (results.match(/🔴/g)||[]).length;
  const warningCount = (results.match(/🟡/g)||[]).length;
  const adaCount = (results.match(/♿/g)||[]).length - 1; // subtract section header

  if (!showChecker) {
    return (
      <div style={{margin:"12px 0"}}>
        <button onClick={()=>setShowChecker(true)}
          style={{width:"100%",padding:"12px 16px",background:`linear-gradient(135deg,${T.purple}20,${T.blue}15)`,border:`1px solid ${T.purple}40`,borderRadius:10,cursor:"pointer",display:"flex",alignItems:"center",gap:10,transition:"all 0.2s"}}
          onMouseEnter={e=>e.currentTarget.style.background=`linear-gradient(135deg,${T.purple}30,${T.blue}25)`}
          onMouseLeave={e=>e.currentTarget.style.background=`linear-gradient(135deg,${T.purple}20,${T.blue}15)`}>
          <span style={{fontSize:20}}>🏛</span>
          <div style={{textAlign:"left",flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text}}>CA Code & ADA Compliance Checker</div>
            <div style={{fontSize:11,color:T.muted}}>Select state & city · ADA 2010 · Full local code compliance check</div>
          </div>
          <div style={{fontSize:11,color:T.purple,fontWeight:600}}>Open →</div>
        </button>
      </div>
    );
  }

  return (
    <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:12,padding:"16px",margin:"12px 0"}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>🏛 CA Code & ADA Compliance Checker</div>
          <div style={{fontSize:11,color:T.muted}}>CBC 2022 · Title 24 · ADA · CalGreen · Bay Area Seismic</div>
        </div>
        <button onClick={()=>setShowChecker(false)} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:16,padding:"4px"}}>✕</button>
      </div>

      {/* Project settings */}
      {/* State + City */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
        <div>
          <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>State</div>
          <select value={selectedState} onChange={e=>{setSelectedState(e.target.value);const c=Object.keys(JURISDICTIONS[e.target.value]?.cities||{});setSelectedCity(c[0]||"");}}
            style={{width:"100%",background:T.bg2,border:`1px solid ${T.gold}60`,borderRadius:7,padding:"7px 9px",color:T.text,fontSize:11,fontFamily:"inherit",fontWeight:600}}>
            {stateList.map(s=><option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>City / Jurisdiction</div>
          <select value={selectedCity} onChange={e=>setSelectedCity(e.target.value)}
            style={{width:"100%",background:T.bg2,border:`1px solid ${T.gold}60`,borderRadius:7,padding:"7px 9px",color:T.text,fontSize:11,fontFamily:"inherit",fontWeight:600}}>
            {cityList.map(c=><option key={c}>{c}</option>)}
          </select>
        </div>
      </div>
      {localAmendments.length>0&&(
        <div style={{background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:8,padding:"7px 11px",marginBottom:8,display:"flex",gap:4,flexWrap:"wrap",alignItems:"center"}}>
          <span style={{fontSize:9,fontWeight:700,color:T.amber,textTransform:"uppercase",flexShrink:0}}>Local:</span>
          {localAmendments.map((a,i)=><span key={i} style={{fontSize:9,background:"rgba(0,0,0,0.08)",color:T.amber,borderRadius:5,padding:"2px 7px"}}>{a}</span>)}
        </div>
      )}
      {/* Project type, occupancy, age */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
        <div>
          <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Project Type</div>
          <select value={projectTypeLocal} onChange={e=>setProjectTypeLocal(e.target.value)}
            style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"7px 9px",color:T.text,fontSize:11,fontFamily:"inherit"}}>
            {PROJECT_TYPES.map(t=><option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Occupancy Class</div>
          <select value={occupancy} onChange={e=>setOccupancy(e.target.value)}
            style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"7px 9px",color:T.text,fontSize:11,fontFamily:"inherit"}}>
            {OCCUPANCIES.map(o=><option key={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Building Age</div>
          <select value={buildingAgeLocal} onChange={e=>setBuildingAgeLocal(e.target.value)}
            style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"7px 9px",color:T.text,fontSize:11,fontFamily:"inherit"}}>
            {["Pre-1930","1930-1950","1950-1978 (lead/asbestos)","1978-2000","2000-2013","2013-present"].map(a=><option key={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {/* Code category filter */}
      <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:12}}>
        {CODE_CATEGORIES.map(cat=>(
          <button key={cat.id} onClick={()=>setActiveCategory(cat.id)}
            style={{padding:"5px 10px",borderRadius:7,fontSize:10,fontWeight:600,cursor:"pointer",border:`1px solid ${activeCategory===cat.id?T.purple:T.subtle}`,background:activeCategory===cat.id?T.purple+"20":T.bg2,color:activeCategory===cat.id?T.purple:T.muted,transition:"all 0.15s",whiteSpace:"nowrap"}}>
            {cat.icon} {cat.label}
          </button>
        ))}
      </div>

      {!activeBlueprint ? (
        <div style={{background:T.amberBg,borderRadius:8,padding:"12px 14px",fontSize:12,color:T.amber,textAlign:"center"}}>
          ↑ Upload a blueprint above to run code compliance check
        </div>
      ) : (
        <button onClick={runCodeCheck} disabled={checking}
          style={{width:"100%",padding:"13px",background:checking?T.bg3:`linear-gradient(135deg,${T.purple},${T.blue})`,border:"none",borderRadius:10,cursor:checking?"not-allowed":"pointer",color:"white",fontSize:14,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",gap:10,marginBottom:12,transition:"all 0.2s"}}>
          {checking ? (
            <><Spin/>Analyzing blueprint against {CODE_CATEGORIES.find(c=>c.id===activeCategory)?.label||"all codes"}...</>
          ) : (
            <><span style={{fontSize:16}}>🏛</span>Run {activeCategory==="all"?"Full":""}Code Compliance Check{activeCategory!=="all"?" — "+CODE_CATEGORIES.find(c=>c.id===activeCategory)?.label:""}</>
          )}
        </button>
      )}

      {/* Results summary badges */}
      {results && (
        <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
          {criticalCount>0&&<div style={{background:T.redBg,border:`1px solid ${T.red}30`,borderRadius:20,padding:"4px 12px",fontSize:11,color:T.red,fontWeight:700}}>🔴 {criticalCount} Critical</div>}
          {warningCount>0&&<div style={{background:T.amberBg,border:`1px solid ${T.amber}30`,borderRadius:20,padding:"4px 12px",fontSize:11,color:T.amber,fontWeight:700}}>🟡 {warningCount} Warnings</div>}
          {adaCount>0&&<div style={{background:T.blueBg,border:`1px solid ${T.blue}30`,borderRadius:20,padding:"4px 12px",fontSize:11,color:T.blue,fontWeight:700}}>♿ ADA Issues</div>}
          {autoFlags.length>0&&<div style={{background:T.purpleBg,border:`1px solid ${T.purple}30`,borderRadius:20,padding:"4px 12px",fontSize:11,color:T.purple,fontWeight:700}}>📍 {autoFlags.length} Auto-Flagged</div>}
          {results&&<button onClick={()=>navigator.clipboard.writeText(results)} style={{background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:20,padding:"4px 12px",fontSize:11,color:T.muted,cursor:"pointer",fontWeight:600}}>Copy Report</button>}
        </div>
      )}

      {/* Auto-flagged items */}
      {autoFlags.length>0&&(
        <div style={{background:T.purpleBg,border:`1px solid ${T.purple}30`,borderRadius:9,padding:"10px 14px",marginBottom:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{fontSize:11,fontWeight:700,color:T.purple}}>📍 {autoFlags.length} violations auto-pinned to drawing</div>
            <span style={{fontSize:9,background:T.green,color:"white",borderRadius:20,padding:"2px 8px",fontWeight:700}}>✓ Saved</span>
          </div>
          {autoFlags.map(f=>(
            <div key={f.id} style={{fontSize:11,color:T.text,display:"flex",alignItems:"flex-start",gap:8,marginBottom:6,background:"rgba(0,0,0,0.06)",borderRadius:7,padding:"6px 9px"}}>
              <span style={{color:f.priority==="critical"?T.red:T.amber,flexShrink:0,marginTop:1}}>●</span>
              <div style={{flex:1}}>
                <div style={{fontWeight:600,marginBottom:2}}>{f.title.slice(0,60)}{f.title.length>60?"…":""}</div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {f.adaType&&<span style={{fontSize:9,color:T.blue}}>♿ {f.adaType.replace(/_/g," ")}</span>}
                  {f.codeRef&&<span style={{fontSize:9,color:T.muted,fontFamily:"monospace"}}>{f.codeRef}</span>}
                  {f.measured&&<span style={{fontSize:9,color:T.red}}>Measured: {f.measured}</span>}
                  {f.required&&<span style={{fontSize:9,color:T.green}}>Required: {f.required}</span>}
                </div>
              </div>
              <span style={{fontSize:9,color:T.muted,flexShrink:0}}>📍{f.pos?.x?.toFixed(0)}%,{f.pos?.y?.toFixed(0)}%</span>
            </div>
          ))}
          <div style={{fontSize:10,color:T.purple,marginTop:6,opacity:0.8}}>
            Scroll up to see pins on the drawing. Click any pin to view details and mark resolved.
          </div>
        </div>
      )}

      {/* Results output */}
      {(results||checking)&&(
        <div style={{background:T.bg0,borderRadius:9,padding:"14px 16px",fontSize:12,color:T.text,lineHeight:1.9,whiteSpace:"pre-wrap",maxHeight:500,overflowY:"auto",fontFamily:"inherit",borderLeft:`3px solid ${T.purple}`}}>
          {results||<div style={{display:"flex",alignItems:"center",gap:10,color:T.muted}}><Spin/>Reading blueprint and checking against CA codes...</div>}
        </div>
      )}

      {/* Code reference quick guide */}
      {!results&&!checking&&(
        <div style={{marginTop:4}}>
          <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>What gets checked:</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
            {[
              {icon:"♿",l:"ADA door widths (32\" clear min), turning radii (60\"), grab bars, ramp slopes (1:12 max)"},
              {icon:"🚪",l:"CBC egress widths, travel distances, door hardware, fire ratings"},
              {icon:"⚡",l:"Title 24 insulation R-values, glazing SHGC, lighting power density"},
              {icon:"🌿",l:"CalGreen indoor water use, EV ready, low-VOC materials, waste diversion"},
              {icon:"🏗",l:"Bay Area seismic SDC D — anchor bolts, shear walls, hold-downs"},
              {icon:"🏠",l:"SF SRO minimums (150 sqft), CBC 11A/11B housing accessibility"},
            ].map((item,i)=>(
              <div key={i} style={{display:"flex",gap:7,fontSize:10,color:T.muted,lineHeight:1.5,background:T.bg2,borderRadius:7,padding:"7px 9px"}}>
                <span style={{flexShrink:0}}>{item.icon}</span><span>{item.l}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── BLUEPRINT PRODUCT SEARCH (click blueprint to place pin + search) ──────────
const PRODUCT_CATEGORIES = [
  {id:"auto",      label:"Auto-detect from click",  icon:"🎯"},
  {id:"plumbing",  label:"Plumbing Fixtures",        icon:"🚿"},
  {id:"electrical",label:"Electrical / Lighting",    icon:"💡"},
  {id:"hvac",      label:"HVAC / Ventilation",       icon:"🌡"},
  {id:"appliances",label:"Appliances",               icon:"🍳"},
  {id:"doors",     label:"Doors & Hardware",         icon:"🚪"},
  {id:"flooring",  label:"Flooring",                 icon:"🏠"},
  {id:"tile",      label:"Tile & Stone",             icon:"🔷"},
  {id:"cabinets",  label:"Cabinets & Countertops",   icon:"🗄"},
  {id:"windows",   label:"Windows & Glazing",        icon:"🪟"},
  {id:"fire",      label:"Fire Safety / Sprinklers", icon:"🧯"},
  {id:"accessibility",label:"ADA / Accessibility",  icon:"♿"},
];

function BlueprintProductSearch({proj, activeBlueprint, session}) {
  const key = "grok_bp_products_" + proj?.id;
  const [pins, setPins]           = useState(()=>{try{return JSON.parse(localStorage.getItem(key)||"[]");}catch{return [];}});
  const [placingPin, setPlacingPin] = useState(false);
  const [category, setCategory]   = useState("auto");
  const [searching, setSearching] = useState(false);
  const [activePin, setActivePin] = useState(null);
  const [results, setResults]     = useState("");
  const [freeSearch, setFreeSearch] = useState("");
  const [showCOForm, setShowCOForm] = useState(false);
  const [coForm, setCoForm] = useState({selectedProduct:"",quantity:"1",unit_cost:"",spec_section:"",description:""});
  const imgRef = useRef();
  const apiKey = "proxy"; // Using Supabase AI proxy

  const savePins = (updated) => { setPins(updated); try{localStorage.setItem(key,JSON.stringify(updated));}catch(e){} };

  const handleBlueprintClick = async (e) => {
    if (!placingPin || !activeBlueprint) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width * 100).toFixed(1);
    const y = ((e.clientY - rect.top)  / rect.height * 100).toFixed(1);

    const newPin = {
      id: "pp_" + Date.now(),
      x: parseFloat(x), y: parseFloat(y),
      category,
      label: category === "auto" ? "Searching..." : (PRODUCT_CATEGORIES.find(c=>c.id===category)?.label||"Product"),
      results: "",
      searching: true,
    };
    const updatedPins = [...pins, newPin];
    savePins(updatedPins);
    setPlacingPin(false);
    setActivePin(newPin.id);
    setResults("");

    // Auto-detect or search based on category + blueprint image
    if (true) {
      setSearching(true);
      try {
        const prompt = category === "auto"
          ? `I clicked on a point at position (${x}%, ${y}%) of this construction blueprint. What type of product/fixture/equipment is typically found at this area? Then recommend 4-5 qualifying products for that type with: manufacturer, model, specs, price range, ADA compliance if applicable, CalGreen compliance, and where to source in the Bay Area (Home Depot Pro, Ferguson, Grainger, etc). Project type: ${proj?.type||"renovation"}.`
          : `Recommend 4-5 qualifying ${PRODUCT_CATEGORIES.find(c=>c.id===category)?.label} products for this ${proj?.type||"renovation"} project. For each: manufacturer, model, key specs, price range (Bay Area 2025/2026), ADA compliant (yes/no), CalGreen compliant (yes/no), local supplier and lead time. Focus on what a Bay Area GC would actually spec.`;

        const content_parts = [{
          type:"image",
          source:{type:"base64",media_type:activeBlueprint?.mediaType||"image/jpeg",data:(activeBlueprint?.dataUrl||"").split(",")[1]}
        },{type:"text",text:prompt}];

        const resp = await fetch(AI_PROXY_URL,{
          method:"POST",
          headers:{"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
          body:JSON.stringify({model:"claude-haiku-4-5",max_tokens:1500,messages:[{role:"user",content:content_parts}]})
        });
        const data = await resp.json();
        const text = data.content?.[0]?.text||"No results";

        const label = category==="auto"
          ? (text.match(/^[^\n]+/)?.[0]?.slice(0,40)||"Product")
          : (PRODUCT_CATEGORIES.find(c=>c.id===category)?.label||"Product");

        savePins(updatedPins.map(p=>p.id===newPin.id?{...p,label,results:text,searching:false}:p));
        setResults(text);
      } catch(e) {
        savePins(updatedPins.map(p=>p.id===newPin.id?{...p,label:"Error",results:"Search failed: "+e.message,searching:false}:p));
        setResults("Error: "+e.message);
      }
      setSearching(false);
    }
  };

  const searchFree = async () => {
    if (!freeSearch.trim()) return;
    setSearching(true); setResults("");
    try {
      const parts = activeBlueprint ? [
        (activeBlueprint?.fileType==="pdf"?{type:"document",source:{type:"base64",media_type:"application/pdf",data:(activeBlueprint.dataUrl||"").split(",")[1]}}:{type:"image",source:{type:"base64",media_type:activeBlueprint?.mediaType||"image/jpeg",data:(activeBlueprint.dataUrl||"").split(",")[1]}}),
        {type:"text",text:`Search for: "${freeSearch}" for this ${proj?.type||"renovation"} project. Recommend 4-5 products with manufacturer, model, specs, Bay Area price, ADA/CalGreen compliance, local supplier.`}
      ] : [{type:"text",text:`Recommend 4-5 "${freeSearch}" products for a ${proj?.type||"renovation"} in Bay Area. Include manufacturer, model, specs, price, ADA/CalGreen compliance, local supplier.`}];

      const resp = await fetch(AI_PROXY_URL,{
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
        body:JSON.stringify({model:"claude-haiku-4-5",max_tokens:1500,messages:[{role:"user",content:parts}]})
      });
      const data = await resp.json();
      setResults(data.content?.[0]?.text||"No results");
    } catch(e) { setResults("Error: "+e.message); }
    setSearching(false);
  };

  const CAT_ICONS = Object.fromEntries(PRODUCT_CATEGORIES.map(c=>[c.id,c.icon]));

  return (
    <div>
      {/* Instructions */}
      <div style={{background:T.purpleBg,border:"1px solid "+T.purple+"30",borderRadius:9,padding:"10px 14px",marginBottom:12,fontSize:11,color:T.muted,lineHeight:1.7}}>
        <strong style={{color:T.purple}}>Click + Search:</strong> Select a category below, then click <strong style={{color:T.text}}>📍 Pin & Search</strong> and click anywhere on the blueprint to auto-search for products at that location. Or use free search below.
      </div>

      {/* Category selector */}
      <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:10}}>
        {PRODUCT_CATEGORIES.map(cat=>(
          <button key={cat.id} onClick={()=>setCategory(cat.id)}
            style={{fontSize:10,fontWeight:600,padding:"4px 9px",borderRadius:6,border:"1px solid "+(category===cat.id?T.purple:T.subtle),background:category===cat.id?T.purpleBg:T.bg2,color:category===cat.id?T.purple:T.muted,cursor:"pointer",whiteSpace:"nowrap"}}>
            {cat.icon} {cat.label}
          </button>
        ))}
      </div>

      {/* Actions */}
      <div style={{display:"flex",gap:8,marginBottom:12,alignItems:"center"}}>
        {activeBlueprint&&(
          <button onClick={()=>setPlacingPin(p=>!p)}
            style={{fontSize:12,fontWeight:700,padding:"8px 16px",borderRadius:8,border:"1px solid "+(placingPin?T.red:T.purple),background:placingPin?T.redBg:T.purple,color:placingPin?T.red:"white",cursor:"pointer",transition:"all 0.15s",flexShrink:0}}>
            {placingPin?"🎯 Click blueprint to pin...":"📍 Pin & Search on Blueprint"}
          </button>
        )}
        <input value={freeSearch} onChange={e=>setFreeSearch(e.target.value)} onKeyDown={e=>e.key==="Enter"&&searchFree()}
          placeholder={`Search ${PRODUCT_CATEGORIES.find(c=>c.id===category)?.label||"products"}...`}
          style={{flex:1,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"8px 12px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
        <Btn primary small onClick={searchFree} disabled={searching||!freeSearch.trim()}>
          {searching?<Spin/>:"🔍"}
        </Btn>
      </div>

      {/* Blueprint with pins */}
      {activeBlueprint&&(
        <div style={{position:"relative",display:"inline-block",width:"100%",cursor:placingPin?"crosshair":"default",borderRadius:10,overflow:"hidden",border:"1px solid "+T.subtle,marginBottom:12}}
          onClick={handleBlueprintClick}>
          <img ref={imgRef} src={activeBlueprint.dataUrl} alt="Blueprint"
            style={{width:"100%",display:"block",maxHeight:350,objectFit:"contain",background:T.bg3}}/>
          {/* Product pins */}
          {pins.map(pin=>(
            <div key={pin.id}
              onClick={e=>{e.stopPropagation();setActivePin(pin.id===activePin?null:pin.id);setResults(pin.results||"");}}
              style={{position:"absolute",left:pin.x+"%",top:pin.y+"%",transform:"translate(-50%,-100%)",cursor:"pointer",zIndex:10}}>
              <div style={{background:activePin===pin.id?T.purple:T.gold,color:"white",width:24,height:24,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,border:"2px solid white",boxShadow:"0 2px 8px rgba(0,0,0,0.3)",transition:"all 0.15s"}}>
                {pin.searching?<Spin/>:(CAT_ICONS[pin.category]||"🔍")}
              </div>
              <div style={{width:2,height:8,background:activePin===pin.id?T.purple:T.gold,margin:"0 auto"}}/>
            </div>
          ))}
        </div>
      )}

      {/* Pin list + results */}
      {pins.length>0&&(
        <div style={{marginBottom:12}}>
          <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Pinned Locations ({pins.length})</div>
          <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
            {pins.map(pin=>(
              <button key={pin.id}
                onClick={()=>{setActivePin(pin.id===activePin?null:pin.id);setResults(pin.results||"");}}
                style={{fontSize:10,fontWeight:600,padding:"4px 10px",borderRadius:6,border:"1px solid "+(activePin===pin.id?T.purple:T.subtle),background:activePin===pin.id?T.purpleBg:T.bg2,color:activePin===pin.id?T.purple:T.muted,cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
                {CAT_ICONS[pin.category]||"📍"} {pin.label.slice(0,25)}
                <span onClick={e=>{e.stopPropagation();savePins(pins.filter(p=>p.id!==pin.id));if(activePin===pin.id){setActivePin(null);setResults("");}}}
                  style={{color:T.muted,marginLeft:2,fontSize:9}}>✕</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {(results||searching)&&(
        <div style={{background:T.bg2,borderRadius:9,padding:"14px",fontSize:12,color:T.text,lineHeight:1.9,whiteSpace:"pre-wrap",maxHeight:450,overflowY:"auto",borderLeft:"3px solid "+T.purple}}>
          {searching
            ?<div style={{display:"flex",gap:10,color:T.muted,alignItems:"center"}}><Spin/>Searching for products...</div>
            :results}
        </div>
      )}

      {/* Product selected — create CO or submittal */}
      {activePin&&results&&!searching&&(
        <div style={{background:T.bg1,border:"1px solid "+T.teal+"40",borderRadius:10,padding:"14px 16px",marginTop:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{fontSize:12,fontWeight:700,color:T.text}}>Select & Add to Project</div>
            <button onClick={()=>setShowCOForm(p=>!p)}
              style={{fontSize:11,fontWeight:700,background:showCOForm?T.bg3:T.teal,border:"none",borderRadius:7,padding:"5px 12px",cursor:"pointer",color:showCOForm?T.muted:"white"}}>
              {showCOForm?"Cancel":"+ Create CO / Submittal"}
            </button>
          </div>
          {showCOForm&&(
            <div style={{background:T.bg2,borderRadius:9,padding:"12px"}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                {[["selectedProduct","Product Selected","Kohler ADA Toilet K-3999"],["quantity","Qty","1"],["unit_cost","Unit Cost $","485"],["spec_section","Spec Section","15430"]].map(([k,l,ph])=>(
                  <div key={k}>
                    <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>{l}</div>
                    <input value={coForm[k]||""} onChange={e=>setCoForm(p=>({...p,[k]:e.target.value}))} placeholder={ph}
                      style={{width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"6px 8px",color:T.text,fontSize:11,fontFamily:"inherit"}}/>
                  </div>
                ))}
              </div>
              <div style={{marginBottom:10}}>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Description for Owner</div>
                <textarea value={coForm.description} onChange={e=>setCoForm(p=>({...p,description:e.target.value}))}
                  placeholder="Replace existing fixture with selected product. ADA compliant. Requires owner approval." rows={2}
                  style={{width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"6px 8px",color:T.text,fontSize:11,fontFamily:"inherit",resize:"none"}}/>
              </div>
              <div style={{display:"flex",gap:7}}>
                <button onClick={()=>{
                  const coKey="grok_product_cos_"+proj?.id;
                  const pin=pins.find(p=>p.id===activePin)||{};
                  const item={...coForm,id:"pco_"+Date.now(),pin_category:pin.category,pin_label:pin.label,date:new Date().toLocaleDateString(),status:"pending_approval",project:proj?.name};
                  try{const ex=JSON.parse(localStorage.getItem(coKey)||"[]");localStorage.setItem(coKey,JSON.stringify([...ex,item]));}catch(e){}
                  alert("Product CO saved — viewable in Admin → COs");
                  setShowCOForm(false);
                  setCoForm({selectedProduct:"",quantity:"1",unit_cost:"",spec_section:"",description:""});
                }}
                  style={{flex:2,padding:"9px",background:T.teal,color:"white",border:"none",borderRadius:8,cursor:"pointer",fontSize:11,fontWeight:700}}>
                  ✓ Create CO for Owner Approval
                </button>
                <button onClick={()=>{
                  const subKey="grok_materio_"+proj?.id;
                  const sub={id:"sub_"+Date.now(),product:coForm.selectedProduct,description:coForm.description,spec_section:coForm.spec_section,submitted_to:"Architect",status:"draft",date_submitted:new Date().toLocaleDateString()};
                  try{const ex=JSON.parse(localStorage.getItem(subKey)||"[]");localStorage.setItem(subKey,JSON.stringify([...ex,sub]));}catch(e){}
                  alert("Submittal created");
                  setShowCOForm(false);
                }}
                  style={{flex:1,padding:"9px",background:T.purple,color:"white",border:"none",borderRadius:8,cursor:"pointer",fontSize:11,fontWeight:700}}>
                  📋 Submittal
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {!activeBlueprint&&!results&&(
        <div style={{textAlign:"center",padding:"32px",color:T.muted,background:T.bg2,borderRadius:10,border:"2px dashed "+T.subtle}}>
          <div style={{fontSize:32,opacity:0.3,marginBottom:8}}>📐</div>
          <div style={{fontSize:12}}>Upload a blueprint in the Review tab first, then come here to pin and search for products.</div>
        </div>
      )}
    </div>
  );
}


// ─── SPECKLE / GUILD.AI 3D VIEWER ─────────────────────────────────────────────
// Embeds a 3D model viewer supporting Speckle, IFC, and basic 3D file previews
// Guild.ai (formerly SpeckleSystems) provides free IFC/BIM web viewer via iframe
function SpeckleViewer({proj, blueprints}) {
  const key = "grok_3d_links_" + proj?.id;
  const [links, setLinks] = useState(()=>{try{return JSON.parse(localStorage.getItem(key)||"[]");}catch{return [];}});
  const [activeLink, setActiveLink] = useState(links[0]||null);
  const [addMode, setAddMode] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newLabel, setNewLabel] = useState("");

  const saveLinks = (updated) => {
    setLinks(updated);
    try{localStorage.setItem(key,JSON.stringify(updated));}catch(e){}
  };

  const addLink = () => {
    if (!newUrl.trim()) return;
    const link = {id:"3d_"+Date.now(), url:normalizeUrl(newUrl), label:newLabel||"3D Model", added:new Date().toLocaleDateString()};
    const updated = [...links, link];
    saveLinks(updated);
    setActiveLink(link);
    setNewUrl(""); setNewLabel(""); setAddMode(false);
  };

  const normalizeUrl = (url) => {
    // Support Speckle share links → convert to embed
    if (url.includes("app.speckle.systems") && !url.includes("/embed")) {
      return url.replace("app.speckle.systems/projects", "app.speckle.systems/projects") + (url.includes("?") ? "&embed=true" : "?embed=true");
    }
    return url;
  };

  return (
    <div>
      <div style={{background:T.purpleBg,border:"1px solid "+T.purple+"30",borderRadius:10,padding:"12px 16px",marginBottom:12}}>
        <div style={{fontSize:12,fontWeight:700,color:T.purple,marginBottom:4}}>🏗 3D Blueprint Viewer</div>
        <div style={{fontSize:11,color:T.muted,lineHeight:1.7,marginBottom:8}}>
          Embed 3D models from <strong style={{color:T.text}}>Speckle</strong> (app.speckle.systems), <strong style={{color:T.text}}>Autodesk Viewer</strong>, or any IFC web viewer. Paste the share/embed link below.
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <a href="https://app.speckle.systems" target="_blank" rel="noreferrer"
            style={{fontSize:10,fontWeight:700,background:T.purple,color:"white",borderRadius:6,padding:"4px 10px",textDecoration:"none"}}>
            Open Speckle →
          </a>
          <a href="https://viewer.autodesk.com" target="_blank" rel="noreferrer"
            style={{fontSize:10,fontWeight:700,background:T.bg2,color:T.muted,borderRadius:6,padding:"4px 10px",textDecoration:"none",border:"1px solid "+T.subtle}}>
            Autodesk Viewer →
          </a>
          <a href="https://xeokit.io/demo.html" target="_blank" rel="noreferrer"
            style={{fontSize:10,fontWeight:700,background:T.bg2,color:T.muted,borderRadius:6,padding:"4px 10px",textDecoration:"none",border:"1px solid "+T.subtle}}>
            xeokit IFC →
          </a>
        </div>
      </div>

      {/* Model selector */}
      <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap",alignItems:"center"}}>
        {links.map(link=>(
          <button key={link.id} onClick={()=>setActiveLink(link)}
            style={{fontSize:11,fontWeight:600,padding:"5px 12px",borderRadius:7,border:"1px solid "+(activeLink?.id===link.id?T.purple:T.subtle),background:activeLink?.id===link.id?T.purpleBg:T.bg2,color:activeLink?.id===link.id?T.purple:T.muted,cursor:"pointer",display:"flex",gap:5,alignItems:"center"}}>
            🏗 {link.label}
            <span onClick={e=>{e.stopPropagation();const u=links.filter(l=>l.id!==link.id);saveLinks(u);if(activeLink?.id===link.id)setActiveLink(u[0]||null);}}
              style={{color:T.muted,fontSize:9}}>✕</span>
          </button>
        ))}
        <button onClick={()=>setAddMode(p=>!p)}
          style={{fontSize:11,fontWeight:600,padding:"5px 12px",borderRadius:7,border:"1px dashed "+T.subtle,background:T.bg2,color:T.muted,cursor:"pointer"}}>
          + Add 3D Link
        </button>
      </div>

      {/* Add link form */}
      {addMode&&(
        <div style={{background:T.bg2,borderRadius:9,padding:"12px",marginBottom:10}}>
          <div style={{marginBottom:8}}>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>3D Model URL or Embed Link</div>
            <input value={newUrl} onChange={e=>setNewUrl(e.target.value)} placeholder="https://app.speckle.systems/projects/... or any IFC viewer URL"
              style={{width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
          </div>
          <div style={{display:"flex",gap:8}}>
            <input value={newLabel} onChange={e=>setNewLabel(e.target.value)} placeholder="Label (e.g. Floor Plan 3D)"
              style={{flex:1,background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
            <button onClick={addLink} disabled={!newUrl.trim()}
              style={{fontSize:11,fontWeight:700,background:T.purple,color:"white",border:"none",borderRadius:7,padding:"7px 14px",cursor:"pointer"}}>
              Add
            </button>
          </div>
        </div>
      )}

      {/* 3D Viewer iframe */}
      {activeLink ? (
        <div style={{borderRadius:10,overflow:"hidden",border:"1px solid "+T.subtle,background:T.bg3}}>
          <div style={{background:T.bg2,padding:"8px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:"1px solid "+T.subtle}}>
            <span style={{fontSize:11,fontWeight:600,color:T.text}}>🏗 {activeLink.label}</span>
            <a href={activeLink.url} target="_blank" rel="noreferrer"
              style={{fontSize:10,color:T.blue,textDecoration:"none"}}>Open full screen ↗</a>
          </div>
          <iframe
            src={activeLink.url}
            style={{width:"100%",height:500,border:"none",display:"block"}}
            allow="fullscreen; camera; microphone"
            title="3D Model Viewer"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        </div>
      ) : (
        <div style={{textAlign:"center",padding:"60px 20px",background:T.bg2,borderRadius:10,border:"2px dashed "+T.subtle}}>
          <div style={{fontSize:36,opacity:0.3,marginBottom:10}}>🏗</div>
          <div style={{fontSize:13,fontWeight:600,color:T.text,marginBottom:8}}>No 3D model linked yet</div>
          <div style={{fontSize:11,color:T.muted,lineHeight:1.7,maxWidth:400,margin:"0 auto 16px"}}>
            Upload your IFC or BIM model to <strong>Speckle</strong> (free), then paste the share link above.<br/>
            Supports Revit, Archicad, AutoCAD, SketchUp via Speckle connectors.
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}}>
            <a href="https://speckle.guide" target="_blank" rel="noreferrer"
              style={{fontSize:11,fontWeight:700,background:T.purple,color:"white",borderRadius:7,padding:"6px 14px",textDecoration:"none"}}>
              How to use Speckle →
            </a>
            <button onClick={()=>setAddMode(true)}
              style={{fontSize:11,fontWeight:600,background:T.bg1,color:T.muted,border:"1px solid "+T.subtle,borderRadius:7,padding:"6px 14px",cursor:"pointer"}}>
              + Add Model Link
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// Renders a PDF blueprint via a Blob URL. Chrome's PDF viewer blanks out on
// large base64 data: URLs (the format we persist to localStorage for AI calls).
// Converting to a transient blob URL fixes the white-iframe bug. The blob URL
// is revoked when the dataUrl changes or the component unmounts.
// Renders a PDF blueprint as a canvas via pdfjs-dist, one page at a time.
// Replaces the previous iframe-based PdfFrame so that mouse clicks land on the
// canvas (flag placement works) and pages can be navigated programmatically.
// Lazy-loads pdfjs-dist on first render so the ~280KB worker only ships when
// a PDF is actually opened.
//
// Props:
//   dataUrl       — required, base64 data URL of the PDF
//   name          — display name (for error fallback download link)
//   zoom          — multiplier on top of fit-to-width scale (default 1)
//   currentPage   — controlled page number (1-indexed)
//   onPageChange  — fires when page changes (including initial load)
//   onPagesLoaded — fires once with the total page count when the doc loads
//   onCanvasClick — fires on canvas click with {x:0-100, y:0-100, page}
//   placing       — when truthy, cursor switches to crosshair
//   maxHeight     — outer container max-height (for fit-to-window behavior)
//   children      — overlay rendered on top of the canvas in the same coord system
// Small thumbnail render of a PDF's first page — used in the drawings left
// rail. Lazy-imports pdfjs-dist (which is already chunked separately by
// PdfCanvas). Caches the rendered canvas data URL on the bp object so we
// don't re-render thumbs as the user clicks around. Image blueprints skip
// this entirely — caller renders an <img> directly.
function PdfThumbnail({dataUrl, width = 140, height = 100}) {
  const canvasRef = React.useRef(null);
  const [ready, setReady] = React.useState(false);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    if (!dataUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const pdfjs = await import("pdfjs-dist/legacy/build/pdf");
        pdfjs.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js";
        const commaIdx = dataUrl.indexOf(",");
        const b64 = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;
        const bin = atob(b64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const doc = await pdfjs.getDocument({data: bytes}).promise;
        if (cancelled) return;
        const page = await doc.getPage(1);
        if (cancelled) return;
        const base = page.getViewport({scale: 1});
        const scale = Math.min(width / base.width, height / base.height);
        const vp = page.getViewport({scale});
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = vp.width;
        canvas.height = vp.height;
        await page.render({canvasContext: canvas.getContext("2d"), viewport: vp}).promise;
        if (!cancelled) setReady(true);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [dataUrl, width, height]);

  return (
    <div style={{width, height, background:"#0f1623", borderRadius:6, display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden"}}>
      {error ? <span style={{fontSize:18,opacity:0.5}}>📄</span> :
       !ready ? <span style={{fontSize:9,color:"#64748b"}}>…</span> :
       null}
      <canvas ref={canvasRef} style={{display:ready?"block":"none",maxWidth:"100%",maxHeight:"100%"}}/>
    </div>
  );
}

function PdfCanvas({dataUrl, name, zoom, onZoomChange, currentPage, onPageChange, onPagesLoaded, onCanvasClick, placing, maxHeight, children, captureRef}) {
  const canvasRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const [pdfDoc, setPdfDoc] = React.useState(null);
  const [pageCount, setPageCount] = React.useState(0);
  const [rendering, setRendering] = React.useState(false);
  const [error, setError] = React.useState("");
  const [pageDims, setPageDims] = React.useState({width: 0, height: 0});
  const [containerWidth, setContainerWidth] = React.useState(800);

  const activePage = Math.max(1, Math.min(currentPage || 1, Math.max(pageCount, 1)));

  // Lazy-load pdfjs + parse the document
  React.useEffect(() => {
    if (!dataUrl) { setPdfDoc(null); setPageCount(0); return; }
    let cancelled = false;
    setError("");
    setPdfDoc(null);
    (async () => {
      try {
        const pdfjs = await import("pdfjs-dist/legacy/build/pdf");
        pdfjs.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js";
        const commaIdx = dataUrl.indexOf(",");
        const b64 = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;
        const bin = atob(b64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const loadingTask = pdfjs.getDocument({data: bytes});
        const doc = await loadingTask.promise;
        if (cancelled) return;
        setPdfDoc(doc);
        setPageCount(doc.numPages);
        if (onPagesLoaded) onPagesLoaded(doc.numPages);
        if (onPageChange && (!currentPage || currentPage > doc.numPages)) onPageChange(1);
      } catch (e) {
        if (!cancelled) setError(e?.message || "PDF load failed");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUrl]);

  // Track container dimensions so we can recompute fit-to-window scale on resize.
  // ResizeObserver isn't on older mobile Safari — fall back to a window resize
  // listener so the canvas still renders (just won't react to internal layout
  // shifts).
  const [containerHeight, setContainerHeight] = React.useState(600);
  React.useEffect(() => {
    if (!containerRef.current) return;
    const update = () => {
      try {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.clientWidth || 800);
          setContainerHeight(containerRef.current.clientHeight || 600);
        }
      } catch {}
    };
    update();
    if (typeof ResizeObserver !== "undefined") {
      try {
        const ro = new ResizeObserver(update);
        ro.observe(containerRef.current);
        return () => { try { ro.disconnect(); } catch {} };
      } catch {}
    }
    // Fallback for browsers without ResizeObserver — listen for window resize.
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [pdfDoc]);

  // Render the active page when doc/page/zoom/dimensions change.
  // Default scale = fit-to-window (whole page visible without scrolling).
  // User zoom multiplier applies on top — they can zoom in beyond fit.
  React.useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;
    let cancelled = false;
    let renderTask = null;
    setRendering(true);
    (async () => {
      try {
        const page = await pdfDoc.getPage(activePage);
        if (cancelled) return;
        const baseViewport = page.getViewport({scale: 1});
        const padding = 24; // breathing room inside the scroll container
        const fitW = ((containerWidth || 800) - padding) / baseViewport.width;
        const fitH = ((containerHeight || 600) - padding) / baseViewport.height;
        const fitScale = Math.min(fitW, fitH); // fit whole page in viewport
        const finalScale = Math.max(0.25, fitScale * (zoom || 1));
        const viewport = page.getViewport({scale: finalScale});
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = viewport.width + "px";
        canvas.style.height = viewport.height + "px";
        renderTask = page.render({canvasContext: ctx, viewport});
        await renderTask.promise;
        if (!cancelled) {
          setPageDims({width: viewport.width, height: viewport.height});
          setRendering(false);
        }
      } catch (e) {
        if (!cancelled && e?.name !== "RenderingCancelledException") {
          setError(e?.message || "Page render failed");
          setRendering(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (renderTask) try { renderTask.cancel(); } catch {}
    };
  }, [pdfDoc, activePage, zoom, containerWidth, containerHeight]);

  // Expose an imperative capture: report the currently-visible slice of the
  // rendered page as fractions [0..1] of the page, so the parent can render just
  // that zoomed region at high resolution for AI analysis. Reads live DOM rects
  // at call time (fresh scroll position); only `activePage` is closed over.
  React.useEffect(() => {
    if (!captureRef) return;
    captureRef.current = () => {
      const canvas = canvasRef.current, container = containerRef.current;
      if (!canvas || !container) return null;
      const cr = canvas.getBoundingClientRect();
      const kr = container.getBoundingClientRect();
      if (cr.width <= 0 || cr.height <= 0) return null;
      const ix0 = Math.max(cr.left, kr.left), iy0 = Math.max(cr.top, kr.top);
      const ix1 = Math.min(cr.right, kr.right), iy1 = Math.min(cr.bottom, kr.bottom);
      if (ix1 <= ix0 || iy1 <= iy0) return null; // page scrolled fully out of view
      return {
        page: activePage,
        frac: {
          x: (ix0 - cr.left) / cr.width,
          y: (iy0 - cr.top) / cr.height,
          w: (ix1 - ix0) / cr.width,
          h: (iy1 - iy0) / cr.height,
        },
      };
    };
    return () => { if (captureRef) captureRef.current = null; };
  }, [captureRef, activePage, pdfDoc]);

  const handleClick = (e) => {
    if (!onCanvasClick) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    onCanvasClick({x: Math.max(1, Math.min(99, x)), y: Math.max(1, Math.min(99, y)), page: activePage});
  };

  if (error) {
    return (
      <div style={{padding:40,textAlign:"center",color:"#94a3b8",fontSize:12,background:"#0f1623"}}>
        <div style={{fontSize:22,marginBottom:8}}>📄</div>
        <div>Couldn't render this PDF — {error}</div>
        {dataUrl && <a href={dataUrl} download={(name||"blueprint")+".pdf"} style={{fontSize:11,color:"#d4a017",display:"inline-block",marginTop:10}}>Download to view</a>}
      </div>
    );
  }

  const goToPage = (n) => {
    if (!onPageChange) return;
    const next = Math.max(1, Math.min(n, pageCount));
    if (next !== activePage) onPageChange(next);
  };

  return (
    <div style={{position:"relative",width:"100%",background:"#0f1623",borderRadius:8,overflow:"hidden"}}>
      {pageCount > 1 && (
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"7px 10px",background:"rgba(0,0,0,0.4)",borderBottom:"1px solid rgba(255,255,255,0.06)",fontSize:11,color:"#e2e8f0"}}>
          <button onClick={()=>goToPage(activePage-1)} disabled={activePage<=1}
            style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.1)",color:activePage<=1?"#64748b":"#e2e8f0",borderRadius:6,padding:"3px 10px",cursor:activePage<=1?"default":"pointer",fontSize:12,fontWeight:700}}>‹ Prev</button>
          <span style={{fontWeight:700,minWidth:90,textAlign:"center"}}>Page {activePage} of {pageCount}</span>
          <button onClick={()=>goToPage(activePage+1)} disabled={activePage>=pageCount}
            style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.1)",color:activePage>=pageCount?"#64748b":"#e2e8f0",borderRadius:6,padding:"3px 10px",cursor:activePage>=pageCount?"default":"pointer",fontSize:12,fontWeight:700}}>Next ›</button>
          <span style={{marginLeft:10,fontSize:10,color:"#94a3b8"}}>Jump:</span>
          <input type="number" min={1} max={pageCount} value={activePage}
            onChange={e=>{const n=parseInt(e.target.value,10);if(!isNaN(n))goToPage(n);}}
            style={{width:50,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",color:"#e2e8f0",borderRadius:6,padding:"3px 7px",fontSize:11,fontFamily:"inherit"}}/>
        </div>
      )}
      <div ref={containerRef}
        onWheel={(e)=>{
          if (!onZoomChange) return;
          // Cmd/Ctrl+wheel OR plain wheel = zoom. Use ctrlKey because pinch on
          // trackpads is reported as wheel + ctrlKey by the browser.
          if (!(e.ctrlKey || e.metaKey)) return;
          e.preventDefault();
          const delta = e.deltaY > 0 ? -0.1 : 0.1;
          const next = Math.max(0.5, Math.min(5, (zoom || 1) + delta));
          if (next !== zoom) onZoomChange(next);
        }}
        onTouchStart={(e)=>{
          if (!onZoomChange || e.touches.length !== 2) return;
          const dx = e.touches[0].clientX - e.touches[1].clientX;
          const dy = e.touches[0].clientY - e.touches[1].clientY;
          containerRef.current._pinchStart = Math.hypot(dx, dy);
          containerRef.current._pinchZoom = zoom || 1;
        }}
        onTouchMove={(e)=>{
          if (!onZoomChange || e.touches.length !== 2) return;
          const start = containerRef.current?._pinchStart;
          if (!start) return;
          const dx = e.touches[0].clientX - e.touches[1].clientX;
          const dy = e.touches[0].clientY - e.touches[1].clientY;
          const dist = Math.hypot(dx, dy);
          const ratio = dist / start;
          const next = Math.max(0.5, Math.min(5, (containerRef.current._pinchZoom || 1) * ratio));
          onZoomChange(next);
          e.preventDefault();
        }}
        onTouchEnd={()=>{
          if (containerRef.current) { containerRef.current._pinchStart = null; }
        }}
        style={{position:"relative",width:"100%",height:maxHeight||600,overflow:"auto",display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"12px",touchAction:onZoomChange?"none":"auto"}}>
        <div style={{position:"relative",width:pageDims.width||"auto",height:pageDims.height||"auto",flexShrink:0}}>
          <canvas ref={canvasRef} onClick={handleClick}
            style={{display:"block",cursor:placing?"crosshair":(onCanvasClick?"pointer":"default"),boxShadow:"0 2px 12px rgba(0,0,0,0.25)"}}/>
          {children && (
            <div style={{position:"absolute",top:0,left:0,width:pageDims.width||"100%",height:pageDims.height||"100%",pointerEvents:"none"}}>
              {children}
            </div>
          )}
        </div>
        {!pdfDoc && (
          <div style={{padding:40,textAlign:"center",color:"#94a3b8",fontSize:12}}>Loading PDF…</div>
        )}
        {rendering && pdfDoc && (
          <div style={{position:"absolute",top:8,right:8,background:"rgba(0,0,0,0.65)",color:"white",fontSize:10,padding:"3px 9px",borderRadius:6,fontWeight:700,zIndex:10}}>
            Rendering page {activePage}…
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PROJECT BLUEPRINT VIEW (project-aware wrapper around BlueprintView) ──────
// When used inside a project: blueprints auto-scope to that project + transfer to scope/inspection
// When used standalone: regular review mode only
function ProjectBlueprintView({proj, session, projects}) {
  const [scopeResult, setScopeResult]   = useState("");
  const [generating,  setGenerating]    = useState(false);
  const [activeTab,   setActiveTab]     = useState("review"); // review | scope | transfer | products
  const [transfered,  setTransfered]    = useState(false);
  const apiKey = "proxy"; // Using Supabase AI proxy

  // Blueprints stored per project
  const bpKey = "grok_blueprints_" + proj?.id;
  const [blueprints, setBlueprints] = useState(()=>{
    try{const s=localStorage.getItem(bpKey);return s?JSON.parse(s):[];}catch{return [];}
  });
  const [activeBlueprint, setActiveBlueprint] = useState(null);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfPageCount, setPdfPageCount] = useState(1);
  const [tradeFilter, setTradeFilter] = useState("all");
  const [flags, setFlags] = useState(()=>{
    try{return JSON.parse(localStorage.getItem("grok_bp_flags_"+(proj?.id||"x"))||"[]");}catch{return [];}
  });
  const [placingFlag, setPlacingFlag] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [flagType, setFlagType] = useState("issue"); // issue | rfi | co | note
  const imgRef2 = useRef();
  // ESC key to exit fullscreen
  React.useEffect(()=>{
    const handler=(e)=>{if(e.key==="Escape")setIsFullscreen(false);};
    window.addEventListener("keydown",handler);
    return()=>window.removeEventListener("keydown",handler);
  },[]);
  const fileRef = useRef();

  useEffect(()=>{
    if(blueprints.length > 0 && !activeBlueprint) setActiveBlueprint(blueprints[0]);
  },[blueprints]);

  const uploadBlueprint = (file) => {
    if (!file) return;

    const isPDF = file.type==="application/pdf"||file.name.toLowerCase().endsWith(".pdf");
    const reader = new FileReader();
    reader.onload = e => {
      const bp = {
        id:"bp_"+Date.now(), name:file.name, dataUrl:e.target.result,
        fileType:isPDF?"pdf":"image",
        mediaType:isPDF?"application/pdf":(file.type||"image/jpeg"),
      };
      const updated = [...blueprints, bp];
      setBlueprints(updated);
      setActiveBlueprint(bp);
      setPdfPage(1);
      try { localStorage.setItem(bpKey, JSON.stringify(updated)); } catch(ex) {
        if (ex?.name === "QuotaExceededError") {
          console.warn("Blueprint too large for localStorage — won't persist on refresh.");
        }
      }
      generateReviewForBP(bp, updated);
    };
    reader.readAsDataURL(file);
  };

  const saveFlags = (updated) => {
    setFlags(updated);
    const pid = proj?.id||"x";
    try{localStorage.setItem("grok_bp_flags_"+pid,JSON.stringify(updated));}catch(e){}
    SyncDB.saveFlags(pid, updated).catch(()=>{});
  };

  const addFlag = (e, bp) => {
    if (!placingFlag || !bp) return;
    const rect = imgRef2.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width * 100).toFixed(1);
    const y = ((e.clientY - rect.top) / rect.height * 100).toFixed(1);
    const label = window.prompt("Add a note for this flag (optional):");
    if (label === null) return; // cancelled
    saveFlags([...flags, {
      id:"flag_"+Date.now(), bpId:bp.id, x:parseFloat(x), y:parseFloat(y),
      type:flagType, note:label||"", createdAt:new Date().toLocaleDateString()
    }]);
    setPlacingFlag(false);
  };

  // Core review function — accepts explicit bp + list (for auto-generate on upload)
  const generateReviewForBP = async (bp, bpList) => {
    if (!bp) return;
    // proxy handles auth
    setGenerating(true); setScopeResult(""); setActiveTab("review");

    const allBps = (bpList||blueprints).slice(0, 4);
    // Ensure the triggering bp is included
    if (!allBps.find(b=>b.id===bp.id)) allBps.unshift(bp);

    await runGenerateReview(bp, allBps);
    setGenerating(false);
  };

  const runGenerateReview = async (activeBP, allBps) => {

    const systemPrompt = `You are a California licensed general contractor and building code expert specializing in SRO, affordable housing, and multi-family renovation in the SF Bay Area.

When reviewing blueprints, always provide:
1. SCOPE OF WORK — complete list of all trades and work visible in the drawings
2. CRITICAL PATH SCHEDULE — phase-by-phase construction sequence
3. CODE COMPLIANCE — CBC 2022, ADA 2010, Title 24, CalGreen, Bay Area Seismic SDC D
4. PERMIT REQUIREMENTS — what permits are needed based on scope
5. MISSING INFORMATION — what's unclear or missing from the drawings
6. COST FLAGS — items that typically cause budget overruns
7. RFIs BEFORE STARTING — questions to answer before mobilizing`;

    // Downscale images / size-guard PDFs before assembling the (multi-drawing)
    // payload so it never trips the edge runtime's payload cliff (which the
    // browser would otherwise surface as a phantom CORS error).
    const attachments = [];
    try {
      for (const b of allBps) {
        const p = await prepBlueprintForAI(b);
        attachments.push({type: p.isPDF ? "document" : "image", source:{type:"base64", media_type:p.mediaType, data:p.data}});
      }
    } catch (e) {
      setScopeResult("⚠ " + (e?.message || "Could not prepare the drawings for analysis."));
      return;
    }
    const userContent = [
      ...attachments,
      {type:"text", text:`Review these construction drawings for ${proj?.name||"this project"} located at ${proj?.addr||"Bay Area, CA"}.

Project Type: ${proj?.type||"Renovation"}
Contract Value: $${Math.round(proj?.value||0).toLocaleString()}

Provide a COMPLETE review covering:

## 📋 SCOPE OF WORK
List every trade and scope item visible. Be specific — this becomes the project scope checklist.

## 🏗 CRITICAL PATH SCHEDULE
Phase-by-phase sequence with dependencies. Include:
- Phase name
- Work included
- Required inspections at this phase
- Approximate duration

## ✅ CODE COMPLIANCE FLAGS
For each item found:
- Code section (CBC/ADA/Title 24/CalGreen)
- What needs to be addressed
- Required before permit or during construction

## ♿ ADA / ACCESSIBILITY
Specific items: door widths, turning radii, grab bars, ramp slopes, counter heights

## 📐 PERMIT REQUIREMENTS
What permits are needed, who pulls them, SF DBI or local AHJ process

## ⚠️ MISSING INFO / RFIs BEFORE STARTING
What's unclear in these drawings that needs clarification before mobilizing

## 💰 COST ESTIMATE — SF BAY AREA 2026
Provide a complete line-item estimate based on what you see in the drawings:
PRICING BASIS — read before pricing:
- COMPETITIVE-BID BASIS (say so under the tables). Do NOT stack markups: sub markup 15% + GC ~8% + ONE OH&P layer of 10–15% — no more. State contingency SEPARATELY as an owner-held risk allowance, NOT inside the bid subtotal.
- Warrantied COLD / FLUID-APPLIED membrane systems (CIM-1000-type reinforced cold fluid-applied and similar) price at INSTALLED certified-applicator rates ~$55–75/SF, NOT generic waterproofing-coating rates of $8–12/SF (the generic rate understates them 5–7× — the biggest lowball cause on roof decks).
- INCLUDE drawing-visible transition scope that's easy to miss: siding repair / remove-and-reinstall at penetrations and terminations, and paint-out / paint-for-flashing at wall transitions.

### Labor (by trade)
| Trade | Scope | Hours Est | Rate | Total |
List every trade needed with realistic Bay Area sub rates:
- Plumber: $145-165/hr (union), $120-140/hr (non-union)
- Electrician: $155-175/hr (union), $130-150/hr (non-union)
- Framing: $85-105/hr
- Drywall/Taper: $90-110/hr
- Tile setter: $95-115/hr
- Painter: $75-90/hr
- General labor/demo: $70-85/hr

### Materials — Current Bay Area Market Pricing (2026)
List all major materials with current market prices from SF Bay Area suppliers:
| Material | Spec | Qty | Unit | Unit Price | Total |
Use current 2026 pricing from suppliers like:
- Home Depot San Mateo/SF ($HD pricing)
- Builders FirstSource Bay Area
- Gypsum drywall: $16-22/sheet (4x8 5/8")
- LVP flooring: $3.50-6.50/sqft installed
- Tile (ceramic): $2-8/sqft material, (porcelain): $4-12/sqft
- Copper pipe: $4-8/lft
- Romex 12/2: $1.20-1.60/lft
- Insulation R-19: $0.85-1.20/sqft
- Paint (commercial): $45-65/gallon
- Lumber 2x4x8: $5.50-7.50/ea
- Drywall 5/8" Type X: $18-22/sheet

### Subcontractor Costs (with GCG markup)
| Sub Trade | Scope | Sub Cost | 15% Markup | Owner Price |

### Cost Summary
| Category | Amount |
|---|---|
| Direct Labor | $X |
| Materials | $X |
| Subcontractors (with markup) | $X |
| General Conditions (8%) | $X |
| GC Overhead & Profit (10–15%) | $X |
| **Competitive-bid subtotal** | **$X** |
| Contingency (10%, owner-held risk — separate, NOT in the bid subtotal) | $X |
| **Owner budget incl. contingency** | **$X** |
| Low estimate (competitive-bid basis) | $X |
| High estimate (competitive-bid basis) | $X |

_AI pricing is directional — calibrate against local sub quotes._

## 🔍 CO RISK FLAGS
Items in the drawings that will likely generate change orders — hidden conditions, unclear specs, scope gaps`}
    ];

    try {
      // Determine if any uploaded blueprint is a PDF (needs beta header)
      const hasPDF = allBps.some(bp=>bp.fileType==="pdf"||bp.name?.toLowerCase().endsWith(".pdf"));
      const headers = {
        "Content-Type":"application/json",
        "Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,
        "anthropic-version":"2023-06-01",
        "anthropic-dangerous-direct-browser-access":"true",
      };
      if (hasPDF) headers["anthropic-beta"] = "pdfs-2024-09-25";
      const resp = await fetch(AI_PROXY_URL, {
        method: "POST",
        headers,
        body: JSON.stringify({
          model: "claude-haiku-4-5",
          max_tokens: 6000,
          system: systemPrompt,
          messages: [{role:"user", content:userContent}]
        })
      });

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while(true) {
        const {done, value} = await reader.read();
        if (done) break;
        buf += decoder.decode(value, {stream:true});
        const lines = buf.split("\n"); buf = lines.pop()||"";
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try{const p=JSON.parse(line.slice(6));const t=p.delta?.text||"";if(t)setScopeResult(prev=>prev+t);}catch(e){}
          }
        }
      }
    } catch(e) { setScopeResult("⚠ Error: "+e.message+"\n\nCheck your API key in AI Agent settings."); }
  };

  // Public wrapper — called by button
  const generateReview = async () => {
    if (!activeBlueprint) { alert("Upload a blueprint or drawing first."); return; }
    
    setGenerating(true); setScopeResult(""); setActiveTab("review");
    await runGenerateReview(activeBlueprint, blueprints.slice(0,4));
    setGenerating(false);
  };

  // Parse trades from completed scope result and tag the active blueprint
  React.useEffect(()=>{
    if (!scopeResult || !activeBlueprint) return;
    const TRADE_KEYWORDS = {
      "Electrical":["electrical","wiring","panel","circuit","outlet","switch","lighting","low voltage"],
      "Plumbing":["plumbing","drain","water supply","pipe","fixture","toilet","sink","shower","water heater"],
      "HVAC":["hvac","mechanical","duct","ventilation","exhaust","ac unit","heat pump","furnace"],
      "Framing":["framing","structural","stud","header","sheathing","blocking","shear wall"],
      "Concrete":["concrete","foundation","slab","footing","retaining wall","masonry"],
      "Finishes":["drywall","paint","flooring","tile","ceiling","finish","trim","millwork"],
      "Site":["site","grading","landscaping","paving","fence","gate","exterior"],
    };
    const lower = scopeResult.toLowerCase();
    const detectedTrades = Object.entries(TRADE_KEYWORDS)
      .filter(([trade, kws]) => kws.some(k => lower.includes(k)))
      .map(([trade]) => trade);
    if (detectedTrades.length > 0) {
      const updated = blueprints.map(b=>
        b.id===activeBlueprint.id ? {...b, trades:detectedTrades} : b
      );
      setBlueprints(updated);
      try{localStorage.setItem(bpKey,JSON.stringify(updated));}catch(e){}
    }
  }, [scopeResult]);

  // Transfer scope to project scope checklist and inspection schedule
  const transferToProject = () => {
    if (!scopeResult || !proj) {
      alert("Generate an AI review first, then click Transfer.");
      return;
    }

    // ── Parse each section from the AI review ──────────────────────────────
    const getSection = (text, ...headers) => {
      for (const h of headers) {
        const re = new RegExp(h + "[^\n]*\n([\s\S]*?)(?=##|===|$)", "i");
        const m = text.match(re);
        if (m) return m[1].trim();
      }
      return "";
    };

    const scopeSection    = getSection(scopeResult, "SCOPE OF WORK", "SCOPE", "📋 SCOPE");
    const schedSection    = getSection(scopeResult, "CRITICAL PATH", "SCHEDULE", "🏗 CRITICAL");
    const codeSection     = getSection(scopeResult, "CODE COMPLIANCE", "COMPLIANCE FLAGS", "✅ CODE");
    const adaSection      = getSection(scopeResult, "ADA", "ACCESSIBILITY", "♿");
    const permitSection   = getSection(scopeResult, "PERMIT REQUIREMENTS", "PERMITS", "📐 PERMIT");
    const rfiSection      = getSection(scopeResult, "MISSING INFO", "RFI", "⚠️ MISSING", "MISSING INFO");
    const costSection     = getSection(scopeResult, "COST FLAGS", "BUDGET RISK", "💰 COST");

    // ── Parse trade-level included/excluded from scope section ─────────────
    const TRADE_KEYWORDS = {
      demo:     ["demo","demolition","protection","temporary"],
      framing:  ["framing","structural","sheathing","blocking","header"],
      plumb:    ["plumbing","drain","water","pipe","fixture","toilet","sink","shower","tub"],
      elec:     ["electrical","wiring","panel","circuit","outlet","lighting","switch"],
      hvac:     ["hvac","mechanical","duct","ventilation","exhaust","ac","heat"],
      drywall:  ["drywall","gypsum","tape","mud","texture","paint","finish"],
      tile:     ["tile","flooring tile","ceramic","porcelain","grout","shower tile"],
      floor:    ["floor","flooring","hardwood","lvp","vinyl","carpet","subfloor"],
      cab:      ["cabinet","counter","countertop","vanity"],
      doors:    ["door","hardware","lockset","hinge","threshold","window"],
      fix:      ["fixture","appliance","faucet","toilet","light fixture"],
      exterior: ["exterior","site","landscaping","concrete","paving","fence","roof","gutter"],
    };

    const tradeIncludes = {};
    const tradeExcludes = {};
    const scopeLower = scopeSection.toLowerCase();
    const fullLower = scopeResult.toLowerCase();

    Object.entries(TRADE_KEYWORDS).forEach(([id, kws]) => {
      const lines = scopeResult.split("\n").filter(l => kws.some(k => l.toLowerCase().includes(k)));
      if (lines.length > 0) {
        // Lines with "not included", "excluded", "by owner", "NIC" → excluded
        const excl = lines.filter(l => /not includ|exclud|n\.i\.c|by owner|by others|not in scope/i.test(l));
        const incl = lines.filter(l => !excl.includes(l));
        if (incl.length) tradeIncludes[id] = incl.slice(0,3).map(l=>l.replace(/^[-•*]\s*/,"").trim()).join("; ");
        if (excl.length) tradeExcludes[id] = excl.slice(0,2).map(l=>l.replace(/^[-•*]\s*/,"").trim()).join("; ");
      }
    });

    // ── 1. Pre-Con / Scope Checklist ─────────────────────────────────────────
    const scopeKey = "grok_scope_" + proj.id;
    try {
      const existing = JSON.parse(localStorage.getItem(scopeKey)||"{}");
      const updatedTrades = (existing.trades || [
        {id:"demo",label:"Demo & Protection",included:"",excluded:""},
        {id:"framing",label:"Framing / Structural",included:"",excluded:""},
        {id:"plumb",label:"Plumbing",included:"",excluded:""},
        {id:"elec",label:"Electrical",included:"",excluded:""},
        {id:"hvac",label:"HVAC",included:"",excluded:""},
        {id:"drywall",label:"Drywall & Paint",included:"",excluded:""},
        {id:"tile",label:"Tile",included:"",excluded:""},
        {id:"floor",label:"Flooring",included:"",excluded:""},
        {id:"cab",label:"Cabinets & Countertops",included:"",excluded:""},
        {id:"doors",label:"Doors & Hardware",included:"",excluded:""},
        {id:"fix",label:"Fixtures & Appliances",included:"",excluded:""},
        {id:"exterior",label:"Exterior / Site Work",included:"",excluded:""},
      ]).map(trade => ({
        ...trade,
        included: tradeIncludes[trade.id] || trade.included || "",
        excluded: tradeExcludes[trade.id] || trade.excluded || "",
      }));
      localStorage.setItem(scopeKey, JSON.stringify({
        ...existing,
        trades: updatedTrades,
        blueprint_scope: scopeResult,
        blueprint_scope_summary: scopeSection.slice(0, 600),
        blueprint_generated_date: new Date().toLocaleDateString(),
        blueprint_source: blueprints.map(b=>b.name).join(", "),
        transferred_at: new Date().toISOString(),
        // Cost flags and unforeseen work
        cost_flags: costSection.slice(0, 800),
        rfi_list: rfiSection.slice(0, 800),
      }));
    } catch(e) { console.error("Scope save failed:", e); }

    // ── 2. Inspection & Billing — schedule + permit info ─────────────────────
    const inspKey = "grok_inspection_" + proj.id;
    try {
      const existing = JSON.parse(localStorage.getItem(inspKey)||"{}");
      localStorage.setItem(inspKey, JSON.stringify({
        ...existing,
        blueprint_scope_notes: scopeResult,
        blueprint_schedule_notes: schedSection.slice(0, 800),
        blueprint_permit_notes: permitSection.slice(0, 600),
        blueprint_transferred: new Date().toLocaleDateString(),
      }));
    } catch(e) { console.error("Inspection save failed:", e); }

    // ── 3. Field Dashboard — code flags, ADA, unforeseen ─────────────────────
    const fieldKey = "grok_field_issues_" + proj.id;
    try {
      const existing = JSON.parse(localStorage.getItem(fieldKey)||"[]");
      const existingIds = new Set(existing.map(i=>i.id));

      // Parse code compliance flags
      const codeFlagLines = (codeSection+"\n"+adaSection).split("\n")
        .filter(l => l.trim().length > 15 && /CBC|ADA|Title 24|CalGreen|seismic|permit|egress|occupancy|code|section|require/i.test(l))
        .slice(0, 6);

      // Parse cost/unforeseen flags
      const costFlagLines = costSection.split("\n")
        .filter(l => l.trim().length > 15 && /risk|unforeseen|hidden|asbestos|mold|lead|structural|condition|overrun/i.test(l))
        .slice(0, 4);

      const newFlags = [
        ...codeFlagLines.map((flag, i) => ({
          id: "bp_code_"+Date.now()+i,
          type: "code",
          priority: "high",
          desc: flag.replace(/^[-•*🔴⚠✅]\s*/,"").trim().slice(0, 120),
          status: "open",
          source: "Blueprint AI Review — Code Compliance",
          created: new Date().toLocaleDateString(),
        })),
        ...costFlagLines.map((flag, i) => ({
          id: "bp_cost_"+Date.now()+"_"+i,
          type: "risk",
          priority: "medium",
          desc: flag.replace(/^[-•*💰⚠]\s*/,"").trim().slice(0, 120),
          status: "open",
          source: "Blueprint AI Review — Cost/Unforeseen Risk",
          created: new Date().toLocaleDateString(),
        })),
      ].filter(f => f.desc.length > 5 && !existingIds.has(f.id));

      if (newFlags.length > 0) {
        localStorage.setItem(fieldKey, JSON.stringify([...existing, ...newFlags]));
      }
    } catch(e) { console.error("Field flags failed:", e); }

    // ── 4. RFIs — create open RFIs from the missing info section ─────────────
    const rfiKey = "grok_rfis_" + proj.id;
    try {
      const existing = JSON.parse(localStorage.getItem(rfiKey)||"[]");
      const rfiLines = rfiSection.split("\n")
        .filter(l => l.trim().length > 15)
        .slice(0, 5);
      const newRFIs = rfiLines.map((line, i) => ({
        id: "bp_rfi_"+Date.now()+"_"+i,
        number: "RFI-BP-"+(i+1),
        subject: line.replace(/^[-•*\d.]\s*/,"").trim().slice(0, 80),
        desc: "Generated from Blueprint AI Review — requires clarification before starting work.",
        status: "open",
        source: "Blueprint Review",
        created: new Date().toLocaleDateString(),
        priority: "high",
      }));
      if (newRFIs.length > 0) {
        localStorage.setItem(rfiKey, JSON.stringify([...existing, ...newRFIs]));
      }
    } catch(e) { console.error("RFI save failed:", e); }

    setTransfered(true);
    setActiveTab("transfer");
  };

  const TabBtn = ({id, label}) => (
    <button onClick={()=>setActiveTab(id)}
      style={{padding:"6px 14px",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",
        background:activeTab===id?T.gold:T.bg2,color:activeTab===id?T.bg0:T.muted,transition:"all 0.15s"}}>
      {label}
    </button>
  );

  return (
    <div>
      {/* Project context banner */}
      {proj&&(
        <div style={{background:T.tealBg,border:"1px solid "+T.teal+"30",borderRadius:10,padding:"10px 14px",marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:16}}>📐</span>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:T.teal}}>Blueprint Review — {proj.name}</div>
              <div style={{fontSize:10,color:T.muted}}>Blueprints upload here auto-link to this project · AI review generates scope, schedule & code flags</div>
            </div>
          </div>
          {blueprints.length > 0 && (
            <div style={{fontSize:10,color:T.teal,fontWeight:600}}>{blueprints.length} drawing{blueprints.length>1?"s":""} uploaded</div>
          )}
        </div>
      )}

      {blueprints.some(b=>b.trades?.length>0)&&(
        <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:8,alignItems:"center"}}>
          <span style={{fontSize:9,color:T.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em"}}>Filter:</span>
          {["all",...[...new Set(blueprints.flatMap(b=>b.trades||[]))]].map(trade=>(
            <button key={trade} onClick={()=>setTradeFilter(trade)}
              style={{fontSize:9,fontWeight:600,padding:"3px 10px",borderRadius:12,cursor:"pointer",border:"none",background:tradeFilter===trade?T.teal:T.bg2,color:tradeFilter===trade?"white":T.muted}}>
              {trade==="all"?"All Drawings":trade}
            </button>
          ))}
        </div>
      )}
      {/* Blueprint upload & selector */}
      <div style={{display:"flex",gap:8,marginBottom:12,alignItems:"center",flexWrap:"wrap"}}>
        {blueprints.filter(bp=>tradeFilter==="all"||!bp.trades||bp.trades.includes(tradeFilter)).map(bp=>(
          <button key={bp.id} onClick={()=>setActiveBlueprint(bp)}
            style={{fontSize:11,fontWeight:600,padding:"5px 12px",borderRadius:7,border:"1px solid "+(activeBlueprint?.id===bp.id?T.gold:T.subtle),background:activeBlueprint?.id===bp.id?T.goldBg:T.bg2,color:activeBlueprint?.id===bp.id?T.gold:T.muted,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
            📄 {bp.name.length>20?bp.name.slice(0,18)+"...":bp.name}
            <span onClick={e=>{e.stopPropagation();const upd=blueprints.filter(b=>b.id!==bp.id);setBlueprints(upd);if(activeBlueprint?.id===bp.id)setActiveBlueprint(upd[0]||null);try{localStorage.setItem(bpKey,JSON.stringify(upd));}catch(ex){}}}
              style={{color:T.muted,fontSize:10,marginLeft:2}}>✕</span>
          </button>
        ))}
        <input ref={fileRef} type="file" accept="image/*,application/pdf,.pdf,.png,.jpg,.jpeg,.tiff,.heic,.webp" multiple style={{display:"none"}}
          onChange={e=>Array.from(e.target.files).forEach(f=>uploadBlueprint(f))}/>
        <Btn small ghost onClick={()=>fileRef.current.click()}><Ic d={ICONS.plus} size={11}/>Upload Drawings</Btn>
        {blueprints.length > 0 && (
          <Btn primary small onClick={generateReview} disabled={generating}>
            {generating?<><Spin/>Analyzing...</>:"✨ AI Review — Scope, Schedule & Codes"}
          </Btn>
        )}
        {activeBlueprint&&activeBlueprint.fileType!=="pdf"&&(
          <div style={{display:"flex",gap:4,padding:"4px 8px",background:T.bg2,borderRadius:8,border:"1px solid "+T.subtle,alignItems:"center"}}>
            {[["issue","🔴","#ef4444"],["rfi","❓","#f59e0b"],["co","📝","#a78bfa"],["note","📌","#10b981"]].map(([type,icon,col])=>(
              <button key={type} title={"Flag: "+type}
                onClick={()=>{setFlagType(type);setPlacingFlag(p=>flagType===type?!p:true);}}
                style={{fontSize:13,padding:"3px 6px",borderRadius:5,cursor:"pointer",border:"1px solid "+(placingFlag&&flagType===type?col:T.subtle),background:placingFlag&&flagType===type?col+"20":"transparent"}}>
                {icon}
              </button>
            ))}
            <span style={{fontSize:9,color:T.muted,paddingLeft:2}}>{placingFlag?"Click blueprint to mark":"Mark:"}</span>
          </div>
        )}
      </div>

      {/* Blueprint preview with flag overlay */}
      {activeBlueprint && (
        <>
        {/* Fullscreen overlay */}
        {isFullscreen&&(
          <div style={{position:"fixed",inset:0,zIndex:1000,background:"#02040A",display:"flex",flexDirection:"column"}}>
            {/* Fullscreen toolbar */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 16px",background:"rgba(2,4,10,0.95)",borderBottom:"1px solid rgba(255,255,255,0.08)",flexShrink:0,zIndex:10}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{fontSize:13,fontWeight:700,color:"#f1f5f9"}}>{activeBlueprint.name}</div>
                <div style={{fontSize:10,color:"#64748b",background:"rgba(255,255,255,0.06)",padding:"2px 8px",borderRadius:9}}>{proj?.name}</div>
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                {/* Flag type buttons */}
                {[["issue","🔴","#ef4444"],["rfi","❓","#f59e0b"],["co","📝","#a78bfa"],["note","📌","#10b981"]].map(([type,icon,col])=>(
                  <button key={type}
                    onClick={()=>{setFlagType(type);setPlacingFlag(p=>flagType===type?!p:true);}}
                    style={{padding:"6px 12px",borderRadius:7,border:"1px solid "+(placingFlag&&flagType===type?col:"rgba(255,255,255,0.12)"),
                      background:placingFlag&&flagType===type?col+"25":"rgba(255,255,255,0.06)",
                      color:placingFlag&&flagType===type?col:"#94a3b8",cursor:"pointer",fontSize:12,fontWeight:700,
                      display:"flex",alignItems:"center",gap:5}}>
                    {icon} {type.toUpperCase()}
                  </button>
                ))}
                <div style={{width:1,height:24,background:"rgba(255,255,255,0.1)"}}/>
                <span style={{fontSize:10,color:"#64748b"}}>{flags.filter(f=>f.bpId===activeBlueprint.id).length} flags</span>
                <button onClick={()=>setIsFullscreen(false)}
                  style={{padding:"7px 16px",background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:8,color:"#f1f5f9",cursor:"pointer",fontSize:12,fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
                  ✕ Exit Fullscreen
                </button>
              </div>
            </div>
            {/* Fullscreen image */}
            <div style={{flex:1,overflow:"auto",position:"relative",cursor:placingFlag?"crosshair":"default"}}
              onClick={e=>addFlag(e,activeBlueprint)}>
              <img ref={imgRef2} src={activeBlueprint.dataUrl} alt={activeBlueprint.name}
                style={{width:"100%",height:"100%",objectFit:"contain",display:"block",background:"#02040A"}}/>
              {/* Flags overlay */}
              {flags.filter(f=>f.bpId===activeBlueprint.id).map(flag=>{
                const colors={"issue":"#ef4444","rfi":"#f59e0b","co":"#a78bfa","note":"#10b981"};
                const col=colors[flag.type]||"#64748b";
                return(
                  <div key={flag.id} title={flag.note||flag.type}
                    onClick={e=>{e.stopPropagation();if(window.confirm("Remove this flag?"))saveFlags(flags.filter(f=>f.id!==flag.id));}}
                    style={{position:"absolute",left:flag.x+"%",top:flag.y+"%",transform:"translate(-50%,-50%)",cursor:"pointer",zIndex:10}}>
                    <div style={{background:col,color:"white",borderRadius:"50%",width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,boxShadow:"0 3px 12px rgba(0,0,0,0.5)",border:"2px solid white"}}>
                      {{"issue":"!","rfi":"?","co":"CO","note":"★"}[flag.type]||"!"}
                    </div>
                    {flag.note&&(
                      <div style={{position:"absolute",left:"50%",top:"110%",transform:"translateX(-50%)",background:"#0A1220",color:"#f1f5f9",fontSize:10,padding:"4px 8px",borderRadius:6,whiteSpace:"nowrap",boxShadow:"0 2px 8px rgba(0,0,0,0.5)",border:"1px solid "+col+"40",maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",zIndex:20}}>
                        {flag.note}
                      </div>
                    )}
                  </div>
                );
              })}
              {placingFlag&&(
                <div style={{position:"fixed",bottom:20,left:"50%",transform:"translateX(-50%)",background:"rgba(2,4,10,0.9)",border:"2px dashed #f59e0b",borderRadius:10,padding:"8px 20px",fontSize:12,fontWeight:700,color:"#f1f5f9",pointerEvents:"none",zIndex:20}}>
                  🎯 Click anywhere on the drawing to place {flagType} flag
                </div>
              )}
            </div>
            {/* Bottom status bar */}
            <div style={{padding:"8px 16px",background:"rgba(2,4,10,0.95)",borderTop:"1px solid rgba(255,255,255,0.06)",display:"flex",gap:12,alignItems:"center",flexShrink:0}}>
              {[["issue","🔴","#ef4444"],["rfi","❓","#f59e0b"],["co","📝","#a78bfa"],["note","📌","#10b981"]].map(([type,icon,col])=>{
                const count=flags.filter(f=>f.bpId===activeBlueprint.id&&f.type===type).length;
                return count>0?(
                  <div key={type} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:col}}>
                    {icon} <span style={{fontWeight:700}}>{count}</span> {type}
                  </div>
                ):null;
              })}
              {placingFlag&&<span style={{fontSize:10,color:"#f59e0b",marginLeft:"auto"}}>⚠ Placement mode active — click drawing</span>}
              <span style={{fontSize:10,color:"#64748b",marginLeft:"auto"}}>Press ESC or click ✕ to exit fullscreen</span>
            </div>
          </div>
        )}
        <div style={{border:"1px solid "+T.subtle,borderRadius:10,overflow:"hidden",marginBottom:14,position:"relative"}}>
          {/* Fullscreen button */}
          <button onClick={()=>setIsFullscreen(true)}
            style={{position:"absolute",top:8,right:8,zIndex:20,background:"rgba(2,4,10,0.75)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:7,padding:"5px 10px",color:"#f1f5f9",fontSize:11,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:5,backdropFilter:"blur(4px)"}}>
            ⛶ Fullscreen
          </button>
          {activeBlueprint.fileType==="pdf" ? (
            <div style={{background:T.bg2,padding:"8px"}}>
              <PdfCanvas dataUrl={activeBlueprint.dataUrl} name={activeBlueprint.name}
                currentPage={pdfPage} onPageChange={setPdfPage} onPagesLoaded={setPdfPageCount}
                maxHeight="calc(100vh - 320px)"/>
              <div style={{textAlign:"center",padding:"6px 0 2px",fontSize:10,color:T.muted}}>📄 {activeBlueprint.name}{pdfPageCount>1?` · ${pdfPageCount} pages`:""}</div>
            </div>
          ) : (
            <div style={{position:"relative",cursor:placingFlag?"crosshair":"default"}}
              onClick={e=>addFlag(e,activeBlueprint)}>
              <img ref={imgRef2} src={activeBlueprint.dataUrl} alt={activeBlueprint.name}
                style={{width:"100%",display:"block",maxHeight:500,objectFit:"contain",background:T.bg3}}/>
              {/* Flag pins overlay */}
              {flags.filter(f=>f.bpId===activeBlueprint.id).map(flag=>{
                const colors={"issue":"#ef4444","rfi":"#f59e0b","co":"#a78bfa","note":"#10b981"};
                const icons={"issue":"🔴","rfi":"❓","co":"📝","note":"📌"};
                const col = colors[flag.type]||"#64748b";
                return (
                  <div key={flag.id}
                    title={flag.note||flag.type}
                    onClick={e=>{e.stopPropagation();if(true)saveFlags(flags.filter(f=>f.id!==flag.id));}}
                    style={{position:"absolute",left:flag.x+"%",top:flag.y+"%",transform:"translate(-50%,-50%)",cursor:"pointer",zIndex:10}}>
                    <div style={{background:col,color:"white",borderRadius:"50%",width:24,height:24,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,boxShadow:"0 2px 6px rgba(0,0,0,0.4)",border:"2px solid white"}}>{
                      {issue:"!",rfi:"?",co:"CO",note:"★"}[flag.type]||"!"}
                    </div>
                    {flag.note&&(
                      <div style={{position:"absolute",left:"50%",top:"110%",transform:"translateX(-50%)",background:T.bg0,color:T.text,fontSize:9,padding:"3px 6px",borderRadius:5,whiteSpace:"nowrap",boxShadow:"0 2px 4px rgba(0,0,0,0.3)",border:"1px solid "+col+"40",maxWidth:120,overflow:"hidden",textOverflow:"ellipsis"}}>
                        {flag.note}
                      </div>
                    )}
                  </div>
                );
              })}
              {placingFlag&&(
                <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.08)",display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none"}}>
                  <div style={{background:T.bg0,borderRadius:8,padding:"6px 14px",fontSize:11,fontWeight:700,color:T.text,border:"2px dashed "+T.amber}}>🎯 Click anywhere to place flag</div>
                </div>
              )}
            </div>
          )}
        </div>
        </>
      )}

      {!activeBlueprint && (
        <div style={{textAlign:"center",padding:"50px",color:T.muted,background:T.bg2,borderRadius:12,border:"2px dashed "+T.subtle,marginBottom:14}}>
          <div style={{fontSize:36,opacity:0.3,marginBottom:10}}>📐</div>
          <div style={{fontSize:13,fontWeight:600,marginBottom:6}}>No blueprints uploaded yet</div>
          <div style={{fontSize:11,lineHeight:1.7,marginBottom:16,color:T.muted}}>
            Upload drawings to get AI-generated scope of work,<br/>critical path schedule, and full CA/ADA code compliance review
          </div>
          <Btn primary onClick={()=>fileRef.current.click()}>Upload Blueprints</Btn>
        </div>
      )}

      {/* Flags list for this drawing */}
      {flags.filter(f=>f.bpId===activeBlueprint?.id).length>0&&(
        <div style={{background:T.bg2,borderRadius:9,padding:"10px 14px",marginBottom:12,border:"1px solid "+T.subtle}}>
          <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:8}}>
            Flags on this drawing ({flags.filter(f=>f.bpId===activeBlueprint?.id).length})
            <button onClick={()=>{if(true)saveFlags(flags.filter(f=>f.bpId!==activeBlueprint?.id));}}
              style={{fontSize:9,background:"none",border:"none",color:T.muted,cursor:"pointer",marginLeft:8}}>Clear all</button>
          </div>
          {flags.filter(f=>f.bpId===activeBlueprint?.id).map(flag=>{
            const cols={"issue":"#ef4444","rfi":"#f59e0b","co":"#a78bfa","note":"#10b981"};
            const col=cols[flag.type]||T.muted;
            return (
              <div key={flag.id} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0",borderBottom:"1px solid "+T.subtle+"30"}}>
                <div style={{width:16,height:16,borderRadius:"50%",background:col,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,color:"white",fontWeight:800,flexShrink:0}}>
                  {({issue:"!",rfi:"?",co:"C",note:"★"})[flag.type]}
                </div>
                <span style={{fontWeight:600,color:col,textTransform:"capitalize",width:36,flexShrink:0,fontSize:10}}>{flag.type}</span>
                <span style={{color:T.muted,flex:1,fontSize:10}}>{flag.note||"No note"} · {flag.createdAt}</span>
                <button onClick={()=>saveFlags(flags.filter(f=>f.id!==flag.id))}
                  style={{fontSize:9,background:"none",border:"none",color:T.muted,cursor:"pointer"}}>✕</button>
              </div>
            );
          })}
        </div>
      )}

      {/* Results tabs */}
      {(scopeResult || generating) && (
        <div>
          <div style={{display:"flex",gap:4,marginBottom:14,alignItems:"center",justifyContent:"space-between"}}>
            <div style={{display:"flex",gap:4}}>
              <TabBtn id="review" label="📋 Full Review"/>
              <TabBtn id="takeoff" label="📐 AI Takeoff"/>
              {proj && <TabBtn id="transfer" label="🔗 Transfer to Project"/>}
              <TabBtn id="products" label="🏷 Product Search"/>
            </div>
            <div style={{display:"flex",gap:6}}>
              {scopeResult && <button onClick={()=>navigator.clipboard.writeText(scopeResult)}
                style={{fontSize:11,fontWeight:600,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"5px 12px",cursor:"pointer",color:T.muted}}>
                Copy Report
              </button>}
              {scopeResult && (
                <button onClick={()=>{
                  // Extract scope section from the review result
                  const scopeSection = scopeResult.includes("SCOPE OF WORK")
                    ? scopeResult.split("SCOPE OF WORK")[1]?.split(/##|===/)[0]?.trim() || scopeResult.slice(0,1200)
                    : scopeResult.slice(0,1200);
                  const prefill = "Project: "+(proj?.name||"This project")+"\nAddress: "+(proj?.addr||"")+"\n\nScope from Blueprint Review:\n"+scopeSection;
                  localStorage.setItem("grok_estimate_prefill", prefill);
                  window.dispatchEvent(new CustomEvent("grok-navigate-estimate", {detail:{view:"ai-agent", prefill}}));
                  window.dispatchEvent(new CustomEvent("grok-navigate", {detail:{view:"ai-agent"}}));
                }}
                style={{fontSize:11,fontWeight:700,background:"linear-gradient(135deg,"+T.purple+","+T.blue+")",color:"white",border:"none",borderRadius:7,padding:"6px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                  💰 Price this Scope →
                </button>
              )}
              {proj && scopeResult && !transfered && (
                <button onClick={transferToProject}
                  style={{fontSize:11,fontWeight:700,background:T.teal,color:"white",border:"none",borderRadius:7,padding:"6px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                  🔗 Push to Project Scope & Schedule
                </button>
              )}
              {transfered && <span style={{fontSize:11,color:T.green,fontWeight:700}}>✓ Transferred to project</span>}
            </div>
          </div>

          {activeTab==="review" && (
            <div style={{background:T.bg1,borderRadius:10,padding:"16px",fontSize:12,color:T.text,lineHeight:1.9,whiteSpace:"pre-wrap",maxHeight:600,overflowY:"auto",fontFamily:"inherit",borderLeft:"3px solid "+T.purple}}>
              {generating && !scopeResult ? (
                <div style={{display:"flex",alignItems:"center",gap:10,color:T.muted}}><Spin/>Reading blueprints and generating scope, schedule, and code review...</div>
              ) : scopeResult}
            </div>
          )}

          {activeTab==="transfer" && proj && (
            <Card>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Transfer Review to Project</div>
              {transfered ? (
                <div style={{background:T.greenBg,border:"1px solid "+T.green+"30",borderRadius:9,padding:"14px 16px"}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.green,marginBottom:10}}>✓ Blueprint review fully transferred to {proj.name}</div>
                  <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:14}}>
                    {[
                      {icon:"📋",tab:"precon",label:"Pre-Con Scope Checklist","desc":"Trade included/excluded filled in from drawings"},
                      {icon:"🏗",tab:"field",label:"Field Dashboard","desc":"Code flags + unforeseen risk items added as open issues"},
                      {icon:"📐",tab:"inspection",label:"Inspection & Billing","desc":"Schedule notes + permit requirements saved"},
                      {icon:"❓",tab:"admin",label:"RFIs","desc":"Open RFIs created from missing info/clarifications"},
                    ].map(item=>(
                      <div key={item.tab} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:T.bg1,borderRadius:8,border:"1px solid "+T.green+"30"}}>
                        <span style={{fontSize:16,flexShrink:0}}>{item.icon}</span>
                        <div style={{flex:1}}>
                          <div style={{fontSize:11,fontWeight:700,color:T.text}}>{item.label}</div>
                          <div style={{fontSize:10,color:T.muted}}>{item.desc}</div>
                        </div>
                        <button onClick={()=>window.dispatchEvent(new CustomEvent("grok-navigate",{detail:{tab:item.tab}}))}
                          style={{fontSize:10,fontWeight:700,background:T.teal,color:"white",border:"none",borderRadius:6,padding:"4px 10px",cursor:"pointer",flexShrink:0}}>
                          Open →
                        </button>
                      </div>
                    ))}
                  </div>
                  {scopeResult&&(
                    <div style={{background:T.purpleBg,border:"1px solid "+T.purple+"30",borderRadius:9,padding:"12px 14px"}}>
                      <div style={{fontSize:11,fontWeight:700,color:T.purple,marginBottom:6}}>📐 → 💰 Next: Price this Scope</div>
                      <div style={{fontSize:10,color:T.muted,marginBottom:8,lineHeight:1.6}}>Your scope is ready. Send it to AI Agent for a full itemized estimate with Bay Area materials, labor, and O&P.</div>
                      <Btn primary small onClick={()=>{
                        const scopeText = scopeResult.slice(0,1500);
                        const prefill = "Project: "+(proj?.name||"")+"\nAddress: "+(proj?.addr||"")+"\n\nScope from Blueprint Review:\n"+scopeText;
                        localStorage.setItem("grok_estimate_prefill", prefill);
                        window.dispatchEvent(new CustomEvent("grok-navigate-estimate",{detail:{view:"ai-agent",prefill}}));
                        window.dispatchEvent(new CustomEvent("grok-navigate",{detail:{view:"ai-agent"}}));
                      }} style={{width:"100%",justifyContent:"center",background:T.purple,color:"white"}}>
                        💰 Get Full Estimate from this Scope →
                      </Btn>
                    </div>
                  )}
                </div>
              ):(
                <div>
                  <div style={{fontSize:11,color:T.muted,lineHeight:1.7,marginBottom:14}}>
                    Push the AI review to your project. This will:
                  </div>
                  {[
                    {icon:"📋",title:"Pre-Con Scope Checklist",desc:"Trade included/excluded pre-filled per trade from drawings"},
                    {icon:"🏗",title:"Field Dashboard",desc:"Code flags + unforeseen risks become open issues"},
                    {icon:"📐",title:"Inspection & Billing",desc:"Schedule sequence + permit requirements pre-loaded"},
                    {icon:"❓",title:"RFIs",desc:"Open RFIs created from missing info and clarifications needed"},
                  ].map(item=>(
                    <div key={item.title} style={{display:"flex",gap:12,padding:"10px 12px",background:T.bg2,borderRadius:8,marginBottom:6}}>
                      <span style={{fontSize:18,flexShrink:0}}>{item.icon}</span>
                      <div>
                        <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:2}}>{item.title}</div>
                        <div style={{fontSize:11,color:T.muted}}>{item.desc}</div>
                      </div>
                    </div>
                  ))}
                  <Btn primary onClick={transferToProject} style={{marginTop:12,width:"100%",justifyContent:"center"}}>
                    🔗 Transfer to {proj.name}
                  </Btn>
                  {scopeResult&&(
                    <div style={{marginTop:10,background:T.purpleBg,border:"1px solid "+T.purple+"30",borderRadius:9,padding:"12px 14px"}}>
                      <div style={{fontSize:11,fontWeight:700,color:T.purple,marginBottom:6}}>📐 → 💰 Next Step: Price this Scope</div>
                      <div style={{fontSize:10,color:T.muted,marginBottom:8,lineHeight:1.6}}>
                        Your scope is ready. Send it to AI Agent to get a full itemized estimate with Bay Area materials, labor, and O&P.
                      </div>
                      <Btn primary onClick={()=>{
                        // Save scope to localStorage so AI Agent can pick it up
                        try{
                          localStorage.setItem("grok_pending_estimate",JSON.stringify({
                            scope:scopeResult,
                            projectId:proj?.id,
                            projectName:proj?.name,
                            projectType:proj?.type,
                            projectAddr:proj?.addr,
                            fromBlueprint:blueprints[0]?.name||"Blueprint Review",
                            createdAt:new Date().toISOString()
                          }));
                        }catch(e){}
                        window.dispatchEvent(new CustomEvent("grok-navigate",{detail:{view:"ai-agent",tab:"estimate"}}));
                        alert("Opening AI Agent — your scope has been pre-loaded into the estimate input.");
                      }} style={{width:"100%",justifyContent:"center",background:T.purple,color:"white"}}>
                        💰 Get Full Estimate from this Scope →
                      </Btn>
                    </div>
                  )}
                </div>
              )}
            </Card>
          )}
        </div>
      )}

      {/* Products tab */}
      {activeTab==="products"&&(
        <BlueprintProductSearch proj={proj} activeBlueprint={activeBlueprint} session={session}/>
      )}

      {/* AI Takeoff tab — quantity extraction from drawings */}
      {activeTab==="takeoff"&&(
        <AITakeoffView proj={proj} activeBlueprint={activeBlueprint} session={session}/>
      )}

      {/* Code compliance checker */}
      {activeTab!=="products"&&activeBlueprint && (
        <CodeComplianceChecker
          activeBlueprint={activeBlueprint}
          flags={[]}
          projectType={proj?.type}
          buildingAge={proj?.age}
          onAutoFlag={(newFlags) => {
            // Save auto-flags to blueprint storage for Code Compliance tab
            try {
              const bpKey = "grok_blueprints_"+(proj?.id||"x");
              const existing = JSON.parse(localStorage.getItem(bpKey)||"[]");
              const merged = [...existing.filter(f=>!f.autoGenerated), ...newFlags];
              localStorage.setItem(bpKey, JSON.stringify(merged));
            } catch(e) {}
          }}
        />
      )}
    </div>
  );
}


// ─── BLUEPRINT REVIEW TOOL ────────────────────────────────────────────────────
// ── Deep Analysis helpers ────────────────────────────────────────────────────
// All pure — used by the Deep tab's collapsible-sections render + the print
// view. Defined at module level so they aren't re-created every render.

// Split deep-analysis markdown into ordered {header, body} sections by H2
// markers (## ...). Drops the LEARNINGS / JSON-block trailer entirely.
function parseDeepSections(text) {
  if (!text) return [];
  const parts = text.split(/^(##\s+[^\n]+)$/m);
  const sections = [];
  for (let i = 1; i < parts.length; i += 2) {
    const header = (parts[i] || "").replace(/^##\s+/, "").trim();
    const body = (parts[i + 1] || "").trim();
    if (!header) continue;
    if (/LEARNINGS/i.test(header)) continue;
    sections.push({ header, body });
  }
  return sections;
}

// Strip the internal learnings block from any text before it is rendered to a
// user. The JSON still gets parsed for the learning system upstream — this only
// trims the RENDERED copy. Module-level so both the module-level renderers
// (ReviewSections fallback, deep-analysis inline) and the component share ONE
// implementation. Matches any heading whose line contains "LEARNINGS" (the model
// sometimes drops the emoji, changes the level, or wraps it in parens) and, as a
// safety net, kills any leftover fenced/bare JSON trailer.
function stripLearningsBlock(text) {
  if (!text) return text;
  let out = text;
  const m = out.match(/^[ \t]*#+[ \t]*[^\n]*LEARNINGS[^\n]*$/im);
  if (m) out = out.slice(0, out.indexOf(m[0]));
  out = out.replace(/```json[\s\S]*?```/gi, "");
  out = out.replace(/```\s*\{[\s\S]*?\}\s*```/g, "");
  out = out.replace(/\n\s*\{[\s\S]*"project_type"[\s\S]*\}\s*$/m, "");
  out = out.replace(/\n\s*\{[\s\S]*"(?:material_takeoff|scope_by_trade|constructibility_flags|ve_suggestions|inspection_sequence)"[\s\S]*\}\s*$/m, "");
  return out.trimEnd();
}

// Split a "🏗 PROJECT OVERVIEW" section header into { icon, label }. Falls back
// to a wrench when the header has no leading emoji. Used to render arbitrary,
// trade-organized deep-analysis sections with a consistent icon + label.
function deepHeaderIcon(header) {
  const h = String(header || "").trim();
  const m = h.match(/^([\p{Extended_Pictographic}️☀-➿]+)\s*(.*)$/u);
  return (m && m[1]) ? { icon: m[1].trim(), label: (m[2] || h).trim() } : { icon: "🔧", label: h };
}

// Pull the actionable items out of the review's "⚠️ NOT SPECIFIED / VERIFY"
// section so each can get a "Draft RFI" affordance. Returns [] when the section
// is absent or explicitly says no gaps — never fabricated items.
function extractSpecGaps(reviewText) {
  if (!reviewText) return [];
  const parts = String(reviewText).split(/^##\s+/m);
  const sec = parts.find(s => /^[^\n]*(NOT SPECIFIED|SPECIFICATION GAP|VERIFY)/i.test(s));
  if (!sec) return [];
  const body = sec.replace(/^[^\n]*\n?/, "");
  if (/no (specification )?gaps?\s+(noted|found|identified)/i.test(body)) return [];
  const items = [];
  for (const raw of body.split("\n")) {
    const m = raw.match(/^\s*[-*•]\s+(.+?)\s*$/);
    if (!m) continue;
    const txt = m[1].replace(/\*\*/g, "").replace(/^["']|["']$/g, "").trim();
    if (txt && txt.length > 4) items.push(txt);
  }
  return items.slice(0, 24);
}

// Map a section header to a stable pill-nav id. Returns null when the section
// doesn't fit any known bucket — that section still renders, just without nav.
function sectionIdFor(header) {
  const h = String(header || "").toUpperCase();
  if (h.includes("MATERIAL TAKEOFF") || h.includes("MATERIAL SPECIFIC")) return "materials";
  if (h.includes("SCOPE CHECKLIST") || h.includes("SCOPE BY TRADE") || h.includes("TRADE-BY-TRADE") || h.includes("SCOPE OF WORK")) return "scope";
  if (h.includes("CONSTRUCTIBILITY") || h.includes("CODE FLAG") || h.includes("CODE COMPLIANCE")) return "flags";
  if (h.includes("VALUE ENGINEERING") || /\bVE\b/.test(h)) return "ve";
  if (h.includes("PERMIT")) return "permits";
  if (h.includes("INSPECTION")) return "inspections";
  return null;
}

// Split a section body into an ordered list of text + markdown-table chunks
// so we can render tables as proper HTML tables and prose as paragraphs.
function extractTablesAndText(body) {
  const lines = String(body || "").split("\n");
  const out = [];
  let buf = []; let tbl = []; let inTbl = false;
  const flushText = () => { if (buf.length) { out.push({ type: "text", content: buf.join("\n").trim() }); buf = []; }};
  const flushTbl = () => { if (tbl.length) { out.push({ type: "table", lines: tbl }); tbl = []; }};
  for (const line of lines) {
    const isTblLine = /^\s*\|.+\|\s*$/.test(line);
    if (isTblLine) {
      if (!inTbl) { flushText(); inTbl = true; }
      tbl.push(line);
    } else {
      if (inTbl) { flushTbl(); inTbl = false; }
      buf.push(line);
    }
  }
  flushText(); flushTbl();
  return out.filter(c => (c.type === "table" ? c.lines.length : c.content.length));
}

// Parse a block of markdown `|...|` lines into {header, rows}. Returns null
// when the block is too short to be a valid table.
function parseMarkdownTable(lines) {
  const rows = lines.map(l => l.trim().replace(/^\||\|$/g, "").split("|").map(c => c.trim()));
  if (rows.length < 2) return null;
  const header = rows[0];
  const sep = rows[1].every(c => /^:?-{2,}:?$/.test(c));
  const body = sep ? rows.slice(2) : rows.slice(1);
  return { header, rows: body };
}

// Lightweight markdown renderer (no deps). Handles #/##/### headings, **bold**,
// *italic*, `code`, and - / * / numbered lists. Everything else renders as a
// paragraph with line breaks preserved. Used to render AI (ARIA) responses as
// formatted text instead of raw markdown syntax. XSS-safe: builds React nodes,
// never dangerouslySetInnerHTML.
function MarkdownLite({ text, style }) {
  if (text == null || text === "") return null;
  const codeStyle = { background: T.bg2, border: "1px solid " + T.subtle, borderRadius: 4, padding: "1px 5px", fontSize: "0.9em", fontFamily: "ui-monospace,Menlo,monospace" };
  // Inline: **bold** / __bold__ / *italic* / `code`
  const inline = (s, kb) => {
    const out = [];
    const re = /\*\*([^*]+)\*\*|__([^_]+)__|\*([^*]+)\*|`([^`]+)`/g;
    let last = 0, m, k = 0;
    while ((m = re.exec(s)) !== null) {
      if (m.index > last) out.push(s.slice(last, m.index));
      if (m[1] != null || m[2] != null) out.push(<strong key={kb + "b" + k}>{m[1] || m[2]}</strong>);
      else if (m[3] != null) out.push(<em key={kb + "i" + k}>{m[3]}</em>);
      else if (m[4] != null) out.push(<code key={kb + "c" + k} style={codeStyle}>{m[4]}</code>);
      last = m.index + m[0].length; k++;
    }
    if (last < s.length) out.push(s.slice(last));
    return out;
  };
  const blocks = [];
  let list = null; // { ordered:bool, items:[] }
  const flushList = () => {
    if (!list) return;
    const Tag = list.ordered ? "ol" : "ul";
    blocks.push(
      <Tag key={"l" + blocks.length} style={{ margin: "4px 0 6px", paddingLeft: 20 }}>
        {list.items.map((it, j) => <li key={j} style={{ margin: "2px 0" }}>{inline(it, "l" + blocks.length + "-" + j)}</li>)}
      </Tag>
    );
    list = null;
  };
  String(text).split("\n").forEach((ln, i) => {
    const h = /^(#{1,3})\s+(.*)$/.exec(ln);
    const ul = /^\s*[-*]\s+(.*)$/.exec(ln);
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(ln);
    if (h) {
      flushList();
      const lvl = h[1].length;
      blocks.push(<div key={i} style={{ fontWeight: 800, fontSize: lvl === 1 ? "1.15em" : lvl === 2 ? "1.07em" : "1em", margin: "8px 0 3px" }}>{inline(h[2], "h" + i)}</div>);
    } else if (ul) {
      if (!list || list.ordered) { flushList(); list = { ordered: false, items: [] }; }
      list.items.push(ul[1]);
    } else if (ol) {
      if (!list || !list.ordered) { flushList(); list = { ordered: true, items: [] }; }
      list.items.push(ol[1]);
    } else if (ln.trim() === "") {
      flushList();
      blocks.push(<div key={i} style={{ height: 6 }} />);
    } else {
      flushList();
      blocks.push(<div key={i} style={{ margin: "2px 0" }}>{inline(ln, "p" + i)}</div>);
    }
  });
  flushList();
  return <div style={style}>{blocks}</div>;
}

// Tally helpers — give each section a useful "N items" hint in its collapsed
// header so the GC can see what's inside without expanding.
function deepSectionCount(body) {
  const chunks = extractTablesAndText(body);
  let total = 0;
  for (const c of chunks) {
    if (c.type === "table") {
      const t = parseMarkdownTable(c.lines);
      if (t) total += t.rows.length;
    } else {
      // Count bullet rows as items
      total += (c.content.match(/^\s*[-*•]\s+\S/gm) || []).length;
    }
  }
  return total;
}

// React component — render a markdown table as a proper HTML <table> with
// zebra striping, sticky thead, no horizontal scroll on desktop. Accepts a
// theme T from the call site to avoid global lookups.
function DeepTable({ table, T }) {
  if (!table) return null;
  const cellStyle = { padding: "6px 8px", fontSize: 11, color: T.text, borderBottom: "1px solid " + T.subtle, verticalAlign: "top", textAlign: "left" };
  return (
    <div style={{ overflowX: "auto", borderRadius: 7, border: "1px solid " + T.subtle, marginBottom: 10 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, tableLayout: "auto" }}>
        <thead>
          <tr>
            {table.header.map((h, i) => (
              <th key={i} style={{ ...cellStyle, position: "sticky", top: 0, background: T.bg2, fontWeight: 800, color: T.text, fontSize: 10, textTransform: "uppercase", letterSpacing: ".04em", borderBottom: "2px solid " + T.subtle, zIndex: 1 }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, ri) => (
            <tr key={ri} style={{ background: ri % 2 ? T.bg2 + "40" : "transparent" }}>
              {row.map((cell, ci) => (
                <td key={ci} style={cellStyle}>
                  <DeepInline text={cell}/>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Inline markdown: bold **...**, line breaks. Cheap, no full parser.
function DeepInline({ text }) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**")
          ? <strong key={i}>{p.slice(2, -2)}</strong>
          : <span key={i}>{p}</span>
      )}
    </>
  );
}

// Render section body as ordered text + table chunks.
function DeepSectionBody({ body, T }) {
  const chunks = React.useMemo(() => extractTablesAndText(body), [body]);
  if (!chunks.length) return null;
  return (
    <>
      {chunks.map((c, i) => {
        if (c.type === "table") {
          const tbl = parseMarkdownTable(c.lines);
          return <DeepTable key={i} table={tbl} T={T}/>;
        }
        // Text: render bullets + headings + paragraphs with light inline parsing
        const lines = c.content.split("\n");
        return (
          <div key={i} style={{ marginBottom: 10, fontSize: 11.5, color: T.text, lineHeight: 1.65 }}>
            {lines.map((ln, li) => {
              if (!ln.trim()) return <div key={li} style={{ height: 6 }}/>;
              if (/^###\s+/.test(ln)) {
                return <div key={li} style={{ fontSize: 11, fontWeight: 800, color: T.text, marginTop: 10, marginBottom: 4 }}>{ln.replace(/^###\s+/, "")}</div>;
              }
              if (/^\s*[-*•]\s+/.test(ln)) {
                return (
                  <div key={li} style={{ display: "flex", gap: 6, padding: "2px 0" }}>
                    <span style={{ color: T.muted, flexShrink: 0 }}>•</span>
                    <span><DeepInline text={ln.replace(/^\s*[-*•]\s+/, "")}/></span>
                  </div>
                );
              }
              return <div key={li}><DeepInline text={ln}/></div>;
            })}
          </div>
        );
      })}
    </>
  );
}

// Collapsible section card.
function DeepSection({ id, icon, label, header, body, defaultOpen, T, sectionRef, badge }) {
  const [open, setOpen] = React.useState(!!defaultOpen);
  const count = React.useMemo(() => deepSectionCount(body), [body]);
  return (
    <div ref={sectionRef} id={id ? "deep-sec-" + id : undefined}
      style={{ marginBottom: 10, background: T.bg1, border: "1px solid " + T.subtle, borderRadius: 9, scrollMarginTop: 56 }}>
      <button onClick={() => setOpen(o => !o)} aria-expanded={open}
        style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", color: T.text }}>
        <span style={{ fontSize: 14 }}>{icon || "▸"}</span>
        <span style={{ flex: 1, fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em" }}>{label || header}</span>
        {badge ? <span style={{ fontSize: 10.5, color: T.gold, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{badge}</span>
          : count > 0 && <span style={{ fontSize: 10, color: T.muted, fontWeight: 700 }}>{count} item{count === 1 ? "" : "s"}</span>}
        <span style={{ fontSize: 12, color: T.muted, transition: "transform 0.15s", transform: open ? "rotate(90deg)" : "rotate(0deg)", display: "inline-block" }}>▸</span>
      </button>
      {open && (
        <div style={{ padding: "0 12px 12px 12px", borderTop: "1px solid " + T.subtle }}>
          <div style={{ paddingTop: 10 }}>
            <DeepSectionBody body={body} T={T}/>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Initial ARIA review rendering (reader-first) ----------------------------
// A GC should get the story in ~10s, then drill. Order: PROJECT SUMMARY (top,
// always open) → SPECIFIED MATERIALS grouped by system with verbatim quotes
// styled distinctly → NOT SPECIFIED / VERIFY (Draft-RFI rows) → SCOPE BY TRADE →
// Codes / Cost / Schedule collapsed. Reuses the DeepSection card so the review
// and deep tabs look like one system. All markdown artifacts (---, ```, stray
// **) are stripped so nothing raw leaks into the UI.

// Drop rule lines / stray fences that would otherwise render as literal text.
function cleanReviewBody(body) {
  return String(body || "")
    .split("\n")
    .filter(ln => !/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(ln) && !/^\s*```/.test(ln))
    .join("\n")
    .trim();
}

// Classify a review section header into a stable kind for ordering + styling.
function reviewSectionKind(header) {
  const h = String(header || "").toUpperCase();
  if (/PROJECT SUMMARY|OVERVIEW|^SUMMARY/.test(h)) return "summary";
  if (/SPECIFIED MATERIAL|MATERIALS?\s*&?\s*SYSTEM/.test(h)) return "materials";
  if (/NOT SPECIFIED|VERIFY|SPEC(IFICATION)? GAP/.test(h)) return "gaps";
  if (/SCOPE/.test(h)) return "scope";
  if (/CODE/.test(h)) return "codes";
  if (/COST|BUDGET|\$/.test(h)) return "cost";
  if (/SCHEDULE|DURATION|TIMELINE/.test(h)) return "schedule";
  return "other";
}

// A section's collapsed HEADLINE badge — so a GC reads the takeaway without
// expanding (cost range, schedule length, trade/flag counts).
function reviewBadge(kind, body) {
  const b = String(body || "");
  if (kind === "cost") {
    const rangeRe = /\$[\d.,]+\s*[kKmM]?\s*[–—-]\s*\$[\d.,]+\s*[kKmM]?/;
    const rangeReG = new RegExp(rangeRe.source, "g");
    // Prefer the explicit headline Low/High row so the badge always reflects THIS
    // run's number, never a prior/superseded figure that also carries a range.
    const hl = b.match(new RegExp("low\\s*/\\s*high[^\\n]*?(" + rangeRe.source + ")", "i"));
    if (hl) return hl[1].replace(/\s+/g, "");
    // Otherwise the last range that is NOT on a "supersedes prior estimate" line
    // (that line quotes the OLD range and must never become the collapsed badge).
    const ranges = b.split("\n").filter(ln => !/supersed|prior estimate/i.test(ln)).join("\n").match(rangeReG);
    if (ranges && ranges.length) return ranges[ranges.length - 1].replace(/\s+/g, "");
    const total = b.match(/TOTAL[^$]*(\$[\d.,]+\s*[kKmM]?)/i);
    return total ? total[1].replace(/\s+/g, "") : null;
  }
  if (kind === "schedule") {
    const wk = b.match(/([\d.]+)\s*weeks?/i);
    return wk ? "~" + wk[1] + " wk" : null;
  }
  if (kind === "scope") {
    const n = (b.match(/^###\s+/gm) || []).length;
    return n ? n + " trade" + (n === 1 ? "" : "s") : null;
  }
  if (kind === "codes") {
    const n = (b.match(/^\s*[-*•]\s+/gm) || []).length;
    return n ? n + " flag" + (n === 1 ? "" : "s") : null;
  }
  return null;
}

// One verbatim callout line: the quoted string is styled distinctly (mono, boxed)
// so extracted drawing text visibly differs from AI commentary.
function VerbatimQuote({ quote, rest, T }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "3px 0" }}>
      <span style={{ color: T.muted, flexShrink: 0 }}>•</span>
      <span style={{ lineHeight: 1.7 }}>
        <span style={{ fontFamily: "ui-monospace,Menlo,monospace", fontSize: 11, fontWeight: 700, color: T.gold, background: T.bg2, border: "1px solid " + T.subtle, borderRadius: 4, padding: "1px 6px", whiteSpace: "pre-wrap" }}>&ldquo;{quote}&rdquo;</span>
        {rest ? <span style={{ fontSize: 11.5, color: T.text }}> — <DeepInline text={rest}/></span> : null}
      </span>
    </div>
  );
}

// Materials body — organized by PRODUCT: a bold "Name — role" header line
// (readable, scannable), then nested verbatim-quote occurrences as evidence (the
// exact drawing text, gold-boxed, + citation). Comprehension first, verbatim
// preserved underneath.
function ReviewMaterialsBody({ body, T }) {
  const lines = cleanReviewBody(body).split("\n");
  const chip = { fontFamily: "ui-monospace,Menlo,monospace", fontSize: 11, fontWeight: 700, color: T.gold, background: T.bg2, border: "1px solid " + T.subtle, borderRadius: 4, padding: "1px 6px", whiteSpace: "pre-wrap" };
  const out = [];
  lines.forEach((ln, i) => {
    const raw = ln.trim();
    if (!raw) { out.push(<div key={i} style={{ height: 4 }}/>); return; }
    // System group divider.
    const grp = /^###\s+(.*)$/.exec(raw);
    if (grp) {
      out.push(<div key={i} style={{ fontSize: 10, fontWeight: 800, color: T.muted, textTransform: "uppercase", letterSpacing: ".06em", margin: "13px 0 5px", paddingBottom: 3, borderBottom: "1px solid " + T.subtle }}>{grp[1].trim()}</div>);
      return;
    }
    // Product header: a fully-bold line (optionally bulleted) — "Name — role".
    const ph = /^(?:[-*•]\s+)?\*\*([^\n]+?)\*\*[:：]?[ \t]*$/.exec(raw);
    if (ph) {
      const inner = ph[1].trim();
      const sp = inner.split(/\s+[—–]\s+|\s+-\s+/);
      const name = sp[0].trim();
      const role = sp.slice(1).join(" — ").trim();
      out.push(
        <div key={i} style={{ margin: "10px 0 2px" }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: T.text }}>{name}</span>
          {role && <span style={{ fontSize: 11, color: T.muted, fontWeight: 600 }}> — {role}</span>}
        </div>
      );
      return;
    }
    // Evidence / deferred bullet.
    const bullet = /^[-*•]\s+([\s\S]*)$/.exec(raw);
    if (bullet) {
      const c = bullet[1].trim();
      if (/^Product not identified/i.test(c)) {
        out.push(<div key={i} style={{ fontSize: 11, color: T.amber, lineHeight: 1.6, padding: "2px 0 2px 14px" }}>{c}</div>);
        return;
      }
      if (/^⚠/.test(c)) {
        out.push(<div key={i} style={{ fontSize: 11, fontWeight: 700, color: T.amber, lineHeight: 1.6, padding: "3px 0 3px 14px" }}>{c}</div>);
        return;
      }
      const ev = /^["“]([\s\S]+?)["”]\s*[—–-]\s*([\s\S]+)$/.exec(c) || /^["“]([\s\S]+)["”]\s*$/.exec(c);
      if (ev) {
        const quote = ev[1].trim();
        const cite = (ev[2] || "").trim();
        out.push(
          <div key={i} style={{ display: "flex", gap: 7, alignItems: "baseline", padding: "2px 0 2px 14px" }}>
            <span style={{ color: T.muted, flexShrink: 0, fontSize: 9 }}>▪</span>
            <span style={{ lineHeight: 1.7 }}>
              <span style={chip}>&ldquo;{quote}&rdquo;</span>
              {cite && <span style={{ fontSize: 11, color: T.muted }}> — {cite}</span>}
            </span>
          </div>
        );
        return;
      }
      out.push(
        <div key={i} style={{ display: "flex", gap: 7, padding: "2px 0 2px 14px" }}>
          <span style={{ color: T.muted, flexShrink: 0, fontSize: 9 }}>▪</span>
          <span style={{ fontSize: 11.5, color: T.text, lineHeight: 1.6 }}><DeepInline text={c}/></span>
        </div>
      );
      return;
    }
    out.push(<div key={i} style={{ fontSize: 11.5, color: T.text, lineHeight: 1.6, margin: "2px 0" }}><DeepInline text={raw}/></div>);
  });
  return <>{out}</>;
}

// A plain (non-collapsible) card shell used for the top/always-open sections.
function ReviewCard({ icon, label, accent, children, T }) {
  return (
    <div style={{ marginBottom: 10, background: T.bg1, border: "1px solid " + T.subtle, borderRadius: 9, padding: "10px 12px" }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: accent || T.text, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6 }}>{icon} {label}</div>
      {children}
    </div>
  );
}

// A collapsible card with the DeepSection chrome but arbitrary children (so the
// custom Materials body — grouped, styled quotes — collapses like the others).
function CollapsibleCard({ icon, label, badge, accent, defaultOpen, T, children }) {
  const [open, setOpen] = React.useState(!!defaultOpen);
  return (
    <div style={{ marginBottom: 10, background: T.bg1, border: "1px solid " + T.subtle, borderRadius: 9 }}>
      <button onClick={() => setOpen(o => !o)} aria-expanded={open}
        style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", color: T.text }}>
        <span style={{ fontSize: 14 }}>{icon || "▸"}</span>
        <span style={{ flex: 1, fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em", color: accent || T.text }}>{label}</span>
        {badge && <span style={{ fontSize: 10.5, color: T.gold, fontWeight: 800 }}>{badge}</span>}
        <span style={{ fontSize: 12, color: T.muted, transition: "transform 0.15s", transform: open ? "rotate(90deg)" : "rotate(0deg)", display: "inline-block" }}>▸</span>
      </button>
      {open && <div style={{ padding: "0 12px 12px", borderTop: "1px solid " + T.subtle }}><div style={{ paddingTop: 10 }}>{children}</div></div>}
    </div>
  );
}

// Collapsed-headline badge for Materials: the headline product + PRODUCT count
// ("CIM 1000 · 8 products"). Products = bold "Name — role" header lines.
function materialsBadge(body) {
  const b = cleanReviewBody(body);
  const prod = b.match(/^(?:[-*•]\s+)?\*\*[^\n]+?\*\*[:：]?[ \t]*$/gm) || [];
  const count = prod.length;
  let head = "";
  if (prod.length) {
    const m = /\*\*([^\n]+?)\*\*/.exec(prod[0]);
    head = m ? m[1].split(/\s+[—–]\s+|\s+-\s+/)[0].trim() : "";
  }
  if (head.length > 22) head = head.slice(0, 20).trim() + "…";
  const n = `${count} product${count === 1 ? "" : "s"}`;
  return head ? `${head} · ${n}` : n;
}

// ── Unified report: deep-analysis slots ──────────────────────────────────────
// The review and the deep pass are ONE document. Below the review, five deep
// sections render as collapsed PLACEHOLDER cards; a single "⚡ Add Deep Analysis"
// button runs the (separately metered) second full-drawing pass, and the results
// fill these same cards IN PLACE. Where the deep pass overlaps a review section
// (scope, code flags) it SUPERSEDES it — the review card is dropped and the deep
// card carries a one-line provenance note. There is never a second cost table.
const DEEP_SLOTS = [
  { key: "takeoff",    icon: "📦", label: "Material Takeoff",       match: /TAKEOFF/i,
    hint: "A consolidated quantity takeoff across every trade — the estimate's evidence layer." },
  { key: "checklists", icon: "✅", label: "Trade Checklists",       match: /TRADE CHECKLIST|CHECKLIST|SCOPE BY TRADE|TRADE-BY-TRADE/i,
    hint: "Per-trade scope checklists in build sequence — each item a sub can check off.",
    provenance: "Supersedes the review's Scope by Trade — second full-drawing pass." },
  { key: "flags",      icon: "⚠️", label: "Constructibility Flags", match: /CONSTRUCTIBILITY|COORDINATION/i,
    hint: "Coordination, sequencing & code risks across all trades, ranked high → low.",
    provenance: "Supersedes the review's Code Flags — second full-drawing pass." },
  { key: "ve",         icon: "💡", label: "Value Engineering",      match: /VALUE ENGINEERING|\bVE\b/i,
    hint: "Cost-savings alternatives, each with its trade-off." },
  { key: "permits",    icon: "📋", label: "Permits & Inspections",  match: /PERMIT|INSPECTION/i,
    hint: "Permits, AHJs, fees, lead times & the inspection sequence." },
];

// One deep slot — collapsed placeholder before the deep pass runs, filled in
// place after. Placeholder shows the hint + a "not yet run" pill and can't
// expand; while the pass streams it shows "analyzing…"; once its body arrives it
// behaves like a normal collapsible section (with an optional provenance note).
function DeepSlotCard({ icon, label, hint, body, analyzing, provenance, defaultOpen, T }) {
  const filled = !!(body && body.trim());
  const [open, setOpen] = React.useState(!!defaultOpen);
  const count = React.useMemo(() => (filled ? deepSectionCount(body) : 0), [filled, body]);
  const interactive = filled;
  return (
    <div style={{ marginBottom: 10, background: T.bg1, border: "1px solid " + T.subtle, borderRadius: 9, opacity: filled ? 1 : 0.9 }}>
      <button onClick={() => interactive && setOpen(o => !o)} aria-expanded={open} disabled={!interactive}
        style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: "transparent", border: "none", cursor: interactive ? "pointer" : "default", textAlign: "left", color: T.text }}>
        <span style={{ fontSize: 14, opacity: filled ? 1 : 0.6 }}>{icon || "▸"}</span>
        <span style={{ flex: 1, fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em", color: filled ? T.text : T.muted }}>{label}</span>
        {analyzing && !filled
          ? <span style={{ fontSize: 10, color: T.purple, fontWeight: 800 }}>analyzing…</span>
          : filled
            ? (count > 0 && <span style={{ fontSize: 10.5, color: T.gold, fontWeight: 800 }}>{count} item{count === 1 ? "" : "s"}</span>)
            : <span style={{ fontSize: 9.5, color: T.muted, fontWeight: 700, border: "1px solid " + T.subtle, borderRadius: 10, padding: "1px 7px", whiteSpace: "nowrap" }}>not yet run</span>}
        {interactive && <span style={{ fontSize: 12, color: T.muted, transition: "transform 0.15s", transform: open ? "rotate(90deg)" : "rotate(0deg)", display: "inline-block" }}>▸</span>}
      </button>
      {!filled && !analyzing && hint && (
        <div style={{ padding: "0 12px 10px 34px", fontSize: 10.5, color: T.muted, lineHeight: 1.5 }}>{hint}</div>
      )}
      {open && filled && (
        <div style={{ padding: "0 12px 12px 12px", borderTop: "1px solid " + T.subtle }}>
          <div style={{ paddingTop: 10 }}>
            {provenance && <div style={{ fontSize: 10, color: T.muted, fontStyle: "italic", marginBottom: 8 }}>↳ {provenance}</div>}
            <DeepSectionBody body={body} T={T}/>
          </div>
        </div>
      )}
    </div>
  );
}

// The deep-analysis region rendered inline at the bottom of the unified report.
// `deep` bundle: { text, analyzing, ready, error, tooLarge, page, onRun, onExport,
// pageBadge, analyzeThisPageBtn }. Renders the single ⚡ button (pre-run), the
// deep overview intro + Export/Re-run (post-run), and the five slot cards.
function DeepRegion({ deep, deepSections, hasDeep, T }) {
  const bySlot = {};
  let overview = null;
  const leftovers = [];
  for (const s of deepSections) {
    if (/PROJECT OVERVIEW|^OVERVIEW/i.test(s.header)) { if (!overview) overview = s; continue; }
    let placed = false;
    for (const slot of DEEP_SLOTS) {
      if (!bySlot[slot.key] && slot.match.test(s.header)) { bySlot[slot.key] = s; placed = true; break; }
    }
    if (!placed) leftovers.push(s);
  }
  const analyzing = !!deep.analyzing;
  const ready = !!deep.ready;
  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: "2px solid " + T.subtle }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: T.purple, textTransform: "uppercase", letterSpacing: ".06em" }}>🔬 Deep Analysis</div>
        {hasDeep && !analyzing && (
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            {deep.onExport && (
              <button onClick={deep.onExport} title="Open a print-ready view in a new window"
                style={{ fontSize: 10, fontWeight: 700, background: T.bg3, color: T.text, border: "1px solid " + T.subtle, borderRadius: 7, padding: "5px 10px", cursor: "pointer" }}>📄 Export</button>
            )}
            <button onClick={deep.onRun}
              style={{ fontSize: 10, fontWeight: 700, background: T.purple, color: "white", border: "none", borderRadius: 7, padding: "5px 10px", cursor: "pointer" }}>🔄 Re-run</button>
          </div>
        )}
      </div>
      {hasDeep && deep.page != null && deep.pageBadge && <div style={{ marginBottom: 8 }}>{deep.pageBadge(deep.page)}</div>}
      {hasDeep && overview && (
        <div style={{ fontSize: 11.5, color: T.text, lineHeight: 1.65, marginBottom: 10, background: T.bg2, border: "1px solid " + T.subtle, borderRadius: 9, padding: "10px 12px" }}>
          <DeepSectionBody body={cleanReviewBody(overview.body)} T={T}/>
        </div>
      )}
      {!hasDeep && (
        <>
          <button onClick={deep.onRun} disabled={!ready || analyzing}
            style={{ width: "100%", background: (!ready || analyzing) ? T.bg3 : "linear-gradient(135deg," + T.purple + "," + (T.purple2 || T.purple) + ")", color: (!ready || analyzing) ? T.muted : "white", border: "none", borderRadius: 10, padding: "14px 16px", fontSize: 14, fontWeight: 800, cursor: (!ready || analyzing) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: (!ready || analyzing) ? "none" : "0 4px 14px " + T.purple + "55", letterSpacing: ".02em", opacity: (!ready || analyzing) ? 0.7 : 1 }}>
            {analyzing ? "🔬 Running Deep Analysis…" : ready ? "⚡ Add Deep Analysis" : "⚡ Add Deep Analysis (finish the review first)"}
          </button>
          <div style={{ fontSize: 10, color: T.muted, textAlign: "center", lineHeight: 1.5, margin: "6px 0 12px" }}>second full-drawing pass · counts as 1 analysis</div>
        </>
      )}
      {analyzing && (
        <div style={{ fontSize: 11, color: T.purple, fontWeight: 700, marginBottom: 10 }}>▍ Reading the full drawing — the sections below fill in as they finish…</div>
      )}
      {deep.error && (
        <div style={{ marginBottom: 10, padding: "9px 12px", background: T.redBg, border: "1px solid " + T.red + "40", borderRadius: 8, fontSize: 11, color: T.red }}>
          ⚠ {deep.error}
          {deep.tooLarge && deep.analyzeThisPageBtn && <div>{deep.analyzeThisPageBtn(deep.onRun, analyzing)}</div>}
        </div>
      )}
      {DEEP_SLOTS.map(slot => {
        const sec = bySlot[slot.key];
        const body = sec ? cleanReviewBody(sec.body) : null;
        return (
          <DeepSlotCard key={slot.key} icon={slot.icon} label={slot.label} hint={slot.hint}
            body={body} analyzing={analyzing && !(body && body.trim())} provenance={slot.provenance}
            defaultOpen={slot.key === "takeoff"} T={T}/>
        );
      })}
      {leftovers.map((s, i) => {
        const { icon, label } = deepHeaderIcon(s.header);
        return <DeepSection key={"dl" + i} icon={icon} label={label} header={s.header} body={cleanReviewBody(s.body)} defaultOpen={false} T={T}/>;
      })}
    </div>
  );
}

// Full review renderer. `renderGaps(gapsBody)` supplies the NOT SPECIFIED body so
// the component (which owns RFI state) can render Draft-RFI rows. `deep` (optional)
// bundles the deep-analysis state + callbacks; when present, the deep region is
// rendered inline below the review as ONE document (see DeepRegion), and the
// review's Scope / Code-Flags cards are dropped once deep supersedes them.
function ReviewSections({ text, T, renderGaps, deep }) {
  const sections = React.useMemo(() => parseDeepSections(text || ""), [text]);
  const deepText = (deep && deep.text) || "";
  const deepSections = React.useMemo(() => parseDeepSections(deepText), [deepText]);
  const hasDeep = deepSections.length > 0;
  const byKind = {};
  const others = [];
  for (const s of sections) {
    const kind = reviewSectionKind(s.header);
    if (kind !== "other" && !byKind[kind]) byKind[kind] = s;
    else others.push(s);
  }
  const blocks = [];
  if (!sections.length && text) blocks.push(<MarkdownLite key="raw" text={stripLearningsBlock(text)}/>);
  if (byKind.summary) blocks.push(
    <div key="summary" style={{ marginBottom: 12, background: T.bg2, border: "1px solid " + (T.gold || T.subtle), borderRadius: 9, padding: "12px 14px" }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: T.gold, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6 }}>📋 Project Summary</div>
      <div style={{ fontSize: 12, color: T.text, lineHeight: 1.65 }}><DeepSectionBody body={cleanReviewBody(byKind.summary.body)} T={T}/></div>
      {hasDeep && <div style={{ fontSize: 10, color: T.muted, fontStyle: "italic", marginTop: 8 }}>🔬 Refined by deep analysis — takeoff, checklists, VE & permits below.</div>}
    </div>
  );
  if (byKind.materials) blocks.push(
    <CollapsibleCard key="materials" icon="📐" label="Specified Materials & Systems" defaultOpen={false} badge={materialsBadge(byKind.materials.body)} T={T}>
      <ReviewMaterialsBody body={byKind.materials.body} T={T}/>
    </CollapsibleCard>
  );
  blocks.push(
    <ReviewCard key="gaps" icon="⚠️" label="Not Specified / Verify" accent={T.amber} T={T}>
      {(renderGaps && renderGaps(byKind.gaps && byKind.gaps.body)) ||
        <div style={{ fontSize: 11.5, color: T.muted, lineHeight: 1.6 }}><DeepSectionBody body={cleanReviewBody((byKind.gaps && byKind.gaps.body) || "No specification gaps noted.")} T={T}/></div>}
    </ReviewCard>
  );
  const collapse = (kind, open) => { const s = byKind[kind]; if (!s) return; const { icon, label } = deepHeaderIcon(s.header); const cleaned = cleanReviewBody(s.body); blocks.push(<DeepSection key={kind} icon={icon} label={label} header={s.header} body={cleaned} defaultOpen={open} badge={reviewBadge(kind, cleaned)} T={T}/>); };
  // Scope by Trade — DROPPED once the deep pass supersedes it with Trade
  // Checklists (see DeepRegion). Never render both: no duplicated scope.
  if (!hasDeep) collapse("scope", true);
  // Cost: hard-render the permanent directional disclaimer under the tables,
  // regardless of whether the model included it (deduped so it never doubles up).
  // The deep pass has no cost table, so this stays the single, authoritative one.
  if (byKind.cost) {
    const s = byKind.cost; const { icon, label } = deepHeaderIcon(s.header);
    let cleaned = cleanReviewBody(s.body);
    if (!/pricing is directional/i.test(cleaned)) cleaned += "\n\n_AI pricing is directional — calibrate against local sub quotes._";
    blocks.push(<DeepSection key="cost" icon={icon} label={label} header={s.header} body={cleaned} defaultOpen={false} badge={reviewBadge("cost", cleaned)} T={T}/>);
  }
  collapse("schedule", false);
  // Code flags render LAST of the review sections — reference material a GC checks
  // after the narrative. DROPPED once the deep pass supersedes them with the
  // consolidated Constructibility Flags (see DeepRegion): no duplicated flags.
  if (!hasDeep) collapse("codes", false);
  others.forEach((s, i) => { const { icon, label } = deepHeaderIcon(s.header); blocks.push(<DeepSection key={"o" + i} icon={icon} label={label} header={s.header} body={cleanReviewBody(s.body)} defaultOpen={false} T={T}/>); });
  // The deep-analysis region — one document: placeholders before the pass runs,
  // filled in place after. Rendered only when the caller wires up `deep`.
  if (deep) blocks.push(<DeepRegion key="deep" deep={deep} deepSections={deepSections} hasDeep={hasDeep} T={T}/>);
  return <>{blocks}</>;
}

// Pill nav definitions for the sticky header. Order matches user spec.
const DEEP_NAV = [
  { id: "materials",   icon: "📦", label: "Materials" },
  { id: "scope",       icon: "✅", label: "Scope" },
  { id: "flags",       icon: "⚠️", label: "Flags" },
  { id: "ve",          icon: "💡", label: "VE" },
  { id: "permits",     icon: "📋", label: "Permits" },
  { id: "inspections", icon: "🔍", label: "Inspections" },
];

// Interactive scope checklist — wraps deepStructured.scope_by_trade in a
// collapsible card with the same look as the other DeepSections so the GC
// gets a consistent UI across sections.
function ScopeSectionInteractive({ trades, checkedScope, toggleScopeItem, T, defaultOpen }) {
  const [open, setOpen] = React.useState(!!defaultOpen);
  const total = trades.reduce((s, t) => s + (t.items || []).length, 0);
  const done = trades.reduce((s, t, ti) => s + (t.items || []).filter((_, ii) => checkedScope[`${ti}_${ii}`]).length, 0);
  return (
    <>
      <button onClick={() => setOpen(o => !o)} aria-expanded={open}
        style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", color: T.text }}>
        <span style={{ fontSize: 14 }}>✅</span>
        <span style={{ flex: 1, fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em" }}>Scope</span>
        <span style={{ fontSize: 10, color: done === total && total > 0 ? T.green : T.muted, fontWeight: 700 }}>{done}/{total} done</span>
        <span style={{ fontSize: 12, color: T.muted, transform: open ? "rotate(90deg)" : "rotate(0deg)", display: "inline-block" }}>▸</span>
      </button>
      {open && (
        <div style={{ padding: "0 12px 12px 12px", borderTop: "1px solid " + T.subtle }}>
          <div style={{ paddingTop: 10 }}>
            {trades.map((trade, ti) => {
              const tTotal = (trade.items || []).length;
              const tDone = (trade.items || []).filter((_, ii) => checkedScope[`${ti}_${ii}`]).length;
              return (
                <div key={ti} style={{ marginBottom: 10, background: T.bg2, borderRadius: 8, padding: "9px 11px", border: "1px solid " + T.subtle }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: T.text }}>{trade.trade}</span>
                    <span style={{ fontSize: 9, color: tDone === tTotal && tTotal > 0 ? T.green : T.muted, fontWeight: 700 }}>{tDone}/{tTotal} complete</span>
                  </div>
                  {(trade.items || []).map((item, ii) => {
                    const key = `${ti}_${ii}`;
                    const checked = !!checkedScope[key];
                    return (
                      <label key={ii} style={{ display: "flex", alignItems: "flex-start", gap: 7, padding: "4px 0", cursor: "pointer", fontSize: 11, color: checked ? T.muted : T.text, lineHeight: 1.5, textDecoration: checked ? "line-through" : "none" }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleScopeItem(key)}
                          style={{ marginTop: 3, accentColor: T.purple, cursor: "pointer", flexShrink: 0 }}/>
                        <span>{item}</span>
                      </label>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

// Opens a clean print-ready window for the deep analysis. Uses the same
// section parser as the panel render so what you see matches what prints.
function openDeepPrintView(deepText, blueprint, project) {
  const w = window.open("", "_blank", "noopener");
  if (!w) return;
  const sections = parseDeepSections(deepText || "");
  const projName = project?.name || "Project";
  const projAddr = project?.addr || "";
  const bpName   = blueprint?.name || "Blueprint";
  const date = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#39;"})[c]);
  const renderInlineHTML = (text) => esc(text).replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  const renderBodyHTML = (body) => {
    const chunks = extractTablesAndText(body);
    return chunks.map(c => {
      if (c.type === "table") {
        const t = parseMarkdownTable(c.lines);
        if (!t) return "";
        return `<table><thead><tr>${t.header.map(h => `<th>${renderInlineHTML(h)}</th>`).join("")}</tr></thead>`
             + `<tbody>${t.rows.map(r => `<tr>${r.map(cell => `<td>${renderInlineHTML(cell)}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
      }
      const lines = c.content.split("\n");
      const html = lines.map(ln => {
        if (!ln.trim()) return "<br/>";
        if (/^###\s+/.test(ln)) return `<h4>${renderInlineHTML(ln.replace(/^###\s+/, ""))}</h4>`;
        if (/^\s*[-*•]\s+/.test(ln)) return `<li>${renderInlineHTML(ln.replace(/^\s*[-*•]\s+/, ""))}</li>`;
        return `<p>${renderInlineHTML(ln)}</p>`;
      }).join("");
      return html.includes("<li>") ? `<ul>${html.replace(/<\/li><p>/g, "</li><p>")}</ul>` : html;
    }).join("");
  };
  w.document.write(`<!DOCTYPE html><html><head><title>Deep Analysis — ${esc(projName)}</title>
<style>
  @page { size: letter; margin: 0.6in; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif; color:#1a1a1a; font-size: 12px; line-height: 1.55; max-width: 720px; margin: 24px auto; padding: 0 24px; }
  .banner { background:#0F1623; padding:18px 24px; }
  .brand { font-size:22px; font-weight:900; color:#D4A843; }
  .brand-sub { font-size:11px; color:#fff; font-weight:700; margin-top:3px; }
  .gold-bar { height:3px; background:#D4A843; margin-bottom:18px; }
  .meta { display:flex; gap:24px; flex-wrap:wrap; margin: 12px 0 18px; font-size:11px; color:#444; }
  .meta b { color:#1a1a1a; }
  h2 { font-size: 14px; color:#1a1a1a; margin: 22px 0 10px; padding-bottom: 4px; border-bottom: 2px solid #D4A843; text-transform: uppercase; letter-spacing: .04em; }
  h4 { font-size: 12px; margin: 12px 0 4px; color:#1a1a1a; }
  p { margin: 4px 0; }
  ul { margin: 4px 0 8px 18px; padding: 0; }
  li { margin: 2px 0; }
  table { width: 100%; border-collapse: collapse; margin: 6px 0 12px; font-size: 11px; }
  th, td { padding: 6px 8px; text-align: left; border-bottom: 1px solid #ddd; vertical-align: top; }
  th { background: #f4f1e6; font-weight: 800; text-transform: uppercase; font-size: 10px; letter-spacing: .04em; border-bottom: 2px solid #D4A843; }
  tbody tr:nth-child(even) { background: #faf9f5; }
  .footer { margin-top: 28px; padding-top: 12px; border-top: 1px solid #eee; font-size: 10px; color: #888; text-align: center; }
  @media print { body { margin: 0; padding: 0; } .no-print { display: none; } }
  .print-btn { position: fixed; top: 16px; right: 16px; background:#D4A843; color:#0F1623; border:none; border-radius: 7px; padding: 8px 14px; font-size: 12px; font-weight: 800; cursor: pointer; }
</style></head><body>
<button class="print-btn no-print" onclick="window.print()">Print / Save PDF</button>
<div class="banner">
  <div class="brand">🔬 Deep Analysis</div>
  <div class="brand-sub">${esc(projName)}${projAddr ? " · " + esc(projAddr) : ""}</div>
</div>
<div class="gold-bar"></div>
<div class="meta">
  <span><b>Project:</b> ${esc(projName)}</span>
  <span><b>Drawing:</b> ${esc(bpName)}</span>
  <span><b>Generated:</b> ${esc(date)}</span>
</div>
${sections.map(s => `<h2>${renderInlineHTML(s.header)}</h2>${renderBodyHTML(s.body)}`).join("")}
<div class="footer">Generated by Grok Field Ops · Review on-site before procurement.</div>
</body></html>`);
  w.document.close();
}

function BlueprintView({projects, proj, session, projectMode, setView, onWorkProduct}) {
  const getCid = () => session?.companyId || window._grokCompanyId ||
    (() => { try { return JSON.parse(localStorage.getItem("grok_auth_session")||"{}").companyId || ""; } catch { return ""; }})();
  const liveProjects = (() => {
    const cid = getCid();
    // Always prefer real localStorage data over any passed-in props (which may be demo data)
    try { const s = localStorage.getItem("grok_projects_"+cid); if(s) { const p=JSON.parse(s); if(p.length) return p; } } catch{}
    // Only use passed projects if they are not demo projects
    const realProjects = (projects||[]).filter(p=>!p.id?.startsWith('demo_'));
    if (realProjects.length) return realProjects;
    return [];
  })();
  const [selectedProject, setSelectedProject] = useState(proj || liveProjects[0] || null);

  // Re-check after mount in case session resolved late
  React.useEffect(()=>{
    if(!selectedProject && !proj){
      const cid = getCid();
      try { const s = localStorage.getItem("grok_projects_"+cid); if(s){ const p=JSON.parse(s); if(p.length){setSelectedProject(p[0]);return;}} } catch{}
    }
  },[]); // eslint-disable-line
  const [blueprints, setBlueprints] = useState({});
  const [activeBlueprint, setActiveBlueprint] = useState(null);

  // PDF page navigation state. currentPage is 1-indexed. pageCount populated
  // when PdfCanvas finishes loading the document.
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  // AI review state. aiBpId tracks which blueprint id the current review is for
  // so switching blueprints clears the panel.
  const [aiReview, setAiReview] = useState("");
  const [aiReviewing, setAiReviewing] = useState(false);
  const [aiError, setAiError] = useState("");
  // Set when a continuation round fails AFTER we already have review content — the
  // sections we have are kept and shown with a retry affordance instead of a bare
  // error wiping the whole review.
  const [aiIncomplete, setAiIncomplete] = useState(false);
  // When each analysis was last saved to the cloud (blueprints table), so it
  // survives reload and shows an "Analyzed <when>" stamp.
  const [analyzedAt, setAnalyzedAt] = useState(null);
  const [deepAnalyzedAt, setDeepAnalyzedAt] = useState(null);
  const [aiBpId, setAiBpId] = useState(null);
  // True while a review/deep run is in flight — guards the async saved-analysis
  // loader from clobbering a running analysis (a blank-over-partial race).
  const runningRef = useRef(false);
  // Set when an upload matched an already-analyzed file (same SHA-256) — we open
  // the existing record instead of re-analyzing, and show this note.
  const [dupNotice, setDupNotice] = useState(null);
  // Per-blueprint user corrections/notes — authoritative facts the user confirms
  // (e.g. actual level, field-measured area). Injected into every Re-analyze as
  // authoritative context; the ref keeps runAIReview reading the latest value.
  const [corrections, setCorrections] = useState("");
  const [correctionsSaving, setCorrectionsSaving] = useState(false);
  const correctionsRef = useRef("");

  // Right-panel tabs. "ai" = AI Review, "deep" = Deep, "flag" = Flag Details, "notes" = Annotations.
  const [panelTab, setPanelTab] = useState("ai");
  // Viewport detection — used to default the right panel closed on small
  // screens so the drawing fills full width on a phone.
  const [isMobBp, setIsMobBp] = useState(()=> typeof window !== "undefined" && window.innerWidth < 1024);
  React.useEffect(()=>{
    const onResize = () => setIsMobBp(window.innerWidth < 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // Collapsible right panel — closed by default on small screens, open on desktop.
  const [panelOpen, setPanelOpen] = useState(()=> typeof window === "undefined" || window.innerWidth >= 1024);

  // Deep Analysis — second-pass contractor-grade review with material takeoff,
  // scope checklist, constructibility flags, VE, permits, inspection sequence.
  const [deepAnalysis, setDeepAnalysis] = useState("");
  const [deepStructured, setDeepStructured] = useState(null);
  const [deepAnalyzing, setDeepAnalyzing] = useState(false);
  const [deepError, setDeepError] = useState("");
  const [deepBpId, setDeepBpId] = useState(null);

  // Per-page analysis fallback for PDFs too large to forward whole. When set,
  // the corresponding review/analysis was run on a single rasterized page and
  // the UI labels it "Page N analysis". *TooLarge flags gate the "Analyze This
  // Page" button shown next to the too-large error.
  const [aiReviewPage, setAiReviewPage] = useState(null);
  const [aiTooLarge, setAiTooLarge] = useState(false);
  const [deepAnalysisPage, setDeepAnalysisPage] = useState(null);
  const [deepTooLarge, setDeepTooLarge] = useState(false);
  // Draft-RFI state per spec-gap item (index → "drafting" | "done" | "unavailable").
  const [draftedGaps, setDraftedGaps] = useState({});

  // Publish the AI-extracted drawing analysis to ARIA's context (Blueprint AI tab).
  // Text only for now — the drawing image itself is a separate ticket (see TODO.md).
  React.useEffect(() => {
    if (!onWorkProduct) return;
    const analysisText = [aiReview, deepAnalysis].filter(Boolean).join("\n\n");
    if (activeBlueprint && analysisText) {
      onWorkProduct({ kind: "blueprint", text: analysisText, meta: { drawing: activeBlueprint.name, pages: pageCount } });
    } else {
      onWorkProduct(null);
    }
  }, [aiReview, deepAnalysis, activeBlueprint, pageCount]); // eslint-disable-line

  // Persisted checkbox state for the scope checklist, keyed by bpId.
  const [checkedScope, setCheckedScope] = useState({});

  // Command center: full-screen overlay that hides the app shell. ESC exits.
  const [isCommandCenter, setIsCommandCenter] = useState(false);
  React.useEffect(() => {
    if (!isCommandCenter) return;
    const onKey = (e) => { if (e.key === "Escape") setIsCommandCenter(false); };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while the overlay is up
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isCommandCenter]);

  // ── Load flags from localStorage so auto-flags persist and render ──────────
  const flagsKey = "grok_flags_" + (proj?.id || selectedProject?.id || "global");
  const [flags, setFlags] = useState(()=>{
    try { return JSON.parse(localStorage.getItem(flagsKey)||"[]"); } catch { return []; }
  });

  // Keep flags in sync with localStorage on every change
  const saveFlags = (updated) => {
    setFlags(updated);
    const pid = proj?.id || selectedProject?.id || "global";
    try { localStorage.setItem(flagsKey, JSON.stringify(updated)); } catch(e) {}
    SyncDB.saveFlags(pid, updated).catch(()=>{});
  };

  // When project changes, reload flags for that project
  useEffect(()=>{
    const key = "grok_flags_" + (proj?.id || selectedProject?.id || "global");
    try { setFlags(JSON.parse(localStorage.getItem(key)||"[]")); } catch { setFlags([]); }
  }, [selectedProject?.id, proj?.id]);
  const [dragging, setDragging] = useState(false);
  const [placingFlag, setPlacingFlag] = useState(false);
  const [newFlagPos, setNewFlagPos] = useState(null);
  const [newFlagData, setNewFlagData] = useState({title:"",desc:"",priority:"medium",trade:"",photos:[]});
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [submittalPins, setSubmittalPins] = useState(()=>{
    const key = `grok_submittal_pins_${proj?.id||"x"}`;
    try{return JSON.parse(localStorage.getItem(key)||"[]");}catch{return [];}
  });
  const [selectedSubmittalPin, setSelectedSubmittalPin] = useState(null);
  const [placingSubmittalPin, setPlacingSubmittalPin] = useState(false);
  const [newSubmittalPinForm, setNewSubmittalPinForm] = useState({product:"",trade:"",spec_section:"",status:"pending",notes:"",submittal_ref:""});
  const [newSubmittalPinPos, setNewSubmittalPinPos] = useState(null);
  const [pinMode, setPinMode] = useState("flag"); // "flag" | "submittal"
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({x:0,y:0});
  const [panStart, setPanStart] = useState(null);
  // Set by PdfCanvas; calling it returns the currently-visible page region.
  // Internal machinery only — no user-facing manual-crop workflow (the detail
  // reading is automatic via tiling); kept because renderPdfRegionToImage reuses
  // the same idea.
  const regionCaptureRef = useRef(null);
  // Progress of the automatic detail-sheet tiling pass ({done,total}) and a
  // per-blueprint cache of the character-exact callouts it transcribed, so Deep
  // Analysis reuses them without re-tiling.
  const [tileProgress, setTileProgress] = useState(null);
  const verifiedCalloutsRef = useRef({});
  const [viewMode, setViewMode] = useState("all");    // all | open | resolved
  const [filterTrade, setFilterTrade] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [loggedSub] = useState(null);                 // can be passed in from sub portal
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);

  const TRADES = ["All Trades","Plumbing","Electrical","Framing","HVAC","Tile","Drywall","Structural","General"];
  const PRIORITY_COLORS = {low:"#22c55e",medium:"#f59e0b",high:"#ef4444",critical:"#dc2626"};
  const PRIORITY_ICONS = {low:"●",medium:"▲",high:"■",critical:"★"};

  const projId = selectedProject?.id?.toString();
  const projBlueprints = blueprints[projId] || [];
  const activeFlags = flags.filter(f => f.blueprintId === activeBlueprint?.id);
  const filteredFlags = activeFlags.filter(f => {
    if (viewMode === "open") return f.status !== "resolved";
    if (viewMode === "resolved") return f.status === "resolved";
    return true;
  }).filter(f => !filterTrade || filterTrade === "All Trades" || f.trade === filterTrade);

  const openCount = activeFlags.filter(f=>f.status!=="resolved").length;
  const critCount = activeFlags.filter(f=>f.priority==="critical"&&f.status!=="resolved").length;

  // Upload one or many blueprint files. The file input has `multiple`, so the
  // caller passes a FileList or array. We process them in parallel, append all
  // to state at the end, and only auto-fire AI review on the LAST file (which
  // also becomes the active blueprint).
  const uploadBlueprint = (filesOrFile) => {
    const files = filesOrFile?.length != null && typeof filesOrFile !== "string"
      ? Array.from(filesOrFile)
      : (filesOrFile ? [filesOrFile] : []);
    if (!files.length) return;

    const readOne = (file) => new Promise((resolve) => {
      const isPDF = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
      const isImage = file.type.startsWith("image/");
      if (!isPDF && !isImage) { resolve(null); return; }
      const reader = new FileReader();
      reader.onload = async (e) => {
        const bp = {
          id: "bp" + Date.now() + "_" + Math.random().toString(36).slice(2,6),
          name: file.name.replace(/\.[^.]+$/, ""),
          url: e.target.result,
          dataUrl: e.target.result,
          fileType: isPDF ? "pdf" : "image",
          mediaType: isPDF ? "application/pdf" : (file.type || "image/jpeg"),
          uploadDate: new Date().toLocaleDateString(),
          flags: [],
        };
        // Content fingerprint for duplicate detection (same file → never re-bill).
        bp.fileHash = await sha256Hex(file);
        // Push the ORIGINAL bytes to the private Storage bucket so analysis can
        // run server-side (no browser body limit). Best-effort — on failure the
        // bp keeps its dataUrl and analysis falls back to the inline path.
        const up = await uploadBlueprintToStorage(file, bp.id, session);
        if (up.ok) bp.storagePath = up.storagePath;
        else console.warn("[blueprint] storage upload skipped:", up.reason);
        resolve(bp);
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });

    Promise.all(files.map(readOne)).then(async (bps) => {
      const valid = bps.filter(Boolean);
      if (!valid.length) { alert("No valid files — upload PNG, JPG, or PDF."); return; }
      const existing = blueprints[projId] || [];
      // Same file already in THIS project's list (fast, same-session path).
      const localByHash = new Map(existing.filter(b => b.fileHash).map(b => [b.fileHash, b]));
      const fresh = [];
      let localDup = null;
      for (const bp of valid) {
        if (bp.fileHash && localByHash.has(bp.fileHash)) { localDup = localByHash.get(bp.fileHash); continue; }
        fresh.push(bp);
      }
      const openView = (bp) => { setActiveBlueprint(bp); setCurrentPage(1); setPageCount(1); setPanelTab("ai"); setZoom(1); setPan({x:0,y:0}); };

      // Nothing new — the file(s) are already here. Open the existing record; its
      // saved analysis auto-loads. Never re-analyze (never re-bill).
      if (!fresh.length && localDup) {
        openView(localDup);
        setDupNotice({ name: localDup.name });
        return;
      }

      // Same file analyzed before on ANOTHER device (cloud hash match)? Open that
      // record with its saved analysis instead of creating a duplicate + re-billing.
      const last = fresh[fresh.length - 1];
      const cloudDup = last?.fileHash ? await findCloudBlueprintByHash(last.fileHash) : null;
      if (cloudDup) {
        const mat = { id: cloudDup.id, name: cloudDup.name || last.name, url: last.dataUrl, dataUrl: last.dataUrl,
          fileType: cloudDup.file_type || last.fileType, mediaType: last.mediaType, uploadDate: last.uploadDate,
          fileHash: last.fileHash, storagePath: last.storagePath, corrections: cloudDup.corrections || "", flags: [] };
        const others = fresh.slice(0, -1);
        const list = [...existing, ...others, ...(existing.some(b => b.id === mat.id) ? [] : [mat])];
        setBlueprints({ ...blueprints, [projId]: list });
        openView(mat);
        setDupNotice({ name: mat.name, when: cloudDup.analyzed_at });
        return;
      }

      // Genuinely new — append and analyze the last one.
      const updated = { ...blueprints, [projId]: [...existing, ...fresh] };
      setBlueprints(updated);
      setDupNotice(null);
      openView(last);
      // Auto-fire AI review on the last uploaded blueprint only.
      runAIReview(last);
    });
  };

  // Rename a blueprint via prompt(). Inline editing would be nicer but prompt
  // is one line of code and works on every browser including mobile.
  const renameBlueprint = (bp) => {
    const next = window.prompt("Rename drawing:", bp.name || "");
    if (next == null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === bp.name) return;
    const list = (blueprints[projId] || []).map(b => b.id === bp.id ? {...b, name: trimmed} : b);
    setBlueprints({...blueprints, [projId]: list});
    if (activeBlueprint?.id === bp.id) setActiveBlueprint({...bp, name: trimmed});
  };

  // Delete a blueprint + drop its flags/submittal pins. Confirms first.
  const deleteBlueprint = (bp) => {
    if (!window.confirm(`Delete "${bp.name||"this drawing"}"? Flags and submittal pins on it will also be removed.`)) return;
    // Release the blueprint's staged Anthropic Files API upload (best-effort,
    // fire-and-forget). The proxy reads anthropic_file_id off the row, deletes the
    // file, and nulls the column — so a deleted blueprint doesn't leak a staged file.
    if (bp?.id) {
      try {
        fetch(AI_PROXY_URL, {
          method: "POST",
          headers: { "Content-Type":"application/json", "Authorization":"Bearer "+sb._token(), "apikey":SUPABASE_ANON_KEY },
          body: JSON.stringify({ release_file: { bpId: bp.id }, company_id: session?.companyId || window._grokCompanyId }),
        }).catch(()=>{});
      } catch {}
    }
    const list = (blueprints[projId] || []).filter(b => b.id !== bp.id);
    setBlueprints({...blueprints, [projId]: list});
    // Drop flags scoped to this bp.
    const nextFlags = flags.filter(f => f.blueprintId !== bp.id && f.bpId !== bp.id);
    if (nextFlags.length !== flags.length) saveFlags(nextFlags);
    // Drop submittal pins.
    const nextPins = (submittalPins || []).filter(p => p.bpId !== bp.id);
    if (nextPins.length !== (submittalPins||[]).length) {
      setSubmittalPins(nextPins);
      try { localStorage.setItem(`grok_submittal_pins_${proj?.id||"x"}`, JSON.stringify(nextPins)); } catch {}
    }
    // If we deleted the active one, fall back to the next available or clear.
    if (activeBlueprint?.id === bp.id) {
      setActiveBlueprint(list[0] || null);
      setSelectedFlag(null); setSelectedSubmittalPin(null);
      setCurrentPage(1); setPageCount(1);
    }
  };

  // Write a prefill hint + the blueprint itself to localStorage, then navigate
  // to Projects. ProjectsView reads the hint on mount, opens ProjectModal
  // pre-populated, and on save persists the blueprint to the new project's
  // blueprint store + deletes the hint.
  const createProjectFromBlueprint = (bp, learnings, fullReview) => {
    if (!bp || !learnings || !setView) return;
    const lowN = Number(learnings.total_low) || 0;
    const highN = Number(learnings.total_high) || 0;
    const valueGuess = (lowN && highN) ? Math.round((lowN + highN) / 2) : (highN || lowN || 0);
    // Map ARIA project_type to ProjectModal's select options when we can.
    const typeMap = {
      "SRO": "Residential — SRO / Affordable",
      "Affordable Housing": "Residential — SRO / Affordable",
      "Senior/ADA Housing": "Senior Housing",
      "Multi-Family TI": "Residential — Multi-Family",
      "Residential Remodel": "Residential — Single Family",
      "Commercial TI": "Commercial — Tenant Improvement",
    };
    const typeGuess = typeMap[learnings.project_type] || "";
    // Short scope summary from the trades list — full text would be huge.
    const tradesLine = (learnings.trades_identified || []).join(", ");
    const desc = `Auto-drafted from blueprint "${bp.name}". Trades identified: ${tradesLine || "see analysis"}. Estimated $${lowN.toLocaleString()} – $${highN.toLocaleString()}.`;

    const prefill = {
      name: bp.name || "",
      type: typeGuess,
      value: valueGuess,
      desc,
    };
    try {
      sessionStorage.setItem("grok_project_prefill", JSON.stringify(prefill));
      sessionStorage.setItem("grok_project_prefill_blueprints", JSON.stringify([bp]));
    } catch {}
    setView("projects");
  };

  // Add photo to flag
  const addPhoto = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setNewFlagData(p=>({...p, photos:[...p.photos, {id:Date.now(), url:e.target.result, name:file.name}]}));
    reader.readAsDataURL(file);
  };

  // Fetch the company's prior blueprint learnings for this project type and
  // boil them down into a short context block. Returns "" if none exist yet.
  const loadPastLearnings = async (cid, projectType) => {
    if (!cid || cid === "local" || !projectType) return "";
    try {
      const url = `${SUPABASE_URL}/rest/v1/grok_learning?company_id=eq.${encodeURIComponent(cid)}&event_type=eq.blueprint_analyzed&project_type=eq.${encodeURIComponent(projectType)}&order=created_at.desc&limit=20`;
      const r = await fetch(url, {
        headers: {"apikey": SUPABASE_ANON_KEY, "Authorization": "Bearer " + sb._token()},
      });
      if (!r.ok) return "";
      const rows = await r.json();
      if (!rows?.length) return "";
      const flagFreq = {};
      const costs = [];
      const tradeFreq = {};
      const matFreq = {};
      rows.forEach(row => {
        const p = row.payload || {};
        (p.code_flags||[]).forEach(f => {
          const k = (f.category||"") + "::" + (f.section||"").slice(0,40);
          flagFreq[k] = (flagFreq[k]||0) + 1;
        });
        if (Number(p.total_low) && Number(p.total_high)) {
          costs.push({low: Number(p.total_low), high: Number(p.total_high)});
        }
        (p.trades_identified||[]).forEach(t => { tradeFreq[t] = (tradeFreq[t]||0)+1; });
        (p.materials_called_out||[]).forEach(m => {
          const item = (m?.item || m || "").toString().slice(0,40);
          if (item) matFreq[item] = (matFreq[item]||0)+1;
        });
      });
      const topFlags = Object.entries(flagFreq).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([k,n]) => `${k.split("::")[1]||k} (${n}×)`);
      const topTrades = Object.entries(tradeFreq).sort((a,b)=>b[1]-a[1]).slice(0,8).map(([t])=>t);
      const topMats = Object.entries(matFreq).sort((a,b)=>b[1]-a[1]).slice(0,8).map(([m])=>m);
      const avgLow = costs.length ? Math.round(costs.reduce((s,c)=>s+c.low,0)/costs.length) : 0;
      const avgHigh = costs.length ? Math.round(costs.reduce((s,c)=>s+c.high,0)/costs.length) : 0;
      return `\n## 🧠 What ARIA Has Learned About This Company's ${projectType} Projects\nBased on ${rows.length} past blueprint analysis${rows.length===1?"":"es"}:\n- Typical contract value range: ${avgLow?"$"+avgLow.toLocaleString():"unknown"} – ${avgHigh?"$"+avgHigh.toLocaleString():"unknown"}\n- Common trades on these jobs: ${topTrades.join(", ")||"none yet"}\n- Recurring materials: ${topMats.join(", ")||"none yet"}\n- Code issues that come up: ${topFlags.join("; ")||"none recorded"}\n\nFactor these patterns into your analysis where the drawing supports them.\n`;
    } catch { return ""; }
  };

  // Hide the internal learnings block from anything rendered to the GC. We still
  // parse the JSON for the learning system — only the rendered text is trimmed.
  // Delegates to the module-level helper so the review, deep-analysis inline, and
  // print paths all strip identically.
  const stripLearnings = stripLearningsBlock;

  // Cache the cost estimate (per-bp) so subsequent analyses anchor to the
  // same number and don't drift. Cache wins for the same drawing forever
  // unless the user explicitly clears it (delete + re-upload).
  const estimateCacheKey = (bpId) => "grok_blueprint_estimate_" + bpId;
  const cacheEstimate = (bpId, learnings) => {
    if (!bpId || !learnings) return;
    const entry = {
      total_low: learnings.total_low,
      total_high: learnings.total_high,
      cost_by_trade: learnings.cost_by_trade,
      cached_at: new Date().toISOString(),
    };
    if (!entry.total_low && !entry.total_high) return;
    try { localStorage.setItem(estimateCacheKey(bpId), JSON.stringify(entry)); } catch {}
  };
  const getCachedEstimate = (bpId) => {
    if (!bpId) return null;
    try { return JSON.parse(localStorage.getItem(estimateCacheKey(bpId)) || "null"); } catch { return null; }
  };

  // Pull the structured JSON learnings block out of the AI's markdown response.
  // The prompt asks for a fenced ```json block at the very end; fall back to
  // the last balanced { ... } block if the fence is missing.
  const extractLearningsJSON = (text) => {
    if (!text) return null;
    const fenced = text.match(/```json\s*([\s\S]*?)\s*```/i);
    if (fenced) {
      try { return JSON.parse(fenced[1]); } catch {}
    }
    const lastOpen = text.lastIndexOf("{");
    if (lastOpen >= 0) {
      try { return JSON.parse(text.slice(lastOpen)); } catch {}
    }
    return null;
  };

  // Persist a learning row to grok_learning. Best-effort — failure is silent
  // because losing a single learning row shouldn't block the user.
  const saveLearning = async ({event_type, project_id, project_type, payload, cid}) => {
    if (!cid || cid === "local") return;
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/grok_learning`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apikey": SUPABASE_ANON_KEY,
          "Authorization": "Bearer " + sb._token(),
          "Prefer": "return=minimal",
        },
        body: JSON.stringify({
          company_id: cid,
          event_type,
          project_id: project_id ? String(project_id) : null,
          project_type: project_type || null,
          payload,
          created_by: session?.ownerName || session?.email || null,
        }),
      });
    } catch {}
  };

  // Persist the latest review / deep-analysis TEXT to the cloud (blueprints
  // table, company-scoped via RLS) so it survives reload — analyses cost real
  // money. `patch` is {ai_review, analyzed_at} or {deep_analysis, deep_analyzed_at}.
  // Upsert on the blueprint id so we create the row if it doesn't exist yet and
  // update only the provided columns otherwise (never wipes the sibling analysis).
  const saveBlueprintAnalysis = async (bp, patch) => {
    try {
      const cid = session?.companyId || window._grokCompanyId;
      const tok = sb._token();
      if (!cid || cid === "local" || !bp?.id || !tok || tok === SUPABASE_ANON_KEY) return;
      const pc = selectedProject || proj;
      const headers = {
        "Content-Type": "application/json",
        "apikey": SUPABASE_ANON_KEY,
        "Authorization": "Bearer " + tok,
        "Prefer": "resolution=merge-duplicates,return=minimal",
      };
      const body = {
        id: bp.id,
        company_id: cid,
        project_id: pc?.id ? String(pc.id) : null,
        name: bp.name || null,
        file_type: bp.fileType || null,
        ...(bp.fileHash ? { file_hash: bp.fileHash } : {}),
        ...patch,
      };
      const url = `${SUPABASE_URL}/rest/v1/blueprints?on_conflict=id`;
      let res = await fetch(url, { method: "POST", headers, body: JSON.stringify(body) });
      // engine_version is a newer column — if the DB hasn't been migrated yet the
      // whole upsert 400s. Retry without the stamp so the analysis still persists.
      if (res && !res.ok && "engine_version" in body) {
        const { engine_version, ...rest } = body;
        await fetch(url, { method: "POST", headers, body: JSON.stringify(rest) });
      }
    } catch {}
  };

  // Save the current blueprint's user corrections (cloud + local list + ref).
  const saveCorrections = async () => {
    const bp = activeBlueprint;
    if (!bp?.id) return;
    const text = corrections;
    correctionsRef.current = text;
    setCorrectionsSaving(true);
    try {
      const list = (blueprints[projId] || []).map(b => b.id === bp.id ? { ...b, corrections: text } : b);
      setBlueprints({ ...blueprints, [projId]: list });
    } catch {}
    await saveBlueprintAnalysis(bp, { corrections: text });
    setCorrectionsSaving(false);
  };

  // Find an already-analyzed blueprint in THIS company by content hash (duplicate
  // detection across devices). Returns the row or null.
  const findCloudBlueprintByHash = async (hash) => {
    try {
      const cid = session?.companyId || window._grokCompanyId;
      const tok = sb._token();
      if (!cid || cid === "local" || !hash || !tok || tok === SUPABASE_ANON_KEY) return null;
      const r = await fetch(`${SUPABASE_URL}/rest/v1/blueprints?company_id=eq.${encodeURIComponent(cid)}&file_hash=eq.${encodeURIComponent(hash)}&select=id,name,file_type,ai_review,deep_analysis,analyzed_at,corrections&limit=1`, {
        headers: { "apikey": SUPABASE_ANON_KEY, "Authorization": "Bearer " + tok },
      });
      if (!r.ok) return null;
      const rows = await r.json();
      return (rows && rows[0]) || null;
    } catch { return null; }
  };

  // Load a blueprint's saved analysis text (returns null if none / anon / error).
  const loadBlueprintAnalysis = async (bpId) => {
    try {
      const cid = session?.companyId || window._grokCompanyId;
      const tok = sb._token();
      if (!cid || cid === "local" || !bpId || !tok || tok === SUPABASE_ANON_KEY) return null;
      const r = await fetch(`${SUPABASE_URL}/rest/v1/blueprints?id=eq.${encodeURIComponent(bpId)}&select=ai_review,deep_analysis,analyzed_at,deep_analyzed_at,corrections`, {
        headers: { "apikey": SUPABASE_ANON_KEY, "Authorization": "Bearer " + tok },
      });
      if (!r.ok) return null;
      const rows = await r.json();
      return (rows && rows[0]) || null;
    } catch { return null; }
  };

  // ARIA's blueprint review — scope by trade, materials, code flags, cost
  // estimate, schedule estimate. Auto-continues if the response is cut off by
  // max_tokens, then extracts the structured learnings JSON and persists.
  const runAIReview = async (bp, opts = {}) => {
    if (!bp || (!bp.dataUrl && !bp.storagePath)) return;
    runningRef.current = true;
    setAiReviewing(true);
    setAiReview("");
    setAiError("");
    setAiIncomplete(false);
    setDupNotice(null);
    setAiBpId(bp.id);
    setPanelTab("ai");
    setDraftedGaps({});

    const projContext = selectedProject || proj;
    const cid = session?.companyId || window._grokCompanyId || "local";
    // Per-analysis instrumentation — dumped to the console at the end so an empty
    // or expensive run tells us exactly where it died and what it cost.
    const t0 = Date.now();
    const alog = { bp: bp.id, name: bp.name, stage: "prep", path: null, tiles: null, tilesMs: null, ttftMs: null, reviewMs: null, totalMs: null, reviewCalls: 0, continuations: 0, emptyRetries: 0, stopReason: null, reviewChars: 0, reviewTokensIn: 0, reviewTokensOut: 0, saved: false, error: null };
    let stage = "prep";

    // Per-page mode: rasterize the current PDF page to an image and review that
    // single page (fallback when the whole PDF is too large to forward).
    // Otherwise downscale the image / size-guard the whole drawing BEFORE
    // building the request, so an oversized body never trips the edge runtime's
    // payload cliff (which the browser would otherwise surface as phantom CORS).
    const pageMode = !!opts.pageMode;
    const regionMode = !!opts.regionMode;
    const pageNum = opts.pageNum || currentPage || 1;
    let bpIsPDF, bpData, bpMediaType, storageRef = null;
    try {
      if (regionMode) {
        // Legible zoomed crop of one detail — the reliable path for fine callouts.
        const rendered = await renderPdfRegionToImage(bp.dataUrl, pageNum, opts.region);
        const small = await downscaleImageForAI(rendered);
        bpData = small.split(",")[1] || "";
        bpMediaType = "image/jpeg";
        bpIsPDF = false;
        setAiReviewPage(pageNum);
        setAiTooLarge(false);
      } else if (pageMode) {
        const rendered = await renderPdfPageToImage(bp.dataUrl, pageNum);
        const small = await downscaleImageForAI(rendered);
        bpData = small.split(",")[1] || "";
        bpMediaType = "image/jpeg";
        bpIsPDF = false;
        setAiReviewPage(pageNum);
        setAiTooLarge(false);
      } else if (bp.storagePath) {
        // Primary path: reference the file in Storage; the proxy fetches it
        // server-side, so the browser never sends the base64 body.
        bpIsPDF = bp.fileType === "pdf" || bp.name?.toLowerCase().endsWith(".pdf");
        bpMediaType = bp.mediaType || (bpIsPDF ? "application/pdf" : "image/jpeg");
        storageRef = { path: bp.storagePath, isPDF: bpIsPDF, mediaType: bpMediaType, bpId: bp.id };
        setAiReviewPage(null);
        setAiTooLarge(false);
      } else {
        const bpPrep = await prepBlueprintForAI(bp);
        bpIsPDF = bpPrep.isPDF;
        bpData = bpPrep.data;
        bpMediaType = bpPrep.mediaType;
        setAiReviewPage(null);
        setAiTooLarge(false);
      }
    } catch (e) {
      if (e?.code === "PDF_TOO_LARGE") {
        setAiTooLarge(true);
        setAiError(`This PDF is too large to analyze whole (${e.mb} MB). Reduce or split the file — or analyze the current page using the button below.`);
      } else {
        setAiError(e?.message || "Could not prepare the drawing for analysis.");
      }
      setAiReviewing(false);
      return;
    }

    // Inject prior learnings for this project type so each new analysis is
    // smarter than the last. Best-effort — empty string if none yet.
    const learnedBlock = await loadPastLearnings(cid, projContext?.type);

    // Inject the cached estimate (if any) so the cost number stays stable
    // across re-analyses of the same drawing.
    const cached = getCachedEstimate(bp.id);
    const anchorBlock = cached?.total_low ? `\n\n## PRIOR ESTIMATE ON FILE FOR THIS DRAWING\nA prior estimate for this drawing was $${Number(cached.total_low).toLocaleString()} – $${Number(cached.total_high).toLocaleString()}. Treat this ONLY as a reference checkpoint — NOT a target to hit. Build the cost up FRESH from this run's line items, then reconcile against the prior number:\n- If your fresh line-item build-up lands within ~10% of the prior estimate, you may keep the prior range for consistency.\n- If your fresh build-up MATERIALLY disagrees with the prior estimate, your CURRENT calibrated build-up WINS the headline. Do NOT keep the prior range "for consistency" and do NOT bury the disagreement in a footnote. Put THIS run's numbers in the headline and rollup, and add exactly ONE line immediately under the rollup table, verbatim in this shape: "Supersedes prior estimate of $${Number(cached.total_low).toLocaleString()} – $${Number(cached.total_high).toLocaleString()} — recalibrated: <one concrete reason, e.g. priced from the line-item build-up against comparable local bids>."\n- The headline Low/High range MUST be computed FROM this run's competitive-bid subtotal (subtotal ±10–15%). NEVER inherit, copy, or blend in the prior Low/High range.` : "";

    // AUTOMATIC detail-sheet reading. Sonnet reaches the detail sheets but garbles
    // fine text at full-sheet resolution, so before the review we render each page
    // at high DPI, tile it, and transcribe callouts character-exact with Haiku,
    // then hand Sonnet those verbatim strings as authoritative. Runs only on the
    // whole-set review of a multi-page PDF we have bytes for; per-page/region
    // passes are already legible crops. Best-effort — on failure the review still
    // runs. This is why there is no manual "analyze region" step for the user.
    const isPdfDrawing = bp.fileType === "pdf" || bp.name?.toLowerCase().endsWith(".pdf");
    let verifiedBlock = "";
    if (!pageMode && !regionMode && isPdfDrawing && bp.dataUrl) {
      const tileHeaders = { "Content-Type":"application/json", "Authorization":"Bearer "+sb._token(), "apikey":SUPABASE_ANON_KEY };
      stage = "reading detail sheets"; alog.stage = stage;
      try {
        setTileProgress({ done: 0, total: 0 });
        const tTiles = Date.now();
        const { callouts, stats } = await extractDetailCallouts(bp.dataUrl, tileHeaders, (p) => setTileProgress(p));
        alog.tilesMs = Date.now() - tTiles;
        alog.tiles = stats;
        verifiedCalloutsRef.current[bp.id] = callouts;
        console.log(`[bp ${bp.id}] tiling: ${stats.tilesOk}/${stats.tilesTotal} tiles ok, ${callouts.length} callouts, ${stats.tokensIn}→${stats.tokensOut} tok${stats.timedOut ? " (TIMED OUT)" : ""}`, stats.classified);
        if (callouts.length) {
          verifiedBlock = `\n\n## ✅ VERIFIED DETAIL-SHEET CALLOUTS (character-exact)\nThe strings below were transcribed from high-resolution crops of the detail sheets and are MORE ACCURATE than a whole-sheet read. Whenever one of these belongs in SPECIFIED MATERIALS, reproduce it with these EXACT characters — do not re-spell, round, expand abbreviations, or merge them, and cite the detail it appears on:\n${callouts.map(c => `- "${c}"`).join("\n")}`;
        }
      } catch (e) { console.warn("[blueprint] detail tiling failed:", e?.message || e); }
      setTileProgress(null);
    }

    // Project Notes are a SEPARATE, user-provided layer — NEVER blended into the
    // drawing-derived analysis. The AI MAY use a note, but every use must be
    // ATTRIBUTED INLINE, notes must stay out of Specified Materials, and any
    // drawing-vs-note conflict must be surfaced, never silently resolved.
    const corr = (correctionsRef.current || "").trim();
    const correctionsBlock = corr ? `\n\n## 📝 PROJECT NOTES (USER-PROVIDED — NOT FROM THE DRAWINGS)\nThe user typed the notes below. They are a SEPARATE layer of context — NOT extracted from the drawing set — and are already shown to the user in their own labeled block. Handle them under these strict PROVENANCE rules:\n- You MAY use a note to inform the analysis, but EVERY use must be ATTRIBUTED INLINE at the point of use, e.g. "Using user-provided area (2,850 SF, field measure) — plan-derived was ~3,200 SF (A1.01)". NEVER present a user-provided value as if you read it off a sheet.\n- NEVER place a note, or anything derived from it, inside SPECIFIED MATERIALS. That section is drawings-only.\n- Where a note CONFLICTS with the drawings, or asserts something the set does not show, SURFACE the conflict explicitly (e.g. "user note says 2nd floor; set does not identify level") — do NOT silently adopt the note and do NOT silently keep only the drawing value.\n- Do NOT restate the notes verbatim as findings; only reference a note where it actually informs a specific line.\nNOTES:\n${corr}` : "";

    // PROMPT CONTRACT: this systemPrompt + the userText section spec below are the
    // blueprint analysis prompt. Materially changing them requires bumping
    // ENGINE_PROMPT_REV (~line 1111) so saved analyses stamp the new engine version
    // and stale-engine notices stay accurate.
    const systemPrompt = `You are a California licensed general contractor reviewing construction drawings for a Bay Area project. Be specific, direct, and grounded in current SF Bay Area market rates (2026). Always provide complete sections — never leave a section blank or say "TBD." Use the exact section headers and structure requested. When the drawings name a product, manufacturer, model or spec number, thickness, mil coverage, or application rate, you MUST reproduce it VERBATIM — quote the exact annotation text and never paraphrase a product spec into a generic term. Product callouts live in TWO places and you must read BOTH: the PDF text layer (typed schedules and the cover-sheet scope list) AND the drawing image itself — detail and section sheets carry leader-line callouts (e.g. a waterproofing membrane's product name and wet-mil coverage pointing at a wall transition) that are frequently NOT in the text layer and must be read VISUALLY off the sheet. Walk EVERY sheet, not just the cover; the richest specs are usually on the detail sheets. Only say "not specified" after you have checked both the text layer AND visually scanned each sheet's callouts. NEVER invent a plausible product name, model number, or coverage. Fabricating a spec destroys the trustworthiness of the review; an admitted gap is always the correct answer over a made-up value. This anti-fabrication contract covers CONTEXT and QUANTITIES, not just product names: do NOT state a floor, level, location, building use, or dimension the drawings don't show; use the drawing's OWN terminology for the work; where the set is silent on level/location, write "not specified in set — verify" instead of guessing. For any quantity — if a drawing STATES it, cite the sheet; if you DERIVED it, you must SHOW THE BASIS: name the sheet whose dimensions you used and the arithmetic (e.g. "derived from plan dimensions on A1.01, ≈80' × 40' footprint"), then label it "verify before bidding". If you cannot articulate a concrete basis, do NOT state a number at all — say "not determinable from set". Any area you use later in the cost estimate must reference the SAME basis so the $/SF math is auditable end-to-end.${learnedBlock}${anchorBlock}${verifiedBlock}${correctionsBlock}`;

    const userText = `Analyze this construction drawing for ${projContext?.name || "the project"}${projContext?.addr ? " at " + projContext.addr : ""}.

Project type: ${projContext?.type || "General renovation"}
Contract value (if known): ${projContext?.value ? "$"+Math.round(projContext.value).toLocaleString() : "not provided"}

Produce a COMPLETE blueprint review in exactly these sections, IN THIS ORDER, using these exact H2 headers. A general contractor should get the whole story from the top in ~10 seconds, then drill down. "Reader-first" applies to ORDER and SCANNABILITY, NOT to depth: every section below its header must be FULLY detailed — tables complete, every trade and flag worked out — exactly as thorough as the Specified Materials section. Do not summarize or truncate a section because it comes later. Do NOT use horizontal rules ("---") or code fences anywhere except the final JSON block. No preamble, no sign-off.

## 📋 PROJECT SUMMARY
Lead with 2–3 plain-English sentences saying what this project IS — the building/address ONLY if the drawings state it, the work, and the outcome. Use the DRAWING'S OWN terminology for the work (if the cover sheet says "ROOF DECK REMEDIATION", call it that — don't rephrase or reclassify it). Do NOT add a floor, level, location, or building-use detail the drawings don't state. If PROJECT NOTES (user-provided) are supplied, do NOT restate them as a list here and do NOT treat them as drawing facts; only where a note actually informs a summary line, use it WITH inline attribution (e.g. "using user-provided ~2,850 SF, field measure — plan-derived was ~3,200 SF"), and surface any note that conflicts with or goes beyond the set in the Cross-check subsection. Then exactly these four labelled lines:
- **Primary trade:** <the lead trade>
- **Level / location:** ALWAYS address which building level the work is on. If ANY sheet shows it (a section, elevation, floor reference, or key plan), name it and cite that sheet. If it is truly absent from the set, write exactly: "level not identified in drawing set — confirm (affects staging, access, occupant protection below)". Never let a term like "roof deck" imply top-of-building when the level is unknown; where a section shows conditioned space below, say so (e.g. "deck assembly over occupied space, per A8.01").
- **Scope scale:** the size/scale. If a drawing STATES the area/count, cite the sheet. If you DERIVED it, show the basis — the sheet and the arithmetic (e.g. "~3,200 SF — derived from plan dimensions on A1.01, ≈80' × 40' deck footprint; verify before bidding"). If you cannot articulate a basis, write "area not determinable from set" — never a bare number.
- **Headline spec:** <the defining system and its product, quoted verbatim once verified; if the product is not identified in the set, say so and name the spec section>

Then a **Cross-check** subsection — this is how an estimator actually reads a set: the pages AGAINST each other, not page by page. Do all of these:
- Reconcile the cover/scope-sheet items against the detail-sheet evidence: flag any scope item with NO supporting detail, and any detailed callout with NO corresponding scope item.
- Flag any product/system that appears with CONFLICTING specs across sheets (it is also marked with a ⚠ under that product in Materials).
- Sanity-check quantities where a plan AND a section both give a basis; note any mismatch.
- If PROJECT NOTES (user-provided) were supplied, reconcile them against the set: adopt a note only with inline attribution, and list here any note that conflicts with or is unsupported by the drawings (e.g. "user note says 2nd floor; set does not identify level"). Never silently resolve a drawing-vs-note conflict.
Output either the single line "All scope items supported by details ✓ — no cross-sheet conflicts found" (ONLY if genuinely true) OR a short bulleted list of the specific discrepancies, each with its citations. Never claim ✓ if you flagged any gap, conflict, or mismatch.

## 📐 SPECIFIED MATERIALS & SYSTEMS
Organize this section by PRODUCT — ONE entry per distinct product/material/system, NOT one per occurrence. A GC scans the bold product names top-to-bottom in seconds; the exact drawing language sits underneath each as proof. Each entry has this exact shape:
**<Product name> — <3–6 word plain-English role>**
- "<verbatim callout, exactly as printed on the sheet>" — <sheet/detail citation>
- "<a genuinely DIFFERENT verbatim callout for the same product>" — <citation>

Rules:
- Line 1 is a BOLD header: the product NAME plus a short plain-English role (e.g. **CIM 1000 — fluid-applied waterproofing membrane**). It is NOT a quote.
- Each nested "- " bullet is one verbatim occurrence used as EVIDENCE: the exact drawing text in "double quotes", then " — " and where it appears (e.g. - "CIM 1000 130 WET MILS MIN" — A8.01 details 5, 10–12). The quoted text must be the exact characters from the drawing — do not round or re-spell.
- DEDUP: if the SAME callout text repeats across sheets/details, collapse it into ONE bullet with the citations merged ("..." — A8.01 details 5, 10–12). But keep genuinely DIFFERENT callouts for the same product as SEPARATE bullets (e.g. a field application "130 WET MILS MIN" vs a joint/cant condition "CANT STRIP ... CIM 1000 TROWELGRADE") — that distinction matters to a bidder.
- CONFLICTS: if the SAME product/system appears with DIFFERENT, INCOMPATIBLE specs on different sheets (e.g. "130 WET MILS MIN" on the detail vs "90 MILS" in a schedule), NEVER silently pick one. Keep BOTH as separate evidence bullets, then add one more bullet that begins exactly "⚠ Conflicting callouts — " naming both values with their citations and ending "confirm with architect".
- Optionally group entries under "### <system>" subheaders (Waterproofing, Substrate / Decking, Flashing & Accessories) when it helps a bidder; keep product headers bold and scannable under them.
- Walk EVERY sheet — cover AND every plan, section, and DETAIL sheet. Capture manufacturer/product names, model/spec numbers, thicknesses, mil coverages, application rates, each layer of a multi-layer system, assemblies, named fasteners/adhesives.
- Read from BOTH the PDF text layer AND the drawing image. If an occurrence was read off the drawing rather than the text layer, append " (vision-read)" to that bullet. Any VERIFIED DETAIL-SHEET CALLOUTS provided to you above are character-exact — use those exact strings.
- DEFERRED products: when a system is called out generically with only a spec-section reference and NO product name appears anywhere in the set, make its entry header **<system> — deferred to spec** and a single bullet: "Product not identified in drawing set — specified by reference to Spec Section <number> (not included). Confirm product with architect." Quote the section number verbatim.
- CRITICAL — never fabricate: if you cannot find a product/model/thickness/coverage written verbatim, do NOT invent one. Omit it or list it as a gap below. A made-up spec is worse than an admitted gap.
- CRITICAL — drawings only: NEVER put a user-provided PROJECT NOTE (or anything derived from one) in this section. A spec belongs here ONLY if it is printed on a sheet. User-provided facts, if relevant, belong in the summary/cross-check WITH inline attribution — never here.

## ⚠️ NOT SPECIFIED / VERIFY
List scope items VISIBLE in the drawings that LACK a material spec, product callout, dimension, or code reference — the gaps a GC must resolve before pricing. Phrase each as an actionable bullet naming the item and what's missing.
- Format each as a bullet: <item / location> — <what's missing>; <who to confirm with>. Example: "Guardrail attachment at parapet — no fastener spec shown; confirm with architect."
- ONLY genuine gaps. Do NOT pad, and do NOT restate items that ARE specified above. Never invent a missing item.
- If every visible item is adequately specified, output exactly one line: "No specification gaps noted."

## 🔨 SCOPE OF WORK BY TRADE
Treat each trade as its own MINI-REVIEW, in CONSTRUCTION-SEQUENCE order (demo → substrate → waterproofing → flashing → finishes). Use a "### <Trade name>" subheader per trade, then under each, these four labelled parts:
- **Scope:** 3–6 bullets of exactly what they install/do, phrased close to the drawing language.
- **Materials:** cross-reference the products from SPECIFIED MATERIALS that belong to this trade — name them and cite the detail; do NOT re-quote their full callout (e.g. "CIM 1000 membrane — see Waterproofing, Detail 4").
- **Quantities:** where derivable from the drawings, give a number with units, each followed by "(verify before bidding)"; if not derivable, write "not derivable from drawings."
- **Flags / RFIs:** any NOT SPECIFIED item or dependency that blocks this trade (or "none").

## 💰 COST ESTIMATE — SF BAY AREA 2026

AREA GATE — settle this BEFORE you price, then STATE which tier you are in. Decide by WHAT THE SET CONTAINS, in this order. The default whenever any plan is visible is Tier 2, NOT Tier 3.

TIER 1 — STATED or DIMENSIONED: a drawing STATES the area/count, OR you derive it from a DIMENSIONED plan (show the sheet + the arithmetic), OR the user gave it in PROJECT NOTES (attribute it inline). → Produce the FULL priced build-up in the two tables below, basis shown on every line that uses it. State: "Area basis: <value> — Tier 1, stated/dimensioned (cite sheet or note)."

TIER 2 — VISUAL ESTIMATE (THE DEFAULT whenever a plan is present): if the set contains ANY plan, roof plan, floor plan, or key plan of the work area but no stated/dimensioned number, you ARE in Tier 2 — you MUST produce a visual area estimate and PRICE ON IT. Do NOT drop to Tier 3 because dimensions are missing: a plan you can see is a plan you can scale. Do BOTH:
  • SHOW THE ESTIMATE MATH — how you got the area: use a printed scale bar / graphic scale if present, else a KNOWN ELEMENT of standard size (a 3'-0" door leaf, a 9'×18' parking stall, a stated room dimension, a 12" grid, a ~11" stair tread). e.g. "roof plan reads ≈ 4 bays × 2 deep; scaled off the 3'-0" door on A2.01 ≈ 78' × 41' ≈ 3,200 SF — visual estimate, NOT dimensioned."
  • KNOWN-ELEMENT CROSS-CHECK — restate the reference and the bound it gives, e.g. "cross-checked against the door leaf — supports 3,000–3,400 SF."
  Lead the section with this exact callout on its own line: "⚠ PRELIMINARY — priced on a visual area estimate (not dimensioned); ±20–30%. Add a field-measured SF in Project Notes to tighten it." Then produce the FULL priced build-up, every headline number kept marked preliminary.

TIER 3 — NO PLAN VIEW AT ALL (rare): reserved ONLY for a set that contains NO plan / roof plan / floor plan / key plan of the work area — e.g. a details-only, schedule-only, or single-detail sheet where the extent of work simply is not drawn anywhere. This is the ONLY case where you may withhold a total. Then:
  1. First line, verbatim on its own line: "⚠ Work area not shown in this set — add a plan sheet or a measured SF in Project Notes for a priced estimate."
  2. ONE table of UNIT RATES BY TRADE — rates only, NO extended amounts, NO subtotal, NO rollup, NO Low/High, NO owner budget:
| Trade | Unit rate (SF Bay Area 2026) | Basis / assumption |
     Give the $/SF (or $/LF, $/unit) each trade would run with the pricing rules below baked into the rate. Do NOT multiply by an assumed area anywhere.
  3. Close with exactly: "Add a plan sheet or a measured area to unlock a full priced estimate." Then STOP — do NOT output the line-item or rollup tables below.

DECISION RULE: if you can SEE the work area drawn in plan, you are in Tier 2 at minimum — estimate it and price it. A visible plan must NEVER produce the Tier-3 lockout. The only failure worse than a loud preliminary estimate is a silent refusal to price a job whose plan is right there on the sheet.

PRICING BASIS — read before you price:
- This estimate is on a COMPETITIVE-BID BASIS (say so under the tables). Do NOT stack markups: subcontractor markup (15%), General Conditions, and ONE OH&P layer of 10–15% — no more. General Conditions are ITEMIZED as real operations in the line-item table (supervision, access control, rigging, mobilization, debris) and their sum is carried up to the rollup — do NOT ALSO pile a blanket ~8% on top of itemized GCs (that double-counts). Only if some GC scope genuinely can't be itemized may you add a residual ~8% allowance on the remainder. State contingency SEPARATELY as an owner-held risk allowance, NOT inside the bid subtotal. Owners carry contingency; a bid that buries it reads as padded and loses the job.
- WARRANTIED COLD / FLUID-APPLIED membrane systems (CIM-1000-type reinforced cold fluid-applied and similar) are installed by CERTIFIED applicators under manufacturer warranty. Price them at INSTALLED certified-applicator rates of ~$55–75/SF, NOT generic waterproofing-coating rates of $8–12/SF — the generic rate understates these systems by 5–7× and is the single biggest cause of a lowball roof-deck number.
- INCLUDE drawing-visible transition scope that is easy to miss: siding repair / remove-and-reinstall at wall penetrations and terminations, and paint-out / paint-for-flashing at wall transitions. If a detail shows a membrane turning up a wall behind siding, the siding R&R and the flashing paint ARE in scope — price them as their own line items.
- The headline Low/High range is THIS run's competitive-bid subtotal ±10–15% — compute it FROM the subtotal you just built, never carried over from any prior estimate.
When any line uses an area or count for its pricing ($/SF, $/LF, $/unit), reference the SAME quantity and basis stated in the PROJECT SUMMARY "Scope scale" line, so the math is auditable end-to-end — e.g. "Waterproofing | CIM 1000 field @ $X/SF over 3,200 SF (area per Summary, derived from A1.01) | …". Do not introduce a different area than the Summary's.
(a) LINE-ITEM table — this must read like a GC's BID SCHEDULE, built from OPERATIONS a crew actually performs and mobilizes for, NOT a single $/SF line. GROUP BY trade/division with a bolded "**<Trade> subtotal**" row before the next group; cite the scope's sheet/detail in the Scope column where possible.
| Trade | Scope (cite sheet) | Labor $ | Material $ | Total $ |
List each OPERATION as its own row (e.g. "Waterproofing | Surface prep & primer at deck (A8.01) | 9,000 | 3,000 | 12,000"), then the bolded subtotal row. Itemize at operation granularity — do NOT collapse a job into one "waterproofing @ $X/SF" line. In particular:
  • GENERAL CONDITIONS / GENERAL REQUIREMENTS as their OWN line items (a "### General Requirements" group at the top), not merely a % in the rollup: project supervision / superintendent, site access control & occupant protection, rigging / hoisting & material handling, mobilization / demobilization, dumpster & debris haul. Real dollars on each.
  • DISASSEMBLY, RELOCATION-TO-STORAGE, and DEMOLITION as SEPARATE operations — never lumped. Elements that come off and get stored for reuse (railings, pavers, equipment, fixtures) are one line; tear-out/haul of what's discarded is another.
  • REINSTALLATION as a SEPARATE line from INSTALLATION — putting stored/removed elements back is distinct labor from installing the new system.
  • WARRANTY and INSURANCE as line items WHEN the spec requires them — a manufacturer-warranted system (e.g. one specified by reference to a spec section like 07 14 16, or any certified-applicator membrane) carries a manufacturer warranty registration and applicator/insurance cost; show them explicitly.
  • FURNITURE / CONTENTS PROTECTION & MOVES when the set indicates occupied or furnished space below or adjacent (protection, covering, temporary relocation).
  • Easy-to-miss transition scope (siding R&R at penetrations/terminations, paint-for-flashing at wall transitions) each as its own line, per the pricing rules above.
Every row is a bid item a sub or GC would actually quote, so a reader can explain the variance against a real bid line by line.
(b) ROLLUP table:
| Category | Amount |
|---|---|
| Direct Labor | $X |
| Materials | $X |
| Subcontractors (incl. 15% markup) | $X |
| General Conditions (sum of the itemized General Requirements lines above — NOT an added blanket %) | $X |
| GC Overhead & Profit (10–15%) | $X |
| **Competitive-bid subtotal** | **$X** |
| Contingency (10%, owner-held risk — stated separately, NOT in the bid subtotal) | $X |
| **Owner budget incl. contingency** | **$X** |
| **Low / High range (competitive-bid basis)** | **$X – $X** |
Use labor rates: Plumber $145–165/hr, Electrician $155–175/hr, Framing $85–105/hr, Drywall/Taper $90–110/hr, Tile $95–115/hr, Painter $75–90/hr, Waterproofing/Roofing $95–120/hr. For warrantied cold/fluid-applied membrane assemblies, price by INSTALLED area at ~$55–75/SF (certified-applicator, materials+labor+warranty), not by crew hour.
Directly under the tables output this exact line, verbatim, every time: "_AI pricing is directional — calibrate against local sub quotes._"

## 📅 SCHEDULE ESTIMATE
Per-PHASE table in construction order. EVERY phase row gives the responsible crew/trade, the predecessor/dependency, and the duration. Include cure / flood-test / inspection WAIT times as their OWN explicit rows — they drive roof-deck schedules (e.g. "Membrane cure + flood test | Waterproofing | after topcoat applied | 3 days (hold)").
| Phase | Crew / Trade | Predecessor / dependency | Duration (days) |
End with one line: "**Total project duration: ~N weeks**".

## ✅ CODE COMPLIANCE FLAGS
Reference material — placed after the schedule on purpose. One "### " subheader per category that actually applies (ADA / CBC 11B, Title 24, Structural / Seismic, OSHA, CBC 2022 / CalGreen / SF amendments) — do not pad with categories irrelevant to this project. Under each, one bullet per flag, and EVERY flag must give all three: the specific code section; what triggers it IN THIS drawing set (name the sheet/detail); and the required action. Example: "OSHA 1926.501 — open roof edge during tear-off (Detail 4/A5.01); provide guardrail or personal fall-arrest before work at edge."

## 🧠 LEARNINGS (for ARIA's memory)
After the schedule section, add EXACTLY this fenced JSON block — it feeds the company's learning system, do not omit it or change the key names:

\`\`\`json
{
  "project_type": "SRO | Affordable Housing | Senior/ADA Housing | Multi-Family TI | Residential Remodel | Commercial TI | Other",
  "trades_identified": ["Plumbing", "Electrical", "..."],
  "materials_called_out": [{"item": "5/8\\" Type X drywall", "qty": "850 sf"}, {"item": "...", "qty": "..."}],
  "cost_by_trade": [{"trade": "Plumbing", "low": 12000, "high": 16000}, {"trade": "...", "low": 0, "high": 0}],
  "code_flags": [{"category": "ADA|Title24|OSHA|Structural|Other", "section": "ADA §404.2.3", "issue": "door width below 32\\" clear"}],
  "total_low": 100000,
  "total_high": 150000
}
\`\`\`

Numbers are plain integers (no commas, no $). Be direct, no preamble, no sign-off. Start with "## 📋 PROJECT SUMMARY".`;

    const userContent = [];
    if (!storageRef) {
      if (bpIsPDF) {
        userContent.push({type:"document", source:{type:"base64", media_type:"application/pdf", data:bpData}});
      } else {
        userContent.push({type:"image", source:{type:"base64", media_type:bpMediaType, data:bpData}});
      }
    }
    const scopeNote = regionMode
      ? `You are analyzing a ZOOMED-IN CROP of one region of page ${pageNum} of a larger drawing set — the user zoomed into this detail specifically so the callout text is legible. Read EVERY product callout, manufacturer, model/spec number, thickness, mil coverage, dimension, and note visible in this crop, VERBATIM, and base your review on them. This zoomed crop is the reliable way to read fine leader-line specs, so extract them fully.\n\n`
      : pageMode ? `You are analyzing ONLY page ${pageNum} of a larger multi-page drawing set. Base your entire review on what is visible on this single page.\n\n` : "";
    userContent.push({type:"text", text: scopeNote + userText});

    const headers = {
      "Content-Type":"application/json",
      "Authorization":"Bearer "+sb._token(),
      "apikey":SUPABASE_ANON_KEY,
      "anthropic-version":"2023-06-01",
      "anthropic-dangerous-direct-browser-access":"true",
    };

    let messages = [{role:"user", content:userContent}];
    let fullText = "";
    let rounds = 0;
    const MAX_ROUNDS = 3; // initial + up to 2 continuations

    stage = "review"; alog.stage = stage;
    alog.path = storageRef ? "storage(file_id)" : (pageMode ? "page" : (regionMode ? "region" : "inline"));
    const tReview = Date.now();
    let lastStop = null;
    try {
      // Reliability guard: one automatic retry if the review comes back completely
      // empty. The empty responses trace to the old per-request file shuttle; retry
      // fresh once before surfacing failure. "Empty" means nothing usable came back,
      // so the retry restarts generation from scratch rather than resuming a partial.
      for (let attempt = 0; attempt < 2; attempt++) {
        if (attempt > 0) {
          messages = [{role:"user", content:userContent}];
          rounds = 0; lastStop = null;
          alog.emptyRetries = (alog.emptyRetries || 0) + 1;
          console.warn("[blueprint] empty review response — one automatic retry");
        }
      while (rounds < MAX_ROUNDS) {
        const reqBody = {
          model: BLUEPRINT_ANALYSIS_MODEL, // high-res vision for detail-sheet callouts
          max_tokens: 16000,
          // claude-sonnet-5 has extended thinking ON by default; on this demanding
          // review over a ~210K-token PDF it spent the ENTIRE max_tokens budget
          // thinking and emitted ZERO text → the "empty response" outage. Adaptive
          // thinking at LOW effort is the fix AND a quality win: an A/B on a real
          // 20MB set gave +64% verbatim callouts (252 vs 154) and finished all
          // sections in ONE call (end_turn, no continuation) vs disabled which
          // truncated at max_tokens. See commit for the numbers.
          thinking: { type: "adaptive" },
          output_config: { effort: "low" },
          // No temperature: Sonnet 5 rejects non-default sampling params
          // (temperature:0 → HTTP 400 "temperature is deprecated for this model").
          // Run-to-run consistency rides on the deterministic prompt plus the
          // prior-estimate reference block (which reconciles rather than clamps
          // to ±5% — the current build-up wins on material disagreement).
          usage_type: "blueprint",
          company_id: cid,
          plan: session?.plan || "starter",
          system: systemPrompt,
          messages,
        };
        if (bpIsPDF) reqBody.betas = ["pdfs-2024-09-25"];
        if (storageRef) reqBody.storage_ref = storageRef;
        reqBody.stream = true; // stream so a long Sonnet run doesn't hit the gateway 504

        // Progressive render: show tokens as they arrive, on top of prior rounds.
        // If a round fails AFTER we already have content (e.g. a continuation
        // request drops), keep what we have and surface a retry — don't throw
        // away a complete-looking review over a failed tail request.
        let chunk, stopReason, usage, diag;
        try {
          ({ text: chunk, stopReason, usage, diag } = await proxyStreamText(reqBody, headers, (partial)=>{
            if (alog.ttftMs == null && partial) alog.ttftMs = Date.now() - tReview; // time-to-first-token
            setAiReview(fullText + partial);
          }));
        } catch (roundErr) {
          if (fullText) { console.warn("Blueprint review: round failed, keeping partial:", roundErr?.message || roundErr); setAiIncomplete(true); break; }
          throw roundErr;
        }
        alog.reviewCalls++;
        alog.reviewTokensIn += (usage && usage.input) || 0;
        alog.reviewTokensOut += (usage && usage.output) || 0;
        lastStop = stopReason;
        if (!chunk) {
          // EMPTY round: a 200 stream with no text_delta. Capture the exact request
          // shape + response event breakdown so the next failing run self-diagnoses
          // (the delta vs a working replay is content-specific, not size/betas).
          alog.emptyDiag = {
            attempt, round: rounds,
            req: { model: reqBody.model, max_tokens: reqBody.max_tokens, betas: reqBody.betas || null,
                   sysChars: (systemPrompt || "").length, verifiedChars: (verifiedBlock || "").length,
                   msgs: messages.length, hasStorageRef: !!storageRef },
            resp: diag || null,
          };
          console.warn("[blueprint] EMPTY review round — diag:", JSON.stringify(alog.emptyDiag));
          break;
        }
        fullText += chunk;
        setAiReview(fullText);
        rounds++;
        if (rounds > 1) alog.continuations++;

        if (stopReason !== "max_tokens") break;
        // Cut off — send a continuation (capped at MAX_ROUNDS-1 continuations so we
        // never re-process the PDF unboundedly). Reuse the same user content.
        if (rounds >= MAX_ROUNDS) break;
        messages = [
          {role:"user", content:userContent},
          {role:"assistant", content: fullText},
          {role:"user", content: "Continue from exactly where you left off mid-sentence. Do not repeat any content. Maintain the same section structure and finish all remaining sections, including the closing ```json learnings block."},
        ];
      }
        if (fullText) break; // got usable content — don't burn the retry
      }

      alog.stopReason = lastStop; alog.reviewChars = fullText.length;
      alog.reviewMs = Date.now() - tReview;
      if (!fullText) throw new Error("AI returned an empty response");
      // Hit the continuation cap while still truncated — flag it so the user gets
      // a readable "truncated — Retry" affordance instead of unbounded re-rounds.
      if (lastStop === "max_tokens") setAiIncomplete(true);

      // Persist the review text to the cloud so it survives reload (analyses cost
      // real money). Save whatever we have, even a continuation-cut partial.
      stage = "saving"; alog.stage = stage;
      const stamp = new Date().toISOString();
      setAnalyzedAt(stamp);
      saveBlueprintAnalysis(bp, { ai_review: fullText, analyzed_at: stamp, engine_version: ENGINE_VERSION });
      alog.saved = true;

      // Extract structured learnings and persist to grok_learning.
      const learnings = extractLearningsJSON(fullText);
      if (learnings) {
        cacheEstimate(bp.id, learnings);
        saveLearning({
          event_type: "blueprint_analyzed",
          project_id: projContext?.id,
          project_type: learnings.project_type || projContext?.type || null,
          payload: {
            blueprint_name: bp.name,
            blueprint_file_type: bp.fileType,
            ...learnings,
          },
          cid,
        });
      }
    } catch (e) {
      // Never end silently empty — say where it died and offer Retry.
      const msg = e?.message || "unknown error";
      alog.error = `${stage}: ${msg}`;
      console.error("Blueprint AI review failed at", stage, "—", msg);
      setAiError(`Analysis failed at ${stage} — ${msg}. Tap Retry.`);
    } finally {
      runningRef.current = false;
      setAiReviewing(false);
      alog.totalMs = Date.now() - t0;
      alog.ranAt = new Date().toISOString();
      persistBpRun(alog); // ring buffer in localStorage["grok_bp_runlog"] for before/after analysis
      console.log(`[bp ${bp.id}] analysis ${alog.error ? "FAILED ("+alog.error+")" : "done"} in ${(alog.totalMs/1000).toFixed(1)}s · ttft ${alog.ttftMs??"—"}ms · tiles ${alog.tilesMs??"—"}ms (p50 ${alog.tiles?.tileP50??"—"}/p95 ${alog.tiles?.tileP95??"—"}) · review ${alog.reviewMs??"—"}ms · ${alog.continuations} continuation(s) · ${alog.emptyRetries} empty-retry`, alog);
    }
  };

  // Deep contractor-grade analysis. Second pass after the initial review.
  // Produces material takeoff, scope-by-trade checklist, constructibility
  // flags, VE suggestions, permits, inspection sequence. Uses the same
  // continuation loop as runAIReview so SROs don't get truncated.
  const runDeepAnalysis = async (bp, opts = {}) => {
    if (!bp || (!bp.dataUrl && !bp.storagePath)) return;
    runningRef.current = true;
    setDeepAnalyzing(true);
    setDeepAnalysis("");
    setDeepStructured(null);
    setDeepError("");
    setDeepBpId(bp.id);
    setPanelTab("ai"); // deep now fills in place inside the unified AI report

    const projContext = selectedProject || proj;
    const cid = session?.companyId || window._grokCompanyId || "local";

    // Per-page mode: rasterize a single PDF page and analyze that (fallback for
    // PDFs too large to forward whole). Inherit the page the initial review ran
    // on (aiReviewPage) when the caller doesn't specify, so Deep Analysis stays
    // on the same page. Otherwise downscale / size-guard the whole drawing BEFORE
    // building the request so an oversized body never trips the runtime cliff.
    const pageMode = opts.pageMode ?? (aiReviewPage != null);
    const regionMode = !!opts.regionMode;
    const pageNum = opts.pageNum || aiReviewPage || currentPage || 1;
    let bpIsPDF, bpData, bpMediaType, storageRef = null;
    try {
      if (regionMode) {
        // Legible zoomed crop of one detail — the reliable path for fine callouts.
        const rendered = await renderPdfRegionToImage(bp.dataUrl, pageNum, opts.region);
        const small = await downscaleImageForAI(rendered);
        bpData = small.split(",")[1] || "";
        bpMediaType = "image/jpeg";
        bpIsPDF = false;
        setDeepAnalysisPage(pageNum);
        setDeepTooLarge(false);
      } else if (pageMode) {
        const rendered = await renderPdfPageToImage(bp.dataUrl, pageNum);
        const small = await downscaleImageForAI(rendered);
        bpData = small.split(",")[1] || "";
        bpMediaType = "image/jpeg";
        bpIsPDF = false;
        setDeepAnalysisPage(pageNum);
        setDeepTooLarge(false);
      } else if (bp.storagePath) {
        // Primary path: reference the file in Storage; the proxy fetches it
        // server-side, so the browser never sends the base64 body.
        bpIsPDF = bp.fileType === "pdf" || bp.name?.toLowerCase().endsWith(".pdf");
        bpMediaType = bp.mediaType || (bpIsPDF ? "application/pdf" : "image/jpeg");
        storageRef = { path: bp.storagePath, isPDF: bpIsPDF, mediaType: bpMediaType, bpId: bp.id };
        setDeepAnalysisPage(null);
        setDeepTooLarge(false);
      } else {
        const bpPrep = await prepBlueprintForAI(bp);
        bpIsPDF = bpPrep.isPDF;
        bpData = bpPrep.data;
        bpMediaType = bpPrep.mediaType;
        setDeepAnalysisPage(null);
        setDeepTooLarge(false);
      }
    } catch (e) {
      if (e?.code === "PDF_TOO_LARGE") {
        setDeepTooLarge(true);
        setDeepError(`This PDF is too large to analyze whole (${e.mb} MB). Reduce or split the file — or analyze the current page using the button below.`);
      } else {
        setDeepError(e?.message || "Could not prepare the drawing for analysis.");
      }
      setDeepAnalyzing(false);
      return;
    }

    const cachedDeep = getCachedEstimate(bp.id);
    const deepAnchor = cachedDeep?.total_low ? `\n\nA prior estimate for this drawing was $${Number(cachedDeep.total_low).toLocaleString()} – $${Number(cachedDeep.total_high).toLocaleString()}. Treat it as a reference checkpoint, not a target. Build your trade-by-trade costs up fresh; where this deeper read materially disagrees with the prior number (added or removed scope, corrected rates), your CURRENT build-up wins — state the recalibrated numbers and note in one line what changed. Do not force costs back to the prior total to stay consistent.` : "";

    // Reuse the character-exact callouts the initial review's tiling pass already
    // transcribed for this drawing (no re-tiling), so Deep Analysis quotes the
    // same verbatim product strings.
    const deepCallouts = verifiedCalloutsRef.current[bp.id] || [];
    const deepVerifiedBlock = deepCallouts.length ? `\n\n## ✅ VERIFIED DETAIL-SHEET CALLOUTS (character-exact)\nTranscribed from high-resolution crops of the detail sheets — reproduce these EXACT characters wherever they belong (do not re-spell, round, or expand), and cite the detail:\n${deepCallouts.map(c => `- "${c}"`).join("\n")}` : "";

    const systemPrompt = `You are a California licensed GC running a DEEP analysis on a construction drawing. The initial scope review is already done. Now produce a contractor-grade, TRADE-ORGANIZED buildable plan. Be specific and quantitative, grounded in 2026 SF Bay Area conditions. When the drawings name a product, manufacturer, model or spec number, thickness, mil coverage, or application rate, reproduce it VERBATIM — quote the exact annotation text and never paraphrase a product spec into a generic term. Read from BOTH the PDF text layer AND the drawing image, and walk EVERY sheet including the detail sheets — leader-line callouts on detail/section sheets are frequently NOT in the text layer and must be read VISUALLY off the sheet; append " (vision-read)" to any spec you read off the drawing rather than the text layer. Only say "not specified" after checking both the text layer and the visible callouts on every sheet — NEVER invent a plausible product name, model number, or coverage; a fabricated spec is worse than an admitted gap. Distinguish a product named elsewhere in the set (extract it) from one deferred to a spec document you don't have: when a system is called out generically with only a spec-section reference (e.g. "per spec section 07 14 16") and no product name appears anywhere in the set, state "Product not identified in drawing set — specified by reference to Spec Section <number> (not included); confirm with architect" and quote the section number verbatim, so the GC knows which document holds the answer. Use the exact structure requested.${deepAnchor}${deepVerifiedBlock}`;

    const userText = `Run a deep analysis on this drawing for ${projContext?.name || "the project"}${projContext?.addr ? " at " + projContext.addr : ""} (${projContext?.type || "renovation"}).

This is the DEEP-ANALYSIS companion to the initial review — the SAME document family. Use the SAME "## " (level-2) headers (each renders as its own collapsible card) and the SAME order philosophy: summary first, then evidence, then reference. Produce EXACTLY these sections, IN THIS ORDER. End with the JSON block.

## 🏗 PROJECT OVERVIEW
3–4 sentences: what the whole job is, the primary systems involved, and the overall build sequence. A tight paragraph — no bullet list.

## 📦 MATERIAL TAKEOFF
ONE consolidated takeoff across ALL trades — the estimate's evidence layer. A single table:
| Item / Material | Spec (verbatim) | Qty | Unit | Trade |
Every material/product/system the set specifies, one row each, products quoted VERBATIM (never paraphrased). Give a quantity with units where derivable and append "(verify)"; where not derivable write "not derivable." Read from BOTH the text layer AND the drawing image; append " (vision-read)" to any spec read off the sheet. For a system deferred to a spec document (only a spec-section reference, no product name in the set), write "deferred to Spec Section <number>" verbatim in the Spec column.

## ✅ TRADE CHECKLISTS
One "### <emoji> <Trade>" subsection per trade actually present, in construction sequence (Demolition → Concrete/Foundations → Framing & Carpentry → Waterproofing/Membrane → Plumbing → Electrical → Mechanical/HVAC → Drywall & Finishes → Flooring/Pavers → Painting/Coatings → Specialties). Under each trade, exactly:
- a **Scope** checklist of 5–15 items, each written as "- [ ] <concrete, quantitative task a sub can check off>".
- a one-line "**Materials:**" cross-reference to the MATERIAL TAKEOFF above (name the products + the detail; do NOT re-quote the full callout, e.g. "CIM 1000 membrane — see Takeoff / Detail 4").
Omit trades with no visible scope; do not invent trades not in the drawings. Constructibility flags are consolidated in the NEXT section — do NOT repeat them per trade.

## ⚠️ CONSTRUCTIBILITY FLAGS
ALL constructibility, coordination, sequencing, and code issues across every trade, consolidated into ONE list ordered HIGH → LOW. One bullet each: "**[HIGH/MED/LOW]** <issue> (<trade>) — <what to do about it>."

## 💡 VALUE ENGINEERING
| Item | Standard Spec | VE Alternative | Est. $ Savings | Trade-off |
4–8 suggestions ordered by $ savings descending. Do not VE away an explicitly specified proprietary system without naming the spec it deviates from.

## 📋 PERMITS & INSPECTIONS
A permits table — | Permit | AHJ | Est. Fee | Lead Time | Trigger | — then an inspection-sequence table — | # | Phase | Inspection | Code Reference |.

## 🧠 LEARNINGS (deep analysis)
\`\`\`json
{
  "material_takeoff": [{"item": "5/8\\" Type X drywall", "spec": "ASTM C1396", "qty": 850, "unit": "sf"}],
  "scope_by_trade": [{"trade": "Electrical", "items": ["Install 200A panel", "Pull service feeder", "..."]}],
  "constructibility_flags": [{"severity": "HIGH|MED|LOW", "issue": "...", "action": "..."}],
  "ve_suggestions": [{"item": "...", "standard": "...", "alternative": "...", "savings": 5000, "tradeoff": "..."}],
  "permits": [{"name": "...", "ahj": "...", "fee": 0, "lead_time_days": 0}],
  "inspection_sequence": [{"phase": "...", "inspection": "...", "code_ref": "..."}]
}
\`\`\`

Numbers as plain integers (no commas, no $). Be direct, no preamble. Start with "## 🏗 PROJECT OVERVIEW".`;

    const userContent = [];
    if (!storageRef) {
      if (bpIsPDF) {
        userContent.push({type:"document", source:{type:"base64", media_type:"application/pdf", data:bpData}});
      } else {
        userContent.push({type:"image", source:{type:"base64", media_type:bpMediaType, data:bpData}});
      }
    }
    const scopeNote = regionMode
      ? `You are analyzing a ZOOMED-IN CROP of one region of page ${pageNum} of a larger drawing set — the user zoomed into this detail specifically so the callout text is legible. Read EVERY product callout, manufacturer, model/spec number, thickness, mil coverage, dimension, and note visible in this crop, VERBATIM, and base your review on them. This zoomed crop is the reliable way to read fine leader-line specs, so extract them fully.\n\n`
      : pageMode ? `You are analyzing ONLY page ${pageNum} of a larger multi-page drawing set. Base your entire review on what is visible on this single page.\n\n` : "";
    userContent.push({type:"text", text: scopeNote + userText});

    const headers = {
      "Content-Type":"application/json",
      "Authorization":"Bearer "+sb._token(),
      "apikey":SUPABASE_ANON_KEY,
      "anthropic-version":"2023-06-01",
      "anthropic-dangerous-direct-browser-access":"true",
    };

    let messages = [{role:"user", content:userContent}];
    let fullText = "";
    let rounds = 0;
    const MAX_ROUNDS = 4; // deep analysis is longer than the initial review

    try {
      // Same reliability guard as the review path: one automatic retry if deep
      // analysis returns completely empty, before surfacing failure.
      for (let attempt = 0; attempt < 2; attempt++) {
        if (attempt > 0) {
          messages = [{role:"user", content:userContent}];
          rounds = 0;
          console.warn("[blueprint] empty deep-analysis response — one automatic retry");
        }
      while (rounds < MAX_ROUNDS) {
        const reqBody = {
          model: BLUEPRINT_ANALYSIS_MODEL, // high-res vision for detail-sheet callouts
          max_tokens: 16000,
          // claude-sonnet-5 has extended thinking ON by default; on this demanding
          // review over a ~210K-token PDF it spent the ENTIRE max_tokens budget
          // thinking and emitted ZERO text → the "empty response" outage. Adaptive
          // thinking at LOW effort is the fix AND a quality win: an A/B on a real
          // 20MB set gave +64% verbatim callouts (252 vs 154) and finished all
          // sections in ONE call (end_turn, no continuation) vs disabled which
          // truncated at max_tokens. See commit for the numbers.
          thinking: { type: "adaptive" },
          output_config: { effort: "low" },
          // No temperature: Sonnet 5 rejects non-default sampling params
          // (temperature:0 → HTTP 400 "temperature is deprecated for this model").
          // Run-to-run consistency rides on the deterministic prompt plus the
          // prior-estimate reference block (which reconciles rather than clamps
          // to ±5% — the current build-up wins on material disagreement).
          usage_type: "blueprint",
          company_id: cid,
          plan: session?.plan || "starter",
          system: systemPrompt,
          messages,
        };
        if (bpIsPDF) reqBody.betas = ["pdfs-2024-09-25"];
        if (storageRef) reqBody.storage_ref = storageRef;
        reqBody.stream = true; // stream so a long Sonnet run doesn't hit the gateway 504

        const { text: chunk, stopReason } = await proxyStreamText(reqBody, headers, (partial)=>setDeepAnalysis(fullText + partial));
        if (!chunk) break;
        fullText += chunk;
        setDeepAnalysis(fullText);
        rounds++;
        if (stopReason !== "max_tokens") break;
        messages = [
          {role:"user", content:userContent},
          {role:"assistant", content: fullText},
          {role:"user", content: "Continue from exactly where you left off mid-sentence. Do not repeat any content. Finish all remaining sections including the closing ```json learnings block."},
        ];
      }
        if (fullText) break; // got usable content — don't burn the retry
      }

      if (!fullText) throw new Error("AI returned an empty response");

      // Persist the deep-analysis text so it survives reload.
      const deepStamp = new Date().toISOString();
      setDeepAnalyzedAt(deepStamp);
      saveBlueprintAnalysis(bp, { deep_analysis: fullText, deep_analyzed_at: deepStamp, engine_version: ENGINE_VERSION });

      const structured = extractLearningsJSON(fullText);
      if (structured) {
        setDeepStructured(structured);
        // Deep analysis often produces firmer cost numbers — refresh the cache
        // only if the deep pass produced totals (some deep responses don't).
        if (structured.total_low) cacheEstimate(bp.id, structured);
        saveLearning({
          event_type: "blueprint_deep_analyzed",
          project_id: projContext?.id,
          project_type: projContext?.type || null,
          payload: {
            blueprint_name: bp.name,
            blueprint_file_type: bp.fileType,
            ...structured,
          },
          cid,
        });
      }
    } catch (e) {
      setDeepError(e?.message || "Deep analysis failed");
    }
    runningRef.current = false;
    setDeepAnalyzing(false);
  };

  // Persistent checkbox state for the scope checklist, keyed by bpId.
  const scopeChecksKey = activeBlueprint?.id ? "grok_scope_checks_" + activeBlueprint.id : null;
  React.useEffect(() => {
    if (!scopeChecksKey) { setCheckedScope({}); return; }
    try { setCheckedScope(JSON.parse(localStorage.getItem(scopeChecksKey) || "{}")); } catch { setCheckedScope({}); }
  }, [scopeChecksKey]);

  const toggleScopeItem = (itemId) => {
    if (!scopeChecksKey) return;
    setCheckedScope(prev => {
      const next = {...prev, [itemId]: !prev[itemId]};
      try { localStorage.setItem(scopeChecksKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Clear AI + deep state when switching to a different blueprint without re-running.
  React.useEffect(() => {
    if (activeBlueprint?.id && aiBpId && activeBlueprint.id !== aiBpId) {
      setAiReview("");
      setAiError("");
      setAiBpId(null);
      setAiReviewPage(null);
      setAiTooLarge(false);
      setDraftedGaps({});
    }
    if (activeBlueprint?.id && deepBpId && activeBlueprint.id !== deepBpId) {
      setDeepAnalysis("");
      setDeepStructured(null);
      setDeepError("");
      setDeepBpId(null);
      setDeepAnalysisPage(null);
      setDeepTooLarge(false);
    }
  }, [activeBlueprint?.id, aiBpId, deepBpId]);

  // Viewer source of truth: if the active blueprint has a Storage path but no
  // local dataURL (metadata-only on this device), hydrate the dataURL from a
  // signed Storage URL so the viewer + per-page render share one source.
  React.useEffect(() => {
    const bp = activeBlueprint;
    if (!bp || bp.dataUrl || !bp.storagePath) return;
    let cancelled = false;
    (async () => {
      const url = await signedBlueprintUrl(bp.storagePath);
      if (!url || cancelled) return;
      try {
        const resp = await fetch(url);
        if (!resp.ok) return;
        const blob = await resp.blob();
        const dataUrl = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = (e) => res(e.target.result);
          r.onerror = rej;
          r.readAsDataURL(blob);
        });
        if (!cancelled) setActiveBlueprint(prev => (prev && prev.id === bp.id ? { ...prev, dataUrl, url: dataUrl } : prev));
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [activeBlueprint?.id, activeBlueprint?.storagePath]); // eslint-disable-line

  // Auto-load a blueprint's saved analysis when it (re)opens, so review + deep
  // analysis survive reload without paying to re-run. Skips while an analysis is
  // in flight (don't clobber live output) and when we're already showing this
  // blueprint's analysis. Clears stale text when the reopened blueprint has none.
  React.useEffect(() => {
    const bp = activeBlueprint;
    if (!bp || !bp.id || aiReviewing || deepAnalyzing || aiBpId === bp.id) return;
    let cancelled = false;
    (async () => {
      const saved = await loadBlueprintAnalysis(bp.id);
      // A Re-analyze may have started while we were fetching — never overwrite a
      // running analysis (blank-over-partial race).
      if (cancelled || runningRef.current) return;
      setAiBpId(bp.id);
      setAiError(""); setAiIncomplete(false);
      const corr = (saved && saved.corrections) || bp.corrections || "";
      setCorrections(corr); correctionsRef.current = corr;
      if (saved && (saved.ai_review || saved.deep_analysis)) {
        setAiReview(saved.ai_review || "");
        setDeepAnalysis(saved.deep_analysis || "");
        setDeepStructured(null);
        setAnalyzedAt(saved.analyzed_at || null);
        setDeepAnalyzedAt(saved.deep_analyzed_at || null);
      } else {
        setAiReview(""); setDeepAnalysis(""); setDeepStructured(null);
        setAnalyzedAt(null); setDeepAnalyzedAt(null);
      }
    })();
    return () => { cancelled = true; };
  }, [activeBlueprint?.id]); // eslint-disable-line

  const isPdfBp = activeBlueprint?.fileType === "pdf" || activeBlueprint?.name?.toLowerCase().endsWith(".pdf");

  // "Analyze This Page" fallback button — rendered next to the too-large error.
  // Rasterizes the current PDF page and runs the given analysis on just it.
  const analyzeThisPageBtn = (onRun, busy) => (isPdfBp && activeBlueprint ? (
    <button onClick={()=>onRun(activeBlueprint, {pageMode:true, pageNum:currentPage})} disabled={busy}
      style={{marginTop:8,fontSize:11,fontWeight:800,background:busy?T.bg3:T.gold,color:busy?T.muted:T.bg0,border:"none",borderRadius:7,padding:"7px 12px",cursor:busy?"wait":"pointer"}}>
      {busy ? "Analyzing…" : `📄 Analyze This Page (Page ${currentPage})`}
    </button>
  ) : null);

  // Detail-sheet callouts are read automatically by the tiling pass in
  // runAIReview (high-DPI crops → Haiku transcription → injected into the review),
  // so there is no user-facing "analyze region" button. renderPdfRegionToImage /
  // the regionMode code paths remain as internal machinery only.

  // "Page N analysis" badge shown above a per-page review result.
  const pageBadge = (pn) => (pn ? (
    <div style={{display:"inline-block",fontSize:10,fontWeight:800,color:T.bg0,background:T.gold,borderRadius:6,padding:"2px 8px",marginBottom:8}}>📄 Page {pn} analysis</div>
  ) : null);

  // Draft a pending RFI from a spec gap (reuses draftRFIFromFlag with a synthetic
  // flag). Spec gaps ARE RFIs in real workflow. Needs a synced account.
  const draftGapRFI = async (gapText, i) => {
    if (draftedGaps[i] === "done" || draftedGaps[i] === "drafting") return;
    setDraftedGaps(prev => ({ ...prev, [i]: "drafting" }));
    try {
      const rfi = await draftRFIFromFlag({
        flag: { id: "gap_" + Date.now() + "_" + i, title: gapText.slice(0, 90), desc: gapText, priority: "medium", trade: "", flaggedBy: session?.ownerName || session?.subName || "Field" },
        blueprint: activeBlueprint,
        proj: proj || selectedProject,
        session,
      });
      setDraftedGaps(prev => ({ ...prev, [i]: rfi ? "done" : "unavailable" }));
    } catch { setDraftedGaps(prev => ({ ...prev, [i]: "unavailable" })); }
  };

  // Renders the review's spec gaps as rows with a "→ Draft RFI" button.
  const renderSpecGaps = () => {
    const gaps = extractSpecGaps(aiReview);
    if (!gaps.length) return null;
    return (
      <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${T.subtle}`}}>
        <div style={{fontSize:10,fontWeight:800,color:T.amber,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>⚠ Spec gaps → RFI</div>
        {gaps.map((g, i) => {
          const st = draftedGaps[i];
          return (
            <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",padding:"6px 0",borderTop:i?`1px solid ${T.subtle}`:"none"}}>
              <span style={{flex:1,fontSize:11,color:T.text,lineHeight:1.5}}>{g}</span>
              <button onClick={()=>draftGapRFI(g, i)} disabled={st==="done"||st==="drafting"}
                title={st==="unavailable"?"Sign in with a synced account to create RFIs":"Draft a pending RFI from this gap"}
                style={{flexShrink:0,fontSize:10,fontWeight:700,border:"none",borderRadius:6,padding:"4px 9px",whiteSpace:"nowrap",
                  cursor:(st==="done"||st==="drafting")?"default":"pointer",
                  background:st==="done"?(T.greenBg||T.bg3):(st==="unavailable"?T.bg3:T.purple),
                  color:st==="done"?(T.green||T.text):(st==="unavailable"?T.muted:"white")}}>
                {st==="drafting"?"…":st==="done"?"✓ RFI drafted":st==="unavailable"?"sign in to sync":"→ Draft RFI"}
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  // Draft-RFI rows for the NOT SPECIFIED / VERIFY card in the reader-first review
  // layout. Returns the rows (no header — the card supplies it), or the section's
  // own text when there are no actionable gaps.
  const renderReviewGaps = (gapsBody) => {
    const gaps = extractSpecGaps(aiReview);
    if (!gaps.length) {
      return <div style={{fontSize:11.5,color:T.muted,lineHeight:1.6}}><MarkdownLite text={cleanReviewBody(gapsBody) || "No specification gaps noted."}/></div>;
    }
    return (
      <div>
        {gaps.map((g, i) => {
          const st = draftedGaps[i];
          return (
            <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",padding:"6px 0",borderTop:i?`1px solid ${T.subtle}`:"none"}}>
              <span style={{flex:1,fontSize:11,color:T.text,lineHeight:1.5}}>{g}</span>
              <button onClick={()=>draftGapRFI(g, i)} disabled={st==="done"||st==="drafting"}
                title={st==="unavailable"?"Sign in with a synced account to create RFIs":"Draft a pending RFI from this gap"}
                style={{flexShrink:0,fontSize:10,fontWeight:700,border:"none",borderRadius:6,padding:"4px 9px",whiteSpace:"nowrap",
                  cursor:(st==="done"||st==="drafting")?"default":"pointer",
                  background:st==="done"?(T.greenBg||T.bg3):(st==="unavailable"?T.bg3:T.purple),
                  color:st==="done"?(T.green||T.text):(st==="unavailable"?T.muted:"white")}}>
                {st==="drafting"?"…":st==="done"?"✓ RFI drafted":st==="unavailable"?"sign in to sync":"→ Draft RFI"}
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  // Full-failure error box (only when there's no content to keep) — with retry.
  const reviewErrorBox = () => (
    <div style={{background:T.redBg,border:`1px solid ${T.red}40`,borderRadius:8,padding:"9px 12px",fontSize:11,color:T.red,marginBottom:10}}>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <span style={{flex:1}}>⚠ {aiError}</span>
        {!aiTooLarge && <button onClick={()=>runAIReview(activeBlueprint)} disabled={aiReviewing}
          style={{flexShrink:0,fontSize:10,fontWeight:800,background:aiReviewing?T.bg3:T.gold,color:aiReviewing?T.muted:T.bg0,border:"none",borderRadius:6,padding:"4px 10px",cursor:aiReviewing?"wait":"pointer"}}>{aiReviewing?"…":"↻ Retry"}</button>}
      </div>
      {aiTooLarge && <div>{analyzeThisPageBtn(runAIReview, aiReviewing)}</div>}
    </div>
  );

  // Bottom-of-review affordance when a continuation dropped but the sections we
  // have are complete — retry on the section instead of a bare error line.
  const reviewRetryNotice = () => (
    <div style={{marginTop:12,background:T.bg2,border:`1px solid ${T.amber}55`,borderRadius:8,padding:"9px 12px",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
      <span style={{flex:1,fontSize:11,color:T.amber,lineHeight:1.5}}>⚠ Analysis didn't finish cleanly — the sections above are complete, but the tail (learnings/estimate cache) may be missing.</span>
      <button onClick={()=>runAIReview(activeBlueprint)} disabled={aiReviewing}
        style={{flexShrink:0,fontSize:11,fontWeight:800,background:aiReviewing?T.bg3:T.gold,color:aiReviewing?T.muted:T.bg0,border:"none",borderRadius:7,padding:"6px 12px",cursor:aiReviewing?"wait":"pointer"}}>{aiReviewing?"Retrying…":"↻ Retry"}</button>
    </div>
  );

  // "Analyzed <when>" stamp — shows a saved analysis survived reload.
  const fmtWhen = (ts) => { try { return new Date(ts).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
  const analyzedStamp = () => (analyzedAt && !aiReviewing
    ? <div style={{fontSize:9.5,color:T.muted,marginBottom:8}}>🕓 Analyzed {fmtWhen(analyzedAt)} · saved to cloud</div>
    : null);
  // "Already analyzed" banner shown when an upload matched an existing file.
  const dupNoticeBox = () => (dupNotice
    ? <div style={{background:T.bg2,border:`1px solid ${T.gold}55`,borderRadius:8,padding:"9px 11px",marginBottom:10,fontSize:11,color:T.text,lineHeight:1.5}}>
        📎 Previously analyzed{dupNotice.when ? ` ${fmtWhen(dupNotice.when)}` : ""} — showing saved results. <b>Re-analyze</b> anytime (drawings changed, notes added, or for the latest improvements).
      </div>
    : null);
  // Saved user notes shown in their OWN labeled block immediately on save — this is
  // a SEPARATE provenance layer, never blended into the drawing-derived analysis and
  // requiring NO re-analysis to display. Re-analyze only feeds them (attributed) to AI.
  const userNotesBox = () => (corrections.trim()
    ? <div style={{background:T.bg2,border:`1px solid ${T.subtle}`,borderLeft:`3px solid ${T.gold}`,borderRadius:8,padding:"9px 11px",marginBottom:10}}>
        <div style={{fontSize:10.5,fontWeight:800,color:T.gold,marginBottom:4,letterSpacing:0.2}}>📝 User-provided context — not from drawings</div>
        <div style={{fontSize:11.5,color:T.text,lineHeight:1.55,whiteSpace:"pre-wrap"}}>{corrections.trim()}</div>
        <div style={{fontSize:9.5,color:T.muted,marginTop:5,lineHeight:1.4}}>Kept separate from the drawing analysis. Re-analyze to let the AI use these — every use is attributed inline and any conflict with the drawings is surfaced.</div>
      </div>
    : null);
  // Per-blueprint project-notes editor — a SEPARATE user-provided layer (never
  // blended into Specified Materials; used only with inline attribution on Re-analyze).
  const correctionsEditor = () => (activeBlueprint
    ? <CollapsibleCard icon="📝" label="Project Notes (user-provided)" defaultOpen={false} badge={corrections.trim() ? "1 saved" : null} T={T}>
        <div style={{fontSize:10,color:T.muted,lineHeight:1.5,marginBottom:6}}>Optional — the estimate prices from the drawings alone. Add notes only to REFINE it (a field-measured area tightens a preliminary estimate; level, site conditions). Kept as a SEPARATE labeled layer, shown on save with no re-analysis. On Re-analyze the AI may USE a note but must ATTRIBUTE it inline and surface any drawing conflict. Notes never enter Specified Materials.</div>
        <textarea value={corrections} onChange={e=>{ setCorrections(e.target.value); correctionsRef.current = e.target.value; }}
          placeholder={"e.g. Deck is 2nd floor, over occupied units; actual work area ~2,850 SF per field measure"}
          style={{width:"100%",minHeight:64,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,color:T.text,fontSize:11.5,padding:"8px 10px",fontFamily:"inherit",resize:"vertical",boxSizing:"border-box"}}/>
        <div style={{display:"flex",gap:8,marginTop:6,alignItems:"center"}}>
          <button onClick={saveCorrections} disabled={correctionsSaving}
            style={{fontSize:11,fontWeight:800,background:correctionsSaving?T.bg3:T.gold,color:correctionsSaving?T.muted:T.bg0,border:"none",borderRadius:7,padding:"6px 12px",cursor:correctionsSaving?"wait":"pointer"}}>{correctionsSaving?"Saving…":"Save notes"}</button>
          <span style={{fontSize:9.5,color:T.muted}}>shown below immediately · Re-analyze to let the AI use them</span>
        </div>
      </CollapsibleCard>
    : null);

  // Place flag on canvas click (image path — uses pan/zoom transform math)
  const handleCanvasClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left - pan.x) / zoom / rect.width) * 100;
    const y = ((e.clientY - rect.top - pan.y) / zoom / rect.height) * 100;
    const pos = {x: Math.max(1, Math.min(99, x)), y: Math.max(1, Math.min(99, y)), page: 1};

    if (placingSubmittalPin && activeBlueprint) {
      setNewSubmittalPinPos(pos);
      setPlacingSubmittalPin(false);
      setPanelTab("flag");
      return;
    }
    if (!placingFlag || !activeBlueprint) return;
    setNewFlagPos(pos);
    setPlacingFlag(false);
    setPanelTab("flag");
  };

  // Place flag on PDF canvas (PDF path — coords already normalized + page-aware)
  const handlePdfClick = ({x, y, page}) => {
    const pos = {x, y, page};
    if (placingSubmittalPin && activeBlueprint) {
      setNewSubmittalPinPos(pos);
      setPlacingSubmittalPin(false);
      setPanelTab("flag");
      return;
    }
    if (!placingFlag || !activeBlueprint) return;
    setNewFlagPos(pos);
    setPlacingFlag(false);
    setPanelTab("flag");
  };

  const saveSubmittalPin = () => {
    if (!newSubmittalPinForm.product.trim() || !newSubmittalPinPos) return;
    const pin = {
      ...newSubmittalPinForm,
      id: "sp"+Date.now(),
      bpId: activeBlueprint?.id || "manual",
      projectId: projId,
      pos: newSubmittalPinPos,
      date: new Date().toLocaleDateString(),
      addedBy: session?.ownerName || "GC",
    };
    const updated = [...submittalPins, pin];
    setSubmittalPins(updated);
    const key = `grok_submittal_pins_${proj?.id||"x"}`;
    try{localStorage.setItem(key, JSON.stringify(updated));}catch(e){}
    setSelectedSubmittalPin(pin);
    setNewSubmittalPinPos(null);
    setNewSubmittalPinForm({product:"",trade:"",spec_section:"",status:"pending",notes:"",submittal_ref:""});
  };

  const saveFlag = () => {
    if (!newFlagData.title.trim() || !newFlagPos) return;
    const flag = {
      ...newFlagData,
      id: "f"+Date.now(),
      blueprintId: activeBlueprint?.id || "manual",
      projectId: projId,
      pos: newFlagPos,
      status: "open",
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),
      flaggedBy: session?.ownerName || "GC",
      comments: [],
    };
    const updated = [flag, ...flags];
    saveFlags(updated);
    setSelectedFlag(flag);
    setNewFlagPos(null);
    setNewFlagData({title:"",desc:"",priority:"medium",trade:"",photos:[]});

    // ARIA auto-drafts an RFI in "pending_gc_approval" status. Runs in the
    // background — UI stays responsive. On completion, mark the flag so we
    // don't draft a duplicate if the same flag is touched again.
    draftRFIFromFlag({flag, blueprint: activeBlueprint, proj: proj || selectedProject, session})
      .then((rfi) => {
        if (!rfi) return;
        const withDraft = flags.map(f => f.id === flag.id ? {...f, rfiDraftId: rfi.id} : f);
        // Re-build from the freshest list to avoid clobbering concurrent edits
        const cur = (() => { try { return JSON.parse(localStorage.getItem(flagsKey)||"[]"); } catch { return []; }})();
        const next = cur.map(f => f.id === flag.id ? {...f, rfiDraftId: rfi.id} : f);
        saveFlags(next.length ? next : withDraft);
      })
      .catch(()=>{});
  };

  const addComment = (flagId, text) => {
    if (!text.trim()) return;
    const updated = flags.map(f => f.id===flagId ? {...f, comments:[...f.comments, {id:Date.now(), text, author: session?.ownerName||"GC", date:new Date().toLocaleDateString()}]} : f);
    saveFlags(updated);
  };

  const resolveFlag = (flagId) => {
    const updated = flags.map(f => f.id===flagId ? {...f, status:"resolved", resolvedDate:new Date().toLocaleDateString()} : f);
    saveFlags(updated);
    if (selectedFlag?.id === flagId) setSelectedFlag(prev=>({...prev,status:"resolved"}));
  };

  // Pan handlers
  const onMouseDown = (e) => { if (e.button===1||e.altKey) { setPanStart({x:e.clientX-pan.x, y:e.clientY-pan.y}); setDragging(true); e.preventDefault(); }};
  const onMouseMove = (e) => { if (dragging && panStart) setPan({x:e.clientX-panStart.x, y:e.clientY-panStart.y}); };
  const onMouseUp = () => { setDragging(false); setPanStart(null); };

  if (!liveProjects.length && !proj) {
    // No projects — show standalone blueprint tool
    const earlyOuterStyle = isCommandCenter
      ? {position:"fixed",inset:0,zIndex:9999,background:T.bg0,padding:"14px 18px",overflowY:"auto"}
      : {padding:24};
    return (
      <div style={earlyOuterStyle}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div>
            <div style={{fontSize:isCommandCenter?16:20,fontWeight:800,color:T.text}}>Blueprint Review{isCommandCenter?" — Command Center":""}</div>
            <div style={{fontSize:12,color:T.muted,marginTop:3}}>Upload drawings · Click to flag issues · Tag photos</div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn ghost small onClick={()=>fileInputRef.current?.click()}><Ic d={ICONS.plus} size={12}/>Upload Blueprint</Btn>
            <button onClick={()=>setIsCommandCenter(v=>!v)}
              title={isCommandCenter?"Exit command center (ESC)":"Expand to full-screen command center"}
              style={{background:isCommandCenter?T.gold:"transparent",border:`1px solid ${isCommandCenter?T.gold:T.subtle}`,color:isCommandCenter?T.bg0:T.text,borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
              {isCommandCenter ? "✕ Exit Full Screen" : "⛶ Full Screen"}
            </button>
          </div>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*,application/pdf,.pdf" multiple style={{display:"none"}} onChange={e=>uploadBlueprint(e.target.files)}/>
        <input ref={photoInputRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>addPhoto(e.target.files[0])}/>
        {!activeBlueprint ? (
          <div style={{background:T.bg1,border:"2px dashed "+T.subtle,borderRadius:12,padding:"60px 32px",textAlign:"center"}}>
            <div style={{fontSize:40,marginBottom:12,opacity:0.4}}>📐</div>
            <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:8}}>Upload a Blueprint</div>
            <div style={{fontSize:12,color:T.muted,marginBottom:20}}>Upload any drawing — floor plan, elevation, section. Click anywhere to flag issues.</div>
            <button onClick={()=>fileInputRef.current?.click()}
              style={{fontSize:13,fontWeight:700,background:T.gold,color:T.bg0,border:"none",borderRadius:9,padding:"10px 24px",cursor:"pointer"}}>
              📎 Upload Drawing
            </button>
          </div>
        ) : (
          <div style={{display:"grid",gridTemplateColumns:"1fr 380px",gap:14,alignItems:"start"}}>
            {/* LEFT — blueprint canvas */}
            <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:12,overflow:"hidden"}}>
              <div style={{padding:"10px 14px",borderBottom:"1px solid "+T.subtle,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{fontSize:12,fontWeight:700,color:T.text}}>📐 {activeBlueprint.name}</div>
                <button onClick={()=>setActiveBlueprint(null)} style={{fontSize:11,color:T.muted,background:"transparent",border:"1px solid "+T.subtle,borderRadius:7,padding:"4px 10px",cursor:"pointer"}}>Clear</button>
              </div>
              {activeBlueprint.fileType === "pdf" ? (
                <PdfCanvas dataUrl={activeBlueprint.dataUrl} name={activeBlueprint.name}
                  currentPage={currentPage} onPageChange={setCurrentPage} onPagesLoaded={setPageCount}
                  zoom={zoom} onZoomChange={setZoom} captureRef={regionCaptureRef}
                  maxHeight={isCommandCenter?"calc(100vh - 140px)":"calc(100vh - 200px)"}/>
              ) : (
                <img src={activeBlueprint.dataUrl} alt={activeBlueprint.name}
                  style={{width:"100%",display:"block",maxHeight:isCommandCenter?"calc(100vh - 140px)":"calc(100vh - 200px)",objectFit:"contain",background:T.bg0}}/>
              )}
            </div>

            {/* RIGHT — AI Review side panel */}
            <div style={{position:"sticky",top:80}}>
              <Card style={{padding:0,overflow:"hidden"}}>
                <div style={{padding:"12px 14px",borderBottom:`1px solid ${T.subtle}`,display:"flex",justifyContent:"space-between",alignItems:"center",background:T.bg2}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:800,color:T.text,display:"flex",alignItems:"center",gap:6}}>
                      <span>🤖</span> ARIA Blueprint Review
                    </div>
                    <div style={{fontSize:9,color:T.muted,marginTop:2}}>Scope · Materials · Codes · Cost · Schedule</div>
                  </div>
                  <button onClick={()=>runAIReview(activeBlueprint)} disabled={aiReviewing||!activeBlueprint?.dataUrl}
                    style={{fontSize:10,fontWeight:700,background:aiReviewing?T.bg3:T.gold,color:aiReviewing?T.muted:T.bg0,border:"none",borderRadius:7,padding:"5px 11px",cursor:aiReviewing?"wait":"pointer"}}>
                    {aiReviewing?"Analyzing…":"🔄 Re-analyze"}
                  </button>
                </div>
                <div style={{padding:"12px 14px",maxHeight:"calc(100vh - 200px)",overflowY:"auto"}}>
                  {dupNoticeBox()}
                  {correctionsEditor()}
                  {userNotesBox()}
                  {aiError && !aiReview && reviewErrorBox()}
                  {!aiReview && aiReviewing && (
                    <div style={{padding:"30px 20px",textAlign:"center",color:T.muted,fontSize:12}}>
                      <Spin/> {tileProgress ? `Reading detail sheets${tileProgress.total ? ` — ${tileProgress.done}/${tileProgress.total} crops` : "…"}` : "ARIA is reviewing your drawing — scope, materials, code flags, cost, schedule…"}
                    </div>
                  )}
                  {!aiReview && !aiReviewing && !aiError && (
                    <div style={{padding:"30px 20px",textAlign:"center",color:T.muted,fontSize:12,lineHeight:1.7}}>
                      Click "🔄 Re-analyze" to get an ARIA review — scope of work by trade, material specs, code compliance flags, Bay Area cost estimate, and schedule.
                    </div>
                  )}
                  {/* Unified report — review + deep as ONE document. The deep
                      region (placeholders → filled in place, single ⚡ button)
                      lives inside ReviewSections via the `deep` bundle. */}
                  {aiReview && (
                    <div style={{fontSize:11.5,color:T.text,lineHeight:1.7,fontFamily:"inherit"}}>
                      <div style={{fontSize:10,color:T.muted,fontStyle:"italic",marginBottom:8,paddingBottom:8,borderBottom:`1px solid ${T.subtle}`}}>
                        AI-estimated from drawing review — verify quantities before bidding.
                      </div>
                      {analyzedStamp()}
                      {pageBadge(aiReviewPage)}
                      <ReviewSections text={aiReview} T={T} renderGaps={aiReviewing ? null : renderReviewGaps}
                        deep={{ text: stripLearnings(deepAnalysis), analyzing: deepAnalyzing, ready: (aiReview||"").length>0 && !aiReviewing, error: deepError, tooLarge: deepTooLarge, page: deepAnalysisPage, onRun: ()=>runDeepAnalysis(activeBlueprint), onExport: ()=>openDeepPrintView(stripLearnings(deepAnalysis), activeBlueprint, proj || selectedProject), pageBadge, analyzeThisPageBtn }}/>
                      {aiReviewing && <span style={{color:T.gold,fontWeight:700}}> ▍</span>}
                      {!aiReviewing && (aiIncomplete || aiError) && reviewRetryNotice()}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    );
  }


  // When a blueprint is open, the viewer becomes a full-height flex column so
  // the canvas can fill every remaining pixel below the compact header + thumb
  // strip. Command-center mode pins it over the entire window (incl. sidebar).
  // Empty / upload state keeps normal in-flow padding so it doesn't feel
  // claustrophobic.
  const outerStyle = isCommandCenter
    ? {position:"fixed",inset:0,zIndex:9999,background:T.bg0,padding:"10px 12px",display:"flex",flexDirection:"column",overflow:"hidden",gap:8}
    : activeBlueprint
      ? {height: isMobBp ? "calc(100dvh - 64px)" : "100vh", padding: isMobBp ? 6 : 10, display:"flex", flexDirection:"column", overflow:"hidden", gap:8}
      : {padding:24};

  return (
    <div style={outerStyle}>
      {/* Header — compact one-row when a blueprint is open so canvas gets the space. */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom: activeBlueprint ? 0 : 16, flexShrink:0, gap:8, flexWrap:"wrap"}}>
        <div style={{minWidth:0, flex:"0 1 auto"}}>
          <div style={{fontSize: activeBlueprint ? 14 : 20,fontWeight:800,color:T.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
            {activeBlueprint ? (activeBlueprint.name || "Drawing") : "Blueprint Review"}{isCommandCenter?" — Full Screen":""}
          </div>
          {!activeBlueprint && (
            <div style={{fontSize:12,color:T.muted,marginTop:3}}>Upload drawings · Click to flag issues · Tag photos · Subs can flag from field</div>
          )}
        </div>
        <div style={{display:"flex",gap:8}}>
          {activeBlueprint && (
            <>
              <Btn primary small onClick={()=>{setPlacingFlag(!placingFlag);setPlacingSubmittalPin(false);}} style={{background:placingFlag?T.amber:"",color:placingFlag?T.bg0:""}}>
                {placingFlag ? "📍 Click to Place Flag" : <><Ic d={ICONS.plus} size={12}/>Flag Issue</>}
              </Btn>
              <Btn ghost small onClick={()=>{setPlacingSubmittalPin(!placingSubmittalPin);setPlacingFlag(false);}}
                style={{background:placingSubmittalPin?T.teal+"20":"",color:placingSubmittalPin?T.teal:"",border:placingSubmittalPin?"1px solid "+T.teal:""}}>
                {placingSubmittalPin?"📦 Click to Place Pin":"📦 Pin Submittal"}
              </Btn>
            </>
          )}
          <Btn ghost small onClick={()=>fileInputRef.current?.click()}><Ic d={ICONS.plus} size={12}/>Upload Blueprint</Btn>
          <button onClick={()=>setIsCommandCenter(v=>!v)}
            title={isCommandCenter?"Exit command center (ESC)":"Expand to full-screen command center"}
            style={{background:isCommandCenter?T.gold:"transparent",border:`1px solid ${isCommandCenter?T.gold:T.subtle}`,color:isCommandCenter?T.bg0:T.text,borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
            {isCommandCenter ? "✕ Exit Full Screen" : "⛶ Full Screen"}
          </button>
        </div>
      </div>
      <input ref={fileInputRef} type="file" accept="image/*,application/pdf,.pdf" multiple style={{display:"none"}} onChange={e=>uploadBlueprint(e.target.files)}/>
      <input ref={photoInputRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>addPhoto(e.target.files[0])}/>

      {/* Code Compliance Checker */}
      <CodeComplianceChecker
        activeBlueprint={activeBlueprint}
        flags={flags}
        projectType={selectedProject?.type}
        buildingAge={selectedProject?.age}
        onAutoFlag={(newFlags) => {
          // Remove old auto-flags, add new ones — pins appear immediately on drawing
          const manual = flags.filter(f=>!f.autoGenerated);
          const merged = [...newFlags, ...manual];
          saveFlags(merged);
        }}
      />

      {/* Project selector */}
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom: activeBlueprint ? 0 : 14, flexShrink:0, flexWrap:"wrap"}}>
        <div style={{flex:1,maxWidth:280}}>
          <Select label="" value={selectedProject?.id} onChange={v=>{setSelectedProject(liveProjects.find(p=>p.id===parseInt(v)||p.id===v));setActiveBlueprint(null);}} options={liveProjects.map(p=>({value:p.id,label:(p.name||String(p.id)||"Project")}))}/>
        </div>
        {activeBlueprint && (
          <>
            <div style={{display:"flex",gap:4,background:T.bg2,borderRadius:9,padding:3}}>
              {["all","open","resolved"].map(m=>(
                <button key={m} onClick={()=>setViewMode(m)} style={{padding:"5px 12px",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",background:viewMode===m?T.gold:T.bg2,color:viewMode===m?T.bg0:T.muted,transition:"all 0.15s",textTransform:"capitalize"}}>{m}</button>
              ))}
            </div>
            <select value={filterTrade} onChange={e=>setFilterTrade(e.target.value)}
              style={{background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"6px 12px",color:filterTrade?T.text:T.muted,fontSize:12,fontFamily:"inherit"}}>
              {TRADES.map(t=><option key={t} value={t==="All Trades"?"":t}>{t}</option>)}
            </select>
          </>
        )}
      </div>

      {/* Stats row — compact pill format inline with header. Hidden in
          command center to free up vertical space. */}
      {activeBlueprint && !isCommandCenter && (
        <div style={{display:"flex",gap:8,flexShrink:0,flexWrap:"wrap"}}>
          {[
            {l:"Flags",v:activeFlags.length,c:T.muted},
            {l:"Open",v:openCount,c:T.amber},
            {l:"Critical",v:critCount,c:T.red},
            {l:"Resolved",v:activeFlags.filter(f=>f.status==="resolved").length,c:T.green},
          ].map(s=>(
            <div key={s.l} style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"4px 10px",display:"flex",gap:6,alignItems:"center"}}>
              <span style={{fontSize:12,fontWeight:800,color:s.c,fontFamily:"'Space Mono',monospace"}}>{s.v}</span>
              <span style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em"}}>{s.l}</span>
            </div>
          ))}
        </div>
      )}

      {/* HORIZONTAL THUMB STRIP — fixed 60px cards above the canvas */}
      {activeBlueprint && projBlueprints.length > 0 && (
        <div style={{display:"flex",alignItems:"center",gap:6,padding:"2px 2px",overflowX:"auto",flexShrink:0,maxHeight:74}}>
          {projBlueprints.map(bp => {
            const isActive = activeBlueprint?.id === bp.id;
            const flagsOnBp = flags.filter(f => (f.blueprintId === bp.id || f.bpId === bp.id)).length;
            return (
              <div key={bp.id}
                onClick={()=>{setActiveBlueprint(bp);setSelectedFlag(null);setCurrentPage(1);setZoom(1);setPan({x:0,y:0});}}
                onDoubleClick={()=>renameBlueprint(bp)}
                title={bp.name + (flagsOnBp?` · ${flagsOnBp} flag(s)`:"")}
                style={{background:isActive?T.goldBg:T.bg1,border:`1.5px solid ${isActive?T.gold:T.subtle}`,borderRadius:7,padding:4,cursor:"pointer",flexShrink:0,position:"relative",transition:"all 0.15s"}}>
                {bp.fileType === "pdf" ? (
                  <PdfThumbnail dataUrl={bp.dataUrl} width={80} height={50}/>
                ) : (
                  <div style={{width:80,height:50,background:T.bg0,borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
                    <img src={bp.dataUrl} alt={bp.name} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
                  </div>
                )}
                <div style={{fontSize:8,fontWeight:700,color:isActive?T.gold:T.text,marginTop:2,maxWidth:80,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {bp.name}
                </div>
                {flagsOnBp > 0 && (
                  <span style={{position:"absolute",top:-4,right:-4,background:T.amber,color:T.bg0,fontSize:9,fontWeight:800,borderRadius:10,padding:"1px 5px",minWidth:14,textAlign:"center"}}>{flagsOnBp}</span>
                )}
                <button onClick={(e)=>{e.stopPropagation();deleteBlueprint(bp);}}
                  title="Delete drawing"
                  style={{position:"absolute",top:-4,left:-4,background:T.bg0,border:`1px solid ${T.subtle}`,color:T.muted,borderRadius:"50%",width:16,height:16,cursor:"pointer",fontSize:11,lineHeight:1,padding:0,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
              </div>
            );
          })}
          <button onClick={()=>fileInputRef.current?.click()}
            title="Upload more drawings"
            style={{background:T.bg1,border:`2px dashed ${T.subtle}`,borderRadius:7,padding:4,cursor:"pointer",flexShrink:0,width:80,height:64,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",color:T.muted,fontSize:9,fontWeight:700,gap:2}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=T.gold;e.currentTarget.style.color=T.gold;}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor=T.subtle;e.currentTarget.style.color=T.muted;}}>
            <span style={{fontSize:14}}>+</span>
            <span>Upload</span>
          </button>
        </div>
      )}

      {/* MAIN ROW — fills remaining viewport. Canvas grows, right panel fixed 360. */}
      <div style={{display:"flex",gap:10,alignItems:"stretch",position:"relative", flex: activeBlueprint ? "1 1 auto" : undefined, minHeight: 0}}>

        {/* CENTER — blueprint canvas (flex-grows to fill, flex column so wrapper can be height:100%) */}
        <div style={{flex:1,minWidth:0,position:"relative", display: activeBlueprint ? "flex" : "block", flexDirection: "column", minHeight: 0}}>
          {!activeBlueprint ? (
            <div
              onClick={()=>fileInputRef.current?.click()}
              style={{border:`2px dashed ${T.subtle}`,borderRadius:14,padding:"80px 40px",textAlign:"center",cursor:"pointer",transition:"all 0.2s"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=T.gold;e.currentTarget.style.background=T.goldBg}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=T.subtle;e.currentTarget.style.background="transparent"}}>
              <div style={{fontSize:48,marginBottom:16,opacity:0.4}}>📐</div>
              <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:8}}>Upload a Blueprint or Drawing</div>
              <div style={{fontSize:13,color:T.muted,marginBottom:20,lineHeight:1.6}}>PNG, JPG, or screenshot of PDF plan. Upload floor plans, elevations, or site plans. Click anywhere to flag issues directly on the drawing.</div>
              <Btn primary>Upload Blueprint</Btn>
            </div>
          ) : (
            <div style={{position:"relative",background:T.bg0,borderRadius:12,overflow:"hidden",border:`1px solid ${T.subtle}`, flex:"1 1 auto", display:"flex", flexDirection:"column", minHeight:0}}>
              {/* Zoom + fit controls — floating top-right, CAD-viewer style */}
              <div style={{position:"absolute",top:12,right:12,zIndex:10,display:"flex",gap:4,background:"rgba(13,20,32,0.92)",borderRadius:8,padding:"4px",backdropFilter:"blur(6px)"}}>
                <button onClick={()=>setZoom(z=>Math.max(0.5,z-0.25))} title="Zoom out"
                  style={{width:30,height:30,borderRadius:6,border:"none",background:"transparent",color:"#f1f5f9",cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>−</button>
                <button onClick={()=>{setZoom(1);setPan({x:0,y:0});}} title="Fit to width (reset)"
                  style={{minWidth:50,height:30,padding:"0 9px",borderRadius:6,border:"none",background:"rgba(255,255,255,0.06)",color:"#f1f5f9",cursor:"pointer",fontSize:10,fontWeight:700}}>
                  {Math.round(zoom*100)}%
                </button>
                <button onClick={()=>setZoom(z=>Math.min(5,z+0.25))} title="Zoom in"
                  style={{width:30,height:30,borderRadius:6,border:"none",background:"transparent",color:"#f1f5f9",cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>+</button>
                <div style={{width:1,background:"rgba(255,255,255,0.1)",margin:"4px 2px"}}/>
                <button onClick={()=>{setZoom(1);setPan({x:0,y:0});}} title="Fit to window"
                  style={{height:30,padding:"0 10px",borderRadius:6,border:"none",background:"transparent",color:"#f1f5f9",cursor:"pointer",fontSize:11,fontWeight:700,display:"flex",alignItems:"center",gap:4}}>⛶ Fit</button>
              </div>

              {placingFlag && (
                <div style={{position:"absolute",top:12,left:12,zIndex:10,background:T.amber,color:T.bg0,borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,display:"flex",gap:8,alignItems:"center"}}>
                  <span style={{fontSize:16}}>📍</span> Click on drawing to place flag
                </div>
              )}

              {(() => {
                // Per-page flag/pin filtering. Image blueprints all live on page 1.
                const isPDF = activeBlueprint.fileType === "pdf";
                const flagsHere = filteredFlags.filter(f => !isPDF || (f.pos?.page || 1) === currentPage);
                const pinsHere = (submittalPins||[]).filter(p => p.bpId===activeBlueprint?.id && (!isPDF || (p.pos?.page || 1) === currentPage));
                const showNewPin = newFlagPos && (!isPDF || (newFlagPos.page || 1) === currentPage);

                // The overlay nodes — reused inside both PDF and image render paths.
                const overlay = (
                  <>
                    {flagsHere.map(flag=>{
                      const pc = PRIORITY_COLORS[flag.priority];
                      const isSelected = selectedFlag?.id === flag.id;
                      return (
                        <div key={flag.id}
                          onClick={(e)=>{e.stopPropagation();setSelectedFlag(flag);setPanelTab("flag");}}
                          style={{position:"absolute",left:`${flag.pos.x}%`,top:`${flag.pos.y}%`,transform:"translate(-50%,-100%)",cursor:"pointer",zIndex:5,pointerEvents:"auto",transition:"transform 0.15s"}}>
                          <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
                            <div style={{
                              width:isSelected?36:30,height:isSelected?36:30,
                              borderRadius:"50% 50% 50% 0",
                              background:flag.status==="resolved"?T.green:pc,
                              border:`3px solid ${isSelected?"white":pc}`,
                              display:"flex",alignItems:"center",justifyContent:"center",
                              fontSize:isSelected?14:11,
                              color:"white",fontWeight:800,
                              boxShadow:`0 4px 12px ${pc}60`,
                              transform:"rotate(-45deg)",
                              transition:"all 0.15s",
                            }}>
                              <span style={{transform:"rotate(45deg)"}}>{flag.status==="resolved"?"✓":PRIORITY_ICONS[flag.priority]}</span>
                            </div>
                          </div>
                          {isSelected && (
                            <div style={{position:"absolute",top:-50,left:"50%",transform:"translateX(-50%)",background:"rgba(13,20,32,0.95)",border:`1px solid ${pc}`,borderRadius:6,padding:"4px 10px",whiteSpace:"nowrap",fontSize:10,color:T.text,fontWeight:700,pointerEvents:"none"}}>
                              {flag.title}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {pinsHere.map(pin=>{
                      const statusColor = pin.status==="approved"?T.green:pin.status==="rejected"?T.red:T.amber;
                      const isSelected = selectedSubmittalPin?.id===pin.id;
                      return (
                        <div key={pin.id}
                          onClick={e=>{e.stopPropagation();setSelectedSubmittalPin(isSelected?null:pin);setPanelTab("flag");}}
                          style={{position:"absolute",left:`${pin.pos.x}%`,top:`${pin.pos.y}%`,transform:"translate(-50%,-100%)",cursor:"pointer",zIndex:6,pointerEvents:"auto"}}>
                          <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
                            <div style={{
                              width:isSelected?34:28,height:isSelected?34:28,
                              borderRadius:"50%",
                              background:statusColor,
                              border:`3px solid ${isSelected?"white":statusColor+"80"}`,
                              display:"flex",alignItems:"center",justifyContent:"center",
                              fontSize:isSelected?14:11,
                              boxShadow:`0 3px 10px ${statusColor}60`,
                              transition:"all 0.15s",
                            }}>
                              <span>📦</span>
                            </div>
                            {isSelected&&(
                              <div style={{position:"absolute",top:-90,left:"50%",transform:"translateX(-50%)",background:"rgba(13,20,32,0.97)",border:`1px solid ${statusColor}`,borderRadius:8,padding:"8px 12px",whiteSpace:"nowrap",fontSize:10,color:T.text,zIndex:10,minWidth:180,pointerEvents:"none"}}>
                                <div style={{fontWeight:800,color:statusColor,marginBottom:3}}>{pin.product}</div>
                                <div style={{color:T.muted,marginBottom:2}}>{pin.trade} · {pin.spec_section}</div>
                                <div style={{display:"flex",alignItems:"center",gap:5}}>
                                  <div style={{width:6,height:6,borderRadius:"50%",background:statusColor}}/>
                                  <span style={{color:statusColor,fontWeight:700,textTransform:"uppercase",fontSize:9}}>{pin.status}</span>
                                </div>
                                {pin.notes&&<div style={{color:T.muted,marginTop:2,maxWidth:200,whiteSpace:"normal"}}>{pin.notes.slice(0,80)}</div>}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {showNewPin && (
                      <div style={{position:"absolute",left:`${newFlagPos.x}%`,top:`${newFlagPos.y}%`,transform:"translate(-50%,-100%)",zIndex:6,pointerEvents:"none"}}>
                        <div style={{width:32,height:32,borderRadius:"50% 50% 50% 0",background:T.amber,border:"3px solid white",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,transform:"rotate(-45deg)",boxShadow:"0 4px 12px rgba(245,158,11,0.6)"}}>
                          <span style={{transform:"rotate(45deg)",color:"white",fontWeight:800}}>+</span>
                        </div>
                      </div>
                    )}
                  </>
                );

                if (isPDF) {
                  return (
                    <PdfCanvas dataUrl={activeBlueprint.dataUrl} name={activeBlueprint.name}
                      currentPage={currentPage} onPageChange={setCurrentPage} onPagesLoaded={setPageCount}
                      placing={placingFlag||placingSubmittalPin} onCanvasClick={handlePdfClick}
                      zoom={zoom} onZoomChange={setZoom} captureRef={regionCaptureRef}
                      maxHeight={"100%"}>
                      {overlay}
                    </PdfCanvas>
                  );
                }
                return (
                  <div
                    ref={containerRef}
                    onClick={handleCanvasClick}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseUp}
                    style={{position:"relative",width:"100%",flex:"1 1 auto",minHeight:0,overflow:"hidden",cursor:placingFlag?"crosshair":dragging?"grabbing":"default"}}>
                    <div style={{transform:`translate(${pan.x}px,${pan.y}px) scale(${zoom})`,transformOrigin:"0 0",transition:dragging?"none":"transform 0.1s",width:"100%",height:"100%",display:"flex",alignItems:"flex-start",justifyContent:"center"}}>
                      <img ref={imgRef} src={activeBlueprint.dataUrl} alt="Blueprint"
                        style={{maxWidth:"100%",maxHeight:"100%",width:"auto",height:"auto",display:"block",userSelect:"none",pointerEvents:"none",objectFit:"contain"}}
                        draggable={false}/>
                      {overlay}
                    </div>
                  </div>
                );
              })()}

              {/* Blueprint info bar */}
              <div style={{padding:"8px 14px",background:T.bg2,borderTop:`1px solid ${T.subtle}`,display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:10}}>
                <span style={{color:T.muted}}>📐 {activeBlueprint.name} · Uploaded {activeBlueprint.uploadDate}{activeBlueprint.fileType==="pdf"&&pageCount>1?` · ${pageCount} pages`:""}</span>
                <span style={{color:T.muted}}>{activeBlueprint.fileType==="pdf" ? "Click drawing to place flag · Use page nav above" : "Alt+drag to pan · Scroll to zoom"}</span>
              </div>
            </div>
          )}

          {/* Flag list below blueprint — hidden when a blueprint is open since
              the Flags tab on the right panel shows the same list. Avoids
              eating vertical space the canvas should own. */}
          {false && activeBlueprint && filteredFlags.length > 0 && (
            <div style={{marginTop:14}}>
              <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Issue Log — {filteredFlags.length} item{filteredFlags.length!==1?"s":""}</div>
              {filteredFlags.map(flag=>{
                const pc = PRIORITY_COLORS[flag.priority];
                const isSel = selectedFlag?.id === flag.id;
                return (
                  <div key={flag.id} onClick={()=>setSelectedFlag(flag)}
                    style={{background:T.bg1,border:`1px solid ${isSel?pc:T.subtle}`,borderLeft:`3px solid ${flag.status==="resolved"?T.green:pc}`,borderRadius:10,padding:"12px 14px",marginBottom:7,cursor:"pointer",transition:"all 0.15s"}}
                    onMouseEnter={e=>{if(!isSel)e.currentTarget.style.borderColor=pc}}
                    onMouseLeave={e=>{if(!isSel)e.currentTarget.style.borderColor=T.subtle}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:3}}>
                          <span style={{fontSize:12,fontWeight:800,color:T.text}}>{flag.title}</span>
                          {flag.photos?.length>0&&<span style={{fontSize:9,background:T.bg3,color:T.muted,borderRadius:20,padding:"1px 6px"}}>{flag.photos.length} photo{flag.photos.length!==1?"s":""}</span>}
                        </div>
                        <div style={{fontSize:11,color:T.muted,lineHeight:1.5,marginBottom:4}}>{flag.desc}</div>
                        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                          <span style={{fontSize:9,fontWeight:700,background:pc+"18",color:pc,borderRadius:20,padding:"2px 8px",border:`1px solid ${pc}30`}}>{flag.priority.toUpperCase()}</span>
                          {flag.trade&&<span style={{fontSize:9,background:T.bg3,color:T.muted,borderRadius:20,padding:"2px 8px"}}>{flag.trade}</span>}
                          <span style={{fontSize:9,fontWeight:700,background:flag.status==="resolved"?T.greenBg:T.amberBg,color:flag.status==="resolved"?T.green:T.amber,borderRadius:20,padding:"2px 8px"}}>{flag.status}</span>
                          {flag.comments?.length>0&&<span style={{fontSize:9,color:T.muted}}>💬 {flag.comments.length}</span>}
                        </div>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0,marginLeft:10}}>
                        <div style={{fontSize:10,color:T.muted,fontFamily:"monospace"}}>{flag.date}</div>
                        <div style={{fontSize:10,color:T.muted}}>{flag.flaggedBy}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Slim expand button when panel is collapsed */}
        {activeBlueprint && !panelOpen && (
          <button onClick={()=>setPanelOpen(true)} title="Show side panel"
            style={{position:"absolute",top:0,right:0,zIndex:5,width:28,background:T.bg2,border:`1px solid ${T.subtle}`,color:T.muted,borderRadius:"7px 0 0 7px",borderRight:"none",padding:"10px 4px",cursor:"pointer",fontSize:10,fontWeight:800,writingMode:"vertical-rl",letterSpacing:".08em"}}
            onMouseEnter={e=>{e.currentTarget.style.background=T.goldBg;e.currentTarget.style.color=T.gold;}}
            onMouseLeave={e=>{e.currentTarget.style.background=T.bg2;e.currentTarget.style.color=T.muted;}}>
            ◀ AI · DEEP · FLAGS
          </button>
        )}

        {/* Right panel — fixed 360 desktop, off-canvas on mobile (overlay). */}
        {activeBlueprint && panelOpen && (
        <div style={isMobBp
          ? {position:"absolute",top:0,right:0,bottom:0,width:"min(360px, 95vw)",zIndex:20,background:T.bg1,borderLeft:`1px solid ${T.subtle}`,padding:8,overflowY:"auto",boxShadow:"-4px 0 16px rgba(0,0,0,0.35)"}
          : {width:360,flexShrink:0,height:"100%",overflowY:"auto",display:"flex",flexDirection:"column"}}>
          {/* Tab strip — full labels + larger touch targets so it works on both mobile and desktop. */}
          <div style={{display:"flex",gap:4,marginBottom:10,background:T.bg2,borderRadius:10,padding:4,alignItems:"stretch"}}>
            <button onClick={()=>setPanelTab("ai")}
              style={{flex:1,minHeight:40,padding:"8px 6px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",border:"none",background:panelTab==="ai"?T.gold:"transparent",color:panelTab==="ai"?T.bg0:T.text,transition:"all 0.15s",display:"flex",alignItems:"center",justifyContent:"center",gap:5,whiteSpace:"nowrap"}}>
              🤖 <span>AI Review</span>{aiReviewing?" ·…":""}
            </button>
            <button onClick={()=>setPanelTab("flag")}
              style={{flex:1,minHeight:40,padding:"8px 6px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",border:"none",background:panelTab==="flag"?T.gold:"transparent",color:panelTab==="flag"?T.bg0:T.text,transition:"all 0.15s",display:"flex",alignItems:"center",justifyContent:"center",gap:5,whiteSpace:"nowrap"}}>
              🚩 <span>Flags</span>{activeFlags.length>0?` ${activeFlags.length}`:""}
            </button>
            <button onClick={()=>setPanelTab("notes")}
              style={{flex:1,minHeight:40,padding:"8px 6px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",border:"none",background:panelTab==="notes"?T.gold:"transparent",color:panelTab==="notes"?T.bg0:T.text,transition:"all 0.15s",display:"flex",alignItems:"center",justifyContent:"center",gap:5,whiteSpace:"nowrap"}}>
              📝 <span>Notes</span>
            </button>
            <button onClick={()=>setPanelOpen(false)} title="Hide panel"
              style={{flexShrink:0,background:"transparent",border:"none",color:T.muted,cursor:"pointer",fontSize:18,padding:"0 10px",borderRadius:7,lineHeight:1,minHeight:40}}>×</button>
          </div>

          {panelTab === "ai" && (
            <Card style={{padding:0,overflow:"hidden"}}>
              <div style={{padding:"12px 14px",borderBottom:`1px solid ${T.subtle}`,display:"flex",justifyContent:"space-between",alignItems:"center",background:T.bg2,gap:8}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{fontSize:12,fontWeight:800,color:T.text,display:"flex",alignItems:"center",gap:6}}>
                    <span>🤖</span> ARIA Blueprint Review
                  </div>
                  <div style={{fontSize:9,color:T.muted,marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>Summary · Materials · Cost · Schedule · Deep takeoff below</div>
                </div>
                <div style={{display:"flex",gap:5,flexShrink:0}}>
                  <button onClick={()=>runAIReview(activeBlueprint)} disabled={aiReviewing||!activeBlueprint?.dataUrl}
                    style={{fontSize:10,fontWeight:700,background:aiReviewing?T.bg3:T.gold,color:aiReviewing?T.muted:T.bg0,border:"none",borderRadius:7,padding:"5px 9px",cursor:aiReviewing?"wait":"pointer"}}>
                    {aiReviewing?"…":"🔄 Re-run"}
                  </button>
                </div>
              </div>
              <div style={{padding:"12px 14px",maxHeight:"calc(100vh - 220px)",overflowY:"auto"}}>
                {dupNoticeBox()}
                {correctionsEditor()}
                {userNotesBox()}
                {aiError && !aiReview && reviewErrorBox()}
                {!aiReview && aiReviewing && (
                  <div style={{padding:"30px 20px",textAlign:"center",color:T.muted,fontSize:12}}>
                    <Spin/> {tileProgress ? `Reading detail sheets${tileProgress.total ? ` — ${tileProgress.done}/${tileProgress.total} crops` : "…"}` : "ARIA is reviewing your drawing — scope, materials, code flags, cost, schedule…"}
                  </div>
                )}
                {!aiReview && !aiReviewing && !aiError && (
                  <div style={{padding:"30px 20px",textAlign:"center",color:T.muted,fontSize:12,lineHeight:1.7}}>
                    Upload a blueprint to get an automatic ARIA review — scope of work by trade, material specs, code compliance flags, Bay Area cost estimate, and schedule.
                  </div>
                )}
                {/* Create-Project affordance — offered once the initial review
                    has parseable learnings and we're not already in a project.
                    The deep-analysis ⚡ button now lives inside the unified report
                    below (see ReviewSections → DeepRegion), not up here. */}
                {(() => {
                  const aiReviewLen = (aiReview || "").length;
                  const learnings = aiReviewLen > 0 ? extractLearningsJSON(aiReview) : null;
                  const canCreate = activeBlueprint && learnings && !proj;
                  if (!canCreate) return null;
                  return (
                    <div style={{marginBottom:14}}>
                      <button onClick={()=>createProjectFromBlueprint(activeBlueprint, learnings, aiReview)}
                        style={{width:"100%",background:`linear-gradient(135deg,${T.gold},${T.gold2||T.gold})`,color:T.bg0,border:"none",borderRadius:9,padding:"11px 14px",fontSize:12,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,boxShadow:`0 3px 10px ${T.gold}40`}}>
                        ➕ Create Project from This Blueprint →
                      </button>
                    </div>
                  );
                })()}
                {aiReview && (
                  <div style={{fontSize:11.5,color:T.text,lineHeight:1.7,fontFamily:"inherit"}}>
                    <div style={{fontSize:10,color:T.muted,fontStyle:"italic",marginBottom:8,paddingBottom:8,borderBottom:`1px solid ${T.subtle}`}}>
                      AI-estimated from drawing review — verify quantities before bidding.
                    </div>
                    {analyzedStamp()}
                    {pageBadge(aiReviewPage)}
                    <ReviewSections text={aiReview} T={T} renderGaps={aiReviewing ? null : renderReviewGaps}
                      deep={{ text: stripLearnings(deepAnalysis), analyzing: deepAnalyzing, ready: (aiReview||"").length>0 && !aiReviewing, error: deepError, tooLarge: deepTooLarge, page: deepAnalysisPage, onRun: ()=>runDeepAnalysis(activeBlueprint), onExport: ()=>openDeepPrintView(stripLearnings(deepAnalysis), activeBlueprint, proj || selectedProject), pageBadge, analyzeThisPageBtn }}/>
                    {aiReviewing && <span style={{color:T.gold,fontWeight:700}}> ▍</span>}
                    {!aiReviewing && (aiIncomplete || aiError) && reviewRetryNotice()}
                  </div>
                )}
              </div>
            </Card>
          )}

          {panelTab === "flag" && !newFlagPos && !selectedFlag && !selectedSubmittalPin && (
            <Card>
              <div style={{padding:"20px",textAlign:"center",color:T.muted,fontSize:12,lineHeight:1.7}}>
                <div style={{fontSize:24,marginBottom:8,opacity:0.4}}>🚩</div>
                Click "Flag Issue" above, then click anywhere on the drawing to drop a flag.<br/>
                Or click an existing flag pin to view details.
              </div>
            </Card>
          )}

          {panelTab === "flag" && newFlagPos && (
          <div>
            <Card>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>New Flag</div>
              <div style={{fontSize:11,color:T.amber,marginBottom:14,display:"flex",alignItems:"center",gap:6}}>
                <span>📍</span> Placed at {newFlagPos.x.toFixed(1)}%, {newFlagPos.y.toFixed(1)}%
              </div>

              <Field label="Issue Title" value={newFlagData.title} onChange={v=>setNewFlagData(p=>({...p,title:v}))} placeholder="Drain conflicts with electrical conduit"/>

              <div style={{marginBottom:12}}>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Description</div>
                <textarea value={newFlagData.desc} onChange={e=>setNewFlagData(p=>({...p,desc:e.target.value}))}
                  placeholder="Describe the issue, location, what needs to happen before proceeding..."
                  rows={3} style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 11px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit"}}/>
              </div>

              <Select label="Priority" value={newFlagData.priority} onChange={v=>setNewFlagData(p=>({...p,priority:v}))}
                options={[{value:"low",label:"Low"},{value:"medium",label:"Medium"},{value:"high",label:"High"},{value:"critical",label:"Critical — Stop Work"}]}/>

              <Select label="Trade" value={newFlagData.trade} onChange={v=>setNewFlagData(p=>({...p,trade:v}))}
                options={TRADES.filter(t=>t!=="All Trades")}/>

              {/* ── ADA / Code Compliance Fields ── */}
              <div style={{marginBottom:12,background:T.blueBg,border:`1px solid ${T.blue}30`,borderRadius:8,padding:"12px 14px"}}>
                <div style={{fontSize:10,fontWeight:700,color:T.blue,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10}}>♿ ADA / Code Compliance</div>

                <Select label="Violation Type" value={newFlagData.adaType||""} onChange={v=>setNewFlagData(p=>({...p,adaType:v}))}
                  options={[
                    {value:"",label:"— Select type (optional) —"},
                    {value:"door_width",label:"Door Width"},
                    {value:"door_hardware",label:"Door Hardware / Lever"},
                    {value:"ramp_slope",label:"Ramp Slope"},
                    {value:"ramp_width",label:"Ramp Width"},
                    {value:"grab_bar",label:"Grab Bar Placement"},
                    {value:"turning_radius",label:"Turning Radius / Clear Floor Space"},
                    {value:"counter_height",label:"Counter / Work Surface Height"},
                    {value:"reach_range",label:"Reach Range"},
                    {value:"threshold",label:"Threshold / Level Change"},
                    {value:"parking",label:"Accessible Parking"},
                    {value:"signage",label:"Signage / Braille"},
                    {value:"restroom",label:"Restroom Clearance"},
                    {value:"elevator",label:"Elevator / Lift"},
                    {value:"path_of_travel",label:"Path of Travel"},
                    {value:"other_code",label:"Other Code Issue"},
                  ]}/>

                {newFlagData.adaType && (() => {
                  const ADA_STANDARDS = {
                    door_width:      {req:"32\" min clear (36\" preferred)", code:"ADA §404.2.3"},
                    door_hardware:   {req:"Lever or loop hardware, 34\"–48\" AFF", code:"ADA §404.2.7"},
                    ramp_slope:      {req:"Max 1:12 slope (8.33%)", code:"ADA §405.2"},
                    ramp_width:      {req:"36\" min clear width", code:"ADA §405.5"},
                    grab_bar:        {req:"33\"–36\" AFF, 1.5\" from wall, 42\" length min", code:"ADA §609"},
                    turning_radius:  {req:"60\" diameter clear floor space", code:"ADA §304.3"},
                    counter_height:  {req:"Max 34\" AFF for accessible", code:"ADA §902.3"},
                    reach_range:     {req:"15\"–48\" AFF forward, 9\"–54\" side reach", code:"ADA §308"},
                    threshold:       {req:"Max ½\" vertical, ¾\" beveled", code:"ADA §303"},
                    parking:         {req:"1 per 25 spaces, 8' stall + 5' aisle, 98\" vertical", code:"ADA §502"},
                    signage:         {req:"60\" AFF to centerline, Grade 2 Braille", code:"ADA §703"},
                    restroom:        {req:"60\" turning radius, 18\" side wall clearance", code:"ADA §604"},
                    elevator:        {req:"68\" deep × 80\" wide min cab", code:"ADA §407"},
                    path_of_travel:  {req:"36\" min width, no steps", code:"ADA §402"},
                  };
                  const std = ADA_STANDARDS[newFlagData.adaType];
                  if (!std) return null;
                  return (
                    <div style={{background:T.bg2,borderRadius:7,padding:"8px 10px",marginBottom:10,fontSize:11}}>
                      <div style={{color:T.blue,fontWeight:700,marginBottom:2}}>{std.code}</div>
                      <div style={{color:T.muted}}>Required: <strong style={{color:T.text}}>{std.req}</strong></div>
                    </div>
                  );
                })()}

                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Measured (Actual)</div>
                    <input value={newFlagData.measured||""} onChange={e=>setNewFlagData(p=>({...p,measured:e.target.value}))}
                      placeholder={`e.g. 28"`}
                      style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"7px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
                  </div>
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Required (Code)</div>
                    <input value={newFlagData.required||""} onChange={e=>setNewFlagData(p=>({...p,required:e.target.value}))}
                      placeholder={`e.g. 32" min`}
                      style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"7px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
                  </div>
                </div>

                <div style={{marginBottom:6}}>
                  <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Code Reference</div>
                  <input value={newFlagData.codeRef||""} onChange={e=>setNewFlagData(p=>({...p,codeRef:e.target.value}))}
                    placeholder="e.g. ADA §404.2.3 · CBC 11B-404.2.3"
                    style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"7px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
                </div>

                {/* Compliance indicator */}
                {newFlagData.measured && newFlagData.required && (
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",borderRadius:7,background:T.redBg,border:`1px solid ${T.red}30`,marginTop:4}}>
                    <span style={{fontSize:16}}>⚠️</span>
                    <div style={{fontSize:11,color:T.red,fontWeight:600}}>
                      Non-compliant — {newFlagData.measured} measured vs {newFlagData.required} required
                    </div>
                  </div>
                )}
              </div>

              {/* Photos */}
              <div style={{marginBottom:14}}>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Site Photos</div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>
                  {newFlagData.photos.map(ph=>(
                    <div key={ph.id} style={{position:"relative",width:64,height:64,borderRadius:8,overflow:"hidden",border:`1px solid ${T.subtle}`}}>
                      <img src={ph.url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                      <button onClick={()=>setNewFlagData(p=>({...p,photos:p.photos.filter(x=>x.id!==ph.id)}))}
                        style={{position:"absolute",top:2,right:2,width:16,height:16,borderRadius:"50%",background:"rgba(239,68,68,0.9)",border:"none",color:"white",fontSize:10,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>×</button>
                    </div>
                  ))}
                  <button onClick={()=>photoInputRef.current?.click()}
                    style={{width:64,height:64,borderRadius:8,border:`2px dashed ${T.subtle}`,background:T.bg2,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:3,color:T.muted,fontSize:9,transition:"all 0.15s"}}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=T.gold;e.currentTarget.style.color=T.gold}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=T.subtle;e.currentTarget.style.color=T.muted}}>
                    <span style={{fontSize:20}}>📷</span>
                    <span>Add Photo</span>
                  </button>
                </div>
                <div style={{fontSize:10,color:T.muted}}>Photos are linked to this flag location on the drawing</div>
              </div>

              <div style={{display:"flex",gap:8}}>
                <Btn ghost onClick={()=>{setNewFlagPos(null);setNewFlagData({title:"",desc:"",priority:"medium",trade:"",photos:[]});}} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
                <Btn primary disabled={!newFlagData.title.trim()} onClick={saveFlag} style={{flex:2,justifyContent:"center"}}>Save Flag</Btn>
              </div>
            </Card>
          </div>
        )}

        {panelTab === "flag" && selectedFlag && !newFlagPos && !newSubmittalPinPos && (
          <div>
            <Card>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>{selectedFlag.title}</div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    <span style={{fontSize:9,fontWeight:700,background:PRIORITY_COLORS[selectedFlag.priority]+"18",color:PRIORITY_COLORS[selectedFlag.priority],borderRadius:20,padding:"2px 8px",border:`1px solid ${PRIORITY_COLORS[selectedFlag.priority]}30`}}>{selectedFlag.priority?.toUpperCase()}</span>
                    {selectedFlag.trade&&<span style={{fontSize:9,background:T.bg3,color:T.muted,borderRadius:20,padding:"2px 8px"}}>{selectedFlag.trade}</span>}
                    <span style={{fontSize:9,fontWeight:700,background:selectedFlag.status==="resolved"?T.greenBg:T.amberBg,color:selectedFlag.status==="resolved"?T.green:T.amber,borderRadius:20,padding:"2px 8px"}}>{selectedFlag.status}</span>
                  </div>
                </div>
                <Btn small ghost onClick={()=>setSelectedFlag(null)}><Ic d={ICONS.x} size={11}/></Btn>
              </div>

              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontFamily:"monospace"}}>{selectedFlag.date} · {selectedFlag.flaggedBy}</div>
              {selectedFlag.desc&&<div style={{fontSize:12,color:T.text,lineHeight:1.6,background:T.bg2,borderRadius:7,padding:"10px 12px",marginBottom:12}}>{selectedFlag.desc}</div>}

              <div style={{fontSize:10,color:T.muted,marginBottom:4}}>📍 Location: {selectedFlag.pos?.x?.toFixed(1)}%, {selectedFlag.pos?.y?.toFixed(1)}%</div>

              {/* Photos */}
              {selectedFlag.photos?.length > 0 && (
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Site Photos ({selectedFlag.photos.length})</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {selectedFlag.photos.map(ph=>(
                      <div key={ph.id} onClick={()=>window.open(ph.url,"_blank")}
                        style={{width:72,height:72,borderRadius:8,overflow:"hidden",border:`1px solid ${T.subtle}`,cursor:"pointer",transition:"transform 0.15s"}}
                        onMouseEnter={e=>e.currentTarget.style.transform="scale(1.05)"}
                        onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}>
                        <img src={ph.url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Comments */}
              {selectedFlag.comments?.length > 0 && (
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Discussion</div>
                  {selectedFlag.comments.map(c=>(
                    <div key={c.id} style={{background:T.bg2,borderRadius:7,padding:"8px 11px",marginBottom:6}}>
                      <div style={{display:"flex",gap:8,marginBottom:3}}>
                        <span style={{fontSize:10,fontWeight:700,color:T.text}}>{c.author}</span>
                        <span style={{fontSize:9,color:T.muted,fontFamily:"monospace"}}>{c.date}</span>
                      </div>
                      <div style={{fontSize:12,color:T.muted,lineHeight:1.5}}>{c.text}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add comment */}
              <CommentInput flagId={selectedFlag.id} onAdd={addComment}/>

              {/* Actions */}
              <div style={{display:"flex",gap:7,marginTop:12}}>
                {selectedFlag.status !== "resolved" && (
                  <Btn small primary onClick={()=>resolveFlag(selectedFlag.id)} style={{flex:1,justifyContent:"center",background:T.green,color:"white"}}>
                    ✓ Mark Resolved
                  </Btn>
                )}
                <Btn small ghost onClick={()=>setSelectedFlag(null)} style={{flex:1,justifyContent:"center"}}>Close</Btn>
              </div>
            </Card>
          </div>
        )}

        {/* New Submittal Pin Form with AI Product Search */}
        {panelTab === "flag" && newSubmittalPinPos&&(
          <div>
            <Card>
              <div style={{fontSize:13,fontWeight:700,color:T.teal,marginBottom:2}}>📦 Pin Product / Submittal</div>
              <div style={{fontSize:11,color:T.muted,marginBottom:12}}>Search for qualifying products or enter manually</div>

              {/* AI Product Search */}
              <SubmittalProductSearch
                onSelect={product=>{
                  setNewSubmittalPinForm(p=>({...p,
                    product: product.name,
                    spec_section: product.spec_section||p.spec_section,
                    trade: product.trade||p.trade,
                    submittal_ref: p.submittal_ref,
                    notes: product.compliance_notes||p.notes,
                  }));
                }}
                session={session}
              />

              {/* Manual fields */}
              <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid "+T.subtle}}>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Or Enter Manually</div>
                {[
                  {k:"product",l:"Product / Material *",ph:"e.g. Kohler K-5172 Undermount Sink"},
                  {k:"spec_section",l:"Spec Section",ph:"e.g. 15430 — Plumbing Fixtures"},
                  {k:"trade",l:"Trade",ph:"e.g. Plumbing, Electrical, Tile"},
                  {k:"submittal_ref",l:"Submittal Reference #",ph:"e.g. S-003"},
                ].map(f=>(
                  <div key={f.k} style={{marginBottom:8}}>
                    <div style={{fontSize:10,color:T.muted,marginBottom:3}}>{f.l}</div>
                    <input value={newSubmittalPinForm[f.k]} onChange={e=>setNewSubmittalPinForm(p=>({...p,[f.k]:e.target.value}))}
                      placeholder={f.ph} style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
                  </div>
                ))}
                <div style={{marginBottom:8}}>
                  <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Approval Status</div>
                  <div style={{display:"flex",gap:6}}>
                    {[{v:"pending",l:"⏳ Pending",c:T.amber},{v:"approved",l:"✓ Approved",c:T.green},{v:"rejected",l:"✕ Rejected",c:T.red}].map(s=>(
                      <button key={s.v} onClick={()=>setNewSubmittalPinForm(p=>({...p,status:s.v}))}
                        style={{flex:1,padding:"6px",borderRadius:6,border:"1px solid "+(newSubmittalPinForm.status===s.v?s.c:T.subtle),background:newSubmittalPinForm.status===s.v?s.c+"20":"transparent",color:s.c,fontSize:10,fontWeight:700,cursor:"pointer"}}>
                        {s.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:10,color:T.muted,marginBottom:3}}>Notes / Approval Conditions</div>
                  <textarea value={newSubmittalPinForm.notes} onChange={e=>setNewSubmittalPinForm(p=>({...p,notes:e.target.value}))}
                    placeholder="Approval conditions, alternatives, architect notes..." rows={2}
                    style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 10px",color:T.text,fontSize:12,fontFamily:"inherit",resize:"none"}}/>
                </div>
              </div>
              <div style={{display:"flex",gap:8}}>
                <Btn ghost small onClick={()=>setNewSubmittalPinPos(null)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
                <Btn small onClick={saveSubmittalPin} disabled={!newSubmittalPinForm.product.trim()} style={{flex:2,justifyContent:"center",background:T.teal,color:"white"}}>Pin to Drawing</Btn>
              </div>
            </Card>
          </div>
        )}

        {/* Selected Submittal Pin Detail */}
        {panelTab === "flag" && selectedSubmittalPin&&!newSubmittalPinPos&&(
          <div>
            <Card>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>📦 {selectedSubmittalPin.product}</div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {selectedSubmittalPin.trade&&<span style={{fontSize:9,background:T.bg3,color:T.muted,borderRadius:20,padding:"2px 8px"}}>{selectedSubmittalPin.trade}</span>}
                    {selectedSubmittalPin.spec_section&&<span style={{fontSize:9,background:T.blueBg,color:T.blue,borderRadius:20,padding:"2px 8px"}}>{selectedSubmittalPin.spec_section}</span>}
                    <span style={{fontSize:9,fontWeight:700,background:selectedSubmittalPin.status==="approved"?T.greenBg:selectedSubmittalPin.status==="rejected"?T.redBg:T.amberBg,color:selectedSubmittalPin.status==="approved"?T.green:selectedSubmittalPin.status==="rejected"?T.red:T.amber,borderRadius:20,padding:"2px 8px"}}>
                      {selectedSubmittalPin.status?.toUpperCase()}
                    </span>
                  </div>
                </div>
                <button onClick={()=>setSelectedSubmittalPin(null)} style={{background:"none",border:"none",color:T.muted,fontSize:16,cursor:"pointer"}}>✕</button>
              </div>
              {selectedSubmittalPin.submittal_ref&&<div style={{fontSize:11,color:T.muted,marginBottom:6}}>Ref: {selectedSubmittalPin.submittal_ref}</div>}
              {selectedSubmittalPin.notes&&<div style={{fontSize:12,color:T.muted,lineHeight:1.6,marginBottom:12,background:T.bg2,borderRadius:7,padding:"8px 10px"}}>{selectedSubmittalPin.notes}</div>}
              <div style={{fontSize:10,color:T.muted,marginBottom:12}}>Pinned by {selectedSubmittalPin.addedBy} · {selectedSubmittalPin.date}</div>
              <div style={{display:"flex",gap:6}}>
                {["approved","pending","rejected"].map(s=>(
                  <button key={s} onClick={()=>{
                    const updated=submittalPins.map(p=>p.id===selectedSubmittalPin.id?{...p,status:s}:p);
                    setSubmittalPins(updated);
                    const key=`grok_submittal_pins_${proj?.id||"x"}`;
                    try{localStorage.setItem(key,JSON.stringify(updated));}catch(e){}
                    setSelectedSubmittalPin({...selectedSubmittalPin,status:s});
                  }} style={{flex:1,padding:"6px",borderRadius:6,border:"1px solid "+(selectedSubmittalPin.status===s?T.teal:T.subtle),background:selectedSubmittalPin.status===s?T.teal+"20":"transparent",color:selectedSubmittalPin.status===s?T.teal:T.muted,fontSize:10,fontWeight:700,cursor:"pointer",textTransform:"capitalize"}}>
                    {s}
                  </button>
                ))}
                <button onClick={()=>{
                  if(!window.confirm("Remove this submittal pin?")) return;
                  const updated=submittalPins.filter(p=>p.id!==selectedSubmittalPin.id);
                  setSubmittalPins(updated);
                  const key=`grok_submittal_pins_${proj?.id||"x"}`;
                  try{localStorage.setItem(key,JSON.stringify(updated));}catch(e){}
                  setSelectedSubmittalPin(null);
                }} style={{padding:"6px 8px",borderRadius:6,border:"1px solid "+T.subtle,background:"transparent",color:T.muted,fontSize:10,cursor:"pointer"}}>🗑</button>
              </div>
            </Card>
          </div>
        )}

        {/* Annotations tab — placeholder for text notes on the drawing (Item 5) */}
        {panelTab === "notes" && (
          <Card>
            <div style={{padding:"24px 18px",textAlign:"center",color:T.muted,fontSize:12,lineHeight:1.7}}>
              <div style={{fontSize:30,marginBottom:10,opacity:0.4}}>📝</div>
              <div style={{fontWeight:700,color:T.text,marginBottom:6}}>Annotations</div>
              <div>Add text notes directly on the drawing. Coming next — flag pins work today for similar use cases.</div>
            </div>
          </Card>
        )}
        </div>
        )}
      </div>
    </div>
  );
}

// ─── SUBMITTAL PRODUCT SEARCH ─────────────────────────────────────────────────
// AI-powered live search for qualifying products based on spec callouts from drawings
function SubmittalProductSearch({onSelect, session}) {
  const [query, setQuery]       = React.useState("");
  const [searching, setSearching] = React.useState(false);
  const [results, setResults]   = React.useState(null);
  const [products, setProducts] = React.useState([]); // parsed product list
  const [selected, setSelected] = React.useState(null);
  const inp = {width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:12,fontFamily:"inherit"};

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true); setResults(null); setProducts([]); setSelected(null);

    const system = `You are a construction product specification expert. When given a spec callout or requirement from construction drawings, find 3-5 qualifying products that meet those exact specifications. 

For each product return a JSON array with objects containing:
- name: full product name with manufacturer
- model: model number
- manufacturer: brand name  
- spec_section: CSI spec section (e.g. "22 40 00 — Plumbing Fixtures")
- trade: trade type (Plumbing, Electrical, HVAC, Tile, etc.)
- compliance: what standards/codes it meets (ADA, CalGreen, Title 24, etc.)
- compliance_notes: brief compliance summary
- approx_cost: approximate unit cost
- lead_time: typical lead time
- notes: key specs and why it qualifies

Return ONLY valid JSON array, no other text.`;

    const kb = (typeof loadKB === 'function') ? loadKB() : {};
    const projectType = kb.project_type || "";

    try {
      const res = await fetch("https://qnjlirkgumhzzcavqsxt.supabase.co/functions/v1/ai-proxy", {
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFuamxpcmtndW1oenpjYXZxc3h0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc3NTQxNjgsImV4cCI6MjA5MzMzMDE2OH0.fmwdMo6-ae5Jyx8b3n44FlOQUSSUX9drdt2UYJd7Mws"},
        body:JSON.stringify({
          model:"claude-sonnet-4-20250514",
          max_tokens:1500,
          system,
          messages:[{role:"user",content:`Find products matching this spec callout from construction drawings:\n\n"${query}"\n\nProject context: ${projectType||"California residential/multifamily"}\n\nReturn qualifying products as JSON array.`}]
        })
      });
      const data = await res.json();
      const text = (data.content||[]).map(c=>c.text||"").join("");
      const clean = text.replace(/\x60\x60\x60json|\x60\x60\x60/g,"").trim();
      const parsed = JSON.parse(clean);
      setProducts(parsed);
      setResults(text);
    } catch(e) {
      setResults("Error searching products. Try again or enter manually below.");
    }
    setSearching(false);
  };

  return (
    <div>
      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>
        🔍 Search Qualifying Products
      </div>
      <div style={{display:"flex",gap:6,marginBottom:8}}>
        <input value={query} onChange={e=>setQuery(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&search()}
          placeholder='Paste spec callout e.g. "ADA lavatory faucet, 1.2 GPM max, chrome, ASME A112.18.1"'
          style={{...inp,flex:1}}/>
        <button onClick={search} disabled={searching||!query.trim()}
          style={{padding:"9px 14px",background:T.teal,color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:searching||!query.trim()?"not-allowed":"pointer",whiteSpace:"nowrap",opacity:searching||!query.trim()?0.6:1}}>
          {searching?"⏳ Searching...":"🔍 Search"}
        </button>
      </div>

      {/* Quick spec chips */}
      <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:8}}>
        {["ADA faucet 1.2 GPM chrome","Low-flow toilet 1.28 GPF","GFCI 20A tamper-resistant","MERV-13 air filter","ADA grab bar 1.5\" SS","CalGreen low-VOC paint"].map(chip=>(
          <button key={chip} onClick={()=>{setQuery(chip);}}
            style={{fontSize:9,padding:"3px 8px",borderRadius:10,border:"1px solid "+T.subtle,background:T.bg2,color:T.muted,cursor:"pointer"}}>
            {chip}
          </button>
        ))}
      </div>

      {/* Results */}
      {searching&&(
        <div style={{padding:"20px",textAlign:"center",color:T.muted,fontSize:12}}>
          <div style={{fontSize:20,marginBottom:6}}>🔍</div>
          Searching products that meet spec requirements...
        </div>
      )}

      {products.length>0&&(
        <div>
          <div style={{fontSize:10,fontWeight:700,color:T.teal,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>
            {products.length} Qualifying Products Found
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:320,overflowY:"auto"}}>
            {products.map((p,i)=>(
              <div key={i}
                onClick={()=>setSelected(selected===i?null:i)}
                style={{background:selected===i?T.teal+"18":T.bg2,border:"1px solid "+(selected===i?T.teal:T.subtle),borderRadius:8,padding:"10px 12px",cursor:"pointer",transition:"all 0.15s"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:700,color:T.text}}>{p.name}</div>
                    <div style={{fontSize:10,color:T.muted,marginTop:1}}>{p.manufacturer} · {p.model}</div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0,marginLeft:8}}>
                    {p.approx_cost&&<div style={{fontSize:11,fontWeight:700,color:T.gold}}>{p.approx_cost}</div>}
                    {p.lead_time&&<div style={{fontSize:9,color:T.muted}}>{p.lead_time}</div>}
                  </div>
                </div>
                {/* Compliance badges */}
                <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:selected===i?6:0}}>
                  {(p.compliance||"").split(",").map(c=>c.trim()).filter(Boolean).map(c=>(
                    <span key={c} style={{fontSize:8,fontWeight:700,background:T.greenBg,color:T.green,borderRadius:10,padding:"1px 6px"}}>{c}</span>
                  ))}
                  <span style={{fontSize:8,background:T.blueBg,color:T.blue,borderRadius:10,padding:"1px 6px"}}>{p.spec_section}</span>
                  <span style={{fontSize:8,background:T.bg3,color:T.muted,borderRadius:10,padding:"1px 6px"}}>{p.trade}</span>
                </div>
                {/* Expanded detail */}
                {selected===i&&(
                  <div>
                    <div style={{fontSize:11,color:T.muted,lineHeight:1.6,marginBottom:8}}>{p.compliance_notes||p.notes}</div>
                    <button onClick={e=>{e.stopPropagation();onSelect(p);setSelected(null);setQuery("");setProducts([]);}}
                      style={{width:"100%",padding:"8px",background:T.teal,color:"white",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                      ✓ Use This Product
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {results&&!searching&&products.length===0&&(
        <div style={{fontSize:11,color:T.red,padding:"8px 10px",background:T.redBg,borderRadius:6}}>{results}</div>
      )}
    </div>
  );
}

// Tiny comment input component
function CommentInput({flagId, onAdd}) {
  const [text, setText] = useState("");
  return (
    <div style={{display:"flex",gap:7,marginTop:4}}>
      <input value={text} onChange={e=>setText(e.target.value)} placeholder="Add a comment..."
        onKeyDown={e=>{if(e.key==="Enter"&&text.trim()){onAdd(flagId,text);setText("");}}}
        style={{flex:1,background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"8px 11px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
      <Btn small primary disabled={!text.trim()} onClick={()=>{if(text.trim()){onAdd(flagId,text);setText("");}}}>Post</Btn>
    </div>
  );
}



// ─── WEBSITE EMBED MANAGER ────────────────────────────────────────────────────
// ─── SUBSCRIBER MANAGEMENT ───────────────────────────────────────────────────
function SubscriberManagement({session}) {
  const [companies, setCompanies] = React.useState(()=>getCompanies().filter(c=>c.email!=="jhcftgroup@gmail.com"&&c.email!=="jehoon@grokconstructiongroupinc.com"));
  const [filter, setFilter] = React.useState("all");
  const [search, setSearch] = React.useState("");
  const [selected, setSelected] = React.useState(null);
  const [actionMsg, setActionMsg] = React.useState("");

  const refresh = () => setCompanies(getCompanies().filter(c=>c.email!=="jhcftgroup@gmail.com"&&c.email!=="jehoon@grokconstructiongroupinc.com"));

  const filtered = companies.filter(c=>{
    if (filter==="unverified") return !c.emailVerified;
    if (filter==="unpaid") return !c.emailVerified||(!c.trialEnds||new Date(c.trialEnds)<new Date());
    if (filter==="active") return c.emailVerified&&new Date(c.trialEnds||0)>=new Date();
    if (search) return c.email?.includes(search)||c.name?.toLowerCase().includes(search.toLowerCase());
    return true;
  });

  const verifyAccount = (co) => {
    const all = getCompanies();
    saveCompanies(all.map(c=>c.id===co.id?{...c,emailVerified:true}:c));
    refresh(); setActionMsg("✓ Email verified for "+co.email);
    setTimeout(()=>setActionMsg(""),3000);
  };

  const extendTrial = (co, days) => {
    const all = getCompanies();
    const newDate = new Date(Date.now()+days*864e5).toISOString().slice(0,10);
    saveCompanies(all.map(c=>c.id===co.id?{...c,trialEnds:newDate}:c));
    refresh(); setActionMsg(`✓ Trial extended ${days} days for ${co.email}`);
    setTimeout(()=>setActionMsg(""),3000);
  };

  const deleteAccount = (co) => {
    if(!window.confirm(`Delete account for ${co.email}? This cannot be undone.`)) return;
    saveCompanies(getCompanies().filter(c=>c.id!==co.id));
    refresh(); setSelected(null); setActionMsg("✓ Account deleted: "+co.email);
    setTimeout(()=>setActionMsg(""),3000);
  };

  const sendEmail = async (co, subject, message) => {
    try {
      await fetch("https://qnjlirkgumhzzcavqsxt.supabase.co/functions/v1/send-email",{
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFuamxpcmtndW1oenpjYXZxc3h0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc3NTQxNjgsImV4cCI6MjA5MzMzMDE2OH0.fmwdMo6-ae5Jyx8b3n44FlOQUSSUX9drdt2UYJd7Mws"},
        body:JSON.stringify({to:co.email,subject,html:`<div style="font-family:Arial,sans-serif;padding:32px;max-width:600px">${message}</div>`})
      });
      setActionMsg("✓ Email sent to "+co.email);
    } catch(e) { setActionMsg("✗ Email failed"); }
    setTimeout(()=>setActionMsg(""),3000);
  };

  const now = new Date();
  const stats = {
    total: companies.length,
    verified: companies.filter(c=>c.emailVerified).length,
    unverified: companies.filter(c=>!c.emailVerified).length,
    active: companies.filter(c=>c.emailVerified&&new Date(c.trialEnds||0)>=now).length,
    expired: companies.filter(c=>c.emailVerified&&new Date(c.trialEnds||0)<now).length,
  };

  return (
    <div style={{padding:24,maxWidth:900}}>
      <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:4}}>👥 Subscriber Management</div>
      <div style={{fontSize:11,color:T.muted,marginBottom:20}}>Manage all registered accounts — verify emails, extend trials, send messages</div>

      {actionMsg&&<div style={{background:T.greenBg,border:"1px solid "+T.green+"40",borderRadius:8,padding:"9px 14px",fontSize:12,color:T.green,marginBottom:14,fontWeight:600}}>{actionMsg}</div>}

      {/* Stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10,marginBottom:16}}>
        {[
          {l:"Total",v:stats.total,c:T.text},
          {l:"Verified",v:stats.verified,c:T.green},
          {l:"Unverified",v:stats.unverified,c:T.amber},
          {l:"Active",v:stats.active,c:T.blue},
          {l:"Expired",v:stats.expired,c:T.red},
        ].map(s=>(
          <div key={s.l} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:8,padding:"10px",textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:800,color:s.c}}>{s.v}</div>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em"}}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Filters + search */}
      <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
        {["all","unverified","unpaid","active"].map(f=>(
          <button key={f} onClick={()=>setFilter(f)}
            style={{padding:"6px 12px",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",border:"1px solid "+(filter===f?T.gold:T.subtle),background:filter===f?T.goldBg:T.bg1,color:filter===f?T.gold:T.muted,textTransform:"capitalize"}}>
            {f==="all"?"All":f==="unverified"?"⚠ Unverified":f==="unpaid"?"🔒 No Access":"✓ Active"}
          </button>
        ))}
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search email or company..."
          style={{flex:1,minWidth:200,background:T.bg1,border:"1px solid "+T.subtle,borderRadius:7,padding:"6px 12px",color:T.text,fontSize:11,fontFamily:"inherit"}}/>
      </div>

      {/* Table */}
      <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,overflow:"hidden",marginBottom:selected?14:0}}>
        {filtered.length===0&&<div style={{padding:"24px",textAlign:"center",color:T.muted,fontSize:12}}>No accounts match this filter</div>}
        {filtered.map((co,i)=>{
          const isVerified = !!co.emailVerified;
          const trialEnd = co.trialEnds ? new Date(co.trialEnds) : null;
          const isActive = isVerified && trialEnd && trialEnd >= now;
          const isExpired = isVerified && (!trialEnd || trialEnd < now);
          return (
            <div key={co.id} onClick={()=>setSelected(selected?.id===co.id?null:co)}
              style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderBottom:i<filtered.length-1?"1px solid "+T.subtle:"none",cursor:"pointer",background:selected?.id===co.id?T.bg2:"transparent"}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:600,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{co.name||co.email}</div>
                <div style={{fontSize:10,color:T.muted}}>{co.email} · {co.plan||"starter"} · Joined {co.createdAt||"unknown"}</div>
              </div>
              <div style={{display:"flex",gap:6,flexShrink:0}}>
                {!isVerified&&<span style={{fontSize:9,fontWeight:700,background:T.amberBg,color:T.amber,borderRadius:10,padding:"2px 7px"}}>UNVERIFIED</span>}
                {isActive&&<span style={{fontSize:9,fontWeight:700,background:T.greenBg,color:T.green,borderRadius:10,padding:"2px 7px"}}>ACTIVE</span>}
                {isExpired&&<span style={{fontSize:9,fontWeight:700,background:T.redBg,color:T.red,borderRadius:10,padding:"2px 7px"}}>EXPIRED</span>}
                <span style={{fontSize:9,color:T.muted}}>{trialEnd?trialEnd.toLocaleDateString():"-"}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail panel */}
      {selected&&(
        <div style={{background:T.bg1,border:"1px solid "+T.gold,borderRadius:10,padding:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:T.text}}>{selected.name||selected.email}</div>
              <div style={{fontSize:11,color:T.muted,marginTop:2}}>{selected.email} · Plan: {selected.plan||"starter"} · CSLB: {selected.cslb||"N/A"}</div>
              <div style={{fontSize:10,color:T.muted,marginTop:1}}>Joined: {selected.createdAt||"unknown"} · Trial ends: {selected.trialEnds||"N/A"} · Email verified: {selected.emailVerified?"✓":"✗"}</div>
            </div>
            <button onClick={()=>setSelected(null)} style={{background:"none",border:"none",color:T.muted,fontSize:16,cursor:"pointer"}}>✕</button>
          </div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {!selected.emailVerified&&(
              <button onClick={()=>verifyAccount(selected)} style={{fontSize:11,fontWeight:700,background:T.green,color:"white",border:"none",borderRadius:7,padding:"6px 12px",cursor:"pointer"}}>✓ Verify Email</button>
            )}
            <button onClick={()=>extendTrial(selected,7)} style={{fontSize:11,fontWeight:700,background:T.blue,color:"white",border:"none",borderRadius:7,padding:"6px 12px",cursor:"pointer"}}>+7 Days</button>
            <button onClick={()=>extendTrial(selected,15)} style={{fontSize:11,fontWeight:700,background:T.blue,color:"white",border:"none",borderRadius:7,padding:"6px 12px",cursor:"pointer"}}>+15 Days</button>
            <button onClick={()=>extendTrial(selected,30)} style={{fontSize:11,fontWeight:700,background:T.blue,color:"white",border:"none",borderRadius:7,padding:"6px 12px",cursor:"pointer"}}>+30 Days</button>
            <button onClick={()=>{
              const msg = window.prompt("Message to send:");
              if(msg) sendEmail(selected,"Message from Grok Field Ops",msg);
            }} style={{fontSize:11,fontWeight:700,background:T.purple,color:"white",border:"none",borderRadius:7,padding:"6px 12px",cursor:"pointer"}}>✉ Send Email</button>
            <button onClick={()=>deleteAccount(selected)} style={{fontSize:11,fontWeight:700,background:"transparent",color:T.red,border:"1px solid "+T.red+"40",borderRadius:7,padding:"6px 12px",cursor:"pointer"}}>Delete Account</button>
          </div>
        </div>
      )}
    </div>
  );
}

function EmbedManagerView({session}) {
  const [color, setColor] = useState(session?.primaryColor || "#D4A843");
  const [position, setPosition] = useState("bottom-right");
  const [showFloat, setShowFloat] = useState(true);
  const [companyName, setCompanyName] = useState(session?.companyName || "Your Company");
  const [copied, setCopied] = useState("");

  const companyId = session?.companyId || window._grokCompanyId || "local";
  const webhookPlaceholder = "https://hooks.zapier.com/your-webhook";

  const userEmail = session?.email || "your@email.com";
  const scriptTag = `<!-- Grok Field Ops Estimate Widget -->
<script src="https://grokfieldops.com/embed.js"
        data-company="${companyId}"
        data-company-name="${companyName}"
        data-color="${color}"
        data-float="${showFloat}"
        data-position="${position}"
        data-email="${userEmail}"
        async></script>`;

  const btnTag = `<button onclick="GrokEstimate.open()">Get Free Estimate</button>`;

  const copy = (text, id) => {
    navigator.clipboard.writeText(text).then(()=>{setCopied(id);setTimeout(()=>setCopied(""),2000);});
  };

  const CopyBtn = ({text, id}) => (
    <button onClick={()=>copy(text,id)}
      style={{background:copied===id?T.green:T.bg3,border:`1px solid ${copied===id?T.green:T.subtle}`,borderRadius:7,padding:"5px 14px",fontSize:11,color:copied===id?"white":T.muted,cursor:"pointer",transition:"all 0.2s",fontWeight:600}}>
      {copied===id?"✓ Copied":"Copy"}
    </button>
  );

  const CodeBlock = ({code}) => (
    <div style={{background:T.bg0,borderRadius:9,padding:"14px 16px",fontFamily:"'Space Mono',monospace",fontSize:11,color:"#a6e3a1",lineHeight:1.8,overflowX:"auto",whiteSpace:"pre"}}>
      {code}
    </div>
  );

  return (
    <div style={{padding:24,maxWidth:800}}>
      <div style={{marginBottom:20}}>
        <div style={{fontSize:20,fontWeight:800,color:T.text}}>Website Embed Widget</div>
        <div style={{fontSize:12,color:T.muted,marginTop:3}}>Add an estimate request form to any website — submissions land directly in your Estimate Requests dashboard</div>
      </div>

      {/* How it works */}
      <div style={{display:"flex",gap:0,background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:10,overflow:"hidden",marginBottom:20}}>
        {[
          {n:"1",l:"Customer visits your site",sub:"Sees floating button or your CTA"},
          {n:"2",l:"Fills 3-step form",sub:"Project type, scope, contact"},
          {n:"3",l:"You get notified",sub:"Email + dashboard alert"},
          {n:"4",l:"AI generates estimate",sub:"You review and approve"},
        ].map((s,i)=>(
          <div key={i} style={{flex:1,padding:"12px 14px",borderRight:i<3?`1px solid ${T.subtle}`:"none"}}>
            <div style={{width:24,height:24,borderRadius:"50%",background:T.goldBg,border:`1px solid ${T.goldDim}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:T.gold,marginBottom:7}}>{s.n}</div>
            <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:2}}>{s.l}</div>
            <div style={{fontSize:10,color:T.muted}}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Customize */}
      <Card style={{marginBottom:14}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14}}>Customize Your Widget</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Company Name (shown in widget)</div>
            <input value={companyName} onChange={e=>setCompanyName(e.target.value)}
              style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
          </div>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Accent Color</div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <input type="color" value={color} onChange={e=>setColor(e.target.value)}
                style={{width:40,height:36,borderRadius:6,border:`1px solid ${T.subtle}`,cursor:"pointer",background:T.bg2,padding:2}}/>
              <input value={color} onChange={e=>setColor(e.target.value)}
                style={{flex:1,background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"monospace"}}/>
              <div style={{width:32,height:32,borderRadius:8,background:color}}/>
            </div>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Float Button Position</div>
            <div style={{display:"flex",gap:6}}>
              {["bottom-right","bottom-left"].map(p=>(
                <button key={p} onClick={()=>setPosition(p)}
                  style={{flex:1,padding:"7px 12px",borderRadius:8,border:`1px solid ${position===p?T.gold:T.subtle}`,background:position===p?T.goldBg:T.bg2,color:position===p?T.gold:T.muted,fontSize:11,fontWeight:600,cursor:"pointer",transition:"all 0.15s"}}>
                  {p==="bottom-right"?"↘ Bottom Right":"↙ Bottom Left"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Float Button</div>
            <div onClick={()=>setShowFloat(p=>!p)}
              style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderRadius:8,border:`1px solid ${T.subtle}`,cursor:"pointer",background:T.bg2}}>
              <div style={{width:36,height:20,borderRadius:10,background:showFloat?T.green:T.bg3,transition:"background 0.2s",position:"relative",flexShrink:0}}>
                <div style={{position:"absolute",top:2,left:showFloat?18:2,width:16,height:16,borderRadius:"50%",background:"white",transition:"left 0.2s"}}/>
              </div>
              <span style={{fontSize:12,color:T.text}}>{showFloat?"Floating button enabled":"No floating button — use your own trigger"}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Install code */}
      <Card style={{marginBottom:14}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Your Install Code</div>
        <div style={{display:"flex",gap:8,alignItems:"center",background:T.greenBg,border:`1px solid ${T.green}30`,borderRadius:8,padding:"8px 14px",marginBottom:12}}>
          <span style={{color:T.green}}>✓</span>
          <div style={{fontSize:12,color:T.text,lineHeight:1.5}}>Pre-configured for <strong>{session?.companyName||"Your Company"}</strong> · Submissions routed to <strong style={{color:T.teal}}>{userEmail}</strong></div>
        </div>
        <div style={{background:T.bg2,borderRadius:9,padding:"12px 14px",marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>How it works for your customers</div>
          {[
            {n:"1",c:T.blue,l:"Customer visits your website",sub:"Sees floating button or your CTA button"},
            {n:"2",c:T.teal,l:"Fills 3-step estimate form",sub:"Project type → scope → contact info"},
            {n:"3",c:T.gold,l:"Submission emails you at "+userEmail,sub:"Full scope + contact formatted and sent to your inbox"},
            {n:"4",c:T.purple,l:"Appears in your Estimate Requests dashboard",sub:"Review scope, generate AI estimate, approve and send"},
          ].map((s,i)=>(
            <div key={i} style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:i<3?8:0}}>
              <div style={{width:20,height:20,borderRadius:"50%",background:s.c+"20",border:`1px solid ${s.c}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:800,color:s.c,flexShrink:0,marginTop:1}}>{s.n}</div>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:T.text}}>{s.l}</div>
                <div style={{fontSize:10,color:T.muted}}>{s.sub}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{fontSize:12,color:T.muted,marginBottom:12}}>Paste before <code style={{background:T.bg3,padding:"1px 5px",borderRadius:4,fontFamily:"monospace"}}>&lt;/body&gt;</code> on every page:</div>
        <div style={{display:"flex",justifyContent:"flex-end",marginBottom:6}}>
          <CopyBtn text={scriptTag} id="script"/>
        </div>
        <CodeBlock code={scriptTag}/>
      </Card>

      {/* Button trigger */}
      <Card style={{marginBottom:14}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Trigger from Your Own Button</div>
        <div style={{fontSize:12,color:T.muted,marginBottom:12}}>Add <code style={{background:T.bg3,padding:"1px 5px",borderRadius:4,fontFamily:"monospace"}}>onclick="GrokEstimate.open()"</code> to any element:</div>
        <div style={{display:"flex",justifyContent:"flex-end",marginBottom:6}}>
          <CopyBtn text={btnTag} id="btn"/>
        </div>
        <CodeBlock code={btnTag}/>
      </Card>

      {/* What it looks like */}
      <Card>
        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>What Customers See</div>
        <div style={{fontSize:12,color:T.muted,marginBottom:12}}>A clean 3-step form — project type → scope description → contact. Mobile friendly. Validates each step.</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
          {[
            {n:"1",l:"Project Info",items:["Project type","Address/area","Building age","Budget range"]},
            {n:"2",l:"Scope",items:["Detailed description","Special requirements","Any known conditions"]},
            {n:"3",l:"Contact",items:["Name + company","Email + phone","Best time to reach"]},
          ].map(step=>(
            <div key={step.n} style={{background:T.bg2,borderRadius:9,padding:"12px 14px"}}>
              <div style={{width:22,height:22,borderRadius:"50%",background:color+"20",border:`1px solid ${color}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color,marginBottom:8}}>{step.n}</div>
              <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:6}}>{step.l}</div>
              {step.items.map(item=>(
                <div key={item} style={{fontSize:10,color:T.muted,display:"flex",gap:5,marginBottom:3,alignItems:"center"}}>
                  <span style={{color,flexShrink:0}}>→</span>{item}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div style={{background:T.amberBg,borderLeft:`3px solid ${T.amber}`,borderRadius:8,padding:"10px 14px",marginTop:14,fontSize:12,color:T.muted,lineHeight:1.6}}>
          <strong style={{color:T.amber}}>How routing works:</strong> The <code style={{background:T.bg3,padding:"1px 5px",borderRadius:3,fontFamily:"monospace",fontSize:10}}>data-email</code> attribute in your script tag is set to <strong style={{color:T.teal}}>{userEmail}</strong>. When a customer submits, the widget emails that address and the request appears in your Estimate Requests dashboard. Every GC gets their own isolated routing — your customers never go to another GC's inbox.
        </div>
      </Card>
    </div>
  );
}

const PRICING_RANGES = [
  {type:"Small Service / Maintenance",   markup:[25,38], gcfee:[5,8],   contingency:[5,8],   display:"One lump-sum number",               color:"#22c55e", note:"High labor cost market — Bay Area rates"},
  {type:"Residential Remodel",           markup:[22,30], gcfee:[6,9],   contingency:[8,12],  display:"Light breakdown",                   color:"#60a5fa", note:"SF Bay Area material costs 20-30% above national"},
  {type:"Commercial TI — Peninsula",     markup:[20,28], gcfee:[7,11],  contingency:[7,10],  display:"Structured if $250K+",              color:"#f59e0b", note:"Burlingame/San Mateo market — quality buyers"},
  {type:"SRO / Occupied Rehab",          markup:[25,35], gcfee:[9,13],  contingency:[13,18], display:"Structured + protective wording",   color:"#a78bfa", note:"Highest risk — occupied, compliance, MOHCD oversight"},
  {type:"Senior / ADA Housing",          markup:[25,35], gcfee:[9,13],  contingency:[12,17], display:"Structured + protective wording",   color:"#2dd4bf", note:"ADA compliance, phased delivery, HUD funded"},
  {type:"Affordable Housing",            markup:[25,33], gcfee:[9,13],  contingency:[12,16], display:"Structured + DIR/MOHCD wording",    color:"#a78bfa", note:"Prevailing wage, MOHCD, DIR certified payroll"},
  {type:"Multi-Family / Mid-Rise",       markup:[22,30], gcfee:[9,13],  contingency:[10,14], display:"Structured breakdown",              color:"#60a5fa", note:"Occupied renovation — coordination premium"},
  {type:"ADU / Room Addition",           markup:[22,28], gcfee:[7,11],  contingency:[8,13],  display:"Light breakdown",                  color:"#22c55e", note:"Bay Area ADU demand high — price accordingly"},
  {type:"High-Risk / Fast Track",        markup:[30,40], gcfee:[12,16], contingency:[15,22], display:"Structured + schedule caveats",     color:"#ef4444", note:"Pre-1978, expedited, or complex site conditions"},
];

const CONTRACT_CLAUSES = [
  {
    title:"Lump-Sum Pricing",
    text:"Contractor pricing is based on complete scope delivery and is not a pass-through of subcontractor costs. Subcontractor pricing, vendor pricing, procurement strategy, and internal cost structure are proprietary to Contractor and are not subject to disclosure unless expressly required by the written contract.",
    tag:"Protects margin privacy"
  },
  {
    title:"GC Fee / General Conditions",
    text:"General Conditions and Project Management fees compensate Contractor for scheduling, coordination, supervision, communication, inspections, administration, insurance, and overall execution of the work.",
    tag:"Justifies GC fee line"
  },
  {
    title:"Markup & Profit",
    text:"Contractor markup and profit compensate Contractor for business overhead, warranty administration, procurement responsibility, risk, and profit.",
    tag:"Protects profit margin"
  },
  {
    title:"Contingency Allowance",
    text:"Contractor may include a contingency allowance to address minor unforeseen conditions and maintain project progress. Items exceeding contingency or outside the agreed scope will be addressed by written change order.",
    tag:"Covers unknowns"
  },
  {
    title:"Change Order",
    text:"Any work outside the original scope must be approved in writing before execution. Contractor is not responsible for delays or costs associated with changes until written approval is received.",
    tag:"Stops unauthorized work"
  },
  {
    title:"Owner Breakdown Request Response",
    text:"We competitively sourced qualified bids for this scope. Pricing reflects full project delivery, including coordination, scheduling, supervision, insurance, warranty responsibility, and execution risk. We do not structure this as pass-through subcontractor pricing.",
    tag:"Use when owner asks for subs"
  },
];

function PricingCalculatorView({session, onWorkProduct}) {
  const [isMob, setIsMob] = React.useState(window.innerWidth < 768);
  React.useEffect(()=>{
    const handler = ()=>setIsMob(window.innerWidth<768);
    window.addEventListener('resize',handler);
    return ()=>window.removeEventListener('resize',handler);
  },[]);
  const [jobType, setJobType] = useState("SRO / Occupied Rehab");
  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientAddr, setClientAddr] = useState("");
  const [proposalDate, setProposalDate] = useState(new Date().toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"}));
  const [scopeLines, setScopeLines] = useState("");
  const [showCSLB, setShowCSLB] = useState(false);
  const [validityDays, setValidityDays] = useState(30);
  const [subCost, setSubCost] = useState("");
  const [matCost, setMatCost] = useState("");
  const [selfPerform, setSelfPerform] = useState("");
  const [permits, setPermits] = useState("");
  const [markupPct, setMarkupPct] = useState(25);
  const [gcFeePct, setGcFeePct] = useState(10);
  const [contingencyPct, setContingencyPct] = useState(12);
  const [format, setFormat] = useState("structured");
  const [activeTab, setActiveTab] = useState("calc");
  const [copiedClause, setCopiedClause] = useState(null);

  const range = PRICING_RANGES.find(r => r.type === jobType) || PRICING_RANGES[3];

  // Core formula from the guide
  const n = v => parseFloat(String(v).replace(/,/g,'')) || 0;
  const directCost = n(subCost) + n(matCost) + n(selfPerform) + n(permits);
  const markupAmt = directCost * (markupPct / 100);
  const afterMarkup = directCost + markupAmt;
  const gcFeeAmt = afterMarkup * (gcFeePct / 100);
  const subtotal = afterMarkup + gcFeeAmt;
  const contingencyAmt = subtotal * (contingencyPct / 100);
  const clientPrice = subtotal + contingencyAmt;
  const grossMargin = clientPrice - directCost;
  const grossMarginPct = directCost > 0 ? (grossMargin / clientPrice) * 100 : 0;

  // Publish the current calculator estimate to ARIA's context (Calc & Proposal tab).
  React.useEffect(() => {
    if (!onWorkProduct) return;
    const hasData = scopeLines.trim() || n(subCost) || n(matCost) || n(selfPerform) || n(permits);
    if (!hasData) { onWorkProduct(null); return; }
    const m = v => "$" + Math.round(v || 0).toLocaleString();
    const text = `Job type: ${jobType}
Scope:
${scopeLines || "(none entered)"}

Direct cost: ${m(directCost)}
Markup ${markupPct}%: ${m(markupAmt)}
GC fee ${gcFeePct}%: ${m(gcFeeAmt)}
Subtotal: ${m(subtotal)}
Contingency ${contingencyPct}%: ${m(contingencyAmt)}
Client price: ${m(clientPrice)}
Gross margin: ${m(grossMargin)} (${grossMarginPct.toFixed(1)}%)`;
    onWorkProduct({ kind: "calc", text, meta: { job: jobType, client: clientName } });
  }, [subCost, matCost, selfPerform, permits, markupPct, gcFeePct, contingencyPct, jobType, clientName, scopeLines]); // eslint-disable-line

  const fmt = v => '$' + Math.round(v).toLocaleString();
  const fmtPct = v => v.toFixed(1) + '%';

  const inRange = (val, [lo, hi]) => val >= lo && val <= hi;
  const markupOk = inRange(markupPct, range.markup);
  const gcOk = inRange(gcFeePct, range.gcfee);
  const contOk = inRange(contingencyPct, range.contingency);

  const setMidRange = () => {
    setMarkupPct(Math.round((range.markup[0] + range.markup[1]) / 2));
    setGcFeePct(Math.round((range.gcfee[0] + range.gcfee[1]) / 2));
    setContingencyPct(Math.round((range.contingency[0] + range.contingency[1]) / 2));
  };

  // Proposal outputs
  const proposalLumpSum = () =>
`${projectName || "Construction Scope"} — ${fmt(clientPrice)}

Includes labor, materials, coordination, supervision, permitting, scheduling, quality control, insurance, warranty responsibility, and completion of all scope items.

Grok Construction Group Inc — CSLB #1152750`;

  const proposalLight = () =>
`${projectName || "Construction Scope"}

Construction Scope:        ${fmt(subCost ? afterMarkup - gcFeeAmt : clientPrice * 0.88)}
Project Management & GC:   ${fmt(gcFeeAmt)}
Contingency Allowance:     ${fmt(contingencyAmt)}
─────────────────────────────────
Total Project Cost:        ${fmt(clientPrice)}

Grok Construction Group Inc — CSLB #1152750`;

  const proposalStructured = () =>
`${projectName || "Construction Scope"}

Base Construction Cost:              ${fmt(directCost + markupAmt)}
General Conditions / Project Mgmt:   ${fmt(gcFeeAmt)}
Contingency Allowance:               ${fmt(contingencyAmt)}
─────────────────────────────────────────
Total Contract Value:                 ${fmt(clientPrice)}

Gross Margin: ${fmtPct(grossMarginPct)} | Markup: ${markupPct}% | GC Fee: ${gcFeePct}% | Contingency: ${contingencyPct}%

[INTERNAL — remove before sending to client]

Grok Construction Group Inc — CSLB #1152750`;

  const getProposal = () => format === 'lumpsum' ? proposalLumpSum() : format === 'light' ? proposalLight() : proposalStructured();

  // Build a full professional HTML proposal — pulls from company settings (scoped per company)
  const buildProposalHTML = () => {
    const settKey = "grok_company_settings_" + (window._grokCompanyId||"local");
    const cs = (()=>{ try{ return JSON.parse(localStorage.getItem(settKey)||"{}"); } catch{ return {}; } })();

    const co   = cs.company_name  || session?.companyName || "Your Company";
    const own  = cs.owner_name    || session?.ownerName   || "";
    const lic  = cs.cslb          || session?.cslb        || "";
    const addr = cs.location      || "";
    const ph   = cs.phone         || "";
    const em   = cs.email         || session?.email       || "";
    const web  = cs.website       || "";
    const logo = cs.logo_url      || "";
    const today   = proposalDate || new Date().toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"});
    const expDate = new Date(Date.now()+(validityDays||30)*86400000).toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"});
    const pName   = projectName  || "Construction Project";
    const refId   = (pName.replace(/[^A-Z0-9]/gi,"").slice(0,6).toUpperCase()||"PROP") + "-" + Date.now().toString().slice(-4);
    const items   = (scopeLines||"").split("\n").filter(l=>l.trim());

    const css = `
    *{box-sizing:border-box;margin:0;padding:0}html{-webkit-text-size-adjust:100%;text-size-adjust:100%}body{overflow-x:hidden;max-width:100vw;-webkit-font-smoothing:antialiased}
    body{font-family:'Helvetica Neue',Arial,sans-serif;font-size:12px;color:#0f1923;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .page{max-width:760px;margin:0 auto;padding:48px 56px}
    .hdr{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:18px;margin-bottom:28px;border-bottom:2.5px solid #0f1923}
    .co-name{font-size:17px;font-weight:600;letter-spacing:-0.3px;color:#0f1923}
    .co-sub{font-size:10px;color:#64748b;line-height:1.9;margin-top:5px}
    .doc-word{font-size:28px;font-weight:300;letter-spacing:-0.8px;line-height:1;color:#0f1923;text-align:right}
    .doc-ref{font-size:9px;color:#94a3b8;letter-spacing:.14em;text-transform:uppercase;text-align:right;margin-top:3px}
    .doc-dates{font-size:10px;color:#64748b;line-height:1.9;text-align:right;margin-top:7px}
    .sect{margin-bottom:26px}
    .sect-lbl{font-size:9px;font-weight:600;text-transform:uppercase;letter-spacing:.14em;color:#94a3b8;padding-bottom:6px;border-bottom:1px solid #e2e8f0;margin-bottom:12px}
    .proj-box{background:#f8fafc;border-radius:6px;padding:14px 18px;display:grid;grid-template-columns:1fr 1fr;gap:8px 32px}
    .pf-lbl{font-size:9px;color:#94a3b8;text-transform:uppercase;letter-spacing:.1em;margin-bottom:2px}
    .pf-val{font-size:12px;font-weight:500;color:#0f1923}
    .sc-item{display:flex;align-items:baseline;gap:10px;padding:8px 0;border-bottom:1px solid #f1f5f9;font-size:12px;color:#1e293b;line-height:1.5}
    .sc-item:last-child{border-bottom:none}
    .chk{color:#16a34a;font-size:11px;font-weight:600;flex-shrink:0}
    .sc-note{font-size:10px;color:#94a3b8;font-style:italic;margin-top:8px}
    .pt{width:100%;border-collapse:collapse}
    .pt td{padding:10px 0;border-bottom:1px solid #f1f5f9;vertical-align:top}
    .pt td:last-child{text-align:right;padding-left:24px;white-space:nowrap}
    .pt-lbl{font-size:12px;color:#334155}
    .pt-note{font-size:10px;color:#94a3b8;margin-top:2px}
    .pt-val{font-size:12px;font-weight:500;color:#0f1923}
    .pt-total td{border-top:2px solid #0f1923;border-bottom:none!important;padding-top:14px}
    .pt-total-lbl{font-size:13px;font-weight:600;color:#0f1923}
    .pt-total-val{font-size:20px;font-weight:600;color:#0f1923}
    .lone-total{text-align:right;margin-top:16px;padding-top:16px;border-top:2px solid #0f1923}
    .lone-label{font-size:10px;text-transform:uppercase;letter-spacing:.12em;color:#94a3b8;margin-bottom:4px}
    .lone-val{font-size:36px;font-weight:300;letter-spacing:-1px;color:#0f1923;line-height:1}
    .cont-box{background:#fffbeb;border-left:3px solid #f59e0b;padding:11px 15px;border-radius:0 5px 5px 0;margin-top:12px;font-size:10px;color:#78350f;line-height:1.75}
    .tg{display:grid;grid-template-columns:1fr 1fr;gap:10px}
    .tb{background:#f8fafc;border-radius:5px;padding:11px 13px}
    .tb-ttl{font-size:9px;font-weight:600;text-transform:uppercase;letter-spacing:.1em;color:#334155;margin-bottom:4px}
    .tb-txt{font-size:10px;color:#64748b;line-height:1.65}
    .div-table{width:100%;border-collapse:collapse;font-size:11px}
    .div-table th{font-size:9px;font-weight:600;text-transform:uppercase;letter-spacing:.1em;color:#94a3b8;padding:8px 0;border-bottom:1.5px solid #0f1923;text-align:left}
    .div-table th:last-child{text-align:right}
    .div-table td{padding:9px 0;border-bottom:1px solid #f1f5f9;color:#334155;vertical-align:top}
    .div-table td:last-child{text-align:right;font-weight:500;color:#0f1923;padding-left:16px;white-space:nowrap}
    .div-total td{font-weight:600;font-size:12px;color:#0f1923;border-top:2px solid #0f1923;border-bottom:none;padding-top:12px}
    .alt-row{background:#fafafa}
    .allow-badge{display:inline-block;background:#e2e8f0;color:#475569;font-size:9px;font-weight:600;padding:1px 6px;border-radius:3px;text-transform:uppercase;letter-spacing:.06em;margin-left:6px}
    .sig-grid{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:32px;padding-top:24px;border-top:1px solid #e2e8f0}
    .sig-line{border-bottom:1.5px solid #0f1923;height:44px;margin-bottom:5px}
    .sig-lbl{font-size:9px;color:#94a3b8;text-transform:uppercase;letter-spacing:.08em}
    .sig-name{font-size:11px;font-weight:500;color:#334155;margin-top:2px}
    .sig-title{font-size:10px;color:#94a3b8;margin-top:1px}
    .dt-line{border-bottom:1px solid #cbd5e1;height:26px;margin-top:10px;margin-bottom:3px}
    .accept-txt{font-size:11px;color:#64748b;line-height:1.75;margin-bottom:20px}
    .ftr{margin-top:28px;padding-top:14px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:9px;color:#94a3b8}
    @media print{.page{padding:28px 36px}}
`;

    const hdr = `
      <div class="hdr">
        <div>
          ${logo ? `<img src="${logo}" style="max-height:52px;max-width:160px;object-fit:contain;display:block;margin-bottom:6px" alt="${co}"/>` : `<div class="co-name">${co}</div>`}
          <div class="co-sub">${[own,lic,addr,ph,em,web].filter(Boolean).join('<br>')}</div>
        </div>
        <div>
          <div class="doc-word">Proposal</div>
          <div class="doc-ref">${refId}</div>
          <div class="doc-dates"><strong>Date:</strong> ${today}<br><strong>Valid through:</strong> ${expDate}</div>
        </div>
      </div>`;

    const projInfo = `
      <div class="sect">
        <div class="sect-lbl">Project information</div>
        <div class="proj-box">
          <div><div class="pf-lbl">Prepared for</div><div class="pf-val">${clientName||"—"}</div></div>
          <div><div class="pf-lbl">Project name</div><div class="pf-val">${pName}</div></div>
          <div><div class="pf-lbl">Project address</div><div class="pf-val">${clientAddr||"—"}</div></div>
          <div><div class="pf-lbl">Project type</div><div class="pf-val">${jobType}</div></div>
        </div>
      </div>`;

    const scopeSect = items.length ? `
      <div class="sect">
        <div class="sect-lbl">Scope of work</div>
        ${items.map(i=>`<div class="sc-item"><span class="chk">&#10003;</span><span>${i}</span></div>`).join("")}
        <div class="sc-note">Work outside the scope listed above requires a written Change Order executed prior to commencement.</div>
      </div>` : "";

    const terms = `
      <div class="sect">
        <div class="sect-lbl">Terms &amp; conditions</div>
        <div class="tg">
          <div class="tb"><div class="tb-ttl">Contract type</div><div class="tb-txt">Fixed-price lump sum. All listed scope items included. Additional work requires a written, signed Change Order before commencement.</div></div>
          <div class="tb"><div class="tb-ttl">Payment</div><div class="tb-txt">10% mobilization &middot; milestone progress billings &middot; 10% retention released at final completion and punchlist sign-off.</div></div>
          <div class="tb"><div class="tb-ttl">Timeline</div><div class="tb-txt">Confirmed at contract execution. Contingent on permit issuance, owner access, and site conditions. Owner-caused delays not contractor&rsquo;s responsibility.</div></div>
          <div class="tb"><div class="tb-ttl">Validity</div><div class="tb-txt">Proposal valid ${validityDays} days from date above. Pricing subject to revision after ${expDate} due to material escalation and subcontractor availability.</div></div>
          <div class="tb"><div class="tb-ttl">License &amp; insurance</div><div class="tb-txt">${co} holds ${lic||"a valid contractor license"}. GL and Workers&rsquo; Compensation insurance maintained. COI provided on request.</div></div>
          <div class="tb"><div class="tb-ttl">Exclusions</div><div class="tb-txt">Hazardous material abatement, owner-furnished equipment, utility company fees, and work outside the stated project address unless explicitly listed in scope.</div></div>
        </div>
      </div>`;

    const acceptance = `
      <div class="sect">
        <div class="sect-lbl">Acceptance</div>
        <div class="accept-txt">By signing below, Owner authorizes ${co} to proceed with the Scope of Work described in this proposal under the terms stated above. This signed proposal constitutes the Contract for Construction between the parties.</div>
        <div class="sig-grid">
          <div>
            <div class="sig-line"></div>
            <div class="sig-lbl">Contractor signature</div>
            <div class="sig-name">${own||co}</div>
            <div class="sig-title">${co}${lic?" &middot; "+lic:""}</div>
            <div class="dt-line"></div><div class="sig-lbl">Date</div>
          </div>
          <div>
            <div class="sig-line"></div>
            <div class="sig-lbl">Owner / Client signature</div>
            <div class="sig-name">${clientName||"_______________________________"}</div>
            <div class="sig-title">Print name &amp; title</div>
            <div class="dt-line"></div><div class="sig-lbl">Date</div>
          </div>
        </div>
      </div>`;

    const footer = `<div class="ftr"><span>${co}${lic?" &middot; "+lic:""}</span><span>Page 1 of 1 &middot; ${today}</span></div>`;

    const wrap = (body) => `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>Proposal &mdash; ${pName}</title><style>${css}</style></head><body><div class="page">${hdr}${body}${footer}</div></body></html>`;

    // ── FORMAT 1: LUMP SUM ────────────────────────────────────────────────────
    if (format === "lumpsum") {
      return wrap(`
        ${projInfo}
        ${scopeSect}
        <div class="sect">
          <div class="sect-lbl">Contract Amount</div>
          <div style="background:#f8fafc;border-radius:8px;padding:24px 28px;margin-bottom:12px">
            <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.12em;color:#94a3b8;margin-bottom:6px">Total Fixed-Price Contract</div>
            <div style="font-size:40px;font-weight:300;letter-spacing:-1.5px;color:#0f1923;line-height:1">${fmt(clientPrice)}</div>
            <div style="font-size:10px;color:#94a3b8;margin-top:8px;line-height:1.7">Fixed lump-sum price inclusive of all labor, materials, subcontractors, project management, supervision, scheduling, insurance, and warranty responsibility for the full scope of work listed above.</div>
          </div>
          <div style="font-size:10px;color:#64748b;line-height:1.75;padding:12px 16px;border-left:3px solid #e2e8f0">
            No additional costs will be incurred by the Owner without a fully executed written Change Order signed by both parties prior to commencement of additional work.
          </div>
        </div>
        ${terms}
        ${acceptance}
      `);
    }

    // ── FORMAT 2: STANDARD ────────────────────────────────────────────────────
    if (format === "light") {
      return wrap(`
        ${projInfo}
        ${scopeSect}
        <div class="sect">
          <div class="sect-lbl">Project Cost Summary</div>
          <table class="pt">
            <tr><td>
              <div class="pt-lbl">Direct Construction Cost</div>
              <div class="pt-note">Labor, materials &amp; subcontractors for all scope items</div>
            </td><td><span class="pt-val">${fmt(directCost)}</span></td></tr>
            ${contingencyPct > 0 ? `<tr><td>
              <div class="pt-lbl">Construction Contingency (${contingencyPct}%)</div>
              <div class="pt-note">Standard allowance for unforeseen field conditions &mdash; unused amount credited to Owner</div>
            </td><td><span class="pt-val">${fmt(contingencyAmt)}</span></td></tr>` : ""}
            <tr><td>
              <div class="pt-lbl">General Conditions, Overhead &amp; Profit (${markupPct + gcFeePct}%)</div>
              <div class="pt-note">Project coordination, site supervision, scheduling, subcontractor management, permitting coordination, insurance, bonding, and operational oversight</div>
            </td><td><span class="pt-val">${fmt(markupAmt + gcFeeAmt)}</span></td></tr>
            <tr class="pt-total"><td><span class="pt-total-lbl">TOTAL PROJECT BUDGET</span></td><td><span class="pt-total-val">${fmt(clientPrice)}</span></td></tr>
          </table>
          ${contingencyPct > 0 ? `<div class="cont-box"><strong>Construction Contingency:</strong> A contingency reserve is standard practice on all construction projects, recommended by the AIA and AGC. It covers unforeseen field conditions, minor scope adjustments required by inspection, and material cost fluctuations. Any unused contingency is credited to the Owner at project close-out.</div>` : ""}
        </div>
        ${terms}
        ${acceptance}
      `);
    }

    // ── FORMAT 3: DETAILED ────────────────────────────────────────────────────
    if (format === "structured") {
      return wrap(`
        ${projInfo}
        ${scopeSect}
        <div class="sect">
          <div class="sect-lbl">Project Cost Summary</div>
          <table class="pt">
            <tr><td>
              <div class="pt-lbl">Direct Construction Cost</div>
              <div class="pt-note">All labor, materials, and subcontractor costs for the scope of work listed above</div>
            </td><td><span class="pt-val">${fmt(directCost)}</span></td></tr>
            ${contingencyPct > 0 ? `<tr><td>
              <div class="pt-lbl">Construction Contingency (${contingencyPct}%)</div>
              <div class="pt-note">Standard allowance for unforeseen conditions &mdash; unused amount returned to Owner</div>
            </td><td><span class="pt-val">${fmt(contingencyAmt)}</span></td></tr>` : ""}
            <tr><td>
              <div class="pt-lbl">Construction Management &amp; Supervision (${markupPct}%)</div>
              <div class="pt-note">Field supervision, daily site management, quality control, crew coordination, and safety compliance</div>
            </td><td><span class="pt-val">${fmt(markupAmt)}</span></td></tr>
            <tr><td>
              <div class="pt-lbl">Project Coordination &amp; Site Management (${gcFeePct}%)</div>
              <div class="pt-note">Project scheduling, subcontractor procurement &amp; management, permit coordination, owner communication, insurance, bonding, and administrative overhead</div>
            </td><td><span class="pt-val">${fmt(gcFeeAmt)}</span></td></tr>
            <tr class="pt-total"><td><span class="pt-total-lbl">TOTAL PROJECT BUDGET</span></td><td><span class="pt-total-val">${fmt(clientPrice)}</span></td></tr>
          </table>
          ${contingencyPct > 0 ? `<div class="cont-box"><strong>Construction Contingency:</strong> Industry-standard reserve covering unforeseen field conditions, code-required adjustments, and material escalation. Any unused contingency is credited to the Owner or applied to Owner-directed additional scope prior to close-out.</div>` : ""}
        </div>
        ${terms}
        ${acceptance}
      `);
    }

    // ── FORMAT 4: DEVELOPER / INSTITUTIONAL ───────────────────────────────────
    // CSI division breakdown. For banks, MOHCD, HUD, investor owners, AIA G702.
    // Allowances called out. Alternates section. Schedule of Values ready.
    if (format === "developer") {
      const divTotal = clientPrice;
      const divs = [
        {code:"01",name:"General Requirements",pct:0.05},
        {code:"02",name:"Existing Conditions (Demo & Abatement)",pct:0.06},
        {code:"03",name:"Concrete & Masonry",pct:0.04},
        {code:"04",name:"Metals & Structural Steel",pct:0.03},
        {code:"05",name:"Wood, Plastics & Composites (Framing)",pct:0.10},
        {code:"06",name:"Thermal & Moisture Protection",pct:0.04},
        {code:"07",name:"Openings (Doors, Frames & Hardware)",pct:0.04},
        {code:"08",name:"Finishes (Drywall, Paint, Flooring, Tile)",pct:0.12},
        {code:"09",name:"Specialties & Equipment",pct:0.03},
        {code:"21",name:"Fire Suppression",pct:0.02},
        {code:"22",name:"Plumbing",pct:0.10},
        {code:"23",name:"HVAC & Mechanical",pct:0.08},
        {code:"26",name:"Electrical",pct:0.10},
        {code:"00",name:"General Conditions & Project Management",pct:gcFeeAmt/clientPrice,fixed:gcFeeAmt},
        {code:"00",name:"Contingency Reserve ("+contingencyPct+"%)",pct:contingencyAmt/clientPrice,fixed:contingencyAmt,allow:true},
      ];
      const baseTotal = divs.slice(0,-2).reduce((s,d)=>s+(d.fixed||Math.round(d.pct*clientPrice)),0);
      const adjust = clientPrice - baseTotal - gcFeeAmt - contingencyAmt;
      return wrap(`
        ${projInfo}
        ${scopeSect}
        <div class="sect">
          <div class="sect-lbl">Schedule of values — CSI MasterFormat</div>
          <table class="div-table">
            <tr>
              <th style="width:48px">Div.</th>
              <th>Description</th>
              <th>Amount</th>
            </tr>
            ${divs.slice(0,-2).map((d,i)=>{
              const amt = d.fixed || Math.round(d.pct * clientPrice) + (i===0?adjust:0);
              return `<tr${i%2===1?' class="alt-row"':''}>
                <td style="color:#94a3b8;font-size:10px;font-weight:600">${d.code}</td>
                <td>${d.name}</td>
                <td>$${amt.toLocaleString()}</td>
              </tr>`;
            }).join("")}
            <tr><td colspan="3" style="padding:6px 0;border-bottom:1px solid #f1f5f9"></td></tr>
            <tr>
              <td style="color:#94a3b8;font-size:10px;font-weight:600">00</td>
              <td>${divs[divs.length-2].name}</td>
              <td>$${gcFeeAmt.toLocaleString()}</td>
            </tr>
            <tr>
              <td style="color:#94a3b8;font-size:10px;font-weight:600">00</td>
              <td>${divs[divs.length-1].name}<span class="allow-badge">Allowance</span></td>
              <td>$${contingencyAmt.toLocaleString()}</td>
            </tr>
            <tr class="div-total">
              <td colspan="2">Total contract value</td>
              <td style="font-size:16px">$${Math.round(clientPrice).toLocaleString()}</td>
            </tr>
          </table>
          <div style="margin-top:12px;font-size:10px;color:#94a3b8;line-height:1.7">
            Division allocations are for Schedule of Values and progress billing purposes. Amounts within each division may be reallocated with contractor&rsquo;s prior written approval. This breakdown does not constitute a disclosure of subcontract amounts, unit costs, or contractor margin.
          </div>
          <div class="cont-box" style="margin-top:12px">
            <strong>Contingency reserve (${contingencyPct}%):</strong> Industry standard per AIA and AGC guidelines. Covers unforeseen field conditions, inspection-required scope adjustments, and material cost fluctuations. Any unused contingency credited to Owner or applied to additional Owner-directed scope prior to close-out. Draws from contingency require written authorization from Owner.
          </div>
        </div>
        ${items.length ? `
        <div class="sect">
          <div class="sect-lbl">Alternates</div>
          <table class="pt" style="font-size:11px">
            <tr><td style="color:#94a3b8">Alt-01</td><td>Add: owner-provided fixtures (allowance credit)</td><td style="text-align:right;color:#16a34a;white-space:nowrap">TBD at buyout</td></tr>
            <tr><td style="color:#94a3b8">Alt-02</td><td>Deduct: phase construction to minimize tenant displacement</td><td style="text-align:right;color:#16a34a;white-space:nowrap">TBD at schedule</td></tr>
          </table>
          <div style="font-size:10px;color:#94a3b8;margin-top:8px">Alternates are not included in the base contract value above. Pricing for selected alternates issued as Change Order upon Owner election.</div>
        </div>` : ""}
        ${terms}
        ${acceptance}
      `);
    }

    return wrap(`${projInfo}${scopeSect}${terms}${acceptance}`);
  };


  const copyText = (text, id) => {
    navigator.clipboard.writeText(text).then(() => { setCopiedClause(id); setTimeout(()=>setCopiedClause(null), 2000); });
  };

  const SliderInput = ({label, value, onChange, min, max, rangeArr, unit="%"}) => {
    const ok = inRange(value, rangeArr);
    return (
      <div style={{marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
          <span style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em"}}>{label}</span>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:11,color:T.muted}}>SF Bay Area: {rangeArr[0]}–{rangeArr[1]}{unit}</span>
            <span style={{fontSize:13,fontWeight:800,color:ok?T.green:T.amber,minWidth:40,textAlign:"right"}}>{value}{unit}</span>
            <div style={{width:8,height:8,borderRadius:"50%",background:ok?T.green:T.amber,flexShrink:0}}/>
          </div>
        </div>
        <input type="range" min={min} max={max} value={value} onChange={e=>onChange(parseInt(e.target.value))}
          style={{width:"100%",accentColor:ok?T.green:T.amber,cursor:"pointer",height:isMob?"24px":"auto",touchAction:"manipulation"}}/>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:T.muted,marginTop:2}}>
          <span>{min}{unit}</span><span style={{color:ok?T.green:T.amber,fontWeight:700}}>{ok?"✓ In range":"⚠ Outside range"}</span><span>{max}{unit}</span>
        </div>
      </div>
    );
  };

  const MoneyInput = ({label, defaultVal, onCommit, ph}) => {
    const ref = React.useRef(null);
    return (
      <div style={{marginBottom:12}}>
        <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>{label}</div>
        <div style={{position:"relative"}}>
          <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:T.muted,fontSize:14,pointerEvents:"none"}}>$</span>
          <input
            ref={ref}
            type="text"
            inputMode="decimal"
            defaultValue={defaultVal||""}
            onBlur={e=>onCommit(e.target.value)}
            placeholder={ph||"0"}
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"10px 12px 10px 24px",color:T.text,fontSize:14,fontFamily:"'Space Mono',monospace",fontWeight:600,WebkitAppearance:"none"}}/>
        </div>
      </div>
    );
  };

  const TabBtn = ({id,label}) => (
    <button onClick={()=>setActiveTab(id)} style={{padding:"7px 18px",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:activeTab===id?T.gold:T.bg2,color:activeTab===id?T.bg0:T.muted,transition:"all 0.15s",whiteSpace:"nowrap"}}>{label}</button>
  );

  return (
    <React.Fragment>
    <div style={{padding:isMob?"12px 14px":"24px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div>
          <div style={{fontSize:isMob?16:20,fontWeight:800,color:T.text}}>Pricing Calculator</div>
          <div style={{fontSize:12,color:T.muted,marginTop:3}}>Sub cost → markup → GC fee → contingency → client price. Protects your margin.</div>
        </div>
      </div>

      <div style={{display:"flex",gap:4,marginBottom:16,overflowX:"auto",WebkitOverflowScrolling:"touch",paddingBottom:4,flexWrap:isMob?"nowrap":"wrap"}}>
        <TabBtn id="calc" label="Calculator"/>
        <TabBtn id="proposal" label="Proposal"/>
        <TabBtn id="clauses" label="Clauses"/>
        <TabBtn id="ranges" label="Rate Guide"/>
      </div>

      {/* CALCULATOR TAB */}
      {activeTab==="calc"&&(
        <div style={{display:"grid",gridTemplateColumns:isMob?"1fr":"1fr 1fr",gap:16}}>

          {/* Inputs */}
          <div>
            <Card>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14}}>Project Inputs</div>

              <div style={{marginBottom:14}}>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Project Name</div>
                <input defaultValue={projectName} onBlur={e=>setProjectName(e.target.value)} placeholder="SRO Electrical Scope — Turk St"
                  autoComplete="off" autoCorrect="off"
                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:13,fontFamily:"inherit",WebkitAppearance:"none"}}/>
              </div>

              <div style={{marginBottom:14}}>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Job Type</div>
                <select value={jobType} onChange={e=>{ setJobType(e.target.value); const r=PRICING_RANGES.find(x=>x.type===e.target.value); if(r){setMarkupPct(Math.round((r.markup[0]+r.markup[1])/2));setGcFeePct(Math.round((r.gcfee[0]+r.gcfee[1])/2));setContingencyPct(Math.round((r.contingency[0]+r.contingency[1])/2));} }}
                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}>
                  {PRICING_RANGES.map(r=><option key={r.type}>{r.type}</option>)}
                </select>
              </div>

              <div style={{background:T.bg3,borderLeft:`3px solid ${range.color}`,borderRadius:7,padding:"10px 12px",marginBottom:16,fontSize:11,color:T.muted}}>
                <strong style={{color:range.color}}>SF Bay Area 2025/2026 — {jobType}:</strong> Markup {range.markup[0]}–{range.markup[1]}% · GC Fee {range.gcfee[0]}–{range.gcfee[1]}% · Contingency {range.contingency[0]}–{range.contingency[1]}%{range.note?<><br/><span style={{fontSize:10,opacity:0.8}}>{range.note}</span></>:""}
                <br/><strong style={{color:T.text}}>Show owner as:</strong> {range.display}
              </div>

              <MoneyInput label="Subcontractor / Trade Cost" defaultVal={subCost} onCommit={setSubCost} ph="500,000"/>
              <MoneyInput label="Materials / Owner-Furnished Coordination" defaultVal={matCost} onCommit={setMatCost} ph="0"/>
              <MoneyInput label="Direct Labor / Self-Perform" defaultVal={selfPerform} onCommit={setSelfPerform} ph="0"/>
              <MoneyInput label="Permits / Testing / Misc." defaultVal={permits} onCommit={setPermits} ph="0"/>

              {directCost > 0 && (
                <div style={{background:T.bg3,borderRadius:8,padding:"10px 12px",marginTop:4,marginBottom:4,fontSize:12}}>
                  <span style={{color:T.muted}}>Direct Cost Base: </span>
                  <span style={{color:T.text,fontWeight:800,fontFamily:"'Space Mono',monospace"}}>{fmt(directCost)}</span>
                </div>
              )}
            </Card>

            <Card style={{marginTop:12}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>Markup & Fees</div>
                <Btn small ghost onClick={setMidRange}>Set Mid-Range</Btn>
              </div>

              <SliderInput label="CM & Supervision %" value={markupPct} onChange={setMarkupPct} min={10} max={40} rangeArr={range.markup}/>
              <SliderInput label="Gen. Conditions %" value={gcFeePct} onChange={setGcFeePct} min={3} max={20} rangeArr={range.gcfee}/>
              <SliderInput label="Contingency %" value={contingencyPct} onChange={setContingencyPct} min={2} max={25} rangeArr={range.contingency}/>
            </Card>
          </div>

          {/* Output */}
          <div>
            <Card>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:16}}>Internal Calculation</div>

              {directCost === 0 ? (
                <div style={{textAlign:"center",padding:"40px 20px",color:T.muted,fontSize:13}}>
                  <div style={{fontSize:32,marginBottom:12}}>💰</div>
                  Enter costs on the left to calculate
                </div>
              ) : (
                <div>
                  {[
                    {l:"Direct Cost Base", v:directCost, muted:true},
                    {l:`Markup (${markupPct}%)`, v:markupAmt, color:T.green},
                    {l:"Price After Markup", v:afterMarkup, bold:true},
                    {l:`GC Fee (${gcFeePct}%)`, v:gcFeeAmt, color:T.teal},
                    {l:"Subtotal Before Contingency", v:subtotal, bold:true},
                    {l:`Contingency (${contingencyPct}%)`, v:contingencyAmt, color:T.amber},
                  ].map(({l,v,muted,bold,color})=>(
                    <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${T.subtle}`}}>
                      <span style={{fontSize:12,color:muted?T.muted:T.text,fontWeight:bold?700:400}}>{l}</span>
                      <span style={{fontSize:13,fontWeight:bold?800:600,color:color||T.text,fontFamily:"'Space Mono',monospace"}}>{fmt(v)}</span>
                    </div>
                  ))}

                  {/* CLIENT PRICE — hero number */}
                  <div style={{background:`linear-gradient(135deg,${T.gold}18,${T.teal}10)`,border:`1px solid ${T.gold}30`,borderRadius:10,padding:"16px",margin:"12px 0"}}>
                    <div style={{fontSize:10,fontWeight:700,color:T.gold,textTransform:"uppercase",letterSpacing:"0.15em",marginBottom:4}}>Recommended Client Price</div>
                    <div style={{fontSize:36,fontWeight:800,color:T.gold,fontFamily:"'Space Mono',monospace",lineHeight:1}}>{fmt(clientPrice)}</div>
                  </div>

                  {/* Margin stats */}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                    {[
                      {l:"Gross Margin",v:fmt(grossMargin),c:T.green},
                      {l:"Margin %",v:fmtPct(grossMarginPct),c:grossMarginPct>30?T.green:grossMarginPct>20?T.amber:T.red},
                    ].map(s=>(
                      <div key={s.l} style={{background:T.bg2,borderRadius:8,padding:"10px 12px",textAlign:"center"}}>
                        <div style={{fontSize:10,color:T.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.08em"}}>{s.l}</div>
                        <div style={{fontSize:20,fontWeight:800,color:s.c,fontFamily:"'Space Mono',monospace"}}>{s.v}</div>
                      </div>
                    ))}
                  </div>

                  {/* Warnings */}
                  {(!markupOk || !gcOk || !contOk) && (
                    <div style={{background:T.amberBg,borderLeft:`3px solid ${T.amber}`,borderRadius:7,padding:"10px 12px",fontSize:11,color:T.muted,marginBottom:8}}>
                      <strong style={{color:T.amber}}>Outside recommended range for {jobType}:</strong>
                      {!markupOk&&<div>· Markup {markupPct}% (recommended {range.markup[0]}–{range.markup[1]}%)</div>}
                      {!gcOk&&<div>· GC Fee {gcFeePct}% (recommended {range.gcfee[0]}–{range.gcfee[1]}%)</div>}
                      {!contOk&&<div>· Contingency {contingencyPct}% (recommended {range.contingency[0]}–{range.contingency[1]}%)</div>}
                    </div>
                  )}

                  {/* Quick actions */}
                  <div style={{display:"flex",gap:8}}>
                    <Btn small primary onClick={()=>setActiveTab("proposal")} style={{flex:1,justifyContent:"center"}}>Generate Proposal →</Btn>
                    <Btn small ghost onClick={()=>copyText(getProposal(),"calc")} style={{flex:1,justifyContent:"center"}}>
                      {copiedClause==="calc"?"Copied!":"Copy Proposal"}
                    </Btn>
                  </div>
                </div>
              )}
            </Card>

            {/* Breakdown protection reminder */}
            <Card style={{marginTop:12,background:T.bg3}}>
              <div style={{fontSize:11,fontWeight:700,color:T.red,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>If Owner Asks for Sub Breakdown</div>
              <div style={{fontSize:12,color:T.text,lineHeight:1.7,marginBottom:10,fontStyle:"italic"}}>
                "We competitively sourced qualified bids for this scope. Pricing reflects full project delivery — coordination, scheduling, supervision, insurance, warranty responsibility, and execution risk. We do not structure this as pass-through subcontractor pricing."
              </div>
              <div style={{fontSize:10,color:T.muted,lineHeight:1.5}}>
                <strong style={{color:T.amber}}>Exception:</strong> If the contract is cost-plus, open-book, or public works with audit rights, disclosure may be required. Use lump-sum wording for all private work.
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* PROPOSAL OUTPUT TAB */}
      {activeTab==="proposal"&&(
        <div style={{display:"grid",gridTemplateColumns:isMob?"1fr":"260px 1fr",gap:16}}>
          <div>
            <Card>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14}}>Proposal Details</div>
              {[
                {label:"Client / Owner Name", val:clientName, set:setClientName, placeholder:"Marcus Reid"},
                {label:"Project Address", val:clientAddr, set:setClientAddr, placeholder:"466 Turk St, San Francisco CA"},
                {label:"Project Name / Scope", val:projectName, set:setProjectName, placeholder:"SRO Renovation — Electrical & Plumbing"},
                {label:"Proposal Date", val:proposalDate, set:setProposalDate, placeholder:"May 1, 2026"},
              ].map(f=>(
                <div key={f.label} style={{marginBottom:10}}>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>{f.label}</div>
                  <input value={f.val} onChange={e=>f.set(e.target.value)} placeholder={f.placeholder}
                    style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"6px 8px",color:T.text,fontSize:11,fontFamily:"inherit"}}/>
                </div>
              ))}
              <div style={{marginBottom:10}}>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>Scope of Work (one item per line)</div>
                <textarea value={scopeLines} onChange={e=>setScopeLines(e.target.value)}
                  rows={5} placeholder={"Demo existing bathroom tile\nRough plumbing — 2 fixtures\nInstall dual-flush toilets\nNew vanity and faucets\nFinal inspection and punchlist"}
                  style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"6px 8px",color:T.text,fontSize:11,fontFamily:"inherit",resize:"vertical"}}/>
                <div style={{fontSize:9,color:T.muted,marginTop:2}}>Each line becomes a ✓ item on the proposal</div>
              </div>
              <div style={{marginBottom:14}}>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>Proposal Valid For (days)</div>
                <input type="number" value={validityDays} onChange={e=>setValidityDays(Number(e.target.value))} min={7} max={90}
                  style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"6px 8px",color:T.text,fontSize:11,fontFamily:"inherit"}}/>
              </div>

              <div style={{marginBottom:12}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:10}}>Format</div>
                {[
                  {id:"lumpsum",    l:"Lump Sum",    sub:"One number. Repeat clients, simple scopes, under $75K."},
                  {id:"light",      l:"Standard",    sub:"3 lines. First-time owners. $75K–$300K."},
                  {id:"structured", l:"Detailed",    sub:"Cost categories. Sophisticated owners. $300K+."},
                  {id:"developer",  l:"Institutional", sub:"CSI divisions + alternates. Banks, MOHCD, HUD, lenders."},
                ].map(f=>(
                  <div key={f.id} onClick={()=>setFormat(f.id)}
                    style={{padding:"10px",borderRadius:8,border:"1px solid "+(format===f.id?T.gold:T.subtle),background:format===f.id?T.goldBg:T.bg2,cursor:"pointer",marginBottom:6}}>
                    <div style={{fontSize:11,fontWeight:700,color:format===f.id?T.gold:T.text}}>{f.l}</div>
                    <div style={{fontSize:10,color:T.muted}}>{f.sub}</div>
                  </div>
                ))}
              </div>

              <div style={{background:T.amberBg,borderLeft:"3px solid "+T.amber,borderRadius:"0 7px 7px 0",padding:"9px 12px",fontSize:10,color:T.muted,lineHeight:1.6}}>
                <strong style={{color:T.amber}}>Never expose:</strong> Sub costs, internal markup, or margin % to the owner.
              </div>
            </Card>
          </div>
          <Card>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>Proposal</div>
                <button onClick={()=>setShowCSLB(true)}
                  style={{fontSize:10,fontWeight:700,background:"#1e3a5f",color:"#60a5fa",border:"1px solid #2d5a8e",borderRadius:6,padding:"3px 9px",cursor:"pointer"}}>
                  🏛 Check CA Compliance
                </button>
              </div>
              <div style={{display:"flex",gap:7}}>
                <Btn small ghost onClick={async ()=>{
                  const cid = window._grokCompanyId || session?.companyId || "local";
                  const tpls = (()=>{try{return JSON.parse(localStorage.getItem("grok_templates_"+cid)||"[]");}catch{return [];}})();
                  const tpl = tpls.find(t=>t.cat==="contracts" && t.data && t.fileName&&t.fileName.toLowerCase().includes("proposal"));
                  if (!tpl) { alert("GCG_Proposal.docx not found in Document Kit. Please upload it first."); return; }
                  if (!window.JSZip) {
                    await new Promise((res,rej)=>{const s=document.createElement("script");s.src="https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";s.onload=res;s.onerror=rej;document.head.appendChild(s);});
                  }
                  const b64=tpl.data.split(",")[1]; const bin=atob(b64); const bytes=new Uint8Array(bin.length);
                  for(let i=0;i<bin.length;i++) bytes[i]=bin.charCodeAt(i);
                  const zip=await window.JSZip.loadAsync(bytes);
                  let xml=await zip.file("word/document.xml").async("string");
                  const esc=v=>String(v||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
                  const today=new Date().toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"});
                  const exp=new Date(Date.now()+(validityDays||30)*86400000).toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"});
                  const refId=(projectName||"PROP").replace(/[^A-Z0-9]/gi,"").slice(0,6).toUpperCase()+"-"+Date.now().toString().slice(-4);
                  const scopeItems=(scopeLines||"").split("\n").filter(l=>l.trim()).map(l=>"• "+l).join("  |  ");
                  const clientInfo=[clientName,clientAddr].filter(Boolean).join("  |  ");
                  // Fill specific cells by index — based on GCG_Proposal.docx cell map
                  const FILL={
                    1: refId, 2: today, 3: exp, 4: fmt(clientPrice),
                    6: [clientName, clientAddr].filter(Boolean).join("\n"),
                    10: scopeItems,
                    11: fmt(clientPrice),
                    13: fmt(clientPrice),
                    14: "Permits unless otherwise noted",
                    15: "Owner-furnished materials",
                    16: "Items not listed in scope",
                  };
                  let ci=0;
                  xml=xml.replace(/<w:tc>([\s\S]*?)<\/w:tc>/g,(match)=>{
                    const idx=ci++;
                    if(FILL[idx]!==undefined){
                      const val=FILL[idx];
                      return match.replace(/(<w:p\b[^>]*>)(\s*<w:pPr>[\s\S]*?<\/w:pPr>)?\s*(<\/w:p>)/,
                        (m,open,pPr,close)=>`${open}${pPr||""}<w:r><w:t xml:space="preserve">${esc(val)}</w:t></w:r>${close}`);
                    }
                    return match;
                  });
                  zip.file("word/document.xml",xml);
                  const out=await zip.generateAsync({type:"blob",mimeType:"application/vnd.openxmlformats-officedocument.wordprocessingml.document"});
                  const url=URL.createObjectURL(out);
                  const a=document.createElement("a"); a.href=url;
                  a.download=`GCG_Proposal_${(projectName||"Project").replace(/\s+/g,"_")}.docx`;
                  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
                }}>📄 Fill & Download GCG Template</Btn>
                <Btn small ghost onClick={()=>{
                  const w=window.open("","_blank","width=900,height=700");
                  if(!w){alert("Allow popups to use Print.");return;}
                  w.document.write(buildProposalHTML());
                  w.document.close();
                  setTimeout(()=>w.print(),400);
                }}>🖨 Print / Save PDF</Btn>
              </div>
            </div>
            {directCost===0 ? (
              <div style={{textAlign:"center",padding:"40px",color:T.muted,fontSize:13}}>Enter costs in the Calculator tab first</div>
            ) : (
              <div style={{background:T.bg2,border:"1px solid "+T.subtle,borderRadius:10,padding:"20px 24px"}}>
                {/* Preview summary */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16,paddingBottom:16,borderBottom:"2px solid "+T.subtle}}>
                  <div>
                    <div style={{fontSize:16,fontWeight:800,color:T.text}}>{projectName||"Construction Project"}</div>
                    <div style={{fontSize:11,color:T.muted,marginTop:2}}>{clientName||"—"} · {clientAddr||"—"}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:24,fontWeight:800,color:T.gold}}>{fmt(clientPrice)}</div>
                    <div style={{fontSize:10,color:T.muted}}>Total Proposal Price</div>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:isMob?"1fr":"1fr 1fr",gap:12,marginBottom:16}}>
                  {[
                    {l:"Job Type",v:jobType},
                    {l:"Valid Through",v:new Date(Date.now()+(validityDays||30)*86400000).toLocaleDateString()},
                    {l:"Direct Cost",v:fmt(directCost)},
                    {l:"CM & Supervision ("+markupPct+"%)",v:fmt(markupAmt)},
                    {l:"Gen. Conditions ("+gcFeePct+"%)",v:fmt(gcFeeAmt)},
                    {l:"Contingency ("+contingencyPct+"%)",v:fmt(contingencyAmt)},
                  ].map(r=>(
                    <div key={r.l} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"5px 0",borderBottom:"1px solid "+T.subtle,flexWrap:"wrap",gap:4}}>
                      <span style={{color:T.muted}}>{r.l}</span>
                      <span style={{color:T.text,fontWeight:600}}>{r.v}</span>
                    </div>
                  ))}
                </div>
                {scopeLines&&(
                  <div style={{marginBottom:12}}>
                    <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Scope of Work</div>
                    {scopeLines.split("\n").filter(l=>l.trim()).map((l,i)=>(
                      <div key={i} style={{fontSize:11,color:T.text,padding:"4px 0",borderBottom:"1px solid "+T.subtle,display:"flex",gap:8}}>
                        <span style={{color:T.muted,flexShrink:0}}>{i+1}.</span><span>{l}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{background:T.greenBg,border:"1px solid "+T.green+"40",borderRadius:8,padding:"10px 14px",fontSize:11,color:T.green,fontWeight:600,textAlign:"center"}}>
                  ✓ Click "📄 Fill & Download GCG Template" above to generate your filled Word proposal
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* CONTRACT CLAUSES TAB */}
      {activeTab==="clauses"&&(
        <div>
          <div style={{background:T.blueBg,borderLeft:`3px solid ${T.blue}`,borderRadius:9,padding:"12px 16px",marginBottom:16,fontSize:12,color:T.muted,lineHeight:1.6}}>
            <strong style={{color:T.blue}}>Use these clauses in every proposal and contract.</strong> Copy the exact text — these protect your margin, prevent forced cost disclosure, and create clear CO triggers. The lump-sum clause is the most important one.
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            {CONTRACT_CLAUSES.map((c,i)=>(
              <div key={i} style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:11,padding:"18px 20px",position:"relative"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>{c.title}</div>
                    <span style={{fontSize:9,fontWeight:700,background:T.goldBg,color:T.gold,borderRadius:20,padding:"2px 9px",border:`1px solid ${T.goldDim}`}}>{c.tag}</span>
                  </div>
                  <button onClick={()=>copyText(c.text, i)}
                    style={{background:copiedClause===i?T.green:T.bg3,border:`1px solid ${copiedClause===i?T.green:T.subtle}`,borderRadius:8,padding:"5px 12px",fontSize:11,color:copiedClause===i?"white":T.muted,cursor:"pointer",transition:"all 0.2s",flexShrink:0,marginLeft:8}}>
                    {copiedClause===i?"✓ Copied":"Copy"}
                  </button>
                </div>
                <div style={{fontSize:12,color:T.muted,lineHeight:1.7,fontStyle:"italic"}}>
                  "{c.text}"
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* RATE GUIDE TAB */}
      {activeTab==="ranges"&&(
        <div>
          <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:11,overflow:"hidden"}}>
            <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 2fr",background:T.bg2,padding:"10px 16px",fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",borderBottom:`1px solid ${T.subtle}`}}>
              <div>Job Type</div><div style={{textAlign:"center"}}>Markup</div><div style={{textAlign:"center"}}>GC Fee</div><div style={{textAlign:"center"}}>Contingency</div><div>Show Owner As</div>
            </div>
            {PRICING_RANGES.map((r,i)=>(
              <div key={i} onClick={()=>{setJobType(r.type);setMidRange();setActiveTab("calc");}}
                style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 2fr",padding:"12px 16px",borderBottom:`1px solid ${T.subtle}`,cursor:"pointer",transition:"background 0.15s",alignItems:"center"}}
                onMouseEnter={e=>e.currentTarget.style.background=T.bg2}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:r.color,flexShrink:0}}/>
                  <span style={{fontSize:12,fontWeight:600,color:T.text}}>{r.type}</span>
                </div>
                <div style={{textAlign:"center",fontSize:12,fontFamily:"'Space Mono',monospace",color:T.green}}>{r.markup[0]}–{r.markup[1]}%</div>
                <div style={{textAlign:"center",fontSize:12,fontFamily:"'Space Mono',monospace",color:T.teal}}>{r.gcfee[0]}–{r.gcfee[1]}%</div>
                <div style={{textAlign:"center",fontSize:12,fontFamily:"'Space Mono',monospace",color:T.amber}}>{r.contingency[0]}–{r.contingency[1]}%</div>
                <div style={{fontSize:11,color:T.muted}}>{r.display}</div>
              </div>
            ))}
          </div>
          <div style={{marginTop:16,background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:11,padding:"16px 20px"}}>
            <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Core Formula</div>
            <div style={{fontFamily:"'Space Mono',monospace",fontSize:12,color:T.gold,background:T.bg0,borderRadius:8,padding:"12px 16px",lineHeight:2}}>
              Client Price = (Direct Cost × (1 + Markup%)) + GC Fee% + Contingency%
              <br/>
              <span style={{color:T.muted,fontSize:10}}>GC fee applied after markup. Contingency applied after subtotal. Protects margin better than flat stacking.</span>
            </div>
          </div>
        </div>
      )}
    </div>
    {showCSLB&&(
      <CSLBComplianceChecker
        fields={{
          contractorName: (()=>{try{return JSON.parse(localStorage.getItem("grok_company_settings_"+(window._grokCompanyId||"local"))||"{}").company_name||""}catch{return ""}})(),
          cslb:           (()=>{try{return JSON.parse(localStorage.getItem("grok_company_settings_"+(window._grokCompanyId||"local"))||"{}").cslb||""}catch{return ""}})(),
          email:          (()=>{try{return JSON.parse(localStorage.getItem("grok_company_settings_"+(window._grokCompanyId||"local"))||"{}").email||""}catch{return ""}})(),
          phone:          (()=>{try{return JSON.parse(localStorage.getItem("grok_company_settings_"+(window._grokCompanyId||"local"))||"{}").phone||""}catch{return ""}})(),
          address:        (()=>{try{return JSON.parse(localStorage.getItem("grok_company_settings_"+(window._grokCompanyId||"local"))||"{}").location||""}catch{return ""}})(),
          clientName:     clientName,
          clientAddr:     clientAddr,
          contractPrice:  clientPrice,
          depositAmt:     (()=>{
            const price=parseFloat(clientPrice)||0;
            return Math.min(1000, price*0.10).toString();
          })(),
          scope:          scopeLines,
          startDate:      startDate,
          endDate:        endDate,
          paymentSchedule:format,
          hasPermitClause:        scopeLines.toLowerCase().includes("permit"),
          hasCancellationNotice:  true, // GCG template has this
          hasLienNotice:          true, // GCG template has this
          hasCslbNotice:          true, // GCG template has this
          hasSubcontractorDisclosure: scopeLines.toLowerCase().includes("subcontractor")||scopeLines.toLowerCase().includes("sub"),
          hasEmailCancelNotice:   false, // needs to be added to GCG template
        }}
        onClose={()=>setShowCSLB(false)}
      />
    )}
  </React.Fragment>
  );
}


function AIAgentView({session}) {
  const [tab, setTab] = React.useState("quick");
  // Active work product of the current tab, stamped with the tab it came from so
  // ARIASideChat only ever injects the tab the user is actually looking at.
  const [workProduct, setWorkProduct] = React.useState(null);
  const publishWP = wp => setWorkProduct(wp ? {...wp, tab} : null);
  // Blueprint tab is a full-width workspace: ARIA collapses to a dock button that
  // opens as an overlay drawer (it's wired to the drawing analysis, so nothing is
  // lost by keeping it closed by default).
  const isBP = tab === "blueprint";
  const [ariaOpen, setAriaOpen] = React.useState(false);
  const [isMob, setIsMob] = React.useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  React.useEffect(()=>{
    const onResize = () => setIsMob(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Measure the real top of each estimate-layout column so ARIA and the tool
  // content fill the viewport exactly, instead of a hand-tuned calc(100vh-200px).
  // Re-measures when the tab changes so returning from the full-width Blueprint
  // tab restores the correct column height (Blueprint's ARIA drawer is measured
  // too but its bound is deliberately NOT applied — it stays a full-height overlay).
  const contentRef = useRef(null);
  const ariaColRef = useRef(null);
  const contentBound = useViewportBound(contentRef, 16, [tab, isMob]);
  const ariaBound = useViewportBound(ariaColRef, 60, [tab, isMob]);
  // Mobile: the panel stacks at the top of the scroll column, so the desktop
  // measured-bound (60px pad) isn't applied. The fixed bottom nav plus its
  // safe-area eats the last chunk of the viewport, so bound the mobile panel
  // with a deliberately generous 160px reserve — its bottom, and therefore its
  // input row, sits visibly clear of the nav with room to spare. A slightly
  // shorter chat is fine; an unreachable input is not.
  const ariaMobileBound = useViewportBound(ariaColRef, 160, [tab, isMob]);

  const isTrade = session?.accountType === "trade";
  const TABS = [
    {id:"quick",     label:"Quick Estimate",     desc:"Describe job → AI scope + price"},
    ...(!isTrade ? [{id:"estimate", label:"Calc & Proposal", desc:"Manual calculator + proposal"}] : []),
    {id:"blueprint", label:"Blueprint AI",        desc:"Upload drawings for scope review"},
    ...(!isTrade ? [{id:"video",    label:"Video Estimator",  desc:"Film a walkthrough for instant estimate"}] : []),
  ];

  const tabContent = (
    <>
      {tab==="quick"     && <QuickEstimateView session={session} onWorkProduct={publishWP}/>}
      {tab==="estimate"  && <PricingCalculatorView session={session} onWorkProduct={publishWP}/>}
      {tab==="blueprint" && <BlueprintView projects={PROJECTS} session={session} setView={()=>{}} onWorkProduct={publishWP}/>}
      {tab==="video"     && <WalkthroughEstimatorView/>}
    </>
  );

  return (
    <div style={{padding:isMob?"12px":"24px",paddingBottom:isMob?"100px":"24px"}}>
      {/* Header */}
      <div style={{marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:isMob?16:20,fontWeight:800,color:T.text,marginBottom:4}}>AI Tools</div>
          <div style={{background:T.greenBg,border:"1px solid "+T.green+"30",borderRadius:8,padding:"6px 12px",fontSize:11,color:T.green,display:"inline-flex",alignItems:"center",gap:6}}>
            <span>🤖</span> ARIA is with you on every tab — same conversation, same estimate.
          </div>
        </div>
      </div>

      {/* Tab bar — always visible (no chat/content toggle; chat stacks above on mobile). */}
      <div style={{display:"flex",gap:4,marginBottom:16,overflowX:"auto",flexWrap:"nowrap",WebkitOverflowScrolling:"touch",scrollbarWidth:"none",paddingBottom:2}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{padding:isMob?"7px 12px":"8px 18px",borderRadius:9,fontSize:isMob?11:12,fontWeight:600,cursor:"pointer",border:"none",
              background:tab===t.id?"linear-gradient(135deg,"+T.purple+","+T.blue+")":T.bg2,
              color:tab===t.id?"white":T.muted,whiteSpace:"nowrap",flexShrink:0,transition:"all 0.15s"}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Main layout —
          Estimate tabs: ARIA left column (2fr) + tool content right (3fr).
          Blueprint tab: full-width workspace; ARIA collapses to a dock button that
          opens as an overlay drawer. The single <ARIASideChat> keeps a stable tree
          position across tabs (style-swapped, never remounted) so the conversation
          and running estimate survive tab switches. */}
      <div style={isMob
        ? {display:"flex", flexDirection:"column", gap:14, position:"relative"}
        : isBP
          ? {position:"relative", minHeight:"calc(100vh - 180px)"}
          : {display:"grid", gridTemplateColumns:"2fr 3fr", gap:16, alignItems:"start"}}>

        {/* Tool content — full width on Blueprint, right column elsewhere */}
        <div ref={contentRef} style={isBP
          ? {minWidth:0, width:"100%"}
          : isMob
            ? {order:1, minWidth:0}
            : {gridColumn:2, gridRow:1, minWidth:0, maxHeight: contentBound || "calc(100vh - 200px)", overflowY:"auto", paddingRight:4}}>
          {tabContent}
        </div>

        {/* Scrim behind the ARIA drawer (Blueprint tab only) */}
        {isBP && ariaOpen && (
          <div onClick={()=>setAriaOpen(false)} style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.35)", zIndex:59}}/>
        )}

        {/* ARIA — grid column on estimate tabs; overlay drawer on Blueprint */}
        <div ref={ariaColRef} style={isBP
          ? (isMob
              ? {position:"fixed", inset:0, zIndex:60, background:T.bg1, transform:ariaOpen?"none":"translateY(100%)", transition:"transform .2s ease", overflow:"hidden", pointerEvents:ariaOpen?"auto":"none", display:"flex", flexDirection:"column"}
              : {position:"fixed", top:0, right:0, bottom:0, width:ariaOpen?"min(400px, 92vw)":0, zIndex:60, background:T.bg1, borderLeft:ariaOpen?`1px solid ${T.subtle}`:"none", boxShadow:ariaOpen?"-8px 0 28px rgba(0,0,0,0.4)":"none", overflow:"hidden", pointerEvents:ariaOpen?"auto":"none", transition:"width .2s ease"})
          : isMob
            ? {order:0}
            : {gridColumn:1, gridRow:1, minWidth:0, display:"flex", flexDirection:"column", maxHeight: ariaBound || "calc(100vh - 200px)"}}>
          {isBP && ariaOpen && (
            <button onClick={()=>setAriaOpen(false)} title="Close ARIA"
              style={{position:"absolute", top:10, right:12, zIndex:5, background:T.bg2, color:T.text, border:`1px solid ${T.subtle}`, borderRadius:8, width:30, height:30, cursor:"pointer", fontSize:13, fontWeight:800}}>✕</button>
          )}
          <ARIASideChat session={session} tab={tab} compact={isMob} docked={isBP} bound={!isBP && !isMob ? ariaBound : null} mobileBound={isMob && !isBP ? ariaMobileBound : null} workProduct={workProduct && workProduct.tab===tab ? workProduct : null}/>
        </div>

        {/* Blueprint: floating "Ask ARIA" dock when the drawer is closed */}
        {isBP && !ariaOpen && (
          <button onClick={()=>setAriaOpen(true)}
            style={{position:"fixed", right:isMob?16:22, bottom:isMob?110:26, zIndex:55,
              background:"linear-gradient(135deg,"+T.gold+","+T.gold2+")", color:T.bg0, border:"none", borderRadius:24,
              padding:"11px 18px", fontSize:12.5, fontWeight:800, cursor:"pointer", display:"flex", alignItems:"center", gap:7,
              boxShadow:"0 6px 20px rgba(0,0,0,0.35)"}}>
            💬 Ask ARIA
            {workProduct && workProduct.tab===tab && <span style={{width:7,height:7,borderRadius:"50%",background:T.green,boxShadow:`0 0 6px ${T.green}`}}/>}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── ARIA SIDE CHAT ──────────────────────────────────────────────────────────
// Persistent sidebar that lives on the left of every AI Tools sub-tab. Same
// conversation thread across Quick Estimate / Calc & Proposal / Blueprint AI
// / Video Estimator. Tab-aware system prompt — ARIA changes how she frames
// help based on which sub-tab the GC is currently looking at, but the chat
// history and the running line-items list are shared.

// Turn the active tab's work product (a generated estimate, calculator snapshot,
// or blueprint analysis) into a system-context block so ARIA can answer
// questions about "this estimate" / "this drawing" directly. One shared shape:
// { kind, text, meta } — see the onWorkProduct publishers in QuickEstimateView /
// PricingCalculatorView / BlueprintView.
function formatWorkProductContext(wp) {
  if (!wp || !wp.text) return "";
  const label =
    wp.kind === "blueprint" ? "the construction drawing the user is currently viewing (AI-extracted analysis)"
    : wp.kind === "calc"    ? "the estimate the user is currently building in the Calc & Proposal calculator"
    : "the estimate the user just generated in Quick Estimate";
  const meta = wp.meta
    ? Object.entries(wp.meta).filter(([, v]) => v != null && v !== "").map(([k, v]) => `${k}: ${v}`).join(" · ")
    : "";
  return `--- ACTIVE WORK PRODUCT: ${label} ---
${meta ? meta + "\n" : ""}${wp.text}
--- END WORK PRODUCT ---
When the user says "this", "the estimate", "the drawing", "this line item", "why is X priced that way", "page N", etc., they mean the work product above. Answer using it directly and cite the specific numbers, line items, or code sections from it.
`;
}

function ARIASideChat({session, tab, compact, workProduct, docked, bound, mobileBound}) {
  const T = useTheme();
  const cid = session?.companyId || window._grokCompanyId || "local";
  const storageKey = "grok_aria_estimate_" + cid;

  // Load persisted session so a refresh doesn't lose work.
  const initial = React.useMemo(() => {
    try {
      const s = JSON.parse(localStorage.getItem(storageKey) || "null");
      if (s) return s;
    } catch {}
    return {messages: [], items: [], pendingImages: []};
  }, [storageKey]);

  const [messages, setMessages] = React.useState(initial.messages || []);
  const [items, setItems] = React.useState(initial.items || []);
  const [pendingImages, setPendingImages] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState("");
  const fileRef = React.useRef(null);
  const scrollRef = React.useRef(null);

  // Persist on every change so users don't lose their estimate session.
  React.useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify({messages, items})); } catch {}
  }, [messages, items, storageKey]);

  // Auto-scroll to bottom on new message.
  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const total = items.reduce((s, i) => s + (Number(i.total) || 0), 0);
  const fmt = (n) => "$" + Math.round(n || 0).toLocaleString();

  // Add files to the next user message. Held as base64 data URLs.
  const onPickImages = (fileList) => {
    if (!fileList?.length) return;
    Array.from(fileList).forEach(file => {
      if (!file.type.startsWith("image/")) return;
      const reader = new FileReader();
      reader.onload = e => setPendingImages(p => [...p, {
        id: Date.now() + "_" + Math.random().toString(36).slice(2,5),
        name: file.name,
        mediaType: file.type,
        dataUrl: e.target.result,
      }]);
      reader.readAsDataURL(file);
    });
  };

  const removePendingImage = (id) => setPendingImages(p => p.filter(i => i.id !== id));

  const resetSession = () => {
    if (!window.confirm("Clear the chat and the running estimate? This can't be undone.")) return;
    setMessages([]); setItems([]); setPendingImages([]); setInput(""); setError("");
    try { localStorage.removeItem(storageKey); } catch {}
  };

  // Send the input + pending images to ARIA. AI returns {reply, addItems,
  // updateItem, removeItem} — apply ops to the running list.
  const send = async () => {
    if (sending) return;
    const text = (input || "").trim();
    if (!text && !pendingImages.length) return;
    if (!enforceLimit("estimates", session)) return;

    const userMsg = {
      role: "user",
      text,
      images: pendingImages,
      ts: new Date().toISOString(),
    };
    const nextMsgs = [...messages, userMsg];
    setMessages(nextMsgs);
    setInput("");
    setPendingImages([]);
    setSending(true);
    setError("");

    const itemsSummary = items.length
      ? items.map((i, idx) => `${idx+1}. ${i.trade ? "["+i.trade+"] " : ""}${i.description} — ${i.qty||1} ${i.unit||"ea"} @ ${fmt(i.unitPrice)} = ${fmt(i.total)}`).join("\n")
      : "(no items yet)";

    const TAB_CTX = {
      quick:     "Tab: Quick Estimate. Help the user build line items by trade and scope. When they ask 'what should I charge for X?' give a Bay Area number and ALSO add the item via addItems. When they say 'add Y' just add it.",
      estimate:  "Tab: Calc & Proposal. The user is using the pricing calculator. Help them think through markup %, GC fee %, overhead, profit, contingency. You can still add/edit estimate line items via ops — they share with the other tabs.",
      blueprint: "Tab: Blueprint AI. The user is reviewing construction drawings. Help them interpret what's in the drawing, suggest scope items based on what they describe seeing, and answer code/material questions. Items you add via addItems show up on every tab.",
      video:     "Tab: Video Estimator. The user is reviewing a walkthrough video. Help them turn what they see in the video into priced line items. addItems based on rooms/conditions they describe.",
    };
    const tabContext = TAB_CTX[tab] || TAB_CTX.quick;

    const systemPrompt = `You are ARIA, a senior California GC estimator. The user moves between four AI Tools tabs (Quick Estimate, Calc & Proposal, Blueprint AI, Video Estimator) — you stay with them as a persistent assistant. Same conversation thread, same shared line-items list across all tabs.

Each reply must be helpful, specific, and grounded in 2026 SF Bay Area market rates.

You return TWO things in every reply:
1. A short conversational message to the user (1-4 sentences max).
2. A structured JSON ops block describing changes to the running line-item list (addItems / updateItem / removeItem).

${tabContext}
${formatWorkProductContext(workProduct)}
The current shared running estimate has these items:
${itemsSummary}
Current total: ${fmt(total)}

When the user asks for a price or to add work, give a realistic Bay Area number and include items to add. When they say "change", "update", "make it $X", "remove item 3" etc., use updateItem or removeItem ops.

Reply in EXACTLY this format — no preamble, no closing:

<chat>
Your short reply to the user.
</chat>

<ops>
{
  "addItems": [{"description":"...", "qty":1, "unit":"ea|sf|lf|hr|bath|...", "unitPrice":0, "trade":"Electrical|Plumbing|...", "notes":""}],
  "updateItem": {"index":0, "patch":{...}},
  "removeItem": null
}
</ops>

Use null for unused op keys. addItems can be empty array. unitPrice and total are integers (no commas, no $). All three keys present every time.`;

    // Build the Anthropic messages array from chat history (with images on
    // the latest user message only).
    const history = nextMsgs.map((m, idx) => {
      if (m.role !== "user") return {role: "assistant", content: m.text || ""};
      // For the LATEST user message only, attach images.
      const isLatest = idx === nextMsgs.length - 1;
      const blocks = [];
      if (isLatest && m.images?.length) {
        m.images.forEach(img => {
          const b64 = (img.dataUrl || "").split(",")[1] || "";
          if (b64) blocks.push({type:"image", source:{type:"base64", media_type: img.mediaType || "image/jpeg", data: b64}});
        });
      }
      blocks.push({type:"text", text: m.text || ""});
      return {role:"user", content: blocks};
    });

    try {
      const resp = await fetch(AI_PROXY_URL, {
        method: "POST",
        headers: {
          "Content-Type":"application/json",
          "Authorization":"Bearer "+sb._token(),
          "apikey":SUPABASE_ANON_KEY,
          "anthropic-version":"2023-06-01",
          "anthropic-dangerous-direct-browser-access":"true",
        },
        body: JSON.stringify({
          max_tokens: 2000,
          temperature: 0, // deterministic estimate generation — identical inputs → identical numbers
          usage_type: "estimates",
          company_id: cid,
          plan: session?.plan || "starter",
          system: systemPrompt,
          messages: history,
        }),
      });
      const raw = await resp.text();
      let data;
      try { data = JSON.parse(raw); } catch { throw new Error("Non-JSON from proxy: " + raw.slice(0,150)); }
      if (!resp.ok || data?.error) throw new Error(errString(data?.error) || ("HTTP " + resp.status));
      const text = data?.content?.[0]?.text || "";
      if (!text) throw new Error("Empty AI response");

      // Parse <chat>...</chat> and <ops>...</ops> blocks.
      const chatMatch = text.match(/<chat>([\s\S]*?)<\/chat>/i);
      const opsMatch = text.match(/<ops>([\s\S]*?)<\/ops>/i);
      const reply = (chatMatch ? chatMatch[1] : text).trim();

      let ops = null;
      if (opsMatch) {
        try { ops = JSON.parse(opsMatch[1].trim()); } catch {}
      }

      // Apply ops to running list.
      let nextItems = [...items];
      if (ops?.addItems?.length) {
        ops.addItems.forEach(it => {
          const qty = Number(it.qty) || 1;
          const unitPrice = Number(it.unitPrice) || 0;
          nextItems.push({
            id: "li_" + Date.now() + "_" + Math.random().toString(36).slice(2,5),
            description: it.description || "(unspecified)",
            qty,
            unit: it.unit || "ea",
            unitPrice,
            total: qty * unitPrice,
            trade: it.trade || "",
            notes: it.notes || "",
          });
        });
      }
      if (ops?.updateItem && Number.isInteger(ops.updateItem.index)) {
        const i = ops.updateItem.index;
        if (i >= 0 && i < nextItems.length) {
          const patched = {...nextItems[i], ...ops.updateItem.patch};
          const qty = Number(patched.qty) || 1;
          const unitPrice = Number(patched.unitPrice) || 0;
          nextItems[i] = {...patched, qty, unitPrice, total: qty * unitPrice};
        }
      }
      if (ops?.removeItem != null) {
        const i = Number(ops.removeItem);
        if (i >= 0 && i < nextItems.length) nextItems.splice(i, 1);
      }
      setItems(nextItems);

      setMessages([...nextMsgs, {role:"assistant", text: reply, ts:new Date().toISOString(), opsApplied: ops}]);
    } catch (e) {
      setError(e?.message || "Send failed");
      // Drop the user message back to the input so they can retry.
      setMessages(messages);
      setInput(text);
      setPendingImages(userMsg.images || []);
    }
    setSending(false);
  };

  // Manual line-item edit: in-place update on the running list. Recomputes total.
  const updateItemField = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const patched = {...it, [field]: field === "qty" || field === "unitPrice" ? Number(value) || 0 : value};
      patched.total = (patched.qty || 1) * (patched.unitPrice || 0);
      return patched;
    }));
  };
  const removeItemAt = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  // Export the chat → estimate as a print-ready window. User picks PDF via browser.
  const exportEstimate = () => {
    if (!items.length) { alert("Add some line items first."); return; }
    const w = window.open("", "_blank");
    if (!w) { alert("Allow popups to export."); return; }
    const today = new Date().toLocaleDateString("en-US", {year:"numeric", month:"long", day:"numeric"});
    const companyName = (() => {
      try { return JSON.parse(localStorage.getItem("grok_company_settings_"+cid)||"{}").company_name; } catch { return null; }
    })() || session?.companyName || "Grok Construction Group";
    const rows = items.map(i => `
      <tr>
        <td>${(i.trade?`<strong>${i.trade}</strong> · `:"") + (i.description||"")}</td>
        <td style="text-align:right">${i.qty||1} ${i.unit||"ea"}</td>
        <td style="text-align:right">${fmt(i.unitPrice)}</td>
        <td style="text-align:right"><strong>${fmt(i.total)}</strong></td>
      </tr>`).join("");
    w.document.write(`<!DOCTYPE html><html><head><title>Estimate</title>
      <style>
        body{font-family:Arial,sans-serif;padding:36px;max-width:820px;margin:0 auto;color:#1a1a1a}
        h1{color:#d4a017;font-size:22px;margin:0 0 4px;letter-spacing:.02em}
        .meta{color:#666;font-size:11px;margin-bottom:24px;border-bottom:2px solid #d4a017;padding-bottom:14px}
        table{width:100%;border-collapse:collapse;margin-bottom:18px}
        th{background:#f5f0e8;font-size:11px;text-transform:uppercase;letter-spacing:.06em;text-align:left;padding:9px 10px;border-bottom:2px solid #d4a017}
        td{padding:9px 10px;border-bottom:1px solid #eee;font-size:13px;vertical-align:top}
        .total-row{background:#f6f5ef;font-size:16px;font-weight:800;color:#d4a017}
        .footer{font-size:10px;color:#888;margin-top:30px;padding-top:14px;border-top:1px solid #eee}
        @media print{body{padding:18px}}
      </style></head><body>
      <h1>Construction Estimate</h1>
      <div class="meta">${companyName} · ${today}</div>
      <table>
        <thead><tr><th>Scope</th><th style="text-align:right">Qty</th><th style="text-align:right">Unit Price</th><th style="text-align:right">Total</th></tr></thead>
        <tbody>${rows}
          <tr class="total-row"><td colspan="3" style="text-align:right">TOTAL</td><td style="text-align:right">${fmt(total)}</td></tr>
        </tbody>
      </table>
      <div class="footer">Built collaboratively with ARIA. 2026 SF Bay Area pricing. Subject to standard contract terms — license, insurance, and warranty per CSLB requirements.</div>
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  // Items panel collapse state — expanded if there are items, collapsed when empty.
  const [itemsOpen, setItemsOpen] = React.useState(false);
  React.useEffect(() => { if (items.length > 0) setItemsOpen(true); }, [items.length]);

  // Tab label for the header subtitle so the user knows ARIA is context-aware.
  const TAB_LABEL = {
    quick: "Quick Estimate", estimate: "Calc & Proposal",
    blueprint: "Blueprint AI", video: "Video Estimator",
  };

  return (
    <div style={{background:T.bg1,border:docked?"none":"1px solid "+T.subtle,borderRadius:docked?0:12,overflow:"hidden",display:"flex",flexDirection:"column",height: docked ? "100%" : compact ? (mobileBound ? `min(45vh, 460px, ${mobileBound})` : "min(45vh, 460px)") : (bound || "calc(100vh - 200px)"),minHeight: docked ? 0 : compact ? 240 : 480}}>
      {/* Header */}
      <div style={{padding:"10px 12px",borderBottom:"1px solid "+T.subtle,display:"flex",justifyContent:"space-between",alignItems:"center",background:T.bg2,gap:6}}>
        <div style={{minWidth:0,flex:1}}>
          <div style={{fontSize:12,fontWeight:800,color:T.text,display:"flex",alignItems:"center",gap:6}}>🤖 ARIA</div>
          <div style={{fontSize:9,color:T.muted,marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
            On: <strong style={{color:T.gold}}>{TAB_LABEL[tab]||"AI Tools"}</strong> · same thread everywhere
          </div>
        </div>
        <button onClick={resetSession} disabled={!messages.length && !items.length}
          title="Clear conversation + estimate"
          style={{fontSize:10,fontWeight:700,background:"transparent",border:"1px solid "+T.subtle,color:T.muted,borderRadius:7,padding:"4px 8px",cursor:(messages.length||items.length)?"pointer":"default",opacity:(messages.length||items.length)?1:0.4,flexShrink:0}}>
          🗑
        </button>
      </div>

      {/* Active-context chip — tells the user ARIA can see the current tab's work product */}
      {workProduct && (
        <div style={{flexShrink:0,padding:"5px 12px",background:T.greenBg,borderBottom:`1px solid ${T.green}30`,display:"flex",alignItems:"center",gap:6}}>
          <span style={{width:6,height:6,borderRadius:"50%",background:T.green,flexShrink:0,boxShadow:`0 0 5px ${T.green}`}}/>
          <span style={{fontSize:10,color:T.text,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
            ARIA can see {workProduct.kind==="blueprint"?"this drawing's analysis":workProduct.kind==="calc"?"your calculator estimate":"this estimate"} — ask about it
          </span>
        </div>
      )}

      {/* Items panel (collapsible) */}
      <div style={{background:`linear-gradient(135deg,${T.gold}14,${T.gold}04)`,borderBottom:`1px solid ${T.gold}30`,flexShrink:0}}>
        <button onClick={()=>setItemsOpen(o=>!o)}
          style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",background:"transparent",border:"none",cursor:"pointer",textAlign:"left"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:10,color:T.muted,fontWeight:700}}>{itemsOpen?"▾":"▸"}</span>
            <span style={{fontSize:10,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:".07em"}}>Running Estimate</span>
            <span style={{fontSize:9,color:T.muted}}>· {items.length} item{items.length===1?"":"s"}</span>
          </div>
          <span style={{fontSize:15,fontWeight:900,color:T.gold,fontVariantNumeric:"tabular-nums"}}>{fmt(total)}</span>
        </button>
        {itemsOpen && items.length > 0 && (
          <div style={{maxHeight:200,overflowY:"auto",padding:"0 10px 8px"}}>
            {items.map((it, idx) => (
              <div key={it.id||idx} style={{borderBottom:`1px solid ${T.subtle}`,padding:"7px 0",fontSize:11}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:6,marginBottom:3}}>
                  <div style={{flex:1,minWidth:0}}>
                    {it.trade && <span style={{fontSize:9,fontWeight:800,color:T.gold,background:T.goldBg,padding:"1px 5px",borderRadius:6,marginRight:5}}>{it.trade}</span>}
                    <span style={{color:T.text}}>{it.description}</span>
                  </div>
                  <button onClick={()=>removeItemAt(idx)} title="Remove"
                    style={{background:"transparent",border:"none",color:T.muted,cursor:"pointer",fontSize:13,padding:0,lineHeight:1,flexShrink:0}}>×</button>
                </div>
                <div style={{display:"flex",gap:4,alignItems:"center",fontSize:10,color:T.muted}}>
                  <input type="number" value={it.qty} onChange={e=>updateItemField(idx,"qty",e.target.value)}
                    style={{width:44,background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:5,padding:"2px 4px",color:T.text,fontSize:10,fontFamily:"inherit"}}/>
                  <input value={it.unit} onChange={e=>updateItemField(idx,"unit",e.target.value)}
                    style={{width:40,background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:5,padding:"2px 4px",color:T.text,fontSize:10,fontFamily:"inherit"}}/>
                  <span>@</span>
                  <input type="number" value={it.unitPrice} onChange={e=>updateItemField(idx,"unitPrice",e.target.value)}
                    style={{width:70,background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:5,padding:"2px 4px",color:T.text,fontSize:10,fontFamily:"inherit"}}/>
                  <span style={{marginLeft:"auto",fontWeight:800,color:T.text,fontVariantNumeric:"tabular-nums"}}>{fmt(it.total)}</span>
                </div>
              </div>
            ))}
            <button onClick={exportEstimate}
              style={{width:"100%",marginTop:8,background:T.gold,color:T.bg0,border:"none",borderRadius:7,padding:"7px 10px",fontSize:11,fontWeight:800,cursor:"pointer"}}>
              📄 Export as PDF
            </button>
          </div>
        )}
      </div>

      {/* Message list */}
      <div ref={scrollRef} style={{flex:1,overflowY:"auto",padding:"12px 12px 6px",display:"flex",flexDirection:"column",gap:9,minHeight:0}}>
        {!messages.length && (
          <div style={{textAlign:"center",color:T.muted,fontSize:11,lineHeight:1.7,padding:"30px 18px"}}>
            <div style={{fontSize:26,marginBottom:8,opacity:0.5}}>💬</div>
            <div style={{fontWeight:700,color:T.text,marginBottom:5}}>Talk to ARIA</div>
            <div>I stay with you across all four tabs. Try: <em>"what's electrical rough-in for 3 baths?"</em> or attach a photo and tell me what you see.</div>
          </div>
        )}
        {messages.map((m, idx) => (
          <div key={idx} style={{display:"flex",justifyContent:m.role==="user"?"flex-end":"flex-start"}}>
            <div style={{maxWidth:"86%",background:m.role==="user"?T.gold:T.bg2,color:m.role==="user"?T.bg0:T.text,padding:"9px 13px",borderRadius:11,fontSize:12.5,lineHeight:1.6,whiteSpace:m.role==="user"?"pre-wrap":"normal"}}>
              {m.images?.length > 0 && (
                <div style={{display:"flex",gap:5,marginBottom:5,flexWrap:"wrap"}}>
                  {m.images.map(img => (
                    <img key={img.id} src={img.dataUrl} alt="" style={{width:64,height:64,objectFit:"cover",borderRadius:5,border:"1px solid rgba(0,0,0,0.1)"}}/>
                  ))}
                </div>
              )}
              {m.role==="user"?m.text:<MarkdownLite text={m.text}/>}
              {m.opsApplied?.addItems?.length > 0 && (
                <div style={{fontSize:9,color:m.role==="user"?T.bg0:T.muted,opacity:0.7,marginTop:4,paddingTop:4,borderTop:`1px solid ${m.role==="user"?"rgba(0,0,0,0.15)":T.subtle}`}}>
                  ✓ Added {m.opsApplied.addItems.length} item{m.opsApplied.addItems.length===1?"":"s"}
                </div>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div style={{display:"flex",justifyContent:"flex-start"}}>
            <div style={{background:T.bg2,padding:"8px 11px",borderRadius:11,fontSize:11,color:T.muted}}>
              <Spin/> ARIA is thinking…
            </div>
          </div>
        )}
        {error && (
          <div style={{background:T.redBg,color:T.red,padding:"7px 10px",borderRadius:7,fontSize:11,fontWeight:600}}>⚠ {error}</div>
        )}
      </div>

      {/* Pending images */}
      {pendingImages.length > 0 && (
        <div style={{padding:"6px 12px",borderTop:"1px solid "+T.subtle,background:T.bg2,display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
          <span style={{fontSize:10,color:T.muted,fontWeight:700,marginRight:3}}>Attaching:</span>
          {pendingImages.map(img => (
            <div key={img.id} style={{position:"relative"}}>
              <img src={img.dataUrl} alt="" style={{width:38,height:38,objectFit:"cover",borderRadius:5,border:"1px solid "+T.subtle}}/>
              <button onClick={()=>removePendingImage(img.id)} title="Remove"
                style={{position:"absolute",top:-4,right:-4,background:T.red,color:"white",border:"none",borderRadius:"50%",width:14,height:14,fontSize:9,cursor:"pointer",lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* Input — no chat-input-row nav clearance here: the panel is height-bounded
          (desktop measured-bound / mobile mobileBound) so its own bottom already
          clears the fixed nav; the 80px inner padding would just add dead space. */}
      <div style={{padding:"8px 10px",borderTop:"1px solid "+T.subtle,display:"flex",gap:5,alignItems:"flex-end",background:T.bg2,flexShrink:0}}>
        <input ref={fileRef} type="file" accept="image/*" multiple style={{display:"none"}}
          onChange={e => { onPickImages(e.target.files); e.target.value = ""; }}/>
        <button onClick={()=>fileRef.current?.click()} disabled={sending} title="Attach photos"
          style={{background:T.bg1,border:"1px solid "+T.subtle,color:T.muted,borderRadius:7,padding:"7px 9px",cursor:sending?"default":"pointer",fontSize:13,lineHeight:1,flexShrink:0}}>📎</button>
        <textarea value={input} onChange={e=>setInput(e.target.value)}
          placeholder="Ask ARIA anything…"
          rows={1}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          style={{flex:1,background:T.bg1,border:"1px solid "+T.subtle,borderRadius:7,padding:"7px 10px",color:T.text,fontSize:12,fontFamily:"inherit",resize:"none",minHeight:34,maxHeight:110}}/>
        <button onClick={send} disabled={sending||(!input.trim()&&!pendingImages.length)}
          style={{background:sending?T.bg3:T.gold,color:sending?T.muted:T.bg0,border:"none",borderRadius:7,padding:"7px 11px",fontSize:12,fontWeight:800,cursor:(sending||(!input.trim()&&!pendingImages.length))?"default":"pointer",flexShrink:0}}>
          Send
        </button>
      </div>
    </div>
  );
}

// ─── QUICK ESTIMATE VIEW ─────────────────────────────────────────────────────
// Describe the job in plain English → AI generates scope + full estimate
// Perfect for small-medium jobs where you don't have drawings yet
function QuickEstimateView({session, onWorkProduct}) {
  const [desc, setDesc] = React.useState("");
  const [jobType, setJobType] = React.useState("SRO / Occupied Rehab");
  const [addr, setAddr] = React.useState("");
  const [units, setUnits] = React.useState("");
  const [result, setResult] = React.useState("");

  // Publish the generated estimate to ARIA's context (Quick Estimate tab). The
  // result is already Markdown with scope / tier / line items / total.
  React.useEffect(() => {
    if (!onWorkProduct) return;
    onWorkProduct(result ? { kind: "quick-estimate", text: result, meta: { job: jobType, address: addr, units } } : null);
  }, [result]); // eslint-disable-line
  const [loading, setLoading] = React.useState(false);
  const [saved, setSaved] = React.useState([]);
  const [tab, setTab] = React.useState("estimate");
  const [subMode, setSubMode] = React.useState(!!session?.isSub || session?.accountType==="trade");
  const [isMob, setIsMob] = React.useState(window.innerWidth < 768);
  React.useEffect(()=>{
    const h=()=>setIsMob(window.innerWidth<768);
    window.addEventListener("resize",h);return()=>window.removeEventListener("resize",h);
  },[]);

  const EXAMPLES = [
    "Full bathroom gut and replace in 2 SRO units. Demo existing tile, new waterproof membrane, 4x16 subway tile walls and floor, new toilet, vanity, fixtures. Occupied building.",
    "Kitchen remodel — remove wall between kitchen and living room, new shaker cabinets, quartz counters, LVP flooring throughout 450 sqft unit.",
    "Paint entire 12-unit apartment building interior — walls, ceilings, trim. All units occupied, need to coordinate with tenants.",
    "Replace all plumbing fixtures in 6-unit building — toilets, faucets, shower valves. No demo, direct replacement only.",
    "Roof replacement 3,200 sqft flat roof. Remove existing 2-ply, install new TPO membrane, new drains and flashing.",
  ];

  const GC_JOB_TYPES = [
    "SRO / Occupied Rehab","Multi-Family Apartment","Single Family Residential",
    "ADU / Garage Conversion","Commercial TI","Kitchen Renovation",
    "Bathroom Renovation","Exterior / Envelope","Roofing","Flooring",
  ];
  const TRADE_JOB_TYPES = [
    "Electrical — Residential","Electrical — Commercial","Plumbing — Residential",
    "Plumbing — Commercial","HVAC Install","HVAC Service","Framing & Rough Carpentry",
    "Drywall & Plaster","Tile & Stone","Flooring Install","Painting — Interior",
    "Painting — Exterior","Roofing","Concrete & Flatwork","Insulation",
    "Fire Sprinkler","Low Voltage / Data","Kitchen Renovation","Bathroom Renovation","Custom Scope",
  ];
  const JOB_TYPES = session?.accountType==="trade" ? TRADE_JOB_TYPES : GC_JOB_TYPES;

  const run = async () => {
    if (!desc.trim()) return;
    if (!enforceLimit("estimates", session)) return;
    setLoading(true); setResult("");

    const isTrade = session?.accountType === "trade";
    const sys = subMode
      ? `You are an experienced trade contractor (electrician, plumber, HVAC tech, etc.) preparing a sub-bid for a general contractor.

RULES FOR SUB BID:
1. Show ONLY your trade scope — labor and materials for your specific work
2. NO general conditions, NO project management fees, NO GC overhead
3. NO permit coordination fees (GC handles permits)
4. NO contingency unless specifically requested
5. NO supervision or site cleanup (GC handles)
6. Break out labor and materials clearly — GCs need this for their own markup
7. Be specific on quantities, hours, and unit costs where relevant
8. Tone: professional trade contractor giving a clean number to a GC`
      : isTrade
      ? `You are an experienced trade contractor (electrician, plumber, HVAC, painter, etc.) preparing a direct-to-owner quote.

RULES FOR TRADE DIRECT-TO-OWNER QUOTE:
1. Include your labor, materials, and a reasonable profit margin — but NO GC fee layer
2. You ARE the prime contractor on this scope — price accordingly
3. Include permit coordination if you pull permits for your trade
4. Include a modest contingency (5-8%) for your own risk
5. Present professionally — "Project Total" not "Investment Summary"
6. Show 2-4 line items max — labor, materials, any allowances
7. Tone: professional licensed trade contractor quoting directly to a property owner
8. Price confidently — licensed, insured, warranted trade work commands fair pricing
9. Do NOT add GC overhead, general conditions, or project management fees`
      : `You are a senior estimator for a licensed Bay Area general contractor. You write transparent, competitive proposals that win small-residential and remodel work — not inflated cost sheets that get talked down.

${PRICING_RULES}

TONE: confident, plain-spoken, no marketing fluff. Itemize what you're doing so the owner can see exactly what they're paying for. A door conversion is not a "complete project delivery system" — it's a door conversion.`
    const contingencyPct = 10; // legacy — only used if you decide tier C scope justifies it

    const msg = subMode ? [
      "Trade: "+jobType+(units?" · "+units:"")+(addr?" · "+addr:""),
      "Scope: "+desc,
      "",
      "Reply in EXACTLY this format:",
      "",
      "## SUB-BID ESTIMATE",
      "",
      "**Labor**",
      "• [Specific task description] — [X hrs @ $XX/hr] ........... $X,XXX",
      "• [Add all labor line items for your trade only]",
      "",
      "**Materials**",
      "• [Specific material, qty, unit cost] ........... $X,XXX",
      "• [List all materials needed]",
      "",
      "## TOTAL SUB-BID",
      "$XX,XXX",
      "",
      "## SCOPE INCLUDES",
      "✓ [What is included in this sub-bid]",
      "",
      "## EXCLUSIONS",
      "- GC overhead, supervision, permit fees",
      "- [Any other exclusions specific to your trade]",
      "",
      "## NOTES",
      "[Any important conditions, access requirements, or assumptions]",
      "",
      "2026 SF Bay Area trade pricing. Labor and materials only — no GC markup.",
    ].filter(Boolean).join("\n") : [
      "Job: "+jobType+(units?" · "+units:"")+(addr?" · "+addr:""),
      "Scope: "+desc,
      "",
      "Decide the project TIER per the rules above, then reply in EXACTLY this format:",
      "",
      "## SCOPE",
      "[2–4 sentences describing what's actually being built. Plain language.]",
      "",
      "## TIER",
      "[TIER A | TIER B | TIER C] — one-line rationale on why.",
      "",
      "## LINE ITEMS",
      "[Itemize per the LINE-ITEM STRUCTURE rules. ALWAYS separate lines for: demo, framing (if any), trade install labor, waterproofing (if exterior), stucco patch (if exterior), interior drywall/paint (if interior touched), PM/supervision/warranty. Each on its own row with a dollar amount. Don't bundle.]",
      "| Item | Description | Amount |",
      "| ---- | ----------- | ------:|",
      "| ... | ... | $X,XXX |",
      "",
      "## SUBTOTAL",
      "$XX,XXX",
      "",
      "## OPTIONAL CONTINGENCY",
      "[ONLY render this section if concealed-condition risk is detected per the rules. Otherwise omit ENTIRELY. When rendered:]",
      "Optional contingency (concealed conditions, e.g. pre-1978 framing behind stucco) — billed only if used, unused credited to Owner: $X,XXX",
      "",
      "## TOTAL",
      "$XX,XXX",
      "(If optional contingency was rendered, also show: 'or $X,XXX with optional contingency')",
      "",
      "## WHAT'S INCLUDED",
      "[3–5 specific bullets. Licensed/insured, warranty period, anything trade-specific.]",
      "",
      "## EXCLUSIONS",
      "[2–4 bullets MAX. ALWAYS include 'Door & hardware (Owner-supplied or separately quoted)' when relevant. Permit fees: pick ONE — 'included' OR 'by Owner, estimated $X' — never both. Don't pad with generic exclusions.]",
      "",
      "## SCHEDULE",
      "Estimated duration: [X days for TIER A, X weeks for TIER B/C] | Start upon signed contract" + (jobType.includes("SRO") || jobType.includes("Multi-Family") ? " & permit issuance" : ""),
      "",
      "2026 SF Bay Area pricing. Transparent itemization — owner sees exactly what they're paying for.",
    ].filter(Boolean).join("\n");

    try {
      const data = await sb.ai(
        [{role:"user", content:sys+"\n\n"+msg}],
        {max_tokens:2500, temperature:0, usage_type:"estimates", company_id:session?.companyId, plan:session?.plan||"starter"}
      );
      if (data.error) { setResult("Error: "+(data.error.message||JSON.stringify(data.error))); setLoading(false); return; }
      if (!data.content?.[0]?.text) { setResult("No response received. Please try again."); setLoading(false); return; }
      setResult(data.content[0].text);
    } catch(e) {
      setResult("Error: "+e.message);
    }
    setLoading(false);
  };

  const buildProposalFromEstimate = async () => {
    // Use the shared findCompanyTemplate helper — prefers a template whose
    // filename contains "proposal", falls back to any cat:"proposal" or
    // cat:"contracts" template. Works across the original GCG templates and
    // user-uploaded ones regardless of how they were categorized.
    const tpl = findCompanyTemplate(["proposal", "contracts"], "proposal");
    console.log("Quick Estimate template search →", tpl?.fileName || "no template found, will use HTML fallback");
    if (!tpl) {
      // Fall back to HTML if no template found
      const w=window.open("","_blank"); if(!w){alert("Allow popups.");return;}
      w.document.write(`<!DOCTYPE html><html><head><title>Proposal</title><style>body{font-family:Arial,sans-serif;padding:40px;max-width:800px;margin:0 auto}pre{white-space:pre-wrap;line-height:1.7;font-size:13px}</style></head><body><h2>${jobType} — Proposal</h2><pre>${result}</pre></body></html>`);
      w.document.close(); setTimeout(()=>w.print(),400); return;
    }
    if (!window.JSZip) {
      await new Promise((res,rej)=>{const s=document.createElement("script");s.src="https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";s.onload=res;s.onerror=rej;document.head.appendChild(s);});
    }
    const b64=tpl.data.split(",")[1]; const bin=atob(b64); const bytes=new Uint8Array(bin.length);
    for(let i=0;i<bin.length;i++) bytes[i]=bin.charCodeAt(i);
    const zip=await window.JSZip.loadAsync(bytes);
    let xml=await zip.file("word/document.xml").async("string");
    const esc=v=>String(v||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
    const today=new Date().toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"});
    const exp=new Date(Date.now()+30*86400000).toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"});
    const totalMatch=result.match(/\$([0-9,]+)/g);
    const totalAmt=totalMatch?totalMatch[totalMatch.length-1]:"TBD";
    const refId=(jobType||"PROP").replace(/[^A-Z0-9]/gi,"").slice(0,4).toUpperCase()+"-"+Date.now().toString().slice(-4);
    const scopeMatch=result.match(/(?:##\s*SCOPE|SCOPE\s*OF\s*WORK)[\s\S]*?(?=##|$)/i);
    const scopeText=scopeMatch?scopeMatch[0].replace(/##.*\n/,"").trim().split("\n").filter(l=>l.trim()&&!l.startsWith("#")).slice(0,8).map(l=>"• "+l.replace(/^\d+\.\s*/,"").trim()).join("  |  "):"Per estimate attached.";
    // Fill specific cells by index — based on GCG_Proposal.docx cell map
    // Row 1: C1=Proposal No., C2=Date, C3=Valid Until, C4=Total Price (labels that also hold data)
    // Row 2: C6=Client info (empty)
    // Row 4: C10=Scope description (empty), C11=$ amount
    // Row 5: C13=Total $ 
    // Row 6-8: C14,C15,C16=Exclusions
    const FILL={
      1: refId + "\n" + "Proposal No.",   // append to label cell
      2: today,
      3: exp,
      4: totalAmt,
      6: (addr||"Client") + "\n" + (jobType||""),
      10: scopeText,
      11: totalAmt,
      13: totalAmt,
      14: "Permits unless otherwise noted",
      15: "Owner-furnished materials",
      16: "Items not listed in scope",
    };
    let ci=0;
    xml=xml.replace(/<w:tc>([\s\S]*?)<\/w:tc>/g,(match)=>{
      const idx=ci++;
      if(FILL[idx]!==undefined){
        const val=FILL[idx];
        return match.replace(/(<w:p\b[^>]*>)(\s*<w:pPr>[\s\S]*?<\/w:pPr>)?\s*(<\/w:p>)/,
          (m,open,pPr,close)=>`${open}${pPr||""}<w:r><w:t xml:space="preserve">${esc(val)}</w:t></w:r>${close}`);
      }
      return match;
    });
    zip.file("word/document.xml",xml);
    const out=await zip.generateAsync({type:"blob",mimeType:"application/vnd.openxmlformats-officedocument.wordprocessingml.document"});
    const url=URL.createObjectURL(out);
    const a=document.createElement("a"); a.href=url;
    a.download=`GCG_Proposal_${(jobType||"Project").replace(/\s+/g,"_")}.docx`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };


  const saveEst = () => {
    const s = {id:"qe_"+Date.now(), desc:desc.slice(0,60)+"...", jobType, addr, result, date:new Date().toLocaleDateString()};
    const updated = [s,...saved];
    setSaved(updated);
    try{ localStorage.setItem("grok_quick_estimates_"+(session?.companyId||"local"), JSON.stringify(updated)); }catch(e){}
  };

  React.useEffect(()=>{
    try{
      const s = JSON.parse(localStorage.getItem("grok_quick_estimates_"+(session?.companyId||"local"))||"[]");
      setSaved(s);
    }catch(e){}
  },[]);

  return (
    <div style={{display:"grid",gridTemplateColumns:isMob?"1fr":"1fr 1fr",gap:16,alignItems:"start"}}>
      {/* LEFT — Input */}
      <div>
        <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:12,padding:"16px 18px",marginBottom:12}}>
          {/* Mode toggle — hidden for subs, they are locked to sub-bid mode */}
          {session?.isSub ? (
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14,padding:"8px 12px",background:"rgba(59,130,246,0.08)",border:"1px solid rgba(59,130,246,0.2)",borderRadius:8}}>
              <span style={{fontSize:14}}>⚡</span>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:T.blue}}>Sub-Bid Mode</div>
                <div style={{fontSize:10,color:T.muted}}>Labor & material cost only — no GC fees or overhead</div>
              </div>
            </div>
          ) : (
            <div style={{marginBottom:14}}>
              {/* GC only: switch to sub-bid mode if working as a sub */}
              {subMode ? (
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:T.bg2,borderRadius:9,marginBottom:6}}>
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:T.blue}}>Sub-Bid Mode</div>
                    <div style={{fontSize:9,color:T.muted,marginTop:1}}>Labor & materials for your scope only — no GC overhead</div>
                  </div>
                  <button onClick={()=>{setSubMode(false);setResult("");}}
                    style={{fontSize:10,color:T.gold,background:"none",border:"none",cursor:"pointer",fontWeight:600,flexShrink:0}}>
                    Switch →
                  </button>
                </div>
              ) : (
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:T.bg2,borderRadius:9,marginBottom:6}}>
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:T.gold}}>GC Proposal Mode</div>
                    <div style={{fontSize:9,color:T.muted,marginTop:1}}>Owner-facing — GC fee, contingency, investment summary</div>
                  </div>
                  <button onClick={()=>{setSubMode(true);setResult("");}}
                    style={{fontSize:10,color:T.muted,background:"none",border:"none",cursor:"pointer",fontWeight:600,flexShrink:0}}>
                    Working as sub? →
                  </button>
                </div>
              )}
            </div>
          )}
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>
            {subMode?"⚡ Sub-Bid Estimate":"✍ Describe the Job"}
          </div>
          <div style={{fontSize:11,color:T.muted,marginBottom:12,lineHeight:1.6}}>
            {subMode
              ? "For trade contractors — generates labor & material breakdown only. No GC fees, no overhead, no PM. Clean number to send to your GC."
              : "Write in plain English — no drawings needed. AI generates a complete client-ready investment summary with Bay Area pricing."}
          </div>


          <div style={{marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Job Type</div>
            <select value={jobType} onChange={e=>setJobType(e.target.value)}
              style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"8px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}>
              {JOB_TYPES.map(t=><option key={t}>{t}</option>)}
            </select>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Address (optional)</div>
              <input value={addr} onChange={e=>setAddr(e.target.value)} placeholder="466 Turk St, SF"
                style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"8px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Units / Size</div>
              <input value={units} onChange={e=>setUnits(e.target.value)} placeholder="6 units, 450 sqft"
                style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"8px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
            </div>
          </div>

          <div style={{marginBottom:12}}>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Describe the work</div>
            <textarea value={desc} onChange={e=>setDesc(e.target.value)} rows={isMob?5:8}
              placeholder="Describe what needs to be done — what you're demolishing, installing, the materials you want, any special conditions like occupied building, prevailing wage, tight access..."
              style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:13,resize:"vertical",fontFamily:"inherit",lineHeight:1.6}}/>
          </div>

          {!subMode&&!session?.isSub&&(
            <div style={{fontSize:10,color:T.muted,marginBottom:8,padding:"6px 10px",background:T.bg2,borderRadius:6,lineHeight:1.6}}>
              💡 Pricing reflects licensed, insured, permit-pulled Bay Area GC work with 1-year warranty — positioned at professional market rate.
            </div>
          )}
          <button onClick={run} disabled={loading||!desc.trim()}
            style={{width:"100%",padding:"13px",background:loading||!desc.trim()?"#374151":subMode?"linear-gradient(135deg,"+T.blue+","+T.purple+")":"linear-gradient(135deg,"+T.purple+","+T.blue+")",
              color:"white",border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:loading||!desc.trim()?"not-allowed":"pointer",
              display:"flex",alignItems:"center",justifyContent:"center",gap:10,transition:"all 0.2s"}}>
            {loading?<><Spin/> Generating...</>:subMode?"⚡ Generate Sub-Bid":"✨ Generate Client Estimate"}
          </button>
        </div>

        {/* Quick examples */}
        <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:12,padding:"14px 16px"}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Quick Examples</div>
          {EXAMPLES.map((ex,i)=>(
            <div key={i} onClick={()=>setDesc(ex)}
              style={{padding:"8px 10px",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,fontSize:11,color:T.muted,cursor:"pointer",marginBottom:6,lineHeight:1.5,transition:"border-color 0.15s"}}
              onMouseEnter={e=>e.currentTarget.style.borderColor=T.purple}
              onMouseLeave={e=>e.currentTarget.style.borderColor=T.subtle}>
              {ex.slice(0,90)}…
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT — Output */}
      <div style={{position:isMob?"static":"sticky",top:20}}>
        <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:12,padding:"16px 18px",minHeight:300}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:loading?T.amber:result?T.green:T.subtle,animation:loading?"pulse 1s infinite":"none"}}/>
              <div style={{fontSize:13,fontWeight:700,color:T.text}}>{loading?"Generating…":result?"Scope & Estimate":"Output"}</div>
            </div>
            {result&&(
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                <button onClick={()=>{saveEst();alert("✓ Estimate saved! Find it below in Saved Estimates.");}}
                  style={{fontSize:11,background:T.greenBg,border:"1px solid "+T.green+"40",borderRadius:6,padding:"4px 10px",color:T.green,cursor:"pointer",fontWeight:600}}>
                  💾 Save
                </button>
                <button onClick={()=>{
                  if(navigator.clipboard){
                    navigator.clipboard.writeText(result).then(()=>alert("✓ Copied to clipboard!")).catch(()=>{
                      const ta=document.createElement("textarea");ta.value=result;document.body.appendChild(ta);ta.select();document.execCommand("copy");document.body.removeChild(ta);alert("✓ Copied!");
                    });
                  } else {
                    const ta=document.createElement("textarea");ta.value=result;document.body.appendChild(ta);ta.select();document.execCommand("copy");document.body.removeChild(ta);alert("✓ Copied!");
                  }
                }} style={{fontSize:11,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"4px 10px",color:T.muted,cursor:"pointer"}}>
                  📋 Copy
                </button>
                <button onClick={()=>{
                  const w=window.open("","_blank");
                  if(!w){alert("Please allow popups for this site to use Print.");return;}
                  w.document.write(`<!DOCTYPE html><html><head><title>Estimate</title><style>body{font-family:Arial,sans-serif;padding:32px;max-width:800px;margin:0 auto;color:#1a1a1a}pre{white-space:pre-wrap;line-height:1.7;font-size:13px}h2{color:#d4a017}@media print{body{padding:16px}}</style></head><body><h2>Scope & Estimate</h2><pre>${result.replace(/</g,"&lt;").replace(/>/g,"&gt;")}</pre></body></html>`);
                  w.document.close(); setTimeout(()=>w.print(),400);
                }} style={{fontSize:11,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"4px 10px",color:T.muted,cursor:"pointer"}}>
                  🖨 Print
                </button>
                <button onClick={buildProposalFromEstimate}
                  style={{fontSize:11,background:"linear-gradient(135deg,"+T.purple+","+T.blue+")",border:"none",borderRadius:6,padding:"4px 10px",color:"white",cursor:"pointer",fontWeight:700}}>
                  📄 Create Proposal
                </button>
              </div>
            )}
          </div>

          {!result&&!loading&&(
            <div style={{textAlign:"center",padding:"48px 20px",color:T.muted}}>
              <div style={{fontSize:48,marginBottom:12}}>✍</div>
              <div style={{fontSize:14,fontWeight:600,color:T.text,marginBottom:8}}>Describe any job — get a full estimate</div>
              <div style={{fontSize:12,lineHeight:1.7,maxWidth:320,margin:"0 auto"}}>
                No drawings needed. Write what needs to be done and AI generates scope, costs, and permit info with current SF Bay Area pricing.
              </div>
            </div>
          )}

          {loading&&(
            <div style={{textAlign:"center",padding:"48px 20px",color:T.muted}}>
              <Spin/><div style={{marginTop:12,fontSize:13,color:T.muted}}>Generating estimate…</div>
            </div>
          )}

          {result&&!loading&&(()=>{
            // Parse structured format sections
            const getSection = (label) => {
              const m = result.match(new RegExp("##\\s*"+label+"[\\s\\S]*?(?=##|$)","i"));
              if (!m) return [];
              return m[0].replace(/##[^\n]*/,"").trim().split("\n")
                .map(l=>l.replace(/^[\d]+\.\s*/,"").replace(/^[-•*✓]\s*/,"").trim())
                .filter(Boolean);
            };

            const investmentLines = getSection("INVESTMENT SUMMARY");
            const whatsIncluded = getSection("WHAT.S INCLUDED");
            const excl = getSection("EXCLUSIONS");
            const permit = getSection("PERMIT").join(" ");
            const schedule = getSection("SCHEDULE").join(" ");

            const parseSections = (lines) => {
              const sections = [];
              let current = null;
              lines.forEach(line => {
                const isSectionHeader = (line.startsWith("**") && line.endsWith("**")) ||
                  (line.startsWith("##") === false && /^[A-Z][^a-z]{0,5}/.test(line) && line.length < 50 && !line.includes("$"));
                if (line.startsWith("**") && line.endsWith("**")) {
                  if (current) sections.push(current);
                  current = {title: line.replace(/\*\*/g,""), items:[]};
                } else if (line && current) {
                  const dotMatch = line.match(/^(.+?)\s*[\.·]{2,}\s*(\$[\d,]+|included)$/i);
                  if (dotMatch) {
                    current.items.push({label: dotMatch[1].replace(/^[•\-*]\s*/,"").trim(), value: dotMatch[2]});
                  } else if (line.match(/^[•\-*]/)) {
                    // check if has dollar amount at end
                    const dol = line.match(/\$[\d,]+/);
                    current.items.push({
                      label: line.replace(/^[•\-*]\s*/,"").replace(/\$[\d,]+/,"").replace(/\.+$/,"").trim(),
                      value: dol ? dol[0] : ""
                    });
                  }
                }
              });
              if (current) sections.push(current);
              return sections;
            };

            const invSections = parseSections(investmentLines);

            // Extract total - try multiple patterns
            const totalMatch = result.match(/##\s*TOTAL INVESTMENT[\s\S]*?\n(\$[\d,]+)/i) ||
              result.match(/TOTAL[\s\S]{0,30}?\n(\$[\d,]+)/i);
            const allAmounts = result.match(/\$[\d]{2,3}[,\d]*/g) || [];
            const grandTotal = totalMatch ? totalMatch[1] :
              allAmounts.length ? allAmounts[allAmounts.length-1] : "";

            const sectionColors = {
              "Construction": T.blue,
              "Labor": T.blue,
              "Materials": T.teal,
              "Fixtures": T.teal,
              "Project Management": T.purple,
              "Coordination": T.purple,
              "Contingency": T.amber,
            };

            const getSectionColor = (title) => {
              for (const [k,v] of Object.entries(sectionColors)) {
                if (title.includes(k)) return v;
              }
              return T.gold;
            };

            // If structured parse worked show premium UI, else show clean markdown
            const hasStructured = invSections.length > 0 && invSections.some(s=>s.items.length>0);

            if (!hasStructured) {
              // Fallback: render raw text with simple markdown formatting
              const lines = result.split("\n");
              return (
                <div style={{fontSize:12,color:T.text,lineHeight:1.7}}>
                  {lines.map((line,i) => {
                    if (line.startsWith("## ")) return (
                      <div key={i} style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:T.gold,marginTop:14,marginBottom:6,paddingBottom:5,borderBottom:"1px solid "+T.subtle}}>
                        {line.replace(/^##\s*/,"")}
                      </div>
                    );
                    if (line.startsWith("**") && line.endsWith("**")) return (
                      <div key={i} style={{fontSize:11,fontWeight:700,color:T.text,marginTop:10,marginBottom:4}}>
                        {line.replace(/\*\*/g,"")}
                      </div>
                    );
                    if (line.match(/^[•\-*]/)) {
                      const dolMatch = line.match(/\$[\d,]+/);
                      const lbl = line.replace(/^[•\-*]\s*/,"").replace(/\$[\d,]+/,"").replace(/\.+$/,"").trim();
                      return (
                        <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",padding:"4px 0 4px 8px",borderBottom:"1px solid "+T.bg3}}>
                          <span style={{color:T.text,flex:1,paddingRight:8}}>{lbl}</span>
                          {dolMatch && <span style={{fontWeight:700,color:T.gold,flexShrink:0,fontVariantNumeric:"tabular-nums"}}>{dolMatch[0]}</span>}
                        </div>
                      );
                    }
                    if (line.trim() === "") return <div key={i} style={{height:6}}/>;
                    return <div key={i} style={{fontSize:11,color:T.muted,padding:"1px 0"}}>{line}</div>;
                  })}
                </div>
              );
            }

            return (
              <div style={{fontSize:12,color:T.text}}>
                <div style={{textAlign:"center",marginBottom:20,paddingBottom:16,borderBottom:"2px solid "+T.subtle}}>
                  <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.15em",color:T.muted,marginBottom:4}}>Investment Summary</div>
                  <div style={{fontSize:10,color:T.muted}}>{jobType}{addr?" · "+addr:""}{units?" · "+units:""}</div>
                </div>

                {invSections.map((section,si)=>{
                  const color = getSectionColor(section.title);
                  return (
                    <div key={si} style={{marginBottom:14}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8,paddingBottom:5,borderBottom:"1px solid "+T.subtle}}>
                        <div style={{width:3,height:14,background:color,borderRadius:2,flexShrink:0}}/>
                        <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:color}}>{section.title}</div>
                      </div>
                      {section.items.map((item,ii)=>(
                        <div key={ii} style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",padding:"5px 0 5px 10px",borderBottom:"1px solid "+T.bg3}}>
                          <span style={{color:T.text,lineHeight:1.5,flex:1,paddingRight:12}}>{item.label}</span>
                          <span style={{
                            fontWeight:item.value&&item.value.toLowerCase()==="included"?400:600,
                            color:item.value&&item.value.toLowerCase()==="included"?T.green:T.text,
                            fontVariantNumeric:"tabular-nums",
                            fontSize:item.value&&item.value.toLowerCase()==="included"?10:12,
                            flexShrink:0,
                            fontStyle:item.value&&item.value.toLowerCase()==="included"?"italic":"normal"
                          }}>{item.value||""}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}

                {grandTotal&&(
                  <div style={{background:`linear-gradient(135deg,${T.gold}18,${T.gold}08)`,border:"1px solid "+T.gold+"40",borderRadius:10,padding:"16px 20px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.12em",color:T.gold,marginBottom:2}}>Total Investment</div>
                      <div style={{fontSize:10,color:T.muted}}>Fixed-price · All labor, materials & management</div>
                    </div>
                    <div style={{fontSize:28,fontWeight:800,color:T.gold,letterSpacing:"-0.5px"}}>{grandTotal}</div>
                  </div>
                )}

                {whatsIncluded.length>0&&(
                  <div style={{background:T.greenBg,border:"1px solid "+T.green+"30",borderRadius:8,padding:"12px 14px",marginBottom:14}}>
                    <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.12em",color:T.green,marginBottom:8}}>What's Included</div>
                    {whatsIncluded.map((item,i)=>(
                      <div key={i} style={{display:"flex",gap:8,padding:"3px 0",fontSize:11,color:T.text}}>
                        <span style={{color:T.green,flexShrink:0}}>✓</span>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                )}

                {(excl.length>0||permit||schedule)&&(
                  <div style={{display:"grid",gridTemplateColumns:isMob?"1fr":"1fr 1fr",gap:10,marginBottom:8}}>
                    {excl.length>0&&(
                      <div style={{background:T.bg2,borderRadius:8,padding:"10px 12px"}}>
                        <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:T.muted,marginBottom:6}}>Exclusions</div>
                        {excl.map((e,i)=><div key={i} style={{fontSize:10,color:T.muted,padding:"2px 0"}}>— {e}</div>)}
                      </div>
                    )}
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {permit&&(
                        <div style={{background:T.bg2,borderRadius:8,padding:"10px 12px"}}>
                          <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:T.muted,marginBottom:4}}>Permits</div>
                          <div style={{fontSize:10,color:T.text}}>{permit}</div>
                        </div>
                      )}
                      {schedule&&(
                        <div style={{background:T.bg2,borderRadius:8,padding:"10px 12px"}}>
                          <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:T.muted,marginBottom:4}}>Schedule</div>
                          <div style={{fontSize:10,color:T.text}}>{schedule}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

        </div>

        {/* Saved estimates */}
        {saved.length>0&&(
          <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:12,padding:"14px 16px",marginTop:12}}>
            <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Saved Estimates ({saved.length})</div>
            {saved.slice(0,5).map(s=>(
              <div key={s.id} onClick={()=>setResult(s.result)}
                style={{padding:"8px 10px",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,fontSize:11,color:T.muted,cursor:"pointer",marginBottom:5,display:"flex",justifyContent:"space-between"}}>
                <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{s.desc}</span>
                <span style={{flexShrink:0,marginLeft:8}}>{s.date}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BidTemplatesView({session}) {
  const cid = session?.companyId || window._grokCompanyId || "local";
  const myTplKey = "grok_bid_company_templates_" + cid;

  // Company's own uploaded bid templates
  const [myTemplates, setMyTemplates] = useState(()=>{
    try { return JSON.parse(localStorage.getItem(myTplKey)||"[]"); } catch { return []; }
  });
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [uploadForm, setUploadForm] = useState({name:"", type:"proposal", notes:""});
  const [showExamples, setShowExamples] = useState(false);
  const uploadRef = React.useRef();

  const saveMyTemplates = (updated) => {
    setMyTemplates(updated);
    try { localStorage.setItem(myTplKey, JSON.stringify(updated)); } catch(e) {}
  };

  const handleTemplateUpload = (file) => {
    if (!file) return;
    if (file.size > 5*1024*1024) { alert("File too large — max 5MB"); return; }
    const reader = new FileReader();
    reader.onload = e => {
      const tpl = {
        id: "my_tpl_"+Date.now(),
        name: uploadForm.name || file.name.replace(/\.[^.]+$/,""),
        type: uploadForm.type,
        notes: uploadForm.notes,
        fileName: file.name,
        size: (file.size/1024).toFixed(0)+"KB",
        data: e.target.result,
        uploadedAt: new Date().toLocaleDateString(),
      };
      saveMyTemplates([tpl, ...myTemplates]);
      setUploadForm({name:"",type:"proposal",notes:""});
      setShowUploadPanel(false);
    };
    reader.readAsDataURL(file);
  };

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [activeSection, setActiveSection] = useState(0);
  const [checks, setChecks] = useState({});
  const [savedBids, setSavedBids] = useState([]);
  const [viewMode, setViewMode] = useState("list"); // list | form | saved
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState("");
  const [bidName, setBidName] = useState("");

  const f = (k,v) => setFormData(p=>({...p,[k]:v}));
  const togCheck = (k) => setChecks(p=>({...p,[k]:!p[k]}));

  const startBid = (tmpl) => {
    setSelectedTemplate(tmpl);
    setFormData({bid_date: new Date().toLocaleDateString(), bid_expires: "30 days from date above"});
    setChecks({});
    setActiveSection(0);
    setAiResult("");
    setBidName(tmpl.name + " - " + new Date().toLocaleDateString());
    setViewMode("form");
  };

  const generateAIEstimate = async () => {
    if (!enforceLimit("estimates", session)) return;
    const key = "proxy";
    const kb = loadKB();
    setAiLoading(true); setAiResult("");
    const scopeFields = Object.entries(formData).filter(([k,v])=>v&&v.length>10).map(([k,v])=>`${k}: ${v}`).join("\n");
    const flaggedChecks = Object.entries(checks).filter(([k,v])=>v).map(([k])=>k).join(", ");
    const tmpl = selectedTemplate;
    const prompt = "Generate a complete bid estimate for Grok Construction Group Inc.\n\nProject Type: " + tmpl.name + "\n\nProject Details:\n" + scopeFields + "\n\nCompliance items flagged: " + (flaggedChecks||"None") + "\n\nUsing Grok standard rates:\n" + kb.labor_rates + "\nSubs: " + kb.sub_rates + "\nO&P: " + kb.op_structure + "\n\nProvide:\n1. SCOPE SUMMARY - confirm what is included\n2. MATERIALS - line by line with quantities and SF Bay Area 2025/2026 unit costs\n3. SELF-PERFORM LABOR - by trade, hours, rate\n4. SUBCONTRACTOR COSTS - by trade with 15% markup\n5. OVERHEAD AND PROFIT - line by line at Grok standard rates\n6. TOTAL ESTIMATE - Low / Mid / High range\n7. HIDDEN CONDITION RISKS - specific to this project type and building age\n8. SCHEDULE - estimated duration and crew size\n9. QUESTIONS TO CLARIFY before finalizing";
    await callAI(
      buildSystemPrompt("Generating bid estimate for " + tmpl.name, kb),
      prompt,
      t => setAiResult(p => p + t),
      key,
      {temperature:0} // deterministic estimate generation — identical inputs → identical numbers
    ).catch(e => setAiResult("Error: " + e.message));
    setAiLoading(false);
  };

  const saveBid = () => {
    const bid = {
      id: Date.now(),
      name: bidName,
      template: selectedTemplate.id,
      templateName: selectedTemplate.name,
      templateIcon: selectedTemplate.icon,
      templateColor: selectedTemplate.color,
      formData: {...formData},
      checks: {...checks},
      aiResult,
      saved: new Date().toLocaleString(),
      status: "draft",
    };
    setSavedBids(p => [bid, ...p]);
    setViewMode("saved");
  };

  const exportBid = (bid) => {
    const tmpl = BID_TEMPLATES.find(t=>t.id===bid.template);
    let text = "GROK CONSTRUCTION GROUP INC\nCSLB #1152750 | San Mateo, CA\n" + "=".repeat(55) + "\n\n";
    text += "BID / PROPOSAL\n" + "=".repeat(55) + "\n";
    text += "Template: " + bid.templateName + "\n";
    text += "Saved: " + bid.saved + "\n\n";
    if (tmpl) {
      tmpl.sections.forEach(sec => {
        text += sec.title.toUpperCase() + "\n" + "-".repeat(40) + "\n";
        if (sec.fields) sec.fields.forEach(field => {
          const val = bid.formData[field.k];
          if (val) text += field.l + ": " + val + "\n";
        });
        if (sec.checks) sec.checks.forEach(c => {
          text += (bid.checks[c] ? "[X] " : "[ ] ") + c + "\n";
        });
        if (sec.exclusions) sec.exclusions.forEach(e => { text += "- " + e + "\n"; });
        text += "\n";
      });
    }
    if (bid.aiResult) {
      text += "=".repeat(55) + "\nAI ESTIMATE\n" + "=".repeat(55) + "\n" + bid.aiResult + "\n";
    }
    text += "\n" + "=".repeat(55) + "\nGrok Construction Group Inc | CSLB #1152750\nSan Mateo, CA | info@grokconstruction.com\n";
    const blob = new Blob([text], {type:"text/plain"});
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "Grok_Bid_" + bid.templateName.replace(/[^a-z0-9]/gi,"_") + "_" + new Date().toISOString().slice(0,10) + ".txt";
    a.click();
  };

  // ── LIST VIEW ──────────────────────────────────────────────────────────────
  if (viewMode === "list" || viewMode === "saved") return (
    <div style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>Bid Templates</div>
          <div style={{fontSize:12,color:T.muted,marginTop:3}}>Upload your own bid format — app auto-fills scope, estimate & compliance</div>
        </div>
        <div style={{display:"flex",gap:6,background:T.bg2,borderRadius:9,padding:3}}>
          {[{id:"list",l:"Templates"},{id:"saved",l:"Saved Bids "+savedBids.length}].map(t=>(
            <button key={t.id} onClick={()=>setViewMode(t.id)} style={{padding:"6px 16px",borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:viewMode===t.id?T.gold:T.bg2,color:viewMode===t.id?T.bg0:T.muted,transition:"all 0.15s"}}>{t.l}</button>
          ))}
        </div>
      </div>

      {viewMode === "list" && (
        <>
          {/* My Company Templates — uploaded by this GC */}
          <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:10,padding:"14px 16px",marginBottom:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:myTemplates.length?10:0}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>My Bid Templates</div>
                <div style={{fontSize:10,color:T.muted,marginTop:2}}>Upload your own proposal/bid format — AI fills in scope, estimate, and compliance for each job type</div>
              </div>
              <button onClick={()=>setShowUploadPanel(p=>!p)}
                style={{fontSize:11,fontWeight:700,background:T.gold,color:T.bg0,border:"none",borderRadius:7,padding:"6px 14px",cursor:"pointer",flexShrink:0}}>
                + Upload Template
              </button>
            </div>

            {showUploadPanel&&(
              <div style={{background:T.bg2,borderRadius:8,padding:"12px 14px",marginBottom:10,border:`1px solid ${T.gold}30`}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                  <div>
                    <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Template Name</div>
                    <input value={uploadForm.name} onChange={e=>setUploadForm(p=>({...p,name:e.target.value}))} placeholder="e.g. My Residential Bid"
                      style={{width:"100%",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
                  </div>
                  <div>
                    <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Job Type</div>
                    <select value={uploadForm.type} onChange={e=>setUploadForm(p=>({...p,type:e.target.value}))}
                      style={{width:"100%",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}>
                      <option value="proposal">General Proposal</option>
                      <option value="residential">Residential Remodel</option>
                      <option value="commercial">Commercial TI</option>
                      <option value="sro">SRO / Affordable Housing</option>
                      <option value="adu">ADU / Garage Conversion</option>
                      <option value="roofing">Roofing</option>
                      <option value="mechanical">Mechanical / HVAC</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div style={{marginBottom:8}}>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Notes (optional)</div>
                  <input value={uploadForm.notes} onChange={e=>setUploadForm(p=>({...p,notes:e.target.value}))} placeholder="e.g. Use for jobs over $50K"
                    style={{width:"100%",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
                </div>
                <input type="file" ref={uploadRef} accept=".pdf,.doc,.docx,.txt" style={{display:"none"}} onChange={e=>handleTemplateUpload(e.target.files[0])}/>
                <div style={{display:"flex",gap:7}}>
                  <button onClick={()=>setShowUploadPanel(false)} style={{flex:1,padding:"7px",borderRadius:6,border:`1px solid ${T.subtle}`,background:"none",color:T.muted,fontSize:11,cursor:"pointer"}}>Cancel</button>
                  <button onClick={()=>uploadRef.current?.click()} style={{flex:2,padding:"7px",borderRadius:6,border:"none",background:T.gold,color:T.bg0,fontSize:11,fontWeight:700,cursor:"pointer"}}>Choose File (PDF, Word, TXT)</button>
                </div>
              </div>
            )}

            {myTemplates.length===0&&!showUploadPanel&&(
              <div style={{fontSize:11,color:T.muted,fontStyle:"italic",padding:"4px 0"}}>No bid templates uploaded yet — upload your own proposal format to use it automatically</div>
            )}
            {myTemplates.length>0&&(
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {myTemplates.map(tpl=>(
                  <div key={tpl.id} style={{display:"flex",alignItems:"center",gap:10,background:T.bg2,borderRadius:7,padding:"8px 12px"}}>
                    <div style={{fontSize:18}}>📄</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:700,color:T.text}}>{tpl.name}</div>
                      <div style={{fontSize:10,color:T.muted}}>{tpl.type} · {tpl.fileName} · {tpl.uploadedAt}</div>
                      {tpl.notes&&<div style={{fontSize:10,color:T.muted,fontStyle:"italic"}}>{tpl.notes}</div>}
                    </div>
                    <span style={{fontSize:9,fontWeight:700,background:T.green+"15",color:T.green,borderRadius:20,padding:"2px 8px"}}>Active</span>
                    <button onClick={()=>saveMyTemplates(myTemplates.filter(t=>t.id!==tpl.id))}
                      style={{fontSize:10,color:T.red,background:"none",border:"none",cursor:"pointer",padding:"2px 6px"}}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Platform examples — collapsed, for inspiration only */}
          <div style={{background:T.bg2,borderRadius:10,padding:"12px 16px",marginBottom:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}}
              onClick={()=>setShowExamples(p=>!p)}>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:T.text}}>📋 Example Bid Templates</div>
                <div style={{fontSize:10,color:T.muted,marginTop:2}}>See how a bid template is structured — for inspiration before uploading your own</div>
              </div>
              <span style={{fontSize:14,color:T.muted}}>{showExamples?"▼":"›"}</span>
            </div>
            {showExamples&&(
              <>
                <div style={{background:T.amberBg,border:`1px solid ${T.amber}30`,borderRadius:7,padding:"8px 12px",margin:"10px 0",fontSize:11,color:T.amber,lineHeight:1.6}}>
                  ⚠️ These are platform examples — not tailored to your business. Use them to understand the format, then upload your own.
                </div>
                <div style={{display:"grid",gridTemplateColumns:window.innerWidth<768?"1fr 1fr":"repeat(3,1fr)",gap:10,marginTop:10}}>
                  {BID_TEMPLATES.map(tmpl=>(
                    <div key={tmpl.id} onClick={()=>startBid(tmpl)}
                      style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:12,padding:"20px 16px",cursor:"pointer",transition:"all 0.2s",position:"relative",overflow:"hidden"}}
                      onMouseEnter={e=>{e.currentTarget.style.borderColor=tmpl.color;}}
                      onMouseLeave={e=>{e.currentTarget.style.borderColor=T.subtle;}}>
                      <div style={{fontSize:tmpl.icon.length>2?16:20,fontWeight:800,fontFamily:"'Space Mono',monospace",color:tmpl.color,marginBottom:8}}>{tmpl.icon}</div>
                      <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>{tmpl.name}</div>
                      <div style={{fontSize:10,color:T.muted,marginBottom:10,lineHeight:1.5}}>
                        {tmpl.sections.filter(s=>!s.unforeseen).length} sections · {tmpl.sections.filter(s=>s.checks).reduce((a,s)=>a+s.checks.length,0)} checks
                      </div>
                      <span style={{fontSize:11,color:tmpl.color,fontWeight:700}}>Use as example →</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {viewMode === "saved" && (
        <div>
          {savedBids.length === 0 ? (
            <div style={{textAlign:"center",padding:"60px",color:T.muted}}>
              <div style={{fontSize:14,marginBottom:8}}>No saved bids yet</div>
              <Btn primary onClick={()=>setViewMode("list")}><Ic d={ICONS.plus} size={13}/>Start a Bid</Btn>
            </div>
          ) : savedBids.map(bid=>(
            <div key={bid.id} style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:11,padding:"16px 18px",marginBottom:8,display:"flex",gap:14,alignItems:"center"}}>
              <div style={{width:42,height:42,borderRadius:10,background:bid.templateColor+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:bid.templateIcon?.length>2?11:16,fontWeight:800,fontFamily:"'Space Mono',monospace",color:bid.templateColor,flexShrink:0,letterSpacing:"0.02em"}}>{bid.templateIcon}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:2}}>{bid.name}</div>
                <div style={{fontSize:11,color:T.muted}}>{bid.templateName} · Saved {bid.saved}</div>
              </div>
              <span style={{fontSize:10,fontWeight:700,background:T.goldBg,color:T.gold,borderRadius:20,padding:"2px 10px",border:`1px solid ${T.goldDim}`}}>{bid.status}</span>
              <div style={{display:"flex",gap:7}}>
                <Btn small ghost onClick={()=>exportBid(bid)}>↓ Export</Btn>
                <Btn small primary onClick={()=>{setSelectedTemplate(BID_TEMPLATES.find(t=>t.id===bid.template));setFormData(bid.formData);setChecks(bid.checks);setAiResult(bid.aiResult||"");setBidName(bid.name);setActiveSection(0);setViewMode("form");}}>Edit</Btn>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── FORM VIEW ──────────────────────────────────────────────────────────────
  const tmpl = selectedTemplate;
  const section = tmpl.sections[activeSection];
  const totalSections = tmpl.sections.length;
  const pct = Math.round((activeSection / (totalSections - 1)) * 100);

  return (
    <div style={{padding:24}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
        <button onClick={()=>setViewMode("list")} style={{background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"7px 14px",color:T.muted,fontSize:12,cursor:"pointer"}}>← Templates</button>
        <div style={{flex:1}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:20}}>{tmpl.icon}</span>
            <div style={{fontSize:16,fontWeight:800,color:T.text}}>{tmpl.name}</div>
          </div>
          <input value={bidName} onChange={e=>setBidName(e.target.value)}
            style={{fontSize:11,color:T.muted,background:"transparent",border:"none",marginTop:2,width:"100%",fontFamily:"inherit",cursor:"text"}}
            placeholder="Bid name..."/>
        </div>
        <div style={{display:"flex",gap:7}}>
          <Btn small ghost onClick={saveBid}>Save Bid</Btn>
          <Btn small primary onClick={()=>{saveBid();exportBid({id:"temp",template:tmpl.id,templateName:tmpl.name,templateIcon:tmpl.icon,templateColor:tmpl.color,formData,checks,aiResult,saved:new Date().toLocaleString(),status:"draft"});}}>↓ Export</Btn>
        </div>
      </div>

      {/* Progress */}
      <div style={{display:"flex",gap:4,marginBottom:12,overflowX:"auto",paddingBottom:6,WebkitOverflowScrolling:"touch"}}>
        {tmpl.sections.map((sec,i)=>(
          <button key={i} onClick={()=>setActiveSection(i)}
            style={{flex:"0 0 auto",padding:"6px 14px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${i===activeSection?tmpl.color:T.subtle}`,background:i===activeSection?tmpl.color+"18":T.bg2,color:i===activeSection?tmpl.color:T.muted,transition:"all 0.15s",whiteSpace:"nowrap"}}>
            {i+1}. {sec.title}
          </button>
        ))}
      </div>

      <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 400px",gap:16}}>

        {/* Section content */}
        <Card>
          <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:4}}>{section.title}</div>
          <div style={{fontSize:11,color:T.muted,marginBottom:20}}>Section {activeSection+1} of {totalSections}</div>

          {/* Fields */}
          {section.fields && section.fields.map(field=>(
            <div key={field.k} style={{marginBottom:14}}>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>{field.l}</div>
              {field.type==="select" ? (
                <select value={formData[field.k]||""} onChange={e=>f(field.k,e.target.value)}
                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 12px",color:formData[field.k]?T.text:T.muted,fontSize:13,fontFamily:"inherit"}}>
                  <option value="">Select...</option>
                  {field.options?.map(o=><option key={o}>{o}</option>)}
                </select>
              ) : field.type==="date" ? (
                <input type="text" value={formData[field.k]||""} onChange={e=>f(field.k,e.target.value)}
                  placeholder="MM/DD/YYYY"
                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
              ) : field.rows ? (
                <textarea value={formData[field.k]||""} onChange={e=>f(field.k,e.target.value)}
                  placeholder={field.ph} rows={field.rows||2}
                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 12px",color:T.text,fontSize:13,resize:"vertical",fontFamily:"inherit",lineHeight:1.5}}/>
              ) : (
                <input type="text" value={formData[field.k]||""} onChange={e=>f(field.k,e.target.value)}
                  placeholder={field.ph}
                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
              )}
            </div>
          ))}

          {/* Compliance checks */}
          {section.checks && (
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:12,lineHeight:1.5}}>Check all items that apply to this project. Unchecked items will be noted as "to confirm."</div>
              {section.checks.map(c=>(
                <div key={c} onClick={()=>togCheck(c)}
                  style={{display:"flex",gap:9,alignItems:"flex-start",padding:"8px 10px",borderRadius:7,cursor:"pointer",background:checks[c]?T.greenBg:"transparent",transition:"background 0.15s",marginBottom:3}}>
                  <div style={{width:16,height:16,borderRadius:3,border:`2px solid ${checks[c]?T.green:T.subtle}`,background:checks[c]?T.green:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1,transition:"all 0.15s"}}>
                    {checks[c]&&<Ic d={ICONS.check} size={9} style={{color:T.bg0}}/>}
                  </div>
                  <span style={{fontSize:12,color:checks[c]?T.green:T.text,lineHeight:1.4}}>{c}</span>
                </div>
              ))}
            </div>
          )}

          {/* Exclusions */}
          {section.exclusions && (
            <div>
              <div style={{background:T.redBg,borderLeft:`3px solid ${T.red}`,borderRadius:7,padding:"10px 12px",marginBottom:12,fontSize:11,color:T.muted,lineHeight:1.5}}>
                <strong style={{color:T.red}}>Standard exclusions</strong> — these are already in your contract. Review with the client before signing.
              </div>
              {section.exclusions.map((ex,i)=>(
                <div key={i} style={{display:"flex",gap:9,padding:"7px 0",borderBottom:`1px solid ${T.subtle}`,alignItems:"flex-start"}}>
                  <div style={{width:5,height:5,borderRadius:"50%",background:T.red,flexShrink:0,marginTop:5}}/>
                  <span style={{fontSize:12,color:T.muted,lineHeight:1.5}}>{ex}</span>
                </div>
              ))}
            </div>
          )}

          {/* Unforeseen Work */}
          {section.unforeseen && (
            <div>
              <div style={{background:T.amberBg,borderLeft:`3px solid ${T.amber}`,borderRadius:7,padding:"10px 12px",marginBottom:12,fontSize:11,color:T.muted,lineHeight:1.5}}>
                <strong style={{color:T.amber}}>Possible unforeseen work</strong> — common hidden conditions for this project type. Each one is a potential Change Order. Flag these during site walkthrough and protect yourself with the right exclusion language before signing.
              </div>
              {section.unforeseen.map((item,i)=>{
                const [title,...rest] = item.split(" — ");
                return (
                  <div key={i} style={{display:"flex",gap:9,padding:"8px 0",borderBottom:`1px solid ${T.subtle}`,alignItems:"flex-start"}}>
                    <div style={{width:16,height:16,borderRadius:3,background:T.amberBg,border:`1px solid ${T.amber}30`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
                      <span style={{fontSize:9,color:T.amber,fontWeight:800}}>!</span>
                    </div>
                    <div>
                      <div style={{fontSize:12,fontWeight:600,color:T.text}}>{title}</div>
                      {rest.length>0&&<div style={{fontSize:11,color:T.muted,lineHeight:1.4,marginTop:2}}>{rest.join(" — ")}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Nav */}
          <div style={{display:"flex",gap:10,marginTop:24,justifyContent:"space-between"}}>
            <Btn ghost onClick={()=>setActiveSection(Math.max(0,activeSection-1))} disabled={activeSection===0}>← Previous</Btn>
            {activeSection < totalSections-1 ? (
              <Btn primary onClick={()=>setActiveSection(activeSection+1)}>Next Section →</Btn>
            ) : (
              <Btn primary onClick={generateAIEstimate} disabled={aiLoading}
                style={{background:`linear-gradient(135deg,${T.purple},${T.blue})`,color:"white"}}>
                {aiLoading?<><Spin/>Generating...</>:<><Ic d={ICONS.robot} size={13}/>Generate AI Estimate</>}
              </Btn>
            )}
          </div>
        </Card>

        {/* Right — AI estimate + summary */}
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Card>
            <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Bid Progress</div>
            <div style={{height:4,background:T.bg3,borderRadius:2,marginBottom:10}}>
              <div style={{height:"100%",width:pct+"%",background:tmpl.color,borderRadius:2,transition:"width 0.3s"}}/>
            </div>
            <div style={{fontSize:11,color:T.muted,marginBottom:12}}>{activeSection+1} of {totalSections} sections</div>
            <div style={{fontSize:11,color:T.muted,marginBottom:6}}>Fields filled: {Object.values(formData).filter(v=>v&&v.trim()).length}</div>
            <div style={{fontSize:11,color:T.muted}}>Checks marked: {Object.values(checks).filter(Boolean).length}</div>
            <Divider/>
            <Btn primary onClick={generateAIEstimate} disabled={aiLoading} style={{width:"100%",justifyContent:"center",background:`linear-gradient(135deg,${T.purple},${T.blue})`,color:"white"}}>
              {aiLoading?<><Spin/>Generating...</>:<><Ic d={ICONS.robot} size={13}/>Generate AI Estimate</>}
            </Btn>
          </Card>

          {(aiResult||aiLoading) && (
            <Card style={{flex:1}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <div style={{fontSize:12,fontWeight:700,color:T.text}}>AI Estimate</div>
                <div style={{display:"flex",gap:6}}>
                  {aiResult&&<Btn small ghost onClick={()=>setAiResult("")}><Ic d={ICONS.x} size={11}/></Btn>}
                </div>
              </div>
              <div style={{background:T.bg0,borderRadius:8,padding:"10px 12px",fontSize:11,color:T.text,lineHeight:1.8,whiteSpace:"pre-wrap",maxHeight:400,overflowY:"auto",fontFamily:"monospace"}}>
                {aiResult}{aiLoading&&<Spin/>}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function EstimateRequestsView() {
  const [requests, setRequests] = useState(()=>{
    // Will be populated from EST_REQUESTS which is set per-company in App
    return EST_REQUESTS;
  });
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [aiEstimate, setAiEstimate] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [editedEstimate, setEditedEstimate] = useState("");
  const [showSendModal, setShowSendModal] = useState(false);

  const filtered = filter === "all" ? requests : requests.filter(r => r.status === filter);
  const newCount = requests.filter(r => r.status === "new").length;

  const updateRequest = (id, updates) => {
    const updated = requests.map(r => r.id === id ? {...r, ...updates} : r);
    EST_REQUESTS = updated;
    setRequests(updated);
    saveEstRequests(updated, "default");
    if (selected?.id === id) setSelected(r => ({...r, ...updates}));
  };

  const STATUS_COLORS = {
    new: T.blue, reviewed: T.amber, estimated: T.purple,
    approved: T.green, sent: T.teal, declined: T.red
  };
  const STATUS_LABELS = {
    new: "New Request", reviewed: "Under Review", estimated: "Estimate Ready",
    approved: "Approved", sent: "Sent to Client", declined: "Declined"
  };

  const runAIEstimate = async () => {
    if (!selected) return;
    const key = "proxy"; // Using Supabase AI proxy
    const kb = loadKB();
    setAiLoading(true); setAiEstimate("");

    const prompt = "Generate a complete construction estimate for this project request:\n\nPROJECT REQUEST\nType: " + selected.type + "\nAddress: " + selected.addr + "\nSize: " + selected.size + "\nBuilding Age: " + selected.age + "\nBudget Range: " + selected.budget + "\nClient Description: " + selected.desc + "\n\nUse CURRENT SF Bay Area 2025/2026 pricing. Format:\n\nSCOPE REVIEW\n- What is included based on the description\n- Missing information or clarifications needed\n- Hidden condition risks given building age and type\n\nMATERIALS BREAKDOWN\nList every material with quantity and current SF Bay Area unit price.\nMaterials Subtotal: $\n\nLABOR BREAKDOWN\nSelf-perform (" + kb.self_perform + "):\n- List tasks with hours and rate\nSubcontractors:\n- List trades needed with estimated cost\nLabor Subtotal: $\n\nOVERHEAD AND PROFIT\n- Project management (5%): $\n- General conditions: $\n- Insurance and bonding (2.5%): $\n- Overhead (7.5%): $\n- Profit (10%): $\nO&P Subtotal: $\n\nESTIMATE RANGE\nLOW: $\nMID: $\nHIGH: $\n\nHIDDEN CONDITION RISKS\nList each risk with estimated CO cost range.\n\nRECOMMENDED NEXT STEPS\nWhat to clarify before finalizing this estimate.";
    await callAI(
      buildSystemPrompt(`Reviewing estimate request from ${selected.name} for ${selected.type} at ${selected.addr}`, kb),
      prompt,
      t => setAiEstimate(p => p + t),
      key,
      {temperature:0} // deterministic estimate generation — identical inputs → identical numbers
    ).catch(e => setAiEstimate("Error: " + e.message));

    setAiLoading(false);
    updateRequest(selected.id, { status: "estimated" });
  };

  return (
    <div style={{padding:24}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>Estimate Requests</div>
          <div style={{fontSize:12,color:T.muted,marginTop:3}}>
            From your website · Review scope · Generate AI estimate · Approve and send
          </div>
        </div>
        {newCount > 0 && (
          <div style={{background:T.blueBg,border:`1px solid ${T.blue}30`,borderRadius:8,padding:"10px 18px",display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:T.blue,animation:"pulse 1.5s infinite"}}/>
            <span style={{fontSize:13,fontWeight:700,color:T.blue}}>{newCount} new request{newCount>1?"s":""} waiting</span>
          </div>
        )}
      </div>

      {/* Workflow banner */}
      <div style={{display:"flex",gap:0,marginBottom:20,background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:10,overflow:"hidden"}}>
        {[
          {n:"1",l:"Request Received",sub:"From website form",c:T.blue},
          {n:"2",l:"You Review Scope",sub:"Check details, add notes",c:T.amber},
          {n:"3",l:"AI Generates Estimate",sub:"Full breakdown with SF pricing",c:T.purple},
          {n:"4",l:"You Review & Edit",sub:"Adjust numbers and scope",c:T.gold},
          {n:"5",l:"Approve & Send",sub:"Email goes to client",c:T.green},
        ].map((s,i)=>(
          <div key={i} style={{flex:1,padding:"14px 16px",borderRight:i<4?`1px solid ${T.subtle}`:"none",display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:28,height:28,borderRadius:"50%",background:s.c+"18",border:`1px solid ${s.c}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:s.c,flexShrink:0}}>{s.n}</div>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:T.text}}>{s.l}</div>
              <div style={{fontSize:10,color:T.muted}}>{s.sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{display:"flex",gap:4,background:T.bg2,borderRadius:9,padding:3,width:"fit-content",marginBottom:16}}>
        {["all","new","reviewed","estimated","approved","sent"].map(f=>(
          <button key={f} onClick={()=>setFilter(f)}
            style={{padding:"5px 14px",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",background:filter===f?T.gold:T.bg2,color:filter===f?T.bg0:T.muted,transition:"all 0.15s",textTransform:"capitalize",whiteSpace:"nowrap"}}>
            {f==="all"?"All Requests":STATUS_LABELS[f]||f}
            {f==="new"&&newCount>0&&<span style={{marginLeft:5,background:T.blue,color:"white",borderRadius:10,padding:"0 5px",fontSize:9}}>{newCount}</span>}
          </button>
        ))}
      </div>

      <div className="lead-detail-grid" style={{display:"grid",gridTemplateColumns:selected?"1fr 1.4fr":"1fr",gap:14}}>

        {/* Request list */}
        <div>
          {filtered.length === 0 && (
            <div style={{textAlign:"center",padding:"60px",color:T.muted,fontSize:13}}>
              No requests in this category yet.<br/>They'll appear here when submitted from your website.
            </div>
          )}
          {filtered.map(req => {
            const sc = STATUS_COLORS[req.status] || T.muted;
            const sl = STATUS_LABELS[req.status] || req.status;
            const isSel = selected?.id === req.id;
            return (
              <div key={req.id} onClick={()=>{setSelected(req);setAiEstimate(req.estimate||"");setNotes(req.notes||"");setEditedEstimate(req.estimate||"");}}
                style={{background:T.bg1,border:`1px solid ${isSel?T.gold:T.subtle}`,borderRadius:11,padding:"16px 18px",marginBottom:8,cursor:"pointer",transition:"all 0.15s"}}
                onMouseEnter={e=>{if(!isSel)e.currentTarget.style.borderColor=T.goldDim}}
                onMouseLeave={e=>{if(!isSel)e.currentTarget.style.borderColor=T.subtle}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                      <div style={{fontSize:14,fontWeight:700,color:T.text}}>{req.name}</div>
                      {req.status==="new"&&<div style={{width:7,height:7,borderRadius:"50%",background:T.blue,animation:"pulse 1.5s infinite"}}/>}
                    </div>
                    <div style={{fontSize:11,color:T.muted}}>{req.type} · {req.addr}</div>
                  </div>
                  <div style={{display:"flex",gap:7,alignItems:"center",flexShrink:0}}>
                    <span style={{fontSize:10,fontWeight:700,background:sc+"18",color:sc,borderRadius:20,padding:"2px 9px",border:`1px solid ${sc}30`}}>{sl}</span>
                    <span style={{fontSize:11,color:T.gold,fontWeight:600}}>{req.budget}</span>
                  </div>
                </div>
                <div style={{fontSize:12,color:T.muted,lineHeight:1.5,overflow:"hidden",textOverflow:"ellipsis",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",marginBottom:8}}>{req.desc}</div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:10,color:T.muted,fontFamily:"monospace"}}>{req.submitted}</span>
                  <span style={{fontSize:10,color:T.muted}}>{req.size} · {req.age}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail panel */}
        {selected && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>

            {/* Request details */}
            <Card>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={{fontSize:14,fontWeight:700,color:T.text}}>{selected.name}</div>
                <div style={{display:"flex",gap:6}}>
                  {["reviewed","estimated","approved","sent"].map(s=>(
                    <button key={s} onClick={()=>updateRequest(selected.id,{status:s})}
                      style={{padding:"4px 10px",borderRadius:20,fontSize:9,fontWeight:700,cursor:"pointer",border:`1px solid ${STATUS_COLORS[s]}40`,background:selected.status===s?STATUS_COLORS[s]+"18":T.bg2,color:selected.status===s?STATUS_COLORS[s]:T.muted,textTransform:"capitalize",transition:"all 0.15s"}}>
                      {s}
                    </button>
                  ))}
                  <Btn small ghost onClick={()=>setSelected(null)}><Ic d={ICONS.x} size={11}/></Btn>
                </div>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                {[["Contact",selected.contact],["Type",selected.type],["Address",selected.addr],["Size",selected.size],["Building Age",selected.age],["Budget",selected.budget],["Submitted",selected.submitted]].map(([k,v])=>(
                  <div key={k} style={{fontSize:11}}>
                    <span style={{color:T.muted}}>{k}: </span>
                    <span style={{color:T.text,fontWeight:600}}>{v}</span>
                  </div>
                ))}
              </div>

              <div style={{background:T.bg2,borderRadius:7,padding:"12px 14px",marginBottom:12}}>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Client's Description</div>
                <div style={{fontSize:12,color:T.text,lineHeight:1.7}}>{selected.desc}</div>
              </div>

              <div>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Your Review Notes</div>
                <textarea value={notes} onChange={e=>setNotes(e.target.value)}
                  onBlur={()=>updateRequest(selected.id,{notes})}
                  placeholder="Flag missing info, questions to ask, scope concerns, risks to note before generating estimate..."
                  rows={3} style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 11px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit"}}/>
              </div>
            </Card>

            {/* AI Estimate generation */}
            <Card>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>AI Estimate</div>
                <div style={{display:"flex",gap:7}}>
                  {aiEstimate && (
                    <>
                      <Btn small ghost onClick={()=>{updateRequest(selected.id,{estimate:aiEstimate,status:"approved"});}}>
                        <Ic d={ICONS.check} size={11}/>Approve
                      </Btn>
                      <Btn small primary onClick={()=>{
                        const subj = encodeURIComponent(`Your Estimate — ${selected.type} at ${selected.addr}`);
                        const body = encodeURIComponent(
                          `Hi ${selected.name.split(" ")[0]},\n\nThank you for reaching out to Grok Construction Group Inc.\n\nHere is the detailed estimate for your ${selected.type} project at ${selected.addr}:\n\n` +
                          (editedEstimate || aiEstimate) +
                          `\n\nThis estimate is based on current SF Bay Area pricing and the scope described. Please note that a site walkthrough may reveal conditions that affect the final cost.\n\nI'd love to schedule a site visit to walk through the project with you and discuss next steps.\n\nBest regards,\nJehoon\nGrok Construction Group Inc\nCSLB #1152750\n(650) 555-0100`
                        );
                        window.location.href = `mailto:${selected.contact.includes('@')?selected.contact:''}?subject=${subj}&body=${body}`;
                        updateRequest(selected.id,{status:"sent",estimate:editedEstimate||aiEstimate});
                      }} style={{background:T.green,color:"white"}}>
                        ✉ Send to Client
                      </Btn>
                    </>
                  )}
                </div>
              </div>

              {!aiEstimate && !aiLoading && (
                <div style={{marginBottom:12,background:T.bg2,borderRadius:8,padding:"14px 16px",fontSize:12,color:T.muted,lineHeight:1.6}}>
                  <strong style={{color:T.text}}>Before generating:</strong> Review the client's description above, add your notes, and make sure you understand the scope. The AI uses your knowledge base and current SF Bay Area pricing.
                </div>
              )}

              <Btn primary onClick={runAIEstimate} disabled={aiLoading}
                style={{width:"100%",justifyContent:"center",background:`linear-gradient(135deg,${T.purple},${T.blue})`,color:"white",marginBottom:12}}>
                {aiLoading ? <><Spin/>Generating estimate...</> : aiEstimate ? <><Ic d={ICONS.robot} size={13}/>Regenerate Estimate</> : <><Ic d={ICONS.robot} size={13}/>Generate AI Estimate</>}
              </Btn>

              {(aiEstimate || aiLoading) && (
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>
                    {aiLoading ? "Generating..." : "AI Output — Review and Edit Before Sending"}
                  </div>
                  <textarea
                    value={aiLoading ? aiEstimate : (editedEstimate || aiEstimate)}
                    onChange={e=>setEditedEstimate(e.target.value)}
                    readOnly={aiLoading}
                    rows={16}
                    style={{width:"100%",background:T.bg0,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"12px 14px",color:T.text,fontSize:11,resize:"vertical",fontFamily:"monospace",lineHeight:1.7}}/>
                  {!aiLoading && (
                    <div style={{fontSize:10,color:T.muted,marginTop:4}}>
                      You can edit the estimate above before approving or sending. Changes are saved when you click Approve or Send.
                    </div>
                  )}
                </div>
              )}
            </Card>

          </div>
        )}
      </div>
    </div>
  );
}

function LeadsView({setView, setActiveProject, projects, setProjects, session, leads: leadsFromApp, setLeads: setLeadsFromApp}) {
  const cid = session?.companyId || window._grokCompanyId || (()=>{try{return JSON.parse(localStorage.getItem("grok_auth_session")||"{}").companyId||"gcg_001";}catch{return "gcg_001";}})();
  const [leadsLocal, setLeadsLocal] = useState(()=>{
    try{const s=localStorage.getItem(leadsKey(cid));if(s!==null)return JSON.parse(s);}catch{}
    return [];
  });
  // Use App-level state if provided (single source of truth), else local
  const leads = leadsFromApp !== undefined ? leadsFromApp : leadsLocal;
  const setLeads = (val) => {
    const updated = typeof val === 'function' ? val(leads) : val;
    if (setLeadsFromApp) { setLeadsFromApp(updated); }
    else { setLeadsLocal(updated); try{localStorage.setItem(leadsKey(cid),JSON.stringify(updated));}catch(e){} }
  };
  const persist = (updated) => {
    try{localStorage.setItem(leadsKey(cid),JSON.stringify(updated));}catch(e){}
  };
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState("");
  const [activeLeadTab, setActiveLeadTab] = useState("info");

  const saveLead = (lead) => {
    if (lead.id) {
      const updated = leads.map(l => l.id === lead.id ? lead : l);
      LEADS = updated; setLeads(updated); persist(updated);
    } else {
      const newLead = {...lead, id: Date.now(), created: new Date().toLocaleDateString(), lastContact: new Date().toLocaleDateString()};
      const updated = [newLead, ...leads];
      LEADS = updated; setLeads(updated); persist(updated);
    }
    setShowForm(false); setEditLead(null);
  };

  const deleteLead = (id) => { const updated = leads.filter(l=>l.id!==id); LEADS = updated; setLeads(updated); persist(updated); setSelected(null); };
  const updateStatus = (lead, status) => saveLead({...lead, status});

  const filtered = filterStatus === "all" ? leads : leads.filter(l => l.status === filterStatus);
  const hotLeads = leads.filter(l => l.priority === "hot" && l.status !== "won" && l.status !== "lost");
  const pipeline = leads.filter(l => l.status !== "won" && l.status !== "lost");

  const LeadForm = ({lead, onSave, onClose}) => {
    const [form, setForm] = useState({
      name: lead?.name||"", phone: lead?.phone||"", email: lead?.email||"",
      company: lead?.company||"", type: lead?.type||"SRO", addr: lead?.addr||"",
      budget: lead?.budget||"", units: lead?.units||"", status: lead?.status||"new",
      priority: lead?.priority||"warm", source: lead?.source||"referral", notes: lead?.notes||"",
    });
    const f = (k,v) => setForm(p=>({...p,[k]:v}));
    return (
      <Modal title={lead?.id ? "Edit Lead" : "New Lead"} onClose={onClose} wide>
        <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <div>
            <Field label="Contact Name" value={form.name} onChange={v=>f("name",v)} placeholder="John Smith"/>
            <Field label="Phone" value={form.phone} onChange={v=>f("phone",v)} placeholder="(415) 555-0100"/>
            <Field label="Email" value={form.email} onChange={v=>f("email",v)} placeholder="owner@email.com"/>
            <Field label="Company / Organization" value={form.company} onChange={v=>f("company",v)} placeholder="SF Properties LLC"/>
            <Field label="Property Address" value={form.addr} onChange={v=>f("addr",v)} placeholder="Tenderloin, SF"/>
            <Field label="Number of Units" value={form.units} onChange={v=>f("units",v)} placeholder="8" type="number"/>
          </div>
          <div>
            <Select label="Project Type" value={form.type} onChange={v=>f("type",v)} options={["SRO","Affordable Housing","Senior Housing","Market Rate","Maintenance","Commercial TI","Other"]}/>
            <Select label="Budget Range" value={form.budget} onChange={v=>f("budget",v)} options={["Under $50K","$50K-$100K","$100K-$200K","$200K-$350K","$300K-$500K","$500K-$1M","$1M+"]}/>
            <Select label="Status" value={form.status} onChange={v=>f("status",v)} options={LEAD_STATUSES.map(s=>({value:s.id,label:s.label}))}/>
            <Select label="Priority" value={form.priority} onChange={v=>f("priority",v)} options={[{value:"hot",label:"Hot"},{value:"warm",label:"Warm"},{value:"cold",label:"Cold"}]}/>
            <Select label="Lead Source" value={form.source} onChange={v=>f("source",v)} options={[{value:"referral",label:"Referral"},{value:"website",label:"Website"},{value:"direct",label:"Direct Outreach"},{value:"bid-board",label:"Bid Board"},{value:"repeat",label:"Repeat Client"},{value:"other",label:"Other"}]}/>
            <Field label="Notes" value={form.notes} onChange={v=>f("notes",v)} placeholder="Project details, timeline, key contacts..." rows={3}/>
          </div>
        </div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
          <Btn ghost onClick={onClose}>Cancel</Btn>
          <Btn primary disabled={!form.name.trim()} onClick={()=>onSave({...lead,...form})}>
            {lead?.id ? "Save Changes" : "Add Lead"}
          </Btn>
        </div>
      </Modal>
    );
  };

  return (
    <div style={{padding:24}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>Lead Pipeline</div>
          <div style={{fontSize:12,color:T.muted,marginTop:3}}>
            {hotLeads.length} hot leads · {pipeline.length} in pipeline · {leads.filter(l=>l.status==="won").length} won
          </div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <Btn ghost small onClick={()=>{setEditLead(null);setShowForm(true);}}>
            <Ic d={ICONS.plus} size={12}/>New Lead
          </Btn>
          <Btn primary small onClick={()=>{
            const key="proxy"; // proxy handles auth
            if(!key){alert("Add API key in AI Agent first.");return;}
            setAiLoading(true); setAiResult(""); 
            if (!enforceLimit("pm_chats", session)) { setAiLoading(false); return; }
            const hotList = hotLeads.map(l=>`${l.name} (${l.company}) — ${l.type}, ${l.addr}, ${l.budget}, Status: ${l.status}, Notes: ${l.notes}`).join("\n");
            callAI(
              "You are an expert business development advisor for Grok Construction Group Inc (CSLB #1152750), a GC in SF Bay Area specializing in SRO, affordable housing, and multi-family renovation.\n\nAnalyze the current lead pipeline and provide:\n1. PRIORITY ACTIONS — what to do TODAY for each hot lead\n2. FOLLOW-UP SCRIPTS — exact words to say/email for each lead\n3. DEAL RISKS — what could kill each deal\n4. REVENUE FORECAST — expected close timeline and value\n5. RECOMMENDATIONS — which leads to prioritize and why",
              `Current hot leads:\n${hotList || "No hot leads currently"}\n\nAll pipeline leads: ${pipeline.length}\nTotal leads: ${leads.length}`,
              t=>setAiResult(t),
              key
            ).then(()=>setAiLoading(false)).catch(()=>setAiLoading(false));
          }} style={{background:`linear-gradient(135deg,${T.purple},${T.blue})`,color:"white"}}>
            {aiLoading ? <><Spin/>Analyzing...</> : <><Ic d={ICONS.robot} size={12}/>AI Pipeline Review</>}
          </Btn>
        </div>
      </div>

      {/* Pipeline stats */}
      <div className="stat-grid-4" style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:10,marginBottom:16}}>
        {LEAD_STATUSES.map(s=>{
          const count = leads.filter(l=>l.status===s.id).length;
          return (
            <div key={s.id} onClick={()=>setFilterStatus(filterStatus===s.id?"all":s.id)}
              style={{background:filterStatus===s.id?s.color+"18":T.bg1,border:`1px solid ${filterStatus===s.id?s.color:T.subtle}`,borderRadius:10,padding:"10px 12px",cursor:"pointer",transition:"all 0.15s",textAlign:"center"}}>
              <div style={{fontSize:22,fontWeight:800,color:filterStatus===s.id?s.color:T.text}}>{count}</div>
              <div style={{fontSize:10,color:T.muted,fontWeight:600}}>{s.label}</div>
            </div>
          );
        })}
      </div>

      <div style={{display:"grid",gridTemplateColumns:selected?"1fr 380px":"1fr",gap:14}}>
        {/* Lead list */}
        <div>
          {filtered.length===0 && (
            <div style={{textAlign:"center",padding:"60px",color:T.muted}}>
              <div style={{fontSize:14,marginBottom:8}}>No leads in this stage</div>
              <Btn primary onClick={()=>setShowForm(true)}><Ic d={ICONS.plus} size={13}/>Add Lead</Btn>
            </div>
          )}
          {filtered.map(lead => {
            const statusObj = LEAD_STATUSES.find(s=>s.id===lead.status)||LEAD_STATUSES[0];
            const isSel = selected?.id === lead.id;
            return (
              <div key={lead.id} onClick={()=>setSelected(isSel?null:lead)}
                style={{background:T.bg1,border:`1px solid ${isSel?T.gold:T.subtle}`,borderRadius:11,padding:"14px 16px",marginBottom:8,cursor:"pointer",transition:"all 0.15s"}}
                onMouseEnter={e=>{if(!isSel)e.currentTarget.style.borderColor=T.goldDim}}
                onMouseLeave={e=>{if(!isSel)e.currentTarget.style.borderColor=T.subtle}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                      <div style={{fontSize:14,fontWeight:700,color:T.text}}>{lead.name}</div>
                      {lead.priority==="hot"&&<span style={{fontSize:9,fontWeight:800,background:T.redBg,color:T.red,borderRadius:20,padding:"1px 7px"}}>HOT</span>}
                    </div>
                    <div style={{fontSize:11,color:T.muted}}>{lead.company} · {lead.addr}</div>
                  </div>
                  <div style={{display:"flex",gap:7,alignItems:"center",flexShrink:0}}>
                    <span style={{fontSize:10,fontWeight:700,background:statusObj.color+"18",color:statusObj.color,borderRadius:20,padding:"2px 9px"}}>{statusObj.label}</span>
                    <span style={{fontSize:11,fontWeight:700,color:T.gold}}>{lead.budget}</span>
                    <button onClick={(e)=>{e.stopPropagation();if(window.confirm(`Delete ${lead.name}?`))deleteLead(lead.id);}}
                      style={{background:T.redBg,border:`1px solid ${T.red}30`,borderRadius:6,padding:"2px 7px",fontSize:10,color:T.red,cursor:"pointer",fontWeight:700}}>🗑</button>
                  </div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                  <span style={{fontSize:10,background:T.bg3,color:T.muted,borderRadius:20,padding:"1px 7px"}}>{lead.type}</span>
                  <span style={{fontSize:10,color:T.muted}}>{lead.units} units</span>
                  <span style={{fontSize:10,color:T.muted}}>via {lead.source}</span>
                  <span style={{fontSize:10,color:T.muted,marginLeft:"auto"}}>Last contact: {lead.lastContact}</span>
                </div>
                {lead.notes&&<div style={{fontSize:11,color:T.muted,marginTop:6,lineHeight:1.4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{lead.notes}</div>}
              </div>
            );
          })}
        </div>

        {/* Lead detail panel */}
        {selected&&(
          <div style={{position:"sticky",top:0}}>
            <Card>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={{fontSize:14,fontWeight:700,color:T.text}}>{selected.name}</div>
                <div style={{display:"flex",gap:6}}>
                  <Btn small ghost onClick={()=>{setEditLead(selected);setShowForm(true);}}>Edit</Btn>
                  <Btn small ghost onClick={()=>setSelected(null)}><Ic d={ICONS.x} size={11}/></Btn>
                </div>
              </div>

              {/* Sub tabs */}
              <div style={{display:"flex",gap:3,background:T.bg2,borderRadius:8,padding:3,marginBottom:14}}>
                {["info","actions","ai"].map(t=>(
                  <button key={t} onClick={()=>setActiveLeadTab(t)} style={{flex:1,padding:"5px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",background:activeLeadTab===t?T.gold:T.bg2,color:activeLeadTab===t?T.bg0:T.muted,transition:"all 0.15s",textTransform:"capitalize"}}>{t==="ai"?"AI Help":t.charAt(0).toUpperCase()+t.slice(1)}</button>
                ))}
              </div>

              {activeLeadTab==="info"&&(
                <div>
                  {[["Phone",selected.phone],["Email",selected.email],["Company",selected.company],["Address",selected.addr],["Type",selected.type],["Budget",selected.budget],["Units",selected.units+" units"],["Source",selected.source],["Added",selected.created]].map(([k,v])=>(
                    <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${T.subtle}`,fontSize:12}}>
                      <span style={{color:T.muted}}>{k}</span>
                      <span style={{color:T.text,fontWeight:600}}>{v}</span>
                    </div>
                  ))}
                  {selected.notes&&(
                    <div style={{marginTop:10,fontSize:12,color:T.muted,lineHeight:1.5,background:T.bg2,borderRadius:7,padding:"9px 11px"}}>{selected.notes}</div>
                  )}
                  <div style={{marginTop:14}}>
                    <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Move to Stage</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                      {LEAD_STATUSES.map(s=>(
                        <button key={s.id} onClick={()=>{const updated={...selected,status:s.id};updateStatus(updated,s.id);setSelected(updated);}}
                          style={{padding:"4px 10px",borderRadius:20,fontSize:10,fontWeight:700,cursor:"pointer",border:`1px solid ${s.color}40`,background:selected.status===s.id?s.color+"18":T.bg2,color:selected.status===s.id?s.color:T.muted,transition:"all 0.15s"}}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{marginTop:12,display:"flex",gap:7}}>
                    <Btn small primary onClick={()=>{
                      const proj = {id:Date.now(),name:`${selected.addr} — ${selected.type}`,addr:selected.addr,client:selected.name,type:selected.type,value:0,status:"bidding",pct:0,phases:[0,0,0,0,0,0,0],openCOs:0,openRFIs:0,walk:{access:{},plumbing:{},walls:{},owner:{}},redFlags:{},walkComplete:false,notes:`Lead converted from pipeline. Contact: ${selected.phone} | ${selected.email}. Budget: ${selected.budget}.`,desc:""};
                      PROJECTS=[proj,...PROJECTS];setProjects(p=>[proj,...p]);
                      const updated={...selected,status:"won"};saveLead(updated);
                      setView("project-detail");setActiveProject(proj);
                    }} style={{flex:1,justifyContent:"center"}}>Convert to Project</Btn>
                    <Btn small danger onClick={()=>deleteLead(selected.id)}>Delete</Btn>
                  </div>
                </div>
              )}

              {activeLeadTab==="actions"&&(
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Quick Actions</div>
                  {[
                    {label:"Call now",icon:"📞",action:()=>window.open(`tel:${selected.phone}`)},
                    {label:"Send email",icon:"✉️",action:()=>window.open(`mailto:${selected.email}?subject=Grok Construction — ${selected.type} at ${selected.addr}&body=Hi ${selected.name.split(" ")[0]},%0D%0A%0D%0AThank you for your interest in working with Grok Construction Group Inc. I would love to schedule a site visit to discuss your project at ${selected.addr}.%0D%0A%0D%0APlease let me know your availability.%0D%0A%0D%0ABest regards,%0D%0AGrok Construction Group Inc%0D%0ACSLB #1152750`)},
                    {label:"Schedule site walk",icon:"📅",action:()=>window.open(`mailto:${selected.email}?subject=Site Walk — ${selected.addr}&body=Hi ${selected.name.split(" ")[0]},%0D%0A%0D%0AI would like to schedule a site walkthrough for ${selected.addr}. Are you available this week?`)},
                    {label:"Send estimate request",icon:"💰",action:()=>setView("ai-agent")},
                  ].map(a=>(
                    <button key={a.label} onClick={a.action} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"10px 12px",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,cursor:"pointer",marginBottom:7,transition:"border-color 0.15s",color:T.text,fontSize:12,fontWeight:600,textAlign:"left"}}
                      onMouseEnter={e=>e.currentTarget.style.borderColor=T.goldDim}
                      onMouseLeave={e=>e.currentTarget.style.borderColor=T.subtle}>
                      <span style={{fontSize:16}}>{a.icon}</span>{a.label}
                    </button>
                  ))}
                  <Divider/>
                  <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Add Note</div>
                  <textarea placeholder="Call notes, meeting outcomes, next steps..."
                    onBlur={e=>{if(e.target.value.trim()){const updated={...selected,notes:selected.notes?selected.notes+"\n"+new Date().toLocaleDateString()+": "+e.target.value:e.target.value,lastContact:new Date().toLocaleDateString()};saveLead(updated);setSelected(updated);e.target.value="";}}}
                    rows={3} style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit"}}/>
                </div>
              )}

              {activeLeadTab==="ai"&&(
                <div>
                  <div style={{fontSize:11,color:T.muted,marginBottom:10,lineHeight:1.5}}>AI will analyze this lead and give you a follow-up strategy, talking points, and risk flags.</div>
                  <Btn primary onClick={async()=>{
                    const key="proxy"; // proxy handles auth
                    if(!key){alert("Add API key in AI Agent first.");return;}
                    setAiLoading(true);setAiResult("");
                    await callAI(
                      "You are a business development advisor for Grok Construction Group Inc (CSLB #1152750), a GC in SF Bay Area. Give specific, actionable advice for this lead.",
                      `Lead: ${selected.name} (${selected.company})\nPhone: ${selected.phone}\nEmail: ${selected.email}\nProject: ${selected.type} at ${selected.addr}\nBudget: ${selected.budget}\nUnits: ${selected.units}\nStatus: ${selected.status}\nSource: ${selected.source}\nNotes: ${selected.notes}\n\nProvide:\n1. FOLLOW-UP STRATEGY — exactly what to say/send next\n2. KEY TALKING POINTS for Grok's value prop for this project type\n3. DEAL RISKS — what could kill this deal\n4. QUESTIONS TO ASK at the site walk\n5. ESTIMATE RANGE based on project type and budget`,
                      t=>setAiResult(p=>p+t),
                      key
                    );
                    setAiLoading(false);
                  }} style={{width:"100%",justifyContent:"center",background:`linear-gradient(135deg,${T.purple},${T.blue})`,color:"white",marginBottom:10}}>
                    {aiLoading?<><Spin/>Analyzing lead...</>:<><Ic d={ICONS.robot} size={13}/>Analyze This Lead</>}
                  </Btn>
                  {aiResult&&(
                    <div style={{background:T.bg0,borderRadius:8,padding:"10px 12px",fontSize:11,color:T.text,lineHeight:1.7,whiteSpace:"pre-wrap",maxHeight:300,overflowY:"auto",fontFamily:"monospace"}}>
                      {aiResult}
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        )}
      </div>

      {/* AI Pipeline output */}
      {aiResult&&activeLeadTab!=="ai"&&(
        <Card style={{marginTop:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text}}>AI Pipeline Analysis</div>
            <Btn small ghost onClick={()=>setAiResult("")}><Ic d={ICONS.x} size={11}/>Clear</Btn>
          </div>
          <div style={{background:T.bg0,borderRadius:8,padding:"12px 14px",fontSize:12,color:T.text,lineHeight:1.7,whiteSpace:"pre-wrap",maxHeight:400,overflowY:"auto",fontFamily:"monospace"}}>
            {aiResult}{aiLoading&&<Spin/>}
          </div>
        </Card>
      )}

      {showForm&&<LeadForm lead={editLead} onSave={saveLead} onClose={()=>{setShowForm(false);setEditLead(null);}}/>}
    </div>
  );
}

// ─── CITY BUILDING DEPARTMENT DATABASE ───────────────────────────────────────
const BUILDING_DEPTS = {
  "San Francisco": {
    name: "SF Department of Building Inspection",
    abbr: "SF DBI",
    phone: "(628) 652-3700",
    inspection_phone: "(628) 652-3700",
    automated_line: "(628) 652-3700 — Press 1 for inspections, then enter permit #",
    website: "https://sfdbi.org",
    schedule_url: "https://aca.accela.com/CCSF",
    online_scheduling: true,
    hours: "Mon–Fri 8AM–5PM",
    inspection_request_cutoff: "By 3:30PM for next-day inspection",
    avg_wait: "1–3 business days",
    notes: "Use ACA Accela portal to schedule online. Have permit # and property address ready.",
    key_contacts: {
      "General Inspection": "(628) 652-3700",
      "Electrical Inspection": "(628) 652-3700 opt 3",
      "Plumbing Inspection": "(628) 652-3700 opt 4",
      "Building Inspection": "(628) 652-3700 opt 2",
    }
  },
  "Oakland": {
    name: "Oakland Building Services Division",
    abbr: "Oakland BSD",
    phone: "(510) 238-3444",
    inspection_phone: "(510) 238-3444",
    automated_line: "(510) 238-3444 — automated system available 24/7",
    website: "https://www.oaklandca.gov/departments/planning-building",
    schedule_url: "https://aca.accela.com/oakland",
    online_scheduling: true,
    hours: "Mon–Fri 8AM–4PM",
    inspection_request_cutoff: "By 4PM for next-day inspection",
    avg_wait: "2–5 business days",
    notes: "Online scheduling via Accela. Automated phone system for after-hours requests.",
    key_contacts: {
      "General": "(510) 238-3444",
      "Inspection Request": "(510) 238-3444 opt 1",
    }
  },
  "San Jose": {
    name: "San Jose Development Services",
    abbr: "SJ Dev Services",
    phone: "(408) 535-3555",
    inspection_phone: "(408) 535-3560",
    automated_line: "(408) 535-3560 — 24-hour automated inspection scheduling",
    website: "https://www.sanjoseca.gov/business/permits-licenses",
    schedule_url: "https://sjpermits.org",
    online_scheduling: true,
    hours: "Mon–Fri 7:30AM–5PM",
    inspection_request_cutoff: "By 3PM for next-day inspection",
    avg_wait: "1–2 business days",
    notes: "sjpermits.org for online scheduling. 24-hour automated phone line.",
    key_contacts: {
      "Inspections": "(408) 535-3560",
      "Permits": "(408) 535-3555",
    }
  },
  "Los Angeles": {
    name: "LA Department of Building and Safety",
    abbr: "LADBS",
    phone: "(213) 482-0000",
    inspection_phone: "(888) LA-4-BUILD",
    automated_line: "(888) 524-2845 — 24-hour IVR inspection scheduling",
    website: "https://www.ladbs.org",
    schedule_url: "https://ladbsservices2.lacity.org",
    online_scheduling: true,
    hours: "Mon–Fri 7AM–4:30PM",
    inspection_request_cutoff: "By 5PM for next-business-day inspection",
    avg_wait: "1–5 business days",
    notes: "LADBSservices2 online portal. IVR phone 24/7. Have permit # ready.",
    key_contacts: {
      "IVR Inspections": "(888) 524-2845",
      "Main Office": "(213) 482-0000",
    }
  },
  "Sacramento": {
    name: "Sacramento Community Development",
    abbr: "Sacramento CDD",
    phone: "(916) 808-5616",
    inspection_phone: "(916) 808-5616",
    automated_line: "(916) 808-5616 — option 2 for inspection scheduling",
    website: "https://www.cityofsacramento.org/community-development",
    schedule_url: "https://sacramentoca.gov/permits",
    online_scheduling: true,
    hours: "Mon–Fri 8AM–5PM",
    inspection_request_cutoff: "By 3PM for next-day inspection",
    avg_wait: "2–4 business days",
    notes: "Online portal available. Call for automated scheduling.",
    key_contacts: {
      "Inspections": "(916) 808-5616 opt 2",
    }
  },
  "San Diego": {
    name: "San Diego Development Services",
    abbr: "SD DSD",
    phone: "(619) 446-5000",
    inspection_phone: "(619) 446-5300",
    automated_line: "(619) 446-5300 — IVR available Mon–Fri 7AM–4PM",
    website: "https://www.sandiego.gov/development-services",
    schedule_url: "https://aca.sandiego.gov",
    online_scheduling: true,
    hours: "Mon–Fri 8AM–5PM",
    inspection_request_cutoff: "By 3:30PM for next-day",
    avg_wait: "2–5 business days",
    notes: "ACA online portal. IVR phone for automated scheduling.",
    key_contacts: {
      "Inspections": "(619) 446-5300",
      "Main": "(619) 446-5000",
    }
  },
  "New York City": {
    name: "NYC Department of Buildings",
    abbr: "NYC DOB",
    phone: "(212) 566-5000",
    inspection_phone: "(212) 566-5000",
    automated_line: "Schedule via DOB NOW online portal — no phone scheduling",
    website: "https://www.nyc.gov/site/buildings",
    schedule_url: "https://a810-bisweb.nyc.gov",
    online_scheduling: true,
    hours: "Mon–Fri 8AM–5PM",
    inspection_request_cutoff: "Varies by borough",
    avg_wait: "3–10 business days",
    notes: "DOB NOW portal required. Different borough offices. Have BIS # ready.",
    key_contacts: {
      "DOB NOW Portal": "dobonline.buildings.nyc.gov",
      "Main": "(212) 566-5000",
    }
  },
  "Seattle": {
    name: "Seattle Department of Construction & Inspections",
    abbr: "SDCI",
    phone: "(206) 684-8850",
    inspection_phone: "(206) 684-8850",
    automated_line: "(206) 684-8850 — online scheduling preferred",
    website: "https://www.seattle.gov/sdci",
    schedule_url: "https://cosaccela.seattle.gov",
    online_scheduling: true,
    hours: "Mon–Fri 8AM–5PM",
    inspection_request_cutoff: "By 3PM for next-day",
    avg_wait: "2–5 business days",
    notes: "Accela online portal. Phone available but online preferred.",
    key_contacts: {
      "Inspections": "(206) 684-8850",
    }
  },
  "Houston": {
    name: "Houston Dept of Planning & Development",
    abbr: "Houston P&D",
    phone: "(832) 394-8800",
    inspection_phone: "(832) 394-8800",
    automated_line: "(832) 394-8800 — 24-hour automated system available",
    website: "https://www.houstontx.gov/planning",
    schedule_url: "https://permits.houstontx.gov",
    online_scheduling: true,
    hours: "Mon–Fri 8AM–5PM",
    inspection_request_cutoff: "By 4PM for next-day",
    avg_wait: "1–3 business days",
    notes: "Online portal and 24-hr automated phone.",
    key_contacts: {
      "Main": "(832) 394-8800",
    }
  },
  "Chicago": {
    name: "Chicago Dept of Buildings",
    abbr: "Chicago DOB",
    phone: "(312) 744-3449",
    inspection_phone: "(312) 744-3449",
    automated_line: "(312) 744-3449 — automated scheduling available",
    website: "https://www.chicago.gov/buildings",
    schedule_url: "https://webapps1.chicago.gov/buildingrecords",
    online_scheduling: true,
    hours: "Mon–Fri 8AM–4:30PM",
    inspection_request_cutoff: "By 3PM for next-day",
    avg_wait: "3–7 business days",
    notes: "Online records portal. Call for inspection scheduling.",
    key_contacts: {
      "Main": "(312) 744-3449",
    }
  },
  "Default": {
    name: "Local Building Department",
    abbr: "Building Dept",
    phone: "Search your city name + 'building department'",
    inspection_phone: "Call main number for inspection department",
    automated_line: "Many cities have automated IVR systems — call main number and listen for options",
    website: "Search '[your city] building permits inspections'",
    schedule_url: "Check city website for online scheduling portal",
    online_scheduling: false,
    hours: "Typically Mon–Fri 8AM–5PM",
    inspection_request_cutoff: "Usually by 3–4PM for next-day inspection",
    avg_wait: "1–5 business days",
    notes: "Always have your permit number ready. Request inspection in writing (email) when possible for documentation.",
    key_contacts: {}
  }
};

function getBuildingDept(projCity, projAddr) {
  if (!projCity && !projAddr) return BUILDING_DEPTS["Default"];
  const loc = (projCity || projAddr || "").toLowerCase();
  if (loc.includes("san francisco") || loc.includes("sf") || loc.includes("tenderloin") || loc.includes("mission") || loc.includes("soma")) return BUILDING_DEPTS["San Francisco"];
  if (loc.includes("oakland")) return BUILDING_DEPTS["Oakland"];
  if (loc.includes("san jose")) return BUILDING_DEPTS["San Jose"];
  if (loc.includes("los angeles") || loc.includes("l.a.") || loc.includes("la,")) return BUILDING_DEPTS["Los Angeles"];
  if (loc.includes("sacramento")) return BUILDING_DEPTS["Sacramento"];
  if (loc.includes("san diego")) return BUILDING_DEPTS["San Diego"];
  if (loc.includes("new york") || loc.includes("nyc") || loc.includes("brooklyn") || loc.includes("manhattan")) return BUILDING_DEPTS["New York City"];
  if (loc.includes("seattle")) return BUILDING_DEPTS["Seattle"];
  if (loc.includes("houston")) return BUILDING_DEPTS["Houston"];
  if (loc.includes("chicago")) return BUILDING_DEPTS["Chicago"];
  return BUILDING_DEPTS["Default"];
}

// ─── PROJECT CALENDAR ─────────────────────────────────────────────────────────
function ProjectCalendarView({proj, session}) {
  const schedKey = "grok_schedule_" + proj?.id;
  const logKey   = "grok_daily_log_" + proj?.id;
  const inspKey  = "grok_calendar_insp_" + proj?.id;

  const [schedule, setSchedule] = useState(()=>{try{return JSON.parse(localStorage.getItem(schedKey)||"null");}catch{return null;}});
  const [logs, setLogs]         = useState(()=>{try{return JSON.parse(localStorage.getItem(logKey)||"[]");}catch{return [];}});
  const [inspections, setInspections] = useState(()=>{try{return JSON.parse(localStorage.getItem(inspKey)||"[]");}catch{return [];}});
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [showScheduleInsp, setShowScheduleInsp] = useState(false);
  const [newInsp, setNewInsp] = useState({type:"",date:"",time:"",inspector:"",permit_no:"",notes:"",status:"scheduled"});
  const [view, setView] = useState("month"); // month | week

  const dept = proj?.building_dept ? {
    name: proj.building_dept,
    abbr: proj.building_dept.split(" ").slice(-2).join(" "),
    phone: proj.building_dept_phone || "See project settings",
    inspection_phone: proj.building_dept_phone || "See project settings",
    automated_line: proj.building_dept_phone ? proj.building_dept_phone + " — call for inspection scheduling" : "Set phone in project settings",
    website: proj.building_dept_url || "See project settings",
    schedule_url: proj.building_dept_url || "See project settings",
    online_scheduling: !!proj.building_dept_url,
    hours: "Mon–Fri 8AM–5PM",
    inspection_request_cutoff: proj.inspection_cutoff || "Check with department",
    avg_wait: "1–5 business days",
    notes: proj.inspector_name ? "Assigned inspector: " + proj.inspector_name : "Update building dept info in project settings",
    key_contacts: proj.building_dept_phone ? {"Inspections": proj.building_dept_phone} : {},
  } : getBuildingDept(proj?.addr, proj?.addr);
  const apiKey = "proxy"; // Using Supabase AI proxy

  const saveInspections = (updated) => {
    setInspections(updated);
    try{localStorage.setItem(inspKey, JSON.stringify(updated));}catch(e){}
  };

  const addInspection = () => {
    if (!newInsp.type || !newInsp.date) return;
    const insp = {...newInsp, id:"insp_"+Date.now(), created:new Date().toISOString()};
    saveInspections([...inspections, insp]);
    setNewInsp({type:"",date:"",time:"",inspector:"",permit_no:"",notes:"",status:"scheduled"});
    setShowScheduleInsp(false);
  };

  const updateInspStatus = (id, status) => {
    saveInspections(inspections.map(i=>i.id===id?{...i,status}:i));
  };

  // Build calendar data
  const year  = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month+1, 0).getDate();

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    const events = [];

    // Daily logs
    logs.filter(l=>l.date===dateStr).forEach(log=>{
      events.push({type:"log", icon:"📋", label:`Daily Log — ${log.weather} ${log.temp}°F, ${log.crew_count} crew`, color:T.blue, data:log});
    });

    // Schedule tasks
    schedule?.weeks?.forEach(week=>{
      week.days?.forEach(d=>{
        if (d.date===dateStr) {
          d.tasks?.forEach(task=>{
            events.push({
              type: task.type==="inspection"?"inspection":"task",
              icon: task.type==="inspection"?"🔍":task.type==="delivery"?"📦":task.type==="meeting"?"👥":"🔨",
              label: task.task + (task.assignee?" — "+task.assignee:""),
              color: task.type==="inspection"?T.amber:task.type==="delivery"?T.teal:task.critical?T.red:T.blue,
              data: task,
              done: task.done,
            });
          });
        }
      });
    });

    // Manual inspections
    inspections.filter(i=>i.date===dateStr).forEach(insp=>{
      events.push({
        type:"inspection",
        icon:"🔍",
        label:`${insp.type} Inspection${insp.time?" @ "+insp.time:""}`,
        color: insp.status==="passed"?T.green:insp.status==="failed"?T.red:T.amber,
        data: insp,
      });
    });

    return events;
  };

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;

  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  const INSP_TYPES = ["Foundation","Framing","Rough Plumbing","Rough Electrical","Rough Mechanical","Insulation","Drywall Nail","Title 24 HERS","Accessibility","Final Building","Final Plumbing","Final Electrical","Final Mechanical","Special Inspection"];

  return (
    <div>
      {/* City Building Dept Card */}
      <div style={{background:T.bg1,border:"1px solid "+T.teal+"30",borderRadius:12,padding:"14px 16px",marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div style={{flex:1}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
              <span style={{fontSize:16}}>🏛</span>
              <div style={{fontSize:13,fontWeight:700,color:T.text}}>{dept.name}</div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:10}}>
              {[
                {l:"📞 Main Phone",     v:dept.phone},
                {l:"⏱ Cutoff",         v:dept.inspection_request_cutoff},
                {l:"📅 Wait Time",      v:dept.avg_wait},
              ].map(({l,v})=>(
                <div key={l} style={{background:T.bg2,borderRadius:7,padding:"7px 10px"}}>
                  <div style={{fontSize:9,color:T.muted,fontWeight:700,marginBottom:2}}>{l}</div>
                  <div style={{fontSize:11,color:T.text,fontWeight:600}}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:8,padding:"9px 12px",marginBottom:8}}>
              <div style={{fontSize:10,fontWeight:700,color:T.amber,marginBottom:3}}>📱 How to Schedule an Inspection</div>
              <div style={{fontSize:11,color:T.muted,lineHeight:1.7}}>{dept.automated_line}</div>
              {dept.online_scheduling&&<div style={{fontSize:10,color:T.blue,marginTop:4}}>🌐 Online: <span style={{fontDecoration:"underline"}}>{dept.schedule_url}</span></div>}
            </div>
            {Object.keys(dept.key_contacts).length>0&&(
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {Object.entries(dept.key_contacts).map(([type,num])=>(
                  <a key={type} href={"tel:"+num.replace(/[^\d]/g,"")}
                    style={{fontSize:10,fontWeight:600,background:T.blueBg,color:T.blue,border:"1px solid "+T.blue+"30",borderRadius:6,padding:"3px 9px",textDecoration:"none",cursor:"pointer"}}>
                    📞 {type}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{marginTop:10,fontSize:10,color:T.muted,fontStyle:"italic",lineHeight:1.6}}>
          💡 {dept.notes}
        </div>
      </div>

      {/* Calendar header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button onClick={()=>setCurrentDate(new Date(year,month-1,1))}
            style={{background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,width:28,height:28,cursor:"pointer",color:T.muted,fontSize:14}}>‹</button>
          <div style={{fontSize:14,fontWeight:700,color:T.text,minWidth:140,textAlign:"center"}}>{MONTHS[month]} {year}</div>
          <button onClick={()=>setCurrentDate(new Date(year,month+1,1))}
            style={{background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,width:28,height:28,cursor:"pointer",color:T.muted,fontSize:14}}>›</button>
          <button onClick={()=>setCurrentDate(new Date())}
            style={{fontSize:10,fontWeight:600,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"4px 9px",cursor:"pointer",color:T.muted}}>Today</button>
        </div>
        <Btn small primary onClick={()=>setShowScheduleInsp(true)}>
          🔍 Schedule Inspection
        </Btn>
      </div>

      {/* Schedule inspection form */}
      {showScheduleInsp&&(
        <Card style={{marginBottom:12,border:"1px solid "+T.amber+"30"}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>📋 Log Scheduled Inspection</div>
          <div style={{background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"10px 12px",marginBottom:12,fontSize:11,color:T.muted,lineHeight:1.7}}>
            <strong style={{color:T.amber}}>To schedule:</strong> Call <strong style={{color:T.text}}>{dept.inspection_phone}</strong> or visit <strong style={{color:T.text}}>{dept.schedule_url}</strong><br/>
            {dept.automated_line}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Inspection Type *</div>
              <select value={newInsp.type} onChange={e=>setNewInsp(p=>({...p,type:e.target.value}))}
                style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:11,fontFamily:"inherit"}}>
                <option value="">Select type...</option>
                {INSP_TYPES.map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Date *</div>
              <input type="date" value={newInsp.date} onChange={e=>setNewInsp(p=>({...p,date:e.target.value}))}
                style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:11,fontFamily:"inherit"}}/>
            </div>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Time</div>
              <input type="time" value={newInsp.time} onChange={e=>setNewInsp(p=>({...p,time:e.target.value}))}
                style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:11,fontFamily:"inherit"}}/>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Inspector Name</div>
              <input value={newInsp.inspector} onChange={e=>setNewInsp(p=>({...p,inspector:e.target.value}))} placeholder="Inspector name (if known)"
                style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:11,fontFamily:"inherit"}}/>
            </div>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Permit #</div>
              <input value={newInsp.permit_no} onChange={e=>setNewInsp(p=>({...p,permit_no:e.target.value}))} placeholder="202400123456"
                style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:11,fontFamily:"inherit"}}/>
            </div>
          </div>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Notes / Pre-inspection checklist</div>
            <textarea value={newInsp.notes} onChange={e=>setNewInsp(p=>({...p,notes:e.target.value}))}
              placeholder="What needs to be ready before inspection..." rows={2}
              style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:11,fontFamily:"inherit",resize:"none"}}/>
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn ghost small onClick={()=>setShowScheduleInsp(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
            <Btn primary small disabled={!newInsp.type||!newInsp.date} onClick={addInspection}
              style={{flex:2,justifyContent:"center",background:T.amber,color:T.bg0}}>
              Add to Calendar
            </Btn>
          </div>
        </Card>
      )}

      {/* Monthly calendar grid */}
      <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:12,overflow:"hidden",marginBottom:14}}>
        {/* Day headers */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",background:T.bg2,borderBottom:"1px solid "+T.subtle}}>
          {DAYS.map(d=>(
            <div key={d} style={{padding:"8px 4px",textAlign:"center",fontSize:10,fontWeight:700,color:T.muted}}>{d}</div>
          ))}
        </div>
        {/* Calendar cells */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)"}}>
          {/* Empty cells before first day */}
          {Array(firstDay).fill(null).map((_,i)=>(
            <div key={"empty"+i} style={{minHeight:72,borderRight:"1px solid "+T.subtle,borderBottom:"1px solid "+T.subtle,background:T.bg2}}/>
          ))}
          {/* Day cells */}
          {Array(daysInMonth).fill(null).map((_,i)=>{
            const day = i+1;
            const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
            const events = getEventsForDay(day);
            const isToday = dateStr === todayStr;
            const isSelected = selectedDay === day;
            return (
              <div key={day} onClick={()=>setSelectedDay(isSelected?null:day)}
                style={{minHeight:72,borderRight:"1px solid "+T.subtle,borderBottom:"1px solid "+T.subtle,padding:"4px",cursor:"pointer",background:isSelected?T.goldBg:isToday?T.bg2:"transparent",transition:"background 0.15s"}}
                onMouseEnter={e=>{if(!isSelected&&!isToday)e.currentTarget.style.background=T.bg2;}}
                onMouseLeave={e=>{if(!isSelected&&!isToday)e.currentTarget.style.background="transparent";}}>
                <div style={{fontSize:11,fontWeight:isToday?800:500,color:isToday?T.gold:T.muted,marginBottom:3,width:20,height:20,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",background:isToday?T.goldBg:"transparent"}}>
                  {day}
                </div>
                {events.slice(0,3).map((ev,ei)=>(
                  <div key={ei} style={{fontSize:9,color:ev.color,background:ev.color+"15",borderRadius:4,padding:"1px 4px",marginBottom:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",opacity:ev.done?0.5:1}}>
                    {ev.icon} {ev.label.slice(0,22)}{ev.label.length>22?"...":""}
                  </div>
                ))}
                {events.length>3&&<div style={{fontSize:8,color:T.muted,paddingLeft:4}}>+{events.length-3} more</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected day detail */}
      {selectedDay&&(()=>{
        const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(selectedDay).padStart(2,"0")}`;
        const events = getEventsForDay(selectedDay);
        const dayLogs = logs.filter(l=>l.date===dateStr);
        const dayInspections = inspections.filter(i=>i.date===dateStr);
        return (
          <Card style={{marginBottom:14}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:10}}>
              {MONTHS[month]} {selectedDay}, {year}
              <span style={{fontSize:11,color:T.muted,fontWeight:400,marginLeft:8}}>{events.length} events</span>
            </div>
            {events.length===0&&(
              <div style={{fontSize:12,color:T.muted,fontStyle:"italic"}}>No events on this day.</div>
            )}
            {events.map((ev,i)=>(
              <div key={i} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"8px 10px",background:T.bg2,borderRadius:8,marginBottom:5,borderLeft:"3px solid "+ev.color}}>
                <span style={{fontSize:14,flexShrink:0}}>{ev.icon}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:600,color:ev.done?T.muted:T.text,textDecoration:ev.done?"line-through":"none"}}>{ev.label}</div>
                  {ev.type==="log"&&ev.data&&(
                    <div style={{fontSize:10,color:T.muted,lineHeight:1.6,marginTop:3}}>
                      {ev.data.work_done&&<div><strong>Work:</strong> {ev.data.work_done.slice(0,120)}</div>}
                      {ev.data.delays&&<div style={{color:T.amber}}><strong>Delays:</strong> {ev.data.delays}</div>}
                    </div>
                  )}
                  {ev.type==="inspection"&&ev.data?.status&&(
                    <div style={{marginTop:4,display:"flex",gap:6,alignItems:"center"}}>
                      <select value={ev.data.status} onChange={e=>{if(ev.data.id)updateInspStatus(ev.data.id,e.target.value);}}
                        style={{fontSize:9,fontWeight:700,background:ev.data.status==="passed"?T.greenBg:ev.data.status==="failed"?T.redBg:T.amberBg,color:ev.data.status==="passed"?T.green:ev.data.status==="failed"?T.red:T.amber,border:"none",borderRadius:10,padding:"2px 8px",cursor:"pointer",fontFamily:"inherit"}}>
                        {["scheduled","passed","failed","rescheduled"].map(s=><option key={s} value={s}>{s.toUpperCase()}</option>)}
                      </select>
                      {ev.data.permit_no&&<span style={{fontSize:9,color:T.muted}}>Permit: {ev.data.permit_no}</span>}
                      {ev.data.inspector&&<span style={{fontSize:9,color:T.muted}}>Inspector: {ev.data.inspector}</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </Card>
        );
      })()}

      {/* Upcoming inspections list */}
      {inspections.filter(i=>i.status==="scheduled"&&i.date>=todayStr).length>0&&(
        <Card>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:10}}>🔍 Upcoming Inspections</div>
          {inspections.filter(i=>i.status==="scheduled"&&i.date>=todayStr).sort((a,b)=>a.date>b.date?1:-1).map(insp=>(
            <div key={insp.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 12px",background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:8,marginBottom:6}}>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:T.text}}>{insp.type} Inspection</div>
                <div style={{fontSize:10,color:T.muted}}>{insp.date}{insp.time?" @ "+insp.time:""}{insp.permit_no?" · Permit: "+insp.permit_no:""}</div>
              </div>
              <div style={{display:"flex",gap:6}}>
                <a href={"tel:"+dept.inspection_phone.replace(/[^\d]/g,"")}
                  style={{fontSize:10,fontWeight:700,background:T.teal,color:"white",border:"none",borderRadius:6,padding:"4px 9px",textDecoration:"none"}}>
                  📞 Call {dept.abbr}
                </a>
                <select value={insp.status} onChange={e=>updateInspStatus(insp.id,e.target.value)}
                  style={{fontSize:9,fontWeight:700,background:T.amberBg,color:T.amber,border:"1px solid "+T.amber+"30",borderRadius:6,padding:"3px 6px",cursor:"pointer",fontFamily:"inherit"}}>
                  {["scheduled","passed","failed","rescheduled"].map(s=><option key={s} value={s}>{s.toUpperCase()}</option>)}
                </select>
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}



// ─── PM SUB-PANEL (Calendar + Daily Log + Schedule) ──────────────────────────

// ─── CRITICAL PATH SCHEDULE PANEL — embeds directly in project PM tab ────────
function CriticalPathPanel({proj, session}) {
  const apiKey = "proxy"; // Using Supabase AI proxy
  const [scheduleInput, setScheduleInput] = React.useState("");
  const [scheduleResult, setScheduleResult] = React.useState(()=>{
    try{return localStorage.getItem("grok_crit_"+proj?.id)||"";}catch{return "";}
  });
  const [scheduleLoading, setScheduleLoading] = React.useState(false);
  const [scheduleType, setScheduleType] = React.useState(proj?.type||"SRO");
  const [scheduleDuration, setScheduleDuration] = React.useState("8");
  const [scheduleWorkers, setScheduleWorkers] = React.useState("4");
  const schedRef = React.useRef(null);
  React.useEffect(()=>{if(schedRef.current)schedRef.current.scrollTop=schedRef.current.scrollHeight;},[scheduleResult]);

  const runSchedule = async() => {
    const key = apiKey;
    
    setScheduleLoading(true); setScheduleResult("");
    const scopeText = scheduleInput.trim()||proj?.desc||"Full renovation scope";
    try {
      await callAI(
        `You are a construction scheduling expert for Bay Area GCs. Generate a precise, day-by-day critical path schedule for a ${scheduleType} project.

Rules:
- Show SPECIFIC dates (use real calendar, starting from today)
- List EVERY phase with exact duration in working days
- Mark INSPECTION HOLD POINTS where work stops pending inspection
- Include SUBCONTRACTOR coordination sequence
- Note MATERIAL LEAD TIMES that affect schedule
- Flag SF/Bay Area specific risks (permit timing, holiday delays, rain season)
- Crew size: ${scheduleWorkers} workers | Target duration: ${scheduleDuration} weeks

FORMAT:
PHASE 1 — [Name] (Days 1–X)
□ Task: [specific task] — [duration] — [trade]
□ Inspection: [type] — [hold point Y/N]
...material lead time warnings...`,
        `Project: ${proj?.name||"Renovation"}
Address: ${proj?.addr||"Bay Area, CA"}
Type: ${scheduleType}
Target Duration: ${scheduleDuration} weeks
Crew Size: ${scheduleWorkers} workers

Scope:
${scopeText}`,
        (text)=>setScheduleResult(prev=>{
          const updated = prev+text;
          try{localStorage.setItem("grok_crit_"+proj?.id, updated);}catch(e){}
          return updated;
        }),
        key
      );
    } catch(e) { setScheduleResult("Error: "+e.message); }
    setScheduleLoading(false);
  };

  const inp = {width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit"};

  return (
    <div>
      <div style={{background:T.blueBg,borderLeft:"3px solid "+T.blue,borderRadius:7,padding:"9px 12px",marginBottom:12,fontSize:11,color:T.muted,lineHeight:1.5}}>
        AI generates a <strong style={{color:T.blue}}>day-by-day critical path</strong> with phase sequence, inspection hold points, sub coordination order, material lead times, and SF-specific schedule risks.
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10}}>
        <div>
          <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:4}}>Building Type</div>
          <select value={scheduleType} onChange={e=>setScheduleType(e.target.value)} style={inp}>
            {["SRO","Affordable Housing","Senior Housing","Market Rate Multi-Family","Single Family","Commercial TI","Maintenance"].map(t=><option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:4}}>Duration Goal</div>
          <select value={scheduleDuration} onChange={e=>setScheduleDuration(e.target.value)} style={inp}>
            {["4","6","8","10","12","16","20","24"].map(w=><option key={w} value={w}>{w} weeks</option>)}
          </select>
        </div>
        <div>
          <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:4}}>Crew Size</div>
          <select value={scheduleWorkers} onChange={e=>setScheduleWorkers(e.target.value)} style={inp}>
            {["2","3","4","5","6","8","10","12"].map(n=><option key={n} value={n}>{n} workers</option>)}
          </select>
        </div>
      </div>
      <div style={{marginBottom:10}}>
        <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:4}}>Additional scope notes (optional — uses project scope by default)</div>
        <textarea value={scheduleInput} onChange={e=>setScheduleInput(e.target.value)}
          placeholder={"Scope auto-loaded from project: "+((proj?.desc||"").slice(0,80)||"Add project description in project settings")}
          rows={3} style={{...inp,resize:"vertical"}}/>
      </div>
      <div style={{display:"flex",gap:7,marginBottom:12}}>
        <Btn primary onClick={runSchedule} disabled={scheduleLoading}
          style={{flex:1,justifyContent:"center",background:T.blue,color:"white"}}>
          {scheduleLoading?<><Spin/>Building critical path...</>:"📅 Generate Critical Path Schedule"}
        </Btn>
        {scheduleResult&&<Btn ghost small onClick={()=>{setScheduleResult("");try{localStorage.removeItem("grok_crit_"+proj?.id);}catch(e){}}}>Clear</Btn>}
        {scheduleResult&&<Btn ghost small onClick={()=>{const a=document.createElement("a");a.href="data:text/plain;charset=utf-8,"+encodeURIComponent(scheduleResult);a.download=((proj?.name||"Project").split(" ")[0])+"_CriticalPath.txt";a.click();}}>↓ Save</Btn>}
      </div>
      {scheduleLoading&&<div style={{display:"flex",alignItems:"center",gap:8,color:T.muted,fontSize:11,marginBottom:8}}><Spin/>Building day-by-day critical path...</div>}
      {scheduleResult&&(
        <div ref={schedRef} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:9,padding:"14px 16px",fontSize:12,color:T.text,lineHeight:1.8,whiteSpace:"pre-wrap",maxHeight:500,overflowY:"auto",fontFamily:"monospace"}}>
          {scheduleResult}
        </div>
      )}
    </div>
  );
}

function PMSubPanel({proj, session}) {
  const [sub, setSub] = useState("calendar");
  return (
    <div>
      <div style={{display:"flex",gap:3,marginBottom:12,flexWrap:"wrap"}}>
        {[["calendar","📅 Calendar"],["schedule","📈 2-Wk Schedule"],["gantt","📊 Gantt"],["critpath","🤖 AI Schedule"],["dailylog","📋 Daily Log"]].map(([id,label])=>(
          <button key={id} onClick={()=>setSub(id)}
            style={{padding:"5px 12px",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",background:sub===id?T.gold:T.bg2,color:sub===id?T.bg0:T.muted,transition:"all 0.15s"}}>
            {label}
          </button>
        ))}
      </div>
      {sub==="calendar"&&<ProjectCalendarView proj={proj} session={session}/>}
      {sub==="schedule"&&<ScheduleWithInspections proj={proj} session={session}/>}
      {sub==="gantt"&&<GanttView proj={proj}/>}
      {sub==="critpath"&&<CriticalPathPanel proj={proj} session={session}/>}
      {sub==="dailylog"&&<DailyLogPanel proj={proj} session={session}/>}
    </div>
  );
}

// ─── SCHEDULE WITH AUTO INSPECTION CHECKPOINTS ────────────────────────────────
function ScheduleWithInspections({proj, session}) {
  return (
    <div>
      <div style={{background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:9,padding:"10px 14px",marginBottom:12,fontSize:11,color:T.muted,lineHeight:1.7}}>
        <strong style={{color:T.amber}}>Auto Inspection Logic:</strong> When rough-in tasks are scheduled, the system adds an inspection window 1 day after completion. Subs are notified to schedule their trade inspection before the next phase begins.
      </div>
      <SchedulePanel proj={proj} session={session} autoInspections={true}/>
    </div>
  );
}

// ─── ENHANCED AI PM — Daily Log, Schedule, Inspections, Materials ─────────────
function DailyLogPanel({proj, session=null}) {
  const T = useTheme();
  const key = "grok_daily_log_" + proj?.id;
  const cid = session?.companyId || window._grokCompanyId || "local";
  const isSub = session?.plan === "sub";
  const [logs, setLogs] = useState(()=>{try{return JSON.parse(localStorage.getItem(key)||"[]");}catch{return [];}});
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0,10),
    weather:"Clear", temp:"65",
    crew_count:"", superintendent:"",
    work_done:"", materials_used:"",
    delays:"", safety_notes:"",
    photos_taken:false, inspector_visit:false,
    // GC only
    subcontractors:"", equipment_on_site:"",
    visitors:"", next_day_plan:"",
  });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const ff = (k,v) => setForm(p=>({...p,[k]:v}));

  const WEATHER = ["Clear","Partly Cloudy","Overcast","Light Rain","Heavy Rain","Fog","Windy","Hot","Cold"];

  const saveLog = async () => {
    if(!form.work_done.trim()) return;
    setSaving(true);
    const log = {...form, id:"log_"+Date.now(), created:new Date().toISOString(), loggedBy: session?.ownerName||session?.subName||"GCG"};
    const updated = [log, ...logs];
    setLogs(updated);
    try { localStorage.setItem(key, JSON.stringify(updated)); } catch(e){}

    if(!isSub) {
      // GC: generate Word doc and save to project docs
      generateLogDoc(log);
    }
    setForm({date:new Date().toISOString().slice(0,10),weather:"Clear",temp:"65",crew_count:"",superintendent:"",work_done:"",materials_used:"",delays:"",safety_notes:"",photos_taken:false,inspector_visit:false,subcontractors:"",equipment_on_site:"",visitors:"",next_day_plan:""});
    setShowForm(false);
    setSaving(false);
  };

  const generateLogDoc = (log) => {
    // Get company settings
    const cs = (() => { try { return JSON.parse(localStorage.getItem("grok_company_settings_"+cid)||"{}"); } catch { return {}; }})();
    const coName = cs.company_name || "Grok Construction Group Inc";
    const cslb = cs.cslb || "CSLB #1152750";

    // Open printable daily log in new window
    const w = window.open("","_blank");
    const dateStr = new Date(log.date+"T12:00:00").toLocaleDateString("en-US",{weekday:"long",year:"numeric",month:"long",day:"numeric"});
    w.document.write(`<!DOCTYPE html><html><head><title>Daily Log — ${log.date}</title>
    <style>
      body{font-family:Arial,sans-serif;max-width:700px;margin:32px auto;padding:24px;color:#1a1a1a;font-size:13px;}
      .banner{background:#0F1623;padding:24px 28px;margin-bottom:0;}
      .brand{font-size:28px;font-weight:900;color:#D4A843;}
      .brand-sub{font-size:13px;color:#fff;font-weight:700;margin-top:4px;}
      .gold-bar{height:4px;background:#D4A843;margin-bottom:20px;}
      .meta{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:8px;margin-bottom:20px;}
      .meta-box{background:#f9f9f9;border-bottom:3px solid #D4A843;padding:10px 12px;}
      .meta-label{font-size:9px;font-weight:700;text-transform:uppercase;color:#888;margin-bottom:4px;}
      .meta-val{font-size:15px;font-weight:700;color:#0F1623;}
      .section{margin-bottom:16px;}
      .sec-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#888;margin-bottom:6px;padding-bottom:4px;border-bottom:1px solid #e0e0e0;}
      .sec-val{font-size:13px;color:#1a1a1a;line-height:1.6;min-height:24px;}
      .flags{display:flex;gap:12px;margin-bottom:16px;}
      .flag{background:#FDF6E3;border:1px solid #D4A843;border-radius:6px;padding:6px 12px;font-size:12px;font-weight:700;color:#8B6914;}
      .sig{display:grid;grid-template-columns:1fr 1fr;gap:32px;margin-top:32px;}
      .sig-line{border-top:2px solid #0F1623;margin-top:40px;padding-top:6px;font-size:10px;color:#888;}
      @media print{body{margin:0;padding:16px;}}
    </style></head><body>
    <div class="banner">
      <div class="brand">GROK</div>
      <div class="brand-sub">${coName}  ·  ${cslb}</div>
    </div>
    <div class="gold-bar"></div>
    <div style="font-size:18px;font-weight:800;color:#0F1623;margin-bottom:4px;">Daily Construction Log</div>
    <div style="font-size:13px;color:#888;margin-bottom:16px;">${dateStr}</div>
    <div class="meta">
      <div class="meta-box"><div class="meta-label">Project</div><div class="meta-val" style="font-size:12px">${proj?.name||"—"}</div></div>
      <div class="meta-box"><div class="meta-label">Weather</div><div class="meta-val">${log.weather} ${log.temp}°F</div></div>
      <div class="meta-box"><div class="meta-label">Crew on Site</div><div class="meta-val">${log.crew_count||"—"}</div></div>
      <div class="meta-box"><div class="meta-label">Superintendent</div><div class="meta-val" style="font-size:11px">${log.superintendent||"—"}</div></div>
    </div>
    ${(log.inspector_visit||log.photos_taken)?('<div class="flags">'+(log.inspector_visit?'<div class="flag">🔍 Inspector On Site</div>':'')+(log.photos_taken?'<div class="flag">📷 Photos Taken</div>':'')+'</div>'):''}
    <div class="section"><div class="sec-label">Work Completed Today</div><div class="sec-val">${log.work_done||"—"}</div></div>
    <div class="section"><div class="sec-label">Materials Used</div><div class="sec-val">${log.materials_used||"—"}</div></div>
    <div class="section"><div class="sec-label">Subcontractors on Site</div><div class="sec-val">${log.subcontractors||"None"}</div></div>
    <div class="section"><div class="sec-label">Equipment on Site</div><div class="sec-val">${log.equipment_on_site||"—"}</div></div>
    <div class="section"><div class="sec-label">Delays / Issues</div><div class="sec-val">${log.delays||"None"}</div></div>
    <div class="section"><div class="sec-label">Visitors</div><div class="sec-val">${log.visitors||"None"}</div></div>
    <div class="section"><div class="sec-label">Safety Notes</div><div class="sec-val">${log.safety_notes||"No incidents"}</div></div>
    <div class="section"><div class="sec-label">Next Day Plan</div><div class="sec-val">${log.next_day_plan||"—"}</div></div>
    <div class="sig">
      <div><div class="sig-line">Superintendent Signature  ·  Date</div><div style="margin-top:4px;font-size:11px;color:#666">${log.superintendent||coName}</div></div>
      <div><div class="sig-line">Printed Name & Title</div></div>
    </div>
    <script>setTimeout(()=>window.print(),400)</script>
    </body></html>`);
    w.document.close();

    // Also save to project docs folder
    const docsKey = "grok_proj_docs_" + cid + "_" + proj?.id;
    try {
      const docs = JSON.parse(localStorage.getItem(docsKey)||"[]");
      const doc = {
        id:"doc_"+Date.now(),
        name:`Daily Log — ${log.date} — ${proj?.name||"Project"}`,
        type:"Daily Log",
        folder:"daily",
        date:new Date().toLocaleDateString(),
        generated:true,
        logDate:log.date,
        notes:`Crew: ${log.crew_count||"?"} · ${log.weather} ${log.temp}°F · ${log.work_done?.slice(0,60)}...`,
      };
      localStorage.setItem(docsKey, JSON.stringify([doc,...docs]));
    } catch(e){}
  };

  const inputStyle = {width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"};

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>Daily Construction Log</div>
          <div style={{fontSize:10,color:T.muted,marginTop:2}}>{isSub?"Real-time updates · Syncs to project calendar":"GC view · Generates Word doc · Saves to calendar & project docs"}</div>
        </div>
        <Btn small primary onClick={()=>setShowForm(p=>!p)}><Ic d={ICONS.plus} size={11}/>New Entry</Btn>
      </div>

      {showForm&&(
        <div style={{background:T.bg1,border:`1px solid ${T.blue}30`,borderRadius:10,padding:"14px 16px",marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:12}}>
            {isSub ? "📋 Today's Report" : "📋 Daily Log — Generates Word Doc Automatically"}
          </div>

          {/* Row 1 — Date, Weather, Temp, Crew */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8,marginBottom:10}}>
            {[
              {k:"date",l:"Date",type:"date"},
              {k:"weather",l:"Weather",type:"select",opts:WEATHER},
              {k:"temp",l:"Temp °F",type:"number"},
              {k:"crew_count",l:"Crew on Site",type:"number"},
            ].map(({k,l,type,opts})=>(
              <div key={k}>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>{l}</div>
                {type==="select"
                  ? <select value={form[k]} onChange={e=>ff(k,e.target.value)} style={inputStyle}>{opts.map(o=><option key={o}>{o}</option>)}</select>
                  : <input type={type} value={form[k]} onChange={e=>ff(k,e.target.value)} style={inputStyle}/>
                }
              </div>
            ))}
          </div>

          {/* Superintendent — GC only */}
          {!isSub&&(
            <div style={{marginBottom:10}}>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Superintendent on Site</div>
              <input value={form.superintendent} onChange={e=>ff("superintendent",e.target.value)} placeholder="Name" style={inputStyle}/>
            </div>
          )}

          {/* Work done — always */}
          <div style={{marginBottom:8}}>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Work Completed Today *</div>
            <textarea value={form.work_done} onChange={e=>ff("work_done",e.target.value)}
              placeholder="Describe all work performed today..." rows={4}
              style={{...inputStyle,resize:"vertical"}}/>
          </div>

          {/* Sub-only simplified fields */}
          {isSub&&(
            <div style={{marginBottom:8}}>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Delays / Issues</div>
              <textarea value={form.delays} onChange={e=>ff("delays",e.target.value)}
                placeholder="Any delays or issues today?" rows={2}
                style={{...inputStyle,resize:"none"}}/>
            </div>
          )}

          {/* GC full fields */}
          {!isSub&&([
            ["materials_used","Materials Used","Materials delivered or used today...",2],
            ["subcontractors","Subcontractors on Site","Trades and company names...",2],
            ["equipment_on_site","Equipment on Site","List equipment...",2],
            ["delays","Delays / Issues","Any delays, issues, or problems...",2],
            ["visitors","Visitors","Owner, inspector, architect...",2],
            ["safety_notes","Safety Notes","PPE compliance, incidents, toolbox talk...",2],
            ["next_day_plan","Next Day Plan","What's planned for tomorrow...",2],
          ].map(([k,l,ph,rows])=>(
            <div key={k} style={{marginBottom:8}}>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>{l}</div>
              <textarea value={form[k]} onChange={e=>ff(k,e.target.value)} placeholder={ph} rows={rows}
                style={{...inputStyle,resize:"none"}}/>
            </div>
          )))}

          {/* Checkboxes */}
          <div style={{display:"flex",gap:20,marginBottom:12}}>
            {[["photos_taken","📷 Photos taken"],["inspector_visit","🔍 Inspector visited"]].map(([k,l])=>(
              <label key={k} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12,color:T.muted}}>
                <input type="checkbox" checked={form[k]} onChange={e=>ff(k,e.target.checked)} style={{width:14,height:14}}/>
                {l}
              </label>
            ))}
          </div>

          {!isSub&&(
            <div style={{background:T.bg2,borderRadius:6,padding:"8px 10px",marginBottom:12,fontSize:11,color:T.muted}}>
              📄 Saving will generate a printable Word doc and add this log to your project Documents folder.
            </div>
          )}

          <div style={{display:"flex",gap:8}}>
            <Btn ghost small onClick={()=>setShowForm(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
            <Btn primary small disabled={!form.work_done||saving} onClick={saveLog} style={{flex:2,justifyContent:"center"}}>
              {saving?"Saving...":(isSub?"✓ Save Report":"💾 Save + Generate Doc")}
            </Btn>
          </div>
        </div>
      )}

      {/* Log entries */}
      {logs.length===0?(
        <div style={{textAlign:"center",padding:"32px",color:T.muted,fontSize:12}}>
          No log entries yet.<br/>
          {isSub?"Add your first daily report above.":"Start logging daily — every entry generates a dated Word doc."}
        </div>
      ):logs.map(log=>(
        <div key={log.id} style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:10,padding:"12px 16px",marginBottom:8}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{fontSize:12,fontWeight:700,color:T.text}}>
              {new Date(log.date+"T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}
            </div>
            <div style={{display:"flex",gap:8,fontSize:10,color:T.muted,flexWrap:"wrap",justifyContent:"flex-end"}}>
              <span>☁ {log.weather} {log.temp}°F</span>
              {log.crew_count&&<span>👷 {log.crew_count} crew</span>}
              {log.inspector_visit&&<span style={{color:T.amber,fontWeight:700}}>🔍 Inspector</span>}
              {log.photos_taken&&<span style={{color:T.blue}}>📷 Photos</span>}
              {!isSub&&<button onClick={()=>generateLogDoc(log)}
                style={{background:"none",border:`1px solid ${T.subtle}`,borderRadius:4,padding:"1px 6px",color:T.muted,cursor:"pointer",fontSize:10}}>
                📄 Re-print
              </button>}
            </div>
          </div>
          {log.work_done&&<div style={{fontSize:12,color:T.text,marginBottom:4,lineHeight:1.5}}><strong style={{color:T.muted}}>Work:</strong> {log.work_done}</div>}
          {log.materials_used&&<div style={{fontSize:11,color:T.muted,marginBottom:4}}><strong>Materials:</strong> {log.materials_used}</div>}
          {log.subcontractors&&<div style={{fontSize:11,color:T.muted,marginBottom:4}}><strong>Subs:</strong> {log.subcontractors}</div>}
          {log.delays&&<div style={{fontSize:11,color:T.amber}}><strong>Delays:</strong> {log.delays}</div>}
          {log.next_day_plan&&<div style={{fontSize:11,color:T.muted,marginTop:4,borderTop:`1px solid ${T.subtle}`,paddingTop:4}}><strong>Tomorrow:</strong> {log.next_day_plan}</div>}
        </div>
      ))}
    </div>
  );
}

// ─── 2-WEEK SCHEDULE + INSPECTION TRACKER ─────────────────────────────────────
function SchedulePanel({proj, session, autoInspections=false}) {
  const key = "grok_schedule_" + proj?.id;
  const [schedule, setSchedule] = useState(()=>{try{return JSON.parse(localStorage.getItem(key)||"null");}catch{return null;}});
  const [generating, setGenerating] = useState(false);
  const [activeWeek, setActiveWeek] = useState(0); // 0=this week, 1=next week
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({day:"",task:"",trade:"",assignee:"",type:"work",time:""});

  const apiKey = "proxy"; // Using Supabase AI proxy

  const generateSchedule = async () => {
    
    setGenerating(true);
    try {
      const resp = await fetch(AI_PROXY_URL,{
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
        body:JSON.stringify({
          model:"claude-haiku-4-5",
          max_tokens:2000,
          messages:[{role:"user",content:`Generate a realistic 2-week construction schedule for this project.

Project: ${proj?.name}
Type: ${proj?.type||"Renovation"}
Phase Progress: ${JSON.stringify(proj?.phases||[])}
Description: ${proj?.desc||""}

Return ONLY valid JSON, no markdown:
{
  "generated": "ISO date",
  "weeks": [
    {
      "week": 1,
      "label": "Week of [date]",
      "days": [
        {
          "day": "Mon Apr 28",
          "date": "2026-04-28",
          "tasks": [
            {"id":"t1","task":"Task description","trade":"Plumbing","assignee":"Mike Chen","type":"work","time":"7:00 AM","critical":false},
            {"id":"t2","task":"Rough plumbing inspection","trade":"Inspection","assignee":"SF DBI","type":"inspection","time":"10:00 AM","critical":true}
          ]
        }
      ]
    }
  ]
}`}]})});
      const data = await resp.json();
      const text = data.content?.[0]?.text||"{}";
      const parsed = JSON.parse(text.replace(/\x60\x60\x60json|\x60\x60\x60/g,"").trim());
      // Auto-add inspection checkpoints after rough-in tasks
      if (autoInspections && parsed.weeks) {
        const ROUGH_KEYWORDS = ['rough','framing','foundation','drywall nail','insulation','underground','slab'];
        const INSP_MAP = {
          'rough plumbing':'Rough Plumbing Inspection 🔍',
          'rough electrical':'Rough Electrical Inspection 🔍',
          'rough mechanical':'Rough Mechanical Inspection 🔍',
          'rough framing':'Framing Inspection 🔍',
          'foundation':'Foundation Inspection 🔍',
          'drywall nail':'Drywall Nail Inspection 🔍',
          'insulation':'Insulation Inspection 🔍',
          'underground':'Underground Inspection 🔍',
          'slab':'Pre-Slab Inspection 🔍',
        };
        parsed.weeks.forEach(week=>{
          week.days?.forEach((day,di)=>{
            const newTasks = [];
            day.tasks?.forEach(task=>{
              const tl = task.task.toLowerCase();
              if (ROUGH_KEYWORDS.some(k=>tl.includes(k)) && task.type==="work") {
                // Find what inspection this maps to
                const inspLabel = Object.entries(INSP_MAP).find(([k])=>tl.includes(k))?.[1] || "Trade Inspection 🔍";
                // Add inspection task next day if not already there
                const nextDay = week.days[di+1];
                if (nextDay && !nextDay.tasks?.some(t=>t.task.includes('Inspection'))) {
                  nextDay.tasks = [...(nextDay.tasks||[]), {
                    id:"ai_"+Date.now()+Math.random(), task:inspLabel,
                    trade:"Inspection", assignee:proj?.inspector_name||"Building Dept",
                    type:"inspection", time:proj?.inspection_cutoff||"Call before 3:30PM",
                    critical:true, done:false, auto_generated:true,
                  }];
                }
              }
            });
          });
        });
      }
      setSchedule(parsed);
      try{localStorage.setItem(key,JSON.stringify(parsed));}catch(e){}
    } catch(e) { alert("Error: "+e.message); }
    setGenerating(false);
  };

  const toggleTask = (weekIdx, dayIdx, taskId) => {
    const updated = {...schedule};
    const task = updated.weeks[weekIdx].days[dayIdx].tasks.find(t=>t.id===taskId);
    if (task) task.done = !task.done;
    setSchedule(updated);
    try{localStorage.setItem(key,JSON.stringify(updated));}catch(e){}
  };

  const addManualTask = () => {
    if (!newTask.task || !newTask.day) return;
    const updated = {...schedule};
    const week = updated.weeks[activeWeek];
    const day = week?.days?.find(d=>d.day===newTask.day||d.date===newTask.day);
    if (day) {
      day.tasks.push({...newTask, id:"mt_"+Date.now(), done:false});
      setSchedule(updated);
      try{localStorage.setItem(key,JSON.stringify(updated));}catch(e){}
    }
    setNewTask({day:"",task:"",trade:"",assignee:"",type:"work",time:""});
    setShowAddTask(false);
  };

  const generateSubEmail = (task, day) => {
    const subject = encodeURIComponent(`Schedule Notice — ${proj?.name} — ${day.day}`);
    const body = encodeURIComponent(`Hi ${task.assignee},

This is a scheduling notice for ${proj?.name}.

Task: ${task.task}
Date: ${day.day}
Time: ${task.time||"TBD"}
Location: ${proj?.addr||"Project site"}

Please confirm your availability by replying to this email.

${task.type==="inspection"?`INSPECTION NOTICE: Please ensure all required documentation and corrections are complete before the inspection.`:""}

Thank you,
${session?.ownerName||"Jehoon Chun"}
${session?.companyName||"Grok Construction Group Inc"}
${session?.email||""}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  // Post a follow-up message to the project chat
  const postChatFollowUp = (task, day) => {
    const chatKey = "grok_chat_" + proj?.id;
    try {
      const existing = JSON.parse(localStorage.getItem(chatKey)||"[]");
      const msg = {
        id: Date.now(),
        author: session?.ownerName||"GC",
        role: "GC",
        avatar: (session?.ownerName||"GC").slice(0,2).toUpperCase(),
        color: T.gold,
        text: `📅 SCHEDULE FOLLOW-UP — ${day.day}\n\nFollowing up on: ${task.task}\nAssigned to: ${task.assignee||"TBD"}\nTime: ${task.time||"TBD"}\n\n${task.type==="inspection"?"Please ensure all work is ready for inspection. Have permit on site.":task.type==="delivery"?"Please confirm delivery window and site access.":"Please confirm you are on schedule for this task. Any issues, flag immediately."}`,
        time: new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),
        date: new Date().toLocaleDateString(),
        tag:"SCHEDULE",
      };
      localStorage.setItem(chatKey, JSON.stringify([...existing, msg]));
      alert("Follow-up posted to project chat ✓");
    } catch(e) {}
  };

  // Proactive follow-up: message subs about UPCOMING work in the 2-week window
  const sendProactiveFollowUp = () => {
    const chatKey = "grok_chat_" + proj?.id;
    try {
      const existing = JSON.parse(localStorage.getItem(chatKey)||"[]");
      const today = new Date();
      const upcoming = [];
      schedule?.weeks?.forEach(week=>{
        week.days?.forEach(d=>{
          const dayDate = new Date(d.date);
          const daysOut = Math.round((dayDate-today)/(1000*60*60*24));
          if (daysOut >= 1 && daysOut <= 14) {
            d.tasks?.filter(t=>!t.done&&t.assignee&&t.assignee!=="Grok").forEach(task=>{
              upcoming.push({...task, day:d.day, date:d.date, daysOut});
            });
          }
        });
      });

      if (upcoming.length===0) { alert("No upcoming tasks assigned to subs in the 2-week window."); return; }

      // Group by assignee
      const byAssignee = {};
      upcoming.forEach(t=>{
        if (!byAssignee[t.assignee]) byAssignee[t.assignee]=[];
        byAssignee[t.assignee].push(t);
      });

      const lines = ["📅 2-WEEK SCHEDULE OUTLOOK — FOLLOW-UP TO ALL SUBS\n"];
      Object.entries(byAssignee).forEach(([assignee, tasks])=>{
        lines.push(`\n👷 ${assignee}:`);
        tasks.forEach(t=>{
          lines.push(`  • ${t.day}${t.time?" @ "+t.time:""} — ${t.task} (${t.daysOut} days out)${t.type==="inspection"?" 🔍 INSPECTION":""}`);
        });
        lines.push(`  → Please confirm availability and flag any scheduling conflicts.`);
      });
      lines.push("\nReply to this message to confirm or flag any issues.");

      const msg = {
        id: Date.now(),
        author: session?.ownerName||"GC",
        role: "GC",
        avatar: (session?.ownerName||"GC").slice(0,2).toUpperCase(),
        color: T.gold,
        text: lines.join("\n"),
        time: new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),
        date: new Date().toLocaleDateString(),
        tag:"SCHEDULE_BLAST",
      };
      localStorage.setItem(chatKey, JSON.stringify([...existing, msg]));
      alert(`2-week follow-up posted to project chat ✓\n${Object.keys(byAssignee).length} subs, ${upcoming.length} tasks`);
    } catch(e) { alert("Error: "+e.message); }
  };

  const TYPE_COLOR = {work:T.blue,inspection:T.amber,delivery:T.teal,meeting:T.purple};
  const TYPE_ICON  = {work:"🔨",inspection:"🔍",delivery:"📦",meeting:"👥"};

  const currentWeek = schedule?.weeks?.[activeWeek];
  const inspections = schedule?.weeks?.flatMap(w=>w.days?.flatMap(d=>d.tasks?.filter(t=>t.type==="inspection"&&!t.done))||[])||[];

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text}}>2-Week Schedule Outlook</div>
        <div style={{display:"flex",gap:7}}>
          {schedule&&<Btn small ghost onClick={()=>setShowAddTask(p=>!p)}><Ic d={ICONS.plus} size={11}/>Add Task</Btn>}
          {schedule&&<button onClick={sendProactiveFollowUp}
            style={{fontSize:11,fontWeight:600,background:T.purpleBg,border:"1px solid "+T.purple+"30",borderRadius:7,padding:"5px 12px",cursor:"pointer",color:T.purple,display:"flex",alignItems:"center",gap:5}}>
            💬 Follow Up All Subs
          </button>}
          <Btn small primary onClick={generateSchedule} disabled={generating}>
            {generating?<><Spin/>Generating...</>:schedule?"♻ Refresh":"✨ Generate Schedule"}
          </Btn>
        </div>
      </div>

      {/* Upcoming inspections alert */}
      {inspections.length>0&&(
        <div style={{background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:9,padding:"10px 14px",marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:700,color:T.amber,marginBottom:6}}>🔍 Upcoming Inspections ({inspections.length})</div>
          {inspections.slice(0,3).map((insp,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.muted,padding:"3px 0"}}>
              <span>{insp.task}</span>
              <div style={{display:"flex",gap:8}}>
                <span style={{color:T.amber}}>{insp.assignee}</span>
                <span style={{color:insp.time?T.text:T.muted}}>{insp.time||"TBD"}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {!schedule?(
        <div style={{textAlign:"center",padding:"40px",color:T.muted,background:T.bg2,borderRadius:12,border:"2px dashed "+T.subtle}}>
          <div style={{fontSize:30,opacity:0.3,marginBottom:10}}>📅</div>
          <div style={{fontSize:13,marginBottom:6}}>No schedule generated yet</div>
          <div style={{fontSize:11,lineHeight:1.7,marginBottom:16}}>AI generates a 2-week schedule based on your project type, phases, and scope.</div>
          <Btn primary onClick={generateSchedule} disabled={generating}>
            {generating?<><Spin/>Generating...</>:"✨ Generate 2-Week Schedule"}
          </Btn>
        </div>
      ):(
        <div>
          {/* Week selector */}
          <div style={{display:"flex",gap:4,marginBottom:12,background:T.bg2,borderRadius:8,padding:3,width:"fit-content"}}>
            {schedule.weeks?.map((w,i)=>(
              <button key={i} onClick={()=>setActiveWeek(i)}
                style={{padding:"6px 14px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",background:activeWeek===i?T.gold:T.bg2,color:activeWeek===i?T.bg0:T.muted,whiteSpace:"nowrap"}}>
                {w.label||`Week ${i+1}`}
              </button>
            ))}
          </div>

          {/* Add task form */}
          {showAddTask&&(
            <Card style={{marginBottom:10,border:"1px solid "+T.blue+"30"}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8,marginBottom:8}}>
                {[["task","Task","Install drywall"],["trade","Trade","Drywall"],["assignee","Assigned To","Tom Lee"],["time","Time","7:00 AM"]].map(([k,l,ph])=>(
                  <div key={k}>
                    <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>{l}</div>
                    <input value={newTask[k]} onChange={e=>setNewTask(p=>({...p,[k]:e.target.value}))} placeholder={ph}
                      style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"6px 8px",color:T.text,fontSize:11,fontFamily:"inherit"}}/>
                  </div>
                ))}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                <div>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Day</div>
                  <select value={newTask.day} onChange={e=>setNewTask(p=>({...p,day:e.target.value}))}
                    style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"6px 8px",color:T.text,fontSize:11,fontFamily:"inherit"}}>
                    <option value="">Select day...</option>
                    {currentWeek?.days?.map(d=><option key={d.day} value={d.day}>{d.day}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Type</div>
                  <select value={newTask.type} onChange={e=>setNewTask(p=>({...p,type:e.target.value}))}
                    style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"6px 8px",color:T.text,fontSize:11,fontFamily:"inherit"}}>
                    {["work","inspection","delivery","meeting"].map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div style={{display:"flex",gap:7}}>
                <Btn ghost small onClick={()=>setShowAddTask(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
                <Btn primary small onClick={addManualTask} disabled={!newTask.task||!newTask.day} style={{flex:2,justifyContent:"center"}}>Add to Schedule</Btn>
              </div>
            </Card>
          )}

          {/* Day-by-day view */}
          {currentWeek?.days?.map((day,di)=>{
            const criticalTasks = day.tasks?.filter(t=>t.critical&&!t.done)||[];
            const doneTasks = day.tasks?.filter(t=>t.done)||[];
            return (
              <div key={di} style={{marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                  <div style={{fontSize:11,fontWeight:700,color:T.text,minWidth:80}}>{day.day}</div>
                  {criticalTasks.length>0&&<span style={{fontSize:9,fontWeight:700,background:T.redBg,color:T.red,borderRadius:10,padding:"1px 7px"}}>⚡ {criticalTasks.length} critical</span>}
                  {day.tasks?.length>0&&<div style={{flex:1,height:2,background:T.bg3,borderRadius:1,overflow:"hidden"}}><div style={{height:"100%",width:(doneTasks.length/day.tasks.length*100)+"%",background:T.green,borderRadius:1}}/></div>}
                  <span style={{fontSize:9,color:T.muted}}>{doneTasks.length}/{day.tasks?.length||0}</span>
                </div>
                {day.tasks?.map((task,ti)=>{
                  const tc = TYPE_COLOR[task.type]||T.muted;
                  return (
                    <div key={ti} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",borderRadius:8,marginBottom:3,background:task.done?T.bg2:T.bg1,border:"1px solid "+(task.critical&&!task.done?T.red+"30":T.subtle),opacity:task.done?0.6:1}}>
                      <button onClick={()=>toggleTask(activeWeek,di,task.id)}
                        style={{width:18,height:18,borderRadius:5,border:"2px solid "+(task.done?T.green:T.subtle),background:task.done?T.green:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.15s"}}>
                        {task.done&&<span style={{color:"white",fontSize:10}}>✓</span>}
                      </button>
                      <span style={{fontSize:12,flexShrink:0}}>{TYPE_ICON[task.type]||"🔨"}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:11,fontWeight:task.critical?700:500,color:task.done?T.muted:T.text,textDecoration:task.done?"line-through":"none"}}>{task.task}</div>
                        <div style={{fontSize:9,color:T.muted}}>{task.trade}{task.assignee?" · "+task.assignee:""}{task.time?" · "+task.time:""}</div>
                      </div>
                      <span style={{fontSize:9,fontWeight:700,background:tc+"15",color:tc,borderRadius:10,padding:"1px 7px",flexShrink:0}}>{task.type}</span>
                      {task.assignee&&task.assignee!=="Grok"&&(
                        <div style={{display:"flex",gap:3,flexShrink:0}}>
                          <button onClick={()=>generateSubEmail(task,day)}
                            style={{fontSize:9,background:T.bg3,border:"none",borderRadius:5,padding:"2px 6px",cursor:"pointer",color:T.muted}}>
                            📧
                          </button>
                          <button onClick={()=>postChatFollowUp(task,day)}
                            style={{fontSize:9,background:T.bg3,border:"none",borderRadius:5,padding:"2px 6px",cursor:"pointer",color:T.muted}}>
                            💬
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── MATERIO — PRODUCT SEARCH & SUBMITTAL WORKFLOW ────────────────────────────
function MaterioPanel({proj, session}) {
  const key = "grok_materio_" + proj?.id;
  const [submittals, setSubmittals] = useState(()=>{try{return JSON.parse(localStorage.getItem(key)||"[]");}catch{return [];}});
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [showSubmitForm, setShowSubmitForm] = useState(null);
  const [submitForm, setSubmitForm] = useState({spec_section:"",description:"",submitted_to:"",notes:""});
  const [searchResults, setSearchResults] = useState("");
  const apiKey = "proxy"; // Using Supabase AI proxy

  const CATEGORIES = [
    {id:"all",label:"All"},
    {id:"plumbing",label:"Plumbing Fixtures"},
    {id:"electrical",label:"Electrical"},
    {id:"hvac",label:"HVAC"},
    {id:"doors",label:"Doors & Hardware"},
    {id:"flooring",label:"Flooring"},
    {id:"tile",label:"Tile & Stone"},
    {id:"cabinets",label:"Cabinets"},
    {id:"windows",label:"Windows"},
    {id:"appliances",label:"Appliances"},
    {id:"lighting",label:"Lighting"},
  ];

  const saveSubmittals = (updated) => { setSubmittals(updated); try{localStorage.setItem(key,JSON.stringify(updated));}catch(e){} };

  const searchProducts = async () => {
    if (!search.trim()) return;
    setSearching(true); setSearchResults("");
    try {
      const resp = await fetch(AI_PROXY_URL,{
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
        body:JSON.stringify({
          model:"claude-haiku-4-5",
          max_tokens:1500,
          messages:[{role:"user",content:`You are a construction product specialist for Bay Area GCs.

Search query: "${search}"
Category: ${category==="all"?"All categories":category}
Project type: ${proj?.type||"Renovation"}

Return 4-5 qualifying products that match the search. For each include:
- Manufacturer and model name
- Key specs relevant to construction
- Estimated price range (Bay Area 2025/2026)
- Where to source locally (Ferguson, Home Depot Pro, Grainger, contractor supply)
- ADA compliant: yes/no (if applicable)
- CALGreen/Title 24 compliant: yes/no (if applicable)
- Lead time estimate

Format as clean readable text with clear product headers. Be specific with real manufacturer names and model numbers where possible.`}]})});
      const data = await resp.json();
      setSearchResults(data.content?.[0]?.text||"No results");
    } catch(e) { setSearchResults("Error: "+e.message); }
    setSearching(false);
  };

  const addSubmittal = (productName) => {
    setShowSubmitForm(productName);
    setSubmitForm({spec_section:"",description:productName,submitted_to:"Architect",notes:"",status:"draft"});
  };

  const saveSubmittal = () => {
    const sub = {
      ...submitForm,
      id:"sub_"+Date.now(),
      product:showSubmitForm,
      project:proj?.name,
      date_submitted:new Date().toLocaleDateString(),
      status:submitForm.status||"draft",
    };
    saveSubmittals([...submittals, sub]);
    setShowSubmitForm(null);
  };

  const updateStatus = (id, status) => {
    saveSubmittals(submittals.map(s=>s.id===id?{...s,status}:s));
  };

  const STATUS_COLORS = {
    draft:   {c:T.muted,  bg:T.bg3},
    submitted:{c:T.blue,  bg:T.blueBg},
    approved:{c:T.green,  bg:T.greenBg},
    rejected:{c:T.red,    bg:T.redBg},
    revision:{c:T.amber,  bg:T.amberBg},
  };

  return (
    <div>
      {/* Product Search */}
      <Card style={{marginBottom:12}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Product Search & Specifications</div>
        <div style={{fontSize:11,color:T.muted,marginBottom:12}}>Search qualifying products · Check ADA/CalGreen compliance · Generate submittals</div>

        <div style={{display:"flex",gap:6,marginBottom:8,flexWrap:"wrap"}}>
          {CATEGORIES.map(cat=>(
            <button key={cat.id} onClick={()=>setCategory(cat.id)}
              style={{fontSize:10,fontWeight:600,padding:"4px 10px",borderRadius:6,border:"1px solid "+(category===cat.id?T.purple:T.subtle),background:category===cat.id?T.purpleBg:T.bg2,color:category===cat.id?T.purple:T.muted,cursor:"pointer"}}>
              {cat.label}
            </button>
          ))}
        </div>

        <div style={{display:"flex",gap:8,marginBottom:12}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} onKeyDown={e=>e.key==="Enter"&&searchProducts()}
            placeholder={`Search ${category==="all"?"products":category}... e.g. "ADA compliant bathroom faucet" or "low flow toilet"`}
            style={{flex:1,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
          <Btn primary onClick={searchProducts} disabled={searching||!search.trim()}>
            {searching?<><Spin/>Searching...</>:"🔍 Search"}
          </Btn>
        </div>

        {(searchResults||searching)&&(
          <div>
            <div style={{background:T.bg2,borderRadius:9,padding:"14px",fontSize:12,color:T.text,lineHeight:1.8,whiteSpace:"pre-wrap",maxHeight:400,overflowY:"auto",marginBottom:10,borderLeft:"3px solid "+T.purple}}>
              {searching?<div style={{display:"flex",gap:10,color:T.muted,alignItems:"center"}}><Spin/>Searching products...</div>:<MarkdownLite text={searchResults}/>}
            </div>
            {searchResults&&(
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>addSubmittal(search)}
                  style={{fontSize:11,fontWeight:700,background:T.teal,color:"white",border:"none",borderRadius:7,padding:"7px 14px",cursor:"pointer"}}>
                  📋 Create Submittal for This Product
                </button>
                <button onClick={()=>navigator.clipboard.writeText(searchResults)}
                  style={{fontSize:11,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"7px 12px",cursor:"pointer",color:T.muted}}>
                  Copy Results
                </button>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Submittal Form */}
      {showSubmitForm&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}}>
          <div style={{background:T.bg1,borderRadius:16,padding:24,width:"100%",maxWidth:480,border:"1px solid "+T.teal+"30"}}>
            <div style={{fontSize:15,fontWeight:800,color:T.text,marginBottom:4}}>Create Submittal</div>
            <div style={{fontSize:11,color:T.muted,marginBottom:16}}>{showSubmitForm}</div>
            {[["spec_section","Spec Section","15430 — Plumbing Fixtures"],["description","Description",showSubmitForm||""],["submitted_to","Submit To","Architect / Owner / Engineer"]].map(([k,l,ph])=>(
              <div key={k} style={{marginBottom:10}}>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{l}</div>
                <input value={submitForm[k]||""} onChange={e=>setSubmitForm(p=>({...p,[k]:e.target.value}))} placeholder={ph}
                  style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
              </div>
            ))}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Notes</div>
              <textarea value={submitForm.notes} onChange={e=>setSubmitForm(p=>({...p,notes:e.target.value}))} rows={2}
                style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit",resize:"none"}}/>
            </div>
            <div style={{display:"flex",gap:8}}>
              <Btn ghost onClick={()=>setShowSubmitForm(null)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
              <Btn primary onClick={saveSubmittal} style={{flex:2,justifyContent:"center",background:T.teal,color:"white"}}>Save Submittal</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Submittals Log */}
      <Card>
        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Submittal Log</div>
        {submittals.length===0?(
          <div style={{fontSize:12,color:T.muted,textAlign:"center",padding:"20px"}}>No submittals yet. Search for a product above to create your first submittal.</div>
        ):submittals.map(sub=>{
          const sc = STATUS_COLORS[sub.status]||STATUS_COLORS.draft;
          return (
            <div key={sub.id} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"10px 12px",background:T.bg2,borderRadius:8,marginBottom:6}}>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:2}}>{sub.description||sub.product}</div>
                <div style={{fontSize:10,color:T.muted}}>{sub.spec_section&&sub.spec_section+" · "}{sub.submitted_to} · {sub.date_submitted}</div>
                {sub.notes&&<div style={{fontSize:10,color:T.muted,marginTop:2}}>{sub.notes}</div>}
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0,marginLeft:12}}>
                <select value={sub.status} onChange={e=>updateStatus(sub.id,e.target.value)}
                  style={{fontSize:9,fontWeight:700,background:sc.bg,color:sc.c,border:"none",borderRadius:10,padding:"2px 8px",cursor:"pointer",fontFamily:"inherit"}}>
                  {Object.keys(STATUS_COLORS).map(s=><option key={s} value={s}>{s.toUpperCase()}</option>)}
                </select>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

// ─── MARKET COST TRACKER ──────────────────────────────────────────────────────
function MarketCostPanel({proj, session}) {
  const [searching, setSearching] = useState(false);
  const [costData, setCostData] = useState("");
  const [category, setCategory] = useState("lumber");
  const [location, setLocation] = useState(session?.location||"San Francisco, CA");
  const apiKey = "proxy"; // Using Supabase AI proxy

  const COST_CATEGORIES = [
    {id:"lumber",label:"Lumber & Framing"},
    {id:"drywall",label:"Drywall & Finishing"},
    {id:"plumbing",label:"Plumbing Supplies"},
    {id:"electrical",label:"Electrical Supplies"},
    {id:"tile",label:"Tile & Stone"},
    {id:"flooring",label:"Flooring"},
    {id:"paint",label:"Paint & Coatings"},
    {id:"concrete",label:"Concrete & Masonry"},
    {id:"hardware",label:"Doors & Hardware"},
    {id:"hvac",label:"HVAC Supplies"},
    {id:"labor",label:"Labor Rates"},
    {id:"all",label:"All Categories"},
  ];

  const fetchMarketCosts = async () => {
    
    setSearching(true); setCostData("");
    try {
      const resp = await fetch(AI_PROXY_URL,{
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
        tools:[{type:"web_search_20250305",name:"web_search"}],
        body:JSON.stringify({
          model:"claude-haiku-4-5",
          max_tokens:2000,
          messages:[{role:"user",content:`Search for current 2025-2026 construction material costs for ${category} in ${location}.

Search for:
1. Current pricing at major suppliers (Home Depot, Lowe's, Grainger, Ferguson, local supply houses)
2. Recent price trends (up/down from 2024)
3. Lead times for materials
4. Nearby supplier locations in ${location}
5. Any supply chain issues or shortages

Provide practical pricing a contractor can use for estimates. Include unit prices, bulk pricing if available, and which suppliers have best availability.`}]})});
      const data = await resp.json();
      const text = (data.content||[]).filter(b=>b.type==="text").map(b=>b.text).join("\n");
      setCostData(text||"No data returned. Web search may not be available.");
    } catch(e) { setCostData("Error: "+e.message); }
    setSearching(false);
  };

  return (
    <div>
      <Card>
        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Live Market Cost Tracker</div>
        <div style={{fontSize:11,color:T.muted,marginBottom:14}}>Real-time pricing from local suppliers · Track trends · Find best availability</div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
          <div>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Material Category</div>
            <select value={category} onChange={e=>setCategory(e.target.value)}
              style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"9px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}>
              {COST_CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Location</div>
            <input value={location} onChange={e=>setLocation(e.target.value)}
              style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"9px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
          </div>
        </div>

        <Btn primary onClick={fetchMarketCosts} disabled={searching} style={{width:"100%",justifyContent:"center",marginBottom:12}}>
          {searching?<><Spin/>Searching live prices...</>:"🔍 Get Current Market Prices"}
        </Btn>

        {(costData||searching)&&(
          <div style={{background:T.bg2,borderRadius:9,padding:"14px",fontSize:12,color:T.text,lineHeight:1.8,whiteSpace:"pre-wrap",maxHeight:500,overflowY:"auto",borderLeft:"3px solid "+T.teal}}>
            {searching?<div style={{display:"flex",gap:10,color:T.muted,alignItems:"center"}}><Spin/>Searching current prices from local suppliers...</div>:<MarkdownLite text={costData}/>}
          </div>
        )}

        {!costData&&!searching&&(
          <div style={{background:T.bg2,borderRadius:9,padding:"16px",fontSize:11,color:T.muted,lineHeight:1.8}}>
            <div style={{fontWeight:700,color:T.text,marginBottom:8}}>What you'll get:</div>
            {["Current unit prices from Home Depot, Lowe's, Grainger, Ferguson","Price trends — up or down from last year","Lead times and availability warnings","Nearby supplier locations and hours","Bulk pricing for larger orders"].map((item,i)=>(
              <div key={i} style={{display:"flex",gap:7,marginBottom:4}}><span style={{color:T.teal}}>→</span>{item}</div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}


function ProjectManagerView({projects, setView, setActiveProject}) {
  const [selectedProj, setSelectedProj] = useState(projects[0]);
  const [messages, setMessages] = useState(()=>projects[0]?loadProjectMemory(projects[0].id):[]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionItems, setActionItems] = useState([]);
  const [showAction, setShowAction] = useState(false);
  const [newAction, setNewAction] = useState({task:"",due:"",owner:"Me",priority:"medium"});
  const [pmTab, setPmTab] = useState("chat"); // chat | briefing | report | risk | sub
  const [kb] = useState(()=>loadKB());
  const [briefing, setBriefing] = useState("");
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [report, setReport] = useState("");
  const [reportLoading, setReportLoading] = useState(false);
  const [riskScan, setRiskScan] = useState("");
  const [riskLoading, setRiskLoading] = useState(false);
  const [subMsg, setSubMsg] = useState("");
  const [subMsgLoading, setSubMsgLoading] = useState(false);
  const [subRecipient, setSubRecipient] = useState("");
  const [subTopic, setSubTopic] = useState("");
  const msgRef = useRef(null);
  useEffect(()=>{if(msgRef.current)msgRef.current.scrollTop=msgRef.current.scrollHeight;},[messages]);

  const apiKey = "proxy"; // proxy handles auth

  const projContext = selectedProj ? `Project: ${selectedProj.name}
Address: ${selectedProj.addr}
Client: ${selectedProj.client||""}
Type: ${selectedProj.type||""}
Contract Value: $${(selectedProj.value||0).toLocaleString()}
Progress: ${selectedProj.pct||0}%
Open COs: ${COS.filter(c=>String(c.projectId)===String(selectedProj.id)&&c.status==="pending").length}
Open RFIs: ${RFIS.filter(r=>String(r.projectId)===String(selectedProj.id)&&r.status!=="closed").length}
Status: ${selectedProj.status}
Notes: ${selectedProj.notes||"None"}` : "";

  const allProjectsContext = projects.filter(p=>p.status==="active").map(p=>`
Project: ${p.name} | ${p.pct||0}% complete | $${(p.value||0).toLocaleString()}
Open COs: ${COS.filter(c=>String(c.projectId)===String(p.id)&&c.status==="pending").length} | Open RFIs: ${RFIS.filter(r=>String(r.projectId)===String(p.id)&&r.status!=="closed").length}
Status: ${p.status} | Address: ${p.addr}`).join("\n");

  const callAISimple = async (prompt, onChunk) => {
    const resp = await fetch("https://api.anthropic.com/v1/messages", {
      method:"POST",
      headers:{"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
      body:JSON.stringify({model:"claude-haiku-4-5",max_tokens:1500,stream:true,messages:[{role:"user",content:prompt}]})
    });
    const reader = resp.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    while(true) {
      const {done,value} = await reader.read();
      if(done) break;
      buf += dec.decode(value,{stream:true});
      const lines = buf.split("\n");
      buf = lines.pop()||"";
      for(const line of lines) {
        if(!line.startsWith("data:")) continue;
        const d = line.slice(5).trim();
        if(d==="[DONE]") break;
        try{ const j=JSON.parse(d); if(j.delta?.text) onChunk(j.delta.text); }catch{}
      }
    }
  };

  const QUICK_PROMPTS = [
    "What should I do on this project today?",
    "Draft a CO notification email to the owner",
    "Write an RFI about unclear details on the plans",
    "What are the top 3 risks on this project?",
    "Draft a progress update email to the client",
    "Generate a punch list for final walkthrough",
    "Summarize all open sub issues and RFIs",
    "Check for trade conflicts in the schedule",
    "What permits do I need for this scope?",
    "Write a CO justification for unforeseen conditions",
  ];

  const send = async (msg) => {
    const userMsg = msg || input.trim();
    if (!userMsg) return;
    if (!enforceLimit("pm_chats", session)) return;
    setInput("");
    const newMessages = [...messages, {role:"user",content:userMsg}];
    setMessages(newMessages);
    if(selectedProj) saveProjectMemory(selectedProj.id, newMessages);
    setLoading(true);
    let response = "";
    const systemPrompt = buildSystemPrompt(projContext, kb);
    try {
      await callAI(systemPrompt, userMsg, t => {
        response += t;
        setMessages([...newMessages, {role:"assistant",content:response}]);
      }, apiKey);
      if(selectedProj) saveProjectMemory(selectedProj.id, [...newMessages, {role:"assistant",content:response}]);
    } catch(e) { setMessages([...newMessages, {role:"assistant",content:"Error: "+e.message}]); }
    setLoading(false);
  };

  // ── DAILY BRIEFING ────────────────────────────────────────────────────────
  const runBriefing = async () => {
    // proxy handles auth
    setBriefingLoading(true); setBriefing("");
    const today = new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"});
    const pendingCOs = COS.filter(c=>c.status==="pending");
    const openRFIs = RFIS.filter(r=>r.status!=="closed");
    try {
      await callAISimple(
        `You are a construction project manager AI. Generate a concise daily briefing for ${today}.

ACTIVE PROJECTS:
${allProjectsContext}

PENDING COs (${pendingCOs.length}):
${pendingCOs.map(c=>`• ${c.num||c.id} — ${c.desc||c.description||""} — $${(c.amount||0).toLocaleString()} — ${c.status}`).join("\n")||"None"}

OPEN RFIs (${openRFIs.length}):
${openRFIs.slice(0,5).map(r=>`• ${r.num||r.id} — ${r.subject||""} — Priority: ${r.priority||"normal"}`).join("\n")||"None"}

Generate a morning briefing with these sections:
## Good Morning, Jehoon 👋
One sentence about today.

## ⚡ Urgent — Needs Your Attention Today
Bullet list of things requiring action TODAY. Be specific.

## 📋 Top Priorities This Week
3-5 bullets of what to focus on across all projects.

## 🏗 Project Pulse
One line per active project: name, progress, one key thing happening.

## 📅 Upcoming
Any inspections, permit deadlines, or key dates in the next 7 days based on the data.

## 💡 PM Tip
One practical tip relevant to current project situations.

Be specific, practical, direct. No fluff. This is for a busy Bay Area GC.`,
        t => { setBriefing(p=>p+t); }
      );
    } catch(e) { setBriefing("Error: "+e.message); }
    setBriefingLoading(false);
  };

  // ── WEEKLY REPORT ─────────────────────────────────────────────────────────
  const runReport = async () => {
    // proxy handles auth
    if (!selectedProj) { alert("Select a project first."); return; }
    setReportLoading(true); setReport("");
    try {
      await callAISimple(
        `You are a construction project manager. Write a professional weekly progress report for the OWNER/CLIENT.

${projContext}

Open Change Orders: ${COS.filter(c=>String(c.projectId)===String(selectedProj.id)&&c.status==="pending").map(c=>`${c.num} — ${c.desc||c.description||""} — $${(c.amount||0).toLocaleString()}`).join(", ")||"None"}
Open RFIs: ${RFIS.filter(r=>String(r.projectId)===String(selectedProj.id)&&r.status!=="closed").map(r=>`${r.num} — ${r.subject||""}`).join(", ")||"None"}

Write a CLIENT-READY weekly report with:
## Project Update — [Project Name]
Week ending: [today's date]

## Progress This Week
What was accomplished. Be specific about trades and scope items.

## Current Status
Overall health of the project. Are we on schedule and budget?

## Work Scheduled Next Week
What's planned for the coming week.

## Open Items Requiring Your Attention
Any COs needing signature, decisions needed, or access requirements.

## Schedule & Budget
Brief status on both.

Keep it professional, positive where accurate, honest about issues. Owner should feel informed and confident.`,
        t => { setReport(p=>p+t); }
      );
    } catch(e) { setReport("Error: "+e.message); }
    setReportLoading(false);
  };

  // ── RISK RADAR ────────────────────────────────────────────────────────────
  const runRiskScan = async () => {
    // proxy handles auth
    setRiskLoading(true); setRiskScan("");
    const pendingCOs = COS.filter(c=>c.status==="pending");
    const urgentRFIs = RFIS.filter(r=>r.status!=="closed"&&(r.priority==="urgent"||r.priority==="critical"));
    const lowProgress = projects.filter(p=>p.status==="active"&&(p.pct||0)<25);
    try {
      await callAISimple(
        `You are a construction risk analyst. Scan these projects and identify real risks.

ACTIVE PROJECTS:
${allProjectsContext}

UNSIGNED COs (${pendingCOs.length}): ${pendingCOs.map(c=>`${c.num} $${(c.amount||0).toLocaleString()} (${c.status})`).join(", ")||"None"}
URGENT/CRITICAL RFIs (${urgentRFIs.length}): ${urgentRFIs.map(r=>`${r.num} — ${r.subject||""}`).join(", ")||"None"}
LOW PROGRESS PROJECTS: ${lowProgress.map(p=>`${p.name} ${p.pct||0}%`).join(", ")||"None"}

Generate a Risk Radar report:

## 🔴 Critical — Act Today
Risks that could stop work or cause legal exposure. Max 3 bullets.

## 🟡 Watch Closely — This Week
Issues that could become critical if ignored. Max 5 bullets.

## 🟢 On Track
What's going well. Brief.

## 💰 Financial Risks
Unsigned COs, scope creep, budget concerns.

## 📋 Recommended Actions
Top 5 specific actions to take this week to mitigate risks.

Be direct and specific. No generic advice. Reference actual project names and numbers.`,
        t => { setRiskScan(p=>p+t); }
      );
    } catch(e) { setRiskScan("Error: "+e.message); }
    setRiskLoading(false);
  };

  // ── SUB COORDINATION ──────────────────────────────────────────────────────
  const runSubMsg = async () => {
    // proxy handles auth
    if (!subRecipient || !subTopic) { alert("Enter recipient and topic."); return; }
    setSubMsgLoading(true); setSubMsg("");
    try {
      await callAISimple(
        `You are a GC project manager. Write a professional but direct message to a subcontractor.

Project: ${selectedProj?.name||"the project"}
Address: ${selectedProj?.addr||""}
Recipient: ${subRecipient}
Topic/Situation: ${subTopic}

Write a short, clear message (text or email). Include:
- The specific request or information
- Timeline/deadline if relevant
- Any prep they need to do
- Professional but firm tone — this is a business relationship

Format as ready-to-send text. Include a subject line if email.`,
        t => { setSubMsg(p=>p+t); }
      );
    } catch(e) { setSubMsg("Error: "+e.message); }
    setSubMsgLoading(false);
  };

  const inp = {width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"8px 12px",color:T.text,fontSize:12,fontFamily:"inherit"};
  const TABS = [
    {id:"chat",     label:"AI Chat"},
    {id:"briefing", label:"Daily Brief"},
    {id:"report",   label:"Weekly Report"},
    {id:"risk",     label:"Risk Radar"},
    {id:"sub",      label:"Sub Comms"},
  ];

  return (
    <div style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>AI Project Manager</div>
          <div style={{fontSize:12,color:T.muted,marginTop:2}}>Daily briefings · Risk radar · Weekly reports · Sub coordination · AI chat</div>
        </div>
        <select value={selectedProj?.id||""} onChange={e=>{
          const p=projects.find(proj=>String(proj.id)===e.target.value);
          setSelectedProj(p);
          if(p) setMessages(loadProjectMemory(p.id));
        }} style={{...inp,width:220,fontWeight:600}}>
          <option value="">All Projects</option>
          {projects.filter(p=>p.status==="active").map(p=>(
            <option key={p.id} value={String(p.id)}>{p.name.slice(0,35)}</option>
          ))}
        </select>
      </div>

      {/* Tab bar */}
      <div style={{display:"flex",gap:4,marginBottom:16,overflowX:"auto",paddingBottom:2}} className="section-tabs">
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setPmTab(t.id)}
            style={{padding:"8px 16px",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:pmTab===t.id?T.purple:"transparent",color:pmTab===t.id?"white":T.muted,whiteSpace:"nowrap",transition:"all 0.15s"}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── CHAT TAB ── */}
      {pmTab==="chat"&&(
        <div className="pm-three-col" style={{display:"grid",gridTemplateColumns:"220px 1fr 240px",gap:12}}>
          {/* Left: action items */}
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <Card>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <SectionTitle style={{marginBottom:0}}>Action Items</SectionTitle>
                <button onClick={()=>setShowAction(!showAction)}
                  style={{background:T.gold,border:"none",borderRadius:6,width:22,height:22,cursor:"pointer",color:T.bg0,fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800}}>+</button>
              </div>
              {showAction&&(
                <div style={{marginBottom:10,background:T.bg2,borderRadius:7,padding:"9px 10px"}}>
                  <input value={newAction.task} onChange={e=>setNewAction(p=>({...p,task:e.target.value}))}
                    placeholder="Action item..." style={{...inp,marginBottom:6,fontSize:11}}/>
                  <div style={{display:"flex",gap:6}}>
                    <input type="date" value={newAction.due} onChange={e=>setNewAction(p=>({...p,due:e.target.value}))}
                      style={{...inp,flex:1,fontSize:11}}/>
                    <Btn small primary onClick={()=>{
                      if(newAction.task.trim()){
                        setActionItems(p=>[...p,{...newAction,id:Date.now(),done:false,project:selectedProj?.name?.split(" — ")[0]}]);
                        setNewAction({task:"",due:"",owner:"Me",priority:"medium"});
                        setShowAction(false);
                      }
                    }}>Add</Btn>
                  </div>
                </div>
              )}
              {actionItems.filter(a=>!a.done).length===0&&(
                <div style={{fontSize:11,color:T.muted,textAlign:"center",padding:"10px 0"}}>No open items</div>
              )}
              {actionItems.filter(a=>!a.done).map(item=>(
                <div key={item.id} style={{display:"flex",gap:7,alignItems:"flex-start",padding:"6px 0",borderBottom:"1px solid "+T.subtle}}>
                  <div onClick={()=>setActionItems(p=>p.map(a=>a.id===item.id?{...a,done:true}:a))}
                    style={{width:14,height:14,borderRadius:3,border:"2px solid "+T.subtle,cursor:"pointer",flexShrink:0,marginTop:2}}/>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,color:T.text}}>{item.task}</div>
                    {item.due&&<div style={{fontSize:9,color:T.muted}}>{item.due}</div>}
                  </div>
                </div>
              ))}
            </Card>
          </div>

          {/* Center: chat */}
          <Card style={{display:"flex",flexDirection:"column",minHeight:520}}>
            {selectedProj&&(
              <div style={{background:T.bg2,borderRadius:8,padding:"9px 12px",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:T.text}}>{selectedProj.name}</div>
                  <div style={{fontSize:10,color:T.muted}}>{selectedProj.addr} · {selectedProj.pct||0}% complete</div>
                </div>
                <Btn small ghost onClick={()=>{setActiveProject(selectedProj);setView("project-detail");}}>Open →</Btn>
              </div>
            )}
            <div ref={msgRef} style={{flex:1,overflowY:"auto",maxHeight:400,marginBottom:12,display:"flex",flexDirection:"column",gap:10}}>
              {messages.length===0&&(
                <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,textAlign:"center"}}>
                  <div style={{width:52,height:52,borderRadius:14,background:"linear-gradient(135deg,"+T.purple+","+T.blue+")",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:14}}>
                    <Ic d={ICONS.robot} size={24} style={{color:"white"}}/>
                  </div>
                  <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:6}}>AI Project Manager</div>
                  <div style={{fontSize:12,color:T.muted,lineHeight:1.6}}>
                    {selectedProj?`I know ${selectedProj.name} — ask me anything.`:"Select a project to get started."}
                  </div>
                </div>
              )}
              {messages.map((m,i)=>(
                <div key={i} style={{display:"flex",justifyContent:m.role==="user"?"flex-end":"flex-start"}}>
                  <div style={{maxWidth:"85%",background:m.role==="user"?T.gold:T.bg2,borderRadius:m.role==="user"?"12px 12px 2px 12px":"12px 12px 12px 2px",padding:"10px 14px",fontSize:12,color:m.role==="user"?T.bg0:T.text,lineHeight:1.6,whiteSpace:m.role==="user"?"pre-wrap":"normal"}}>
                    {m.role==="user"?m.content:<MarkdownLite text={m.content}/>}
                  </div>
                </div>
              ))}
              {loading&&<div style={{display:"flex",gap:8,alignItems:"center"}}><Spin/><span style={{fontSize:11,color:T.muted}}>thinking...</span></div>}
            </div>
            <div style={{display:"flex",gap:8}}>
              <input value={input} onChange={e=>setInput(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
                placeholder={selectedProj?`Ask about ${selectedProj.name.split(" — ")[0]}...`:"Select a project first..."}
                disabled={!selectedProj}
                style={{flex:1,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:9,padding:"10px 14px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
              <Btn primary onClick={()=>send()} disabled={!input.trim()||loading||!selectedProj}>
                {loading?<Spin/>:"Send"}
              </Btn>
            </div>
          </Card>

          {/* Right: quick prompts */}
          <Card>
            <SectionTitle>Quick Prompts</SectionTitle>
            {QUICK_PROMPTS.map((p,i)=>(
              <button key={i} onClick={()=>send(p)}
                style={{display:"block",width:"100%",textAlign:"left",padding:"8px 10px",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,fontSize:11,color:T.muted,cursor:"pointer",marginBottom:5,lineHeight:1.4}}>
                {p}
              </button>
            ))}
          </Card>
        </div>
      )}

      {/* ── DAILY BRIEFING TAB ── */}
      {pmTab==="briefing"&&(
        <div style={{maxWidth:760,margin:"0 auto"}}>
          <div style={{background:T.purpleBg,border:"1px solid "+T.purple+"30",borderRadius:12,padding:"20px 24px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:15,fontWeight:700,color:T.purple,marginBottom:4}}>🌅 Daily Briefing</div>
              <div style={{fontSize:12,color:T.muted,lineHeight:1.6}}>
                AI scans ALL your active projects and generates your morning briefing — urgent items, priorities, project pulse, and upcoming deadlines. One tap, every morning.
              </div>
            </div>
            <Btn primary onClick={runBriefing} disabled={briefingLoading}
              style={{background:"linear-gradient(135deg,"+T.purple+","+T.blue+")",color:"white",flexShrink:0}}>
              {briefingLoading?<><Spin/>Generating...</>:"Generate Briefing"}
            </Btn>
          </div>
          {briefing?(
            <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:12,padding:"20px 24px"}}>
              <div style={{display:"flex",justifyContent:"flex-end",marginBottom:10,gap:8}}>
                <Btn small ghost onClick={()=>navigator.clipboard.writeText(briefing)}>Copy</Btn>
                <Btn small ghost onClick={()=>setBriefing("")}>Clear</Btn>
              </div>
              <div style={{fontSize:13,color:T.text,lineHeight:1.9}}><MarkdownLite text={briefing}/></div>
            </div>
          ):!briefingLoading&&(
            <div style={{textAlign:"center",padding:"48px 24px",color:T.muted}}>
              <div style={{fontSize:48,marginBottom:12}}>🌅</div>
              <div style={{fontSize:14,fontWeight:600,color:T.text,marginBottom:6}}>Start your day right</div>
              <div style={{fontSize:12,lineHeight:1.7}}>Hit Generate Briefing every morning to get a personalized rundown of what needs your attention across all {projects.filter(p=>p.status==="active").length} active projects.</div>
            </div>
          )}
        </div>
      )}

      {/* ── WEEKLY REPORT TAB ── */}
      {pmTab==="report"&&(
        <div style={{maxWidth:760,margin:"0 auto"}}>
          <div style={{background:T.tealBg,border:"1px solid "+T.teal+"30",borderRadius:12,padding:"16px 20px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:700,color:T.teal,marginBottom:4}}>📊 Weekly Owner Report</div>
              <div style={{fontSize:11,color:T.muted}}>Professional progress report ready to email to your client. Select a project above then generate.</div>
            </div>
            <div style={{display:"flex",gap:8,flexShrink:0}}>
              <Btn primary onClick={runReport} disabled={reportLoading||!selectedProj}
                style={{background:T.teal,color:"white"}}>
                {reportLoading?<><Spin/>Writing...</>:"Generate Report"}
              </Btn>
              {report&&(
                <Btn small ghost onClick={()=>{
                  const subj = encodeURIComponent("Project Update — "+(selectedProj?.name||""));
                  const body = encodeURIComponent(report);
                  window.open("mailto:?subject="+subj+"&body="+body);
                }}>✉ Email to Owner</Btn>
              )}
            </div>
          </div>
          {!selectedProj&&<div style={{textAlign:"center",padding:"32px",color:T.muted,fontSize:13}}>Select a project from the dropdown above to generate its report</div>}
          {report?(
            <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:12,padding:"20px 24px"}}>
              <div style={{display:"flex",justifyContent:"flex-end",marginBottom:10,gap:8}}>
                <Btn small ghost onClick={()=>navigator.clipboard.writeText(report)}>Copy</Btn>
                <Btn small ghost onClick={()=>setReport("")}>Clear</Btn>
              </div>
              <div style={{fontSize:13,color:T.text,lineHeight:1.9}}><MarkdownLite text={report}/></div>
            </div>
          ):!reportLoading&&selectedProj&&(
            <div style={{textAlign:"center",padding:"40px 24px",color:T.muted}}>
              <div style={{fontSize:40,marginBottom:10}}>📊</div>
              <div style={{fontSize:14,fontWeight:600,color:T.text,marginBottom:6}}>Weekly report for {selectedProj.name}</div>
              <div style={{fontSize:12}}>Generates a client-ready progress update you can email directly to your owner.</div>
            </div>
          )}
        </div>
      )}

      {/* ── RISK RADAR TAB ── */}
      {pmTab==="risk"&&(
        <div style={{maxWidth:760,margin:"0 auto"}}>
          <div style={{background:T.redBg,border:"1px solid "+T.red+"30",borderRadius:12,padding:"16px 20px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:T.red,marginBottom:4}}>🎯 Risk Radar</div>
              <div style={{fontSize:11,color:T.muted}}>AI scans ALL active projects for risks — unsigned COs, overdue RFIs, schedule problems, financial exposure. Know your risks before they become problems.</div>
            </div>
            <Btn primary onClick={runRiskScan} disabled={riskLoading}
              style={{background:T.red,color:"white",flexShrink:0}}>
              {riskLoading?<><Spin/>Scanning...</>:"Scan All Projects"}
            </Btn>
          </div>
          {riskScan?(
            <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:12,padding:"20px 24px"}}>
              <div style={{display:"flex",justifyContent:"flex-end",marginBottom:10,gap:8}}>
                <Btn small ghost onClick={()=>navigator.clipboard.writeText(riskScan)}>Copy</Btn>
                <Btn small ghost onClick={()=>setRiskScan("")}>Clear</Btn>
              </div>
              <div style={{fontSize:13,color:T.text,lineHeight:1.9}}><MarkdownLite text={riskScan}/></div>
            </div>
          ):!riskLoading&&(
            <div style={{textAlign:"center",padding:"48px 24px",color:T.muted}}>
              <div style={{fontSize:48,marginBottom:12}}>🎯</div>
              <div style={{fontSize:14,fontWeight:600,color:T.text,marginBottom:6}}>Know your risks before they cost you</div>
              <div style={{fontSize:12,lineHeight:1.7,maxWidth:400,margin:"0 auto"}}>
                Scans {projects.filter(p=>p.status==="active").length} active projects, {COS.filter(c=>c.status==="pending").length} pending COs, and {RFIS.filter(r=>r.status!=="closed").length} open RFIs to find what could hurt you this week.
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── SUB COORDINATION TAB ── */}
      {pmTab==="sub"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,maxWidth:900,margin:"0 auto"}}>
          <Card>
            <SectionTitle>👷 Draft Sub Message</SectionTitle>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Recipient</div>
              <input value={subRecipient} onChange={e=>setSubRecipient(e.target.value)}
                placeholder="e.g. Tom — Lee Electric, our plumber Dave" style={inp}/>
            </div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Topic / Situation</div>
              <textarea value={subTopic} onChange={e=>setSubTopic(e.target.value)} rows={4}
                placeholder="e.g. Need him on site Monday 7am for rough-in inspection. Remind him to bring the permit card and all his rough-in is ready for inspector."
                style={{...inp,resize:"vertical"}}/>
            </div>
            <Btn primary onClick={runSubMsg} disabled={subMsgLoading||!subRecipient||!subTopic} style={{width:"100%",justifyContent:"center"}}>
              {subMsgLoading?<><Spin/>Writing...</>:"✍ Draft Message"}
            </Btn>

            <div style={{marginTop:14}}>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Quick Templates</div>
              {[
                ["On-site reminder","Remind [sub] to be on site [day] at [time] for [reason]"],
                ["Inspection prep","[Sub] needs to have [scope] ready for [inspection type] on [date]"],
                ["Delay notice","Inform [sub] their start date is pushed to [new date] due to [reason]"],
                ["Safety violation","Address safety issue observed on site — [describe]"],
                ["Payment hold","Notify [sub] payment is on hold until [condition] is met"],
              ].map(([label,template])=>(
                <button key={label} onClick={()=>setSubTopic(template)}
                  style={{display:"block",width:"100%",textAlign:"left",padding:"7px 10px",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,fontSize:11,color:T.muted,cursor:"pointer",marginBottom:4,lineHeight:1.4}}>
                  {label}
                </button>
              ))}
            </div>
          </Card>

          <Card>
            <SectionTitle>Message Preview</SectionTitle>
            {subMsg?(
              <div>
                <div style={{background:T.bg2,borderRadius:8,padding:"14px",fontSize:12,color:T.text,lineHeight:1.8,whiteSpace:"pre-wrap",marginBottom:10}}><MarkdownLite text={subMsg}/></div>
                <div style={{display:"flex",gap:7}}>
                  <Btn small ghost onClick={()=>navigator.clipboard.writeText(subMsg)} style={{flex:1,justifyContent:"center"}}>Copy</Btn>
                  <Btn small ghost onClick={()=>{
                    const body = encodeURIComponent(subMsg);
                    const phone = "";
                    window.open("sms:"+phone+"?body="+body);
                  }} style={{flex:1,justifyContent:"center"}}>💬 SMS</Btn>
                  <Btn small ghost onClick={()=>{
                    const body = encodeURIComponent(subMsg);
                    window.open("mailto:?body="+body);
                  }} style={{flex:1,justifyContent:"center"}}>✉ Email</Btn>
                </div>
              </div>
            ):(
              <div style={{textAlign:"center",padding:"40px 20px",color:T.muted}}>
                <div style={{fontSize:36,marginBottom:10}}>👷</div>
                <div style={{fontSize:13,color:T.text,marginBottom:6}}>Professional sub messages in seconds</div>
                <div style={{fontSize:11,lineHeight:1.7}}>Fill in the recipient and topic, then hit Draft Message. Copy to SMS or email directly.</div>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}


// ─── COMPANY SETTINGS & ESTIMATING CALCULATOR ────────────────────────────────
const SETTINGS_KEY = "grok_company_settings";
function settingsKey(cid){return "grok_company_settings_"+(cid||"local");}
function kbKey(cid){return "grok_knowledge_base_"+(cid||"local");}
function leadsKey(cid){return "grok_leads_"+(cid||"local");}
function subDbKey(cid){return "grok_sub_database_"+(cid||"local");}

function loadSettings() {
  try {
    const s = localStorage.getItem(SETTINGS_KEY);
    return s ? JSON.parse(s) : null;
  } catch { return null; }
}

function saveSettings(settings) {
  try { localStorage.setItem(settingsKey(window._grokCompanyId||"local"), JSON.stringify(settings)); } catch(e) {}
}

function getDefaultSettings(session) {
  return {
    // Company info
    company_name: session?.companyName || "Your Construction Co",
    owner_name: session?.ownerName || "",
    cslb: session?.cslb || "",
    location: session?.location || "Bay Area, CA",
    email: session?.email || "",
    phone: "",
    website: "",

    // Labor rates (self-perform)
    labor: {
      lead_carpenter:  { rate: 105, label: "Lead Carpenter / Foreman" },
      journeyman:      { rate: 85,  label: "Journeyman Carpenter" },
      laborer:         { rate: 70,  label: "Skilled Laborer / Demo" },
      tile:            { rate: 95,  label: "Tile Setter" },
      drywall:         { rate: 90,  label: "Drywall / Taper" },
      painter:         { rate: 75,  label: "Painter" },
      countertop:      { rate: 95,  label: "Countertop Installer" },
      supervisor:      { rate: 120, label: "Project Supervisor (PM)" },
      prevailing_low:  { rate: 95,  label: "Prevailing Wage — Low" },
      prevailing_high: { rate: 145, label: "Prevailing Wage — High" },
    },

    // Sub markup
    sub_markup: 15,

    // O&P by project type
    op: {
      sro:         { markup: 26, gc_fee: 8,  insurance: 2.5, overhead: 7, contingency: 15, label: "SRO / Affordable Housing" },
      senior:      { markup: 25, gc_fee: 8,  insurance: 2.5, overhead: 7, contingency: 13, label: "Senior / ADA Housing" },
      multifamily: { markup: 25, gc_fee: 8,  insurance: 2.5, overhead: 7, contingency: 12, label: "Multi-Family Apartment" },
      residential: { markup: 24, gc_fee: 7,  insurance: 2.5, overhead: 7, contingency: 10, label: "Residential Remodel" },
      adu:         { markup: 23, gc_fee: 7,  insurance: 2.5, overhead: 7, contingency: 10, label: "ADU / Garage Conversion" },
      commercial:  { markup: 22, gc_fee: 8,  insurance: 2.5, overhead: 8, contingency: 10, label: "Commercial TI" },
      kitchen:     { markup: 26, gc_fee: 7,  insurance: 2.5, overhead: 7, contingency: 10, label: "Kitchen Renovation" },
      bathroom:    { markup: 27, gc_fee: 7,  insurance: 2.5, overhead: 7, contingency: 10, label: "Bathroom Renovation" },
    },

    // Material unit costs (for quick estimating)
    material_costs: {
      lvp_flooring:    { cost: 3.50, unit: "sqft", label: "LVP Flooring (material)" },
      tile_4x12:       { cost: 4.00, unit: "sqft", label: "Subway Tile 4x12" },
      drywall_sheet:   { cost: 18,   unit: "sheet", label: "5/8\" Drywall Sheet" },
      paint_interior:  { cost: 35,   unit: "gal",  label: "Interior Paint (premium)" },
      framing_lumber:  { cost: 0.85, unit: "lf",   label: "2x4 Framing Lumber (per LF)" },
      concrete:        { cost: 175,  unit: "cy",   label: "Ready-mix Concrete (per CY)" },
      insulation_r15:  { cost: 1.20, unit: "sqft", label: "Batt Insulation R-15 (sqft)" },
      cabinet_linear:  { cost: 350,  unit: "lf",   label: "Shaker Cabinet (per LF)" },
      countertop_qtz:  { cost: 85,   unit: "sqft", label: "Quartz Countertop (per sqft)" },
      window_std:      { cost: 450,  unit: "ea",   label: "Standard Window (per unit)" },
      door_int:        { cost: 280,  unit: "ea",   label: "Interior Door + Frame" },
      door_ext:        { cost: 650,  unit: "ea",   label: "Exterior Door + Frame" },
    },

    // Unit cost estimating (cost per unit type)
    unit_costs: {
      sro_unit:    { low: 35000, high: 55000, label: "SRO Unit Full Reno" },
      senior_unit: { low: 45000, high: 65000, label: "Senior / ADA Unit Reno" },
      apt_unit:    { low: 28000, high: 45000, label: "Apartment Unit Reno" },
      kitchen:     { low: 25000, high: 55000, label: "Kitchen Renovation" },
      bathroom:    { low: 12000, high: 28000, label: "Bathroom Renovation" },
      sqft_new:    { low: 350,   high: 550,   label: "New Construction ($/sqft)" },
      sqft_ti:     { low: 85,    high: 200,   label: "Commercial TI ($/sqft)" },
    },

    // Billing / payment terms
    billing: {
      retainage: 10,
      payment_terms: "Net 30",
      progress_billing: "1st of every month",
      lien_notice_day: 1,
      late_fee: 1.5,
    },
  };
}

function CompanySettingsView({session}) {
  const [settings, setSettings] = useState(()=> loadSettings() || getDefaultSettings(session));
  const [activeTab, setActiveTab] = useState("rates");
  const [saved, setSaved] = useState(false);
  const [calcInput, setCalcInput] = useState({type:"sro", directCost:"", units:"1"});
  const [calcResult, setCalcResult] = useState(null);

  const save = (updated) => {
    const s = updated || settings;
    setSettings(s);
    saveSettings(s);
    // Also update the AI knowledge base with new rates
    const kb = loadKB();
    const laborStr = Object.values(s.labor).map(l=>`${l.label}: $${l.rate}/hr`).join(", ");
    const opStr = Object.entries(s.op).map(([k,v])=>`${v.label}: ${v.markup}% markup, ${v.gc_fee}% GC fee, ${v.contingency}% contingency`).join("; ");
    saveKB({...kb,
      company_name: s.company_name,
      cslb: s.cslb,
      location: s.location,
      labor_rates: laborStr,
      op_structure: opStr,
      sub_rates: `Sub markup: +${s.sub_markup}%. All subs carry minimum $1M liability.`,
    });
    setSaved(true);
    setTimeout(()=>setSaved(false), 2000);
  };

  const updateLabor = (key, val) => {
    const updated = {...settings, labor:{...settings.labor, [key]:{...settings.labor[key], rate:parseFloat(val)||0}}};
    setSettings(updated);
  };

  const updateOP = (type, field, val) => {
    const updated = {...settings, op:{...settings.op, [type]:{...settings.op[type], [field]:parseFloat(val)||0}}};
    setSettings(updated);
  };

  const updateMaterial = (key, field, val) => {
    const updated = {...settings, material_costs:{...settings.material_costs, [key]:{...settings.material_costs[key], [field]:field==="label"||field==="unit"?val:parseFloat(val)||0}}};
    setSettings(updated);
  };

  const updateUnit = (key, field, val) => {
    const updated = {...settings, unit_costs:{...settings.unit_costs, [key]:{...settings.unit_costs[key], [field]:parseFloat(val)||0}}};
    setSettings(updated);
  };

  // Quick estimate calculator
  const runCalc = () => {
    const op = settings.op[calcInput.type];
    if (!op || !calcInput.directCost) return;
    const dc = parseFloat(calcInput.directCost) || 0;
    const units = parseFloat(calcInput.units) || 1;
    const dcTotal = dc * units;
    const markup = dcTotal * (op.markup/100);
    const afterMarkup = dcTotal + markup;
    const gcFee = afterMarkup * (op.gc_fee/100);
    const insurance = afterMarkup * (op.insurance/100);
    const overhead = afterMarkup * (op.overhead/100);
    const sub = afterMarkup + gcFee + insurance + overhead;
    const contingency = sub * (op.contingency/100);
    const total = sub + contingency;
    const grossProfit = total - dcTotal;
    const margin = total > 0 ? grossProfit/total*100 : 0;
    setCalcResult({dc:dcTotal, markup, afterMarkup, gcFee, insurance, overhead, sub, contingency, total, grossProfit, margin, op});
  };

  const fmt = v => "$"+Math.round(v||0).toLocaleString();
  const TabBtn = ({id,label,icon}) => (
    <button onClick={()=>setActiveTab(id)} style={{padding:"7px 16px",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:activeTab===id?T.gold:T.bg2,color:activeTab===id?T.bg0:T.muted,transition:"all 0.15s",display:"flex",alignItems:"center",gap:6,whiteSpace:"nowrap"}}>
      {icon} {label}
    </button>
  );

  const NumberField = ({label, value, onChange, suffix="", prefix="$", small=false}) => (
    <div style={{marginBottom:small?6:10}}>
      {label&&<div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>{label}</div>}
      <div style={{display:"flex",alignItems:"center",gap:4}}>
        {prefix&&<span style={{fontSize:11,color:T.muted,flexShrink:0}}>{prefix}</span>}
        <input type="number" defaultValue={value}
          onBlur={e=>onChange(e.target.value)}
          style={{width:small?70:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:small?"5px 8px":"8px 10px",color:T.text,fontSize:small?12:13,fontFamily:"'Space Mono',monospace",textAlign:"right"}}/>
        {suffix&&<span style={{fontSize:11,color:T.muted,flexShrink:0}}>{suffix}</span>}
      </div>
    </div>
  );

  return (
    <div style={{padding:24,maxWidth:1000}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>Company Settings & Rates</div>
          <div style={{fontSize:12,color:T.muted,marginTop:3}}>Your rates power all AI estimates, pricing calculator, and budget tracker — set them once, use everywhere</div>
        </div>
        <Btn primary onClick={()=>save()} style={{background:saved?T.green:"",color:saved?"white":""}}>
          {saved ? "✓ Saved!" : "Save All Settings"}
        </Btn>
      </div>

      <div style={{display:"flex",gap:4,marginBottom:16,flexWrap:"wrap"}}>
        <TabBtn id="rates"     label="Labor Rates"      icon="👷"/>
        <TabBtn id="op"        label="Markup & Fees"    icon="💰"/>
        <TabBtn id="materials" label="Material Costs"   icon="📦"/>
        <TabBtn id="units"     label="Unit Cost Guide"  icon="🏗"/>
        <TabBtn id="calc"      label="Quick Calculator" icon="🧮"/>
        <TabBtn id="billing"   label="Billing Terms"    icon="📋"/>
        <TabBtn id="company"   label="Company Info"     icon="🏢"/>
        <TabBtn id="team"      label="👥 Team Members"  icon="👥"/>
        <TabBtn id="roles"     label="🔒 Role Permissions" icon="🔒"/>
        <TabBtn id="account"   label="⚙️ Account & Subscription" icon="⚙️"/>
      </div>

      {/* LABOR RATES */}
      {activeTab==="rates" && (
        <div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Card>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Self-Perform Labor Rates</div>
              <div style={{fontSize:11,color:T.muted,marginBottom:14}}>Your own crew rates — used in AI estimates and time tracking</div>
              {Object.entries(settings.labor).map(([key, l])=>(
                <div key={key} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:`1px solid ${T.subtle}`}}>
                  <div style={{flex:1,fontSize:12,color:T.text}}>{l.label}</div>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:11,color:T.muted}}>$</span>
                    <input type="number" defaultValue={l.rate} onBlur={e=>updateLabor(key,e.target.value)}
                      style={{width:70,background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"5px 8px",color:T.text,fontSize:12,fontFamily:"'Space Mono',monospace",textAlign:"right"}}/>
                    <span style={{fontSize:11,color:T.muted}}>/hr</span>
                  </div>
                </div>
              ))}
            </Card>

            <Card>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Subcontractor Markup</div>
              <div style={{fontSize:11,color:T.muted,marginBottom:14}}>Markup added on top of all sub invoices</div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
                <input type="number" defaultValue={settings.sub_markup} onBlur={e=>setSettings(p=>({...p,sub_markup:parseFloat(e.target.value)||0}))}
                  style={{width:80,background:T.bg2,border:`1px solid ${T.gold}`,borderRadius:8,padding:"10px 12px",color:T.gold,fontSize:22,fontFamily:"'Space Mono',monospace",fontWeight:800,textAlign:"center"}}/>
                <div>
                  <div style={{fontSize:16,fontWeight:700,color:T.muted}}>%</div>
                  <div style={{fontSize:11,color:T.muted}}>on all sub invoices</div>
                </div>
              </div>
              <div style={{background:T.bg2,borderRadius:8,padding:"12px 14px"}}>
                <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>What this means:</div>
                {[50000,100000,200000].map(v=>(
                  <div key={v} style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"4px 0",borderBottom:`1px solid ${T.subtle}`,color:T.muted}}>
                    <span>Sub invoice: {fmt(v)}</span>
                    <span style={{color:T.green,fontWeight:600}}>You charge: {fmt(v*(1+settings.sub_markup/100))}</span>
                  </div>
                ))}
              </div>

              <div style={{marginTop:16}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Key Sub Rates (reference)</div>
                <div style={{fontSize:11,color:T.muted,marginBottom:10}}>Market rates for your AI estimates — update to match your actual sub costs</div>
                {[
                  ["Plumber","plumber_rate",145],
                  ["Electrician","electrician_rate",155],
                  ["HVAC Tech","hvac_rate",140],
                  ["Concrete/Structural","concrete_rate",130],
                ].map(([label,key,def])=>(
                  <div key={key} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 0",borderBottom:`1px solid ${T.subtle}`}}>
                    <div style={{flex:1,fontSize:12,color:T.text}}>{label}</div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:11,color:T.muted}}>$</span>
                      <input type="number" defaultValue={settings[key]||def} onBlur={e=>setSettings(p=>({...p,[key]:parseFloat(e.target.value)||0}))}
                        style={{width:70,background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"5px 8px",color:T.text,fontSize:12,fontFamily:"'Space Mono',monospace",textAlign:"right"}}/>
                      <span style={{fontSize:11,color:T.muted}}>/hr</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* MARKUP & FEES BY PROJECT TYPE */}
      {activeTab==="op" && (
        <Card>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Markup & Fees by Project Type</div>
          <div style={{fontSize:11,color:T.muted,marginBottom:16}}>These drive your pricing calculator, budget tracker, and AI estimates. Set them to match your market and target margin.</div>

          {/* Header */}
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 1fr 1fr",gap:6,padding:"6px 8px",marginBottom:4}}>
            {["Project Type","Markup %","GC Fee %","Insurance %","Overhead %","Contingency %","Total O&P"].map(h=>(
              <div key={h} style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",textAlign:h==="Project Type"?"left":"center"}}>{h}</div>
            ))}
          </div>

          {Object.entries(settings.op).map(([key, op])=>{
            const totalOP = op.markup + op.gc_fee + op.insurance + op.overhead + op.contingency;
            // Approximate client price multiplier
            const dc = 100;
            const afterMarkup = dc*(1+op.markup/100);
            const afterFees = afterMarkup*(1+op.gc_fee/100+op.insurance/100+op.overhead/100);
            const total = afterFees*(1+op.contingency/100);
            const margin = (total-dc)/total*100;
            return (
              <div key={key} style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 1fr 1fr",gap:6,padding:"8px",borderRadius:8,marginBottom:4,background:T.bg1,border:`1px solid ${T.subtle}`,alignItems:"center"}}
                onMouseEnter={e=>e.currentTarget.style.background=T.bg2}
                onMouseLeave={e=>e.currentTarget.style.background=T.bg1}>
                <div style={{fontSize:12,fontWeight:600,color:T.text}}>{op.label}</div>
                {["markup","gc_fee","insurance","overhead","contingency"].map(field=>(
                  <div key={field} style={{textAlign:"center"}}>
                    <input type="number" defaultValue={op[field]} onBlur={e=>updateOP(key,field,e.target.value)}
                      style={{width:56,background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"5px 6px",color:field==="markup"?T.blue:field==="gc_fee"?T.teal:field==="contingency"?T.amber:T.muted,fontSize:12,fontFamily:"'Space Mono',monospace",textAlign:"center"}}/>
                    <span style={{fontSize:9,color:T.muted}}>%</span>
                  </div>
                ))}
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:11,fontWeight:700,color:margin>30?T.green:margin>20?T.amber:T.red}}>{margin.toFixed(0)}% margin</div>
                  <div style={{fontSize:9,color:T.muted}}>{(total/dc).toFixed(2)}x cost</div>
                </div>
              </div>
            );
          })}

          <div style={{background:T.bg2,borderRadius:8,padding:"12px 14px",marginTop:12,fontSize:11,color:T.muted,lineHeight:1.7}}>
            <strong style={{color:T.gold}}>Bay Area benchmarks (2025/2026):</strong> SRO/pre-1978: 26-30% markup, 15-18% contingency · Senior/ADA: 25-28%, 13-15% · Residential: 22-26%, 10-13% · Commercial TI: 20-25%, 10-12% · Target gross margin: 30-40% for specialty GCs
          </div>
        </Card>
      )}

      {/* MATERIAL COSTS */}
      {activeTab==="materials" && (
        <Card>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Material Unit Costs</div>
          <div style={{fontSize:11,color:T.muted,marginBottom:14}}>Your actual material costs — used in quick estimates and AI pricing. Update as prices change.</div>
          <div style={{display:"grid",gridTemplateColumns:window.innerWidth<768?"1fr":"1fr 1fr",gap:8}}>
            {Object.entries(settings.material_costs).map(([key,m])=>(
              <div key={key} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:T.bg2,borderRadius:8}}>
                <div style={{flex:1,fontSize:12,color:T.muted}}>{m.label}</div>
                <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
                  <span style={{fontSize:11,color:T.muted}}>$</span>
                  <input type="number" defaultValue={m.cost} step="0.01" onBlur={e=>updateMaterial(key,"cost",e.target.value)}
                    style={{width:72,background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"5px 7px",color:T.text,fontSize:12,fontFamily:"'Space Mono',monospace",textAlign:"right"}}/>
                  <span style={{fontSize:10,color:T.muted}}>/{m.unit}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* UNIT COST GUIDE */}
      {activeTab==="units" && (
        <Card>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Unit Cost Estimating Guide</div>
          <div style={{fontSize:11,color:T.muted,marginBottom:14}}>Quick budget ranges per unit or sqft — used for ROM estimates and sanity checks</div>
          {Object.entries(settings.unit_costs).map(([key,u])=>(
            <div key={key} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",background:T.bg2,borderRadius:9,marginBottom:8}}>
              <div style={{flex:1,fontSize:13,fontWeight:600,color:T.text}}>{u.label}</div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:9,color:T.muted,marginBottom:3}}>LOW</div>
                  <div style={{display:"flex",alignItems:"center",gap:4}}>
                    <span style={{fontSize:10,color:T.muted}}>$</span>
                    <input type="number" defaultValue={u.low} onBlur={e=>updateUnit(key,"low",e.target.value)}
                      style={{width:80,background:T.bg1,border:`1px solid ${T.green}40`,borderRadius:6,padding:"5px 7px",color:T.green,fontSize:12,fontFamily:"'Space Mono',monospace",textAlign:"right"}}/>
                  </div>
                </div>
                <span style={{color:T.muted}}>—</span>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:9,color:T.muted,marginBottom:3}}>HIGH</div>
                  <div style={{display:"flex",alignItems:"center",gap:4}}>
                    <span style={{fontSize:10,color:T.muted}}>$</span>
                    <input type="number" defaultValue={u.high} onBlur={e=>updateUnit(key,"high",e.target.value)}
                      style={{width:80,background:T.bg1,border:`1px solid ${T.red}40`,borderRadius:6,padding:"5px 7px",color:T.red,fontSize:12,fontFamily:"'Space Mono',monospace",textAlign:"right"}}/>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* QUICK CALCULATOR */}
      {activeTab==="calc" && (
        <div style={{display:"grid",gridTemplateColumns:"360px 1fr",gap:14}}>
          <Card>
            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14}}>Quick Estimate Calculator</div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Project Type</div>
              <select value={calcInput.type} onChange={e=>setCalcInput(p=>({...p,type:e.target.value}))}
                style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}>
                {Object.entries(settings.op).map(([k,op])=><option key={k} value={k}>{op.label}</option>)}
              </select>
            </div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Direct Cost (subs + labor + materials)</div>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:16,color:T.muted}}>$</span>
                <input type="number" value={calcInput.directCost} onChange={e=>setCalcInput(p=>({...p,directCost:e.target.value}))}
                  placeholder="0"
                  style={{flex:1,background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:18,fontFamily:"'Space Mono',monospace",fontWeight:700}}/>
              </div>
            </div>
            <div style={{marginBottom:16}}>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Number of Units (if multi-unit)</div>
              <input type="number" value={calcInput.units} onChange={e=>setCalcInput(p=>({...p,units:e.target.value}))}
                style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:14,fontFamily:"'Space Mono',monospace"}}/>
            </div>
            <Btn primary onClick={runCalc} disabled={!calcInput.directCost} style={{width:"100%",justifyContent:"center",fontSize:14,padding:"13px"}}>
              🧮 Calculate Client Price
            </Btn>
            {calcResult && (
              <div style={{marginTop:14,padding:"12px",background:T.bg2,borderRadius:8,fontSize:11,color:T.muted,lineHeight:1.8}}>
                Using: {calcResult.op.markup}% markup · {calcResult.op.gc_fee}% GC fee · {calcResult.op.contingency}% contingency
              </div>
            )}
          </Card>

          {calcResult && (
            <Card>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:16}}>Estimate Breakdown</div>
              {[
                {l:"Direct Cost",                 v:calcResult.dc,          c:T.muted, bold:false, border:""},
                {l:`+ Markup (${calcResult.op.markup}%)`, v:calcResult.markup, c:T.blue, bold:false, border:T.blue},
                {l:"= Price After Markup",         v:calcResult.afterMarkup, c:T.text, bold:true, border:""},
                {l:`+ GC Fee (${calcResult.op.gc_fee}%)`,  v:calcResult.gcFee, c:T.teal, bold:false, border:T.teal},
                {l:`+ Insurance (${calcResult.op.insurance}%)`, v:calcResult.insurance, c:T.purple, bold:false, border:T.purple},
                {l:`+ Overhead (${calcResult.op.overhead}%)`,   v:calcResult.overhead, c:T.purple, bold:false, border:T.purple},
                {l:"= Subtotal",                  v:calcResult.sub,         c:T.text, bold:true, border:""},
                {l:`+ Contingency (${calcResult.op.contingency}%)`, v:calcResult.contingency, c:T.amber, bold:false, border:T.amber},
              ].map((row,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:row.bold?"10px 12px":"7px 12px",marginBottom:3,borderRadius:7,background:row.bold?T.bg3:T.bg2,borderLeft:row.border?`3px solid ${row.border}`:"3px solid transparent"}}>
                  <span style={{fontSize:row.bold?13:12,fontWeight:row.bold?700:400,color:row.c}}>{row.l}</span>
                  <span style={{fontSize:row.bold?14:12,fontWeight:row.bold?800:600,color:row.c,fontFamily:"'Space Mono',monospace"}}>{fmt(row.v)}</span>
                </div>
              ))}
              <div style={{background:`linear-gradient(135deg,${T.gold}20,${T.teal}10)`,border:`2px solid ${T.gold}50`,borderRadius:10,padding:"16px",marginTop:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:2}}>Client Price</div>
                  <div style={{fontSize:11,color:T.muted}}>{calcResult.margin.toFixed(1)}% gross margin · {fmt(calcResult.grossProfit)} profit</div>
                </div>
                <div style={{fontSize:30,fontWeight:800,color:T.gold,fontFamily:"'Space Mono',monospace"}}>{fmt(calcResult.total)}</div>
              </div>
              <div style={{display:"flex",gap:8,marginTop:12}}>
                <Btn ghost small onClick={()=>navigator.clipboard.writeText(`Direct Cost: ${fmt(calcResult.dc)}\nMarkup (${calcResult.op.markup}%): ${fmt(calcResult.markup)}\nGC Fee (${calcResult.op.gc_fee}%): ${fmt(calcResult.gcFee)}\nContingency (${calcResult.op.contingency}%): ${fmt(calcResult.contingency)}\nCLIENT PRICE: ${fmt(calcResult.total)}\nGross Margin: ${calcResult.margin.toFixed(1)}%`)} style={{flex:1,justifyContent:"center",fontSize:11}}>
                  Copy Breakdown
                </Btn>
                <Btn ghost small onClick={()=>setCalcResult(null)} style={{fontSize:11}}>Clear</Btn>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* BILLING TERMS */}
      {activeTab==="billing" && (
        <div>
        <Card style={{marginBottom:12}}>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>CO & RFI Document Templates</div>
          <div style={{fontSize:11,color:T.muted,marginBottom:14}}>Your company info on all exported CO and RFI PDFs — overrides default</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            {[["co_header_name","Company Name on Documents",settings.company_name||""],["co_header_cslb","CSLB #",settings.cslb||""],["co_header_email","Email on Documents",settings.email||""],["co_header_phone","Phone",""],["co_header_address","City, State","San Mateo, CA"],["co_header_license","License Type","General Contractor B"]].map(([k,l,def])=>(
              <div key={k}>
                <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{l}</div>
                <input defaultValue={settings[k]||def} onBlur={e=>setSettings(p=>({...p,[k]:e.target.value}))}
                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
              </div>
            ))}
          </div>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Standard CO Terms (on every CO PDF)</div>
            <textarea defaultValue={settings.co_terms||"This Change Order, when signed by both parties, becomes part of and subject to the terms of the original contract. Work described above shall not commence until this Change Order is fully executed."} onBlur={e=>setSettings(p=>({...p,co_terms:e.target.value}))} rows={2}
              style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 12px",color:T.text,fontSize:12,fontFamily:"inherit",resize:"vertical"}}/>
          </div>
          <div>
            <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>RFI Response Protocol (on every RFI PDF)</div>
            <textarea defaultValue={settings.rfi_terms||"Contractor shall respond to all RFIs within 48 hours. Work shall not proceed pending a response without written authorization."} onBlur={e=>setSettings(p=>({...p,rfi_terms:e.target.value}))} rows={2}
              style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 12px",color:T.text,fontSize:12,fontFamily:"inherit",resize:"vertical"}}/>
          </div>
          <div style={{marginTop:10,background:T.amberBg,borderRadius:8,padding:"9px 12px",fontSize:11,color:T.amber}}>
            ⚡ Hit Save All Settings above to apply to all future exports.
          </div>
        </Card>
        <Card>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14}}>Billing & Payment Terms</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
            {[
              {key:"retainage", label:"Retainage %", suffix:"%", prefix:""},
              {key:"late_fee", label:"Late Payment Fee (monthly %)", suffix:"%", prefix:""},
            ].map(f=>(
              <div key={f.key}>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>{f.label}</div>
                <div style={{display:"flex",alignItems:"center",gap:5}}>
                  <input type="number" defaultValue={settings.billing[f.key]} onBlur={e=>setSettings(p=>({...p,billing:{...p.billing,[f.key]:parseFloat(e.target.value)||0}}))}
                    style={{width:80,background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 10px",color:T.text,fontSize:14,fontFamily:"'Space Mono',monospace",textAlign:"right"}}/>
                  <span style={{color:T.muted}}>%</span>
                </div>
              </div>
            ))}
            {[
              {key:"payment_terms", label:"Payment Terms", placeholder:"Net 30"},
              {key:"progress_billing", label:"Progress Billing Schedule", placeholder:"1st of every month"},
            ].map(f=>(
              <div key={f.key}>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>{f.label}</div>
                <input value={settings.billing[f.key]} onChange={e=>setSettings(p=>({...p,billing:{...p.billing,[f.key]:e.target.value}}))}
                  placeholder={f.placeholder} style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
              </div>
            ))}
          </div>
          <div style={{marginTop:14,background:T.bg2,borderRadius:8,padding:"12px 14px",fontSize:12,color:T.muted,lineHeight:1.7}}>
            <strong style={{color:T.text}}>Standard protections:</strong> 20-Day Preliminary Notice served Day 1 on every job. Conditional lien waiver with every pay app. Unconditional only after check clears. These are auto-included in all your document templates.
          </div>
        </Card>
        </div>
      )}

      {/* COMPANY INFO */}
      {activeTab==="company" && (
        <Card>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14}}>Company Information</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            {[
              {key:"company_name",label:"Company Legal Name"},
              {key:"owner_name",label:"Owner / Principal Name"},
              {key:"cslb",label:"CSLB License Number"},
              {key:"location",label:"City, State"},
              {key:"email",label:"Business Email"},
              {key:"phone",label:"Phone Number"},
              {key:"website",label:"Website"},
            ].map(f=>(
              <div key={f.key}>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>{f.label}</div>
                <input value={settings[f.key]||""} onChange={e=>setSettings(p=>({...p,[f.key]:e.target.value}))}
                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
              </div>
            ))}
          </div>
          {/* Logo upload */}
          <div style={{marginTop:16,gridColumn:"1/-1"}}>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Company Logo (appears on all proposals & documents)</div>
            <div style={{display:"flex",alignItems:"center",gap:14}}>
              {settings.logo_url ? (
                <div style={{position:"relative"}}>
                  <img src={settings.logo_url} alt="Logo"
                    style={{height:60,maxWidth:200,objectFit:"contain",border:"1px solid "+T.subtle,borderRadius:8,padding:4,background:"white"}}/>
                  <button onClick={()=>setSettings(p=>({...p,logo_url:null}))}
                    style={{position:"absolute",top:-6,right:-6,width:18,height:18,borderRadius:"50%",background:T.red,color:"white",border:"none",fontSize:10,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
                </div>
              ) : (
                <div style={{width:100,height:60,border:"2px dashed "+T.subtle,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",color:T.muted,fontSize:10}}>No logo</div>
              )}
              <div>
                <input type="file" accept="image/*" id="logo-upload" style={{display:"none"}}
                  onChange={e=>{
                    const file=e.target.files[0]; if(!file) return;
                    const reader=new FileReader();
                    reader.onload=ev=>setSettings(p=>({...p,logo_url:ev.target.result}));
                    reader.readAsDataURL(file);
                  }}/>
                <label htmlFor="logo-upload"
                  style={{display:"inline-block",background:T.teal,color:"white",borderRadius:7,padding:"7px 14px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                  Upload Logo
                </label>
                <div style={{fontSize:10,color:T.muted,marginTop:4}}>PNG or SVG recommended · Max 2MB · Displayed on proposals, COs, and RFIs</div>
              </div>
            </div>
          </div>

          <div style={{marginTop:16,background:T.amberBg,borderRadius:8,padding:"10px 14px",fontSize:12,color:T.amber,lineHeight:1.6,gridColumn:"1/-1"}}>
            ⚡ Your company info auto-populates in all proposals, bid templates, lien documents, and AI estimates. Update here once — updates everywhere.
          </div>

          {/* Company Policy Statement */}
          <div style={{gridColumn:"1/-1",marginTop:8,background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"16px 18px"}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Company Standards & Accountability Policy</div>
            <div style={{fontSize:11,color:T.muted,marginBottom:12,lineHeight:1.6}}>This policy is automatically shown to all subcontractors when they register and to owners before they sign Change Orders. It creates a legal record that all parties have been informed of your standards.</div>
            <div style={{background:T.bg2,borderRadius:8,padding:"12px 16px",border:"1px solid "+T.subtle}}>
              <div style={{fontSize:13,fontWeight:800,color:T.red,marginBottom:10,textAlign:"center"}}>{GCG_POLICY.headline}</div>
              {GCG_POLICY.statement.map((s,i)=>(
                <div key={i} style={{fontSize:11,color:T.muted,lineHeight:1.7,marginBottom:6,paddingLeft:10,borderLeft:"2px solid "+T.subtle}}>{s}</div>
              ))}
              <div style={{marginTop:12,fontSize:10,color:T.muted,borderTop:"1px solid "+T.subtle,paddingTop:8}}>{GCG_POLICY.subNote}</div>
            </div>
          </div>
        </Card>
      )}

      {activeTab==="team" && (
        <TeamManagerView session={session}/>
      )}

      {activeTab==="roles" && (
        <RoleCustomizerTab session={session}/>
      )}

      {/* ACCOUNT & SUBSCRIPTION */}
      {activeTab==="account" && (
        <AccountSubscriptionTab session={session}/>
      )}

      {/* Save button at bottom */}
      <div style={{marginTop:20,display:"flex",justifyContent:"flex-end"}}>
        <Btn primary onClick={()=>save()} style={{padding:"12px 32px",fontSize:14,background:saved?T.green:"",color:saved?"white":""}}>
          {saved?"✓ All Settings Saved!":"Save Settings"}
        </Btn>
      </div>
    </div>
  );
}


// ── Demo Project Tab ──────────────────────────────────────────────────────────
function DemoProjectTab({session}) {
  const cid = session?.companyId || "local";
  const projKey = projectsKey(cid);
  const [projects, setProjects] = React.useState(()=>{
    try { return JSON.parse(localStorage.getItem(projKey)||"[]"); } catch { return []; }
  });
  const [msg, setMsg] = React.useState(null);

  const hasSample = projects.some(p => p.isSample);

  const createSample = () => {
    const sampleProjectId = "proj_sample_"+cid+"_"+Date.now();
    const today = new Date().toLocaleDateString();
    const sampleProject = {
      id: sampleProjectId,
      name: "🏗 Sample Project — Explore Grok Field Ops",
      address: "123 Main St, San Francisco CA 94103",
      addr: "123 Main St, San Francisco CA 94103",
      type: "Full Interior Renovation",
      status: "active",
      budget: 285000,
      spent: 142000,
      pct: 45,
      value: 285000,
      start: new Date().toISOString().slice(0,10),
      end: new Date(Date.now()+90*864e5).toISOString().slice(0,10),
      units: "1,800 SF · 3 Bed / 2 Bath",
      companyId: cid,
      isSample: true,
      client: "Sample Owner",
      description: "This is a sample project to help you explore all features. Feel free to click around, add notes, try the AI tools, and see how everything works. Delete this project when ready.",
      phases: [
        {name:"Demo & Site Prep", pct:100, budget:18000},
        {name:"Rough Framing", pct:100, budget:32000},
        {name:"MEP Rough-In", pct:80, budget:54000},
        {name:"Drywall & Tape", pct:40, budget:28000},
        {name:"Tile & Finishes", pct:0, budget:42000},
        {name:"Paint", pct:0, budget:18000},
        {name:"Closeout", pct:0, budget:12000},
      ],
      openCOs: 2,
      openRFIs: 2,
      createdAt: new Date().toISOString().slice(0,10),
    };

    const updated = [sampleProject, ...projects.filter(p=>!p.isSample)];
    localStorage.setItem(projKey, JSON.stringify(updated));
    setProjects(updated);

    // Sample RFIs
    localStorage.setItem("grok_rfis_global_"+cid, JSON.stringify([
      {id:"rfi_s1", num:"RFI-001", project:sampleProject.name, subject:"Drain location — plan vs field conflict", priority:"urgent", status:"open", submitted:today, desc:"Drain on plan P-3 conflicts with existing slab penetration. Clarify before pouring.", comments:[], attachments:[]},
      {id:"rfi_s2", num:"RFI-002", project:sampleProject.name, subject:"Paint color — hallway not specified", priority:"routine", status:"pending", submitted:today, desc:"Hallway paint not in finish schedule. Owner to select.", comments:[{id:1,author:"Field Supervisor",text:"Owner notified. Awaiting selection.",date:today}], attachments:[]},
    ]));

    // Sample COs
    localStorage.setItem("grok_cos_global_"+cid, JSON.stringify([
      {id:"co_s1", num:"CO-001", project:sampleProject.name, desc:"Water-damaged subfloor — unforeseen during demo", amount:4200, status:"approved", date:today, days:0, comments:[{id:1,author:"Owner",text:"Approved. Please proceed.",date:today}], attachments:[]},
      {id:"co_s2", num:"CO-002", project:sampleProject.name, desc:"Owner requested tile upgrade — master bath", amount:2800, status:"pending", date:today, days:3, comments:[], attachments:[]},
    ]));

    // Sample docs
    localStorage.setItem("grok_templates_"+cid, JSON.stringify([
      {id:"doc_s1", name:"Owner Contract — Sample", cat:"contracts", notes:"Sample fixed-price contract.", uploadedAt:today, uploadedBy:session?.ownerName||"Owner", isSample:true},
      {id:"doc_s2", name:"Scope of Work — Sample", cat:"contracts", notes:"Sample scope document.", uploadedAt:today, uploadedBy:session?.ownerName||"Owner", isSample:true},
      {id:"doc_s3", name:"Conditional Lien Waiver — Sample", cat:"lien", notes:"Sample lien waiver.", uploadedAt:today, uploadedBy:session?.ownerName||"Owner", isSample:true},
    ]));

    setMsg({type:"ok", text:"✅ Sample project added! Go to Projects to explore it."});
    setTimeout(()=>setMsg(null), 4000);
  };

  const deleteSample = () => {
    const updated = projects.filter(p=>!p.isSample);
    localStorage.setItem(projKey, JSON.stringify(updated));
    setProjects(updated);
    setMsg({type:"ok", text:"🗑 Sample project removed."});
    setTimeout(()=>setMsg(null), 3000);
  };

  return (
    <Card>
      <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>🏗 Demo / Sample Project</div>
      <div style={{fontSize:11,color:T.muted,marginBottom:20,lineHeight:1.6}}>
        The sample project lets you explore all features — drawings, RFIs, COs, AI tools, punch list — without touching your real jobs. Add it anytime to test new features or show the app to someone.
      </div>

      {msg&&(
        <div style={{marginBottom:14,padding:"10px 14px",borderRadius:8,background:msg.type==="ok"?T.greenBg:T.redBg,border:`1px solid ${msg.type==="ok"?T.green:T.red}30`,fontSize:12,color:msg.type==="ok"?T.green:T.red}}>
          {msg.text}
        </div>
      )}

      {/* Status */}
      <div style={{background:T.bg2,borderRadius:10,padding:"14px 16px",marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:2}}>🏗 Sample Project — Explore Grok Field Ops</div>
          <div style={{fontSize:11,color:T.muted}}>
            {hasSample ? "Currently active in your Projects list" : "Not currently in your Projects list"}
          </div>
          {hasSample&&(
            <div style={{fontSize:10,color:T.green,marginTop:4,fontWeight:600}}>✅ Active — visible in Projects</div>
          )}
        </div>
        <div style={{fontSize:32}}>{hasSample?"🟢":"⚪"}</div>
      </div>

      {/* What's included */}
      <div style={{background:T.goldBg,border:`1px solid ${T.gold}30`,borderRadius:10,padding:"12px 14px",marginBottom:16}}>
        <div style={{fontSize:10,fontWeight:700,color:T.gold,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:8}}>What's included in the sample</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
          {[
            ["🏗","Full project at 45% completion with phase progress"],
            ["❓","2 sample RFIs — one urgent, one pending with comments"],
            ["📝","2 sample COs — one approved, one pending signature"],
            ["📁","3 sample documents — contract, scope, lien waiver"],
            ["💰","Budget vs actual with real numbers to explore"],
            ["🤖","AI tools all work — estimates, PM chat, blueprint review"],
          ].map(([icon,desc])=>(
            <div key={desc} style={{display:"flex",gap:7,alignItems:"flex-start",fontSize:10,color:T.muted}}>
              <span style={{flexShrink:0}}>{icon}</span><span>{desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div style={{display:"flex",gap:10}}>
        {!hasSample ? (
          <Btn primary onClick={createSample} style={{flex:1,justifyContent:"center"}}>
            + Add Sample Project
          </Btn>
        ) : (
          <>
            <Btn primary onClick={createSample} style={{flex:1,justifyContent:"center"}}>
              🔄 Reset Sample Project
            </Btn>
            <Btn ghost onClick={deleteSample} style={{flex:1,justifyContent:"center",color:T.red,borderColor:T.red+"40"}}>
              🗑 Remove Sample
            </Btn>
          </>
        )}
      </div>
      <div style={{fontSize:10,color:T.muted,marginTop:8,textAlign:"center"}}>
        {hasSample ? "Reset recreates the sample fresh — useful after testing." : "Adding won't affect your real projects."}
      </div>
    </Card>
  );
}

function AccountSubscriptionTab({session}) {
  const [acctTab, setAcctTab] = useState("profile");
  const [profileForm, setProfileForm] = useState({
    name: session?.ownerName || "",
    email: session?.email || "",
    newPassword: "",
    confirmPassword: "",
  });
  const [profileMsg, setProfileMsg] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const plan = session?.plan || "starter";
  const tier = TIERS[plan] || TIERS.starter;
  const usage = getUsage(session?.companyId);

  const usageItems = [
    {key:"estimates",   label:"AI Estimates",    icon:"🧮"},
    {key:"pm_chats",    label:"AI PM Chats",      icon:"💬"},
    {key:"video",       label:"Video Analysis",   icon:"🎥"},
    {key:"meetings",    label:"Meeting Summaries",icon:"📋"},
    {key:"blueprint",   label:"Blueprint AI",     icon:"📐"},
  ];

  const stripeLinks = {
    starter_monthly: "https://buy.stripe.com/5kQeVeddjdGe2UDesh4Rq00",
    starter_annual:  "https://buy.stripe.com/9B6bJ2gpv6dMeDl1Fv4Rq01",
    pro_monthly:     "https://buy.stripe.com/fZu8wQ2yF0Ts1Qzesh4Rq02",
    pro_annual:      "https://buy.stripe.com/9B67sMflr6dM3YH1Fv4Rq03",
    gcpro_monthly:   "https://buy.stripe.com/5kQ6oI5KRgSqcvdck94Rq07",
    gcpro_annual:    "https://buy.stripe.com/7sY3cw4GNeKi8eXesh4Rq05",
  };

  const handleProfileSave = async () => {
    if (profileForm.newPassword && profileForm.newPassword !== profileForm.confirmPassword) {
      setProfileMsg({type:"error", text:"Passwords do not match."});
      return;
    }
    setSaving(true);
    try {
      // Update Supabase auth email/password
      const updates = {};
      if (profileForm.email !== session?.email) updates.email = profileForm.email;
      if (profileForm.newPassword) updates.password = profileForm.newPassword;

      if (Object.keys(updates).length > 0) {
        const token = localStorage.getItem("sb_token");
        const r = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
          method: "PUT",
          headers: {"apikey": SUPABASE_ANON_KEY, "Authorization": "Bearer "+token, "Content-Type":"application/json"},
          body: JSON.stringify(updates),
        });
        const d = await r.json();
        if (d.error) { setProfileMsg({type:"error", text:d.error.message||"Update failed."}); setSaving(false); return; }
      }

      // Update local company record name
      const companies = DB.get("grok_companies") || [];
      const updated = companies.map(c => c.id===session?.companyId ? {...c, owner:profileForm.name, email:profileForm.email||c.email} : c);
      DB.set("grok_companies", updated);

      // Update session
      const sess = DB.get("grok_auth_session");
      if (sess) DB.set("grok_auth_session", {...sess, ownerName:profileForm.name, email:profileForm.email||sess.email});

      setProfileMsg({type:"ok", text:"Profile updated! Changes take effect on next login."});
    } catch(e) {
      setProfileMsg({type:"error", text:"Update failed. Try again."});
    }
    setSaving(false);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm.toLowerCase() !== "delete my account") return;
    const email = session?.email;

    // 1. Add email to self-banned list so they can never re-register
    if (email) addSelfBanned(email);

    // 2. Remove from grok_companies
    try {
      const companies = JSON.parse(localStorage.getItem(COMPANIES_KEY)||"[]");
      const filtered = companies.filter(c => (c.email||"").toLowerCase() !== (email||"").toLowerCase());
      localStorage.setItem(COMPANIES_KEY, JSON.stringify(filtered));
    } catch(e) {}

    // 3. Delete all company-scoped data
    const keysToRemove = [];
    for (let i=0; i<localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith("grok_") || k === "sb_token")) keysToRemove.push(k);
    }
    // Keep the self-banned list
    keysToRemove.filter(k => k !== SELF_BANNED_KEY).forEach(k => localStorage.removeItem(k));

    // 4. Delete from Supabase auth
    try {
      const token = localStorage.getItem("sb_token");
      if (token) {
        await fetch(`${SUPABASE_URL}/auth/v1/user`, {
          method: "DELETE",
          headers: {"apikey": SUPABASE_ANON_KEY, "Authorization": "Bearer "+token},
        });
      }
    } catch(e) {}

    // 5. Sign out and reload
    localStorage.removeItem("sb_token");
    localStorage.removeItem("grok_auth_session");
    window.location.reload();
  };

  const TabB = ({id,label}) => (
    <button onClick={()=>setAcctTab(id)} style={{padding:"7px 16px",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:acctTab===id?T.gold:T.bg2,color:acctTab===id?T.bg0:T.muted,transition:"all 0.15s"}}>
      {label}
    </button>
  );

  return (
    <div>
      <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap"}}>
        <TabB id="profile"      label="👤 Profile"/>
        <TabB id="subscription" label="💳 Subscription"/>
        <TabB id="usage"        label="📊 Usage"/>
        <TabB id="demo"         label="🏗 Demo Project"/>
        <TabB id="danger"       label="🗑 Danger Zone"/>
      </div>

      {/* PROFILE */}
      {acctTab==="profile" && (
        <Card>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Account Profile</div>
          <div style={{fontSize:11,color:T.muted,marginBottom:18}}>Update your name, email, or password. Email changes require re-verification.</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Owner / Contact Name</div>
              <input value={profileForm.name} onChange={e=>setProfileForm(p=>({...p,name:e.target.value}))}
                style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}
                placeholder="Your full name"/>
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Email Address</div>
              <input value={profileForm.email} onChange={e=>setProfileForm(p=>({...p,email:e.target.value}))}
                style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}
                placeholder="you@company.com" type="email"/>
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>New Password (leave blank to keep current)</div>
              <input value={profileForm.newPassword} onChange={e=>setProfileForm(p=>({...p,newPassword:e.target.value}))}
                style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}
                placeholder="New password" type="password"/>
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Confirm New Password</div>
              <input value={profileForm.confirmPassword} onChange={e=>setProfileForm(p=>({...p,confirmPassword:e.target.value}))}
                style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}
                placeholder="Confirm new password" type="password"/>
            </div>
          </div>
          {profileMsg && (
            <div style={{marginBottom:12,padding:"10px 14px",borderRadius:8,background:profileMsg.type==="ok"?T.greenBg||"rgba(34,197,94,0.1)":T.redBg||"rgba(239,68,68,0.1)",border:`1px solid ${profileMsg.type==="ok"?T.green:T.red}30`,fontSize:12,color:profileMsg.type==="ok"?T.green:T.red}}>
              {profileMsg.text}
            </div>
          )}
          <Btn primary onClick={handleProfileSave} disabled={saving}>
            {saving ? "Saving…" : "Save Profile Changes"}
          </Btn>
        </Card>
      )}

      {/* SUBSCRIPTION */}
      {acctTab==="subscription" && (
        <div>
          {/* Current plan card */}
          <Card style={{marginBottom:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
              <div>
                <div style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Current Plan</div>
                <div style={{fontSize:24,fontWeight:800,color:tier.color||T.gold}}>{tier.name}</div>
                <div style={{fontSize:13,color:T.muted,marginTop:2}}>{tier.price} · billed monthly</div>
                {session?.moneyBackDeadline && new Date(session.moneyBackDeadline)>new Date() && <div style={{fontSize:11,color:T.green,marginTop:6,fontWeight:700}}>✓ 15-day money-back guarantee active until {session.moneyBackDeadline}</div>}
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8,alignItems:"flex-end"}}>
                <a href="https://billing.stripe.com/p/login/test_00000000" target="_blank" rel="noopener noreferrer"
                  style={{background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"8px 16px",fontSize:12,color:T.text,textDecoration:"none",fontWeight:600,cursor:"pointer"}}>
                  Manage Billing (Stripe Portal) →
                </a>
                <div style={{fontSize:10,color:T.muted}}>Update card · download invoices · view history</div>
              </div>
            </div>
          </Card>

          {/* Upgrade options */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
            {[
              {id:"starter", name:"Starter", price:"$149", color:T.blue, limits:"3 projects · 25 estimates · 150 AI chats", stripe:"starter_monthly"},
              {id:"pro",     name:"Pro",     price:"$249", color:T.purple, limits:"10 projects · 50 estimates · 350 AI chats", stripe:"pro_monthly"},
              {id:"gcpro",   name:"GC Pro",  price:"$399", color:T.gold, limits:"Unlimited everything · Team roles (coming soon)", stripe:"gcpro_monthly"},
            ].map(p=>(
              <div key={p.id} style={{background:plan===p.id?T.bg3:T.bg2,border:`2px solid ${plan===p.id?p.color:T.subtle}`,borderRadius:12,padding:"16px",position:"relative"}}>
                {plan===p.id && <div style={{position:"absolute",top:10,right:10,background:p.color,color:T.bg0,fontSize:9,fontWeight:800,borderRadius:20,padding:"2px 8px",textTransform:"uppercase"}}>Current</div>}
                <div style={{fontSize:16,fontWeight:800,color:p.color,marginBottom:2}}>{p.name}</div>
                <div style={{fontSize:20,fontWeight:800,color:T.text,marginBottom:2}}>{p.price}<span style={{fontSize:11,fontWeight:400,color:T.muted}}>/mo</span></div>
                <div style={{fontSize:11,color:T.muted,marginBottom:14,lineHeight:1.6}}>{p.limits}</div>
                {plan!==p.id && (
                  <a href={buildStripeUrl(stripeLinks[p.stripe], {companyId: session?.companyId, email: session?.email})||"#"} target="_blank" rel="noopener noreferrer"
                    style={{display:"block",textAlign:"center",background:p.color,color:T.bg0,borderRadius:8,padding:"8px",fontSize:12,fontWeight:700,textDecoration:"none",cursor:"pointer"}}>
                    {TIERS[plan]?.price > TIERS[p.id]?.price ? "Downgrade" : "Upgrade"} to {p.name}
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* Cancellation policy */}
          <Card style={{background:T.amberBg,border:`1px solid ${T.amber}30`}}>
            <div style={{fontSize:12,fontWeight:700,color:T.amber,marginBottom:6}}>⚠️ Cancellation Policy</div>
            <div style={{fontSize:11,color:T.muted,lineHeight:1.7}}>{SUBSCRIPTION_POLICY.message}</div>
            {!cancelConfirm ? (
              <button onClick={()=>setCancelConfirm(true)} style={{marginTop:12,background:"transparent",border:`1px solid ${T.red}60`,borderRadius:7,padding:"7px 16px",color:T.red,fontSize:11,cursor:"pointer",fontWeight:600}}>
                Cancel Subscription
              </button>
            ) : (
              <div style={{marginTop:12,background:T.bg2,borderRadius:8,padding:"12px 14px"}}>
                <div style={{fontSize:12,color:T.red,fontWeight:700,marginBottom:8}}>Are you sure? This will end your access at the current billing period.</div>
                <div style={{display:"flex",gap:8}}>
                  <a href="https://billing.stripe.com/p/login/test_00000000" target="_blank" rel="noopener noreferrer"
                    style={{background:T.red,color:"white",borderRadius:7,padding:"8px 16px",fontSize:12,fontWeight:700,textDecoration:"none",cursor:"pointer"}}>
                    Yes, Cancel in Stripe →
                  </a>
                  <button onClick={()=>setCancelConfirm(false)} style={{background:T.bg3,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"8px 16px",fontSize:12,color:T.muted,cursor:"pointer"}}>
                    Keep My Subscription
                  </button>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* USAGE */}
      {acctTab==="usage" && (
        <Card>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Monthly Usage — {tier.name} Plan</div>
          <div style={{fontSize:11,color:T.muted,marginBottom:20}}>Resets on the 1st of each month. Upgrade your plan to increase limits.</div>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {usageItems.map(({key,label,icon})=>{
              const limit = tier.limits[key]||9999;
              const used = usage[key]||0;
              const pct = limit>=9999 ? 100 : Math.min(100,Math.round(used/limit*100));
              const color = pct>90?T.red:pct>70?T.amber:T.green;
              return (
                <div key={key}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                    <div style={{fontSize:13,color:T.text,fontWeight:600}}>{icon} {label}</div>
                    <div style={{fontSize:12,fontWeight:700,color,fontFamily:"'Space Mono',monospace"}}>
                      {used} / {limit>=9999?"∞":limit}
                    </div>
                  </div>
                  {limit < 9999 && (
                    <div style={{height:8,background:T.bg3,borderRadius:4,overflow:"hidden"}}>
                      <div style={{width:pct+"%",height:"100%",background:color,borderRadius:4,transition:"width 0.4s"}}/>
                    </div>
                  )}
                  {limit >= 9999 && (
                    <div style={{height:8,background:`linear-gradient(90deg,${T.green},${T.teal})`,borderRadius:4,opacity:0.5}}/>
                  )}
                  {pct>90&&limit<9999&&<div style={{fontSize:10,color:T.red,marginTop:4}}>⚠️ Almost at limit — consider upgrading</div>}
                </div>
              );
            })}
          </div>
          <div style={{marginTop:20,background:T.bg2,borderRadius:8,padding:"10px 14px",fontSize:11,color:T.muted,lineHeight:1.6}}>
            Usage resets monthly on your billing anniversary. AI estimates, PM chats, and blueprint analysis all count toward your plan limits.
          </div>
        </Card>
      )}

      {/* DANGER ZONE */}
      {acctTab==="demo" && (
        <>
        <DemoProjectTab session={session}/>
        <Card style={{marginTop:12}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:8}}>🔧 Supabase Connection Test</div>
          <button onClick={async()=>{
            const cid = session?.companyId||window._grokCompanyId||"local";
            try {
              // Test 1: simple insert
              const r = await fetch(`${SUPABASE_URL}/rest/v1/projects`,{
                method:"POST",
                headers:{"apikey":SUPABASE_ANON_KEY,"Authorization":"Bearer "+sb._token(),"Content-Type":"application/json","Prefer":"resolution=merge-duplicates,return=representation"},
                body:JSON.stringify([{id:"test_123",company_id:cid,data:{name:"test"},updated_at:new Date().toISOString()}])
              });
              const txt = await r.text();
              alert(`Status: ${r.status}\nCID: ${cid}\nURL: ${SUPABASE_URL}/rest/v1/projects\nResponse: ${txt.slice(0,400)}`);
            } catch(e) {
              alert(`Fetch error: ${e.message}`);
            }
          }} style={{background:T.blue,color:"white",border:"none",borderRadius:7,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
            Test Supabase Write
          </button>
        </Card>
        </>
      )}

      {acctTab==="danger" && (
        <Card style={{border:`1px solid ${T.red}40`}}>
          <div style={{fontSize:13,fontWeight:700,color:T.red,marginBottom:4}}>⚠️ Danger Zone</div>
          <div style={{fontSize:11,color:T.muted,marginBottom:20,lineHeight:1.6}}>These actions are permanent. Please export your data before proceeding.</div>

          <div style={{background:T.bg2,borderRadius:10,padding:"16px",marginBottom:12}}>
            <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:4}}>Export All Data</div>
            <div style={{fontSize:11,color:T.muted,marginBottom:10}}>Download a full JSON backup of all your projects, estimates, and settings before deleting.</div>
            <Btn ghost small onClick={()=>{
              const data = DB.exportAll(session?.companyId);
              const blob = new Blob([JSON.stringify(data,null,2)],{type:"application/json"});
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a"); a.href=url; a.download="grokfieldops_backup_"+new Date().toISOString().slice(0,10)+".json"; a.click();
            }}>⬇ Download Data Backup</Btn>
          </div>

          <div style={{background:T.redBg||"rgba(239,68,68,0.08)",border:`1px solid ${T.red}30`,borderRadius:10,padding:"16px"}}>
            <div style={{fontSize:12,fontWeight:700,color:T.red,marginBottom:4}}>Delete Account Permanently</div>
            <div style={{fontSize:11,color:T.muted,marginBottom:14,lineHeight:1.6}}>
              This will permanently delete all your company data, projects, estimates, and documents. This cannot be undone. Your Stripe subscription must be cancelled separately.
            </div>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>
              Type "delete my account" to confirm
            </div>
            <input value={deleteConfirm} onChange={e=>setDeleteConfirm(e.target.value)}
              placeholder='delete my account'
              style={{width:"100%",background:T.bg2,border:`1px solid ${T.red}50`,borderRadius:7,padding:"9px 12px",color:T.red,fontSize:13,fontFamily:"inherit",marginBottom:10}}/>
            <button
              disabled={deleteConfirm.toLowerCase()!=="delete my account"}
              onClick={handleDeleteAccount}
              style={{background:deleteConfirm.toLowerCase()==="delete my account"?T.red:T.bg3,color:"white",border:"none",borderRadius:8,padding:"10px 20px",fontSize:12,fontWeight:700,cursor:deleteConfirm.toLowerCase()==="delete my account"?"pointer":"not-allowed",opacity:deleteConfirm.toLowerCase()==="delete my account"?1:0.5,transition:"all 0.2s"}}>
              Permanently Delete My Account
            </button>
          </div>
        </Card>
      )}
    </div>
  );
}


// ── Standalone Account Settings Page (accessible to ALL users) ────────────────
// ── PLAN PAGE ─────────────────────────────────────────────────────────────────
function PlanPage({session, setView}) {
  const T = useTheme();
  const planId = session?.plan || "starter";
  const plans = [
    {id:"starter", name:"Starter",  price:"$149", annual:"$1,490", color:T.blue,
     features:["Up to 3 active projects","25 AI estimates/mo","150 AI chats/mo","10 blueprint reviews/mo","15 meetings/mo","10 subs","CSLB compliance checker","Document kit auto-fill","Email support"]},
    {id:"pro",     name:"Pro",      price:"$249", annual:"$2,490", color:T.purple,
     features:["Up to 10 active projects","50 AI estimates/mo","350 AI chats/mo","25 blueprint reviews/mo","30 meetings/mo","20 subs","Unit tracking & draw schedule","Sub portal + clock in/out","Submittal pins + AI product search","Owner/investor reports","Priority email support"]},
    {id:"gcpro",   name:"GC Pro",   price:"$399", annual:"$3,990", color:T.gold,
     features:["Unlimited projects","Unlimited AI estimates","Unlimited AI chats","Everything in Pro","Unlimited team members","Custom AI knowledge base","Phone + email support","Dedicated onboarding"]},
  ];

  const current = plans.find(p=>p.id===planId) || plans[0];
  const trialEnds = session?.trialEnds;
  const isOnTrial = trialEnds && new Date(trialEnds) > new Date();
  const daysLeft = isOnTrial ? Math.ceil((new Date(trialEnds)-new Date())/864e5) : 0;

  // Plan limits
  const limits = {
    starter: {projects:3, estimates:15, chats:100},
    pro:     {projects:10, estimates:40, chats:300},
    gcpro:   {projects:9999, estimates:9999, chats:9999},
    owner:   {projects:9999, estimates:9999, chats:9999},
  };
  const limit = limits[planId] || limits.starter;

  return (
    <div style={{padding:24,maxWidth:700}}>
      <div style={{fontSize:20,fontWeight:800,color:T.text,marginBottom:4}}>My Plan</div>
      <div style={{fontSize:12,color:T.muted,marginBottom:20}}>Manage your subscription and usage</div>

      {/* Current plan card */}
      <div style={{background:`linear-gradient(135deg,${current.color}20,${current.color}08)`,border:`2px solid ${current.color}40`,borderRadius:12,padding:"20px 24px",marginBottom:20}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:current.color,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Current Plan</div>
            <div style={{fontSize:24,fontWeight:800,color:T.text}}>{current.name}</div>
            <div style={{fontSize:14,color:T.muted,marginTop:2}}>{current.price}/mo · {current.annual}/yr</div>
          </div>
          <div style={{textAlign:"right"}}>
            {isOnTrial ? (
              <div style={{background:T.amberBg,border:`1px solid ${T.amber}30`,borderRadius:8,padding:"8px 14px"}}>
                <div style={{fontSize:11,fontWeight:700,color:T.amber}}>🎯 Free Trial</div>
                <div style={{fontSize:20,fontWeight:800,color:T.text}}>{daysLeft}</div>
                <div style={{fontSize:10,color:T.muted}}>days left</div>
              </div>
            ) : (
              <div style={{background:T.green+"15",border:`1px solid ${T.green}30`,borderRadius:8,padding:"8px 14px"}}>
                <div style={{fontSize:11,fontWeight:700,color:T.green}}>✅ Active</div>
              </div>
            )}
          </div>
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
          {current.features.map(f=>(
            <span key={f} style={{fontSize:11,background:current.color+"12",color:current.color,borderRadius:20,padding:"3px 10px",fontWeight:600}}>✓ {f}</span>
          ))}
        </div>
      </div>

      {/* Usage */}
      <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:12,padding:"16px 20px",marginBottom:20}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Usage This Month</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
          {[
            {label:"Projects",used:0,max:limit.projects},
            {label:"AI Estimates",used:0,max:limit.estimates},
            {label:"AI Chats",used:0,max:limit.chats},
          ].map(({label,used,max})=>(
            <div key={label} style={{background:T.bg2,borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:T.muted,marginBottom:4}}>{label}</div>
              <div style={{fontSize:20,fontWeight:800,color:T.text}}>{used}</div>
              <div style={{fontSize:10,color:T.muted}}>of {max===9999?"∞":max}</div>
              {max!==9999&&(
                <div style={{height:3,background:T.bg3,borderRadius:2,marginTop:6}}>
                  <div style={{height:"100%",width:`${Math.min(100,(used/max)*100)}%`,background:used/max>0.8?T.red:T.gold,borderRadius:2}}/>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Upgrade options */}
      {planId!=="gcpro"&&planId!=="owner"&&(
        <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:12,padding:"16px 20px",marginBottom:20}}>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Upgrade Your Plan</div>
          <div style={{fontSize:11,color:T.muted,marginBottom:12}}>Get more projects, AI usage, and features</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {plans.filter(p=>p.id!==planId&&p.id!=="starter"||(planId==="starter")).map(p=>(
              <div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:T.bg2,borderRadius:9,padding:"12px 16px",border:`1px solid ${p.color}30`}}>
                <div>
                  <span style={{fontSize:13,fontWeight:700,color:p.color}}>{p.name}</span>
                  <span style={{fontSize:12,color:T.muted,marginLeft:8}}>{p.price}/mo · {p.annual}/yr</span>
                </div>
                <button onClick={()=>alert("Contact support to upgrade your plan.")}
                  style={{fontSize:11,fontWeight:700,background:p.color,color:"white",border:"none",borderRadius:7,padding:"6px 14px",cursor:"pointer"}}>
                  Upgrade →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>setView("account-settings")}
          style={{fontSize:12,fontWeight:600,background:T.bg2,color:T.muted,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"9px 18px",cursor:"pointer"}}>
          ⚙ Account Settings
        </button>
        <button onClick={()=>alert("To cancel, contact support at support@grokfieldops.com")}
          style={{fontSize:12,fontWeight:600,background:"none",color:T.red,border:`1px solid ${T.red}30`,borderRadius:8,padding:"9px 18px",cursor:"pointer"}}>
          Cancel Plan
        </button>
      </div>
    </div>
  );
}

// ── ONBOARDING MODAL ──────────────────────────────────────────────────────────
function OnboardingModal({session, onDone, setView}) {
  const T = useTheme();
  const [step, setStep] = useState(0);
  const steps = [
    {
      icon:"🎉",
      title:"Welcome to Grok Field Ops!",
      desc:`You're all set, ${session?.ownerName||"there"}. This is your construction management platform — built for GCs in the field.`,
      content:(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:12}}>
          {[["📋","Manage Projects","Track progress, phases, and budgets"],
            ["💰","Change Orders","Create and track COs with AI"],
            ["📐","Blueprint Review","Upload drawings and flag issues"],
            ["🎥","Video Estimates","Film a walkthrough, get a scope"],
            ["📄","Document Kit","Store and generate your contracts"],
            ["👷","Sub Portal","Give subs access to their jobs"],
          ].map(([icon,title,desc])=>(
            <div key={title} style={{background:T.bg2,borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:18,marginBottom:4}}>{icon}</div>
              <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:2}}>{title}</div>
              <div style={{fontSize:10,color:T.muted,lineHeight:1.4}}>{desc}</div>
            </div>
          ))}
        </div>
      )
    },
    {
      icon:"🏢",
      title:"Set Up Your Company",
      desc:"Add your company info so it auto-fills on all your contracts, proposals, and lien waivers.",
      content:(
        <div style={{marginTop:12}}>
          <div style={{background:T.goldBg,border:`1px solid ${T.gold}30`,borderRadius:8,padding:"10px 14px",fontSize:11,color:T.muted,marginBottom:12,lineHeight:1.6}}>
            ✏️ Go to <strong style={{color:T.gold}}>Account Settings</strong> to enter your company name, CSLB license, address, phone, and email. This info will automatically appear on all generated documents.
          </div>
          {[["Company Name","Grok Construction Inc"],
            ["CSLB License #","1234567"],
            ["Address","123 Main St, San Francisco CA"],
            ["Phone","(415) 555-0100"],
          ].map(([label,placeholder])=>(
            <div key={label} style={{marginBottom:8}}>
              <div style={{fontSize:10,color:T.muted,fontWeight:700,marginBottom:3}}>{label}</div>
              <div style={{background:T.bg2,borderRadius:6,padding:"8px 10px",fontSize:12,color:T.subtle,fontStyle:"italic"}}>{placeholder}</div>
            </div>
          ))}
        </div>
      )
    },
    {
      icon:"🚀",
      title:"Let's Get You Started",
      desc:"You're all set. Try the demo to explore all features, or jump straight in with your first real project.",
      content:(
        <div style={{marginTop:12,display:"flex",flexDirection:"column",gap:8}}>
          <div style={{background:T.goldBg,border:`1px solid ${T.gold}30`,borderRadius:9,padding:"12px 14px"}}>
            <div style={{fontSize:12,fontWeight:700,color:T.gold,marginBottom:4}}>🧪 Try Demo First (Recommended)</div>
            <div style={{fontSize:11,color:T.muted,lineHeight:1.5}}>Load 2 sample projects with real COs, RFIs, blueprints, and financials — see every feature in action before adding your real jobs.</div>
          </div>
          {[["📋","Add your first real project","Projects → + Add Project"],
            ["⚙","Set up your company info","Account Settings → name, CSLB, address"],
            ["📄","Upload your templates","Document Kit → your contracts and forms"],
          ].map(([icon,title,desc])=>(
            <div key={title} style={{display:"flex",gap:10,alignItems:"center",background:T.bg2,borderRadius:8,padding:"9px 12px"}}>
              <div style={{fontSize:16,flexShrink:0}}>{icon}</div>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:T.text}}>{title}</div>
                <div style={{fontSize:10,color:T.muted}}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      )
    }
  ];

  const current = steps[step];

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:T.bg1,borderRadius:16,padding:28,width:"100%",maxWidth:480,boxShadow:"0 24px 60px rgba(0,0,0,0.4)"}}>
        {/* Progress dots */}
        <div style={{display:"flex",gap:6,justifyContent:"center",marginBottom:20}}>
          {steps.map((_,i)=>(
            <div key={i} style={{width:i===step?24:8,height:8,borderRadius:4,background:i===step?T.gold:T.subtle,transition:"all 0.3s"}}/>
          ))}
        </div>

        <div style={{textAlign:"center",marginBottom:4}}>
          <div style={{fontSize:48,marginBottom:12}}>{current.icon}</div>
          <div style={{fontSize:20,fontWeight:800,color:T.text,marginBottom:8}}>{current.title}</div>
          <div style={{fontSize:13,color:T.muted,lineHeight:1.6}}>{current.desc}</div>
        </div>

        {current.content}

        <div style={{display:"flex",gap:8,marginTop:20}}>
          {step>0&&(
            <button onClick={()=>setStep(s=>s-1)}
              style={{flex:1,padding:"11px",borderRadius:10,border:`1px solid ${T.subtle}`,background:"none",color:T.muted,fontSize:13,fontWeight:600,cursor:"pointer"}}>
              ← Back
            </button>
          )}
          {step<steps.length-1?(
            <button onClick={()=>setStep(s=>s+1)}
              style={{flex:2,padding:"11px",borderRadius:10,border:"none",background:T.gold,color:T.bg0,fontSize:13,fontWeight:700,cursor:"pointer"}}>
              Next →
            </button>
          ):(
            <div style={{flex:2,display:"flex",flexDirection:"column",gap:7}}>
              <button onClick={()=>{
                // Load demo projects then go to projects tab
                const cid = session?.companyId || window._grokCompanyId;
                if (cid) {
                  const demo = [
                    ...[
                    {id:"demo_1",name:"466 Turk St — SRO Renovation",addr:"Tenderloin, SF",client:"NCR Construction",pct:68,value:420000,type:"SRO",status:"active",phases:[100,100,100,55,0,0,0],openCOs:2,openRFIs:3,desc:"Full gut renovation of 12 SRO units.",walk:{access:{s1:true,s2:true,s3:true,s4:true,s5:true},plumbing:{p1:true,p2:false,p3:true,p4:true,p5:true},walls:{w1:true,w2:false,w3:true,w4:false,w5:true},owner:{q1:true,q2:true,q3:true,q4:true,q5:false}},redFlags:{"Drain location conflict":true},walkComplete:false,notes:"Demo project."},
                    {id:"demo_2",name:"1200 Mission — Affordable Housing TI",addr:"SoMa, SF",client:"Direct Owner",pct:32,value:285000,type:"Affordable Housing",status:"active",phases:[100,100,40,0,0,0,0],openCOs:1,openRFIs:1,desc:"Tenant improvement of 6 affordable units.",walk:{access:{s1:true,s2:true,s3:true,s4:true,s5:true},plumbing:{p1:true,p2:true,p3:true,p4:true,p5:true},walls:{w1:true,w2:true,w3:true,w4:true,w5:true},owner:{q1:true,q2:true,q3:true,q4:true,q5:true}},redFlags:{},walkComplete:true,notes:"Demo project."},
                    ].filter(p=>{const dk="grok_deleted_demos_"+(window._grokCompanyId||"");const del=(()=>{try{return JSON.parse(localStorage.getItem(dk)||"[]");}catch{return [];}})();return !del.includes(p.id);}),
                  ];
                  localStorage.setItem("grok_projects_"+cid, JSON.stringify(demo));
                }
                setView("projects"); onDone();
              }}
                style={{width:"100%",padding:"11px",borderRadius:10,border:"none",background:T.gold,color:T.bg0,fontSize:13,fontWeight:700,cursor:"pointer"}}>
                🧪 Load Demo & Explore
              </button>
              <button onClick={()=>{setView("projects");onDone();}}
                style={{width:"100%",padding:"9px",borderRadius:10,border:"none",background:T.bg2,color:T.text,fontSize:12,fontWeight:600,cursor:"pointer"}}>
                🏗 Add My First Real Project
              </button>
              <button onClick={onDone}
                style={{width:"100%",padding:"9px",borderRadius:10,border:`1px solid ${T.subtle}`,background:"none",color:T.muted,fontSize:11,cursor:"pointer"}}>
                I'll explore on my own
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AccountSettingsPage({session, onLogout}) {
  return (
    <div style={{padding:24, maxWidth:900}}>
      <div style={{fontSize:20,fontWeight:800,color:T.text,marginBottom:4}}>Account Settings</div>
      <div style={{fontSize:12,color:T.muted,marginBottom:24}}>Manage your profile, subscription, and account preferences</div>
      <AccountSubscriptionTab session={session} onLogout={onLogout}/>
    </div>
  );
}

// ─── TRADE BLUEPRINT VIEW ─────────────────────────────────────────────────────
const TRADE_DEFS = [
  {id:"architectural", label:"Architectural",  icon:"📐", color:"#3b82f6",
   aiFocus:"ADA compliance, door widths (32\" min clear), turning radii (60\"), egress, room dimensions, accessible routes, CBC 11A/11B"},
  {id:"electrical",    label:"Electrical",     icon:"⚡", color:"#f59e0b",
   aiFocus:"Panel locations, circuit loads, GFCI/AFCI requirements, outlet heights (15\"–48\" AFF), conduit routing, service entrance, NEC violations, CEC compliance"},
  {id:"plumbing",      label:"Plumbing",       icon:"🔧", color:"#06b6d4",
   aiFocus:"Drain slope (1/4\" per foot), vent requirements, fixture clearances (21\" min front of toilet), water heater clearances, CPC violations, accessible fixture heights"},
  {id:"mechanical",    label:"Mechanical/HVAC",icon:"💨", color:"#8b5cf6",
   aiFocus:"Duct sizing, clearances, fresh air requirements (15 CFM/person), equipment pad clearances, filter access, Title 24 energy compliance, CMC violations"},
  {id:"framing",       label:"Framing/Structural",icon:"🏗", color:"#ef4444",
   aiFocus:"Shear wall locations, hold-down hardware, beam sizing, header spans, bearing points, seismic straps, anchor bolt spacing, CBC structural violations"},
  {id:"fire",          label:"Fire/Life Safety",icon:"🔥", color:"#f97316",
   aiFocus:"Sprinkler head coverage (15' max spacing), egress lighting, exit signs, smoke detector placement (within 21\" of ceiling), fire rating assemblies, NFPA violations"},
  {id:"ada",           label:"ADA / Accessibility",icon:"♿", color:"#10b981",
   aiFocus:"All ADA 2010 Standards — door hardware, ramp slopes (1:12 max), grab bar placement (33\"–36\" AFF), turning radius (60\"), reach ranges (15\"–48\" AFF), path of travel"},
];

const TRADE_AI_SYSTEM = (trade) => `You are an expert ${trade.label} code reviewer and field inspector.
Analyze this drawing specifically for ${trade.label} issues.
Focus on: ${trade.aiFocus}

For EACH issue found, respond in EXACTLY this format (one per line group):
VIOLATION: [concise title — max 8 words]
LOCATION: [specific location on drawing — e.g. "Unit 4B bathroom", "Panel room north wall", "Grid line C-4"]
MEASURED: [actual dimension or condition visible on drawing, or "Not shown"]
REQUIRED: [exact code requirement with number]
CODE: [code section — e.g. ADA §404.2.3, NEC §210.8, CPC §408.5]
PRIORITY: [critical / high / medium]
---
List ALL violations you find. Be specific about locations and measurements.`;

function TradeBlueprintView({proj, session}) {
  const projId = proj?.id || "x";
  const storeKey = (tradeId) => `grok_trade_bp_${projId}_${tradeId}`;
  const flagsKey  = (tradeId) => `grok_trade_flags_${projId}_${tradeId}`;
  const tabsKey   = `grok_trade_tabs_${projId}`;

  // Which trade tabs are active for this project
  const [activeTabs, setActiveTabs] = React.useState(()=>{
    try { return JSON.parse(localStorage.getItem(tabsKey)||"[]"); } catch { return []; }
  });
  const [selectedTrade, setSelectedTrade] = React.useState(activeTabs[0]||null);
  const [showAddTrade, setShowAddTrade] = React.useState(false);

  // Per-trade state
  const [tradeDrawing, setTradeDrawing] = React.useState({}); // tradeId -> {url, name}
  const [tradeFlags, setTradeFlags]     = React.useState({}); // tradeId -> [flags]
  const [reviewing, setReviewing]       = React.useState({}); // tradeId -> bool
  const [reviewResult, setReviewResult] = React.useState({}); // tradeId -> text
  const [placingFlag, setPlacingFlag]   = React.useState(null);
  const [markingRoute, setMarkingRoute] = React.useState(null); // tradeId being route-marked
  const [routeType, setRouteType]       = React.useState("conduit"); // type of run being marked
  const [newFlagPos, setNewFlagPos]     = React.useState(null);
  const [newFlagForm, setNewFlagForm]   = React.useState({title:"",desc:"",priority:"medium",photos:[]});
  const [selectedFlag, setSelectedFlag] = React.useState(null);
  const [comment, setComment]           = React.useState("");
  const [analyzing, setAnalyzing]       = React.useState(false); // GC full-set analysis
  const fileRef   = React.useRef();
  const photoRef  = React.useRef();

  const isGC = session?.plan !== "sub";
  const subTrade = session?.trade; // for subs — which trade they belong to

  // Load drawings and flags from storage on mount
  React.useEffect(()=>{
    const drawings = {}; const flags = {};
    TRADE_DEFS.forEach(t=>{
      try { const d=localStorage.getItem(storeKey(t.id)); if(d) drawings[t.id]=JSON.parse(d); } catch{}
      try { const f=localStorage.getItem(flagsKey(t.id)); if(f) flags[t.id]=JSON.parse(f); } catch{}
    });
    setTradeDrawing(drawings);
    setTradeFlags(flags);
    // Load saved conflict results
    try {
      const saved = localStorage.getItem(`grok_conflicts_${projId}`);
      if (saved) setConflictResults(JSON.parse(saved));
    } catch{}
  },[projId]);

  const saveTabs = (tabs) => {
    setActiveTabs(tabs);
    localStorage.setItem(tabsKey, JSON.stringify(tabs));
  };

  const saveDrawing = (tradeId, drawing) => {
    setTradeDrawing(p=>({...p,[tradeId]:drawing}));
    localStorage.setItem(storeKey(tradeId), JSON.stringify(drawing));
  };

  const saveFlags = (tradeId, flags) => {
    setTradeFlags(p=>({...p,[tradeId]:flags}));
    localStorage.setItem(flagsKey(tradeId), JSON.stringify(flags));
    SyncDB.saveFlags(projId+"_"+tradeId, flags).catch(()=>{});
  };

  const addTab = (tradeId) => {
    if (activeTabs.includes(tradeId)) return;
    const updated = [...activeTabs, tradeId];
    saveTabs(updated);
    setSelectedTrade(tradeId);
    setShowAddTrade(false);
  };

  const removeTab = (tradeId) => {
    if (!true) return;
    const updated = activeTabs.filter(t=>t!==tradeId);
    saveTabs(updated);
    localStorage.removeItem(storeKey(tradeId));
    localStorage.removeItem(flagsKey(tradeId));
    setTradeDrawing(p=>{const n={...p};delete n[tradeId];return n;});
    setTradeFlags(p=>{const n={...p};delete n[tradeId];return n;});
    if (selectedTrade===tradeId) setSelectedTrade(updated[0]||null);
  };

  // Upload drawing for a trade
  const uploadDrawing = (tradeId, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => saveDrawing(tradeId, {url:e.target.result, name:file.name, uploadedAt:new Date().toLocaleDateString()});
    reader.readAsDataURL(file);
  };

  // GC uploads full set → AI splits into trade tabs automatically
  const analyzeFullSet = async (file) => {
    if (!file) return;
    setAnalyzing(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const imageData = e.target.result.split(",")[1];
      try {
        const resp = await fetch("https://qnjlirkgumhzzcavqsxt.supabase.co/functions/v1/ai-proxy", {
          method:"POST",
          headers:{"Content-Type":"application/json","Authorization":"Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFuamxpcmtndW1oenpjYXZxc3h0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc3NTQxNjgsImV4cCI6MjA5MzMzMDE2OH0.fmwdMo6-ae5Jyx8b3n44FlOQUSSUX9drdt2UYJd7Mws","apikey":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFuamxpcmtndW1oenpjYXZxc3h0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc3NTQxNjgsImV4cCI6MjA5MzMzMDE2OH0.fmwdMo6-ae5Jyx8b3n44FlOQUSSUX9drdt2UYJd7Mws"},
          body:JSON.stringify({
            model:"claude-opus-4-5",
            max_tokens:500,
            messages:[{role:"user",content:[
              {type:"image",source:{type:"base64",media_type:"image/jpeg",data:imageData}},
              {type:"text",text:"Look at this construction drawing. List ONLY which of these trades are shown (respond with comma-separated IDs only, nothing else): architectural, electrical, plumbing, mechanical, framing, fire, ada"}
            ]}]
          })
        });
        const data = await resp.json();
        const detected = (data.content?.[0]?.text||"architectural").toLowerCase()
          .split(/[,\s]+/).map(s=>s.trim()).filter(id=>TRADE_DEFS.find(t=>t.id===id));

        // Create tabs for detected trades + save drawing to each
        const url = e.target.result;
        const name = file.name;
        const newTabs = [...new Set([...activeTabs, ...detected])];
        saveTabs(newTabs);
        detected.forEach(tradeId => saveDrawing(tradeId, {url, name, uploadedAt:new Date().toLocaleDateString(), fromFullSet:true}));
        setSelectedTrade(detected[0]||newTabs[0]);
      } catch(err) {
        // Fallback — just add architectural tab with the drawing
        const url = e.target.result;
        saveDrawing("architectural", {url, name:file.name, uploadedAt:new Date().toLocaleDateString()});
        if (!activeTabs.includes("architectural")) saveTabs([...activeTabs,"architectural"]);
        setSelectedTrade("architectural");
      }
      setAnalyzing(false);
    };
    reader.readAsDataURL(file);
  };

  // AI review for specific trade
  const runTradeReview = async (tradeId) => {
    const drawing = tradeDrawing[tradeId];
    if (!drawing?.url) return;
    const trade = TRADE_DEFS.find(t=>t.id===tradeId);
    setReviewing(p=>({...p,[tradeId]:true}));
    setReviewResult(p=>({...p,[tradeId]:""}));
    try {
      const imageData = drawing.url.split(",")[1];
      const resp = await fetch("https://qnjlirkgumhzzcavqsxt.supabase.co/functions/v1/ai-proxy", {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFuamxpcmtndW1oenpjYXZxc3h0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc3NTQxNjgsImV4cCI6MjA5MzMzMDE2OH0.fmwdMo6-ae5Jyx8b3n44FlOQUSSUX9drdt2UYJd7Mws","apikey":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFuamxpcmtndW1oenpjYXZxc3h0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc3NTQxNjgsImV4cCI6MjA5MzMzMDE2OH0.fmwdMo6-ae5Jyx8b3n44FlOQUSSUX9drdt2UYJd7Mws"},
        body:JSON.stringify({
          model:"claude-opus-4-5",
          max_tokens:2000,
          system: TRADE_AI_SYSTEM(trade),
          messages:[{role:"user",content:[
            {type:"image",source:{type:"base64",media_type:"image/jpeg",data:imageData}},
            {type:"text",text:`Review this ${trade.label} drawing for all code violations, measurement issues, and compliance problems. Be specific about locations and dimensions.`}
          ]}]
        })
      });
      const data = await resp.json();
      const text = data.content?.[0]?.text || "No issues detected.";
      setReviewResult(p=>({...p,[tradeId]:text}));

      // Auto-create flags from violations
      const autoFlags = parseTradeViolations(text, tradeId, trade);
      if (autoFlags.length > 0) {
        const existing = tradeFlags[tradeId]||[];
        saveFlags(tradeId, [...autoFlags, ...existing.filter(f=>!f.autoGenerated)]);
      }
    } catch(e) {
      setReviewResult(p=>({...p,[tradeId]:"Error: "+e.message}));
    }
    setReviewing(p=>({...p,[tradeId]:false}));
  };

  // Parse AI review text into flag objects
  const parseTradeViolations = (text, tradeId, trade) => {
    const blocks = text.split("---").filter(b=>b.trim());
    return blocks.map((block, i) => {
      const get = (key) => { const m=block.match(new RegExp(key+":\\s*(.+)","i")); return m?m[1].trim():""; };
      const title = get("VIOLATION");
      if (!title) return null;
      const cols = 3;
      return {
        id: `ai_${tradeId}_${Date.now()}_${i}`,
        tradeId,
        title,
        location: get("LOCATION"),
        measured: get("MEASURED"),
        required: get("REQUIRED"),
        codeRef: get("CODE"),
        priority: get("PRIORITY")||"medium",
        desc: block.trim().slice(0,300),
        status: "open",
        autoGenerated: true,
        date: new Date().toLocaleDateString(),
        flaggedBy: "AI Review",
        pos: {
          x: 10 + (i % cols) * 28 + Math.random()*8,
          y: 10 + Math.floor(i/cols) * 22 + Math.random()*6
        },
        photos: [],
        comments: [],
      };
    }).filter(Boolean);
  };

  // Place manual flag
  const [conflictChecking, setConflictChecking] = React.useState(false);
  const [conflictResults, setConflictResults]   = React.useState(null);
  const [showConflicts, setShowConflicts]        = React.useState(false);

  // GC runs cross-trade conflict check
  const runConflictCheck = async () => {
    const tradesWithDrawings = activeTabs.filter(tid => tradeDrawing[tid]?.url);
    if (tradesWithDrawings.length < 2) {
      alert("Need at least 2 trade drawings uploaded to check for conflicts.");
      return;
    }
    setConflictChecking(true);
    setConflictResults(null);
    setShowConflicts(true);

    try {
      // Build message with all trade drawings
      const content = [];
      tradesWithDrawings.forEach(tid => {
        const trade = TRADE_DEFS.find(t=>t.id===tid);
        const drawing = tradeDrawing[tid];
        const imageData = drawing.url.split(",")[1];
        const flags = (tradeFlags[tid]||[]).filter(f=>f.status!=="resolved");
        const routePins = flags.filter(f=>f.isRoutePin);
        const issuePins = flags.filter(f=>!f.isRoutePin);
        const routeSummary = routePins.length>0
          ? routePins.map(f=>`${f.routeType||"route"} at ${f.pos?.x?.toFixed(0)}%,${f.pos?.y?.toFixed(0)}%`).join("; ")
          : "No route marked yet";
        content.push({type:"text", text:`=== ${trade.label.toUpperCase()} DRAWING (${drawing.name}) ===\nPlanned route markers: ${routeSummary}\nOpen issues: ${issuePins.map(f=>`${f.title} at ${f.location||f.pos?.x?.toFixed(0)+"%,"+f.pos?.y?.toFixed(0)+"%"}`).join("; ")||"None"}`});
        content.push({type:"image", source:{type:"base64", media_type:"image/jpeg", data:imageData}});
      });
      content.push({type:"text", text:`You are a senior construction coordinator reviewing ${tradesWithDrawings.length} trade drawings for coordination conflicts.

Each drawing shows the GC's blueprint. Teal circle markers (🗺) show where each trade has marked their planned route — conduit runs, pipe runs, drain lines, duct runs, etc.

Analyze all provided drawings and their marked route pins. Identify where trade routes will physically conflict or create problems in the field.

For EACH conflict found, respond in this EXACT format:
CONFLICT: [short title]
TRADES: [e.g. "Plumbing vs Electrical"]
LOCATION: [specific location — room, wall, ceiling zone, grid coordinates if visible]
DESCRIPTION: [what exactly will conflict and why it's a problem in the field]
SEVERITY: [critical / high / medium]
RECOMMENDATION: [specific fix — who moves, what changes, how to resolve]
---

Be specific about locations using the route markers as reference. A conflict is critical if it requires stopping work or major rework. High if it needs coordination before rough-in. Medium if resolvable with minor adjustment. If no conflicts found between marked routes, say "NO CONFLICTS DETECTED" followed by a coordination summary.`});

      const resp = await fetch("https://qnjlirkgumhzzcavqsxt.supabase.co/functions/v1/ai-proxy", {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFuamxpcmtndW1oenpjYXZxc3h0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc3NTQxNjgsImV4cCI6MjA5MzMzMDE2OH0.fmwdMo6-ae5Jyx8b3n44FlOQUSSUX9drdt2UYJd7Mws","apikey":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFuamxpcmtndW1oenpjYXZxc3h0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc3NTQxNjgsImV4cCI6MjA5MzMzMDE2OH0.fmwdMo6-ae5Jyx8b3n44FlOQUSSUX9drdt2UYJd7Mws"},
        body:JSON.stringify({model:"claude-opus-4-5", max_tokens:3000, messages:[{role:"user",content}]})
      });
      const data = await resp.json();
      const text = data.content?.[0]?.text || "No response from AI.";

      // Parse conflicts
      const noConflicts = text.toUpperCase().includes("NO CONFLICTS DETECTED");
      const blocks = text.split("---").filter(b=>b.trim()&&b.includes("CONFLICT:"));
      const conflicts = blocks.map((block,i)=>{
        const get = (key) => { const m=block.match(new RegExp(key+":\\s*(.+)","i")); return m?m[1].trim():""; };
        return {
          id:"conflict_"+i,
          title: get("CONFLICT"),
          trades: get("TRADES"),
          location: get("LOCATION"),
          description: get("DESCRIPTION"),
          severity: get("SEVERITY")||"medium",
          recommendation: get("RECOMMENDATION"),
          status: "open",
        };
      }).filter(c=>c.title);

      setConflictResults({conflicts, noConflicts, rawText:text, checkedAt:new Date().toLocaleString(), tradesChecked:tradesWithDrawings.length});

      // Save to localStorage
      localStorage.setItem(`grok_conflicts_${projId}`, JSON.stringify({conflicts,noConflicts,checkedAt:new Date().toLocaleString(),tradesChecked:tradesWithDrawings.length}));

    } catch(e) {
      setConflictResults({error:e.message, conflicts:[], noConflicts:false});
    }
    setConflictChecking(false);
  };

  const handleCanvasClick = (e, tradeId) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const pos = {x:Math.max(2,Math.min(98,x)), y:Math.max(2,Math.min(98,y))};

    // Route marking mode — drop a route pin
    if (markingRoute === tradeId) {
      const ROUTE_LABELS = {
        conduit:"Electrical conduit run", pipe:"Plumbing pipe run",
        drain:"Drain line", duct:"HVAC duct run", vent:"Vent/exhaust run",
        beam:"Structural beam", sprinkler:"Fire sprinkler line", other:"Route point",
      };
      const routePin = {
        id: `route_${tradeId}_${Date.now()}`,
        tradeId,
        title: ROUTE_LABELS[routeType] || "Route point",
        desc: `${ROUTE_LABELS[routeType]||"Planned route"} — ${currentTrade?.label}`,
        priority: "medium",
        status: "open",
        isRoutePin: true,
        routeType,
        pos,
        date: new Date().toLocaleDateString(),
        flaggedBy: session?.subName||session?.ownerName||"Sub",
        comments: [],
        photos: [],
        location: `${pos.x.toFixed(0)}%, ${pos.y.toFixed(0)}%`,
      };
      const existing = tradeFlags[tradeId]||[];
      saveFlags(tradeId, [routePin, ...existing]);
      return; // stay in route marking mode so they can drop multiple points
    }

    // Normal flag placing mode
    if (placingFlag === tradeId) {
      setNewFlagPos(pos);
      setPlacingFlag(null);
    }
  };

  const saveManualFlag = (tradeId) => {
    if (!newFlagForm.title.trim() || !newFlagPos) return;
    const tradeDef = TRADE_DEFS.find(t => t.id === tradeId);
    const flag = {
      id: `manual_${tradeId}_${Date.now()}`,
      tradeId,
      trade: tradeDef?.label || tradeId,
      ...newFlagForm,
      location: `${newFlagPos.x.toFixed(0)}%, ${newFlagPos.y.toFixed(0)}%`,
      status:"open",
      date:new Date().toLocaleDateString(),
      flaggedBy: session?.ownerName||session?.subName||"Field",
      pos: newFlagPos,
      autoGenerated: false,
      comments:[],
    };
    const existing = tradeFlags[tradeId]||[];
    saveFlags(tradeId, [flag,...existing]);
    setSelectedFlag(flag);
    setNewFlagPos(null);
    setNewFlagForm({title:"",desc:"",priority:"medium",photos:[]});

    // ARIA auto-drafts an RFI in "pending_gc_approval" status. Background.
    const drawing = tradeDrawing[tradeId];
    const blueprint = drawing ? {
      id: `trade_${tradeId}_drawing`,
      name: drawing.name || (tradeDef?.label ? tradeDef.label + " Drawing" : "Trade Drawing"),
    } : null;
    draftRFIFromFlag({flag, blueprint, proj, session})
      .then((rfi) => {
        if (!rfi) return;
        const cur = (() => { try { return JSON.parse(localStorage.getItem(flagsKey(tradeId))||"[]"); } catch { return []; }})();
        const next = cur.map(f => f.id === flag.id ? {...f, rfiDraftId: rfi.id} : f);
        if (next.length) saveFlags(tradeId, next);
      })
      .catch(()=>{});
  };

  const addComment = (tradeId, flagId) => {
    if (!comment.trim()) return;
    const flags = (tradeFlags[tradeId]||[]).map(f=>f.id===flagId?{...f,comments:[...f.comments,{id:Date.now(),text:comment,author:session?.ownerName||session?.subName||"Field",date:new Date().toLocaleDateString()}]}:f);
    saveFlags(tradeId, flags);
    setComment("");
    setSelectedFlag(flags.find(f=>f.id===flagId)||null);
  };

  const resolveFlag = (tradeId, flagId) => {
    const flags = (tradeFlags[tradeId]||[]).map(f=>f.id===flagId?{...f,status:"resolved"}:f);
    saveFlags(tradeId, flags);
    setSelectedFlag(flags.find(f=>f.id===flagId)||null);
  };

  const deleteFlag = (tradeId, flagId) => {
    saveFlags(tradeId, (tradeFlags[tradeId]||[]).filter(f=>f.id!==flagId));
    setSelectedFlag(null);
  };

  const addPhotoToFlag = (tradeId, flagId, file) => {
    const reader = new FileReader();
    reader.onload = e => {
      const flags = (tradeFlags[tradeId]||[]).map(f=>f.id===flagId?{...f,photos:[...(f.photos||[]),{id:Date.now(),url:e.target.result,name:file.name}]}:f);
      saveFlags(tradeId, flags);
      setSelectedFlag(flags.find(f=>f.id===flagId)||null);
    };
    reader.readAsDataURL(file);
  };

  const PRIORITY_COLOR = {critical:T.red, high:T.amber, medium:T.blue, low:T.green};

  const currentTrade = TRADE_DEFS.find(t=>t.id===selectedTrade);
  const currentDrawing = selectedTrade ? tradeDrawing[selectedTrade] : null;
  const currentFlags = selectedTrade ? (tradeFlags[selectedTrade]||[]) : [];
  const visibleFlags = currentFlags.filter(f => isGC || f.tradeId === subTrade || f.tradeId === selectedTrade);

  return (
    <div style={{padding:"0 4px"}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
        <div>
          <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:4}}>📐 Trade Drawings</div>
          <div style={{fontSize:12,color:T.muted}}>Each trade has its own drawing workspace. Upload the full drawing set and AI auto-creates trade tabs, or add trades manually.</div>
        </div>
      {isGC && (
          <div style={{display:"flex",gap:8}}>
            <input ref={fileRef} type="file" accept="image/*,.pdf" style={{display:"none"}}
              onChange={e=>analyzeFullSet(e.target.files[0])}/>
            <button onClick={()=>fileRef.current?.click()} disabled={analyzing}
              style={{background:analyzing?T.bg3:`linear-gradient(135deg,${T.gold},${T.teal})`,color:analyzing?T.muted:"white",border:"none",borderRadius:8,padding:"9px 16px",fontSize:12,fontWeight:700,cursor:analyzing?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:7}}>
              {analyzing?<><Spin/>Analyzing drawing set…</>:<><span style={{fontSize:14}}>⬆</span> Upload Full Drawing Set</>}
            </button>
            {activeTabs.filter(tid=>tradeDrawing[tid]?.url).length>=2&&(
              <button onClick={runConflictCheck} disabled={conflictChecking}
                style={{background:conflictChecking?T.bg3:`linear-gradient(135deg,${T.red},${T.amber})`,color:conflictChecking?T.muted:"white",border:"none",borderRadius:8,padding:"9px 16px",fontSize:12,fontWeight:700,cursor:conflictChecking?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:7}}>
                {conflictChecking?<><Spin/>Checking conflicts…</>:<>⚡ Check Trade Conflicts</>}
              </button>
            )}
            {conflictResults&&!conflictChecking&&(
              <button onClick={()=>setShowConflicts(p=>!p)}
                style={{background:conflictResults.noConflicts?T.green:T.red,color:"white",border:"none",borderRadius:8,padding:"9px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                {conflictResults.noConflicts?"✓ No Conflicts":`⚠ ${conflictResults.conflicts?.length||0} Conflicts`}
              </button>
            )}
            <button onClick={()=>setShowAddTrade(p=>!p)}
              style={{background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"9px 14px",fontSize:12,fontWeight:600,color:T.muted,cursor:"pointer"}}>
              + Add Trade
            </button>
          </div>
        )}
      </div>

      {/* Conflict results panel */}
      {showConflicts && conflictResults && (
        <div style={{background:T.bg1,border:`2px solid ${conflictResults.noConflicts?T.green:T.red}40`,borderRadius:12,padding:16,marginBottom:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div>
              <div style={{fontSize:14,fontWeight:800,color:conflictResults.noConflicts?T.green:T.red,marginBottom:2}}>
                {conflictResults.noConflicts ? "✅ No Trade Conflicts Detected" : `⚠️ ${conflictResults.conflicts?.length} Trade Conflict${conflictResults.conflicts?.length!==1?"s":""} Found`}
              </div>
              <div style={{fontSize:10,color:T.muted}}>{conflictResults.tradesChecked} trades checked · {conflictResults.checkedAt}</div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={runConflictCheck} disabled={conflictChecking}
                style={{background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:600,color:T.muted,cursor:"pointer"}}>
                🔄 Re-run Check
              </button>
              <button onClick={()=>setShowConflicts(false)} style={{background:"none",border:"none",color:T.muted,fontSize:18,cursor:"pointer",lineHeight:1}}>×</button>
            </div>
          </div>

          {conflictResults.noConflicts && (
            <div style={{background:T.greenBg,border:`1px solid ${T.green}30`,borderRadius:8,padding:"10px 14px",fontSize:12,color:T.green}}>
              All trade rough-in paths reviewed — no conflicts detected. Trades can proceed with coordination as marked.
            </div>
          )}

          {(conflictResults.conflicts||[]).map((c,i)=>{
            const sevColor = c.severity==="critical"?T.red:c.severity==="high"?T.amber:T.blue;
            const trades = c.trades.split(/vs|and|,/i).map(t=>t.trim()).filter(Boolean);
            return (
              <div key={c.id||i} style={{background:T.bg2,border:`1px solid ${sevColor}30`,borderRadius:10,padding:"12px 14px",marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:800,color:T.text,marginBottom:4}}>{c.title}</div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:6}}>
                      <span style={{fontSize:9,fontWeight:700,background:sevColor+"20",color:sevColor,borderRadius:20,padding:"2px 10px",border:`1px solid ${sevColor}30`,textTransform:"capitalize"}}>{c.severity}</span>
                      {trades.map(t=>(
                        <span key={t} style={{fontSize:9,background:T.bg3,color:T.muted,borderRadius:20,padding:"2px 8px"}}>{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
                {c.location&&(
                  <div style={{fontSize:11,color:T.muted,marginBottom:6,display:"flex",gap:6,alignItems:"flex-start"}}>
                    <span style={{flexShrink:0}}>📍</span>
                    <span><strong style={{color:T.text}}>Location:</strong> {c.location}</span>
                  </div>
                )}
                {c.description&&(
                  <div style={{fontSize:11,color:T.muted,marginBottom:8,lineHeight:1.6,background:T.bg3,borderRadius:7,padding:"8px 10px"}}>
                    {c.description}
                  </div>
                )}
                {c.recommendation&&(
                  <div style={{fontSize:11,color:T.green,display:"flex",gap:7,alignItems:"flex-start",background:T.greenBg,borderRadius:7,padding:"8px 10px",border:`1px solid ${T.green}20`}}>
                    <span style={{flexShrink:0}}>💡</span>
                    <span><strong>Fix:</strong> {c.recommendation}</span>
                  </div>
                )}
              </div>
            );
          })}

          {conflictResults.error&&(
            <div style={{background:T.redBg,borderRadius:8,padding:"10px 14px",fontSize:12,color:T.red}}>
              Error: {conflictResults.error}
            </div>
          )}
        </div>
      )}

      {/* Add Trade dropdown */}
      {showAddTrade && (
        <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:10,padding:"12px 14px",marginBottom:14,display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
          {TRADE_DEFS.filter(t=>!activeTabs.includes(t.id)).map(t=>(
            <button key={t.id} onClick={()=>addTab(t.id)}
              style={{background:T.bg2,border:`2px solid ${t.color}30`,borderRadius:8,padding:"10px 8px",cursor:"pointer",textAlign:"center",transition:"all 0.15s"}}
              onMouseEnter={e=>e.currentTarget.style.borderColor=t.color}
              onMouseLeave={e=>e.currentTarget.style.borderColor=t.color+"30"}>
              <div style={{fontSize:18,marginBottom:4}}>{t.icon}</div>
              <div style={{fontSize:10,fontWeight:700,color:t.color}}>{t.label}</div>
            </button>
          ))}
          {TRADE_DEFS.filter(t=>!activeTabs.includes(t.id)).length===0&&(
            <div style={{gridColumn:"1/-1",fontSize:12,color:T.muted,textAlign:"center",padding:8}}>All trades added</div>
          )}
        </div>
      )}

      {/* No tabs yet */}
      {activeTabs.length===0&&(
        <div style={{background:T.bg2,border:`2px dashed ${T.subtle}`,borderRadius:12,padding:"40px",textAlign:"center"}}>
          <div style={{fontSize:40,marginBottom:12}}>📐</div>
          <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:8}}>No trade drawings yet</div>
          <div style={{fontSize:12,color:T.muted,marginBottom:20,maxWidth:400,margin:"0 auto 20px"}}>
            {isGC ? "Upload the full drawing set and AI will auto-create a tab for each trade — or add trades manually." : "Your GC hasn't uploaded drawings for this project yet."}
          </div>
          {isGC&&<button onClick={()=>fileRef.current?.click()} style={{background:T.gold,color:T.bg0,border:"none",borderRadius:8,padding:"10px 24px",fontSize:13,fontWeight:700,cursor:"pointer"}}>⬆ Upload Drawing Set</button>}
        </div>
      )}

      {/* Trade tabs */}
      {activeTabs.length>0&&(
        <div>
          {/* Tab bar */}
          <div style={{display:"flex",gap:4,marginBottom:0,overflowX:"auto",paddingBottom:0,borderBottom:`2px solid ${T.subtle}`}} className="section-tabs">
            {activeTabs.map(tid=>{
              const trade = TRADE_DEFS.find(t=>t.id===tid);
              if (!trade) return null;
              const flagCount = (tradeFlags[tid]||[]).filter(f=>f.status!=="resolved").length;
              const isActive = selectedTrade===tid;
              return (
                <button key={tid} onClick={()=>setSelectedTrade(tid)}
                  style={{display:"flex",alignItems:"center",gap:6,padding:"10px 16px",borderRadius:"8px 8px 0 0",border:`1px solid ${isActive?trade.color:T.subtle}`,borderBottom:isActive?"none":"1px solid "+T.subtle,background:isActive?T.bg1:T.bg2,cursor:"pointer",whiteSpace:"nowrap",position:"relative",bottom:-2,transition:"all 0.15s",marginBottom:0}}>
                  <span style={{fontSize:14}}>{trade.icon}</span>
                  <span style={{fontSize:11,fontWeight:700,color:isActive?trade.color:T.muted}}>{trade.label}</span>
                  {flagCount>0&&<span style={{fontSize:9,background:T.red,color:"white",borderRadius:20,padding:"1px 6px",fontWeight:700}}>{flagCount}</span>}
                  {isGC&&<span onClick={e=>{e.stopPropagation();removeTab(tid);}} style={{fontSize:11,color:T.muted,marginLeft:4,cursor:"pointer",opacity:0.5}}> ×</span>}
                </button>
              );
            })}
          </div>

          {/* Trade workspace */}
          {currentTrade&&(
            <div style={{border:`1px solid ${currentTrade.color}30`,borderTop:"none",borderRadius:"0 0 10px 10px",background:T.bg1,padding:16}}>
              <div style={{display:"grid",gridTemplateColumns:selectedFlag||newFlagPos?"1fr 380px":"1fr",gap:14,alignItems:"start"}}>

                {/* Drawing canvas */}
                <div>
                  {/* Trade header */}
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:20}}>{currentTrade.icon}</span>
                      <div>
                        <div style={{fontSize:13,fontWeight:800,color:currentTrade.color}}>{currentTrade.label} Drawings</div>
                        <div style={{fontSize:10,color:T.muted}}>{currentFlags.length} flags · {currentFlags.filter(f=>f.status==="resolved").length} resolved</div>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
                      {currentDrawing&&(
                        <>
                          <button onClick={()=>setPlacingFlag(placingFlag===selectedTrade?null:selectedTrade)}
                            style={{background:placingFlag===selectedTrade?currentTrade.color:T.bg2,color:placingFlag===selectedTrade?"white":T.muted,border:`1px solid ${placingFlag===selectedTrade?currentTrade.color:T.subtle}`,borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                            {placingFlag===selectedTrade?"📍 Click drawing to place":"+ Flag Issue"}
                          </button>
                          <button onClick={()=>setMarkingRoute(markingRoute===selectedTrade?null:selectedTrade)}
                            style={{background:markingRoute===selectedTrade?T.teal:T.bg2,color:markingRoute===selectedTrade?"white":T.teal,border:`1px solid ${T.teal}${markingRoute===selectedTrade?"":"60"}`,borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
                            {markingRoute===selectedTrade?"🗺 Click to mark route points":"🗺 Mark My Route"}
                          </button>
                          <button onClick={()=>runTradeReview(selectedTrade)} disabled={reviewing[selectedTrade]}
                            style={{background:reviewing[selectedTrade]?T.bg3:`linear-gradient(135deg,${currentTrade.color},${currentTrade.color}cc)`,color:"white",border:"none",borderRadius:7,padding:"6px 14px",fontSize:11,fontWeight:700,cursor:reviewing[selectedTrade]?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:6}}>
                            {reviewing[selectedTrade]?<><Spin/>Reviewing…</>:"🤖 AI Review"}
                          </button>
                        </>
                      )}
                      <input type="file" accept="image/*,.pdf" style={{display:"none"}} id={`upload_${selectedTrade}`}
                        onChange={e=>uploadDrawing(selectedTrade, e.target.files[0])}/>
                      <button onClick={()=>document.getElementById(`upload_${selectedTrade}`)?.click()}
                        style={{background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:600,color:T.muted,cursor:"pointer"}}>
                        ⬆ {currentDrawing?"Replace Drawing":"Upload Drawing"}
                      </button>
                    </div>
                  </div>

                  {/* Route marking toolbar */}
                  {markingRoute===selectedTrade&&(
                    <div style={{background:T.teal+"15",border:`1px solid ${T.teal}40`,borderRadius:8,padding:"10px 14px",marginBottom:8,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                      <div style={{fontSize:11,fontWeight:700,color:T.teal}}>🗺 Mark your planned route — click anywhere on the drawing</div>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        {[
                          {id:"conduit",label:"Conduit",icon:"⚡"},
                          {id:"pipe",label:"Pipe",icon:"🔧"},
                          {id:"drain",label:"Drain",icon:"💧"},
                          {id:"duct",label:"Duct",icon:"💨"},
                          {id:"vent",label:"Vent",icon:"🌀"},
                          {id:"sprinkler",label:"Sprinkler",icon:"🔥"},
                          {id:"beam",label:"Beam",icon:"🏗"},
                          {id:"other",label:"Other",icon:"📍"},
                        ].map(r=>(
                          <button key={r.id} onClick={()=>setRouteType(r.id)}
                            style={{fontSize:10,fontWeight:600,padding:"3px 9px",borderRadius:20,border:`1px solid ${routeType===r.id?T.teal:T.subtle}`,background:routeType===r.id?T.teal:"transparent",color:routeType===r.id?"white":T.muted,cursor:"pointer"}}>
                            {r.icon} {r.label}
                          </button>
                        ))}
                      </div>
                      <button onClick={()=>{
                        // Clear all route pins for this trade
                        const remaining = (tradeFlags[selectedTrade]||[]).filter(f=>!f.isRoutePin);
                        saveFlags(selectedTrade, remaining);
                      }} style={{fontSize:10,color:T.red,background:"none",border:`1px solid ${T.red}30`,borderRadius:6,padding:"3px 8px",cursor:"pointer",marginLeft:"auto"}}>
                        Clear Route
                      </button>
                      <button onClick={()=>setMarkingRoute(null)}
                        style={{fontSize:10,fontWeight:700,color:"white",background:T.teal,border:"none",borderRadius:6,padding:"4px 10px",cursor:"pointer"}}>
                        Done
                      </button>
                    </div>
                  )}

                  {/* Drawing with pins */}
                  {currentDrawing ? (
                    <div style={{position:"relative",borderRadius:8,overflow:"hidden",border:`1px solid ${currentTrade.color}30`,cursor:markingRoute===selectedTrade?"crosshair":placingFlag===selectedTrade?"crosshair":"default"}}
                      onClick={e=>handleCanvasClick(e,selectedTrade)}>
                      <img src={currentDrawing.url} alt={currentDrawing.name}
                        style={{width:"100%",display:"block",maxHeight:500,objectFit:"contain",background:"#f8f9fa"}}/>

                      {/* Pins — route markers and issue flags */}
                      {visibleFlags.map((flag,idx)=>(
                        <div key={flag.id}
                          onClick={e=>{e.stopPropagation();setSelectedFlag(selectedFlag?.id===flag.id?null:flag);setNewFlagPos(null);}}
                          style={{position:"absolute",left:`${flag.pos?.x||50}%`,top:`${flag.pos?.y||50}%`,
                            transform:flag.isRoutePin?"translate(-50%,-50%)":"translate(-50%,-100%)",
                            cursor:"pointer",zIndex:5,transition:"transform 0.15s"}}
                          onMouseEnter={e=>e.currentTarget.style.transform=flag.isRoutePin?"translate(-50%,-50%) scale(1.3)":"translate(-50%,-100%) scale(1.2)"}
                          onMouseLeave={e=>e.currentTarget.style.transform=flag.isRoutePin?"translate(-50%,-50%)":"translate(-50%,-100%)"}>
                          {flag.isRoutePin ? (
                            // Route pin — circle with route type icon
                            <div style={{width:22,height:22,borderRadius:"50%",background:T.teal,border:"2px solid white",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 6px rgba(0,0,0,0.3)",fontSize:10}}>
                              {flag.routeType==="conduit"?"⚡":flag.routeType==="pipe"?"🔧":flag.routeType==="drain"?"💧":flag.routeType==="duct"?"💨":flag.routeType==="vent"?"🌀":flag.routeType==="sprinkler"?"🔥":flag.routeType==="beam"?"🏗":"📍"}
                            </div>
                          ) : (
                            // Issue flag — label bubble with arrow
                            <>
                              <div style={{background:flag.status==="resolved"?T.green:PRIORITY_COLOR[flag.priority]||T.red,color:"white",borderRadius:"8px 8px 8px 0",padding:"4px 8px",fontSize:9,fontWeight:700,whiteSpace:"nowrap",maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",boxShadow:"0 2px 6px rgba(0,0,0,0.3)"}}>
                                {flag.status==="resolved"?"✓":flag.autoGenerated?"🤖":"📍"} {flag.title?.slice(0,20)}
                              </div>
                              <div style={{width:0,height:0,borderLeft:"6px solid transparent",borderRight:"6px solid transparent",borderTop:`8px solid ${flag.status==="resolved"?T.green:PRIORITY_COLOR[flag.priority]||T.red}`}}/>
                            </>
                          )}
                        </div>
                      ))}

                      {/* New flag cursor */}
                      {newFlagPos&&(
                        <div style={{position:"absolute",left:`${newFlagPos.x}%`,top:`${newFlagPos.y}%`,transform:"translate(-50%,-100%)",zIndex:6,pointerEvents:"none"}}>
                          <div style={{background:currentTrade.color,color:"white",borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:700}}>New flag here</div>
                        </div>
                      )}

                      {(placingFlag===selectedTrade||markingRoute===selectedTrade)&&(
                        <div style={{position:"absolute",top:8,left:"50%",transform:"translateX(-50%)",background:"rgba(0,0,0,0.75)",color:"white",borderRadius:6,padding:"6px 14px",fontSize:11,fontWeight:600,pointerEvents:"none"}}>
                          {markingRoute===selectedTrade
                            ? `Click to mark ${routeType} route — click Done when finished`
                            : "Click anywhere on the drawing to place a flag"}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{background:T.bg2,border:`2px dashed ${currentTrade.color}30`,borderRadius:10,padding:"40px",textAlign:"center",cursor:"pointer"}}
                      onClick={()=>document.getElementById(`upload_${selectedTrade}`)?.click()}>
                      <div style={{fontSize:40,marginBottom:8}}>{currentTrade.icon}</div>
                      <div style={{fontSize:13,fontWeight:700,color:currentTrade.color,marginBottom:4}}>Upload {currentTrade.label} Drawing</div>
                      <div style={{fontSize:11,color:T.muted}}>{currentTrade.desc}</div>
                    </div>
                  )}

                  {/* AI review result */}
                  {reviewResult[selectedTrade]&&(
                    <div style={{marginTop:12,background:T.bg2,border:`1px solid ${currentTrade.color}30`,borderRadius:8,padding:"12px 14px"}}>
                      <div style={{fontSize:10,fontWeight:700,color:currentTrade.color,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:8}}>
                        🤖 AI {currentTrade.label} Review — {currentFlags.filter(f=>f.autoGenerated).length} violations auto-flagged on drawing
                      </div>
                      <div style={{fontSize:11,color:T.muted,lineHeight:1.8,whiteSpace:"pre-wrap",maxHeight:200,overflowY:"auto"}}>
                        <MarkdownLite text={reviewResult[selectedTrade]}/>
                      </div>
                    </div>
                  )}

                  {/* Flag list below drawing */}
                  {visibleFlags.length>0&&(
                    <div style={{marginTop:14}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:8}}>
                        Flagged Issues ({visibleFlags.filter(f=>f.status!=="resolved").length} open)
                      </div>
                      {visibleFlags.map(f=>(
                        <div key={f.id} onClick={()=>setSelectedFlag(selectedFlag?.id===f.id?null:f)}
                          style={{display:"flex",gap:10,padding:"8px 10px",background:selectedFlag?.id===f.id?currentTrade.color+"12":T.bg2,border:`1px solid ${selectedFlag?.id===f.id?currentTrade.color:T.subtle}`,borderRadius:8,marginBottom:6,cursor:"pointer",transition:"all 0.15s"}}>
                          <div style={{width:8,height:8,borderRadius:"50%",background:f.status==="resolved"?T.green:PRIORITY_COLOR[f.priority]||T.red,flexShrink:0,marginTop:3}}/>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:11,fontWeight:600,color:T.text,marginBottom:2}}>{f.title}</div>
                            {f.location&&<div style={{fontSize:9,color:T.muted}}>📍 {f.location}</div>}
                          </div>
                          <div style={{fontSize:9,color:f.status==="resolved"?T.green:T.muted,fontWeight:600,flexShrink:0,textTransform:"capitalize"}}>{f.status}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Flag detail / new flag panel */}
                {(selectedFlag||newFlagPos)&&(
                  <div style={{position:"sticky",top:80}}>
                    <div style={{background:T.bg1,border:`1px solid ${currentTrade.color}40`,borderRadius:10,overflow:"hidden"}}>
                      <div style={{padding:"12px 14px",background:currentTrade.color+"18",borderBottom:`1px solid ${currentTrade.color}30`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div style={{fontSize:12,fontWeight:700,color:currentTrade.color}}>{newFlagPos?"New Flag":"Flag Detail"}</div>
                        <button onClick={()=>{setSelectedFlag(null);setNewFlagPos(null);setNewFlagForm({title:"",desc:"",priority:"medium",photos:[]});}} style={{background:"none",border:"none",color:T.muted,fontSize:18,cursor:"pointer",lineHeight:1}}>×</button>
                      </div>
                      <div style={{padding:"14px",maxHeight:560,overflowY:"auto"}}>

                        {newFlagPos&&(
                          <>
                            <div style={{fontSize:10,color:T.amber,marginBottom:12,display:"flex",gap:6,alignItems:"center"}}>
                              <span>📍</span>Location: {newFlagPos.x.toFixed(1)}%, {newFlagPos.y.toFixed(1)}%
                            </div>
                            <div style={{marginBottom:10}}>
                              <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Issue Title</div>
                              <input value={newFlagForm.title} onChange={e=>setNewFlagForm(p=>({...p,title:e.target.value}))}
                                placeholder="e.g. Door width too narrow"
                                style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
                            </div>
                            <div style={{marginBottom:10}}>
                              <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Description</div>
                              <textarea value={newFlagForm.desc} onChange={e=>setNewFlagForm(p=>({...p,desc:e.target.value}))}
                                placeholder="Describe the issue and what needs to happen..."
                                rows={3} style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit",resize:"vertical"}}/>
                            </div>
                            <div style={{marginBottom:14}}>
                              <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Priority</div>
                              <div style={{display:"flex",gap:6}}>
                                {["critical","high","medium","low"].map(p=>(
                                  <button key={p} onClick={()=>setNewFlagForm(f=>({...f,priority:p}))}
                                    style={{flex:1,padding:"5px",borderRadius:6,border:"none",fontSize:9,fontWeight:700,cursor:"pointer",background:newFlagForm.priority===p?PRIORITY_COLOR[p]:T.bg2,color:newFlagForm.priority===p?"white":T.muted,textTransform:"capitalize",transition:"all 0.15s"}}>
                                    {p}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div style={{display:"flex",gap:7}}>
                              <button onClick={()=>{setNewFlagPos(null);setNewFlagForm({title:"",desc:"",priority:"medium",photos:[]});}} style={{flex:1,padding:"8px",borderRadius:7,border:`1px solid ${T.subtle}`,background:T.bg2,color:T.muted,fontSize:11,cursor:"pointer",fontWeight:600}}>Cancel</button>
                              <button disabled={!newFlagForm.title.trim()} onClick={()=>saveManualFlag(selectedTrade)}
                                style={{flex:2,padding:"8px",borderRadius:7,border:"none",background:newFlagForm.title.trim()?currentTrade.color:T.bg3,color:"white",fontSize:11,fontWeight:700,cursor:newFlagForm.title.trim()?"pointer":"not-allowed"}}>
                                Save Flag
                              </button>
                            </div>
                          </>
                        )}

                        {selectedFlag&&!newFlagPos&&(
                          <>
                            <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
                              <span style={{fontSize:9,fontWeight:700,background:PRIORITY_COLOR[selectedFlag.priority]+"20",color:PRIORITY_COLOR[selectedFlag.priority],borderRadius:20,padding:"2px 8px",textTransform:"capitalize"}}>{selectedFlag.priority}</span>
                              <span style={{fontSize:9,fontWeight:700,background:selectedFlag.status==="resolved"?T.greenBg:T.redBg,color:selectedFlag.status==="resolved"?T.green:T.red,borderRadius:20,padding:"2px 8px",textTransform:"capitalize"}}>{selectedFlag.status}</span>
                              {selectedFlag.autoGenerated&&<span style={{fontSize:9,background:T.purpleBg,color:T.purple,borderRadius:20,padding:"2px 8px"}}>🤖 AI</span>}
                            </div>
                            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:8}}>{selectedFlag.title}</div>

                            {selectedFlag.location&&(
                              <div style={{fontSize:10,color:T.muted,marginBottom:8}}>📍 {selectedFlag.location}</div>
                            )}

                            {(selectedFlag.measured||selectedFlag.required)&&(
                              <div style={{background:T.bg2,borderRadius:7,padding:"8px 10px",marginBottom:10,display:"flex",gap:16}}>
                                {selectedFlag.measured&&<div><div style={{fontSize:8,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:2}}>Measured</div><div style={{fontSize:12,fontWeight:700,color:T.red}}>{selectedFlag.measured}</div></div>}
                                {selectedFlag.required&&<div><div style={{fontSize:8,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:2}}>Required</div><div style={{fontSize:12,fontWeight:700,color:T.green}}>{selectedFlag.required}</div></div>}
                              </div>
                            )}
                            {selectedFlag.codeRef&&<div style={{fontSize:10,color:T.blue,fontFamily:"monospace",marginBottom:10}}>{selectedFlag.codeRef}</div>}
                            {selectedFlag.desc&&<div style={{fontSize:11,color:T.muted,lineHeight:1.6,marginBottom:12,background:T.bg2,borderRadius:7,padding:"8px 10px"}}>{selectedFlag.desc}</div>}

                            {selectedFlag.isRoughIn && selectedFlag.roughInUrl && (
                              <div style={{marginBottom:12}}>
                                <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>📸 Rough-In Markup</div>
                                <img src={selectedFlag.roughInUrl} alt="Rough-in markup"
                                  style={{width:"100%",borderRadius:7,border:`1px solid ${T.subtle}`,cursor:"pointer"}}
                                  onClick={()=>window.open(selectedFlag.roughInUrl,"_blank")}/>
                                <div style={{fontSize:9,color:T.muted,marginTop:4}}>Click to open full size · Uploaded {selectedFlag.date} by {selectedFlag.flaggedBy}</div>
                              </div>
                            )}
                            {/* Photos */}
                            <div style={{marginBottom:12}}>
                              <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Photos</div>
                              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:6}}>
                                {(selectedFlag.photos||[]).map(ph=>(
                                  <div key={ph.id} onClick={()=>window.open(ph.url,"_blank")} style={{width:60,height:60,borderRadius:6,overflow:"hidden",border:`1px solid ${T.subtle}`,cursor:"pointer"}}>
                                    <img src={ph.url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                                  </div>
                                ))}
                                <input type="file" accept="image/*" style={{display:"none"}} id={`photo_${selectedFlag.id}`}
                                  onChange={e=>{if(e.target.files[0])addPhotoToFlag(selectedTrade,selectedFlag.id,e.target.files[0]);}}/>
                                <button onClick={()=>document.getElementById(`photo_${selectedFlag.id}`)?.click()}
                                  style={{width:60,height:60,borderRadius:6,border:`2px dashed ${T.subtle}`,background:T.bg2,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:2,color:T.muted,fontSize:8}}>
                                  <span style={{fontSize:16}}>📷</span>Add
                                </button>
                              </div>
                            </div>

                            {/* Comments */}
                            <div style={{marginBottom:12}}>
                              <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Comments</div>
                              {(selectedFlag.comments||[]).length===0&&<div style={{fontSize:10,color:T.muted,fontStyle:"italic",marginBottom:6}}>No comments yet</div>}
                              {(selectedFlag.comments||[]).map(c=>(
                                <div key={c.id} style={{background:T.bg2,borderRadius:7,padding:"7px 9px",marginBottom:5}}>
                                  <div style={{fontSize:9,fontWeight:700,color:T.text,marginBottom:2}}>{c.author} · {c.date}</div>
                                  <div style={{fontSize:11,color:T.muted}}>{c.text}</div>
                                </div>
                              ))}
                              <textarea value={comment} onChange={e=>setComment(e.target.value)}
                                placeholder="Add a comment..."
                                rows={2} style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:11,fontFamily:"inherit",resize:"none",marginBottom:6}}/>
                              <button onClick={()=>addComment(selectedTrade,selectedFlag.id)} disabled={!comment.trim()}
                                style={{width:"100%",padding:"7px",borderRadius:6,border:"none",background:comment.trim()?currentTrade.color:T.bg3,color:"white",fontSize:11,fontWeight:600,cursor:comment.trim()?"pointer":"not-allowed"}}>
                                💬 Post Comment
                              </button>
                            </div>

                            {/* Actions */}
                            <div style={{display:"flex",gap:7}}>
                              {selectedFlag.status!=="resolved"&&(
                                <button onClick={()=>resolveFlag(selectedTrade,selectedFlag.id)}
                                  style={{flex:2,padding:"8px",borderRadius:7,border:"none",background:T.green,color:"white",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                                  ✓ Mark Resolved
                                </button>
                              )}
                              <button onClick={()=>deleteFlag(selectedTrade,selectedFlag.id)}
                                style={{flex:1,padding:"8px",borderRadius:7,border:`1px solid ${T.red}40`,background:"none",color:T.red,fontSize:11,fontWeight:600,cursor:"pointer"}}>
                                🗑 Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KnowledgeBaseView() {
  const [kb, setKb] = useState(()=>loadKB());
  const [saved, setSaved] = useState(false);
  const [section, setSection] = useState("company");
  const f = (k,v) => setKb(p=>({...p,[k]:v}));

  const save = () => { saveKB(kb); setSaved(true); setTimeout(()=>setSaved(false), 2500); };

  const Field2 = ({label, k, placeholder, rows, hint}) => (
    <div style={{marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>{label}</div>
      {hint&&<div style={{fontSize:10,color:T.muted,marginBottom:5,lineHeight:1.4}}>{hint}</div>}
      {rows ? (
        <textarea value={kb[k]} onChange={e=>f(k,e.target.value)} placeholder={placeholder} rows={rows}
          style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit"}}/>
      ) : (
        <input value={kb[k]} onChange={e=>f(k,e.target.value)} placeholder={placeholder}
          style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,fontFamily:"inherit"}}/>
      )}
    </div>
  );

  const sections = [
    {id:"company", label:"Company Info"},
    {id:"rates", label:"Rates & Pricing"},
    {id:"process", label:"Process & Policy"},

    {id:"custom", label:"Custom Training"},
    {id:"preview", label:"Preview AI Prompt"},
  ];

  return (
    <div style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>AI Knowledge Base</div>
          <div style={{fontSize:12,color:T.muted,marginTop:3}}>
            Train your AI PM with Grok-specific knowledge — rates, processes, subs, preferences
          </div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {saved&&<span style={{fontSize:12,color:T.green,fontWeight:600}}>✓ Saved</span>}
          <Btn primary onClick={save}>Save Knowledge Base</Btn>
        </div>
      </div>

      <div style={{background:T.purpleBg,borderLeft:`3px solid ${T.purple}`,borderRadius:9,padding:"12px 16px",marginBottom:20,fontSize:12,color:T.muted,lineHeight:1.6}}>
        <strong style={{color:T.purple}}>How this works:</strong> Everything you enter here gets injected into every AI PM conversation automatically. The more specific you are — exact rates, your actual subs, your exact CO process — the better the AI performs. It becomes a digital version of how YOU run jobs.
      </div>

      {/* Section tabs */}
      <div style={{display:"flex",gap:4,background:T.bg2,borderRadius:10,padding:4,width:"fit-content",marginBottom:20,flexWrap:"wrap"}}>
        {sections.map(s=>(
          <button key={s.id} onClick={()=>setSection(s.id)} style={{padding:"6px 14px",borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:section===s.id?T.purple:T.bg2,color:section===s.id?"white":T.muted,transition:"all 0.15s"}}>{s.label}</button>
        ))}
      </div>

      {section==="company"&&(
        <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <Card>
            <SectionTitle>Company Details</SectionTitle>
            <Field2 label="Company Name" k="company_name" placeholder="Grok Construction Group Inc"/>
            <Field2 label="CSLB License Number" k="cslb" placeholder="1152750"/>
            <Field2 label="Office Location" k="location" placeholder="San Mateo, CA"/>
            <Field2 label="Specialty / Market Focus" k="specialty" placeholder="SRO, affordable housing, senior housing, multi-family renovation in SF Bay Area"
              hint="Be specific — this tells the AI what types of projects you normally bid and what standards apply." rows={3}/>
          </Card>
          <Card>
            <SectionTitle>About Grok</SectionTitle>
            <Field2 label="Self-Perform Trades" k="self_perform" placeholder="Carpentry, drywall, tile, countertops, paint"
              hint="List exactly which trades Grok does in-house. The AI uses this to determine sub vs self-perform on estimates."/>
            <Field2 label="About the Principal / Team" k="owner_intro" placeholder="Jehoon Chun, principal. Grok specializes in..."
              hint="Who runs the company, your background, your approach. The AI uses this to personalize communications." rows={4}/>
          </Card>
        </div>
      )}

      {section==="rates"&&(
        <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <Card>
            <SectionTitle>Self-Perform Labor Rates</SectionTitle>
            <Field2 label="Labor Rate Schedule" k="labor_rates"
              hint="List each role and hourly rate. The AI uses these for all estimates."
              placeholder="Lead carpenter $95/hr, Journeyman $75/hr, Laborer $55/hr, Tile setter $85/hr, Painter $65/hr" rows={5}/>
            <Field2 label="Overhead & Profit Structure" k="op_structure"
              hint="Your standard O&P breakdown as percentages."
              placeholder="Project management 5%, Insurance/bonding 2.5%, Overhead 7.5%, Profit 10%" rows={4}/>
          </Card>
          <Card>
            <SectionTitle>Subcontractor Rates</SectionTitle>
            <Field2 label="Sub Rate Schedule" k="sub_rates"
              hint="Your typical sub costs and your markup percentage."
              placeholder="Licensed plumber $145/hr, Licensed electrician $145/hr, HVAC tech $135/hr. Sub markup: +15%" rows={5}/>
            <div style={{background:T.blueBg,borderLeft:`3px solid ${T.blue}`,borderRadius:7,padding:"9px 11px",fontSize:11,color:T.muted,lineHeight:1.6}}>
              <strong style={{color:T.blue}}>Tip:</strong> Include your actual markup percentage, any preferred pricing arrangements with recurring subs, and any trades where you have fixed-price agreements.
            </div>
          </Card>
        </div>
      )}

      {section==="process"&&(
        <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <Card>
            <SectionTitle>Change Order Policy</SectionTitle>
            <Field2 label="CO Process" k="co_policy"
              hint="Your exact CO process — when to stop work, how to notify, what documentation is required."
              placeholder="Stop work immediately on hidden conditions. Notify owner within 2 hours in writing. No work proceeds without signed CO." rows={4}/>
            <Field2 label="Standard Exclusions" k="standard_exclusions"
              hint="Your standard list of things that are always excluded from every contract."
              placeholder="Structural modifications, asbestos/lead abatement, drain relocation, panel upgrades..." rows={5}/>
          </Card>
          <Card>
            <SectionTitle>Billing & Lien Process</SectionTitle>
            <Field2 label="Billing Process" k="billing"
              hint="Your monthly billing cycle, lien waiver process, payment terms."
              placeholder="Progress billing on the 1st monthly. Conditional waiver with each pay app. Unconditional after check clears. Retainage 10%." rows={4}/>
            <Field2 label="Prevailing Wage Policy" k="prevailing_wage"
              hint="When prevailing wage applies to your projects and how you handle it."
              placeholder="Required on all MOHCD, HUD, and public-funded projects. DIR certified payroll mandatory." rows={3}/>
          </Card>
        </div>
      )}

      {section==="subs"&&(
        <Card>
          <SectionTitle>Your Subcontractor Network</SectionTitle>
          <div style={{background:T.goldBg,borderLeft:`3px solid ${T.gold}`,borderRadius:7,padding:"10px 12px",marginBottom:14,fontSize:11,color:T.muted,lineHeight:1.6}}>
            <strong style={{color:T.gold}}>This is powerful:</strong> List your actual subs with their names, trades, typical rates, and notes. The AI PM will recommend specific subs for each project, include them in schedules, and reference them in communications.
          </div>
          <Field2 label="Your Subcontractor List" k="key_subs"
            hint='Format: "Trade — Company Name — Contact — Rate/notes". One sub per line.'
            placeholder={"Plumbing — SF Plumbing Inc — Mike Chen — $145/hr, reliable, good on SROs\nElectrical — Bay Electrical — Tom Lee — $145/hr, SFPUC approved\nHVAC — Golden Gate HVAC — $3,500 typical SRO unit\nTile — Pedro Martinez (solo) — $85/hr, excellent work\nDrywall — Bay Area Drywall — $55/hr labor only\nPainting — SF Paint Crew — $4K typical unit turnover"}
            rows={10}/>
        </Card>
      )}

      {section==="custom"&&(
        <div>
          <Card style={{marginBottom:14}}>
            <SectionTitle>Custom Training Notes</SectionTitle>
            <div style={{background:T.purpleBg,borderLeft:`3px solid ${T.purple}`,borderRadius:7,padding:"10px 12px",marginBottom:14,fontSize:11,color:T.muted,lineHeight:1.6}}>
              This is free-form — write anything you want the AI to know about how Grok operates. The more detail, the better it performs. Examples below.
            </div>
            <Field2 label="Custom Knowledge — Write Anything" k="custom_notes"
              hint=""
              placeholder={"Examples of what to add here:\n\n- 'We never start demo without a signed contract and deposit cleared.'\n- 'For SRO projects we always serve the 20-Day Notice on day 1 via certified mail to both owner and lender.'\n- 'Our standard kitchen renovation in an SRO unit runs $18K-$22K all-in for gut and replace.'\n- 'We always photograph every unit before touching anything — this is non-negotiable.'\n- 'When quoting maintenance work we use Maintenance Agreement Option A at $105/hr standard.'\n- 'All subs must have their own liability insurance — minimum $1M. We verify before they start.'\n- 'Our typical SRO unit renovation takes 3-4 weeks with a crew of 3.'\n- 'We prefer to self-perform tile on SRO bathrooms — Pedro is our go-to sub when we're overloaded.'"}
              rows={14}/>
          </Card>
          <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            {[
              {title:"Bidding Preferences",hint:"What types of jobs you prefer, minimum project size, how you evaluate bids"},
              {title:"Client Communication Style",hint:"How formal/informal, response time expectations, preferred communication method"},
              {title:"Quality Standards",hint:"Your specific quality requirements, materials you prefer, things you never cut corners on"},
              {title:"Lessons Learned",hint:"Common mistakes on past projects, things to always check, red flags you've encountered"},
            ].map(item=>(
              <div key={item.title} style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:9,padding:"12px 14px"}}>
                <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:4}}>{item.title}</div>
                <div style={{fontSize:10,color:T.muted,marginBottom:8,lineHeight:1.4}}>{item.hint}</div>
                <div style={{fontSize:10,color:T.muted,fontStyle:"italic"}}>Add this in the field above — the AI learns from all of it.</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {section==="preview"&&(
        <Card>
          <SectionTitle>AI System Prompt Preview</SectionTitle>
          <div style={{fontSize:11,color:T.muted,marginBottom:12,lineHeight:1.5}}>
            This is exactly what gets sent to the AI at the start of every conversation. The more complete your knowledge base, the more specific and accurate the AI PM becomes.
          </div>
          <div style={{background:T.bg0,borderRadius:9,padding:"14px 16px",fontSize:11,color:T.text,lineHeight:1.8,whiteSpace:"pre-wrap",fontFamily:"monospace",maxHeight:500,overflowY:"auto",border:`1px solid ${T.subtle}`}}>
            {buildSystemPrompt("Project: [Selected project context goes here]", kb)}
          </div>
          <div style={{marginTop:12,display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn primary onClick={save}>{saved?"✓ Saved":"Save Knowledge Base"}</Btn>
          </div>
        </Card>
      )}

      <div style={{marginTop:20,display:"flex",justifyContent:"flex-end",gap:8}}>
        <Btn ghost onClick={()=>{ if(true) { setKb(KB_DEFAULTS); saveKB(KB_DEFAULTS); }}}>Reset to Defaults</Btn>
        <Btn primary onClick={save}>{saved?"✓ Saved — AI PM Updated":"Save Knowledge Base"}</Btn>
      </div>
    </div>
  );
}


// ─── ARIA PM AGENT VIEW ──────────────────────────────────────────────────────
function ARIAAgentView({projects, cos, rfis, leads, session, setView}) {
  const T = useTheme();
  const isMob = window.innerWidth < 768;
  const cid = session?.companyId || 'local';
  // Measured height replaces the inspection-tuned calc(100vh - 96px/130px) that
  // the sweep flagged for eyeballing — the real top of the view now sets the bound.
  const viewRef = useRef(null);
  const viewBound = useViewportBound(viewRef, 60);

  const hour = new Date().getHours();
  const greeting = hour<12?'Good morning':hour<17?'Good afternoon':'Good evening';
  const dayName = new Date().toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'});

  // Live data
  const liveProjects = (()=>{try{const s=localStorage.getItem('grok_projects_'+cid);if(s){const p=cleanProjects(JSON.parse(s).filter(x=>!x.isSample));if(p.length)return p;}return [];}catch{return [];}})();
  const liveCOs      = (()=>{try{return JSON.parse(localStorage.getItem('grok_cos_global_'+cid)||'[]');}catch{return cos||[];}})();
  const liveRFIs     = (()=>{try{return JSON.parse(localStorage.getItem('grok_rfis_global_'+cid)||'[]');}catch{return rfis||[];}})();
  const liveLeads    = (()=>{try{return JSON.parse(localStorage.getItem('grok_leads_'+cid)||'[]');}catch{return leads||[];}})();

  const ap  = liveProjects.filter(p=>p.status==='active');
  const pc  = liveCOs.filter(c=>c.status==='pending');
  const or_ = liveRFIs.filter(r=>r.status!=='closed');
  const al  = liveLeads.filter(l=>l.status!=='won'&&l.status!=='lost');

  // Usage
  const usageInfo = checkLimit('pm_chats', session);
  const usedChats = usageInfo.used || 0;
  const limitChats = usageInfo.limit >= 9999 ? null : usageInfo.limit;

  // Build opening briefing
  const buildBriefing = () => {
    const risks = [];
    pc.forEach(co=>{if((Date.now()-new Date(co.createdAt||Date.now()))>3*86400000) risks.push('CO "'+((co.desc||co.title||'CO').slice(0,30))+'" pending '+Math.floor((Date.now()-new Date(co.createdAt||Date.now()))/86400000)+'d');});
    or_.forEach(rfi=>{if((Date.now()-new Date(rfi.createdAt||Date.now()))>5*86400000) risks.push('RFI "'+((rfi.subject||'RFI').slice(0,30))+'" open '+Math.floor((Date.now()-new Date(rfi.createdAt||Date.now()))/86400000)+'d');});
    ap.forEach(p=>{if((p.progress||0)<20&&p.status==='active') risks.push((p.name||'Project').slice(0,25)+' only at '+(p.progress||0)+'% — check schedule');});

    let msg = greeting+(session?.ownerName?', '+session.ownerName:'')+'.\n\n';
    msg += '📅 '+dayName+'\n\n';
    if(ap.length===0&&pc.length===0&&or_.length===0){
      msg += "Everything looks clear today — no active projects or open items. Ready to help whenever you need me.";
    } else {
      if(ap.length) msg += '🏗 **'+ap.length+' active project'+(ap.length>1?'s':'')+'** — '+ap.map(p=>(p.name||'Project')+' at '+(p.progress||0)+'%').join(', ')+'.\n';
      if(pc.length) msg += '📝 **'+pc.length+' pending CO'+(pc.length>1?'s':'')+'** need'+(pc.length===1?'s':'')+' a decision.\n';
      if(or_.length) msg += '❓ **'+or_.length+' open RFI'+(or_.length>1?'s':'')+'** in progress.\n';
      if(al.length) msg += '🔥 **'+al.length+' lead'+(al.length>1?'s':'')+'** in your pipeline.\n';
      if(risks.length){
        msg += '\n⚠️ **Heads up:**\n';
        risks.slice(0,3).forEach(r=>{ msg += '• '+r+'\n'; });
      }
      msg += '\nWhat would you like to tackle first?';
    }
    return msg;
  };

  const [msgs, setMsgs] = React.useState([{role:'aria', text:buildBriefing(), time:new Date().toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}]);
  const [input, setInput] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('chat');
  const [reportText, setReportText] = React.useState('');
  const [reportLoading, setReportLoading] = React.useState(false);
  const [showReport, setShowReport] = React.useState(false);
  const [isListening, setIsListening] = React.useState(false);
  const msgEndRef = React.useRef(null);
  React.useEffect(()=>{ msgEndRef.current?.scrollIntoView({behavior:'smooth'}); },[msgs]);

  const buildContext = () =>
    `You are ARIA, an AI project manager built into Grok Field Ops — a construction management platform for ${session?.companyName||'a contractor'}. You are sharp, direct, proactive, and genuinely helpful — like a trusted chief of staff who knows construction inside out.

Today: ${dayName}
Owner: ${session?.ownerName||'the contractor'}
Company: ${session?.companyName||''}
Plan: ${session?.plan||'starter'}

Live project data:
- Active projects (${ap.length}): ${ap.map(p=>(p.name||'Project')+' — '+(p.progress||0)+'% complete, phase: '+(p.phase||'active')+', value: $'+(p.value||0).toLocaleString()).join(' | ')||'none'}
- Pending COs (${pc.length}): ${pc.slice(0,5).map(c=>(c.desc||c.title||'CO')+' — $'+(c.amount||0).toLocaleString()+' — pending '+(c.status||'')).join(' | ')||'none'}
- Open RFIs (${or_.length}): ${or_.slice(0,5).map(r=>(r.subject||r.num||'RFI')+' — '+(r.status||'open')).join(', ')||'none'}
- Active leads (${al.length}): ${al.slice(0,5).map(l=>(l.name||'Lead')+' — $'+(l.value||0).toLocaleString()+' — '+(l.status||'new')).join(', ')||'none'}

Your capabilities as AI PM Agent:
1. Project status, schedule tracking, budget analysis
2. Change order management — draft responses, flag overdue items
3. RFI drafting and escalation recommendations
4. Sub follow-up — draft emails and reminders
5. Daily briefings and priority ranking
6. Weekly owner progress reports
7. Risk identification — schedule slippage, budget overruns, compliance gaps
8. Lead follow-up drafts and pipeline advice
9. Contract and scope questions (CA construction law context)
10. Permit and inspection scheduling advice

Be specific using real data above. Keep responses concise (3-6 sentences) unless drafting a document. Sound human and direct. When drafting emails/letters, format them properly with Subject:, greeting, body, sign-off.`;

  const sendMessage = async(text) => {
    if(!text.trim()) return;
    if(!enforceLimit('pm_chats', session)) return;
    const userMsg = {role:'user', text:text.trim(), time:new Date().toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})};
    setMsgs(m=>[...m, userMsg]);
    setInput('');
    setLoading(true);
    try {
      const history = msgs.filter((_,i)=>i>0).map(m=>({role:m.role==='aria'?'assistant':'user',content:m.text}));
      const resp = await fetch(AI_PROXY_URL,{method:'POST',
        headers:{'Content-Type':'application/json','Authorization':'Bearer '+SUPABASE_ANON_KEY,'apikey':SUPABASE_ANON_KEY},
        body:JSON.stringify({model:'claude-haiku-4-5',max_tokens:800,
          system: buildContext(),
          messages:[...history,{role:'user',content:text}]
        })});
      const data = await resp.json();
      const answer = data.content?.[0]?.text || generateFallback(text);
      trackUsage('pm_chats', cid);
      setMsgs(m=>[...m,{role:'aria',text:answer,time:new Date().toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}]);
    } catch(e) {
      setMsgs(m=>[...m,{role:'aria',text:generateFallback(text),time:new Date().toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}]);
    }
    setLoading(false);
  };

  const generateFallback = (q) => {
    const ql = q.toLowerCase();
    if(ql.includes('project')||ql.includes('status')) return `You have ${ap.length} active project${ap.length!==1?'s':''}: ${ap.slice(0,2).map(p=>p.name+' at '+(p.progress||0)+'%').join(', ')||'none yet'}.`;
    if(ql.includes('change order')||ql.includes(' co ')) return pc.length>0?`${pc.length} pending CO${pc.length>1?'s':''}. Top item: ${(pc[0]?.desc||pc[0]?.title||'CO')+' — $'+(pc[0]?.amount||0).toLocaleString()}.`:'No pending change orders.';
    if(ql.includes('rfi')) return or_.length>0?`${or_.length} open RFI${or_.length>1?'s':''}. Oldest: ${or_[0]?.subject||'open RFI'}.`:'No open RFIs.';
    if(ql.includes('lead')) return al.length>0?`${al.length} active lead${al.length>1?'s':''} in pipeline.`:'Pipeline is clear.';
    if(ql.includes('report')||ql.includes('summary')) { generateReport(); return 'Generating your weekly report...'; }
    return `You have ${ap.length} active projects, ${pc.length} pending COs, ${or_.length} open RFIs. What do you need?`;
  };

  const generateReport = async() => {
    setReportLoading(true);
    const prompt = `Write a professional weekly progress report email from ${session?.companyName||'our company'} to project owners/clients.

Date: ${dayName}
Projects: ${ap.map(p=>(p.name||'Project')+' — '+(p.progress||0)+'% complete, phase: '+(p.phase||'active')+', contract value: $'+(p.value||0).toLocaleString()).join('; ')||'No active projects'}
Pending change orders: ${pc.length}
Open RFIs: ${or_.length}
Active leads: ${al.length}

Write a warm, professional weekly update email under 250 words. Include:
- Subject line
- Brief project status for each active project
- Any items needing client attention (COs, RFIs)
- Next week outlook
Sign as ${session?.ownerName||'the team'}, ${session?.companyName||''}.`;

    try {
      const resp = await fetch(AI_PROXY_URL,{method:'POST',
        headers:{'Content-Type':'application/json','Authorization':'Bearer '+SUPABASE_ANON_KEY,'apikey':SUPABASE_ANON_KEY},
        body:JSON.stringify({model:'claude-haiku-4-5',max_tokens:600,messages:[{role:'user',content:prompt}]})});
      const data = await resp.json();
      setReportText(data.content?.[0]?.text||'');
    } catch(e) {
      setReportText('Subject: Weekly Project Update — '+new Date().toLocaleDateString()+'\n\nHello,\n\nHere is your weekly project update from '+( session?.companyName||'our team')+'.\n\n'+ap.map(p=>(p.name||'Project')+': '+(p.progress||0)+'% complete.').join('\n')+'\n\nBest regards,\n'+(session?.ownerName||'The Team'));
    }
    setReportLoading(false);
    setShowReport(true);
  };

  const draftSubFollowUp = async() => {
    if(pc.length===0&&or_.length===0){alert('No pending COs or RFIs to follow up on.');return;}
    setLoading(true);
    const items = [...pc.slice(0,3).map(c=>'CO: '+(c.desc||c.title||'change order')+' $'+(c.amount||0).toLocaleString()), ...or_.slice(0,3).map(r=>'RFI: '+(r.subject||'RFI'))];
    const prompt = `Draft a professional follow-up email to subcontractors regarding outstanding items.

Company: ${session?.companyName||'our company'}
Owner: ${session?.ownerName||''}
Outstanding items:
${items.map((item,i)=>(i+1)+'. '+item).join(' | ')}

Write a firm but professional email requesting responses within 48 hours. Keep it under 150 words.`;
    try {
      const resp = await fetch(AI_PROXY_URL,{method:'POST',
        headers:{'Content-Type':'application/json','Authorization':'Bearer '+SUPABASE_ANON_KEY,'apikey':SUPABASE_ANON_KEY},
        body:JSON.stringify({model:'claude-haiku-4-5',max_tokens:400,messages:[{role:'user',content:prompt}]})});
      const data = await resp.json();
      const draft = data.content?.[0]?.text || '';
      setMsgs(m=>[...m,{role:'aria',text:'📧 **Sub Follow-Up Draft:**\n\n'+draft,time:new Date().toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}]);
      trackUsage('pm_chats', cid);
    } catch(e) {}
    setLoading(false);
    setActiveTab('chat');
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition||window.webkitSpeechRecognition;
    if(!SR){const q=prompt('Ask ARIA:');if(q)sendMessage(q);return;}
    const r=new SR();r.lang='en-US';r.interimResults=false;
    r.onstart=()=>setIsListening(true);
    r.onresult=e=>{setIsListening(false);sendMessage(e.results[0][0].transcript);};
    r.onerror=r.onend=()=>setIsListening(false);
    r.start();
  };

  const quickActions = [
    {label:"What needs my attention today?", icon:"🎯"},
    {label:"How are my projects doing?", icon:"📊"},
    {label:"Any overdue COs or RFIs?", icon:"⚠️"},
    {label:"Draft a sub follow-up email", icon:"📧"},
    {label:"Generate weekly owner report", icon:"📋"},
    {label:"What are my biggest risks right now?", icon:"🚨"},
    {label:"Summarize my pipeline", icon:"🔥"},
    {label:"Draft a project status update", icon:"✉️"},
  ];

  const pmActions = [
    {label:'Weekly Owner Report', icon:'📋', desc:'Auto-generate and copy', action:generateReport, loading:reportLoading},
    {label:'Sub Follow-Up Email', icon:'📧', desc:'Draft follow-up on open COs/RFIs', action:draftSubFollowUp},
    {label:'Risk Assessment', icon:'🚨', desc:'Flag schedule and budget risks', action:()=>sendMessage('Give me a full risk assessment of all my active projects — flag anything behind schedule, over budget, or with unresolved items.')},
    {label:'Morning Briefing', icon:'🌅', desc:'Today\'s priorities and alerts', action:()=>sendMessage('Give me a detailed morning briefing for today — priorities, risks, what needs a decision, and recommended actions.')},
    {label:'CO Status Summary', icon:'📝', desc:'All pending change orders', action:()=>sendMessage('Summarize all pending change orders — amounts, how long they\'ve been open, and recommended next steps.')},
    {label:'RFI Status Summary', icon:'❓', desc:'All open RFIs', action:()=>sendMessage('Summarize all open RFIs — subjects, age, and what\'s needed to close them.')},
    {label:'Lead Pipeline Review', icon:'💰', desc:'Pipeline status and follow-ups', action:()=>sendMessage('Review my lead pipeline — who needs follow-up, estimated close dates, and total pipeline value.')},
    {label:'Project Schedule Check', icon:'📅', desc:'Are we on track?', action:()=>sendMessage('Check the schedule on all active projects — are we on track? Flag anything behind and suggest corrective actions.')},
  ];

  const TABS = [{id:'chat',label:'Chat'},{id:'actions',label:'Actions'}];

  return (
    <div ref={viewRef} style={{height: viewBound || (isMob?'calc(100vh - 130px)':'calc(100vh - 96px)'),minHeight:400,display:'flex',flexDirection:'column',background:T.bg0}}>

      {/* REPORT MODAL */}
      {showReport&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.8)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:T.bg1,border:'1px solid '+T.subtle,borderRadius:16,width:'100%',maxWidth:520,maxHeight:'85vh',overflow:'hidden',display:'flex',flexDirection:'column'}}>
            <div style={{padding:'14px 18px',borderBottom:'1px solid '+T.subtle,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontSize:14,fontWeight:700,color:T.text}}>📋 Weekly Owner Report</div>
              <button onClick={()=>setShowReport(false)} style={{background:'none',border:'none',color:T.muted,cursor:'pointer',fontSize:18}}>✕</button>
            </div>
            <div style={{flex:1,overflow:'auto',padding:'14px 18px'}}>
              {reportLoading ? (
                <div style={{display:'flex',alignItems:'center',gap:10,color:T.muted,padding:'32px 0'}}><Spin/>Generating report...</div>
              ) : (
                <textarea value={reportText} onChange={e=>setReportText(e.target.value)}
                  style={{width:'100%',minHeight:300,background:T.bg2,border:'1px solid '+T.subtle,borderRadius:8,padding:'12px',color:T.text,fontSize:12,lineHeight:1.85,fontFamily:'inherit',resize:'vertical',outline:'none'}}/>
              )}
            </div>
            <div style={{padding:'12px 18px',borderTop:'1px solid '+T.subtle,display:'flex',gap:8}}>
              <button onClick={()=>{navigator.clipboard?.writeText(reportText).catch(()=>{});setShowReport(false);}}
                style={{flex:1,background:'linear-gradient(135deg,'+T.gold+','+T.gold2+')',color:'#02040A',border:'none',borderRadius:9,padding:'10px',fontSize:12,fontWeight:700,cursor:'pointer'}}>
                📋 Copy & Close
              </button>
              <button onClick={()=>setShowReport(false)} style={{padding:'10px 16px',background:T.bg2,border:'1px solid '+T.subtle,borderRadius:9,color:T.muted,cursor:'pointer',fontSize:12}}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div style={{padding:'14px 16px 0',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <div style={{width:36,height:36,borderRadius:10,background:'linear-gradient(135deg,'+T.gold+','+T.gold2+')',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>🤖</div>
            <div>
              <div style={{fontSize:14,fontWeight:800,color:T.text}}>ARIA</div>
              <div style={{fontSize:10,color:T.muted}}>AI Project Manager · {session?.companyName||''}</div>
            </div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            {limitChats && (
              <div style={{fontSize:10,color:usedChats>limitChats*0.8?T.amber:T.muted,background:T.bg2,border:'1px solid '+T.subtle,borderRadius:6,padding:'3px 8px'}}>
                {usedChats}/{limitChats} msgs
              </div>
            )}
            <div style={{width:8,height:8,borderRadius:'50%',background:T.green,boxShadow:'0 0 6px '+T.green}}/>
            <span style={{fontSize:10,color:T.green,fontWeight:600}}>Online</span>
          </div>
        </div>

        {/* Stats row */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:6,marginBottom:12}}>
          {[
            {n:ap.length, l:'Projects', c:T.blue},
            {n:pc.length, l:'Open COs', c:pc.length>0?T.amber:T.muted},
            {n:or_.length, l:'Open RFIs', c:or_.length>0?T.amber:T.muted},
            {n:al.length, l:'Leads', c:T.green},
          ].map((s,i)=>(
            <div key={i} style={{background:T.bg2,border:'1px solid '+T.subtle,borderRadius:8,padding:'8px',textAlign:'center'}}>
              <div style={{fontSize:18,fontWeight:800,color:s.c,lineHeight:1}}>{s.n}</div>
              <div style={{fontSize:9,color:T.muted,marginTop:2,textTransform:'uppercase',letterSpacing:'0.08em'}}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{display:'flex',gap:4,marginBottom:10}}>
          {TABS.map(tab=>(
            <button key={tab.id} onClick={()=>setActiveTab(tab.id)}
              style={{padding:'6px 14px',borderRadius:7,fontSize:12,fontWeight:600,cursor:'pointer',border:'none',
                background:activeTab===tab.id?T.gold:T.bg2,color:activeTab===tab.id?T.bg0:T.muted,transition:'all 0.15s'}}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* CHAT TAB */}
      {activeTab==='chat'&&(
        <>
          <div style={{flex:1,overflowY:'auto',padding:'0 16px',display:'flex',flexDirection:'column',gap:10}}>
            {msgs.map((msg,i)=>(
              <div key={i} style={{display:'flex',flexDirection:'column',alignItems:msg.role==='aria'?'flex-start':'flex-end'}}>
                {msg.role==='aria'&&(
                  <div style={{fontSize:9,color:T.muted,marginBottom:3,marginLeft:2,fontWeight:600}}>ARIA · {msg.time}</div>
                )}
                <div style={{maxWidth:'88%',padding:'10px 14px',borderRadius:msg.role==='aria'?'4px 12px 12px 12px':'12px 4px 12px 12px',
                  background:msg.role==='aria'?T.bg1:'linear-gradient(135deg,'+T.blue+','+T.purple+')',
                  border:'1px solid '+(msg.role==='aria'?T.subtle:'transparent'),
                  fontSize:13,color:msg.role==='aria'?T.text:'white',lineHeight:1.65,whiteSpace:msg.role==='aria'?'normal':'pre-wrap'}}>
                  {msg.role==='aria'?<MarkdownLite text={msg.text}/>:msg.text}
                </div>
                {msg.role==='user'&&(
                  <div style={{fontSize:9,color:T.muted,marginTop:3,marginRight:2}}>{msg.time}</div>
                )}
              </div>
            ))}
            {loading&&(
              <div style={{display:'flex',alignItems:'flex-start',gap:8}}>
                <div style={{padding:'10px 14px',background:T.bg1,border:'1px solid '+T.subtle,borderRadius:'4px 12px 12px 12px',display:'flex',gap:5,alignItems:'center'}}>
                  {[0,1,2].map(i=><div key={i} style={{width:6,height:6,borderRadius:'50%',background:T.gold,animation:'pulse 1.2s '+(i*0.2)+'s infinite'}}/>)}
                </div>
              </div>
            )}
            <div ref={msgEndRef}/>
          </div>

          {/* Quick actions */}
          {msgs.length<3&&(
            <div style={{padding:'0 16px 8px',flexShrink:0}}>
              <div style={{fontSize:10,color:T.muted,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.08em'}}>Quick actions</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                {quickActions.map((qa,i)=>(
                  <button key={i} onClick={()=>sendMessage(qa.label)}
                    style={{padding:'6px 11px',background:T.bg2,border:'1px solid '+T.subtle,borderRadius:20,fontSize:11,color:T.muted,cursor:'pointer',display:'flex',alignItems:'center',gap:5,transition:'border-color 0.15s'}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=T.gold}
                    onMouseLeave={e=>e.currentTarget.style.borderColor=T.subtle}>
                    <span>{qa.icon}</span>{qa.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="chat-input-row" style={{padding:'8px 16px 16px',flexShrink:0,borderTop:'1px solid '+T.subtle}}>
            <div style={{display:'flex',gap:8,alignItems:'flex-end'}}>
              <textarea value={input} onChange={e=>setInput(e.target.value)}
                onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendMessage(input);}}}
                placeholder="Ask ARIA anything about your projects..."
                rows={2}
                style={{flex:1,background:T.bg2,border:'1px solid '+T.subtle,borderRadius:10,padding:'10px 13px',color:T.text,fontSize:13,fontFamily:'inherit',resize:'none',outline:'none',lineHeight:1.5}}/>
              <div style={{display:'flex',flexDirection:'column',gap:6}}>
                <button onClick={startVoice}
                  style={{width:38,height:38,borderRadius:9,background:isListening?T.red:T.bg2,border:'1px solid '+(isListening?T.red:T.subtle),cursor:'pointer',fontSize:16,display:'flex',alignItems:'center',justifyContent:'center'}}>
                  🎤
                </button>
                <button onClick={()=>sendMessage(input)} disabled={!input.trim()||loading}
                  style={{width:38,height:38,borderRadius:9,background:input.trim()&&!loading?T.gold:T.bg2,border:'none',cursor:input.trim()&&!loading?'pointer':'not-allowed',fontSize:16,display:'flex',alignItems:'center',justifyContent:'center'}}>
                  ➤
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ACTIONS TAB */}
      {activeTab==='actions'&&(
        <div style={{flex:1,overflowY:'auto',padding:'0 16px 16px'}}>
          <div style={{fontSize:11,color:T.muted,marginBottom:12,lineHeight:1.6}}>
            One-tap PM actions — ARIA handles the drafting and analysis for you.
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {pmActions.map((a,i)=>(
              <button key={i} onClick={()=>{a.action();if(a.action!==generateReport)setActiveTab('chat');}}
                style={{display:'flex',alignItems:'center',gap:12,padding:'14px 16px',background:T.bg1,border:'1px solid '+T.subtle,borderRadius:12,cursor:'pointer',textAlign:'left',transition:'all 0.2s',width:'100%'}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=T.gold;e.currentTarget.style.background=T.bg2;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=T.subtle;e.currentTarget.style.background=T.bg1;}}>
                <div style={{width:40,height:40,borderRadius:10,background:T.bg2,border:'1px solid '+T.subtle,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>
                  {a.loading?<Spin/>:a.icon}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:2}}>{a.label}</div>
                  <div style={{fontSize:11,color:T.muted}}>{a.desc}</div>
                </div>
                <div style={{fontSize:16,color:T.muted,flexShrink:0}}>›</div>
              </button>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

function COModal({onClose,onSave,session}) {
  const T = useTheme();
  const cid = session?.companyId || window._grokCompanyId || "local";
  const [form,setForm]=useState({project:(PROJECTS[0]||{}).name||"",desc:"",amount:"",trigger:"Hidden condition",scope_original:"",scope_change:""});
  const f=(k,v)=>setForm(p=>({...p,[k]:v}));

  // Get matching CO template if uploaded
  const getCoTemplate = () => {
    try {
      const templates = JSON.parse(localStorage.getItem("grok_templates_"+cid)||"[]");
      return templates.find(t=>t.cat==="co"&&(t.data||t.link));
    } catch { return null; }
  };

  const generateFromTemplate = () => {
    if(!form.desc||!form.amount){alert("Add description and amount first.");return;}
    const tpl = getCoTemplate();
    const coNum = `CO-${String(COS.length+1).padStart(3,"0")}`;
    const today = new Date().toLocaleDateString();
    if(tpl?.data){
      const a=document.createElement("a"); a.href=tpl.data; a.download=tpl.fileName||"CO-Template.pdf";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } else if(tpl?.link){
      window.open(tpl.link,"_blank");
    } else {
      const w = window.open("","_blank");
      const cs = (() => { try { return JSON.parse(localStorage.getItem("grok_company_settings_"+cid)||"{}"); } catch { return {}; }})();
      const coName = cs.company_name || session?.companyName || "Your Company";
      w.document.write(`<!DOCTYPE html><html><head><title>${coNum}</title>
        <style>body{font-family:Arial,sans-serif;max-width:700px;margin:40px auto;padding:20px;color:#1a1a1a;}
        h1{font-size:20px;} .header{display:flex;justify-content:space-between;margin-bottom:24px;}
        .co-num{font-size:28px;font-weight:900;color:#D4A843;} table{width:100%;border-collapse:collapse;margin:16px 0;}
        td{padding:8px 12px;border:1px solid #ddd;font-size:13px;} .label{background:#f8f8f8;font-weight:700;width:35%;}
        .sig{margin-top:40px;display:grid;grid-template-columns:1fr 1fr;gap:40px;}
        .sig-line{border-top:1px solid #333;margin-top:40px;padding-top:6px;font-size:11px;color:#666;}
        .warn{background:#fff3cd;border:1px solid #ffc107;border-radius:6px;padding:10px 14px;font-size:12px;margin:16px 0;}
        </style></head><body>
        <div class="header"><div><div class="co-num">${coNum}</div><div style="font-size:13px;color:#666">Change Order</div></div>
        <div style="text-align:right"><div style="font-size:15px;font-weight:700">${coName}</div>
        <div style="font-size:12px;color:#666">${cs.cslb||""}</div>
        <div style="font-size:12px;color:#666">${cs.phone||""}</div></div></div>
        <table><tr><td class="label">Project</td><td>${form.project}</td></tr>
        <tr><td class="label">Date</td><td>${today}</td></tr>
        <tr><td class="label">Reason</td><td>${form.trigger}</td></tr>
        <tr><td class="label">Description</td><td>${form.desc}</td></tr>
        <tr><td class="label">Amount</td><td style="font-size:18px;font-weight:700;color:#D4A843">$${parseFloat(form.amount||0).toLocaleString()}</td></tr>
        ${form.scope_original?`<tr><td class="label">Original Scope</td><td>${form.scope_original}</td></tr>`:""}
        ${form.scope_change?`<tr><td class="label">Changed Scope</td><td>${form.scope_change}</td></tr>`:""}
        </table>
        <div class="warn">⚠️ No work on changed scope until signed by both parties.</div>
        <div class="sig">
          <div><div class="sig-line">GC Signature · Date</div><div style="margin-top:4px;font-size:12px">${coName}</div></div>
          <div><div class="sig-line">Owner / Client Signature · Date</div></div>
        </div>
        <script>setTimeout(()=>window.print(),400)</script></body></html>`);
      w.document.close();
    }
    // Save to project docs folder
    try {
      const proj = (PROJECTS||[]).find(p=>p.name===form.project||p.name.startsWith(form.project));
      if(proj){
        const docsKey = "grok_proj_docs_"+cid+"_"+proj.id;
        const docs = JSON.parse(localStorage.getItem(docsKey)||"[]");
        const doc = {id:"doc_"+Date.now(),name:`${coNum} — ${form.desc?.slice(0,40)} — ${today}`,type:"Change Order",folder:"co",date:today,generated:true,notes:`$${parseFloat(form.amount||0).toLocaleString()} · ${form.trigger}`};
        localStorage.setItem(docsKey, JSON.stringify([doc,...docs]));
      }
    } catch(e){}
  };
  return (
    <Modal title="New Change Order" onClose={onClose} wide>
      <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
        <div>
          <Select label="Project" value={form.project} onChange={v=>f("project",v)} options={(PROJECTS||[]).map(p=>({value:p.name,label:(p.name||"").split(" — ")[0]}))}/>
          <Field label="Description of Changed Work" value={form.desc} onChange={v=>f("desc",v)} placeholder="Describe what was found and what needs to be done..." rows={3}/>
          <Select label="Reason for Change Order" value={form.trigger} onChange={v=>f("trigger",v)} options={["Hidden condition discovered during demo","Client-directed scope change","Code compliance requirement","Unforeseen structural/plumbing/electrical","Design change / RFI resolution","Material substitution","Other"]}/>
          <Field label="Change Order Amount ($)" value={form.amount} onChange={v=>f("amount",v)} placeholder="e.g. 3500" type="number"/>
        </div>
        <div>
          <Field label="Original Contract Scope (what you agreed to)" value={form.scope_original} onChange={v=>f("scope_original",v)} placeholder="e.g. Remove and replace kitchen cabinets only — no plumbing relocation..." rows={4}/>
          <Field label="Changed / Added Scope (this CO)" value={form.scope_change} onChange={v=>f("scope_change",v)} placeholder="e.g. Remove water-damaged drywall (2 sheets), treat mold, replace drywall, tape, mud..." rows={4}/>
          <div style={{background:T.redBg,borderLeft:`3px solid ${T.red}`,borderRadius:6,padding:"10px 12px"}}>
            <div style={{fontSize:11,fontWeight:700,color:T.red,marginBottom:3}}>Stop Work Rule</div>
            <div style={{fontSize:11,color:T.muted}}>No work on changed scope until this CO is signed by both parties.</div>
          </div>
        </div>
      </div>
      <div style={{display:"flex",gap:10,marginTop:20,justifyContent:"flex-end"}}>
        <Btn ghost onClick={onClose}>Cancel</Btn>
        <button onClick={generateFromTemplate}
          style={{padding:"9px 16px",borderRadius:9,border:`1px solid ${T.gold}`,background:T.goldBg,color:T.gold,fontSize:12,fontWeight:700,cursor:"pointer"}}>
          📄 Generate CO Document
        </button>
        <Btn primary onClick={()=>{
          if(!form.desc||!form.amount){alert("Add description and amount.");return;}
          onSave({id:Date.now(),num:`CO-${String(COS.length+1).padStart(3,"0")}`,project:form.project.split(" — ")[0],desc:form.desc,amount:parseFloat(form.amount)||0,status:"pending",date:new Date().toLocaleDateString(),days:0,comments:[]});
          onClose();
        }}>Save Change Order</Btn>
      </div>
    </Modal>
  );
}

function RFIModal({onClose,onSave,session}) {
  const T = useTheme();
  const cid = session?.companyId || window._grokCompanyId || "local";
  const [form,setForm]=useState({project:(PROJECTS[0]||{}).name||"",subject:"",drawing:"",description:"",groks_understanding:"",clarification:"",priority:"routine",schedule_impact:false,cost_impact:false});
  const f=(k,v)=>setForm(p=>({...p,[k]:v}));

  const generateRFI = () => {
    if(!form.subject){alert("Add RFI subject first.");return;}
    try {
      const templates = JSON.parse(localStorage.getItem("grok_templates_"+cid)||"[]");
      const tpl = templates.find(t=>t.cat==="rfi"&&t.data);
      if(tpl){
        const a=document.createElement("a"); a.href=tpl.data; a.download=tpl.fileName||"RFI-Template.pdf";
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        return;
      }
    } catch{}
    // Generate basic RFI document
    const rfiNum = `RFI-${String(RFIS.length+1).padStart(3,"0")}`;
    const cs = (() => { try { return JSON.parse(localStorage.getItem("grok_company_settings_"+cid)||"{}"); } catch { return {}; }})();
    const coName = cs.company_name || session?.companyName || "Your Company";
    const w = window.open("","_blank");
    w.document.write(`<!DOCTYPE html><html><head><title>${rfiNum}</title>
      <style>body{font-family:Arial,sans-serif;max-width:700px;margin:40px auto;padding:20px;}
      h1{font-size:20px;} .rfi-num{font-size:28px;font-weight:900;color:#8b5cf6;}
      table{width:100%;border-collapse:collapse;margin:16px 0;}
      td{padding:8px 12px;border:1px solid #ddd;font-size:13px;} .label{background:#f8f8f8;font-weight:700;width:35%;}
      .priority{display:inline-block;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700;
        background:${form.priority==="critical"?"#fee2e2":form.priority==="urgent"?"#fff3cd":"#f0fdf4"};
        color:${form.priority==="critical"?"#dc2626":form.priority==="urgent"?"#92400e":"#166534"};}
      .response{margin-top:24px;border:1px solid #ddd;border-radius:6px;padding:16px;}
      .sig-line{border-top:1px solid #333;margin-top:40px;padding-top:6px;font-size:11px;color:#666;}
      </style></head><body>
      <div style="display:flex;justify-content:space-between;margin-bottom:24px">
        <div><div class="rfi-num">${rfiNum}</div><div style="font-size:13px;color:#666">Request for Information</div></div>
        <div style="text-align:right"><div style="font-size:15px;font-weight:700">${coName}</div>
        <div style="font-size:12px;color:#666">${new Date().toLocaleDateString()}</div></div>
      </div>
      <table>
        <tr><td class="label">Project</td><td>${form.project}</td></tr>
        <tr><td class="label">Subject</td><td>${form.subject}</td></tr>
        <tr><td class="label">Priority</td><td><span class="priority">${form.priority.toUpperCase()}</span></td></tr>
        ${form.drawing?`<tr><td class="label">Drawing / Spec Ref</td><td>${form.drawing}</td></tr>`:""}
        <tr><td class="label">Description</td><td>${form.description}</td></tr>
        ${form.groks_understanding?`<tr><td class="label">GC's Understanding</td><td>${form.groks_understanding}</td></tr>`:""}
        <tr><td class="label">Clarification Needed</td><td>${form.clarification}</td></tr>
        <tr><td class="label">Work on Hold</td><td>${form.schedule_impact?"YES — Work stopped pending response":"No"}</td></tr>
        <tr><td class="label">Cost Impact</td><td>${form.cost_impact?"YES — CO may follow":"No"}</td></tr>
      </table>
      <div class="response"><strong>RESPONSE REQUIRED BY:</strong> ${new Date(Date.now()+5*864e5).toLocaleDateString()}<br/><br/>
      <div style="height:80px;border-bottom:1px solid #ddd;margin-top:8px"></div>
      <div class="sig-line">Architect / Engineer Response · Date</div></div>
      <script>setTimeout(()=>window.print(),400)</script></body></html>`);
    w.document.close();
  };
  return (
    <Modal title="New RFI — Request for Information" onClose={onClose} wide>
      <div style={{background:T.blueBg,borderLeft:`3px solid ${T.blue}`,borderRadius:6,padding:"10px 12px",marginBottom:16}}>
        <div style={{fontSize:11,color:T.text}}>Submit an RFI when something in the plans or specs is unclear, conflicting, or missing. <strong>Never proceed on a verbal answer.</strong> Get the response in writing.</div>
      </div>
      <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
        <div>
          <Select label="Project" value={form.project} onChange={v=>f("project",v)} options={(PROJECTS||[]).map(p=>({value:p.name,label:(p.name||"").split(" — ")[0]}))}/>
          <Field label="RFI Subject" value={form.subject} onChange={v=>f("subject",v)} placeholder="e.g. Drain elevation conflict — Unit 4B plan vs field"/>
          <Field label="Drawing / Spec Reference" value={form.drawing} onChange={v=>f("drawing",v)} placeholder="e.g. A3.02, Detail 5 / Spec Section 22 00 00"/>
          <Select label="Priority" value={form.priority} onChange={v=>f("priority",v)} options={["routine","urgent","critical"]}/>
        </div>
        <div>
          <Field label="Description of Issue / Conflict" value={form.description} onChange={v=>f("description",v)} placeholder="What is unclear, conflicting, or missing?" rows={3}/>
          <Field label="Grok's Understanding (what we believe is correct)" value={form.groks_understanding} onChange={v=>f("groks_understanding",v)} placeholder="What Grok interprets the plans to mean..." rows={2}/>
          <Field label="Specific Clarification Needed" value={form.clarification} onChange={v=>f("clarification",v)} placeholder="Exactly what direction is needed to proceed..." rows={2}/>
        </div>
      </div>
      <div style={{display:"flex",gap:20,marginTop:8,marginBottom:16}}>
        <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:12,color:T.text}}>
          <input type="checkbox" checked={form.schedule_impact} onChange={e=>f("schedule_impact",e.target.checked)}/> Work on hold pending response
        </label>
        <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:12,color:T.text}}>
          <input type="checkbox" checked={form.cost_impact} onChange={e=>f("cost_impact",e.target.checked)}/> Potential cost impact (CO may follow)
        </label>
      </div>
      <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
        <Btn ghost onClick={onClose}>Cancel</Btn>
        <button onClick={generateRFI}
          style={{padding:"9px 16px",borderRadius:9,border:`1px solid ${T.purple}`,background:T.purple+"15",color:T.purple,fontSize:12,fontWeight:700,cursor:"pointer"}}>
          📄 Generate RFI Document
        </button>
        <Btn primary onClick={()=>{
          if(!form.subject){alert("Add RFI subject.");return;}
          onSave({id:Date.now(),num:`RFI-${String(RFIS.length+1).padStart(3,"0")}`,project:form.project.split(" — ")[0],subject:form.subject,priority:form.priority,status:"open",submitted:new Date().toLocaleDateString(),due:new Date(Date.now()+5*864e5).toLocaleDateString(),comments:[]});
          onClose();
        }}>Submit RFI</Btn>
      </div>
    </Modal>
  );
}


function LienWaiverModal({onClose, session}) {
  const T = useTheme();
  const cid = session?.companyId || window._grokCompanyId || "local";
  const projects = (() => { try { return cleanProjects(JSON.parse(localStorage.getItem("grok_projects_"+cid)||"[]")); } catch { return cleanProjects(PROJECTS||[]); }})();
  const cs = (() => { try { return JSON.parse(localStorage.getItem("grok_company_settings_"+cid)||"{}"); } catch { return {}; }})();
  const coName = cs.company_name || session?.companyName || "Grok Construction Group Inc";
  const cslb = cs.cslb || "CSLB #1152750";
  const coAddr = cs.address || "2201 Bridgepointe Pkwy, Suite F125, San Mateo, CA 94404";

  const [step, setStep] = useState(1); // 1=type, 2=details, 3=preview
  const [form, setForm] = useState({
    project: (projects[0]||{}).name||"",
    owner: "",
    paymentType: "", // "progress" | "final"
    waiverType: "",  // "conditional" | "unconditional"
    amount: "",
    throughDate: new Date().toLocaleDateString(),
    exceptions: "None",
  });
  const f = (k,v) => setForm(p=>({...p,[k]:v}));

  const formNum = form.paymentType==="progress"
    ? (form.waiverType==="conditional" ? "§8132" : "§8134")
    : (form.waiverType==="conditional" ? "§8136" : "§8138");

  const formTitle = form.paymentType==="progress"
    ? (form.waiverType==="conditional" ? "Conditional Waiver and Release on Progress Payment" : "Unconditional Waiver and Release on Progress Payment")
    : (form.waiverType==="conditional" ? "Conditional Waiver and Release on Final Payment" : "Unconditional Waiver and Release on Final Payment");

  const isUnconditional = form.waiverType === "unconditional";
  const isFinal = form.paymentType === "final";

  const generateWaiver = () => {
    if(!form.project||!form.owner||!form.amount){alert("Please fill in all fields.");return;}
    const w = window.open("","_blank");
    const today = new Date().toLocaleDateString();
    w.document.write(`<!DOCTYPE html><html><head><title>${formTitle}</title>
    <style>
      body{font-family:Arial,sans-serif;max-width:680px;margin:40px auto;padding:24px;color:#1a1a1a;font-size:13px;}
      .banner{background:#0F1623;padding:28px 32px;margin-bottom:0;}
      .brand{font-size:32px;font-weight:900;color:#D4A843;margin-bottom:4px;}
      .brand-sub{font-size:14px;color:#fff;font-weight:700;}
      .gold-bar{height:5px;background:#D4A843;margin-bottom:24px;}
      .form-num{font-size:11px;color:#999;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;}
      .form-title{font-size:18px;font-weight:700;color:#0F1623;margin-bottom:4px;}
      .code{font-size:12px;color:#888;font-style:italic;margin-bottom:20px;}
      .warn{border-left:4px solid ${isUnconditional?"#C0392B":"#1A3C8F"};background:${isUnconditional?"#FDF2F2":"#EEF4FB"};padding:10px 14px;margin:16px 0;font-size:12px;font-weight:700;color:${isUnconditional?"#7B1A1A":"#1A3C8F"};}
      .body-text{line-height:1.8;color:#1a1a1a;margin:16px 0;font-size:13px;}
      .field-label{font-size:10px;font-weight:700;color:#999;text-transform:uppercase;margin-bottom:3px;margin-top:16px;}
      .field-val{border-bottom:1px solid #ccc;padding:4px 0;min-height:20px;font-size:13px;}
      .claimant-box{background:#f9f9f9;border-top:1px solid #e0e0e0;padding:16px;margin:24px 0;}
      .sig-grid{display:grid;grid-template-columns:1fr 1fr;gap:32px;margin-top:40px;}
      .sig-line{border-top:2px solid #0F1623;margin-top:48px;padding-top:6px;font-size:11px;color:#666;}
      @media print{body{margin:0;padding:20px;}}
    </style></head><body>
    <div class="banner">
      <div class="brand">GROK</div>
      <div class="brand-sub">Construction Group Inc</div>
    </div>
    <div class="gold-bar"></div>
    <div class="form-num">${isFinal?"Form 3 of 4 — Final Payment":"Form 1 of 4 — Progress Payment"}${isUnconditional?" (Unconditional)":""}</div>
    <div class="form-title">${formTitle}</div>
    <div class="code">California Civil Code ${formNum}</div>
    <div class="warn">${isUnconditional
      ? "⚠️ WARNING: This waiver is effective upon signing regardless of payment status. DO NOT sign unless payment has actually been received and cleared."
      : "NOTICE: This document waives and releases lien and payment bond rights only upon receipt of payment. Read it before signing."
    }</div>
    ${isUnconditional ? `
      <p class="body-text">The undersigned has been paid and has received a ${isFinal?"final":"progress"} payment in the sum of <strong>$${parseFloat(form.amount||0).toLocaleString()}</strong> for labor, services, equipment, or material furnished to _________________________ on the job of the Owner identified below.</p>
    ` : `
      <p class="body-text">Upon receipt by the undersigned of a check from _________________________ in the sum of <strong>$${parseFloat(form.amount||0).toLocaleString()}</strong>, payable to <strong>${coName}</strong>, and when the check has been properly endorsed and has been paid by the bank upon which it is drawn, this document shall become effective to release any mechanic's lien, stop payment notice, or bond right the undersigned has on the job of the Owner identified below.</p>
    `}
    <div class="field-label">Job Location / Property Address</div>
    <div class="field-val">${form.project}</div>
    <div class="field-label">Owner</div>
    <div class="field-val">${form.owner}</div>
    ${!isFinal ? `<div class="field-label">Through Date (work completed through)</div><div class="field-val">${form.throughDate}</div>` : `<div class="field-label">Amount of ${isFinal?"Final":"Progress"} Payment</div><div class="field-val">$${parseFloat(form.amount||0).toLocaleString()}</div>`}
    <p class="body-text">This release covers a ${isFinal?"final":"progress"} payment for all labor, services, equipment, or material furnished${!isFinal?` through the Through Date identified above and does not cover any retention or items furnished after that date`:` on the above job`}. ${isUnconditional&&!isFinal?"Rights based upon work performed or items furnished under a written change order which has been fully executed by the parties prior to the date of this release are covered by this release unless specifically reserved below.":""}</p>
    ${isUnconditional ? `<div class="field-label">Exceptions, if any (write "None" if no exceptions)</div><div class="field-val">${form.exceptions||"None"}</div>` : ""}
    <div class="claimant-box">
      <strong>Claimant:</strong> ${coName}<br>
      <strong>License:</strong> ${cslb}<br>
      <strong>Address:</strong> ${coAddr}
    </div>
    <div class="sig-grid">
      <div>
        <div class="sig-line">Authorized Signature</div>
        <div style="font-size:11px;margin-top:4px;color:#666">${coName}</div>
      </div>
      <div>
        <div class="sig-line">Printed Name &amp; Title</div>
      </div>
      <div style="margin-top:20px">
        <div class="sig-line">Date</div>
      </div>
    </div>
    <script>setTimeout(()=>window.print(),500)</script>
    </body></html>`);
    w.document.close();
  };

  return (
    <Modal title="Generate Lien Waiver" onClose={onClose}>
      {/* Step 1 — Select type */}
      {step===1 && (
        <div>
          <div style={{fontSize:13,color:T.muted,marginBottom:20}}>California requires 4 specific lien waiver forms. Answer two questions and GrokFieldOps will generate the correct one.</div>

          <div style={{marginBottom:20}}>
            <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Is this a progress payment or final payment?</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {[["progress","📋 Progress Payment","Mid-project draw — not the last one"],["final","🏁 Final Payment","Last payment — project complete"]].map(([val,label,sub])=>(
                <button key={val} onClick={()=>f("paymentType",val)}
                  style={{padding:"14px 16px",borderRadius:10,border:`2px solid ${form.paymentType===val?T.gold:T.border}`,background:form.paymentType===val?T.goldBg:T.bg2,cursor:"pointer",textAlign:"left"}}>
                  <div style={{fontSize:14,fontWeight:700,color:form.paymentType===val?T.gold:T.text}}>{label}</div>
                  <div style={{fontSize:11,color:T.muted,marginTop:3}}>{sub}</div>
                </button>
              ))}
            </div>
          </div>

          <div style={{marginBottom:20}}>
            <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Has payment actually cleared your bank?</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {[["conditional","⏳ Not Yet Cleared","Safe to send with application"],["unconditional","✅ Payment Cleared","Confirms payment received"]].map(([val,label,sub])=>(
                <button key={val} onClick={()=>f("waiverType",val)}
                  style={{padding:"14px 16px",borderRadius:10,border:`2px solid ${form.waiverType===val?T.gold:T.border}`,background:form.waiverType===val?T.goldBg:T.bg2,cursor:"pointer",textAlign:"left"}}>
                  <div style={{fontSize:14,fontWeight:700,color:form.waiverType===val?T.gold:T.text}}>{label}</div>
                  <div style={{fontSize:11,color:T.muted,marginTop:3}}>{sub}</div>
                </button>
              ))}
            </div>
          </div>

          {form.paymentType&&form.waiverType&&(
            <div style={{background:T.bg2,borderRadius:8,padding:"12px 14px",marginBottom:16,border:`1px solid ${T.border}`}}>
              <div style={{fontSize:11,color:T.muted,marginBottom:4}}>This will generate</div>
              <div style={{fontSize:14,fontWeight:700,color:T.text}}>{formTitle}</div>
              <div style={{fontSize:11,color:T.gold,marginTop:2}}>California Civil Code {formNum}</div>
            </div>
          )}

          {isUnconditional&&(
            <div style={{background:"#FDF2F2",border:"1px solid #C0392B",borderRadius:8,padding:"10px 14px",marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:"#7B1A1A"}}>⚠️ Unconditional Waiver Warning</div>
              <div style={{fontSize:11,color:"#7B1A1A",marginTop:4}}>This waiver is legally binding upon signing, regardless of whether payment has cleared. Only sign after confirming funds in your account.</div>
            </div>
          )}

          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:8}}>
            <Btn ghost onClick={onClose}>Cancel</Btn>
            <Btn primary onClick={()=>setStep(2)} disabled={!form.paymentType||!form.waiverType}>Continue →</Btn>
          </div>
        </div>
      )}

      {/* Step 2 — Fill details */}
      {step===2 && (
        <div>
          <div style={{fontSize:12,color:T.muted,marginBottom:16,padding:"8px 12px",background:T.bg2,borderRadius:8,borderLeft:`3px solid ${T.gold}`}}>
            <strong style={{color:T.gold}}>{formTitle}</strong><br/>
            <span style={{fontSize:11}}>{formNum}</span>
          </div>

          <Select label="Project" value={form.project} onChange={v=>f("project",v)}
            options={(projects||[]).map(p=>({value:p.name,label:(p.name||"").split(" — ")[0]}))}/>
          <Field label="Owner Name" value={form.owner} onChange={v=>f("owner",v)} placeholder="Property owner or entity name"/>
          <Field label={isFinal?"Final Payment Amount ($)":"Payment Amount This Draw ($)"} value={form.amount} onChange={v=>f("amount",v)} placeholder="e.g. 45000" type="number"/>
          {!isFinal&&(
            <Field label="Through Date (work completed through)" value={form.throughDate} onChange={v=>f("throughDate",v)} placeholder="e.g. 05/21/2026"/>
          )}
          {isUnconditional&&(
            <Field label="Exceptions (write None if no exceptions)" value={form.exceptions} onChange={v=>f("exceptions",v)} placeholder="None"/>
          )}

          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:16}}>
            <Btn ghost onClick={()=>setStep(1)}>← Back</Btn>
            <button onClick={generateWaiver}
              style={{padding:"9px 18px",borderRadius:9,border:`1px solid ${T.gold}`,background:T.goldBg,color:T.gold,fontSize:13,fontWeight:700,cursor:"pointer"}}>
              📄 Generate &amp; Print
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function ChangeOrdersView({setCOModal, session}) {
  const cid = session?.companyId || window._grokCompanyId || "local";
  const CO_KEY = "grok_cos_global_" + cid;
  const [cos, setCos] = useState(()=>{
    try {
      const stored = localStorage.getItem(CO_KEY);
      if (stored) return JSON.parse(stored);
      // First time - seed from global then save
      const initial = COS||[];
      if (initial.length) try{localStorage.setItem(CO_KEY,JSON.stringify(initial));}catch(e){}
      return initial;
    } catch { return []; }
  });
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");
  const [confirmDeleteCO, setConfirmDeleteCO] = useState(false);
  const fileRef = useRef();

  const save = (updated) => {
    setCos(updated);
    try { COS.splice(0, COS.length, ...updated); } catch(e) {}
    try { localStorage.setItem(CO_KEY, JSON.stringify(updated)); } catch(e) {}
    try { window._grokBroadcast?.("data_updated"); } catch(e) {}
    SyncDB.saveCOs(updated).catch(()=>{});
  };

  const selectedCO = cos.find(c => String(c.id) === String(selected));
  const filtered = filter === "all" ? cos : cos.filter(c => c.status === filter);
  const pending = cos.filter(c => c.status === "pending");
  const approved = cos.filter(c => c.status === "approved");

  const addComment = (coId) => {
    if (!comment.trim()) return;
    const updated = cos.map(c => String(c.id) === String(coId) ? {
      ...c,
      comments: [...(c.comments||[]), {
        text: comment.trim(),
        author: session?.ownerName || session?.email || "You",
        date: new Date().toLocaleDateString(),
        id: Date.now()
      }]
    } : c);
    save(updated);
    setComment("");
  };

  const deleteCO = (id) => { DELETED_IDS.add("cos", id); save(cos.filter(c => String(c.id) !== String(id))); setSelected(null); SyncDB.deleteById("change_orders", id).catch(()=>{}); };

  const updateStatus = (id, status) => save(cos.map(c => String(c.id) === String(id) ? {...c, status} : c));
  const deleteComment = (coId, cid2) => save(cos.map(c => String(c.id) === String(coId) ? {...c, comments:(c.comments||[]).filter(cm=>cm.id!==cid2)} : c));

  const handleFileAttach = (file, coId) => {
    if (file.size > 5*1024*1024) { alert("Max 5MB"); return; }
    const reader = new FileReader();
    reader.onload = ev => {
      save(cos.map(c => String(c.id) === String(coId) ? {
        ...c,
        attachments: [...(c.attachments||[]), {name:file.name, size:(file.size/1024).toFixed(0)+"KB", data:ev.target.result, id:Date.now()}]
      } : c));
    };
    reader.readAsDataURL(file);
  };

  return (
    <div style={{padding:24, display:"grid", gridTemplateColumns: selected ? "1fr 420px" : "1fr", gap:16, alignItems:"start"}}>
      {/* LEFT — CO LIST */}
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div>
            <div style={{fontSize:20,fontWeight:800,color:T.text}}>Change Orders</div>
            <div style={{fontSize:12,color:T.muted,marginTop:3}}>{pending.length} pending · ${pending.reduce((s,c)=>s+(c.amount||0),0).toLocaleString()} outstanding</div>
          </div>
          <Btn primary onClick={()=>setCOModal&&setCOModal(true)}><Ic d={ICONS.plus} size={13}/>New Change Order</Btn>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
          <StatCard label="Pending Approval" value={pending.length} color={T.red} sub={`$${pending.reduce((s,c)=>s+(c.amount||0),0).toLocaleString()} outstanding`}/>
          <StatCard label="Approved" value={approved.length} color={T.green} sub={`$${approved.reduce((s,c)=>s+(c.amount||0),0).toLocaleString()} added`}/>
          <StatCard label="Longest Unsigned" value={`${Math.max(...pending.map(c=>c.days||0),0)} days`} color={T.amber} sub={pending.length>0?"Needs attention":"All clear"}/>
        </div>

        <div style={{display:"flex",gap:6,marginBottom:12}}>
          {["all","pending","approved","void"].map(f=>(
            <button key={f} onClick={()=>setFilter(f)} style={{padding:"5px 14px",borderRadius:20,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:filter===f?T.gold:T.bg2,color:filter===f?T.bg0:T.muted,textTransform:"capitalize"}}>{f}</button>
          ))}
        </div>

        <Card style={{padding:0,overflow:"hidden"}}>
          {filtered.length === 0 ? (
            <div style={{padding:40,textAlign:"center",color:T.muted,fontSize:13}}>No change orders found. Create one to get started.</div>
          ) : filtered.map((co,i)=>(
            <div key={co.id}
              onClick={()=>setSelected(selected===co.id?null:co.id)}
              style={{display:"grid",gridTemplateColumns:"72px 1fr 100px 85px 90px",gap:0,padding:"10px 14px",borderBottom:`1px solid ${T.subtle}`,cursor:"pointer",background:selected===co.id?T.goldBg:i%2===0?"transparent":T.bg2+"40",transition:"background 0.15s"}}
              onMouseEnter={e=>e.currentTarget.style.background=T.goldBg}
              onMouseLeave={e=>e.currentTarget.style.background=selected===co.id?T.goldBg:i%2===0?"transparent":T.bg2+"40"}>
              <div style={{fontFamily:"monospace",fontSize:10,color:T.gold,fontWeight:700,paddingTop:2}}>{co.num}</div>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:T.text,marginBottom:2}}>{co.desc}</div>
                <div style={{fontSize:10,color:T.muted}}>{co.project} {co.date?"· "+co.date:""}</div>
              </div>
              <div style={{fontSize:13,fontWeight:700,color:T.gold,display:"flex",alignItems:"center"}}>${(co.amount||0).toLocaleString()}</div>
              <div style={{display:"flex",alignItems:"center"}}><Badge label={co.status||"pending"} color={co.status==="approved"?T.green:co.status==="void"?T.muted:T.amber}/></div>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                {(co.comments||[]).length>0&&<span style={{fontSize:10,color:T.muted}}>💬{co.comments.length}</span>}
                {(co.attachments||[]).length>0&&<span style={{fontSize:10,color:T.muted}}>📎{co.attachments.length}</span>}
              </div>
            </div>
          ))}
        </Card>

        <div style={{marginTop:14,background:T.redBg,border:`1px solid ${T.red}30`,borderLeft:`3px solid ${T.red}`,borderRadius:8,padding:"12px 16px",display:"flex",gap:12,alignItems:"flex-start"}}>
          <Ic d={ICONS.alert} size={20} style={{color:T.red,flexShrink:0,marginTop:1}}/>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:T.red,marginBottom:3}}>Stop Work Rule — Never Proceed Without a Signed CO</div>
            <div style={{fontSize:12,color:T.muted}}>Anything outside original scope — stop, photograph, notify owner within 2 hours, submit a Change Order before touching it.</div>
          </div>
        </div>
      </div>

      {/* RIGHT — CO DETAIL PANEL */}
      {selectedCO && (
        <div style={{position:"sticky",top:80}}>
          <Card style={{padding:0,overflow:"hidden"}}>
            <div style={{padding:"14px 16px",borderBottom:`1px solid ${T.subtle}`,display:"flex",justifyContent:"space-between",alignItems:"center",background:T.bg2}}>
              <div>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.1em"}}>{selectedCO.num} · ${(selectedCO.amount||0).toLocaleString()}</div>
                <div style={{fontSize:13,fontWeight:700,color:T.text,marginTop:2}}>{selectedCO.desc}</div>
              </div>
              <button onClick={()=>setSelected(null)} style={{background:"none",border:"none",color:T.muted,fontSize:18,cursor:"pointer",lineHeight:1}}>×</button>
            </div>

            <div style={{padding:"14px 16px",maxHeight:600,overflowY:"auto"}}>

              {/* Status */}
              <div style={{marginBottom:14}}>
                <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:T.muted,marginBottom:6}}>Status</div>
                <div style={{display:"flex",gap:6}}>
                  {["pending","approved","void"].map(s=>(
                    <button key={s} onClick={()=>updateStatus(selectedCO.id,s)}
                      style={{padding:"4px 12px",borderRadius:20,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",
                        background:selectedCO.status===s?(s==="approved"?T.green:s==="void"?T.muted:T.amber):T.bg2,
                        color:selectedCO.status===s?"white":T.muted,textTransform:"capitalize",transition:"all 0.15s"}}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Details */}
              <div style={{background:T.bg2,borderRadius:8,padding:"10px 12px",marginBottom:14,fontSize:11,color:T.muted,lineHeight:1.7}}>
                <div><strong style={{color:T.text}}>Project:</strong> {selectedCO.project||"—"}</div>
                <div><strong style={{color:T.text}}>Date:</strong> {selectedCO.date||"—"}</div>
                <div><strong style={{color:T.text}}>Amount:</strong> ${(selectedCO.amount||0).toLocaleString()}</div>
                {selectedCO.days>0&&<div><strong style={{color:T.text}}>Days Unsigned:</strong> <span style={{color:T.amber}}>{selectedCO.days} days</span></div>}
              </div>

              {/* Attachments */}
              <div style={{marginBottom:14}}>
                <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:T.muted,marginBottom:6}}>Attachments</div>
                {(selectedCO.attachments||[]).map(att=>(
                  <div key={att.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:T.bg2,borderRadius:7,marginBottom:5,border:`1px solid ${T.subtle}`}}>
                    <span style={{fontSize:14}}>📎</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:11,fontWeight:600,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{att.name}</div>
                      <div style={{fontSize:9,color:T.muted}}>{att.size}</div>
                    </div>
                    <button onClick={()=>{const a=document.createElement("a");a.href=att.data;a.download=att.name;a.click();}} style={{background:"none",border:"none",color:T.blue,fontSize:10,cursor:"pointer",fontWeight:600}}>↓</button>
                    <button onClick={()=>save(cos.map(c=>String(c.id)===String(selectedCO.id)?{...c,attachments:(c.attachments||[]).filter(a=>a.id!==att.id)}:c))} style={{background:"none",border:"none",color:T.red,fontSize:12,cursor:"pointer"}}>×</button>
                  </div>
                ))}
                <input ref={fileRef} type="file" style={{display:"none"}} onChange={e=>{if(e.target.files[0])handleFileAttach(e.target.files[0],selectedCO.id);}}/>
                <button onClick={()=>fileRef.current?.click()} style={{fontSize:11,color:T.blue,background:"none",border:`1px dashed ${T.subtle}`,borderRadius:7,padding:"6px 12px",cursor:"pointer",width:"100%",marginTop:4}}>
                  + Attach File
                </button>
              </div>

              {/* Comments */}
              <div style={{marginBottom:14}}>
                <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:T.muted,marginBottom:8}}>Comments</div>
                {(selectedCO.comments||[]).length===0&&<div style={{fontSize:11,color:T.muted,fontStyle:"italic",padding:"8px 0"}}>No comments yet.</div>}
                {(selectedCO.comments||[]).map(c=>(
                  <div key={c.id} style={{background:T.bg2,borderRadius:8,padding:"9px 11px",marginBottom:7,border:`1px solid ${T.subtle}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.text}}>{c.author}</div>
                      <div style={{display:"flex",gap:8,alignItems:"center"}}>
                        <span style={{fontSize:9,color:T.muted}}>{c.date}</span>
                        <button onClick={()=>deleteComment(selectedCO.id,c.id)} style={{background:"none",border:"none",color:T.red,fontSize:11,cursor:"pointer"}}>×</button>
                      </div>
                    </div>
                    <div style={{fontSize:11,color:T.muted,lineHeight:1.6}}>{c.text}</div>
                  </div>
                ))}
                <textarea value={comment} onChange={e=>setComment(e.target.value)}
                  placeholder="Add a note or approval comment…"
                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit",resize:"vertical",minHeight:64,marginTop:4}}/>
                <Btn primary small onClick={()=>addComment(selectedCO.id)} style={{marginTop:6,width:"100%",justifyContent:"center"}}>
                  💬 Save Comment
                </Btn>
              </div>

              {/* Delete */}
              {/* Delete */}
              {!confirmDeleteCO ? (
                <button onClick={()=>setConfirmDeleteCO(true)}
                  style={{width:"100%",background:"none",border:`1px solid ${T.red}40`,borderRadius:8,padding:"8px",color:T.red,fontSize:11,cursor:"pointer",fontWeight:600,marginTop:4}}>
                  🗑 Delete Change Order
                </button>
              ) : (
                <div style={{marginTop:4,background:T.redBg,border:`1px solid ${T.red}30`,borderRadius:8,padding:"10px 12px"}}>
                  <div style={{fontSize:11,color:T.red,fontWeight:700,marginBottom:8}}>Delete this CO permanently?</div>
                  <div style={{display:"flex",gap:7}}>
                    <button onClick={()=>deleteCO(selectedCO.id)}
                      style={{flex:1,background:T.red,color:"white",border:"none",borderRadius:7,padding:"7px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                      Yes, Delete
                    </button>
                    <button onClick={()=>setConfirmDeleteCO(false)}
                      style={{flex:1,background:T.bg2,color:T.muted,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"7px",fontSize:11,cursor:"pointer"}}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function RFIsView({setRFIModal, proj, session}) {
  const cid = session?.companyId || window._grokCompanyId || "local";
  const RFI_KEY = "grok_rfis_global_" + cid; // unified key - no project suffix
  const [rfis, setRfis] = useState(()=>{
    try {
      const stored = localStorage.getItem(RFI_KEY);
      if (stored) return JSON.parse(stored);
      // First time - seed from global then save
      const initial = RFIS||[];
      if (initial.length) try{localStorage.setItem(RFI_KEY,JSON.stringify(initial));}catch(e){}
      return initial;
    } catch { return []; }
  });
  const [filter, setFilter] = useState(() => {
    try {
      const hint = localStorage.getItem("grok_rfi_init_filter");
      if (hint) { localStorage.removeItem("grok_rfi_init_filter"); return hint; }
    } catch {}
    return "all";
  });
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");
  const [editForm, setEditForm] = useState(null);
  const fileRef = useRef();

  const save = (updated) => {
    setRfis(updated);
    RFIS = updated;
    try { localStorage.setItem(RFI_KEY, JSON.stringify(updated)); } catch(e) {}
    try { localStorage.setItem("grok_rfis_global_"+cid, JSON.stringify(updated)); } catch(e) {}
    try { window._grokBroadcast?.("data_updated"); } catch(e) {}
    SyncDB.saveRFIs(updated).catch(()=>{});
  };

  const selectedRFI = rfis.find(r => String(r.id) === String(selected));
  const filtered = filter === "all" ? rfis : rfis.filter(r => r.status === filter);
  const open = rfis.filter(r => r.status !== "closed");

  const addComment = (rfiId) => {
    if (!comment.trim()) return;
    const updated = rfis.map(r => String(r.id) === String(rfiId) ? {
      ...r,
      comments: [...(r.comments||[]), {
        text: comment.trim(),
        author: session?.ownerName || session?.email || "You",
        date: new Date().toLocaleDateString(),
        id: Date.now()
      }]
    } : r);
    save(updated);
    setComment("");
    // Update selectedRFI display
    const updatedRFI = updated.find(r => String(r.id) === String(rfiId));
    if (updatedRFI) setSelected(updatedRFI.id);
  };

  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const deleteRFI = (id) => {
    DELETED_IDS.add("rfis", id);
    save(rfis.filter(r => String(r.id) !== String(id)));
    setSelected(null);
    setConfirmDelete(false);
    SyncDB.deleteById("rfis", id).catch(()=>{});
  };

  const updateStatus = (id, status) => {
    save(rfis.map(r => String(r.id) === String(id) ? {...r, status} : r));
  };

  const deleteComment = (rfiId, commentId) => {
    save(rfis.map(r => String(r.id) === String(rfiId) ? {...r, comments: (r.comments||[]).filter(c => c.id !== commentId)} : r));
  };

  const handleFileAttach = (file, rfiId) => {
    if (file.size > 5*1024*1024) { alert("Max 5MB"); return; }
    const reader = new FileReader();
    reader.onload = ev => {
      save(rfis.map(r => String(r.id) === String(rfiId) ? {
        ...r,
        attachments: [...(r.attachments||[]), {
          name: file.name,
          size: (file.size/1024).toFixed(0)+"KB",
          data: ev.target.result,
          id: Date.now()
        }]
      } : r));
    };
    reader.readAsDataURL(file);
  };

  const priorityColor = (p) => p==="critical"?T.red:p==="urgent"?T.amber:p==="high"?T.orange||T.amber:T.blue;
  const statusColor = (s) => s==="closed"?T.green:s==="pending"?T.amber:s==="pending_gc_approval"?T.gold:T.blue;
  const statusLabel = (s) => s==="pending_gc_approval"?"Pending GC":(s||"open");

  // Refresh rfis when an auto-drafted RFI lands from a blueprint flag.
  React.useEffect(() => {
    const reload = () => {
      try { setRfis(JSON.parse(localStorage.getItem(RFI_KEY)||"[]")); } catch {}
    };
    window.addEventListener("grok-rfis-updated", reload);
    return () => window.removeEventListener("grok-rfis-updated", reload);
  }, [RFI_KEY]);

  const updateField = (id, patch) => {
    save(rfis.map(r => String(r.id)===String(id) ? {...r, ...patch} : r));
  };

  // Approve & Send drafted RFI to the architect. Looks up architect email
  // from project.members first; if none, prompts inline. On success: sets
  // status to "open" and clears the pending_gc_approval flag.
  const [archEmailDraft, setArchEmailDraft] = React.useState("");
  const [sendingRfi, setSendingRfi] = React.useState(false);
  const [sendMsg, setSendMsg] = React.useState("");

  const findArchitectEmail = (rfi) => {
    try {
      const projs = JSON.parse(localStorage.getItem("grok_projects_"+cid)||"[]");
      const p = projs.find(pp => String(pp.id) === String(rfi.projectId));
      const m = p?.members?.find(mm => (mm.role||"").toLowerCase() === "architect");
      return m?.email || p?.architectEmail || "";
    } catch { return ""; }
  };

  const approveAndSendRFI = async (rfi) => {
    setSendMsg("");
    const onFile = findArchitectEmail(rfi);
    const recipient = (onFile || archEmailDraft || "").trim();
    if (!recipient) { setSendMsg("Enter the architect's email to send."); return; }
    setSendingRfi(true);
    try {
      const html =
        `<div style="font-family:Arial,sans-serif;max-width:640px;padding:24px;color:#1a1a1a;font-size:14px;line-height:1.7">` +
        `<div style="font-size:11px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:.1em;margin-bottom:4px">${rfi.num || "RFI"} · ${rfi.project || ""}</div>` +
        `<h2 style="margin:0 0 16px 0;font-size:18px;color:#1a1a1a">${rfi.subject}</h2>` +
        (rfi.drawingRef ? `<div style="background:#f6f5ef;border-left:3px solid #d4a843;padding:8px 12px;margin-bottom:14px;font-size:12px;color:#444"><strong>Drawing:</strong> ${rfi.drawingRef}</div>` : "") +
        `<div style="white-space:pre-wrap;margin-bottom:18px">${(rfi.description || rfi.desc || "").replace(/</g,"&lt;")}</div>` +
        (rfi.suggestedResolution ? `<div style="background:#f8f9fa;border:1px solid #e5e7eb;border-radius:8px;padding:12px 14px;margin-bottom:18px"><div style="font-size:11px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:.08em;margin-bottom:6px">Suggested Resolution (from GC)</div><div style="white-space:pre-wrap;font-size:13px;color:#333">${rfi.suggestedResolution.replace(/</g,"&lt;")}</div></div>` : "") +
        `<div style="margin-top:24px;padding-top:14px;border-top:1px solid #e5e7eb;font-size:11px;color:#666">Please respond to this RFI at your earliest convenience.</div>` +
        `</div>`;
      const r = await fetch("https://qnjlirkgumhzzcavqsxt.supabase.co/functions/v1/send-email", {
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":SUPABASE_ANON_KEY},
        body: JSON.stringify({to: recipient, subject: `[${rfi.num || "RFI"}] ${rfi.subject}`, html}),
      });
      if (!r.ok) throw new Error("HTTP "+r.status);
      save(rfis.map(rr => String(rr.id)===String(rfi.id) ? {...rr, status:"open", sentToArchitect:recipient, sentAt:new Date().toISOString()} : rr));
      setSendMsg("✓ Sent to " + recipient);
      setArchEmailDraft("");
    } catch (e) {
      setSendMsg("✗ Send failed — " + (e.message || "network error"));
    }
    setSendingRfi(false);
  };

  return (
    <div style={{padding:24, display:"grid", gridTemplateColumns: selected ? "1fr 420px" : "1fr", gap:16, alignItems:"start"}}>
      {/* LEFT — RFI LIST */}
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div>
            <div style={{fontSize:20,fontWeight:800,color:T.text}}>Requests for Information</div>
            <div style={{fontSize:12,color:T.muted,marginTop:3}}>{open.length} open · {rfis.length} total</div>
          </div>
          <Btn primary onClick={()=>setRFIModal&&setRFIModal(true)}><Ic d={ICONS.plus} size={13}/>New RFI</Btn>
        </div>

        {/* Stats */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10,marginBottom:16}}>
          <StatCard label="Pending GC" value={rfis.filter(r=>r.status==="pending_gc_approval").length} color={T.gold}/>
          <StatCard label="Open" value={open.length} color={T.blue}/>
          <StatCard label="Urgent / Critical" value={rfis.filter(r=>r.priority==="critical"||r.priority==="urgent").length} color={T.red}/>
          <StatCard label="Pending Response" value={rfis.filter(r=>r.status==="pending").length} color={T.amber}/>
          <StatCard label="Closed" value={rfis.filter(r=>r.status==="closed").length} color={T.green}/>
        </div>

        {/* Filter */}
        <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
          {[
            ["all","All"],
            ["pending_gc_approval","🔔 Drafts"],
            ["open","Open"],
            ["pending","Pending"],
            ["closed","Closed"],
          ].map(([f,l])=>(
            <button key={f} onClick={()=>setFilter(f)} style={{padding:"5px 14px",borderRadius:20,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:filter===f?T.gold:T.bg2,color:filter===f?T.bg0:T.muted}}>{l}</button>
          ))}
        </div>

        {/* RFI Table */}
        <Card style={{padding:0,overflow:"hidden"}}>
          {filtered.length === 0 ? (
            <div style={{padding:40,textAlign:"center",color:T.muted,fontSize:13}}>No RFIs found. Create one to get started.</div>
          ) : filtered.map((r,i)=>(
            <div key={r.id}
              onClick={()=>setSelected(String(selected)===String(r.id)?null:r.id)}
              style={{display:"grid",gridTemplateColumns:"60px 1fr 90px 80px 80px",gap:0,padding:"10px 14px",borderBottom:`1px solid ${T.subtle}`,cursor:"pointer",background:selected===r.id?T.goldBg:i%2===0?"transparent":T.bg2+"40",transition:"background 0.15s"}}
              onMouseEnter={e=>e.currentTarget.style.background=T.goldBg}
              onMouseLeave={e=>e.currentTarget.style.background=selected===r.id?T.goldBg:i%2===0?"transparent":T.bg2+"40"}>
              <div style={{fontFamily:"monospace",fontSize:10,color:T.blue,fontWeight:700,paddingTop:2}}>{r.num||"RFI-"+String(i+1).padStart(3,"0")}</div>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:T.text,marginBottom:2}}>{r.subject}</div>
                <div style={{fontSize:10,color:T.muted}}>{r.project||proj?.name||""} {r.submitted ? "· "+r.submitted : ""}</div>
              </div>
              <div style={{display:"flex",alignItems:"center"}}><Badge label={r.priority||"normal"} color={priorityColor(r.priority)}/></div>
              <div style={{display:"flex",alignItems:"center"}}><Badge label={statusLabel(r.status)} color={statusColor(r.status)}/></div>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                {(r.comments||[]).length>0&&<span style={{fontSize:10,color:T.muted}}>💬{r.comments.length}</span>}
                {(r.attachments||[]).length>0&&<span style={{fontSize:10,color:T.muted}}>📎{r.attachments.length}</span>}
              </div>
            </div>
          ))}
        </Card>
      </div>

      {/* RIGHT — RFI DETAIL PANEL */}
      {selectedRFI && (
        <div style={{position:"sticky",top:80}}>
          <Card style={{padding:0,overflow:"hidden"}}>
            {/* Panel header */}
            <div style={{padding:"14px 16px",borderBottom:`1px solid ${T.subtle}`,display:"flex",justifyContent:"space-between",alignItems:"center",background:T.bg2}}>
              <div>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.1em"}}>{selectedRFI.num||"RFI"}</div>
                <div style={{fontSize:13,fontWeight:700,color:T.text,marginTop:2}}>{selectedRFI.subject}</div>
              </div>
              <button onClick={()=>setSelected(null)} style={{background:"none",border:"none",color:T.muted,fontSize:18,cursor:"pointer",lineHeight:1}}>×</button>
            </div>

            <div style={{padding:"14px 16px",maxHeight:600,overflowY:"auto"}}>

              {selectedRFI.status === "pending_gc_approval" ? (
                <>
                  {/* ARIA-drafted RFI — GC review & approve flow */}
                  <div style={{background:T.goldBg,border:`1px solid ${T.gold}40`,borderRadius:9,padding:"10px 14px",marginBottom:14,display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:14}}>⚡</span>
                    <div style={{flex:1}}>
                      <div style={{fontSize:11,fontWeight:800,color:T.gold}}>Drafted by ARIA · Pending Your Approval</div>
                      <div style={{fontSize:10,color:T.muted,marginTop:2}}>Auto-generated from a blueprint flag. Edit anything below, then approve to send.</div>
                    </div>
                  </div>

                  {selectedRFI.drawingRef && (
                    <div style={{background:T.bg2,borderLeft:`3px solid ${T.gold}`,borderRadius:5,padding:"7px 10px",marginBottom:12,fontSize:11,color:T.text}}>
                      <span style={{color:T.muted,fontWeight:700}}>Drawing:</span> {selectedRFI.drawingRef}
                    </div>
                  )}

                  <div style={{marginBottom:12}}>
                    <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:T.muted,marginBottom:5}}>Subject</div>
                    <input value={selectedRFI.subject||""} onChange={e=>updateField(selectedRFI.id,{subject:e.target.value})}
                      style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"8px 11px",color:T.text,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
                  </div>

                  <div style={{marginBottom:12}}>
                    <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:T.muted,marginBottom:5}}>Description</div>
                    <textarea value={selectedRFI.description||selectedRFI.desc||""} rows={6}
                      onChange={e=>updateField(selectedRFI.id,{description:e.target.value,desc:e.target.value})}
                      style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"8px 11px",color:T.text,fontSize:12,fontFamily:"inherit",lineHeight:1.6,resize:"vertical",boxSizing:"border-box"}}/>
                  </div>

                  <div style={{marginBottom:12}}>
                    <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:T.gold,marginBottom:5}}>Suggested Resolution</div>
                    <textarea value={selectedRFI.suggestedResolution||""} rows={5}
                      onChange={e=>updateField(selectedRFI.id,{suggestedResolution:e.target.value})}
                      placeholder="ARIA's read on the issue and what the architect needs to clarify."
                      style={{width:"100%",background:T.bg2,border:`1px solid ${T.gold}40`,borderRadius:7,padding:"8px 11px",color:T.text,fontSize:12,fontFamily:"inherit",lineHeight:1.6,resize:"vertical",boxSizing:"border-box"}}/>
                  </div>

                  {(() => {
                    const onFile = findArchitectEmail(selectedRFI);
                    return (
                      <div style={{marginBottom:12}}>
                        <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:T.muted,marginBottom:5}}>Send to Architect</div>
                        {onFile ? (
                          <div style={{background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"8px 11px",fontSize:11,color:T.text}}>
                            On file: <strong>{onFile}</strong>
                          </div>
                        ) : (
                          <input value={archEmailDraft} onChange={e=>setArchEmailDraft(e.target.value)}
                            placeholder="architect@firm.com"
                            style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"8px 11px",color:T.text,fontSize:12,fontFamily:"inherit",boxSizing:"border-box"}}/>
                        )}
                      </div>
                    );
                  })()}

                  {sendMsg && (
                    <div style={{fontSize:11,color:sendMsg.startsWith("✓")?T.green:T.red,marginBottom:10,fontWeight:600}}>{sendMsg}</div>
                  )}

                  <div style={{display:"flex",gap:8,marginBottom:14}}>
                    <button onClick={()=>deleteRFI(selectedRFI.id)}
                      style={{flex:1,background:"none",border:`1px solid ${T.subtle}`,color:T.muted,fontSize:12,fontWeight:600,padding:"10px 14px",borderRadius:8,cursor:"pointer"}}>
                      Dismiss Draft
                    </button>
                    <button onClick={()=>approveAndSendRFI(selectedRFI)} disabled={sendingRfi}
                      style={{flex:2,background:sendingRfi?T.bg3:T.gold,border:"none",color:sendingRfi?T.muted:T.bg0,fontSize:12,fontWeight:800,padding:"10px 14px",borderRadius:8,cursor:sendingRfi?"wait":"pointer"}}>
                      {sendingRfi ? "Sending…" : "✓ Approve & Send to Architect"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Status controls */}
                  <div style={{marginBottom:14}}>
                    <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:T.muted,marginBottom:6}}>Status</div>
                    <div style={{display:"flex",gap:6}}>
                      {["open","pending","closed"].map(s=>(
                        <button key={s} onClick={()=>updateStatus(selectedRFI.id,s)}
                          style={{padding:"4px 12px",borderRadius:20,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",
                            background:selectedRFI.status===s?statusColor(s):T.bg2,
                            color:selectedRFI.status===s?"white":T.muted,textTransform:"capitalize",transition:"all 0.15s"}}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  {selectedRFI.drawingRef && (
                    <div style={{background:T.bg2,borderLeft:`3px solid ${T.gold}`,borderRadius:5,padding:"7px 10px",marginBottom:10,fontSize:11,color:T.text}}>
                      <span style={{color:T.muted,fontWeight:700}}>Drawing:</span> {selectedRFI.drawingRef}
                    </div>
                  )}

                  {/* Details */}
                  <div style={{background:T.bg2,borderRadius:8,padding:"10px 12px",marginBottom:14,fontSize:11,color:T.muted,lineHeight:1.7,whiteSpace:"pre-wrap"}}>
                    {selectedRFI.description||selectedRFI.question||"No description provided."}
                  </div>

                  {selectedRFI.suggestedResolution && (
                    <div style={{marginBottom:14}}>
                      <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:T.gold,marginBottom:5}}>Suggested Resolution</div>
                      <div style={{background:T.goldBg,border:`1px solid ${T.gold}30`,borderRadius:8,padding:"9px 12px",fontSize:11,color:T.text,lineHeight:1.7,whiteSpace:"pre-wrap"}}>{selectedRFI.suggestedResolution}</div>
                    </div>
                  )}
                </>
              )}

              {selectedRFI.status !== "pending_gc_approval" && (<>
              {/* Attachments */}
              <div style={{marginBottom:14}}>
                <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:T.muted,marginBottom:6}}>Attachments</div>
                {(selectedRFI.attachments||[]).map(att=>(
                  <div key={att.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:T.bg2,borderRadius:7,marginBottom:5,border:`1px solid ${T.subtle}`}}>
                    <span style={{fontSize:14}}>📎</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:11,fontWeight:600,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{att.name}</div>
                      <div style={{fontSize:9,color:T.muted}}>{att.size}</div>
                    </div>
                    <button onClick={()=>{
                      const a=document.createElement("a");a.href=att.data;a.download=att.name;a.click();
                    }} style={{background:"none",border:"none",color:T.blue,fontSize:10,cursor:"pointer",fontWeight:600}}>↓</button>
                    <button onClick={()=>save(rfis.map(r=>String(r.id)===String(selectedRFI.id)?{...r,attachments:(r.attachments||[]).filter(a=>a.id!==att.id)}:r))}
                      style={{background:"none",border:"none",color:T.red,fontSize:12,cursor:"pointer"}}>×</button>
                  </div>
                ))}
                <input ref={fileRef} type="file" style={{display:"none"}} onChange={e=>{if(e.target.files[0])handleFileAttach(e.target.files[0],selectedRFI.id);}}/>
                <button onClick={()=>fileRef.current?.click()} style={{fontSize:11,color:T.blue,background:"none",border:`1px dashed ${T.subtle}`,borderRadius:7,padding:"6px 12px",cursor:"pointer",width:"100%",marginTop:4}}>
                  + Attach File
                </button>
              </div>

              {/* Comments */}
              <div style={{marginBottom:14}}>
                <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:T.muted,marginBottom:8}}>Comments</div>
                {(selectedRFI.comments||[]).length===0&&(
                  <div style={{fontSize:11,color:T.muted,fontStyle:"italic",padding:"8px 0"}}>No comments yet.</div>
                )}
                {(selectedRFI.comments||[]).map(c=>(
                  <div key={c.id} style={{background:T.bg2,borderRadius:8,padding:"9px 11px",marginBottom:7,border:`1px solid ${T.subtle}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.text}}>{c.author}</div>
                      <div style={{display:"flex",gap:8,alignItems:"center"}}>
                        <span style={{fontSize:9,color:T.muted}}>{c.date}</span>
                        <button onClick={()=>deleteComment(selectedRFI.id,c.id)} style={{background:"none",border:"none",color:T.red,fontSize:11,cursor:"pointer"}}>×</button>
                      </div>
                    </div>
                    <div style={{fontSize:11,color:T.muted,lineHeight:1.6}}>{c.text}</div>
                  </div>
                ))}

                {/* Add comment */}
                <textarea value={comment} onChange={e=>setComment(e.target.value)}
                  placeholder="Add a comment or response…"
                  style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit",resize:"vertical",minHeight:64,marginTop:4}}/>
                <Btn primary small onClick={()=>addComment(selectedRFI.id)} style={{marginTop:6,width:"100%",justifyContent:"center"}}>
                  💬 Save Comment
                </Btn>
              </div>

              {/* Delete */}
              {!confirmDelete ? (
                <button onClick={()=>setConfirmDelete(true)}
                  style={{width:"100%",background:"none",border:`1px solid ${T.red}40`,borderRadius:8,padding:"8px",color:T.red,fontSize:11,cursor:"pointer",fontWeight:600,marginTop:4}}>
                  🗑 Delete RFI
                </button>
              ) : (
                <div style={{marginTop:4,background:T.redBg,border:`1px solid ${T.red}30`,borderRadius:8,padding:"10px 12px"}}>
                  <div style={{fontSize:11,color:T.red,fontWeight:700,marginBottom:8}}>Delete this RFI permanently?</div>
                  <div style={{display:"flex",gap:7}}>
                    <button onClick={()=>deleteRFI(selectedRFI.id)}
                      style={{flex:1,background:T.red,color:"white",border:"none",borderRadius:7,padding:"7px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                      Yes, Delete
                    </button>
                    <button onClick={()=>setConfirmDelete(false)}
                      style={{flex:1,background:T.bg2,color:T.muted,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"7px",fontSize:11,cursor:"pointer"}}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              </>)}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// --- COP VIEW -----------------------------------------------------------------
function COPView({session}) {
  const cid = session?.companyId || window._grokCompanyId ||
    (() => { try { return JSON.parse(localStorage.getItem("grok_auth_session")||"{}").companyId || "gcg_001"; } catch { return "gcg_001"; }})();
  const liveProjects = (() => {
    try { const s = localStorage.getItem("grok_projects_"+cid); if(s){const p=JSON.parse(s);if(p.length)return p;} } catch{}
    return getInitialProjects(cid);
  })();
  const [proj,setProj]=useState(liveProjects[0]||null);
  // Re-check after mount in case cid resolved late
  React.useEffect(()=>{
    if(!proj){const p=getInitialProjects(cid);if(p.length)setProj(p[0]);}
  },[cid]); // eslint-disable-line
  const [appNum,setAppNum]=useState("1");
  const [period,setPeriod]=useState("April 1–30, 2026");
  const [tab,setTab]=useState("billing");
  const [checks,setChecks]=useState({});
  const tog=k=>setChecks(p=>({...p,[k]:!p[k]}));

  const phases=[
    {name:"Demolition & Protection",val:28000,pct:100},
    {name:"Rough Framing",val:52000,pct:100},
    {name:"MEP Rough-In",val:86000,pct:100},
    {name:"Drywall & Tape",val:64000,pct:55},
    {name:"Tile & Waterproofing",val:48000,pct:0},
    {name:"Paint & Finishes",val:38000,pct:0},
    {name:"Doors & Hardware",val:24000,pct:0},
    {name:"Countertops & Fixtures",val:32000,pct:0},
    {name:"General Conditions",val:28000,pct:68},
    {name:"Change Orders",val:7950,pct:80},
  ];
  const totalSched=phases.reduce((s,p)=>s+p.val,0);
  const totalEarned=phases.reduce((s,p)=>s+Math.round(p.val*p.pct/100),0);
  const ret=Math.round(totalEarned*0.1);
  const earnedLessRet=totalEarned-ret;
  const prev=Math.round(earnedLessRet*0.7);
  const due=earnedLessRet-prev;
  const bal=totalSched-totalEarned+ret;

  const CB=({id,label,sub,color=T.green})=>(
    <div onClick={()=>tog(id)} style={{display:"flex",gap:9,alignItems:"flex-start",padding:"8px 10px",borderRadius:7,cursor:"pointer",background:checks[id]?color+"14":"transparent",transition:"background 0.15s",marginBottom:3}}>
      <div style={{width:16,height:16,borderRadius:4,border:`2px solid ${checks[id]?color:T.subtle}`,background:checks[id]?color:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1,transition:"all 0.15s"}}>
        {checks[id]&&<Ic d={ICONS.check} size={9} style={{color:T.bg0}}/>}
      </div>
      <div>
        <div style={{fontSize:12,fontWeight:600,color:checks[id]?color:T.text}}>{label}</div>
        {sub&&<div style={{fontSize:10,color:T.muted,marginTop:1,lineHeight:1.4}}>{sub}</div>}
      </div>
    </div>
  );

  const DocPill=({name,file,color})=>(
    <div style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,marginBottom:5}}>
      <div style={{width:20,height:20,borderRadius:4,background:color+"18",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <Ic d={ICONS.docs} size={11} style={{color}}/>
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:11,fontWeight:600,color:T.text}}>{name}</div>
        <div style={{fontSize:9,color:T.muted,fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{file}</div>
      </div>
    </div>
  );

  const tabBtn=(id,label)=>(
    <button onClick={()=>setTab(id)} style={{padding:"7px 16px",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:tab===id?T.gold:T.bg2,color:tab===id?T.bg0:T.muted,transition:"all 0.15s"}}>{label}</button>
  );

  const s1done=["s1a","s1b","s1c","s1d"].every(k=>checks[k]);
  const cleared=checks["s2b"];
  const allDone=["s1a","s1b","s1c","s1d","s2a","s2b","s2c","s3a"].every(k=>checks[k]);

  return (
    <div style={{padding:24}}>
      {!proj&&(
        <div style={{textAlign:"center",padding:"60px 20px",color:T.muted}}>
          <div style={{fontSize:40,marginBottom:12}}>📋</div>
          <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:8}}>Certificate of Payment</div>
          <div style={{fontSize:13,color:T.muted,marginBottom:20}}>Add a project first to generate monthly progress billing and lien waiver checklists.</div>
          <div style={{fontSize:11,color:T.muted}}>Go to Projects → Add Project, then return here.</div>
        </div>
      )}
      {proj&&<>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>Certificate of Payment</div>
          <div style={{fontSize:12,color:T.muted,marginTop:3}}>Monthly progress billing + lien waiver payment checklist</div>
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center"}}>
          <div style={{display:"flex",gap:3,background:T.bg2,borderRadius:9,padding:3}}>
            {tabBtn("billing","Billing")}
            {tabBtn("checklist","Payment Checklist")}
            {tabBtn("lien-docs","Lien Documents")}
          </div>
          <Btn primary><Ic d={ICONS.docs} size={13}/>Export PDF</Btn>
        </div>
      </div>

      {/* BILLING TAB */}
      {tab==="billing"&&(
        <div>
          <div style={{display:"grid",gridTemplateColumns:"280px 1fr",gap:14,marginBottom:14}}>
            <Card>
              <SectionTitle>Application Info</SectionTitle>
              <Select label="Project" value={proj?.id} onChange={v=>setProj(liveProjects.find(p=>p.id===parseInt(v)||p.id===v))} options={liveProjects.map(p=>({value:p.id,label:(p.name||"").split(" — ")[0]}))}/>
              <Field label="Application #" value={appNum} onChange={setAppNum}/>
              <Field label="Billing Period" value={period} onChange={setPeriod}/>
              <div style={{fontSize:11,color:T.muted,marginTop:6}}>Owner: <strong style={{color:T.text}}>{proj?.client}</strong></div>
            </Card>
            <Card>
              <SectionTitle>Payment Summary</SectionTitle>
              {[
                {l:"Original Contract Amount",v:"$"+(proj?.value||0).toLocaleString(),c:T.text},
                {l:"Approved Change Orders",v:"$7,950",c:T.text},
                {l:"Revised Contract Total",v:"$"+((proj?.value||0)+7950).toLocaleString(),c:T.gold,bold:true},
                {l:"Total Completed to Date",v:"$"+totalEarned.toLocaleString(),c:T.text},
                {l:"Retainage Withheld (10%)",v:"($"+ret.toLocaleString()+")",c:T.amber},
                {l:"Total Earned Less Retainage",v:"$"+earnedLessRet.toLocaleString(),c:T.text},
                {l:"Less: Previous Certificates",v:"($"+prev.toLocaleString()+")",c:T.muted},
                {l:"CURRENT PAYMENT DUE",v:"$"+due.toLocaleString(),c:T.green,bold:true,hi:true},
              ].map(r=>(
                <div key={r.l} style={{display:"flex",justifyContent:"space-between",padding:"5px 8px",borderRadius:6,background:r.hi?T.greenBg:"transparent",marginBottom:2}}>
                  <span style={{fontSize:11,color:r.bold?T.text:T.muted,fontWeight:r.bold?700:400}}>{r.l}</span>
                  <span style={{fontSize:12,fontWeight:r.bold?800:600,color:r.c}}>{r.v}</span>
                </div>
              ))}
            </Card>
          </div>
          <Card>
            <SectionTitle>Schedule of Values — G703</SectionTitle>
            <div style={{display:"grid",gridTemplateColumns:"2fr 100px 80px 110px 80px 75px",fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",borderBottom:`1px solid ${T.subtle}`,paddingBottom:7,marginBottom:4}}>
              {["Description","Scheduled","% Done","Earned","Balance","Retainage"].map(h=><div key={h} style={{padding:"0 6px"}}>{h}</div>)}
            </div>
            {phases.map((ph,i)=>{
              const e=Math.round(ph.val*ph.pct/100),b=ph.val-e,r2=Math.round(e*0.1);
              return(
                <div key={i} style={{display:"grid",gridTemplateColumns:"2fr 100px 80px 110px 80px 75px",background:i%2===0?"rgba(255,255,255,0.02)":"transparent",borderBottom:`1px solid rgba(255,255,255,0.04)`}}>
                  <div style={{padding:"7px 6px",fontSize:11,color:T.text,fontWeight:500}}>{ph.name}</div>
                  <div style={{padding:"7px 6px",fontSize:11,color:T.muted}}>${ph.val.toLocaleString()}</div>
                  <div style={{padding:"7px 6px"}}><div style={{display:"flex",alignItems:"center",gap:4}}><div style={{flex:1,height:3,background:T.bg3,borderRadius:2}}><div style={{height:"100%",width:ph.pct+"%",background:ph.pct===100?T.green:T.gold,borderRadius:2}}/></div><span style={{fontSize:9,color:ph.pct===100?T.green:T.gold,fontWeight:700}}>{ph.pct}%</span></div></div>
                  <div style={{padding:"7px 6px",fontSize:11,fontWeight:600,color:ph.pct>0?T.gold:T.muted}}>${e.toLocaleString()}</div>
                  <div style={{padding:"7px 6px",fontSize:11,color:T.muted}}>${b.toLocaleString()}</div>
                  <div style={{padding:"7px 6px",fontSize:11,color:T.amber}}>${r2.toLocaleString()}</div>
                </div>
              );
            })}
            <div style={{display:"grid",gridTemplateColumns:"2fr 100px 80px 110px 80px 75px",background:T.bg3,borderRadius:"0 0 8px 8px"}}>
              {["TOTALS","$"+totalSched.toLocaleString(),"","$"+totalEarned.toLocaleString(),"$"+bal.toLocaleString(),"$"+ret.toLocaleString()].map((v,i)=>(
                <div key={i} style={{padding:"8px 6px",fontSize:11,fontWeight:700,color:i===0?T.gold:i===3?T.green:T.text}}>{v}</div>
              ))}
            </div>
          </Card>
          <div style={{marginTop:12,background:T.amberBg,borderLeft:`3px solid ${T.amber}`,borderRadius:8,padding:"10px 14px",display:"flex",gap:10,alignItems:"center"}}>
            <Ic d={ICONS.alert} size={16} style={{color:T.amber,flexShrink:0}}/>
            <div style={{fontSize:12,color:T.muted}}>
              <strong style={{color:T.amber}}>Before submitting:</strong> go to the{" "}
              <button onClick={()=>setTab("checklist")} style={{background:"none",border:"none",cursor:"pointer",color:T.gold,fontSize:12,fontWeight:700,padding:0,textDecoration:"underline"}}>Payment Checklist tab</button>
              {" "}and complete all steps first. Never submit without collecting sub lien waivers.
            </div>
          </div>
        </div>
      )}

      {/* PAYMENT CHECKLIST TAB */}
      {tab==="checklist"&&(
        <div>
          <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:11,padding:"14px 18px",marginBottom:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{fontSize:13,fontWeight:700,color:T.text}}>Monthly Billing Cycle — Complete in Order</div>
              <span style={{fontSize:12,fontWeight:700,color:allDone?T.green:T.amber}}>
                {["s1a","s1b","s1c","s1d","s2a","s2b","s2c","s3a"].filter(k=>checks[k]).length}/8 steps
              </span>
            </div>
            <div style={{height:5,background:T.bg3,borderRadius:3}}>
              <div style={{height:"100%",width:Math.round(["s1a","s1b","s1c","s1d","s2a","s2b","s2c","s3a"].filter(k=>checks[k]).length/8*100)+"%",background:allDone?T.green:T.gold,borderRadius:3,transition:"width 0.4s"}}/>
            </div>
            {allDone&&<div style={{marginTop:8,fontSize:12,fontWeight:700,color:T.green}}>✓ Full billing cycle complete — file all waivers in project folder</div>}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14}}>

            {/* STEP 1 */}
            <Card>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                <div style={{width:24,height:24,borderRadius:"50%",background:T.teal+"20",color:T.teal,fontSize:11,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>1</div>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>Before You Submit</div>
              </div>
              <div style={{fontSize:11,color:T.muted,marginBottom:12,paddingLeft:32,lineHeight:1.5}}>Complete all 4 before sending pay app to Owner/GC</div>
              <CB id="s1a" color={T.teal} label="Sub Conditional Waivers collected"
                sub="Every sub on this job — for THIS period. Use the Sub Lien Waiver Log."/>
              <CB id="s1b" color={T.teal} label="Sub Unconditional Waivers collected"
                sub="Confirming subs received PRIOR period payment. Attach to this app."/>
              <CB id="s1c" color={T.teal} label="Grok Conditional Waiver signed & attached"
                sub="Cal. Civil Code §8132 — attach with this pay application."/>
              <CB id="s1d" color={T.teal} label="Certificate of Payment complete"
                sub="G703 schedule of values updated, all line items correct."/>
              <div style={{marginTop:10,padding:"9px 12px",background:s1done?T.greenBg:T.bg2,borderRadius:8,border:`1px solid ${s1done?T.green+"40":T.subtle}`,fontSize:11,fontWeight:700,color:s1done?T.green:T.muted,transition:"all 0.3s"}}>
                {s1done?"✓ Ready to submit pay application":"Complete all 4 items above first"}
              </div>
              <Divider/>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Documents needed</div>
              <DocPill name="Sub Lien Waiver Collection Log" file="Grok_Sub_Lien_Waiver_Collection_Log.docx" color={T.teal}/>
              <DocPill name="Conditional Waiver — Progress" file="Grok_Lien_Waiver_Conditional_Progress.docx" color={T.teal}/>
              <DocPill name="Certificate of Payment" file="Grok_Certificate_of_Payment.docx" color={T.gold}/>
            </Card>

            {/* STEP 2 */}
            <Card>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                <div style={{width:24,height:24,borderRadius:"50%",background:T.amber+"20",color:T.amber,fontSize:11,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>2</div>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>After Payment Received</div>
              </div>
              <div style={{fontSize:11,color:T.muted,marginBottom:12,paddingLeft:32,lineHeight:1.5}}>Do NOT sign Unconditional until bank confirms cleared</div>
              <CB id="s2a" color={T.amber} label="Payment received from Owner/GC"
                sub="Check or wire received — do not sign unconditional yet."/>
              <CB id="s2b" color={T.amber} label="Payment cleared in your bank account"
                sub="Log in and confirm funds available. This is the trigger."/>
              {checks["s2a"]&&!checks["s2b"]&&(
                <div style={{margin:"4px 0 8px",padding:"8px 10px",background:T.redBg,borderRadius:7,border:`1px solid ${T.red}30`,fontSize:11,fontWeight:700,color:T.red}}>
                  ⚠ Check received but not cleared — wait before signing unconditional
                </div>
              )}
              <CB id="s2c" color={cleared?T.green:T.muted} label="Grok Unconditional Waiver signed & sent"
                sub="Cal. Civil Code §8134 — sign and send ONLY after check clears."/>
              {!cleared&&checks["s2c"]&&(
                <div style={{margin:"4px 0 8px",padding:"8px 10px",background:T.redBg,borderRadius:7,border:`1px solid ${T.red}30`,fontSize:11,fontWeight:700,color:T.red}}>
                  ⚠ Signed unconditional before confirming funds cleared — review immediately
                </div>
              )}
              {cleared&&checks["s2c"]&&(
                <div style={{marginTop:8,padding:"9px 12px",background:T.greenBg,borderRadius:8,border:`1px solid ${T.green}30`,fontSize:11,fontWeight:700,color:T.green}}>
                  ✓ Unconditional sent correctly — this period is closed
                </div>
              )}
              <Divider/>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Document needed</div>
              <DocPill name="Unconditional Waiver — Progress" file="Grok_Lien_Waiver_Unconditional_Progress.docx" color={T.amber}/>
              <div style={{marginTop:8,background:T.redBg,borderLeft:`3px solid ${T.red}`,borderRadius:6,padding:"9px 11px",fontSize:11,color:T.muted,lineHeight:1.6}}>
                <strong style={{color:T.red}}>The golden rule:</strong> Conditional → payment clears → Unconditional. Always in that order. If you sign unconditional before the check clears and it bounces, your lien rights for that period are permanently gone.
              </div>
            </Card>

            {/* STEP 3 */}
            <Card>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                <div style={{width:24,height:24,borderRadius:"50%",background:T.blue+"20",color:T.blue,fontSize:11,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>3</div>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>Pay Your Subs</div>
              </div>
              <div style={{fontSize:11,color:T.muted,marginBottom:12,paddingLeft:32,lineHeight:1.5}}>Pay subs after you receive payment. Collect their unconditionals next cycle.</div>
              <CB id="s3a" color={T.blue} label="All subs paid for this period"
                sub="Pay every sub listed in the lien waiver log for this period."/>
              {checks["s3a"]&&(
                <div style={{marginTop:8,padding:"9px 12px",background:T.blueBg,borderRadius:8,border:`1px solid ${T.blue}30`,fontSize:11,color:T.blue,lineHeight:1.5}}>
                  Next billing cycle: collect <strong>Unconditional Waivers</strong> from these subs confirming they received this payment.
                </div>
              )}
              <Divider/>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:10}}>Final Closeout Only</div>
              <div style={{fontSize:11,color:T.muted,marginBottom:10,lineHeight:1.5}}>At project end, execute these in exact order:</div>
              {[
                {n:"1",c:T.green,l:"Collect sub final unconditionals first"},
                {n:"2",c:T.gold,l:"Submit final Certificate of Payment"},
                {n:"3",c:T.amber,l:"Attach Conditional Waiver — Final"},
                {n:"4",c:T.red,l:"Sign Unconditional — Final only after all payments clear"},
              ].map(s=>(
                <div key={s.n} style={{display:"flex",gap:8,alignItems:"flex-start",padding:"6px 0",borderBottom:`1px solid ${T.subtle}`}}>
                  <div style={{width:18,height:18,borderRadius:"50%",background:s.c+"20",color:s.c,fontSize:10,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>{s.n}</div>
                  <div style={{fontSize:11,color:T.muted,lineHeight:1.4}}>{s.l}</div>
                </div>
              ))}
              <Divider/>
              <DocPill name="Conditional Waiver — Final" file="Grok_Lien_Waiver_Conditional_Final.docx" color={T.amber}/>
              <DocPill name="Unconditional Waiver — Final" file="Grok_Lien_Waiver_Unconditional_Final.docx" color={T.red}/>
            </Card>
          </div>
        </div>
      )}

      {/* LIEN DOCUMENTS TAB */}
      {tab==="lien-docs"&&(
        <div>
          <div style={{background:T.blueBg,borderLeft:`3px solid ${T.blue}`,borderRadius:9,padding:"12px 16px",marginBottom:20,fontSize:12,color:T.muted,lineHeight:1.6}}>
            <strong style={{color:T.blue}}>How lien documents protect Grok:</strong> As GC you have lien rights against the Owner's property. Your subs have lien rights too. These documents flow in both directions — Grok to Owner, and Subs to Grok — to protect everyone's right to be paid.
          </div>
          <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>

            <Card>
              <div style={{fontSize:12,fontWeight:800,color:T.teal,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:14}}>Grok → Owner (flowing down)</div>
              {[
                {name:"20-Day Preliminary Notice",file:"Grok_20Day_Preliminary_Notice.docx",color:T.amber,when:"Day 1 of every job",what:"Serves Owner + Construction Lender. Preserves your mechanics lien rights. Miss the 20-day window = lose lien rights for early work. Certified mail. No exceptions."},
                {name:"Conditional Waiver — Progress",file:"Grok_Lien_Waiver_Conditional_Progress.docx",color:T.teal,when:"With every monthly pay application",what:"Cal. Civil Code §8132. 'I waive lien rights for this period IF you pay me.' Conditional = not effective until check clears."},
                {name:"Unconditional Waiver — Progress",file:"Grok_Lien_Waiver_Unconditional_Progress.docx",color:T.green,when:"After check clears — every month",what:"Cal. Civil Code §8134. Permanently closes lien rights for that period. NEVER sign before the check clears."},
                {name:"Conditional Waiver — Final",file:"Grok_Lien_Waiver_Conditional_Final.docx",color:T.amber,when:"With final pay application",what:"Cal. Civil Code §8136. Covers entire project. Still conditional on receiving final payment including retainage."},
                {name:"Unconditional Waiver — Final",file:"Grok_Lien_Waiver_Unconditional_Final.docx",color:T.red,when:"After ALL payments including retainage clear",what:"Cal. Civil Code §8138. Permanently closes ALL lien rights for the entire project. The last document you ever sign on a job."},
              ].map(d=>(
                <div key={d.file} style={{background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:9,padding:"12px 14px",marginBottom:8}}>
                  <div style={{display:"flex",gap:9,alignItems:"flex-start",marginBottom:8}}>
                    <div style={{width:28,height:28,borderRadius:6,background:d.color+"18",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <Ic d={ICONS.docs} size={13} style={{color:d.color}}/>
                    </div>
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:2}}>{d.name}</div>
                      <span style={{fontSize:10,fontWeight:700,background:d.color+"18",color:d.color,borderRadius:20,padding:"1px 8px"}}>{d.when}</span>
                    </div>
                  </div>
                  <div style={{fontSize:11,color:T.muted,lineHeight:1.5,marginBottom:6}}>{d.what}</div>
                  <div style={{fontSize:9,color:T.muted,fontFamily:"monospace",background:T.bg3,borderRadius:5,padding:"3px 7px"}}>{d.file}</div>
                </div>
              ))}
            </Card>

            <Card>
              <div style={{fontSize:12,fontWeight:800,color:T.green,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:14}}>Subs → Grok (flowing up)</div>
              <div style={{background:T.greenBg,borderLeft:`3px solid ${T.green}`,borderRadius:7,padding:"10px 12px",marginBottom:14,fontSize:11,color:T.muted,lineHeight:1.6}}>
                <strong style={{color:T.green}}>Why this matters:</strong> If your sub files a lien against the Owner's property and you can't prove you collected a waiver, the Owner can withhold payment from Grok — even if you paid the sub. Collect these from <strong style={{color:T.text}}>every sub</strong> before you submit your pay application.
              </div>
              {[
                {name:"Sub 20-Day Preliminary Notice",file:"(Sub serves their own — verify in Sub Agreement)",color:T.amber,when:"Sub's Day 1",what:"Your Subcontractor Agreement requires each sub to serve their own 20-Day Notice. Follow up to confirm they did it."},
                {name:"Sub Conditional Waiver — Progress",file:"Grok_Lien_Waiver_Conditional_Progress.docx",color:T.teal,when:"Collect before you pay sub each month",what:"Sub provides this covering their work for this billing period. Attach to your pay application to Owner."},
                {name:"Sub Unconditional Waiver — Progress",file:"Grok_Lien_Waiver_Unconditional_Progress.docx",color:T.green,when:"Collect after prior period payment sent to sub",what:"Confirms sub received last month's payment. Attach to your next pay application to Owner as proof you paid them."},
                {name:"Sub Final Unconditional Waiver",file:"Grok_Lien_Waiver_Unconditional_Final.docx",color:T.red,when:"Collect before final closeout",what:"Collect from every sub before submitting your own final pay application. Proves all subs were paid in full."},
              ].map(d=>(
                <div key={d.name} style={{background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:9,padding:"12px 14px",marginBottom:8}}>
                  <div style={{display:"flex",gap:9,alignItems:"flex-start",marginBottom:8}}>
                    <div style={{width:28,height:28,borderRadius:6,background:d.color+"18",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <Ic d={ICONS.docs} size={13} style={{color:d.color}}/>
                    </div>
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:2}}>{d.name}</div>
                      <span style={{fontSize:10,fontWeight:700,background:d.color+"18",color:d.color,borderRadius:20,padding:"1px 8px"}}>{d.when}</span>
                    </div>
                  </div>
                  <div style={{fontSize:11,color:T.muted,lineHeight:1.5,marginBottom:6}}>{d.what}</div>
                  <div style={{fontSize:9,color:T.muted,fontFamily:"monospace",background:T.bg3,borderRadius:5,padding:"3px 7px"}}>{d.file}</div>
                </div>
              ))}
              <div style={{marginTop:4}}>
                <DocPill name="Sub Lien Waiver Collection Log" file="Grok_Sub_Lien_Waiver_Collection_Log.docx" color={T.green}/>
                <div style={{fontSize:11,color:T.muted,marginTop:4,lineHeight:1.5}}>Use this log to track every waiver collected from every sub each billing period. Attach it and the waivers to your pay application.</div>
              </div>
            </Card>
          </div>
        </div>
      )}
      </>}
    </div>
  );
}


// Daily Reports view — reads daily_reports submitted via QR code at /log.
// RLS gates the SELECT by companies.auth_user_id, so the company_id filter is
// belt-and-suspenders for clarity. Groups by project_id then by date (descending).
// Project names come from local grok_projects_<cid> to avoid a second roundtrip.
function DailyReportsView({session}) {
  const cid = session?.companyId || window._grokCompanyId || "local";
  const [reports, setReports] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [refreshKey, setRefreshKey] = React.useState(0);

  const projectNames = React.useMemo(() => {
    try {
      const p = cleanProjects(JSON.parse(localStorage.getItem("grok_projects_"+cid)||"[]"));
      const map = {};
      p.forEach(proj => {
        map[String(proj.id)] = proj.name?.split(" — ")[0] || proj.name || ("Project "+proj.id);
      });
      return map;
    } catch { return {}; }
  }, [cid]);

  React.useEffect(() => {
    if (!cid || cid === "local") { setLoading(false); return; }
    setLoading(true); setError("");
    fetch(`${SUPABASE_URL}/rest/v1/daily_reports?company_id=eq.${encodeURIComponent(cid)}&order=submitted_at.desc&limit=500`, {
      headers: {
        "apikey": SUPABASE_ANON_KEY,
        "Authorization": "Bearer " + sb._token(),
      },
    })
      .then(r => r.ok ? r.json() : Promise.reject("HTTP "+r.status))
      .then(data => { setReports(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(e => { setError(String(e)); setLoading(false); });
  }, [cid, refreshKey]);

  // Group: { [projectId]: { [YYYY-MM-DD]: [report, report, ...] } }
  const grouped = React.useMemo(() => {
    const out = {};
    reports.forEach(r => {
      const date = (r.submitted_at || "").slice(0, 10);
      if (!out[r.project_id]) out[r.project_id] = {};
      if (!out[r.project_id][date]) out[r.project_id][date] = [];
      out[r.project_id][date].push(r);
    });
    return out;
  }, [reports]);

  const formatTime = ts => {
    try { return new Date(ts).toLocaleTimeString([], {hour:"numeric", minute:"2-digit"}); }
    catch { return ""; }
  };
  const formatDate = ymd => {
    try { return new Date(ymd + "T12:00:00").toLocaleDateString([], {weekday:"short", month:"short", day:"numeric", year:"numeric"}); }
    catch { return ymd; }
  };

  if (loading) {
    return (
      <div style={{padding:40, textAlign:"center", color:T.muted}}>
        <Spin/>
        <div style={{marginTop:14, fontSize:13}}>Loading daily reports…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{padding:40, textAlign:"center"}}>
        <div style={{fontSize:32, marginBottom:12}}>⚠️</div>
        <div style={{fontSize:14, fontWeight:700, color:T.text, marginBottom:6}}>Couldn't load reports</div>
        <div style={{fontSize:11, color:T.red, fontFamily:"monospace", marginBottom:14}}>{error}</div>
        <Btn small primary onClick={()=>setRefreshKey(k=>k+1)}>
          <Ic d={ICONS.refresh} size={12}/> Try again
        </Btn>
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div style={{padding:24}}>
        <div style={{marginBottom:20}}>
          <div style={{fontSize:20, fontWeight:800, color:T.text}}>Daily Reports</div>
          <div style={{fontSize:12, color:T.muted, marginTop:3}}>Reports submitted by field workers via QR code</div>
        </div>
        <Card style={{textAlign:"center", padding:40}}>
          <div style={{fontSize:40, marginBottom:12}}>📋</div>
          <div style={{fontSize:14, fontWeight:700, color:T.text, marginBottom:6}}>No reports yet</div>
          <div style={{fontSize:12, color:T.muted, lineHeight:1.7, maxWidth:380, margin:"0 auto"}}>
            Print QR site cards from <strong>QR Site Cards</strong> and post them at your job sites.
            Workers scan to file a quick daily report — submissions land here.
          </div>
        </Card>
      </div>
    );
  }

  // Sort projects by latest activity (most recent date first)
  const projectIds = Object.keys(grouped).sort((a, b) => {
    const aLatest = Object.keys(grouped[a]).sort().reverse()[0] || "";
    const bLatest = Object.keys(grouped[b]).sort().reverse()[0] || "";
    return bLatest.localeCompare(aLatest);
  });

  return (
    <div style={{padding:24}}>
      <div style={{marginBottom:20, display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap"}}>
        <div>
          <div style={{fontSize:20, fontWeight:800, color:T.text}}>Daily Reports</div>
          <div style={{fontSize:12, color:T.muted, marginTop:3}}>
            {reports.length} report{reports.length===1?"":"s"} from {projectIds.length} project{projectIds.length===1?"":"s"} · submitted via QR code at /log
          </div>
        </div>
        <Btn small ghost onClick={()=>setRefreshKey(k=>k+1)}>
          <Ic d={ICONS.refresh} size={12}/> Refresh
        </Btn>
      </div>

      <div style={{display:"flex", flexDirection:"column", gap:18}}>
        {projectIds.map(projId => {
          const projName = projectNames[String(projId)] || `Project ${String(projId).slice(0,12)}…`;
          const dates = Object.keys(grouped[projId]).sort().reverse();
          const totalForProj = dates.reduce((sum, d) => sum + grouped[projId][d].length, 0);
          const hasOpenRequests = dates.some(d => grouped[projId][d].some(r => r.requests && r.requests.trim()));
          return (
            <Card key={projId}>
              <div style={{display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:10, marginBottom:14, paddingBottom:10, borderBottom:`1px solid ${T.subtle}`}}>
                <div style={{display:"flex", alignItems:"baseline", gap:10, flexWrap:"wrap"}}>
                  <div style={{fontSize:15, fontWeight:800, color:T.text}}>{projName}</div>
                  {hasOpenRequests && <span style={{background:T.amberBg, color:T.amber, border:`1px solid ${T.amber}40`, borderRadius:5, padding:"2px 8px", fontSize:10, fontWeight:700}}>Has requests</span>}
                </div>
                <div style={{fontSize:10, color:T.muted}}>{totalForProj} report{totalForProj===1?"":"s"}</div>
              </div>

              {dates.map(date => (
                <div key={date} style={{marginBottom:14}}>
                  <div style={{fontSize:10, fontWeight:700, color:T.gold, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:8}}>{formatDate(date)}</div>
                  <div style={{display:"flex", flexDirection:"column", gap:8}}>
                    {grouped[projId][date].map(r => (
                      <div key={r.id} style={{background:T.bg2, border:`1px solid ${T.subtle}`, borderRadius:9, padding:"12px 14px"}}>
                        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, marginBottom:8, flexWrap:"wrap"}}>
                          <div style={{display:"flex", alignItems:"center", gap:8, flexWrap:"wrap"}}>
                            <span style={{background:T.goldBg, color:T.gold, border:`1px solid ${T.goldDim}`, borderRadius:5, padding:"2px 8px", fontSize:11, fontWeight:700}}>{r.trade}</span>
                            <span style={{fontSize:12, color:T.text, fontWeight:600}}>{r.crew_count} on crew</span>
                          </div>
                          <span style={{fontSize:11, color:T.muted}}>{formatTime(r.submitted_at)}</span>
                        </div>

                        {r.work_done && (
                          <div style={{marginBottom: (r.requests && r.requests.trim()) ? 10 : 0}}>
                            <div style={{fontSize:10, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3}}>Work done</div>
                            <div style={{fontSize:13, color:T.text, lineHeight:1.5, whiteSpace:"pre-wrap"}}>{r.work_done}</div>
                          </div>
                        )}

                        {r.requests && r.requests.trim() && (
                          <div style={{background:T.amberBg, border:`1px solid ${T.amber}40`, borderRadius:7, padding:"8px 12px", marginTop:4}}>
                            <div style={{fontSize:10, fontWeight:700, color:T.amber, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3}}>⚠ Needs GC action</div>
                            <div style={{fontSize:13, color:T.text, lineHeight:1.5, whiteSpace:"pre-wrap"}}>{r.requests}</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function QRView({session}) {
  const cid = session?.companyId || window._grokCompanyId ||
    (() => { try { return JSON.parse(localStorage.getItem("grok_auth_session")||"{}").companyId || "admin_001"; } catch { return "admin_001"; }})();
  const liveProjects = (() => {
    try { const s = localStorage.getItem("grok_projects_"+cid); if(s){const p=cleanProjects(JSON.parse(s));if(p.length)return p;} } catch{}
    return cleanProjects(getInitialProjects(cid));
  })();
  const [selected,setSelected]=useState(liveProjects[0]||null);
  const [copied,setCopied]=useState(false);
  // Daily-log URL: field workers scan the QR and land on /log to submit a report.
  // Includes company id (for daily_reports.company_id) + project id + a display
  // name so /log can show which project they're submitting against without a DB lookup.
  const projectDisplayName = selected ? (selected.name?.split(" — ")[0] || selected.name || "Project") : "";
  const url = selected && cid
    ? `https://grokfieldops.com/log?c=${encodeURIComponent(cid)}&p=${encodeURIComponent(selected.id)}&pn=${encodeURIComponent(projectDisplayName)}`
    : "";
  const qrImgSrc = url ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&margin=8&data=${encodeURIComponent(url)}` : "";
  const copy=()=>{if(url){navigator.clipboard?.writeText(url);setCopied(true);setTimeout(()=>setCopied(false),2000);}};

  // Re-check after mount
  React.useEffect(()=>{
    if(!selected){const p=getInitialProjects(cid);if(p.length)setSelected(p[0]);}
  },[cid]); // eslint-disable-line

  if (!liveProjects.length && !selected) return (
    <div style={{padding:24,textAlign:"center",paddingTop:60}}>
      <div style={{fontSize:40,marginBottom:12}}>📱</div>
      <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:8}}>No projects yet</div>
      <div style={{fontSize:13,color:T.muted}}>Create a project first, then generate QR site cards here.</div>
    </div>
  );

  return (
    <div style={{padding:24}}>
      <div style={{marginBottom:20}}><div style={{fontSize:20,fontWeight:800,color:T.text}}>QR Site Cards</div><div style={{fontSize:12,color:T.muted,marginTop:3}}>Generate QR codes for any project — scan to see live status</div></div>
      <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
        <Card>
          <SectionTitle>Select Project</SectionTitle>
          {liveProjects.map(p=>(
            <div key={p.id} onClick={()=>setSelected(p)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:9,marginBottom:6,cursor:"pointer",border:`1px solid ${selected?.id===p.id?T.goldDim:T.subtle}`,background:selected?.id===p.id?T.goldBg:T.bg2,transition:"all 0.15s"}}>
              <div style={{width:7,height:7,borderRadius:"50%",flexShrink:0,background:p.pct>70?T.green:p.pct>30?T.amber:T.blue}}/>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:T.text}}>{p.name?.split(" — ")[0]}</div>
                <div style={{fontSize:11,color:T.muted}}>{p.addr}</div>
              </div>
              <div style={{fontSize:12,fontWeight:700,color:T.gold}}>{p.pct}%</div>
            </div>
          ))}
        </Card>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <Card>
            <SectionTitle>QR Code — {selected?.name?.split(" — ")[0]||"Project"}</SectionTitle>
            <div style={{display:"flex",justifyContent:"center",marginBottom:14}}>
              <div style={{width:200,height:200,background:"white",borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",padding:8}}>
                {qrImgSrc
                  ? <img src={qrImgSrc} alt={`QR code linking to daily site report for ${projectDisplayName}`} style={{width:"100%",height:"100%",display:"block"}}/>
                  : <div style={{fontSize:11,color:"#666",textAlign:"center"}}>Select a project to generate a QR code</div>
                }
              </div>
            </div>
            <div style={{textAlign:"center",marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>{selected.name.split(" — ")[0]}</div>
              <div style={{fontSize:11,color:T.muted,marginBottom:10}}>{selected.addr}</div>
              <div style={{display:"flex",gap:6,justifyContent:"center"}}>
                <div style={{background:T.greenBg,border:`1px solid ${T.green}30`,borderRadius:6,padding:"4px 10px",fontSize:10,color:T.green,fontWeight:700}}>{selected.pct}% Complete</div>
                <div style={{background:T.goldBg,border:`1px solid ${T.goldDim}`,borderRadius:6,padding:"4px 10px",fontSize:10,color:T.gold,fontWeight:700}}>{selected.type}</div>
              </div>
            </div>
            <div style={{background:T.bg2,borderRadius:8,padding:"8px 12px",display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
              <span style={{fontSize:11,color:T.muted,fontFamily:"monospace"}}>{url}</span>
              <Btn small ghost onClick={copy}><Ic d={ICONS.copy} size={12}/>{copied?"Copied!":"Copy"}</Btn>
            </div>
            <div style={{display:"flex",gap:8}}>
              <Btn small primary style={{flex:1}}>Download PNG</Btn>
              <Btn small ghost style={{flex:1}}>Print Card</Btn>
            </div>
          </Card>
          <Card>
            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:8}}>What the QR Code Shows</div>
            {["Live phase progress & completion %","Open Change Orders and status","Open RFIs","Project team contact info","Phase timeline & milestones"].map(item=>(
              <div key={item} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:"1px solid "+T.subtle}}>
                <span style={{color:T.green,fontSize:12}}>✓</span>
                <span style={{fontSize:12,color:T.muted}}>{item}</span>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}



// ─── AUTO DAILY LOG GENERATOR ─────────────────────────────────────────────────
// Pulls from time tracker, sub logs, meetings, COs, RFIs for today
function AutoDailyLogBtn({color, session}) {
  const [proj, setProj] = React.useState(PROJECTS[0]||null);
  const [generating, setGenerating] = React.useState(false);
  const [showPicker, setShowPicker] = React.useState(false);

  const generate = () => {
    setGenerating(true);
    const today = new Date().toLocaleDateString();
    const todayFull = new Date().toLocaleDateString("en-US",{weekday:"long",year:"numeric",month:"long",day:"numeric"});
    const refNum = "LOG-"+(proj?.id?.toString().slice(-4)||"0001")+"-"+Date.now().toString().slice(-4);

    // Pull time tracker entries for today
    let timeEntries = [];
    try { timeEntries = JSON.parse(localStorage.getItem("grok_time_"+(proj?.id||""))||"[]"); } catch(e) {}
    const todayTime = timeEntries.filter(e => e.date === today || e.date === new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"}));

    // Pull sub logs
    const subLogs = (SUB_PORTAL_DATA?.logs||[]).filter(l =>
      (String(l.projectId)===String(proj?.id)) && (l.date === today)
    );

    // Pull meetings
    let meetings = [];
    try {
      const mk = "grok_meetings_"+(session?.companyId||"local")+"_"+proj?.id;
      const allMtgs = JSON.parse(localStorage.getItem(mk)||"[]");
      meetings = allMtgs.filter(m => m.date === today || m.date === new Date().toLocaleDateString("en-US",{month:"numeric",day:"numeric",year:"numeric"}));
    } catch(e) {}

    // Build manpower rows
    const gcByTrade = {};
    todayTime.forEach(e => {
      if (!gcByTrade[e.trade]) gcByTrade[e.trade] = {workers:0, hours:0, names:[]};
      gcByTrade[e.trade].workers++;
      gcByTrade[e.trade].hours += parseFloat(e.hours)||0;
      gcByTrade[e.trade].names.push(e.worker);
    });

    let manpowerHTML = "";
    Object.entries(gcByTrade).forEach(([trade,d]) => {
      manpowerHTML += "<tr><td>"+trade+"</td><td>Grok Construction Group</td><td>"+(d.names[0]||"—")+"</td><td>"+d.workers+"</td><td>"+d.hours+"h</td><td>"+(d.workers*d.hours)+"</td></tr>";
    });
    subLogs.forEach(l => {
      manpowerHTML += "<tr><td>"+(l.trade||"General")+"</td><td>"+(l.company||l.subName||"")+"</td><td>"+(l.subName||"")+"</td><td>"+(l.crew_size||"—")+"</td><td>"+(l.hours||"—")+"h</td><td>"+(parseInt(l.crew_size||1)*parseFloat(l.hours||0))+"</td></tr>";
    });
    if (!manpowerHTML) manpowerHTML = "<tr><td colspan='6' style='color:#94a3b8;text-align:center'>No time entries logged today — add via Field tab</td></tr>";
    const totalW = todayTime.length + subLogs.reduce((s,l)=>s+(parseInt(l.crew_size)||1),0);
    const totalH = todayTime.reduce((s,e)=>s+(parseFloat(e.hours)||0),0) + subLogs.reduce((s,l)=>s+(parseFloat(l.hours)||0)*(parseInt(l.crew_size)||1),0);
    manpowerHTML += "<tr style='font-weight:700;border-top:2px solid #0f1923'><td colspan='4' style='text-align:right'>Total</td><td>"+totalW+" workers</td><td>"+Math.round(totalH*10)/10+" hrs</td></tr>";

    // Build work performed
    let workLines = [];
    todayTime.filter(e=>e.notes).forEach(e => workLines.push("• "+e.trade+": "+e.notes+" ("+e.worker+", "+e.hours+"h)"));
    subLogs.filter(l=>l.work_done).forEach(l => {
      let line = "• "+(l.trade||"General")+" ("+l.subName+"): "+l.work_done;
      if (l.progress_pct) line += " — "+l.progress_pct+"% complete";
      workLines.push(line);
    });
    const workHTML = workLines.length ? workLines.join("<br>") : "No work logged today. Add via Field → Time Tracker or sub PIN login.";

    // Meetings
    const meetingHTML = meetings.length ? meetings.map(m => "• "+m.type+(m.attendees?" ("+m.attendees+")":"")+": "+(m.summary||m.notes||"Notes recorded")).join("<br>") : "";

    // Tomorrow plan
    const tomorrowLines = subLogs.filter(l=>l.tomorrow_plan).map(l => "• "+l.subName+": "+l.tomorrow_plan);
    const tomorrowHTML = tomorrowLines.length ? tomorrowLines.join("<br>") : "Add tomorrow's plan...";

    // Open COs and RFIs
    const openCOs = COS.filter(co=>String(co.projectId)===String(proj?.id)&&co.status==="pending");
    const openRFIs = RFIS.filter(r=>String(r.projectId)===String(proj?.id)&&r.status!=="closed");
    const rfiHTML = openRFIs.length ? openRFIs.slice(0,5).map(r=>"• "+(r.num||r.rfi_number||"")+" — "+(r.subject||"")+" ("+(r.priority||"normal")+" priority)").join("<br>") : "None open.";

    const css = "*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Helvetica Neue',Arial,sans-serif;font-size:11px;color:#0f1923;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}.page{max-width:780px;margin:0 auto;padding:48px 56px}.hdr{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:16px;margin-bottom:24px;border-bottom:2.5px solid #0f1923}.co-name{font-size:16px;font-weight:700}.co-sub{font-size:9px;color:#64748b;line-height:1.9;margin-top:4px}.sect{margin-bottom:20px}.sect-lbl{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.14em;color:#94a3b8;padding-bottom:5px;border-bottom:1px solid #e2e8f0;margin-bottom:10px}table{width:100%;border-collapse:collapse;font-size:10px}th{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#94a3b8;padding:6px 8px;border-bottom:1.5px solid #0f1923;text-align:left}td{padding:7px 8px;border-bottom:1px solid #f1f5f9;color:#334155;vertical-align:top}.work-box{background:#f8fafc;border-radius:6px;padding:12px 14px;font-size:10px;color:#334155;line-height:1.9}.sig-grid{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:24px;padding-top:20px;border-top:1px solid #e2e8f0}.sig-line{border-bottom:1.5px solid #0f1923;height:40px;margin-bottom:4px}.sig-lbl{font-size:9px;color:#94a3b8;text-transform:uppercase;letter-spacing:.08em}.dt-line{border-bottom:1px solid #cbd5e1;height:22px;margin-top:8px;margin-bottom:3px}.ftr{margin-top:20px;padding-top:12px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:9px;color:#94a3b8}.highlight{background:#fffbeb;border-left:3px solid #f59e0b;padding:8px 12px;font-size:10px;color:#78350f;line-height:1.7;margin:6px 0}@media print{.page{padding:32px 40px}}";

    const html = "<!DOCTYPE html><html><head><meta charset='utf-8'><title>Daily Log</title><style>"+css+"</style></head><body><div class='page'>"
      +"<div class='hdr'><div><div class='co-name'>[Your Company Name]</div><div class='co-sub'>CSLB #[Your License] &middot; [Your Address]<br>[Your Phone] &middot; [Your Email]</div></div>"
      +"<div style='text-align:right'><div style='font-size:22px;font-weight:300'>Daily Construction Log</div><div style='font-size:9px;color:#94a3b8;margin-top:3px;text-transform:uppercase;letter-spacing:.14em'>"+refNum+"</div><div style='font-size:10px;color:#64748b;margin-top:6px'>"+todayFull+"</div></div></div>"
      +"<div class='sect'><div class='sect-lbl'>Project</div>"
      +"<div style='display:grid;grid-template-columns:1fr 1fr;gap:16px'>"
      +"<div><div style='font-size:9px;color:#94a3b8;text-transform:uppercase;margin-bottom:2px'>Project</div><div style='font-size:12px;font-weight:600'>"+(proj?.name||"—")+"</div></div>"
      +"<div><div style='font-size:9px;color:#94a3b8;text-transform:uppercase;margin-bottom:2px'>Address</div><div style='font-size:12px;font-weight:600'>"+(proj?.addr||"—")+"</div></div>"
      +"<div><div style='font-size:9px;color:#94a3b8;text-transform:uppercase;margin-bottom:2px'>Progress</div><div style='font-size:12px;font-weight:600'>"+(proj?.pct||0)+"% Complete</div></div>"
      +"<div><div style='font-size:9px;color:#94a3b8;text-transform:uppercase;margin-bottom:2px'>Open COs / RFIs</div><div style='font-size:12px;font-weight:600'>"+openCOs.length+" COs &middot; "+openRFIs.length+" RFIs</div></div>"
      +"</div></div>"
      +"<div class='sect'><div class='sect-lbl'>Manpower on Site &mdash; "+todayFull+" <span style='font-size:9px;color:#84cc16;font-weight:600'>&bull; AUTO FROM TIME TRACKER + SUB LOGS</span></div>"
      +"<table><tr><th>Trade</th><th>Company</th><th>Superintendent</th><th>Workers</th><th>Hours</th><th>Man-Hours</th></tr>"+manpowerHTML+"</table></div>"
      +"<div class='sect'><div class='sect-lbl'>Work Performed Today <span style='font-size:9px;color:#84cc16;font-weight:600'>&bull; FROM TIME TRACKER + SUB LOGS</span></div><div class='work-box'>"+workHTML+"</div></div>"
      +(meetings.length ? "<div class='sect'><div class='sect-lbl'>Meetings Today <span style='font-size:9px;color:#84cc16;font-weight:600'>&bull; FROM MEETING NOTES</span></div><div class='work-box'>"+meetingHTML+"</div></div>" : "")
      +"<div class='sect'><div class='sect-lbl'>Open RFIs</div><div style='font-size:10px;color:#334155;line-height:1.9'>"+rfiHTML+"</div></div>"
      +"<div class='sect'><div class='sect-lbl'>Inspections &amp; Safety</div>"
      +"<div style='display:flex;gap:16px;flex-wrap:wrap;font-size:10px;color:#334155'>"
      +"<label><input type='checkbox'> No inspection today</label>"
      +"<label><input type='checkbox'> Inspection passed</label>"
      +"<label><input type='checkbox'> No safety incidents</label>"
      +"<label><input type='checkbox'> Site secured end of day</label>"
      +"<label><input type='checkbox'> Occupied units protected</label>"
      +"</div></div>"
      +"<div class='sect'><div class='sect-lbl'>Tomorrow's Planned Work <span style='font-size:9px;color:#84cc16;font-weight:600'>&bull; FROM SUB LOGS</span></div><div class='work-box' style='color:#64748b'>"+tomorrowHTML+"</div></div>"
      +"<div class='highlight'>Auto-generated from Grok Field Ops &middot; "+todayFull+"<br>Sources: Time Tracker ("+todayTime.length+" entries) &middot; Sub Logs ("+subLogs.length+") &middot; Meetings ("+meetings.length+")</div>"
      +"<div class='sig-grid'><div><div class='sig-line'></div><div class='sig-lbl'>Superintendent Signature</div><div style='font-size:10px;font-weight:600;color:#334155;margin-top:3px'>[Your Name] &mdash; [Your Company Name]</div><div class='dt-line'></div><div class='sig-lbl'>Date &amp; Time</div></div>"
      +"<div style='font-size:10px;color:#64748b;padding-top:8px;line-height:1.7'>Auto-generated from Grok Field Ops. Data from time tracker, sub PIN logins, and meeting notes. Official project record.</div></div>"
      +"<div class='ftr'><span>Grok Construction Group Inc &middot; CSLB #1152750</span><span>Daily Log &middot; "+todayFull+"</span></div>"
      +"</div></body></html>";

    const w = window.open("","_blank");
    w.document.write(html);
    w.document.close();
    setTimeout(()=>w.print(),400);
    setGenerating(false);
    setShowPicker(false);
  };

  return (
    <div>
      {showPicker&&(
        <div style={{marginBottom:8}}>
          <div style={{fontSize:10,color:T.muted,marginBottom:4}}>Select Project</div>
          <select value={proj?.id||""} onChange={e=>setProj((PROJECTS||[]).find(p=>String(p.id)===e.target.value))}
            style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"5px 8px",color:T.text,fontSize:12,marginBottom:6,fontFamily:"inherit"}}>
            {(PROJECTS||[]).filter(p=>p.status==="active").map(p=><option key={p.id} value={String(p.id)}>{(p.name||"").slice(0,35)}</option>)}
          </select>
        </div>
      )}
      <button onClick={()=>{
        if(!showPicker){setShowPicker(true);return;}
        generate();
      }} disabled={generating}
        style={{width:"100%",padding:"8px",background:generating?"#374151":"linear-gradient(135deg,"+color+","+(color+"cc")+")",color:"white",border:"none",borderRadius:7,fontSize:11,fontWeight:700,cursor:generating?"not-allowed":"pointer"}}>
        {generating?"⏳ Building...":showPicker?"🚀 Generate Now":"📊 Auto-Generate from Data"}
      </button>
    </div>
  );
}


function DocsView({session}) {
  const cid = session?.companyId || window._grokCompanyId || "local";
  const STORE_KEY = "grok_templates_" + cid;
  const getTemplates = () => {
    try { return JSON.parse(localStorage.getItem(STORE_KEY)||"[]"); } catch { return []; }
  };

  const [templates, setTemplates] = React.useState(getTemplates);

  // Re-read 500ms after mount to catch AuthGate seeding
  React.useEffect(()=>{
    const timer = setTimeout(()=>{
      const fresh = getTemplates();
      setTemplates(fresh);
    }, 500);
    return ()=>clearTimeout(timer);
  }, [STORE_KEY]);
  const TEMPLATE_CATS = [
    // Grok Construction Group Inc — Document Kit
    // CSLB #1152750 · 2201 Bridgepointe Pkwy F125, San Mateo CA 94404
    { id:"contracts",   label:"Contracts & Proposals",  icon:"📑", color:"#3b82f6",
      examples:["GCG Owner Contract (Fixed Price)","GCG Subcontractor Agreement","GCG Proposal Template — Lump Sum","GCG Scope of Work","NDA — Grok Construction Group","Pre-Construction Agreement"] },
    { id:"co",          label:"Change Orders",           icon:"📝", color:"#f59e0b",
      examples:["GCG Change Order Form","CO Log — Grok","Pending CO Request","Directive Letter","T&M Change Order"] },
    { id:"rfi",         label:"RFIs",                    icon:"❓", color:"#8b5cf6",
      examples:["GCG RFI Form","RFI Log","Architect Response Form","Clarification Request"] },
    { id:"submittals",  label:"Submittals",              icon:"📋", color:"#06b6d4",
      examples:["Submittal Cover Sheet","Shop Drawing Log","Product Data Sheet","Submittal Transmittal"] },
    { id:"lien",        label:"Lien Waivers",            icon:"📜", color:"#f43f5e",
      examples:["Conditional Lien Waiver on Progress Payment","Unconditional Lien Waiver on Progress Payment","Conditional Lien Waiver on Final Payment","Unconditional Lien Waiver on Final Payment","20-Day Preliminary Notice — GCG","Lien Release"] },
    { id:"billing",     label:"Billing & Pay Apps",      icon:"💰", color:"#d4a843",
      examples:["Pay Application (AIA G702/G703)","Schedule of Values — GCG","GCG Invoice Template","Certified Payroll (DIR)","Retention Release Request","Stored Materials Form"] },
    { id:"safety",      label:"Safety & Compliance",     icon:"🦺", color:"#ef4444",
      examples:["GCG Safety Plan","Incident Report","Toolbox Talk Form","OSHA Checklist","Hazard Communication Plan","SRO Occupied Building Safety Protocol"] },
    { id:"sro",         label:"SRO & Affordable Housing", icon:"🏘", color:"#8b5cf6",
      examples:["SRO Tenant Notice (7-Day)","HUD Lead Paint Disclosure","Relocation Notice","Occupied Unit Protocol","Prevailing Wage Certification","DIR Registration Confirmation"] },
    { id:"closeout",    label:"Closeout Documents",      icon:"✅", color:"#14b8a6",
      examples:["GCG Punch List Template","Warranty Letter — 1 Year","O&M Manual Cover","Final Lien Waiver Package","Certificate of Substantial Completion","Closeout Checklist"] },
    { id:"field",       label:"Field Reports",           icon:"📷", color:"#84cc16",
      examples:["GCG Daily Log","Photo Report","Site Meeting Minutes","Site Observation Report","Subcontractor Daily Report","Weather Delay Notice"] },
    { id:"other",       label:"Internal Templates",      icon:"📁", color:"#64748b",
      examples:["Job Cost Worksheet","GCG Budget Tracker","Sub Bid Comparison","Project Checklist — SRO","Overhead Calculator","New Employee Onboarding"] },
  ];

  const [activeTab, setActiveTab] = React.useState("all");
  const [search, setSearch] = React.useState("");
  const [showAdd, setShowAdd] = React.useState(false);
  const [form, setForm] = React.useState({name:"", cat:"contracts", notes:""});
  const [editId, setEditId] = React.useState(null);
  const fileRef = React.useRef();

  const save = (updated) => {
    setTemplates(updated);
    try{localStorage.setItem(STORE_KEY, JSON.stringify(updated));}catch(e){}
  };

  // Auto-detect category from filename
  const autoDetectCat = (filename) => {
    const n = filename.toLowerCase();
    if (/contract|agreement|proposal|scope.*work|nda|pre.?construction/.test(n)) return "contracts";
    if (/change.?order|^co[-_\s]|directive/.test(n)) return "co";
    if (/rfi|request.*info|clarif/.test(n)) return "rfi";
    if (/lien|waiver|prelim|20.?day/.test(n)) return "lien";
    if (/invoice|pay.?app|billing|payroll|g702|g703|retention/.test(n)) return "billing";
    if (/safety|osha|incident|hazard|toolbox/.test(n)) return "safety";
    if (/sro|affordable|tenant|relocation|hud/.test(n)) return "sro";
    if (/punch|closeout|warranty|substantial|completion/.test(n)) return "closeout";
    if (/daily.?log|site.*report|photo|meeting|weather/.test(n)) return "field";
    if (/submittal|shop.?drawing|product.?data/.test(n)) return "submittals";
    return "other";
  };

  // Auto-detect name from filename
  const autoDetectName = (filename) => {
    return filename.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ").replace(/\b\w/g, c=>c.toUpperCase());
  };

  const handleUpload = (file) => {
    if(file.size > 5*1024*1024) {
      alert("File too large (max 5MB). Use Google Drive and add a link instead.");
      return;
    }
    const cat = autoDetectCat(file.name);
    const name = autoDetectName(file.name);
    const reader = new FileReader();
    reader.onload = ev => {
      const t = {
        id: "tpl_"+Date.now(),
        name,
        cat,
        notes: "",
        fileName: file.name,
        size: (file.size/1024).toFixed(0)+"KB",
        data: ev.target.result,
        stored: true,
        uploadedAt: new Date().toLocaleDateString(),
        uploadedBy: session?.email||"owner",
      };
      save([t,...templates]);
    };
    reader.readAsDataURL(file);
  };

  const addLink = () => {
    if(!form.name && !form.link) return;
    const t = {
      id:"tpl_"+Date.now(), name:form.name||form.link,
      cat:form.cat, notes:form.notes, link:form.link,
      uploadedAt:new Date().toLocaleDateString(),
      uploadedBy:session?.email||"owner",
    };
    save([t,...templates]);
    setForm({name:"",cat:"contracts",notes:"",link:""}); setShowAdd(false);
  };

  const del = (id) => {
    if(!true) return;
    save(templates.filter(t=>t.id!==id));
  };

  const openTemplate = (t) => {
    if(t.htmlContent){
      // Embedded HTML content — open in new tab
      const w = window.open("","_blank");
      w.document.write(t.htmlContent);
      w.document.close();
      setTimeout(()=>w.print(),600);
    } else if(t.data){
      // Uploaded file — open in new tab
      const w = window.open("","_blank");
      if(t.fileName?.match(/\.(pdf)$/i)){
        w.document.write(`<iframe src="${t.data}" style="width:100%;height:100vh;border:none;"></iframe>`);
      } else {
        const a=document.createElement("a"); a.href=t.data; a.download=t.fileName||t.name;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        w.close();
      }
    } else if(t.link){
      window.open(t.link.startsWith("http")?t.link:"https://"+t.link,"_blank");
    } else if(t.isGCG){
      // GCG template — show info card with notes
      const cs = (() => { try { return JSON.parse(localStorage.getItem("grok_company_settings_"+cid)||"{}"); } catch { return {}; }})();
      const coName = cs.company_name || session?.companyName || "Grok Construction Group Inc";
      const w = window.open("","_blank");
      w.document.write(`<!DOCTYPE html><html><head><title>${t.name}</title>
        <style>body{font-family:Arial,sans-serif;max-width:700px;margin:40px auto;padding:20px;color:#1a1a1a;}
        h1{font-size:22px;margin-bottom:4px;} .co{font-size:13px;color:#666;margin-bottom:24px;}
        .info{background:#f8f8f8;border:1px solid #ddd;border-radius:8px;padding:16px;margin-bottom:20px;}
        .label{font-size:11px;font-weight:700;text-transform:uppercase;color:#999;margin-bottom:4px;}
        .note{font-size:14px;line-height:1.6;color:#333;} .badge{display:inline-block;background:#D4A843;color:white;font-size:11px;font-weight:700;padding:3px 10px;border-radius:20px;margin-bottom:16px;}
        .upload-note{background:#fff3cd;border:1px solid #ffc107;border-radius:8px;padding:12px 16px;font-size:12px;color:#856404;margin-top:20px;}
        </style></head><body>
        <div class="badge">GCG Template</div>
        <h1>${t.name}</h1>
        <div class="co">${coName} · CSLB #1152750</div>
        <div class="info">
          <div class="label">Category</div>
          <div class="note" style="margin-bottom:12px">${t.cat}</div>
          <div class="label">Description</div>
          <div class="note">${t.notes||"Standard GCG document template."}</div>
        </div>
        <div class="upload-note">📎 This is a template record. To access the actual document file, upload the PDF or Word version using the Upload button in Document Templates.</div>
        <script>setTimeout(()=>window.print(),300)</script>
        </body></html>`);
      w.document.close();
    }
  };

  const filtered = templates.filter(t=>{
    const matchCat = activeTab==="all" || t.cat===activeTab;
    const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const catCount = (id) => templates.filter(t=>t.cat===id).length;
  const inp = {width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"};

  return (
    <div style={{padding:24}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:T.text,marginBottom:4}}>Document Templates</div>
          <div style={{fontSize:13,color:T.muted}}>
            Your company's own templates · {templates.length} uploaded · Private to {session?.companyName||"your company"}
          </div>
        </div>
        <div style={{display:"flex",gap:7}}>
          <input ref={fileRef} type="file" style={{display:"none"}} multiple
            accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx"
            onChange={e=>{
              Array.from(e.target.files||[]).forEach(f=>handleUpload(f));
              e.target.value="";
            }}/>
          <Btn ghost onClick={()=>setShowAdd(p=>!p)}>+ Add Link</Btn>
          <Btn primary onClick={()=>fileRef.current?.click()}>
            <Ic d={ICONS.plus} size={12}/> Upload Templates
          </Btn>
        </div>
      </div>

      {/* Empty state */}
      {templates.length===0&&!showAdd&&(
        <div>
          <div style={{background:T.amberBg,border:"1px solid "+T.amber+"40",borderRadius:10,padding:"14px 18px",marginBottom:12,display:"flex",gap:12,alignItems:"flex-start"}}>
            <span style={{fontSize:20,flexShrink:0}}>📁</span>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:T.amber,marginBottom:4}}>Your Document Library is Empty</div>
              <div style={{fontSize:11,color:T.muted,lineHeight:1.7}}>
                Upload your own contracts, CO forms, lien waivers, proposals — any Word doc or PDF. The app will use your templates to auto-fill documents.
              </div>
            </div>
          </div>
          <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:10,padding:"16px 18px",marginBottom:12}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:10}}>⚡ How it works</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {[["1️⃣","Upload your templates","Your CO form, owner contract, lien waiver, proposal"],
                ["2️⃣","App auto-fills them","Fill in project details automatically when you create a CO or RFI"],
                ["3️⃣","Send to owner or sub","Generate, review, sign, and send from within the project"],
                ["📎","Stays private","Only visible to your account and team"],
              ].map(([icon,title,desc])=>(
                <div key={title} style={{background:T.bg2,borderRadius:8,padding:"10px 12px"}}>
                  <div style={{fontSize:16,marginBottom:4}}>{icon}</div>
                  <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:3}}>{title}</div>
                  <div style={{fontSize:10,color:T.muted,lineHeight:1.5}}>{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add/Upload form */}
      {showAdd&&(
        <div style={{background:T.bg1,border:"1px solid "+T.blue+"30",borderRadius:10,padding:"16px 18px",marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Add Template</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Template Name *</div>
              <input value={form.name||""} onChange={e=>setForm(p=>({...p,name:e.target.value}))}
                placeholder="e.g. Change Order Form v3" style={inp}/>
            </div>
            <div>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Category</div>
              <select value={form.cat||"contracts"} onChange={e=>setForm(p=>({...p,cat:e.target.value}))} style={inp}>
                {TEMPLATE_CATS.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Link (Google Drive, Dropbox, etc.)</div>
            <input value={form.link||""} onChange={e=>setForm(p=>({...p,link:e.target.value}))}
              placeholder="https://drive.google.com/..." style={inp}/>
          </div>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Notes</div>
            <input value={form.notes||""} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}
              placeholder="e.g. Use for all projects over $50K" style={inp}/>
          </div>
          <div style={{display:"flex",gap:7}}>
            <Btn ghost small onClick={()=>setShowAdd(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
            <button onClick={()=>fileRef.current.click()}
              style={{flex:2,padding:"8px",background:T.teal,color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>
              📎 Upload File (.docx, .pdf, .xlsx)
            </button>
            {form.link&&<Btn primary small onClick={addLink} style={{flex:1,justifyContent:"center"}}>Save Link</Btn>}
          </div>
        </div>
      )}

      {/* GCG-only generate section — only for jehoon@grokconstructiongroupinc.com */}
      {cid===GCG_COMPANY_ID&&templates.length>0&&(
        <div style={{background:T.bg2,borderRadius:10,padding:"12px 16px",marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>✨ Generate GCG Documents</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:8}}>
            {[
              {icon:"📑",label:"GCG Owner Contract",color:"#3b82f6",cat:"contracts"},
              {icon:"🤝",label:"GCG Subcontractor Agreement",color:"#3b82f6",cat:"contracts"},
              {icon:"📝",label:"GCG Change Order Form",color:"#f59e0b",cat:"co"},
              {icon:"💰",label:"GCG Invoice Template",color:"#d4a843",cat:"billing"},
              {icon:"✅",label:"GCG Punch List",color:"#14b8a6",cat:"closeout"},
            ].map(t=>{
              const match = templates.find(tp=>tp.isGCG&&tp.cat===t.cat);
              return (
                <div key={t.label} style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"10px 12px"}}>
                  <div style={{fontSize:14,marginBottom:4}}>{t.icon}</div>
                  <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:6}}>{t.label}</div>
                  {match ? (
                    <button onClick={()=>openTemplate(match)}
                      style={{width:"100%",padding:"6px",background:`linear-gradient(135deg,${t.color},${t.color}cc)`,color:"white",border:"none",borderRadius:6,fontSize:10,fontWeight:700,cursor:"pointer"}}>
                      📄 Open & Print
                    </button>
                  ) : (
                    <div style={{fontSize:9,color:T.muted,fontStyle:"italic"}}>Upload to enable</div>
                  )}
                </div>
              );
            })}
            <div style={{background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:14,marginBottom:4}}>📷</div>
              <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:6}}>GCG Daily Log</div>
              <AutoDailyLogBtn color="#84cc16" session={session}/>
            </div>
          </div>
        </div>
      )}

      {/* Template list */}
      {templates.length>0&&(
        <>
          {/* Category tabs */}
          <div style={{display:"flex",gap:4,marginBottom:14,overflowX:"auto",paddingBottom:4}}>
            <button onClick={()=>setActiveTab("all")}
              style={{padding:"5px 12px",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",background:activeTab==="all"?T.gold:T.bg2,color:activeTab==="all"?T.bg0:T.muted,whiteSpace:"nowrap",flexShrink:0}}>
              All ({templates.length})
            </button>
            {TEMPLATE_CATS.filter(cat=>catCount(cat.id)>0).map(cat=>(
              <button key={cat.id} onClick={()=>setActiveTab(cat.id)}
                style={{padding:"5px 12px",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",
                  background:activeTab===cat.id?cat.color:T.bg2,
                  color:activeTab===cat.id?"white":T.muted,whiteSpace:"nowrap",flexShrink:0}}>
                {cat.icon} {cat.label} ({catCount(cat.id)})
              </button>
            ))}
          </div>

          {/* Search */}
          <div style={{position:"relative",marginBottom:12}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search templates..."
              style={{...inp,paddingLeft:30,fontSize:11}}/>
            <span style={{position:"absolute",left:9,top:"50%",transform:"translateY(-50%)",fontSize:12,color:T.muted}}>🔍</span>
            {search&&<button onClick={()=>setSearch("")} style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:T.muted,cursor:"pointer"}}>✕</button>}
          </div>

          {/* Recommended docs banner if missing key types */}
          {templates.length>0&&templates.length<5&&(()=>{
            const cats = new Set(templates.map(t=>t.cat));
            const missing = [
              {cat:"co",label:"Change Order Form",icon:"📝"},
              {cat:"lien",label:"Lien Waiver",icon:"📜"},
              {cat:"contracts",label:"Owner Contract",icon:"📑"},
              {cat:"billing",label:"Pay Application",icon:"💰"},
            ].filter(r=>!cats.has(r.cat));
            if(!missing.length) return null;
            return (
              <div style={{background:T.goldBg,border:`1px solid ${T.gold}30`,borderRadius:9,padding:"10px 14px",marginBottom:12,fontSize:11,color:T.muted,lineHeight:1.7}}>
                <strong style={{color:T.gold}}>📋 Recommended to add:</strong> {missing.map(m=>`${m.icon} ${m.label}`).join(" · ")}
              </div>
            );
          })()}

          {/* Template grid */}
          {filtered.length===0 ? (
            <div style={{textAlign:"center",padding:"32px",color:T.muted,background:T.bg2,borderRadius:10}}>
              <div style={{fontSize:12}}>No templates match your search.</div>
            </div>
          ) : (
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10}}>
              {filtered.map(t=>{
                const cat = TEMPLATE_CATS.find(c=>c.id===t.cat)||TEMPLATE_CATS[0];
                const canOpen = t.data || t.link || t.isGCG;
                return (
                  <div key={t.id} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,overflow:"hidden",
                    transition:"border-color 0.15s"}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=cat.color}
                    onMouseLeave={e=>e.currentTarget.style.borderColor=T.subtle}>
                    <div style={{background:cat.color+"12",borderBottom:"1px solid "+cat.color+"25",padding:"8px 12px",display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:16}}>{cat.icon}</span>
                      <div style={{flex:1,minWidth:0}}>
                        {editId===t.id ? (
                          <input defaultValue={t.name} autoFocus
                            onBlur={e=>{save(templates.map(x=>x.id===t.id?{...x,name:e.target.value}:x));setEditId(null);}}
                            onKeyDown={e=>{if(e.key==="Enter"){save(templates.map(x=>x.id===t.id?{...x,name:e.target.value}:x));setEditId(null);}}}
                            style={{fontSize:11,fontWeight:700,background:"none",border:"none",borderBottom:`1px solid ${cat.color}`,color:T.text,width:"100%",padding:"0 0 2px",fontFamily:"inherit"}}/>
                        ) : (
                          <div onClick={()=>setEditId(t.id)} title="Click to rename"
                            style={{fontSize:11,fontWeight:700,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",cursor:"text"}}>{t.name}</div>
                        )}
                        <select value={t.cat} onChange={e=>save(templates.map(x=>x.id===t.id?{...x,cat:e.target.value}:x))}
                          style={{fontSize:9,color:T.muted,background:"none",border:"none",cursor:"pointer",padding:0,fontFamily:"inherit"}}>
                          {TEMPLATE_CATS.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
                        </select>
                      </div>
                      <button onClick={()=>del(t.id)} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:12,padding:"2px 4px"}}>✕</button>
                    </div>
                    <div style={{padding:"10px 12px"}}>
                      {t.notes&&<div style={{fontSize:10,color:T.muted,marginBottom:8,lineHeight:1.5}}>{t.notes}</div>}
                      <div style={{fontSize:9,color:T.muted,marginBottom:8}}>
                        {t.fileName&&<span>📎 {t.fileName} · </span>}
                        {t.size&&<span>{t.size} · </span>}
                        <span>{t.uploadedAt}</span>
                        {t.isGCG&&<span style={{marginLeft:4,background:T.gold+"20",color:T.gold,borderRadius:3,padding:"0 4px"}}>GCG</span>}
                      </div>
                      <button onClick={()=>openTemplate(t)}
                        style={{width:"100%",padding:"6px",background:canOpen?cat.color:T.bg3,color:canOpen?"white":T.muted,border:"none",borderRadius:6,fontSize:10,fontWeight:700,cursor:canOpen?"pointer":"default"}}>
                        {t.link?"🔗 Open Link":t.htmlContent?"📄 Open & Print":t.isGCG?"📋 View Info":"📄 Open File"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{marginTop:14,background:T.bg2,borderRadius:9,padding:"10px 14px",fontSize:11,color:T.muted,display:"flex",gap:10,alignItems:"center"}}>
            <span style={{fontSize:16}}>💡</span>
            <div><strong style={{color:T.text}}>Tip:</strong> Upload your CO forms, lien waivers, proposals, and pay applications. Every project has access to all company templates.</div>
          </div>
        </>
      )}
    </div>
  );
}

function WalkthroughView() {
  const [step,setStep]=useState(0);
  const [checks,setChecks]=useState({});
  const toggle=k=>setChecks(p=>({...p,[k]:!p[k]}));
  const CB=({id,label,sub,warn})=>(
    <div onClick={()=>toggle(id)} style={{display:"flex",gap:10,alignItems:"flex-start",padding:"8px 10px",borderRadius:7,cursor:"pointer",background:checks[id]?warn?T.redBg:T.greenBg:"transparent",transition:"background 0.15s",marginBottom:3}}>
      <div style={{width:17,height:17,borderRadius:4,border:`2px solid ${checks[id]?warn?T.red:T.green:T.subtle}`,background:checks[id]?warn?T.red:T.green:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1,transition:"all 0.15s"}}>
        {checks[id]&&<Ic d={ICONS.check} size={9} style={{color:T.bg0}}/>}
      </div>
      <div>
        <div style={{fontSize:12,color:checks[id]?warn?T.red:T.green:T.text}}>{label}</div>
        {sub&&<div style={{fontSize:10,color:T.muted,marginTop:1}}>{sub}</div>}
      </div>
    </div>
  );
  const steps=[
    {title:"Access & Site",items:[
      {id:"s1",label:"Building entrance accessible — note restrictions"},
      {id:"s2",label:"Parking / staging area confirmed"},
      {id:"s3",label:"Access hours confirmed with manager"},
      {id:"s4",label:"Key / code obtained and tested"},
      {id:"s5",label:"Existing conditions photographed BEFORE anything"},
    ]},
    {title:"Plumbing & Electrical",items:[
      {id:"p1",label:"Main water shut-off located"},
      {id:"p2",label:"Drain locations confirmed — no conflicts with new layout",sub:"Red flag: drain in wrong spot = Change Order",warn:true},
      {id:"p3",label:"Checked for moisture at connections"},
      {id:"p4",label:"Electrical panel located — amperage noted"},
      {id:"p5",label:"GFCI protection near water sources"},
    ]},
    {title:"Walls & Red Flags",items:[
      {id:"w1",label:"Drywall checked for water stains / soft spots"},
      {id:"w2",label:"Ceiling checked for stains / leaks from above"},
      {id:"w3",label:"Floor checked for soft spots or bounce"},
      {id:"w4",label:"Musty or mold smell noted",sub:"Stop and advise owner immediately",warn:true},
      {id:"w5",label:"Building age noted — pre-1978 flag for lead/asbestos",sub:"If pre-1978, note in contract exclusions",warn:true},
    ]},
    {title:"Owner Q&A",items:[
      {id:"q1",label:"Asked: any water damage or leaks before?"},
      {id:"q2",label:"Asked: any mold found in unit or building?"},
      {id:"q3",label:"Asked: known plumbing or drain issues?"},
      {id:"q4",label:"Asked: renovated before? Permits pulled?"},
      {id:"q5",label:"Asked: unit occupied during construction?"},
    ]},
  ];
  const totalItems=steps.reduce((s,st)=>s+st.items.length,0);
  const done=Object.keys(checks).filter(k=>!k.startsWith("rf_")&&checks[k]).length;
  const redFlags=["Water stains / soft drywall","Musty smell / mold","Drain location conflict","Pre-1978 building","Visible mold","Non-standard electrical"];
  const flagsMarked=redFlags.filter(f=>checks["rf_"+f]).length;
  return (
    <div style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div><div style={{fontSize:20,fontWeight:800,color:T.text}}>Site Walkthrough</div><div style={{fontSize:12,color:T.muted,marginTop:3}}>Complete with owner before estimating</div></div>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          <span style={{fontSize:12,color:T.muted}}>{done}/{totalItems} checked</span>
          <div style={{width:100,height:5,background:T.bg3,borderRadius:3}}><div style={{height:"100%",width:`${Math.round(done/totalItems*100)}%`,background:T.gold,borderRadius:3}}/></div>
          {flagsMarked>0&&<span style={{background:T.redBg,border:`1px solid ${T.red}30`,borderRadius:20,fontSize:10,fontWeight:700,color:T.red,padding:"2px 8px"}}>{flagsMarked} red flags</span>}
        </div>
      </div>
      <div style={{display:"flex",gap:7,marginBottom:18}}>
        {steps.map((s,i)=>(
          <button key={i} onClick={()=>setStep(i)} style={{padding:"6px 14px",borderRadius:20,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:step===i?T.gold:T.bg2,color:step===i?T.bg0:T.muted,transition:"all 0.15s"}}>{s.title}</button>
        ))}
      </div>
      <div className="two-col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
        <Card>
          <SectionTitle>{steps[step].title}</SectionTitle>
          {steps[step].items.map(item=><CB key={item.id} {...item}/>)}
        </Card>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <Card>
            <SectionTitle>Red Flags — Mark If Seen</SectionTitle>
            {redFlags.map(f=>(
              <div key={f} onClick={()=>toggle("rf_"+f)} style={{display:"flex",gap:9,alignItems:"center",padding:"7px 9px",borderRadius:6,cursor:"pointer",marginBottom:3,background:checks["rf_"+f]?T.redBg:"transparent",transition:"background 0.15s"}}>
                <div style={{width:16,height:16,borderRadius:4,border:`2px solid ${checks["rf_"+f]?T.red:T.subtle}`,background:checks["rf_"+f]?T.red:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.15s"}}>
                  {checks["rf_"+f]&&<Ic d={ICONS.alert} size={9} style={{color:T.bg0}}/>}
                </div>
                <span style={{fontSize:12,color:checks["rf_"+f]?T.red:T.muted}}>{f}</span>
              </div>
            ))}
          </Card>
          <Card>
            <div style={{display:"flex",gap:8,marginBottom:12}}>
              <Btn small ghost onClick={()=>setStep(Math.max(0,step-1))} style={{opacity:step===0?0.4:1}}>← Prev</Btn>
              <Btn small primary onClick={()=>setStep(Math.min(steps.length-1,step+1))} style={{opacity:step===steps.length-1?0.4:1}}>Next →</Btn>
              <Btn small ghost>Save</Btn>
            </div>
            {done===totalItems&&(
              <div style={{padding:"10px 12px",background:T.greenBg,borderRadius:8,border:`1px solid ${T.green}30`}}>
                <div style={{fontSize:13,fontWeight:700,color:T.green}}>✓ Walkthrough Complete</div>
                <div style={{fontSize:11,color:T.muted,marginTop:2}}>Have owner sign. Proceed to scope checklist.</div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

// --- MAIN APP -----------------------------------------------------------------

// ─── SUB DASHBOARD — what a logged-in sub sees ───────────────────────────────
// Subs only see projects they're linked to, with limited tabs
// ── Sub Schedule Tab — sub marks their dates, auto-updates project schedule ────
function SubScheduleTab({proj, session, trade, tradeIcon}) {
  const schedKey = "grok_schedule_" + proj?.id;
  const subSchedKey = "grok_sub_schedule_" + proj?.id + "_" + (session?.subId||session?.subName||"sub");

  const [subSchedule, setSubSchedule] = React.useState(()=>{
    try { return JSON.parse(localStorage.getItem(subSchedKey)||"[]"); } catch { return []; }
  });
  const [form, setForm] = React.useState({
    milestone:"",
    startDate:"",
    endDate:"",
    notes:"",
    status:"planned",
  });
  const [saved, setSaved] = React.useState(false);

  const MILESTONES = [
    "Mobilization / Site setup",
    "Material delivery",
    "Rough-in start",
    "Rough-in complete",
    "Inspection ready",
    "Inspection passed",
    "Trim-out / Finish start",
    "Trim-out complete",
    "Final walkthrough",
    "Punch list complete",
    "Custom milestone",
  ];

  const saveEntry = () => {
    if (!form.milestone || !form.startDate) return;
    const entry = {
      id: Date.now(),
      trade,
      tradeIcon,
      subName: session?.subName || session?.company || "Sub",
      milestone: form.milestone,
      startDate: form.startDate,
      endDate: form.endDate,
      notes: form.notes,
      status: form.status,
      submittedAt: new Date().toISOString(),
    };

    const updated = [entry, ...subSchedule.filter(e=>e.milestone!==form.milestone)];
    setSubSchedule(updated);
    localStorage.setItem(subSchedKey, JSON.stringify(updated));

    // ── Write to project schedule so GC sees it ──────────────────────────────
    try {
      const existing = JSON.parse(localStorage.getItem(schedKey)||"null");
      if (existing?.weeks) {
        // Find the week matching startDate and inject as a task
        const startD = new Date(form.startDate);
        existing.weeks = existing.weeks.map(week=>{
          return {
            ...week,
            days: week.days.map(day=>{
              const dayD = new Date(day.date);
              if (Math.abs(dayD - startD) < 2*864e5) {
                // Within 2 days — add sub milestone as a task
                const subTask = {
                  id: "sub_"+entry.id,
                  task: `${tradeIcon} ${trade}: ${form.milestone}`,
                  trade,
                  assignee: entry.subName,
                  type: "sub_milestone",
                  time: "7:00 AM",
                  critical: form.milestone.toLowerCase().includes("inspection")||form.milestone.toLowerCase().includes("complete"),
                  subEntry: true,
                  endDate: form.endDate,
                  notes: form.notes,
                };
                // Remove old entry for same milestone if exists
                const filtered = (day.tasks||[]).filter(t=>t.id!=="sub_"+entry.id&&!(t.subEntry&&t.trade===trade&&t.task.includes(form.milestone)));
                return {...day, tasks:[...filtered, subTask]};
              }
              return day;
            })
          };
        });
        localStorage.setItem(schedKey, JSON.stringify(existing));
      }
    } catch(e) {}

    setForm({milestone:"",startDate:"",endDate:"",notes:"",status:"planned"});
    setSaved(true);
    setTimeout(()=>setSaved(false), 3000);
  };

  const removeEntry = (id) => {
    const updated = subSchedule.filter(e=>e.id!==id);
    setSubSchedule(updated);
    localStorage.setItem(subSchedKey, JSON.stringify(updated));
  };

  const STATUS_COLOR = {planned:T.blue, confirmed:T.green, delayed:T.red, complete:T.green};
  const STATUS_LABEL = {planned:"📅 Planned", confirmed:"✅ Confirmed", delayed:"⚠️ Delayed", complete:"✓ Complete"};

  return (
    <div>
      <div style={{background:T.tealBg,border:`1px solid ${T.teal}30`,borderRadius:9,padding:"10px 14px",marginBottom:16,fontSize:11,color:T.teal,display:"flex",gap:8,alignItems:"center"}}>
        <span style={{fontSize:16}}>{tradeIcon}</span>
        <span>Mark your upcoming milestones below — your GC's project schedule updates automatically so they always know where you stand.</span>
      </div>

      {/* Add milestone form */}
      <Card style={{marginBottom:16}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14}}>Add / Update a Milestone</div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <div style={{gridColumn:"1/-1"}}>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Milestone</div>
            <select value={form.milestone} onChange={e=>setForm(p=>({...p,milestone:e.target.value}))}
              style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}>
              <option value="">— Select milestone —</option>
              {MILESTONES.map(m=><option key={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Start Date</div>
            <input type="date" value={form.startDate} onChange={e=>setForm(p=>({...p,startDate:e.target.value}))}
              style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
          </div>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>End Date (optional)</div>
            <input type="date" value={form.endDate} onChange={e=>setForm(p=>({...p,endDate:e.target.value}))}
              style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
          </div>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Status</div>
            <select value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))}
              style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}>
              <option value="planned">📅 Planned</option>
              <option value="confirmed">✅ Confirmed</option>
              <option value="delayed">⚠️ Delayed</option>
              <option value="complete">✓ Complete</option>
            </select>
          </div>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Notes (optional)</div>
            <input value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}
              placeholder="e.g. Need access to unit 4B by 7am"
              style={{width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:7,padding:"9px 10px",color:T.text,fontSize:12,fontFamily:"inherit"}}/>
          </div>
        </div>

        <button disabled={!form.milestone||!form.startDate} onClick={saveEntry}
          style={{width:"100%",padding:"11px",borderRadius:8,border:"none",background:form.milestone&&form.startDate?T.teal:T.bg3,color:"white",fontSize:13,fontWeight:700,cursor:form.milestone&&form.startDate?"pointer":"not-allowed",transition:"all 0.2s"}}>
          {saved?"✅ Saved — GC schedule updated!":"Save & Update Project Schedule"}
        </button>
      </Card>

      {/* My milestones list */}
      {subSchedule.length>0&&(
        <div>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Your Submitted Milestones</div>
          {subSchedule.map(entry=>(
            <div key={entry.id} style={{background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:10,padding:"12px 14px",marginBottom:8,display:"flex",gap:12,alignItems:"flex-start"}}>
              <div style={{fontSize:20,flexShrink:0}}>{tradeIcon}</div>
              <div style={{flex:1}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                  <div style={{fontSize:12,fontWeight:700,color:T.text}}>{entry.milestone}</div>
                  <span style={{fontSize:9,fontWeight:700,background:STATUS_COLOR[entry.status]+"18",color:STATUS_COLOR[entry.status],borderRadius:20,padding:"2px 8px",border:`1px solid ${STATUS_COLOR[entry.status]}30`}}>
                    {STATUS_LABEL[entry.status]}
                  </span>
                </div>
                <div style={{fontSize:11,color:T.muted,display:"flex",gap:16,flexWrap:"wrap"}}>
                  <span>📅 Start: <strong style={{color:T.text}}>{new Date(entry.startDate).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</strong></span>
                  {entry.endDate&&<span>🏁 End: <strong style={{color:T.text}}>{new Date(entry.endDate).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</strong></span>}
                </div>
                {entry.notes&&<div style={{fontSize:11,color:T.muted,marginTop:4,fontStyle:"italic"}}>{entry.notes}</div>}
                <div style={{fontSize:9,color:T.muted,marginTop:4}}>Submitted {new Date(entry.submittedAt).toLocaleDateString()} · GC schedule updated ✓</div>
              </div>
              <button onClick={()=>removeEntry(entry.id)}
                style={{background:"none",border:"none",color:T.muted,fontSize:12,cursor:"pointer",flexShrink:0,opacity:0.5}}>×</button>
            </div>
          ))}
        </div>
      )}

      {subSchedule.length===0&&(
        <div style={{textAlign:"center",padding:"32px",background:T.bg2,borderRadius:10,color:T.muted,fontSize:12}}>
          No milestones submitted yet. Add your first one above and it'll appear on the GC's project schedule.
        </div>
      )}
    </div>
  );
}

function SubDashboard({session, onLogout}) {
  const linkedProjects = React.useMemo(()=>{
    const accts = getSubAccounts();
    const acct = accts.find(a=>a.id===session?.subId);
    if (!acct?.projects?.length) return [];
    return acct.projects.map(lp=>{
      try {
        const gcProjs = JSON.parse(localStorage.getItem(projectsKey(lp.gcCompanyId))||"[]");
        const proj = gcProjs.find(p=>String(p.id)===String(lp.projectId));
        return proj ? {...proj, gcCompanyId:lp.gcCompanyId, gcName:lp.gcName} : null;
      } catch { return null; }
    }).filter(Boolean);
  }, [session?.subId]);

  const [activeProj, setActiveProj] = React.useState(linkedProjects[0]||null);
  const [tab, setTab] = React.useState("today");
  const [clockedIn, setClockedIn] = React.useState(false);
  const [clockInTime, setClockInTime] = React.useState(null);
  const [showPhotoLog, setShowPhotoLog] = React.useState(false);
  const [showMaterialReq, setShowMaterialReq] = React.useState(false);
  const [showIssueFlag, setShowIssueFlag] = React.useState(false);
  const [photoNote, setPhotoNote] = React.useState("");
  const [matReq, setMatReq] = React.useState({item:"",qty:"",urgency:"Normal",notes:""});
  const [issueFlag, setIssueFlag] = React.useState({desc:"",severity:"yellow",location:""});
  const [notifications, setNotifications] = React.useState([]);

  const trade = session?.trade||"General";
  const tradeIcon = {Electrical:"⚡",Plumbing:"🔧",HVAC:"💨",Mechanical:"💨",Framing:"🏗",Structural:"🏗",Fire:"🔥","Fire Sprinkler":"🔥",ADA:"♿",Architectural:"📐",Tile:"🪟",Painting:"🎨",Drywall:"🧱",Flooring:"🪵",Roofing:"🏠"}[trade]||"🔧";
  const today = new Date().toLocaleDateString("en-US",{weekday:"long",month:"short",day:"numeric"});
  const timeNow = new Date().toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"});
  const inp = {width:"100%",background:"#1a2332",border:"1px solid #2d3f55",borderRadius:8,padding:"9px 12px",color:"#e2e8f0",fontSize:13,fontFamily:"inherit",marginBottom:10};

  // Clock in/out
  const handleClock = () => {
    if (!clockedIn) {
      const t = new Date().toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"});
      setClockInTime(t);
      setClockedIn(true);
      // Save to localStorage
      const key = `grok_timelog_${activeProj?.id}_${session?.subId}`;
      const logs = (()=>{try{return JSON.parse(localStorage.getItem(key)||"[]");}catch{return [];}})();
      logs.push({date:new Date().toLocaleDateString(),clockIn:t,clockOut:null,trade,name:session?.subName});
      localStorage.setItem(key, JSON.stringify(logs));
    } else {
      const t = new Date().toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"});
      setClockedIn(false);
      const key = `grok_timelog_${activeProj?.id}_${session?.subId}`;
      const logs = (()=>{try{return JSON.parse(localStorage.getItem(key)||"[]");}catch{return [];}})();
      if(logs.length) logs[logs.length-1].clockOut = t;
      localStorage.setItem(key, JSON.stringify(logs));
    }
  };

  // Submit issue flag. Writes the flag to the project's shared flag store and
  // asks ARIA to draft a "pending_gc_approval" RFI on the GC's tenant so the GC
  // can review and send to the architect.
  const submitFlag = () => {
    if(!issueFlag.desc) return;
    const key = `grok_flags_${activeProj?.id}`;
    const flags = (()=>{try{return JSON.parse(localStorage.getItem(key)||"[]");}catch{return [];}})();
    const sev = issueFlag.severity;
    const priority = sev==="red"?"critical":sev==="yellow"?"medium":"low";
    const title = issueFlag.desc.slice(0,80).split("\n")[0];
    const newFlag = {
      ...issueFlag,
      id: Date.now(),
      title,
      priority,
      date: new Date().toLocaleDateString(),
      time: timeNow,
      submittedBy: session?.subName,
      flaggedBy: session?.subName || "Field",
      trade,
      status: "open",
    };
    flags.push(newFlag);
    localStorage.setItem(key, JSON.stringify(flags));
    setIssueFlag({desc:"",severity:"yellow",location:""});
    setShowIssueFlag(false);
    alert("✓ Issue flagged — your GC has been notified");

    // ARIA auto-drafts an RFI under the GC's tenant scope. Background — the
    // sub doesn't wait. RfiDraftId is written back so the same flag doesn't
    // produce a duplicate draft on a re-submit.
    const gcCid = activeProj?.gcCompanyId;
    if (!gcCid) return;
    const gcSession = {...(session || {}), companyId: gcCid};
    const bp = {
      id: null,
      name: activeProj?.name ? `${activeProj.name} (${trade})` : `${trade} drawing`,
    };
    draftRFIFromFlag({flag: newFlag, blueprint: bp, proj: activeProj, session: gcSession})
      .then((rfi) => {
        if (!rfi) return;
        const cur = (()=>{try{return JSON.parse(localStorage.getItem(key)||"[]");}catch{return [];}})();
        const next = cur.map(f => f.id === newFlag.id ? {...f, rfiDraftId: rfi.id} : f);
        try { localStorage.setItem(key, JSON.stringify(next)); } catch {}
      })
      .catch(()=>{});
  };

  // Submit material request
  const submitMatReq = () => {
    if(!matReq.item) return;
    const key = `grok_matreq_${activeProj?.id}`;
    const reqs = (()=>{try{return JSON.parse(localStorage.getItem(key)||"[]");}catch{return [];}})();
    reqs.push({...matReq, id:Date.now(), date:new Date().toLocaleDateString(), submittedBy:session?.subName, trade, status:"pending"});
    localStorage.setItem(key, JSON.stringify(reqs));
    setMatReq({item:"",qty:"",urgency:"Normal",notes:""});
    setShowMaterialReq(false);
    alert("✓ Material request sent to GC");
  };

  // Today's RFIs and COs
  const openRFIs = activeProj ? (()=>{try{return JSON.parse(localStorage.getItem(`grok_rfis_global_${activeProj.gcCompanyId}`)||"[]").filter(r=>r.status!=="closed");}catch{return [];}})() : [];
  const openCOs  = activeProj ? (()=>{try{return JSON.parse(localStorage.getItem(`grok_cos_global_${activeProj.gcCompanyId}`)||"[]").filter(c=>c.status==="pending");}catch{return [];}})() : [];
  const myFlags  = activeProj ? (()=>{try{return JSON.parse(localStorage.getItem(`grok_flags_${activeProj?.id}`)||"[]").filter(f=>f.trade===trade);}catch{return [];}})() : [];

  const SUB_TABS = [
    {id:"today",    label:"Today"},
    {id:"drawings", label:`${tradeIcon} Drawings`},
    {id:"schedule", label:"Schedule"},
    {id:"dailylog", label:"Log"},
    {id:"rfis",     label:"RFIs", badge:openRFIs.length},
    {id:"cos",      label:"COs",  badge:openCOs.length},
    {id:"punch",    label:"Punch"},
    {id:"docs",     label:"Docs"},
    {id:"upgrade",  label:"Upgrade"},
  ];

  const sevColor = s => s==="red"?T.red:s==="yellow"?T.amber:T.green;

  return (
    <div style={{minHeight:"100vh",background:"#0d1520",color:"#e2e8f0",fontFamily:"system-ui,-apple-system,sans-serif"}}>

      {/* Header */}
      <div style={{background:"#111d2e",borderBottom:"1px solid #1e3a5f",padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:20}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:38,height:38,borderRadius:10,background:`linear-gradient(135deg,#0ea5e9,#6366f1)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{tradeIcon}</div>
          <div>
            <div style={{fontSize:13,fontWeight:800,color:"#f1f5f9"}}>{session?.subName||"Sub Portal"}</div>
            <div style={{fontSize:10,color:"#64748b"}}>{trade} · {activeProj?.name?.slice(0,28)||"No project"}</div>
          </div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {/* Clock in/out */}
          <button onClick={handleClock}
            style={{fontSize:11,fontWeight:700,background:clockedIn?"#dc2626":"#16a34a",color:"white",border:"none",borderRadius:8,padding:"7px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
            {clockedIn?"⏹ Clock Out":"▶ Clock In"}
          </button>
          <button onClick={onLogout} style={{fontSize:11,background:"#1e2d40",border:"1px solid #2d3f55",borderRadius:7,padding:"5px 10px",cursor:"pointer",color:"#64748b"}}>Out</button>
        </div>
      </div>

      {/* Clock status bar */}
      {clockedIn&&(
        <div style={{background:"#16a34a18",borderBottom:"1px solid #16a34a30",padding:"6px 16px",fontSize:11,color:"#4ade80",display:"flex",alignItems:"center",gap:6}}>
          <div style={{width:7,height:7,borderRadius:"50%",background:"#4ade80",animation:"pulse 2s infinite"}}/>
          On site since {clockInTime} · {activeProj?.name?.slice(0,30)} · {trade}
        </div>
      )}

      <div style={{padding:16,maxWidth:600,margin:"0 auto",paddingBottom:100}}>

        {/* No projects */}
        {linkedProjects.length===0&&(
          <div style={{textAlign:"center",padding:"60px 20px",background:"#111d2e",borderRadius:12,marginTop:16,border:"1px solid #1e3a5f"}}>
            <div style={{fontSize:48,marginBottom:12}}>⏳</div>
            <div style={{fontSize:16,fontWeight:800,color:"#f1f5f9",marginBottom:8}}>Waiting for Project Access</div>
            <div style={{fontSize:12,color:"#64748b",lineHeight:1.9,marginBottom:16}}>Your GC needs to add you to a project.<br/>You'll see your drawings, schedule, and daily log once approved.</div>
            <div style={{background:"#0ea5e918",border:"1px solid #0ea5e930",borderRadius:8,padding:"10px 16px",fontSize:11,color:"#0ea5e9",display:"inline-block"}}>
              Logged in as {session?.email}
            </div>
          </div>
        )}

        {linkedProjects.length>0&&(
          <>
            {/* Project selector */}
            {linkedProjects.length>1&&(
              <div style={{display:"flex",gap:6,marginBottom:12,overflowX:"auto",paddingBottom:4}}>
                {linkedProjects.map(p=>(
                  <button key={p.id} onClick={()=>{setActiveProj(p);setTab("today");}}
                    style={{padding:"7px 14px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${activeProj?.id===p.id?"#0ea5e9":"#2d3f55"}`,background:activeProj?.id===p.id?"#0ea5e918":"#111d2e",color:activeProj?.id===p.id?"#0ea5e9":"#64748b",whiteSpace:"nowrap",flexShrink:0}}>
                    {p.name?.slice(0,25)}
                  </button>
                ))}
              </div>
            )}

            {/* Quick action bar */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:14}}>
              {[
                {icon:"📋",label:"Log Day",  action:()=>setTab("dailylog"),  color:"#0ea5e9"},
                {icon:"🚩",label:"Flag Issue",action:()=>setShowIssueFlag(true),  color:"#ef4444"},
                {icon:"📦",label:"Need Materials",action:()=>setShowMaterialReq(true),color:"#f59e0b"},
                {icon:"❓",label:"New RFI",  action:()=>setTab("rfis"),      color:"#8b5cf6"},
              ].map(a=>(
                <button key={a.label} onClick={a.action}
                  style={{background:a.color+"15",border:`1px solid ${a.color}30`,borderRadius:10,padding:"12px 6px",cursor:"pointer",textAlign:"center",color:a.color,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                  <span style={{fontSize:22}}>{a.icon}</span>
                  <span style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",lineHeight:1.2}}>{a.label}</span>
                </button>
              ))}
            </div>

            {/* Tab bar */}
            <div style={{display:"flex",gap:3,marginBottom:14,overflowX:"auto",paddingBottom:4,scrollbarWidth:"none"}}>
              {SUB_TABS.map(t=>(
                <button key={t.id} onClick={()=>setTab(t.id)}
                  style={{padding:"8px 12px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",whiteSpace:"nowrap",background:tab===t.id?"#0ea5e9":"#111d2e",color:tab===t.id?"white":"#64748b",transition:"all 0.15s",flexShrink:0,position:"relative"}}>
                  {t.label}
                  {t.badge>0&&<span style={{position:"absolute",top:-4,right:-4,background:"#ef4444",color:"white",fontSize:8,fontWeight:800,borderRadius:10,padding:"1px 5px",minWidth:14}}>{t.badge}</span>}
                </button>
              ))}
            </div>

            {/* TODAY TAB */}
            {tab==="today"&&activeProj&&(
              <div>
                {/* Date + weather placeholder */}
                <div style={{background:"#111d2e",border:"1px solid #1e3a5f",borderRadius:10,padding:"14px 16px",marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:"#f1f5f9"}}>{today}</div>
                      <div style={{fontSize:11,color:"#64748b"}}>{activeProj.addr||activeProj.name}</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:11,color:"#64748b"}}>My Trade</div>
                      <div style={{fontSize:14,fontWeight:800,color:"#0ea5e9"}}>{tradeIcon} {trade}</div>
                    </div>
                  </div>
                </div>

                {/* My scope */}
                <div style={{background:"#111d2e",border:"1px solid #1e3a5f",borderRadius:10,padding:"14px 16px",marginBottom:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>My Scope on This Project</div>
                  {(activeProj.subScopes||{})[trade] ? (
                    <div style={{fontSize:12,color:"#e2e8f0",lineHeight:1.7}}>{(activeProj.subScopes||{})[trade]}</div>
                  ) : (
                    <div style={{fontSize:12,color:"#64748b",fontStyle:"italic"}}>Your GC hasn't defined your specific scope yet. Check drawings or contact your GC.</div>
                  )}
                </div>

                {/* Open items */}
                {(openRFIs.length>0||openCOs.length>0||myFlags.length>0)&&(
                  <div style={{background:"#111d2e",border:"1px solid #1e3a5f",borderRadius:10,padding:"14px 16px",marginBottom:12}}>
                    <div style={{fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Needs Attention</div>
                    {openRFIs.slice(0,3).map(r=>(
                      <div key={r.id} onClick={()=>setTab("rfis")} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"1px solid #1e3a5f",cursor:"pointer"}}>
                        <span style={{fontSize:16}}>❓</span>
                        <div style={{flex:1}}>
                          <div style={{fontSize:12,fontWeight:600,color:"#f1f5f9"}}>{r.subject||r.num}</div>
                          <div style={{fontSize:10,color:"#64748b"}}>RFI · Due: {r.due_date||"TBD"}</div>
                        </div>
                        <span style={{fontSize:10,color:"#8b5cf6",fontWeight:700}}>View →</span>
                      </div>
                    ))}
                    {openCOs.slice(0,2).map(c=>(
                      <div key={c.id} onClick={()=>setTab("cos")} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"1px solid #1e3a5f",cursor:"pointer"}}>
                        <span style={{fontSize:16}}>📝</span>
                        <div style={{flex:1}}>
                          <div style={{fontSize:12,fontWeight:600,color:"#f1f5f9"}}>{c.id} — {(c.desc||"").slice(0,40)}</div>
                          <div style={{fontSize:10,color:"#64748b"}}>Change Order · Pending</div>
                        </div>
                        <span style={{fontSize:10,color:"#f59e0b",fontWeight:700}}>View →</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Today's time log */}
                <div style={{background:"#111d2e",border:"1px solid #1e3a5f",borderRadius:10,padding:"14px 16px",marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <div style={{fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.08em"}}>Time on Site Today</div>
                    <button onClick={handleClock}
                      style={{fontSize:10,fontWeight:700,background:clockedIn?"#dc262620":"#16a34a20",color:clockedIn?"#f87171":"#4ade80",border:`1px solid ${clockedIn?"#dc262640":"#16a34a40"}`,borderRadius:6,padding:"4px 10px",cursor:"pointer"}}>
                      {clockedIn?`⏹ Clock Out (in since ${clockInTime})`:"▶ Clock In"}
                    </button>
                  </div>
                  {(()=>{
                    const key=`grok_timelog_${activeProj?.id}_${session?.subId}`;
                    const logs=(()=>{try{return JSON.parse(localStorage.getItem(key)||"[]");}catch{return [];}})();
                    const todayLogs=logs.filter(l=>l.date===new Date().toLocaleDateString());
                    if(!todayLogs.length) return <div style={{fontSize:11,color:"#64748b"}}>No time logged yet today</div>;
                    return todayLogs.map((l,i)=>(
                      <div key={i} style={{fontSize:12,color:"#e2e8f0",display:"flex",gap:8}}>
                        <span style={{color:"#4ade80"}}>In: {l.clockIn}</span>
                        {l.clockOut&&<span style={{color:"#f87171"}}>· Out: {l.clockOut}</span>}
                        {!l.clockOut&&<span style={{color:"#f59e0b"}}>· Still on site</span>}
                      </div>
                    ));
                  })()}
                </div>

                {/* My flagged issues */}
                {myFlags.length>0&&(
                  <div style={{background:"#111d2e",border:"1px solid #1e3a5f",borderRadius:10,padding:"14px 16px",marginBottom:12}}>
                    <div style={{fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>My Flagged Issues</div>
                    {myFlags.slice(0,3).map(f=>(
                      <div key={f.id} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:"1px solid #1e3a5f"}}>
                        <div style={{width:10,height:10,borderRadius:"50%",background:sevColor(f.severity),flexShrink:0}}/>
                        <div style={{flex:1}}>
                          <div style={{fontSize:12,color:"#e2e8f0"}}>{f.desc}</div>
                          <div style={{fontSize:10,color:"#64748b"}}>{f.date} · {f.location||"No location"}</div>
                        </div>
                        <span style={{fontSize:9,fontWeight:700,background:f.status==="resolved"?"#16a34a20":"#1e3a5f",color:f.status==="resolved"?"#4ade80":"#64748b",borderRadius:10,padding:"2px 7px"}}>{f.status||"open"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* DRAWINGS TAB */}
            {tab==="drawings"&&(
              <div>
                <div style={{background:"#0ea5e918",border:"1px solid #0ea5e930",borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:11,color:"#0ea5e9",display:"flex",gap:8,alignItems:"center"}}>
                  <span style={{fontSize:16}}>{tradeIcon}</span>
                  <span>You're viewing the <strong>{trade}</strong> drawing workspace. Flag issues directly on drawings — your GC sees it in real time.</span>
                </div>
                <TradeBlueprintView proj={{...activeProj, id:activeProj.id}} session={session}/>
              </div>
            )}

            {tab==="schedule"&&<SubScheduleTab proj={activeProj} session={session} trade={trade} tradeIcon={tradeIcon}/>}
            {tab==="dailylog"&&<DailyLogPanel proj={activeProj} session={session}/>}
            {tab==="rfis"&&<RFIsView proj={activeProj} readOnly={false} session={session}/>}
            {tab==="cos"&&(
              <div>
                <div style={{background:"#f59e0b18",border:"1px solid #f59e0b30",borderRadius:8,padding:"9px 12px",marginBottom:12,fontSize:11,color:"#f59e0b"}}>
                  You can view approved and pending COs. Submit change requests through RFIs or contact your GC directly.
                </div>
                <ChangeOrdersView proj={activeProj} readOnly={true} session={session}/>
              </div>
            )}
            {tab==="punch"&&<PunchListView proj={activeProj} session={session}/>}
            {tab==="docs"&&<ProjectDocsPanel proj={activeProj} session={session}/>}

            {/* UPGRADE TAB */}
            {tab==="submittals"&&(
              <div style={{background:"#111d2e",border:"1px solid #1e3a5f",borderRadius:12,padding:16}}>
                <div style={{fontSize:13,fontWeight:700,color:"#f1f5f9",marginBottom:14}}>📨 Submittals</div>

                {/* Upload submittal */}
                <div style={{background:"#0d1520",border:"1px dashed #1e3a5f",borderRadius:10,padding:16,marginBottom:14,textAlign:"center"}}>
                  <div style={{fontSize:24,marginBottom:6}}>📎</div>
                  <div style={{fontSize:12,fontWeight:700,color:"#f1f5f9",marginBottom:4}}>Upload Submittal</div>
                  <div style={{fontSize:10,color:"#64748b",marginBottom:12}}>Shop drawings, product data, samples, test reports</div>
                  <input type="file" multiple accept=".pdf,.dwg,.jpg,.png,.xlsx,.docx"
                    onChange={e=>{
                      if(!e.target.files.length) return;
                      const key=`grok_submittals_${activeProj?.id}_${session?.subId}`;
                      const subs=(()=>{try{return JSON.parse(localStorage.getItem(key)||"[]");}catch{return [];}})();
                      Array.from(e.target.files).forEach(f=>{
                        subs.push({id:Date.now()+"_"+f.name,name:f.name,size:f.size,type:f.type,
                          date:new Date().toLocaleDateString(),status:"pending",
                          submittedBy:session?.subName,trade});
                      });
                      localStorage.setItem(key,JSON.stringify(subs));
                      alert("✓ "+e.target.files.length+" submittal(s) uploaded — GC will review");
                      e.target.value="";
                    }}
                    style={{display:"none"}} id="sub-upload"/>
                  <label htmlFor="sub-upload"
                    style={{display:"inline-block",padding:"8px 20px",background:"#0ea5e9",color:"white",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                    Choose Files
                  </label>
                </div>

                {/* Submittal list */}
                {(()=>{
                  const key=`grok_submittals_${activeProj?.id}_${session?.subId}`;
                  const subs=(()=>{try{return JSON.parse(localStorage.getItem(key)||"[]");}catch{return [];}})();
                  if(!subs.length) return (
                    <div style={{textAlign:"center",padding:"20px",color:"#64748b",fontSize:12}}>
                      No submittals yet — upload your first one above
                    </div>
                  );
                  return subs.map((s,i)=>(
                    <div key={i} style={{background:"#0d1520",border:"1px solid #1e3a5f",borderRadius:8,padding:"10px 12px",marginBottom:6,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div>
                        <div style={{fontSize:11,fontWeight:700,color:"#f1f5f9"}}>{s.name}</div>
                        <div style={{fontSize:9,color:"#64748b",marginTop:2}}>{s.date} · {trade}</div>
                      </div>
                      <div style={{fontSize:9,fontWeight:700,padding:"3px 8px",borderRadius:10,
                        background:s.status==="approved"?"#16a34a20":s.status==="rejected"?"#dc262620":"#f59e0b20",
                        color:s.status==="approved"?"#4ade80":s.status==="rejected"?"#f87171":"#fbbf24"}}>
                        {s.status==="approved"?"✓ Approved":s.status==="rejected"?"✕ Rejected":"⏳ Pending Review"}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}

            {tab==="upgrade"&&(
              <div style={{background:"#111d2e",border:"1px solid #1e3a5f",borderRadius:12,padding:24,textAlign:"center"}}>
                <div style={{fontSize:32,marginBottom:8}}>🔒</div>
                <div style={{fontSize:14,fontWeight:700,color:"#f1f5f9",marginBottom:6}}>Premium Features</div>
                <div style={{fontSize:12,color:"#64748b",lineHeight:1.7}}>
                  AI estimating, advanced analytics, and additional project tools are available to GC accounts.<br/>
                  Contact your GC to learn more about Grok Field Ops.
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Flag Issue Modal */}
      {showIssueFlag&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.8)",zIndex:1000,display:"flex",alignItems:"flex-end",padding:0}}>
          <div style={{background:"#111d2e",borderRadius:"14px 14px 0 0",padding:20,width:"100%",border:"1px solid #1e3a5f"}}>
            <div style={{fontSize:14,fontWeight:800,color:"#f1f5f9",marginBottom:14}}>🚩 Flag an Issue</div>
            <div style={{fontSize:10,color:"#64748b",marginBottom:4}}>Severity</div>
            <div style={{display:"flex",gap:8,marginBottom:12}}>
              {[{v:"green",l:"Minor"},  {v:"yellow",l:"Moderate"},{v:"red",l:"Stop Work"}].map(s=>(
                <button key={s.v} onClick={()=>setIssueFlag(p=>({...p,severity:s.v}))}
                  style={{flex:1,padding:"8px",borderRadius:7,border:`2px solid ${issueFlag.severity===s.v?sevColor(s.v):"#2d3f55"}`,background:issueFlag.severity===s.v?sevColor(s.v)+"20":"transparent",color:sevColor(s.v),fontSize:11,fontWeight:700,cursor:"pointer"}}>
                  {s.l}
                </button>
              ))}
            </div>
            <div style={{fontSize:10,color:"#64748b",marginBottom:4}}>Description *</div>
            <textarea value={issueFlag.desc} onChange={e=>setIssueFlag(p=>({...p,desc:e.target.value}))}
              placeholder="What's the issue? Be specific — include location, trade affected, and any safety concern." rows={3} style={{...inp,resize:"none"}}/>
            <div style={{fontSize:10,color:"#64748b",marginBottom:4}}>Location on site</div>
            <input value={issueFlag.location} onChange={e=>setIssueFlag(p=>({...p,location:e.target.value}))}
              placeholder="e.g. Unit 4B bathroom, 3rd floor stairwell" style={inp}/>
            <div style={{display:"flex",gap:8,marginTop:4}}>
              <button onClick={()=>setShowIssueFlag(false)} style={{flex:1,padding:"10px",background:"#1e2d40",border:"1px solid #2d3f55",borderRadius:8,color:"#64748b",fontSize:12,cursor:"pointer"}}>Cancel</button>
              <button onClick={submitFlag} style={{flex:2,padding:"10px",background:"#ef4444",border:"none",borderRadius:8,color:"white",fontSize:12,fontWeight:700,cursor:"pointer"}}>Flag Issue</button>
            </div>
          </div>
        </div>
      )}

      {/* Material Request Modal */}
      {showMaterialReq&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.8)",zIndex:1000,display:"flex",alignItems:"flex-end"}}>
          <div style={{background:"#111d2e",borderRadius:"14px 14px 0 0",padding:20,width:"100%",border:"1px solid #1e3a5f"}}>
            <div style={{fontSize:14,fontWeight:800,color:"#f1f5f9",marginBottom:14}}>📦 Request Materials</div>
            <div style={{fontSize:10,color:"#64748b",marginBottom:4}}>Material / Item *</div>
            <input value={matReq.item} onChange={e=>setMatReq(p=>({...p,item:e.target.value}))} placeholder="e.g. 2x6 lumber, 16d nails, PVC 3-inch" style={inp}/>
            <div style={{display:"grid",gridTemplateColumns:window.innerWidth<768?"1fr":"1fr 1fr",gap:8}}>
              <div>
                <div style={{fontSize:10,color:"#64748b",marginBottom:4}}>Quantity</div>
                <input value={matReq.qty} onChange={e=>setMatReq(p=>({...p,qty:e.target.value}))} placeholder="e.g. 50 pcs, 2 boxes" style={inp}/>
              </div>
              <div>
                <div style={{fontSize:10,color:"#64748b",marginBottom:4}}>Urgency</div>
                <select value={matReq.urgency} onChange={e=>setMatReq(p=>({...p,urgency:e.target.value}))} style={inp}>
                  {["Normal","Urgent — Need Today","ASAP — Work Stopped"].map(o=><option key={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div style={{fontSize:10,color:"#64748b",marginBottom:4}}>Notes</div>
            <input value={matReq.notes} onChange={e=>setMatReq(p=>({...p,notes:e.target.value}))} placeholder="Any specific brand, size, or delivery notes" style={inp}/>
            <div style={{display:"flex",gap:8,marginTop:4}}>
              <button onClick={()=>setShowMaterialReq(false)} style={{flex:1,padding:"10px",background:"#1e2d40",border:"1px solid #2d3f55",borderRadius:8,color:"#64748b",fontSize:12,cursor:"pointer"}}>Cancel</button>
              <button onClick={submitMatReq} style={{flex:2,padding:"10px",background:"#f59e0b",border:"none",borderRadius:8,color:"#0d1520",fontSize:12,fontWeight:700,cursor:"pointer"}}>Send Request</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PROJECT SUBS PANEL — GC-facing sub list per project (replaces sub portal in project) ──
// --- SUB INVITE PANEL - GC invites subs to a specific project ---
function SubInvitePanel({proj, session}) {
  const [code, setCode] = React.useState(()=>{
    const invites = getSubInvites();
    const ex = invites.find(i=>String(i.projectId)===String(proj?.id));
    return ex?.code||"";
  });
  const [showForm, setShowForm] = React.useState(false);
  const [subName, setSubName] = React.useState("");
  const [subEmail, setSubEmail] = React.useState("");
  const [subPhone, setSubPhone] = React.useState("");
  const [subTrade, setSubTrade] = React.useState("Electrical");
  const [copied, setCopied] = React.useState(false);
  const TRADES = ["Electrical","Plumbing","HVAC","Framing","Concrete","Roofing","Drywall","Painting","Flooring","Tile","Fire Sprinkler","Other"];
  const inp = {width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,fontFamily:"inherit"};

  const genCode = () => {
    const nc = generateProjectInviteCode(proj, session);
    setCode(nc); return nc;
  };

  const regLink = (ic) => window.location.origin + "?sub_invite=" + encodeURIComponent(ic);

  const copyCode = () => {
    const ac = code || genCode();
    navigator.clipboard.writeText(ac).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false),2000); });
  };

  const sendEmail = () => {
    const ac = code || genCode();
    const link = regLink(ac);
    const subj = encodeURIComponent("You are invited to join " + (proj?.name||"a project") + " on Grok Field Ops");
    const body = encodeURIComponent(
      "Hi " + (subName||"there") + ",\n\n" +
      (session?.companyName||"Your GC") + " has invited you to join the project:\n\n" +
      "Project: " + (proj?.name||"") + "\n" +
      "Address: " + (proj?.addr||"") + "\n\n" +
      "HOW TO JOIN:\n" +
      "1. Go to: " + link + "\n" +
      "   (The invite code will be pre-filled)\n\n" +
      "OR manually:\n" +
      "1. Go to: " + window.location.origin + "\n" +
      "2. Click Log In, then Create Account\n" +
      "3. Choose Join a GC Project\n" +
      "4. Enter invite code: " + ac + "\n" +
      "5. Fill out your info and submit\n" +
      "6. " + (session?.companyName||"The GC") + " will approve your request\n\n" +
      "Once approved you can view your project schedule, RFIs, daily logs, blueprints, and punch list.\n\n" +
      "If you want to manage your own projects in Grok, you can subscribe after registering.\n\n" +
      "Grok Field Ops - Built for US Construction GCs"
    );
    window.open("mailto:" + encodeURIComponent(subEmail||"") + "?subject=" + subj + "&body=" + body);
  };

  const sendSMS = () => {
    const ac = code || genCode();
    const link = regLink(ac);
    const msg = encodeURIComponent(
      (session?.companyName||"Your GC") + " invited you to " + (proj?.name||"a project") + " on Grok Field Ops.\n" +
      "Register here: " + link + "\n" +
      "Code: " + ac + "\n" +
      "GC must approve before you can log in."
    );
    window.open("sms:" + subPhone.replace(/\D/g,"") + "?body=" + msg);
  };

  return (
    <div style={{marginTop:14,background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,overflow:"hidden"}}>
      <div style={{padding:"10px 14px",background:T.bg2,borderBottom:"1px solid "+T.subtle,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:T.text}}>📨 Invite a Subcontractor</div>
          <div style={{fontSize:10,color:T.muted,marginTop:1}}>Send invite code → sub registers → you approve → they see this project only</div>
        </div>
        <Btn small primary onClick={()=>setShowForm(p=>!p)}>{showForm?"✕ Cancel":"+ Invite Sub"}</Btn>
      </div>

      {showForm&&(
        <div style={{padding:"14px 16px"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
            <div><div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Sub Name</div>
            <input value={subName} onChange={e=>setSubName(e.target.value)} placeholder="Tom Lee" style={inp}/></div>
            <div><div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Trade</div>
            <select value={subTrade} onChange={e=>setSubTrade(e.target.value)} style={inp}>{TRADES.map(t=><option key={t}>{t}</option>)}</select></div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
            <div><div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Sub Email</div>
            <input type="email" value={subEmail} onChange={e=>setSubEmail(e.target.value)} placeholder="tom@leeelectric.com" style={inp}/></div>
            <div><div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Sub Phone (SMS)</div>
            <input type="tel" value={subPhone} onChange={e=>setSubPhone(e.target.value)} placeholder="(415) 555-0100" style={inp}/></div>
          </div>

          <div style={{background:T.teal+"12",border:"1px solid "+T.teal+"40",borderRadius:9,padding:"12px 14px",marginBottom:12}}>
            <div style={{fontSize:10,fontWeight:700,color:T.teal,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Invite Code</div>
            {code ? (
              <div>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  <div style={{flex:1,background:T.bg1,borderRadius:7,padding:"9px 12px",fontFamily:"monospace",fontSize:18,fontWeight:800,color:T.teal,letterSpacing:"0.12em"}}>{code}</div>
                  <button onClick={copyCode} style={{fontSize:11,fontWeight:700,background:copied?T.green:T.teal,color:"white",border:"none",borderRadius:7,padding:"8px 14px",cursor:"pointer",transition:"background 0.2s"}}>{copied?"✓ Copied!":"Copy"}</button>
                  <button onClick={genCode} style={{fontSize:10,background:T.bg2,color:T.muted,border:"1px solid "+T.subtle,borderRadius:7,padding:"8px 10px",cursor:"pointer"}}>🔄</button>
                </div>
                <div style={{fontSize:10,color:T.muted,lineHeight:1.6}}>
                  Registration link (code pre-filled): <span style={{color:T.blue,wordBreak:"break-all"}}>{regLink(code)}</span>
                </div>
              </div>
            ) : (
              <button onClick={genCode} style={{width:"100%",padding:"10px",background:T.teal,color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>Generate Invite Code</button>
            )}
          </div>

          <div style={{background:T.bg2,borderRadius:8,padding:"9px 12px",marginBottom:12,fontSize:10,color:T.muted,lineHeight:1.7}}>
            <strong style={{color:T.text}}>Flow:</strong> Sub gets code → registers at the link → pending your approval → you approve in Sub Roster → sub sees only this project (schedule, RFIs, daily logs, blueprints, punch list). No financials. No other projects. Free for them.
          </div>

          <div style={{display:"flex",gap:7}}>
            <button onClick={sendEmail} style={{flex:1,padding:"10px",background:code?T.blue:T.bg3,color:code?"white":T.muted,border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:code?"pointer":"not-allowed"}}>
              ✉ Send Email
            </button>
            <button onClick={sendSMS} disabled={!code||!subPhone} style={{flex:1,padding:"10px",background:code&&subPhone?T.green:T.bg3,color:code&&subPhone?"white":T.muted,border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:code&&subPhone?"pointer":"not-allowed"}}>
              💬 Send SMS
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function ProjectSubsPanel({proj, session}) {
  const [showInvite, setShowInvite] = React.useState(false);
  const [showAddFromRoster, setShowAddFromRoster] = React.useState(false);
  const [expandedSub, setExpandedSub] = React.useState(null);
  const isMob = window.innerWidth < 768;

  // Load sub DB with error safety
  let db = {subs:[]};
  try { db = loadSubDB(session?.companyId) || {subs:[]}; } catch(e) {}

  // Seed demo subs linked to this project if none exist
  const DEMO_PROJ_SUBS = [
    {
      id:"ps1", name:"Mike Chen", company:"SF Plumbing Inc", trade:"Plumbing",
      phone:"(415) 555-0111", email:"mike@sfplumbing.com",
      cslb:"C-36 #987654", coi_expiry:"2026-12-31",
      status:"active", rating:4.5, on_time_pct:92,
      scope:"Rough plumbing — drain stack relocation, supply lines all units",
      contract_amount:18400, paid_to_date:12000,
      notes:"Reliable. Slight delays on material lead times but communicates well.",
      color:"#60a5fa", avatar:"🔧",
    },
    {
      id:"ps2", name:"Tom Lee", company:"Bay Electrical LLC", trade:"Electrical",
      phone:"(510) 555-0122", email:"tom@bayelectrical.com",
      cslb:"C-10 #123456", coi_expiry:"2026-09-30",
      status:"active", rating:4, on_time_pct:85,
      scope:"Panel upgrade, GFCI circuits, rough-in all units",
      contract_amount:14200, paid_to_date:8000,
      notes:"Good work quality. Needs follow-up on punch items.",
      color:"#f59e0b", avatar:"⚡",
    },
    {
      id:"ps3", name:"Pedro Ramirez", company:"PR Tile & Finish", trade:"Tile & Finishes",
      phone:"(415) 555-0133", email:"pedro@prtile.com",
      cslb:"C-54 #456789", coi_expiry:"2027-03-31",
      status:"pending", rating:5, on_time_pct:98,
      scope:"Bathroom tile — floor and walls, 3 units",
      contract_amount:9600, paid_to_date:0,
      notes:"First time on this project. Excellent references from past jobs.",
      color:"#a78bfa", avatar:"🪟",
    },
  ];

  // Match real subs from DB or fall back to demo
  const realSubs = (db.subs||[]).filter(s=>
    String(s.projectId)===String(proj?.id) ||
    (s.projects||[]).includes(String(proj?.id)) ||
    (s.projects_worked||[]).includes(String(proj?.id))
  );
  const projSubs = realSubs.length > 0 ? realSubs : DEMO_PROJ_SUBS;

  const TRADE_COLORS = {
    "Plumbing":"#60a5fa","Electrical":"#f59e0b","HVAC":"#06b6d4",
    "Framing":"#22c55e","Drywall":"#e879f9","Tile & Finishes":"#a78bfa",
    "Concrete":"#6b7280","Roofing":"#f97316","Painting":"#84cc16",
    "Demo":"#ef4444","General":"#94a3b8",
  };
  const tradeColor = (t) => TRADE_COLORS[t]||T.teal;

  const totalContract = projSubs.reduce((s,sub)=>s+(sub.contract_amount||0),0);
  const totalPaid = projSubs.reduce((s,sub)=>s+(sub.paid_to_date||0),0);
  const activeSubs = projSubs.filter(s=>s.status==="active").length;
  const pendingSubs = projSubs.filter(s=>s.status==="pending").length;

  return (
    <div style={{paddingBottom:isMob?80:0}}>

      {/* Header stats */}
      <div style={{display:"grid",gridTemplateColumns:isMob?"repeat(2,1fr)":"repeat(4,1fr)",gap:8,marginBottom:14}}>
        {[
          {label:"Total Subs",    val:projSubs.length,              color:T.teal},
          {label:"Active",        val:activeSubs,                   color:T.green},
          {label:"Pending",       val:pendingSubs,                  color:T.amber},
          {label:"Total Contract",val:"$"+(totalContract/1000).toFixed(0)+"K", color:T.gold},
        ].map(s=>(
          <div key={s.label} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"10px 12px",textAlign:"center"}}>
            <div style={{fontSize:isMob?18:22,fontWeight:700,color:s.color,lineHeight:1}}>{s.val}</div>
            <div style={{fontSize:10,color:T.muted,marginTop:3}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Section title + action buttons */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text}}>
          Subcontractors on this Project
          {realSubs.length===0&&<span style={{fontSize:10,color:T.amber,fontWeight:400,marginLeft:8}}>Demo data</span>}
        </div>
        <div style={{display:"flex",gap:6}}>
          <button onClick={()=>setShowAddFromRoster(p=>!p)}
            style={{background:T.bg2,color:T.muted,border:"1px solid "+T.subtle,borderRadius:8,padding:"7px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
            + Add from Roster
          </button>
          <button onClick={()=>setShowInvite(p=>!p)}
            style={{background:showInvite?T.bg2:T.teal,color:showInvite?T.muted:"white",border:"1px solid "+(showInvite?T.subtle:T.teal),borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
            {showInvite?"✕ Close":"+ Invite New Sub"}
          </button>
        </div>
      </div>

      {/* Add from Roster */}
      {showAddFromRoster&&(()=>{
        const allSubs = (db.subs||[]).filter(s=>!projSubs.find(ps=>ps.id===s.id));
        return (
          <div style={{background:T.bg2,border:"1px solid "+T.teal+"40",borderRadius:10,padding:14,marginBottom:12}}>
            <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:8}}>Add from Your Sub Roster</div>
            {allSubs.length===0?(
              <div style={{fontSize:11,color:T.muted}}>All your subs are already on this project.</div>
            ):allSubs.map(s=>(
              <div key={s.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",background:T.bg1,borderRadius:8,marginBottom:6,border:"1px solid "+T.subtle}}>
                <div>
                  <span style={{fontSize:12,fontWeight:700,color:T.text}}>{s.name}</span>
                  <span style={{fontSize:10,color:T.muted,marginLeft:8}}>{s.company} · {s.trade}</span>
                </div>
                <button onClick={()=>{
                  const updated = {...db, subs:[...db.subs, {...s, projectId:proj?.id, status:"active", addedToProject:new Date().toLocaleDateString()}]};
                  setDb(updated); saveSubDB(updated, session?.companyId);
                  setShowAddFromRoster(false);
                }} style={{fontSize:11,fontWeight:700,background:T.teal,color:"white",border:"none",borderRadius:6,padding:"5px 12px",cursor:"pointer"}}>
                  + Add
                </button>
              </div>
            ))}
            <button onClick={()=>setShowAddFromRoster(false)} style={{fontSize:11,color:T.muted,background:"none",border:"none",cursor:"pointer",marginTop:4}}>Cancel</button>
          </div>
        );
      })()}

      {/* Sub cards */}
      <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
        {projSubs.map(sub=>{
          const isExpanded = expandedSub===sub.id;
          const tc = tradeColor(sub.trade);
          const paidPct = sub.contract_amount>0 ? Math.round((sub.paid_to_date||0)/sub.contract_amount*100) : 0;
          return (
            <div key={sub.id}
              style={{background:T.bg1,border:"1px solid "+(sub.status==="pending"?T.amber+"40":T.subtle),borderRadius:12,overflow:"hidden"}}>

              {/* Card header — always visible */}
              <div onClick={()=>setExpandedSub(isExpanded?null:sub.id)}
                style={{padding:"12px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:12}}>
                {/* Avatar */}
                <div style={{width:40,height:40,borderRadius:10,background:tc+"20",border:"1px solid "+tc+"40",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>
                  {sub.avatar||"👷"}
                </div>
                {/* Info */}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                    <span style={{fontSize:13,fontWeight:700,color:T.text}}>{sub.name}</span>
                    <span style={{fontSize:9,fontWeight:700,background:sub.status==="active"?T.greenBg:T.amberBg,color:sub.status==="active"?T.green:T.amber,borderRadius:10,padding:"1px 7px",flexShrink:0}}>
                      {sub.status==="active"?"ACTIVE":"PENDING"}
                    </span>
                  </div>
                  <div style={{fontSize:11,color:T.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {sub.company} · <span style={{color:tc,fontWeight:600}}>{sub.trade}</span>
                  </div>
                </div>
                {/* Expand arrow */}
                <span style={{color:T.muted,fontSize:14,transition:"transform 0.2s",transform:isExpanded?"rotate(180deg)":"none",flexShrink:0}}>▾</span>
              </div>

              {/* Expanded detail */}
              {isExpanded&&(
                <div style={{borderTop:"1px solid "+T.subtle,padding:"12px 14px",background:T.bg2}}>

                  {/* Scope */}
                  {sub.scope&&(
                    <div style={{marginBottom:10,padding:"8px 12px",background:T.bg1,borderRadius:8,borderLeft:"3px solid "+tc}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>Scope</div>
                      <div style={{fontSize:12,color:T.text,lineHeight:1.5}}>{sub.scope}</div>
                    </div>
                  )}

                  {/* Contact + payment row */}
                  <div style={{display:"grid",gridTemplateColumns:isMob?"1fr":"1fr 1fr",gap:8,marginBottom:10}}>
                    <div style={{background:T.bg1,borderRadius:8,padding:"10px 12px"}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>Contact</div>
                      <a href={"tel:"+sub.phone} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:T.blue,textDecoration:"none",marginBottom:4}}>
                        📞 {sub.phone}
                      </a>
                      <a href={"mailto:"+sub.email} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:T.blue,textDecoration:"none"}}>
                        ✉ {sub.email}
                      </a>
                    </div>
                    <div style={{background:T.bg1,borderRadius:8,padding:"10px 12px"}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>Payment</div>
                      <div style={{fontSize:12,color:T.text,marginBottom:4}}>
                        Contract: <strong style={{color:T.gold}}>${(sub.contract_amount||0).toLocaleString()}</strong>
                      </div>
                      <div style={{fontSize:12,color:T.text,marginBottom:6}}>
                        Paid: <strong style={{color:T.green}}>${(sub.paid_to_date||0).toLocaleString()}</strong>
                        <span style={{color:T.muted}}> ({paidPct}%)</span>
                      </div>
                      {/* Progress bar */}
                      <div style={{background:T.bg3,borderRadius:4,height:4,overflow:"hidden"}}>
                        <div style={{width:paidPct+"%",height:"100%",background:T.green,borderRadius:4}}/>
                      </div>
                    </div>
                  </div>

                  {/* License + performance */}
                  <div style={{display:"grid",gridTemplateColumns:isMob?"1fr":"1fr 1fr",gap:8,marginBottom:10}}>
                    <div style={{background:T.bg1,borderRadius:8,padding:"10px 12px"}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>License & Compliance</div>
                      <div style={{fontSize:11,color:T.text}}>{sub.cslb}</div>
                      <div style={{fontSize:11,color:T.muted}}>COI expires: {sub.coi_expiry}</div>
                    </div>
                    <div style={{background:T.bg1,borderRadius:8,padding:"10px 12px"}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>Performance</div>
                      <div style={{display:"flex",gap:4,marginBottom:4}}>
                        {"★★★★★".slice(0,Math.round(sub.rating||0)).split("").map((_,i)=>(
                          <span key={i} style={{color:T.gold,fontSize:14}}>★</span>
                        ))}
                        {"★★★★★".slice(Math.round(sub.rating||0)).split("").map((_,i)=>(
                          <span key={i} style={{color:T.bg3,fontSize:14}}>★</span>
                        ))}
                        <span style={{fontSize:11,color:T.muted,marginLeft:4}}>{sub.rating}/5</span>
                      </div>
                      <div style={{fontSize:11,color:T.muted}}>On-time: {sub.on_time_pct}%</div>
                    </div>
                  </div>

                  {/* Notes */}
                  {sub.notes&&(
                    <div style={{background:T.amberBg,borderRadius:7,padding:"8px 11px",fontSize:11,color:T.muted,lineHeight:1.6,marginBottom:10}}>
                      📝 {sub.notes}
                    </div>
                  )}

                  {/* Quick actions */}
                  <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
                    <a href={"tel:"+sub.phone}
                      style={{flex:1,minWidth:100,padding:"8px",background:T.teal,color:"white",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",border:"none",textDecoration:"none",textAlign:"center"}}>
                      📞 Call
                    </a>
                    <a href={"sms:"+sub.phone}
                      style={{flex:1,minWidth:100,padding:"8px",background:T.blue,color:"white",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",border:"none",textDecoration:"none",textAlign:"center"}}>
                      💬 Text
                    </a>
                    <a href={"mailto:"+sub.email}
                      style={{flex:1,minWidth:100,padding:"8px",background:T.purple,color:"white",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",border:"none",textDecoration:"none",textAlign:"center"}}>
                      ✉ Email
                    </a>
                    {realSubs.length>0&&(
                      <button onClick={(e)=>{
                        e.stopPropagation();
                        if(!window.confirm(`Remove ${sub.name} from this project?`)) return;
                        try {
                          const d = loadSubDB(session?.companyId);
                          const updated = (d.subs||[]).map(s=>s.id===sub.id
                            ? {...s, projects:(s.projects||[]).filter(p=>p!==String(proj?.id)), projects_worked:(s.projects_worked||[]).filter(p=>p!==String(proj?.id))}
                            : s
                          );
                          saveSubDB(session?.companyId, {...d, subs:updated});
                          setExpandedSub(null);
                          window.location.reload();
                        } catch(e2) { alert("Could not remove sub: "+e2.message); }
                      }}
                        style={{flex:1,minWidth:80,padding:"8px",background:T.redBg,color:T.red,borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",border:`1px solid ${T.red}30`,textAlign:"center"}}>
                        🗑 Remove
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Invite panel */}
      {showInvite&&(
        <div style={{background:T.bg1,border:"1px solid "+T.teal+"40",borderRadius:12,overflow:"hidden",marginTop:8}}>
          <div style={{background:T.teal+"15",borderBottom:"1px solid "+T.teal+"30",padding:"12px 16px"}}>
            <div style={{fontSize:13,fontWeight:700,color:T.teal,marginBottom:2}}>📨 Invite a Subcontractor</div>
            <div style={{fontSize:11,color:T.muted}}>Send invite code → sub registers → you approve → they see this project only</div>
          </div>
          <div style={{padding:"14px 16px"}}>
            <SubInvitePanel proj={proj} session={session}/>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PROJECT TEAM CHAT ────────────────────────────────────────────────────────
// ─── TIME TRACKER ─────────────────────────────────────────────────────────────


function GanttView({proj}) {
  const key = "grok_schedule_" + proj?.id;
  const schedule = (()=>{ try{return JSON.parse(localStorage.getItem(key)||"null");}catch{return null;} })();
  const COLORS = {"Inspection":"#f97316","Plumbing":"#3b82f6","Electrical":"#a78bfa","Framing":"#f59e0b","HVAC":"#06b6d4","Drywall":"#10b981","Concrete":"#6b7280","Roofing":"#8b5cf6","Flooring":"#ec4899","Painting":"#84cc16","default":"#60a5fa"};
  const tc = (trade) => COLORS[trade]||COLORS.default;

  if (!schedule) return (
    <div style={{textAlign:"center",padding:"40px 20px",background:T.bg2,borderRadius:10,border:"2px dashed "+T.subtle}}>
      <div style={{fontSize:28,opacity:0.3,marginBottom:8}}>📊</div>
      <div style={{fontSize:13,fontWeight:600,color:T.text,marginBottom:6}}>No schedule yet</div>
      <div style={{fontSize:11,color:T.muted,lineHeight:1.7}}>Generate your 2-week schedule first in the <strong>📈 2-Wk Schedule</strong> tab. Gantt will visualize it automatically.</div>
    </div>
  );

  const allDays = schedule.weeks?.flatMap(w=>w.days||[])||[];
  const allTasks = [];
  allDays.forEach((day,di)=>(day.tasks||[]).forEach(t=>allTasks.push({...t,dayIdx:di,dayLabel:day.day})));
  const byTrade = {};
  allTasks.forEach(t=>{const tr=t.trade||"General";if(!byTrade[tr])byTrade[tr]=[];byTrade[tr].push(t);});
  const DAY_W = Math.max(30, Math.floor(600/Math.max(allDays.length,1)));

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text}}>Gantt Timeline</div>
        <div style={{fontSize:10,color:T.muted}}>Visual view of your AI schedule · Edit tasks in 📈 2-Wk Schedule</div>
      </div>
      <div style={{overflowX:"auto",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10}}>
        <div style={{display:"flex",borderBottom:"1px solid "+T.subtle,background:T.bg2,position:"sticky",top:0}}>
          <div style={{width:120,flexShrink:0,padding:"8px 10px",fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",borderRight:"1px solid "+T.subtle}}>Trade</div>
          {allDays.map((day,i)=>(
            <div key={i} style={{width:DAY_W,flexShrink:0,textAlign:"center",padding:"4px 2px",borderRight:"1px solid "+T.subtle+"30"}}>
              <div style={{fontSize:8,fontWeight:700,color:T.muted}}>{(day.day||"").split(" ")[0]}</div>
              <div style={{fontSize:9,color:T.text,fontWeight:600}}>{(day.day||"").split(" ")[1]||""}</div>
            </div>
          ))}
        </div>
        {Object.entries(byTrade).map(([trade,tasks])=>(
          <div key={trade} style={{borderBottom:"1px solid "+T.subtle+"30"}}>
            <div style={{display:"flex",alignItems:"center",background:tc(trade)+"12",borderBottom:"1px solid "+T.subtle+"20"}}>
              <div style={{width:120,flexShrink:0,padding:"5px 10px",fontSize:10,fontWeight:700,color:tc(trade),borderRight:"1px solid "+T.subtle}}>{trade}</div>
              <div style={{flex:1,height:4,background:tc(trade)+"20"}}/>
            </div>
            {tasks.map((task,ti)=>(
              <div key={task.id||ti} style={{display:"flex",alignItems:"center",minHeight:26}}>
                <div style={{width:120,flexShrink:0,padding:"3px 10px",fontSize:9,color:T.muted,borderRight:"1px solid "+T.subtle,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis"}} title={task.task}>{task.task.slice(0,14)}{task.task.length>14?"…":""}</div>
                <div style={{display:"flex",flex:1,position:"relative",height:26}}>
                  {allDays.map((_,di)=><div key={di} style={{width:DAY_W,flexShrink:0,height:"100%",borderRight:"1px solid "+T.subtle+"20",background:di%2===0?"transparent":T.bg2+"50"}}/>)}
                  <div style={{position:"absolute",left:task.dayIdx*DAY_W+2,top:3,width:Math.max(DAY_W-4,18),height:20,background:task.type==="inspection"?T.amber:tc(trade),borderRadius:3,fontSize:7,color:"white",fontWeight:600,paddingLeft:3,overflow:"hidden",whiteSpace:"nowrap",boxShadow:"0 1px 2px rgba(0,0,0,0.15)",opacity:task.done?0.5:1}} title={task.task}>
                    {task.done&&"✓ "}{task.task.slice(0,Math.max(2,Math.floor(DAY_W/6)))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:10,marginTop:8,flexWrap:"wrap"}}>
        <span style={{fontSize:9,color:T.muted,fontWeight:700,textTransform:"uppercase",alignSelf:"center"}}>Legend:</span>
        {Object.entries(byTrade).map(([tr])=><div key={tr} style={{display:"flex",alignItems:"center",gap:3}}><div style={{width:10,height:6,borderRadius:2,background:tc(tr)}}/><span style={{fontSize:9,color:T.muted}}>{tr}</span></div>)}
        <div style={{display:"flex",alignItems:"center",gap:3}}><div style={{width:10,height:6,borderRadius:2,background:T.amber}}/><span style={{fontSize:9,color:T.muted}}>Inspection</span></div>
      </div>
    </div>
  );
}

// ─── PURCHASE ORDERS VIEW ─────────────────────────────────────────────────────────────────
// Formal POs issued to vendors/subs after bid award.
// Created automatically when you click "Convert to PO" in Sub Bid Compare,
// or manually from Finance > Purchase Orders tab.
function PurchaseOrdersView({proj, session}) {
  const key = "grok_pos_" + proj?.id;
  const [pos, setPos] = React.useState(()=>{try{return JSON.parse(localStorage.getItem(key)||"[]");}catch{return [];}});
  const [showAdd, setShowAdd] = React.useState(false);
  const [form, setForm] = React.useState({vendor:"",contact:"",trade:"",scope:"",amount:"",terms:"Net 30 · Lien waiver required at each payment",notes:""});
  const [activeId, setActiveId] = React.useState(null);
  const save = (u)=>{setPos(u);try{localStorage.setItem(key,JSON.stringify(u));}catch(e){}};
  const addPO = ()=>{
    if(!form.vendor||!form.amount)return;
    const po={id:"PO-"+Date.now(),projectId:proj?.id,projectName:proj?.name,vendor:form.vendor,contact:form.contact,trade:form.trade,scope:form.scope,amount:parseFloat(form.amount)||0,status:"draft",terms:form.terms,notes:form.notes,createdAt:new Date().toLocaleDateString()};
    save([po,...pos]);setForm({vendor:"",contact:"",trade:"",scope:"",amount:"",terms:"Net 30 · Lien waiver required at each payment",notes:""});setShowAdd(false);
  };
  const exportPO = (po)=>{
    const cs=(()=>{try{return JSON.parse(localStorage.getItem("grok_company_settings_"+(session?.companyId||"local"))||"{}");}catch{return {};}})();
    const w=window.open("","_blank");
    w.document.write(`<html><head><title>${po.id}</title><style>body{font-family:Arial,sans-serif;max-width:700px;margin:40px auto}.po{font-size:22px;font-weight:800;color:#0d9488}table{width:100%;border-collapse:collapse;margin:16px 0}th{background:#f0f4f8;padding:8px;font-size:11px;text-align:left}td{padding:8px;border-bottom:1px solid #eee;font-size:12px}.amt{font-size:20px;font-weight:800;color:#0d9488;text-align:right;margin:16px 0}.sig{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:40px}.sl{border-top:1px solid #111;padding-top:5px;font-size:10px}</style></head><body>
    <div style="display:flex;justify-content:space-between;margin-bottom:24px"><div><div style="font-size:18px;font-weight:800">${cs.co_header_name||"GC Company"}</div><div style="font-size:11px;color:#666">CSLB ${cs.cslb||""} · ${cs.co_header_email||""}</div></div><div style="text-align:right"><div class="po">${po.id}</div><div style="font-size:11px;color:#666">Date: ${po.createdAt}<br/>Project: ${po.projectName}</div></div></div>
    <table><tr><th>Vendor</th><th>Contact</th><th>Trade</th></tr><tr><td><strong>${po.vendor}</strong></td><td>${po.contact||"—"}</td><td>${po.trade||"—"}</td></tr></table>
    <p><strong>Scope:</strong> ${po.scope||"As agreed"}</p><div class="amt">Contract Amount: $${(po.amount||0).toLocaleString()}</div>
    <div style="background:#fffbeb;border:1px solid #fcd34d;padding:12px;border-radius:6px;font-size:11px"><strong>Terms:</strong> ${po.terms}</div>
    ${po.notes?`<p style="font-size:11px"><strong>Notes:</strong> ${po.notes}</p>`:""}
    <div class="sig"><div><div class="sl">GC Authorized Signature · Date</div></div><div><div class="sl">Vendor Signature · Date</div></div></div></body></html>`);
    w.document.close();w.print();
  };
  const SC={draft:"#94a3b8",sent:"#60a5fa",signed:"#10b981",paid:"#a78bfa",cancelled:"#ef4444"};
  const inp={width:"100%",background:T.bg1,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"};
  const total=pos.reduce((s,p)=>s+(p.amount||0),0);
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div><div style={{fontSize:13,fontWeight:700,color:T.text}}>Purchase Orders</div>
        <div style={{fontSize:10,color:T.muted,marginTop:2}}>{pos.length} PO{pos.length!==1?"s":""} · Total: ${total.toLocaleString()}</div></div>
        <Btn small primary onClick={()=>setShowAdd(p=>!p)}><Ic d={ICONS.plus} size={11}/> New PO</Btn>
      </div>
      {showAdd&&(
        <div style={{background:T.bg2,border:"1px solid "+T.blue+"30",borderRadius:10,padding:"14px 16px",marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>New Purchase Order</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
            {[["vendor","Vendor *","Lee Electric Inc"],["contact","Contact","Tom (415) 555-0100"],["trade","Trade","Electrical"]].map(([k,l,ph])=>(
              <div key={k}><div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>{l}</div><input value={form[k]} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} placeholder={ph} style={inp}/></div>
            ))}
          </div>
          <div style={{marginBottom:8}}><div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Scope of Work *</div><input value={form.scope} onChange={e=>setForm(p=>({...p,scope:e.target.value}))} placeholder="Electrical rough-in — all units" style={inp}/></div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 2fr",gap:8,marginBottom:8}}>
            <div><div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Amount *</div><input type="number" value={form.amount} onChange={e=>setForm(p=>({...p,amount:e.target.value}))} placeholder="148000" style={inp}/></div>
            <div><div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Terms</div><input value={form.terms} onChange={e=>setForm(p=>({...p,terms:e.target.value}))} style={inp}/></div>
          </div>
          <div style={{marginBottom:10}}><div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Notes</div><input value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="Schedule, deliverables, conditions..." style={inp}/></div>
          <div style={{display:"flex",gap:7}}><Btn ghost small onClick={()=>setShowAdd(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn><Btn primary small disabled={!form.vendor||!form.amount} onClick={addPO} style={{flex:2,justifyContent:"center"}}>Create Purchase Order</Btn></div>
        </div>
      )}
      {pos.length===0&&!showAdd&&(
        <div style={{textAlign:"center",padding:"32px",color:T.muted,background:T.bg2,borderRadius:10,border:"2px dashed "+T.subtle}}>
          <div style={{fontSize:28,opacity:0.3,marginBottom:8}}>📋</div>
          <div style={{fontSize:12,fontWeight:600,marginBottom:4}}>No Purchase Orders yet</div>
          <div style={{fontSize:11,lineHeight:1.7}}>Create manually above, or click <strong>Convert to PO</strong> after awarding a bid in Sub Bid Compare.</div>
        </div>
      )}
      {pos.map(po=>(
        <div key={po.id} style={{background:T.bg1,borderRadius:10,border:"1px solid "+T.subtle,marginBottom:8,overflow:"hidden"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",cursor:"pointer"}} onClick={()=>setActiveId(activeId===po.id?null:po.id)}>
            <div style={{flex:1}}>
              <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:2}}>
                <span style={{fontSize:11,fontWeight:800,color:T.text,fontFamily:"monospace"}}>{po.id}</span>
                <span style={{fontSize:9,fontWeight:700,background:SC[po.status]+"20",color:SC[po.status],borderRadius:10,padding:"1px 7px",textTransform:"capitalize"}}>{po.status}</span>
              </div>
              <div style={{fontSize:12,fontWeight:700,color:T.text}}>{po.vendor} — {po.trade}</div>
              <div style={{fontSize:10,color:T.muted}}>{(po.scope||"").slice(0,60)}{(po.scope||"").length>60?"…":""}</div>
            </div>
            <div style={{textAlign:"right",flexShrink:0}}>
              <div style={{fontSize:15,fontWeight:800,color:T.teal,fontFamily:"monospace"}}>${(po.amount||0).toLocaleString()}</div>
              <div style={{fontSize:9,color:T.muted}}>{po.createdAt}</div>
            </div>
            <span style={{color:T.muted,fontSize:11}}>{activeId===po.id?"▲":"▼"}</span>
          </div>
          {activeId===po.id&&(
            <div style={{padding:"12px 14px",background:T.bg2,borderTop:"1px solid "+T.subtle}}>
              <div style={{fontSize:11,color:T.muted,marginBottom:8,lineHeight:1.7}}>
                <strong style={{color:T.text}}>Contact:</strong> {po.contact||"—"}<br/>
                <strong style={{color:T.text}}>Terms:</strong> {po.terms}<br/>
                {po.notes&&<><strong style={{color:T.text}}>Notes:</strong> {po.notes}</>}
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {[["draft","Draft"],["sent","Sent"],["signed","Signed"],["paid","Paid"],["cancelled","Cancelled"]].map(([s,l])=>(
                  <button key={s} onClick={()=>save(pos.map(p=>p.id===po.id?{...p,status:s}:p))}
                    style={{fontSize:10,fontWeight:600,padding:"4px 10px",borderRadius:7,cursor:"pointer",border:"1px solid "+(po.status===s?SC[s]:T.subtle),background:po.status===s?SC[s]+"20":T.bg1,color:po.status===s?SC[s]:T.muted}}>{l}</button>
                ))}
                <button onClick={()=>exportPO(po)} style={{fontSize:10,fontWeight:700,background:T.teal,color:"white",border:"none",borderRadius:7,padding:"4px 12px",cursor:"pointer",marginLeft:"auto"}}>Print PO</button>
                <button onClick={()=>{if(!true)return;save(pos.filter(p=>String(p.id)!==String(po.id)));}} style={{fontSize:10,background:"none",color:T.muted,border:"1px solid "+T.subtle,borderRadius:7,padding:"4px 8px",cursor:"pointer"}}>Delete</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── AI TAKEOFF VIEW ───────────────────────────────────────────────────────────────────────
// Blueprint Full Review = scope + code flags (our strength).
// AI Material Takeoff — full quantity extraction + pricing for GC and Trade accounts
function AITakeoffView({proj, activeBlueprint, session}) {
  const key = "grok_takeoff_" + proj?.id;
  const isTrade = session?.accountType === "trade";
  const [result, setResult] = React.useState(()=>{try{return JSON.parse(localStorage.getItem(key)||"null");}catch{return null;}});
  const [loading, setLoading] = React.useState(false);
  const [pricingLoading, setPricingLoading] = React.useState(false);
  const [pricing, setPricing] = React.useState(()=>{try{return JSON.parse(localStorage.getItem(key+"_pricing")||"null");}catch{return null;}});
  const [mode, setMode] = React.useState("quantities"); // quantities | pricing | full
  const [tradeFilter, setTradeFilter] = React.useState("All");
  const isMob = window.innerWidth < 768;

  const TRADE_CATEGORIES = {
    "Electrical":["Electrical Panel & Service","Circuits & Wiring","Devices & Fixtures","Low Voltage / Data","EV Charger"],
    "Plumbing":["Drain & Waste","Supply Lines","Fixtures","Water Heater","Gas Piping"],
    "HVAC":["Ductwork","Equipment","Controls & Thermostats","Grilles & Diffusers"],
    "Framing":["Lumber","Hardware & Fasteners","Sheathing","Headers & Beams"],
    "All":["Doors & Windows","Floor Areas","Wall Lengths","Ceilings","Plumbing Fixtures","Electrical","Structural","Finishes & Tile","Cabinetry & Millwork","Mechanical"]
  };

  const runTakeoff = async()=>{
    if(!activeBlueprint?.data){alert("Upload a blueprint first.");return;}
    if(!enforceLimit("blueprint",session))return;
    setLoading(true);
    try{
      const tradeCtx = isTrade ? "\nFocus especially on: " + (session?.trade||"all trade") + " work visible in the drawing." : "";
      const resp=await fetch(AI_PROXY_URL,{method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
        body:JSON.stringify({model:"claude-haiku-4-5",max_tokens:3000,
          messages:[{role:"user",content:[
            {type:"image",source:{type:"base64",media_type:activeBlueprint.type||"image/png",data:activeBlueprint.data.split(",")[1]||activeBlueprint.data}},
            {type:"text",text:"You are a professional construction takeoff estimator with 20 years of Bay Area experience." + tradeCtx + "\n\n" +
            "Carefully analyze this blueprint and extract ALL quantities visible. Be precise.\n\n" +
            "Return ONLY valid JSON (no markdown, no backticks):\n" +
            '{"summary":"what you see","drawing_type":"Floor Plan|Elevation|Section|MEP","categories":[{' +
            '"category":"Doors & Windows","trade":"Carpentry","items":[{' +
            '"item":"Exterior Door 3070","qty":2,"unit":"EA","unit_cost_low":450,"unit_cost_high":850,"notes":"ADA per plan"}' +
            ']},{"category":"Floor Areas","trade":"General","items":[{"item":"Living Area","qty":380,"unit":"SF","unit_cost_low":0,"unit_cost_high":0,"notes":""}]},' +
            '{"category":"Wall Framing","trade":"Framing","items":[]},' +
            '{"category":"Plumbing Fixtures","trade":"Plumbing","items":[]},' +
            '{"category":"Electrical","trade":"Electrical","items":[]},' +
            '{"category":"Finishes & Tile","trade":"Tile","items":[]},' +
            '{"category":"Cabinetry & Millwork","trade":"Carpentry","items":[]},' +
            '{"category":"Structural","trade":"Structural","items":[]}' +
            '],"total_sf":0,"notes":"assumptions and field verification items","confidence":"high|medium|low"}' +
            "\n\nFor each item include unit_cost_low and unit_cost_high for 2026 SF Bay Area. Return quantities you can see - leave qty as 0 if not visible."}
          ]}]})});
      const data=await resp.json();
      const text=data.content?.[0]?.text||"{}";
      const parsed=JSON.parse(text.replace(/\x60\x60\x60json|\x60\x60\x60/g,"").trim());
      const saved={...parsed,generatedAt:new Date().toLocaleDateString(),generatedTime:new Date().toLocaleTimeString(),source:activeBlueprint.name};
      setResult(saved);
      try{localStorage.setItem(key,JSON.stringify(saved));}catch(e){}
    }catch(e){alert("Takeoff error: "+e.message);}
    setLoading(false);
  };

  const runPricing = async()=>{
    if(!result)return;
    setPricingLoading(true);
    // Build item list for pricing
    const items = result.categories?.flatMap(c=>c.items?.map(i=>({...i,category:c.category,trade:c.trade}))||[]).filter(i=>i.qty>0) || [];
    try{
      const resp=await fetch(AI_PROXY_URL,{method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
        body:JSON.stringify({model:"claude-haiku-4-5",max_tokens:3000,
          messages:[{role:"user",content:[{type:"text",text:
            "You are a Bay Area construction cost estimator. Price this material takeoff for 2026 SF Bay Area market rates.\n\n" +
            "Items to price:\n" +
            items.map(i=>"- "+i.category+": "+i.item+" x "+i.qty+" "+i.unit).join("\n") +
            "\n\nReturn ONLY valid JSON (no markdown):\n" +
            '{"line_items":[{"item":"name","qty":2,"unit":"EA","material_unit":450,"labor_unit":180,"total":1260,"trade":"Carpentry","notes":"allowance"}],' +
            '"subtotals":{"material":0,"labor":0,"total":0},' +
            '"gc_notes":"Items needing field verification","confidence":"high|medium|low"}' +
            "\n\nUse 2026 SF Bay Area pricing. Separate material and labor. Flag allowances."
          }]}]})});
      const pData=await resp.json();
      const pText=pData.content?.[0]?.text||"{}";
      const pParsed=JSON.parse(pText.replace(/\x60\x60\x60json|\x60\x60\x60/g,"").trim());
      const pSaved={...pParsed,generatedAt:new Date().toLocaleDateString(),source:result.source};
      setPricing(pSaved);
      try{localStorage.setItem(key+"_pricing",JSON.stringify(pSaved));}catch(e){}
    }catch(e){alert("Pricing error: "+e.message);}
    setPricingLoading(false);
  };

  const exportCSV=()=>{
    if(!result&&!pricing)return;
    let csv = "Category,Trade,Item,Qty,Unit,Material Unit,Labor Unit,Total,Notes\n";
    if(pricing?.line_items?.length){
      pricing.line_items.forEach(item=>{
        csv+='"'+(item.trade||"")+'","'+(item.trade||"")+'","'+item.item+'",'+item.qty+',"'+item.unit+'",'+(item.material_unit||0)+','+(item.labor_unit||0)+','+(item.total||0)+',"'+(item.notes||"")+'"\n';
      });
      csv+="\nMaterial Subtotal,,,,,,,"+(pricing.subtotals?.material||0)+"\n";
      csv+="Labor Subtotal,,,,,,,"+(pricing.subtotals?.labor||0)+"\n";
      csv+="TOTAL,,,,,,,"+(pricing.subtotals?.total||0)+"\n";
    } else if(result?.categories) {
      result.categories.forEach(cat=>cat.items?.forEach(item=>{
        csv+='"'+cat.category+'","'+(cat.trade||"")+'","'+(item.item)+'",'+item.qty+',"'+(item.unit)+'","","","","'+(item.notes||"")+'"\n';
      }));
    }
    const a=document.createElement("a");a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);
    a.download=(proj?.name||"project").replace(/\s/g,"_")+"_takeoff.csv";document.body.appendChild(a);a.click();document.body.removeChild(a);
  };

  const totalItems = result?.categories?.reduce((s,c)=>s+(c.items?.length||0),0)||0;
  const totalWithQty = result?.categories?.reduce((s,c)=>s+(c.items?.filter(i=>i.qty>0).length||0),0)||0;
  const allTrades = result ? ["All",...new Set(result.categories?.map(c=>c.trade).filter(Boolean)||[])] : ["All"];
  const filteredCats = result?.categories?.filter(c=>tradeFilter==="All"||c.trade===tradeFilter).filter(c=>(c.items?.length||0)>0) || [];

  return (
    <div>
      {/* Header */}
      <div style={{background:`linear-gradient(135deg,${T.purple}18,${T.blue}12)`,border:"1px solid "+T.purple+"30",borderRadius:12,padding:"14px 16px",marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div>
            <div style={{fontSize:13,fontWeight:800,color:T.purple,marginBottom:3}}>📐 AI Material Takeoff</div>
            <div style={{fontSize:11,color:T.muted,lineHeight:1.6}}>
              {isTrade
                ? "Upload any blueprint → AI extracts quantities for your " + (session?.trade||"trade") + " scope → instant Bay Area pricing."
                : "Upload any blueprint → AI extracts all quantities → adds Bay Area 2026 material + labor pricing."}
            </div>
          </div>
          {result&&<div style={{textAlign:"right",flexShrink:0,marginLeft:12}}>
            <div style={{fontSize:18,fontWeight:800,color:T.teal}}>{totalWithQty}</div>
            <div style={{fontSize:9,color:T.muted}}>items with qty</div>
          </div>}
        </div>
      </div>

      {/* Action buttons */}
      <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
        <button onClick={runTakeoff} disabled={loading||!activeBlueprint}
          style={{padding:"9px 16px",background:loading||!activeBlueprint?"#374151":`linear-gradient(135deg,${T.purple},${T.blue})`,
            color:"white",border:"none",borderRadius:9,fontSize:12,fontWeight:700,cursor:loading||!activeBlueprint?"not-allowed":"pointer",
            display:"flex",alignItems:"center",gap:7}}>
          {loading?<><Spin/> Extracting quantities...</>:"📐 Run Takeoff"}
        </button>
        {result&&(
          <button onClick={runPricing} disabled={pricingLoading}
            style={{padding:"9px 16px",background:pricingLoading?"#374151":`linear-gradient(135deg,${T.gold},${T.gold2})`,
              color:pricingLoading?"white":"#02040A",border:"none",borderRadius:9,fontSize:12,fontWeight:700,cursor:pricingLoading?"not-allowed":"pointer",
              display:"flex",alignItems:"center",gap:7}}>
            {pricingLoading?<><Spin/> Pricing...</>:"💰 Add Bay Area Pricing"}
          </button>
        )}
        {(result||pricing)&&(
          <button onClick={exportCSV}
            style={{padding:"9px 14px",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:9,fontSize:12,color:T.muted,cursor:"pointer",fontWeight:600}}>
            📥 Export CSV
          </button>
        )}
        {!activeBlueprint&&<span style={{fontSize:11,color:T.amber}}>⚠ Upload a blueprint in Full Review first</span>}
      </div>

      {/* Pricing summary card */}
      {pricing?.subtotals&&(
        <div style={{display:"grid",gridTemplateColumns:isMob?"1fr 1fr":"1fr 1fr 1fr",gap:8,marginBottom:14}}>
          {[
            {label:"Material Cost",val:"$"+(pricing.subtotals.material||0).toLocaleString(),color:T.blue},
            {label:"Labor Cost",val:"$"+(pricing.subtotals.labor||0).toLocaleString(),color:T.purple},
            {label:"Total (Mat + Labor)",val:"$"+(pricing.subtotals.total||0).toLocaleString(),color:T.gold},
          ].map((s,i)=>(
            <div key={i} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 14px",textAlign:"center"}}>
              <div style={{fontSize:18,fontWeight:800,color:s.color,letterSpacing:"-0.5px"}}>{s.val}</div>
              <div style={{fontSize:9,color:T.muted,marginTop:3,textTransform:"uppercase",letterSpacing:".08em"}}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Trade filter */}
      {result&&allTrades.length>2&&(
        <div style={{display:"flex",gap:6,marginBottom:12,overflowX:"auto",paddingBottom:4}}>
          {allTrades.map(t=>(
            <button key={t} onClick={()=>setTradeFilter(t)}
              style={{padding:"5px 12px",borderRadius:20,fontSize:10,fontWeight:700,border:"1px solid "+(tradeFilter===t?T.gold:T.subtle),
                background:tradeFilter===t?T.gold+"20":T.bg2,color:tradeFilter===t?T.gold:T.muted,cursor:"pointer",whiteSpace:"nowrap"}}>
              {t}
            </button>
          ))}
        </div>
      )}

      {/* Quantity results */}
      {result&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{fontSize:10,color:T.muted}}>
              From <strong style={{color:T.text}}>{result.source}</strong> · {result.generatedAt}
              {result.drawing_type&&<span style={{marginLeft:8,background:T.bg2,padding:"2px 7px",borderRadius:9,fontSize:9}}>{result.drawing_type}</span>}
              {result.confidence&&<span style={{marginLeft:6,fontSize:9,color:result.confidence==="high"?T.green:result.confidence==="medium"?T.amber:T.red}}>● {result.confidence} confidence</span>}
            </div>
          </div>
          {result.summary&&<div style={{background:T.bg2,borderRadius:8,padding:"9px 12px",marginBottom:12,fontSize:11,color:T.muted,lineHeight:1.6}}><strong style={{color:T.text}}>Drawing: </strong>{result.summary}</div>}

          {/* Pricing line items if available */}
          {pricing?.line_items?.length>0 ? (
            <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:12,overflow:"hidden",marginBottom:12}}>
              <div style={{background:T.bg2,padding:"9px 14px",borderBottom:"1px solid "+T.subtle,display:"grid",gridTemplateColumns:"1fr 60px 60px 80px 80px 80px",gap:8}}>
                {["Item","Qty","Unit","Material","Labor","Total"].map(h=>(
                  <span key={h} style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:".08em"}}>{h}</span>
                ))}
              </div>
              {pricing.line_items.filter(i=>tradeFilter==="All"||i.trade===tradeFilter).map((item,ii)=>(
                <div key={ii} style={{display:"grid",gridTemplateColumns:"1fr 60px 60px 80px 80px 80px",gap:8,padding:"8px 14px",borderBottom:"1px solid "+T.subtle+"40",alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:11,color:T.text}}>{item.item}</div>
                    {item.notes&&<div style={{fontSize:9,color:T.amber,marginTop:1}}>{item.notes}</div>}
                  </div>
                  <div style={{fontSize:12,fontWeight:700,color:T.teal,fontFamily:"monospace"}}>{item.qty}</div>
                  <div style={{fontSize:10,color:T.muted}}>{item.unit}</div>
                  <div style={{fontSize:11,color:T.blue,fontFamily:"monospace"}}>${(item.material_unit||0).toLocaleString()}</div>
                  <div style={{fontSize:11,color:T.purple,fontFamily:"monospace"}}>${(item.labor_unit||0).toLocaleString()}</div>
                  <div style={{fontSize:12,fontWeight:700,color:T.gold,fontFamily:"monospace"}}>${(item.total||0).toLocaleString()}</div>
                </div>
              ))}
              <div style={{padding:"10px 14px",background:T.bg2,display:"grid",gridTemplateColumns:"1fr 60px 60px 80px 80px 80px",gap:8}}>
                <div style={{fontSize:11,fontWeight:700,color:T.text}}>SUBTOTAL</div>
                <div/><div/>
                <div style={{fontSize:11,fontWeight:700,color:T.blue,fontFamily:"monospace"}}>${(pricing.subtotals?.material||0).toLocaleString()}</div>
                <div style={{fontSize:11,fontWeight:700,color:T.purple,fontFamily:"monospace"}}>${(pricing.subtotals?.labor||0).toLocaleString()}</div>
                <div style={{fontSize:12,fontWeight:800,color:T.gold,fontFamily:"monospace"}}>${(pricing.subtotals?.total||0).toLocaleString()}</div>
              </div>
            </div>
          ) : (
            // Quantities only view
            filteredCats.map((cat,ci)=>(
              <div key={ci} style={{marginBottom:9,background:T.bg1,borderRadius:10,border:"1px solid "+T.subtle,overflow:"hidden"}}>
                <div style={{background:T.bg2,padding:"7px 14px",borderBottom:"1px solid "+T.subtle,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:11,fontWeight:700,color:T.text}}>{cat.category}</span>
                    {cat.trade&&<span style={{fontSize:9,background:T.bg3,color:T.muted,padding:"2px 7px",borderRadius:9}}>{cat.trade}</span>}
                  </div>
                  <span style={{fontSize:9,color:T.muted}}>{cat.items?.length} items</span>
                </div>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr style={{background:T.bg2+"80"}}>{["Item","Qty","Unit","Unit Cost Range","Notes"].map(h=>(
                    <th key={h} style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",padding:"5px 12px",textAlign:"left",borderBottom:"1px solid "+T.subtle+"50"}}>{h}</th>
                  ))}</tr></thead>
                  <tbody>{cat.items?.map((item,ii)=>(
                    <tr key={ii} style={{borderBottom:"1px solid "+T.subtle+"30"}}>
                      <td style={{padding:"6px 12px",fontSize:11,color:T.text}}>{item.item}</td>
                      <td style={{padding:"6px 12px",fontSize:14,fontWeight:800,color:item.qty>0?T.teal:T.muted,fontFamily:"monospace"}}>{item.qty||"—"}</td>
                      <td style={{padding:"6px 12px",fontSize:10,color:T.muted}}>{item.unit}</td>
                      <td style={{padding:"6px 12px",fontSize:10,color:T.muted}}>
                        {item.unit_cost_low>0?`$${item.unit_cost_low.toLocaleString()}–$${item.unit_cost_high.toLocaleString()}`:"—"}
                      </td>
                      <td style={{padding:"6px 12px",fontSize:10,color:T.muted}}>{item.notes||"—"}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            ))
          )}

          {pricing?.gc_notes&&(
            <div style={{background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:9,padding:"10px 14px",fontSize:11,color:T.amber,lineHeight:1.7,marginBottom:10}}>
              <strong>⚠ Field Verification: </strong>{pricing.gc_notes}
            </div>
          )}
          {result.notes&&!pricing&&(
            <div style={{background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:9,padding:"10px 14px",fontSize:11,color:T.amber,lineHeight:1.7}}>
              <strong>⚠ Assumptions: </strong>{result.notes}
            </div>
          )}

          {/* Push to estimate button */}
          {pricing?.subtotals?.total>0&&(
            <div style={{marginTop:12,padding:"12px 16px",background:T.greenBg,border:"1px solid "+T.green+"30",borderRadius:10,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:T.green}}>Ready to build your estimate?</div>
                <div style={{fontSize:10,color:T.muted,marginTop:2}}>Total material + labor: ${(pricing.subtotals.total||0).toLocaleString()} · Add markup in Pricing Calculator</div>
              </div>
              <button onClick={()=>{
                // Store takeoff total for use in pricing calculator
                localStorage.setItem("grok_takeoff_total_"+(proj?.id||"x"), JSON.stringify({
                  material: pricing.subtotals.material,
                  labor: pricing.subtotals.labor,
                  total: pricing.subtotals.total,
                  source: result.source,
                  date: result.generatedAt
                }));
                alert("✓ Takeoff total saved! Open AI Tools → Quick Estimate or Pricing Calculator to build your proposal.");
              }} style={{padding:"8px 16px",background:T.green,color:"white",border:"none",borderRadius:8,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",marginLeft:12}}>
                → Use in Estimate
              </button>
            </div>
          )}
        </div>
      )}

      {!result&&!loading&&(
        <div style={{textAlign:"center",padding:"48px 20px",background:T.bg2,borderRadius:12,border:"2px dashed "+T.subtle}}>
          <div style={{fontSize:32,opacity:0.25,marginBottom:10}}>📐</div>
          <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:6}}>AI Material Takeoff</div>
          <div style={{fontSize:11,color:T.muted,lineHeight:1.8,maxWidth:400,margin:"0 auto"}}>
            Upload any blueprint in the <strong>Full Review</strong> tab, then click <strong>Run Takeoff</strong>.<br/>
            AI extracts every quantity — doors, windows, floor areas, wall lengths, fixtures, electrical circuits — then prices everything at 2026 SF Bay Area rates.
          </div>
          {!activeBlueprint&&(
            <div style={{marginTop:14,fontSize:11,color:T.amber,fontWeight:600}}>⚠ Upload a blueprint first</div>
          )}
        </div>
      )}
    </div>
  );
}



// ─── PERMIT FLOW VIEW ──────────────────────────────────────────────────────────────────────────────
// Centralized permit tracker across all projects.
// Builds on the existing building dept database and sub permit panel.
// Adds: cross-project permit dashboard, AI code compliance check, inspection scheduler.
function PermitFlowView({projects, session}) {
  const PERMIT_KEY = "grok_permits_" + (session?.companyId||"local");
  const apiKey = "proxy"; // Using Supabase AI proxy

  const [permits, setPermits] = React.useState(()=>{
    try{return JSON.parse(localStorage.getItem(PERMIT_KEY)||"[]");}catch{return [];}
  });
  const [selected, setSelected] = React.useState(null);
  const [showAdd, setShowAdd] = React.useState(false);
  const [filter, setFilter] = React.useState("all");
  const [activeTab, setActiveTab] = React.useState("permits"); // permits | requirements | ahj
  const [form, setForm] = React.useState({
    project_id:"", permit_type:"Building", permit_no:"", jurisdiction:"",
    issued_date:"", expiry_date:"", inspector_name:"", inspector_phone:"",
    status:"pending", notes:"", inspections:[]
  });
  const [aiChecking, setAiChecking] = React.useState(false);
  const [aiResult, setAiResult] = React.useState("");
  // Requirements lookup
  const [reqJurisdiction, setReqJurisdiction] = React.useState("San Francisco, CA");
  const [reqPermitType, setReqPermitType] = React.useState("Building");
  const [reqProjectType, setReqProjectType] = React.useState("SRO Renovation");
  const [reqLoading, setReqLoading] = React.useState(false);
  const [reqResult, setReqResult] = React.useState("");

  const PERMIT_TYPES = ["Building","Plumbing","Electrical","Mechanical","Fire Sprinkler","Demolition","Grading","Encroachment","ADU","Solar/PV","EV Charger","Other"];
  const STATUS_COLORS = {
    pending:"#f59e0b", submitted:"#3b82f6", active:"#10b981", inspection_ready:"#06b6d4",
    failed:"#ef4444", expired:"#94a3b8", closed:"#6b7280"
  };
  const STATUS_LABELS = {
    pending:"Pending Submission", submitted:"Submitted — Under Review", active:"Issued / Active",
    inspection_ready:"Ready for Inspection", failed:"Failed Inspection", expired:"Expired", closed:"Final / Closed"
  };
  const INSP_TYPES = [
    "Foundation","Pre-Slab","Underground Plumbing","Underground Electrical","Rough Framing",
    "Rough Plumbing","Rough Electrical","Rough Mechanical","Insulation","Drywall Nail",
    "Title 24 / HERS","Waterproofing","Structural Steel","Accessibility / ADA",
    "Final Building","Final Plumbing","Final Electrical","Final Mechanical","Certificate of Occupancy"
  ];

  // Bay Area AHJ Directory
  const AHJ_DIRECTORY = [
    {city:"San Francisco",dept:"SF Dept of Building Inspection (DBI)",phone:"(628) 652-3700",schedule:"https://sfdbi.org",cutoff:"By 3:30 PM for next-day morning",notes:"Use Accela portal (aca.accela.com/CCSF). OTC permits for small work. Major projects need plans examiner."},
    {city:"San Mateo",dept:"San Mateo Building Division",phone:"(650) 522-7172",schedule:"https://sanmateo.gov/permits",cutoff:"By 4:00 PM for next day",notes:"Online portal for simple permits. City Hall 330 W 20th Ave. Counter hours 8am-12pm."},
    {city:"Oakland",dept:"Oakland Building Services",phone:"(510) 238-3891",schedule:"https://oaklandca.gov/services/building",cutoff:"By 2:30 PM for next day",notes:"Use OAK Planning portal. High volume — expect delays. Pre-application meeting recommended for major work."},
    {city:"South San Francisco",dept:"SSF Building Division",phone:"(650) 877-8535",schedule:"https://ssf.net/departments/economic-community-development/building-division",cutoff:"By 3:30 PM for next day",notes:"Relatively streamlined. Same-day OTC for simple permits."},
    {city:"Daly City",dept:"Daly City Building Division",phone:"(650) 991-8061",schedule:"https://dalycity.org/279/Building-Permits",cutoff:"By 4:00 PM for next day",notes:"Counter permits available for simple work."},
    {city:"Redwood City",dept:"Redwood City Building Services",phone:"(650) 780-7350",schedule:"https://redwoodcity.org/departments/community-development/building",cutoff:"By 4:00 PM for next day",notes:""},
    {city:"Palo Alto",dept:"Palo Alto Planning & Development",phone:"(650) 329-2496",schedule:"https://cityofpaloalto.org/permits",cutoff:"By 3:00 PM for next day",notes:"Strict energy compliance (Title 24). Cal Green required. Long review times for major projects."},
    {city:"San Jose",dept:"San Jose Building Permits",phone:"(408) 535-7601",schedule:"https://permits.sanjoseca.gov",cutoff:"By 3:00 PM for next day",notes:"Accela portal. Major projects: allow 4-6 weeks for plan check."},
    {city:"Berkeley",dept:"Berkeley Planning & Development",phone:"(510) 981-7500",schedule:"https://www.cityofberkeley.info/permits",cutoff:"By 3:30 PM for next day",notes:"Very active inspectors. Zero-tolerance on energy code. Steep fees."},
  ];

  // Permit document checklists by type
  const DOC_CHECKLISTS = {
    "Building": ["Completed permit application (owner/contractor signature)","Two sets of plans (site plan, floor plan, elevations, sections)","Title 24 energy compliance forms","Structural calculations (if applicable)","Soils report (if foundation work)","Owner-Builder declaration (if no contractor)","Property tax bill or deed (proof of ownership)","Contractor license + insurance","Subcontractor list"],
    "Plumbing": ["Completed permit application","Plumbing plans (for new systems)","Fixture schedule","Water heater specs (if replacing)","Licensed plumber C-36 license","Contractor insurance/COI","Isometric drawing (for complex systems)"],
    "Electrical": ["Completed permit application","Electrical plans (load calc, panel schedule, single-line)","Load calculation worksheet","Licensed electrician C-10 license","Contractor insurance/COI","Equipment specs (panels, EVSE, solar inverters)"],
    "Mechanical": ["Completed permit application","Mechanical plans + equipment specs","Title 24 mechanical compliance (if AC/heating)","Licensed HVAC contractor C-20/C-38","Equipment cut sheets","Contractor insurance/COI"],
    "ADU": ["All Building permit docs","ADU deed restriction (recorded before permit)","Utility coordination letter","Setback/coverage calculations","Fire sprinkler plans (if required)","Owner-occupied declaration"],
    "Demolition": ["Completed demo permit application","Asbestos/lead clearance report (if pre-1978)","Hazmat disposal plan","Utility disconnect confirmation (gas, electric, water)","Dust/erosion control plan"],
    "Fire Sprinkler": ["Hydraulic calculations","Sprinkler head layout plans","Underground/aboveground permit","Water supply data","Licensed C-16 contractor"],
  };

  const save = (updated) => { setPermits(updated); try{localStorage.setItem(PERMIT_KEY,JSON.stringify(updated));}catch(e){} };
  const addPermit = () => {
    if (!form.permit_type||!form.project_id) return;
    const proj = projects.find(p=>String(p.id)===form.project_id);
    const p = { id:"PRMT-"+Date.now(), ...form, project_name:proj?.name||"Unknown", project_addr:proj?.addr||"", created:new Date().toLocaleDateString(), inspections:[], docs_checklist: DOC_CHECKLISTS[form.permit_type]||[] };
    save([p,...permits]); setShowAdd(false);
    setForm({project_id:"",permit_type:"Building",permit_no:"",jurisdiction:"",issued_date:"",expiry_date:"",inspector_name:"",inspector_phone:"",status:"pending",notes:"",inspections:[]});
  };
  const addInspection = (permitId, insp) => save(permits.map(p=>p.id===permitId?{...p,inspections:[...(p.inspections||[]),{...insp,id:"I"+Date.now(),status:"scheduled"}]}:p));
  const updateInspStatus = (permitId, inspId, status, result) => save(permits.map(p=>p.id===permitId?{...p,
    status:status==="passed"?(p.inspections?.filter(i=>i.id!==inspId).every(i=>i.status==="passed")?"active":"inspection_ready"):status==="failed"?"failed":p.status,
    inspections:(p.inspections||[]).map(i=>i.id===inspId?{...i,status,result,completedDate:new Date().toLocaleDateString()}:i)
  }:p));
  const updatePermitStatus = (id, status) => save(permits.map(p=>p.id===id?{...p,status}:p));

  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    return Math.ceil((new Date(dateStr)-new Date())/(1000*60*60*24));
  };

  const runAICompliance = async (permit) => {
    
    setAiChecking(true); setAiResult("");
    const proj = projects.find(p=>String(p.id)===String(permit.project_id))||{name:permit.project_name,addr:permit.project_addr,type:"renovation"};
    try {
      const resp = await fetch(AI_PROXY_URL,{
        method:"POST", headers:{"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
        body:JSON.stringify({model:"claude-haiku-4-5",max_tokens:1500,messages:[{role:"user",content:
          `You are a Bay Area construction permit expert. For this specific permit situation provide a complete guide:

Permit Type: ${permit.permit_type}
Jurisdiction: ${permit.jurisdiction||"Bay Area, CA"}
Project: ${proj.name} — ${proj.type||"renovation"} at ${proj.addr}

Provide:
## Required Inspections (in order)
List each inspection with the code reference (CBC/CPC/CEC section).

## Common Reasons Permits Fail
Top 5 specific failure points for ${permit.permit_type} permits in ${permit.jurisdiction||"Bay Area"}.

## What to Have Ready Before Each Inspection
Specific items — not generic advice.

## Typical Timeline
From application to final sign-off in ${permit.jurisdiction||"Bay Area"}.

## Fees (approximate)
Typical fee range for this type of project.

## Inspection Scheduling
How to call for inspection, cutoff times, online portal if available.

Keep it specific to Bay Area GCs — practical, not generic.`
        }]})});
      const data = await resp.json();
      setAiResult(data.content?.[0]?.text||"No response");
    } catch(e) { setAiResult("Error: "+e.message); }
    setAiChecking(false);
  };

  const runRequirementsLookup = async () => {
    
    setReqLoading(true); setReqResult("");
    try {
      const resp = await fetch(AI_PROXY_URL,{
        method:"POST", headers:{"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
        body:JSON.stringify({model:"claude-haiku-4-5",max_tokens:2000,messages:[{role:"user",content:
          `You are a Bay Area permit expert. Provide a complete permit requirements guide for:

Jurisdiction: ${reqJurisdiction}
Permit Type: ${reqPermitType}
Project Type: ${reqProjectType}

## Documents Required
Complete list of everything needed at submittal.

## Plan Check Requirements
What the plans examiner will look for. Code sections.

## Fees (estimate)
Typical fee range and how fees are calculated.

## Timeline
Current wait times for plan check and permit issuance in ${reqJurisdiction}.

## AHJ Contact & Portal
How to submit, schedule inspections, track status.

## Common Mistakes That Cause Rejections
Top issues that delay permits in ${reqJurisdiction}.

## Specific Code Requirements
Key CBC/CPC/CEC sections that apply. Title 24 requirements. CalGreen requirements.

## Tips for Faster Approval
Specific to ${reqJurisdiction} — what works, what doesn't.

Be specific, practical, and current (2025/2026 requirements).`
        }]})});
      const data = await resp.json();
      setReqResult(data.content?.[0]?.text||"No response");
    } catch(e) { setReqResult("Error: "+e.message); }
    setReqLoading(false);
  };

  // Stats
  const active = permits.filter(p=>p.status==="active"||p.status==="inspection_ready");
  const pending = permits.filter(p=>p.status==="pending"||p.status==="submitted");
  const failed = permits.filter(p=>p.status==="failed");
  const expiringSoon = permits.filter(p=>{if(!p.expiry_date)return false;const d=daysUntil(p.expiry_date);return d!==null&&d>0&&d<30;});
  const filtered = filter==="all"?permits:permits.filter(p=>p.status===filter);
  const inp = {width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:6,padding:"7px 9px",color:T.text,fontSize:12,fontFamily:"inherit"};

  // Hooks hoisted above conditional — Rules of Hooks
  const [addingInsp, setAddingInsp] = React.useState(false);
  const [newInsp, setNewInsp] = React.useState({type:"",date:"",time:"",notes:""});
  const [showChecklist, setShowChecklist] = React.useState(false);

  // ── DETAIL VIEW ──────────────────────────────────────────────────────────────
  if (selected) {
    const permit = permits.find(p=>p.id===selected);
    if (!permit) { setSelected(null); return null; }
    const checklist = permit.docs_checklist || DOC_CHECKLISTS[permit.permit_type] || [];
    const expDays = daysUntil(permit.expiry_date);
    const ahj = AHJ_DIRECTORY.find(a=>permit.jurisdiction?.includes(a.city));

    return (
      <div style={{padding:"0 0 24px"}}>
        <button onClick={()=>{setSelected(null);setAiResult("");}} style={{fontSize:12,background:"none",border:"none",color:T.blue,cursor:"pointer",marginBottom:12,display:"flex",alignItems:"center",gap:4}}>
          ← All Permits
        </button>

        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12,flexWrap:"wrap",gap:8}}>
          <div>
            <div style={{fontSize:16,fontWeight:800,color:T.text,marginBottom:2}}>{permit.permit_type} Permit</div>
            <div style={{fontSize:12,color:T.muted}}>{permit.project_name} · {permit.project_addr}</div>
            {permit.permit_no&&<div style={{fontSize:11,color:T.muted,fontFamily:"monospace",marginTop:2}}>#{permit.permit_no}</div>}
          </div>
          <span style={{fontSize:11,fontWeight:700,background:STATUS_COLORS[permit.status]+"20",color:STATUS_COLORS[permit.status],borderRadius:10,padding:"4px 14px"}}>
            {STATUS_LABELS[permit.status]||permit.status}
          </span>
        </div>

        {/* Expiry alert */}
        {expDays!==null&&expDays<30&&expDays>0&&(
          <div style={{background:T.amberBg,border:"1px solid "+T.amber+"40",borderRadius:8,padding:"9px 14px",marginBottom:12,fontSize:11,color:T.amber,fontWeight:600}}>
            ⚠ Permit expires in {expDays} day{expDays!==1?"s":""} · Renew before {permit.expiry_date} to avoid stop-work order
          </div>
        )}
        {expDays!==null&&expDays<=0&&(
          <div style={{background:T.redBg,border:"1px solid "+T.red+"40",borderRadius:8,padding:"9px 14px",marginBottom:12,fontSize:11,color:T.red,fontWeight:700}}>
            🔴 PERMIT EXPIRED — Work must stop until renewed. Contact {permit.jurisdiction||"your AHJ"} immediately.
          </div>
        )}

        {/* Status controls */}
        <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:14}}>
          {Object.entries(STATUS_LABELS).map(([s,l])=>(
            <button key={s} onClick={()=>updatePermitStatus(permit.id,s)}
              style={{fontSize:10,fontWeight:600,padding:"4px 10px",borderRadius:7,cursor:"pointer",border:"1px solid "+(permit.status===s?STATUS_COLORS[s]:T.subtle),background:permit.status===s?STATUS_COLORS[s]+"20":T.bg2,color:permit.status===s?STATUS_COLORS[s]:T.muted}}>
              {l}
            </button>
          ))}
        </div>

        {/* Info grid */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
          {[["Jurisdiction",permit.jurisdiction||"—"],["Inspector",permit.inspector_name||"—"],["Inspector Phone",permit.inspector_phone||"—"],["Issued",permit.issued_date||"Pending"],["Expires",permit.expiry_date||(expDays!==null?`${expDays}d left`:"—")],["Created",permit.created]].map(([l,v])=>(
            <div key={l} style={{background:T.bg1,borderRadius:8,padding:"8px 12px",border:"1px solid "+T.subtle}}>
              <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:2}}>{l}</div>
              <div style={{fontSize:12,fontWeight:600,color:T.text}}>{v}</div>
            </div>
          ))}
        </div>

        {/* AHJ Info panel */}
        {ahj&&(
          <div style={{background:T.tealBg,border:"1px solid "+T.teal+"30",borderRadius:10,padding:"12px 14px",marginBottom:14}}>
            <div style={{fontSize:11,fontWeight:700,color:T.teal,marginBottom:6}}>🏛 AHJ: {ahj.dept}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:6}}>
              <div style={{fontSize:10,color:T.muted}}><strong style={{color:T.text}}>Phone:</strong> {ahj.phone}</div>
              <div style={{fontSize:10,color:T.muted}}><strong style={{color:T.text}}>Cutoff:</strong> {ahj.cutoff}</div>
            </div>
            {ahj.notes&&<div style={{fontSize:10,color:T.muted,lineHeight:1.6}}>{ahj.notes}</div>}
            <button onClick={()=>window.open(ahj.schedule,"_blank")}
              style={{marginTop:8,fontSize:10,fontWeight:700,background:T.teal,color:"white",border:"none",borderRadius:6,padding:"4px 12px",cursor:"pointer"}}>
              📅 Schedule Inspection →
            </button>
          </div>
        )}

        {/* Document checklist */}
        {checklist.length>0&&(
          <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,marginBottom:14,overflow:"hidden"}}>
            <div style={{padding:"10px 14px",background:T.bg2,borderBottom:"1px solid "+T.subtle,display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}} onClick={()=>setShowChecklist(p=>!p)}>
              <div style={{fontSize:12,fontWeight:700,color:T.text}}>📋 Document Checklist — {permit.permit_type}</div>
              <span style={{fontSize:11,color:T.muted}}>{showChecklist?"▲":"▼"} {checklist.length} items</span>
            </div>
            {showChecklist&&(
              <div style={{padding:"10px 14px"}}>
                {checklist.map((item,i)=>(
                  <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",padding:"5px 0",borderBottom:i<checklist.length-1?"1px solid "+T.subtle+"30":"none"}}>
                    <span style={{fontSize:12,color:T.muted,flexShrink:0}}>☐</span>
                    <span style={{fontSize:11,color:T.muted,lineHeight:1.5}}>{item}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Inspection Schedule */}
        <div style={{background:T.bg1,borderRadius:10,border:"1px solid "+T.subtle,marginBottom:14,overflow:"hidden"}}>
          <div style={{padding:"10px 14px",borderBottom:"1px solid "+T.subtle,display:"flex",justifyContent:"space-between",alignItems:"center",background:T.bg2}}>
            <div style={{fontSize:12,fontWeight:700,color:T.text}}>Inspection Schedule
              <span style={{fontSize:9,fontWeight:600,background:T.green+"20",color:T.green,borderRadius:10,padding:"1px 7px",marginLeft:8}}>
                {(permit.inspections||[]).filter(i=>i.status==="passed").length}/{(permit.inspections||[]).length} passed
              </span>
            </div>
            <Btn small primary onClick={()=>setAddingInsp(p=>!p)}>+ Add</Btn>
          </div>
          {addingInsp&&(
            <div style={{padding:"12px 14px",borderBottom:"1px solid "+T.subtle,background:T.amberBg}}>
              <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:8,marginBottom:8}}>
                <div><div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Type *</div>
                <select value={newInsp.type} onChange={e=>setNewInsp(p=>({...p,type:e.target.value}))} style={inp}>
                  <option value="">Select...</option>{INSP_TYPES.map(t=><option key={t}>{t}</option>)}
                </select></div>
                <div><div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Date *</div>
                <input type="date" value={newInsp.date} onChange={e=>setNewInsp(p=>({...p,date:e.target.value}))} style={inp}/></div>
                <div><div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Time</div>
                <input type="time" value={newInsp.time} onChange={e=>setNewInsp(p=>({...p,time:e.target.value}))} style={inp}/></div>
              </div>
              <input value={newInsp.notes} onChange={e=>setNewInsp(p=>({...p,notes:e.target.value}))} placeholder="Ready items, access notes..." style={{...inp,marginBottom:8}}/>
              <div style={{display:"flex",gap:7}}>
                <Btn ghost small onClick={()=>setAddingInsp(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
                <Btn primary small disabled={!newInsp.type||!newInsp.date} onClick={()=>{addInspection(permit.id,newInsp);setNewInsp({type:"",date:"",time:"",notes:""});setAddingInsp(false);}} style={{flex:2,justifyContent:"center"}}>Schedule</Btn>
              </div>
            </div>
          )}
          {(permit.inspections||[]).length===0&&!addingInsp
            ?<div style={{padding:"20px",textAlign:"center",color:T.muted,fontSize:11}}>No inspections scheduled — use AI Guide to get required inspections for this permit</div>
            :(permit.inspections||[]).map(insp=>(
              <div key={insp.id} style={{padding:"10px 14px",borderBottom:"1px solid "+T.subtle+"50",display:"flex",alignItems:"center",gap:10}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:600,color:T.text}}>{insp.type}</div>
                  <div style={{fontSize:10,color:T.muted}}>{insp.date}{insp.time?" at "+insp.time:""}{insp.notes?" · "+insp.notes:""}</div>
                  {insp.result&&<div style={{fontSize:10,color:insp.status==="passed"?T.green:T.red,marginTop:2,fontWeight:600}}>{insp.result}</div>}
                </div>
                <div style={{display:"flex",gap:5,flexShrink:0}}>
                  {insp.status!=="passed"&&<button onClick={()=>updateInspStatus(permit.id,insp.id,"passed","✓ Passed")} style={{fontSize:10,fontWeight:700,background:T.green,color:"white",border:"none",borderRadius:6,padding:"3px 9px",cursor:"pointer"}}>✓ Pass</button>}
                  {insp.status!=="failed"&&<button onClick={()=>{const r=window.prompt("Reason for failure:");if(r)updateInspStatus(permit.id,insp.id,"failed",r);}} style={{fontSize:10,fontWeight:600,background:T.bg2,border:"1px solid "+T.red+"40",color:T.red,borderRadius:6,padding:"3px 9px",cursor:"pointer"}}>✗ Fail</button>}
                  <span style={{fontSize:9,fontWeight:700,background:insp.status==="passed"?T.greenBg:insp.status==="failed"?T.redBg:T.amberBg,color:insp.status==="passed"?T.green:insp.status==="failed"?T.red:T.amber,borderRadius:10,padding:"2px 7px",textTransform:"capitalize"}}>{insp.status||"scheduled"}</span>
                </div>
              </div>
          ))}
        </div>

        {/* AI Compliance Guide */}
        <div style={{background:T.purpleBg,border:"1px solid "+T.purple+"30",borderRadius:10,padding:"14px 16px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:T.purple}}>🤖 AI Permit Guide</div>
              <div style={{fontSize:10,color:T.muted,marginTop:2}}>Required inspections, common failures, fees, and timeline for {permit.permit_type} in {permit.jurisdiction||"Bay Area"}</div>
            </div>
            <Btn small primary onClick={()=>runAICompliance(permit)} disabled={aiChecking}
              style={{background:"linear-gradient(135deg,"+T.purple+","+T.blue+")",color:"white"}}>
              {aiChecking?<><Spin/>Checking...</>:<><Ic d={ICONS.robot} size={12}/>Run Guide</>}
            </Btn>
          </div>
          {aiResult&&<div style={{fontSize:11,color:T.muted,lineHeight:1.8,whiteSpace:"pre-wrap",maxHeight:400,overflowY:"auto",background:T.bg2,borderRadius:7,padding:"10px 12px",marginTop:8}}><MarkdownLite text={aiResult}/></div>}
          {!aiResult&&!aiChecking&&(
            <div style={{fontSize:11,color:T.muted,lineHeight:1.7}}>
              Click Run Guide to get: required inspections in order · common failure reasons · what to have ready · fees · scheduling info for {permit.jurisdiction||"this AHJ"}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── DASHBOARD VIEW ────────────────────────────────────────────────────────────
  return (
    <div style={{padding:"0 0 24px"}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>Permit Manager</div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>Track permits · Schedule inspections · AI requirements lookup · AHJ directory</div>
        </div>
        <Btn primary onClick={()=>setShowAdd(p=>!p)}><Ic d={ICONS.plus} size={12}/> Add Permit</Btn>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:4,marginBottom:16,background:T.bg2,borderRadius:9,padding:3,width:"fit-content"}}>
        {[["permits","🏛 My Permits"],["requirements","🔍 Requirements Lookup"],["ahj","📞 AHJ Directory"]].map(([id,label])=>(
          <button key={id} onClick={()=>setActiveTab(id)}
            style={{padding:"6px 14px",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",background:activeTab===id?T.bg1:"transparent",color:activeTab===id?T.text:T.muted,boxShadow:activeTab===id?"0 1px 3px rgba(0,0,0,0.15)":"none",transition:"all 0.15s"}}>
            {label}
          </button>
        ))}
      </div>

      {/* ── PERMITS TAB ─────────────────────────────────────────────────────── */}
      {activeTab==="permits"&&(
        <div>
          {/* Alert banners */}
          {failed.length>0&&(
            <div style={{background:T.redBg,border:"1px solid "+T.red+"30",borderRadius:10,padding:"10px 16px",marginBottom:10,display:"flex",gap:10,alignItems:"center"}}>
              <span style={{fontSize:16}}>🔴</span>
              <div style={{fontSize:12,color:T.red}}><strong>{failed.length} permit{failed.length>1?"s":""} failed inspection</strong> — work must stop until re-inspection passes</div>
            </div>
          )}
          {expiringSoon.length>0&&(
            <div style={{background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:10,padding:"10px 16px",marginBottom:10,display:"flex",gap:10,alignItems:"center"}}>
              <span style={{fontSize:16}}>⚠</span>
              <div style={{fontSize:12,color:T.amber}}><strong>{expiringSoon.length} permit{expiringSoon.length>1?"s":""} expiring within 30 days</strong> — renew now to avoid stop-work</div>
            </div>
          )}

          {/* Stats */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:16}}>
            {[["Active",active.length,T.green,"active"],["Pending",pending.length,T.amber,"pending"],["Failed",failed.length,T.red,"failed"],["Total",permits.length,T.blue,"all"]].map(([l,v,color,f])=>(
              <div key={l} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:9,padding:"10px 12px",textAlign:"center",cursor:"pointer"}} onClick={()=>setFilter(f)}>
                <div style={{fontSize:22,fontWeight:800,color,fontFamily:"monospace"}}>{v}</div>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em"}}>{l}</div>
              </div>
            ))}
          </div>

          {/* Add permit form */}
          {showAdd&&(
            <div style={{background:T.bg1,border:"1px solid "+T.blue+"30",borderRadius:10,padding:"16px",marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>New Permit</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
                <div>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Project *</div>
                  <select value={form.project_id} onChange={e=>setForm(p=>({...p,project_id:e.target.value}))} style={inp}>
                    <option value="">Select project...</option>
                    {projects.map(p=><option key={p.id} value={String(p.id)}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Permit Type *</div>
                  <select value={form.permit_type} onChange={e=>setForm(p=>({...p,permit_type:e.target.value}))} style={inp}>
                    {PERMIT_TYPES.map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Jurisdiction</div>
                  <input value={form.jurisdiction} onChange={e=>setForm(p=>({...p,jurisdiction:e.target.value}))} placeholder="San Francisco, CA" style={inp}/>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
                <div>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Permit #</div>
                  <input value={form.permit_no} onChange={e=>setForm(p=>({...p,permit_no:e.target.value}))} placeholder="202400123456" style={inp}/>
                </div>
                <div>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Issued Date</div>
                  <input type="date" value={form.issued_date} onChange={e=>setForm(p=>({...p,issued_date:e.target.value}))} style={inp}/>
                </div>
                <div>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Expiry Date</div>
                  <input type="date" value={form.expiry_date} onChange={e=>setForm(p=>({...p,expiry_date:e.target.value}))} style={inp}/>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                <div>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Inspector Name</div>
                  <input value={form.inspector_name} onChange={e=>setForm(p=>({...p,inspector_name:e.target.value}))} placeholder="John Smith" style={inp}/>
                </div>
                <div>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Inspector Phone</div>
                  <input value={form.inspector_phone} onChange={e=>setForm(p=>({...p,inspector_phone:e.target.value}))} placeholder="(628) 652-3700" style={inp}/>
                </div>
              </div>
              <input value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="Notes (scope, conditions, expedite request...)" style={{...inp,marginBottom:10}}/>
              <div style={{display:"flex",gap:7}}>
                <Btn ghost small onClick={()=>setShowAdd(false)} style={{flex:1,justifyContent:"center"}}>Cancel</Btn>
                <Btn primary small disabled={!form.project_id||!form.permit_type} onClick={addPermit} style={{flex:2,justifyContent:"center"}}>Create Permit</Btn>
              </div>
            </div>
          )}

          {/* Filter tabs */}
          <div style={{display:"flex",gap:4,marginBottom:12,flexWrap:"wrap"}}>
            {[["all","All"],["pending","Pending"],["submitted","Submitted"],["active","Active"],["inspection_ready","Ready for Inspection"],["failed","Failed"],["expired","Expired"],["closed","Closed"]].map(([f,l])=>(
              <button key={f} onClick={()=>setFilter(f)}
                style={{fontSize:10,fontWeight:600,padding:"4px 10px",borderRadius:6,border:"1px solid "+(filter===f?T.blue:T.subtle),background:filter===f?T.blueBg:T.bg2,color:filter===f?T.blue:T.muted,cursor:"pointer"}}>
                {l}
              </button>
            ))}
          </div>

          {/* Permit cards */}
          {filtered.length===0?(
            <div style={{textAlign:"center",padding:"48px 20px",color:T.muted,background:T.bg1,borderRadius:12,border:"2px dashed "+T.subtle}}>
              <div style={{fontSize:32,opacity:0.3,marginBottom:10}}>🏛</div>
              <div style={{fontSize:13,fontWeight:600,color:T.text,marginBottom:6}}>No permits yet</div>
              <div style={{fontSize:11,lineHeight:1.7}}>Add a permit to track status, schedule inspections, and get AI requirements guides.</div>
            </div>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {filtered.map(permit=>{
                const expDays = daysUntil(permit.expiry_date);
                const passedInsp = (permit.inspections||[]).filter(i=>i.status==="passed").length;
                const totalInsp = (permit.inspections||[]).length;
                return (
                  <div key={permit.id} onClick={()=>setSelected(permit.id)}
                    style={{background:T.bg1,border:"1px solid "+(permit.status==="failed"?T.red+"50":expDays!==null&&expDays<30&&expDays>0?T.amber+"50":T.subtle),borderRadius:10,padding:"12px 16px",cursor:"pointer",transition:"all 0.15s"}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=STATUS_COLORS[permit.status]}
                    onMouseLeave={e=>e.currentTarget.style.borderColor=permit.status==="failed"?T.red+"50":expDays!==null&&expDays<30&&expDays>0?T.amber+"50":T.subtle}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:T.text}}>{permit.permit_type} Permit</div>
                        <div style={{fontSize:11,color:T.muted}}>{permit.project_name} · {permit.jurisdiction||permit.project_addr}</div>
                        {permit.permit_no&&<div style={{fontSize:10,color:T.muted,fontFamily:"monospace"}}>#{permit.permit_no}</div>}
                      </div>
                      <span style={{fontSize:10,fontWeight:700,background:STATUS_COLORS[permit.status]+"20",color:STATUS_COLORS[permit.status],borderRadius:10,padding:"3px 10px",flexShrink:0}}>
                        {STATUS_LABELS[permit.status]||permit.status}
                      </span>
                    </div>
                    <div style={{display:"flex",gap:12,alignItems:"center"}}>
                      {totalInsp>0&&<div style={{fontSize:10,color:T.muted}}>Inspections: <strong style={{color:passedInsp===totalInsp?T.green:T.amber}}>{passedInsp}/{totalInsp} passed</strong></div>}
                      {permit.expiry_date&&<div style={{fontSize:10,color:expDays!==null&&expDays<30?T.amber:T.muted}}>Expires: {permit.expiry_date}{expDays!==null&&expDays<30?` (${expDays}d)`:""}</div>}
                      {permit.inspector_name&&<div style={{fontSize:10,color:T.muted}}>Inspector: {permit.inspector_name}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── REQUIREMENTS LOOKUP TAB ──────────────────────────────────────────── */}
      {activeTab==="requirements"&&(
        <div>
          <div style={{background:T.purpleBg,border:"1px solid "+T.purple+"30",borderRadius:10,padding:"14px 16px",marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:700,color:T.purple,marginBottom:4}}>🔍 AI Requirements Lookup</div>
            <div style={{fontSize:11,color:T.muted,marginBottom:12,lineHeight:1.6}}>
              Enter your jurisdiction and permit type — AI returns the exact document checklist, plan check requirements, fees, timeline, and common mistakes that delay approval.
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10}}>
              <div>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Jurisdiction *</div>
                <input value={reqJurisdiction} onChange={e=>setReqJurisdiction(e.target.value)} placeholder="San Francisco, CA" style={inp}/>
              </div>
              <div>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Permit Type *</div>
                <select value={reqPermitType} onChange={e=>setReqPermitType(e.target.value)} style={inp}>
                  {PERMIT_TYPES.map(t=><option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>Project Type</div>
                <input value={reqProjectType} onChange={e=>setReqProjectType(e.target.value)} placeholder="SRO Renovation" style={inp}/>
              </div>
            </div>
            <Btn primary onClick={runRequirementsLookup} disabled={reqLoading||!reqJurisdiction}
              style={{background:"linear-gradient(135deg,"+T.purple+","+T.blue+")",color:"white"}}>
              {reqLoading?<><Spin/>Looking up requirements...</>:<><Ic d={ICONS.robot} size={12}/>Get Requirements</>}
            </Btn>
          </div>
          {reqResult&&(
            <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"16px",fontSize:11,color:T.muted,lineHeight:1.9,whiteSpace:"pre-wrap",maxHeight:600,overflowY:"auto"}}>
              <MarkdownLite text={reqResult}/>
            </div>
          )}
          {!reqResult&&!reqLoading&&(
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
              {[["SF DBI — Building","San Francisco, CA","SRO Renovation"],["SF DBI — Electrical","San Francisco, CA","Electrical"],["San Mateo — Building","San Mateo, CA","Multi-Family Renovation"],["Oakland — ADU","Oakland, CA","ADU Addition"],["Palo Alto — Mechanical","Palo Alto, CA","HVAC Replacement"],["Berkeley — Plumbing","Berkeley, CA","Plumbing"]].map(([title,j,pt])=>(
                <button key={title} onClick={()=>{setReqJurisdiction(j);setReqPermitType(pt.split(" ")[0]==="SRO"||pt.split(" ")[0]==="Multi-Family"||pt.split(" ")[0]==="ADU"?"Building":pt.split(" ")[0]);setReqProjectType(pt);}}
                  style={{background:T.bg2,border:"1px solid "+T.subtle,borderRadius:9,padding:"10px 12px",cursor:"pointer",textAlign:"left",transition:"border-color 0.15s"}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=T.purple}
                  onMouseLeave={e=>e.currentTarget.style.borderColor=T.subtle}>
                  <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:3}}>{title}</div>
                  <div style={{fontSize:9,color:T.muted}}>{j} · {pt}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── AHJ DIRECTORY TAB ────────────────────────────────────────────────── */}
      {activeTab==="ahj"&&(
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Bay Area AHJ Directory</div>
          <div style={{fontSize:11,color:T.muted,marginBottom:14,lineHeight:1.6}}>
            Phone numbers, inspection scheduling portals, and cutoff times for Bay Area building departments. Updated 2025/2026.
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {AHJ_DIRECTORY.map(ahj=>(
              <div key={ahj.city} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 16px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:T.text}}>{ahj.city}</div>
                    <div style={{fontSize:11,color:T.muted}}>{ahj.dept}</div>
                  </div>
                  <button onClick={()=>window.open(ahj.schedule,"_blank")}
                    style={{fontSize:10,fontWeight:700,background:T.teal,color:"white",border:"none",borderRadius:7,padding:"5px 12px",cursor:"pointer",flexShrink:0}}>
                    Portal ↗
                  </button>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:6}}>
                  <div style={{background:T.bg2,borderRadius:7,padding:"7px 10px"}}>
                    <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:2}}>Phone</div>
                    <a href={"tel:"+ahj.phone.replace(/\D/g,"")} style={{fontSize:12,fontWeight:700,color:T.blue,textDecoration:"none"}}>{ahj.phone}</a>
                  </div>
                  <div style={{background:T.bg2,borderRadius:7,padding:"7px 10px"}}>
                    <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:2}}>Inspection Cutoff</div>
                    <div style={{fontSize:11,fontWeight:600,color:T.text}}>{ahj.cutoff}</div>
                  </div>
                </div>
                {ahj.notes&&<div style={{fontSize:10,color:T.muted,lineHeight:1.6,background:T.bg2,borderRadius:7,padding:"7px 10px"}}>{ahj.notes}</div>}
              </div>
            ))}
          </div>
          <div style={{marginTop:12,background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:9,padding:"10px 14px",fontSize:10,color:T.amber,lineHeight:1.6}}>
            ⚠ Always verify current hours and portal links directly with each AHJ — these change frequently. Phone numbers verified Q1 2026.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MORNING BRIEFING — ARIA summarizes yesterday's daily_reports ─────────────
// Renders at top of GC dashboard. Pulls last 7 days from daily_reports, builds
// a yesterday-only summary + flagged requests + "missing trades" heuristic
// (any (project, trade) pair seen ≥2 days in last week but not yesterday).
// AI summary is cached per-day in localStorage to avoid re-billing tokens
// every time the dashboard mounts.
function MorningBriefingCard({cid, session, setView}) {
  const T = useTheme();
  const isMob = window.innerWidth < 768;
  const [state, setState] = React.useState({loading:true, error:null, brief:null, stats:null});

  React.useEffect(() => {
    if (!cid || cid === "local") { setState({loading:false, error:null, brief:null, stats:null}); return; }

    const ymd = (d) => d.toISOString().slice(0,10);
    const today = new Date();
    const yest = new Date(today); yest.setDate(yest.getDate() - 1);
    const weekAgo = new Date(today); weekAgo.setDate(weekAgo.getDate() - 7);
    const todayYmd = ymd(today);
    const yestYmd = ymd(yest);

    const cacheKey = `grok_briefing_${cid}_${todayYmd}`;
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
      if (cached) { setState({loading:false, error:null, ...cached}); return; }
    } catch {}

    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${SUPABASE_URL}/rest/v1/daily_reports?company_id=eq.${encodeURIComponent(cid)}&submitted_at=gte.${ymd(weekAgo)}&order=submitted_at.desc&limit=500`,
          {headers: {"apikey": SUPABASE_ANON_KEY, "Authorization": "Bearer " + sb._token()}}
        );
        if (!r.ok) throw new Error("HTTP " + r.status);
        const reports = await r.json();
        if (cancelled) return;

        const projectNames = {};
        try {
          cleanProjects(JSON.parse(localStorage.getItem("grok_projects_"+cid)||"[]")).forEach(p => {
            projectNames[String(p.id)] = (p.name||"").split(" — ")[0] || ("Project "+p.id);
          });
        } catch {}

        const yestReports = reports.filter(rr => (rr.submitted_at||"").slice(0,10) === yestYmd);

        const dayCount = {};
        reports.forEach(rr => {
          const key = rr.project_id + "::" + rr.trade;
          dayCount[key] = (dayCount[key] || 0) + 1;
        });
        const yestPairs = new Set(yestReports.map(rr => rr.project_id + "::" + rr.trade));
        const missing = Object.keys(dayCount)
          .filter(k => dayCount[k] >= 2 && !yestPairs.has(k))
          .map(k => {
            const [pid, trade] = k.split("::");
            return {projectId: pid, projectName: projectNames[String(pid)] || ("Project "+String(pid).slice(0,8)), trade};
          });

        const requests = yestReports
          .filter(rr => rr.requests && rr.requests.trim())
          .map(rr => ({
            projectName: projectNames[String(rr.project_id)] || ("Project "+String(rr.project_id).slice(0,8)),
            trade: rr.trade,
            text: rr.requests.trim(),
          }));

        const stats = {
          yesterdayCount: yestReports.length,
          projectsCovered: new Set(yestReports.map(rr => rr.project_id)).size,
          requestsCount: requests.length,
          missingCount: missing.length,
        };

        let brief = null;
        if (yestReports.length === 0) {
          brief = reports.length === 0
            ? "No reports filed yet. Print QR site cards from the QR Site Cards view and post them at your jobs — workers scan to file a 30-second daily report."
            : "No daily reports filed for " + yestYmd + ". Either no crews worked yesterday, or no one scanned the QR.";
        } else {
          const summaryRows = yestReports.map(rr =>
            `- ${projectNames[String(rr.project_id)] || ("Project "+rr.project_id)} · ${rr.trade} (${rr.crew_count} crew): ${(rr.work_done||"").slice(0,200)}`
          ).join("\n");
          const requestsRows = requests.map(rq => `- [${rq.projectName} / ${rq.trade}] ${rq.text.slice(0,180)}`).join("\n");
          const missingRows = missing.map(m => `- ${m.projectName} · ${m.trade}`).join("\n");

          const userPrompt =
`Yesterday (${yestYmd}) at ${session?.companyName || "the company"}:

${yestReports.length} field report${yestReports.length===1?"":"s"} from ${stats.projectsCovered} project${stats.projectsCovered===1?"":"s"}.

Reports:
${summaryRows}

${requests.length > 0 ? `Items flagged for GC action:\n${requestsRows}\n` : ""}${missing.length > 0 ? `Trades that worked recently but didn't file yesterday:\n${missingRows}\n` : ""}`;

          try {
            const resp = await fetch(AI_PROXY_URL, {
              method: "POST",
              headers: {"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
              body: JSON.stringify({
                model: "claude-haiku-4-5",
                max_tokens: 500,
                usage_type: "chat",
                company_id: cid,
                plan: session?.plan || "starter",
                messages: [{role:"user", content:
`You are ARIA, a construction PM assistant. Write a morning briefing for the GC from yesterday's field reports.

${userPrompt}

Write a tight briefing — no preamble, no sign-off. Use exactly these 4 section headers:

YESTERDAY
(1–2 sentences on what got done across the company)

ACTION NEEDED
(bullet list of the flagged requests, or "Nothing flagged" if empty)

MISSING REPORTS
(bullet list of trades that didn't file, or "All expected trades reported in")

TODAY
(one sentence — what to follow up on first)

Be direct. No corporate filler.`}],
              }),
            });
            const data = await resp.json();
            brief = data.content?.[0]?.text || null;
          } catch {}

          if (!brief) {
            brief =
`YESTERDAY
${yestReports.length} report${yestReports.length===1?"":"s"} filed across ${stats.projectsCovered} project${stats.projectsCovered===1?"":"s"}.

ACTION NEEDED
${requests.length === 0 ? "Nothing flagged." : requests.map(rq => `• [${rq.projectName} / ${rq.trade}] ${rq.text}`).join("\n")}

MISSING REPORTS
${missing.length === 0 ? "All expected trades reported in." : missing.map(m => `• ${m.projectName} · ${m.trade}`).join("\n")}

TODAY
${requests.length > 0 ? "Clear the flagged requests first." : missing.length > 0 ? "Check in with the trades that didn't file." : "Keep the momentum — all clear from yesterday."}`;
          }
        }

        const payload = {brief, stats};
        try { localStorage.setItem(cacheKey, JSON.stringify(payload)); } catch {}
        if (!cancelled) setState({loading:false, error:null, ...payload});
      } catch (e) {
        if (!cancelled) setState({loading:false, error:String(e), brief:null, stats:null});
      }
    })();
    return () => { cancelled = true; };
  }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.loading) {
    return (
      <div style={{background:T.bg1,border:'1px solid '+T.subtle,borderRadius:11,padding:'12px 14px',marginBottom:14,display:'flex',alignItems:'center',gap:10}}>
        <span style={{fontSize:18}}>🌅</span>
        <div style={{fontSize:12,color:T.muted}}>ARIA is preparing your morning briefing…</div>
      </div>
    );
  }
  if (state.error || !state.brief) return null;

  return (
    <div style={{background:`linear-gradient(135deg,${T.gold}10,${T.gold}04)`,border:'1px solid '+T.gold+'40',borderRadius:11,overflow:'hidden',marginBottom:14}}>
      <div style={{padding:'10px 14px',borderBottom:'1px solid '+T.gold+'20',display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:24,height:24,borderRadius:6,background:`linear-gradient(135deg,${T.gold},${T.gold2})`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:900,color:'#02040A'}}>A</div>
          <div>
            <div style={{fontSize:12,fontWeight:700,color:T.text}}>🌅 Morning Briefing</div>
            <div style={{fontSize:9,color:T.muted}}>ARIA · from yesterday's field reports</div>
          </div>
        </div>
        {state.stats && state.stats.yesterdayCount > 0 && (
          <button onClick={()=>setView('daily-reports')} style={{fontSize:10,color:T.gold,background:'none',border:'none',cursor:'pointer',fontWeight:600}}>View reports →</button>
        )}
      </div>
      {state.stats && state.stats.yesterdayCount > 0 && (
        <div style={{display:'grid',gridTemplateColumns:isMob?'repeat(2,1fr)':'repeat(4,1fr)',gap:1,background:T.subtle+'40',borderBottom:'1px solid '+T.subtle}}>
          {[
            {label:'Reports',  value:state.stats.yesterdayCount,  color:T.text},
            {label:'Projects', value:state.stats.projectsCovered, color:T.text},
            {label:'Flagged',  value:state.stats.requestsCount,   color:state.stats.requestsCount>0?T.amber:T.text},
            {label:'Missing',  value:state.stats.missingCount,    color:state.stats.missingCount>0?T.red:T.text},
          ].map((s,i)=>(
            <div key={i} style={{background:T.bg1,padding:'8px 10px'}}>
              <div style={{fontSize:18,fontWeight:800,color:s.color,lineHeight:1}}>{s.value}</div>
              <div style={{fontSize:9,color:T.muted,marginTop:3,textTransform:'uppercase',letterSpacing:'.06em'}}>{s.label}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{padding:'12px 16px',fontSize:12,color:T.text,lineHeight:1.75}}>
        <MarkdownLite text={state.brief}/>
      </div>
    </div>
  );
}

// ─── PENDING PAY APP DRAFTS — ARIA-drafted G702s from milestone completion ────
// Scans every project's grok_payapps_<projectId> store and counts pay apps in
// pending_gc_approval status. Click → opens the first project that has one and
// jumps to its pay-apps tab via the grok_project_initial_tab hint that
// ProjectDetail reads on mount.
function PendingPayAppDraftsCard({cid, projects, setView, setActiveProject}) {
  const T = useTheme();
  const [count, setCount] = React.useState(0);
  const [firstProject, setFirstProject] = React.useState(null);
  const [firstMilestone, setFirstMilestone] = React.useState("");

  React.useEffect(() => {
    if (!cid || cid === "local") return;
    const load = () => {
      try {
        const projs = cleanProjects((projects && projects.length)
          ? projects
          : JSON.parse(localStorage.getItem("grok_projects_"+cid) || "[]"));
        let total = 0;
        let firstP = null, firstMs = "";
        for (const p of projs) {
          let apps = [];
          try { apps = JSON.parse(localStorage.getItem("grok_payapps_"+p.id) || "[]"); } catch {}
          const drafts = apps.filter(a => a.status === "pending_gc_approval");
          if (drafts.length) {
            total += drafts.length;
            if (!firstP) {
              firstP = p;
              firstMs = drafts[0]?.milestoneName || "";
            }
          }
        }
        setCount(total);
        setFirstProject(firstP);
        setFirstMilestone(firstMs);
      } catch { setCount(0); }
    };
    load();
    window.addEventListener("grok-payapps-updated", load);
    const id = setInterval(load, 6000);
    return () => { window.removeEventListener("grok-payapps-updated", load); clearInterval(id); };
  }, [cid, projects]);

  if (!count || !firstProject) return null;

  const firstProjectName = (firstProject.name || "").split(" — ")[0] || firstProject.name || "Project";

  const open = () => {
    try { localStorage.setItem("grok_project_initial_tab", "payapp"); } catch {}
    if (setActiveProject && setView) {
      setActiveProject(firstProject);
      setView("project-detail");
    } else if (setView) {
      setView("projects");
    }
  };

  return (
    <div onClick={open}
      style={{background:`linear-gradient(135deg,${T.green}14,${T.green}06)`,border:'1px solid '+T.green+'50',borderRadius:11,padding:'12px 14px',marginBottom:14,cursor:'pointer',display:'flex',alignItems:'center',gap:12,transition:'all 0.2s'}}
      onMouseEnter={e=>{e.currentTarget.style.borderColor=T.green;}}
      onMouseLeave={e=>{e.currentTarget.style.borderColor=T.green+'50';}}>
      <div style={{width:32,height:32,borderRadius:8,background:T.green,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,color:'#02040A',fontWeight:900,flexShrink:0}}>💰</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:12,fontWeight:800,color:T.text}}>
          {count} pay app draft{count===1?"":"s"} awaiting your approval
        </div>
        <div style={{fontSize:10,color:T.muted,marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
          ARIA pre-filled {count===1?"a G702":"G702s"} from completed milestone{count===1?"":"s"} · {firstProjectName || ""}{firstMilestone?` — ${firstMilestone}`:""}
        </div>
      </div>
      <div style={{fontSize:11,color:T.green,fontWeight:700,flexShrink:0}}>Review →</div>
    </div>
  );
}

// ─── PENDING RFI DRAFTS — ARIA-drafted RFIs from blueprint flags ──────────────
// Counts RFIs in pending_gc_approval status across the company's RFI store.
// Click → RFIs view, pre-filtered to drafts via the grok_rfi_init_filter hint.
// Renders nothing when there are no drafts so the dashboard stays clean.
function PendingRFIDraftsCard({cid, setView}) {
  const T = useTheme();
  const [count, setCount] = React.useState(0);
  const [latest, setLatest] = React.useState(null);

  React.useEffect(() => {
    if (!cid || cid === "local") return;
    const load = () => {
      try {
        const rfis = JSON.parse(localStorage.getItem("grok_rfis_global_"+cid) || "[]");
        const drafts = rfis.filter(r => r.status === "pending_gc_approval");
        setCount(drafts.length);
        setLatest(drafts[0] || null);
      } catch { setCount(0); setLatest(null); }
    };
    load();
    window.addEventListener("grok-rfis-updated", load);
    const id = setInterval(load, 6000);
    return () => { window.removeEventListener("grok-rfis-updated", load); clearInterval(id); };
  }, [cid]);

  if (!count) return null;

  const open = () => {
    try { localStorage.setItem("grok_rfi_init_filter", "pending_gc_approval"); } catch {}
    setView && setView("rfis");
  };

  return (
    <div onClick={open}
      style={{background:`linear-gradient(135deg,${T.gold}14,${T.gold}06)`,border:'1px solid '+T.gold+'40',borderRadius:11,padding:'12px 14px',marginBottom:14,cursor:'pointer',display:'flex',alignItems:'center',gap:12,transition:'all 0.2s'}}
      onMouseEnter={e=>{e.currentTarget.style.borderColor=T.gold;}}
      onMouseLeave={e=>{e.currentTarget.style.borderColor=T.gold+'40';}}>
      <div style={{width:32,height:32,borderRadius:8,background:T.gold,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,color:'#02040A',fontWeight:900,flexShrink:0}}>🔔</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:12,fontWeight:800,color:T.text}}>
          {count} RFI draft{count===1?"":"s"} awaiting your approval
        </div>
        <div style={{fontSize:10,color:T.muted,marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
          ARIA drafted {count===1?"this RFI":"these RFIs"} from blueprint flags{latest?.subject?` · "${latest.subject.slice(0,60)}"`:""}
        </div>
      </div>
      <div style={{fontSize:11,color:T.gold,fontWeight:700,flexShrink:0}}>Review →</div>
    </div>
  );
}

// ─── PENDING NOTIFICATIONS — ARIA-drafted mobilization emails awaiting GC ─────
// One row per drafted email. Click to open a review modal with subject + body
// editable, then Approve & Send (calls send-email edge function) or Dismiss.
function PendingNotificationsCard({cid, session}) {
  const T = useTheme();
  const [notifs, setNotifs] = React.useState(() => PendingNotifs.load(cid));
  const [openId, setOpenId] = React.useState(null);
  const [editSubject, setEditSubject] = React.useState("");
  const [editBody, setEditBody] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [sendMsg, setSendMsg] = React.useState("");

  React.useEffect(() => {
    const reload = () => setNotifs(PendingNotifs.load(cid));
    window.addEventListener("grok-pending-notifs-changed", reload);
    // Light polling so a draft completing in another tab updates here too
    const id = setInterval(reload, 4000);
    return () => { window.removeEventListener("grok-pending-notifs-changed", reload); clearInterval(id); };
  }, [cid]);

  const pending = notifs.filter(n => n.status === "pending" || n.status === "drafting" || n.status === "needs_contact");
  if (pending.length === 0) return null;

  const openModal = (n) => {
    setOpenId(n.id);
    setEditSubject(n.draftSubject || "");
    setEditBody(n.draftBody || "");
    setSendMsg("");
  };
  const closeModal = () => { setOpenId(null); setSendMsg(""); };

  const dismiss = (id) => {
    PendingNotifs.update(cid, id, {status: "dismissed"});
    setNotifs(PendingNotifs.load(cid));
    if (openId === id) closeModal();
  };

  const approveAndSend = async (n) => {
    if (!n.subEmail) { setSendMsg("No recipient on file — can't send."); return; }
    setSending(true); setSendMsg("");
    try {
      const r = await fetch("https://qnjlirkgumhzzcavqsxt.supabase.co/functions/v1/send-email", {
        method: "POST",
        headers: {"Content-Type":"application/json","apikey":SUPABASE_ANON_KEY},
        body: JSON.stringify({
          to: n.subEmail,
          subject: editSubject,
          html: `<div style="font-family:Arial,sans-serif;max-width:600px;padding:24px;color:#1a1a1a;font-size:14px;line-height:1.7;white-space:pre-wrap">${editBody.replace(/</g,"&lt;")}</div>`,
        }),
      });
      if (!r.ok) throw new Error("HTTP "+r.status);
      PendingNotifs.update(cid, n.id, {
        status: "sent",
        draftSubject: editSubject,
        draftBody: editBody,
        sentAt: new Date().toISOString(),
      });
      setNotifs(PendingNotifs.load(cid));
      setSendMsg("✓ Sent to " + n.subEmail);
      setTimeout(() => closeModal(), 1200);
    } catch (e) {
      setSendMsg("✗ Send failed — " + (e.message || "network error"));
    }
    setSending(false);
  };

  const open = notifs.find(n => n.id === openId);

  return (
    <div style={{background:T.bg1,border:'1px solid '+T.gold+'40',borderRadius:11,overflow:'hidden',marginBottom:14}}>
      <div style={{padding:'10px 14px',borderBottom:'1px solid '+T.subtle,display:'flex',alignItems:'center',gap:8}}>
        <span style={{fontSize:14}}>📧</span>
        <div style={{fontSize:12,fontWeight:700,color:T.text}}>Pending Notifications</div>
        <span style={{background:T.gold,color:T.bg0,fontSize:9,fontWeight:800,padding:'2px 7px',borderRadius:10}}>{pending.length}</span>
        <div style={{flex:1}}/>
        <span style={{fontSize:9,color:T.muted}}>ARIA-drafted · awaiting your approval</span>
      </div>
      {pending.map((n, i) => (
        <div key={n.id}
          style={{padding:'10px 14px',borderBottom:i<pending.length-1?'1px solid '+T.subtle:'none',display:'flex',alignItems:'center',gap:10,cursor: n.status==='drafting' ? 'wait' : 'pointer',transition:'background 0.15s'}}
          onClick={() => n.status === "drafting" ? null : openModal(n)}
          onMouseEnter={e => e.currentTarget.style.background = T.bg2}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:12,fontWeight:700,color:T.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
              {n.projectName} · {n.trade}
              {n.status === "drafting" && <span style={{marginLeft:8,fontSize:10,color:T.muted}}>· drafting…</span>}
              {n.status === "needs_contact" && <span style={{marginLeft:8,fontSize:10,color:T.amber,fontWeight:600}}>· no sub on file</span>}
            </div>
            <div style={{fontSize:10,color:T.muted,marginTop:2}}>
              {n.phaseName} hit 80% → mobilize for {n.nextPhaseName}
              {n.subEmail && <span> · → {n.subEmail}</span>}
            </div>
          </div>
          {n.status !== "drafting" && <span style={{fontSize:12,color:T.gold,fontWeight:700}}>›</span>}
        </div>
      ))}

      {open && (
        <div onClick={closeModal}
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}}>
          <div onClick={e => e.stopPropagation()}
            style={{background:T.bg1,border:'1px solid '+T.subtle,borderRadius:14,width:'100%',maxWidth:560,maxHeight:'90vh',overflowY:'auto',padding:24}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12,gap:10}}>
              <div>
                <div style={{fontSize:9,fontWeight:700,color:T.gold,textTransform:'uppercase',letterSpacing:'.1em',marginBottom:3}}>Mobilization email</div>
                <div style={{fontSize:15,fontWeight:800,color:T.text}}>{open.projectName} · {open.trade}</div>
                <div style={{fontSize:11,color:T.muted,marginTop:2}}>{open.phaseName} hit 80% → mobilize for {open.nextPhaseName}</div>
              </div>
              <button onClick={closeModal} style={{background:'none',border:'none',color:T.muted,fontSize:18,cursor:'pointer'}}>×</button>
            </div>

            {open.status === "needs_contact" ? (
              <div style={{background:T.amberBg,border:'1px solid '+T.amber+'40',borderRadius:9,padding:'12px 14px',fontSize:12,color:T.text,lineHeight:1.7,marginBottom:14}}>
                No <strong>{open.trade}</strong> sub on file for this company. Add a sub from the Subs panel with their trade set to "{open.trade}", and ARIA will draft the email automatically on the next phase update.
              </div>
            ) : (
              <>
                <div style={{fontSize:11,color:T.muted,marginBottom:14,background:T.bg2,borderRadius:7,padding:'8px 12px'}}>
                  To: <strong style={{color:T.text}}>{open.subName} &lt;{open.subEmail}&gt;</strong> {open.subCompany && <>· {open.subCompany}</>}
                </div>

                <div style={{marginBottom:12}}>
                  <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:5}}>Subject</div>
                  <input value={editSubject} onChange={e => setEditSubject(e.target.value)}
                    style={{width:'100%',background:T.bg2,border:'1px solid '+T.subtle,borderRadius:7,padding:'9px 12px',color:T.text,fontSize:13,boxSizing:'border-box',fontFamily:'inherit'}}/>
                </div>

                <div style={{marginBottom:14}}>
                  <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:5}}>Body</div>
                  <textarea value={editBody} onChange={e => setEditBody(e.target.value)}
                    rows={12}
                    style={{width:'100%',background:T.bg2,border:'1px solid '+T.subtle,borderRadius:7,padding:'10px 12px',color:T.text,fontSize:13,boxSizing:'border-box',resize:'vertical',fontFamily:'inherit',lineHeight:1.6}}/>
                </div>
              </>
            )}

            {sendMsg && (
              <div style={{fontSize:11,color:sendMsg.startsWith("✓")?T.green:T.red,marginBottom:10,fontWeight:600}}>{sendMsg}</div>
            )}

            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={() => dismiss(open.id)}
                style={{background:'none',border:'1px solid '+T.subtle,color:T.muted,fontSize:12,fontWeight:600,padding:'9px 16px',borderRadius:8,cursor:'pointer'}}>
                Dismiss
              </button>
              {open.status !== "needs_contact" && (
                <button onClick={() => approveAndSend(open)} disabled={sending || !open.subEmail}
                  style={{background:sending||!open.subEmail?T.bg3:T.gold,border:'none',color:sending||!open.subEmail?T.muted:T.bg0,fontSize:12,fontWeight:800,padding:'9px 18px',borderRadius:8,cursor:sending||!open.subEmail?'not-allowed':'pointer'}}>
                  {sending ? "Sending…" : "✓ Approve & Send"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Dashboard({setView, setActiveProject, projects, session}) {
  const T = useTheme();
  const cid = session?.companyId || 'local';
  const isMob = window.innerWidth < 768;

  const liveProjects = (()=>{try{const s=localStorage.getItem('grok_projects_'+cid);if(s){const p=cleanProjects(JSON.parse(s).filter(x=>!x.isSample));if(p.length)return p;}return [];}catch{return [];}})();
  const liveCOs  = (()=>{try{return JSON.parse(localStorage.getItem('grok_cos_global_'+cid)||'[]');}catch{return [];}})();
  const liveRFIs = (()=>{try{return JSON.parse(localStorage.getItem('grok_rfis_global_'+cid)||'[]');}catch{return [];}})();
  const liveLeads= (()=>{try{return JSON.parse(localStorage.getItem('grok_leads_'+cid)||'[]');}catch{return [];}})();
  const dailyLogs= (()=>{try{return JSON.parse(localStorage.getItem('grok_daily_logs_'+cid)||'[]');}catch{return [];}})();

  const activeProjects = liveProjects.filter(p=>p.status==='active');
  const pendingCOs = liveCOs.filter(c=>c.status==='pending');
  const openRFIs = liveRFIs.filter(r=>r.status!=='closed');
  const activeLeads = liveLeads.filter(l=>l.status!=='won'&&l.status!=='lost');
  const todayLogs = dailyLogs.filter(l=>l.date===new Date().toLocaleDateString());

  // Overdue detection
  const now = Date.now();
  const overdueCOs = pendingCOs.filter(co=>(now - new Date(co.createdAt||now)) > 3*86400000);
  const overdueRFIs = openRFIs.filter(r=>(now - new Date(r.createdAt||now)) > 5*86400000);

  // Weather rain-risk per project — keyed by project id, only includes risk >= 50%
  // over the next 3 days. Cached in localStorage so this stays cheap on each load.
  const [weatherRisks, setWeatherRisks] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out = {};
      for (const p of activeProjects) {
        let lat = p.lat, lon = p.lon;
        if (typeof lat !== "number" || typeof lon !== "number") {
          if (!p.addr) continue;
          const geo = await Weather.geocode(p.addr);
          if (cancelled) return;
          if (!geo) continue;
          lat = geo.lat; lon = geo.lon;
        }
        const fc = await Weather.forecast(lat, lon);
        if (cancelled) return;
        const risk = Weather.rainRisk(fc);
        if (risk && risk.risk >= 50) out[p.id] = risk;
      }
      if (!cancelled) setWeatherRisks(out);
    })();
    return () => { cancelled = true; };
  }, [activeProjects.length]);

  // Urgent items
  const urgentItems = [
    ...overdueCOs.map(co=>({type:'co', color:T.amber, icon:'📝', text:`CO pending ${Math.floor((now-new Date(co.createdAt||now))/86400000)}d: ${(co.desc||co.title||'Change Order').slice(0,40)}`, action:()=>setView('cos')})),
    ...overdueRFIs.map(r=>({type:'rfi', color:T.red, icon:'❓', text:`RFI open ${Math.floor((now-new Date(r.createdAt||now))/86400000)}d: ${(r.subject||'RFI').slice(0,40)}`, action:()=>setView('rfis')})),
    ...activeProjects.filter(p=>weatherRisks[p.id]).map(p=>{
      const r = weatherRisks[p.id];
      const when = r.topDay && r.topDay.date ? (r.topDay.date === r.rainDays[0].date ? 'today' : r.topDay.date) : 'soon';
      return { type:'weather', color:T.amber, icon:'🌧', text:`${(p.name||'Project').slice(0,30)} — ${r.risk}% rain ${when}`, action:()=>{setActiveProject(p);setView('project-detail');} };
    }),
    ...activeProjects.filter(p=>(p.progress||0)<10&&p.status==='active').map(p=>({type:'proj', color:T.amber, icon:'⚠️', text:`${(p.name||'Project').slice(0,30)} only at ${p.progress||0}% — check schedule`, action:()=>{setActiveProject(p);setView('project-detail');}})),
  ].slice(0,5);

  const today = new Date().toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'});

  return (
    <div style={{padding:isMob?'12px':'20px 24px',paddingBottom:isMob?'100px':'24px',overflowY:'auto'}}>

      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:18}}>
        <div>
          <div style={{fontSize:isMob?16:18,fontWeight:800,color:T.text}}>Dashboard</div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>{today}</div>
        </div>
        <div onClick={()=>setView('aria')}
          style={{display:'flex',alignItems:'center',gap:7,background:'linear-gradient(135deg,'+T.gold+'18,'+T.gold+'08)',border:'1px solid '+T.gold+'30',borderRadius:9,padding:'7px 13px',cursor:'pointer',transition:'all 0.2s'}}
          onMouseEnter={e=>e.currentTarget.style.borderColor=T.gold}
          onMouseLeave={e=>e.currentTarget.style.borderColor=T.gold+'30'}>
          <span style={{fontSize:16}}>🤖</span>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:T.text}}>ARIA PM Agent</div>
            <div style={{fontSize:9,color:T.muted}}>Ask anything →</div>
          </div>
        </div>
      </div>

      {/* Morning briefing — ARIA summary of yesterday's daily_reports */}
      <MorningBriefingCard cid={cid} session={session} setView={setView}/>

      {/* RFI drafts ARIA built from blueprint flags, awaiting GC approval */}
      <PendingRFIDraftsCard cid={cid} setView={setView}/>

      {/* Pay app drafts ARIA pre-filled from milestones hitting 100% */}
      <PendingPayAppDraftsCard cid={cid} projects={liveProjects} setView={setView} setActiveProject={setActiveProject}/>

      {/* Pending trade-mobilization emails awaiting GC approval */}
      <PendingNotificationsCard cid={cid} session={session}/>

      {/* Stat cards */}
      <div style={{display:'grid',gridTemplateColumns:isMob?'1fr 1fr':'repeat(4,1fr)',gap:8,marginBottom:16}}>
        {[
          {label:'Active Projects', value:activeProjects.length, icon:'🏗', color:T.blue, action:()=>setView('projects'), sub: activeProjects.length ? activeProjects.slice(0,1).map(p=>p.name).join(', ') : 'No active projects'},
          {label:'Pending COs', value:pendingCOs.length, icon:'📝', color:pendingCOs.length>0?T.amber:T.green, action:()=>setView('cos'), sub: pendingCOs.length>0 ? overdueCOs.length+' overdue' : 'All clear'},
          {label:'Open RFIs', value:openRFIs.length, icon:'❓', color:openRFIs.length>0?T.amber:T.green, action:()=>setView('rfis'), sub: openRFIs.length>0 ? overdueRFIs.length+' overdue' : 'All clear'},
          {label:'Active Leads', value:activeLeads.length, icon:'🔥', color:T.purple, action:()=>setView('leads'), sub: activeLeads.length ? '$'+(activeLeads.reduce((s,l)=>s+(l.value||0),0)).toLocaleString()+' pipeline' : 'No active leads'},
        ].map((s,i)=>(
          <div key={i} onClick={s.action}
            style={{background:T.bg1,border:'1px solid '+T.subtle,borderRadius:11,padding:'14px',cursor:'pointer',transition:'all 0.2s'}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=s.color;e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,.1)';}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor=T.subtle;e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='none';}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6}}>
              <span style={{fontSize:18}}>{s.icon}</span>
              <div style={{fontSize:isMob?22:28,fontWeight:800,color:s.color,lineHeight:1}}>{s.value}</div>
            </div>
            <div style={{fontSize:10,fontWeight:700,color:T.text,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:3}}>{s.label}</div>
            <div style={{fontSize:9,color:T.muted,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Urgent / flagged items */}
      {urgentItems.length>0&&(
        <div style={{background:T.bg1,border:'1px solid '+T.amber+'40',borderRadius:11,overflow:'hidden',marginBottom:14}}>
          <div style={{padding:'10px 14px',borderBottom:'1px solid '+T.subtle,display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontSize:14}}>⚠️</span>
            <div style={{fontSize:12,fontWeight:700,color:T.amber}}>Needs Attention ({urgentItems.length})</div>
          </div>
          {urgentItems.map((item,i)=>(
            <div key={i} onClick={item.action}
              style={{padding:'10px 14px',borderBottom:i<urgentItems.length-1?'1px solid '+T.subtle:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:10,transition:'background 0.15s'}}
              onMouseEnter={e=>e.currentTarget.style.background=T.bg2}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <span style={{fontSize:14,flexShrink:0}}>{item.icon}</span>
              <div style={{fontSize:11,color:T.text,flex:1}}>{item.text}</div>
              <div style={{fontSize:11,color:item.color,fontWeight:700,flexShrink:0}}>›</div>
            </div>
          ))}
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:isMob?'1fr':'1fr 1fr',gap:12,marginBottom:14}}>

        {/* Active projects */}
        <div style={{background:T.bg1,border:'1px solid '+T.subtle,borderRadius:11,overflow:'hidden'}}>
          <div style={{padding:'10px 14px',borderBottom:'1px solid '+T.subtle,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div style={{fontSize:12,fontWeight:700,color:T.text}}>🏗 Active Projects</div>
            <button onClick={()=>setView('projects')} style={{fontSize:10,color:T.gold,background:'none',border:'none',cursor:'pointer',fontWeight:600}}>View all →</button>
          </div>
          {activeProjects.length===0?(
            <div style={{padding:'20px 14px',textAlign:'center',color:T.muted,fontSize:11}}>
              No active projects<br/>
              <span onClick={()=>setView('projects')} style={{color:T.gold,cursor:'pointer',fontWeight:600}}>+ Create your first project</span>
            </div>
          ):activeProjects.slice(0,4).map((p,i)=>(
            <div key={i} onClick={()=>{setActiveProject(p);setView('project-detail');}}
              style={{padding:'10px 14px',borderBottom:i<Math.min(activeProjects.length,4)-1?'1px solid '+T.subtle:'none',cursor:'pointer',transition:'background 0.15s'}}
              onMouseEnter={e=>e.currentTarget.style.background=T.bg2}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:5}}>
                <div style={{fontSize:12,fontWeight:600,color:T.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1,paddingRight:8}}>{p.name||'Untitled'}</div>
                <div style={{fontSize:12,fontWeight:700,color:T.blue,flexShrink:0}}>{p.progress||p.pct||0}%</div>
              </div>
              <div style={{height:3,background:T.bg2,borderRadius:2,overflow:'hidden'}}>
                <div style={{width:(p.progress||p.pct||0)+'%',height:'100%',background:T.blue,borderRadius:2,transition:'width 0.5s'}}/>
              </div>
              <div style={{fontSize:9,color:T.muted,marginTop:4}}>{p.phase||'Active'} · {p.addr||p.address||'No address'}</div>
            </div>
          ))}
        </div>

        {/* Right column — COs + RFIs */}
        <div style={{display:'flex',flexDirection:'column',gap:10}}>

          {/* Pending COs */}
          <div style={{background:T.bg1,border:'1px solid '+T.subtle,borderRadius:11,overflow:'hidden'}}>
            <div style={{padding:'10px 14px',borderBottom:'1px solid '+T.subtle,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontSize:12,fontWeight:700,color:T.text}}>📝 Pending COs {pendingCOs.length>0&&<span style={{color:T.amber,fontSize:11}}>({pendingCOs.length})</span>}</div>
              <button onClick={()=>setView('cos')} style={{fontSize:10,color:T.gold,background:'none',border:'none',cursor:'pointer',fontWeight:600}}>View all</button>
            </div>
            {pendingCOs.length===0?(
              <div style={{padding:'12px 14px',fontSize:11,color:T.muted,textAlign:'center'}}>✓ No pending change orders</div>
            ):pendingCOs.slice(0,3).map((co,i)=>(
              <div key={i} onClick={()=>setView('cos')}
                style={{padding:'9px 14px',borderBottom:i<Math.min(pendingCOs.length,3)-1?'1px solid '+T.subtle:'none',cursor:'pointer',transition:'background 0.15s'}}
                onMouseEnter={e=>e.currentTarget.style.background=T.bg2}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div style={{fontSize:11,fontWeight:600,color:T.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1,paddingRight:8}}>{co.desc||co.title||'Change Order'}</div>
                  <div style={{fontSize:11,color:T.amber,fontWeight:700,flexShrink:0}}>${(co.amount||0).toLocaleString()}</div>
                </div>
                <div style={{fontSize:9,color:T.muted,marginTop:2}}>{co.project||''} · {co.status||'pending'}</div>
              </div>
            ))}
          </div>

          {/* Open RFIs */}
          <div style={{background:T.bg1,border:'1px solid '+T.subtle,borderRadius:11,overflow:'hidden'}}>
            <div style={{padding:'10px 14px',borderBottom:'1px solid '+T.subtle,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontSize:12,fontWeight:700,color:T.text}}>❓ Open RFIs {openRFIs.length>0&&<span style={{color:T.amber,fontSize:11}}>({openRFIs.length})</span>}</div>
              <button onClick={()=>setView('rfis')} style={{fontSize:10,color:T.gold,background:'none',border:'none',cursor:'pointer',fontWeight:600}}>View all</button>
            </div>
            {openRFIs.length===0?(
              <div style={{padding:'12px 14px',fontSize:11,color:T.muted,textAlign:'center'}}>✓ No open RFIs</div>
            ):openRFIs.slice(0,3).map((rfi,i)=>(
              <div key={i} onClick={()=>setView('rfis')}
                style={{padding:'9px 14px',borderBottom:i<Math.min(openRFIs.length,3)-1?'1px solid '+T.subtle:'none',cursor:'pointer',transition:'background 0.15s'}}
                onMouseEnter={e=>e.currentTarget.style.background=T.bg2}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <div style={{fontSize:11,fontWeight:600,color:T.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{rfi.subject||'RFI #'+rfi.num}</div>
                <div style={{fontSize:9,color:T.muted,marginTop:2}}>{rfi.project||''} · {rfi.status||'open'}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Today's Activity */}
      {(todayLogs.length>0||activeLeads.length>0)&&(
        <div style={{display:'grid',gridTemplateColumns:isMob?'1fr':'1fr 1fr',gap:12}}>

          {todayLogs.length>0&&(
            <div style={{background:T.bg1,border:'1px solid '+T.subtle,borderRadius:11,overflow:'hidden'}}>
              <div style={{padding:'10px 14px',borderBottom:'1px solid '+T.subtle}}>
                <div style={{fontSize:12,fontWeight:700,color:T.text}}>📋 Today's Daily Logs</div>
              </div>
              {todayLogs.slice(0,3).map((log,i)=>(
                <div key={i} style={{padding:'9px 14px',borderBottom:i<Math.min(todayLogs.length,3)-1?'1px solid '+T.subtle:'none'}}>
                  <div style={{fontSize:11,fontWeight:600,color:T.text}}>{log.project||'Project'}</div>
                  <div style={{fontSize:9,color:T.muted,marginTop:2}}>{log.crew||0} workers · {log.notes?.slice(0,50)||'Daily log entry'}</div>
                </div>
              ))}
            </div>
          )}

          {activeLeads.length>0&&(
            <div style={{background:T.bg1,border:'1px solid '+T.subtle,borderRadius:11,overflow:'hidden'}}>
              <div style={{padding:'10px 14px',borderBottom:'1px solid '+T.subtle,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div style={{fontSize:12,fontWeight:700,color:T.text}}>🔥 Lead Pipeline</div>
                <button onClick={()=>setView('leads')} style={{fontSize:10,color:T.gold,background:'none',border:'none',cursor:'pointer',fontWeight:600}}>View all</button>
              </div>
              {activeLeads.slice(0,3).map((lead,i)=>(
                <div key={i} onClick={()=>setView('leads')}
                  style={{padding:'9px 14px',borderBottom:i<Math.min(activeLeads.length,3)-1?'1px solid '+T.subtle:'none',cursor:'pointer',transition:'background 0.15s'}}
                  onMouseEnter={e=>e.currentTarget.style.background=T.bg2}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div style={{fontSize:11,fontWeight:600,color:T.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1,paddingRight:8}}>{lead.name||'Lead'}</div>
                    {lead.value&&<div style={{fontSize:11,color:T.green,fontWeight:700,flexShrink:0}}>${(lead.value||0).toLocaleString()}</div>}
                  </div>
                  <div style={{fontSize:9,color:T.muted,marginTop:2}}>{lead.status||'new'} · {lead.type||'Project'}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {activeProjects.length===0&&pendingCOs.length===0&&openRFIs.length===0&&activeLeads.length===0&&(
        <div style={{textAlign:'center',padding:'48px 20px',color:T.muted}}>
          <div style={{fontSize:48,marginBottom:12}}>🏗</div>
          <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:8}}>Welcome to Grok Field Ops</div>
          <div style={{fontSize:13,lineHeight:1.7,maxWidth:320,margin:'0 auto 20px'}}>Create your first project to get started. ARIA will help you manage everything from estimates to closeout.</div>
          <div style={{display:'flex',gap:10,justifyContent:'center',flexWrap:'wrap'}}>
            <button onClick={()=>setView('projects')}
              style={{background:'linear-gradient(135deg,'+T.gold+','+T.gold2+')',color:'#02040A',border:'none',borderRadius:9,padding:'11px 22px',fontSize:12,fontWeight:700,cursor:'pointer'}}>
              + Create First Project
            </button>
            <button onClick={()=>setView('aria')}
              style={{background:T.bg1,color:T.text,border:'1px solid '+T.subtle,borderRadius:9,padding:'11px 22px',fontSize:12,fontWeight:600,cursor:'pointer'}}>
              🤖 Ask ARIA
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// Property Manager / Portfolio Dashboard — for nonprofit operators, SRO owners,
// and property managers who oversee multiple GCs at once. Aggregates every
// project the device knows about (scans grok_projects_<cid>) and rolls up open
// COs, open RFIs, recent field reports, and milestone progress per GC and
// across the portfolio. Drill-down opens a read-only summary modal — does NOT
// navigate into the GC-scoped ProjectDetail, which would break tenant isolation.
function PropertyManagerDashboardView({session, setView}) {
  const T = useTheme();
  const isMob = window.innerWidth < 768;
  const [filter, setFilter] = useState("active"); // all | active | at_risk
  const [groupBy, setGroupBy] = useState("gc");   // gc | status | none
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Refresh when localStorage changes from another tab or when a GC view in this
  // tab broadcasts a save.
  React.useEffect(() => {
    const bump = () => setRefreshTick(t => t + 1);
    window.addEventListener("storage", bump);
    window.addEventListener("focus", bump);
    let ch = null;
    try {
      ch = new BroadcastChannel("grok_sync_" + (session?.companyId || ""));
      ch.onmessage = bump;
    } catch {}
    return () => {
      window.removeEventListener("storage", bump);
      window.removeEventListener("focus", bump);
      try { ch?.close(); } catch {}
    };
  }, [session?.companyId]);

  // ── Aggregate every (project, GC) pair on this device ─────────────────────
  const data = React.useMemo(() => {
    let companies = [];
    try { companies = JSON.parse(localStorage.getItem("grok_companies") || "[]"); } catch {}
    const gcName = (cid) => {
      const c = companies.find(x => String(x.id) === String(cid));
      return c?.companyName || c?.name || c?.email || cid;
    };

    const projects = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      const m = key.match(/^grok_projects_(.+)$/);
      if (!m) continue;
      const cid = m[1];
      if (cid === "local") continue;
      let projs = [];
      try { projs = JSON.parse(localStorage.getItem(key) || "[]"); } catch {}
      if (!Array.isArray(projs) || !projs.length) continue;
      const cos  = (() => { try { return JSON.parse(localStorage.getItem("grok_cos_global_"  + cid) || "[]"); } catch { return []; } })();
      const rfis = (() => { try { return JSON.parse(localStorage.getItem("grok_rfis_global_" + cid) || "[]"); } catch { return []; } })();
      projs.forEach(p => {
        // Apply the global junk filter — test rows / stubs never surface here.
        if (isJunkProject(p)) return;
        const projCOs  = cos.filter(c => String(c.projectId || "") === String(p.id) || c.project === p.name || c.projectName === p.name);
        const projRFIs = rfis.filter(r => String(r.projectId || "") === String(p.id) || r.project === p.name || r.projectName === p.name);
        let logs = [];
        try { logs = JSON.parse(localStorage.getItem("grok_daily_log_" + p.id) || "[]"); } catch {}
        let photoLogs = [];
        try { photoLogs = JSON.parse(localStorage.getItem("grok_photolog_" + p.id) || "[]"); } catch {}
        const allLogs = [...logs, ...photoLogs].sort((a,b) => String(b.date||"").localeCompare(String(a.date||"")));
        const lastLog = allLogs[0] || null;
        const daysSinceLog = lastLog?.date ? Math.floor((Date.now() - new Date(lastLog.date + "T12:00:00").getTime()) / 86400000) : null;
        const recentLogsCount = allLogs.filter(l => {
          if (!l.date) return false;
          const d = new Date(l.date + "T12:00:00");
          return (Date.now() - d.getTime()) < 7 * 86400000;
        }).length;
        const openCOs  = projCOs.filter(c => c.status === "pending" || c.status === "pending_gc_approval").length;
        const openRFIs = projRFIs.filter(r => r.status !== "closed").length;

        const atRiskReasons = [];
        if (openCOs >= 3) atRiskReasons.push(openCOs + " open COs");
        if (openRFIs >= 5) atRiskReasons.push(openRFIs + " open RFIs");
        if (daysSinceLog !== null && daysSinceLog > 7) atRiskReasons.push("no log in " + daysSinceLog + " d");
        if (daysSinceLog === null && p.status === "active") atRiskReasons.push("no field reports");

        const pctVal = (entry) => (entry && typeof entry === "object") ? (entry.pct || 0) : (entry || 0);
        const phaseAvg = Array.isArray(p.phases) && p.phases.length
          ? Math.round(p.phases.reduce((s,e)=>s+pctVal(e),0) / p.phases.length)
          : (p.pct || p.progress || 0);

        projects.push({
          ...p,
          gcCompanyId: cid,
          gcName: gcName(cid),
          openCOs,
          openRFIs,
          lastLogDate: lastLog?.date || "",
          daysSinceLog,
          recentLogsCount,
          allCOs: projCOs,
          allRFIs: projRFIs,
          allLogs,
          phaseAvg,
          atRisk: atRiskReasons.length > 0,
          atRiskReasons,
        });
      });
    }

    const active = projects.filter(p => p.status === "active");
    const atRisk = active.filter(p => p.atRisk);
    const totalCOs  = active.reduce((s, p) => s + p.openCOs, 0);
    const totalRFIs = active.reduce((s, p) => s + p.openRFIs, 0);
    const totalRecentLogs = active.reduce((s, p) => s + p.recentLogsCount, 0);
    const avgProgress = active.length ? Math.round(active.reduce((s, p) => s + (p.phaseAvg || 0), 0) / active.length) : 0;
    const totalValue = active.reduce((s, p) => s + (Number(p.value) || 0), 0);
    const gcCount = new Set(projects.map(p => p.gcCompanyId)).size;

    return { projects, active, atRisk, totalCOs, totalRFIs, totalRecentLogs, avgProgress, totalValue, gcCount };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick, session?.companyId]);

  // ── Filter + group for the list ───────────────────────────────────────────
  const filtered = (() => {
    let pool = filter === "active" ? data.active
             : filter === "at_risk" ? data.atRisk
             : data.projects;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      pool = pool.filter(p =>
        (p.name||"").toLowerCase().includes(q) ||
        (p.client||"").toLowerCase().includes(q) ||
        (p.gcName||"").toLowerCase().includes(q) ||
        (p.addr||"").toLowerCase().includes(q));
    }
    return pool;
  })();

  const grouped = (() => {
    if (groupBy === "none") return [{ key: "all", name: "All Projects", projects: filtered }];
    if (groupBy === "status") {
      const g = {};
      filtered.forEach(p => {
        const s = (p.status || "unknown");
        if (!g[s]) g[s] = { key: s, name: s.charAt(0).toUpperCase()+s.slice(1), projects: [] };
        g[s].projects.push(p);
      });
      return Object.values(g);
    }
    const g = {};
    filtered.forEach(p => {
      if (!g[p.gcCompanyId]) g[p.gcCompanyId] = { key: p.gcCompanyId, name: p.gcName, projects: [] };
      g[p.gcCompanyId].projects.push(p);
    });
    return Object.values(g).sort((a, b) => b.projects.length - a.projects.length);
  })();

  // ── UI helpers ────────────────────────────────────────────────────────────
  const StatBox = ({label, value, sub, color}) => (
    <div style={{background: T.card, border: "1px solid " + T.subtle, borderRadius: 10, padding: "12px 14px"}}>
      <div style={{fontSize: 10, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em"}}>{label}</div>
      <div style={{fontSize: 22, color: color || T.text, fontWeight: 800, marginTop: 4}}>{value}</div>
      {sub && <div style={{fontSize: 10, color: T.muted, marginTop: 3}}>{sub}</div>}
    </div>
  );

  const chip = (id, label, count, accent) => (
    <button key={id} onClick={() => setFilter(id)}
      style={{padding: "6px 12px", fontSize: 11, fontWeight: 700, border: "none", cursor: "pointer", borderRadius: 7,
        background: filter === id ? (accent || T.gold) : T.bg2, color: filter === id ? (accent ? "white" : T.bg0) : T.muted,
        display: "flex", alignItems: "center", gap: 6}}>
      {label}
      <span style={{fontSize: 10, fontWeight: 800, background: filter === id ? "rgba(0,0,0,0.18)" : T.bg, color: filter === id ? "inherit" : T.muted, padding: "2px 6px", borderRadius: 10}}>{count}</span>
    </button>
  );

  const groupBtn = (id, label) => (
    <button key={id} onClick={() => setGroupBy(id)}
      style={{padding: "5px 10px", fontSize: 10, fontWeight: 700, border: "1px solid " + T.subtle, cursor: "pointer", borderRadius: 6,
        background: groupBy === id ? T.bg2 : "transparent", color: groupBy === id ? T.text : T.muted}}>
      {label}
    </button>
  );

  const ProjectCard = ({p}) => {
    const progColor = p.phaseAvg >= 80 ? T.green : p.phaseAvg >= 30 ? T.gold : T.amber;
    return (
      <div onClick={() => setSelected(p)}
        style={{background: T.card, border: "1px solid " + (p.atRisk ? T.amber + "60" : T.subtle), borderRadius: 10, padding: "12px 14px", cursor: "pointer", transition: "all 0.15s"}}
        onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
        onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 6}}>
          <div style={{flex: 1, minWidth: 0}}>
            <div style={{fontSize: 13, fontWeight: 800, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{p.name}</div>
            <div style={{fontSize: 10, color: T.muted, marginTop: 2}}>{p.gcName} · {p.client || p.owner_name || "—"}</div>
          </div>
          <div style={{fontSize: 16, fontWeight: 800, color: progColor, flexShrink: 0}}>{p.phaseAvg}%</div>
        </div>
        {/* Progress bar */}
        <div style={{height: 5, background: T.bg, borderRadius: 3, overflow: "hidden", marginBottom: 10}}>
          <div style={{height: "100%", width: p.phaseAvg + "%", background: progColor, transition: "width 0.2s"}}/>
        </div>
        {/* Stat row */}
        <div style={{display: "flex", gap: 12, fontSize: 10, color: T.muted, flexWrap: "wrap"}}>
          <span title="Open change orders">📝 <strong style={{color: p.openCOs > 0 ? T.amber : T.text}}>{p.openCOs}</strong> CO</span>
          <span title="Open RFIs">❓ <strong style={{color: p.openRFIs > 0 ? T.red : T.text}}>{p.openRFIs}</strong> RFI</span>
          <span title="Field reports in last 7 days">📋 <strong style={{color: T.text}}>{p.recentLogsCount}</strong> /7d</span>
          {p.daysSinceLog !== null && (
            <span title="Days since last field report" style={{color: p.daysSinceLog > 7 ? T.amber : T.muted}}>🕐 {p.daysSinceLog === 0 ? "today" : p.daysSinceLog + "d"}</span>
          )}
        </div>
        {p.atRiskReasons.length > 0 && (
          <div style={{marginTop: 8, paddingTop: 8, borderTop: "1px solid " + T.subtle, fontSize: 10, color: T.amber, fontWeight: 600}}>
            ⚠ {p.atRiskReasons.join(" · ")}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{padding: isMob ? "12px" : "20px 24px", paddingBottom: isMob ? "100px" : "24px", overflowY: "auto"}}>
      <div style={{display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14}}>
        <div>
          <div style={{fontSize: isMob ? 16 : 20, fontWeight: 800, color: T.text}}>🏢 Portfolio Dashboard</div>
          <div style={{fontSize: 11, color: T.muted, marginTop: 3}}>{data.projects.length} project{data.projects.length === 1 ? "" : "s"} across {data.gcCount} GC{data.gcCount === 1 ? "" : "s"}</div>
        </div>
        <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search projects, GCs, addresses…"
          style={{flex: "1 1 200px", maxWidth: 320, background: T.bg, border: "1px solid " + T.subtle, borderRadius: 7, padding: "8px 11px", fontSize: 12, color: T.text, fontFamily: "inherit"}}/>
      </div>

      {/* Big stats */}
      <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14}}>
        <StatBox label="Active" value={data.active.length} sub={data.gcCount + " GC" + (data.gcCount === 1 ? "" : "s")}/>
        <StatBox label="Portfolio Value" value={"$" + (data.totalValue / 1_000_000).toFixed(1) + "M"}/>
        <StatBox label="Open COs" value={data.totalCOs} color={data.totalCOs > 0 ? T.amber : T.text}/>
        <StatBox label="Open RFIs" value={data.totalRFIs} color={data.totalRFIs > 0 ? T.red : T.text}/>
        <StatBox label="Reports (7d)" value={data.totalRecentLogs}/>
        <StatBox label="Avg Progress" value={data.avgProgress + "%"} color={T.green}/>
      </div>

      {data.atRisk.length > 0 && filter !== "at_risk" && (
        <div onClick={() => setFilter("at_risk")}
          style={{background: T.amberBg, border: "1px solid " + T.amber + "60", borderRadius: 10, padding: "10px 14px", marginBottom: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 10}}>
          <span style={{fontSize: 18}}>⚠</span>
          <div style={{flex: 1, fontSize: 12, color: T.text, fontWeight: 700}}>
            {data.atRisk.length} project{data.atRisk.length === 1 ? "" : "s"} need{data.atRisk.length === 1 ? "s" : ""} attention
          </div>
          <span style={{fontSize: 11, color: T.amber, fontWeight: 700}}>Show →</span>
        </div>
      )}

      {/* Filter chips + group toggle */}
      <div style={{display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12}}>
        {chip("active",  "Active",   data.active.length)}
        {chip("at_risk", "At Risk",  data.atRisk.length, T.amber)}
        {chip("all",     "All",      data.projects.length)}
        <div style={{flex: 1}}/>
        <div style={{display: "flex", gap: 4, alignItems: "center"}}>
          <span style={{fontSize: 10, color: T.muted, marginRight: 4}}>Group by:</span>
          {groupBtn("gc",     "GC")}
          {groupBtn("status", "Status")}
          {groupBtn("none",   "None")}
        </div>
      </div>

      {/* Empty state */}
      {!filtered.length && (
        <div style={{padding: 40, textAlign: "center", background: T.bg, borderRadius: 10, border: "1px dashed " + T.subtle, fontSize: 12, color: T.muted}}>
          {data.projects.length === 0
            ? "No projects on this device yet. Sign in to a GC account or have a GC share their projects with you to populate this dashboard."
            : "No projects match the current filter / search."}
        </div>
      )}

      {/* Grouped project list */}
      {grouped.map(g => (
        <div key={g.key} style={{marginBottom: 18}}>
          {groupBy !== "none" && (
            <div style={{display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8}}>
              <div style={{fontSize: 12, fontWeight: 800, color: T.text}}>{g.name}</div>
              <div style={{fontSize: 10, color: T.muted}}>{g.projects.length} project{g.projects.length === 1 ? "" : "s"}</div>
            </div>
          )}
          <div style={{display: "grid", gridTemplateColumns: isMob ? "1fr" : "repeat(auto-fit, minmax(300px, 1fr))", gap: 10}}>
            {g.projects.map(p => <ProjectCard key={p.gcCompanyId + ":" + p.id} p={p}/>)}
          </div>
        </div>
      ))}

      {selected && <PortfolioProjectModal project={selected} onClose={() => setSelected(null)}/>}
    </div>
  );
}

// Read-only drill-down for a project the PM doesn't own. Does NOT mutate state
// or call any GC-scoped writes — pure presentation of the data already in
// localStorage. Click "Open in GC view" only works if the active session
// matches the project's gcCompanyId; otherwise the link tells the PM to switch.
function PortfolioProjectModal({project, onClose}) {
  const T = useTheme();
  const isMob = window.innerWidth < 768;
  const p = project;
  const PHASE_NAMES_LOCAL = ["Demo","Rough Framing","MEP Rough-In","Drywall & Tape","Tile & Finish","Paint","Closeout"];
  const phaseVal = (entry) => (entry && typeof entry === "object") ? (entry.pct || 0) : (entry || 0);

  const recentCOs  = (p.allCOs  || []).slice().sort((a,b) => String(b.createdAt||b.date||"").localeCompare(String(a.createdAt||a.date||""))).slice(0, 5);
  const recentRFIs = (p.allRFIs || []).slice().sort((a,b) => String(b.createdAt||"").localeCompare(String(a.createdAt||""))).slice(0, 5);
  const recentLogs = (p.allLogs || []).slice(0, 5);

  const sectionTitle = (icon, t) => (
    <div style={{fontSize: 11, fontWeight: 800, color: T.text, marginBottom: 8, marginTop: 14, display: "flex", alignItems: "center", gap: 6, textTransform: "uppercase", letterSpacing: ".05em"}}>
      <span>{icon}</span>{t}
    </div>
  );

  return (
    <div style={{position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: isMob ? 8 : 20}}>
      <div style={{background: T.bg1, borderRadius: 12, border: "1px solid " + T.subtle, maxWidth: 780, width: "100%", maxHeight: "92vh", overflow: "auto"}}>
        <div style={{padding: "14px 18px", borderBottom: "1px solid " + T.subtle, display: "flex", alignItems: "flex-start", gap: 10}}>
          <div style={{flex: 1, minWidth: 0}}>
            <div style={{fontSize: 14, fontWeight: 800, color: T.text}}>{p.name}</div>
            <div style={{fontSize: 11, color: T.muted, marginTop: 3}}>{p.gcName} · {p.addr || "—"} · {p.client || p.owner_name || ""}</div>
          </div>
          <button onClick={onClose} style={{background: "transparent", border: "none", color: T.muted, fontSize: 18, cursor: "pointer", padding: 4}}>✕</button>
        </div>

        <div style={{padding: 16}}>
          {/* Stats */}
          <div style={{display: "grid", gridTemplateColumns: isMob ? "1fr 1fr" : "repeat(4, 1fr)", gap: 8, marginBottom: 14}}>
            <div style={{background: T.bg, borderRadius: 8, padding: "10px 12px"}}>
              <div style={{fontSize: 10, color: T.muted, fontWeight: 700}}>Progress</div>
              <div style={{fontSize: 18, fontWeight: 800, color: T.text, marginTop: 3}}>{p.phaseAvg}%</div>
            </div>
            <div style={{background: T.bg, borderRadius: 8, padding: "10px 12px"}}>
              <div style={{fontSize: 10, color: T.muted, fontWeight: 700}}>Contract Value</div>
              <div style={{fontSize: 18, fontWeight: 800, color: T.text, marginTop: 3}}>${(((Number(p.value)||0)/1000).toFixed(0))}K</div>
            </div>
            <div style={{background: T.bg, borderRadius: 8, padding: "10px 12px"}}>
              <div style={{fontSize: 10, color: T.muted, fontWeight: 700}}>Open COs</div>
              <div style={{fontSize: 18, fontWeight: 800, color: p.openCOs > 0 ? T.amber : T.text, marginTop: 3}}>{p.openCOs}</div>
            </div>
            <div style={{background: T.bg, borderRadius: 8, padding: "10px 12px"}}>
              <div style={{fontSize: 10, color: T.muted, fontWeight: 700}}>Open RFIs</div>
              <div style={{fontSize: 18, fontWeight: 800, color: p.openRFIs > 0 ? T.red : T.text, marginTop: 3}}>{p.openRFIs}</div>
            </div>
          </div>

          {p.atRiskReasons.length > 0 && (
            <div style={{background: T.amberBg, border: "1px solid " + T.amber + "60", borderRadius: 8, padding: "9px 12px", marginBottom: 14, fontSize: 11, color: T.amber, fontWeight: 700}}>
              ⚠ {p.atRiskReasons.join(" · ")}
            </div>
          )}

          {/* Phase progress */}
          {sectionTitle("📊", "Milestone Progress")}
          <div style={{background: T.bg, borderRadius: 8, padding: "10px 12px"}}>
            {PHASE_NAMES_LOCAL.map((name, i) => {
              const v = phaseVal((p.phases || [])[i]);
              const col = v === 100 ? T.green : v >= 80 ? T.gold : v > 0 ? T.amber : T.muted;
              return (
                <div key={i} style={{display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: i < 6 ? "1px solid " + T.subtle : "none"}}>
                  <div style={{width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: v === 100 ? T.greenBg : v > 0 ? T.goldBg : T.bg3, color: col, fontSize: 9, fontWeight: 800}}>
                    {v === 100 ? "✓" : i+1}
                  </div>
                  <div style={{flex: 1, fontSize: 11, color: T.text}}>{name}</div>
                  <div style={{width: 100, height: 5, background: T.bg2, borderRadius: 3, overflow: "hidden"}}>
                    <div style={{height: "100%", width: v + "%", background: col}}/>
                  </div>
                  <div style={{width: 40, textAlign: "right", fontSize: 11, fontWeight: 700, color: col}}>{v}%</div>
                </div>
              );
            })}
          </div>

          {/* Recent COs */}
          {sectionTitle("📝", "Recent Change Orders")}
          {recentCOs.length ? (
            <div style={{background: T.bg, borderRadius: 8, overflow: "hidden"}}>
              {recentCOs.map((c, i) => (
                <div key={c.id || i} style={{padding: "9px 12px", borderBottom: i < recentCOs.length - 1 ? "1px solid " + T.subtle : "none", display: "flex", alignItems: "center", gap: 8}}>
                  <div style={{flex: 1, minWidth: 0}}>
                    <div style={{fontSize: 11, fontWeight: 700, color: T.text}}>{c.num || ("CO-" + c.id)} · {(c.desc || c.title || "—").slice(0, 60)}</div>
                    <div style={{fontSize: 10, color: T.muted, marginTop: 2}}>{c.date || (c.createdAt || "").slice(0, 10)} {c.amount ? "· $" + Number(c.amount).toLocaleString() : ""}</div>
                  </div>
                  <span style={{fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 10, background: c.status === "approved" ? T.green + "22" : c.status === "pending" || c.status === "pending_gc_approval" ? T.amber + "22" : T.bg2, color: c.status === "approved" ? T.green : c.status === "pending" || c.status === "pending_gc_approval" ? T.amber : T.muted}}>
                    {c.status || "open"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{fontSize: 11, color: T.muted, padding: "10px 0"}}>No change orders on this project.</div>
          )}

          {/* Recent RFIs */}
          {sectionTitle("❓", "Recent RFIs")}
          {recentRFIs.length ? (
            <div style={{background: T.bg, borderRadius: 8, overflow: "hidden"}}>
              {recentRFIs.map((r, i) => (
                <div key={r.id || i} style={{padding: "9px 12px", borderBottom: i < recentRFIs.length - 1 ? "1px solid " + T.subtle : "none", display: "flex", alignItems: "center", gap: 8}}>
                  <div style={{flex: 1, minWidth: 0}}>
                    <div style={{fontSize: 11, fontWeight: 700, color: T.text}}>{r.num || ("RFI-" + r.id)} · {(r.subject || r.title || "—").slice(0, 60)}</div>
                    <div style={{fontSize: 10, color: T.muted, marginTop: 2}}>{(r.createdAt || r.date || "").slice(0, 10)} {r.priority ? "· " + r.priority : ""}</div>
                  </div>
                  <span style={{fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 10, background: r.status === "closed" ? T.green + "22" : T.amber + "22", color: r.status === "closed" ? T.green : T.amber}}>
                    {r.status || "open"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{fontSize: 11, color: T.muted, padding: "10px 0"}}>No RFIs on this project.</div>
          )}

          {/* Recent field reports */}
          {sectionTitle("📋", "Recent Field Reports")}
          {recentLogs.length ? (
            <div style={{background: T.bg, borderRadius: 8, overflow: "hidden"}}>
              {recentLogs.map((l, i) => (
                <div key={l.id || i} style={{padding: "9px 12px", borderBottom: i < recentLogs.length - 1 ? "1px solid " + T.subtle : "none"}}>
                  <div style={{display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2}}>
                    <span style={{fontSize: 11, fontWeight: 700, color: T.text}}>{l.date || "—"}</span>
                    <span style={{fontSize: 10, color: T.muted}}>{l.phase || l.trade || ""} {l.crew_count ? "· " + l.crew_count + " crew" : ""}</span>
                  </div>
                  <div style={{fontSize: 10, color: T.muted, lineHeight: 1.5}}>{(l.work_performed || l.work_done || "—").slice(0, 200)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{fontSize: 11, color: T.muted, padding: "10px 0"}}>No field reports filed yet.</div>
          )}

          <div style={{marginTop: 14, paddingTop: 14, borderTop: "1px solid " + T.subtle, fontSize: 10, color: T.muted, textAlign: "center"}}>
            Read-only view · Data from {p.gcName}'s account. Contact the GC for changes.
          </div>
        </div>
      </div>
    </div>
  );
}

// QuickBooks Export — generates CSV files (QBO format) for Customers, Vendors,
// Invoices, and CO line items, plus a combined .IIF for QB Desktop. Export-only:
// no OAuth, no live API. The bookkeeper imports the file into QuickBooks.
// Tracks what's been exported in grok_qb_exported_<cid> so the UI shows new vs.
// previously exported rows.
function QuickBooksExportView({projects, session}) {
  const T = useTheme();
  const isMob = window.innerWidth < 768;
  const cid = session?.companyId || window._grokCompanyId || "local";

  const [tab, setTab] = useState("customers");
  // Default date window: this month + last month (covers most pay-app cycles)
  const today = new Date();
  const firstLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().slice(0, 10);
  const lastToday = today.toISOString().slice(0, 10);
  const [from, setFrom] = useState(firstLastMonth);
  const [to, setTo] = useState(lastToday);

  // Track which rows have been exported before. Key: "<type>:<id>".
  const exportedKey = "grok_qb_exported_" + cid;
  const [exportedSet, setExportedSet] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(exportedKey) || "[]")); }
    catch { return new Set(); }
  });
  const markExported = (keys) => {
    setExportedSet(prev => {
      const next = new Set(prev);
      keys.forEach(k => next.add(k));
      try { localStorage.setItem(exportedKey, JSON.stringify([...next])); } catch {}
      return next;
    });
  };
  const clearExportedLog = () => {
    if (!window.confirm("Clear the exported-log? Future exports will re-include rows already sent to QuickBooks.")) return;
    setExportedSet(new Set());
    try { localStorage.removeItem(exportedKey); } catch {}
  };

  // ── Data builders ────────────────────────────────────────────────────────
  const inRange = (iso) => {
    if (!iso) return true;
    const d = String(iso).slice(0, 10);
    return (!from || d >= from) && (!to || d <= to);
  };

  // Uses the global isJunkProject / cleanProjects helpers from the data-sync
  // layer. cleanedProjects is the single source of truth for all builders.
  const cleanedProjects = React.useMemo(
    () => cleanProjects(projects),
    [projects]
  );
  const validProjectNames = React.useMemo(
    () => new Set(cleanedProjects.map(p => (p.name || "").trim().toLowerCase()).filter(Boolean)),
    [cleanedProjects]
  );

  // Customers from projects — one row per active+archived project.
  const customers = React.useMemo(() => {
    return cleanedProjects.map(p => ({
      qb_key: "cust:" + p.id,
      name: (p.client || p.owner_name || p.name || "Project " + p.id).slice(0, 50),
      company: p.client || "",
      project_name: p.name || "",
      project_id: p.id,
      email: p.owner_email || "",
      phone: p.owner_phone || "",
      address: p.addr || "",
      contract_value: p.value || 0,
      type: p.type || "",
      status: p.status || "active",
    }));
  }, [cleanedProjects]);

  // Vendors from sub roster.
  const vendors = React.useMemo(() => {
    let db = { subs: [] };
    try { db = loadSubDB(cid) || { subs: [] }; } catch {}
    return (db.subs || []).map(s => ({
      qb_key: "vend:" + s.id,
      vendor_name: s.company || s.name || "Vendor",
      display_name: s.name || s.company || "Vendor",
      company: s.company || "",
      email: s.email || "",
      phone: s.phone || "",
      address: s.address || "",
      tax_id: s.ein || s.tax_id || "",
      track_1099: s.is_corp === false ? "Yes" : "No",
      terms: s.payment_terms || "Net 30",
      trade: s.trade || "",
      license: s.license || s.cslb || "",
    }));
  }, [cid]);

  // Invoices from pay apps. Each pay app on each project becomes one invoice.
  const invoices = React.useMemo(() => {
    const out = [];
    cleanedProjects.forEach(p => {
      let apps = [];
      try { apps = JSON.parse(localStorage.getItem("grok_payapps_" + p.id) || "[]"); } catch {}
      apps.forEach(a => {
        const date = (a.submitted_date || a.approved_date || a.draftedAt || "").slice(0, 10);
        if (!inRange(date)) return;
        // Only export approved or submitted pay apps — drafts don't belong in books.
        if (a.status === "pending_gc_approval" || a.status === "draft") return;
        out.push({
          qb_key: "inv:" + p.id + ":" + a.id,
          invoice_no: "PA-" + (a.app_num || a.id),
          customer: (p.client || p.owner_name || p.name || "Project " + p.id).slice(0, 50),
          project_name: p.name || "",
          invoice_date: date || lastToday,
          due_date: (() => {
            try { const d = new Date(date || lastToday); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); }
            catch { return ""; }
          })(),
          memo: "Pay Application #" + (a.app_num || "") + " — " + (a.milestoneName || p.name || ""),
          item: "Construction Services",
          item_description: (a.narrative || a.milestoneName || "Progress billing").slice(0, 200),
          qty: 1,
          rate: Number(a.amount_due || a.work_completed || 0),
          amount: Number(a.amount_due || a.work_completed || 0),
          status: a.status || "",
          period: (a.period_from || "") + " → " + (a.period_to || ""),
        });
      });
    });
    return out.sort((a, b) => (b.invoice_date || "").localeCompare(a.invoice_date || ""));
  }, [cleanedProjects, from, to]);

  // CO line items — only approved COs, optionally filtered by date. Skip COs
  // whose project name doesn't match a non-junk project (so an orphaned CO
  // pointing at "test" never makes it into the books).
  const coLines = React.useMemo(() => {
    let all = [];
    try { all = JSON.parse(localStorage.getItem("grok_cos_global_" + cid) || "[]"); } catch {}
    return all
      .filter(c => c.status === "approved")
      .filter(c => {
        const d = (c.approvedAt || c.createdAt || c.date || "").slice(0, 10);
        return !d || inRange(d);
      })
      .filter(c => {
        const proj = String(c.project || "").trim().toLowerCase();
        if (!proj) return false;
        return validProjectNames.has(proj);
      })
      .map(c => ({
        qb_key: "co:" + c.id,
        customer: c.project || "Project",
        co_num: c.num || ("CO-" + c.id),
        date: (c.approvedAt || c.createdAt || c.date || "").slice(0, 10),
        item: "Change Order",
        item_description: (c.desc || c.title || c.num || "Change order"),
        qty: 1,
        rate: Number(c.amount || 0),
        amount: Number(c.amount || 0),
      }));
  }, [cid, from, to, validProjectNames]);

  // ── CSV / IIF generators ─────────────────────────────────────────────────
  const csvCell = (v) => {
    const s = v == null ? "" : String(v);
    if (s.includes(",") || s.includes("\"") || s.includes("\n")) return "\"" + s.replace(/"/g, "\"\"") + "\"";
    return s;
  };
  const toCSV = (header, rows) => {
    return [header.map(csvCell).join(","), ...rows.map(r => header.map(h => csvCell(r[h])).join(","))].join("\n");
  };
  const download = (filename, content, mime = "text/csv;charset=utf-8") => {
    const url = "data:" + mime + "," + encodeURIComponent(content);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  // QBO CSV column orders chosen to match QuickBooks Online import templates.
  const CSV_SPECS = {
    customers: {
      filename: "qb_customers_" + lastToday + ".csv",
      header: ["Name", "Company", "Email", "Phone", "Bill Address 1", "Notes", "Customer Type", "Display Name"],
      mapRow: (r) => ({
        "Name": r.name,
        "Company": r.company,
        "Email": r.email,
        "Phone": r.phone,
        "Bill Address 1": r.address,
        "Notes": "Project: " + r.project_name + (r.contract_value ? " · $" + r.contract_value.toLocaleString() : ""),
        "Customer Type": r.type,
        "Display Name": r.name,
      }),
    },
    vendors: {
      filename: "qb_vendors_" + lastToday + ".csv",
      header: ["Vendor Name", "Display Name", "Company", "Email", "Phone", "Bill From Address 1", "Tax ID", "Track Payments for 1099", "Terms", "Notes"],
      mapRow: (r) => ({
        "Vendor Name": r.vendor_name,
        "Display Name": r.display_name,
        "Company": r.company,
        "Email": r.email,
        "Phone": r.phone,
        "Bill From Address 1": r.address,
        "Tax ID": r.tax_id,
        "Track Payments for 1099": r.track_1099,
        "Terms": r.terms,
        "Notes": "Trade: " + r.trade + (r.license ? " · CSLB " + r.license : ""),
      }),
    },
    invoices: {
      filename: "qb_invoices_" + lastToday + ".csv",
      header: ["InvoiceNo", "Customer", "InvoiceDate", "DueDate", "Terms", "Memo", "Item", "ItemDescription", "ItemQuantity", "ItemRate", "ItemAmount"],
      mapRow: (r) => ({
        "InvoiceNo": r.invoice_no,
        "Customer": r.customer,
        "InvoiceDate": r.invoice_date,
        "DueDate": r.due_date,
        "Terms": "Net 30",
        "Memo": r.memo,
        "Item": r.item,
        "ItemDescription": r.item_description,
        "ItemQuantity": r.qty,
        "ItemRate": r.rate.toFixed(2),
        "ItemAmount": r.amount.toFixed(2),
      }),
    },
    co_lines: {
      filename: "qb_change_orders_" + lastToday + ".csv",
      header: ["Customer", "CO Number", "Date", "Item", "Description", "Quantity", "Rate", "Amount"],
      mapRow: (r) => ({
        "Customer": r.customer,
        "CO Number": r.co_num,
        "Date": r.date,
        "Item": r.item,
        "Description": r.item_description,
        "Quantity": r.qty,
        "Rate": r.rate.toFixed(2),
        "Amount": r.amount.toFixed(2),
      }),
    },
  };

  const DATA = { customers, vendors, invoices, co_lines: coLines };
  const TAB_LABELS = { customers: "Customers", vendors: "Vendors", invoices: "Invoices", co_lines: "CO Line Items" };

  const exportTab = (which) => {
    const spec = CSV_SPECS[which];
    const rows = DATA[which];
    if (!rows.length) { alert("Nothing to export in " + TAB_LABELS[which] + "."); return; }
    const mapped = rows.map(spec.mapRow);
    const csv = toCSV(spec.header, mapped);
    download(spec.filename, csv);
    markExported(rows.map(r => r.qb_key));
  };

  // Combined IIF for QB Desktop. Tab-delimited; CUST, VEND, TRNS/SPL blocks.
  const buildIIF = () => {
    const tab = "\t";
    const esc = (s) => "\"" + String(s == null ? "" : s).replace(/"/g, "'") + "\"";
    const lines = [];

    if (customers.length) {
      lines.push(["!CUST", "NAME", "BADDR1", "PHONE1", "EMAIL", "NOTE", "CTYPE"].join(tab));
      customers.forEach(c => {
        lines.push(["CUST", esc(c.name), esc(c.address), esc(c.phone), esc(c.email), esc("Project: " + c.project_name), esc(c.type)].join(tab));
      });
      lines.push(["!ENDGRP"].join(tab));
    }

    if (vendors.length) {
      lines.push(["!VEND", "NAME", "VTYPE", "BADDR1", "PHONE1", "EMAIL", "TAXID", "NOTE", "TERMS"].join(tab));
      vendors.forEach(v => {
        lines.push(["VEND", esc(v.vendor_name), esc("Vendor"), esc(v.address), esc(v.phone), esc(v.email), esc(v.tax_id), esc("Trade: " + v.trade), esc(v.terms)].join(tab));
      });
      lines.push(["!ENDGRP"].join(tab));
    }

    if (invoices.length) {
      // INVOICE = TRNS (header) + one SPL per line, ENDTRNS per invoice
      lines.push(["!TRNS", "TRNSTYPE", "DATE", "ACCNT", "NAME", "AMOUNT", "DOCNUM", "MEMO"].join(tab));
      lines.push(["!SPL",  "TRNSTYPE", "DATE", "ACCNT", "NAME", "AMOUNT", "MEMO"].join(tab));
      lines.push(["!ENDTRNS"].join(tab));
      invoices.forEach(inv => {
        // The TRNS row is the receivable (positive); SPL is the income account (negative).
        lines.push(["TRNS", "INVOICE", inv.invoice_date, esc("Accounts Receivable"), esc(inv.customer), inv.amount.toFixed(2), esc(inv.invoice_no), esc(inv.memo)].join(tab));
        lines.push(["SPL",  "INVOICE", inv.invoice_date, esc("Construction Income"),  esc(inv.customer), (-inv.amount).toFixed(2),                       esc(inv.item_description)].join(tab));
        lines.push(["ENDTRNS"].join(tab));
      });
    }

    if (coLines.length) {
      lines.push(["!TRNS", "TRNSTYPE", "DATE", "ACCNT", "NAME", "AMOUNT", "DOCNUM", "MEMO"].join(tab));
      lines.push(["!SPL",  "TRNSTYPE", "DATE", "ACCNT", "NAME", "AMOUNT", "MEMO"].join(tab));
      lines.push(["!ENDTRNS"].join(tab));
      coLines.forEach(c => {
        lines.push(["TRNS", "INVOICE", c.date || lastToday, esc("Accounts Receivable"), esc(c.customer), c.amount.toFixed(2), esc(c.co_num), esc(c.item_description)].join(tab));
        lines.push(["SPL",  "INVOICE", c.date || lastToday, esc("Construction Income"),  esc(c.customer), (-c.amount).toFixed(2),                  esc(c.item_description)].join(tab));
        lines.push(["ENDTRNS"].join(tab));
      });
    }

    return lines.join("\n");
  };

  const exportIIF = () => {
    if (!customers.length && !vendors.length && !invoices.length && !coLines.length) { alert("Nothing to export."); return; }
    const iif = buildIIF();
    download("grokfieldops_quickbooks_" + lastToday + ".iif", iif, "application/octet-stream;charset=utf-8");
    const allKeys = [...customers, ...vendors, ...invoices, ...coLines].map(r => r.qb_key);
    markExported(allKeys);
  };

  // ── UI ────────────────────────────────────────────────────────────────────
  const tabBtn = (id) => {
    const count = DATA[id].length;
    const newCount = DATA[id].filter(r => !exportedSet.has(r.qb_key)).length;
    return (
      <button key={id} onClick={() => setTab(id)}
        style={{ padding: "8px 14px", fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer", background: tab === id ? T.gold : T.bg2, color: tab === id ? T.bg0 : T.muted, borderRadius: 8, display: "flex", alignItems: "center", gap: 6 }}>
        {TAB_LABELS[id]}
        <span style={{ fontSize: 10, fontWeight: 800, background: tab === id ? "rgba(0,0,0,0.15)" : T.bg, color: tab === id ? T.bg0 : T.muted, padding: "2px 6px", borderRadius: 10 }}>{count}</span>
        {newCount > 0 && newCount < count && tab !== id && (
          <span style={{ fontSize: 9, fontWeight: 700, color: T.green }}>+{newCount} new</span>
        )}
      </button>
    );
  };

  const rows = DATA[tab];

  const PreviewTable = () => {
    if (!rows.length) {
      return (
        <div style={{ padding: 32, textAlign: "center", fontSize: 12, color: T.muted, background: T.bg, borderRadius: 8, border: "1px dashed " + T.subtle }}>
          No {TAB_LABELS[tab].toLowerCase()} in the current range.
          {(tab === "invoices" || tab === "co_lines") && <div style={{ fontSize: 10, marginTop: 6 }}>Try widening the date range or approving pay apps / change orders first.</div>}
        </div>
      );
    }
    const cols = tab === "customers" ? ["name", "company", "email", "phone", "contract_value"]
               : tab === "vendors"   ? ["vendor_name", "trade", "email", "phone", "tax_id"]
               : tab === "invoices"  ? ["invoice_no", "customer", "invoice_date", "amount", "status"]
               :                       ["co_num", "customer", "date", "item_description", "amount"];
    const head = tab === "customers" ? ["Customer", "Company", "Email", "Phone", "Contract"]
               : tab === "vendors"   ? ["Vendor", "Trade", "Email", "Phone", "EIN"]
               : tab === "invoices"  ? ["Invoice", "Customer", "Date", "Amount", "Status"]
               :                       ["CO", "Customer", "Date", "Description", "Amount"];
    return (
      <div style={{ overflowX: "auto", background: T.bg, borderRadius: 8, border: "1px solid " + T.subtle }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr>
              {head.map(h => <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontWeight: 800, color: T.muted, borderBottom: "1px solid " + T.subtle, whiteSpace: "nowrap" }}>{h}</th>)}
              <th style={{ padding: "8px 10px", borderBottom: "1px solid " + T.subtle }}/>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 50).map((r, i) => {
              const wasExported = exportedSet.has(r.qb_key);
              return (
                <tr key={i} style={{ borderBottom: "1px solid " + T.subtle, background: wasExported ? "transparent" : T.green + "08" }}>
                  {cols.map(c => (
                    <td key={c} style={{ padding: "8px 10px", color: T.text, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c === "amount" || c === "contract_value" ? (Number(r[c] || 0) ? "$" + Number(r[c]).toLocaleString() : "—") : (r[c] || "—")}
                    </td>
                  ))}
                  <td style={{ padding: "8px 10px", textAlign: "right" }}>
                    {wasExported ? <span style={{ fontSize: 9, color: T.muted }}>exported</span> : <span style={{ fontSize: 9, fontWeight: 700, color: T.green }}>NEW</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length > 50 && (
          <div style={{ padding: "8px 12px", fontSize: 10, color: T.muted, borderTop: "1px solid " + T.subtle }}>Showing 50 of {rows.length} — the full set is exported.</div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: isMob ? "12px" : "20px 24px", paddingBottom: isMob ? "100px" : "24px", overflowY: "auto" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14, gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: isMob ? 16 : 20, fontWeight: 800, color: T.text }}>📒 QuickBooks Export</div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>Generate import-ready files. QB Online → CSV. QB Desktop → IIF.</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11, color: T.muted }}>
            <span>From</span>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ background: T.bg, border: "1px solid " + T.subtle, borderRadius: 6, padding: "5px 8px", color: T.text, fontSize: 11, fontFamily: "inherit" }}/>
            <span>To</span>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ background: T.bg, border: "1px solid " + T.subtle, borderRadius: 6, padding: "5px 8px", color: T.text, fontSize: 11, fontFamily: "inherit" }}/>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {["customers", "vendors", "invoices", "co_lines"].map(tabBtn)}
      </div>

      <PreviewTable/>

      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        <button onClick={() => exportTab(tab)} disabled={!rows.length}
          style={{ flex: "1 1 200px", padding: "11px 16px", fontSize: 12, fontWeight: 800, background: rows.length ? T.gold : T.bg2, color: rows.length ? T.bg0 : T.muted, border: "none", borderRadius: 8, cursor: rows.length ? "pointer" : "not-allowed" }}>
          ⬇ Download {TAB_LABELS[tab]} CSV
        </button>
        <button onClick={exportIIF}
          style={{ flex: "1 1 200px", padding: "11px 16px", fontSize: 12, fontWeight: 700, background: T.bg2, color: T.text, border: "1px solid " + T.subtle, borderRadius: 8, cursor: "pointer" }}>
          ⬇ Download all as .IIF (QB Desktop)
        </button>
        {exportedSet.size > 0 && (
          <button onClick={clearExportedLog}
            style={{ padding: "11px 16px", fontSize: 11, fontWeight: 600, background: "transparent", color: T.muted, border: "1px solid " + T.subtle, borderRadius: 8, cursor: "pointer" }}>
            Reset exported log
          </button>
        )}
      </div>

      <div style={{ marginTop: 18, padding: "12px 14px", background: T.bg, border: "1px solid " + T.subtle, borderRadius: 8, fontSize: 11, color: T.muted, lineHeight: 1.6 }}>
        <div style={{ fontWeight: 800, color: T.text, marginBottom: 6 }}>How to import</div>
        <div><strong style={{ color: T.text }}>QB Online:</strong> Settings ⚙ → Import data → pick Customers / Vendors / Invoices → upload the CSV → map columns (defaults usually match) → import.</div>
        <div style={{ marginTop: 4 }}><strong style={{ color: T.text }}>QB Desktop:</strong> File → Utilities → Import → IIF Files → select the .iif. Back up first.</div>
        <div style={{ marginTop: 6, color: T.amber }}>⚠ Only approved pay apps and approved COs export. Drafts and pending items are excluded so books stay accurate.</div>
      </div>
    </div>
  );
}


function App({session, onLogout}) {
  // Initialize data — always prefer localStorage so deletes persist
  React.useEffect(()=>{
    const cid = session?.companyId || "";
    window._grokCompanyId = cid;

    // Load from localStorage first — fall back to demo data only if empty
    const storedProjects = (() => { try { const s=localStorage.getItem("grok_projects_"+cid); return s?JSON.parse(s):null; } catch{return null;}})();
    const storedCOs      = (() => { try { const s=localStorage.getItem("grok_cos_global_"+cid); return s?JSON.parse(s):null; } catch{return null;}})();
    const storedRFIs     = (() => { try { const s=localStorage.getItem("grok_rfis_global_"+cid); return s?JSON.parse(s):null; } catch{return null;}})();

    PROJECTS = storedProjects !== null ? storedProjects : getInitialProjects(cid);
    COS      = storedCOs      !== null ? storedCOs      : getInitialCOs(cid);
    RFIS     = storedRFIs     !== null ? storedRFIs     : getInitialRFIs(cid);
    LEADS    = getInitialLeads(cid);
  }, [session?.companyId]);

  const [view,setView]=useState("dashboard");
  // Show onboarding on first login
  const [showOnboarding,setShowOnboarding]=useState(()=>{
    const cid = session?.companyId;
    if (!cid) return false;
    return !localStorage.getItem("grok_onboarded_"+cid);
  });
  // Load EST_REQUESTS per company when session changes
  useEffect(()=>{
    if(session?.companyId) {
      EST_REQUESTS = getEstRequests(session.companyId);
    }
  },[session?.companyId]);
  const [isDark,setIsDark]=useState(false); // Light theme only
  useEffect(()=>{ 
    localStorage.removeItem("grok_theme");
    localStorage.removeItem("grok_dark");
    setGlobalTheme(false); 
    document.body.style.background="#f0f4f8"; 
    document.body.style.color="#0f1923";
  },[]);
  const theme = useTheme();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // Check for pending team invite token in the URL
  const [pendingTeamInvite] = useState(()=>checkTeamInviteToken());
  // Listen for blueprint review requesting navigation to AI Agent
  useEffect(()=>{
    const _nav=(e)=>{
      if(e.detail?.view) setView(e.detail.view);
    };
    window.addEventListener("grok-navigate",_nav);
    return ()=>window.removeEventListener("grok-navigate",_nav);
  },[]);
  useEffect(()=>{
    const _rz = ()=>setIsMobile(window.innerWidth<768);
    window.addEventListener("resize",_rz);
    return ()=>window.removeEventListener("resize",_rz);
  },[]);
  // ── Real-time sync — polling + BroadcastChannel ───────────────────────────
  // BroadcastChannel: instant sync between tabs on same device
  // Polling: sync across different devices every 30 seconds
  const [syncTick, setSyncTick] = useState(0);
  const [syncStatus, setSyncStatus] = useState("connected");

  // Detect online/offline
  useEffect(()=>{
    const goOnline  = () => { setSyncStatus("connected"); OfflineQueue.flush(); };
    const goOffline = () => setSyncStatus("offline");
    window.addEventListener("online",  goOnline);
    window.addEventListener("offline", goOffline);
    if (!navigator.onLine) setSyncStatus("offline");
    return ()=>{ window.removeEventListener("online",goOnline); window.removeEventListener("offline",goOffline); };
  },[]);

  useEffect(()=>{
    if (!session?.companyId) return;
    const cid = session.companyId;

    // ── BroadcastChannel — instant same-device sync ──────────────────────────
    let channel = null;
    try {
      channel = new BroadcastChannel("grok_sync_"+cid);
      channel.onmessage = (e) => {
        if (e.data?.type === "data_updated") {
          RFIS = JSON.parse(localStorage.getItem("grok_rfis_global_"+cid)||"[]");
          COS = JSON.parse(localStorage.getItem("grok_cos_global_"+cid)||"[]");
          PROJECTS = JSON.parse(localStorage.getItem(projectsKey(cid))||"[]");
          setSyncTick(t=>t+1);
        }
      };
    } catch(e) {}

    // ── Supabase Realtime — instant push when any user saves ─────────────────
    const syncFromSupabase = async (silent=false) => {
      setSyncStatus("syncing");
      try {
        const [sbProjects, sbRfis, sbCos, sbTeam] = await Promise.all([
          SyncDB.getProjects(),
          SyncDB.getRFIs(),
          SyncDB.getCOs(),
          SyncDB.getTeam(),
        ]);
        let changed = false;

        // Projects — only update if Supabase has items we don't have locally
        if (sbProjects.length) {
          const deletedDemosKey = "grok_deleted_demos_"+cid;
          const deletedDemos = (()=>{try{return JSON.parse(localStorage.getItem(deletedDemosKey)||"[]");}catch{return [];}})();
          const filteredSbProjects = sbProjects.filter(p=>!deletedDemos.includes(String(p.id))&&!String(p.id).startsWith("demo_"));
          const localProjs = JSON.parse(localStorage.getItem(projectsKey(cid))||"[]");
          const localIds = new Set(localProjs.map(p=>String(p.id)));
          const hasNew = filteredSbProjects.some(p=>!localIds.has(String(p.id)));
          if (hasNew && JSON.stringify(filteredSbProjects) !== JSON.stringify(PROJECTS)) {
            PROJECTS = filteredSbProjects;
            localStorage.setItem(projectsKey(cid), JSON.stringify(filteredSbProjects));
            setProjects(filteredSbProjects);
            changed = true;
          }
        } else {
          const local = JSON.parse(localStorage.getItem(projectsKey(cid))||"[]");
          if (local.length) SyncDB.saveProjects(local).catch(()=>{});
        }

        // RFIs — only update if Supabase has MORE or DIFFERENT items, never restore deleted
        if (sbRfis.length) {
          const localRFIs = JSON.parse(localStorage.getItem("grok_rfis_global_"+cid)||"[]");
          // Filter out any IDs we've deleted this session
          const filtered = sbRfis.filter(r=>!DELETED_IDS.has("rfis", r.id));
          const localIds = new Set(localRFIs.map(r=>String(r.id)));
          const hasNew = filtered.some(r=>!localIds.has(String(r.id)));
          if (hasNew && JSON.stringify(filtered) !== JSON.stringify(RFIS)) {
            RFIS = filtered;
            localStorage.setItem("grok_rfis_global_"+cid, JSON.stringify(filtered));
            changed = true;
          }
        } else {
          const local = JSON.parse(localStorage.getItem("grok_rfis_global_"+cid)||"[]");
          if (local.length) SyncDB.saveRFIs(local).catch(()=>{});
        }

        // COs — same logic
        if (sbCos.length) {
          const localCOs = JSON.parse(localStorage.getItem("grok_cos_global_"+cid)||"[]");
          const filtered = sbCos.filter(c=>!DELETED_IDS.has("cos", c.id));
          const localIds = new Set(localCOs.map(c=>String(c.id)));
          const hasNew = filtered.some(c=>!localIds.has(String(c.id)));
          if (hasNew && JSON.stringify(filtered) !== JSON.stringify(COS)) {
            COS = filtered;
            localStorage.setItem("grok_cos_global_"+cid, JSON.stringify(filtered));
            changed = true;
          }
        } else {
          const local = JSON.parse(localStorage.getItem("grok_cos_global_"+cid)||"[]");
          if (local.length) SyncDB.saveCOs(local).catch(()=>{});
        }

        // Team
        if (sbTeam.length) {
          localStorage.setItem("grok_team_"+cid, JSON.stringify(sbTeam));
        }

        setSyncStatus("connected");
        if (changed && !silent) setSyncTick(t=>t+1);
        return changed;
      } catch(e) {
        setSyncStatus("offline");
        return false;
      }
    };

    // Migrate on first login then load fresh from Supabase
    SyncDB.migrate().then(()=>syncFromSupabase(false));

    // Realtime websocket — instant push when any table changes
    const tables = ["projects","rfis","change_orders","team_members","flags"];
    tables.forEach(table => {
      sbRealtime.subscribe(table, cid, async () => {
        await syncFromSupabase(false);
        setSyncTick(t=>t+1);
      });
    });

    // Fallback polling every 15s in case websocket drops
    const poll = setInterval(()=>syncFromSupabase(false), 60000);

    return () => {
      clearInterval(poll);
      try { channel?.close(); } catch(e) {}
      tables.forEach(table => sbRealtime.unsubscribe(table, cid));
    };
  }, [session?.companyId]);

  // Helper: broadcast a change to other tabs instantly
  window._grokBroadcast = (type="data_updated") => {
    try {
      const ch = new BroadcastChannel("grok_sync_"+(session?.companyId||""));
      ch.postMessage({type, ts: Date.now()});
      ch.close();
    } catch(e) {}
  };

  useEffect(()=>{
    const handler = (e) => {
      if (e.target.tagName==="INPUT"||e.target.tagName==="TEXTAREA"||e.target.tagName==="SELECT") return;
      if (e.metaKey||e.ctrlKey) {
        switch(e.key) {
          case "1": e.preventDefault(); setView("dashboard"); break;
          case "2": e.preventDefault(); setView("projects"); break;
          case "3": e.preventDefault(); setView("estimates"); break;
          case "4": e.preventDefault(); setView("leads"); break;
          case "5": e.preventDefault(); setView("ai-agent"); break;
          case "k": e.preventDefault(); setView("kb"); break;
          case "p": e.preventDefault(); setView("pricing"); break;
          case "b": e.preventDefault(); setView("bids"); break;
          case "m": e.preventDefault(); setView("meetings"); break;
        }
      }
    };
    window.addEventListener("keydown", handler);
    return ()=>window.removeEventListener("keydown", handler);
  },[]);
  useEffect(()=>{const h=()=>setIsMobile(window.innerWidth<768);window.addEventListener("resize",h);return()=>window.removeEventListener("resize",h);},[]);
  const [mobileMenuOpen,setMobileMenuOpen]=useState(false);
  const [activeProject,setActiveProject]=useState(()=>{
    // Auto-load first project for Architect/Client roles
    if (session?.teamRole==="architect"||session?.teamRole==="client") {
      const key = projectsKey(session?.companyId||"local");
      try {
        const projs = JSON.parse(localStorage.getItem(key)||"[]");
        // Show projects they are assigned to
        const assigned = projs.find(p=>(p.members||[]).some(m=>m.email===session?.email)) || projs[0];
        return assigned||null;
      } catch { return null; }
    }
    return null;
  });
  // Company-scoped project key — each subscriber has their own isolated project list
  const companyProjectsKey = projectsKey(session?.companyId);
  const [projects,setProjects]=useState(()=>{
    try {
      const cid = session?.companyId || window._grokCompanyId || "";
      const deletedKey = "grok_deleted_demos_"+cid;
      const deletedDemos = (()=>{try{return JSON.parse(localStorage.getItem(deletedKey)||"[]");}catch{return [];}})();
      // Strip tombstones AND junk/test rows. The junk filter is centralized in
      // cleanProjects() — see isJunkProject for criteria.
      const scrub = arr => cleanProjects(arr.filter(p=>!deletedDemos.includes(String(p.id))));
      const keys = [
        projectsKey(cid),
        "grok_projects_" + cid,
        "grok_projects_local",
      ];
      for (const key of keys) {
        try {
          const s = localStorage.getItem(key);
          if (s) {
            const parsed = scrub(JSON.parse(s));
            if (parsed.length) return parsed;
          }
        } catch(e) {}
      }
      const filtered = scrub(PROJECTS||[]);
      if (filtered.length) return filtered;
      return [];
    } catch { return []; }
  });

  // ── Real-time persist — every project change saves instantly ──────────────
  useEffect(()=>{
    try {
      const cid = session?.companyId || window._grokCompanyId || "";
      const deletedKey = "grok_deleted_demos_"+cid;
      const deletedDemos = (()=>{try{return JSON.parse(localStorage.getItem(deletedKey)||"[]");}catch{return [];}})();
      // Same scrub on persist — junk in memory should not survive to disk.
      const filtered = cleanProjects(projects.filter(p=>!deletedDemos.includes(String(p.id))));
      localStorage.setItem(companyProjectsKey, JSON.stringify(filtered));
      PROJECTS = filtered;
      try { window._grokBroadcast?.("data_updated"); } catch(e) {}
    } catch(e) {}
  }, [projects]);
  const [leads,setLeads]=useState(()=>{
    const cid = session?.companyId || window._grokCompanyId || (()=>{try{return JSON.parse(localStorage.getItem("grok_auth_session")||"{}").companyId||"gcg_001";}catch{return "gcg_001";}})();
    try{const s=localStorage.getItem(leadsKey(cid));if(s!==null)return JSON.parse(s);}catch{}
    return [];
  });
  useEffect(()=>{
    const cid = session?.companyId || window._grokCompanyId || (()=>{try{return JSON.parse(localStorage.getItem("grok_auth_session")||"{}").companyId||"gcg_001";}catch{return "gcg_001";}})();
    try{localStorage.setItem(leadsKey(cid),JSON.stringify(leads));}catch(e){}
  }, [leads, session?.companyId]);
  const [showCOModal,setShowCOModal]=useState(false);
  const [navKey,setNavKey]=useState(0);
  const refreshNav = () => setNavKey(k=>k+1);
  const [showRFIModal,setShowRFIModal]=useState(false);
  const [showLienModal,setShowLienModal]=useState(false);

  // Refresh nav badges every 2s to reflect deletes/adds in child components
  useEffect(()=>{
    const interval = setInterval(()=>setNavKey(k=>k+1), 30000);
    return ()=>clearInterval(interval);
  }, []);

  const saveCO=co=>{COS=[co,...COS];setView("change-orders");};
  const saveRFI=rfi=>{RFIS=[rfi,...RFIS];setView("rfis");};

  // navKey forces nav badge re-evaluation after state changes
  const isTrade = session?.accountType==="trade";
  const navItems=[
    {id:"dashboard",        label:"Dashboard",           icon:ICONS.home,     section:"ops"},
    // Portfolio dashboard — for PMs / operators / nonprofit owners who span
    // multiple GCs. Also visible to GCG admin so the platform owner can demo it.
    ...(session?.isAdmin || ["pm","operator","owner","client"].includes(session?.accountType) ? [
      {id:"portfolio",      label:"Portfolio",          icon:ICONS.home,     section:"ops", bColor:T.purple||T.gold},
    ] : []),
    {id:"projects",         label:"Projects",            icon:ICONS.project,  section:"ops", badge:()=>projects.filter(p=>p.status==="active").length,bColor:T.green},
    {id:"leads",            label:isTrade?"Client Pipeline":"Lead Pipeline", icon:ICONS.rfi, section:"ops", badge:()=>{try{const ls=localStorage.getItem("grok_leads_"+(session?.companyId||"local"));const ll=ls?JSON.parse(ls):leads;return ll.filter(l=>l.status!=="won"&&l.status!=="lost").length;}catch{return leads.filter(l=>l.status!=="won"&&l.status!=="lost").length;}},bColor:T.blue},
    {id:"estimates",        label:"Estimate Requests",   icon:ICONS.cop,      section:"ops", badge:()=>EST_REQUESTS.filter(r=>r.status==="new").length,bColor:T.blue},
    ...(!isTrade ? [{id:"sub-manager", label:"Subcontractors", icon:ICONS.project, section:"ops", badge:()=>loadSubDB(session?.companyId).subs.length,bColor:T.teal}] : []),
    {id:"aria",             label:"ARIA PM Agent",     icon:ICONS.robot,    section:"ai",  bColor:T.gold},
    {id:"ai-agent",         label:"AI Estimate",        icon:ICONS.gold,     section:"ai",  bColor:T.blue},
    {id:"blueprints",       label:"Blueprint Review",    icon:ICONS.docs,     section:"ai",  badge:"→ Estimate",bColor:T.teal},
    {id:"change-orders",    label:"Change Orders",       icon:ICONS.co,       section:"field", badge:()=>{try{const cid=session?.companyId||window._grokCompanyId||"local";return JSON.parse(localStorage.getItem("grok_cos_global_"+cid)||"[]").filter(c=>c.status==="pending").length;}catch{return 0;}},bColor:T.red},
    {id:"rfis",             label:"RFIs",                icon:ICONS.rfi,      section:"field", badge:()=>{try{const cid=session?.companyId||window._grokCompanyId||"local";return JSON.parse(localStorage.getItem("grok_rfis_global_"+cid)||"[]").filter(r=>r.status!=="closed").length;}catch{return 0;}},bColor:T.amber},
    {id:"docs",             label:"Document Templates",  icon:ICONS.docs,     section:"field"},
    {id:"daily-reports",    label:"Daily Reports",       icon:ICONS.walk,     section:"field"},
    {id:"qr",               label:"QR Site Cards",       icon:ICONS.qr,       section:"field"},
    ...(!isTrade ? [{id:"quickbooks", label:"QuickBooks Export", icon:ICONS.docs, section:"field"}] : []),
    {id:"settings",         label:"Company Settings",    icon:ICONS.settings, section:"settings"},
    {id:"account-settings", label:"Account & Profile",   icon:ICONS.settings, section:"settings"},
    {id:"plan",             label:"My Plan",              icon:ICONS.gold,     section:"settings"},
    // Admin-only
    ...(session?.isAdmin ? [
      {id:"subscribers",    label:"Subscribers",      icon:ICONS.project,  section:"settings", bColor:T.gold},
    ] : []),
  ];

  const getLabel=n=>typeof n.badge==="function"?n.badge(navKey):n.badge;
  const sections=["ops","ai","field","settings"];
  const secLabels={ops:"Operations",ai:"AI Tools",field:"Field & Docs",settings:"Settings"};

  // Role-based view filtering — Architect and Client get focused views
  const sessionRole = getSessionRole(session);
  const isArchitect = session?.teamRole==="architect";
  const isClient = session?.teamRole==="client";

  // Architect and Client get a focused project view on login
  if ((isArchitect || isClient) && view==="dashboard" && activeProject) {
    return isArchitect
      ? <ArchitectProjectView proj={activeProject} session={session}/>
      : <ClientProjectView proj={activeProject} session={session}/>;
  }

  const renderView=()=>{
    switch(view){
      case "dashboard": return <Dashboard setView={setView} setActiveProject={(p)=>{setActiveProject(p);setView("project-detail");}} projects={projects} session={session}/>;
      case "projects": return <ProjectsView projects={projects} setProjects={setProjects} setView={setView} setActiveProject={setActiveProject} session={session}/>;
      case "project-detail": return activeProject?<ProjectDetail project={activeProject} projects={projects} setProjects={setProjects} setView={setView} setActiveProject={setActiveProject} session={session}/>:<ProjectsView projects={projects} setProjects={setProjects} setView={setView} setActiveProject={setActiveProject} session={session}/>;
      case "change-orders": return <ChangeOrdersView setCOModal={setShowCOModal} session={session}/>;
      case "rfis": return <RFIsView setRFIModal={setShowRFIModal} session={session}/>;
      case "cop": return <COPView session={session}/>;
      case "permits": return <PermitFlowView projects={projects} session={session}/>;
      case "qr": return <QRView session={session}/>;
      case "daily-reports": return <DailyReportsView session={session}/>;
      case "ai-agent": return <AIAgentView session={session}/>;

      case "terms": return <TermsOfServiceView onBack={()=>setView("dashboard")}/>;
      case "privacy": return <PrivacyPolicyView onBack={()=>setView("dashboard")}/>;
      case "sub-manager": return <SubManagerView session={session}/>;

      case "meetings": return <MeetingRecorderView projects={projects}/>;
      case "walkthrough-est": return <WalkthroughEstimatorView/>;
      case "aria": return <ARIAAgentView projects={projects} cos={COS} rfis={RFIS} leads={LEADS} session={session} setView={setView}/>;
      case "blueprints": return <BlueprintView projects={projects.filter(p=>!p.id?.startsWith("demo_"))} session={session} setView={setView}/>;
      case "embed": return <EmbedManagerView session={session}/>;
      case "subscribers": return <SubscriberManagement session={session}/>;
      case "pricing": return (session?.isSub||session?.accountType==="trade") ? <div style={{padding:40,textAlign:"center",color:T.muted}}><div style={{fontSize:32,marginBottom:12}}>🔒</div><div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:6}}>GC Pricing Calculator</div><div style={{fontSize:12}}>This tool is for GC accounts only.</div></div> : <PricingCalculatorView session={session}/>;
      case "bids": return <BidTemplatesView session={session}/>;
      case "estimates": return <EstimateRequestsView/>;
      case "leads": return canSee(session,"leads") ? <LeadsView setView={setView} setActiveProject={setActiveProject} projects={projects} setProjects={setProjects} session={session} leads={leads} setLeads={setLeads}/> : (<div style={{padding:40,textAlign:"center"}}><div style={{fontSize:28,marginBottom:10}}>🔒</div><div style={{fontSize:14,fontWeight:700,color:T.text}}>Lead Pipeline — Restricted</div><div style={{fontSize:12,color:T.muted,marginTop:8}}>Your role does not include lead pipeline access. Contact your GC owner.</div></div>);
      case "pm": return <ProjectManagerView projects={projects} setView={setView} setActiveProject={setActiveProject}/>;
      case "settings": return canSee(session,"settings") ? <CompanySettingsView session={session}/> : (<div style={{padding:40,textAlign:"center"}}><div style={{fontSize:28,marginBottom:10}}>🔒</div><div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:8}}>Settings — Owner & Admin Only</div><div style={{fontSize:12,color:T.muted}}>Your current role does not include company settings access.</div></div>);
      case "kb": return <KnowledgeBaseView/>;
      case "docs": return <DocsView setView={setView} session={session}/>;
      case "portfolio": return <PropertyManagerDashboardView session={session} setView={setView}/>;
      case "quickbooks": return canSee(session,"financials") ? <QuickBooksExportView projects={projects} session={session}/> : (<div style={{padding:40,textAlign:"center"}}><div style={{fontSize:28,marginBottom:10}}>🔒</div><div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:8}}>QuickBooks Export — Owner & Admin Only</div><div style={{fontSize:12,color:T.muted}}>Your current role does not include financials access.</div></div>);
      case "account-settings": return <AccountSettingsPage session={session} onLogout={onLogout}/>;
      case "plan": return <PlanPage session={session} setView={setView}/>;
      default: return <Dashboard setView={setView} setActiveProject={(p)=>{setActiveProject(p);setView("project-detail");}} projects={projects}/>;
    }
  };

  return (
    <div className="app-grid" style={{display:"grid",gridTemplateColumns:"200px 1fr",minHeight:"100vh",background:T.bg0,color:T.text,fontFamily:"system-ui,-apple-system,sans-serif",transition:"background 0.2s,color 0.2s"}}>
      <style>{`*{box-sizing:border-box;margin:0;padding:0}textarea,input,select{outline:none;color-scheme:light}::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:${T.subtle};border-radius:3px}@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}.mobile-nav{display:none}.sidebar-nav{display:flex}@media(max-width:768px){*{-webkit-tap-highlight-color:transparent;box-sizing:border-box}html,body{overflow-x:hidden;width:100%}.app-grid{grid-template-columns:1fr!important}.sidebar-nav{display:none!important}.mobile-nav{display:flex!important;position:fixed;bottom:0;left:0;right:0;z-index:100;background:${T.bg1};border-top:1px solid ${T.subtle};padding:6px 8px;gap:2px;padding-bottom:calc(env(safe-area-inset-bottom,0px) + 6px)}.main-content{padding-bottom:100px!important;padding-top:0!important;overflow-x:hidden!important;max-width:100vw!important}.view-pad{padding:12px 12px 100px!important}.stat-grid-4{grid-template-columns:1fr 1fr!important}.stat-grid-5{grid-template-columns:1fr 1fr!important}.two-col{grid-template-columns:1fr!important}.three-col{grid-template-columns:1fr!important}.cop-grid{grid-template-columns:1fr!important}.est-grid{grid-template-columns:1fr!important}.est-row{grid-template-columns:1fr!important}.est-form-wrap{padding:16px!important}.projects-grid{grid-template-columns:1fr 1fr!important}.kanban-board{flex-direction:column!important;overflow-x:hidden!important}.kanban-col{min-width:unset!important;width:100%!important}.bid-grid{grid-template-columns:1fr!important}.sub-roster-grid{grid-template-columns:1fr!important}input,textarea,select{font-size:16px!important}.mobile-hide{display:none!important}.mobile-full{grid-column:1/-1!important;width:100%!important}.mobile-pad{padding:12px!important}.mobile-stack{flex-direction:column!important;flex-wrap:wrap!important}.mobile-small-text{font-size:11px!important}.view-pad{padding:12px!important}.section-tabs{-webkit-overflow-scrolling:touch;overflow-x:auto!important;flex-wrap:nowrap!important;scrollbar-width:none}.section-tabs::-webkit-scrollbar{display:none}.topbar-title{font-size:13px!important}.card-pad{padding:12px!important}.lead-detail-grid{grid-template-columns:1fr!important}.pm-grid{grid-template-columns:1fr!important}.bid-section-nav{overflow-x:auto;flex-wrap:nowrap!important;scrollbar-width:none}.bid-section-nav::-webkit-scrollbar{display:none}.more-sheet{position:fixed;bottom:68px;left:0;right:0;background:${T.bg1};border-top:1px solid ${T.subtle};border-radius:16px 16px 0 0;padding:16px 12px 32px;z-index:99;max-height:88vh;overflow-y:auto;-webkit-overflow-scrolling:touch}.sub-grid{grid-template-columns:1fr!important}.leads-grid{grid-template-columns:1fr!important}.est-result-wrap{min-height:unset!important}.number-grid{grid-template-columns:1fr 1fr!important}/* Card and content polish */ .card-mobile{padding:12px!important;border-radius:10px!important} /* Lead pipeline mobile */ .lead-card{padding:12px!important}.lead-actions{flex-wrap:wrap!important;gap:6px!important} /* Estimate requests mobile */ .est-req-grid{grid-template-columns:1fr!important}.est-detail{display:none} /* Bid templates mobile */ .bid-template-grid{grid-template-columns:1fr 1fr!important} /* Sub portal mobile */ .sub-detail-grid{grid-template-columns:1fr!important} /* PM view mobile */ .pm-chat-input{font-size:16px!important} /* Project detail mobile */ .project-tabs{overflow-x:auto!important;flex-wrap:nowrap!important;scrollbar-width:none} .project-tabs::-webkit-scrollbar{display:none} /* Typography scale down */ h1,h2{font-size:18px!important}.section-title{font-size:16px!important} /* Buttons full width on mobile forms */ .form-submit-btn{width:100%!important;justify-content:center!important} /* Hide non-essential desktop columns */ .desktop-only{display:none!important} /* Sticky topbar */ .topbar{position:sticky!important;top:0!important;z-index:50!important}.sig-grid{grid-template-columns:1fr!important}.tg,.terms-grid{grid-template-columns:1fr!important}.price-table td{padding:8px 0!important}.modal-wrap{padding:0!important}.modal-inner{width:100%!important;max-width:100vw!important;border-radius:16px 16px 0 0!important;position:fixed!important;bottom:0!important;max-height:92vh!important;overflow-y:auto!important}.bp-viewer{height:280px!important}.thread-input textarea{font-size:16px!important}.thread-msg{max-width:88%!important}.info-grid{grid-template-columns:1fr 1fr!important}.co-detail-grid,.rfi-detail-grid{grid-template-columns:1fr!important}.permit-grid{grid-template-columns:1fr!important}.gantt-wrap{overflow-x:auto!important;-webkit-overflow-scrolling:touch!important}.dash-stats{grid-template-columns:1fr 1fr!important}.chat-input-row{padding-bottom:calc(env(safe-area-inset-bottom,0px) + 80px)!important}}
`}</style>

      {/* Sidebar */}
      <div className="sidebar-nav" style={{background:T.bg1,borderRight:`1px solid ${T.subtle}`,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <div style={{padding:"12px 14px 10px",borderBottom:`1px solid ${T.subtle}`,display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:34,height:34,borderRadius:9,background:T.gold,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <Ic d={ICONS.home} size={17} fill style={{color:T.bg0}}/>
          </div>
          <div style={{minWidth:0}}>
            <div style={{fontSize:14,fontWeight:800,color:T.text,letterSpacing:"-0.01em",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{session?.companyName || "Grok Field Ops"}</div>
            {(() => {
              // Render the logged-in company's own CSLB license from Company Settings.
              // Fallback: company name only, no license line (a new signup without a
              // license, or the demo account, gets no license row here).
              const raw = (session?.cslb || session?.cslb_num || "").toString().trim();
              if (!raw) return null;
              const label = /^cslb/i.test(raw) ? raw : "CSLB #" + raw.replace(/^#/, "");
              return <div style={{fontSize:9,color:T.muted,letterSpacing:"0.1em",textTransform:"uppercase",marginTop:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{label}</div>;
            })()}
          </div>
        </div>

        <nav style={{flex:1,padding:"10px 7px",overflowY:"auto"}}>
          {sections.map(sec=>{
            const items=navItems.filter(n=>n.section===sec);
            return (
              <div key={sec}>
                <div style={{fontSize:9,color:T.muted,letterSpacing:"0.12em",textTransform:"uppercase",fontWeight:700,padding:"0 10px 7px",marginTop:sec==="main"?4:14}}>{secLabels[sec]}</div>
                {items.map(n=>{
                  const badgeVal=getLabel(n);
                  const isActive=view===n.id;
                  return (
                    <button key={n.id} onClick={()=>n.id==='__more__'?setMobileMenuOpen(p=>!p):setView(n.id)} style={{display:"flex",alignItems:"center",gap:9,width:"100%",padding:"6px 10px",margin:"1px 0",borderRadius:7,cursor:"pointer",border:"none",background:isActive?n.id==="ai-agent"?`linear-gradient(135deg,${T.purple}22,${T.blue}22)`:T.gold:"transparent",color:isActive?n.id==="ai-agent"?T.purple:T.bg0:T.muted,fontSize:13,fontWeight:isActive?700:500,transition:"all 0.15s",textAlign:"left",outline:isActive&&n.id==="ai-agent"?`1px solid ${T.purple}44`:"none"}}>
                      <Ic d={n.icon} size={14} style={{opacity:isActive?1:0.7,color:isActive&&n.id==="ai-agent"?T.purple:undefined}}/>
                      <span style={{flex:1}}>{n.label}</span>
                      {badgeVal&&<span style={{background:isActive&&n.id!=="ai-agent"?"rgba(0,0,0,0.25)":n.bColor,color:isActive&&n.id!=="ai-agent"?T.bg0:"white",fontSize:9,fontWeight:800,padding:"1px 6px",borderRadius:10,minWidth:18,textAlign:"center"}}>{badgeVal}</span>}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div style={{padding:"6px 14px 4px",borderTop:`1px solid ${T.subtle}`,display:"flex",flexWrap:"wrap",gap:"3px 10px"}}>
        {[["⌘1","Home"],["⌘2","Projects"],["⌘3","Estimates"],["⌘K","KB"],["⌘P","Pricing"]].map(([k,l])=>(
          <div key={k} style={{fontSize:9,color:T.muted,opacity:0.6}}><span style={{fontFamily:"monospace",background:T.bg3,padding:"1px 4px",borderRadius:3}}>{k}</span> {l}</div>
        ))}
      </div>
      <div style={{padding:"12px 14px",borderTop:`1px solid ${T.subtle}`,display:"flex",alignItems:"center",gap:9}}>
          <div style={{width:30,height:30,borderRadius:"50%",background:T.gold,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:T.bg0,flexShrink:0}}>GC</div>
          <div>
            <div style={{fontSize:12,fontWeight:600,color:T.text}}>Grok Construction</div>
            <div style={{fontSize:10,color:T.muted}}>San Mateo, CA</div>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="main-content" style={{background:T.bg0,overflowY:"auto",display:"flex",flexDirection:"column",paddingBottom:isMobile?"100px":0}}>
        <div style={{background:T.bg1,borderBottom:`1px solid ${T.subtle}`,display:"flex",alignItems:"center",gap:10,padding:"8px 20px",flexShrink:0,position:"sticky",top:0,zIndex:20,flexWrap:"wrap"}}>
          <div style={{flex:1,display:"flex",alignItems:"center",gap:14,minWidth:0,overflow:"hidden"}}>
          {session&&session.plan!=="sub"&&<>
            <span style={{fontSize:12,fontWeight:700,color:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:180}}>{session.companyName}</span>
            {session.moneyBackDeadline&&new Date(session.moneyBackDeadline)>new Date()&&!session.isAdmin&&<span style={{fontSize:10,color:T.green,marginLeft:6,fontWeight:700,whiteSpace:"nowrap"}}>✓ 15-day guarantee</span>}
            {TIERS[session.plan]&&TIERS[session.plan].limits.estimates<9999&&(
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <UsageBadge type="estimates" session={session}/>
                <UsageBadge type="pm_chats" session={session}/>
                <UsageBadge type="video" session={session}/>
                <UsageBadge type="meetings" session={session}/>
                <UsageBadge type="blueprint" session={session}/>
              </div>
            )}
          </>}
        </div>
          <div style={{display:"flex",gap:6,flexShrink:0,alignItems:"center"}}>
            {/* Sync status indicator */}
            {session?.companyId&&session.companyId!=="local"&&(
              <div style={{display:"flex",alignItems:"center",gap:4,fontSize:9,color:syncStatus==="connected"?T.green:syncStatus==="syncing"?T.amber:T.muted,background:T.bg2,borderRadius:20,padding:"3px 8px",border:`1px solid ${syncStatus==="connected"?T.green:T.subtle}30`}}>
                <div style={{width:5,height:5,borderRadius:"50%",background:syncStatus==="connected"?T.green:syncStatus==="syncing"?T.amber:T.muted,animation:syncStatus==="syncing"?"pulse 1s infinite":"none"}}/>
                {syncStatus==="connected"?"Synced":syncStatus==="syncing"?"Syncing…":"Offline"}
              </div>
            )}
            {session&&<button onClick={()=>setView("account-settings")} style={{fontSize:11,color:T.muted,background:"transparent",border:`1px solid ${T.subtle}`,borderRadius:6,padding:"5px 12px",cursor:"pointer",whiteSpace:"nowrap"}} onMouseEnter={e=>e.currentTarget.style.color=T.gold} onMouseLeave={e=>e.currentTarget.style.color=T.muted}>⚙ Account</button>}
            {session&&onLogout&&<button onClick={onLogout} style={{fontSize:11,color:T.muted,background:"transparent",border:`1px solid ${T.subtle}`,borderRadius:6,padding:"5px 12px",cursor:"pointer",whiteSpace:"nowrap"}} onMouseEnter={e=>e.currentTarget.style.color=T.red} onMouseLeave={e=>e.currentTarget.style.color=T.muted}>Sign Out</button>}
          </div>
        </div>
        {/* Topbar */}
        <div style={{background:T.bg1,borderBottom:`1px solid ${T.subtle}`,padding:"0 22px",height:44,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:10,flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{fontSize:12,color:T.muted}}>
            {{dashboard:new Date().toLocaleDateString("en-US",{weekday:"long",year:"numeric",month:"long",day:"numeric"}),projects:"All Projects","project-detail":activeProject?.name||"Project",docs:"Document Kit — All 15 Documents",
              "ai-agent":"AI Estimate — Quick job estimate",
              "change-orders":"Change Orders","rfis":"Requests for Information",
              cop:"Certificate of Payment",qr:"QR Site Cards",}[view]||""}
            </div>
            <span style={{fontSize:9,color:T.muted,background:T.bg3,borderRadius:20,padding:"1px 7px",letterSpacing:"0.05em"}}>v9</span>
          </div>
          <div style={{display:"flex",gap:7}}>
            <span className="desktop-only"><Btn ghost small onClick={()=>setView("projects")} style={{color:T.green}}><Ic d={ICONS.plus} size={12}/>New Project</Btn></span>
            <Btn ghost small onClick={()=>setShowCOModal(true)}><Ic d={ICONS.co} size={12}/>New CO</Btn>
            <Btn ghost small onClick={()=>setShowRFIModal(true)}><Ic d={ICONS.rfi} size={12}/>New RFI</Btn>
            <Btn ghost small onClick={()=>setShowLienModal(true)} style={{color:T.teal}}>Lien Waiver</Btn>
            <Btn primary small onClick={()=>setView("ai-agent")} style={{background:`linear-gradient(135deg,${T.purple},${T.blue})`,color:"white"}}>
              <Ic d={ICONS.robot} size={12}/>AI Agent
            </Btn>
          </div>
        </div>
        {(()=>{ try { return renderView(); } catch(e) { return <div style={{padding:40,textAlign:"center"}}><div style={{fontSize:40,marginBottom:12}}>⚠️</div><div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:8}}>Something went wrong</div><div style={{fontSize:11,color:T.red,background:T.redBg,borderRadius:8,padding:"10px 16px",fontFamily:"monospace",textAlign:"left",maxWidth:600,margin:"0 auto"}}>{e.message}</div><div style={{fontSize:11,color:T.muted,marginTop:12}}>Please report this error</div></div>; }})()}

        {/* Onboarding modal — first login only */}
        {showOnboarding&&<OnboardingModal session={session} onDone={()=>{
          localStorage.setItem("grok_onboarded_"+session?.companyId,"1");
          setShowOnboarding(false);
        }} setView={setView}/>}
      </div>

      {showCOModal&&<COModal onClose={()=>setShowCOModal(false)} onSave={saveCO} session={session}/>}
      {showRFIModal&&<RFIModal onClose={()=>setShowRFIModal(false)} onSave={saveRFI} session={session}/>}
      {showLienModal&&<LienWaiverModal onClose={()=>setShowLienModal(false)} session={session}/>}

      {/* Mobile Bottom Nav */}
      {/* Mobile More Menu */}
      {mobileMenuOpen&&(
        <div onClick={()=>setMobileMenuOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:99,display:"flex",alignItems:"flex-end"}}>
          <div onClick={e=>e.stopPropagation()} style={{background:T.bg1,borderRadius:"16px 16px 0 0",padding:"20px 16px 32px",width:"100%",display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
            <div style={{gridColumn:"1/-1",fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:4}}>More</div>
            {[
              {id:"leads",label:"Leads",icon:ICONS.rfi,badge:()=>{try{const ls=localStorage.getItem("grok_leads_"+(session?.companyId||"local"));const ll=ls?JSON.parse(ls):leads;return ll.filter(l=>l.status!=="won"&&l.status!=="lost").length;}catch{return leads.filter(l=>l.status!=="won"&&l.status!=="lost").length;}},bc:T.blue},
              {id:"estimates",label:"Estimates",icon:ICONS.cop,badge:()=>EST_REQUESTS.filter(r=>r.status==="new").length,bc:T.blue},
              {id:"sub-manager",label:"Subcontractors",icon:ICONS.project},
              
              {id:"blueprints",label:"Blueprints",icon:ICONS.docs},
              {id:"embed",label:"Website Embed",icon:ICONS.docs},
              {id:"bids",label:"Bid Templates",icon:ICONS.docs},
              {id:"pm",label:"AI PM",icon:ICONS.robot,bc:T.purple},
              {id:"ai-agent",label:"AI Estimate",icon:ICONS.robot,bc:T.blue},
              {id:"settings",label:"Company Settings",icon:ICONS.settings},
              {id:"kb",label:"AI Knowledge Base",icon:ICONS.settings},
              {id:"cop",label:"Billing",icon:ICONS.cop},
              {id:"cos",label:"Change Orders",icon:ICONS.co,bc:T.amber},
              {id:"rfis",label:"RFIs",icon:ICONS.rfi},
              {id:"docs",label:"Doc Kit",icon:ICONS.docs},
              {id:"qr",label:"QR Cards",icon:ICONS.qr},
              {id:"daily-reports",label:"Daily Reports",icon:ICONS.walk},
            ].map(n=>{
              const isActive=view===n.id;
              const bv=typeof n.badge==="function"?n.badge(navKey):0;
              return (
                <button key={n.id} onClick={()=>{setView(n.id);setMobileMenuOpen(false);}}
                  style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:5,padding:"12px 8px",borderRadius:10,border:"none",background:isActive?T.gold+"18":T.bg2,cursor:"pointer",position:"relative"}}>
                  {bv>0&&<span style={{position:"absolute",top:6,right:6,background:n.bc||T.amber,color:"white",fontSize:8,fontWeight:800,padding:"1px 4px",borderRadius:10}}>{bv}</span>}
                  <Ic d={n.icon} size={20} style={{color:isActive?T.gold:T.muted}}/>
                  <span style={{fontSize:9,fontWeight:600,color:isActive?T.gold:T.muted,textAlign:"center",lineHeight:1.2}}>{n.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* MOBILE BOTTOM NAV */}
      <div className="mobile-nav" style={{
        position:"fixed",bottom:0,left:0,right:0,zIndex:100,
        background:T.bg1,borderTop:"1px solid "+T.subtle,
        paddingBottom:"env(safe-area-inset-bottom,0px)",
        display:"flex",alignItems:"stretch",
      }}>
        {[
          {id:"dashboard",  label:"Home",     emoji:"🏠"},
          {id:"projects",   label:"Projects", emoji:"🏗",  badge:()=>projects.filter(p=>p.status==="active").length},
      
          {id:"blueprints", label:"Blueprints",emoji:"📐"},
          {id:"__more__",   label:"More",     emoji:"☰",
            badge:()=>COS.filter(co=>co.status==="pending").length+RFIS.filter(r=>r.status!=="closed").length},
        ].map(n=>{
          const isActive = n.id==="__more__" ? mobileMenuOpen
            : view===n.id || (view==="project-detail"&&n.id==="projects");
          const bv = typeof n.badge==="function" ? n.badge(navKey) : 0;
          return (
            <button key={n.id}
              onClick={()=>n.id==="__more__"?setMobileMenuOpen(p=>!p):(setView(n.id),setMobileMenuOpen(false))}
              style={{
                flex:1,display:"flex",flexDirection:"column",alignItems:"center",
                justifyContent:"center",gap:2,padding:"8px 2px 10px",
                border:"none",background:"transparent",cursor:"pointer",
                position:"relative",transition:"all 0.12s",
                borderTop:isActive?"2.5px solid "+T.gold:"2.5px solid transparent",
              }}>
              {bv>0&&(
                <div style={{
                  position:"absolute",top:5,left:"50%",transform:"translateX(4px)",
                  background:T.red,color:"white",fontSize:9,fontWeight:800,
                  padding:"0 4px",borderRadius:10,minWidth:16,height:16,
                  display:"flex",alignItems:"center",justifyContent:"center",
                }}>{bv}</div>
              )}
              <span style={{fontSize:22,lineHeight:1,filter:isActive?"none":"grayscale(30%) opacity(0.65)",marginBottom:1}}>{n.emoji}</span>
              <span style={{fontSize:10,fontWeight:isActive?700:400,color:isActive?T.gold:T.muted}}>{n.label}</span>
            </button>
          );
        })}
      </div>

      {/* MORE DRAWER — full screen overlay */}
      {mobileMenuOpen&&(
        <div style={{position:"fixed",inset:0,zIndex:200,display:"flex",flexDirection:"column",justifyContent:"flex-end"}}
          onClick={()=>setMobileMenuOpen(false)}>
          <div onClick={e=>e.stopPropagation()} style={{
            background:T.bg1,borderRadius:"20px 20px 0 0",
            maxHeight:"88vh",overflowY:"auto",WebkitOverflowScrolling:"touch",
            paddingBottom:"env(safe-area-inset-bottom,16px)",
          }}>
            <div style={{width:36,height:4,background:T.subtle,borderRadius:2,margin:"14px auto 0"}}/>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 20px 8px"}}>
              <div style={{fontSize:17,fontWeight:700,color:T.text}}>All Features</div>
              <button onClick={()=>setMobileMenuOpen(false)} style={{width:32,height:32,borderRadius:"50%",background:T.bg2,border:"none",fontSize:16,cursor:"pointer",color:T.muted}}>✕</button>
            </div>

            {/* Quick action row */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,padding:"8px 16px 16px"}}>
              {[
                {label:"Active Jobs",  val:projects.filter(p=>p.status==="active").length,  color:T.green, emoji:"🏗", go:"projects"},
                {label:"Pending COs",  val:COS.filter(c=>c.status==="pending").length,       color:T.amber, emoji:"📝", go:"change-orders"},
                {label:"Open RFIs",    val:RFIS.filter(r=>r.status!=="closed").length,       color:T.blue,  emoji:"❓", go:"rfis"},
              ].map(s=>(
                <div key={s.label} onClick={()=>{setView(s.go);setMobileMenuOpen(false);}}
                  style={{background:T.bg2,borderRadius:14,padding:"14px 10px",textAlign:"center",cursor:"pointer"}}>
                  <div style={{fontSize:24,marginBottom:4}}>{s.emoji}</div>
                  <div style={{fontSize:22,fontWeight:700,color:s.color,lineHeight:1}}>{s.val}</div>
                  <div style={{fontSize:10,color:T.muted,marginTop:3,lineHeight:1.3}}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Nav items */}
            {[
              {section:"PROJECTS & FIELD", color:T.gold, items:[
                {id:"dashboard",     emoji:"🏠", label:"Dashboard",         desc:"Alerts & overview"},
                {id:"projects",      emoji:"🏗", label:"All Projects",      desc:projects.length+" total jobs"},
                {id:"leads",         emoji:"🎯", label:"Lead Pipeline",     desc:"New opportunities"},
                {id:"sub-manager",    emoji:"👷", label:"Subcontractors",    desc:"Roster & invitations"},
              ]},
              {section:"DOCUMENTS & FINANCE", color:T.green, items:[
                {id:"change-orders", emoji:"📝", label:"Change Orders",     desc:COS.filter(c=>c.status==="pending").length+" pending approval"},
                {id:"rfis",          emoji:"❓",      label:"RFIs",             desc:RFIS.filter(r=>r.status!=="closed").length+" open"},
                {id:"ai-agent",      emoji:"💰", label:"Pricing Calculator",desc:"Inside AI Tools → Calc & Proposal tab"},
                {id:"estimates",     emoji:"📋", label:"Estimate Requests", desc:"Client bid requests"},
                {id:"cop",           emoji:"🧾", label:"Certificate of Payment",desc:"Progress billing"},
              ]},
              {section:"AI & TOOLS", color:T.purple, items:[
                {id:"ai-agent",      emoji:"✨",      label:"AI Tools",          desc:"Estimates, blueprints, meetings, video"},
                {id:"blueprints",    emoji:"📐", label:"Blueprint Review",  desc:"AI scope from drawings"},
                {id:"permits",       emoji:"🏛", label:"Permit Manager",    desc:"Track & schedule"},
                {id:"docs",          emoji:"📁", label:"Document Kit",      desc:"Templates & forms"},
              ]},
              {section:"SETTINGS", color:T.muted, items:[
                {id:"settings",      emoji:"⚙️",      label:"Company Settings",  desc:"Profile, team, billing"},
                {id:"kb",            emoji:"🧠", label:"AI Knowledge Base",  desc:"Train your AI"},
              ]},
            ].map(group=>(
              <div key={group.section} style={{marginBottom:4}}>
                <div style={{padding:"8px 20px 6px",fontSize:10,fontWeight:700,color:group.color,textTransform:"uppercase",letterSpacing:"0.12em"}}>{group.section}</div>
                <div style={{padding:"0 12px"}}>
                  {group.items.map(item=>{
                    const active = view===item.id;
                    return (
                      <button key={item.id}
                        onClick={()=>{setView(item.id);setMobileMenuOpen(false);}}
                        style={{
                          width:"100%",display:"flex",alignItems:"center",gap:14,
                          padding:"13px 12px",borderRadius:12,border:"none",
                          background:active?group.color+"15":"transparent",
                          cursor:"pointer",marginBottom:2,textAlign:"left",
                        }}>
                        <span style={{fontSize:24,width:34,textAlign:"center",flexShrink:0}}>{item.emoji}</span>
                        <div style={{flex:1}}>
                          <div style={{fontSize:14,fontWeight:active?700:500,color:active?group.color:T.text}}>{item.label}</div>
                          <div style={{fontSize:11,color:T.muted,marginTop:1}}>{item.desc}</div>
                        </div>
                        {active&&<span style={{fontSize:14,color:group.color}}>✓</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Sign out */}
            <div style={{padding:"12px 16px 8px",borderTop:"1px solid "+T.subtle,marginTop:8}}>
              <button onClick={()=>{onLogout();setMobileMenuOpen(false);}}
                style={{width:"100%",padding:"15px",borderRadius:14,border:"1px solid "+T.subtle,background:T.bg2,color:T.muted,fontSize:14,fontWeight:500,cursor:"pointer"}}>
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function saveSession(s){
  try{localStorage.setItem("grok_auth_session",JSON.stringify(s));}catch(e){}
  if(s?.companyId){window._grokCompanyKey=settingsKey(s.companyId);window._grokCompanyId=s.companyId;}
}



// ─── ROLE CUSTOMIZER TAB ─────────────────────────────────────────────────────
// Lets Owner/Admin customize what each role can see and do — per company
// Stored in company settings so each GC has their own role config
function RoleCustomizerTab({session}) {
  const STORE_KEY = "grok_role_overrides_" + (session?.companyId||"local");
  const [overrides, setOverrides] = React.useState(()=>{
    try{return JSON.parse(localStorage.getItem(STORE_KEY)||"{}");}catch{return {};}
  });
  const [saved, setSaved] = React.useState(false);

  // Merge overrides into base TEAM_ROLES for display
  const effectiveRoles = Object.fromEntries(
    Object.entries(TEAM_ROLES).map(([key, role])=>[
      key,
      {
        ...role,
        canSee: {...role.canSee, ...(overrides[key]?.canSee||{})},
        canEdit: {...role.canEdit, ...(overrides[key]?.canEdit||{})},
      }
    ])
  );

  const toggle = (roleKey, section, perm) => {
    // Owner and Admin cannot be restricted
    if (roleKey==="owner"||roleKey==="admin") return;
    // Margin, financials, team — always locked regardless of customization
    if (section==="canSee"&&(perm==="margin"||perm==="financials")) return;
    if (section==="canEdit"&&perm==="team") return;

    setOverrides(prev=>{
      const current = prev[roleKey] || {};
      const currentSection = current[section] || {};
      const baseVal = TEAM_ROLES[roleKey]?.[section]?.[perm] || false;
      const overrideVal = currentSection[perm];
      const currentVal = overrideVal !== undefined ? overrideVal : baseVal;
      return {
        ...prev,
        [roleKey]: {
          ...current,
          [section]: {...currentSection, [perm]: !currentVal}
        }
      };
    });
    setSaved(false);
  };

  const save = () => {
    try{localStorage.setItem(STORE_KEY, JSON.stringify(overrides));}catch(e){}
    setSaved(true);
    setTimeout(()=>setSaved(false), 2000);
  };

  const reset = (roleKey) => {
    if (!true) return;
    setOverrides(prev=>{
      const next = {...prev};
      delete next[roleKey];
      return next;
    });
    setSaved(false);
  };

  const EDITABLE_ROLES = ["pm","estimator","field","architect","client","viewer"];
  const SEE_PERMS = [
    {key:"financials", label:"Finance Tab", locked:true, lockReason:"Always Owner/Admin only"},
    {key:"margin",     label:"Margin/Profit", locked:true, lockReason:"Always Owner/Admin only"},
    {key:"subBids",    label:"Sub Bids"},
    {key:"leads",      label:"Lead Pipeline"},
    {key:"settings",   label:"Company Settings"},
    {key:"kb",         label:"AI Knowledge Base"},
  ];
  const EDIT_PERMS = [
    {key:"projects",   label:"Edit Projects"},
    {key:"cos",        label:"Edit Change Orders"},
    {key:"rfis",       label:"Edit RFIs"},
    {key:"subs",       label:"Manage Subs"},
    {key:"budget",     label:"Edit Budget"},
    {key:"team",       label:"Manage Team", locked:true, lockReason:"Always Owner/Admin only"},
  ];

  return (
    <div>
      <div style={{marginBottom:16}}>
        <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:4}}>Role Permissions</div>
        <div style={{fontSize:12,color:T.muted,lineHeight:1.7}}>
          Customize what each role can see and do in your company. 
          <strong style={{color:T.amber}}> Finance, Margin, and Team Management</strong> are always restricted to Owner and Admin — these cannot be changed.
        </div>
      </div>

      {EDITABLE_ROLES.map(roleKey=>{
        const role = effectiveRoles[roleKey];
        const base = TEAM_ROLES[roleKey];
        const hasOverrides = overrides[roleKey] && (
          Object.keys(overrides[roleKey]?.canSee||{}).length > 0 ||
          Object.keys(overrides[roleKey]?.canEdit||{}).length > 0
        );

        return (
          <div key={roleKey} style={{background:T.bg1,border:"1px solid "+(hasOverrides?role.color+"40":T.subtle),borderRadius:10,marginBottom:12,overflow:"hidden"}}>
            {/* Role header */}
            <div style={{padding:"10px 16px",background:T.bg2,borderBottom:"1px solid "+T.subtle,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:10,height:10,borderRadius:"50%",background:role.color,flexShrink:0}}/>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>{role.label}</div>
                {hasOverrides&&<span style={{fontSize:9,fontWeight:700,background:role.color+"20",color:role.color,borderRadius:8,padding:"1px 7px"}}>CUSTOMIZED</span>}
              </div>
              {hasOverrides&&(
                <button onClick={()=>reset(roleKey)}
                  style={{fontSize:10,background:"none",color:T.muted,border:"1px solid "+T.subtle,borderRadius:6,padding:"3px 8px",cursor:"pointer"}}>
                  Reset to Default
                </button>
              )}
            </div>

            <div style={{padding:"12px 16px"}}>
              {/* Can See */}
              <div style={{marginBottom:12}}>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Can View</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {SEE_PERMS.map(perm=>{
                    const isOn = role.canSee[perm.key];
                    const isOverridden = overrides[roleKey]?.canSee?.[perm.key] !== undefined;
                    return (
                      <button key={perm.key}
                        onClick={()=>toggle(roleKey,"canSee",perm.key)}
                        disabled={perm.locked}
                        title={perm.locked?perm.lockReason:"Click to toggle"}
                        style={{display:"flex",alignItems:"center",gap:5,padding:"5px 10px",borderRadius:8,cursor:perm.locked?"not-allowed":"pointer",
                          border:"1px solid "+(isOn?role.color+"60":T.subtle),
                          background:isOn?role.color+"15":T.bg2,
                          opacity:perm.locked?0.5:1,
                          transition:"all 0.15s"}}>
                        <span style={{fontSize:10,fontWeight:700,color:isOn?role.color:T.muted}}>{isOn?"✓":"✗"}</span>
                        <span style={{fontSize:10,color:isOn?role.color:T.muted}}>{perm.label}</span>
                        {perm.locked&&<span style={{fontSize:8,color:T.muted}}>🔒</span>}
                        {isOverridden&&!perm.locked&&<span style={{fontSize:7,color:role.color,fontWeight:800}}>CHANGED</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Can Edit */}
              <div>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Can Edit / Create</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {EDIT_PERMS.map(perm=>{
                    const isOn = role.canEdit[perm.key];
                    const isOverridden = overrides[roleKey]?.canEdit?.[perm.key] !== undefined;
                    return (
                      <button key={perm.key}
                        onClick={()=>toggle(roleKey,"canEdit",perm.key)}
                        disabled={perm.locked}
                        title={perm.locked?perm.lockReason:"Click to toggle"}
                        style={{display:"flex",alignItems:"center",gap:5,padding:"5px 10px",borderRadius:8,cursor:perm.locked?"not-allowed":"pointer",
                          border:"1px solid "+(isOn?T.blue+"60":T.subtle),
                          background:isOn?T.blue+"12":T.bg2,
                          opacity:perm.locked?0.5:1,
                          transition:"all 0.15s"}}>
                        <span style={{fontSize:10,fontWeight:700,color:isOn?T.blue:T.muted}}>{isOn?"✓":"✗"}</span>
                        <span style={{fontSize:10,color:isOn?T.blue:T.muted}}>{perm.label}</span>
                        {perm.locked&&<span style={{fontSize:8,color:T.muted}}>🔒</span>}
                        {isOverridden&&!perm.locked&&<span style={{fontSize:7,color:T.blue,fontWeight:800}}>CHANGED</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Save button */}
      <div style={{display:"flex",gap:8,alignItems:"center",marginTop:8}}>
        <Btn primary onClick={save} style={{background:saved?T.green:T.gold,color:T.bg0}}>
          {saved?"✓ Saved!":"Save Role Permissions"}
        </Btn>
        <div style={{fontSize:10,color:T.muted,lineHeight:1.6}}>
          Changes apply to all team members immediately. GC Owner and Admin always keep full access.
        </div>
      </div>
    </div>
  );
}


// ─── ARCHITECT PROJECT VIEW ────────────────────────────────────────────────────
// What an Architect team member sees — focused on technical docs
// Blueprints, RFIs, Drawings, Specifications — no financials, no subs, no leads
function ArchitectProjectView({proj, session}) {
  const [tab, setTab] = React.useState("rfis");
  const projRFIs = (()=>{try{const cid=session?.companyId||window._grokCompanyId||"local";return JSON.parse(localStorage.getItem("grok_rfis_global_"+cid)||"[]");}catch{return RFIS;}})();
  const projCOs  = (()=>{try{const cid=session?.companyId||window._grokCompanyId||"local";const all=JSON.parse(localStorage.getItem("grok_cos_global_"+cid)||"[]");return all.length?all:COS;}catch{return COS;}})();

  const TABS = [
    {id:"rfis",        label:"RFIs",         badge:projRFIs.filter(r=>r.status!=="closed").length},
    {id:"submittals",  label:"Submittals"},
    {id:"blueprints",  label:"Drawings"},
    {id:"compliance",  label:"Code Compliance"},
    {id:"cos",         label:"Change Orders"},
    {id:"punchlist",   label:"Punch List"},
    {id:"dailylog",    label:"Daily Reports"},
    {id:"docs",        label:"Documents"},
    {id:"schedule",    label:"Schedule"},
    {id:"thread",      label:"Thread"},
  ];

  // Submittal state for architect
  const subKey = "grok_submittals_"+(proj?.id||"x");
  const [archSubmittals, setArchSubmittals] = React.useState(()=>{
    try{return JSON.parse(localStorage.getItem(subKey)||"[]");}catch{return [];}
  });
  const saveArchSubmittals = (u)=>{setArchSubmittals(u);try{localStorage.setItem(subKey,JSON.stringify(u));}catch(e){}}
  const SUB_STATUSES = ["submitted","under_review","approved","rejected","revision_required"];
  const SUB_STATUS_COLORS = {submitted:T.blue,under_review:T.amber,approved:T.green,rejected:T.red,revision_required:T.amber};
  const SUB_STATUS_LABELS = {submitted:"Submitted",under_review:"Under Review",approved:"✓ Approved",rejected:"✗ Rejected",revision_required:"Revision Required"};

  return (
    <div style={{padding:20,maxWidth:900,margin:"0 auto"}}>
      {/* Header */}
      <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:12,padding:"14px 18px",marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div>
            <div style={{fontSize:16,fontWeight:800,color:T.text,marginBottom:2}}>{proj?.name}</div>
            <div style={{fontSize:11,color:T.muted}}>{proj?.addr} · {proj?.type}</div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {[["Progress",(proj?.pct||0)+"%"],["Status",proj?.status||"Active"],["Your Role","Architect"]].map(([l,v])=>(
              <div key={l} style={{textAlign:"center",background:T.bg2,borderRadius:8,padding:"6px 12px"}}>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:1}}>{l}</div>
                <div style={{fontSize:12,fontWeight:700,color:l==="Your Role"?T.purple:T.text}}>{v}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{marginTop:10,background:T.bg2,borderRadius:6,height:6,overflow:"hidden"}}>
          <div style={{width:(proj?.pct||0)+"%",height:"100%",background:T.teal,borderRadius:6,transition:"width 0.5s"}}/>
        </div>
      </div>

      {/* Notice */}
      <div style={{background:T.purpleBg,border:"1px solid "+T.purple+"30",borderRadius:9,padding:"9px 14px",marginBottom:14,fontSize:11,color:T.purple,lineHeight:1.7}}>
        <strong>Architect View</strong> — You can review and respond to RFIs, view blueprints and drawings, see approved Change Orders, and sign documents assigned to you. Financial details and internal pricing are not visible.
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:4,marginBottom:14,overflowX:"auto",paddingBottom:2}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{padding:"7px 14px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",background:tab===t.id?T.purple:"transparent",color:tab===t.id?"white":T.muted,whiteSpace:"nowrap",transition:"all 0.15s"}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab==="rfis"&&(
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Requests for Information</div>
          {projRFIs.length===0?(
            <div style={{textAlign:"center",padding:"32px",color:T.muted,background:T.bg2,borderRadius:10}}>No open RFIs</div>
          ):projRFIs.map(rfi=>(
            <div key={rfi.id} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"14px 16px",marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>{rfi.id} — {rfi.subject}</div>
                <span style={{fontSize:10,fontWeight:700,background:rfi.status==="closed"?T.greenBg:T.amberBg,color:rfi.status==="closed"?T.green:T.amber,borderRadius:10,padding:"2px 8px"}}>{(rfi.status||"open").toUpperCase()}</span>
              </div>
              {rfi.desc&&<div style={{fontSize:11,color:T.muted,marginBottom:8,lineHeight:1.6}}>{rfi.desc}</div>}
              {rfi.status!=="closed"&&(
                <div style={{marginTop:8}}>
                  <div style={{fontSize:10,fontWeight:700,color:T.text,marginBottom:4}}>Architect Response</div>
                  <textarea defaultValue={rfi.arch_response||""} rows={3}
                    onBlur={e=>{const updated=RFIS.map(r=>r.id===rfi.id?{...r,arch_response:e.target.value,answer:e.target.value,status:"answered"}:r);RFIS=updated;}}
                    placeholder="Enter your technical response and code references..."
                    style={{width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,padding:"8px 10px",color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit"}}/>
                </div>
              )}
              <SignatureBlock
                label="Architect Signature" role="Architect"
                signData={rfi.arch_signature} canSign={true}
                signerEmail={session?.email}
                onSign={sig=>{const updated=RFIS.map(r=>r.id===rfi.id?{...r,arch_signature:sig}:r);RFIS=updated;}}
                onSaveToDoc={(sig,lbl)=>saveSignedDocToProject(proj,session,"RFI",rfi.id,rfi.id,sig,lbl)}
              />
            </div>
          ))}
        </div>
      )}
      {/* SUBMITTALS TAB */}
      {tab==="submittals"&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:T.text}}>Submittals</div>
              <div style={{fontSize:10,color:T.muted,marginTop:2}}>Shop drawings, product data, samples — review and approve before installation</div>
            </div>
            <Btn small primary onClick={()=>{
              const name=window.prompt("Submittal description (e.g. \"Kohler K-2210 Lavatory — Product Data\"):");
              if(!name) return;
              const cat=window.prompt("Category (Shop Drawing / Product Data / Sample / Mock-Up):");
              const spec=window.prompt("Spec section (e.g. 22 40 00):");
              saveArchSubmittals([{id:"sub_"+Date.now(),desc:name,category:cat||"Product Data",spec_section:spec||"",status:"submitted",date_submitted:new Date().toLocaleDateString(),submittedBy:"GC",notes:""},
                ...archSubmittals]);
            }}>+ New Submittal</Btn>
          </div>
          {archSubmittals.length===0?(
            <div style={{textAlign:"center",padding:"32px",color:T.muted,background:T.bg2,borderRadius:10,border:"2px dashed "+T.subtle}}>
              <div style={{fontSize:24,marginBottom:8}}>📋</div>
              <div style={{fontSize:12,fontWeight:600,color:T.text,marginBottom:4}}>No submittals yet</div>
              <div style={{fontSize:11}}>GC will submit shop drawings, product data, and samples here for your review and approval.</div>
            </div>
          ):archSubmittals.map(sub=>(
            <div key={sub.id} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 16px",marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:2}}>{sub.desc}</div>
                  <div style={{fontSize:10,color:T.muted}}>{sub.category}{sub.spec_section?" · Section "+sub.spec_section:""} · Submitted {sub.date_submitted}</div>
                </div>
                <span style={{fontSize:10,fontWeight:700,background:(SUB_STATUS_COLORS[sub.status]||T.muted)+"20",color:SUB_STATUS_COLORS[sub.status]||T.muted,borderRadius:10,padding:"2px 8px",flexShrink:0,marginLeft:8}}>
                  {SUB_STATUS_LABELS[sub.status]||sub.status}
                </span>
              </div>
              {sub.notes&&<div style={{fontSize:11,color:T.muted,marginBottom:8,lineHeight:1.5,background:T.bg2,borderRadius:6,padding:"6px 8px"}}>{sub.notes}</div>}
              <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:6}}>
                {[["approved","✓ Approve",T.green],["revision_required","Revise",T.amber],["rejected","✗ Reject",T.red]].map(([s,l,col])=>(
                  <button key={s} onClick={()=>saveArchSubmittals(archSubmittals.map(x=>x.id===sub.id?{...x,status:s,reviewed_date:new Date().toLocaleDateString()}:x))}
                    style={{fontSize:10,fontWeight:600,padding:"3px 10px",borderRadius:6,cursor:"pointer",border:"1px solid "+col+"50",background:sub.status===s?col+"20":T.bg2,color:sub.status===s?col:T.muted}}>
                    {l}
                  </button>
                ))}
                <button onClick={()=>{
                  const note=window.prompt("Add review note:");
                  if(note)saveArchSubmittals(archSubmittals.map(x=>x.id===sub.id?{...x,notes:note}:x));
                }} style={{fontSize:10,background:T.bg2,color:T.muted,border:"1px solid "+T.subtle,borderRadius:6,padding:"3px 10px",cursor:"pointer"}}>📝 Note</button>
                <button onClick={()=>{if(true)saveArchSubmittals(archSubmittals.filter(x=>String(x.id)!==String(sub.id)));}}
                  style={{fontSize:10,background:"none",color:T.muted,border:"none",cursor:"pointer",marginLeft:"auto"}}>✕</button>
              </div>
              {sub.status==="approved"&&(
                <SignatureBlock label="Architect Approval Signature" role="Architect"
                  signData={sub.arch_signature} canSign={true} signerEmail={session?.email}
                  onSign={sig=>saveArchSubmittals(archSubmittals.map(x=>x.id===sub.id?{...x,arch_signature:sig}:x))}
                  onSaveToDoc={(sig,lbl)=>saveSignedDocToProject(proj,session,"Submittal",sub.id,sub.desc,sig,lbl)}/>
              )}
            </div>
          ))}
        </div>
      )}

      {tab==="blueprints"&&<TradeBlueprintView proj={proj} session={session}/>}

      {tab==="compliance"&&(
        <div style={{padding:"0 4px"}}>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:4}}>🏛 Code Compliance</div>
            <div style={{fontSize:12,color:T.muted}}>ADA violations and code issues auto-flagged from your uploaded drawings. Upload a drawing → run Code Compliance Check → violations appear here with location and measurements.</div>
          </div>

          {(()=>{
            try {
              const key = "grok_flags_"+(proj?.id||"x");
              const allFlags = JSON.parse(localStorage.getItem(key)||"[]");
              const codeFlags = allFlags.filter(f=>f.adaType||f.codeRef||f.autoGenerated);

              if (codeFlags.length === 0) {
                // No blueprint reviewed — show project-type specific codes
                const projType = (proj?.type||proj?.name||"").toLowerCase();
                const isSRO = projType.includes("sro")||projType.includes("affordable")||projType.includes("senior");
                const isCommercial = projType.includes("commercial")||projType.includes("office")||projType.includes("retail");
                const isADU = projType.includes("adu")||projType.includes("garage");
                const typeLabel = isSRO?"SRO / Affordable Housing":isCommercial?"Commercial":isADU?"ADU":"Residential";

                const CODES = {
                  residential:[
                    {icon:"🚪",title:"Door Width",req:'32" min clear (36" preferred)',code:"CBC 11A-404 · ADA §404"},
                    {icon:"♿",title:"Accessible Route",req:'36" min path, ½" max threshold',code:"CBC 11A-402 · ADA §402"},
                    {icon:"🪜",title:"Ramp Slope",req:"1:12 max, 36\" wide, handrails both sides",code:"CBC 11A-405 · ADA §405"},
                    {icon:"🤲",title:"Grab Bars",req:'33"–36" AFF, blocking in new construction',code:"CBC 11A-609 · ADA §609"},
                    {icon:"⚡",title:"Title 24 Energy",req:"Insulation, glazing SHGC, LED lighting",code:"CA Title 24 Part 6"},
                    {icon:"🌿",title:"CalGreen",req:"Low-VOC paints, water-efficient fixtures, EV-ready",code:"CALGreen 2022"},
                    {icon:"🏗",title:"Seismic (Bay Area)",req:"SDC D — anchor bolts, shear walls",code:"CBC Ch.23 · ASCE 7"},
                    {icon:"🚿",title:"Bath Clearances",req:'30"×48" clear floor space at fixtures',code:"CBC 11A-603 · ADA §603"},
                  ],
                  sro:[
                    {icon:"📐",title:"SRO Unit Size Min",req:"150 sq ft min per SF Planning Code",code:"SF Planning Code §188"},
                    {icon:"🚪",title:"Accessible Units",req:"5% fully accessible, 2% hearing/vision",code:"CBC 11A-233 · SFBC"},
                    {icon:"🔥",title:"Fire Sprinklers",req:"Required in all SF SRO buildings",code:"SF Fire Code §903"},
                    {icon:"🧪",title:"Lead & Asbestos",req:"Testing required in pre-1978 buildings",code:"EPA RRP Rule · AHERA"},
                    {icon:"👷",title:"Prevailing Wage",req:"DIR registration required if publicly funded",code:"CA Labor Code §1720"},
                    {icon:"📋",title:"Tenant Notice",req:"7-day minimum notice before entry",code:"SF Admin Code §37.10A"},
                    {icon:"⚡",title:"Title 24 Energy",req:"Strict Bay Area climate zone compliance",code:"CA Title 24 Part 6"},
                    {icon:"💨",title:"Ventilation",req:"Operable window or mechanical vent per unit",code:"CBC §1203"},
                  ],
                  commercial:[
                    {icon:"♿",title:"Full ADA Compliance",req:"All areas accessible — path, parking, restrooms",code:"ADA 2010 · CBC 11B"},
                    {icon:"🚪",title:"Exit Requirements",req:"2 exits min >49 occ, panic hardware",code:"CBC §1006 · IBC §1006"},
                    {icon:"🔥",title:"Fire Suppression",req:"Sprinklers required >5,000 SF",code:"CBC §903 · NFPA 13"},
                    {icon:"🅿",title:"Accessible Parking",req:"1 per 25 spaces, 8' stall + 5' aisle",code:"ADA §502 · CBC 11B-502"},
                    {icon:"🪧",title:"Signage",req:"Braille + tactile, 60\" AFF, exit signs lit",code:"ADA §703 · CBC §1013"},
                    {icon:"⚡",title:"Title 24 Energy",req:"Lighting controls, HVAC efficiency",code:"CA Title 24 Part 6"},
                    {icon:"💨",title:"Ventilation",req:"15 CFM/person min fresh air",code:"CBC §1202 · ASHRAE 62.1"},
                    {icon:"🌿",title:"CalGreen Tier",req:"EV charging, cool roof, water -20%",code:"CALGreen 2022 §5"},
                  ],
                  adu:[
                    {icon:"📐",title:"ADU Size Limits",req:"Max 1,200 SF detached, 500 SF JADU",code:"CA Gov Code §65852.2"},
                    {icon:"🚪",title:"Separate Entrance",req:"Independent exterior entry required",code:"CA Gov Code §65852.2"},
                    {icon:"🔌",title:"Utility Connections",req:"Separate electrical panel required",code:"CA PUC · Local utility"},
                    {icon:"🔥",title:"Fire Sprinklers",req:"Required if main house has them",code:"CBC §903"},
                    {icon:"📏",title:"Setbacks",req:"4' rear + side min for detached ADU",code:"CA Gov Code · Local zoning"},
                    {icon:"⚡",title:"Title 24 Energy",req:"Separate energy compliance for ADU",code:"CA Title 24 Part 6"},
                    {icon:"🌿",title:"CalGreen",req:"Low-VOC, water efficiency, EV-ready",code:"CALGreen 2022"},
                    {icon:"🏗",title:"Seismic",req:"Shear walls, anchor bolts required",code:"CBC Chapter 23"},
                  ],
                };
                const codes = isSRO?CODES.sro:isCommercial?CODES.commercial:isADU?CODES.adu:CODES.residential;

                return (
                  <div>
                    <div style={{background:T.amberBg,border:`1px solid ${T.amber}30`,borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",gap:10,alignItems:"flex-start"}}>
                      <span style={{fontSize:20,flexShrink:0}}>📐</span>
                      <div>
                        <div style={{fontSize:12,fontWeight:700,color:T.amber,marginBottom:2}}>No drawing reviewed yet</div>
                        <div style={{fontSize:11,color:T.muted,lineHeight:1.6}}>Upload a drawing in the <strong>📐 Drawings</strong> tab → run Code Compliance Check → violations auto-flag here with location, measurements, and code references. Showing key codes for <strong>{typeLabel}</strong> projects in the meantime:</div>
                      </div>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
                      {codes.map(c=>(
                        <div key={c.title} style={{background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:10,padding:"12px 14px"}}>
                          <div style={{fontSize:18,marginBottom:5}}>{c.icon}</div>
                          <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:3}}>{c.title}</div>
                          <div style={{fontSize:10,color:T.muted,lineHeight:1.6,marginBottom:5}}>{c.req}</div>
                          <div style={{fontSize:9,color:T.blue,fontFamily:"monospace",fontWeight:600}}>{c.code}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{background:T.goldBg,border:`1px solid ${T.gold}30`,borderRadius:10,padding:"14px 16px",display:"flex",gap:12,alignItems:"center"}}>
                      <span style={{fontSize:24}}>🤖</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:2}}>Get violations auto-marked on your actual drawing</div>
                        <div style={{fontSize:11,color:T.muted}}>Upload your blueprint → AI marks every violation directly on the drawing with exact location, measurements, and code section.</div>
                      </div>
                      <button onClick={()=>setTab("blueprints")} style={{background:T.gold,color:T.bg0,border:"none",borderRadius:8,padding:"8px 16px",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>Open Drawings →</button>
                    </div>
                  </div>
                );
              }

              // Has flags — show them
              return (
                <div>
                  <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap",alignItems:"center"}}>
                    <div style={{fontSize:12,fontWeight:700,color:T.text}}>{codeFlags.length} violations found</div>
                    <div style={{fontSize:11,color:T.muted}}>· {codeFlags.filter(f=>f.status!=="resolved").length} open · {codeFlags.filter(f=>f.status==="resolved").length} resolved</div>
                    {codeFlags.filter(f=>f.priority==="critical").length>0&&<span style={{fontSize:10,fontWeight:700,background:T.redBg,color:T.red,borderRadius:20,padding:"2px 10px",border:`1px solid ${T.red}30`}}>🔴 {codeFlags.filter(f=>f.priority==="critical").length} Critical</span>}
                    {codeFlags.filter(f=>f.priority==="high").length>0&&<span style={{fontSize:10,fontWeight:700,background:T.amberBg,color:T.amber,borderRadius:20,padding:"2px 10px",border:`1px solid ${T.amber}30`}}>🟡 {codeFlags.filter(f=>f.priority==="high").length} High</span>}
                  </div>

                  {codeFlags.map((f,i)=>(
                    <div key={f.id||i} style={{background:T.bg2,border:`1px solid ${f.status==="resolved"?T.green:f.priority==="critical"?T.red:T.amber}30`,borderRadius:10,padding:"12px 14px",marginBottom:8,display:"flex",gap:12,alignItems:"flex-start"}}>
                      <div style={{fontSize:20,flexShrink:0}}>{f.status==="resolved"?"✅":f.priority==="critical"?"🔴":"🟡"}</div>
                      <div style={{flex:1}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                          <div style={{fontSize:12,fontWeight:700,color:T.text}}>{f.title}</div>
                          <span style={{fontSize:9,fontWeight:700,background:f.status==="resolved"?T.greenBg:T.redBg,color:f.status==="resolved"?T.green:T.red,borderRadius:20,padding:"2px 8px",border:`1px solid ${f.status==="resolved"?T.green:T.red}30`,flexShrink:0,marginLeft:8,textTransform:"capitalize"}}>{f.status}</span>
                        </div>
                        {f.adaType&&<div style={{fontSize:10,color:T.blue,fontWeight:600,marginBottom:4}}>♿ {f.adaType.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}</div>}
                        {(f.measured||f.required)&&(
                          <div style={{display:"flex",gap:16,marginBottom:6,background:T.bg3,borderRadius:6,padding:"6px 10px"}}>
                            {f.measured&&<span style={{fontSize:11}}><span style={{color:T.muted}}>Measured:</span> <strong style={{color:T.red}}>{f.measured}</strong></span>}
                            {f.required&&<span style={{fontSize:11}}><span style={{color:T.muted}}>Required:</span> <strong style={{color:T.green}}>{f.required}</strong></span>}
                          </div>
                        )}
                        {f.codeRef&&<div style={{fontSize:10,color:T.blue,fontFamily:"monospace",marginBottom:4}}>{f.codeRef}</div>}
                        {f.desc&&<div style={{fontSize:11,color:T.muted,lineHeight:1.5,marginBottom:4}}>{f.desc?.slice(0,150)}{f.desc?.length>150?"…":""}</div>}
                        <div style={{fontSize:9,color:T.muted}}>📍 Drawing location: {f.pos?.x?.toFixed(0)}%, {f.pos?.y?.toFixed(0)}% · {f.date}</div>
                      </div>
                    </div>
                  ))}

                  <div style={{marginTop:12,background:T.goldBg,border:`1px solid ${T.gold}30`,borderRadius:10,padding:"12px 16px",display:"flex",gap:10,alignItems:"center"}}>
                    <span style={{fontSize:18}}>📐</span>
                    <div style={{flex:1,fontSize:11,color:T.muted}}>Click any pin on the drawing to view details and mark resolved as you fix each item.</div>
                    <button onClick={()=>setTab("blueprints")} style={{background:T.gold,color:T.bg0,border:"none",borderRadius:8,padding:"7px 14px",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>View on Drawing →</button>
                  </div>
                </div>
              );
            } catch(e) {
              return <div style={{color:T.muted,fontSize:12}}>Error loading compliance data.</div>;
            }
          })()}

          {/* Quick ADA Reference — always visible at bottom */}
          <div style={{marginTop:20,paddingTop:16,borderTop:`1px solid ${T.subtle}`}}>
            <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10}}>Quick ADA Reference</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
              {[
                {icon:"🚪",label:"Door Width",req:'32" min clear',code:"ADA §404.2.3",color:T.blue},
                {icon:"♿",label:"Turning Radius",req:'60" diameter',code:"ADA §304.3",color:T.purple},
                {icon:"🪜",label:"Ramp Slope",req:"Max 1:12",code:"ADA §405.2",color:T.amber},
                {icon:"🤲",label:"Grab Bar",req:'33"–36" AFF',code:"ADA §609",color:T.green},
                {icon:"📏",label:"Reach Range",req:'15"–48" AFF',code:"ADA §308",color:T.teal},
                {icon:"🚻",label:"Restroom",req:'60" turning radius',code:"ADA §604",color:T.red},
              ].map(s=>(
                <div key={s.label} style={{background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:8,padding:"10px 12px"}}>
                  <div style={{fontSize:16,marginBottom:4}}>{s.icon}</div>
                  <div style={{fontSize:10,fontWeight:700,color:T.text,marginBottom:2}}>{s.label}</div>
                  <div style={{fontSize:10,color:s.color,fontWeight:600,marginBottom:3}}>{s.req}</div>
                  <div style={{fontSize:8,color:T.muted,fontFamily:"monospace"}}>{s.code}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {tab==="punchlist"&&<PunchListView proj={proj} session={session}/>}
      {tab==="dailylog"&&(
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Daily Site Reports</div>
          <div style={{fontSize:11,color:T.muted,marginBottom:12}}>Read-only view of GC daily logs — stay informed on site progress</div>
          <DailyLogPanel proj={proj} session={session}/>
        </div>
      )}
      {tab==="cos"&&(
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Change Orders — View Only</div>
          <div style={{background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:8,padding:"9px 12px",marginBottom:10,fontSize:11,color:T.amber}}>
            You can see approved and pending COs for coordination. Pricing is hidden.
          </div>
          {projCOs.filter(c=>c.status!=="rejected").map(co=>(
            <div key={co.id} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 16px",marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                <div style={{fontSize:12,fontWeight:700,color:T.text}}>{co.num||co.id} — {co.desc||co.description}</div>
                <span style={{fontSize:10,fontWeight:700,background:co.status==="approved"?T.greenBg:T.amberBg,color:co.status==="approved"?T.green:T.amber,borderRadius:10,padding:"2px 8px"}}>{(co.status||"pending").toUpperCase()}</span>
              </div>
              <div style={{fontSize:10,color:T.muted}}>{co.trade||""} · {co.date||""}</div>
            </div>
          ))}
        </div>
      )}
      {tab==="docs"&&<ProjectDocsPanel proj={proj} session={session}/>}
      {tab==="schedule"&&<ScheduleWithInspections proj={proj} session={session}/>}
      {tab==="thread"&&<ProjectThreadView proj={proj} session={session} projCOs={projCOs} projRFIs={projRFIs}/>}
      {/* PAYMENT SCHEDULE TAB */}
      {tab==="payment"&&(
        <div>
          <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"14px 16px",marginBottom:12}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Contract Summary</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10}}>
              {(()=>{
                const origVal = proj?.value||0;
                const coTotal = (projCOs||[]).filter(c=>c.status==="approved").reduce((s,c)=>s+(c.owner_price||c.amount||0),0);
                const currVal = origVal + coTotal;
                return [["Original Contract","$"+Number(origVal).toLocaleString(),T.text],
                  ["Approved COs","+$"+Number(coTotal).toLocaleString(),T.amber],
                  ["Current Contract","$"+Number(currVal).toLocaleString(),T.green]
                ];
              })().map(([l,v,col])=>(
                <div key={l} style={{background:T.bg2,borderRadius:8,padding:"10px 12px",textAlign:"center"}}>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",marginBottom:3}}>{l}</div>
                  <div style={{fontSize:15,fontWeight:700,color:col}}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{fontSize:10,color:T.muted,lineHeight:1.6}}>
              All pricing reflects contract value only. Sub costs, markup, and overhead are internal to your GC.
            </div>
          </div>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Payment Milestones</div>
          <ScheduleWithInspections proj={proj} session={session} showBillingOnly={true}/>
        </div>
      )}

      {/* SITE PHOTOS TAB */}
      {tab==="photos"&&(
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Site Progress Photos</div>
          <div style={{fontSize:11,color:T.muted,marginBottom:12}}>Photos shared by your GC — updated as work progresses</div>
          <DailyLogPanel proj={proj} session={session}/>
        </div>
      )}

      {tab==="schedule"&&<ScheduleWithInspections proj={proj} session={session}/>}
    </div>
  );
}

// ─── CLIENT PROJECT VIEW ─────────────────────────────────────────────────────
// What an Owner/Client team member sees — progress, approved COs to sign, schedule
// No financials, no sub pricing, no margin — clean owner-facing view
function ClientProjectView({proj, session}) {
  const [tab, setTab] = React.useState("overview");
  const projCOs  = (()=>{try{const cid=session?.companyId||window._grokCompanyId||"local";const all=JSON.parse(localStorage.getItem("grok_cos_global_"+cid)||"[]");return all.length?all:COS;}catch{return COS;}})();
  const projRFIs = (()=>{try{const cid=session?.companyId||window._grokCompanyId||"local";return JSON.parse(localStorage.getItem("grok_rfis_global_"+cid)||"[]").filter(r=>r.status!=="closed");}catch{return RFIS.filter(r=>r.status!=="closed");}})();

  const TABS = [
    {id:"overview",  label:"Overview"},
    {id:"cos",       label:"Change Orders",  badge:projCOs.filter(c=>c.status==="pending"&&!c.owner_signature).length},
    {id:"payment",   label:"Payment Schedule"},
    {id:"photos",    label:"Site Photos"},
    {id:"schedule",  label:"Schedule"},
    {id:"docs",      label:"Documents"},
    {id:"contact",   label:"Contact GC"},
    {id:"thread",    label:"Thread"},
  ];

  const progress = proj?.pct || 0;
  const approvedCOTotal = projCOs.filter(c=>c.status==="approved").reduce((s,c)=>s+(c.owner_price||c.amount||0),0);
  const originalContract = proj?.value||0;
  const currentContract = originalContract + approvedCOTotal;

  return (
    <div style={{padding:20,maxWidth:900,margin:"0 auto"}}>
      {/* Clean client header */}
      <div style={{background:"linear-gradient(135deg,"+T.teal+","+T.blue+")",borderRadius:14,padding:"20px 24px",marginBottom:16,color:"white"}}>
        <div style={{fontSize:11,opacity:0.8,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.1em"}}>Your Project</div>
        <div style={{fontSize:22,fontWeight:800,marginBottom:4}}>{proj?.name}</div>
        <div style={{fontSize:12,opacity:0.8,marginBottom:16}}>{proj?.addr}</div>
        <div style={{display:"flex",gap:20}}>
          <div>
            <div style={{fontSize:32,fontWeight:800}}>{progress}%</div>
            <div style={{fontSize:11,opacity:0.8}}>Complete</div>
          </div>
          <div style={{flex:1,display:"flex",alignItems:"center"}}>
            <div style={{flex:1,background:"rgba(255,255,255,0.2)",borderRadius:6,height:10,overflow:"hidden"}}>
              <div style={{width:progress+"%",height:"100%",background:"white",borderRadius:6,transition:"width 1s"}}/>
            </div>
          </div>
        </div>
      </div>

      {/* Notice */}
      <div style={{background:T.tealBg,border:"1px solid "+T.teal+"30",borderRadius:9,padding:"9px 14px",marginBottom:14,fontSize:11,color:T.teal,lineHeight:1.7}}>
        <strong>Owner/Client View</strong> — You can review and sign Change Orders, view your project schedule and progress updates. Your GC manages the project details.
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:4,marginBottom:14,overflowX:"auto",paddingBottom:2}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{padding:"7px 14px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",background:tab===t.id?T.gold:"transparent",color:tab===t.id?T.bg0:T.muted,whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:5,transition:"all 0.15s"}}>
            {t.label}
            {t.badge>0&&<span style={{background:T.red,color:"white",fontSize:9,fontWeight:800,padding:"0 5px",borderRadius:10}}>{t.badge}</span>}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab==="overview"&&(
        <div>
          {/* Contract summary */}
          <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 16px",marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Contract</div>
            <div style={{display:"flex",gap:0}}>
              <div style={{flex:1,padding:"0 12px 0 0",borderRight:"1px solid "+T.subtle}}>
                <div style={{fontSize:9,color:T.muted,marginBottom:2}}>Original</div>
                <div style={{fontSize:14,fontWeight:700,color:T.text}}>${originalContract.toLocaleString()}</div>
              </div>
              <div style={{flex:1,padding:"0 12px",borderRight:"1px solid "+T.subtle}}>
                <div style={{fontSize:9,color:T.muted,marginBottom:2}}>Approved COs</div>
                <div style={{fontSize:14,fontWeight:700,color:approvedCOTotal>0?T.amber:T.muted}}>+${approvedCOTotal.toLocaleString()}</div>
              </div>
              <div style={{flex:1,padding:"0 0 0 12px"}}>
                <div style={{fontSize:9,color:T.muted,marginBottom:2}}>Current Total</div>
                <div style={{fontSize:14,fontWeight:700,color:T.green}}>${currentContract.toLocaleString()}</div>
              </div>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            {[
              ["📋 Pending Your Signature", projCOs.filter(c=>c.status==="pending"&&!c.owner_signature).length, T.amber],
              ["✅ Approved COs", projCOs.filter(c=>c.status==="approved").length, T.green],
              ["❓ Open RFIs", projRFIs.length, T.blue],
              ["📅 Status", proj?.status||"Active", T.teal],
            ].map(([label, val, color])=>(
              <div key={label} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 16px"}}>
                <div style={{fontSize:10,color:T.muted,marginBottom:4}}>{label}</div>
                <div style={{fontSize:18,fontWeight:800,color}}>{val}</div>
              </div>
            ))}
          </div>
          {projCOs.filter(c=>c.status==="pending"&&!c.owner_signature).length>0&&(
            <div style={{background:T.amberBg,border:"2px solid "+T.amber+"60",borderRadius:10,padding:"14px 16px",marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:700,color:T.amber,marginBottom:8}}>⚡ Action Required — Change Orders Need Your Signature</div>
              {projCOs.filter(c=>c.status==="pending"&&!c.owner_signature).map(co=>(
                <div key={co.id} style={{background:T.bg1,borderRadius:8,padding:"10px 12px",marginBottom:6}}>
                  <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:2}}>{co.num||co.id} — {co.desc||co.description}</div>
                  <div style={{fontSize:11,color:T.muted,marginBottom:8}}>Amount: ${(co.owner_price||co.amount||0).toLocaleString()} · {co.date||""}</div>
                  <SignatureBlock
                    label="Your Signature (Owner/Client)" role="Owner"
                    signData={co.owner_signature} required={true} canSign={true}
                    signerEmail={session?.email}
                    onSign={sig=>{COS=COS.map(c=>c.id===co.id?{...c,owner_signature:sig,status:"approved",approvedAt:new Date().toLocaleDateString()}:c);}}
                    onSaveToDoc={(sig,lbl)=>saveSignedDocToProject(proj,session,"Change Order",co.id,co.num||co.id,sig,lbl)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Change Orders */}
      {tab==="cos"&&(
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Change Orders</div>
          {projCOs.length===0?(
            <div style={{textAlign:"center",padding:"32px",color:T.muted,background:T.bg2,borderRadius:10}}>No change orders on this project yet.</div>
          ):projCOs.map(co=>(
            <div key={co.id} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"14px 16px",marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>{co.num||co.id} — {co.desc||co.description}</div>
                <span style={{fontSize:10,fontWeight:700,background:co.status==="approved"?T.greenBg:T.amberBg,color:co.status==="approved"?T.green:T.amber,borderRadius:10,padding:"2px 8px"}}>{(co.status||"pending").toUpperCase()}</span>
              </div>
              <div style={{fontSize:11,color:T.muted,marginBottom:8}}>
                Contract change: ${(co.owner_price||co.amount||0).toLocaleString()} · {co.reason||co.trade||""}
              </div>
              <SignatureBlock
                label="Your Signature (Owner/Client)" role="Owner"
                signData={co.owner_signature} required={true} canSign={true}
                signerEmail={session?.email}
                onSign={sig=>{COS=COS.map(c=>c.id===co.id?{...c,owner_signature:sig,status:"approved",approvedAt:new Date().toLocaleDateString()}:c);}}
                onSaveToDoc={(sig,lbl)=>saveSignedDocToProject(proj,session,"Change Order",co.id,co.num||co.id,sig,lbl)}
              />
            </div>
          ))}
        </div>
      )}

      {tab==="schedule"&&<ScheduleWithInspections proj={proj} session={session}/>}
      {tab==="docs"&&<ProjectDocsPanel proj={proj} session={session}/>}
      {tab==="thread"&&<ProjectThreadView proj={proj} session={session} projCOs={projCOs} projRFIs={projRFIs}/>}
      {tab==="contact"&&(
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"16px 18px"}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Your Project Team</div>
            {[
              ["GC Owner","Jehoon Chun","Jehoon@gcginc.com","(650) 555-0100",T.gold],
              proj?.pm_name?["Project Manager",proj.pm_name,proj.pm_email||"",proj.pm_phone||"",T.blue]:null,
              proj?.super_name?["Field Superintendent",proj.super_name,proj.super_email||"",proj.super_phone||"",T.amber]:null,
            ].filter(Boolean).map(([role,name,email,phone,color])=>(
              <div key={role} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:"1px solid "+T.subtle+"30"}}>
                <div style={{width:36,height:36,borderRadius:"50%",background:color+"20",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color,flexShrink:0}}>
                  {name.slice(0,2).toUpperCase()}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:700,color:T.text}}>{name}</div>
                  <div style={{fontSize:10,color:T.muted}}>{role}</div>
                </div>
                <div style={{display:"flex",gap:6}}>
                  {email&&<a href={"mailto:"+email} style={{fontSize:10,background:T.blueBg,color:T.blue,border:"1px solid "+T.blue+"30",borderRadius:6,padding:"4px 10px",textDecoration:"none",fontWeight:600}}>\u2709 Email</a>}
                  {phone&&<a href={"tel:"+phone.replace(/\D/g,"")} style={{fontSize:10,background:T.greenBg,color:T.green,border:"1px solid "+T.green+"30",borderRadius:6,padding:"4px 10px",textDecoration:"none",fontWeight:600}}>\u260e Call</a>}
                </div>
              </div>
            ))}
          </div>
          <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"14px 16px"}}>
            <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:8}}>License & Insurance</div>
            {[["CSLB License","#1152750"],["Company","Grok Construction Group Inc"],["License Type","Class B — General Building"],["Location","San Mateo, CA"]].map(([l,v])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",fontSize:11,borderBottom:"1px solid "+T.subtle+"20"}}>
                <span style={{color:T.muted}}>{l}</span><span style={{color:T.text,fontWeight:600}}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{background:T.amberBg,border:"1px solid "+T.amber+"30",borderRadius:9,padding:"10px 14px",fontSize:11,color:T.amber,lineHeight:1.7}}>
            For urgent site or safety issues, call your Field Superintendent directly. For contract and payment questions, contact the GC Owner.
          </div>
        </div>
      )}
    </div>
  );
}


// ─── PROJECT THREAD VIEW ─────────────────────────────────────────────────────
// Shared chat between GC, Architect, and Owner/Client only
// Auto-detects CO and RFI triggers · Role-aware display · Document attachment
function ProjectThreadView({proj, session, projCOs, projRFIs, setProjects, projects}) {
  const key = "grok_thread_" + proj?.id;
  const apiKey = "proxy"; // Using Supabase AI proxy

  const [messages, setMessages] = React.useState(()=>{
    try{
      const s = localStorage.getItem(key);
      return s ? JSON.parse(s) : [
        {id:1, authorName:"Jehoon Chun", authorRole:"gc", authorInit:"JK", text:"Project thread is live. This is our shared communication space — GC, Architect, and Owner only. All messages are logged as part of the project record.", ts:new Date(Date.now()-86400000*2).toLocaleString("en-US",{month:"short",day:"numeric",hour:"numeric",minute:"2-digit",hour12:true}), pinned:false},
      ];
    }catch{return [];}
  });

  const [input, setInput] = React.useState("");
  const [filter, setFilter] = React.useState("all");
  const [aiLoading, setAiLoading] = React.useState(false);
  const [aiSummary, setAiSummary] = React.useState("");
  const [showSummary, setShowSummary] = React.useState(false);
  const [attachFile, setAttachFile] = React.useState(null);
  const bottomRef = React.useRef(null);
  const fileRef = React.useRef(null);

  const save = (msgs) => {
    setMessages(msgs);
    try{localStorage.setItem(key, JSON.stringify(msgs));}catch(e){}
  };

  // Determine sender info from session
  const getSenderInfo = () => {
    const role = session?.teamRole || "owner";
    const name = session?.ownerName || session?.companyName || "GC";
    const initials = name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
    const colors = {
      owner: {bg:"#D4A843", text:"#D4A843", label:"GC Owner"},
      admin: {bg:"#a78bfa", text:"#a78bfa", label:"Admin"},
      pm: {bg:"#60a5fa", text:"#60a5fa", label:"Project Manager"},
      architect: {bg:"#818cf8", text:"#818cf8", label:"Architect"},
      client: {bg:"#1D9E75", text:"#1D9E75", label:"Owner/Client"},
      gc: {bg:"#D4A843", text:"#D4A843", label:"GC"},
    };
    return {name, initials, role, color:(colors[role]||colors.gc).bg, label:(colors[role]||colors.gc).label};
  };

  const send = () => {
    if (!input.trim() && !attachFile) return;
    const sender = getSenderInfo();
    const msg = {
      id: Date.now(),
      authorName: sender.name,
      authorRole: sender.role,
      authorLabel: sender.label,
      authorInit: sender.initials,
      authorColor: sender.color,
      text: input.trim(),
      ts: new Date().toLocaleString("en-US",{month:"short",day:"numeric",hour:"numeric",minute:"2-digit",hour12:true}),
      pinned: false,
      attachment: attachFile||null,
    };
    save([...messages, msg]);
    setInput(""); setAttachFile(null);
    setTimeout(()=>bottomRef.current?.scrollIntoView({behavior:"smooth"}),50);
  };

  const handleFile = (e) => {
    const file = e.target.files[0]; if(!file) return;
    const reader = new FileReader();
    reader.onload = ev => setAttachFile({name:file.name, type:file.type, size:file.size, dataUrl:ev.target.result});
    reader.readAsDataURL(file);
  };

  const togglePin = (id) => save(messages.map(m=>String(m.id)===String(id)?{...m,pinned:!m.pinned}:m));
  const deleteMsg = (id) => { if(true) save(messages.filter(m=>String(m.id)!==String(id))); };

  // CO/RFI detection keywords
  const CO_KEYWORDS = ["change order","scope change","extra work","additional scope","not in contract","out of scope","unforeseen","hidden condition","discovered during","asbestos","mold","water damage","owner request","owner wants","need to add","price increase","additional cost","needs CO","CO needed","add to scope","scope addition","washer","upgrade","additional unit","not on drawings"];
  const RFI_KEYWORDS = ["clarification","unclear","not shown","missing on drawings","conflict","coordinate","confirm","which drawing","code section","what does the drawing","vent path","routing","structural","engineer to confirm","need direction","ASI","architect to clarify","specify","specification"];
  const LOGISTICS = ["see you","on my way","heading","arrived","done for","good job","thanks","thank you","sounds good","got it","confirmed","will do","on site","leaving","approved","great","perfect","noted"];

  const detectFlags = (text) => {
    const t = text.toLowerCase();
    if (LOGISTICS.some(l=>t.startsWith(l)||t.includes(l))) return null;
    if (CO_KEYWORDS.some(k=>t.includes(k))) return "co";
    if (RFI_KEYWORDS.some(k=>t.includes(k))) return "rfi";
    return null;
  };

  const flagged = messages.filter(m=>m.text&&detectFlags(m.text));
  const flaggedIds = new Set(flagged.map(m=>m.id));
  const coFlags = messages.filter(m=>m.text&&detectFlags(m.text)==="co");
  const rfiFlags = messages.filter(m=>m.text&&detectFlags(m.text)==="rfi");

  const displayed = filter==="all" ? messages
    : filter==="co" ? coFlags
    : filter==="rfi" ? rfiFlags
    : filter==="pinned" ? messages.filter(m=>m.pinned)
    : messages;

  const createCOfromMsg = (msg) => {
    const co = {
      id:"CO-"+Date.now(), num:"CO-TH-"+messages.indexOf(msg),
      desc:msg.text.slice(0,80)+"...",
      status:"pending", date:new Date().toLocaleDateString(),
      source:"project_thread", sourceMsg:msg.id,
      amount:0, owner_price:0,
    };
    try {
      const cid = session?.companyId||"local";
      const ck = "grok_cos_"+proj?.id+"_"+cid;
      const existing = JSON.parse(localStorage.getItem(ck)||"[]");
      localStorage.setItem(ck, JSON.stringify([co,...existing]));
      alert("CO created from this message. Open the Admin tab to add pricing and send for approval.");
    } catch(e) {}
  };

  const createRFIfromMsg = (msg) => {
    const rfi = {
      id:"RFI-TH-"+Date.now(),
      subject:msg.text.slice(0,60)+"...",
      desc:msg.text,
      status:"open", date:new Date().toLocaleDateString(),
      assignee:"Architect", source:"project_thread",
    };
    try {
      const cid = session?.companyId||"local";
      const rk = "grok_rfis_"+proj?.id+"_"+cid;
      const existing = JSON.parse(localStorage.getItem(rk)||"[]");
      localStorage.setItem(rk, JSON.stringify([rfi,...existing]));
      alert("RFI created from this message. Open the Admin tab to assign and track.");
    } catch(e) {}
  };

  const runAISummary = async () => {
    
    setAiLoading(true); setShowSummary(true); setAiSummary("");
    const transcript = messages.slice(-30).map(m=>`[${m.authorLabel||m.authorRole}] ${m.authorName}: ${m.text}`).join("\n");
    try {
      const resp = await fetch(AI_PROXY_URL,{
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY,"anthropic-version":"2023-06-01"},
        body:JSON.stringify({model:"claude-haiku-4-5",max_tokens:800,messages:[{role:"user",content:
          `You are a construction project manager assistant. Summarize this project thread for the team.

Project: ${proj?.name}
Address: ${proj?.addr}

Thread:
${transcript}

Provide:
## Open Action Items
Bullet list of things that need to happen, who is responsible.

## Decisions Made
What was agreed or confirmed.

## Potential COs
Any scope additions or changes that need a Change Order.

## Pending Clarifications (RFIs)
Technical questions still open.

## Next Steps
Top 3 things to do before the next site visit.

Be concise and practical — this is for a busy GC.`
        }]})});
      const data = await resp.json();
      setAiSummary(data.content?.[0]?.text||"No summary generated.");
    } catch(e) { setAiSummary("Error: "+e.message); }
    setAiLoading(false);
  };

  const ROLE_COLORS = {gc:"#D4A843",owner:"#D4A843",admin:"#a78bfa",pm:"#60a5fa",architect:"#818cf8",client:"#1D9E75",viewer:"#64748b"};
  const isMe = (msg) => msg.authorName===(session?.ownerName||session?.companyName||"GC") || msg.authorRole===(session?.teamRole||"gc")||msg.authorRole==="gc";

  // Team members for display
  const teamMembers = [
    {init:"JK", color:"#D4A843", label:"GC", name:session?.companyName||"Grok Construction"},
    ...(proj?.members||[]).filter(m=>["architect","client","pm"].includes(m.role)).map(m=>({
      init:(m.name||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase(),
      color:ROLE_COLORS[m.role]||"#64748b",
      label:m.role==="architect"?"Architect":m.role==="client"?"Owner/Client":"PM",
      name:m.name||m.email
    }))
  ];

  // Rendered both standalone (Hub tab) and nested inside ProjectHubView's flex
  // slot — measuring its own top makes one bound correct in either context.
  const boundRef = React.useRef(null);
  const bound = useViewportBound(boundRef, 60);

  return (
    <div ref={boundRef} style={{display:"flex",flexDirection:"column",height: bound || "calc(100vh - 300px)",minHeight:380,maxHeight:660}}>

      {/* Header */}
      <div style={{background:T.bg1,borderRadius:10,border:"1px solid "+T.subtle,padding:"10px 14px",marginBottom:10,flexShrink:0}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:T.text}}>💬 Project Thread</div>
            <div style={{fontSize:10,color:T.muted}}>GC · Architect · Owner only — logged as project record</div>
          </div>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            {teamMembers.slice(0,4).map((m,i)=>(
              <div key={i} title={m.name+" — "+m.label}
                style={{width:28,height:28,borderRadius:"50%",background:m.color+"20",border:"2px solid "+m.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:m.color}}>
                {m.init}
              </div>
            ))}
            <span style={{fontSize:10,color:T.muted}}>{teamMembers.length} members</span>
          </div>
        </div>

        {/* Alert banners */}
        {coFlags.length>0&&(
          <div style={{background:T.amberBg,border:"1px solid "+T.amber+"40",borderRadius:7,padding:"7px 12px",fontSize:11,color:T.amber,display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
            <span>⚡</span>
            <div style={{flex:1}}><strong>{coFlags.length} message{coFlags.length>1?"s":""}</strong> flagged as potential scope change — may need a CO</div>
            <button onClick={()=>setFilter("co")} style={{fontSize:10,fontWeight:700,background:T.amber,color:"white",border:"none",borderRadius:5,padding:"2px 8px",cursor:"pointer"}}>Review</button>
          </div>
        )}
        {rfiFlags.length>0&&(
          <div style={{background:T.purpleBg,border:"1px solid "+T.purple+"30",borderRadius:7,padding:"7px 12px",fontSize:11,color:T.purple,display:"flex",alignItems:"center",gap:8}}>
            <span>❓</span>
            <div style={{flex:1}}><strong>{rfiFlags.length} message{rfiFlags.length>1?"s":""}</strong> flagged as technical clarification — may need an RFI</div>
            <button onClick={()=>setFilter("rfi")} style={{fontSize:10,fontWeight:700,background:T.purple,color:"white",border:"none",borderRadius:5,padding:"2px 8px",cursor:"pointer"}}>Review</button>
          </div>
        )}

        {/* Filter tabs */}
        <div style={{display:"flex",gap:4,marginTop:8}}>
          {[["all","All"],["co","⚡ CO Flags"],["rfi","❓ RFI Flags"],["pinned","📌 Pinned"]].map(([f,l])=>(
            <button key={f} onClick={()=>setFilter(f)}
              style={{fontSize:10,fontWeight:600,padding:"3px 10px",borderRadius:12,cursor:"pointer",border:"none",background:filter===f?T.gold:T.bg2,color:filter===f?T.bg0:T.muted}}>
              {l}{f==="co"&&coFlags.length>0?` (${coFlags.length})`:f==="rfi"&&rfiFlags.length>0?` (${rfiFlags.length})`:""}
            </button>
          ))}
          <button onClick={runAISummary}
            style={{fontSize:10,fontWeight:600,padding:"3px 10px",borderRadius:12,cursor:"pointer",border:"none",background:T.purpleBg,color:T.purple,marginLeft:"auto"}}>
            {aiLoading?"⏳ Summarizing...":"🤖 AI Summary"}
          </button>
        </div>
      </div>

      {/* AI Summary panel */}
      {showSummary&&(
        <div style={{background:T.purpleBg,border:"1px solid "+T.purple+"30",borderRadius:10,padding:"12px 16px",marginBottom:10,flexShrink:0}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{fontSize:12,fontWeight:700,color:T.purple}}>🤖 AI Thread Summary</div>
            <button onClick={()=>setShowSummary(false)} style={{fontSize:11,background:"none",border:"none",color:T.muted,cursor:"pointer"}}>✕ Close</button>
          </div>
          {aiLoading
            ? <div style={{fontSize:11,color:T.muted}}>Analyzing thread...</div>
            : <div style={{fontSize:11,color:T.muted,lineHeight:1.8,whiteSpace:"pre-wrap",maxHeight:200,overflowY:"auto"}}>{aiSummary}</div>
          }
        </div>
      )}

      {/* Messages */}
      <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:10,paddingRight:4}}>
        {displayed.length===0&&(
          <div style={{textAlign:"center",padding:"40px",color:T.muted}}>
            <div style={{fontSize:24,marginBottom:8}}>💬</div>
            <div style={{fontSize:13,fontWeight:600,color:T.text,marginBottom:4}}>
              {filter==="all"?"No messages yet":filter==="co"?"No CO flags yet":filter==="rfi"?"No RFI flags yet":"No pinned messages"}
            </div>
            <div style={{fontSize:11}}>This thread is for GC, Architect, and Owner only</div>
          </div>
        )}

        {displayed.map(msg=>{
          const mine = isMe(msg);
          const flag = msg.text ? detectFlags(msg.text) : null;
          const roleColor = ROLE_COLORS[msg.authorRole]||"#64748b";
          return (
            <div key={msg.id} style={{display:"flex",flexDirection:"column",alignItems:mine?"flex-end":"flex-start",gap:3}}>
              {/* Author + time */}
              <div style={{display:"flex",alignItems:"center",gap:6,fontSize:10,color:T.muted}}>
                {!mine&&(
                  <div style={{width:20,height:20,borderRadius:"50%",background:roleColor+"20",border:"1px solid "+roleColor,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:700,color:roleColor,flexShrink:0}}>
                    {msg.authorInit||"?"}
                  </div>
                )}
                <span style={{fontWeight:600,color:roleColor}}>{msg.authorName}</span>
                <span style={{background:roleColor+"15",color:roleColor,borderRadius:8,padding:"0 5px",fontSize:9,fontWeight:600}}>{msg.authorLabel||msg.authorRole}</span>
                <span>{msg.ts}</span>
                {msg.pinned&&<span style={{color:T.gold}}>📌</span>}
              </div>

              {/* Bubble */}
              <div style={{maxWidth:"75%"}}>
                <div style={{
                  background:mine?T.gold:T.bg1,
                  color:mine?T.bg0:T.text,
                  borderRadius:mine?"12px 12px 3px 12px":"12px 12px 12px 3px",
                  padding:"9px 13px",
                  fontSize:12,lineHeight:1.6,
                  border:mine?"none":"1px solid "+T.subtle,
                }}>
                  {msg.text}
                  {/* Attachment */}
                  {msg.attachment&&(
                    <div style={{marginTop:6,background:mine?"rgba(0,0,0,0.15)":T.bg2,borderRadius:6,padding:"6px 10px",display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:14}}>📎</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:11,fontWeight:600}}>{msg.attachment.name}</div>
                        <div style={{fontSize:9,opacity:0.7}}>{Math.round((msg.attachment.size||0)/1024)}KB</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* CO/RFI flag */}
                {flag==="co"&&(
                  <div style={{background:T.amberBg,border:"1px solid "+T.amber+"40",borderRadius:7,padding:"6px 10px",marginTop:4,display:"flex",alignItems:"center",gap:8,fontSize:10,color:T.amber}}>
                    <span>⚡</span>
                    <span style={{flex:1}}>Possible scope change — review if a Change Order is needed</span>
                    <button onClick={()=>createCOfromMsg(msg)}
                      style={{fontSize:9,fontWeight:700,background:T.amber,color:"white",border:"none",borderRadius:5,padding:"2px 8px",cursor:"pointer",flexShrink:0}}>
                      + Create CO
                    </button>
                  </div>
                )}
                {flag==="rfi"&&(
                  <div style={{background:T.purpleBg,border:"1px solid "+T.purple+"30",borderRadius:7,padding:"6px 10px",marginTop:4,display:"flex",alignItems:"center",gap:8,fontSize:10,color:T.purple}}>
                    <span>❓</span>
                    <span style={{flex:1}}>Technical clarification detected — consider opening an RFI</span>
                    <button onClick={()=>createRFIfromMsg(msg)}
                      style={{fontSize:9,fontWeight:700,background:T.purple,color:"white",border:"none",borderRadius:5,padding:"2px 8px",cursor:"pointer",flexShrink:0}}>
                      + Create RFI
                    </button>
                  </div>
                )}

                {/* Actions */}
                <div style={{display:"flex",gap:6,marginTop:3,justifyContent:mine?"flex-end":"flex-start"}}>
                  <button onClick={()=>togglePin(msg.id)}
                    style={{fontSize:9,background:"none",border:"none",color:T.muted,cursor:"pointer",padding:"0 4px"}}
                    title={msg.pinned?"Unpin":"Pin"}>
                    {msg.pinned?"📌":"📍"}
                  </button>
                  {mine&&(
                    <button onClick={()=>deleteMsg(msg.id)}
                      style={{fontSize:9,background:"none",border:"none",color:T.muted,cursor:"pointer",padding:"0 4px"}}>
                      🗑
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>

      {/* Attachment preview */}
      {attachFile&&(
        <div style={{background:T.tealBg,border:"1px solid "+T.teal+"30",borderRadius:8,padding:"8px 12px",marginTop:8,display:"flex",alignItems:"center",gap:8,fontSize:11,flexShrink:0}}>
          <span>📎</span>
          <span style={{flex:1,color:T.text,fontWeight:600}}>{attachFile.name}</span>
          <button onClick={()=>setAttachFile(null)} style={{background:"none",border:"none",color:T.muted,cursor:"pointer"}}>✕</button>
        </div>
      )}

      {/* Input */}
      <div className="chat-input-row" style={{marginTop:10,flexShrink:0,paddingTop:8,borderTop:"1px solid "+T.subtle}}>
        <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
          <div style={{flex:1,background:T.bg1,border:"1px solid "+T.subtle,borderRadius:12,padding:"8px 12px"}}>
            <textarea
              value={input} onChange={e=>setInput(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
              placeholder={"Message as "+(getSenderInfo().name)+" ("+(getSenderInfo().label)+")..."}
              rows={2}
              style={{width:"100%",background:"none",border:"none",color:T.text,fontSize:12,resize:"none",fontFamily:"inherit",lineHeight:1.5,outline:"none"}}
            />
            <div style={{display:"flex",gap:6,alignItems:"center",marginTop:4}}>
              <input ref={fileRef} type="file" style={{display:"none"}} onChange={handleFile}
                accept="image/*,application/pdf,.docx,.xlsx"/>
              <button onClick={()=>fileRef.current.click()}
                style={{fontSize:10,background:"none",border:"1px solid "+T.subtle,borderRadius:6,padding:"2px 8px",cursor:"pointer",color:T.muted}}>
                📎 Attach
              </button>
              <span style={{fontSize:9,color:T.muted}}>Enter to send · Shift+Enter for new line</span>
            </div>
          </div>
          <button onClick={send} disabled={!input.trim()&&!attachFile}
            style={{width:40,height:40,borderRadius:"50%",background:input.trim()||attachFile?T.gold:T.bg3,color:input.trim()||attachFile?T.bg0:"white",border:"none",cursor:input.trim()||attachFile?"pointer":"not-allowed",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0,transition:"all 0.15s"}}>
            ▶
          </button>
        </div>
      </div>
    </div>
  );
}



// ─── PROJECT HUB VIEW ─────────────────────────────────────────────────────────
// Combined: Messages · Meeting Notes · AI PM · Decisions
// Lives in every project — all communication and AI in one place
function ProjectHubView({proj, session, projCOs, projRFIs, projects}) {
  const [sub, setSub] = React.useState("messages");
  const isMob = window.innerWidth < 768;

  const SUBS = [
    {id:"messages",  label:"Thread",       desc:"GC · Architect · Owner"},
    {id:"meetings",  label:"Meetings",      desc:"Notes & recordings"},
    {id:"aipm",      label:"AI PM",         desc:"Project-specific AI chat"},
    {id:"decisions", label:"Decisions",      desc:"Key decisions log"},
  ];

  // Outer bounded box for the Hub — its inner tabs (Thread / AI PM / Meetings)
  // fill the flex:1 slot below, so this is the only viewport-offset guess to fix.
  const boundRef = React.useRef(null);
  const bound = useViewportBound(boundRef, 60);

  return (
    <div ref={boundRef} style={{display:"flex",flexDirection:"column",height: bound || (isMob?"calc(100vh - 320px)":"calc(100vh - 300px)"),minHeight:380,maxHeight:660}}>
      {/* Sub-nav */}
      <div style={{display:"flex",gap:3,marginBottom:14,background:T.bg2,borderRadius:10,padding:4,flexShrink:0,overflowX:"auto",WebkitOverflowScrolling:"touch",scrollbarWidth:"none"}}>
        {SUBS.map(s=>(
          <button key={s.id} onClick={()=>setSub(s.id)}
            style={{flex:isMob?0:1,flexShrink:0,padding:isMob?"8px 12px":"10px 14px",borderRadius:8,border:"none",
              background:sub===s.id?T.bg1:"transparent",
              boxShadow:sub===s.id?"0 1px 3px rgba(0,0,0,0.15)":"none",
              cursor:"pointer",transition:"all 0.15s",textAlign:"center"}}>
            <div style={{fontSize:isMob?11:12,fontWeight:700,color:sub===s.id?T.text:T.muted,whiteSpace:"nowrap"}}>{s.label}</div>
            {!isMob&&<div style={{fontSize:9,color:T.muted,marginTop:1}}>{s.desc}</div>}
          </button>
        ))}
      </div>

      {/* Content — fills remaining space */}
      <div style={{flex:1,minHeight:0,overflow:"hidden"}}>
      {sub==="messages" && (
        <ProjectThreadView proj={proj} session={session} projCOs={projCOs} projRFIs={projRFIs}/>
      )}
      {sub==="meetings" && (
        <div style={{height:"100%",overflowY:"auto",WebkitOverflowScrolling:"touch"}}>
          <ProjectMeetingsView proj={proj} session={session}/>
        </div>
      )}
      {sub==="aipm" && (
        <ProjectAIPMView proj={proj} session={session} projCOs={projCOs} projRFIs={projRFIs}/>
      )}
      {sub==="decisions" && (
        <div style={{height:"100%",overflowY:"auto",WebkitOverflowScrolling:"touch"}}>
          <ProjectDecisionsView proj={proj} session={session}/>
        </div>
      )}
      </div>
    </div>
  );
}

// ─── PROJECT MEETINGS VIEW ────────────────────────────────────────────────────
// Per-project meeting notes with recording + AI summary
function ProjectMeetingsView({proj, session}) {
  const key = "grok_meetings_" + (session?.companyId||"local") + "_" + proj?.id;
  const isMob = window.innerWidth < 768;
  const [meetings, setMeetings] = React.useState(()=>{
    try{ return JSON.parse(localStorage.getItem(key)||"[]"); } catch{ return []; }
  });
  const [showNew, setShowNew] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [form, setForm] = React.useState({
    type:"Site Walkthrough", date:new Date().toLocaleDateString(), attendees:"",
    phase:"Construction", notes:"", summary:"",
  });
  const [recording, setRecording] = React.useState(false);
  const [recTime, setRecTime] = React.useState(0);
  const [aiLoading, setAiLoading] = React.useState(false);
  const recRef = React.useRef(null);
  const timerRef = React.useRef(null);
  const [liveText, setLiveText] = React.useState("");
  const recogRef = React.useRef(null);

  const save = (list) => { setMeetings(list); try{ localStorage.setItem(key,JSON.stringify(list)); }catch(e){} };

  const saveMeeting = () => {
    const m = {...form, id:"mtg_"+Date.now(), proj:proj?.name, projId:proj?.id,
      createdAt:new Date().toISOString(), liveText};
    const updated = [m, ...meetings];
    save(updated); setShowNew(false); setForm({type:"Site Walkthrough",date:new Date().toLocaleDateString(),attendees:"",phase:"Construction",notes:"",summary:""});
    setLiveText(""); setSelected(m);
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio:true});
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = e => chunks.push(e.data);
      rec.onstop = () => { stream.getTracks().forEach(t=>t.stop()); };
      rec.start();
      recRef.current = rec;
      setRecording(true); setRecTime(0);
      timerRef.current = setInterval(()=>setRecTime(p=>p+1),1000);

      // Live speech recognition
      if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recog = new SR();
        recog.continuous = true; recog.interimResults = true;
        recog.onresult = e => {
          let text = "";
          for(let i=0;i<e.results.length;i++) text += e.results[i][0].transcript + " ";
          setLiveText(text);
          setForm(p=>({...p, notes:text}));
        };
        recog.start();
        recogRef.current = recog;
      }
    } catch(e) { alert("Microphone access needed to record. You can also type notes manually."); }
  };

  const stopRec = () => {
    if(recRef.current) { recRef.current.stop(); recRef.current = null; }
    if(recogRef.current) { recogRef.current.stop(); recogRef.current = null; }
    clearInterval(timerRef.current);
    setRecording(false);
  };

  const runAISummary = async () => {
    if (!form.notes && !liveText) { alert("Add meeting notes first."); return; }
    setAiLoading(true);
    try {
      const resp = await fetch(AI_PROXY_URL, {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY},
        body:JSON.stringify({
          body:{messages:[{role:"user",content:
            `Summarize this construction meeting for project "${proj?.name}".\n\nMeeting type: ${form.type}\nDate: ${form.date}\nAttendees: ${form.attendees}\nPhase: ${form.phase}\n\nNotes/Transcript:\n${form.notes||liveText}\n\nProvide:\n## Meeting Summary\nBrief overview.\n\n## Key Decisions Made\nBullet list of decisions.\n\n## Action Items\nWho needs to do what by when.\n\n## Open Issues\nAnything unresolved.\n\n## Next Steps\nWhat happens before next meeting.`
          }],
          max_tokens:1000},
          usage_type:"chat",
        })
      });
      const data = await resp.json();
      const text = data.content?.[0]?.text || "";
      setForm(p=>({...p, summary:text}));
    } catch(e) { console.error(e); }
    setAiLoading(false);
  };

  const MEETING_TYPES = ["Site Walkthrough","Pre-Construction","Owner Meeting","Sub Coordination","Design Review","Inspection Prep","Safety Meeting","Closeout Walkthrough"];
  const PHASES = ["Pre-Construction","Demolition","Rough Framing","MEP Rough-In","Inspections","Finishes","Closeout"];
  const fmt = (s) => s>3600?Math.floor(s/3600)+"h "+(Math.floor(s/60)%60)+"m":Math.floor(s/60)+"m "+( s%60)+"s";
  const inp = {width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"8px 12px",color:T.text,fontSize:13,fontFamily:"inherit"};

  if (selected) return (
    <div>
      <button onClick={()=>setSelected(null)}
        style={{background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"6px 12px",color:T.muted,fontSize:12,cursor:"pointer",marginBottom:14}}>
        ← Back to meetings
      </button>
      <div style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:12,padding:"16px 18px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
          <div>
            <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:2}}>{selected.type}</div>
            <div style={{fontSize:11,color:T.muted}}>{selected.date} · {selected.attendees} · {selected.phase}</div>
          </div>
          <button onClick={()=>{if(true){ save(meetings.filter(m=>String(m.id)!==String(selected.id))); setSelected(null); }}}
            style={{background:T.redBg,border:"1px solid "+T.red+"30",borderRadius:6,padding:"4px 10px",fontSize:11,color:T.red,cursor:"pointer"}}>
            Delete
          </button>
        </div>
        {selected.summary&&(
          <div style={{background:T.purpleBg,border:"1px solid "+T.purple+"30",borderRadius:9,padding:"12px 16px",marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:700,color:T.purple,marginBottom:8}}>🤖 AI Summary</div>
            <div style={{fontSize:12,color:T.muted,lineHeight:1.8,whiteSpace:"pre-wrap"}}>{selected.summary}</div>
          </div>
        )}
        <div style={{background:T.bg2,borderRadius:8,padding:"12px 16px"}}>
          <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:6}}>Meeting Notes</div>
          <div style={{fontSize:12,color:T.muted,lineHeight:1.8,whiteSpace:"pre-wrap"}}>{selected.notes||selected.liveText||"No notes recorded."}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text}}>Meeting Notes — {proj?.name?.split(" — ")[0]}</div>
        <button onClick={()=>setShowNew(p=>!p)}
          style={{background:showNew?T.bg2:T.teal,color:showNew?T.muted:"white",border:"1px solid "+(showNew?T.subtle:T.teal),borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
          {showNew?"✕ Cancel":"+ New Meeting"}
        </button>
      </div>

      {/* New meeting form */}
      {showNew&&(
        <div style={{background:T.bg1,border:"1px solid "+T.teal+"40",borderRadius:12,padding:"16px",marginBottom:16}}>
          <div style={{display:"grid",gridTemplateColumns:isMob?"1fr":"1fr 1fr 1fr",gap:10,marginBottom:12}}>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",marginBottom:4}}>Meeting Type</div>
              <select value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))} style={inp}>
                {MEETING_TYPES.map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",marginBottom:4}}>Phase</div>
              <select value={form.phase} onChange={e=>setForm(p=>({...p,phase:e.target.value}))} style={inp}>
                {PHASES.map(p=><option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",marginBottom:4}}>Date</div>
              <input type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))} style={inp}/>
            </div>
          </div>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",marginBottom:4}}>Attendees</div>
            <input value={form.attendees} onChange={e=>setForm(p=>({...p,attendees:e.target.value}))}
              placeholder="Jehoon, Mike (plumber), Sarah (architect), Marcus (owner)..." style={inp}/>
          </div>

          {/* Recording controls */}
          <div style={{background:T.bg2,borderRadius:9,padding:"12px",marginBottom:10}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
              <button onClick={recording?stopRec:startRec}
                style={{padding:"8px 18px",borderRadius:8,border:"none",
                  background:recording?"linear-gradient(135deg,"+T.red+",#dc2626)":T.teal,
                  color:"white",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:7}}>
                {recording?<><span style={{display:"inline-block",width:10,height:10,borderRadius:2,background:"white"}}/> Stop Recording</>
                  :<><span style={{display:"inline-block",width:10,height:10,borderRadius:"50%",background:"white"}}/> Record Meeting</>}
              </button>
              {recording&&<span style={{fontSize:13,fontWeight:700,color:T.red,fontFamily:"monospace"}}>{fmt(recTime)}</span>}
              {!recording&&<span style={{fontSize:11,color:T.muted}}>Or type notes below</span>}
            </div>
            {recording&&liveText&&(
              <div style={{background:T.bg1,borderRadius:7,padding:"8px 12px",fontSize:11,color:T.muted,lineHeight:1.6,maxHeight:80,overflowY:"auto"}}>
                🎙 {liveText.slice(-200)}
              </div>
            )}
          </div>

          <div style={{marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",marginBottom:4}}>Notes</div>
            <textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}
              rows={5} placeholder="What was discussed, decided, and agreed upon..."
              style={{...inp,resize:"vertical"}}/>
          </div>

          {form.summary&&(
            <div style={{background:T.purpleBg,border:"1px solid "+T.purple+"30",borderRadius:9,padding:"12px",marginBottom:10}}>
              <div style={{fontSize:11,fontWeight:700,color:T.purple,marginBottom:6}}>🤖 AI Summary</div>
              <div style={{fontSize:11,color:T.muted,lineHeight:1.7,whiteSpace:"pre-wrap"}}>{form.summary}</div>
            </div>
          )}

          <div style={{display:"flex",gap:8}}>
            <button onClick={runAISummary} disabled={aiLoading}
              style={{flex:1,padding:"9px",background:T.purpleBg,border:"1px solid "+T.purple+"40",borderRadius:8,color:T.purple,fontSize:12,fontWeight:700,cursor:"pointer"}}>
              {aiLoading?"⏳ Summarizing...":"🤖 AI Summary"}
            </button>
            <button onClick={saveMeeting}
              style={{flex:2,padding:"9px",background:T.teal,border:"none",borderRadius:8,color:"white",fontSize:12,fontWeight:700,cursor:"pointer"}}>
              ✓ Save Meeting Notes
            </button>
          </div>
        </div>
      )}

      {/* Meeting list */}
      {meetings.length===0&&!showNew?(
        <div style={{textAlign:"center",padding:"40px 24px",color:T.muted}}>
          <div style={{fontSize:36,marginBottom:10}}>📝</div>
          <div style={{fontSize:14,fontWeight:600,color:T.text,marginBottom:6}}>No meetings recorded yet</div>
          <div style={{fontSize:12,lineHeight:1.7}}>Record pre-construction meetings, site walkthroughs, owner meetings, and sub coordination calls. All notes saved to this project.</div>
        </div>
      ):meetings.map(m=>(
        <div key={m.id} onClick={()=>setSelected(m)}
          style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 16px",marginBottom:8,cursor:"pointer",display:"flex",alignItems:"center",gap:12}}>
          <div style={{width:36,height:36,borderRadius:8,background:T.teal+"20",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
            {m.type.includes("Pre-Con")?"🏗":m.type.includes("Owner")?"🏠":m.type.includes("Sub")?"👷":m.type.includes("Walk")?"🚶":m.type.includes("Safety")?"🦺":"📝"}
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:2}}>{m.type}</div>
            <div style={{fontSize:11,color:T.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
              {m.date} · {m.phase} · {m.attendees||"No attendees listed"}
            </div>
          </div>
          <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
            {m.summary&&<span style={{fontSize:9,fontWeight:700,background:T.purpleBg,color:T.purple,borderRadius:8,padding:"1px 7px"}}>AI SUMMARY</span>}
            <span style={{fontSize:10,color:T.muted}}>›</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── PROJECT AI PM VIEW ───────────────────────────────────────────────────────
// Project-specific AI PM chat — context-aware per project
function ProjectAIPMView({proj, session, projCOs, projRFIs}) {
  const memKey = "grok_ai_chat_" + (proj?.id||"none");
  const [messages, setMessages] = React.useState(()=>{
    try{ return JSON.parse(localStorage.getItem(memKey)||"[]"); } catch{ return []; }
  });
  const [input, setInput] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const bottomRef = React.useRef(null);
  const isMob = window.innerWidth < 768;

  const save = (msgs) => { setMessages(msgs); try{ localStorage.setItem(memKey,JSON.stringify(msgs.slice(-50))); }catch(e){} };

  React.useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:"smooth"}); },[messages]);

  const QUICK = [
    "What should I prioritize on this project today?",
    "Summarize all open issues and risks",
    "Draft a progress update email to the owner",
    "What permits and inspections are coming up?",
    "Write a CO justification for unforeseen conditions",
    "What sub coordination is needed this week?",
    "Check for schedule risks and delays",
    "Generate a punch list for this phase",
  ];

  const send = async (msg) => {
    const text = msg || input.trim();
    if (!text) return;
    setInput("");
    const newMessages = [...messages, {role:"user", content:text}];
    save(newMessages);
    setLoading(true);

    const projContext = `Project: ${proj?.name}\nAddress: ${proj?.addr}\nType: ${proj?.type}\nProgress: ${proj?.pct||0}%\nContract: $${(proj?.value||0).toLocaleString()}\nStatus: ${proj?.status}\nOpen COs: ${(projCOs||[]).filter(c=>c.status==="pending").length}\nOpen RFIs: ${(projRFIs||[]).filter(r=>r.status!=="closed").length}\nOwner: ${proj?.client||""}`;

    try {
      const resp = await fetch(AI_PROXY_URL, {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+sb._token(),"apikey":SUPABASE_ANON_KEY},
        body:JSON.stringify({
          body:{
          system:`You are the AI Project Manager for Grok Construction Group Inc (CSLB #1152750), a Bay Area GC run by Jehoon Chun. You know this specific project inside out:\n\n${projContext}\n\nBe direct, practical, and specific. Reference actual project names, numbers, and data. No generic advice. This is for a busy GC.`,
          messages:newMessages,
          max_tokens:1500},
          usage_type:"chat",
        })
      });
      const data = await resp.json();
      const reply = data.content?.[0]?.text || "Error — try again.";
      save([...newMessages, {role:"assistant", content:reply}]);
    } catch(e) {
      save([...newMessages, {role:"assistant", content:"Error: "+e.message}]);
    }
    setLoading(false);
  };

  // Only ever rendered inside ProjectHubView's flex:1/minHeight:0/overflow:hidden
  // slot — a real bounding parent — so fill it (height:100%) instead of guessing a
  // viewport offset. This also stops the input row from being clipped by the
  // parent's overflow:hidden when the old calc came out taller than the slot.
  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",minHeight:0}}>
      {/* Project context banner */}
      <div style={{background:T.bg2,borderRadius:9,padding:"10px 14px",marginBottom:10,display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:T.text}}>{proj?.name}</div>
          <div style={{fontSize:10,color:T.muted}}>{proj?.pct||0}% complete · {(projCOs||[]).filter(c=>c.status==="pending").length} pending COs · {(projRFIs||[]).filter(r=>r.status!=="closed").length} open RFIs</div>
        </div>
        <button onClick={()=>{ if(true) save([]); }}
          style={{fontSize:10,background:"none",border:"1px solid "+T.subtle,borderRadius:6,padding:"3px 8px",color:T.muted,cursor:"pointer"}}>
          Clear
        </button>
      </div>

      {/* Messages — flex:1 takes all available space */}
      <div style={{flex:1,overflowY:"auto",marginBottom:10,display:"flex",flexDirection:"column",gap:8,WebkitOverflowScrolling:"touch"}}>
        {messages.length===0&&(
          <div style={{textAlign:"center",padding:"32px 16px",color:T.muted}}>
            <div style={{fontSize:32,marginBottom:8}}>🤖</div>
            <div style={{fontSize:13,fontWeight:600,color:T.text,marginBottom:4}}>AI Project Manager</div>
            <div style={{fontSize:11,lineHeight:1.6}}>I know everything about {proj?.name}. Ask me anything about this project.</div>
          </div>
        )}
        {messages.map((m,i)=>(
          <div key={i} style={{display:"flex",justifyContent:m.role==="user"?"flex-end":"flex-start"}}>
            <div style={{maxWidth:"85%",background:m.role==="user"?T.gold:T.bg2,color:m.role==="user"?T.bg0:T.text,
              borderRadius:m.role==="user"?"12px 12px 3px 12px":"12px 12px 12px 3px",
              padding:"9px 13px",fontSize:12,lineHeight:1.6,whiteSpace:"pre-wrap"}}>
              {m.content}
            </div>
          </div>
        ))}
        {loading&&<div style={{display:"flex",gap:6,alignItems:"center",padding:"4px 0"}}><Spin/><span style={{fontSize:11,color:T.muted}}>thinking about {proj?.name?.split(" — ")[0]}...</span></div>}
        <div ref={bottomRef}/>
      </div>

      {/* Quick prompts */}
      {messages.length===0&&(
        <div style={{display:"grid",gridTemplateColumns:isMob?"1fr":"1fr 1fr",gap:5,marginBottom:10,flexShrink:0}}>
          {QUICK.map((q,i)=>(
            <button key={i} onClick={()=>send(q)}
              style={{textAlign:"left",padding:"7px 10px",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:7,fontSize:11,color:T.muted,cursor:"pointer",lineHeight:1.4}}>
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Input — always pinned at bottom */}
      <div className="chat-input-row" style={{display:"flex",gap:8,flexShrink:0,paddingTop:8,borderTop:"1px solid "+T.subtle}}>
        <input value={input} onChange={e=>setInput(e.target.value)}
          onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
          placeholder={"Ask about "+(proj?.name?.split(" — ")[0]||"this project")+"..."}
          style={{flex:1,background:T.bg2,border:"1px solid "+T.subtle,borderRadius:9,padding:"10px 14px",color:T.text,fontSize:16,fontFamily:"inherit"}}/>
        <button onClick={()=>send()} disabled={!input.trim()||loading}
          style={{width:42,height:42,borderRadius:"50%",background:input.trim()&&!loading?T.gold:T.bg3,color:input.trim()&&!loading?T.bg0:"white",border:"none",cursor:"pointer",fontSize:18,transition:"all 0.15s"}}>
          ▶
        </button>
      </div>
    </div>
  );
}

// ─── PROJECT DECISIONS VIEW ───────────────────────────────────────────────────
// Log of key decisions made on this project — extractable from meetings + manual
function ProjectDecisionsView({proj, session}) {
  const key = "grok_decisions_" + (session?.companyId||"local") + "_" + proj?.id;
  const [decisions, setDecisions] = React.useState(()=>{
    try{ return JSON.parse(localStorage.getItem(key)||"[]"); } catch{ return []; }
  });
  const [showNew, setShowNew] = React.useState(false);
  const [form, setForm] = React.useState({decision:"",madeBy:"",date:new Date().toLocaleDateString(),category:"Design",impact:""});
  const isMob = window.innerWidth < 768;

  const save = (list) => { setDecisions(list); try{ localStorage.setItem(key,JSON.stringify(list)); }catch(e){} };

  const CATS = ["Design","Structural","MEP","Finishes","Schedule","Budget","Contract","Safety","Permit"];
  const CAT_COLORS = {Design:T.blue,Structural:T.amber,MEP:T.teal,Finishes:T.purple,Schedule:T.gold,Budget:T.green,Contract:T.red,Safety:T.orange,Permit:T.blue};

  const inp = {width:"100%",background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"8px 12px",color:T.text,fontSize:13,fontFamily:"inherit"};

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>Decisions Log</div>
          <div style={{fontSize:10,color:T.muted}}>Key decisions made on this project — protects all parties</div>
        </div>
        <button onClick={()=>setShowNew(p=>!p)}
          style={{background:showNew?T.bg2:T.gold,color:showNew?T.muted:T.bg0,border:"1px solid "+(showNew?T.subtle:T.gold),borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
          {showNew?"✕ Cancel":"+ Log Decision"}
        </button>
      </div>

      {showNew&&(
        <div style={{background:T.bg1,border:"1px solid "+T.gold+"40",borderRadius:12,padding:"16px",marginBottom:16}}>
          <div style={{display:"grid",gridTemplateColumns:isMob?"1fr":"1fr 1fr 1fr",gap:10,marginBottom:10}}>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",marginBottom:4}}>Category</div>
              <select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))} style={inp}>
                {CATS.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",marginBottom:4}}>Made By</div>
              <input value={form.madeBy} onChange={e=>setForm(p=>({...p,madeBy:e.target.value}))}
                placeholder="Jehoon, Owner, Architect..." style={inp}/>
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",marginBottom:4}}>Date</div>
              <input type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))} style={inp}/>
            </div>
          </div>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",marginBottom:4}}>Decision</div>
            <textarea value={form.decision} onChange={e=>setForm(p=>({...p,decision:e.target.value}))}
              rows={3} placeholder="e.g. Owner approved upgraded panel to 200A. No CO needed — covered under original contingency."
              style={{...inp,resize:"vertical"}}/>
          </div>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",marginBottom:4}}>Impact (optional)</div>
            <input value={form.impact} onChange={e=>setForm(p=>({...p,impact:e.target.value}))}
              placeholder="e.g. Adds 3 days to electrical schedule, no cost impact" style={inp}/>
          </div>
          <button onClick={()=>{
            if(!form.decision.trim()) return;
            const d = {...form, id:"dec_"+Date.now()};
            save([d,...decisions]);
            setForm({decision:"",madeBy:"",date:new Date().toLocaleDateString(),category:"Design",impact:""});
            setShowNew(false);
          }} style={{width:"100%",padding:"10px",background:T.gold,border:"none",borderRadius:8,color:T.bg0,fontSize:13,fontWeight:700,cursor:"pointer"}}>
            ✓ Save Decision
          </button>
        </div>
      )}

      {decisions.length===0&&!showNew?(
        <div style={{textAlign:"center",padding:"40px 24px",color:T.muted}}>
          <div style={{fontSize:36,marginBottom:10}}>✅</div>
          <div style={{fontSize:14,fontWeight:600,color:T.text,marginBottom:6}}>No decisions logged yet</div>
          <div style={{fontSize:12,lineHeight:1.7,maxWidth:400,margin:"0 auto"}}>Log every key decision made on this project — design choices, scope changes, owner approvals, structural decisions. Creates a legal record that protects all parties.</div>
        </div>
      ):decisions.map(d=>(
        <div key={d.id} style={{background:T.bg1,border:"1px solid "+T.subtle,borderRadius:10,padding:"12px 16px",marginBottom:8}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
            <span style={{fontSize:10,fontWeight:700,background:(CAT_COLORS[d.category]||T.blue)+"20",color:CAT_COLORS[d.category]||T.blue,borderRadius:8,padding:"2px 8px"}}>
              {d.category}
            </span>
            <span style={{fontSize:11,color:T.muted}}>{d.date}</span>
            {d.madeBy&&<span style={{fontSize:11,color:T.muted}}>· {d.madeBy}</span>}
            <button onClick={()=>{ if(true) save(decisions.filter(x=>String(x.id)!==String(d.id))); }}
              style={{marginLeft:"auto",fontSize:10,background:"none",border:"none",color:T.muted,cursor:"pointer"}}>🗑</button>
          </div>
          <div style={{fontSize:12,color:T.text,lineHeight:1.6,marginBottom:d.impact?6:0}}>{d.decision}</div>
          {d.impact&&<div style={{fontSize:11,color:T.muted,fontStyle:"italic"}}>Impact: {d.impact}</div>}
        </div>
      ))}
    </div>
  );
}


// ─── TERMS OF SERVICE ─────────────────────────────────────────────────────────
function TermsOfServiceView({onBack}) {
  return (
    <div style={{maxWidth:760,margin:"0 auto",padding:"40px 24px",color:T.text,fontFamily:"inherit"}}>
      <button onClick={onBack} style={{background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"6px 14px",color:T.muted,fontSize:12,cursor:"pointer",marginBottom:24}}>← Back</button>
      <div style={{fontSize:28,fontWeight:700,color:T.text,marginBottom:4}}>Terms of Service</div>
      <div style={{fontSize:12,color:T.muted,marginBottom:32}}>Last updated: May 3, 2026 · [Your Company Name]</div>

      {[
        ["1. Acceptance of Terms","By accessing or using Grok Field Ops ('the Service'), you agree to be bound by these Terms of Service. If you do not agree, do not use the Service. These terms constitute a legally binding agreement between you and Grok Construction Group Inc ('Company', 'we', 'us')."],
        ["2. Description of Service","Grok Field Ops is a SaaS construction management platform providing AI-powered estimating, project management, document generation, subcontractor management, and field operations tools for general contractors and construction professionals."],
        ["3. Subscription & Payment","Access to the Service requires a paid subscription. Fees are charged in advance on a monthly or annual basis. All fees are non-refundable except as provided in our 15-day money-back guarantee. We reserve the right to change pricing with 30 days notice. Failure to pay may result in suspension or termination of your account."],
        ["4. 7-Day Money-Back Guarantee","If you are not satisfied with the Service, you may request a full refund within 7 days of your initial payment. After 7 days, no refunds will be issued. Each account is limited to a maximum of 2 cancellations. Accounts that exceed 2 cancellations are permanently closed and ineligible to re-subscribe."],
        ["5. AI Features & Usage Limits","AI features are subject to monthly usage limits based on your subscription plan. We use Anthropic's Claude AI models to power these features. AI-generated content including estimates, proposals, and documents should be reviewed by qualified professionals before use. We do not guarantee the accuracy of AI-generated content. Usage limits reset monthly on your billing anniversary."],
        ["6. User Responsibilities","You are responsible for: (a) maintaining the confidentiality of your account credentials; (b) all activity occurring under your account; (c) ensuring all information you provide is accurate and lawful; (d) complying with all applicable laws including California contractor licensing requirements; (e) independently verifying all AI-generated estimates, contracts, and legal documents before use."],
        ["7. Data & Privacy","You retain ownership of all data you input into the Service. By using the Service, you grant us a limited license to process your data to provide the Service. We implement reasonable security measures to protect your data. See our Privacy Policy for details on how we collect, use, and protect your information."],
        ["8. Intellectual Property","The Service, including all software, AI models, templates, and content, is owned by Grok Construction Group Inc and protected by intellectual property laws. You may not copy, modify, distribute, or reverse engineer any part of the Service. AI-generated output created using your inputs belongs to you."],
        ["9. Disclaimer of Warranties","THE SERVICE IS PROVIDED 'AS IS' WITHOUT WARRANTIES OF ANY KIND. WE DO NOT WARRANT THAT AI-GENERATED ESTIMATES, CONTRACTS, OR DOCUMENTS ARE ACCURATE, COMPLETE, OR LEGALLY SUFFICIENT. ALL AI OUTPUT SHOULD BE REVIEWED BY QUALIFIED PROFESSIONALS. WE ARE NOT RESPONSIBLE FOR DECISIONS MADE BASED ON AI-GENERATED CONTENT."],
        ["10. Limitation of Liability","TO THE MAXIMUM EXTENT PERMITTED BY LAW, GROK CONSTRUCTION GROUP INC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID IN THE 12 MONTHS PRECEDING THE CLAIM."],
        ["11. Termination","We may suspend or terminate your account for violation of these Terms, non-payment, or any reason with 30 days notice. Upon termination, your right to access the Service ends immediately. You may export your data within 30 days of termination."],
        ["12. Governing Law","These Terms are governed by the laws of the State of California. Any disputes shall be resolved in San Mateo County, California. You agree to resolve disputes through binding arbitration rather than court proceedings, except for injunctive relief."],
        ["13. Changes to Terms","We may update these Terms at any time. We will notify you by email or in-app notice. Continued use of the Service after changes constitutes acceptance of the new Terms."],
        ["14. Contact","For questions about these Terms, contact us at: Grok Construction Group Inc · 2201 Bridgepointe Pkwy F125, San Mateo CA 94404 · jhcftgroup@gmail.com · (650) 773-5317"],
      ].map(([title,text])=>(
        <div key={title} style={{marginBottom:24}}>
          <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:8}}>{title}</div>
          <div style={{fontSize:13,color:T.muted,lineHeight:1.85}}>{text}</div>
        </div>
      ))}
    </div>
  );
}

// ─── PRIVACY POLICY ───────────────────────────────────────────────────────────
function PrivacyPolicyView({onBack}) {
  return (
    <div style={{maxWidth:760,margin:"0 auto",padding:"40px 24px",color:T.text,fontFamily:"inherit"}}>
      <button onClick={onBack} style={{background:T.bg2,border:"1px solid "+T.subtle,borderRadius:8,padding:"6px 14px",color:T.muted,fontSize:12,cursor:"pointer",marginBottom:24}}>← Back</button>
      <div style={{fontSize:28,fontWeight:700,color:T.text,marginBottom:4}}>Privacy Policy</div>
      <div style={{fontSize:12,color:T.muted,marginBottom:32}}>Last updated: May 3, 2026 · [Your Company Name]</div>

      {[
        ["1. Information We Collect","We collect information you provide directly including: company name, contact information, CSLB license number, project data, estimates, documents, and communications. We also collect usage data, device information, and IP addresses automatically when you use the Service."],
        ["2. How We Use Your Information","We use your information to: provide and improve the Service; process payments via Stripe; send service notifications and updates; generate AI-powered estimates and documents; provide customer support; comply with legal obligations; and analyze usage to improve features."],
        ["3. AI Processing","When you use AI features, your project descriptions, uploaded documents, and inputs are sent to Anthropic's Claude AI via our secure proxy server. We do not store your AI conversations beyond what is necessary to provide the Service. Anthropic's privacy policy applies to AI processing."],
        ["4. Data Storage","Your data is stored in Supabase (PostgreSQL) servers located in the United States. We implement industry-standard encryption for data in transit and at rest. Project data, documents, and account information are stored securely and backed up regularly."],
        ["5. Payment Processing","Payments are processed by Stripe. We do not store credit card information. Stripe's privacy policy governs payment data. We receive transaction confirmation and subscription status from Stripe."],
        ["6. Data Sharing","We do not sell your data. We share data only with: Stripe (payment processing); Anthropic (AI features); Supabase (data storage); and as required by law. We may share aggregated, non-identifiable data for business purposes."],
        ["7. California Privacy Rights (CCPA)","California residents have the right to: know what personal information we collect; request deletion of personal information; opt out of sale of personal information (we do not sell data); and non-discrimination for exercising these rights. To exercise these rights, contact us at jhcftgroup@gmail.com."],
        ["8. Data Retention","We retain your data for as long as your account is active plus 90 days after termination for data export. You may request deletion of your data at any time. Some data may be retained longer as required by law."],
        ["9. Security","We implement reasonable technical and organizational measures to protect your data including: TLS encryption for data in transit; encrypted database storage; access controls; and regular security reviews. No system is 100% secure and we cannot guarantee absolute security."],
        ["10. Children's Privacy","The Service is not directed to children under 18. We do not knowingly collect information from children. If you believe a child has provided us information, contact us immediately."],
        ["11. Contact Us","For privacy questions or to exercise your rights: Grok Construction Group Inc · 2201 Bridgepointe Pkwy F125, San Mateo CA 94404 · jhcftgroup@gmail.com · (650) 773-5317"],
      ].map(([title,text])=>(
        <div key={title} style={{marginBottom:24}}>
          <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:8}}>{title}</div>
          <div style={{fontSize:13,color:T.muted,lineHeight:1.85}}>{text}</div>
        </div>
      ))}
    </div>
  );
}


// Phase 2 auth migration: forced password reset page reached via
// /migrate?migration_token=…&email=…. Validates the token + new password,
// calls the migrate-account edge function to create the auth.users entry
// and mark companies.migrated_at.
function MigrationPage({token, email}) {
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState(false);

  const handleSubmit = async () => {
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirmPassword) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${SUPABASE_URL}/functions/v1/migrate-account`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({token, password}),
      });
      const data = await r.json();
      if (!r.ok) { setError(data.error || "Migration failed"); setSubmitting(false); return; }
      setSuccess(true);
    } catch (e) {
      setError("Network error — please try again.");
    }
    setSubmitting(false);
  };

  const wrap = {minHeight:"100vh",background:T.bg0,display:"flex",alignItems:"center",justifyContent:"center",padding:20};
  const card = {maxWidth:440,width:"100%",background:T.bg1,border:`1px solid ${T.subtle}`,borderRadius:14,padding:32};
  const label = {fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:4};
  const input = {width:"100%",background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:9,padding:"10px 14px",color:T.text,fontSize:13,outline:"none",boxSizing:"border-box"};
  const primaryBtn = {width:"100%",padding:"12px",background:T.gold,color:T.bg0,fontSize:13,fontWeight:700,borderRadius:9,border:"none"};

  if (success) {
    return (
      <div style={wrap}>
        <div style={{...card, textAlign:"center"}}>
          <div style={{fontSize:36,marginBottom:12,color:T.green}}>✓</div>
          <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:8}}>Account migrated</div>
          <div style={{fontSize:13,color:T.muted,lineHeight:1.7,marginBottom:20}}>
            Your account security has been upgraded. Sign in with your new password to continue.
          </div>
          <button onClick={()=>{window.location.href = "/";}}
            style={{...primaryBtn, cursor:"pointer"}}>
            Go to Login →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{fontSize:22,fontWeight:800,color:T.gold,marginBottom:6}}>🔒 Security Upgrade</div>
          <div style={{fontSize:13,color:T.muted,lineHeight:1.7}}>
            We're upgrading Grok Field Ops to a more secure authentication system. Set a new password to keep your account active — your data and subscription are unchanged.
          </div>
        </div>

        {email && (
          <div style={{background:T.bg2,border:`1px solid ${T.subtle}`,borderRadius:9,padding:"10px 14px",marginBottom:16}}>
            <div style={{...label, marginBottom:3}}>Account</div>
            <div style={{fontSize:13,color:T.text,fontWeight:600}}>{email}</div>
          </div>
        )}

        <div style={{marginBottom:14}}>
          <div style={label}>New Password</div>
          <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
            placeholder="At least 8 characters" style={input}/>
        </div>

        <div style={{marginBottom:14}}>
          <div style={label}>Confirm Password</div>
          <input type="password" value={confirmPassword} onChange={e=>setConfirmPassword(e.target.value)}
            placeholder="Re-enter the password"
            onKeyDown={e=>e.key==="Enter"&&!submitting&&handleSubmit()}
            style={input}/>
        </div>

        {error && (
          <div style={{background:T.redBg,border:`1px solid ${T.red}40`,borderRadius:8,padding:"10px 14px",fontSize:12,color:T.red,marginBottom:14}}>
            {error}
          </div>
        )}

        <button onClick={handleSubmit} disabled={submitting||!password||!confirmPassword}
          style={{
            ...primaryBtn,
            cursor: submitting ? "wait" : "pointer",
            opacity: (submitting||!password||!confirmPassword) ? 0.5 : 1,
          }}>
          {submitting ? "Setting password…" : "Set new password →"}
        </button>

        <div style={{fontSize:10,color:T.muted,textAlign:"center",marginTop:14,lineHeight:1.5}}>
          Trouble with this link? Email <span style={{color:T.gold}}>support@grokfieldops.com</span>
        </div>
      </div>
    </div>
  );
}

// Public daily site report at /log?c=<companyId>&p=<projectId>&pn=<projectName>.
// No auth required — field workers scan a QR code on the job site and submit a
// short daily log directly to Supabase via the anon-allowed insert policy on
// daily_reports. The companies row (gated by RLS) is not queried — project name
// is passed in the URL by the QR generator so we don't need a project lookup.
function DailyLogPage() {
  const params = new URLSearchParams(window.location.search);
  const companyId   = params.get("c") || params.get("company") || "";
  const projectId   = params.get("p") || params.get("project") || "";
  const projectName = params.get("pn") || "";

  const [trade, setTrade] = React.useState("");
  const [crew, setCrew] = React.useState("");
  const [workDone, setWorkDone] = React.useState("");
  const [requests, setRequests] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState("");

  const TRADES = [
    "Electrical", "Plumbing", "HVAC", "Framing", "Drywall", "Tile & Finishes",
    "Concrete", "Roofing", "Painting", "Insulation", "Fire Sprinkler",
    "Low Voltage", "Flooring", "Demo", "Site Prep", "General Labor", "Other"
  ];

  const submit = async () => {
    setError("");
    if (!trade) { setError("Pick your trade first."); return; }
    const crewN = parseInt(crew, 10);
    if (!crewN || crewN < 1) { setError("Enter how many were on your crew today."); return; }
    if (!workDone.trim()) { setError("Tell us what work got done today."); return; }

    setSubmitting(true);
    try {
      const r = await fetch(`${SUPABASE_URL}/rest/v1/daily_reports`, {
        method: "POST",
        headers: {
          "apikey": SUPABASE_ANON_KEY,
          "Authorization": "Bearer " + SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
          "Prefer": "return=minimal",
        },
        body: JSON.stringify({
          company_id: companyId,
          project_id: projectId,
          trade,
          crew_count: crewN,
          work_done: workDone.trim(),
          requests: requests.trim() || null,
        }),
      });
      if (!r.ok) {
        const txt = await r.text().catch(() => "");
        console.error("daily_reports insert failed:", r.status, txt);
        setError(`Couldn't submit (status ${r.status}). Try again or text your GC directly.`);
        setSubmitting(false);
        return;
      }
      setSuccess(true);
    } catch (e) {
      setError("Network error. Check your connection and try again.");
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSuccess(false); setTrade(""); setCrew(""); setWorkDone(""); setRequests(""); setError("");
  };

  const wrap = { minHeight: "100vh", background: T.bg0, padding: 24, paddingTop: 32 };
  const card = { maxWidth: 480, margin: "0 auto", background: T.bg1, border: `1px solid ${T.subtle}`, borderRadius: 14, padding: 24 };
  const label = { fontSize: 11, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 };
  const fieldBase = { width: "100%", background: T.bg2, border: `1px solid ${T.subtle}`, borderRadius: 10, color: T.text, fontSize: 16, outline: "none", boxSizing: "border-box", fontFamily: "inherit" };
  const inputStyle = { ...fieldBase, padding: "14px" };
  const textareaStyle = { ...fieldBase, padding: "12px 14px", resize: "vertical" };
  const primaryBtn = { width: "100%", padding: "16px", background: T.gold, color: T.bg0, fontSize: 15, fontWeight: 700, borderRadius: 10, border: "none" };

  if (!companyId || !projectId) {
    return (
      <div style={{ ...wrap, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ maxWidth: 420, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 8 }}>Invalid QR link</div>
          <div style={{ fontSize: 14, color: T.muted, lineHeight: 1.6 }}>
            This link is missing project info. Scan the QR code from your GC's site card again, or ask them for a fresh one.
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{ ...wrap, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ maxWidth: 420, width: "100%", background: T.bg1, border: `1px solid ${T.subtle}`, borderRadius: 14, padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 56, marginBottom: 16, color: T.green }}>✓</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.text, marginBottom: 8 }}>Submitted</div>
          <div style={{ fontSize: 14, color: T.muted, marginBottom: 24, lineHeight: 1.7 }}>
            Thanks — your report is on file with the GC.
            {projectName ? <> Project: <strong style={{ color: T.text }}>{projectName}</strong>.</> : null}
          </div>
          <button onClick={reset} style={{ ...primaryBtn, cursor: "pointer" }}>
            Submit another report →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div style={{ maxWidth: 480, margin: "0 auto 16px", textAlign: "center" }}>
        <div style={{ fontSize: 24, fontWeight: 800, color: T.gold, marginBottom: 4 }}>Daily Site Report</div>
        {projectName && <div style={{ fontSize: 14, color: T.text, fontWeight: 600, marginBottom: 4 }}>{projectName}</div>}
        <div style={{ fontSize: 12, color: T.muted }}>Quick log — about 30 seconds.</div>
      </div>

      <div style={card}>
        <div style={{ marginBottom: 18 }}>
          <div style={label}>Your Trade</div>
          <select value={trade} onChange={e => setTrade(e.target.value)} style={inputStyle}>
            <option value="">Pick a trade…</option>
            {TRADES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={label}>Crew Today</div>
          <input type="number" inputMode="numeric" min="1" value={crew} onChange={e => setCrew(e.target.value)}
            placeholder="How many on site" style={inputStyle} />
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={label}>Work Done Today</div>
          <textarea value={workDone} onChange={e => setWorkDone(e.target.value)} rows={4}
            placeholder="What did the crew complete?" style={textareaStyle} />
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={label}>Requests / Needs from GC</div>
          <textarea value={requests} onChange={e => setRequests(e.target.value)} rows={3}
            placeholder="Access needs, inspections, blockers, anything else (optional)" style={textareaStyle} />
        </div>

        {error && (
          <div style={{ background: T.redBg, border: `1px solid ${T.red}40`, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: T.red, marginBottom: 14 }}>
            {error}
          </div>
        )}

        <button onClick={submit} disabled={submitting}
          style={{ ...primaryBtn, cursor: submitting ? "wait" : "pointer", opacity: submitting ? 0.6 : 1 }}>
          {submitting ? "Submitting…" : "Submit Daily Report →"}
        </button>
      </div>

      <div style={{ textAlign: "center", fontSize: 11, color: T.muted, marginTop: 16, lineHeight: 1.6 }}>
        Powered by Grok Field Ops
      </div>
    </div>
  );
}

export default function AppWithAuth() {
  const [clientSession, setClientSession] = React.useState(null);
  const [migrationSession, setMigrationSession] = React.useState(null);

  // Check URL for migration token or client portal token
  React.useEffect(()=>{
    const params = new URLSearchParams(window.location.search);

    // Phase 2 auth migration takes precedence — single critical flow.
    const migrationToken = params.get("migration_token");
    if (migrationToken) {
      setMigrationSession({token: migrationToken, email: params.get("email") || ""});
      return;
    }

    const token = params.get("client_token");
    if (token) {
      // Find project with this token
      const allKeys = Object.keys(localStorage).filter(k=>k.startsWith("grok_projects_"));
      for (const key of allKeys) {
        try {
          const projs = JSON.parse(localStorage.getItem(key)||"[]");
          const proj = projs.find(p=>p.client_code===token||p.client_token===token);
          if (proj) {
            const cid = key.replace("grok_projects_","");
            setClientSession({proj, companyId:cid, token});
            return;
          }
        } catch(e){}
      }
      // Token in URL but not found yet — show login
      setClientSession({showLogin:true});
    }
  },[]);

  // Public daily site report page at /log (QR-scanned by field workers, no login).
  // Path-based — synchronous so it short-circuits before any state setup.
  if (typeof window !== "undefined" && window.location.pathname === "/log") {
    return <DailyLogPage/>;
  }

  // Migration flow (Phase 2)
  if (migrationSession) {
    return <MigrationPage token={migrationSession.token} email={migrationSession.email}/>;
  }

  // Client portal access
  if (clientSession?.proj) {
    return <ClientProjectPortalView proj={clientSession.proj} companyId={clientSession.companyId}
      onLogout={()=>{setClientSession(null);window.history.replaceState({},"",window.location.pathname);}}/>;
  }
  if (clientSession?.showLogin) {
    return <ClientPortalLogin onSuccess={s=>setClientSession(s)}/>;
  }

  return (
    <AuthGate>
      <App/>
    </AuthGate>
  );
}
